/*!
 * jQuery JavaScript Library v1.12.4
 * http://jquery.com/
 *
 * Includes Sizzle.js
 * http://sizzlejs.com/
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license
 * http://jquery.org/license
 *
 * Date: 2016-05-20T17:17Z
 */


(function( global, factory ) {

	if ( typeof module === "object" && typeof module.exports === "object" ) {
		// For CommonJS and CommonJS-like environments where a proper `window`
		// is present, execute the factory and get jQuery.
		// For environments that do not have a `window` with a `document`
		// (such as Node.js), expose a factory as module.exports.
		// This accentuates the need for the creation of a real `window`.
		// e.g. var jQuery = require("jquery")(window);
		// See ticket #14549 for more info.
		module.exports = global.document ?
			factory( global, true ) :
			function( w ) {
				if ( !w.document ) {
					throw new Error( "jQuery requires a window with a document" );
				}
				return factory( w );
			};
	} else {
		factory( global );
	}

// Pass this if window is not defined yet
}(typeof window !== "undefined" ? window : this, function( window, noGlobal ) {

// Support: Firefox 18+
// Can't be in strict mode, several libs including ASP.NET trace
// the stack via arguments.caller.callee and Firefox dies if
// you try to trace through "use strict" call chains. (#13335)
//"use strict";
var deletedIds = [];

var document = window.document;

var slice = deletedIds.slice;

var concat = deletedIds.concat;

var push = deletedIds.push;

var indexOf = deletedIds.indexOf;

var class2type = {};

var toString = class2type.toString;

var hasOwn = class2type.hasOwnProperty;

var support = {};



var
	version = "1.12.4",

	// Define a local copy of jQuery
	jQuery = function( selector, context ) {

		// The jQuery object is actually just the init constructor 'enhanced'
		// Need init if jQuery is called (just allow error to be thrown if not included)
		return new jQuery.fn.init( selector, context );
	},

	// Support: Android<4.1, IE<9
	// Make sure we trim BOM and NBSP
	rtrim = /^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g,

	// Matches dashed string for camelizing
	rmsPrefix = /^-ms-/,
	rdashAlpha = /-([\da-z])/gi,

	// Used by jQuery.camelCase as callback to replace()
	fcamelCase = function( all, letter ) {
		return letter.toUpperCase();
	};

jQuery.fn = jQuery.prototype = {

	// The current version of jQuery being used
	jquery: version,

	constructor: jQuery,

	// Start with an empty selector
	selector: "",

	// The default length of a jQuery object is 0
	length: 0,

	toArray: function() {
		return slice.call( this );
	},

	// Get the Nth element in the matched element set OR
	// Get the whole matched element set as a clean array
	get: function( num ) {
		return num != null ?

			// Return just the one element from the set
			( num < 0 ? this[ num + this.length ] : this[ num ] ) :

			// Return all the elements in a clean array
			slice.call( this );
	},

	// Take an array of elements and push it onto the stack
	// (returning the new matched element set)
	pushStack: function( elems ) {

		// Build a new jQuery matched element set
		var ret = jQuery.merge( this.constructor(), elems );

		// Add the old object onto the stack (as a reference)
		ret.prevObject = this;
		ret.context = this.context;

		// Return the newly-formed element set
		return ret;
	},

	// Execute a callback for every element in the matched set.
	each: function( callback ) {
		return jQuery.each( this, callback );
	},

	map: function( callback ) {
		return this.pushStack( jQuery.map( this, function( elem, i ) {
			return callback.call( elem, i, elem );
		} ) );
	},

	slice: function() {
		return this.pushStack( slice.apply( this, arguments ) );
	},

	first: function() {
		return this.eq( 0 );
	},

	last: function() {
		return this.eq( -1 );
	},

	eq: function( i ) {
		var len = this.length,
			j = +i + ( i < 0 ? len : 0 );
		return this.pushStack( j >= 0 && j < len ? [ this[ j ] ] : [] );
	},

	end: function() {
		return this.prevObject || this.constructor();
	},

	// For internal use only.
	// Behaves like an Array's method, not like a jQuery method.
	push: push,
	sort: deletedIds.sort,
	splice: deletedIds.splice
};

jQuery.extend = jQuery.fn.extend = function() {
	var src, copyIsArray, copy, name, options, clone,
		target = arguments[ 0 ] || {},
		i = 1,
		length = arguments.length,
		deep = false;

	// Handle a deep copy situation
	if ( typeof target === "boolean" ) {
		deep = target;

		// skip the boolean and the target
		target = arguments[ i ] || {};
		i++;
	}

	// Handle case when target is a string or something (possible in deep copy)
	if ( typeof target !== "object" && !jQuery.isFunction( target ) ) {
		target = {};
	}

	// extend jQuery itself if only one argument is passed
	if ( i === length ) {
		target = this;
		i--;
	}

	for ( ; i < length; i++ ) {

		// Only deal with non-null/undefined values
		if ( ( options = arguments[ i ] ) != null ) {

			// Extend the base object
			for ( name in options ) {
				src = target[ name ];
				copy = options[ name ];

				// Prevent never-ending loop
				if ( target === copy ) {
					continue;
				}

				// Recurse if we're merging plain objects or arrays
				if ( deep && copy && ( jQuery.isPlainObject( copy ) ||
					( copyIsArray = jQuery.isArray( copy ) ) ) ) {

					if ( copyIsArray ) {
						copyIsArray = false;
						clone = src && jQuery.isArray( src ) ? src : [];

					} else {
						clone = src && jQuery.isPlainObject( src ) ? src : {};
					}

					// Never move original objects, clone them
					target[ name ] = jQuery.extend( deep, clone, copy );

				// Don't bring in undefined values
				} else if ( copy !== undefined ) {
					target[ name ] = copy;
				}
			}
		}
	}

	// Return the modified object
	return target;
};

jQuery.extend( {

	// Unique for each copy of jQuery on the page
	expando: "jQuery" + ( version + Math.random() ).replace( /\D/g, "" ),

	// Assume jQuery is ready without the ready module
	isReady: true,

	error: function( msg ) {
		throw new Error( msg );
	},

	noop: function() {},

	// See test/unit/core.js for details concerning isFunction.
	// Since version 1.3, DOM methods and functions like alert
	// aren't supported. They return false on IE (#2968).
	isFunction: function( obj ) {
		return jQuery.type( obj ) === "function";
	},

	isArray: Array.isArray || function( obj ) {
		return jQuery.type( obj ) === "array";
	},

	isWindow: function( obj ) {
		/* jshint eqeqeq: false */
		return obj != null && obj == obj.window;
	},

	isNumeric: function( obj ) {

		// parseFloat NaNs numeric-cast false positives (null|true|false|"")
		// ...but misinterprets leading-number strings, particularly hex literals ("0x...")
		// subtraction forces infinities to NaN
		// adding 1 corrects loss of precision from parseFloat (#15100)
		var realStringObj = obj && obj.toString();
		return !jQuery.isArray( obj ) && ( realStringObj - parseFloat( realStringObj ) + 1 ) >= 0;
	},

	isEmptyObject: function( obj ) {
		var name;
		for ( name in obj ) {
			return false;
		}
		return true;
	},

	isPlainObject: function( obj ) {
		var key;

		// Must be an Object.
		// Because of IE, we also have to check the presence of the constructor property.
		// Make sure that DOM nodes and window objects don't pass through, as well
		if ( !obj || jQuery.type( obj ) !== "object" || obj.nodeType || jQuery.isWindow( obj ) ) {
			return false;
		}

		try {

			// Not own constructor property must be Object
			if ( obj.constructor &&
				!hasOwn.call( obj, "constructor" ) &&
				!hasOwn.call( obj.constructor.prototype, "isPrototypeOf" ) ) {
				return false;
			}
		} catch ( e ) {

			// IE8,9 Will throw exceptions on certain host objects #9897
			return false;
		}

		// Support: IE<9
		// Handle iteration over inherited properties before own properties.
		if ( !support.ownFirst ) {
			for ( key in obj ) {
				return hasOwn.call( obj, key );
			}
		}

		// Own properties are enumerated firstly, so to speed up,
		// if last one is own, then all properties are own.
		for ( key in obj ) {}

		return key === undefined || hasOwn.call( obj, key );
	},

	type: function( obj ) {
		if ( obj == null ) {
			return obj + "";
		}
		return typeof obj === "object" || typeof obj === "function" ?
			class2type[ toString.call( obj ) ] || "object" :
			typeof obj;
	},

	// Workarounds based on findings by Jim Driscoll
	// http://weblogs.java.net/blog/driscoll/archive/2009/09/08/eval-javascript-global-context
	globalEval: function( data ) {
		if ( data && jQuery.trim( data ) ) {

			// We use execScript on Internet Explorer
			// We use an anonymous function so that context is window
			// rather than jQuery in Firefox
			( window.execScript || function( data ) {
				window[ "eval" ].call( window, data ); // jscs:ignore requireDotNotation
			} )( data );
		}
	},

	// Convert dashed to camelCase; used by the css and data modules
	// Microsoft forgot to hump their vendor prefix (#9572)
	camelCase: function( string ) {
		return string.replace( rmsPrefix, "ms-" ).replace( rdashAlpha, fcamelCase );
	},

	nodeName: function( elem, name ) {
		return elem.nodeName && elem.nodeName.toLowerCase() === name.toLowerCase();
	},

	each: function( obj, callback ) {
		var length, i = 0;

		if ( isArrayLike( obj ) ) {
			length = obj.length;
			for ( ; i < length; i++ ) {
				if ( callback.call( obj[ i ], i, obj[ i ] ) === false ) {
					break;
				}
			}
		} else {
			for ( i in obj ) {
				if ( callback.call( obj[ i ], i, obj[ i ] ) === false ) {
					break;
				}
			}
		}

		return obj;
	},

	// Support: Android<4.1, IE<9
	trim: function( text ) {
		return text == null ?
			"" :
			( text + "" ).replace( rtrim, "" );
	},

	// results is for internal usage only
	makeArray: function( arr, results ) {
		var ret = results || [];

		if ( arr != null ) {
			if ( isArrayLike( Object( arr ) ) ) {
				jQuery.merge( ret,
					typeof arr === "string" ?
					[ arr ] : arr
				);
			} else {
				push.call( ret, arr );
			}
		}

		return ret;
	},

	inArray: function( elem, arr, i ) {
		var len;

		if ( arr ) {
			if ( indexOf ) {
				return indexOf.call( arr, elem, i );
			}

			len = arr.length;
			i = i ? i < 0 ? Math.max( 0, len + i ) : i : 0;

			for ( ; i < len; i++ ) {

				// Skip accessing in sparse arrays
				if ( i in arr && arr[ i ] === elem ) {
					return i;
				}
			}
		}

		return -1;
	},

	merge: function( first, second ) {
		var len = +second.length,
			j = 0,
			i = first.length;

		while ( j < len ) {
			first[ i++ ] = second[ j++ ];
		}

		// Support: IE<9
		// Workaround casting of .length to NaN on otherwise arraylike objects (e.g., NodeLists)
		if ( len !== len ) {
			while ( second[ j ] !== undefined ) {
				first[ i++ ] = second[ j++ ];
			}
		}

		first.length = i;

		return first;
	},

	grep: function( elems, callback, invert ) {
		var callbackInverse,
			matches = [],
			i = 0,
			length = elems.length,
			callbackExpect = !invert;

		// Go through the array, only saving the items
		// that pass the validator function
		for ( ; i < length; i++ ) {
			callbackInverse = !callback( elems[ i ], i );
			if ( callbackInverse !== callbackExpect ) {
				matches.push( elems[ i ] );
			}
		}

		return matches;
	},

	// arg is for internal usage only
	map: function( elems, callback, arg ) {
		var length, value,
			i = 0,
			ret = [];

		// Go through the array, translating each of the items to their new values
		if ( isArrayLike( elems ) ) {
			length = elems.length;
			for ( ; i < length; i++ ) {
				value = callback( elems[ i ], i, arg );

				if ( value != null ) {
					ret.push( value );
				}
			}

		// Go through every key on the object,
		} else {
			for ( i in elems ) {
				value = callback( elems[ i ], i, arg );

				if ( value != null ) {
					ret.push( value );
				}
			}
		}

		// Flatten any nested arrays
		return concat.apply( [], ret );
	},

	// A global GUID counter for objects
	guid: 1,

	// Bind a function to a context, optionally partially applying any
	// arguments.
	proxy: function( fn, context ) {
		var args, proxy, tmp;

		if ( typeof context === "string" ) {
			tmp = fn[ context ];
			context = fn;
			fn = tmp;
		}

		// Quick check to determine if target is callable, in the spec
		// this throws a TypeError, but we will just return undefined.
		if ( !jQuery.isFunction( fn ) ) {
			return undefined;
		}

		// Simulated bind
		args = slice.call( arguments, 2 );
		proxy = function() {
			return fn.apply( context || this, args.concat( slice.call( arguments ) ) );
		};

		// Set the guid of unique handler to the same of original handler, so it can be removed
		proxy.guid = fn.guid = fn.guid || jQuery.guid++;

		return proxy;
	},

	now: function() {
		return +( new Date() );
	},

	// jQuery.support is not used in Core but other projects attach their
	// properties to it so it needs to exist.
	support: support
} );

// JSHint would error on this code due to the Symbol not being defined in ES5.
// Defining this global in .jshintrc would create a danger of using the global
// unguarded in another place, it seems safer to just disable JSHint for these
// three lines.
/* jshint ignore: start */
if ( typeof Symbol === "function" ) {
	jQuery.fn[ Symbol.iterator ] = deletedIds[ Symbol.iterator ];
}
/* jshint ignore: end */

// Populate the class2type map
jQuery.each( "Boolean Number String Function Array Date RegExp Object Error Symbol".split( " " ),
function( i, name ) {
	class2type[ "[object " + name + "]" ] = name.toLowerCase();
} );

function isArrayLike( obj ) {

	// Support: iOS 8.2 (not reproducible in simulator)
	// `in` check used to prevent JIT error (gh-2145)
	// hasOwn isn't used here due to false negatives
	// regarding Nodelist length in IE
	var length = !!obj && "length" in obj && obj.length,
		type = jQuery.type( obj );

	if ( type === "function" || jQuery.isWindow( obj ) ) {
		return false;
	}

	return type === "array" || length === 0 ||
		typeof length === "number" && length > 0 && ( length - 1 ) in obj;
}
var Sizzle =
/*!
 * Sizzle CSS Selector Engine v2.2.1
 * http://sizzlejs.com/
 *
 * Copyright jQuery Foundation and other contributors
 * Released under the MIT license
 * http://jquery.org/license
 *
 * Date: 2015-10-17
 */
(function( window ) {

var i,
	support,
	Expr,
	getText,
	isXML,
	tokenize,
	compile,
	select,
	outermostContext,
	sortInput,
	hasDuplicate,

	// Local document vars
	setDocument,
	document,
	docElem,
	documentIsHTML,
	rbuggyQSA,
	rbuggyMatches,
	matches,
	contains,

	// Instance-specific data
	expando = "sizzle" + 1 * new Date(),
	preferredDoc = window.document,
	dirruns = 0,
	done = 0,
	classCache = createCache(),
	tokenCache = createCache(),
	compilerCache = createCache(),
	sortOrder = function( a, b ) {
		if ( a === b ) {
			hasDuplicate = true;
		}
		return 0;
	},

	// General-purpose constants
	MAX_NEGATIVE = 1 << 31,

	// Instance methods
	hasOwn = ({}).hasOwnProperty,
	arr = [],
	pop = arr.pop,
	push_native = arr.push,
	push = arr.push,
	slice = arr.slice,
	// Use a stripped-down indexOf as it's faster than native
	// http://jsperf.com/thor-indexof-vs-for/5
	indexOf = function( list, elem ) {
		var i = 0,
			len = list.length;
		for ( ; i < len; i++ ) {
			if ( list[i] === elem ) {
				return i;
			}
		}
		return -1;
	},

	booleans = "checked|selected|async|autofocus|autoplay|controls|defer|disabled|hidden|ismap|loop|multiple|open|readonly|required|scoped",

	// Regular expressions

	// http://www.w3.org/TR/css3-selectors/#whitespace
	whitespace = "[\\x20\\t\\r\\n\\f]",

	// http://www.w3.org/TR/CSS21/syndata.html#value-def-identifier
	identifier = "(?:\\\\.|[\\w-]|[^\\x00-\\xa0])+",

	// Attribute selectors: http://www.w3.org/TR/selectors/#attribute-selectors
	attributes = "\\[" + whitespace + "*(" + identifier + ")(?:" + whitespace +
		// Operator (capture 2)
		"*([*^$|!~]?=)" + whitespace +
		// "Attribute values must be CSS identifiers [capture 5] or strings [capture 3 or capture 4]"
		"*(?:'((?:\\\\.|[^\\\\'])*)'|\"((?:\\\\.|[^\\\\\"])*)\"|(" + identifier + "))|)" + whitespace +
		"*\\]",

	pseudos = ":(" + identifier + ")(?:\\((" +
		// To reduce the number of selectors needing tokenize in the preFilter, prefer arguments:
		// 1. quoted (capture 3; capture 4 or capture 5)
		"('((?:\\\\.|[^\\\\'])*)'|\"((?:\\\\.|[^\\\\\"])*)\")|" +
		// 2. simple (capture 6)
		"((?:\\\\.|[^\\\\()[\\]]|" + attributes + ")*)|" +
		// 3. anything else (capture 2)
		".*" +
		")\\)|)",

	// Leading and non-escaped trailing whitespace, capturing some non-whitespace characters preceding the latter
	rwhitespace = new RegExp( whitespace + "+", "g" ),
	rtrim = new RegExp( "^" + whitespace + "+|((?:^|[^\\\\])(?:\\\\.)*)" + whitespace + "+$", "g" ),

	rcomma = new RegExp( "^" + whitespace + "*," + whitespace + "*" ),
	rcombinators = new RegExp( "^" + whitespace + "*([>+~]|" + whitespace + ")" + whitespace + "*" ),

	rattributeQuotes = new RegExp( "=" + whitespace + "*([^\\]'\"]*?)" + whitespace + "*\\]", "g" ),

	rpseudo = new RegExp( pseudos ),
	ridentifier = new RegExp( "^" + identifier + "$" ),

	matchExpr = {
		"ID": new RegExp( "^#(" + identifier + ")" ),
		"CLASS": new RegExp( "^\\.(" + identifier + ")" ),
		"TAG": new RegExp( "^(" + identifier + "|[*])" ),
		"ATTR": new RegExp( "^" + attributes ),
		"PSEUDO": new RegExp( "^" + pseudos ),
		"CHILD": new RegExp( "^:(only|first|last|nth|nth-last)-(child|of-type)(?:\\(" + whitespace +
			"*(even|odd|(([+-]|)(\\d*)n|)" + whitespace + "*(?:([+-]|)" + whitespace +
			"*(\\d+)|))" + whitespace + "*\\)|)", "i" ),
		"bool": new RegExp( "^(?:" + booleans + ")$", "i" ),
		// For use in libraries implementing .is()
		// We use this for POS matching in `select`
		"needsContext": new RegExp( "^" + whitespace + "*[>+~]|:(even|odd|eq|gt|lt|nth|first|last)(?:\\(" +
			whitespace + "*((?:-\\d)?\\d*)" + whitespace + "*\\)|)(?=[^-]|$)", "i" )
	},

	rinputs = /^(?:input|select|textarea|button)$/i,
	rheader = /^h\d$/i,

	rnative = /^[^{]+\{\s*\[native \w/,

	// Easily-parseable/retrievable ID or TAG or CLASS selectors
	rquickExpr = /^(?:#([\w-]+)|(\w+)|\.([\w-]+))$/,

	rsibling = /[+~]/,
	rescape = /'|\\/g,

	// CSS escapes http://www.w3.org/TR/CSS21/syndata.html#escaped-characters
	runescape = new RegExp( "\\\\([\\da-f]{1,6}" + whitespace + "?|(" + whitespace + ")|.)", "ig" ),
	funescape = function( _, escaped, escapedWhitespace ) {
		var high = "0x" + escaped - 0x10000;
		// NaN means non-codepoint
		// Support: Firefox<24
		// Workaround erroneous numeric interpretation of +"0x"
		return high !== high || escapedWhitespace ?
			escaped :
			high < 0 ?
				// BMP codepoint
				String.fromCharCode( high + 0x10000 ) :
				// Supplemental Plane codepoint (surrogate pair)
				String.fromCharCode( high >> 10 | 0xD800, high & 0x3FF | 0xDC00 );
	},

	// Used for iframes
	// See setDocument()
	// Removing the function wrapper causes a "Permission Denied"
	// error in IE
	unloadHandler = function() {
		setDocument();
	};

// Optimize for push.apply( _, NodeList )
try {
	push.apply(
		(arr = slice.call( preferredDoc.childNodes )),
		preferredDoc.childNodes
	);
	// Support: Android<4.0
	// Detect silently failing push.apply
	arr[ preferredDoc.childNodes.length ].nodeType;
} catch ( e ) {
	push = { apply: arr.length ?

		// Leverage slice if possible
		function( target, els ) {
			push_native.apply( target, slice.call(els) );
		} :

		// Support: IE<9
		// Otherwise append directly
		function( target, els ) {
			var j = target.length,
				i = 0;
			// Can't trust NodeList.length
			while ( (target[j++] = els[i++]) ) {}
			target.length = j - 1;
		}
	};
}

function Sizzle( selector, context, results, seed ) {
	var m, i, elem, nid, nidselect, match, groups, newSelector,
		newContext = context && context.ownerDocument,

		// nodeType defaults to 9, since context defaults to document
		nodeType = context ? context.nodeType : 9;

	results = results || [];

	// Return early from calls with invalid selector or context
	if ( typeof selector !== "string" || !selector ||
		nodeType !== 1 && nodeType !== 9 && nodeType !== 11 ) {

		return results;
	}

	// Try to shortcut find operations (as opposed to filters) in HTML documents
	if ( !seed ) {

		if ( ( context ? context.ownerDocument || context : preferredDoc ) !== document ) {
			setDocument( context );
		}
		context = context || document;

		if ( documentIsHTML ) {

			// If the selector is sufficiently simple, try using a "get*By*" DOM method
			// (excepting DocumentFragment context, where the methods don't exist)
			if ( nodeType !== 11 && (match = rquickExpr.exec( selector )) ) {

				// ID selector
				if ( (m = match[1]) ) {

					// Document context
					if ( nodeType === 9 ) {
						if ( (elem = context.getElementById( m )) ) {

							// Support: IE, Opera, Webkit
							// TODO: identify versions
							// getElementById can match elements by name instead of ID
							if ( elem.id === m ) {
								results.push( elem );
								return results;
							}
						} else {
							return results;
						}

					// Element context
					} else {

						// Support: IE, Opera, Webkit
						// TODO: identify versions
						// getElementById can match elements by name instead of ID
						if ( newContext && (elem = newContext.getElementById( m )) &&
							contains( context, elem ) &&
							elem.id === m ) {

							results.push( elem );
							return results;
						}
					}

				// Type selector
				} else if ( match[2] ) {
					push.apply( results, context.getElementsByTagName( selector ) );
					return results;

				// Class selector
				} else if ( (m = match[3]) && support.getElementsByClassName &&
					context.getElementsByClassName ) {

					push.apply( results, context.getElementsByClassName( m ) );
					return results;
				}
			}

			// Take advantage of querySelectorAll
			if ( support.qsa &&
				!compilerCache[ selector + " " ] &&
				(!rbuggyQSA || !rbuggyQSA.test( selector )) ) {

				if ( nodeType !== 1 ) {
					newContext = context;
					newSelector = selector;

				// qSA looks outside Element context, which is not what we want
				// Thanks to Andrew Dupont for this workaround technique
				// Support: IE <=8
				// Exclude object elements
				} else if ( context.nodeName.toLowerCase() !== "object" ) {

					// Capture the context ID, setting it first if necessary
					if ( (nid = context.getAttribute( "id" )) ) {
						nid = nid.replace( rescape, "\\$&" );
					} else {
						context.setAttribute( "id", (nid = expando) );
					}

					// Prefix every selector in the list
					groups = tokenize( selector );
					i = groups.length;
					nidselect = ridentifier.test( nid ) ? "#" + nid : "[id='" + nid + "']";
					while ( i-- ) {
						groups[i] = nidselect + " " + toSelector( groups[i] );
					}
					newSelector = groups.join( "," );

					// Expand context for sibling selectors
					newContext = rsibling.test( selector ) && testContext( context.parentNode ) ||
						context;
				}

				if ( newSelector ) {
					try {
						push.apply( results,
							newContext.querySelectorAll( newSelector )
						);
						return results;
					} catch ( qsaError ) {
					} finally {
						if ( nid === expando ) {
							context.removeAttribute( "id" );
						}
					}
				}
			}
		}
	}

	// All others
	return select( selector.replace( rtrim, "$1" ), context, results, seed );
}

/**
 * Create key-value caches of limited size
 * @returns {function(string, object)} Returns the Object data after storing it on itself with
 *	property name the (space-suffixed) string and (if the cache is larger than Expr.cacheLength)
 *	deleting the oldest entry
 */
function createCache() {
	var keys = [];

	function cache( key, value ) {
		// Use (key + " ") to avoid collision with native prototype properties (see Issue #157)
		if ( keys.push( key + " " ) > Expr.cacheLength ) {
			// Only keep the most recent entries
			delete cache[ keys.shift() ];
		}
		return (cache[ key + " " ] = value);
	}
	return cache;
}

/**
 * Mark a function for special use by Sizzle
 * @param {Function} fn The function to mark
 */
function markFunction( fn ) {
	fn[ expando ] = true;
	return fn;
}

/**
 * Support testing using an element
 * @param {Function} fn Passed the created div and expects a boolean result
 */
function assert( fn ) {
	var div = document.createElement("div");

	try {
		return !!fn( div );
	} catch (e) {
		return false;
	} finally {
		// Remove from its parent by default
		if ( div.parentNode ) {
			div.parentNode.removeChild( div );
		}
		// release memory in IE
		div = null;
	}
}

/**
 * Adds the same handler for all of the specified attrs
 * @param {String} attrs Pipe-separated list of attributes
 * @param {Function} handler The method that will be applied
 */
function addHandle( attrs, handler ) {
	var arr = attrs.split("|"),
		i = arr.length;

	while ( i-- ) {
		Expr.attrHandle[ arr[i] ] = handler;
	}
}

/**
 * Checks document order of two siblings
 * @param {Element} a
 * @param {Element} b
 * @returns {Number} Returns less than 0 if a precedes b, greater than 0 if a follows b
 */
function siblingCheck( a, b ) {
	var cur = b && a,
		diff = cur && a.nodeType === 1 && b.nodeType === 1 &&
			( ~b.sourceIndex || MAX_NEGATIVE ) -
			( ~a.sourceIndex || MAX_NEGATIVE );

	// Use IE sourceIndex if available on both nodes
	if ( diff ) {
		return diff;
	}

	// Check if b follows a
	if ( cur ) {
		while ( (cur = cur.nextSibling) ) {
			if ( cur === b ) {
				return -1;
			}
		}
	}

	return a ? 1 : -1;
}

/**
 * Returns a function to use in pseudos for input types
 * @param {String} type
 */
function createInputPseudo( type ) {
	return function( elem ) {
		var name = elem.nodeName.toLowerCase();
		return name === "input" && elem.type === type;
	};
}

/**
 * Returns a function to use in pseudos for buttons
 * @param {String} type
 */
function createButtonPseudo( type ) {
	return function( elem ) {
		var name = elem.nodeName.toLowerCase();
		return (name === "input" || name === "button") && elem.type === type;
	};
}

/**
 * Returns a function to use in pseudos for positionals
 * @param {Function} fn
 */
function createPositionalPseudo( fn ) {
	return markFunction(function( argument ) {
		argument = +argument;
		return markFunction(function( seed, matches ) {
			var j,
				matchIndexes = fn( [], seed.length, argument ),
				i = matchIndexes.length;

			// Match elements found at the specified indexes
			while ( i-- ) {
				if ( seed[ (j = matchIndexes[i]) ] ) {
					seed[j] = !(matches[j] = seed[j]);
				}
			}
		});
	});
}

/**
 * Checks a node for validity as a Sizzle context
 * @param {Element|Object=} context
 * @returns {Element|Object|Boolean} The input node if acceptable, otherwise a falsy value
 */
function testContext( context ) {
	return context && typeof context.getElementsByTagName !== "undefined" && context;
}

// Expose support vars for convenience
support = Sizzle.support = {};

/**
 * Detects XML nodes
 * @param {Element|Object} elem An element or a document
 * @returns {Boolean} True iff elem is a non-HTML XML node
 */
isXML = Sizzle.isXML = function( elem ) {
	// documentElement is verified for cases where it doesn't yet exist
	// (such as loading iframes in IE - #4833)
	var documentElement = elem && (elem.ownerDocument || elem).documentElement;
	return documentElement ? documentElement.nodeName !== "HTML" : false;
};

/**
 * Sets document-related variables once based on the current document
 * @param {Element|Object} [doc] An element or document object to use to set the document
 * @returns {Object} Returns the current document
 */
setDocument = Sizzle.setDocument = function( node ) {
	var hasCompare, parent,
		doc = node ? node.ownerDocument || node : preferredDoc;

	// Return early if doc is invalid or already selected
	if ( doc === document || doc.nodeType !== 9 || !doc.documentElement ) {
		return document;
	}

	// Update global variables
	document = doc;
	docElem = document.documentElement;
	documentIsHTML = !isXML( document );

	// Support: IE 9-11, Edge
	// Accessing iframe documents after unload throws "permission denied" errors (jQuery #13936)
	if ( (parent = document.defaultView) && parent.top !== parent ) {
		// Support: IE 11
		if ( parent.addEventListener ) {
			parent.addEventListener( "unload", unloadHandler, false );

		// Support: IE 9 - 10 only
		} else if ( parent.attachEvent ) {
			parent.attachEvent( "onunload", unloadHandler );
		}
	}

	/* Attributes
	---------------------------------------------------------------------- */

	// Support: IE<8
	// Verify that getAttribute really returns attributes and not properties
	// (excepting IE8 booleans)
	support.attributes = assert(function( div ) {
		div.className = "i";
		return !div.getAttribute("className");
	});

	/* getElement(s)By*
	---------------------------------------------------------------------- */

	// Check if getElementsByTagName("*") returns only elements
	support.getElementsByTagName = assert(function( div ) {
		div.appendChild( document.createComment("") );
		return !div.getElementsByTagName("*").length;
	});

	// Support: IE<9
	support.getElementsByClassName = rnative.test( document.getElementsByClassName );

	// Support: IE<10
	// Check if getElementById returns elements by name
	// The broken getElementById methods don't pick up programatically-set names,
	// so use a roundabout getElementsByName test
	support.getById = assert(function( div ) {
		docElem.appendChild( div ).id = expando;
		return !document.getElementsByName || !document.getElementsByName( expando ).length;
	});

	// ID find and filter
	if ( support.getById ) {
		Expr.find["ID"] = function( id, context ) {
			if ( typeof context.getElementById !== "undefined" && documentIsHTML ) {
				var m = context.getElementById( id );
				return m ? [ m ] : [];
			}
		};
		Expr.filter["ID"] = function( id ) {
			var attrId = id.replace( runescape, funescape );
			return function( elem ) {
				return elem.getAttribute("id") === attrId;
			};
		};
	} else {
		// Support: IE6/7
		// getElementById is not reliable as a find shortcut
		delete Expr.find["ID"];

		Expr.filter["ID"] =  function( id ) {
			var attrId = id.replace( runescape, funescape );
			return function( elem ) {
				var node = typeof elem.getAttributeNode !== "undefined" &&
					elem.getAttributeNode("id");
				return node && node.value === attrId;
			};
		};
	}

	// Tag
	Expr.find["TAG"] = support.getElementsByTagName ?
		function( tag, context ) {
			if ( typeof context.getElementsByTagName !== "undefined" ) {
				return context.getElementsByTagName( tag );

			// DocumentFragment nodes don't have gEBTN
			} else if ( support.qsa ) {
				return context.querySelectorAll( tag );
			}
		} :

		function( tag, context ) {
			var elem,
				tmp = [],
				i = 0,
				// By happy coincidence, a (broken) gEBTN appears on DocumentFragment nodes too
				results = context.getElementsByTagName( tag );

			// Filter out possible comments
			if ( tag === "*" ) {
				while ( (elem = results[i++]) ) {
					if ( elem.nodeType === 1 ) {
						tmp.push( elem );
					}
				}

				return tmp;
			}
			return results;
		};

	// Class
	Expr.find["CLASS"] = support.getElementsByClassName && function( className, context ) {
		if ( typeof context.getElementsByClassName !== "undefined" && documentIsHTML ) {
			return context.getElementsByClassName( className );
		}
	};

	/* QSA/matchesSelector
	---------------------------------------------------------------------- */

	// QSA and matchesSelector support

	// matchesSelector(:active) reports false when true (IE9/Opera 11.5)
	rbuggyMatches = [];

	// qSa(:focus) reports false when true (Chrome 21)
	// We allow this because of a bug in IE8/9 that throws an error
	// whenever `document.activeElement` is accessed on an iframe
	// So, we allow :focus to pass through QSA all the time to avoid the IE error
	// See http://bugs.jquery.com/ticket/13378
	rbuggyQSA = [];

	if ( (support.qsa = rnative.test( document.querySelectorAll )) ) {
		// Build QSA regex
		// Regex strategy adopted from Diego Perini
		assert(function( div ) {
			// Select is set to empty string on purpose
			// This is to test IE's treatment of not explicitly
			// setting a boolean content attribute,
			// since its presence should be enough
			// http://bugs.jquery.com/ticket/12359
			docElem.appendChild( div ).innerHTML = "<a id='" + expando + "'></a>" +
				"<select id='" + expando + "-\r\\' msallowcapture=''>" +
				"<option selected=''></option></select>";

			// Support: IE8, Opera 11-12.16
			// Nothing should be selected when empty strings follow ^= or $= or *=
			// The test attribute must be unknown in Opera but "safe" for WinRT
			// http://msdn.microsoft.com/en-us/library/ie/hh465388.aspx#attribute_section
			if ( div.querySelectorAll("[msallowcapture^='']").length ) {
				rbuggyQSA.push( "[*^$]=" + whitespace + "*(?:''|\"\")" );
			}

			// Support: IE8
			// Boolean attributes and "value" are not treated correctly
			if ( !div.querySelectorAll("[selected]").length ) {
				rbuggyQSA.push( "\\[" + whitespace + "*(?:value|" + booleans + ")" );
			}

			// Support: Chrome<29, Android<4.4, Safari<7.0+, iOS<7.0+, PhantomJS<1.9.8+
			if ( !div.querySelectorAll( "[id~=" + expando + "-]" ).length ) {
				rbuggyQSA.push("~=");
			}

			// Webkit/Opera - :checked should return selected option elements
			// http://www.w3.org/TR/2011/REC-css3-selectors-20110929/#checked
			// IE8 throws error here and will not see later tests
			if ( !div.querySelectorAll(":checked").length ) {
				rbuggyQSA.push(":checked");
			}

			// Support: Safari 8+, iOS 8+
			// https://bugs.webkit.org/show_bug.cgi?id=136851
			// In-page `selector#id sibing-combinator selector` fails
			if ( !div.querySelectorAll( "a#" + expando + "+*" ).length ) {
				rbuggyQSA.push(".#.+[+~]");
			}
		});

		assert(function( div ) {
			// Support: Windows 8 Native Apps
			// The type and name attributes are restricted during .innerHTML assignment
			var input = document.createElement("input");
			input.setAttribute( "type", "hidden" );
			div.appendChild( input ).setAttribute( "name", "D" );

			// Support: IE8
			// Enforce case-sensitivity of name attribute
			if ( div.querySelectorAll("[name=d]").length ) {
				rbuggyQSA.push( "name" + whitespace + "*[*^$|!~]?=" );
			}

			// FF 3.5 - :enabled/:disabled and hidden elements (hidden elements are still enabled)
			// IE8 throws error here and will not see later tests
			if ( !div.querySelectorAll(":enabled").length ) {
				rbuggyQSA.push( ":enabled", ":disabled" );
			}

			// Opera 10-11 does not throw on post-comma invalid pseudos
			div.querySelectorAll("*,:x");
			rbuggyQSA.push(",.*:");
		});
	}

	if ( (support.matchesSelector = rnative.test( (matches = docElem.matches ||
		docElem.webkitMatchesSelector ||
		docElem.mozMatchesSelector ||
		docElem.oMatchesSelector ||
		docElem.msMatchesSelector) )) ) {

		assert(function( div ) {
			// Check to see if it's possible to do matchesSelector
			// on a disconnected node (IE 9)
			support.disconnectedMatch = matches.call( div, "div" );

			// This should fail with an exception
			// Gecko does not error, returns false instead
			matches.call( div, "[s!='']:x" );
			rbuggyMatches.push( "!=", pseudos );
		});
	}

	rbuggyQSA = rbuggyQSA.length && new RegExp( rbuggyQSA.join("|") );
	rbuggyMatches = rbuggyMatches.length && new RegExp( rbuggyMatches.join("|") );

	/* Contains
	---------------------------------------------------------------------- */
	hasCompare = rnative.test( docElem.compareDocumentPosition );

	// Element contains another
	// Purposefully self-exclusive
	// As in, an element does not contain itself
	contains = hasCompare || rnative.test( docElem.contains ) ?
		function( a, b ) {
			var adown = a.nodeType === 9 ? a.documentElement : a,
				bup = b && b.parentNode;
			return a === bup || !!( bup && bup.nodeType === 1 && (
				adown.contains ?
					adown.contains( bup ) :
					a.compareDocumentPosition && a.compareDocumentPosition( bup ) & 16
			));
		} :
		function( a, b ) {
			if ( b ) {
				while ( (b = b.parentNode) ) {
					if ( b === a ) {
						return true;
					}
				}
			}
			return false;
		};

	/* Sorting
	---------------------------------------------------------------------- */

	// Document order sorting
	sortOrder = hasCompare ?
	function( a, b ) {

		// Flag for duplicate removal
		if ( a === b ) {
			hasDuplicate = true;
			return 0;
		}

		// Sort on method existence if only one input has compareDocumentPosition
		var compare = !a.compareDocumentPosition - !b.compareDocumentPosition;
		if ( compare ) {
			return compare;
		}

		// Calculate position if both inputs belong to the same document
		compare = ( a.ownerDocument || a ) === ( b.ownerDocument || b ) ?
			a.compareDocumentPosition( b ) :

			// Otherwise we know they are disconnected
			1;

		// Disconnected nodes
		if ( compare & 1 ||
			(!support.sortDetached && b.compareDocumentPosition( a ) === compare) ) {

			// Choose the first element that is related to our preferred document
			if ( a === document || a.ownerDocument === preferredDoc && contains(preferredDoc, a) ) {
				return -1;
			}
			if ( b === document || b.ownerDocument === preferredDoc && contains(preferredDoc, b) ) {
				return 1;
			}

			// Maintain original order
			return sortInput ?
				( indexOf( sortInput, a ) - indexOf( sortInput, b ) ) :
				0;
		}

		return compare & 4 ? -1 : 1;
	} :
	function( a, b ) {
		// Exit early if the nodes are identical
		if ( a === b ) {
			hasDuplicate = true;
			return 0;
		}

		var cur,
			i = 0,
			aup = a.parentNode,
			bup = b.parentNode,
			ap = [ a ],
			bp = [ b ];

		// Parentless nodes are either documents or disconnected
		if ( !aup || !bup ) {
			return a === document ? -1 :
				b === document ? 1 :
				aup ? -1 :
				bup ? 1 :
				sortInput ?
				( indexOf( sortInput, a ) - indexOf( sortInput, b ) ) :
				0;

		// If the nodes are siblings, we can do a quick check
		} else if ( aup === bup ) {
			return siblingCheck( a, b );
		}

		// Otherwise we need full lists of their ancestors for comparison
		cur = a;
		while ( (cur = cur.parentNode) ) {
			ap.unshift( cur );
		}
		cur = b;
		while ( (cur = cur.parentNode) ) {
			bp.unshift( cur );
		}

		// Walk down the tree looking for a discrepancy
		while ( ap[i] === bp[i] ) {
			i++;
		}

		return i ?
			// Do a sibling check if the nodes have a common ancestor
			siblingCheck( ap[i], bp[i] ) :

			// Otherwise nodes in our document sort first
			ap[i] === preferredDoc ? -1 :
			bp[i] === preferredDoc ? 1 :
			0;
	};

	return document;
};

Sizzle.matches = function( expr, elements ) {
	return Sizzle( expr, null, null, elements );
};

Sizzle.matchesSelector = function( elem, expr ) {
	// Set document vars if needed
	if ( ( elem.ownerDocument || elem ) !== document ) {
		setDocument( elem );
	}

	// Make sure that attribute selectors are quoted
	expr = expr.replace( rattributeQuotes, "='$1']" );

	if ( support.matchesSelector && documentIsHTML &&
		!compilerCache[ expr + " " ] &&
		( !rbuggyMatches || !rbuggyMatches.test( expr ) ) &&
		( !rbuggyQSA     || !rbuggyQSA.test( expr ) ) ) {

		try {
			var ret = matches.call( elem, expr );

			// IE 9's matchesSelector returns false on disconnected nodes
			if ( ret || support.disconnectedMatch ||
					// As well, disconnected nodes are said to be in a document
					// fragment in IE 9
					elem.document && elem.document.nodeType !== 11 ) {
				return ret;
			}
		} catch (e) {}
	}

	return Sizzle( expr, document, null, [ elem ] ).length > 0;
};

Sizzle.contains = function( context, elem ) {
	// Set document vars if needed
	if ( ( context.ownerDocument || context ) !== document ) {
		setDocument( context );
	}
	return contains( context, elem );
};

Sizzle.attr = function( elem, name ) {
	// Set document vars if needed
	if ( ( elem.ownerDocument || elem ) !== document ) {
		setDocument( elem );
	}

	var fn = Expr.attrHandle[ name.toLowerCase() ],
		// Don't get fooled by Object.prototype properties (jQuery #13807)
		val = fn && hasOwn.call( Expr.attrHandle, name.toLowerCase() ) ?
			fn( elem, name, !documentIsHTML ) :
			undefined;

	return val !== undefined ?
		val :
		support.attributes || !documentIsHTML ?
			elem.getAttribute( name ) :
			(val = elem.getAttributeNode(name)) && val.specified ?
				val.value :
				null;
};

Sizzle.error = function( msg ) {
	throw new Error( "Syntax error, unrecognized expression: " + msg );
};

/**
 * Document sorting and removing duplicates
 * @param {ArrayLike} results
 */
Sizzle.uniqueSort = function( results ) {
	var elem,
		duplicates = [],
		j = 0,
		i = 0;

	// Unless we *know* we can detect duplicates, assume their presence
	hasDuplicate = !support.detectDuplicates;
	sortInput = !support.sortStable && results.slice( 0 );
	results.sort( sortOrder );

	if ( hasDuplicate ) {
		while ( (elem = results[i++]) ) {
			if ( elem === results[ i ] ) {
				j = duplicates.push( i );
			}
		}
		while ( j-- ) {
			results.splice( duplicates[ j ], 1 );
		}
	}

	// Clear input after sorting to release objects
	// See https://github.com/jquery/sizzle/pull/225
	sortInput = null;

	return results;
};

/**
 * Utility function for retrieving the text value of an array of DOM nodes
 * @param {Array|Element} elem
 */
getText = Sizzle.getText = function( elem ) {
	var node,
		ret = "",
		i = 0,
		nodeType = elem.nodeType;

	if ( !nodeType ) {
		// If no nodeType, this is expected to be an array
		while ( (node = elem[i++]) ) {
			// Do not traverse comment nodes
			ret += getText( node );
		}
	} else if ( nodeType === 1 || nodeType === 9 || nodeType === 11 ) {
		// Use textContent for elements
		// innerText usage removed for consistency of new lines (jQuery #11153)
		if ( typeof elem.textContent === "string" ) {
			return elem.textContent;
		} else {
			// Traverse its children
			for ( elem = elem.firstChild; elem; elem = elem.nextSibling ) {
				ret += getText( elem );
			}
		}
	} else if ( nodeType === 3 || nodeType === 4 ) {
		return elem.nodeValue;
	}
	// Do not include comment or processing instruction nodes

	return ret;
};

Expr = Sizzle.selectors = {

	// Can be adjusted by the user
	cacheLength: 50,

	createPseudo: markFunction,

	match: matchExpr,

	attrHandle: {},

	find: {},

	relative: {
		">": { dir: "parentNode", first: true },
		" ": { dir: "parentNode" },
		"+": { dir: "previousSibling", first: true },
		"~": { dir: "previousSibling" }
	},

	preFilter: {
		"ATTR": function( match ) {
			match[1] = match[1].replace( runescape, funescape );

			// Move the given value to match[3] whether quoted or unquoted
			match[3] = ( match[3] || match[4] || match[5] || "" ).replace( runescape, funescape );

			if ( match[2] === "~=" ) {
				match[3] = " " + match[3] + " ";
			}

			return match.slice( 0, 4 );
		},

		"CHILD": function( match ) {
			/* matches from matchExpr["CHILD"]
				1 type (only|nth|...)
				2 what (child|of-type)
				3 argument (even|odd|\d*|\d*n([+-]\d+)?|...)
				4 xn-component of xn+y argument ([+-]?\d*n|)
				5 sign of xn-component
				6 x of xn-component
				7 sign of y-component
				8 y of y-component
			*/
			match[1] = match[1].toLowerCase();

			if ( match[1].slice( 0, 3 ) === "nth" ) {
				// nth-* requires argument
				if ( !match[3] ) {
					Sizzle.error( match[0] );
				}

				// numeric x and y parameters for Expr.filter.CHILD
				// remember that false/true cast respectively to 0/1
				match[4] = +( match[4] ? match[5] + (match[6] || 1) : 2 * ( match[3] === "even" || match[3] === "odd" ) );
				match[5] = +( ( match[7] + match[8] ) || match[3] === "odd" );

			// other types prohibit arguments
			} else if ( match[3] ) {
				Sizzle.error( match[0] );
			}

			return match;
		},

		"PSEUDO": function( match ) {
			var excess,
				unquoted = !match[6] && match[2];

			if ( matchExpr["CHILD"].test( match[0] ) ) {
				return null;
			}

			// Accept quoted arguments as-is
			if ( match[3] ) {
				match[2] = match[4] || match[5] || "";

			// Strip excess characters from unquoted arguments
			} else if ( unquoted && rpseudo.test( unquoted ) &&
				// Get excess from tokenize (recursively)
				(excess = tokenize( unquoted, true )) &&
				// advance to the next closing parenthesis
				(excess = unquoted.indexOf( ")", unquoted.length - excess ) - unquoted.length) ) {

				// excess is a negative index
				match[0] = match[0].slice( 0, excess );
				match[2] = unquoted.slice( 0, excess );
			}

			// Return only captures needed by the pseudo filter method (type and argument)
			return match.slice( 0, 3 );
		}
	},

	filter: {

		"TAG": function( nodeNameSelector ) {
			var nodeName = nodeNameSelector.replace( runescape, funescape ).toLowerCase();
			return nodeNameSelector === "*" ?
				function() { return true; } :
				function( elem ) {
					return elem.nodeName && elem.nodeName.toLowerCase() === nodeName;
				};
		},

		"CLASS": function( className ) {
			var pattern = classCache[ className + " " ];

			return pattern ||
				(pattern = new RegExp( "(^|" + whitespace + ")" + className + "(" + whitespace + "|$)" )) &&
				classCache( className, function( elem ) {
					return pattern.test( typeof elem.className === "string" && elem.className || typeof elem.getAttribute !== "undefined" && elem.getAttribute("class") || "" );
				});
		},

		"ATTR": function( name, operator, check ) {
			return function( elem ) {
				var result = Sizzle.attr( elem, name );

				if ( result == null ) {
					return operator === "!=";
				}
				if ( !operator ) {
					return true;
				}

				result += "";

				return operator === "=" ? result === check :
					operator === "!=" ? result !== check :
					operator === "^=" ? check && result.indexOf( check ) === 0 :
					operator === "*=" ? check && result.indexOf( check ) > -1 :
					operator === "$=" ? check && result.slice( -check.length ) === check :
					operator === "~=" ? ( " " + result.replace( rwhitespace, " " ) + " " ).indexOf( check ) > -1 :
					operator === "|=" ? result === check || result.slice( 0, check.length + 1 ) === check + "-" :
					false;
			};
		},

		"CHILD": function( type, what, argument, first, last ) {
			var simple = type.slice( 0, 3 ) !== "nth",
				forward = type.slice( -4 ) !== "last",
				ofType = what === "of-type";

			return first === 1 && last === 0 ?

				// Shortcut for :nth-*(n)
				function( elem ) {
					return !!elem.parentNode;
				} :

				function( elem, context, xml ) {
					var cache, uniqueCache, outerCache, node, nodeIndex, start,
						dir = simple !== forward ? "nextSibling" : "previousSibling",
						parent = elem.parentNode,
						name = ofType && elem.nodeName.toLowerCase(),
						useCache = !xml && !ofType,
						diff = false;

					if ( parent ) {

						// :(first|last|only)-(child|of-type)
						if ( simple ) {
							while ( dir ) {
								node = elem;
								while ( (node = node[ dir ]) ) {
									if ( ofType ?
										node.nodeName.toLowerCase() === name :
										node.nodeType === 1 ) {

										return false;
									}
								}
								// Reverse direction for :only-* (if we haven't yet done so)
								start = dir = type === "only" && !start && "nextSibling";
							}
							return true;
						}

						start = [ forward ? parent.firstChild : parent.lastChild ];

						// non-xml :nth-child(...) stores cache data on `parent`
						if ( forward && useCache ) {

							// Seek `elem` from a previously-cached index

							// ...in a gzip-friendly way
							node = parent;
							outerCache = node[ expando ] || (node[ expando ] = {});

							// Support: IE <9 only
							// Defend against cloned attroperties (jQuery gh-1709)
							uniqueCache = outerCache[ node.uniqueID ] ||
								(outerCache[ node.uniqueID ] = {});

							cache = uniqueCache[ type ] || [];
							nodeIndex = cache[ 0 ] === dirruns && cache[ 1 ];
							diff = nodeIndex && cache[ 2 ];
							node = nodeIndex && parent.childNodes[ nodeIndex ];

							while ( (node = ++nodeIndex && node && node[ dir ] ||

								// Fallback to seeking `elem` from the start
								(diff = nodeIndex = 0) || start.pop()) ) {

								// When found, cache indexes on `parent` and break
								if ( node.nodeType === 1 && ++diff && node === elem ) {
									uniqueCache[ type ] = [ dirruns, nodeIndex, diff ];
									break;
								}
							}

						} else {
							// Use previously-cached element index if available
							if ( useCache ) {
								// ...in a gzip-friendly way
								node = elem;
								outerCache = node[ expando ] || (node[ expando ] = {});

								// Support: IE <9 only
								// Defend against cloned attroperties (jQuery gh-1709)
								uniqueCache = outerCache[ node.uniqueID ] ||
									(outerCache[ node.uniqueID ] = {});

								cache = uniqueCache[ type ] || [];
								nodeIndex = cache[ 0 ] === dirruns && cache[ 1 ];
								diff = nodeIndex;
							}

							// xml :nth-child(...)
							// or :nth-last-child(...) or :nth(-last)?-of-type(...)
							if ( diff === false ) {
								// Use the same loop as above to seek `elem` from the start
								while ( (node = ++nodeIndex && node && node[ dir ] ||
									(diff = nodeIndex = 0) || start.pop()) ) {

									if ( ( ofType ?
										node.nodeName.toLowerCase() === name :
										node.nodeType === 1 ) &&
										++diff ) {

										// Cache the index of each encountered element
										if ( useCache ) {
											outerCache = node[ expando ] || (node[ expando ] = {});

											// Support: IE <9 only
											// Defend against cloned attroperties (jQuery gh-1709)
											uniqueCache = outerCache[ node.uniqueID ] ||
												(outerCache[ node.uniqueID ] = {});

											uniqueCache[ type ] = [ dirruns, diff ];
										}

										if ( node === elem ) {
											break;
										}
									}
								}
							}
						}

						// Incorporate the offset, then check against cycle size
						diff -= last;
						return diff === first || ( diff % first === 0 && diff / first >= 0 );
					}
				};
		},

		"PSEUDO": function( pseudo, argument ) {
			// pseudo-class names are case-insensitive
			// http://www.w3.org/TR/selectors/#pseudo-classes
			// Prioritize by case sensitivity in case custom pseudos are added with uppercase letters
			// Remember that setFilters inherits from pseudos
			var args,
				fn = Expr.pseudos[ pseudo ] || Expr.setFilters[ pseudo.toLowerCase() ] ||
					Sizzle.error( "unsupported pseudo: " + pseudo );

			// The user may use createPseudo to indicate that
			// arguments are needed to create the filter function
			// just as Sizzle does
			if ( fn[ expando ] ) {
				return fn( argument );
			}

			// But maintain support for old signatures
			if ( fn.length > 1 ) {
				args = [ pseudo, pseudo, "", argument ];
				return Expr.setFilters.hasOwnProperty( pseudo.toLowerCase() ) ?
					markFunction(function( seed, matches ) {
						var idx,
							matched = fn( seed, argument ),
							i = matched.length;
						while ( i-- ) {
							idx = indexOf( seed, matched[i] );
							seed[ idx ] = !( matches[ idx ] = matched[i] );
						}
					}) :
					function( elem ) {
						return fn( elem, 0, args );
					};
			}

			return fn;
		}
	},

	pseudos: {
		// Potentially complex pseudos
		"not": markFunction(function( selector ) {
			// Trim the selector passed to compile
			// to avoid treating leading and trailing
			// spaces as combinators
			var input = [],
				results = [],
				matcher = compile( selector.replace( rtrim, "$1" ) );

			return matcher[ expando ] ?
				markFunction(function( seed, matches, context, xml ) {
					var elem,
						unmatched = matcher( seed, null, xml, [] ),
						i = seed.length;

					// Match elements unmatched by `matcher`
					while ( i-- ) {
						if ( (elem = unmatched[i]) ) {
							seed[i] = !(matches[i] = elem);
						}
					}
				}) :
				function( elem, context, xml ) {
					input[0] = elem;
					matcher( input, null, xml, results );
					// Don't keep the element (issue #299)
					input[0] = null;
					return !results.pop();
				};
		}),

		"has": markFunction(function( selector ) {
			return function( elem ) {
				return Sizzle( selector, elem ).length > 0;
			};
		}),

		"contains": markFunction(function( text ) {
			text = text.replace( runescape, funescape );
			return function( elem ) {
				return ( elem.textContent || elem.innerText || getText( elem ) ).indexOf( text ) > -1;
			};
		}),

		// "Whether an element is represented by a :lang() selector
		// is based solely on the element's language value
		// being equal to the identifier C,
		// or beginning with the identifier C immediately followed by "-".
		// The matching of C against the element's language value is performed case-insensitively.
		// The identifier C does not have to be a valid language name."
		// http://www.w3.org/TR/selectors/#lang-pseudo
		"lang": markFunction( function( lang ) {
			// lang value must be a valid identifier
			if ( !ridentifier.test(lang || "") ) {
				Sizzle.error( "unsupported lang: " + lang );
			}
			lang = lang.replace( runescape, funescape ).toLowerCase();
			return function( elem ) {
				var elemLang;
				do {
					if ( (elemLang = documentIsHTML ?
						elem.lang :
						elem.getAttribute("xml:lang") || elem.getAttribute("lang")) ) {

						elemLang = elemLang.toLowerCase();
						return elemLang === lang || elemLang.indexOf( lang + "-" ) === 0;
					}
				} while ( (elem = elem.parentNode) && elem.nodeType === 1 );
				return false;
			};
		}),

		// Miscellaneous
		"target": function( elem ) {
			var hash = window.location && window.location.hash;
			return hash && hash.slice( 1 ) === elem.id;
		},

		"root": function( elem ) {
			return elem === docElem;
		},

		"focus": function( elem ) {
			return elem === document.activeElement && (!document.hasFocus || document.hasFocus()) && !!(elem.type || elem.href || ~elem.tabIndex);
		},

		// Boolean properties
		"enabled": function( elem ) {
			return elem.disabled === false;
		},

		"disabled": function( elem ) {
			return elem.disabled === true;
		},

		"checked": function( elem ) {
			// In CSS3, :checked should return both checked and selected elements
			// http://www.w3.org/TR/2011/REC-css3-selectors-20110929/#checked
			var nodeName = elem.nodeName.toLowerCase();
			return (nodeName === "input" && !!elem.checked) || (nodeName === "option" && !!elem.selected);
		},

		"selected": function( elem ) {
			// Accessing this property makes selected-by-default
			// options in Safari work properly
			if ( elem.parentNode ) {
				elem.parentNode.selectedIndex;
			}

			return elem.selected === true;
		},

		// Contents
		"empty": function( elem ) {
			// http://www.w3.org/TR/selectors/#empty-pseudo
			// :empty is negated by element (1) or content nodes (text: 3; cdata: 4; entity ref: 5),
			//   but not by others (comment: 8; processing instruction: 7; etc.)
			// nodeType < 6 works because attributes (2) do not appear as children
			for ( elem = elem.firstChild; elem; elem = elem.nextSibling ) {
				if ( elem.nodeType < 6 ) {
					return false;
				}
			}
			return true;
		},

		"parent": function( elem ) {
			return !Expr.pseudos["empty"]( elem );
		},

		// Element/input types
		"header": function( elem ) {
			return rheader.test( elem.nodeName );
		},

		"input": function( elem ) {
			return rinputs.test( elem.nodeName );
		},

		"button": function( elem ) {
			var name = elem.nodeName.toLowerCase();
			return name === "input" && elem.type === "button" || name === "button";
		},

		"text": function( elem ) {
			var attr;
			return elem.nodeName.toLowerCase() === "input" &&
				elem.type === "text" &&

				// Support: IE<8
				// New HTML5 attribute values (e.g., "search") appear with elem.type === "text"
				( (attr = elem.getAttribute("type")) == null || attr.toLowerCase() === "text" );
		},

		// Position-in-collection
		"first": createPositionalPseudo(function() {
			return [ 0 ];
		}),

		"last": createPositionalPseudo(function( matchIndexes, length ) {
			return [ length - 1 ];
		}),

		"eq": createPositionalPseudo(function( matchIndexes, length, argument ) {
			return [ argument < 0 ? argument + length : argument ];
		}),

		"even": createPositionalPseudo(function( matchIndexes, length ) {
			var i = 0;
			for ( ; i < length; i += 2 ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		}),

		"odd": createPositionalPseudo(function( matchIndexes, length ) {
			var i = 1;
			for ( ; i < length; i += 2 ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		}),

		"lt": createPositionalPseudo(function( matchIndexes, length, argument ) {
			var i = argument < 0 ? argument + length : argument;
			for ( ; --i >= 0; ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		}),

		"gt": createPositionalPseudo(function( matchIndexes, length, argument ) {
			var i = argument < 0 ? argument + length : argument;
			for ( ; ++i < length; ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		})
	}
};

Expr.pseudos["nth"] = Expr.pseudos["eq"];

// Add button/input type pseudos
for ( i in { radio: true, checkbox: true, file: true, password: true, image: true } ) {
	Expr.pseudos[ i ] = createInputPseudo( i );
}
for ( i in { submit: true, reset: true } ) {
	Expr.pseudos[ i ] = createButtonPseudo( i );
}

// Easy API for creating new setFilters
function setFilters() {}
setFilters.prototype = Expr.filters = Expr.pseudos;
Expr.setFilters = new setFilters();

tokenize = Sizzle.tokenize = function( selector, parseOnly ) {
	var matched, match, tokens, type,
		soFar, groups, preFilters,
		cached = tokenCache[ selector + " " ];

	if ( cached ) {
		return parseOnly ? 0 : cached.slice( 0 );
	}

	soFar = selector;
	groups = [];
	preFilters = Expr.preFilter;

	while ( soFar ) {

		// Comma and first run
		if ( !matched || (match = rcomma.exec( soFar )) ) {
			if ( match ) {
				// Don't consume trailing commas as valid
				soFar = soFar.slice( match[0].length ) || soFar;
			}
			groups.push( (tokens = []) );
		}

		matched = false;

		// Combinators
		if ( (match = rcombinators.exec( soFar )) ) {
			matched = match.shift();
			tokens.push({
				value: matched,
				// Cast descendant combinators to space
				type: match[0].replace( rtrim, " " )
			});
			soFar = soFar.slice( matched.length );
		}

		// Filters
		for ( type in Expr.filter ) {
			if ( (match = matchExpr[ type ].exec( soFar )) && (!preFilters[ type ] ||
				(match = preFilters[ type ]( match ))) ) {
				matched = match.shift();
				tokens.push({
					value: matched,
					type: type,
					matches: match
				});
				soFar = soFar.slice( matched.length );
			}
		}

		if ( !matched ) {
			break;
		}
	}

	// Return the length of the invalid excess
	// if we're just parsing
	// Otherwise, throw an error or return tokens
	return parseOnly ?
		soFar.length :
		soFar ?
			Sizzle.error( selector ) :
			// Cache the tokens
			tokenCache( selector, groups ).slice( 0 );
};

function toSelector( tokens ) {
	var i = 0,
		len = tokens.length,
		selector = "";
	for ( ; i < len; i++ ) {
		selector += tokens[i].value;
	}
	return selector;
}

function addCombinator( matcher, combinator, base ) {
	var dir = combinator.dir,
		checkNonElements = base && dir === "parentNode",
		doneName = done++;

	return combinator.first ?
		// Check against closest ancestor/preceding element
		function( elem, context, xml ) {
			while ( (elem = elem[ dir ]) ) {
				if ( elem.nodeType === 1 || checkNonElements ) {
					return matcher( elem, context, xml );
				}
			}
		} :

		// Check against all ancestor/preceding elements
		function( elem, context, xml ) {
			var oldCache, uniqueCache, outerCache,
				newCache = [ dirruns, doneName ];

			// We can't set arbitrary data on XML nodes, so they don't benefit from combinator caching
			if ( xml ) {
				while ( (elem = elem[ dir ]) ) {
					if ( elem.nodeType === 1 || checkNonElements ) {
						if ( matcher( elem, context, xml ) ) {
							return true;
						}
					}
				}
			} else {
				while ( (elem = elem[ dir ]) ) {
					if ( elem.nodeType === 1 || checkNonElements ) {
						outerCache = elem[ expando ] || (elem[ expando ] = {});

						// Support: IE <9 only
						// Defend against cloned attroperties (jQuery gh-1709)
						uniqueCache = outerCache[ elem.uniqueID ] || (outerCache[ elem.uniqueID ] = {});

						if ( (oldCache = uniqueCache[ dir ]) &&
							oldCache[ 0 ] === dirruns && oldCache[ 1 ] === doneName ) {

							// Assign to newCache so results back-propagate to previous elements
							return (newCache[ 2 ] = oldCache[ 2 ]);
						} else {
							// Reuse newcache so results back-propagate to previous elements
							uniqueCache[ dir ] = newCache;

							// A match means we're done; a fail means we have to keep checking
							if ( (newCache[ 2 ] = matcher( elem, context, xml )) ) {
								return true;
							}
						}
					}
				}
			}
		};
}

function elementMatcher( matchers ) {
	return matchers.length > 1 ?
		function( elem, context, xml ) {
			var i = matchers.length;
			while ( i-- ) {
				if ( !matchers[i]( elem, context, xml ) ) {
					return false;
				}
			}
			return true;
		} :
		matchers[0];
}

function multipleContexts( selector, contexts, results ) {
	var i = 0,
		len = contexts.length;
	for ( ; i < len; i++ ) {
		Sizzle( selector, contexts[i], results );
	}
	return results;
}

function condense( unmatched, map, filter, context, xml ) {
	var elem,
		newUnmatched = [],
		i = 0,
		len = unmatched.length,
		mapped = map != null;

	for ( ; i < len; i++ ) {
		if ( (elem = unmatched[i]) ) {
			if ( !filter || filter( elem, context, xml ) ) {
				newUnmatched.push( elem );
				if ( mapped ) {
					map.push( i );
				}
			}
		}
	}

	return newUnmatched;
}

function setMatcher( preFilter, selector, matcher, postFilter, postFinder, postSelector ) {
	if ( postFilter && !postFilter[ expando ] ) {
		postFilter = setMatcher( postFilter );
	}
	if ( postFinder && !postFinder[ expando ] ) {
		postFinder = setMatcher( postFinder, postSelector );
	}
	return markFunction(function( seed, results, context, xml ) {
		var temp, i, elem,
			preMap = [],
			postMap = [],
			preexisting = results.length,

			// Get initial elements from seed or context
			elems = seed || multipleContexts( selector || "*", context.nodeType ? [ context ] : context, [] ),

			// Prefilter to get matcher input, preserving a map for seed-results synchronization
			matcherIn = preFilter && ( seed || !selector ) ?
				condense( elems, preMap, preFilter, context, xml ) :
				elems,

			matcherOut = matcher ?
				// If we have a postFinder, or filtered seed, or non-seed postFilter or preexisting results,
				postFinder || ( seed ? preFilter : preexisting || postFilter ) ?

					// ...intermediate processing is necessary
					[] :

					// ...otherwise use results directly
					results :
				matcherIn;

		// Find primary matches
		if ( matcher ) {
			matcher( matcherIn, matcherOut, context, xml );
		}

		// Apply postFilter
		if ( postFilter ) {
			temp = condense( matcherOut, postMap );
			postFilter( temp, [], context, xml );

			// Un-match failing elements by moving them back to matcherIn
			i = temp.length;
			while ( i-- ) {
				if ( (elem = temp[i]) ) {
					matcherOut[ postMap[i] ] = !(matcherIn[ postMap[i] ] = elem);
				}
			}
		}

		if ( seed ) {
			if ( postFinder || preFilter ) {
				if ( postFinder ) {
					// Get the final matcherOut by condensing this intermediate into postFinder contexts
					temp = [];
					i = matcherOut.length;
					while ( i-- ) {
						if ( (elem = matcherOut[i]) ) {
							// Restore matcherIn since elem is not yet a final match
							temp.push( (matcherIn[i] = elem) );
						}
					}
					postFinder( null, (matcherOut = []), temp, xml );
				}

				// Move matched elements from seed to results to keep them synchronized
				i = matcherOut.length;
				while ( i-- ) {
					if ( (elem = matcherOut[i]) &&
						(temp = postFinder ? indexOf( seed, elem ) : preMap[i]) > -1 ) {

						seed[temp] = !(results[temp] = elem);
					}
				}
			}

		// Add elements to results, through postFinder if defined
		} else {
			matcherOut = condense(
				matcherOut === results ?
					matcherOut.splice( preexisting, matcherOut.length ) :
					matcherOut
			);
			if ( postFinder ) {
				postFinder( null, results, matcherOut, xml );
			} else {
				push.apply( results, matcherOut );
			}
		}
	});
}

function matcherFromTokens( tokens ) {
	var checkContext, matcher, j,
		len = tokens.length,
		leadingRelative = Expr.relative[ tokens[0].type ],
		implicitRelative = leadingRelative || Expr.relative[" "],
		i = leadingRelative ? 1 : 0,

		// The foundational matcher ensures that elements are reachable from top-level context(s)
		matchContext = addCombinator( function( elem ) {
			return elem === checkContext;
		}, implicitRelative, true ),
		matchAnyContext = addCombinator( function( elem ) {
			return indexOf( checkContext, elem ) > -1;
		}, implicitRelative, true ),
		matchers = [ function( elem, context, xml ) {
			var ret = ( !leadingRelative && ( xml || context !== outermostContext ) ) || (
				(checkContext = context).nodeType ?
					matchContext( elem, context, xml ) :
					matchAnyContext( elem, context, xml ) );
			// Avoid hanging onto element (issue #299)
			checkContext = null;
			return ret;
		} ];

	for ( ; i < len; i++ ) {
		if ( (matcher = Expr.relative[ tokens[i].type ]) ) {
			matchers = [ addCombinator(elementMatcher( matchers ), matcher) ];
		} else {
			matcher = Expr.filter[ tokens[i].type ].apply( null, tokens[i].matches );

			// Return special upon seeing a positional matcher
			if ( matcher[ expando ] ) {
				// Find the next relative operator (if any) for proper handling
				j = ++i;
				for ( ; j < len; j++ ) {
					if ( Expr.relative[ tokens[j].type ] ) {
						break;
					}
				}
				return setMatcher(
					i > 1 && elementMatcher( matchers ),
					i > 1 && toSelector(
						// If the preceding token was a descendant combinator, insert an implicit any-element `*`
						tokens.slice( 0, i - 1 ).concat({ value: tokens[ i - 2 ].type === " " ? "*" : "" })
					).replace( rtrim, "$1" ),
					matcher,
					i < j && matcherFromTokens( tokens.slice( i, j ) ),
					j < len && matcherFromTokens( (tokens = tokens.slice( j )) ),
					j < len && toSelector( tokens )
				);
			}
			matchers.push( matcher );
		}
	}

	return elementMatcher( matchers );
}

function matcherFromGroupMatchers( elementMatchers, setMatchers ) {
	var bySet = setMatchers.length > 0,
		byElement = elementMatchers.length > 0,
		superMatcher = function( seed, context, xml, results, outermost ) {
			var elem, j, matcher,
				matchedCount = 0,
				i = "0",
				unmatched = seed && [],
				setMatched = [],
				contextBackup = outermostContext,
				// We must always have either seed elements or outermost context
				elems = seed || byElement && Expr.find["TAG"]( "*", outermost ),
				// Use integer dirruns iff this is the outermost matcher
				dirrunsUnique = (dirruns += contextBackup == null ? 1 : Math.random() || 0.1),
				len = elems.length;

			if ( outermost ) {
				outermostContext = context === document || context || outermost;
			}

			// Add elements passing elementMatchers directly to results
			// Support: IE<9, Safari
			// Tolerate NodeList properties (IE: "length"; Safari: <number>) matching elements by id
			for ( ; i !== len && (elem = elems[i]) != null; i++ ) {
				if ( byElement && elem ) {
					j = 0;
					if ( !context && elem.ownerDocument !== document ) {
						setDocument( elem );
						xml = !documentIsHTML;
					}
					while ( (matcher = elementMatchers[j++]) ) {
						if ( matcher( elem, context || document, xml) ) {
							results.push( elem );
							break;
						}
					}
					if ( outermost ) {
						dirruns = dirrunsUnique;
					}
				}

				// Track unmatched elements for set filters
				if ( bySet ) {
					// They will have gone through all possible matchers
					if ( (elem = !matcher && elem) ) {
						matchedCount--;
					}

					// Lengthen the array for every element, matched or not
					if ( seed ) {
						unmatched.push( elem );
					}
				}
			}

			// `i` is now the count of elements visited above, and adding it to `matchedCount`
			// makes the latter nonnegative.
			matchedCount += i;

			// Apply set filters to unmatched elements
			// NOTE: This can be skipped if there are no unmatched elements (i.e., `matchedCount`
			// equals `i`), unless we didn't visit _any_ elements in the above loop because we have
			// no element matchers and no seed.
			// Incrementing an initially-string "0" `i` allows `i` to remain a string only in that
			// case, which will result in a "00" `matchedCount` that differs from `i` but is also
			// numerically zero.
			if ( bySet && i !== matchedCount ) {
				j = 0;
				while ( (matcher = setMatchers[j++]) ) {
					matcher( unmatched, setMatched, context, xml );
				}

				if ( seed ) {
					// Reintegrate element matches to eliminate the need for sorting
					if ( matchedCount > 0 ) {
						while ( i-- ) {
							if ( !(unmatched[i] || setMatched[i]) ) {
								setMatched[i] = pop.call( results );
							}
						}
					}

					// Discard index placeholder values to get only actual matches
					setMatched = condense( setMatched );
				}

				// Add matches to results
				push.apply( results, setMatched );

				// Seedless set matches succeeding multiple successful matchers stipulate sorting
				if ( outermost && !seed && setMatched.length > 0 &&
					( matchedCount + setMatchers.length ) > 1 ) {

					Sizzle.uniqueSort( results );
				}
			}

			// Override manipulation of globals by nested matchers
			if ( outermost ) {
				dirruns = dirrunsUnique;
				outermostContext = contextBackup;
			}

			return unmatched;
		};

	return bySet ?
		markFunction( superMatcher ) :
		superMatcher;
}

compile = Sizzle.compile = function( selector, match /* Internal Use Only */ ) {
	var i,
		setMatchers = [],
		elementMatchers = [],
		cached = compilerCache[ selector + " " ];

	if ( !cached ) {
		// Generate a function of recursive functions that can be used to check each element
		if ( !match ) {
			match = tokenize( selector );
		}
		i = match.length;
		while ( i-- ) {
			cached = matcherFromTokens( match[i] );
			if ( cached[ expando ] ) {
				setMatchers.push( cached );
			} else {
				elementMatchers.push( cached );
			}
		}

		// Cache the compiled function
		cached = compilerCache( selector, matcherFromGroupMatchers( elementMatchers, setMatchers ) );

		// Save selector and tokenization
		cached.selector = selector;
	}
	return cached;
};

/**
 * A low-level selection function that works with Sizzle's compiled
 *  selector functions
 * @param {String|Function} selector A selector or a pre-compiled
 *  selector function built with Sizzle.compile
 * @param {Element} context
 * @param {Array} [results]
 * @param {Array} [seed] A set of elements to match against
 */
select = Sizzle.select = function( selector, context, results, seed ) {
	var i, tokens, token, type, find,
		compiled = typeof selector === "function" && selector,
		match = !seed && tokenize( (selector = compiled.selector || selector) );

	results = results || [];

	// Try to minimize operations if there is only one selector in the list and no seed
	// (the latter of which guarantees us context)
	if ( match.length === 1 ) {

		// Reduce context if the leading compound selector is an ID
		tokens = match[0] = match[0].slice( 0 );
		if ( tokens.length > 2 && (token = tokens[0]).type === "ID" &&
				support.getById && context.nodeType === 9 && documentIsHTML &&
				Expr.relative[ tokens[1].type ] ) {

			context = ( Expr.find["ID"]( token.matches[0].replace(runescape, funescape), context ) || [] )[0];
			if ( !context ) {
				return results;

			// Precompiled matchers will still verify ancestry, so step up a level
			} else if ( compiled ) {
				context = context.parentNode;
			}

			selector = selector.slice( tokens.shift().value.length );
		}

		// Fetch a seed set for right-to-left matching
		i = matchExpr["needsContext"].test( selector ) ? 0 : tokens.length;
		while ( i-- ) {
			token = tokens[i];

			// Abort if we hit a combinator
			if ( Expr.relative[ (type = token.type) ] ) {
				break;
			}
			if ( (find = Expr.find[ type ]) ) {
				// Search, expanding context for leading sibling combinators
				if ( (seed = find(
					token.matches[0].replace( runescape, funescape ),
					rsibling.test( tokens[0].type ) && testContext( context.parentNode ) || context
				)) ) {

					// If seed is empty or no tokens remain, we can return early
					tokens.splice( i, 1 );
					selector = seed.length && toSelector( tokens );
					if ( !selector ) {
						push.apply( results, seed );
						return results;
					}

					break;
				}
			}
		}
	}

	// Compile and execute a filtering function if one is not provided
	// Provide `match` to avoid retokenization if we modified the selector above
	( compiled || compile( selector, match ) )(
		seed,
		context,
		!documentIsHTML,
		results,
		!context || rsibling.test( selector ) && testContext( context.parentNode ) || context
	);
	return results;
};

// One-time assignments

// Sort stability
support.sortStable = expando.split("").sort( sortOrder ).join("") === expando;

// Support: Chrome 14-35+
// Always assume duplicates if they aren't passed to the comparison function
support.detectDuplicates = !!hasDuplicate;

// Initialize against the default document
setDocument();

// Support: Webkit<537.32 - Safari 6.0.3/Chrome 25 (fixed in Chrome 27)
// Detached nodes confoundingly follow *each other*
support.sortDetached = assert(function( div1 ) {
	// Should return 1, but returns 4 (following)
	return div1.compareDocumentPosition( document.createElement("div") ) & 1;
});

// Support: IE<8
// Prevent attribute/property "interpolation"
// http://msdn.microsoft.com/en-us/library/ms536429%28VS.85%29.aspx
if ( !assert(function( div ) {
	div.innerHTML = "<a href='#'></a>";
	return div.firstChild.getAttribute("href") === "#" ;
}) ) {
	addHandle( "type|href|height|width", function( elem, name, isXML ) {
		if ( !isXML ) {
			return elem.getAttribute( name, name.toLowerCase() === "type" ? 1 : 2 );
		}
	});
}

// Support: IE<9
// Use defaultValue in place of getAttribute("value")
if ( !support.attributes || !assert(function( div ) {
	div.innerHTML = "<input/>";
	div.firstChild.setAttribute( "value", "" );
	return div.firstChild.getAttribute( "value" ) === "";
}) ) {
	addHandle( "value", function( elem, name, isXML ) {
		if ( !isXML && elem.nodeName.toLowerCase() === "input" ) {
			return elem.defaultValue;
		}
	});
}

// Support: IE<9
// Use getAttributeNode to fetch booleans when getAttribute lies
if ( !assert(function( div ) {
	return div.getAttribute("disabled") == null;
}) ) {
	addHandle( booleans, function( elem, name, isXML ) {
		var val;
		if ( !isXML ) {
			return elem[ name ] === true ? name.toLowerCase() :
					(val = elem.getAttributeNode( name )) && val.specified ?
					val.value :
				null;
		}
	});
}

return Sizzle;

})( window );



jQuery.find = Sizzle;
jQuery.expr = Sizzle.selectors;
jQuery.expr[ ":" ] = jQuery.expr.pseudos;
jQuery.uniqueSort = jQuery.unique = Sizzle.uniqueSort;
jQuery.text = Sizzle.getText;
jQuery.isXMLDoc = Sizzle.isXML;
jQuery.contains = Sizzle.contains;



var dir = function( elem, dir, until ) {
	var matched = [],
		truncate = until !== undefined;

	while ( ( elem = elem[ dir ] ) && elem.nodeType !== 9 ) {
		if ( elem.nodeType === 1 ) {
			if ( truncate && jQuery( elem ).is( until ) ) {
				break;
			}
			matched.push( elem );
		}
	}
	return matched;
};


var siblings = function( n, elem ) {
	var matched = [];

	for ( ; n; n = n.nextSibling ) {
		if ( n.nodeType === 1 && n !== elem ) {
			matched.push( n );
		}
	}

	return matched;
};


var rneedsContext = jQuery.expr.match.needsContext;

var rsingleTag = ( /^<([\w-]+)\s*\/?>(?:<\/\1>|)$/ );



var risSimple = /^.[^:#\[\.,]*$/;

// Implement the identical functionality for filter and not
function winnow( elements, qualifier, not ) {
	if ( jQuery.isFunction( qualifier ) ) {
		return jQuery.grep( elements, function( elem, i ) {
			/* jshint -W018 */
			return !!qualifier.call( elem, i, elem ) !== not;
		} );

	}

	if ( qualifier.nodeType ) {
		return jQuery.grep( elements, function( elem ) {
			return ( elem === qualifier ) !== not;
		} );

	}

	if ( typeof qualifier === "string" ) {
		if ( risSimple.test( qualifier ) ) {
			return jQuery.filter( qualifier, elements, not );
		}

		qualifier = jQuery.filter( qualifier, elements );
	}

	return jQuery.grep( elements, function( elem ) {
		return ( jQuery.inArray( elem, qualifier ) > -1 ) !== not;
	} );
}

jQuery.filter = function( expr, elems, not ) {
	var elem = elems[ 0 ];

	if ( not ) {
		expr = ":not(" + expr + ")";
	}

	return elems.length === 1 && elem.nodeType === 1 ?
		jQuery.find.matchesSelector( elem, expr ) ? [ elem ] : [] :
		jQuery.find.matches( expr, jQuery.grep( elems, function( elem ) {
			return elem.nodeType === 1;
		} ) );
};

jQuery.fn.extend( {
	find: function( selector ) {
		var i,
			ret = [],
			self = this,
			len = self.length;

		if ( typeof selector !== "string" ) {
			return this.pushStack( jQuery( selector ).filter( function() {
				for ( i = 0; i < len; i++ ) {
					if ( jQuery.contains( self[ i ], this ) ) {
						return true;
					}
				}
			} ) );
		}

		for ( i = 0; i < len; i++ ) {
			jQuery.find( selector, self[ i ], ret );
		}

		// Needed because $( selector, context ) becomes $( context ).find( selector )
		ret = this.pushStack( len > 1 ? jQuery.unique( ret ) : ret );
		ret.selector = this.selector ? this.selector + " " + selector : selector;
		return ret;
	},
	filter: function( selector ) {
		return this.pushStack( winnow( this, selector || [], false ) );
	},
	not: function( selector ) {
		return this.pushStack( winnow( this, selector || [], true ) );
	},
	is: function( selector ) {
		return !!winnow(
			this,

			// If this is a positional/relative selector, check membership in the returned set
			// so $("p:first").is("p:last") won't return true for a doc with two "p".
			typeof selector === "string" && rneedsContext.test( selector ) ?
				jQuery( selector ) :
				selector || [],
			false
		).length;
	}
} );


// Initialize a jQuery object


// A central reference to the root jQuery(document)
var rootjQuery,

	// A simple way to check for HTML strings
	// Prioritize #id over <tag> to avoid XSS via location.hash (#9521)
	// Strict HTML recognition (#11290: must start with <)
	rquickExpr = /^(?:\s*(<[\w\W]+>)[^>]*|#([\w-]*))$/,

	init = jQuery.fn.init = function( selector, context, root ) {
		var match, elem;

		// HANDLE: $(""), $(null), $(undefined), $(false)
		if ( !selector ) {
			return this;
		}

		// init accepts an alternate rootjQuery
		// so migrate can support jQuery.sub (gh-2101)
		root = root || rootjQuery;

		// Handle HTML strings
		if ( typeof selector === "string" ) {
			if ( selector.charAt( 0 ) === "<" &&
				selector.charAt( selector.length - 1 ) === ">" &&
				selector.length >= 3 ) {

				// Assume that strings that start and end with <> are HTML and skip the regex check
				match = [ null, selector, null ];

			} else {
				match = rquickExpr.exec( selector );
			}

			// Match html or make sure no context is specified for #id
			if ( match && ( match[ 1 ] || !context ) ) {

				// HANDLE: $(html) -> $(array)
				if ( match[ 1 ] ) {
					context = context instanceof jQuery ? context[ 0 ] : context;

					// scripts is true for back-compat
					// Intentionally let the error be thrown if parseHTML is not present
					jQuery.merge( this, jQuery.parseHTML(
						match[ 1 ],
						context && context.nodeType ? context.ownerDocument || context : document,
						true
					) );

					// HANDLE: $(html, props)
					if ( rsingleTag.test( match[ 1 ] ) && jQuery.isPlainObject( context ) ) {
						for ( match in context ) {

							// Properties of context are called as methods if possible
							if ( jQuery.isFunction( this[ match ] ) ) {
								this[ match ]( context[ match ] );

							// ...and otherwise set as attributes
							} else {
								this.attr( match, context[ match ] );
							}
						}
					}

					return this;

				// HANDLE: $(#id)
				} else {
					elem = document.getElementById( match[ 2 ] );

					// Check parentNode to catch when Blackberry 4.6 returns
					// nodes that are no longer in the document #6963
					if ( elem && elem.parentNode ) {

						// Handle the case where IE and Opera return items
						// by name instead of ID
						if ( elem.id !== match[ 2 ] ) {
							return rootjQuery.find( selector );
						}

						// Otherwise, we inject the element directly into the jQuery object
						this.length = 1;
						this[ 0 ] = elem;
					}

					this.context = document;
					this.selector = selector;
					return this;
				}

			// HANDLE: $(expr, $(...))
			} else if ( !context || context.jquery ) {
				return ( context || root ).find( selector );

			// HANDLE: $(expr, context)
			// (which is just equivalent to: $(context).find(expr)
			} else {
				return this.constructor( context ).find( selector );
			}

		// HANDLE: $(DOMElement)
		} else if ( selector.nodeType ) {
			this.context = this[ 0 ] = selector;
			this.length = 1;
			return this;

		// HANDLE: $(function)
		// Shortcut for document ready
		} else if ( jQuery.isFunction( selector ) ) {
			return typeof root.ready !== "undefined" ?
				root.ready( selector ) :

				// Execute immediately if ready is not present
				selector( jQuery );
		}

		if ( selector.selector !== undefined ) {
			this.selector = selector.selector;
			this.context = selector.context;
		}

		return jQuery.makeArray( selector, this );
	};

// Give the init function the jQuery prototype for later instantiation
init.prototype = jQuery.fn;

// Initialize central reference
rootjQuery = jQuery( document );


var rparentsprev = /^(?:parents|prev(?:Until|All))/,

	// methods guaranteed to produce a unique set when starting from a unique set
	guaranteedUnique = {
		children: true,
		contents: true,
		next: true,
		prev: true
	};

jQuery.fn.extend( {
	has: function( target ) {
		var i,
			targets = jQuery( target, this ),
			len = targets.length;

		return this.filter( function() {
			for ( i = 0; i < len; i++ ) {
				if ( jQuery.contains( this, targets[ i ] ) ) {
					return true;
				}
			}
		} );
	},

	closest: function( selectors, context ) {
		var cur,
			i = 0,
			l = this.length,
			matched = [],
			pos = rneedsContext.test( selectors ) || typeof selectors !== "string" ?
				jQuery( selectors, context || this.context ) :
				0;

		for ( ; i < l; i++ ) {
			for ( cur = this[ i ]; cur && cur !== context; cur = cur.parentNode ) {

				// Always skip document fragments
				if ( cur.nodeType < 11 && ( pos ?
					pos.index( cur ) > -1 :

					// Don't pass non-elements to Sizzle
					cur.nodeType === 1 &&
						jQuery.find.matchesSelector( cur, selectors ) ) ) {

					matched.push( cur );
					break;
				}
			}
		}

		return this.pushStack( matched.length > 1 ? jQuery.uniqueSort( matched ) : matched );
	},

	// Determine the position of an element within
	// the matched set of elements
	index: function( elem ) {

		// No argument, return index in parent
		if ( !elem ) {
			return ( this[ 0 ] && this[ 0 ].parentNode ) ? this.first().prevAll().length : -1;
		}

		// index in selector
		if ( typeof elem === "string" ) {
			return jQuery.inArray( this[ 0 ], jQuery( elem ) );
		}

		// Locate the position of the desired element
		return jQuery.inArray(

			// If it receives a jQuery object, the first element is used
			elem.jquery ? elem[ 0 ] : elem, this );
	},

	add: function( selector, context ) {
		return this.pushStack(
			jQuery.uniqueSort(
				jQuery.merge( this.get(), jQuery( selector, context ) )
			)
		);
	},

	addBack: function( selector ) {
		return this.add( selector == null ?
			this.prevObject : this.prevObject.filter( selector )
		);
	}
} );

function sibling( cur, dir ) {
	do {
		cur = cur[ dir ];
	} while ( cur && cur.nodeType !== 1 );

	return cur;
}

jQuery.each( {
	parent: function( elem ) {
		var parent = elem.parentNode;
		return parent && parent.nodeType !== 11 ? parent : null;
	},
	parents: function( elem ) {
		return dir( elem, "parentNode" );
	},
	parentsUntil: function( elem, i, until ) {
		return dir( elem, "parentNode", until );
	},
	next: function( elem ) {
		return sibling( elem, "nextSibling" );
	},
	prev: function( elem ) {
		return sibling( elem, "previousSibling" );
	},
	nextAll: function( elem ) {
		return dir( elem, "nextSibling" );
	},
	prevAll: function( elem ) {
		return dir( elem, "previousSibling" );
	},
	nextUntil: function( elem, i, until ) {
		return dir( elem, "nextSibling", until );
	},
	prevUntil: function( elem, i, until ) {
		return dir( elem, "previousSibling", until );
	},
	siblings: function( elem ) {
		return siblings( ( elem.parentNode || {} ).firstChild, elem );
	},
	children: function( elem ) {
		return siblings( elem.firstChild );
	},
	contents: function( elem ) {
		return jQuery.nodeName( elem, "iframe" ) ?
			elem.contentDocument || elem.contentWindow.document :
			jQuery.merge( [], elem.childNodes );
	}
}, function( name, fn ) {
	jQuery.fn[ name ] = function( until, selector ) {
		var ret = jQuery.map( this, fn, until );

		if ( name.slice( -5 ) !== "Until" ) {
			selector = until;
		}

		if ( selector && typeof selector === "string" ) {
			ret = jQuery.filter( selector, ret );
		}

		if ( this.length > 1 ) {

			// Remove duplicates
			if ( !guaranteedUnique[ name ] ) {
				ret = jQuery.uniqueSort( ret );
			}

			// Reverse order for parents* and prev-derivatives
			if ( rparentsprev.test( name ) ) {
				ret = ret.reverse();
			}
		}

		return this.pushStack( ret );
	};
} );
var rnotwhite = ( /\S+/g );



// Convert String-formatted options into Object-formatted ones
function createOptions( options ) {
	var object = {};
	jQuery.each( options.match( rnotwhite ) || [], function( _, flag ) {
		object[ flag ] = true;
	} );
	return object;
}

/*
 * Create a callback list using the following parameters:
 *
 *	options: an optional list of space-separated options that will change how
 *			the callback list behaves or a more traditional option object
 *
 * By default a callback list will act like an event callback list and can be
 * "fired" multiple times.
 *
 * Possible options:
 *
 *	once:			will ensure the callback list can only be fired once (like a Deferred)
 *
 *	memory:			will keep track of previous values and will call any callback added
 *					after the list has been fired right away with the latest "memorized"
 *					values (like a Deferred)
 *
 *	unique:			will ensure a callback can only be added once (no duplicate in the list)
 *
 *	stopOnFalse:	interrupt callings when a callback returns false
 *
 */
jQuery.Callbacks = function( options ) {

	// Convert options from String-formatted to Object-formatted if needed
	// (we check in cache first)
	options = typeof options === "string" ?
		createOptions( options ) :
		jQuery.extend( {}, options );

	var // Flag to know if list is currently firing
		firing,

		// Last fire value for non-forgettable lists
		memory,

		// Flag to know if list was already fired
		fired,

		// Flag to prevent firing
		locked,

		// Actual callback list
		list = [],

		// Queue of execution data for repeatable lists
		queue = [],

		// Index of currently firing callback (modified by add/remove as needed)
		firingIndex = -1,

		// Fire callbacks
		fire = function() {

			// Enforce single-firing
			locked = options.once;

			// Execute callbacks for all pending executions,
			// respecting firingIndex overrides and runtime changes
			fired = firing = true;
			for ( ; queue.length; firingIndex = -1 ) {
				memory = queue.shift();
				while ( ++firingIndex < list.length ) {

					// Run callback and check for early termination
					if ( list[ firingIndex ].apply( memory[ 0 ], memory[ 1 ] ) === false &&
						options.stopOnFalse ) {

						// Jump to end and forget the data so .add doesn't re-fire
						firingIndex = list.length;
						memory = false;
					}
				}
			}

			// Forget the data if we're done with it
			if ( !options.memory ) {
				memory = false;
			}

			firing = false;

			// Clean up if we're done firing for good
			if ( locked ) {

				// Keep an empty list if we have data for future add calls
				if ( memory ) {
					list = [];

				// Otherwise, this object is spent
				} else {
					list = "";
				}
			}
		},

		// Actual Callbacks object
		self = {

			// Add a callback or a collection of callbacks to the list
			add: function() {
				if ( list ) {

					// If we have memory from a past run, we should fire after adding
					if ( memory && !firing ) {
						firingIndex = list.length - 1;
						queue.push( memory );
					}

					( function add( args ) {
						jQuery.each( args, function( _, arg ) {
							if ( jQuery.isFunction( arg ) ) {
								if ( !options.unique || !self.has( arg ) ) {
									list.push( arg );
								}
							} else if ( arg && arg.length && jQuery.type( arg ) !== "string" ) {

								// Inspect recursively
								add( arg );
							}
						} );
					} )( arguments );

					if ( memory && !firing ) {
						fire();
					}
				}
				return this;
			},

			// Remove a callback from the list
			remove: function() {
				jQuery.each( arguments, function( _, arg ) {
					var index;
					while ( ( index = jQuery.inArray( arg, list, index ) ) > -1 ) {
						list.splice( index, 1 );

						// Handle firing indexes
						if ( index <= firingIndex ) {
							firingIndex--;
						}
					}
				} );
				return this;
			},

			// Check if a given callback is in the list.
			// If no argument is given, return whether or not list has callbacks attached.
			has: function( fn ) {
				return fn ?
					jQuery.inArray( fn, list ) > -1 :
					list.length > 0;
			},

			// Remove all callbacks from the list
			empty: function() {
				if ( list ) {
					list = [];
				}
				return this;
			},

			// Disable .fire and .add
			// Abort any current/pending executions
			// Clear all callbacks and values
			disable: function() {
				locked = queue = [];
				list = memory = "";
				return this;
			},
			disabled: function() {
				return !list;
			},

			// Disable .fire
			// Also disable .add unless we have memory (since it would have no effect)
			// Abort any pending executions
			lock: function() {
				locked = true;
				if ( !memory ) {
					self.disable();
				}
				return this;
			},
			locked: function() {
				return !!locked;
			},

			// Call all callbacks with the given context and arguments
			fireWith: function( context, args ) {
				if ( !locked ) {
					args = args || [];
					args = [ context, args.slice ? args.slice() : args ];
					queue.push( args );
					if ( !firing ) {
						fire();
					}
				}
				return this;
			},

			// Call all the callbacks with the given arguments
			fire: function() {
				self.fireWith( this, arguments );
				return this;
			},

			// To know if the callbacks have already been called at least once
			fired: function() {
				return !!fired;
			}
		};

	return self;
};


jQuery.extend( {

	Deferred: function( func ) {
		var tuples = [

				// action, add listener, listener list, final state
				[ "resolve", "done", jQuery.Callbacks( "once memory" ), "resolved" ],
				[ "reject", "fail", jQuery.Callbacks( "once memory" ), "rejected" ],
				[ "notify", "progress", jQuery.Callbacks( "memory" ) ]
			],
			state = "pending",
			promise = {
				state: function() {
					return state;
				},
				always: function() {
					deferred.done( arguments ).fail( arguments );
					return this;
				},
				then: function( /* fnDone, fnFail, fnProgress */ ) {
					var fns = arguments;
					return jQuery.Deferred( function( newDefer ) {
						jQuery.each( tuples, function( i, tuple ) {
							var fn = jQuery.isFunction( fns[ i ] ) && fns[ i ];

							// deferred[ done | fail | progress ] for forwarding actions to newDefer
							deferred[ tuple[ 1 ] ]( function() {
								var returned = fn && fn.apply( this, arguments );
								if ( returned && jQuery.isFunction( returned.promise ) ) {
									returned.promise()
										.progress( newDefer.notify )
										.done( newDefer.resolve )
										.fail( newDefer.reject );
								} else {
									newDefer[ tuple[ 0 ] + "With" ](
										this === promise ? newDefer.promise() : this,
										fn ? [ returned ] : arguments
									);
								}
							} );
						} );
						fns = null;
					} ).promise();
				},

				// Get a promise for this deferred
				// If obj is provided, the promise aspect is added to the object
				promise: function( obj ) {
					return obj != null ? jQuery.extend( obj, promise ) : promise;
				}
			},
			deferred = {};

		// Keep pipe for back-compat
		promise.pipe = promise.then;

		// Add list-specific methods
		jQuery.each( tuples, function( i, tuple ) {
			var list = tuple[ 2 ],
				stateString = tuple[ 3 ];

			// promise[ done | fail | progress ] = list.add
			promise[ tuple[ 1 ] ] = list.add;

			// Handle state
			if ( stateString ) {
				list.add( function() {

					// state = [ resolved | rejected ]
					state = stateString;

				// [ reject_list | resolve_list ].disable; progress_list.lock
				}, tuples[ i ^ 1 ][ 2 ].disable, tuples[ 2 ][ 2 ].lock );
			}

			// deferred[ resolve | reject | notify ]
			deferred[ tuple[ 0 ] ] = function() {
				deferred[ tuple[ 0 ] + "With" ]( this === deferred ? promise : this, arguments );
				return this;
			};
			deferred[ tuple[ 0 ] + "With" ] = list.fireWith;
		} );

		// Make the deferred a promise
		promise.promise( deferred );

		// Call given func if any
		if ( func ) {
			func.call( deferred, deferred );
		}

		// All done!
		return deferred;
	},

	// Deferred helper
	when: function( subordinate /* , ..., subordinateN */ ) {
		var i = 0,
			resolveValues = slice.call( arguments ),
			length = resolveValues.length,

			// the count of uncompleted subordinates
			remaining = length !== 1 ||
				( subordinate && jQuery.isFunction( subordinate.promise ) ) ? length : 0,

			// the master Deferred.
			// If resolveValues consist of only a single Deferred, just use that.
			deferred = remaining === 1 ? subordinate : jQuery.Deferred(),

			// Update function for both resolve and progress values
			updateFunc = function( i, contexts, values ) {
				return function( value ) {
					contexts[ i ] = this;
					values[ i ] = arguments.length > 1 ? slice.call( arguments ) : value;
					if ( values === progressValues ) {
						deferred.notifyWith( contexts, values );

					} else if ( !( --remaining ) ) {
						deferred.resolveWith( contexts, values );
					}
				};
			},

			progressValues, progressContexts, resolveContexts;

		// add listeners to Deferred subordinates; treat others as resolved
		if ( length > 1 ) {
			progressValues = new Array( length );
			progressContexts = new Array( length );
			resolveContexts = new Array( length );
			for ( ; i < length; i++ ) {
				if ( resolveValues[ i ] && jQuery.isFunction( resolveValues[ i ].promise ) ) {
					resolveValues[ i ].promise()
						.progress( updateFunc( i, progressContexts, progressValues ) )
						.done( updateFunc( i, resolveContexts, resolveValues ) )
						.fail( deferred.reject );
				} else {
					--remaining;
				}
			}
		}

		// if we're not waiting on anything, resolve the master
		if ( !remaining ) {
			deferred.resolveWith( resolveContexts, resolveValues );
		}

		return deferred.promise();
	}
} );


// The deferred used on DOM ready
var readyList;

jQuery.fn.ready = function( fn ) {

	// Add the callback
	jQuery.ready.promise().done( fn );

	return this;
};

jQuery.extend( {

	// Is the DOM ready to be used? Set to true once it occurs.
	isReady: false,

	// A counter to track how many items to wait for before
	// the ready event fires. See #6781
	readyWait: 1,

	// Hold (or release) the ready event
	holdReady: function( hold ) {
		if ( hold ) {
			jQuery.readyWait++;
		} else {
			jQuery.ready( true );
		}
	},

	// Handle when the DOM is ready
	ready: function( wait ) {

		// Abort if there are pending holds or we're already ready
		if ( wait === true ? --jQuery.readyWait : jQuery.isReady ) {
			return;
		}

		// Remember that the DOM is ready
		jQuery.isReady = true;

		// If a normal DOM Ready event fired, decrement, and wait if need be
		if ( wait !== true && --jQuery.readyWait > 0 ) {
			return;
		}

		// If there are functions bound, to execute
		readyList.resolveWith( document, [ jQuery ] );

		// Trigger any bound ready events
		if ( jQuery.fn.triggerHandler ) {
			jQuery( document ).triggerHandler( "ready" );
			jQuery( document ).off( "ready" );
		}
	}
} );

/**
 * Clean-up method for dom ready events
 */
function detach() {
	if ( document.addEventListener ) {
		document.removeEventListener( "DOMContentLoaded", completed );
		window.removeEventListener( "load", completed );

	} else {
		document.detachEvent( "onreadystatechange", completed );
		window.detachEvent( "onload", completed );
	}
}

/**
 * The ready event handler and self cleanup method
 */
function completed() {

	// readyState === "complete" is good enough for us to call the dom ready in oldIE
	if ( document.addEventListener ||
		window.event.type === "load" ||
		document.readyState === "complete" ) {

		detach();
		jQuery.ready();
	}
}

jQuery.ready.promise = function( obj ) {
	if ( !readyList ) {

		readyList = jQuery.Deferred();

		// Catch cases where $(document).ready() is called
		// after the browser event has already occurred.
		// Support: IE6-10
		// Older IE sometimes signals "interactive" too soon
		if ( document.readyState === "complete" ||
			( document.readyState !== "loading" && !document.documentElement.doScroll ) ) {

			// Handle it asynchronously to allow scripts the opportunity to delay ready
			window.setTimeout( jQuery.ready );

		// Standards-based browsers support DOMContentLoaded
		} else if ( document.addEventListener ) {

			// Use the handy event callback
			document.addEventListener( "DOMContentLoaded", completed );

			// A fallback to window.onload, that will always work
			window.addEventListener( "load", completed );

		// If IE event model is used
		} else {

			// Ensure firing before onload, maybe late but safe also for iframes
			document.attachEvent( "onreadystatechange", completed );

			// A fallback to window.onload, that will always work
			window.attachEvent( "onload", completed );

			// If IE and not a frame
			// continually check to see if the document is ready
			var top = false;

			try {
				top = window.frameElement == null && document.documentElement;
			} catch ( e ) {}

			if ( top && top.doScroll ) {
				( function doScrollCheck() {
					if ( !jQuery.isReady ) {

						try {

							// Use the trick by Diego Perini
							// http://javascript.nwbox.com/IEContentLoaded/
							top.doScroll( "left" );
						} catch ( e ) {
							return window.setTimeout( doScrollCheck, 50 );
						}

						// detach all dom ready events
						detach();

						// and execute any waiting functions
						jQuery.ready();
					}
				} )();
			}
		}
	}
	return readyList.promise( obj );
};

// Kick off the DOM ready check even if the user does not
jQuery.ready.promise();




// Support: IE<9
// Iteration over object's inherited properties before its own
var i;
for ( i in jQuery( support ) ) {
	break;
}
support.ownFirst = i === "0";

// Note: most support tests are defined in their respective modules.
// false until the test is run
support.inlineBlockNeedsLayout = false;

// Execute ASAP in case we need to set body.style.zoom
jQuery( function() {

	// Minified: var a,b,c,d
	var val, div, body, container;

	body = document.getElementsByTagName( "body" )[ 0 ];
	if ( !body || !body.style ) {

		// Return for frameset docs that don't have a body
		return;
	}

	// Setup
	div = document.createElement( "div" );
	container = document.createElement( "div" );
	container.style.cssText = "position:absolute;border:0;width:0;height:0;top:0;left:-9999px";
	body.appendChild( container ).appendChild( div );

	if ( typeof div.style.zoom !== "undefined" ) {

		// Support: IE<8
		// Check if natively block-level elements act like inline-block
		// elements when setting their display to 'inline' and giving
		// them layout
		div.style.cssText = "display:inline;margin:0;border:0;padding:1px;width:1px;zoom:1";

		support.inlineBlockNeedsLayout = val = div.offsetWidth === 3;
		if ( val ) {

			// Prevent IE 6 from affecting layout for positioned elements #11048
			// Prevent IE from shrinking the body in IE 7 mode #12869
			// Support: IE<8
			body.style.zoom = 1;
		}
	}

	body.removeChild( container );
} );


( function() {
	var div = document.createElement( "div" );

	// Support: IE<9
	support.deleteExpando = true;
	try {
		delete div.test;
	} catch ( e ) {
		support.deleteExpando = false;
	}

	// Null elements to avoid leaks in IE.
	div = null;
} )();
var acceptData = function( elem ) {
	var noData = jQuery.noData[ ( elem.nodeName + " " ).toLowerCase() ],
		nodeType = +elem.nodeType || 1;

	// Do not set data on non-element DOM nodes because it will not be cleared (#8335).
	return nodeType !== 1 && nodeType !== 9 ?
		false :

		// Nodes accept data unless otherwise specified; rejection can be conditional
		!noData || noData !== true && elem.getAttribute( "classid" ) === noData;
};




var rbrace = /^(?:\{[\w\W]*\}|\[[\w\W]*\])$/,
	rmultiDash = /([A-Z])/g;

function dataAttr( elem, key, data ) {

	// If nothing was found internally, try to fetch any
	// data from the HTML5 data-* attribute
	if ( data === undefined && elem.nodeType === 1 ) {

		var name = "data-" + key.replace( rmultiDash, "-$1" ).toLowerCase();

		data = elem.getAttribute( name );

		if ( typeof data === "string" ) {
			try {
				data = data === "true" ? true :
					data === "false" ? false :
					data === "null" ? null :

					// Only convert to a number if it doesn't change the string
					+data + "" === data ? +data :
					rbrace.test( data ) ? jQuery.parseJSON( data ) :
					data;
			} catch ( e ) {}

			// Make sure we set the data so it isn't changed later
			jQuery.data( elem, key, data );

		} else {
			data = undefined;
		}
	}

	return data;
}

// checks a cache object for emptiness
function isEmptyDataObject( obj ) {
	var name;
	for ( name in obj ) {

		// if the public data object is empty, the private is still empty
		if ( name === "data" && jQuery.isEmptyObject( obj[ name ] ) ) {
			continue;
		}
		if ( name !== "toJSON" ) {
			return false;
		}
	}

	return true;
}

function internalData( elem, name, data, pvt /* Internal Use Only */ ) {
	if ( !acceptData( elem ) ) {
		return;
	}

	var ret, thisCache,
		internalKey = jQuery.expando,

		// We have to handle DOM nodes and JS objects differently because IE6-7
		// can't GC object references properly across the DOM-JS boundary
		isNode = elem.nodeType,

		// Only DOM nodes need the global jQuery cache; JS object data is
		// attached directly to the object so GC can occur automatically
		cache = isNode ? jQuery.cache : elem,

		// Only defining an ID for JS objects if its cache already exists allows
		// the code to shortcut on the same path as a DOM node with no cache
		id = isNode ? elem[ internalKey ] : elem[ internalKey ] && internalKey;

	// Avoid doing any more work than we need to when trying to get data on an
	// object that has no data at all
	if ( ( !id || !cache[ id ] || ( !pvt && !cache[ id ].data ) ) &&
		data === undefined && typeof name === "string" ) {
		return;
	}

	if ( !id ) {

		// Only DOM nodes need a new unique ID for each element since their data
		// ends up in the global cache
		if ( isNode ) {
			id = elem[ internalKey ] = deletedIds.pop() || jQuery.guid++;
		} else {
			id = internalKey;
		}
	}

	if ( !cache[ id ] ) {

		// Avoid exposing jQuery metadata on plain JS objects when the object
		// is serialized using JSON.stringify
		cache[ id ] = isNode ? {} : { toJSON: jQuery.noop };
	}

	// An object can be passed to jQuery.data instead of a key/value pair; this gets
	// shallow copied over onto the existing cache
	if ( typeof name === "object" || typeof name === "function" ) {
		if ( pvt ) {
			cache[ id ] = jQuery.extend( cache[ id ], name );
		} else {
			cache[ id ].data = jQuery.extend( cache[ id ].data, name );
		}
	}

	thisCache = cache[ id ];

	// jQuery data() is stored in a separate object inside the object's internal data
	// cache in order to avoid key collisions between internal data and user-defined
	// data.
	if ( !pvt ) {
		if ( !thisCache.data ) {
			thisCache.data = {};
		}

		thisCache = thisCache.data;
	}

	if ( data !== undefined ) {
		thisCache[ jQuery.camelCase( name ) ] = data;
	}

	// Check for both converted-to-camel and non-converted data property names
	// If a data property was specified
	if ( typeof name === "string" ) {

		// First Try to find as-is property data
		ret = thisCache[ name ];

		// Test for null|undefined property data
		if ( ret == null ) {

			// Try to find the camelCased property
			ret = thisCache[ jQuery.camelCase( name ) ];
		}
	} else {
		ret = thisCache;
	}

	return ret;
}

function internalRemoveData( elem, name, pvt ) {
	if ( !acceptData( elem ) ) {
		return;
	}

	var thisCache, i,
		isNode = elem.nodeType,

		// See jQuery.data for more information
		cache = isNode ? jQuery.cache : elem,
		id = isNode ? elem[ jQuery.expando ] : jQuery.expando;

	// If there is already no cache entry for this object, there is no
	// purpose in continuing
	if ( !cache[ id ] ) {
		return;
	}

	if ( name ) {

		thisCache = pvt ? cache[ id ] : cache[ id ].data;

		if ( thisCache ) {

			// Support array or space separated string names for data keys
			if ( !jQuery.isArray( name ) ) {

				// try the string as a key before any manipulation
				if ( name in thisCache ) {
					name = [ name ];
				} else {

					// split the camel cased version by spaces unless a key with the spaces exists
					name = jQuery.camelCase( name );
					if ( name in thisCache ) {
						name = [ name ];
					} else {
						name = name.split( " " );
					}
				}
			} else {

				// If "name" is an array of keys...
				// When data is initially created, via ("key", "val") signature,
				// keys will be converted to camelCase.
				// Since there is no way to tell _how_ a key was added, remove
				// both plain key and camelCase key. #12786
				// This will only penalize the array argument path.
				name = name.concat( jQuery.map( name, jQuery.camelCase ) );
			}

			i = name.length;
			while ( i-- ) {
				delete thisCache[ name[ i ] ];
			}

			// If there is no data left in the cache, we want to continue
			// and let the cache object itself get destroyed
			if ( pvt ? !isEmptyDataObject( thisCache ) : !jQuery.isEmptyObject( thisCache ) ) {
				return;
			}
		}
	}

	// See jQuery.data for more information
	if ( !pvt ) {
		delete cache[ id ].data;

		// Don't destroy the parent cache unless the internal data object
		// had been the only thing left in it
		if ( !isEmptyDataObject( cache[ id ] ) ) {
			return;
		}
	}

	// Destroy the cache
	if ( isNode ) {
		jQuery.cleanData( [ elem ], true );

	// Use delete when supported for expandos or `cache` is not a window per isWindow (#10080)
	/* jshint eqeqeq: false */
	} else if ( support.deleteExpando || cache != cache.window ) {
		/* jshint eqeqeq: true */
		delete cache[ id ];

	// When all else fails, undefined
	} else {
		cache[ id ] = undefined;
	}
}

jQuery.extend( {
	cache: {},

	// The following elements (space-suffixed to avoid Object.prototype collisions)
	// throw uncatchable exceptions if you attempt to set expando properties
	noData: {
		"applet ": true,
		"embed ": true,

		// ...but Flash objects (which have this classid) *can* handle expandos
		"object ": "clsid:D27CDB6E-AE6D-11cf-96B8-444553540000"
	},

	hasData: function( elem ) {
		elem = elem.nodeType ? jQuery.cache[ elem[ jQuery.expando ] ] : elem[ jQuery.expando ];
		return !!elem && !isEmptyDataObject( elem );
	},

	data: function( elem, name, data ) {
		return internalData( elem, name, data );
	},

	removeData: function( elem, name ) {
		return internalRemoveData( elem, name );
	},

	// For internal use only.
	_data: function( elem, name, data ) {
		return internalData( elem, name, data, true );
	},

	_removeData: function( elem, name ) {
		return internalRemoveData( elem, name, true );
	}
} );

jQuery.fn.extend( {
	data: function( key, value ) {
		var i, name, data,
			elem = this[ 0 ],
			attrs = elem && elem.attributes;

		// Special expections of .data basically thwart jQuery.access,
		// so implement the relevant behavior ourselves

		// Gets all values
		if ( key === undefined ) {
			if ( this.length ) {
				data = jQuery.data( elem );

				if ( elem.nodeType === 1 && !jQuery._data( elem, "parsedAttrs" ) ) {
					i = attrs.length;
					while ( i-- ) {

						// Support: IE11+
						// The attrs elements can be null (#14894)
						if ( attrs[ i ] ) {
							name = attrs[ i ].name;
							if ( name.indexOf( "data-" ) === 0 ) {
								name = jQuery.camelCase( name.slice( 5 ) );
								dataAttr( elem, name, data[ name ] );
							}
						}
					}
					jQuery._data( elem, "parsedAttrs", true );
				}
			}

			return data;
		}

		// Sets multiple values
		if ( typeof key === "object" ) {
			return this.each( function() {
				jQuery.data( this, key );
			} );
		}

		return arguments.length > 1 ?

			// Sets one value
			this.each( function() {
				jQuery.data( this, key, value );
			} ) :

			// Gets one value
			// Try to fetch any internally stored data first
			elem ? dataAttr( elem, key, jQuery.data( elem, key ) ) : undefined;
	},

	removeData: function( key ) {
		return this.each( function() {
			jQuery.removeData( this, key );
		} );
	}
} );


jQuery.extend( {
	queue: function( elem, type, data ) {
		var queue;

		if ( elem ) {
			type = ( type || "fx" ) + "queue";
			queue = jQuery._data( elem, type );

			// Speed up dequeue by getting out quickly if this is just a lookup
			if ( data ) {
				if ( !queue || jQuery.isArray( data ) ) {
					queue = jQuery._data( elem, type, jQuery.makeArray( data ) );
				} else {
					queue.push( data );
				}
			}
			return queue || [];
		}
	},

	dequeue: function( elem, type ) {
		type = type || "fx";

		var queue = jQuery.queue( elem, type ),
			startLength = queue.length,
			fn = queue.shift(),
			hooks = jQuery._queueHooks( elem, type ),
			next = function() {
				jQuery.dequeue( elem, type );
			};

		// If the fx queue is dequeued, always remove the progress sentinel
		if ( fn === "inprogress" ) {
			fn = queue.shift();
			startLength--;
		}

		if ( fn ) {

			// Add a progress sentinel to prevent the fx queue from being
			// automatically dequeued
			if ( type === "fx" ) {
				queue.unshift( "inprogress" );
			}

			// clear up the last queue stop function
			delete hooks.stop;
			fn.call( elem, next, hooks );
		}

		if ( !startLength && hooks ) {
			hooks.empty.fire();
		}
	},

	// not intended for public consumption - generates a queueHooks object,
	// or returns the current one
	_queueHooks: function( elem, type ) {
		var key = type + "queueHooks";
		return jQuery._data( elem, key ) || jQuery._data( elem, key, {
			empty: jQuery.Callbacks( "once memory" ).add( function() {
				jQuery._removeData( elem, type + "queue" );
				jQuery._removeData( elem, key );
			} )
		} );
	}
} );

jQuery.fn.extend( {
	queue: function( type, data ) {
		var setter = 2;

		if ( typeof type !== "string" ) {
			data = type;
			type = "fx";
			setter--;
		}

		if ( arguments.length < setter ) {
			return jQuery.queue( this[ 0 ], type );
		}

		return data === undefined ?
			this :
			this.each( function() {
				var queue = jQuery.queue( this, type, data );

				// ensure a hooks for this queue
				jQuery._queueHooks( this, type );

				if ( type === "fx" && queue[ 0 ] !== "inprogress" ) {
					jQuery.dequeue( this, type );
				}
			} );
	},
	dequeue: function( type ) {
		return this.each( function() {
			jQuery.dequeue( this, type );
		} );
	},
	clearQueue: function( type ) {
		return this.queue( type || "fx", [] );
	},

	// Get a promise resolved when queues of a certain type
	// are emptied (fx is the type by default)
	promise: function( type, obj ) {
		var tmp,
			count = 1,
			defer = jQuery.Deferred(),
			elements = this,
			i = this.length,
			resolve = function() {
				if ( !( --count ) ) {
					defer.resolveWith( elements, [ elements ] );
				}
			};

		if ( typeof type !== "string" ) {
			obj = type;
			type = undefined;
		}
		type = type || "fx";

		while ( i-- ) {
			tmp = jQuery._data( elements[ i ], type + "queueHooks" );
			if ( tmp && tmp.empty ) {
				count++;
				tmp.empty.add( resolve );
			}
		}
		resolve();
		return defer.promise( obj );
	}
} );


( function() {
	var shrinkWrapBlocksVal;

	support.shrinkWrapBlocks = function() {
		if ( shrinkWrapBlocksVal != null ) {
			return shrinkWrapBlocksVal;
		}

		// Will be changed later if needed.
		shrinkWrapBlocksVal = false;

		// Minified: var b,c,d
		var div, body, container;

		body = document.getElementsByTagName( "body" )[ 0 ];
		if ( !body || !body.style ) {

			// Test fired too early or in an unsupported environment, exit.
			return;
		}

		// Setup
		div = document.createElement( "div" );
		container = document.createElement( "div" );
		container.style.cssText = "position:absolute;border:0;width:0;height:0;top:0;left:-9999px";
		body.appendChild( container ).appendChild( div );

		// Support: IE6
		// Check if elements with layout shrink-wrap their children
		if ( typeof div.style.zoom !== "undefined" ) {

			// Reset CSS: box-sizing; display; margin; border
			div.style.cssText =

				// Support: Firefox<29, Android 2.3
				// Vendor-prefix box-sizing
				"-webkit-box-sizing:content-box;-moz-box-sizing:content-box;" +
				"box-sizing:content-box;display:block;margin:0;border:0;" +
				"padding:1px;width:1px;zoom:1";
			div.appendChild( document.createElement( "div" ) ).style.width = "5px";
			shrinkWrapBlocksVal = div.offsetWidth !== 3;
		}

		body.removeChild( container );

		return shrinkWrapBlocksVal;
	};

} )();
var pnum = ( /[+-]?(?:\d*\.|)\d+(?:[eE][+-]?\d+|)/ ).source;

var rcssNum = new RegExp( "^(?:([+-])=|)(" + pnum + ")([a-z%]*)$", "i" );


var cssExpand = [ "Top", "Right", "Bottom", "Left" ];

var isHidden = function( elem, el ) {

		// isHidden might be called from jQuery#filter function;
		// in that case, element will be second argument
		elem = el || elem;
		return jQuery.css( elem, "display" ) === "none" ||
			!jQuery.contains( elem.ownerDocument, elem );
	};



function adjustCSS( elem, prop, valueParts, tween ) {
	var adjusted,
		scale = 1,
		maxIterations = 20,
		currentValue = tween ?
			function() { return tween.cur(); } :
			function() { return jQuery.css( elem, prop, "" ); },
		initial = currentValue(),
		unit = valueParts && valueParts[ 3 ] || ( jQuery.cssNumber[ prop ] ? "" : "px" ),

		// Starting value computation is required for potential unit mismatches
		initialInUnit = ( jQuery.cssNumber[ prop ] || unit !== "px" && +initial ) &&
			rcssNum.exec( jQuery.css( elem, prop ) );

	if ( initialInUnit && initialInUnit[ 3 ] !== unit ) {

		// Trust units reported by jQuery.css
		unit = unit || initialInUnit[ 3 ];

		// Make sure we update the tween properties later on
		valueParts = valueParts || [];

		// Iteratively approximate from a nonzero starting point
		initialInUnit = +initial || 1;

		do {

			// If previous iteration zeroed out, double until we get *something*.
			// Use string for doubling so we don't accidentally see scale as unchanged below
			scale = scale || ".5";

			// Adjust and apply
			initialInUnit = initialInUnit / scale;
			jQuery.style( elem, prop, initialInUnit + unit );

		// Update scale, tolerating zero or NaN from tween.cur()
		// Break the loop if scale is unchanged or perfect, or if we've just had enough.
		} while (
			scale !== ( scale = currentValue() / initial ) && scale !== 1 && --maxIterations
		);
	}

	if ( valueParts ) {
		initialInUnit = +initialInUnit || +initial || 0;

		// Apply relative offset (+=/-=) if specified
		adjusted = valueParts[ 1 ] ?
			initialInUnit + ( valueParts[ 1 ] + 1 ) * valueParts[ 2 ] :
			+valueParts[ 2 ];
		if ( tween ) {
			tween.unit = unit;
			tween.start = initialInUnit;
			tween.end = adjusted;
		}
	}
	return adjusted;
}


// Multifunctional method to get and set values of a collection
// The value/s can optionally be executed if it's a function
var access = function( elems, fn, key, value, chainable, emptyGet, raw ) {
	var i = 0,
		length = elems.length,
		bulk = key == null;

	// Sets many values
	if ( jQuery.type( key ) === "object" ) {
		chainable = true;
		for ( i in key ) {
			access( elems, fn, i, key[ i ], true, emptyGet, raw );
		}

	// Sets one value
	} else if ( value !== undefined ) {
		chainable = true;

		if ( !jQuery.isFunction( value ) ) {
			raw = true;
		}

		if ( bulk ) {

			// Bulk operations run against the entire set
			if ( raw ) {
				fn.call( elems, value );
				fn = null;

			// ...except when executing function values
			} else {
				bulk = fn;
				fn = function( elem, key, value ) {
					return bulk.call( jQuery( elem ), value );
				};
			}
		}

		if ( fn ) {
			for ( ; i < length; i++ ) {
				fn(
					elems[ i ],
					key,
					raw ? value : value.call( elems[ i ], i, fn( elems[ i ], key ) )
				);
			}
		}
	}

	return chainable ?
		elems :

		// Gets
		bulk ?
			fn.call( elems ) :
			length ? fn( elems[ 0 ], key ) : emptyGet;
};
var rcheckableType = ( /^(?:checkbox|radio)$/i );

var rtagName = ( /<([\w:-]+)/ );

var rscriptType = ( /^$|\/(?:java|ecma)script/i );

var rleadingWhitespace = ( /^\s+/ );

var nodeNames = "abbr|article|aside|audio|bdi|canvas|data|datalist|" +
		"details|dialog|figcaption|figure|footer|header|hgroup|main|" +
		"mark|meter|nav|output|picture|progress|section|summary|template|time|video";



function createSafeFragment( document ) {
	var list = nodeNames.split( "|" ),
		safeFrag = document.createDocumentFragment();

	if ( safeFrag.createElement ) {
		while ( list.length ) {
			safeFrag.createElement(
				list.pop()
			);
		}
	}
	return safeFrag;
}


( function() {
	var div = document.createElement( "div" ),
		fragment = document.createDocumentFragment(),
		input = document.createElement( "input" );

	// Setup
	div.innerHTML = "  <link/><table></table><a href='/a'>a</a><input type='checkbox'/>";

	// IE strips leading whitespace when .innerHTML is used
	support.leadingWhitespace = div.firstChild.nodeType === 3;

	// Make sure that tbody elements aren't automatically inserted
	// IE will insert them into empty tables
	support.tbody = !div.getElementsByTagName( "tbody" ).length;

	// Make sure that link elements get serialized correctly by innerHTML
	// This requires a wrapper element in IE
	support.htmlSerialize = !!div.getElementsByTagName( "link" ).length;

	// Makes sure cloning an html5 element does not cause problems
	// Where outerHTML is undefined, this still works
	support.html5Clone =
		document.createElement( "nav" ).cloneNode( true ).outerHTML !== "<:nav></:nav>";

	// Check if a disconnected checkbox will retain its checked
	// value of true after appended to the DOM (IE6/7)
	input.type = "checkbox";
	input.checked = true;
	fragment.appendChild( input );
	support.appendChecked = input.checked;

	// Make sure textarea (and checkbox) defaultValue is properly cloned
	// Support: IE6-IE11+
	div.innerHTML = "<textarea>x</textarea>";
	support.noCloneChecked = !!div.cloneNode( true ).lastChild.defaultValue;

	// #11217 - WebKit loses check when the name is after the checked attribute
	fragment.appendChild( div );

	// Support: Windows Web Apps (WWA)
	// `name` and `type` must use .setAttribute for WWA (#14901)
	input = document.createElement( "input" );
	input.setAttribute( "type", "radio" );
	input.setAttribute( "checked", "checked" );
	input.setAttribute( "name", "t" );

	div.appendChild( input );

	// Support: Safari 5.1, iOS 5.1, Android 4.x, Android 2.3
	// old WebKit doesn't clone checked state correctly in fragments
	support.checkClone = div.cloneNode( true ).cloneNode( true ).lastChild.checked;

	// Support: IE<9
	// Cloned elements keep attachEvent handlers, we use addEventListener on IE9+
	support.noCloneEvent = !!div.addEventListener;

	// Support: IE<9
	// Since attributes and properties are the same in IE,
	// cleanData must set properties to undefined rather than use removeAttribute
	div[ jQuery.expando ] = 1;
	support.attributes = !div.getAttribute( jQuery.expando );
} )();


// We have to close these tags to support XHTML (#13200)
var wrapMap = {
	option: [ 1, "<select multiple='multiple'>", "</select>" ],
	legend: [ 1, "<fieldset>", "</fieldset>" ],
	area: [ 1, "<map>", "</map>" ],

	// Support: IE8
	param: [ 1, "<object>", "</object>" ],
	thead: [ 1, "<table>", "</table>" ],
	tr: [ 2, "<table><tbody>", "</tbody></table>" ],
	col: [ 2, "<table><tbody></tbody><colgroup>", "</colgroup></table>" ],
	td: [ 3, "<table><tbody><tr>", "</tr></tbody></table>" ],

	// IE6-8 can't serialize link, script, style, or any html5 (NoScope) tags,
	// unless wrapped in a div with non-breaking characters in front of it.
	_default: support.htmlSerialize ? [ 0, "", "" ] : [ 1, "X<div>", "</div>" ]
};

// Support: IE8-IE9
wrapMap.optgroup = wrapMap.option;

wrapMap.tbody = wrapMap.tfoot = wrapMap.colgroup = wrapMap.caption = wrapMap.thead;
wrapMap.th = wrapMap.td;


function getAll( context, tag ) {
	var elems, elem,
		i = 0,
		found = typeof context.getElementsByTagName !== "undefined" ?
			context.getElementsByTagName( tag || "*" ) :
			typeof context.querySelectorAll !== "undefined" ?
				context.querySelectorAll( tag || "*" ) :
				undefined;

	if ( !found ) {
		for ( found = [], elems = context.childNodes || context;
			( elem = elems[ i ] ) != null;
			i++
		) {
			if ( !tag || jQuery.nodeName( elem, tag ) ) {
				found.push( elem );
			} else {
				jQuery.merge( found, getAll( elem, tag ) );
			}
		}
	}

	return tag === undefined || tag && jQuery.nodeName( context, tag ) ?
		jQuery.merge( [ context ], found ) :
		found;
}


// Mark scripts as having already been evaluated
function setGlobalEval( elems, refElements ) {
	var elem,
		i = 0;
	for ( ; ( elem = elems[ i ] ) != null; i++ ) {
		jQuery._data(
			elem,
			"globalEval",
			!refElements || jQuery._data( refElements[ i ], "globalEval" )
		);
	}
}


var rhtml = /<|&#?\w+;/,
	rtbody = /<tbody/i;

function fixDefaultChecked( elem ) {
	if ( rcheckableType.test( elem.type ) ) {
		elem.defaultChecked = elem.checked;
	}
}

function buildFragment( elems, context, scripts, selection, ignored ) {
	var j, elem, contains,
		tmp, tag, tbody, wrap,
		l = elems.length,

		// Ensure a safe fragment
		safe = createSafeFragment( context ),

		nodes = [],
		i = 0;

	for ( ; i < l; i++ ) {
		elem = elems[ i ];

		if ( elem || elem === 0 ) {

			// Add nodes directly
			if ( jQuery.type( elem ) === "object" ) {
				jQuery.merge( nodes, elem.nodeType ? [ elem ] : elem );

			// Convert non-html into a text node
			} else if ( !rhtml.test( elem ) ) {
				nodes.push( context.createTextNode( elem ) );

			// Convert html into DOM nodes
			} else {
				tmp = tmp || safe.appendChild( context.createElement( "div" ) );

				// Deserialize a standard representation
				tag = ( rtagName.exec( elem ) || [ "", "" ] )[ 1 ].toLowerCase();
				wrap = wrapMap[ tag ] || wrapMap._default;

				tmp.innerHTML = wrap[ 1 ] + jQuery.htmlPrefilter( elem ) + wrap[ 2 ];

				// Descend through wrappers to the right content
				j = wrap[ 0 ];
				while ( j-- ) {
					tmp = tmp.lastChild;
				}

				// Manually add leading whitespace removed by IE
				if ( !support.leadingWhitespace && rleadingWhitespace.test( elem ) ) {
					nodes.push( context.createTextNode( rleadingWhitespace.exec( elem )[ 0 ] ) );
				}

				// Remove IE's autoinserted <tbody> from table fragments
				if ( !support.tbody ) {

					// String was a <table>, *may* have spurious <tbody>
					elem = tag === "table" && !rtbody.test( elem ) ?
						tmp.firstChild :

						// String was a bare <thead> or <tfoot>
						wrap[ 1 ] === "<table>" && !rtbody.test( elem ) ?
							tmp :
							0;

					j = elem && elem.childNodes.length;
					while ( j-- ) {
						if ( jQuery.nodeName( ( tbody = elem.childNodes[ j ] ), "tbody" ) &&
							!tbody.childNodes.length ) {

							elem.removeChild( tbody );
						}
					}
				}

				jQuery.merge( nodes, tmp.childNodes );

				// Fix #12392 for WebKit and IE > 9
				tmp.textContent = "";

				// Fix #12392 for oldIE
				while ( tmp.firstChild ) {
					tmp.removeChild( tmp.firstChild );
				}

				// Remember the top-level container for proper cleanup
				tmp = safe.lastChild;
			}
		}
	}

	// Fix #11356: Clear elements from fragment
	if ( tmp ) {
		safe.removeChild( tmp );
	}

	// Reset defaultChecked for any radios and checkboxes
	// about to be appended to the DOM in IE 6/7 (#8060)
	if ( !support.appendChecked ) {
		jQuery.grep( getAll( nodes, "input" ), fixDefaultChecked );
	}

	i = 0;
	while ( ( elem = nodes[ i++ ] ) ) {

		// Skip elements already in the context collection (trac-4087)
		if ( selection && jQuery.inArray( elem, selection ) > -1 ) {
			if ( ignored ) {
				ignored.push( elem );
			}

			continue;
		}

		contains = jQuery.contains( elem.ownerDocument, elem );

		// Append to fragment
		tmp = getAll( safe.appendChild( elem ), "script" );

		// Preserve script evaluation history
		if ( contains ) {
			setGlobalEval( tmp );
		}

		// Capture executables
		if ( scripts ) {
			j = 0;
			while ( ( elem = tmp[ j++ ] ) ) {
				if ( rscriptType.test( elem.type || "" ) ) {
					scripts.push( elem );
				}
			}
		}
	}

	tmp = null;

	return safe;
}


( function() {
	var i, eventName,
		div = document.createElement( "div" );

	// Support: IE<9 (lack submit/change bubble), Firefox (lack focus(in | out) events)
	for ( i in { submit: true, change: true, focusin: true } ) {
		eventName = "on" + i;

		if ( !( support[ i ] = eventName in window ) ) {

			// Beware of CSP restrictions (https://developer.mozilla.org/en/Security/CSP)
			div.setAttribute( eventName, "t" );
			support[ i ] = div.attributes[ eventName ].expando === false;
		}
	}

	// Null elements to avoid leaks in IE.
	div = null;
} )();


var rformElems = /^(?:input|select|textarea)$/i,
	rkeyEvent = /^key/,
	rmouseEvent = /^(?:mouse|pointer|contextmenu|drag|drop)|click/,
	rfocusMorph = /^(?:focusinfocus|focusoutblur)$/,
	rtypenamespace = /^([^.]*)(?:\.(.+)|)/;

function returnTrue() {
	return true;
}

function returnFalse() {
	return false;
}

// Support: IE9
// See #13393 for more info
function safeActiveElement() {
	try {
		return document.activeElement;
	} catch ( err ) { }
}

function on( elem, types, selector, data, fn, one ) {
	var origFn, type;

	// Types can be a map of types/handlers
	if ( typeof types === "object" ) {

		// ( types-Object, selector, data )
		if ( typeof selector !== "string" ) {

			// ( types-Object, data )
			data = data || selector;
			selector = undefined;
		}
		for ( type in types ) {
			on( elem, type, selector, data, types[ type ], one );
		}
		return elem;
	}

	if ( data == null && fn == null ) {

		// ( types, fn )
		fn = selector;
		data = selector = undefined;
	} else if ( fn == null ) {
		if ( typeof selector === "string" ) {

			// ( types, selector, fn )
			fn = data;
			data = undefined;
		} else {

			// ( types, data, fn )
			fn = data;
			data = selector;
			selector = undefined;
		}
	}
	if ( fn === false ) {
		fn = returnFalse;
	} else if ( !fn ) {
		return elem;
	}

	if ( one === 1 ) {
		origFn = fn;
		fn = function( event ) {

			// Can use an empty set, since event contains the info
			jQuery().off( event );
			return origFn.apply( this, arguments );
		};

		// Use same guid so caller can remove using origFn
		fn.guid = origFn.guid || ( origFn.guid = jQuery.guid++ );
	}
	return elem.each( function() {
		jQuery.event.add( this, types, fn, data, selector );
	} );
}

/*
 * Helper functions for managing events -- not part of the public interface.
 * Props to Dean Edwards' addEvent library for many of the ideas.
 */
jQuery.event = {

	global: {},

	add: function( elem, types, handler, data, selector ) {
		var tmp, events, t, handleObjIn,
			special, eventHandle, handleObj,
			handlers, type, namespaces, origType,
			elemData = jQuery._data( elem );

		// Don't attach events to noData or text/comment nodes (but allow plain objects)
		if ( !elemData ) {
			return;
		}

		// Caller can pass in an object of custom data in lieu of the handler
		if ( handler.handler ) {
			handleObjIn = handler;
			handler = handleObjIn.handler;
			selector = handleObjIn.selector;
		}

		// Make sure that the handler has a unique ID, used to find/remove it later
		if ( !handler.guid ) {
			handler.guid = jQuery.guid++;
		}

		// Init the element's event structure and main handler, if this is the first
		if ( !( events = elemData.events ) ) {
			events = elemData.events = {};
		}
		if ( !( eventHandle = elemData.handle ) ) {
			eventHandle = elemData.handle = function( e ) {

				// Discard the second event of a jQuery.event.trigger() and
				// when an event is called after a page has unloaded
				return typeof jQuery !== "undefined" &&
					( !e || jQuery.event.triggered !== e.type ) ?
					jQuery.event.dispatch.apply( eventHandle.elem, arguments ) :
					undefined;
			};

			// Add elem as a property of the handle fn to prevent a memory leak
			// with IE non-native events
			eventHandle.elem = elem;
		}

		// Handle multiple events separated by a space
		types = ( types || "" ).match( rnotwhite ) || [ "" ];
		t = types.length;
		while ( t-- ) {
			tmp = rtypenamespace.exec( types[ t ] ) || [];
			type = origType = tmp[ 1 ];
			namespaces = ( tmp[ 2 ] || "" ).split( "." ).sort();

			// There *must* be a type, no attaching namespace-only handlers
			if ( !type ) {
				continue;
			}

			// If event changes its type, use the special event handlers for the changed type
			special = jQuery.event.special[ type ] || {};

			// If selector defined, determine special event api type, otherwise given type
			type = ( selector ? special.delegateType : special.bindType ) || type;

			// Update special based on newly reset type
			special = jQuery.event.special[ type ] || {};

			// handleObj is passed to all event handlers
			handleObj = jQuery.extend( {
				type: type,
				origType: origType,
				data: data,
				handler: handler,
				guid: handler.guid,
				selector: selector,
				needsContext: selector && jQuery.expr.match.needsContext.test( selector ),
				namespace: namespaces.join( "." )
			}, handleObjIn );

			// Init the event handler queue if we're the first
			if ( !( handlers = events[ type ] ) ) {
				handlers = events[ type ] = [];
				handlers.delegateCount = 0;

				// Only use addEventListener/attachEvent if the special events handler returns false
				if ( !special.setup ||
					special.setup.call( elem, data, namespaces, eventHandle ) === false ) {

					// Bind the global event handler to the element
					if ( elem.addEventListener ) {
						elem.addEventListener( type, eventHandle, false );

					} else if ( elem.attachEvent ) {
						elem.attachEvent( "on" + type, eventHandle );
					}
				}
			}

			if ( special.add ) {
				special.add.call( elem, handleObj );

				if ( !handleObj.handler.guid ) {
					handleObj.handler.guid = handler.guid;
				}
			}

			// Add to the element's handler list, delegates in front
			if ( selector ) {
				handlers.splice( handlers.delegateCount++, 0, handleObj );
			} else {
				handlers.push( handleObj );
			}

			// Keep track of which events have ever been used, for event optimization
			jQuery.event.global[ type ] = true;
		}

		// Nullify elem to prevent memory leaks in IE
		elem = null;
	},

	// Detach an event or set of events from an element
	remove: function( elem, types, handler, selector, mappedTypes ) {
		var j, handleObj, tmp,
			origCount, t, events,
			special, handlers, type,
			namespaces, origType,
			elemData = jQuery.hasData( elem ) && jQuery._data( elem );

		if ( !elemData || !( events = elemData.events ) ) {
			return;
		}

		// Once for each type.namespace in types; type may be omitted
		types = ( types || "" ).match( rnotwhite ) || [ "" ];
		t = types.length;
		while ( t-- ) {
			tmp = rtypenamespace.exec( types[ t ] ) || [];
			type = origType = tmp[ 1 ];
			namespaces = ( tmp[ 2 ] || "" ).split( "." ).sort();

			// Unbind all events (on this namespace, if provided) for the element
			if ( !type ) {
				for ( type in events ) {
					jQuery.event.remove( elem, type + types[ t ], handler, selector, true );
				}
				continue;
			}

			special = jQuery.event.special[ type ] || {};
			type = ( selector ? special.delegateType : special.bindType ) || type;
			handlers = events[ type ] || [];
			tmp = tmp[ 2 ] &&
				new RegExp( "(^|\\.)" + namespaces.join( "\\.(?:.*\\.|)" ) + "(\\.|$)" );

			// Remove matching events
			origCount = j = handlers.length;
			while ( j-- ) {
				handleObj = handlers[ j ];

				if ( ( mappedTypes || origType === handleObj.origType ) &&
					( !handler || handler.guid === handleObj.guid ) &&
					( !tmp || tmp.test( handleObj.namespace ) ) &&
					( !selector || selector === handleObj.selector ||
						selector === "**" && handleObj.selector ) ) {
					handlers.splice( j, 1 );

					if ( handleObj.selector ) {
						handlers.delegateCount--;
					}
					if ( special.remove ) {
						special.remove.call( elem, handleObj );
					}
				}
			}

			// Remove generic event handler if we removed something and no more handlers exist
			// (avoids potential for endless recursion during removal of special event handlers)
			if ( origCount && !handlers.length ) {
				if ( !special.teardown ||
					special.teardown.call( elem, namespaces, elemData.handle ) === false ) {

					jQuery.removeEvent( elem, type, elemData.handle );
				}

				delete events[ type ];
			}
		}

		// Remove the expando if it's no longer used
		if ( jQuery.isEmptyObject( events ) ) {
			delete elemData.handle;

			// removeData also checks for emptiness and clears the expando if empty
			// so use it instead of delete
			jQuery._removeData( elem, "events" );
		}
	},

	trigger: function( event, data, elem, onlyHandlers ) {
		var handle, ontype, cur,
			bubbleType, special, tmp, i,
			eventPath = [ elem || document ],
			type = hasOwn.call( event, "type" ) ? event.type : event,
			namespaces = hasOwn.call( event, "namespace" ) ? event.namespace.split( "." ) : [];

		cur = tmp = elem = elem || document;

		// Don't do events on text and comment nodes
		if ( elem.nodeType === 3 || elem.nodeType === 8 ) {
			return;
		}

		// focus/blur morphs to focusin/out; ensure we're not firing them right now
		if ( rfocusMorph.test( type + jQuery.event.triggered ) ) {
			return;
		}

		if ( type.indexOf( "." ) > -1 ) {

			// Namespaced trigger; create a regexp to match event type in handle()
			namespaces = type.split( "." );
			type = namespaces.shift();
			namespaces.sort();
		}
		ontype = type.indexOf( ":" ) < 0 && "on" + type;

		// Caller can pass in a jQuery.Event object, Object, or just an event type string
		event = event[ jQuery.expando ] ?
			event :
			new jQuery.Event( type, typeof event === "object" && event );

		// Trigger bitmask: & 1 for native handlers; & 2 for jQuery (always true)
		event.isTrigger = onlyHandlers ? 2 : 3;
		event.namespace = namespaces.join( "." );
		event.rnamespace = event.namespace ?
			new RegExp( "(^|\\.)" + namespaces.join( "\\.(?:.*\\.|)" ) + "(\\.|$)" ) :
			null;

		// Clean up the event in case it is being reused
		event.result = undefined;
		if ( !event.target ) {
			event.target = elem;
		}

		// Clone any incoming data and prepend the event, creating the handler arg list
		data = data == null ?
			[ event ] :
			jQuery.makeArray( data, [ event ] );

		// Allow special events to draw outside the lines
		special = jQuery.event.special[ type ] || {};
		if ( !onlyHandlers && special.trigger && special.trigger.apply( elem, data ) === false ) {
			return;
		}

		// Determine event propagation path in advance, per W3C events spec (#9951)
		// Bubble up to document, then to window; watch for a global ownerDocument var (#9724)
		if ( !onlyHandlers && !special.noBubble && !jQuery.isWindow( elem ) ) {

			bubbleType = special.delegateType || type;
			if ( !rfocusMorph.test( bubbleType + type ) ) {
				cur = cur.parentNode;
			}
			for ( ; cur; cur = cur.parentNode ) {
				eventPath.push( cur );
				tmp = cur;
			}

			// Only add window if we got to document (e.g., not plain obj or detached DOM)
			if ( tmp === ( elem.ownerDocument || document ) ) {
				eventPath.push( tmp.defaultView || tmp.parentWindow || window );
			}
		}

		// Fire handlers on the event path
		i = 0;
		while ( ( cur = eventPath[ i++ ] ) && !event.isPropagationStopped() ) {

			event.type = i > 1 ?
				bubbleType :
				special.bindType || type;

			// jQuery handler
			handle = ( jQuery._data( cur, "events" ) || {} )[ event.type ] &&
				jQuery._data( cur, "handle" );

			if ( handle ) {
				handle.apply( cur, data );
			}

			// Native handler
			handle = ontype && cur[ ontype ];
			if ( handle && handle.apply && acceptData( cur ) ) {
				event.result = handle.apply( cur, data );
				if ( event.result === false ) {
					event.preventDefault();
				}
			}
		}
		event.type = type;

		// If nobody prevented the default action, do it now
		if ( !onlyHandlers && !event.isDefaultPrevented() ) {

			if (
				( !special._default ||
				 special._default.apply( eventPath.pop(), data ) === false
				) && acceptData( elem )
			) {

				// Call a native DOM method on the target with the same name name as the event.
				// Can't use an .isFunction() check here because IE6/7 fails that test.
				// Don't do default actions on window, that's where global variables be (#6170)
				if ( ontype && elem[ type ] && !jQuery.isWindow( elem ) ) {

					// Don't re-trigger an onFOO event when we call its FOO() method
					tmp = elem[ ontype ];

					if ( tmp ) {
						elem[ ontype ] = null;
					}

					// Prevent re-triggering of the same event, since we already bubbled it above
					jQuery.event.triggered = type;
					try {
						elem[ type ]();
					} catch ( e ) {

						// IE<9 dies on focus/blur to hidden element (#1486,#12518)
						// only reproducible on winXP IE8 native, not IE9 in IE8 mode
					}
					jQuery.event.triggered = undefined;

					if ( tmp ) {
						elem[ ontype ] = tmp;
					}
				}
			}
		}

		return event.result;
	},

	dispatch: function( event ) {

		// Make a writable jQuery.Event from the native event object
		event = jQuery.event.fix( event );

		var i, j, ret, matched, handleObj,
			handlerQueue = [],
			args = slice.call( arguments ),
			handlers = ( jQuery._data( this, "events" ) || {} )[ event.type ] || [],
			special = jQuery.event.special[ event.type ] || {};

		// Use the fix-ed jQuery.Event rather than the (read-only) native event
		args[ 0 ] = event;
		event.delegateTarget = this;

		// Call the preDispatch hook for the mapped type, and let it bail if desired
		if ( special.preDispatch && special.preDispatch.call( this, event ) === false ) {
			return;
		}

		// Determine handlers
		handlerQueue = jQuery.event.handlers.call( this, event, handlers );

		// Run delegates first; they may want to stop propagation beneath us
		i = 0;
		while ( ( matched = handlerQueue[ i++ ] ) && !event.isPropagationStopped() ) {
			event.currentTarget = matched.elem;

			j = 0;
			while ( ( handleObj = matched.handlers[ j++ ] ) &&
				!event.isImmediatePropagationStopped() ) {

				// Triggered event must either 1) have no namespace, or 2) have namespace(s)
				// a subset or equal to those in the bound event (both can have no namespace).
				if ( !event.rnamespace || event.rnamespace.test( handleObj.namespace ) ) {

					event.handleObj = handleObj;
					event.data = handleObj.data;

					ret = ( ( jQuery.event.special[ handleObj.origType ] || {} ).handle ||
						handleObj.handler ).apply( matched.elem, args );

					if ( ret !== undefined ) {
						if ( ( event.result = ret ) === false ) {
							event.preventDefault();
							event.stopPropagation();
						}
					}
				}
			}
		}

		// Call the postDispatch hook for the mapped type
		if ( special.postDispatch ) {
			special.postDispatch.call( this, event );
		}

		return event.result;
	},

	handlers: function( event, handlers ) {
		var i, matches, sel, handleObj,
			handlerQueue = [],
			delegateCount = handlers.delegateCount,
			cur = event.target;

		// Support (at least): Chrome, IE9
		// Find delegate handlers
		// Black-hole SVG <use> instance trees (#13180)
		//
		// Support: Firefox<=42+
		// Avoid non-left-click in FF but don't block IE radio events (#3861, gh-2343)
		if ( delegateCount && cur.nodeType &&
			( event.type !== "click" || isNaN( event.button ) || event.button < 1 ) ) {

			/* jshint eqeqeq: false */
			for ( ; cur != this; cur = cur.parentNode || this ) {
				/* jshint eqeqeq: true */

				// Don't check non-elements (#13208)
				// Don't process clicks on disabled elements (#6911, #8165, #11382, #11764)
				if ( cur.nodeType === 1 && ( cur.disabled !== true || event.type !== "click" ) ) {
					matches = [];
					for ( i = 0; i < delegateCount; i++ ) {
						handleObj = handlers[ i ];

						// Don't conflict with Object.prototype properties (#13203)
						sel = handleObj.selector + " ";

						if ( matches[ sel ] === undefined ) {
							matches[ sel ] = handleObj.needsContext ?
								jQuery( sel, this ).index( cur ) > -1 :
								jQuery.find( sel, this, null, [ cur ] ).length;
						}
						if ( matches[ sel ] ) {
							matches.push( handleObj );
						}
					}
					if ( matches.length ) {
						handlerQueue.push( { elem: cur, handlers: matches } );
					}
				}
			}
		}

		// Add the remaining (directly-bound) handlers
		if ( delegateCount < handlers.length ) {
			handlerQueue.push( { elem: this, handlers: handlers.slice( delegateCount ) } );
		}

		return handlerQueue;
	},

	fix: function( event ) {
		if ( event[ jQuery.expando ] ) {
			return event;
		}

		// Create a writable copy of the event object and normalize some properties
		var i, prop, copy,
			type = event.type,
			originalEvent = event,
			fixHook = this.fixHooks[ type ];

		if ( !fixHook ) {
			this.fixHooks[ type ] = fixHook =
				rmouseEvent.test( type ) ? this.mouseHooks :
				rkeyEvent.test( type ) ? this.keyHooks :
				{};
		}
		copy = fixHook.props ? this.props.concat( fixHook.props ) : this.props;

		event = new jQuery.Event( originalEvent );

		i = copy.length;
		while ( i-- ) {
			prop = copy[ i ];
			event[ prop ] = originalEvent[ prop ];
		}

		// Support: IE<9
		// Fix target property (#1925)
		if ( !event.target ) {
			event.target = originalEvent.srcElement || document;
		}

		// Support: Safari 6-8+
		// Target should not be a text node (#504, #13143)
		if ( event.target.nodeType === 3 ) {
			event.target = event.target.parentNode;
		}

		// Support: IE<9
		// For mouse/key events, metaKey==false if it's undefined (#3368, #11328)
		event.metaKey = !!event.metaKey;

		return fixHook.filter ? fixHook.filter( event, originalEvent ) : event;
	},

	// Includes some event props shared by KeyEvent and MouseEvent
	props: ( "altKey bubbles cancelable ctrlKey currentTarget detail eventPhase " +
		"metaKey relatedTarget shiftKey target timeStamp view which" ).split( " " ),

	fixHooks: {},

	keyHooks: {
		props: "char charCode key keyCode".split( " " ),
		filter: function( event, original ) {

			// Add which for key events
			if ( event.which == null ) {
				event.which = original.charCode != null ? original.charCode : original.keyCode;
			}

			return event;
		}
	},

	mouseHooks: {
		props: ( "button buttons clientX clientY fromElement offsetX offsetY " +
			"pageX pageY screenX screenY toElement" ).split( " " ),
		filter: function( event, original ) {
			var body, eventDoc, doc,
				button = original.button,
				fromElement = original.fromElement;

			// Calculate pageX/Y if missing and clientX/Y available
			if ( event.pageX == null && original.clientX != null ) {
				eventDoc = event.target.ownerDocument || document;
				doc = eventDoc.documentElement;
				body = eventDoc.body;

				event.pageX = original.clientX +
					( doc && doc.scrollLeft || body && body.scrollLeft || 0 ) -
					( doc && doc.clientLeft || body && body.clientLeft || 0 );
				event.pageY = original.clientY +
					( doc && doc.scrollTop  || body && body.scrollTop  || 0 ) -
					( doc && doc.clientTop  || body && body.clientTop  || 0 );
			}

			// Add relatedTarget, if necessary
			if ( !event.relatedTarget && fromElement ) {
				event.relatedTarget = fromElement === event.target ?
					original.toElement :
					fromElement;
			}

			// Add which for click: 1 === left; 2 === middle; 3 === right
			// Note: button is not normalized, so don't use it
			if ( !event.which && button !== undefined ) {
				event.which = ( button & 1 ? 1 : ( button & 2 ? 3 : ( button & 4 ? 2 : 0 ) ) );
			}

			return event;
		}
	},

	special: {
		load: {

			// Prevent triggered image.load events from bubbling to window.load
			noBubble: true
		},
		focus: {

			// Fire native event if possible so blur/focus sequence is correct
			trigger: function() {
				if ( this !== safeActiveElement() && this.focus ) {
					try {
						this.focus();
						return false;
					} catch ( e ) {

						// Support: IE<9
						// If we error on focus to hidden element (#1486, #12518),
						// let .trigger() run the handlers
					}
				}
			},
			delegateType: "focusin"
		},
		blur: {
			trigger: function() {
				if ( this === safeActiveElement() && this.blur ) {
					this.blur();
					return false;
				}
			},
			delegateType: "focusout"
		},
		click: {

			// For checkbox, fire native event so checked state will be right
			trigger: function() {
				if ( jQuery.nodeName( this, "input" ) && this.type === "checkbox" && this.click ) {
					this.click();
					return false;
				}
			},

			// For cross-browser consistency, don't fire native .click() on links
			_default: function( event ) {
				return jQuery.nodeName( event.target, "a" );
			}
		},

		beforeunload: {
			postDispatch: function( event ) {

				// Support: Firefox 20+
				// Firefox doesn't alert if the returnValue field is not set.
				if ( event.result !== undefined && event.originalEvent ) {
					event.originalEvent.returnValue = event.result;
				}
			}
		}
	},

	// Piggyback on a donor event to simulate a different one
	simulate: function( type, elem, event ) {
		var e = jQuery.extend(
			new jQuery.Event(),
			event,
			{
				type: type,
				isSimulated: true

				// Previously, `originalEvent: {}` was set here, so stopPropagation call
				// would not be triggered on donor event, since in our own
				// jQuery.event.stopPropagation function we had a check for existence of
				// originalEvent.stopPropagation method, so, consequently it would be a noop.
				//
				// Guard for simulated events was moved to jQuery.event.stopPropagation function
				// since `originalEvent` should point to the original event for the
				// constancy with other events and for more focused logic
			}
		);

		jQuery.event.trigger( e, null, elem );

		if ( e.isDefaultPrevented() ) {
			event.preventDefault();
		}
	}
};

jQuery.removeEvent = document.removeEventListener ?
	function( elem, type, handle ) {

		// This "if" is needed for plain objects
		if ( elem.removeEventListener ) {
			elem.removeEventListener( type, handle );
		}
	} :
	function( elem, type, handle ) {
		var name = "on" + type;

		if ( elem.detachEvent ) {

			// #8545, #7054, preventing memory leaks for custom events in IE6-8
			// detachEvent needed property on element, by name of that event,
			// to properly expose it to GC
			if ( typeof elem[ name ] === "undefined" ) {
				elem[ name ] = null;
			}

			elem.detachEvent( name, handle );
		}
	};

jQuery.Event = function( src, props ) {

	// Allow instantiation without the 'new' keyword
	if ( !( this instanceof jQuery.Event ) ) {
		return new jQuery.Event( src, props );
	}

	// Event object
	if ( src && src.type ) {
		this.originalEvent = src;
		this.type = src.type;

		// Events bubbling up the document may have been marked as prevented
		// by a handler lower down the tree; reflect the correct value.
		this.isDefaultPrevented = src.defaultPrevented ||
				src.defaultPrevented === undefined &&

				// Support: IE < 9, Android < 4.0
				src.returnValue === false ?
			returnTrue :
			returnFalse;

	// Event type
	} else {
		this.type = src;
	}

	// Put explicitly provided properties onto the event object
	if ( props ) {
		jQuery.extend( this, props );
	}

	// Create a timestamp if incoming event doesn't have one
	this.timeStamp = src && src.timeStamp || jQuery.now();

	// Mark it as fixed
	this[ jQuery.expando ] = true;
};

// jQuery.Event is based on DOM3 Events as specified by the ECMAScript Language Binding
// http://www.w3.org/TR/2003/WD-DOM-Level-3-Events-20030331/ecma-script-binding.html
jQuery.Event.prototype = {
	constructor: jQuery.Event,
	isDefaultPrevented: returnFalse,
	isPropagationStopped: returnFalse,
	isImmediatePropagationStopped: returnFalse,

	preventDefault: function() {
		var e = this.originalEvent;

		this.isDefaultPrevented = returnTrue;
		if ( !e ) {
			return;
		}

		// If preventDefault exists, run it on the original event
		if ( e.preventDefault ) {
			e.preventDefault();

		// Support: IE
		// Otherwise set the returnValue property of the original event to false
		} else {
			e.returnValue = false;
		}
	},
	stopPropagation: function() {
		var e = this.originalEvent;

		this.isPropagationStopped = returnTrue;

		if ( !e || this.isSimulated ) {
			return;
		}

		// If stopPropagation exists, run it on the original event
		if ( e.stopPropagation ) {
			e.stopPropagation();
		}

		// Support: IE
		// Set the cancelBubble property of the original event to true
		e.cancelBubble = true;
	},
	stopImmediatePropagation: function() {
		var e = this.originalEvent;

		this.isImmediatePropagationStopped = returnTrue;

		if ( e && e.stopImmediatePropagation ) {
			e.stopImmediatePropagation();
		}

		this.stopPropagation();
	}
};

// Create mouseenter/leave events using mouseover/out and event-time checks
// so that event delegation works in jQuery.
// Do the same for pointerenter/pointerleave and pointerover/pointerout
//
// Support: Safari 7 only
// Safari sends mouseenter too often; see:
// https://code.google.com/p/chromium/issues/detail?id=470258
// for the description of the bug (it existed in older Chrome versions as well).
jQuery.each( {
	mouseenter: "mouseover",
	mouseleave: "mouseout",
	pointerenter: "pointerover",
	pointerleave: "pointerout"
}, function( orig, fix ) {
	jQuery.event.special[ orig ] = {
		delegateType: fix,
		bindType: fix,

		handle: function( event ) {
			var ret,
				target = this,
				related = event.relatedTarget,
				handleObj = event.handleObj;

			// For mouseenter/leave call the handler if related is outside the target.
			// NB: No relatedTarget if the mouse left/entered the browser window
			if ( !related || ( related !== target && !jQuery.contains( target, related ) ) ) {
				event.type = handleObj.origType;
				ret = handleObj.handler.apply( this, arguments );
				event.type = fix;
			}
			return ret;
		}
	};
} );

// IE submit delegation
if ( !support.submit ) {

	jQuery.event.special.submit = {
		setup: function() {

			// Only need this for delegated form submit events
			if ( jQuery.nodeName( this, "form" ) ) {
				return false;
			}

			// Lazy-add a submit handler when a descendant form may potentially be submitted
			jQuery.event.add( this, "click._submit keypress._submit", function( e ) {

				// Node name check avoids a VML-related crash in IE (#9807)
				var elem = e.target,
					form = jQuery.nodeName( elem, "input" ) || jQuery.nodeName( elem, "button" ) ?

						// Support: IE <=8
						// We use jQuery.prop instead of elem.form
						// to allow fixing the IE8 delegated submit issue (gh-2332)
						// by 3rd party polyfills/workarounds.
						jQuery.prop( elem, "form" ) :
						undefined;

				if ( form && !jQuery._data( form, "submit" ) ) {
					jQuery.event.add( form, "submit._submit", function( event ) {
						event._submitBubble = true;
					} );
					jQuery._data( form, "submit", true );
				}
			} );

			// return undefined since we don't need an event listener
		},

		postDispatch: function( event ) {

			// If form was submitted by the user, bubble the event up the tree
			if ( event._submitBubble ) {
				delete event._submitBubble;
				if ( this.parentNode && !event.isTrigger ) {
					jQuery.event.simulate( "submit", this.parentNode, event );
				}
			}
		},

		teardown: function() {

			// Only need this for delegated form submit events
			if ( jQuery.nodeName( this, "form" ) ) {
				return false;
			}

			// Remove delegated handlers; cleanData eventually reaps submit handlers attached above
			jQuery.event.remove( this, "._submit" );
		}
	};
}

// IE change delegation and checkbox/radio fix
if ( !support.change ) {

	jQuery.event.special.change = {

		setup: function() {

			if ( rformElems.test( this.nodeName ) ) {

				// IE doesn't fire change on a check/radio until blur; trigger it on click
				// after a propertychange. Eat the blur-change in special.change.handle.
				// This still fires onchange a second time for check/radio after blur.
				if ( this.type === "checkbox" || this.type === "radio" ) {
					jQuery.event.add( this, "propertychange._change", function( event ) {
						if ( event.originalEvent.propertyName === "checked" ) {
							this._justChanged = true;
						}
					} );
					jQuery.event.add( this, "click._change", function( event ) {
						if ( this._justChanged && !event.isTrigger ) {
							this._justChanged = false;
						}

						// Allow triggered, simulated change events (#11500)
						jQuery.event.simulate( "change", this, event );
					} );
				}
				return false;
			}

			// Delegated event; lazy-add a change handler on descendant inputs
			jQuery.event.add( this, "beforeactivate._change", function( e ) {
				var elem = e.target;

				if ( rformElems.test( elem.nodeName ) && !jQuery._data( elem, "change" ) ) {
					jQuery.event.add( elem, "change._change", function( event ) {
						if ( this.parentNode && !event.isSimulated && !event.isTrigger ) {
							jQuery.event.simulate( "change", this.parentNode, event );
						}
					} );
					jQuery._data( elem, "change", true );
				}
			} );
		},

		handle: function( event ) {
			var elem = event.target;

			// Swallow native change events from checkbox/radio, we already triggered them above
			if ( this !== elem || event.isSimulated || event.isTrigger ||
				( elem.type !== "radio" && elem.type !== "checkbox" ) ) {

				return event.handleObj.handler.apply( this, arguments );
			}
		},

		teardown: function() {
			jQuery.event.remove( this, "._change" );

			return !rformElems.test( this.nodeName );
		}
	};
}

// Support: Firefox
// Firefox doesn't have focus(in | out) events
// Related ticket - https://bugzilla.mozilla.org/show_bug.cgi?id=687787
//
// Support: Chrome, Safari
// focus(in | out) events fire after focus & blur events,
// which is spec violation - http://www.w3.org/TR/DOM-Level-3-Events/#events-focusevent-event-order
// Related ticket - https://code.google.com/p/chromium/issues/detail?id=449857
if ( !support.focusin ) {
	jQuery.each( { focus: "focusin", blur: "focusout" }, function( orig, fix ) {

		// Attach a single capturing handler on the document while someone wants focusin/focusout
		var handler = function( event ) {
			jQuery.event.simulate( fix, event.target, jQuery.event.fix( event ) );
		};

		jQuery.event.special[ fix ] = {
			setup: function() {
				var doc = this.ownerDocument || this,
					attaches = jQuery._data( doc, fix );

				if ( !attaches ) {
					doc.addEventListener( orig, handler, true );
				}
				jQuery._data( doc, fix, ( attaches || 0 ) + 1 );
			},
			teardown: function() {
				var doc = this.ownerDocument || this,
					attaches = jQuery._data( doc, fix ) - 1;

				if ( !attaches ) {
					doc.removeEventListener( orig, handler, true );
					jQuery._removeData( doc, fix );
				} else {
					jQuery._data( doc, fix, attaches );
				}
			}
		};
	} );
}

jQuery.fn.extend( {

	on: function( types, selector, data, fn ) {
		return on( this, types, selector, data, fn );
	},
	one: function( types, selector, data, fn ) {
		return on( this, types, selector, data, fn, 1 );
	},
	off: function( types, selector, fn ) {
		var handleObj, type;
		if ( types && types.preventDefault && types.handleObj ) {

			// ( event )  dispatched jQuery.Event
			handleObj = types.handleObj;
			jQuery( types.delegateTarget ).off(
				handleObj.namespace ?
					handleObj.origType + "." + handleObj.namespace :
					handleObj.origType,
				handleObj.selector,
				handleObj.handler
			);
			return this;
		}
		if ( typeof types === "object" ) {

			// ( types-object [, selector] )
			for ( type in types ) {
				this.off( type, selector, types[ type ] );
			}
			return this;
		}
		if ( selector === false || typeof selector === "function" ) {

			// ( types [, fn] )
			fn = selector;
			selector = undefined;
		}
		if ( fn === false ) {
			fn = returnFalse;
		}
		return this.each( function() {
			jQuery.event.remove( this, types, fn, selector );
		} );
	},

	trigger: function( type, data ) {
		return this.each( function() {
			jQuery.event.trigger( type, data, this );
		} );
	},
	triggerHandler: function( type, data ) {
		var elem = this[ 0 ];
		if ( elem ) {
			return jQuery.event.trigger( type, data, elem, true );
		}
	}
} );


var rinlinejQuery = / jQuery\d+="(?:null|\d+)"/g,
	rnoshimcache = new RegExp( "<(?:" + nodeNames + ")[\\s/>]", "i" ),
	rxhtmlTag = /<(?!area|br|col|embed|hr|img|input|link|meta|param)(([\w:-]+)[^>]*)\/>/gi,

	// Support: IE 10-11, Edge 10240+
	// In IE/Edge using regex groups here causes severe slowdowns.
	// See https://connect.microsoft.com/IE/feedback/details/1736512/
	rnoInnerhtml = /<script|<style|<link/i,

	// checked="checked" or checked
	rchecked = /checked\s*(?:[^=]|=\s*.checked.)/i,
	rscriptTypeMasked = /^true\/(.*)/,
	rcleanScript = /^\s*<!(?:\[CDATA\[|--)|(?:\]\]|--)>\s*$/g,
	safeFragment = createSafeFragment( document ),
	fragmentDiv = safeFragment.appendChild( document.createElement( "div" ) );

// Support: IE<8
// Manipulating tables requires a tbody
function manipulationTarget( elem, content ) {
	return jQuery.nodeName( elem, "table" ) &&
		jQuery.nodeName( content.nodeType !== 11 ? content : content.firstChild, "tr" ) ?

		elem.getElementsByTagName( "tbody" )[ 0 ] ||
			elem.appendChild( elem.ownerDocument.createElement( "tbody" ) ) :
		elem;
}

// Replace/restore the type attribute of script elements for safe DOM manipulation
function disableScript( elem ) {
	elem.type = ( jQuery.find.attr( elem, "type" ) !== null ) + "/" + elem.type;
	return elem;
}
function restoreScript( elem ) {
	var match = rscriptTypeMasked.exec( elem.type );
	if ( match ) {
		elem.type = match[ 1 ];
	} else {
		elem.removeAttribute( "type" );
	}
	return elem;
}

function cloneCopyEvent( src, dest ) {
	if ( dest.nodeType !== 1 || !jQuery.hasData( src ) ) {
		return;
	}

	var type, i, l,
		oldData = jQuery._data( src ),
		curData = jQuery._data( dest, oldData ),
		events = oldData.events;

	if ( events ) {
		delete curData.handle;
		curData.events = {};

		for ( type in events ) {
			for ( i = 0, l = events[ type ].length; i < l; i++ ) {
				jQuery.event.add( dest, type, events[ type ][ i ] );
			}
		}
	}

	// make the cloned public data object a copy from the original
	if ( curData.data ) {
		curData.data = jQuery.extend( {}, curData.data );
	}
}

function fixCloneNodeIssues( src, dest ) {
	var nodeName, e, data;

	// We do not need to do anything for non-Elements
	if ( dest.nodeType !== 1 ) {
		return;
	}

	nodeName = dest.nodeName.toLowerCase();

	// IE6-8 copies events bound via attachEvent when using cloneNode.
	if ( !support.noCloneEvent && dest[ jQuery.expando ] ) {
		data = jQuery._data( dest );

		for ( e in data.events ) {
			jQuery.removeEvent( dest, e, data.handle );
		}

		// Event data gets referenced instead of copied if the expando gets copied too
		dest.removeAttribute( jQuery.expando );
	}

	// IE blanks contents when cloning scripts, and tries to evaluate newly-set text
	if ( nodeName === "script" && dest.text !== src.text ) {
		disableScript( dest ).text = src.text;
		restoreScript( dest );

	// IE6-10 improperly clones children of object elements using classid.
	// IE10 throws NoModificationAllowedError if parent is null, #12132.
	} else if ( nodeName === "object" ) {
		if ( dest.parentNode ) {
			dest.outerHTML = src.outerHTML;
		}

		// This path appears unavoidable for IE9. When cloning an object
		// element in IE9, the outerHTML strategy above is not sufficient.
		// If the src has innerHTML and the destination does not,
		// copy the src.innerHTML into the dest.innerHTML. #10324
		if ( support.html5Clone && ( src.innerHTML && !jQuery.trim( dest.innerHTML ) ) ) {
			dest.innerHTML = src.innerHTML;
		}

	} else if ( nodeName === "input" && rcheckableType.test( src.type ) ) {

		// IE6-8 fails to persist the checked state of a cloned checkbox
		// or radio button. Worse, IE6-7 fail to give the cloned element
		// a checked appearance if the defaultChecked value isn't also set

		dest.defaultChecked = dest.checked = src.checked;

		// IE6-7 get confused and end up setting the value of a cloned
		// checkbox/radio button to an empty string instead of "on"
		if ( dest.value !== src.value ) {
			dest.value = src.value;
		}

	// IE6-8 fails to return the selected option to the default selected
	// state when cloning options
	} else if ( nodeName === "option" ) {
		dest.defaultSelected = dest.selected = src.defaultSelected;

	// IE6-8 fails to set the defaultValue to the correct value when
	// cloning other types of input fields
	} else if ( nodeName === "input" || nodeName === "textarea" ) {
		dest.defaultValue = src.defaultValue;
	}
}

function domManip( collection, args, callback, ignored ) {

	// Flatten any nested arrays
	args = concat.apply( [], args );

	var first, node, hasScripts,
		scripts, doc, fragment,
		i = 0,
		l = collection.length,
		iNoClone = l - 1,
		value = args[ 0 ],
		isFunction = jQuery.isFunction( value );

	// We can't cloneNode fragments that contain checked, in WebKit
	if ( isFunction ||
			( l > 1 && typeof value === "string" &&
				!support.checkClone && rchecked.test( value ) ) ) {
		return collection.each( function( index ) {
			var self = collection.eq( index );
			if ( isFunction ) {
				args[ 0 ] = value.call( this, index, self.html() );
			}
			domManip( self, args, callback, ignored );
		} );
	}

	if ( l ) {
		fragment = buildFragment( args, collection[ 0 ].ownerDocument, false, collection, ignored );
		first = fragment.firstChild;

		if ( fragment.childNodes.length === 1 ) {
			fragment = first;
		}

		// Require either new content or an interest in ignored elements to invoke the callback
		if ( first || ignored ) {
			scripts = jQuery.map( getAll( fragment, "script" ), disableScript );
			hasScripts = scripts.length;

			// Use the original fragment for the last item
			// instead of the first because it can end up
			// being emptied incorrectly in certain situations (#8070).
			for ( ; i < l; i++ ) {
				node = fragment;

				if ( i !== iNoClone ) {
					node = jQuery.clone( node, true, true );

					// Keep references to cloned scripts for later restoration
					if ( hasScripts ) {

						// Support: Android<4.1, PhantomJS<2
						// push.apply(_, arraylike) throws on ancient WebKit
						jQuery.merge( scripts, getAll( node, "script" ) );
					}
				}

				callback.call( collection[ i ], node, i );
			}

			if ( hasScripts ) {
				doc = scripts[ scripts.length - 1 ].ownerDocument;

				// Reenable scripts
				jQuery.map( scripts, restoreScript );

				// Evaluate executable scripts on first document insertion
				for ( i = 0; i < hasScripts; i++ ) {
					node = scripts[ i ];
					if ( rscriptType.test( node.type || "" ) &&
						!jQuery._data( node, "globalEval" ) &&
						jQuery.contains( doc, node ) ) {

						if ( node.src ) {

							// Optional AJAX dependency, but won't run scripts if not present
							if ( jQuery._evalUrl ) {
								jQuery._evalUrl( node.src );
							}
						} else {
							jQuery.globalEval(
								( node.text || node.textContent || node.innerHTML || "" )
									.replace( rcleanScript, "" )
							);
						}
					}
				}
			}

			// Fix #11809: Avoid leaking memory
			fragment = first = null;
		}
	}

	return collection;
}

function remove( elem, selector, keepData ) {
	var node,
		elems = selector ? jQuery.filter( selector, elem ) : elem,
		i = 0;

	for ( ; ( node = elems[ i ] ) != null; i++ ) {

		if ( !keepData && node.nodeType === 1 ) {
			jQuery.cleanData( getAll( node ) );
		}

		if ( node.parentNode ) {
			if ( keepData && jQuery.contains( node.ownerDocument, node ) ) {
				setGlobalEval( getAll( node, "script" ) );
			}
			node.parentNode.removeChild( node );
		}
	}

	return elem;
}

jQuery.extend( {
	htmlPrefilter: function( html ) {
		return html.replace( rxhtmlTag, "<$1></$2>" );
	},

	clone: function( elem, dataAndEvents, deepDataAndEvents ) {
		var destElements, node, clone, i, srcElements,
			inPage = jQuery.contains( elem.ownerDocument, elem );

		if ( support.html5Clone || jQuery.isXMLDoc( elem ) ||
			!rnoshimcache.test( "<" + elem.nodeName + ">" ) ) {

			clone = elem.cloneNode( true );

		// IE<=8 does not properly clone detached, unknown element nodes
		} else {
			fragmentDiv.innerHTML = elem.outerHTML;
			fragmentDiv.removeChild( clone = fragmentDiv.firstChild );
		}

		if ( ( !support.noCloneEvent || !support.noCloneChecked ) &&
				( elem.nodeType === 1 || elem.nodeType === 11 ) && !jQuery.isXMLDoc( elem ) ) {

			// We eschew Sizzle here for performance reasons: http://jsperf.com/getall-vs-sizzle/2
			destElements = getAll( clone );
			srcElements = getAll( elem );

			// Fix all IE cloning issues
			for ( i = 0; ( node = srcElements[ i ] ) != null; ++i ) {

				// Ensure that the destination node is not null; Fixes #9587
				if ( destElements[ i ] ) {
					fixCloneNodeIssues( node, destElements[ i ] );
				}
			}
		}

		// Copy the events from the original to the clone
		if ( dataAndEvents ) {
			if ( deepDataAndEvents ) {
				srcElements = srcElements || getAll( elem );
				destElements = destElements || getAll( clone );

				for ( i = 0; ( node = srcElements[ i ] ) != null; i++ ) {
					cloneCopyEvent( node, destElements[ i ] );
				}
			} else {
				cloneCopyEvent( elem, clone );
			}
		}

		// Preserve script evaluation history
		destElements = getAll( clone, "script" );
		if ( destElements.length > 0 ) {
			setGlobalEval( destElements, !inPage && getAll( elem, "script" ) );
		}

		destElements = srcElements = node = null;

		// Return the cloned set
		return clone;
	},

	cleanData: function( elems, /* internal */ forceAcceptData ) {
		var elem, type, id, data,
			i = 0,
			internalKey = jQuery.expando,
			cache = jQuery.cache,
			attributes = support.attributes,
			special = jQuery.event.special;

		for ( ; ( elem = elems[ i ] ) != null; i++ ) {
			if ( forceAcceptData || acceptData( elem ) ) {

				id = elem[ internalKey ];
				data = id && cache[ id ];

				if ( data ) {
					if ( data.events ) {
						for ( type in data.events ) {
							if ( special[ type ] ) {
								jQuery.event.remove( elem, type );

							// This is a shortcut to avoid jQuery.event.remove's overhead
							} else {
								jQuery.removeEvent( elem, type, data.handle );
							}
						}
					}

					// Remove cache only if it was not already removed by jQuery.event.remove
					if ( cache[ id ] ) {

						delete cache[ id ];

						// Support: IE<9
						// IE does not allow us to delete expando properties from nodes
						// IE creates expando attributes along with the property
						// IE does not have a removeAttribute function on Document nodes
						if ( !attributes && typeof elem.removeAttribute !== "undefined" ) {
							elem.removeAttribute( internalKey );

						// Webkit & Blink performance suffers when deleting properties
						// from DOM nodes, so set to undefined instead
						// https://code.google.com/p/chromium/issues/detail?id=378607
						} else {
							elem[ internalKey ] = undefined;
						}

						deletedIds.push( id );
					}
				}
			}
		}
	}
} );

jQuery.fn.extend( {

	// Keep domManip exposed until 3.0 (gh-2225)
	domManip: domManip,

	detach: function( selector ) {
		return remove( this, selector, true );
	},

	remove: function( selector ) {
		return remove( this, selector );
	},

	text: function( value ) {
		return access( this, function( value ) {
			return value === undefined ?
				jQuery.text( this ) :
				this.empty().append(
					( this[ 0 ] && this[ 0 ].ownerDocument || document ).createTextNode( value )
				);
		}, null, value, arguments.length );
	},

	append: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9 ) {
				var target = manipulationTarget( this, elem );
				target.appendChild( elem );
			}
		} );
	},

	prepend: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9 ) {
				var target = manipulationTarget( this, elem );
				target.insertBefore( elem, target.firstChild );
			}
		} );
	},

	before: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.parentNode ) {
				this.parentNode.insertBefore( elem, this );
			}
		} );
	},

	after: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.parentNode ) {
				this.parentNode.insertBefore( elem, this.nextSibling );
			}
		} );
	},

	empty: function() {
		var elem,
			i = 0;

		for ( ; ( elem = this[ i ] ) != null; i++ ) {

			// Remove element nodes and prevent memory leaks
			if ( elem.nodeType === 1 ) {
				jQuery.cleanData( getAll( elem, false ) );
			}

			// Remove any remaining nodes
			while ( elem.firstChild ) {
				elem.removeChild( elem.firstChild );
			}

			// If this is a select, ensure that it displays empty (#12336)
			// Support: IE<9
			if ( elem.options && jQuery.nodeName( elem, "select" ) ) {
				elem.options.length = 0;
			}
		}

		return this;
	},

	clone: function( dataAndEvents, deepDataAndEvents ) {
		dataAndEvents = dataAndEvents == null ? false : dataAndEvents;
		deepDataAndEvents = deepDataAndEvents == null ? dataAndEvents : deepDataAndEvents;

		return this.map( function() {
			return jQuery.clone( this, dataAndEvents, deepDataAndEvents );
		} );
	},

	html: function( value ) {
		return access( this, function( value ) {
			var elem = this[ 0 ] || {},
				i = 0,
				l = this.length;

			if ( value === undefined ) {
				return elem.nodeType === 1 ?
					elem.innerHTML.replace( rinlinejQuery, "" ) :
					undefined;
			}

			// See if we can take a shortcut and just use innerHTML
			if ( typeof value === "string" && !rnoInnerhtml.test( value ) &&
				( support.htmlSerialize || !rnoshimcache.test( value )  ) &&
				( support.leadingWhitespace || !rleadingWhitespace.test( value ) ) &&
				!wrapMap[ ( rtagName.exec( value ) || [ "", "" ] )[ 1 ].toLowerCase() ] ) {

				value = jQuery.htmlPrefilter( value );

				try {
					for ( ; i < l; i++ ) {

						// Remove element nodes and prevent memory leaks
						elem = this[ i ] || {};
						if ( elem.nodeType === 1 ) {
							jQuery.cleanData( getAll( elem, false ) );
							elem.innerHTML = value;
						}
					}

					elem = 0;

				// If using innerHTML throws an exception, use the fallback method
				} catch ( e ) {}
			}

			if ( elem ) {
				this.empty().append( value );
			}
		}, null, value, arguments.length );
	},

	replaceWith: function() {
		var ignored = [];

		// Make the changes, replacing each non-ignored context element with the new content
		return domManip( this, arguments, function( elem ) {
			var parent = this.parentNode;

			if ( jQuery.inArray( this, ignored ) < 0 ) {
				jQuery.cleanData( getAll( this ) );
				if ( parent ) {
					parent.replaceChild( elem, this );
				}
			}

		// Force callback invocation
		}, ignored );
	}
} );

jQuery.each( {
	appendTo: "append",
	prependTo: "prepend",
	insertBefore: "before",
	insertAfter: "after",
	replaceAll: "replaceWith"
}, function( name, original ) {
	jQuery.fn[ name ] = function( selector ) {
		var elems,
			i = 0,
			ret = [],
			insert = jQuery( selector ),
			last = insert.length - 1;

		for ( ; i <= last; i++ ) {
			elems = i === last ? this : this.clone( true );
			jQuery( insert[ i ] )[ original ]( elems );

			// Modern browsers can apply jQuery collections as arrays, but oldIE needs a .get()
			push.apply( ret, elems.get() );
		}

		return this.pushStack( ret );
	};
} );


var iframe,
	elemdisplay = {

		// Support: Firefox
		// We have to pre-define these values for FF (#10227)
		HTML: "block",
		BODY: "block"
	};

/**
 * Retrieve the actual display of a element
 * @param {String} name nodeName of the element
 * @param {Object} doc Document object
 */

// Called only from within defaultDisplay
function actualDisplay( name, doc ) {
	var elem = jQuery( doc.createElement( name ) ).appendTo( doc.body ),

		display = jQuery.css( elem[ 0 ], "display" );

	// We don't have any data stored on the element,
	// so use "detach" method as fast way to get rid of the element
	elem.detach();

	return display;
}

/**
 * Try to determine the default display value of an element
 * @param {String} nodeName
 */
function defaultDisplay( nodeName ) {
	var doc = document,
		display = elemdisplay[ nodeName ];

	if ( !display ) {
		display = actualDisplay( nodeName, doc );

		// If the simple way fails, read from inside an iframe
		if ( display === "none" || !display ) {

			// Use the already-created iframe if possible
			iframe = ( iframe || jQuery( "<iframe frameborder='0' width='0' height='0'/>" ) )
				.appendTo( doc.documentElement );

			// Always write a new HTML skeleton so Webkit and Firefox don't choke on reuse
			doc = ( iframe[ 0 ].contentWindow || iframe[ 0 ].contentDocument ).document;

			// Support: IE
			doc.write();
			doc.close();

			display = actualDisplay( nodeName, doc );
			iframe.detach();
		}

		// Store the correct default display
		elemdisplay[ nodeName ] = display;
	}

	return display;
}
var rmargin = ( /^margin/ );

var rnumnonpx = new RegExp( "^(" + pnum + ")(?!px)[a-z%]+$", "i" );

var swap = function( elem, options, callback, args ) {
	var ret, name,
		old = {};

	// Remember the old values, and insert the new ones
	for ( name in options ) {
		old[ name ] = elem.style[ name ];
		elem.style[ name ] = options[ name ];
	}

	ret = callback.apply( elem, args || [] );

	// Revert the old values
	for ( name in options ) {
		elem.style[ name ] = old[ name ];
	}

	return ret;
};


var documentElement = document.documentElement;



( function() {
	var pixelPositionVal, pixelMarginRightVal, boxSizingReliableVal,
		reliableHiddenOffsetsVal, reliableMarginRightVal, reliableMarginLeftVal,
		container = document.createElement( "div" ),
		div = document.createElement( "div" );

	// Finish early in limited (non-browser) environments
	if ( !div.style ) {
		return;
	}

	div.style.cssText = "float:left;opacity:.5";

	// Support: IE<9
	// Make sure that element opacity exists (as opposed to filter)
	support.opacity = div.style.opacity === "0.5";

	// Verify style float existence
	// (IE uses styleFloat instead of cssFloat)
	support.cssFloat = !!div.style.cssFloat;

	div.style.backgroundClip = "content-box";
	div.cloneNode( true ).style.backgroundClip = "";
	support.clearCloneStyle = div.style.backgroundClip === "content-box";

	container = document.createElement( "div" );
	container.style.cssText = "border:0;width:8px;height:0;top:0;left:-9999px;" +
		"padding:0;margin-top:1px;position:absolute";
	div.innerHTML = "";
	container.appendChild( div );

	// Support: Firefox<29, Android 2.3
	// Vendor-prefix box-sizing
	support.boxSizing = div.style.boxSizing === "" || div.style.MozBoxSizing === "" ||
		div.style.WebkitBoxSizing === "";

	jQuery.extend( support, {
		reliableHiddenOffsets: function() {
			if ( pixelPositionVal == null ) {
				computeStyleTests();
			}
			return reliableHiddenOffsetsVal;
		},

		boxSizingReliable: function() {

			// We're checking for pixelPositionVal here instead of boxSizingReliableVal
			// since that compresses better and they're computed together anyway.
			if ( pixelPositionVal == null ) {
				computeStyleTests();
			}
			return boxSizingReliableVal;
		},

		pixelMarginRight: function() {

			// Support: Android 4.0-4.3
			if ( pixelPositionVal == null ) {
				computeStyleTests();
			}
			return pixelMarginRightVal;
		},

		pixelPosition: function() {
			if ( pixelPositionVal == null ) {
				computeStyleTests();
			}
			return pixelPositionVal;
		},

		reliableMarginRight: function() {

			// Support: Android 2.3
			if ( pixelPositionVal == null ) {
				computeStyleTests();
			}
			return reliableMarginRightVal;
		},

		reliableMarginLeft: function() {

			// Support: IE <=8 only, Android 4.0 - 4.3 only, Firefox <=3 - 37
			if ( pixelPositionVal == null ) {
				computeStyleTests();
			}
			return reliableMarginLeftVal;
		}
	} );

	function computeStyleTests() {
		var contents, divStyle,
			documentElement = document.documentElement;

		// Setup
		documentElement.appendChild( container );

		div.style.cssText =

			// Support: Android 2.3
			// Vendor-prefix box-sizing
			"-webkit-box-sizing:border-box;box-sizing:border-box;" +
			"position:relative;display:block;" +
			"margin:auto;border:1px;padding:1px;" +
			"top:1%;width:50%";

		// Support: IE<9
		// Assume reasonable values in the absence of getComputedStyle
		pixelPositionVal = boxSizingReliableVal = reliableMarginLeftVal = false;
		pixelMarginRightVal = reliableMarginRightVal = true;

		// Check for getComputedStyle so that this code is not run in IE<9.
		if ( window.getComputedStyle ) {
			divStyle = window.getComputedStyle( div );
			pixelPositionVal = ( divStyle || {} ).top !== "1%";
			reliableMarginLeftVal = ( divStyle || {} ).marginLeft === "2px";
			boxSizingReliableVal = ( divStyle || { width: "4px" } ).width === "4px";

			// Support: Android 4.0 - 4.3 only
			// Some styles come back with percentage values, even though they shouldn't
			div.style.marginRight = "50%";
			pixelMarginRightVal = ( divStyle || { marginRight: "4px" } ).marginRight === "4px";

			// Support: Android 2.3 only
			// Div with explicit width and no margin-right incorrectly
			// gets computed margin-right based on width of container (#3333)
			// WebKit Bug 13343 - getComputedStyle returns wrong value for margin-right
			contents = div.appendChild( document.createElement( "div" ) );

			// Reset CSS: box-sizing; display; margin; border; padding
			contents.style.cssText = div.style.cssText =

				// Support: Android 2.3
				// Vendor-prefix box-sizing
				"-webkit-box-sizing:content-box;-moz-box-sizing:content-box;" +
				"box-sizing:content-box;display:block;margin:0;border:0;padding:0";
			contents.style.marginRight = contents.style.width = "0";
			div.style.width = "1px";

			reliableMarginRightVal =
				!parseFloat( ( window.getComputedStyle( contents ) || {} ).marginRight );

			div.removeChild( contents );
		}

		// Support: IE6-8
		// First check that getClientRects works as expected
		// Check if table cells still have offsetWidth/Height when they are set
		// to display:none and there are still other visible table cells in a
		// table row; if so, offsetWidth/Height are not reliable for use when
		// determining if an element has been hidden directly using
		// display:none (it is still safe to use offsets if a parent element is
		// hidden; don safety goggles and see bug #4512 for more information).
		div.style.display = "none";
		reliableHiddenOffsetsVal = div.getClientRects().length === 0;
		if ( reliableHiddenOffsetsVal ) {
			div.style.display = "";
			div.innerHTML = "<table><tr><td></td><td>t</td></tr></table>";
			div.childNodes[ 0 ].style.borderCollapse = "separate";
			contents = div.getElementsByTagName( "td" );
			contents[ 0 ].style.cssText = "margin:0;border:0;padding:0;display:none";
			reliableHiddenOffsetsVal = contents[ 0 ].offsetHeight === 0;
			if ( reliableHiddenOffsetsVal ) {
				contents[ 0 ].style.display = "";
				contents[ 1 ].style.display = "none";
				reliableHiddenOffsetsVal = contents[ 0 ].offsetHeight === 0;
			}
		}

		// Teardown
		documentElement.removeChild( container );
	}

} )();


var getStyles, curCSS,
	rposition = /^(top|right|bottom|left)$/;

if ( window.getComputedStyle ) {
	getStyles = function( elem ) {

		// Support: IE<=11+, Firefox<=30+ (#15098, #14150)
		// IE throws on elements created in popups
		// FF meanwhile throws on frame elements through "defaultView.getComputedStyle"
		var view = elem.ownerDocument.defaultView;

		if ( !view || !view.opener ) {
			view = window;
		}

		return view.getComputedStyle( elem );
	};

	curCSS = function( elem, name, computed ) {
		var width, minWidth, maxWidth, ret,
			style = elem.style;

		computed = computed || getStyles( elem );

		// getPropertyValue is only needed for .css('filter') in IE9, see #12537
		ret = computed ? computed.getPropertyValue( name ) || computed[ name ] : undefined;

		// Support: Opera 12.1x only
		// Fall back to style even without computed
		// computed is undefined for elems on document fragments
		if ( ( ret === "" || ret === undefined ) && !jQuery.contains( elem.ownerDocument, elem ) ) {
			ret = jQuery.style( elem, name );
		}

		if ( computed ) {

			// A tribute to the "awesome hack by Dean Edwards"
			// Chrome < 17 and Safari 5.0 uses "computed value"
			// instead of "used value" for margin-right
			// Safari 5.1.7 (at least) returns percentage for a larger set of values,
			// but width seems to be reliably pixels
			// this is against the CSSOM draft spec:
			// http://dev.w3.org/csswg/cssom/#resolved-values
			if ( !support.pixelMarginRight() && rnumnonpx.test( ret ) && rmargin.test( name ) ) {

				// Remember the original values
				width = style.width;
				minWidth = style.minWidth;
				maxWidth = style.maxWidth;

				// Put in the new values to get a computed value out
				style.minWidth = style.maxWidth = style.width = ret;
				ret = computed.width;

				// Revert the changed values
				style.width = width;
				style.minWidth = minWidth;
				style.maxWidth = maxWidth;
			}
		}

		// Support: IE
		// IE returns zIndex value as an integer.
		return ret === undefined ?
			ret :
			ret + "";
	};
} else if ( documentElement.currentStyle ) {
	getStyles = function( elem ) {
		return elem.currentStyle;
	};

	curCSS = function( elem, name, computed ) {
		var left, rs, rsLeft, ret,
			style = elem.style;

		computed = computed || getStyles( elem );
		ret = computed ? computed[ name ] : undefined;

		// Avoid setting ret to empty string here
		// so we don't default to auto
		if ( ret == null && style && style[ name ] ) {
			ret = style[ name ];
		}

		// From the awesome hack by Dean Edwards
		// http://erik.eae.net/archives/2007/07/27/18.54.15/#comment-102291

		// If we're not dealing with a regular pixel number
		// but a number that has a weird ending, we need to convert it to pixels
		// but not position css attributes, as those are
		// proportional to the parent element instead
		// and we can't measure the parent instead because it
		// might trigger a "stacking dolls" problem
		if ( rnumnonpx.test( ret ) && !rposition.test( name ) ) {

			// Remember the original values
			left = style.left;
			rs = elem.runtimeStyle;
			rsLeft = rs && rs.left;

			// Put in the new values to get a computed value out
			if ( rsLeft ) {
				rs.left = elem.currentStyle.left;
			}
			style.left = name === "fontSize" ? "1em" : ret;
			ret = style.pixelLeft + "px";

			// Revert the changed values
			style.left = left;
			if ( rsLeft ) {
				rs.left = rsLeft;
			}
		}

		// Support: IE
		// IE returns zIndex value as an integer.
		return ret === undefined ?
			ret :
			ret + "" || "auto";
	};
}




function addGetHookIf( conditionFn, hookFn ) {

	// Define the hook, we'll check on the first run if it's really needed.
	return {
		get: function() {
			if ( conditionFn() ) {

				// Hook not needed (or it's not possible to use it due
				// to missing dependency), remove it.
				delete this.get;
				return;
			}

			// Hook needed; redefine it so that the support test is not executed again.
			return ( this.get = hookFn ).apply( this, arguments );
		}
	};
}


var

		ralpha = /alpha\([^)]*\)/i,
	ropacity = /opacity\s*=\s*([^)]*)/i,

	// swappable if display is none or starts with table except
	// "table", "table-cell", or "table-caption"
	// see here for display values:
	// https://developer.mozilla.org/en-US/docs/CSS/display
	rdisplayswap = /^(none|table(?!-c[ea]).+)/,
	rnumsplit = new RegExp( "^(" + pnum + ")(.*)$", "i" ),

	cssShow = { position: "absolute", visibility: "hidden", display: "block" },
	cssNormalTransform = {
		letterSpacing: "0",
		fontWeight: "400"
	},

	cssPrefixes = [ "Webkit", "O", "Moz", "ms" ],
	emptyStyle = document.createElement( "div" ).style;


// return a css property mapped to a potentially vendor prefixed property
function vendorPropName( name ) {

	// shortcut for names that are not vendor prefixed
	if ( name in emptyStyle ) {
		return name;
	}

	// check for vendor prefixed names
	var capName = name.charAt( 0 ).toUpperCase() + name.slice( 1 ),
		i = cssPrefixes.length;

	while ( i-- ) {
		name = cssPrefixes[ i ] + capName;
		if ( name in emptyStyle ) {
			return name;
		}
	}
}

function showHide( elements, show ) {
	var display, elem, hidden,
		values = [],
		index = 0,
		length = elements.length;

	for ( ; index < length; index++ ) {
		elem = elements[ index ];
		if ( !elem.style ) {
			continue;
		}

		values[ index ] = jQuery._data( elem, "olddisplay" );
		display = elem.style.display;
		if ( show ) {

			// Reset the inline display of this element to learn if it is
			// being hidden by cascaded rules or not
			if ( !values[ index ] && display === "none" ) {
				elem.style.display = "";
			}

			// Set elements which have been overridden with display: none
			// in a stylesheet to whatever the default browser style is
			// for such an element
			if ( elem.style.display === "" && isHidden( elem ) ) {
				values[ index ] =
					jQuery._data( elem, "olddisplay", defaultDisplay( elem.nodeName ) );
			}
		} else {
			hidden = isHidden( elem );

			if ( display && display !== "none" || !hidden ) {
				jQuery._data(
					elem,
					"olddisplay",
					hidden ? display : jQuery.css( elem, "display" )
				);
			}
		}
	}

	// Set the display of most of the elements in a second loop
	// to avoid the constant reflow
	for ( index = 0; index < length; index++ ) {
		elem = elements[ index ];
		if ( !elem.style ) {
			continue;
		}
		if ( !show || elem.style.display === "none" || elem.style.display === "" ) {
			elem.style.display = show ? values[ index ] || "" : "none";
		}
	}

	return elements;
}

function setPositiveNumber( elem, value, subtract ) {
	var matches = rnumsplit.exec( value );
	return matches ?

		// Guard against undefined "subtract", e.g., when used as in cssHooks
		Math.max( 0, matches[ 1 ] - ( subtract || 0 ) ) + ( matches[ 2 ] || "px" ) :
		value;
}

function augmentWidthOrHeight( elem, name, extra, isBorderBox, styles ) {
	var i = extra === ( isBorderBox ? "border" : "content" ) ?

		// If we already have the right measurement, avoid augmentation
		4 :

		// Otherwise initialize for horizontal or vertical properties
		name === "width" ? 1 : 0,

		val = 0;

	for ( ; i < 4; i += 2 ) {

		// both box models exclude margin, so add it if we want it
		if ( extra === "margin" ) {
			val += jQuery.css( elem, extra + cssExpand[ i ], true, styles );
		}

		if ( isBorderBox ) {

			// border-box includes padding, so remove it if we want content
			if ( extra === "content" ) {
				val -= jQuery.css( elem, "padding" + cssExpand[ i ], true, styles );
			}

			// at this point, extra isn't border nor margin, so remove border
			if ( extra !== "margin" ) {
				val -= jQuery.css( elem, "border" + cssExpand[ i ] + "Width", true, styles );
			}
		} else {

			// at this point, extra isn't content, so add padding
			val += jQuery.css( elem, "padding" + cssExpand[ i ], true, styles );

			// at this point, extra isn't content nor padding, so add border
			if ( extra !== "padding" ) {
				val += jQuery.css( elem, "border" + cssExpand[ i ] + "Width", true, styles );
			}
		}
	}

	return val;
}

function getWidthOrHeight( elem, name, extra ) {

	// Start with offset property, which is equivalent to the border-box value
	var valueIsBorderBox = true,
		val = name === "width" ? elem.offsetWidth : elem.offsetHeight,
		styles = getStyles( elem ),
		isBorderBox = support.boxSizing &&
			jQuery.css( elem, "boxSizing", false, styles ) === "border-box";

	// some non-html elements return undefined for offsetWidth, so check for null/undefined
	// svg - https://bugzilla.mozilla.org/show_bug.cgi?id=649285
	// MathML - https://bugzilla.mozilla.org/show_bug.cgi?id=491668
	if ( val <= 0 || val == null ) {

		// Fall back to computed then uncomputed css if necessary
		val = curCSS( elem, name, styles );
		if ( val < 0 || val == null ) {
			val = elem.style[ name ];
		}

		// Computed unit is not pixels. Stop here and return.
		if ( rnumnonpx.test( val ) ) {
			return val;
		}

		// we need the check for style in case a browser which returns unreliable values
		// for getComputedStyle silently falls back to the reliable elem.style
		valueIsBorderBox = isBorderBox &&
			( support.boxSizingReliable() || val === elem.style[ name ] );

		// Normalize "", auto, and prepare for extra
		val = parseFloat( val ) || 0;
	}

	// use the active box-sizing model to add/subtract irrelevant styles
	return ( val +
		augmentWidthOrHeight(
			elem,
			name,
			extra || ( isBorderBox ? "border" : "content" ),
			valueIsBorderBox,
			styles
		)
	) + "px";
}

jQuery.extend( {

	// Add in style property hooks for overriding the default
	// behavior of getting and setting a style property
	cssHooks: {
		opacity: {
			get: function( elem, computed ) {
				if ( computed ) {

					// We should always get a number back from opacity
					var ret = curCSS( elem, "opacity" );
					return ret === "" ? "1" : ret;
				}
			}
		}
	},

	// Don't automatically add "px" to these possibly-unitless properties
	cssNumber: {
		"animationIterationCount": true,
		"columnCount": true,
		"fillOpacity": true,
		"flexGrow": true,
		"flexShrink": true,
		"fontWeight": true,
		"lineHeight": true,
		"opacity": true,
		"order": true,
		"orphans": true,
		"widows": true,
		"zIndex": true,
		"zoom": true
	},

	// Add in properties whose names you wish to fix before
	// setting or getting the value
	cssProps: {

		// normalize float css property
		"float": support.cssFloat ? "cssFloat" : "styleFloat"
	},

	// Get and set the style property on a DOM Node
	style: function( elem, name, value, extra ) {

		// Don't set styles on text and comment nodes
		if ( !elem || elem.nodeType === 3 || elem.nodeType === 8 || !elem.style ) {
			return;
		}

		// Make sure that we're working with the right name
		var ret, type, hooks,
			origName = jQuery.camelCase( name ),
			style = elem.style;

		name = jQuery.cssProps[ origName ] ||
			( jQuery.cssProps[ origName ] = vendorPropName( origName ) || origName );

		// gets hook for the prefixed version
		// followed by the unprefixed version
		hooks = jQuery.cssHooks[ name ] || jQuery.cssHooks[ origName ];

		// Check if we're setting a value
		if ( value !== undefined ) {
			type = typeof value;

			// Convert "+=" or "-=" to relative numbers (#7345)
			if ( type === "string" && ( ret = rcssNum.exec( value ) ) && ret[ 1 ] ) {
				value = adjustCSS( elem, name, ret );

				// Fixes bug #9237
				type = "number";
			}

			// Make sure that null and NaN values aren't set. See: #7116
			if ( value == null || value !== value ) {
				return;
			}

			// If a number was passed in, add the unit (except for certain CSS properties)
			if ( type === "number" ) {
				value += ret && ret[ 3 ] || ( jQuery.cssNumber[ origName ] ? "" : "px" );
			}

			// Fixes #8908, it can be done more correctly by specifing setters in cssHooks,
			// but it would mean to define eight
			// (for every problematic property) identical functions
			if ( !support.clearCloneStyle && value === "" && name.indexOf( "background" ) === 0 ) {
				style[ name ] = "inherit";
			}

			// If a hook was provided, use that value, otherwise just set the specified value
			if ( !hooks || !( "set" in hooks ) ||
				( value = hooks.set( elem, value, extra ) ) !== undefined ) {

				// Support: IE
				// Swallow errors from 'invalid' CSS values (#5509)
				try {
					style[ name ] = value;
				} catch ( e ) {}
			}

		} else {

			// If a hook was provided get the non-computed value from there
			if ( hooks && "get" in hooks &&
				( ret = hooks.get( elem, false, extra ) ) !== undefined ) {

				return ret;
			}

			// Otherwise just get the value from the style object
			return style[ name ];
		}
	},

	css: function( elem, name, extra, styles ) {
		var num, val, hooks,
			origName = jQuery.camelCase( name );

		// Make sure that we're working with the right name
		name = jQuery.cssProps[ origName ] ||
			( jQuery.cssProps[ origName ] = vendorPropName( origName ) || origName );

		// gets hook for the prefixed version
		// followed by the unprefixed version
		hooks = jQuery.cssHooks[ name ] || jQuery.cssHooks[ origName ];

		// If a hook was provided get the computed value from there
		if ( hooks && "get" in hooks ) {
			val = hooks.get( elem, true, extra );
		}

		// Otherwise, if a way to get the computed value exists, use that
		if ( val === undefined ) {
			val = curCSS( elem, name, styles );
		}

		//convert "normal" to computed value
		if ( val === "normal" && name in cssNormalTransform ) {
			val = cssNormalTransform[ name ];
		}

		// Return, converting to number if forced or a qualifier was provided and val looks numeric
		if ( extra === "" || extra ) {
			num = parseFloat( val );
			return extra === true || isFinite( num ) ? num || 0 : val;
		}
		return val;
	}
} );

jQuery.each( [ "height", "width" ], function( i, name ) {
	jQuery.cssHooks[ name ] = {
		get: function( elem, computed, extra ) {
			if ( computed ) {

				// certain elements can have dimension info if we invisibly show them
				// however, it must have a current display style that would benefit from this
				return rdisplayswap.test( jQuery.css( elem, "display" ) ) &&
					elem.offsetWidth === 0 ?
						swap( elem, cssShow, function() {
							return getWidthOrHeight( elem, name, extra );
						} ) :
						getWidthOrHeight( elem, name, extra );
			}
		},

		set: function( elem, value, extra ) {
			var styles = extra && getStyles( elem );
			return setPositiveNumber( elem, value, extra ?
				augmentWidthOrHeight(
					elem,
					name,
					extra,
					support.boxSizing &&
						jQuery.css( elem, "boxSizing", false, styles ) === "border-box",
					styles
				) : 0
			);
		}
	};
} );

if ( !support.opacity ) {
	jQuery.cssHooks.opacity = {
		get: function( elem, computed ) {

			// IE uses filters for opacity
			return ropacity.test( ( computed && elem.currentStyle ?
				elem.currentStyle.filter :
				elem.style.filter ) || "" ) ?
					( 0.01 * parseFloat( RegExp.$1 ) ) + "" :
					computed ? "1" : "";
		},

		set: function( elem, value ) {
			var style = elem.style,
				currentStyle = elem.currentStyle,
				opacity = jQuery.isNumeric( value ) ? "alpha(opacity=" + value * 100 + ")" : "",
				filter = currentStyle && currentStyle.filter || style.filter || "";

			// IE has trouble with opacity if it does not have layout
			// Force it by setting the zoom level
			style.zoom = 1;

			// if setting opacity to 1, and no other filters exist -
			// attempt to remove filter attribute #6652
			// if value === "", then remove inline opacity #12685
			if ( ( value >= 1 || value === "" ) &&
					jQuery.trim( filter.replace( ralpha, "" ) ) === "" &&
					style.removeAttribute ) {

				// Setting style.filter to null, "" & " " still leave "filter:" in the cssText
				// if "filter:" is present at all, clearType is disabled, we want to avoid this
				// style.removeAttribute is IE Only, but so apparently is this code path...
				style.removeAttribute( "filter" );

				// if there is no filter style applied in a css rule
				// or unset inline opacity, we are done
				if ( value === "" || currentStyle && !currentStyle.filter ) {
					return;
				}
			}

			// otherwise, set new filter values
			style.filter = ralpha.test( filter ) ?
				filter.replace( ralpha, opacity ) :
				filter + " " + opacity;
		}
	};
}

jQuery.cssHooks.marginRight = addGetHookIf( support.reliableMarginRight,
	function( elem, computed ) {
		if ( computed ) {
			return swap( elem, { "display": "inline-block" },
				curCSS, [ elem, "marginRight" ] );
		}
	}
);

jQuery.cssHooks.marginLeft = addGetHookIf( support.reliableMarginLeft,
	function( elem, computed ) {
		if ( computed ) {
			return (
				parseFloat( curCSS( elem, "marginLeft" ) ) ||

				// Support: IE<=11+
				// Running getBoundingClientRect on a disconnected node in IE throws an error
				// Support: IE8 only
				// getClientRects() errors on disconnected elems
				( jQuery.contains( elem.ownerDocument, elem ) ?
					elem.getBoundingClientRect().left -
						swap( elem, { marginLeft: 0 }, function() {
							return elem.getBoundingClientRect().left;
						} ) :
					0
				)
			) + "px";
		}
	}
);

// These hooks are used by animate to expand properties
jQuery.each( {
	margin: "",
	padding: "",
	border: "Width"
}, function( prefix, suffix ) {
	jQuery.cssHooks[ prefix + suffix ] = {
		expand: function( value ) {
			var i = 0,
				expanded = {},

				// assumes a single number if not a string
				parts = typeof value === "string" ? value.split( " " ) : [ value ];

			for ( ; i < 4; i++ ) {
				expanded[ prefix + cssExpand[ i ] + suffix ] =
					parts[ i ] || parts[ i - 2 ] || parts[ 0 ];
			}

			return expanded;
		}
	};

	if ( !rmargin.test( prefix ) ) {
		jQuery.cssHooks[ prefix + suffix ].set = setPositiveNumber;
	}
} );

jQuery.fn.extend( {
	css: function( name, value ) {
		return access( this, function( elem, name, value ) {
			var styles, len,
				map = {},
				i = 0;

			if ( jQuery.isArray( name ) ) {
				styles = getStyles( elem );
				len = name.length;

				for ( ; i < len; i++ ) {
					map[ name[ i ] ] = jQuery.css( elem, name[ i ], false, styles );
				}

				return map;
			}

			return value !== undefined ?
				jQuery.style( elem, name, value ) :
				jQuery.css( elem, name );
		}, name, value, arguments.length > 1 );
	},
	show: function() {
		return showHide( this, true );
	},
	hide: function() {
		return showHide( this );
	},
	toggle: function( state ) {
		if ( typeof state === "boolean" ) {
			return state ? this.show() : this.hide();
		}

		return this.each( function() {
			if ( isHidden( this ) ) {
				jQuery( this ).show();
			} else {
				jQuery( this ).hide();
			}
		} );
	}
} );


function Tween( elem, options, prop, end, easing ) {
	return new Tween.prototype.init( elem, options, prop, end, easing );
}
jQuery.Tween = Tween;

Tween.prototype = {
	constructor: Tween,
	init: function( elem, options, prop, end, easing, unit ) {
		this.elem = elem;
		this.prop = prop;
		this.easing = easing || jQuery.easing._default;
		this.options = options;
		this.start = this.now = this.cur();
		this.end = end;
		this.unit = unit || ( jQuery.cssNumber[ prop ] ? "" : "px" );
	},
	cur: function() {
		var hooks = Tween.propHooks[ this.prop ];

		return hooks && hooks.get ?
			hooks.get( this ) :
			Tween.propHooks._default.get( this );
	},
	run: function( percent ) {
		var eased,
			hooks = Tween.propHooks[ this.prop ];

		if ( this.options.duration ) {
			this.pos = eased = jQuery.easing[ this.easing ](
				percent, this.options.duration * percent, 0, 1, this.options.duration
			);
		} else {
			this.pos = eased = percent;
		}
		this.now = ( this.end - this.start ) * eased + this.start;

		if ( this.options.step ) {
			this.options.step.call( this.elem, this.now, this );
		}

		if ( hooks && hooks.set ) {
			hooks.set( this );
		} else {
			Tween.propHooks._default.set( this );
		}
		return this;
	}
};

Tween.prototype.init.prototype = Tween.prototype;

Tween.propHooks = {
	_default: {
		get: function( tween ) {
			var result;

			// Use a property on the element directly when it is not a DOM element,
			// or when there is no matching style property that exists.
			if ( tween.elem.nodeType !== 1 ||
				tween.elem[ tween.prop ] != null && tween.elem.style[ tween.prop ] == null ) {
				return tween.elem[ tween.prop ];
			}

			// passing an empty string as a 3rd parameter to .css will automatically
			// attempt a parseFloat and fallback to a string if the parse fails
			// so, simple values such as "10px" are parsed to Float.
			// complex values such as "rotate(1rad)" are returned as is.
			result = jQuery.css( tween.elem, tween.prop, "" );

			// Empty strings, null, undefined and "auto" are converted to 0.
			return !result || result === "auto" ? 0 : result;
		},
		set: function( tween ) {

			// use step hook for back compat - use cssHook if its there - use .style if its
			// available and use plain properties where available
			if ( jQuery.fx.step[ tween.prop ] ) {
				jQuery.fx.step[ tween.prop ]( tween );
			} else if ( tween.elem.nodeType === 1 &&
				( tween.elem.style[ jQuery.cssProps[ tween.prop ] ] != null ||
					jQuery.cssHooks[ tween.prop ] ) ) {
				jQuery.style( tween.elem, tween.prop, tween.now + tween.unit );
			} else {
				tween.elem[ tween.prop ] = tween.now;
			}
		}
	}
};

// Support: IE <=9
// Panic based approach to setting things on disconnected nodes

Tween.propHooks.scrollTop = Tween.propHooks.scrollLeft = {
	set: function( tween ) {
		if ( tween.elem.nodeType && tween.elem.parentNode ) {
			tween.elem[ tween.prop ] = tween.now;
		}
	}
};

jQuery.easing = {
	linear: function( p ) {
		return p;
	},
	swing: function( p ) {
		return 0.5 - Math.cos( p * Math.PI ) / 2;
	},
	_default: "swing"
};

jQuery.fx = Tween.prototype.init;

// Back Compat <1.8 extension point
jQuery.fx.step = {};




var
	fxNow, timerId,
	rfxtypes = /^(?:toggle|show|hide)$/,
	rrun = /queueHooks$/;

// Animations created synchronously will run synchronously
function createFxNow() {
	window.setTimeout( function() {
		fxNow = undefined;
	} );
	return ( fxNow = jQuery.now() );
}

// Generate parameters to create a standard animation
function genFx( type, includeWidth ) {
	var which,
		attrs = { height: type },
		i = 0;

	// if we include width, step value is 1 to do all cssExpand values,
	// if we don't include width, step value is 2 to skip over Left and Right
	includeWidth = includeWidth ? 1 : 0;
	for ( ; i < 4 ; i += 2 - includeWidth ) {
		which = cssExpand[ i ];
		attrs[ "margin" + which ] = attrs[ "padding" + which ] = type;
	}

	if ( includeWidth ) {
		attrs.opacity = attrs.width = type;
	}

	return attrs;
}

function createTween( value, prop, animation ) {
	var tween,
		collection = ( Animation.tweeners[ prop ] || [] ).concat( Animation.tweeners[ "*" ] ),
		index = 0,
		length = collection.length;
	for ( ; index < length; index++ ) {
		if ( ( tween = collection[ index ].call( animation, prop, value ) ) ) {

			// we're done with this property
			return tween;
		}
	}
}

function defaultPrefilter( elem, props, opts ) {
	/* jshint validthis: true */
	var prop, value, toggle, tween, hooks, oldfire, display, checkDisplay,
		anim = this,
		orig = {},
		style = elem.style,
		hidden = elem.nodeType && isHidden( elem ),
		dataShow = jQuery._data( elem, "fxshow" );

	// handle queue: false promises
	if ( !opts.queue ) {
		hooks = jQuery._queueHooks( elem, "fx" );
		if ( hooks.unqueued == null ) {
			hooks.unqueued = 0;
			oldfire = hooks.empty.fire;
			hooks.empty.fire = function() {
				if ( !hooks.unqueued ) {
					oldfire();
				}
			};
		}
		hooks.unqueued++;

		anim.always( function() {

			// doing this makes sure that the complete handler will be called
			// before this completes
			anim.always( function() {
				hooks.unqueued--;
				if ( !jQuery.queue( elem, "fx" ).length ) {
					hooks.empty.fire();
				}
			} );
		} );
	}

	// height/width overflow pass
	if ( elem.nodeType === 1 && ( "height" in props || "width" in props ) ) {

		// Make sure that nothing sneaks out
		// Record all 3 overflow attributes because IE does not
		// change the overflow attribute when overflowX and
		// overflowY are set to the same value
		opts.overflow = [ style.overflow, style.overflowX, style.overflowY ];

		// Set display property to inline-block for height/width
		// animations on inline elements that are having width/height animated
		display = jQuery.css( elem, "display" );

		// Test default display if display is currently "none"
		checkDisplay = display === "none" ?
			jQuery._data( elem, "olddisplay" ) || defaultDisplay( elem.nodeName ) : display;

		if ( checkDisplay === "inline" && jQuery.css( elem, "float" ) === "none" ) {

			// inline-level elements accept inline-block;
			// block-level elements need to be inline with layout
			if ( !support.inlineBlockNeedsLayout || defaultDisplay( elem.nodeName ) === "inline" ) {
				style.display = "inline-block";
			} else {
				style.zoom = 1;
			}
		}
	}

	if ( opts.overflow ) {
		style.overflow = "hidden";
		if ( !support.shrinkWrapBlocks() ) {
			anim.always( function() {
				style.overflow = opts.overflow[ 0 ];
				style.overflowX = opts.overflow[ 1 ];
				style.overflowY = opts.overflow[ 2 ];
			} );
		}
	}

	// show/hide pass
	for ( prop in props ) {
		value = props[ prop ];
		if ( rfxtypes.exec( value ) ) {
			delete props[ prop ];
			toggle = toggle || value === "toggle";
			if ( value === ( hidden ? "hide" : "show" ) ) {

				// If there is dataShow left over from a stopped hide or show
				// and we are going to proceed with show, we should pretend to be hidden
				if ( value === "show" && dataShow && dataShow[ prop ] !== undefined ) {
					hidden = true;
				} else {
					continue;
				}
			}
			orig[ prop ] = dataShow && dataShow[ prop ] || jQuery.style( elem, prop );

		// Any non-fx value stops us from restoring the original display value
		} else {
			display = undefined;
		}
	}

	if ( !jQuery.isEmptyObject( orig ) ) {
		if ( dataShow ) {
			if ( "hidden" in dataShow ) {
				hidden = dataShow.hidden;
			}
		} else {
			dataShow = jQuery._data( elem, "fxshow", {} );
		}

		// store state if its toggle - enables .stop().toggle() to "reverse"
		if ( toggle ) {
			dataShow.hidden = !hidden;
		}
		if ( hidden ) {
			jQuery( elem ).show();
		} else {
			anim.done( function() {
				jQuery( elem ).hide();
			} );
		}
		anim.done( function() {
			var prop;
			jQuery._removeData( elem, "fxshow" );
			for ( prop in orig ) {
				jQuery.style( elem, prop, orig[ prop ] );
			}
		} );
		for ( prop in orig ) {
			tween = createTween( hidden ? dataShow[ prop ] : 0, prop, anim );

			if ( !( prop in dataShow ) ) {
				dataShow[ prop ] = tween.start;
				if ( hidden ) {
					tween.end = tween.start;
					tween.start = prop === "width" || prop === "height" ? 1 : 0;
				}
			}
		}

	// If this is a noop like .hide().hide(), restore an overwritten display value
	} else if ( ( display === "none" ? defaultDisplay( elem.nodeName ) : display ) === "inline" ) {
		style.display = display;
	}
}

function propFilter( props, specialEasing ) {
	var index, name, easing, value, hooks;

	// camelCase, specialEasing and expand cssHook pass
	for ( index in props ) {
		name = jQuery.camelCase( index );
		easing = specialEasing[ name ];
		value = props[ index ];
		if ( jQuery.isArray( value ) ) {
			easing = value[ 1 ];
			value = props[ index ] = value[ 0 ];
		}

		if ( index !== name ) {
			props[ name ] = value;
			delete props[ index ];
		}

		hooks = jQuery.cssHooks[ name ];
		if ( hooks && "expand" in hooks ) {
			value = hooks.expand( value );
			delete props[ name ];

			// not quite $.extend, this wont overwrite keys already present.
			// also - reusing 'index' from above because we have the correct "name"
			for ( index in value ) {
				if ( !( index in props ) ) {
					props[ index ] = value[ index ];
					specialEasing[ index ] = easing;
				}
			}
		} else {
			specialEasing[ name ] = easing;
		}
	}
}

function Animation( elem, properties, options ) {
	var result,
		stopped,
		index = 0,
		length = Animation.prefilters.length,
		deferred = jQuery.Deferred().always( function() {

			// don't match elem in the :animated selector
			delete tick.elem;
		} ),
		tick = function() {
			if ( stopped ) {
				return false;
			}
			var currentTime = fxNow || createFxNow(),
				remaining = Math.max( 0, animation.startTime + animation.duration - currentTime ),

				// Support: Android 2.3
				// Archaic crash bug won't allow us to use `1 - ( 0.5 || 0 )` (#12497)
				temp = remaining / animation.duration || 0,
				percent = 1 - temp,
				index = 0,
				length = animation.tweens.length;

			for ( ; index < length ; index++ ) {
				animation.tweens[ index ].run( percent );
			}

			deferred.notifyWith( elem, [ animation, percent, remaining ] );

			if ( percent < 1 && length ) {
				return remaining;
			} else {
				deferred.resolveWith( elem, [ animation ] );
				return false;
			}
		},
		animation = deferred.promise( {
			elem: elem,
			props: jQuery.extend( {}, properties ),
			opts: jQuery.extend( true, {
				specialEasing: {},
				easing: jQuery.easing._default
			}, options ),
			originalProperties: properties,
			originalOptions: options,
			startTime: fxNow || createFxNow(),
			duration: options.duration,
			tweens: [],
			createTween: function( prop, end ) {
				var tween = jQuery.Tween( elem, animation.opts, prop, end,
						animation.opts.specialEasing[ prop ] || animation.opts.easing );
				animation.tweens.push( tween );
				return tween;
			},
			stop: function( gotoEnd ) {
				var index = 0,

					// if we are going to the end, we want to run all the tweens
					// otherwise we skip this part
					length = gotoEnd ? animation.tweens.length : 0;
				if ( stopped ) {
					return this;
				}
				stopped = true;
				for ( ; index < length ; index++ ) {
					animation.tweens[ index ].run( 1 );
				}

				// resolve when we played the last frame
				// otherwise, reject
				if ( gotoEnd ) {
					deferred.notifyWith( elem, [ animation, 1, 0 ] );
					deferred.resolveWith( elem, [ animation, gotoEnd ] );
				} else {
					deferred.rejectWith( elem, [ animation, gotoEnd ] );
				}
				return this;
			}
		} ),
		props = animation.props;

	propFilter( props, animation.opts.specialEasing );

	for ( ; index < length ; index++ ) {
		result = Animation.prefilters[ index ].call( animation, elem, props, animation.opts );
		if ( result ) {
			if ( jQuery.isFunction( result.stop ) ) {
				jQuery._queueHooks( animation.elem, animation.opts.queue ).stop =
					jQuery.proxy( result.stop, result );
			}
			return result;
		}
	}

	jQuery.map( props, createTween, animation );

	if ( jQuery.isFunction( animation.opts.start ) ) {
		animation.opts.start.call( elem, animation );
	}

	jQuery.fx.timer(
		jQuery.extend( tick, {
			elem: elem,
			anim: animation,
			queue: animation.opts.queue
		} )
	);

	// attach callbacks from options
	return animation.progress( animation.opts.progress )
		.done( animation.opts.done, animation.opts.complete )
		.fail( animation.opts.fail )
		.always( animation.opts.always );
}

jQuery.Animation = jQuery.extend( Animation, {

	tweeners: {
		"*": [ function( prop, value ) {
			var tween = this.createTween( prop, value );
			adjustCSS( tween.elem, prop, rcssNum.exec( value ), tween );
			return tween;
		} ]
	},

	tweener: function( props, callback ) {
		if ( jQuery.isFunction( props ) ) {
			callback = props;
			props = [ "*" ];
		} else {
			props = props.match( rnotwhite );
		}

		var prop,
			index = 0,
			length = props.length;

		for ( ; index < length ; index++ ) {
			prop = props[ index ];
			Animation.tweeners[ prop ] = Animation.tweeners[ prop ] || [];
			Animation.tweeners[ prop ].unshift( callback );
		}
	},

	prefilters: [ defaultPrefilter ],

	prefilter: function( callback, prepend ) {
		if ( prepend ) {
			Animation.prefilters.unshift( callback );
		} else {
			Animation.prefilters.push( callback );
		}
	}
} );

jQuery.speed = function( speed, easing, fn ) {
	var opt = speed && typeof speed === "object" ? jQuery.extend( {}, speed ) : {
		complete: fn || !fn && easing ||
			jQuery.isFunction( speed ) && speed,
		duration: speed,
		easing: fn && easing || easing && !jQuery.isFunction( easing ) && easing
	};

	opt.duration = jQuery.fx.off ? 0 : typeof opt.duration === "number" ? opt.duration :
		opt.duration in jQuery.fx.speeds ?
			jQuery.fx.speeds[ opt.duration ] : jQuery.fx.speeds._default;

	// normalize opt.queue - true/undefined/null -> "fx"
	if ( opt.queue == null || opt.queue === true ) {
		opt.queue = "fx";
	}

	// Queueing
	opt.old = opt.complete;

	opt.complete = function() {
		if ( jQuery.isFunction( opt.old ) ) {
			opt.old.call( this );
		}

		if ( opt.queue ) {
			jQuery.dequeue( this, opt.queue );
		}
	};

	return opt;
};

jQuery.fn.extend( {
	fadeTo: function( speed, to, easing, callback ) {

		// show any hidden elements after setting opacity to 0
		return this.filter( isHidden ).css( "opacity", 0 ).show()

			// animate to the value specified
			.end().animate( { opacity: to }, speed, easing, callback );
	},
	animate: function( prop, speed, easing, callback ) {
		var empty = jQuery.isEmptyObject( prop ),
			optall = jQuery.speed( speed, easing, callback ),
			doAnimation = function() {

				// Operate on a copy of prop so per-property easing won't be lost
				var anim = Animation( this, jQuery.extend( {}, prop ), optall );

				// Empty animations, or finishing resolves immediately
				if ( empty || jQuery._data( this, "finish" ) ) {
					anim.stop( true );
				}
			};
			doAnimation.finish = doAnimation;

		return empty || optall.queue === false ?
			this.each( doAnimation ) :
			this.queue( optall.queue, doAnimation );
	},
	stop: function( type, clearQueue, gotoEnd ) {
		var stopQueue = function( hooks ) {
			var stop = hooks.stop;
			delete hooks.stop;
			stop( gotoEnd );
		};

		if ( typeof type !== "string" ) {
			gotoEnd = clearQueue;
			clearQueue = type;
			type = undefined;
		}
		if ( clearQueue && type !== false ) {
			this.queue( type || "fx", [] );
		}

		return this.each( function() {
			var dequeue = true,
				index = type != null && type + "queueHooks",
				timers = jQuery.timers,
				data = jQuery._data( this );

			if ( index ) {
				if ( data[ index ] && data[ index ].stop ) {
					stopQueue( data[ index ] );
				}
			} else {
				for ( index in data ) {
					if ( data[ index ] && data[ index ].stop && rrun.test( index ) ) {
						stopQueue( data[ index ] );
					}
				}
			}

			for ( index = timers.length; index--; ) {
				if ( timers[ index ].elem === this &&
					( type == null || timers[ index ].queue === type ) ) {

					timers[ index ].anim.stop( gotoEnd );
					dequeue = false;
					timers.splice( index, 1 );
				}
			}

			// start the next in the queue if the last step wasn't forced
			// timers currently will call their complete callbacks, which will dequeue
			// but only if they were gotoEnd
			if ( dequeue || !gotoEnd ) {
				jQuery.dequeue( this, type );
			}
		} );
	},
	finish: function( type ) {
		if ( type !== false ) {
			type = type || "fx";
		}
		return this.each( function() {
			var index,
				data = jQuery._data( this ),
				queue = data[ type + "queue" ],
				hooks = data[ type + "queueHooks" ],
				timers = jQuery.timers,
				length = queue ? queue.length : 0;

			// enable finishing flag on private data
			data.finish = true;

			// empty the queue first
			jQuery.queue( this, type, [] );

			if ( hooks && hooks.stop ) {
				hooks.stop.call( this, true );
			}

			// look for any active animations, and finish them
			for ( index = timers.length; index--; ) {
				if ( timers[ index ].elem === this && timers[ index ].queue === type ) {
					timers[ index ].anim.stop( true );
					timers.splice( index, 1 );
				}
			}

			// look for any animations in the old queue and finish them
			for ( index = 0; index < length; index++ ) {
				if ( queue[ index ] && queue[ index ].finish ) {
					queue[ index ].finish.call( this );
				}
			}

			// turn off finishing flag
			delete data.finish;
		} );
	}
} );

jQuery.each( [ "toggle", "show", "hide" ], function( i, name ) {
	var cssFn = jQuery.fn[ name ];
	jQuery.fn[ name ] = function( speed, easing, callback ) {
		return speed == null || typeof speed === "boolean" ?
			cssFn.apply( this, arguments ) :
			this.animate( genFx( name, true ), speed, easing, callback );
	};
} );

// Generate shortcuts for custom animations
jQuery.each( {
	slideDown: genFx( "show" ),
	slideUp: genFx( "hide" ),
	slideToggle: genFx( "toggle" ),
	fadeIn: { opacity: "show" },
	fadeOut: { opacity: "hide" },
	fadeToggle: { opacity: "toggle" }
}, function( name, props ) {
	jQuery.fn[ name ] = function( speed, easing, callback ) {
		return this.animate( props, speed, easing, callback );
	};
} );

jQuery.timers = [];
jQuery.fx.tick = function() {
	var timer,
		timers = jQuery.timers,
		i = 0;

	fxNow = jQuery.now();

	for ( ; i < timers.length; i++ ) {
		timer = timers[ i ];

		// Checks the timer has not already been removed
		if ( !timer() && timers[ i ] === timer ) {
			timers.splice( i--, 1 );
		}
	}

	if ( !timers.length ) {
		jQuery.fx.stop();
	}
	fxNow = undefined;
};

jQuery.fx.timer = function( timer ) {
	jQuery.timers.push( timer );
	if ( timer() ) {
		jQuery.fx.start();
	} else {
		jQuery.timers.pop();
	}
};

jQuery.fx.interval = 13;

jQuery.fx.start = function() {
	if ( !timerId ) {
		timerId = window.setInterval( jQuery.fx.tick, jQuery.fx.interval );
	}
};

jQuery.fx.stop = function() {
	window.clearInterval( timerId );
	timerId = null;
};

jQuery.fx.speeds = {
	slow: 600,
	fast: 200,

	// Default speed
	_default: 400
};


// Based off of the plugin by Clint Helfers, with permission.
// http://web.archive.org/web/20100324014747/http://blindsignals.com/index.php/2009/07/jquery-delay/
jQuery.fn.delay = function( time, type ) {
	time = jQuery.fx ? jQuery.fx.speeds[ time ] || time : time;
	type = type || "fx";

	return this.queue( type, function( next, hooks ) {
		var timeout = window.setTimeout( next, time );
		hooks.stop = function() {
			window.clearTimeout( timeout );
		};
	} );
};


( function() {
	var a,
		input = document.createElement( "input" ),
		div = document.createElement( "div" ),
		select = document.createElement( "select" ),
		opt = select.appendChild( document.createElement( "option" ) );

	// Setup
	div = document.createElement( "div" );
	div.setAttribute( "className", "t" );
	div.innerHTML = "  <link/><table></table><a href='/a'>a</a><input type='checkbox'/>";
	a = div.getElementsByTagName( "a" )[ 0 ];

	// Support: Windows Web Apps (WWA)
	// `type` must use .setAttribute for WWA (#14901)
	input.setAttribute( "type", "checkbox" );
	div.appendChild( input );

	a = div.getElementsByTagName( "a" )[ 0 ];

	// First batch of tests.
	a.style.cssText = "top:1px";

	// Test setAttribute on camelCase class.
	// If it works, we need attrFixes when doing get/setAttribute (ie6/7)
	support.getSetAttribute = div.className !== "t";

	// Get the style information from getAttribute
	// (IE uses .cssText instead)
	support.style = /top/.test( a.getAttribute( "style" ) );

	// Make sure that URLs aren't manipulated
	// (IE normalizes it by default)
	support.hrefNormalized = a.getAttribute( "href" ) === "/a";

	// Check the default checkbox/radio value ("" on WebKit; "on" elsewhere)
	support.checkOn = !!input.value;

	// Make sure that a selected-by-default option has a working selected property.
	// (WebKit defaults to false instead of true, IE too, if it's in an optgroup)
	support.optSelected = opt.selected;

	// Tests for enctype support on a form (#6743)
	support.enctype = !!document.createElement( "form" ).enctype;

	// Make sure that the options inside disabled selects aren't marked as disabled
	// (WebKit marks them as disabled)
	select.disabled = true;
	support.optDisabled = !opt.disabled;

	// Support: IE8 only
	// Check if we can trust getAttribute("value")
	input = document.createElement( "input" );
	input.setAttribute( "value", "" );
	support.input = input.getAttribute( "value" ) === "";

	// Check if an input maintains its value after becoming a radio
	input.value = "t";
	input.setAttribute( "type", "radio" );
	support.radioValue = input.value === "t";
} )();


var rreturn = /\r/g,
	rspaces = /[\x20\t\r\n\f]+/g;

jQuery.fn.extend( {
	val: function( value ) {
		var hooks, ret, isFunction,
			elem = this[ 0 ];

		if ( !arguments.length ) {
			if ( elem ) {
				hooks = jQuery.valHooks[ elem.type ] ||
					jQuery.valHooks[ elem.nodeName.toLowerCase() ];

				if (
					hooks &&
					"get" in hooks &&
					( ret = hooks.get( elem, "value" ) ) !== undefined
				) {
					return ret;
				}

				ret = elem.value;

				return typeof ret === "string" ?

					// handle most common string cases
					ret.replace( rreturn, "" ) :

					// handle cases where value is null/undef or number
					ret == null ? "" : ret;
			}

			return;
		}

		isFunction = jQuery.isFunction( value );

		return this.each( function( i ) {
			var val;

			if ( this.nodeType !== 1 ) {
				return;
			}

			if ( isFunction ) {
				val = value.call( this, i, jQuery( this ).val() );
			} else {
				val = value;
			}

			// Treat null/undefined as ""; convert numbers to string
			if ( val == null ) {
				val = "";
			} else if ( typeof val === "number" ) {
				val += "";
			} else if ( jQuery.isArray( val ) ) {
				val = jQuery.map( val, function( value ) {
					return value == null ? "" : value + "";
				} );
			}

			hooks = jQuery.valHooks[ this.type ] || jQuery.valHooks[ this.nodeName.toLowerCase() ];

			// If set returns undefined, fall back to normal setting
			if ( !hooks || !( "set" in hooks ) || hooks.set( this, val, "value" ) === undefined ) {
				this.value = val;
			}
		} );
	}
} );

jQuery.extend( {
	valHooks: {
		option: {
			get: function( elem ) {
				var val = jQuery.find.attr( elem, "value" );
				return val != null ?
					val :

					// Support: IE10-11+
					// option.text throws exceptions (#14686, #14858)
					// Strip and collapse whitespace
					// https://html.spec.whatwg.org/#strip-and-collapse-whitespace
					jQuery.trim( jQuery.text( elem ) ).replace( rspaces, " " );
			}
		},
		select: {
			get: function( elem ) {
				var value, option,
					options = elem.options,
					index = elem.selectedIndex,
					one = elem.type === "select-one" || index < 0,
					values = one ? null : [],
					max = one ? index + 1 : options.length,
					i = index < 0 ?
						max :
						one ? index : 0;

				// Loop through all the selected options
				for ( ; i < max; i++ ) {
					option = options[ i ];

					// oldIE doesn't update selected after form reset (#2551)
					if ( ( option.selected || i === index ) &&

							// Don't return options that are disabled or in a disabled optgroup
							( support.optDisabled ?
								!option.disabled :
								option.getAttribute( "disabled" ) === null ) &&
							( !option.parentNode.disabled ||
								!jQuery.nodeName( option.parentNode, "optgroup" ) ) ) {

						// Get the specific value for the option
						value = jQuery( option ).val();

						// We don't need an array for one selects
						if ( one ) {
							return value;
						}

						// Multi-Selects return an array
						values.push( value );
					}
				}

				return values;
			},

			set: function( elem, value ) {
				var optionSet, option,
					options = elem.options,
					values = jQuery.makeArray( value ),
					i = options.length;

				while ( i-- ) {
					option = options[ i ];

					if ( jQuery.inArray( jQuery.valHooks.option.get( option ), values ) > -1 ) {

						// Support: IE6
						// When new option element is added to select box we need to
						// force reflow of newly added node in order to workaround delay
						// of initialization properties
						try {
							option.selected = optionSet = true;

						} catch ( _ ) {

							// Will be executed only in IE6
							option.scrollHeight;
						}

					} else {
						option.selected = false;
					}
				}

				// Force browsers to behave consistently when non-matching value is set
				if ( !optionSet ) {
					elem.selectedIndex = -1;
				}

				return options;
			}
		}
	}
} );

// Radios and checkboxes getter/setter
jQuery.each( [ "radio", "checkbox" ], function() {
	jQuery.valHooks[ this ] = {
		set: function( elem, value ) {
			if ( jQuery.isArray( value ) ) {
				return ( elem.checked = jQuery.inArray( jQuery( elem ).val(), value ) > -1 );
			}
		}
	};
	if ( !support.checkOn ) {
		jQuery.valHooks[ this ].get = function( elem ) {
			return elem.getAttribute( "value" ) === null ? "on" : elem.value;
		};
	}
} );




var nodeHook, boolHook,
	attrHandle = jQuery.expr.attrHandle,
	ruseDefault = /^(?:checked|selected)$/i,
	getSetAttribute = support.getSetAttribute,
	getSetInput = support.input;

jQuery.fn.extend( {
	attr: function( name, value ) {
		return access( this, jQuery.attr, name, value, arguments.length > 1 );
	},

	removeAttr: function( name ) {
		return this.each( function() {
			jQuery.removeAttr( this, name );
		} );
	}
} );

jQuery.extend( {
	attr: function( elem, name, value ) {
		var ret, hooks,
			nType = elem.nodeType;

		// Don't get/set attributes on text, comment and attribute nodes
		if ( nType === 3 || nType === 8 || nType === 2 ) {
			return;
		}

		// Fallback to prop when attributes are not supported
		if ( typeof elem.getAttribute === "undefined" ) {
			return jQuery.prop( elem, name, value );
		}

		// All attributes are lowercase
		// Grab necessary hook if one is defined
		if ( nType !== 1 || !jQuery.isXMLDoc( elem ) ) {
			name = name.toLowerCase();
			hooks = jQuery.attrHooks[ name ] ||
				( jQuery.expr.match.bool.test( name ) ? boolHook : nodeHook );
		}

		if ( value !== undefined ) {
			if ( value === null ) {
				jQuery.removeAttr( elem, name );
				return;
			}

			if ( hooks && "set" in hooks &&
				( ret = hooks.set( elem, value, name ) ) !== undefined ) {
				return ret;
			}

			elem.setAttribute( name, value + "" );
			return value;
		}

		if ( hooks && "get" in hooks && ( ret = hooks.get( elem, name ) ) !== null ) {
			return ret;
		}

		ret = jQuery.find.attr( elem, name );

		// Non-existent attributes return null, we normalize to undefined
		return ret == null ? undefined : ret;
	},

	attrHooks: {
		type: {
			set: function( elem, value ) {
				if ( !support.radioValue && value === "radio" &&
					jQuery.nodeName( elem, "input" ) ) {

					// Setting the type on a radio button after the value resets the value in IE8-9
					// Reset value to default in case type is set after value during creation
					var val = elem.value;
					elem.setAttribute( "type", value );
					if ( val ) {
						elem.value = val;
					}
					return value;
				}
			}
		}
	},

	removeAttr: function( elem, value ) {
		var name, propName,
			i = 0,
			attrNames = value && value.match( rnotwhite );

		if ( attrNames && elem.nodeType === 1 ) {
			while ( ( name = attrNames[ i++ ] ) ) {
				propName = jQuery.propFix[ name ] || name;

				// Boolean attributes get special treatment (#10870)
				if ( jQuery.expr.match.bool.test( name ) ) {

					// Set corresponding property to false
					if ( getSetInput && getSetAttribute || !ruseDefault.test( name ) ) {
						elem[ propName ] = false;

					// Support: IE<9
					// Also clear defaultChecked/defaultSelected (if appropriate)
					} else {
						elem[ jQuery.camelCase( "default-" + name ) ] =
							elem[ propName ] = false;
					}

				// See #9699 for explanation of this approach (setting first, then removal)
				} else {
					jQuery.attr( elem, name, "" );
				}

				elem.removeAttribute( getSetAttribute ? name : propName );
			}
		}
	}
} );

// Hooks for boolean attributes
boolHook = {
	set: function( elem, value, name ) {
		if ( value === false ) {

			// Remove boolean attributes when set to false
			jQuery.removeAttr( elem, name );
		} else if ( getSetInput && getSetAttribute || !ruseDefault.test( name ) ) {

			// IE<8 needs the *property* name
			elem.setAttribute( !getSetAttribute && jQuery.propFix[ name ] || name, name );

		} else {

			// Support: IE<9
			// Use defaultChecked and defaultSelected for oldIE
			elem[ jQuery.camelCase( "default-" + name ) ] = elem[ name ] = true;
		}
		return name;
	}
};

jQuery.each( jQuery.expr.match.bool.source.match( /\w+/g ), function( i, name ) {
	var getter = attrHandle[ name ] || jQuery.find.attr;

	if ( getSetInput && getSetAttribute || !ruseDefault.test( name ) ) {
		attrHandle[ name ] = function( elem, name, isXML ) {
			var ret, handle;
			if ( !isXML ) {

				// Avoid an infinite loop by temporarily removing this function from the getter
				handle = attrHandle[ name ];
				attrHandle[ name ] = ret;
				ret = getter( elem, name, isXML ) != null ?
					name.toLowerCase() :
					null;
				attrHandle[ name ] = handle;
			}
			return ret;
		};
	} else {
		attrHandle[ name ] = function( elem, name, isXML ) {
			if ( !isXML ) {
				return elem[ jQuery.camelCase( "default-" + name ) ] ?
					name.toLowerCase() :
					null;
			}
		};
	}
} );

// fix oldIE attroperties
if ( !getSetInput || !getSetAttribute ) {
	jQuery.attrHooks.value = {
		set: function( elem, value, name ) {
			if ( jQuery.nodeName( elem, "input" ) ) {

				// Does not return so that setAttribute is also used
				elem.defaultValue = value;
			} else {

				// Use nodeHook if defined (#1954); otherwise setAttribute is fine
				return nodeHook && nodeHook.set( elem, value, name );
			}
		}
	};
}

// IE6/7 do not support getting/setting some attributes with get/setAttribute
if ( !getSetAttribute ) {

	// Use this for any attribute in IE6/7
	// This fixes almost every IE6/7 issue
	nodeHook = {
		set: function( elem, value, name ) {

			// Set the existing or create a new attribute node
			var ret = elem.getAttributeNode( name );
			if ( !ret ) {
				elem.setAttributeNode(
					( ret = elem.ownerDocument.createAttribute( name ) )
				);
			}

			ret.value = value += "";

			// Break association with cloned elements by also using setAttribute (#9646)
			if ( name === "value" || value === elem.getAttribute( name ) ) {
				return value;
			}
		}
	};

	// Some attributes are constructed with empty-string values when not defined
	attrHandle.id = attrHandle.name = attrHandle.coords =
		function( elem, name, isXML ) {
			var ret;
			if ( !isXML ) {
				return ( ret = elem.getAttributeNode( name ) ) && ret.value !== "" ?
					ret.value :
					null;
			}
		};

	// Fixing value retrieval on a button requires this module
	jQuery.valHooks.button = {
		get: function( elem, name ) {
			var ret = elem.getAttributeNode( name );
			if ( ret && ret.specified ) {
				return ret.value;
			}
		},
		set: nodeHook.set
	};

	// Set contenteditable to false on removals(#10429)
	// Setting to empty string throws an error as an invalid value
	jQuery.attrHooks.contenteditable = {
		set: function( elem, value, name ) {
			nodeHook.set( elem, value === "" ? false : value, name );
		}
	};

	// Set width and height to auto instead of 0 on empty string( Bug #8150 )
	// This is for removals
	jQuery.each( [ "width", "height" ], function( i, name ) {
		jQuery.attrHooks[ name ] = {
			set: function( elem, value ) {
				if ( value === "" ) {
					elem.setAttribute( name, "auto" );
					return value;
				}
			}
		};
	} );
}

if ( !support.style ) {
	jQuery.attrHooks.style = {
		get: function( elem ) {

			// Return undefined in the case of empty string
			// Note: IE uppercases css property names, but if we were to .toLowerCase()
			// .cssText, that would destroy case sensitivity in URL's, like in "background"
			return elem.style.cssText || undefined;
		},
		set: function( elem, value ) {
			return ( elem.style.cssText = value + "" );
		}
	};
}




var rfocusable = /^(?:input|select|textarea|button|object)$/i,
	rclickable = /^(?:a|area)$/i;

jQuery.fn.extend( {
	prop: function( name, value ) {
		return access( this, jQuery.prop, name, value, arguments.length > 1 );
	},

	removeProp: function( name ) {
		name = jQuery.propFix[ name ] || name;
		return this.each( function() {

			// try/catch handles cases where IE balks (such as removing a property on window)
			try {
				this[ name ] = undefined;
				delete this[ name ];
			} catch ( e ) {}
		} );
	}
} );

jQuery.extend( {
	prop: function( elem, name, value ) {
		var ret, hooks,
			nType = elem.nodeType;

		// Don't get/set properties on text, comment and attribute nodes
		if ( nType === 3 || nType === 8 || nType === 2 ) {
			return;
		}

		if ( nType !== 1 || !jQuery.isXMLDoc( elem ) ) {

			// Fix name and attach hooks
			name = jQuery.propFix[ name ] || name;
			hooks = jQuery.propHooks[ name ];
		}

		if ( value !== undefined ) {
			if ( hooks && "set" in hooks &&
				( ret = hooks.set( elem, value, name ) ) !== undefined ) {
				return ret;
			}

			return ( elem[ name ] = value );
		}

		if ( hooks && "get" in hooks && ( ret = hooks.get( elem, name ) ) !== null ) {
			return ret;
		}

		return elem[ name ];
	},

	propHooks: {
		tabIndex: {
			get: function( elem ) {

				// elem.tabIndex doesn't always return the
				// correct value when it hasn't been explicitly set
				// http://fluidproject.org/blog/2008/01/09/getting-setting-and-removing-tabindex-values-with-javascript/
				// Use proper attribute retrieval(#12072)
				var tabindex = jQuery.find.attr( elem, "tabindex" );

				return tabindex ?
					parseInt( tabindex, 10 ) :
					rfocusable.test( elem.nodeName ) ||
						rclickable.test( elem.nodeName ) && elem.href ?
							0 :
							-1;
			}
		}
	},

	propFix: {
		"for": "htmlFor",
		"class": "className"
	}
} );

// Some attributes require a special call on IE
// http://msdn.microsoft.com/en-us/library/ms536429%28VS.85%29.aspx
if ( !support.hrefNormalized ) {

	// href/src property should get the full normalized URL (#10299/#12915)
	jQuery.each( [ "href", "src" ], function( i, name ) {
		jQuery.propHooks[ name ] = {
			get: function( elem ) {
				return elem.getAttribute( name, 4 );
			}
		};
	} );
}

// Support: Safari, IE9+
// Accessing the selectedIndex property
// forces the browser to respect setting selected
// on the option
// The getter ensures a default option is selected
// when in an optgroup
if ( !support.optSelected ) {
	jQuery.propHooks.selected = {
		get: function( elem ) {
			var parent = elem.parentNode;

			if ( parent ) {
				parent.selectedIndex;

				// Make sure that it also works with optgroups, see #5701
				if ( parent.parentNode ) {
					parent.parentNode.selectedIndex;
				}
			}
			return null;
		},
		set: function( elem ) {
			var parent = elem.parentNode;
			if ( parent ) {
				parent.selectedIndex;

				if ( parent.parentNode ) {
					parent.parentNode.selectedIndex;
				}
			}
		}
	};
}

jQuery.each( [
	"tabIndex",
	"readOnly",
	"maxLength",
	"cellSpacing",
	"cellPadding",
	"rowSpan",
	"colSpan",
	"useMap",
	"frameBorder",
	"contentEditable"
], function() {
	jQuery.propFix[ this.toLowerCase() ] = this;
} );

// IE6/7 call enctype encoding
if ( !support.enctype ) {
	jQuery.propFix.enctype = "encoding";
}




var rclass = /[\t\r\n\f]/g;

function getClass( elem ) {
	return jQuery.attr( elem, "class" ) || "";
}

jQuery.fn.extend( {
	addClass: function( value ) {
		var classes, elem, cur, curValue, clazz, j, finalValue,
			i = 0;

		if ( jQuery.isFunction( value ) ) {
			return this.each( function( j ) {
				jQuery( this ).addClass( value.call( this, j, getClass( this ) ) );
			} );
		}

		if ( typeof value === "string" && value ) {
			classes = value.match( rnotwhite ) || [];

			while ( ( elem = this[ i++ ] ) ) {
				curValue = getClass( elem );
				cur = elem.nodeType === 1 &&
					( " " + curValue + " " ).replace( rclass, " " );

				if ( cur ) {
					j = 0;
					while ( ( clazz = classes[ j++ ] ) ) {
						if ( cur.indexOf( " " + clazz + " " ) < 0 ) {
							cur += clazz + " ";
						}
					}

					// only assign if different to avoid unneeded rendering.
					finalValue = jQuery.trim( cur );
					if ( curValue !== finalValue ) {
						jQuery.attr( elem, "class", finalValue );
					}
				}
			}
		}

		return this;
	},

	removeClass: function( value ) {
		var classes, elem, cur, curValue, clazz, j, finalValue,
			i = 0;

		if ( jQuery.isFunction( value ) ) {
			return this.each( function( j ) {
				jQuery( this ).removeClass( value.call( this, j, getClass( this ) ) );
			} );
		}

		if ( !arguments.length ) {
			return this.attr( "class", "" );
		}

		if ( typeof value === "string" && value ) {
			classes = value.match( rnotwhite ) || [];

			while ( ( elem = this[ i++ ] ) ) {
				curValue = getClass( elem );

				// This expression is here for better compressibility (see addClass)
				cur = elem.nodeType === 1 &&
					( " " + curValue + " " ).replace( rclass, " " );

				if ( cur ) {
					j = 0;
					while ( ( clazz = classes[ j++ ] ) ) {

						// Remove *all* instances
						while ( cur.indexOf( " " + clazz + " " ) > -1 ) {
							cur = cur.replace( " " + clazz + " ", " " );
						}
					}

					// Only assign if different to avoid unneeded rendering.
					finalValue = jQuery.trim( cur );
					if ( curValue !== finalValue ) {
						jQuery.attr( elem, "class", finalValue );
					}
				}
			}
		}

		return this;
	},

	toggleClass: function( value, stateVal ) {
		var type = typeof value;

		if ( typeof stateVal === "boolean" && type === "string" ) {
			return stateVal ? this.addClass( value ) : this.removeClass( value );
		}

		if ( jQuery.isFunction( value ) ) {
			return this.each( function( i ) {
				jQuery( this ).toggleClass(
					value.call( this, i, getClass( this ), stateVal ),
					stateVal
				);
			} );
		}

		return this.each( function() {
			var className, i, self, classNames;

			if ( type === "string" ) {

				// Toggle individual class names
				i = 0;
				self = jQuery( this );
				classNames = value.match( rnotwhite ) || [];

				while ( ( className = classNames[ i++ ] ) ) {

					// Check each className given, space separated list
					if ( self.hasClass( className ) ) {
						self.removeClass( className );
					} else {
						self.addClass( className );
					}
				}

			// Toggle whole class name
			} else if ( value === undefined || type === "boolean" ) {
				className = getClass( this );
				if ( className ) {

					// store className if set
					jQuery._data( this, "__className__", className );
				}

				// If the element has a class name or if we're passed "false",
				// then remove the whole classname (if there was one, the above saved it).
				// Otherwise bring back whatever was previously saved (if anything),
				// falling back to the empty string if nothing was stored.
				jQuery.attr( this, "class",
					className || value === false ?
					"" :
					jQuery._data( this, "__className__" ) || ""
				);
			}
		} );
	},

	hasClass: function( selector ) {
		var className, elem,
			i = 0;

		className = " " + selector + " ";
		while ( ( elem = this[ i++ ] ) ) {
			if ( elem.nodeType === 1 &&
				( " " + getClass( elem ) + " " ).replace( rclass, " " )
					.indexOf( className ) > -1
			) {
				return true;
			}
		}

		return false;
	}
} );




// Return jQuery for attributes-only inclusion


jQuery.each( ( "blur focus focusin focusout load resize scroll unload click dblclick " +
	"mousedown mouseup mousemove mouseover mouseout mouseenter mouseleave " +
	"change select submit keydown keypress keyup error contextmenu" ).split( " " ),
	function( i, name ) {

	// Handle event binding
	jQuery.fn[ name ] = function( data, fn ) {
		return arguments.length > 0 ?
			this.on( name, null, data, fn ) :
			this.trigger( name );
	};
} );

jQuery.fn.extend( {
	hover: function( fnOver, fnOut ) {
		return this.mouseenter( fnOver ).mouseleave( fnOut || fnOver );
	}
} );


var location = window.location;

var nonce = jQuery.now();

var rquery = ( /\?/ );



var rvalidtokens = /(,)|(\[|{)|(}|])|"(?:[^"\\\r\n]|\\["\\\/bfnrt]|\\u[\da-fA-F]{4})*"\s*:?|true|false|null|-?(?!0\d)\d+(?:\.\d+|)(?:[eE][+-]?\d+|)/g;

jQuery.parseJSON = function( data ) {

	// Attempt to parse using the native JSON parser first
	if ( window.JSON && window.JSON.parse ) {

		// Support: Android 2.3
		// Workaround failure to string-cast null input
		return window.JSON.parse( data + "" );
	}

	var requireNonComma,
		depth = null,
		str = jQuery.trim( data + "" );

	// Guard against invalid (and possibly dangerous) input by ensuring that nothing remains
	// after removing valid tokens
	return str && !jQuery.trim( str.replace( rvalidtokens, function( token, comma, open, close ) {

		// Force termination if we see a misplaced comma
		if ( requireNonComma && comma ) {
			depth = 0;
		}

		// Perform no more replacements after returning to outermost depth
		if ( depth === 0 ) {
			return token;
		}

		// Commas must not follow "[", "{", or ","
		requireNonComma = open || comma;

		// Determine new depth
		// array/object open ("[" or "{"): depth += true - false (increment)
		// array/object close ("]" or "}"): depth += false - true (decrement)
		// other cases ("," or primitive): depth += true - true (numeric cast)
		depth += !close - !open;

		// Remove this token
		return "";
	} ) ) ?
		( Function( "return " + str ) )() :
		jQuery.error( "Invalid JSON: " + data );
};


// Cross-browser xml parsing
jQuery.parseXML = function( data ) {
	var xml, tmp;
	if ( !data || typeof data !== "string" ) {
		return null;
	}
	try {
		if ( window.DOMParser ) { // Standard
			tmp = new window.DOMParser();
			xml = tmp.parseFromString( data, "text/xml" );
		} else { // IE
			xml = new window.ActiveXObject( "Microsoft.XMLDOM" );
			xml.async = "false";
			xml.loadXML( data );
		}
	} catch ( e ) {
		xml = undefined;
	}
	if ( !xml || !xml.documentElement || xml.getElementsByTagName( "parsererror" ).length ) {
		jQuery.error( "Invalid XML: " + data );
	}
	return xml;
};


var
	rhash = /#.*$/,
	rts = /([?&])_=[^&]*/,

	// IE leaves an \r character at EOL
	rheaders = /^(.*?):[ \t]*([^\r\n]*)\r?$/mg,

	// #7653, #8125, #8152: local protocol detection
	rlocalProtocol = /^(?:about|app|app-storage|.+-extension|file|res|widget):$/,
	rnoContent = /^(?:GET|HEAD)$/,
	rprotocol = /^\/\//,
	rurl = /^([\w.+-]+:)(?:\/\/(?:[^\/?#]*@|)([^\/?#:]*)(?::(\d+)|)|)/,

	/* Prefilters
	 * 1) They are useful to introduce custom dataTypes (see ajax/jsonp.js for an example)
	 * 2) These are called:
	 *    - BEFORE asking for a transport
	 *    - AFTER param serialization (s.data is a string if s.processData is true)
	 * 3) key is the dataType
	 * 4) the catchall symbol "*" can be used
	 * 5) execution will start with transport dataType and THEN continue down to "*" if needed
	 */
	prefilters = {},

	/* Transports bindings
	 * 1) key is the dataType
	 * 2) the catchall symbol "*" can be used
	 * 3) selection will start with transport dataType and THEN go to "*" if needed
	 */
	transports = {},

	// Avoid comment-prolog char sequence (#10098); must appease lint and evade compression
	allTypes = "*/".concat( "*" ),

	// Document location
	ajaxLocation = location.href,

	// Segment location into parts
	ajaxLocParts = rurl.exec( ajaxLocation.toLowerCase() ) || [];

// Base "constructor" for jQuery.ajaxPrefilter and jQuery.ajaxTransport
function addToPrefiltersOrTransports( structure ) {

	// dataTypeExpression is optional and defaults to "*"
	return function( dataTypeExpression, func ) {

		if ( typeof dataTypeExpression !== "string" ) {
			func = dataTypeExpression;
			dataTypeExpression = "*";
		}

		var dataType,
			i = 0,
			dataTypes = dataTypeExpression.toLowerCase().match( rnotwhite ) || [];

		if ( jQuery.isFunction( func ) ) {

			// For each dataType in the dataTypeExpression
			while ( ( dataType = dataTypes[ i++ ] ) ) {

				// Prepend if requested
				if ( dataType.charAt( 0 ) === "+" ) {
					dataType = dataType.slice( 1 ) || "*";
					( structure[ dataType ] = structure[ dataType ] || [] ).unshift( func );

				// Otherwise append
				} else {
					( structure[ dataType ] = structure[ dataType ] || [] ).push( func );
				}
			}
		}
	};
}

// Base inspection function for prefilters and transports
function inspectPrefiltersOrTransports( structure, options, originalOptions, jqXHR ) {

	var inspected = {},
		seekingTransport = ( structure === transports );

	function inspect( dataType ) {
		var selected;
		inspected[ dataType ] = true;
		jQuery.each( structure[ dataType ] || [], function( _, prefilterOrFactory ) {
			var dataTypeOrTransport = prefilterOrFactory( options, originalOptions, jqXHR );
			if ( typeof dataTypeOrTransport === "string" &&
				!seekingTransport && !inspected[ dataTypeOrTransport ] ) {

				options.dataTypes.unshift( dataTypeOrTransport );
				inspect( dataTypeOrTransport );
				return false;
			} else if ( seekingTransport ) {
				return !( selected = dataTypeOrTransport );
			}
		} );
		return selected;
	}

	return inspect( options.dataTypes[ 0 ] ) || !inspected[ "*" ] && inspect( "*" );
}

// A special extend for ajax options
// that takes "flat" options (not to be deep extended)
// Fixes #9887
function ajaxExtend( target, src ) {
	var deep, key,
		flatOptions = jQuery.ajaxSettings.flatOptions || {};

	for ( key in src ) {
		if ( src[ key ] !== undefined ) {
			( flatOptions[ key ] ? target : ( deep || ( deep = {} ) ) )[ key ] = src[ key ];
		}
	}
	if ( deep ) {
		jQuery.extend( true, target, deep );
	}

	return target;
}

/* Handles responses to an ajax request:
 * - finds the right dataType (mediates between content-type and expected dataType)
 * - returns the corresponding response
 */
function ajaxHandleResponses( s, jqXHR, responses ) {
	var firstDataType, ct, finalDataType, type,
		contents = s.contents,
		dataTypes = s.dataTypes;

	// Remove auto dataType and get content-type in the process
	while ( dataTypes[ 0 ] === "*" ) {
		dataTypes.shift();
		if ( ct === undefined ) {
			ct = s.mimeType || jqXHR.getResponseHeader( "Content-Type" );
		}
	}

	// Check if we're dealing with a known content-type
	if ( ct ) {
		for ( type in contents ) {
			if ( contents[ type ] && contents[ type ].test( ct ) ) {
				dataTypes.unshift( type );
				break;
			}
		}
	}

	// Check to see if we have a response for the expected dataType
	if ( dataTypes[ 0 ] in responses ) {
		finalDataType = dataTypes[ 0 ];
	} else {

		// Try convertible dataTypes
		for ( type in responses ) {
			if ( !dataTypes[ 0 ] || s.converters[ type + " " + dataTypes[ 0 ] ] ) {
				finalDataType = type;
				break;
			}
			if ( !firstDataType ) {
				firstDataType = type;
			}
		}

		// Or just use first one
		finalDataType = finalDataType || firstDataType;
	}

	// If we found a dataType
	// We add the dataType to the list if needed
	// and return the corresponding response
	if ( finalDataType ) {
		if ( finalDataType !== dataTypes[ 0 ] ) {
			dataTypes.unshift( finalDataType );
		}
		return responses[ finalDataType ];
	}
}

/* Chain conversions given the request and the original response
 * Also sets the responseXXX fields on the jqXHR instance
 */
function ajaxConvert( s, response, jqXHR, isSuccess ) {
	var conv2, current, conv, tmp, prev,
		converters = {},

		// Work with a copy of dataTypes in case we need to modify it for conversion
		dataTypes = s.dataTypes.slice();

	// Create converters map with lowercased keys
	if ( dataTypes[ 1 ] ) {
		for ( conv in s.converters ) {
			converters[ conv.toLowerCase() ] = s.converters[ conv ];
		}
	}

	current = dataTypes.shift();

	// Convert to each sequential dataType
	while ( current ) {

		if ( s.responseFields[ current ] ) {
			jqXHR[ s.responseFields[ current ] ] = response;
		}

		// Apply the dataFilter if provided
		if ( !prev && isSuccess && s.dataFilter ) {
			response = s.dataFilter( response, s.dataType );
		}

		prev = current;
		current = dataTypes.shift();

		if ( current ) {

			// There's only work to do if current dataType is non-auto
			if ( current === "*" ) {

				current = prev;

			// Convert response if prev dataType is non-auto and differs from current
			} else if ( prev !== "*" && prev !== current ) {

				// Seek a direct converter
				conv = converters[ prev + " " + current ] || converters[ "* " + current ];

				// If none found, seek a pair
				if ( !conv ) {
					for ( conv2 in converters ) {

						// If conv2 outputs current
						tmp = conv2.split( " " );
						if ( tmp[ 1 ] === current ) {

							// If prev can be converted to accepted input
							conv = converters[ prev + " " + tmp[ 0 ] ] ||
								converters[ "* " + tmp[ 0 ] ];
							if ( conv ) {

								// Condense equivalence converters
								if ( conv === true ) {
									conv = converters[ conv2 ];

								// Otherwise, insert the intermediate dataType
								} else if ( converters[ conv2 ] !== true ) {
									current = tmp[ 0 ];
									dataTypes.unshift( tmp[ 1 ] );
								}
								break;
							}
						}
					}
				}

				// Apply converter (if not an equivalence)
				if ( conv !== true ) {

					// Unless errors are allowed to bubble, catch and return them
					if ( conv && s[ "throws" ] ) { // jscs:ignore requireDotNotation
						response = conv( response );
					} else {
						try {
							response = conv( response );
						} catch ( e ) {
							return {
								state: "parsererror",
								error: conv ? e : "No conversion from " + prev + " to " + current
							};
						}
					}
				}
			}
		}
	}

	return { state: "success", data: response };
}

jQuery.extend( {

	// Counter for holding the number of active queries
	active: 0,

	// Last-Modified header cache for next request
	lastModified: {},
	etag: {},

	ajaxSettings: {
		url: ajaxLocation,
		type: "GET",
		isLocal: rlocalProtocol.test( ajaxLocParts[ 1 ] ),
		global: true,
		processData: true,
		async: true,
		contentType: "application/x-www-form-urlencoded; charset=UTF-8",
		/*
		timeout: 0,
		data: null,
		dataType: null,
		username: null,
		password: null,
		cache: null,
		throws: false,
		traditional: false,
		headers: {},
		*/

		accepts: {
			"*": allTypes,
			text: "text/plain",
			html: "text/html",
			xml: "application/xml, text/xml",
			json: "application/json, text/javascript"
		},

		contents: {
			xml: /\bxml\b/,
			html: /\bhtml/,
			json: /\bjson\b/
		},

		responseFields: {
			xml: "responseXML",
			text: "responseText",
			json: "responseJSON"
		},

		// Data converters
		// Keys separate source (or catchall "*") and destination types with a single space
		converters: {

			// Convert anything to text
			"* text": String,

			// Text to html (true = no transformation)
			"text html": true,

			// Evaluate text as a json expression
			"text json": jQuery.parseJSON,

			// Parse text as xml
			"text xml": jQuery.parseXML
		},

		// For options that shouldn't be deep extended:
		// you can add your own custom options here if
		// and when you create one that shouldn't be
		// deep extended (see ajaxExtend)
		flatOptions: {
			url: true,
			context: true
		}
	},

	// Creates a full fledged settings object into target
	// with both ajaxSettings and settings fields.
	// If target is omitted, writes into ajaxSettings.
	ajaxSetup: function( target, settings ) {
		return settings ?

			// Building a settings object
			ajaxExtend( ajaxExtend( target, jQuery.ajaxSettings ), settings ) :

			// Extending ajaxSettings
			ajaxExtend( jQuery.ajaxSettings, target );
	},

	ajaxPrefilter: addToPrefiltersOrTransports( prefilters ),
	ajaxTransport: addToPrefiltersOrTransports( transports ),

	// Main method
	ajax: function( url, options ) {

		// If url is an object, simulate pre-1.5 signature
		if ( typeof url === "object" ) {
			options = url;
			url = undefined;
		}

		// Force options to be an object
		options = options || {};

		var

			// Cross-domain detection vars
			parts,

			// Loop variable
			i,

			// URL without anti-cache param
			cacheURL,

			// Response headers as string
			responseHeadersString,

			// timeout handle
			timeoutTimer,

			// To know if global events are to be dispatched
			fireGlobals,

			transport,

			// Response headers
			responseHeaders,

			// Create the final options object
			s = jQuery.ajaxSetup( {}, options ),

			// Callbacks context
			callbackContext = s.context || s,

			// Context for global events is callbackContext if it is a DOM node or jQuery collection
			globalEventContext = s.context &&
				( callbackContext.nodeType || callbackContext.jquery ) ?
					jQuery( callbackContext ) :
					jQuery.event,

			// Deferreds
			deferred = jQuery.Deferred(),
			completeDeferred = jQuery.Callbacks( "once memory" ),

			// Status-dependent callbacks
			statusCode = s.statusCode || {},

			// Headers (they are sent all at once)
			requestHeaders = {},
			requestHeadersNames = {},

			// The jqXHR state
			state = 0,

			// Default abort message
			strAbort = "canceled",

			// Fake xhr
			jqXHR = {
				readyState: 0,

				// Builds headers hashtable if needed
				getResponseHeader: function( key ) {
					var match;
					if ( state === 2 ) {
						if ( !responseHeaders ) {
							responseHeaders = {};
							while ( ( match = rheaders.exec( responseHeadersString ) ) ) {
								responseHeaders[ match[ 1 ].toLowerCase() ] = match[ 2 ];
							}
						}
						match = responseHeaders[ key.toLowerCase() ];
					}
					return match == null ? null : match;
				},

				// Raw string
				getAllResponseHeaders: function() {
					return state === 2 ? responseHeadersString : null;
				},

				// Caches the header
				setRequestHeader: function( name, value ) {
					var lname = name.toLowerCase();
					if ( !state ) {
						name = requestHeadersNames[ lname ] = requestHeadersNames[ lname ] || name;
						requestHeaders[ name ] = value;
					}
					return this;
				},

				// Overrides response content-type header
				overrideMimeType: function( type ) {
					if ( !state ) {
						s.mimeType = type;
					}
					return this;
				},

				// Status-dependent callbacks
				statusCode: function( map ) {
					var code;
					if ( map ) {
						if ( state < 2 ) {
							for ( code in map ) {

								// Lazy-add the new callback in a way that preserves old ones
								statusCode[ code ] = [ statusCode[ code ], map[ code ] ];
							}
						} else {

							// Execute the appropriate callbacks
							jqXHR.always( map[ jqXHR.status ] );
						}
					}
					return this;
				},

				// Cancel the request
				abort: function( statusText ) {
					var finalText = statusText || strAbort;
					if ( transport ) {
						transport.abort( finalText );
					}
					done( 0, finalText );
					return this;
				}
			};

		// Attach deferreds
		deferred.promise( jqXHR ).complete = completeDeferred.add;
		jqXHR.success = jqXHR.done;
		jqXHR.error = jqXHR.fail;

		// Remove hash character (#7531: and string promotion)
		// Add protocol if not provided (#5866: IE7 issue with protocol-less urls)
		// Handle falsy url in the settings object (#10093: consistency with old signature)
		// We also use the url parameter if available
		s.url = ( ( url || s.url || ajaxLocation ) + "" )
			.replace( rhash, "" )
			.replace( rprotocol, ajaxLocParts[ 1 ] + "//" );

		// Alias method option to type as per ticket #12004
		s.type = options.method || options.type || s.method || s.type;

		// Extract dataTypes list
		s.dataTypes = jQuery.trim( s.dataType || "*" ).toLowerCase().match( rnotwhite ) || [ "" ];

		// A cross-domain request is in order when we have a protocol:host:port mismatch
		if ( s.crossDomain == null ) {
			parts = rurl.exec( s.url.toLowerCase() );
			s.crossDomain = !!( parts &&
				( parts[ 1 ] !== ajaxLocParts[ 1 ] || parts[ 2 ] !== ajaxLocParts[ 2 ] ||
					( parts[ 3 ] || ( parts[ 1 ] === "http:" ? "80" : "443" ) ) !==
						( ajaxLocParts[ 3 ] || ( ajaxLocParts[ 1 ] === "http:" ? "80" : "443" ) ) )
			);
		}

		// Convert data if not already a string
		if ( s.data && s.processData && typeof s.data !== "string" ) {
			s.data = jQuery.param( s.data, s.traditional );
		}

		// Apply prefilters
		inspectPrefiltersOrTransports( prefilters, s, options, jqXHR );

		// If request was aborted inside a prefilter, stop there
		if ( state === 2 ) {
			return jqXHR;
		}

		// We can fire global events as of now if asked to
		// Don't fire events if jQuery.event is undefined in an AMD-usage scenario (#15118)
		fireGlobals = jQuery.event && s.global;

		// Watch for a new set of requests
		if ( fireGlobals && jQuery.active++ === 0 ) {
			jQuery.event.trigger( "ajaxStart" );
		}

		// Uppercase the type
		s.type = s.type.toUpperCase();

		// Determine if request has content
		s.hasContent = !rnoContent.test( s.type );

		// Save the URL in case we're toying with the If-Modified-Since
		// and/or If-None-Match header later on
		cacheURL = s.url;

		// More options handling for requests with no content
		if ( !s.hasContent ) {

			// If data is available, append data to url
			if ( s.data ) {
				cacheURL = ( s.url += ( rquery.test( cacheURL ) ? "&" : "?" ) + s.data );

				// #9682: remove data so that it's not used in an eventual retry
				delete s.data;
			}

			// Add anti-cache in url if needed
			if ( s.cache === false ) {
				s.url = rts.test( cacheURL ) ?

					// If there is already a '_' parameter, set its value
					cacheURL.replace( rts, "$1_=" + nonce++ ) :

					// Otherwise add one to the end
					cacheURL + ( rquery.test( cacheURL ) ? "&" : "?" ) + "_=" + nonce++;
			}
		}

		// Set the If-Modified-Since and/or If-None-Match header, if in ifModified mode.
		if ( s.ifModified ) {
			if ( jQuery.lastModified[ cacheURL ] ) {
				jqXHR.setRequestHeader( "If-Modified-Since", jQuery.lastModified[ cacheURL ] );
			}
			if ( jQuery.etag[ cacheURL ] ) {
				jqXHR.setRequestHeader( "If-None-Match", jQuery.etag[ cacheURL ] );
			}
		}

		// Set the correct header, if data is being sent
		if ( s.data && s.hasContent && s.contentType !== false || options.contentType ) {
			jqXHR.setRequestHeader( "Content-Type", s.contentType );
		}

		// Set the Accepts header for the server, depending on the dataType
		jqXHR.setRequestHeader(
			"Accept",
			s.dataTypes[ 0 ] && s.accepts[ s.dataTypes[ 0 ] ] ?
				s.accepts[ s.dataTypes[ 0 ] ] +
					( s.dataTypes[ 0 ] !== "*" ? ", " + allTypes + "; q=0.01" : "" ) :
				s.accepts[ "*" ]
		);

		// Check for headers option
		for ( i in s.headers ) {
			jqXHR.setRequestHeader( i, s.headers[ i ] );
		}

		// Allow custom headers/mimetypes and early abort
		if ( s.beforeSend &&
			( s.beforeSend.call( callbackContext, jqXHR, s ) === false || state === 2 ) ) {

			// Abort if not done already and return
			return jqXHR.abort();
		}

		// aborting is no longer a cancellation
		strAbort = "abort";

		// Install callbacks on deferreds
		for ( i in { success: 1, error: 1, complete: 1 } ) {
			jqXHR[ i ]( s[ i ] );
		}

		// Get transport
		transport = inspectPrefiltersOrTransports( transports, s, options, jqXHR );

		// If no transport, we auto-abort
		if ( !transport ) {
			done( -1, "No Transport" );
		} else {
			jqXHR.readyState = 1;

			// Send global event
			if ( fireGlobals ) {
				globalEventContext.trigger( "ajaxSend", [ jqXHR, s ] );
			}

			// If request was aborted inside ajaxSend, stop there
			if ( state === 2 ) {
				return jqXHR;
			}

			// Timeout
			if ( s.async && s.timeout > 0 ) {
				timeoutTimer = window.setTimeout( function() {
					jqXHR.abort( "timeout" );
				}, s.timeout );
			}

			try {
				state = 1;
				transport.send( requestHeaders, done );
			} catch ( e ) {

				// Propagate exception as error if not done
				if ( state < 2 ) {
					done( -1, e );

				// Simply rethrow otherwise
				} else {
					throw e;
				}
			}
		}

		// Callback for when everything is done
		function done( status, nativeStatusText, responses, headers ) {
			var isSuccess, success, error, response, modified,
				statusText = nativeStatusText;

			// Called once
			if ( state === 2 ) {
				return;
			}

			// State is "done" now
			state = 2;

			// Clear timeout if it exists
			if ( timeoutTimer ) {
				window.clearTimeout( timeoutTimer );
			}

			// Dereference transport for early garbage collection
			// (no matter how long the jqXHR object will be used)
			transport = undefined;

			// Cache response headers
			responseHeadersString = headers || "";

			// Set readyState
			jqXHR.readyState = status > 0 ? 4 : 0;

			// Determine if successful
			isSuccess = status >= 200 && status < 300 || status === 304;

			// Get response data
			if ( responses ) {
				response = ajaxHandleResponses( s, jqXHR, responses );
			}

			// Convert no matter what (that way responseXXX fields are always set)
			response = ajaxConvert( s, response, jqXHR, isSuccess );

			// If successful, handle type chaining
			if ( isSuccess ) {

				// Set the If-Modified-Since and/or If-None-Match header, if in ifModified mode.
				if ( s.ifModified ) {
					modified = jqXHR.getResponseHeader( "Last-Modified" );
					if ( modified ) {
						jQuery.lastModified[ cacheURL ] = modified;
					}
					modified = jqXHR.getResponseHeader( "etag" );
					if ( modified ) {
						jQuery.etag[ cacheURL ] = modified;
					}
				}

				// if no content
				if ( status === 204 || s.type === "HEAD" ) {
					statusText = "nocontent";

				// if not modified
				} else if ( status === 304 ) {
					statusText = "notmodified";

				// If we have data, let's convert it
				} else {
					statusText = response.state;
					success = response.data;
					error = response.error;
					isSuccess = !error;
				}
			} else {

				// We extract error from statusText
				// then normalize statusText and status for non-aborts
				error = statusText;
				if ( status || !statusText ) {
					statusText = "error";
					if ( status < 0 ) {
						status = 0;
					}
				}
			}

			// Set data for the fake xhr object
			jqXHR.status = status;
			jqXHR.statusText = ( nativeStatusText || statusText ) + "";

			// Success/Error
			if ( isSuccess ) {
				deferred.resolveWith( callbackContext, [ success, statusText, jqXHR ] );
			} else {
				deferred.rejectWith( callbackContext, [ jqXHR, statusText, error ] );
			}

			// Status-dependent callbacks
			jqXHR.statusCode( statusCode );
			statusCode = undefined;

			if ( fireGlobals ) {
				globalEventContext.trigger( isSuccess ? "ajaxSuccess" : "ajaxError",
					[ jqXHR, s, isSuccess ? success : error ] );
			}

			// Complete
			completeDeferred.fireWith( callbackContext, [ jqXHR, statusText ] );

			if ( fireGlobals ) {
				globalEventContext.trigger( "ajaxComplete", [ jqXHR, s ] );

				// Handle the global AJAX counter
				if ( !( --jQuery.active ) ) {
					jQuery.event.trigger( "ajaxStop" );
				}
			}
		}

		return jqXHR;
	},

	getJSON: function( url, data, callback ) {
		return jQuery.get( url, data, callback, "json" );
	},

	getScript: function( url, callback ) {
		return jQuery.get( url, undefined, callback, "script" );
	}
} );

jQuery.each( [ "get", "post" ], function( i, method ) {
	jQuery[ method ] = function( url, data, callback, type ) {

		// shift arguments if data argument was omitted
		if ( jQuery.isFunction( data ) ) {
			type = type || callback;
			callback = data;
			data = undefined;
		}

		// The url can be an options object (which then must have .url)
		return jQuery.ajax( jQuery.extend( {
			url: url,
			type: method,
			dataType: type,
			data: data,
			success: callback
		}, jQuery.isPlainObject( url ) && url ) );
	};
} );


jQuery._evalUrl = function( url ) {
	return jQuery.ajax( {
		url: url,

		// Make this explicit, since user can override this through ajaxSetup (#11264)
		type: "GET",
		dataType: "script",
		cache: true,
		async: false,
		global: false,
		"throws": true
	} );
};


jQuery.fn.extend( {
	wrapAll: function( html ) {
		if ( jQuery.isFunction( html ) ) {
			return this.each( function( i ) {
				jQuery( this ).wrapAll( html.call( this, i ) );
			} );
		}

		if ( this[ 0 ] ) {

			// The elements to wrap the target around
			var wrap = jQuery( html, this[ 0 ].ownerDocument ).eq( 0 ).clone( true );

			if ( this[ 0 ].parentNode ) {
				wrap.insertBefore( this[ 0 ] );
			}

			wrap.map( function() {
				var elem = this;

				while ( elem.firstChild && elem.firstChild.nodeType === 1 ) {
					elem = elem.firstChild;
				}

				return elem;
			} ).append( this );
		}

		return this;
	},

	wrapInner: function( html ) {
		if ( jQuery.isFunction( html ) ) {
			return this.each( function( i ) {
				jQuery( this ).wrapInner( html.call( this, i ) );
			} );
		}

		return this.each( function() {
			var self = jQuery( this ),
				contents = self.contents();

			if ( contents.length ) {
				contents.wrapAll( html );

			} else {
				self.append( html );
			}
		} );
	},

	wrap: function( html ) {
		var isFunction = jQuery.isFunction( html );

		return this.each( function( i ) {
			jQuery( this ).wrapAll( isFunction ? html.call( this, i ) : html );
		} );
	},

	unwrap: function() {
		return this.parent().each( function() {
			if ( !jQuery.nodeName( this, "body" ) ) {
				jQuery( this ).replaceWith( this.childNodes );
			}
		} ).end();
	}
} );


function getDisplay( elem ) {
	return elem.style && elem.style.display || jQuery.css( elem, "display" );
}

function filterHidden( elem ) {

	// Disconnected elements are considered hidden
	if ( !jQuery.contains( elem.ownerDocument || document, elem ) ) {
		return true;
	}
	while ( elem && elem.nodeType === 1 ) {
		if ( getDisplay( elem ) === "none" || elem.type === "hidden" ) {
			return true;
		}
		elem = elem.parentNode;
	}
	return false;
}

jQuery.expr.filters.hidden = function( elem ) {

	// Support: Opera <= 12.12
	// Opera reports offsetWidths and offsetHeights less than zero on some elements
	return support.reliableHiddenOffsets() ?
		( elem.offsetWidth <= 0 && elem.offsetHeight <= 0 &&
			!elem.getClientRects().length ) :
			filterHidden( elem );
};

jQuery.expr.filters.visible = function( elem ) {
	return !jQuery.expr.filters.hidden( elem );
};




var r20 = /%20/g,
	rbracket = /\[\]$/,
	rCRLF = /\r?\n/g,
	rsubmitterTypes = /^(?:submit|button|image|reset|file)$/i,
	rsubmittable = /^(?:input|select|textarea|keygen)/i;

function buildParams( prefix, obj, traditional, add ) {
	var name;

	if ( jQuery.isArray( obj ) ) {

		// Serialize array item.
		jQuery.each( obj, function( i, v ) {
			if ( traditional || rbracket.test( prefix ) ) {

				// Treat each array item as a scalar.
				add( prefix, v );

			} else {

				// Item is non-scalar (array or object), encode its numeric index.
				buildParams(
					prefix + "[" + ( typeof v === "object" && v != null ? i : "" ) + "]",
					v,
					traditional,
					add
				);
			}
		} );

	} else if ( !traditional && jQuery.type( obj ) === "object" ) {

		// Serialize object item.
		for ( name in obj ) {
			buildParams( prefix + "[" + name + "]", obj[ name ], traditional, add );
		}

	} else {

		// Serialize scalar item.
		add( prefix, obj );
	}
}

// Serialize an array of form elements or a set of
// key/values into a query string
jQuery.param = function( a, traditional ) {
	var prefix,
		s = [],
		add = function( key, value ) {

			// If value is a function, invoke it and return its value
			value = jQuery.isFunction( value ) ? value() : ( value == null ? "" : value );
			s[ s.length ] = encodeURIComponent( key ) + "=" + encodeURIComponent( value );
		};

	// Set traditional to true for jQuery <= 1.3.2 behavior.
	if ( traditional === undefined ) {
		traditional = jQuery.ajaxSettings && jQuery.ajaxSettings.traditional;
	}

	// If an array was passed in, assume that it is an array of form elements.
	if ( jQuery.isArray( a ) || ( a.jquery && !jQuery.isPlainObject( a ) ) ) {

		// Serialize the form elements
		jQuery.each( a, function() {
			add( this.name, this.value );
		} );

	} else {

		// If traditional, encode the "old" way (the way 1.3.2 or older
		// did it), otherwise encode params recursively.
		for ( prefix in a ) {
			buildParams( prefix, a[ prefix ], traditional, add );
		}
	}

	// Return the resulting serialization
	return s.join( "&" ).replace( r20, "+" );
};

jQuery.fn.extend( {
	serialize: function() {
		return jQuery.param( this.serializeArray() );
	},
	serializeArray: function() {
		return this.map( function() {

			// Can add propHook for "elements" to filter or add form elements
			var elements = jQuery.prop( this, "elements" );
			return elements ? jQuery.makeArray( elements ) : this;
		} )
		.filter( function() {
			var type = this.type;

			// Use .is(":disabled") so that fieldset[disabled] works
			return this.name && !jQuery( this ).is( ":disabled" ) &&
				rsubmittable.test( this.nodeName ) && !rsubmitterTypes.test( type ) &&
				( this.checked || !rcheckableType.test( type ) );
		} )
		.map( function( i, elem ) {
			var val = jQuery( this ).val();

			return val == null ?
				null :
				jQuery.isArray( val ) ?
					jQuery.map( val, function( val ) {
						return { name: elem.name, value: val.replace( rCRLF, "\r\n" ) };
					} ) :
					{ name: elem.name, value: val.replace( rCRLF, "\r\n" ) };
		} ).get();
	}
} );


// Create the request object
// (This is still attached to ajaxSettings for backward compatibility)
jQuery.ajaxSettings.xhr = window.ActiveXObject !== undefined ?

	// Support: IE6-IE8
	function() {

		// XHR cannot access local files, always use ActiveX for that case
		if ( this.isLocal ) {
			return createActiveXHR();
		}

		// Support: IE 9-11
		// IE seems to error on cross-domain PATCH requests when ActiveX XHR
		// is used. In IE 9+ always use the native XHR.
		// Note: this condition won't catch Edge as it doesn't define
		// document.documentMode but it also doesn't support ActiveX so it won't
		// reach this code.
		if ( document.documentMode > 8 ) {
			return createStandardXHR();
		}

		// Support: IE<9
		// oldIE XHR does not support non-RFC2616 methods (#13240)
		// See http://msdn.microsoft.com/en-us/library/ie/ms536648(v=vs.85).aspx
		// and http://www.w3.org/Protocols/rfc2616/rfc2616-sec9.html#sec9
		// Although this check for six methods instead of eight
		// since IE also does not support "trace" and "connect"
		return /^(get|post|head|put|delete|options)$/i.test( this.type ) &&
			createStandardXHR() || createActiveXHR();
	} :

	// For all other browsers, use the standard XMLHttpRequest object
	createStandardXHR;

var xhrId = 0,
	xhrCallbacks = {},
	xhrSupported = jQuery.ajaxSettings.xhr();

// Support: IE<10
// Open requests must be manually aborted on unload (#5280)
// See https://support.microsoft.com/kb/2856746 for more info
if ( window.attachEvent ) {
	window.attachEvent( "onunload", function() {
		for ( var key in xhrCallbacks ) {
			xhrCallbacks[ key ]( undefined, true );
		}
	} );
}

// Determine support properties
support.cors = !!xhrSupported && ( "withCredentials" in xhrSupported );
xhrSupported = support.ajax = !!xhrSupported;

// Create transport if the browser can provide an xhr
if ( xhrSupported ) {

	jQuery.ajaxTransport( function( options ) {

		// Cross domain only allowed if supported through XMLHttpRequest
		if ( !options.crossDomain || support.cors ) {

			var callback;

			return {
				send: function( headers, complete ) {
					var i,
						xhr = options.xhr(),
						id = ++xhrId;

					// Open the socket
					xhr.open(
						options.type,
						options.url,
						options.async,
						options.username,
						options.password
					);

					// Apply custom fields if provided
					if ( options.xhrFields ) {
						for ( i in options.xhrFields ) {
							xhr[ i ] = options.xhrFields[ i ];
						}
					}

					// Override mime type if needed
					if ( options.mimeType && xhr.overrideMimeType ) {
						xhr.overrideMimeType( options.mimeType );
					}

					// X-Requested-With header
					// For cross-domain requests, seeing as conditions for a preflight are
					// akin to a jigsaw puzzle, we simply never set it to be sure.
					// (it can always be set on a per-request basis or even using ajaxSetup)
					// For same-domain requests, won't change header if already provided.
					if ( !options.crossDomain && !headers[ "X-Requested-With" ] ) {
						headers[ "X-Requested-With" ] = "XMLHttpRequest";
					}

					// Set headers
					for ( i in headers ) {

						// Support: IE<9
						// IE's ActiveXObject throws a 'Type Mismatch' exception when setting
						// request header to a null-value.
						//
						// To keep consistent with other XHR implementations, cast the value
						// to string and ignore `undefined`.
						if ( headers[ i ] !== undefined ) {
							xhr.setRequestHeader( i, headers[ i ] + "" );
						}
					}

					// Do send the request
					// This may raise an exception which is actually
					// handled in jQuery.ajax (so no try/catch here)
					xhr.send( ( options.hasContent && options.data ) || null );

					// Listener
					callback = function( _, isAbort ) {
						var status, statusText, responses;

						// Was never called and is aborted or complete
						if ( callback && ( isAbort || xhr.readyState === 4 ) ) {

							// Clean up
							delete xhrCallbacks[ id ];
							callback = undefined;
							xhr.onreadystatechange = jQuery.noop;

							// Abort manually if needed
							if ( isAbort ) {
								if ( xhr.readyState !== 4 ) {
									xhr.abort();
								}
							} else {
								responses = {};
								status = xhr.status;

								// Support: IE<10
								// Accessing binary-data responseText throws an exception
								// (#11426)
								if ( typeof xhr.responseText === "string" ) {
									responses.text = xhr.responseText;
								}

								// Firefox throws an exception when accessing
								// statusText for faulty cross-domain requests
								try {
									statusText = xhr.statusText;
								} catch ( e ) {

									// We normalize with Webkit giving an empty statusText
									statusText = "";
								}

								// Filter status for non standard behaviors

								// If the request is local and we have data: assume a success
								// (success with no data won't get notified, that's the best we
								// can do given current implementations)
								if ( !status && options.isLocal && !options.crossDomain ) {
									status = responses.text ? 200 : 404;

								// IE - #1450: sometimes returns 1223 when it should be 204
								} else if ( status === 1223 ) {
									status = 204;
								}
							}
						}

						// Call complete if needed
						if ( responses ) {
							complete( status, statusText, responses, xhr.getAllResponseHeaders() );
						}
					};

					// Do send the request
					// `xhr.send` may raise an exception, but it will be
					// handled in jQuery.ajax (so no try/catch here)
					if ( !options.async ) {

						// If we're in sync mode we fire the callback
						callback();
					} else if ( xhr.readyState === 4 ) {

						// (IE6 & IE7) if it's in cache and has been
						// retrieved directly we need to fire the callback
						window.setTimeout( callback );
					} else {

						// Register the callback, but delay it in case `xhr.send` throws
						// Add to the list of active xhr callbacks
						xhr.onreadystatechange = xhrCallbacks[ id ] = callback;
					}
				},

				abort: function() {
					if ( callback ) {
						callback( undefined, true );
					}
				}
			};
		}
	} );
}

// Functions to create xhrs
function createStandardXHR() {
	try {
		return new window.XMLHttpRequest();
	} catch ( e ) {}
}

function createActiveXHR() {
	try {
		return new window.ActiveXObject( "Microsoft.XMLHTTP" );
	} catch ( e ) {}
}




// Install script dataType
jQuery.ajaxSetup( {
	accepts: {
		script: "text/javascript, application/javascript, " +
			"application/ecmascript, application/x-ecmascript"
	},
	contents: {
		script: /\b(?:java|ecma)script\b/
	},
	converters: {
		"text script": function( text ) {
			jQuery.globalEval( text );
			return text;
		}
	}
} );

// Handle cache's special case and global
jQuery.ajaxPrefilter( "script", function( s ) {
	if ( s.cache === undefined ) {
		s.cache = false;
	}
	if ( s.crossDomain ) {
		s.type = "GET";
		s.global = false;
	}
} );

// Bind script tag hack transport
jQuery.ajaxTransport( "script", function( s ) {

	// This transport only deals with cross domain requests
	if ( s.crossDomain ) {

		var script,
			head = document.head || jQuery( "head" )[ 0 ] || document.documentElement;

		return {

			send: function( _, callback ) {

				script = document.createElement( "script" );

				script.async = true;

				if ( s.scriptCharset ) {
					script.charset = s.scriptCharset;
				}

				script.src = s.url;

				// Attach handlers for all browsers
				script.onload = script.onreadystatechange = function( _, isAbort ) {

					if ( isAbort || !script.readyState || /loaded|complete/.test( script.readyState ) ) {

						// Handle memory leak in IE
						script.onload = script.onreadystatechange = null;

						// Remove the script
						if ( script.parentNode ) {
							script.parentNode.removeChild( script );
						}

						// Dereference the script
						script = null;

						// Callback if not abort
						if ( !isAbort ) {
							callback( 200, "success" );
						}
					}
				};

				// Circumvent IE6 bugs with base elements (#2709 and #4378) by prepending
				// Use native DOM manipulation to avoid our domManip AJAX trickery
				head.insertBefore( script, head.firstChild );
			},

			abort: function() {
				if ( script ) {
					script.onload( undefined, true );
				}
			}
		};
	}
} );




var oldCallbacks = [],
	rjsonp = /(=)\?(?=&|$)|\?\?/;

// Default jsonp settings
jQuery.ajaxSetup( {
	jsonp: "callback",
	jsonpCallback: function() {
		var callback = oldCallbacks.pop() || ( jQuery.expando + "_" + ( nonce++ ) );
		this[ callback ] = true;
		return callback;
	}
} );

// Detect, normalize options and install callbacks for jsonp requests
jQuery.ajaxPrefilter( "json jsonp", function( s, originalSettings, jqXHR ) {

	var callbackName, overwritten, responseContainer,
		jsonProp = s.jsonp !== false && ( rjsonp.test( s.url ) ?
			"url" :
			typeof s.data === "string" &&
				( s.contentType || "" )
					.indexOf( "application/x-www-form-urlencoded" ) === 0 &&
				rjsonp.test( s.data ) && "data"
		);

	// Handle iff the expected data type is "jsonp" or we have a parameter to set
	if ( jsonProp || s.dataTypes[ 0 ] === "jsonp" ) {

		// Get callback name, remembering preexisting value associated with it
		callbackName = s.jsonpCallback = jQuery.isFunction( s.jsonpCallback ) ?
			s.jsonpCallback() :
			s.jsonpCallback;

		// Insert callback into url or form data
		if ( jsonProp ) {
			s[ jsonProp ] = s[ jsonProp ].replace( rjsonp, "$1" + callbackName );
		} else if ( s.jsonp !== false ) {
			s.url += ( rquery.test( s.url ) ? "&" : "?" ) + s.jsonp + "=" + callbackName;
		}

		// Use data converter to retrieve json after script execution
		s.converters[ "script json" ] = function() {
			if ( !responseContainer ) {
				jQuery.error( callbackName + " was not called" );
			}
			return responseContainer[ 0 ];
		};

		// force json dataType
		s.dataTypes[ 0 ] = "json";

		// Install callback
		overwritten = window[ callbackName ];
		window[ callbackName ] = function() {
			responseContainer = arguments;
		};

		// Clean-up function (fires after converters)
		jqXHR.always( function() {

			// If previous value didn't exist - remove it
			if ( overwritten === undefined ) {
				jQuery( window ).removeProp( callbackName );

			// Otherwise restore preexisting value
			} else {
				window[ callbackName ] = overwritten;
			}

			// Save back as free
			if ( s[ callbackName ] ) {

				// make sure that re-using the options doesn't screw things around
				s.jsonpCallback = originalSettings.jsonpCallback;

				// save the callback name for future use
				oldCallbacks.push( callbackName );
			}

			// Call if it was a function and we have a response
			if ( responseContainer && jQuery.isFunction( overwritten ) ) {
				overwritten( responseContainer[ 0 ] );
			}

			responseContainer = overwritten = undefined;
		} );

		// Delegate to script
		return "script";
	}
} );




// data: string of html
// context (optional): If specified, the fragment will be created in this context,
// defaults to document
// keepScripts (optional): If true, will include scripts passed in the html string
jQuery.parseHTML = function( data, context, keepScripts ) {
	if ( !data || typeof data !== "string" ) {
		return null;
	}
	if ( typeof context === "boolean" ) {
		keepScripts = context;
		context = false;
	}
	context = context || document;

	var parsed = rsingleTag.exec( data ),
		scripts = !keepScripts && [];

	// Single tag
	if ( parsed ) {
		return [ context.createElement( parsed[ 1 ] ) ];
	}

	parsed = buildFragment( [ data ], context, scripts );

	if ( scripts && scripts.length ) {
		jQuery( scripts ).remove();
	}

	return jQuery.merge( [], parsed.childNodes );
};


// Keep a copy of the old load method
var _load = jQuery.fn.load;

/**
 * Load a url into a page
 */
jQuery.fn.load = function( url, params, callback ) {
	if ( typeof url !== "string" && _load ) {
		return _load.apply( this, arguments );
	}

	var selector, type, response,
		self = this,
		off = url.indexOf( " " );

	if ( off > -1 ) {
		selector = jQuery.trim( url.slice( off, url.length ) );
		url = url.slice( 0, off );
	}

	// If it's a function
	if ( jQuery.isFunction( params ) ) {

		// We assume that it's the callback
		callback = params;
		params = undefined;

	// Otherwise, build a param string
	} else if ( params && typeof params === "object" ) {
		type = "POST";
	}

	// If we have elements to modify, make the request
	if ( self.length > 0 ) {
		jQuery.ajax( {
			url: url,

			// If "type" variable is undefined, then "GET" method will be used.
			// Make value of this field explicit since
			// user can override it through ajaxSetup method
			type: type || "GET",
			dataType: "html",
			data: params
		} ).done( function( responseText ) {

			// Save response for use in complete callback
			response = arguments;

			self.html( selector ?

				// If a selector was specified, locate the right elements in a dummy div
				// Exclude scripts to avoid IE 'Permission Denied' errors
				jQuery( "<div>" ).append( jQuery.parseHTML( responseText ) ).find( selector ) :

				// Otherwise use the full result
				responseText );

		// If the request succeeds, this function gets "data", "status", "jqXHR"
		// but they are ignored because response was set above.
		// If it fails, this function gets "jqXHR", "status", "error"
		} ).always( callback && function( jqXHR, status ) {
			self.each( function() {
				callback.apply( this, response || [ jqXHR.responseText, status, jqXHR ] );
			} );
		} );
	}

	return this;
};




// Attach a bunch of functions for handling common AJAX events
jQuery.each( [
	"ajaxStart",
	"ajaxStop",
	"ajaxComplete",
	"ajaxError",
	"ajaxSuccess",
	"ajaxSend"
], function( i, type ) {
	jQuery.fn[ type ] = function( fn ) {
		return this.on( type, fn );
	};
} );




jQuery.expr.filters.animated = function( elem ) {
	return jQuery.grep( jQuery.timers, function( fn ) {
		return elem === fn.elem;
	} ).length;
};





/**
 * Gets a window from an element
 */
function getWindow( elem ) {
	return jQuery.isWindow( elem ) ?
		elem :
		elem.nodeType === 9 ?
			elem.defaultView || elem.parentWindow :
			false;
}

jQuery.offset = {
	setOffset: function( elem, options, i ) {
		var curPosition, curLeft, curCSSTop, curTop, curOffset, curCSSLeft, calculatePosition,
			position = jQuery.css( elem, "position" ),
			curElem = jQuery( elem ),
			props = {};

		// set position first, in-case top/left are set even on static elem
		if ( position === "static" ) {
			elem.style.position = "relative";
		}

		curOffset = curElem.offset();
		curCSSTop = jQuery.css( elem, "top" );
		curCSSLeft = jQuery.css( elem, "left" );
		calculatePosition = ( position === "absolute" || position === "fixed" ) &&
			jQuery.inArray( "auto", [ curCSSTop, curCSSLeft ] ) > -1;

		// need to be able to calculate position if either top or left
		// is auto and position is either absolute or fixed
		if ( calculatePosition ) {
			curPosition = curElem.position();
			curTop = curPosition.top;
			curLeft = curPosition.left;
		} else {
			curTop = parseFloat( curCSSTop ) || 0;
			curLeft = parseFloat( curCSSLeft ) || 0;
		}

		if ( jQuery.isFunction( options ) ) {

			// Use jQuery.extend here to allow modification of coordinates argument (gh-1848)
			options = options.call( elem, i, jQuery.extend( {}, curOffset ) );
		}

		if ( options.top != null ) {
			props.top = ( options.top - curOffset.top ) + curTop;
		}
		if ( options.left != null ) {
			props.left = ( options.left - curOffset.left ) + curLeft;
		}

		if ( "using" in options ) {
			options.using.call( elem, props );
		} else {
			curElem.css( props );
		}
	}
};

jQuery.fn.extend( {
	offset: function( options ) {
		if ( arguments.length ) {
			return options === undefined ?
				this :
				this.each( function( i ) {
					jQuery.offset.setOffset( this, options, i );
				} );
		}

		var docElem, win,
			box = { top: 0, left: 0 },
			elem = this[ 0 ],
			doc = elem && elem.ownerDocument;

		if ( !doc ) {
			return;
		}

		docElem = doc.documentElement;

		// Make sure it's not a disconnected DOM node
		if ( !jQuery.contains( docElem, elem ) ) {
			return box;
		}

		// If we don't have gBCR, just use 0,0 rather than error
		// BlackBerry 5, iOS 3 (original iPhone)
		if ( typeof elem.getBoundingClientRect !== "undefined" ) {
			box = elem.getBoundingClientRect();
		}
		win = getWindow( doc );
		return {
			top: box.top  + ( win.pageYOffset || docElem.scrollTop )  - ( docElem.clientTop  || 0 ),
			left: box.left + ( win.pageXOffset || docElem.scrollLeft ) - ( docElem.clientLeft || 0 )
		};
	},

	position: function() {
		if ( !this[ 0 ] ) {
			return;
		}

		var offsetParent, offset,
			parentOffset = { top: 0, left: 0 },
			elem = this[ 0 ];

		// Fixed elements are offset from window (parentOffset = {top:0, left: 0},
		// because it is its only offset parent
		if ( jQuery.css( elem, "position" ) === "fixed" ) {

			// we assume that getBoundingClientRect is available when computed position is fixed
			offset = elem.getBoundingClientRect();
		} else {

			// Get *real* offsetParent
			offsetParent = this.offsetParent();

			// Get correct offsets
			offset = this.offset();
			if ( !jQuery.nodeName( offsetParent[ 0 ], "html" ) ) {
				parentOffset = offsetParent.offset();
			}

			// Add offsetParent borders
			parentOffset.top  += jQuery.css( offsetParent[ 0 ], "borderTopWidth", true );
			parentOffset.left += jQuery.css( offsetParent[ 0 ], "borderLeftWidth", true );
		}

		// Subtract parent offsets and element margins
		// note: when an element has margin: auto the offsetLeft and marginLeft
		// are the same in Safari causing offset.left to incorrectly be 0
		return {
			top:  offset.top  - parentOffset.top - jQuery.css( elem, "marginTop", true ),
			left: offset.left - parentOffset.left - jQuery.css( elem, "marginLeft", true )
		};
	},

	offsetParent: function() {
		return this.map( function() {
			var offsetParent = this.offsetParent;

			while ( offsetParent && ( !jQuery.nodeName( offsetParent, "html" ) &&
				jQuery.css( offsetParent, "position" ) === "static" ) ) {
				offsetParent = offsetParent.offsetParent;
			}
			return offsetParent || documentElement;
		} );
	}
} );

// Create scrollLeft and scrollTop methods
jQuery.each( { scrollLeft: "pageXOffset", scrollTop: "pageYOffset" }, function( method, prop ) {
	var top = /Y/.test( prop );

	jQuery.fn[ method ] = function( val ) {
		return access( this, function( elem, method, val ) {
			var win = getWindow( elem );

			if ( val === undefined ) {
				return win ? ( prop in win ) ? win[ prop ] :
					win.document.documentElement[ method ] :
					elem[ method ];
			}

			if ( win ) {
				win.scrollTo(
					!top ? val : jQuery( win ).scrollLeft(),
					top ? val : jQuery( win ).scrollTop()
				);

			} else {
				elem[ method ] = val;
			}
		}, method, val, arguments.length, null );
	};
} );

// Support: Safari<7-8+, Chrome<37-44+
// Add the top/left cssHooks using jQuery.fn.position
// Webkit bug: https://bugs.webkit.org/show_bug.cgi?id=29084
// getComputedStyle returns percent when specified for top/left/bottom/right
// rather than make the css module depend on the offset module, we just check for it here
jQuery.each( [ "top", "left" ], function( i, prop ) {
	jQuery.cssHooks[ prop ] = addGetHookIf( support.pixelPosition,
		function( elem, computed ) {
			if ( computed ) {
				computed = curCSS( elem, prop );

				// if curCSS returns percentage, fallback to offset
				return rnumnonpx.test( computed ) ?
					jQuery( elem ).position()[ prop ] + "px" :
					computed;
			}
		}
	);
} );


// Create innerHeight, innerWidth, height, width, outerHeight and outerWidth methods
jQuery.each( { Height: "height", Width: "width" }, function( name, type ) {
	jQuery.each( { padding: "inner" + name, content: type, "": "outer" + name },
	function( defaultExtra, funcName ) {

		// margin is only for outerHeight, outerWidth
		jQuery.fn[ funcName ] = function( margin, value ) {
			var chainable = arguments.length && ( defaultExtra || typeof margin !== "boolean" ),
				extra = defaultExtra || ( margin === true || value === true ? "margin" : "border" );

			return access( this, function( elem, type, value ) {
				var doc;

				if ( jQuery.isWindow( elem ) ) {

					// As of 5/8/2012 this will yield incorrect results for Mobile Safari, but there
					// isn't a whole lot we can do. See pull request at this URL for discussion:
					// https://github.com/jquery/jquery/pull/764
					return elem.document.documentElement[ "client" + name ];
				}

				// Get document width or height
				if ( elem.nodeType === 9 ) {
					doc = elem.documentElement;

					// Either scroll[Width/Height] or offset[Width/Height] or client[Width/Height],
					// whichever is greatest
					// unfortunately, this causes bug #3838 in IE6/8 only,
					// but there is currently no good, small way to fix it.
					return Math.max(
						elem.body[ "scroll" + name ], doc[ "scroll" + name ],
						elem.body[ "offset" + name ], doc[ "offset" + name ],
						doc[ "client" + name ]
					);
				}

				return value === undefined ?

					// Get width or height on the element, requesting but not forcing parseFloat
					jQuery.css( elem, type, extra ) :

					// Set width or height on the element
					jQuery.style( elem, type, value, extra );
			}, type, chainable ? margin : undefined, chainable, null );
		};
	} );
} );


jQuery.fn.extend( {

	bind: function( types, data, fn ) {
		return this.on( types, null, data, fn );
	},
	unbind: function( types, fn ) {
		return this.off( types, null, fn );
	},

	delegate: function( selector, types, data, fn ) {
		return this.on( types, selector, data, fn );
	},
	undelegate: function( selector, types, fn ) {

		// ( namespace ) or ( selector, types [, fn] )
		return arguments.length === 1 ?
			this.off( selector, "**" ) :
			this.off( types, selector || "**", fn );
	}
} );

// The number of elements contained in the matched element set
jQuery.fn.size = function() {
	return this.length;
};

jQuery.fn.andSelf = jQuery.fn.addBack;




// Register as a named AMD module, since jQuery can be concatenated with other
// files that may use define, but not via a proper concatenation script that
// understands anonymous AMD modules. A named AMD is safest and most robust
// way to register. Lowercase jquery is used because AMD module names are
// derived from file names, and jQuery is normally delivered in a lowercase
// file name. Do this after creating the global so that if an AMD module wants
// to call noConflict to hide this version of jQuery, it will work.

// Note that for maximum portability, libraries that are not jQuery should
// declare themselves as anonymous modules, and avoid setting a global if an
// AMD loader is present. jQuery is a special case. For more information, see
// https://github.com/jrburke/requirejs/wiki/Updating-existing-libraries#wiki-anon

if ( typeof define === "function" && define.amd ) {
	define( "jquery", [], function() {
		return jQuery;
	} );
}



var

	// Map over jQuery in case of overwrite
	_jQuery = window.jQuery,

	// Map over the $ in case of overwrite
	_$ = window.$;

jQuery.noConflict = function( deep ) {
	if ( window.$ === jQuery ) {
		window.$ = _$;
	}

	if ( deep && window.jQuery === jQuery ) {
		window.jQuery = _jQuery;
	}

	return jQuery;
};

// Expose jQuery and $ identifiers, even in
// AMD (#7102#comment:10, https://github.com/jquery/jquery/pull/557)
// and CommonJS for browser emulators (#13566)
if ( !noGlobal ) {
	window.jQuery = window.$ = jQuery;
}

return jQuery;
}));
(function($, undefined) {

/**
 * Unobtrusive scripting adapter for jQuery
 * https://github.com/rails/jquery-ujs
 *
 * Requires jQuery 1.8.0 or later.
 *
 * Released under the MIT license
 *
 */

  // Cut down on the number of issues from people inadvertently including jquery_ujs twice
  // by detecting and raising an error when it happens.
  'use strict';

  if ( $.rails !== undefined ) {
    $.error('jquery-ujs has already been loaded!');
  }

  // Shorthand to make it a little easier to call public rails functions from within rails.js
  var rails;
  var $document = $(document);

  $.rails = rails = {
    // Link elements bound by jquery-ujs
    linkClickSelector: 'a[data-confirm], a[data-method], a[data-remote]:not([disabled]), a[data-disable-with], a[data-disable]',

    // Button elements bound by jquery-ujs
    buttonClickSelector: 'button[data-remote]:not([form]):not(form button), button[data-confirm]:not([form]):not(form button)',

    // Select elements bound by jquery-ujs
    inputChangeSelector: 'select[data-remote], input[data-remote], textarea[data-remote]',

    // Form elements bound by jquery-ujs
    formSubmitSelector: 'form',

    // Form input elements bound by jquery-ujs
    formInputClickSelector: 'form input[type=submit], form input[type=image], form button[type=submit], form button:not([type]), input[type=submit][form], input[type=image][form], button[type=submit][form], button[form]:not([type])',

    // Form input elements disabled during form submission
    disableSelector: 'input[data-disable-with]:enabled, button[data-disable-with]:enabled, textarea[data-disable-with]:enabled, input[data-disable]:enabled, button[data-disable]:enabled, textarea[data-disable]:enabled',

    // Form input elements re-enabled after form submission
    enableSelector: 'input[data-disable-with]:disabled, button[data-disable-with]:disabled, textarea[data-disable-with]:disabled, input[data-disable]:disabled, button[data-disable]:disabled, textarea[data-disable]:disabled',

    // Form required input elements
    requiredInputSelector: 'input[name][required]:not([disabled]), textarea[name][required]:not([disabled])',

    // Form file input elements
    fileInputSelector: 'input[name][type=file]:not([disabled])',

    // Link onClick disable selector with possible reenable after remote submission
    linkDisableSelector: 'a[data-disable-with], a[data-disable]',

    // Button onClick disable selector with possible reenable after remote submission
    buttonDisableSelector: 'button[data-remote][data-disable-with], button[data-remote][data-disable]',

    // Up-to-date Cross-Site Request Forgery token
    csrfToken: function() {
     return $('meta[name=csrf-token]').attr('content');
    },

    // URL param that must contain the CSRF token
    csrfParam: function() {
     return $('meta[name=csrf-param]').attr('content');
    },

    // Make sure that every Ajax request sends the CSRF token
    CSRFProtection: function(xhr) {
      var token = rails.csrfToken();
      if (token) xhr.setRequestHeader('X-CSRF-Token', token);
    },

    // Make sure that all forms have actual up-to-date tokens (cached forms contain old ones)
    refreshCSRFTokens: function(){
      $('form input[name="' + rails.csrfParam() + '"]').val(rails.csrfToken());
    },

    // Triggers an event on an element and returns false if the event result is false
    fire: function(obj, name, data) {
      var event = $.Event(name);
      obj.trigger(event, data);
      return event.result !== false;
    },

    // Default confirm dialog, may be overridden with custom confirm dialog in $.rails.confirm
    confirm: function(message) {
      return confirm(message);
    },

    // Default ajax function, may be overridden with custom function in $.rails.ajax
    ajax: function(options) {
      return $.ajax(options);
    },

    // Default way to get an element's href. May be overridden at $.rails.href.
    href: function(element) {
      return element[0].href;
    },

    // Checks "data-remote" if true to handle the request through a XHR request.
    isRemote: function(element) {
      return element.data('remote') !== undefined && element.data('remote') !== false;
    },

    // Submits "remote" forms and links with ajax
    handleRemote: function(element) {
      var method, url, data, withCredentials, dataType, options;

      if (rails.fire(element, 'ajax:before')) {
        withCredentials = element.data('with-credentials') || null;
        dataType = element.data('type') || ($.ajaxSettings && $.ajaxSettings.dataType);

        if (element.is('form')) {
          method = element.data('ujs:submit-button-formmethod') || element.attr('method');
          url = element.data('ujs:submit-button-formaction') || element.attr('action');
          data = $(element[0]).serializeArray();
          // memoized value from clicked submit button
          var button = element.data('ujs:submit-button');
          if (button) {
            data.push(button);
            element.data('ujs:submit-button', null);
          }
          element.data('ujs:submit-button-formmethod', null);
          element.data('ujs:submit-button-formaction', null);
        } else if (element.is(rails.inputChangeSelector)) {
          method = element.data('method');
          url = element.data('url');
          data = element.serialize();
          if (element.data('params')) data = data + '&' + element.data('params');
        } else if (element.is(rails.buttonClickSelector)) {
          method = element.data('method') || 'get';
          url = element.data('url');
          data = element.serialize();
          if (element.data('params')) data = data + '&' + element.data('params');
        } else {
          method = element.data('method');
          url = rails.href(element);
          data = element.data('params') || null;
        }

        options = {
          type: method || 'GET', data: data, dataType: dataType,
          // stopping the "ajax:beforeSend" event will cancel the ajax request
          beforeSend: function(xhr, settings) {
            if (settings.dataType === undefined) {
              xhr.setRequestHeader('accept', '*/*;q=0.5, ' + settings.accepts.script);
            }
            if (rails.fire(element, 'ajax:beforeSend', [xhr, settings])) {
              element.trigger('ajax:send', xhr);
            } else {
              return false;
            }
          },
          success: function(data, status, xhr) {
            element.trigger('ajax:success', [data, status, xhr]);
          },
          complete: function(xhr, status) {
            element.trigger('ajax:complete', [xhr, status]);
          },
          error: function(xhr, status, error) {
            element.trigger('ajax:error', [xhr, status, error]);
          },
          crossDomain: rails.isCrossDomain(url)
        };

        // There is no withCredentials for IE6-8 when
        // "Enable native XMLHTTP support" is disabled
        if (withCredentials) {
          options.xhrFields = {
            withCredentials: withCredentials
          };
        }

        // Only pass url to `ajax` options if not blank
        if (url) { options.url = url; }

        return rails.ajax(options);
      } else {
        return false;
      }
    },

    // Determines if the request is a cross domain request.
    isCrossDomain: function(url) {
      var originAnchor = document.createElement('a');
      originAnchor.href = location.href;
      var urlAnchor = document.createElement('a');

      try {
        urlAnchor.href = url;
        // This is a workaround to a IE bug.
        urlAnchor.href = urlAnchor.href;

        // If URL protocol is false or is a string containing a single colon
        // *and* host are false, assume it is not a cross-domain request
        // (should only be the case for IE7 and IE compatibility mode).
        // Otherwise, evaluate protocol and host of the URL against the origin
        // protocol and host.
        return !(((!urlAnchor.protocol || urlAnchor.protocol === ':') && !urlAnchor.host) ||
          (originAnchor.protocol + '//' + originAnchor.host ===
            urlAnchor.protocol + '//' + urlAnchor.host));
      } catch (e) {
        // If there is an error parsing the URL, assume it is crossDomain.
        return true;
      }
    },

    // Handles "data-method" on links such as:
    // <a href="/users/5" data-method="delete" rel="nofollow" data-confirm="Are you sure?">Delete</a>
    handleMethod: function(link) {
      var href = rails.href(link),
        method = link.data('method'),
        target = link.attr('target'),
        csrfToken = rails.csrfToken(),
        csrfParam = rails.csrfParam(),
        form = $('<form method="post" action="' + href + '"></form>'),
        metadataInput = '<input name="_method" value="' + method + '" type="hidden" />';

      if (csrfParam !== undefined && csrfToken !== undefined && !rails.isCrossDomain(href)) {
        metadataInput += '<input name="' + csrfParam + '" value="' + csrfToken + '" type="hidden" />';
      }

      if (target) { form.attr('target', target); }

      form.hide().append(metadataInput).appendTo('body');
      form.submit();
    },

    // Helper function that returns form elements that match the specified CSS selector
    // If form is actually a "form" element this will return associated elements outside the from that have
    // the html form attribute set
    formElements: function(form, selector) {
      return form.is('form') ? $(form[0].elements).filter(selector) : form.find(selector);
    },

    /* Disables form elements:
      - Caches element value in 'ujs:enable-with' data store
      - Replaces element text with value of 'data-disable-with' attribute
      - Sets disabled property to true
    */
    disableFormElements: function(form) {
      rails.formElements(form, rails.disableSelector).each(function() {
        rails.disableFormElement($(this));
      });
    },

    disableFormElement: function(element) {
      var method, replacement;

      method = element.is('button') ? 'html' : 'val';
      replacement = element.data('disable-with');

      if (replacement !== undefined) {
        element.data('ujs:enable-with', element[method]());
        element[method](replacement);
      }

      element.prop('disabled', true);
      element.data('ujs:disabled', true);
    },

    /* Re-enables disabled form elements:
      - Replaces element text with cached value from 'ujs:enable-with' data store (created in `disableFormElements`)
      - Sets disabled property to false
    */
    enableFormElements: function(form) {
      rails.formElements(form, rails.enableSelector).each(function() {
        rails.enableFormElement($(this));
      });
    },

    enableFormElement: function(element) {
      var method = element.is('button') ? 'html' : 'val';
      if (element.data('ujs:enable-with') !== undefined) {
        element[method](element.data('ujs:enable-with'));
        element.removeData('ujs:enable-with'); // clean up cache
      }
      element.prop('disabled', false);
      element.removeData('ujs:disabled');
    },

   /* For 'data-confirm' attribute:
      - Fires `confirm` event
      - Shows the confirmation dialog
      - Fires the `confirm:complete` event

      Returns `true` if no function stops the chain and user chose yes; `false` otherwise.
      Attaching a handler to the element's `confirm` event that returns a `falsy` value cancels the confirmation dialog.
      Attaching a handler to the element's `confirm:complete` event that returns a `falsy` value makes this function
      return false. The `confirm:complete` event is fired whether or not the user answered true or false to the dialog.
   */
    allowAction: function(element) {
      var message = element.data('confirm'),
          answer = false, callback;
      if (!message) { return true; }

      if (rails.fire(element, 'confirm')) {
        try {
          answer = rails.confirm(message);
        } catch (e) {
          (console.error || console.log).call(console, e.stack || e);
        }
        callback = rails.fire(element, 'confirm:complete', [answer]);
      }
      return answer && callback;
    },

    // Helper function which checks for blank inputs in a form that match the specified CSS selector
    blankInputs: function(form, specifiedSelector, nonBlank) {
      var foundInputs = $(),
        input,
        valueToCheck,
        radiosForNameWithNoneSelected,
        radioName,
        selector = specifiedSelector || 'input,textarea',
        requiredInputs = form.find(selector),
        checkedRadioButtonNames = {};

      requiredInputs.each(function() {
        input = $(this);
        if (input.is('input[type=radio]')) {

          // Don't count unchecked required radio as blank if other radio with same name is checked,
          // regardless of whether same-name radio input has required attribute or not. The spec
          // states https://www.w3.org/TR/html5/forms.html#the-required-attribute
          radioName = input.attr('name');

          // Skip if we've already seen the radio with this name.
          if (!checkedRadioButtonNames[radioName]) {

            // If none checked
            if (form.find('input[type=radio]:checked[name="' + radioName + '"]').length === 0) {
              radiosForNameWithNoneSelected = form.find(
                'input[type=radio][name="' + radioName + '"]');
              foundInputs = foundInputs.add(radiosForNameWithNoneSelected);
            }

            // We only need to check each name once.
            checkedRadioButtonNames[radioName] = radioName;
          }
        } else {
          valueToCheck = input.is('input[type=checkbox],input[type=radio]') ? input.is(':checked') : !!input.val();
          if (valueToCheck === nonBlank) {
            foundInputs = foundInputs.add(input);
          }
        }
      });
      return foundInputs.length ? foundInputs : false;
    },

    // Helper function which checks for non-blank inputs in a form that match the specified CSS selector
    nonBlankInputs: function(form, specifiedSelector) {
      return rails.blankInputs(form, specifiedSelector, true); // true specifies nonBlank
    },

    // Helper function, needed to provide consistent behavior in IE
    stopEverything: function(e) {
      $(e.target).trigger('ujs:everythingStopped');
      e.stopImmediatePropagation();
      return false;
    },

    //  Replace element's html with the 'data-disable-with' after storing original html
    //  and prevent clicking on it
    disableElement: function(element) {
      var replacement = element.data('disable-with');

      if (replacement !== undefined) {
        element.data('ujs:enable-with', element.html()); // store enabled state
        element.html(replacement);
      }

      element.bind('click.railsDisable', function(e) { // prevent further clicking
        return rails.stopEverything(e);
      });
      element.data('ujs:disabled', true);
    },

    // Restore element to its original state which was disabled by 'disableElement' above
    enableElement: function(element) {
      if (element.data('ujs:enable-with') !== undefined) {
        element.html(element.data('ujs:enable-with')); // set to old enabled state
        element.removeData('ujs:enable-with'); // clean up cache
      }
      element.unbind('click.railsDisable'); // enable element
      element.removeData('ujs:disabled');
    }
  };

  if (rails.fire($document, 'rails:attachBindings')) {

    $.ajaxPrefilter(function(options, originalOptions, xhr){ if ( !options.crossDomain ) { rails.CSRFProtection(xhr); }});

    // This event works the same as the load event, except that it fires every
    // time the page is loaded.
    //
    // See https://github.com/rails/jquery-ujs/issues/357
    // See https://developer.mozilla.org/en-US/docs/Using_Firefox_1.5_caching
    $(window).on('pageshow.rails', function () {
      $($.rails.enableSelector).each(function () {
        var element = $(this);

        if (element.data('ujs:disabled')) {
          $.rails.enableFormElement(element);
        }
      });

      $($.rails.linkDisableSelector).each(function () {
        var element = $(this);

        if (element.data('ujs:disabled')) {
          $.rails.enableElement(element);
        }
      });
    });

    $document.on('ajax:complete', rails.linkDisableSelector, function() {
        rails.enableElement($(this));
    });

    $document.on('ajax:complete', rails.buttonDisableSelector, function() {
        rails.enableFormElement($(this));
    });

    $document.on('click.rails', rails.linkClickSelector, function(e) {
      var link = $(this), method = link.data('method'), data = link.data('params'), metaClick = e.metaKey || e.ctrlKey;
      if (!rails.allowAction(link)) return rails.stopEverything(e);

      if (!metaClick && link.is(rails.linkDisableSelector)) rails.disableElement(link);

      if (rails.isRemote(link)) {
        if (metaClick && (!method || method === 'GET') && !data) { return true; }

        var handleRemote = rails.handleRemote(link);
        // Response from rails.handleRemote() will either be false or a deferred object promise.
        if (handleRemote === false) {
          rails.enableElement(link);
        } else {
          handleRemote.fail( function() { rails.enableElement(link); } );
        }
        return false;

      } else if (method) {
        rails.handleMethod(link);
        return false;
      }
    });

    $document.on('click.rails', rails.buttonClickSelector, function(e) {
      var button = $(this);

      if (!rails.allowAction(button) || !rails.isRemote(button)) return rails.stopEverything(e);

      if (button.is(rails.buttonDisableSelector)) rails.disableFormElement(button);

      var handleRemote = rails.handleRemote(button);
      // Response from rails.handleRemote() will either be false or a deferred object promise.
      if (handleRemote === false) {
        rails.enableFormElement(button);
      } else {
        handleRemote.fail( function() { rails.enableFormElement(button); } );
      }
      return false;
    });

    $document.on('change.rails', rails.inputChangeSelector, function(e) {
      var link = $(this);
      if (!rails.allowAction(link) || !rails.isRemote(link)) return rails.stopEverything(e);

      rails.handleRemote(link);
      return false;
    });

    $document.on('submit.rails', rails.formSubmitSelector, function(e) {
      var form = $(this),
        remote = rails.isRemote(form),
        blankRequiredInputs,
        nonBlankFileInputs;

      if (!rails.allowAction(form)) return rails.stopEverything(e);

      // Skip other logic when required values are missing or file upload is present
      if (form.attr('novalidate') === undefined) {
        if (form.data('ujs:formnovalidate-button') === undefined) {
          blankRequiredInputs = rails.blankInputs(form, rails.requiredInputSelector, false);
          if (blankRequiredInputs && rails.fire(form, 'ajax:aborted:required', [blankRequiredInputs])) {
            return rails.stopEverything(e);
          }
        } else {
          // Clear the formnovalidate in case the next button click is not on a formnovalidate button
          // Not strictly necessary to do here, since it is also reset on each button click, but just to be certain
          form.data('ujs:formnovalidate-button', undefined);
        }
      }

      if (remote) {
        nonBlankFileInputs = rails.nonBlankInputs(form, rails.fileInputSelector);
        if (nonBlankFileInputs) {
          // Slight timeout so that the submit button gets properly serialized
          // (make it easy for event handler to serialize form without disabled values)
          setTimeout(function(){ rails.disableFormElements(form); }, 13);
          var aborted = rails.fire(form, 'ajax:aborted:file', [nonBlankFileInputs]);

          // Re-enable form elements if event bindings return false (canceling normal form submission)
          if (!aborted) { setTimeout(function(){ rails.enableFormElements(form); }, 13); }

          return aborted;
        }

        rails.handleRemote(form);
        return false;

      } else {
        // Slight timeout so that the submit button gets properly serialized
        setTimeout(function(){ rails.disableFormElements(form); }, 13);
      }
    });

    $document.on('click.rails', rails.formInputClickSelector, function(event) {
      var button = $(this);

      if (!rails.allowAction(button)) return rails.stopEverything(event);

      // Register the pressed submit button
      var name = button.attr('name'),
        data = name ? {name:name, value:button.val()} : null;

      var form = button.closest('form');
      if (form.length === 0) {
        form = $('#' + button.attr('form'));
      }
      form.data('ujs:submit-button', data);

      // Save attributes from button
      form.data('ujs:formnovalidate-button', button.attr('formnovalidate'));
      form.data('ujs:submit-button-formaction', button.attr('formaction'));
      form.data('ujs:submit-button-formmethod', button.attr('formmethod'));
    });

    $document.on('ajax:send.rails', rails.formSubmitSelector, function(event) {
      if (this === event.target) rails.disableFormElements($(this));
    });

    $document.on('ajax:complete.rails', rails.formSubmitSelector, function(event) {
      if (this === event.target) rails.enableFormElements($(this));
    });

    $(function(){
      rails.refreshCSRFTokens();
    });
  }

})( jQuery );
/*! Copyright (c) 2011 Piotr Rochala (http://rocha.la)
 * Dual licensed under the MIT (http://www.opensource.org/licenses/mit-license.php)
 * and GPL (http://www.opensource.org/licenses/gpl-license.php) licenses.
 *
 * Version: 1.3.3
 *
 */

(function(e){e.fn.extend({slimScroll:function(g){var a=e.extend({width:"auto",height:"250px",size:"7px",color:"#000",position:"right",distance:"1px",start:"top",opacity:.4,alwaysVisible:!1,disableFadeOut:!1,railVisible:!1,railColor:"#333",railOpacity:.2,railDraggable:!0,railClass:"slimScrollRail",barClass:"slimScrollBar",wrapperClass:"slimScrollDiv",allowPageScroll:!1,wheelStep:20,touchScrollStep:200,borderRadius:"7px",railBorderRadius:"7px"},g);this.each(function(){function u(d){if(r){d=d||window.event;
var c=0;d.wheelDelta&&(c=-d.wheelDelta/120);d.detail&&(c=d.detail/3);e(d.target||d.srcTarget||d.srcElement).closest("."+a.wrapperClass).is(b.parent())&&m(c,!0);d.preventDefault&&!k&&d.preventDefault();k||(d.returnValue=!1)}}function m(d,e,g){k=!1;var f=d,h=b.outerHeight()-c.outerHeight();e&&(f=parseInt(c.css("top"))+d*parseInt(a.wheelStep)/100*c.outerHeight(),f=Math.min(Math.max(f,0),h),f=0<d?Math.ceil(f):Math.floor(f),c.css({top:f+"px"}));l=parseInt(c.css("top"))/(b.outerHeight()-c.outerHeight());
f=l*(b[0].scrollHeight-b.outerHeight());g&&(f=d,d=f/b[0].scrollHeight*b.outerHeight(),d=Math.min(Math.max(d,0),h),c.css({top:d+"px"}));b.scrollTop(f);b.trigger("slimscrolling",~~f);v();p()}function C(){window.addEventListener?(this.addEventListener("DOMMouseScroll",u,!1),this.addEventListener("mousewheel",u,!1)):document.attachEvent("onmousewheel",u)}function w(){s=Math.max(b.outerHeight()/b[0].scrollHeight*b.outerHeight(),30);c.css({height:s+"px"});var a=s==b.outerHeight()?"none":"block";c.css({display:a})}
function v(){w();clearTimeout(A);l==~~l?(k=a.allowPageScroll,B!=l&&b.trigger("slimscroll",0==~~l?"top":"bottom")):k=!1;B=l;s>=b.outerHeight()?k=!0:(c.stop(!0,!0).fadeIn("fast"),a.railVisible&&h.stop(!0,!0).fadeIn("fast"))}function p(){a.alwaysVisible||(A=setTimeout(function(){a.disableFadeOut&&r||x||y||(c.fadeOut("slow"),h.fadeOut("slow"))},1E3))}var r,x,y,A,z,s,l,B,k=!1,b=e(this);if(b.parent().hasClass(a.wrapperClass)){var n=b.scrollTop(),c=b.parent().find("."+a.barClass),h=b.parent().find("."+a.railClass);
w();if(e.isPlainObject(g)){if("height"in g&&"auto"==g.height){b.parent().css("height","auto");b.css("height","auto");var q=b.parent().parent().height();b.parent().css("height",q);b.css("height",q)}if("scrollTo"in g)n=parseInt(a.scrollTo);else if("scrollBy"in g)n+=parseInt(a.scrollBy);else if("destroy"in g){c.remove();h.remove();b.unwrap();return}m(n,!1,!0)}}else if(!(e.isPlainObject(g)&&"destroy"in g)){a.height="auto"==a.height?b.parent().height():a.height;n=e("<div></div>").addClass(a.wrapperClass).css({position:"relative",
overflow:"hidden",width:a.width,height:a.height});b.css({overflow:"hidden",width:a.width,height:a.height});var h=e("<div></div>").addClass(a.railClass).css({width:a.size,height:"100%",position:"absolute",top:0,display:a.alwaysVisible&&a.railVisible?"block":"none","border-radius":a.railBorderRadius,background:a.railColor,opacity:a.railOpacity,zIndex:90}),c=e("<div></div>").addClass(a.barClass).css({background:a.color,width:a.size,position:"absolute",top:0,opacity:a.opacity,display:a.alwaysVisible?
"block":"none","border-radius":a.borderRadius,BorderRadius:a.borderRadius,MozBorderRadius:a.borderRadius,WebkitBorderRadius:a.borderRadius,zIndex:99}),q="right"==a.position?{right:a.distance}:{left:a.distance};h.css(q);c.css(q);b.wrap(n);b.parent().append(c);b.parent().append(h);a.railDraggable&&c.bind("mousedown",function(a){var b=e(document);y=!0;t=parseFloat(c.css("top"));pageY=a.pageY;b.bind("mousemove.slimscroll",function(a){currTop=t+a.pageY-pageY;c.css("top",currTop);m(0,c.position().top,!1)});
b.bind("mouseup.slimscroll",function(a){y=!1;p();b.unbind(".slimscroll")});return!1}).bind("selectstart.slimscroll",function(a){a.stopPropagation();a.preventDefault();return!1});h.hover(function(){v()},function(){p()});c.hover(function(){x=!0},function(){x=!1});b.hover(function(){r=!0;v();p()},function(){r=!1;p()});b.bind("touchstart",function(a,b){a.originalEvent.touches.length&&(z=a.originalEvent.touches[0].pageY)});b.bind("touchmove",function(b){k||b.originalEvent.preventDefault();b.originalEvent.touches.length&&
(m((z-b.originalEvent.touches[0].pageY)/a.touchScrollStep,!0),z=b.originalEvent.touches[0].pageY)});w();"bottom"===a.start?(c.css({top:b.outerHeight()-c.outerHeight()}),m(0,!0)):"top"!==a.start&&(m(e(a.start).position().top,null,!0),a.alwaysVisible||c.hide());C()}});return this}});e.fn.extend({slimscroll:e.fn.slimScroll})})(jQuery);
//     Underscore.js 1.8.3
//     http://underscorejs.org
//     (c) 2009-2015 Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
//     Underscore may be freely distributed under the MIT license.
(function(){function n(n){function t(t,r,e,u,i,o){for(;i>=0&&o>i;i+=n){var a=u?u[i]:i;e=r(e,t[a],a,t)}return e}return function(r,e,u,i){e=b(e,i,4);var o=!k(r)&&m.keys(r),a=(o||r).length,c=n>0?0:a-1;return arguments.length<3&&(u=r[o?o[c]:c],c+=n),t(r,e,u,o,c,a)}}function t(n){return function(t,r,e){r=x(r,e);for(var u=O(t),i=n>0?0:u-1;i>=0&&u>i;i+=n)if(r(t[i],i,t))return i;return-1}}function r(n,t,r){return function(e,u,i){var o=0,a=O(e);if("number"==typeof i)n>0?o=i>=0?i:Math.max(i+a,o):a=i>=0?Math.min(i+1,a):i+a+1;else if(r&&i&&a)return i=r(e,u),e[i]===u?i:-1;if(u!==u)return i=t(l.call(e,o,a),m.isNaN),i>=0?i+o:-1;for(i=n>0?o:a-1;i>=0&&a>i;i+=n)if(e[i]===u)return i;return-1}}function e(n,t){var r=I.length,e=n.constructor,u=m.isFunction(e)&&e.prototype||a,i="constructor";for(m.has(n,i)&&!m.contains(t,i)&&t.push(i);r--;)i=I[r],i in n&&n[i]!==u[i]&&!m.contains(t,i)&&t.push(i)}var u=this,i=u._,o=Array.prototype,a=Object.prototype,c=Function.prototype,f=o.push,l=o.slice,s=a.toString,p=a.hasOwnProperty,h=Array.isArray,v=Object.keys,g=c.bind,y=Object.create,d=function(){},m=function(n){return n instanceof m?n:this instanceof m?void(this._wrapped=n):new m(n)};"undefined"!=typeof exports?("undefined"!=typeof module&&module.exports&&(exports=module.exports=m),exports._=m):u._=m,m.VERSION="1.8.3";var b=function(n,t,r){if(t===void 0)return n;switch(null==r?3:r){case 1:return function(r){return n.call(t,r)};case 2:return function(r,e){return n.call(t,r,e)};case 3:return function(r,e,u){return n.call(t,r,e,u)};case 4:return function(r,e,u,i){return n.call(t,r,e,u,i)}}return function(){return n.apply(t,arguments)}},x=function(n,t,r){return null==n?m.identity:m.isFunction(n)?b(n,t,r):m.isObject(n)?m.matcher(n):m.property(n)};m.iteratee=function(n,t){return x(n,t,1/0)};var _=function(n,t){return function(r){var e=arguments.length;if(2>e||null==r)return r;for(var u=1;e>u;u++)for(var i=arguments[u],o=n(i),a=o.length,c=0;a>c;c++){var f=o[c];t&&r[f]!==void 0||(r[f]=i[f])}return r}},j=function(n){if(!m.isObject(n))return{};if(y)return y(n);d.prototype=n;var t=new d;return d.prototype=null,t},w=function(n){return function(t){return null==t?void 0:t[n]}},A=Math.pow(2,53)-1,O=w("length"),k=function(n){var t=O(n);return"number"==typeof t&&t>=0&&A>=t};m.each=m.forEach=function(n,t,r){t=b(t,r);var e,u;if(k(n))for(e=0,u=n.length;u>e;e++)t(n[e],e,n);else{var i=m.keys(n);for(e=0,u=i.length;u>e;e++)t(n[i[e]],i[e],n)}return n},m.map=m.collect=function(n,t,r){t=x(t,r);for(var e=!k(n)&&m.keys(n),u=(e||n).length,i=Array(u),o=0;u>o;o++){var a=e?e[o]:o;i[o]=t(n[a],a,n)}return i},m.reduce=m.foldl=m.inject=n(1),m.reduceRight=m.foldr=n(-1),m.find=m.detect=function(n,t,r){var e;return e=k(n)?m.findIndex(n,t,r):m.findKey(n,t,r),e!==void 0&&e!==-1?n[e]:void 0},m.filter=m.select=function(n,t,r){var e=[];return t=x(t,r),m.each(n,function(n,r,u){t(n,r,u)&&e.push(n)}),e},m.reject=function(n,t,r){return m.filter(n,m.negate(x(t)),r)},m.every=m.all=function(n,t,r){t=x(t,r);for(var e=!k(n)&&m.keys(n),u=(e||n).length,i=0;u>i;i++){var o=e?e[i]:i;if(!t(n[o],o,n))return!1}return!0},m.some=m.any=function(n,t,r){t=x(t,r);for(var e=!k(n)&&m.keys(n),u=(e||n).length,i=0;u>i;i++){var o=e?e[i]:i;if(t(n[o],o,n))return!0}return!1},m.contains=m.includes=m.include=function(n,t,r,e){return k(n)||(n=m.values(n)),("number"!=typeof r||e)&&(r=0),m.indexOf(n,t,r)>=0},m.invoke=function(n,t){var r=l.call(arguments,2),e=m.isFunction(t);return m.map(n,function(n){var u=e?t:n[t];return null==u?u:u.apply(n,r)})},m.pluck=function(n,t){return m.map(n,m.property(t))},m.where=function(n,t){return m.filter(n,m.matcher(t))},m.findWhere=function(n,t){return m.find(n,m.matcher(t))},m.max=function(n,t,r){var e,u,i=-1/0,o=-1/0;if(null==t&&null!=n){n=k(n)?n:m.values(n);for(var a=0,c=n.length;c>a;a++)e=n[a],e>i&&(i=e)}else t=x(t,r),m.each(n,function(n,r,e){u=t(n,r,e),(u>o||u===-1/0&&i===-1/0)&&(i=n,o=u)});return i},m.min=function(n,t,r){var e,u,i=1/0,o=1/0;if(null==t&&null!=n){n=k(n)?n:m.values(n);for(var a=0,c=n.length;c>a;a++)e=n[a],i>e&&(i=e)}else t=x(t,r),m.each(n,function(n,r,e){u=t(n,r,e),(o>u||1/0===u&&1/0===i)&&(i=n,o=u)});return i},m.shuffle=function(n){for(var t,r=k(n)?n:m.values(n),e=r.length,u=Array(e),i=0;e>i;i++)t=m.random(0,i),t!==i&&(u[i]=u[t]),u[t]=r[i];return u},m.sample=function(n,t,r){return null==t||r?(k(n)||(n=m.values(n)),n[m.random(n.length-1)]):m.shuffle(n).slice(0,Math.max(0,t))},m.sortBy=function(n,t,r){return t=x(t,r),m.pluck(m.map(n,function(n,r,e){return{value:n,index:r,criteria:t(n,r,e)}}).sort(function(n,t){var r=n.criteria,e=t.criteria;if(r!==e){if(r>e||r===void 0)return 1;if(e>r||e===void 0)return-1}return n.index-t.index}),"value")};var F=function(n){return function(t,r,e){var u={};return r=x(r,e),m.each(t,function(e,i){var o=r(e,i,t);n(u,e,o)}),u}};m.groupBy=F(function(n,t,r){m.has(n,r)?n[r].push(t):n[r]=[t]}),m.indexBy=F(function(n,t,r){n[r]=t}),m.countBy=F(function(n,t,r){m.has(n,r)?n[r]++:n[r]=1}),m.toArray=function(n){return n?m.isArray(n)?l.call(n):k(n)?m.map(n,m.identity):m.values(n):[]},m.size=function(n){return null==n?0:k(n)?n.length:m.keys(n).length},m.partition=function(n,t,r){t=x(t,r);var e=[],u=[];return m.each(n,function(n,r,i){(t(n,r,i)?e:u).push(n)}),[e,u]},m.first=m.head=m.take=function(n,t,r){return null==n?void 0:null==t||r?n[0]:m.initial(n,n.length-t)},m.initial=function(n,t,r){return l.call(n,0,Math.max(0,n.length-(null==t||r?1:t)))},m.last=function(n,t,r){return null==n?void 0:null==t||r?n[n.length-1]:m.rest(n,Math.max(0,n.length-t))},m.rest=m.tail=m.drop=function(n,t,r){return l.call(n,null==t||r?1:t)},m.compact=function(n){return m.filter(n,m.identity)};var S=function(n,t,r,e){for(var u=[],i=0,o=e||0,a=O(n);a>o;o++){var c=n[o];if(k(c)&&(m.isArray(c)||m.isArguments(c))){t||(c=S(c,t,r));var f=0,l=c.length;for(u.length+=l;l>f;)u[i++]=c[f++]}else r||(u[i++]=c)}return u};m.flatten=function(n,t){return S(n,t,!1)},m.without=function(n){return m.difference(n,l.call(arguments,1))},m.uniq=m.unique=function(n,t,r,e){m.isBoolean(t)||(e=r,r=t,t=!1),null!=r&&(r=x(r,e));for(var u=[],i=[],o=0,a=O(n);a>o;o++){var c=n[o],f=r?r(c,o,n):c;t?(o&&i===f||u.push(c),i=f):r?m.contains(i,f)||(i.push(f),u.push(c)):m.contains(u,c)||u.push(c)}return u},m.union=function(){return m.uniq(S(arguments,!0,!0))},m.intersection=function(n){for(var t=[],r=arguments.length,e=0,u=O(n);u>e;e++){var i=n[e];if(!m.contains(t,i)){for(var o=1;r>o&&m.contains(arguments[o],i);o++);o===r&&t.push(i)}}return t},m.difference=function(n){var t=S(arguments,!0,!0,1);return m.filter(n,function(n){return!m.contains(t,n)})},m.zip=function(){return m.unzip(arguments)},m.unzip=function(n){for(var t=n&&m.max(n,O).length||0,r=Array(t),e=0;t>e;e++)r[e]=m.pluck(n,e);return r},m.object=function(n,t){for(var r={},e=0,u=O(n);u>e;e++)t?r[n[e]]=t[e]:r[n[e][0]]=n[e][1];return r},m.findIndex=t(1),m.findLastIndex=t(-1),m.sortedIndex=function(n,t,r,e){r=x(r,e,1);for(var u=r(t),i=0,o=O(n);o>i;){var a=Math.floor((i+o)/2);r(n[a])<u?i=a+1:o=a}return i},m.indexOf=r(1,m.findIndex,m.sortedIndex),m.lastIndexOf=r(-1,m.findLastIndex),m.range=function(n,t,r){null==t&&(t=n||0,n=0),r=r||1;for(var e=Math.max(Math.ceil((t-n)/r),0),u=Array(e),i=0;e>i;i++,n+=r)u[i]=n;return u};var E=function(n,t,r,e,u){if(!(e instanceof t))return n.apply(r,u);var i=j(n.prototype),o=n.apply(i,u);return m.isObject(o)?o:i};m.bind=function(n,t){if(g&&n.bind===g)return g.apply(n,l.call(arguments,1));if(!m.isFunction(n))throw new TypeError("Bind must be called on a function");var r=l.call(arguments,2),e=function(){return E(n,e,t,this,r.concat(l.call(arguments)))};return e},m.partial=function(n){var t=l.call(arguments,1),r=function(){for(var e=0,u=t.length,i=Array(u),o=0;u>o;o++)i[o]=t[o]===m?arguments[e++]:t[o];for(;e<arguments.length;)i.push(arguments[e++]);return E(n,r,this,this,i)};return r},m.bindAll=function(n){var t,r,e=arguments.length;if(1>=e)throw new Error("bindAll must be passed function names");for(t=1;e>t;t++)r=arguments[t],n[r]=m.bind(n[r],n);return n},m.memoize=function(n,t){var r=function(e){var u=r.cache,i=""+(t?t.apply(this,arguments):e);return m.has(u,i)||(u[i]=n.apply(this,arguments)),u[i]};return r.cache={},r},m.delay=function(n,t){var r=l.call(arguments,2);return setTimeout(function(){return n.apply(null,r)},t)},m.defer=m.partial(m.delay,m,1),m.throttle=function(n,t,r){var e,u,i,o=null,a=0;r||(r={});var c=function(){a=r.leading===!1?0:m.now(),o=null,i=n.apply(e,u),o||(e=u=null)};return function(){var f=m.now();a||r.leading!==!1||(a=f);var l=t-(f-a);return e=this,u=arguments,0>=l||l>t?(o&&(clearTimeout(o),o=null),a=f,i=n.apply(e,u),o||(e=u=null)):o||r.trailing===!1||(o=setTimeout(c,l)),i}},m.debounce=function(n,t,r){var e,u,i,o,a,c=function(){var f=m.now()-o;t>f&&f>=0?e=setTimeout(c,t-f):(e=null,r||(a=n.apply(i,u),e||(i=u=null)))};return function(){i=this,u=arguments,o=m.now();var f=r&&!e;return e||(e=setTimeout(c,t)),f&&(a=n.apply(i,u),i=u=null),a}},m.wrap=function(n,t){return m.partial(t,n)},m.negate=function(n){return function(){return!n.apply(this,arguments)}},m.compose=function(){var n=arguments,t=n.length-1;return function(){for(var r=t,e=n[t].apply(this,arguments);r--;)e=n[r].call(this,e);return e}},m.after=function(n,t){return function(){return--n<1?t.apply(this,arguments):void 0}},m.before=function(n,t){var r;return function(){return--n>0&&(r=t.apply(this,arguments)),1>=n&&(t=null),r}},m.once=m.partial(m.before,2);var M=!{toString:null}.propertyIsEnumerable("toString"),I=["valueOf","isPrototypeOf","toString","propertyIsEnumerable","hasOwnProperty","toLocaleString"];m.keys=function(n){if(!m.isObject(n))return[];if(v)return v(n);var t=[];for(var r in n)m.has(n,r)&&t.push(r);return M&&e(n,t),t},m.allKeys=function(n){if(!m.isObject(n))return[];var t=[];for(var r in n)t.push(r);return M&&e(n,t),t},m.values=function(n){for(var t=m.keys(n),r=t.length,e=Array(r),u=0;r>u;u++)e[u]=n[t[u]];return e},m.mapObject=function(n,t,r){t=x(t,r);for(var e,u=m.keys(n),i=u.length,o={},a=0;i>a;a++)e=u[a],o[e]=t(n[e],e,n);return o},m.pairs=function(n){for(var t=m.keys(n),r=t.length,e=Array(r),u=0;r>u;u++)e[u]=[t[u],n[t[u]]];return e},m.invert=function(n){for(var t={},r=m.keys(n),e=0,u=r.length;u>e;e++)t[n[r[e]]]=r[e];return t},m.functions=m.methods=function(n){var t=[];for(var r in n)m.isFunction(n[r])&&t.push(r);return t.sort()},m.extend=_(m.allKeys),m.extendOwn=m.assign=_(m.keys),m.findKey=function(n,t,r){t=x(t,r);for(var e,u=m.keys(n),i=0,o=u.length;o>i;i++)if(e=u[i],t(n[e],e,n))return e},m.pick=function(n,t,r){var e,u,i={},o=n;if(null==o)return i;m.isFunction(t)?(u=m.allKeys(o),e=b(t,r)):(u=S(arguments,!1,!1,1),e=function(n,t,r){return t in r},o=Object(o));for(var a=0,c=u.length;c>a;a++){var f=u[a],l=o[f];e(l,f,o)&&(i[f]=l)}return i},m.omit=function(n,t,r){if(m.isFunction(t))t=m.negate(t);else{var e=m.map(S(arguments,!1,!1,1),String);t=function(n,t){return!m.contains(e,t)}}return m.pick(n,t,r)},m.defaults=_(m.allKeys,!0),m.create=function(n,t){var r=j(n);return t&&m.extendOwn(r,t),r},m.clone=function(n){return m.isObject(n)?m.isArray(n)?n.slice():m.extend({},n):n},m.tap=function(n,t){return t(n),n},m.isMatch=function(n,t){var r=m.keys(t),e=r.length;if(null==n)return!e;for(var u=Object(n),i=0;e>i;i++){var o=r[i];if(t[o]!==u[o]||!(o in u))return!1}return!0};var N=function(n,t,r,e){if(n===t)return 0!==n||1/n===1/t;if(null==n||null==t)return n===t;n instanceof m&&(n=n._wrapped),t instanceof m&&(t=t._wrapped);var u=s.call(n);if(u!==s.call(t))return!1;switch(u){case"[object RegExp]":case"[object String]":return""+n==""+t;case"[object Number]":return+n!==+n?+t!==+t:0===+n?1/+n===1/t:+n===+t;case"[object Date]":case"[object Boolean]":return+n===+t}var i="[object Array]"===u;if(!i){if("object"!=typeof n||"object"!=typeof t)return!1;var o=n.constructor,a=t.constructor;if(o!==a&&!(m.isFunction(o)&&o instanceof o&&m.isFunction(a)&&a instanceof a)&&"constructor"in n&&"constructor"in t)return!1}r=r||[],e=e||[];for(var c=r.length;c--;)if(r[c]===n)return e[c]===t;if(r.push(n),e.push(t),i){if(c=n.length,c!==t.length)return!1;for(;c--;)if(!N(n[c],t[c],r,e))return!1}else{var f,l=m.keys(n);if(c=l.length,m.keys(t).length!==c)return!1;for(;c--;)if(f=l[c],!m.has(t,f)||!N(n[f],t[f],r,e))return!1}return r.pop(),e.pop(),!0};m.isEqual=function(n,t){return N(n,t)},m.isEmpty=function(n){return null==n?!0:k(n)&&(m.isArray(n)||m.isString(n)||m.isArguments(n))?0===n.length:0===m.keys(n).length},m.isElement=function(n){return!(!n||1!==n.nodeType)},m.isArray=h||function(n){return"[object Array]"===s.call(n)},m.isObject=function(n){var t=typeof n;return"function"===t||"object"===t&&!!n},m.each(["Arguments","Function","String","Number","Date","RegExp","Error"],function(n){m["is"+n]=function(t){return s.call(t)==="[object "+n+"]"}}),m.isArguments(arguments)||(m.isArguments=function(n){return m.has(n,"callee")}),"function"!=typeof/./&&"object"!=typeof Int8Array&&(m.isFunction=function(n){return"function"==typeof n||!1}),m.isFinite=function(n){return isFinite(n)&&!isNaN(parseFloat(n))},m.isNaN=function(n){return m.isNumber(n)&&n!==+n},m.isBoolean=function(n){return n===!0||n===!1||"[object Boolean]"===s.call(n)},m.isNull=function(n){return null===n},m.isUndefined=function(n){return n===void 0},m.has=function(n,t){return null!=n&&p.call(n,t)},m.noConflict=function(){return u._=i,this},m.identity=function(n){return n},m.constant=function(n){return function(){return n}},m.noop=function(){},m.property=w,m.propertyOf=function(n){return null==n?function(){}:function(t){return n[t]}},m.matcher=m.matches=function(n){return n=m.extendOwn({},n),function(t){return m.isMatch(t,n)}},m.times=function(n,t,r){var e=Array(Math.max(0,n));t=b(t,r,1);for(var u=0;n>u;u++)e[u]=t(u);return e},m.random=function(n,t){return null==t&&(t=n,n=0),n+Math.floor(Math.random()*(t-n+1))},m.now=Date.now||function(){return(new Date).getTime()};var B={"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#x27;","`":"&#x60;"},T=m.invert(B),R=function(n){var t=function(t){return n[t]},r="(?:"+m.keys(n).join("|")+")",e=RegExp(r),u=RegExp(r,"g");return function(n){return n=null==n?"":""+n,e.test(n)?n.replace(u,t):n}};m.escape=R(B),m.unescape=R(T),m.result=function(n,t,r){var e=null==n?void 0:n[t];return e===void 0&&(e=r),m.isFunction(e)?e.call(n):e};var q=0;m.uniqueId=function(n){var t=++q+"";return n?n+t:t},m.templateSettings={evaluate:/<%([\s\S]+?)%>/g,interpolate:/<%=([\s\S]+?)%>/g,escape:/<%-([\s\S]+?)%>/g};var K=/(.)^/,z={"'":"'","\\":"\\","\r":"r","\n":"n","\u2028":"u2028","\u2029":"u2029"},D=/\\|'|\r|\n|\u2028|\u2029/g,L=function(n){return"\\"+z[n]};m.template=function(n,t,r){!t&&r&&(t=r),t=m.defaults({},t,m.templateSettings);var e=RegExp([(t.escape||K).source,(t.interpolate||K).source,(t.evaluate||K).source].join("|")+"|$","g"),u=0,i="__p+='";n.replace(e,function(t,r,e,o,a){return i+=n.slice(u,a).replace(D,L),u=a+t.length,r?i+="'+\n((__t=("+r+"))==null?'':_.escape(__t))+\n'":e?i+="'+\n((__t=("+e+"))==null?'':__t)+\n'":o&&(i+="';\n"+o+"\n__p+='"),t}),i+="';\n",t.variable||(i="with(obj||{}){\n"+i+"}\n"),i="var __t,__p='',__j=Array.prototype.join,"+"print=function(){__p+=__j.call(arguments,'');};\n"+i+"return __p;\n";try{var o=new Function(t.variable||"obj","_",i)}catch(a){throw a.source=i,a}var c=function(n){return o.call(this,n,m)},f=t.variable||"obj";return c.source="function("+f+"){\n"+i+"}",c},m.chain=function(n){var t=m(n);return t._chain=!0,t};var P=function(n,t){return n._chain?m(t).chain():t};m.mixin=function(n){m.each(m.functions(n),function(t){var r=m[t]=n[t];m.prototype[t]=function(){var n=[this._wrapped];return f.apply(n,arguments),P(this,r.apply(m,n))}})},m.mixin(m),m.each(["pop","push","reverse","shift","sort","splice","unshift"],function(n){var t=o[n];m.prototype[n]=function(){var r=this._wrapped;return t.apply(r,arguments),"shift"!==n&&"splice"!==n||0!==r.length||delete r[0],P(this,r)}}),m.each(["concat","join","slice"],function(n){var t=o[n];m.prototype[n]=function(){return P(this,t.apply(this._wrapped,arguments))}}),m.prototype.value=function(){return this._wrapped},m.prototype.valueOf=m.prototype.toJSON=m.prototype.value,m.prototype.toString=function(){return""+this._wrapped},"function"==typeof define&&define.amd&&define("underscore",[],function(){return m})}).call(this);
/*!
 * Bootstrap v3.3.6 (http://getbootstrap.com)
 * Copyright 2011-2015 Twitter, Inc.
 * Licensed under the MIT license
 */

if("undefined"==typeof jQuery)throw new Error("Bootstrap's JavaScript requires jQuery");+function(a){"use strict";var b=a.fn.jquery.split(" ")[0].split(".");if(b[0]<2&&b[1]<9||1==b[0]&&9==b[1]&&b[2]<1||b[0]>2)throw new Error("Bootstrap's JavaScript requires jQuery version 1.9.1 or higher, but lower than version 3")}(jQuery),+function(a){"use strict";function b(){var a=document.createElement("bootstrap"),b={WebkitTransition:"webkitTransitionEnd",MozTransition:"transitionend",OTransition:"oTransitionEnd otransitionend",transition:"transitionend"};for(var c in b)if(void 0!==a.style[c])return{end:b[c]};return!1}a.fn.emulateTransitionEnd=function(b){var c=!1,d=this;a(this).one("bsTransitionEnd",function(){c=!0});var e=function(){c||a(d).trigger(a.support.transition.end)};return setTimeout(e,b),this},a(function(){a.support.transition=b(),a.support.transition&&(a.event.special.bsTransitionEnd={bindType:a.support.transition.end,delegateType:a.support.transition.end,handle:function(b){return a(b.target).is(this)?b.handleObj.handler.apply(this,arguments):void 0}})})}(jQuery),+function(a){"use strict";function b(b){return this.each(function(){var c=a(this),e=c.data("bs.alert");e||c.data("bs.alert",e=new d(this)),"string"==typeof b&&e[b].call(c)})}var c='[data-dismiss="alert"]',d=function(b){a(b).on("click",c,this.close)};d.VERSION="3.3.6",d.TRANSITION_DURATION=150,d.prototype.close=function(b){function c(){g.detach().trigger("closed.bs.alert").remove()}var e=a(this),f=e.attr("data-target");f||(f=e.attr("href"),f=f&&f.replace(/.*(?=#[^\s]*$)/,""));var g=a(f);b&&b.preventDefault(),g.length||(g=e.closest(".alert")),g.trigger(b=a.Event("close.bs.alert")),b.isDefaultPrevented()||(g.removeClass("in"),a.support.transition&&g.hasClass("fade")?g.one("bsTransitionEnd",c).emulateTransitionEnd(d.TRANSITION_DURATION):c())};var e=a.fn.alert;a.fn.alert=b,a.fn.alert.Constructor=d,a.fn.alert.noConflict=function(){return a.fn.alert=e,this},a(document).on("click.bs.alert.data-api",c,d.prototype.close)}(jQuery),+function(a){"use strict";function b(b){return this.each(function(){var d=a(this),e=d.data("bs.button"),f="object"==typeof b&&b;e||d.data("bs.button",e=new c(this,f)),"toggle"==b?e.toggle():b&&e.setState(b)})}var c=function(b,d){this.$element=a(b),this.options=a.extend({},c.DEFAULTS,d),this.isLoading=!1};c.VERSION="3.3.6",c.DEFAULTS={loadingText:"loading..."},c.prototype.setState=function(b){var c="disabled",d=this.$element,e=d.is("input")?"val":"html",f=d.data();b+="Text",null==f.resetText&&d.data("resetText",d[e]()),setTimeout(a.proxy(function(){d[e](null==f[b]?this.options[b]:f[b]),"loadingText"==b?(this.isLoading=!0,d.addClass(c).attr(c,c)):this.isLoading&&(this.isLoading=!1,d.removeClass(c).removeAttr(c))},this),0)},c.prototype.toggle=function(){var a=!0,b=this.$element.closest('[data-toggle="buttons"]');if(b.length){var c=this.$element.find("input");"radio"==c.prop("type")?(c.prop("checked")&&(a=!1),b.find(".active").removeClass("active"),this.$element.addClass("active")):"checkbox"==c.prop("type")&&(c.prop("checked")!==this.$element.hasClass("active")&&(a=!1),this.$element.toggleClass("active")),c.prop("checked",this.$element.hasClass("active")),a&&c.trigger("change")}else this.$element.attr("aria-pressed",!this.$element.hasClass("active")),this.$element.toggleClass("active")};var d=a.fn.button;a.fn.button=b,a.fn.button.Constructor=c,a.fn.button.noConflict=function(){return a.fn.button=d,this},a(document).on("click.bs.button.data-api",'[data-toggle^="button"]',function(c){var d=a(c.target);d.hasClass("btn")||(d=d.closest(".btn")),b.call(d,"toggle"),a(c.target).is('input[type="radio"]')||a(c.target).is('input[type="checkbox"]')||c.preventDefault()}).on("focus.bs.button.data-api blur.bs.button.data-api",'[data-toggle^="button"]',function(b){a(b.target).closest(".btn").toggleClass("focus",/^focus(in)?$/.test(b.type))})}(jQuery),+function(a){"use strict";function b(b){return this.each(function(){var d=a(this),e=d.data("bs.carousel"),f=a.extend({},c.DEFAULTS,d.data(),"object"==typeof b&&b),g="string"==typeof b?b:f.slide;e||d.data("bs.carousel",e=new c(this,f)),"number"==typeof b?e.to(b):g?e[g]():f.interval&&e.pause().cycle()})}var c=function(b,c){this.$element=a(b),this.$indicators=this.$element.find(".carousel-indicators"),this.options=c,this.paused=null,this.sliding=null,this.interval=null,this.$active=null,this.$items=null,this.options.keyboard&&this.$element.on("keydown.bs.carousel",a.proxy(this.keydown,this)),"hover"==this.options.pause&&!("ontouchstart"in document.documentElement)&&this.$element.on("mouseenter.bs.carousel",a.proxy(this.pause,this)).on("mouseleave.bs.carousel",a.proxy(this.cycle,this))};c.VERSION="3.3.6",c.TRANSITION_DURATION=600,c.DEFAULTS={interval:5e3,pause:"hover",wrap:!0,keyboard:!0},c.prototype.keydown=function(a){if(!/input|textarea/i.test(a.target.tagName)){switch(a.which){case 37:this.prev();break;case 39:this.next();break;default:return}a.preventDefault()}},c.prototype.cycle=function(b){return b||(this.paused=!1),this.interval&&clearInterval(this.interval),this.options.interval&&!this.paused&&(this.interval=setInterval(a.proxy(this.next,this),this.options.interval)),this},c.prototype.getItemIndex=function(a){return this.$items=a.parent().children(".item"),this.$items.index(a||this.$active)},c.prototype.getItemForDirection=function(a,b){var c=this.getItemIndex(b),d="prev"==a&&0===c||"next"==a&&c==this.$items.length-1;if(d&&!this.options.wrap)return b;var e="prev"==a?-1:1,f=(c+e)%this.$items.length;return this.$items.eq(f)},c.prototype.to=function(a){var b=this,c=this.getItemIndex(this.$active=this.$element.find(".item.active"));return a>this.$items.length-1||0>a?void 0:this.sliding?this.$element.one("slid.bs.carousel",function(){b.to(a)}):c==a?this.pause().cycle():this.slide(a>c?"next":"prev",this.$items.eq(a))},c.prototype.pause=function(b){return b||(this.paused=!0),this.$element.find(".next, .prev").length&&a.support.transition&&(this.$element.trigger(a.support.transition.end),this.cycle(!0)),this.interval=clearInterval(this.interval),this},c.prototype.next=function(){return this.sliding?void 0:this.slide("next")},c.prototype.prev=function(){return this.sliding?void 0:this.slide("prev")},c.prototype.slide=function(b,d){var e=this.$element.find(".item.active"),f=d||this.getItemForDirection(b,e),g=this.interval,h="next"==b?"left":"right",i=this;if(f.hasClass("active"))return this.sliding=!1;var j=f[0],k=a.Event("slide.bs.carousel",{relatedTarget:j,direction:h});if(this.$element.trigger(k),!k.isDefaultPrevented()){if(this.sliding=!0,g&&this.pause(),this.$indicators.length){this.$indicators.find(".active").removeClass("active");var l=a(this.$indicators.children()[this.getItemIndex(f)]);l&&l.addClass("active")}var m=a.Event("slid.bs.carousel",{relatedTarget:j,direction:h});return a.support.transition&&this.$element.hasClass("slide")?(f.addClass(b),f[0].offsetWidth,e.addClass(h),f.addClass(h),e.one("bsTransitionEnd",function(){f.removeClass([b,h].join(" ")).addClass("active"),e.removeClass(["active",h].join(" ")),i.sliding=!1,setTimeout(function(){i.$element.trigger(m)},0)}).emulateTransitionEnd(c.TRANSITION_DURATION)):(e.removeClass("active"),f.addClass("active"),this.sliding=!1,this.$element.trigger(m)),g&&this.cycle(),this}};var d=a.fn.carousel;a.fn.carousel=b,a.fn.carousel.Constructor=c,a.fn.carousel.noConflict=function(){return a.fn.carousel=d,this};var e=function(c){var d,e=a(this),f=a(e.attr("data-target")||(d=e.attr("href"))&&d.replace(/.*(?=#[^\s]+$)/,""));if(f.hasClass("carousel")){var g=a.extend({},f.data(),e.data()),h=e.attr("data-slide-to");h&&(g.interval=!1),b.call(f,g),h&&f.data("bs.carousel").to(h),c.preventDefault()}};a(document).on("click.bs.carousel.data-api","[data-slide]",e).on("click.bs.carousel.data-api","[data-slide-to]",e),a(window).on("load",function(){a('[data-ride="carousel"]').each(function(){var c=a(this);b.call(c,c.data())})})}(jQuery),+function(a){"use strict";function b(b){var c,d=b.attr("data-target")||(c=b.attr("href"))&&c.replace(/.*(?=#[^\s]+$)/,"");return a(d)}function c(b){return this.each(function(){var c=a(this),e=c.data("bs.collapse"),f=a.extend({},d.DEFAULTS,c.data(),"object"==typeof b&&b);!e&&f.toggle&&/show|hide/.test(b)&&(f.toggle=!1),e||c.data("bs.collapse",e=new d(this,f)),"string"==typeof b&&e[b]()})}var d=function(b,c){this.$element=a(b),this.options=a.extend({},d.DEFAULTS,c),this.$trigger=a('[data-toggle="collapse"][href="#'+b.id+'"],[data-toggle="collapse"][data-target="#'+b.id+'"]'),this.transitioning=null,this.options.parent?this.$parent=this.getParent():this.addAriaAndCollapsedClass(this.$element,this.$trigger),this.options.toggle&&this.toggle()};d.VERSION="3.3.6",d.TRANSITION_DURATION=350,d.DEFAULTS={toggle:!0},d.prototype.dimension=function(){var a=this.$element.hasClass("width");return a?"width":"height"},d.prototype.show=function(){if(!this.transitioning&&!this.$element.hasClass("in")){var b,e=this.$parent&&this.$parent.children(".panel").children(".in, .collapsing");if(!(e&&e.length&&(b=e.data("bs.collapse"),b&&b.transitioning))){var f=a.Event("show.bs.collapse");if(this.$element.trigger(f),!f.isDefaultPrevented()){e&&e.length&&(c.call(e,"hide"),b||e.data("bs.collapse",null));var g=this.dimension();this.$element.removeClass("collapse").addClass("collapsing")[g](0).attr("aria-expanded",!0),this.$trigger.removeClass("collapsed").attr("aria-expanded",!0),this.transitioning=1;var h=function(){this.$element.removeClass("collapsing").addClass("collapse in")[g](""),this.transitioning=0,this.$element.trigger("shown.bs.collapse")};if(!a.support.transition)return h.call(this);var i=a.camelCase(["scroll",g].join("-"));this.$element.one("bsTransitionEnd",a.proxy(h,this)).emulateTransitionEnd(d.TRANSITION_DURATION)[g](this.$element[0][i])}}}},d.prototype.hide=function(){if(!this.transitioning&&this.$element.hasClass("in")){var b=a.Event("hide.bs.collapse");if(this.$element.trigger(b),!b.isDefaultPrevented()){var c=this.dimension();this.$element[c](this.$element[c]())[0].offsetHeight,this.$element.addClass("collapsing").removeClass("collapse in").attr("aria-expanded",!1),this.$trigger.addClass("collapsed").attr("aria-expanded",!1),this.transitioning=1;var e=function(){this.transitioning=0,this.$element.removeClass("collapsing").addClass("collapse").trigger("hidden.bs.collapse")};return a.support.transition?void this.$element[c](0).one("bsTransitionEnd",a.proxy(e,this)).emulateTransitionEnd(d.TRANSITION_DURATION):e.call(this)}}},d.prototype.toggle=function(){this[this.$element.hasClass("in")?"hide":"show"]()},d.prototype.getParent=function(){return a(this.options.parent).find('[data-toggle="collapse"][data-parent="'+this.options.parent+'"]').each(a.proxy(function(c,d){var e=a(d);this.addAriaAndCollapsedClass(b(e),e)},this)).end()},d.prototype.addAriaAndCollapsedClass=function(a,b){var c=a.hasClass("in");a.attr("aria-expanded",c),b.toggleClass("collapsed",!c).attr("aria-expanded",c)};var e=a.fn.collapse;a.fn.collapse=c,a.fn.collapse.Constructor=d,a.fn.collapse.noConflict=function(){return a.fn.collapse=e,this},a(document).on("click.bs.collapse.data-api",'[data-toggle="collapse"]',function(d){var e=a(this);e.attr("data-target")||d.preventDefault();var f=b(e),g=f.data("bs.collapse"),h=g?"toggle":e.data();c.call(f,h)})}(jQuery),+function(a){"use strict";function b(b){var c=b.attr("data-target");c||(c=b.attr("href"),c=c&&/#[A-Za-z]/.test(c)&&c.replace(/.*(?=#[^\s]*$)/,""));var d=c&&a(c);return d&&d.length?d:b.parent()}function c(c){c&&3===c.which||(a(e).remove(),a(f).each(function(){var d=a(this),e=b(d),f={relatedTarget:this};e.hasClass("open")&&(c&&"click"==c.type&&/input|textarea/i.test(c.target.tagName)&&a.contains(e[0],c.target)||(e.trigger(c=a.Event("hide.bs.dropdown",f)),c.isDefaultPrevented()||(d.attr("aria-expanded","false"),e.removeClass("open").trigger(a.Event("hidden.bs.dropdown",f)))))}))}function d(b){return this.each(function(){var c=a(this),d=c.data("bs.dropdown");d||c.data("bs.dropdown",d=new g(this)),"string"==typeof b&&d[b].call(c)})}var e=".dropdown-backdrop",f='[data-toggle="dropdown"]',g=function(b){a(b).on("click.bs.dropdown",this.toggle)};g.VERSION="3.3.6",g.prototype.toggle=function(d){var e=a(this);if(!e.is(".disabled, :disabled")){var f=b(e),g=f.hasClass("open");if(c(),!g){"ontouchstart"in document.documentElement&&!f.closest(".navbar-nav").length&&a(document.createElement("div")).addClass("dropdown-backdrop").insertAfter(a(this)).on("click",c);var h={relatedTarget:this};if(f.trigger(d=a.Event("show.bs.dropdown",h)),d.isDefaultPrevented())return;e.trigger("focus").attr("aria-expanded","true"),f.toggleClass("open").trigger(a.Event("shown.bs.dropdown",h))}return!1}},g.prototype.keydown=function(c){if(/(38|40|27|32)/.test(c.which)&&!/input|textarea/i.test(c.target.tagName)){var d=a(this);if(c.preventDefault(),c.stopPropagation(),!d.is(".disabled, :disabled")){var e=b(d),g=e.hasClass("open");if(!g&&27!=c.which||g&&27==c.which)return 27==c.which&&e.find(f).trigger("focus"),d.trigger("click");var h=" li:not(.disabled):visible a",i=e.find(".dropdown-menu"+h);if(i.length){var j=i.index(c.target);38==c.which&&j>0&&j--,40==c.which&&j<i.length-1&&j++,~j||(j=0),i.eq(j).trigger("focus")}}}};var h=a.fn.dropdown;a.fn.dropdown=d,a.fn.dropdown.Constructor=g,a.fn.dropdown.noConflict=function(){return a.fn.dropdown=h,this},a(document).on("click.bs.dropdown.data-api",c).on("click.bs.dropdown.data-api",".dropdown form",function(a){a.stopPropagation()}).on("click.bs.dropdown.data-api",f,g.prototype.toggle).on("keydown.bs.dropdown.data-api",f,g.prototype.keydown).on("keydown.bs.dropdown.data-api",".dropdown-menu",g.prototype.keydown)}(jQuery),+function(a){"use strict";function b(b,d){return this.each(function(){var e=a(this),f=e.data("bs.modal"),g=a.extend({},c.DEFAULTS,e.data(),"object"==typeof b&&b);f||e.data("bs.modal",f=new c(this,g)),"string"==typeof b?f[b](d):g.show&&f.show(d)})}var c=function(b,c){this.options=c,this.$body=a(document.body),this.$element=a(b),this.$dialog=this.$element.find(".modal-dialog"),this.$backdrop=null,this.isShown=null,this.originalBodyPad=null,this.scrollbarWidth=0,this.ignoreBackdropClick=!1,this.options.remote&&this.$element.find(".modal-content").load(this.options.remote,a.proxy(function(){this.$element.trigger("loaded.bs.modal")},this))};c.VERSION="3.3.6",c.TRANSITION_DURATION=300,c.BACKDROP_TRANSITION_DURATION=150,c.DEFAULTS={backdrop:!0,keyboard:!0,show:!0},c.prototype.toggle=function(a){return this.isShown?this.hide():this.show(a)},c.prototype.show=function(b){var d=this,e=a.Event("show.bs.modal",{relatedTarget:b});this.$element.trigger(e),this.isShown||e.isDefaultPrevented()||(this.isShown=!0,this.checkScrollbar(),this.setScrollbar(),this.$body.addClass("modal-open"),this.escape(),this.resize(),this.$element.on("click.dismiss.bs.modal",'[data-dismiss="modal"]',a.proxy(this.hide,this)),this.$dialog.on("mousedown.dismiss.bs.modal",function(){d.$element.one("mouseup.dismiss.bs.modal",function(b){a(b.target).is(d.$element)&&(d.ignoreBackdropClick=!0)})}),this.backdrop(function(){var e=a.support.transition&&d.$element.hasClass("fade");d.$element.parent().length||d.$element.appendTo(d.$body),d.$element.show().scrollTop(0),d.adjustDialog(),e&&d.$element[0].offsetWidth,d.$element.addClass("in"),d.enforceFocus();var f=a.Event("shown.bs.modal",{relatedTarget:b});e?d.$dialog.one("bsTransitionEnd",function(){d.$element.trigger("focus").trigger(f)}).emulateTransitionEnd(c.TRANSITION_DURATION):d.$element.trigger("focus").trigger(f)}))},c.prototype.hide=function(b){b&&b.preventDefault(),b=a.Event("hide.bs.modal"),this.$element.trigger(b),this.isShown&&!b.isDefaultPrevented()&&(this.isShown=!1,this.escape(),this.resize(),a(document).off("focusin.bs.modal"),this.$element.removeClass("in").off("click.dismiss.bs.modal").off("mouseup.dismiss.bs.modal"),this.$dialog.off("mousedown.dismiss.bs.modal"),a.support.transition&&this.$element.hasClass("fade")?this.$element.one("bsTransitionEnd",a.proxy(this.hideModal,this)).emulateTransitionEnd(c.TRANSITION_DURATION):this.hideModal())},c.prototype.enforceFocus=function(){a(document).off("focusin.bs.modal").on("focusin.bs.modal",a.proxy(function(a){this.$element[0]===a.target||this.$element.has(a.target).length||this.$element.trigger("focus")},this))},c.prototype.escape=function(){this.isShown&&this.options.keyboard?this.$element.on("keydown.dismiss.bs.modal",a.proxy(function(a){27==a.which&&this.hide()},this)):this.isShown||this.$element.off("keydown.dismiss.bs.modal")},c.prototype.resize=function(){this.isShown?a(window).on("resize.bs.modal",a.proxy(this.handleUpdate,this)):a(window).off("resize.bs.modal")},c.prototype.hideModal=function(){var a=this;this.$element.hide(),this.backdrop(function(){a.$body.removeClass("modal-open"),a.resetAdjustments(),a.resetScrollbar(),a.$element.trigger("hidden.bs.modal")})},c.prototype.removeBackdrop=function(){this.$backdrop&&this.$backdrop.remove(),this.$backdrop=null},c.prototype.backdrop=function(b){var d=this,e=this.$element.hasClass("fade")?"fade":"";if(this.isShown&&this.options.backdrop){var f=a.support.transition&&e;if(this.$backdrop=a(document.createElement("div")).addClass("modal-backdrop "+e).appendTo(this.$body),this.$element.on("click.dismiss.bs.modal",a.proxy(function(a){return this.ignoreBackdropClick?void(this.ignoreBackdropClick=!1):void(a.target===a.currentTarget&&("static"==this.options.backdrop?this.$element[0].focus():this.hide()))},this)),f&&this.$backdrop[0].offsetWidth,this.$backdrop.addClass("in"),!b)return;f?this.$backdrop.one("bsTransitionEnd",b).emulateTransitionEnd(c.BACKDROP_TRANSITION_DURATION):b()}else if(!this.isShown&&this.$backdrop){this.$backdrop.removeClass("in");var g=function(){d.removeBackdrop(),b&&b()};a.support.transition&&this.$element.hasClass("fade")?this.$backdrop.one("bsTransitionEnd",g).emulateTransitionEnd(c.BACKDROP_TRANSITION_DURATION):g()}else b&&b()},c.prototype.handleUpdate=function(){this.adjustDialog()},c.prototype.adjustDialog=function(){var a=this.$element[0].scrollHeight>document.documentElement.clientHeight;this.$element.css({paddingLeft:!this.bodyIsOverflowing&&a?this.scrollbarWidth:"",paddingRight:this.bodyIsOverflowing&&!a?this.scrollbarWidth:""})},c.prototype.resetAdjustments=function(){this.$element.css({paddingLeft:"",paddingRight:""})},c.prototype.checkScrollbar=function(){var a=window.innerWidth;if(!a){var b=document.documentElement.getBoundingClientRect();a=b.right-Math.abs(b.left)}this.bodyIsOverflowing=document.body.clientWidth<a,this.scrollbarWidth=this.measureScrollbar()},c.prototype.setScrollbar=function(){var a=parseInt(this.$body.css("padding-right")||0,10);this.originalBodyPad=document.body.style.paddingRight||"",this.bodyIsOverflowing&&this.$body.css("padding-right",a+this.scrollbarWidth)},c.prototype.resetScrollbar=function(){this.$body.css("padding-right",this.originalBodyPad)},c.prototype.measureScrollbar=function(){var a=document.createElement("div");a.className="modal-scrollbar-measure",this.$body.append(a);var b=a.offsetWidth-a.clientWidth;return this.$body[0].removeChild(a),b};var d=a.fn.modal;a.fn.modal=b,a.fn.modal.Constructor=c,a.fn.modal.noConflict=function(){return a.fn.modal=d,this},a(document).on("click.bs.modal.data-api",'[data-toggle="modal"]',function(c){var d=a(this),e=d.attr("href"),f=a(d.attr("data-target")||e&&e.replace(/.*(?=#[^\s]+$)/,"")),g=f.data("bs.modal")?"toggle":a.extend({remote:!/#/.test(e)&&e},f.data(),d.data());d.is("a")&&c.preventDefault(),f.one("show.bs.modal",function(a){a.isDefaultPrevented()||f.one("hidden.bs.modal",function(){d.is(":visible")&&d.trigger("focus")})}),b.call(f,g,this)})}(jQuery),+function(a){"use strict";function b(b){return this.each(function(){var d=a(this),e=d.data("bs.tooltip"),f="object"==typeof b&&b;(e||!/destroy|hide/.test(b))&&(e||d.data("bs.tooltip",e=new c(this,f)),"string"==typeof b&&e[b]())})}var c=function(a,b){this.type=null,this.options=null,this.enabled=null,this.timeout=null,this.hoverState=null,this.$element=null,this.inState=null,this.init("tooltip",a,b)};c.VERSION="3.3.6",c.TRANSITION_DURATION=150,c.DEFAULTS={animation:!0,placement:"top",selector:!1,template:'<div class="tooltip" role="tooltip"><div class="tooltip-arrow"></div><div class="tooltip-inner"></div></div>',trigger:"hover focus",title:"",delay:0,html:!1,container:!1,viewport:{selector:"body",padding:0}},c.prototype.init=function(b,c,d){if(this.enabled=!0,this.type=b,this.$element=a(c),this.options=this.getOptions(d),this.$viewport=this.options.viewport&&a(a.isFunction(this.options.viewport)?this.options.viewport.call(this,this.$element):this.options.viewport.selector||this.options.viewport),this.inState={click:!1,hover:!1,focus:!1},this.$element[0]instanceof document.constructor&&!this.options.selector)throw new Error("`selector` option must be specified when initializing "+this.type+" on the window.document object!");for(var e=this.options.trigger.split(" "),f=e.length;f--;){var g=e[f];if("click"==g)this.$element.on("click."+this.type,this.options.selector,a.proxy(this.toggle,this));else if("manual"!=g){var h="hover"==g?"mouseenter":"focusin",i="hover"==g?"mouseleave":"focusout";this.$element.on(h+"."+this.type,this.options.selector,a.proxy(this.enter,this)),this.$element.on(i+"."+this.type,this.options.selector,a.proxy(this.leave,this))}}this.options.selector?this._options=a.extend({},this.options,{trigger:"manual",selector:""}):this.fixTitle()},c.prototype.getDefaults=function(){return c.DEFAULTS},c.prototype.getOptions=function(b){return b=a.extend({},this.getDefaults(),this.$element.data(),b),b.delay&&"number"==typeof b.delay&&(b.delay={show:b.delay,hide:b.delay}),b},c.prototype.getDelegateOptions=function(){var b={},c=this.getDefaults();return this._options&&a.each(this._options,function(a,d){c[a]!=d&&(b[a]=d)}),b},c.prototype.enter=function(b){var c=b instanceof this.constructor?b:a(b.currentTarget).data("bs."+this.type);return c||(c=new this.constructor(b.currentTarget,this.getDelegateOptions()),a(b.currentTarget).data("bs."+this.type,c)),b instanceof a.Event&&(c.inState["focusin"==b.type?"focus":"hover"]=!0),c.tip().hasClass("in")||"in"==c.hoverState?void(c.hoverState="in"):(clearTimeout(c.timeout),c.hoverState="in",c.options.delay&&c.options.delay.show?void(c.timeout=setTimeout(function(){"in"==c.hoverState&&c.show()},c.options.delay.show)):c.show())},c.prototype.isInStateTrue=function(){for(var a in this.inState)if(this.inState[a])return!0;return!1},c.prototype.leave=function(b){var c=b instanceof this.constructor?b:a(b.currentTarget).data("bs."+this.type);return c||(c=new this.constructor(b.currentTarget,this.getDelegateOptions()),a(b.currentTarget).data("bs."+this.type,c)),b instanceof a.Event&&(c.inState["focusout"==b.type?"focus":"hover"]=!1),c.isInStateTrue()?void 0:(clearTimeout(c.timeout),c.hoverState="out",c.options.delay&&c.options.delay.hide?void(c.timeout=setTimeout(function(){"out"==c.hoverState&&c.hide()},c.options.delay.hide)):c.hide())},c.prototype.show=function(){var b=a.Event("show.bs."+this.type);if(this.hasContent()&&this.enabled){this.$element.trigger(b);var d=a.contains(this.$element[0].ownerDocument.documentElement,this.$element[0]);if(b.isDefaultPrevented()||!d)return;var e=this,f=this.tip(),g=this.getUID(this.type);this.setContent(),f.attr("id",g),this.$element.attr("aria-describedby",g),this.options.animation&&f.addClass("fade");var h="function"==typeof this.options.placement?this.options.placement.call(this,f[0],this.$element[0]):this.options.placement,i=/\s?auto?\s?/i,j=i.test(h);j&&(h=h.replace(i,"")||"top"),f.detach().css({top:0,left:0,display:"block"}).addClass(h).data("bs."+this.type,this),this.options.container?f.appendTo(this.options.container):f.insertAfter(this.$element),this.$element.trigger("inserted.bs."+this.type);var k=this.getPosition(),l=f[0].offsetWidth,m=f[0].offsetHeight;if(j){var n=h,o=this.getPosition(this.$viewport);h="bottom"==h&&k.bottom+m>o.bottom?"top":"top"==h&&k.top-m<o.top?"bottom":"right"==h&&k.right+l>o.width?"left":"left"==h&&k.left-l<o.left?"right":h,f.removeClass(n).addClass(h)}var p=this.getCalculatedOffset(h,k,l,m);this.applyPlacement(p,h);var q=function(){var a=e.hoverState;e.$element.trigger("shown.bs."+e.type),e.hoverState=null,"out"==a&&e.leave(e)};a.support.transition&&this.$tip.hasClass("fade")?f.one("bsTransitionEnd",q).emulateTransitionEnd(c.TRANSITION_DURATION):q()}},c.prototype.applyPlacement=function(b,c){var d=this.tip(),e=d[0].offsetWidth,f=d[0].offsetHeight,g=parseInt(d.css("margin-top"),10),h=parseInt(d.css("margin-left"),10);isNaN(g)&&(g=0),isNaN(h)&&(h=0),b.top+=g,b.left+=h,a.offset.setOffset(d[0],a.extend({using:function(a){d.css({top:Math.round(a.top),left:Math.round(a.left)})}},b),0),d.addClass("in");var i=d[0].offsetWidth,j=d[0].offsetHeight;"top"==c&&j!=f&&(b.top=b.top+f-j);var k=this.getViewportAdjustedDelta(c,b,i,j);k.left?b.left+=k.left:b.top+=k.top;var l=/top|bottom/.test(c),m=l?2*k.left-e+i:2*k.top-f+j,n=l?"offsetWidth":"offsetHeight";d.offset(b),this.replaceArrow(m,d[0][n],l)},c.prototype.replaceArrow=function(a,b,c){this.arrow().css(c?"left":"top",50*(1-a/b)+"%").css(c?"top":"left","")},c.prototype.setContent=function(){var a=this.tip(),b=this.getTitle();a.find(".tooltip-inner")[this.options.html?"html":"text"](b),a.removeClass("fade in top bottom left right")},c.prototype.hide=function(b){function d(){"in"!=e.hoverState&&f.detach(),e.$element.removeAttr("aria-describedby").trigger("hidden.bs."+e.type),b&&b()}var e=this,f=a(this.$tip),g=a.Event("hide.bs."+this.type);return this.$element.trigger(g),g.isDefaultPrevented()?void 0:(f.removeClass("in"),a.support.transition&&f.hasClass("fade")?f.one("bsTransitionEnd",d).emulateTransitionEnd(c.TRANSITION_DURATION):d(),this.hoverState=null,this)},c.prototype.fixTitle=function(){var a=this.$element;(a.attr("title")||"string"!=typeof a.attr("data-original-title"))&&a.attr("data-original-title",a.attr("title")||"").attr("title","")},c.prototype.hasContent=function(){return this.getTitle()},c.prototype.getPosition=function(b){b=b||this.$element;var c=b[0],d="BODY"==c.tagName,e=c.getBoundingClientRect();null==e.width&&(e=a.extend({},e,{width:e.right-e.left,height:e.bottom-e.top}));var f=d?{top:0,left:0}:b.offset(),g={scroll:d?document.documentElement.scrollTop||document.body.scrollTop:b.scrollTop()},h=d?{width:a(window).width(),height:a(window).height()}:null;return a.extend({},e,g,h,f)},c.prototype.getCalculatedOffset=function(a,b,c,d){return"bottom"==a?{top:b.top+b.height,left:b.left+b.width/2-c/2}:"top"==a?{top:b.top-d,left:b.left+b.width/2-c/2}:"left"==a?{top:b.top+b.height/2-d/2,left:b.left-c}:{top:b.top+b.height/2-d/2,left:b.left+b.width}},c.prototype.getViewportAdjustedDelta=function(a,b,c,d){var e={top:0,left:0};if(!this.$viewport)return e;var f=this.options.viewport&&this.options.viewport.padding||0,g=this.getPosition(this.$viewport);if(/right|left/.test(a)){var h=b.top-f-g.scroll,i=b.top+f-g.scroll+d;h<g.top?e.top=g.top-h:i>g.top+g.height&&(e.top=g.top+g.height-i)}else{var j=b.left-f,k=b.left+f+c;j<g.left?e.left=g.left-j:k>g.right&&(e.left=g.left+g.width-k)}return e},c.prototype.getTitle=function(){var a,b=this.$element,c=this.options;return a=b.attr("data-original-title")||("function"==typeof c.title?c.title.call(b[0]):c.title)},c.prototype.getUID=function(a){do a+=~~(1e6*Math.random());while(document.getElementById(a));return a},c.prototype.tip=function(){if(!this.$tip&&(this.$tip=a(this.options.template),1!=this.$tip.length))throw new Error(this.type+" `template` option must consist of exactly 1 top-level element!");return this.$tip},c.prototype.arrow=function(){return this.$arrow=this.$arrow||this.tip().find(".tooltip-arrow")},c.prototype.enable=function(){this.enabled=!0},c.prototype.disable=function(){this.enabled=!1},c.prototype.toggleEnabled=function(){this.enabled=!this.enabled},c.prototype.toggle=function(b){var c=this;b&&(c=a(b.currentTarget).data("bs."+this.type),c||(c=new this.constructor(b.currentTarget,this.getDelegateOptions()),a(b.currentTarget).data("bs."+this.type,c))),b?(c.inState.click=!c.inState.click,c.isInStateTrue()?c.enter(c):c.leave(c)):c.tip().hasClass("in")?c.leave(c):c.enter(c)},c.prototype.destroy=function(){var a=this;clearTimeout(this.timeout),this.hide(function(){a.$element.off("."+a.type).removeData("bs."+a.type),a.$tip&&a.$tip.detach(),a.$tip=null,a.$arrow=null,a.$viewport=null})};var d=a.fn.tooltip;a.fn.tooltip=b,a.fn.tooltip.Constructor=c,a.fn.tooltip.noConflict=function(){return a.fn.tooltip=d,this}}(jQuery),+function(a){"use strict";function b(b){return this.each(function(){var d=a(this),e=d.data("bs.popover"),f="object"==typeof b&&b;(e||!/destroy|hide/.test(b))&&(e||d.data("bs.popover",e=new c(this,f)),"string"==typeof b&&e[b]())})}var c=function(a,b){this.init("popover",a,b)};if(!a.fn.tooltip)throw new Error("Popover requires tooltip.js");c.VERSION="3.3.6",c.DEFAULTS=a.extend({},a.fn.tooltip.Constructor.DEFAULTS,{placement:"right",trigger:"click",content:"",template:'<div class="popover" role="tooltip"><div class="arrow"></div><h3 class="popover-title"></h3><div class="popover-content"></div></div>'}),c.prototype=a.extend({},a.fn.tooltip.Constructor.prototype),c.prototype.constructor=c,c.prototype.getDefaults=function(){return c.DEFAULTS},c.prototype.setContent=function(){var a=this.tip(),b=this.getTitle(),c=this.getContent();a.find(".popover-title")[this.options.html?"html":"text"](b),a.find(".popover-content").children().detach().end()[this.options.html?"string"==typeof c?"html":"append":"text"](c),a.removeClass("fade top bottom left right in"),a.find(".popover-title").html()||a.find(".popover-title").hide()},c.prototype.hasContent=function(){return this.getTitle()||this.getContent()},c.prototype.getContent=function(){var a=this.$element,b=this.options;return a.attr("data-content")||("function"==typeof b.content?b.content.call(a[0]):b.content)},c.prototype.arrow=function(){return this.$arrow=this.$arrow||this.tip().find(".arrow")};var d=a.fn.popover;a.fn.popover=b,a.fn.popover.Constructor=c,a.fn.popover.noConflict=function(){return a.fn.popover=d,this}}(jQuery),+function(a){"use strict";function b(c,d){this.$body=a(document.body),this.$scrollElement=a(a(c).is(document.body)?window:c),this.options=a.extend({},b.DEFAULTS,d),this.selector=(this.options.target||"")+" .nav li > a",this.offsets=[],this.targets=[],this.activeTarget=null,this.scrollHeight=0,this.$scrollElement.on("scroll.bs.scrollspy",a.proxy(this.process,this)),this.refresh(),this.process()}function c(c){return this.each(function(){var d=a(this),e=d.data("bs.scrollspy"),f="object"==typeof c&&c;e||d.data("bs.scrollspy",e=new b(this,f)),"string"==typeof c&&e[c]()})}b.VERSION="3.3.6",b.DEFAULTS={offset:10},b.prototype.getScrollHeight=function(){return this.$scrollElement[0].scrollHeight||Math.max(this.$body[0].scrollHeight,document.documentElement.scrollHeight)},b.prototype.refresh=function(){var b=this,c="offset",d=0;this.offsets=[],this.targets=[],this.scrollHeight=this.getScrollHeight(),a.isWindow(this.$scrollElement[0])||(c="position",d=this.$scrollElement.scrollTop()),this.$body.find(this.selector).map(function(){var b=a(this),e=b.data("target")||b.attr("href"),f=/^#./.test(e)&&a(e);return f&&f.length&&f.is(":visible")&&[[f[c]().top+d,e]]||null}).sort(function(a,b){return a[0]-b[0]}).each(function(){b.offsets.push(this[0]),b.targets.push(this[1])})},b.prototype.process=function(){var a,b=this.$scrollElement.scrollTop()+this.options.offset,c=this.getScrollHeight(),d=this.options.offset+c-this.$scrollElement.height(),e=this.offsets,f=this.targets,g=this.activeTarget;if(this.scrollHeight!=c&&this.refresh(),b>=d)return g!=(a=f[f.length-1])&&this.activate(a);if(g&&b<e[0])return this.activeTarget=null,this.clear();for(a=e.length;a--;)g!=f[a]&&b>=e[a]&&(void 0===e[a+1]||b<e[a+1])&&this.activate(f[a])},b.prototype.activate=function(b){this.activeTarget=b,this.clear();var c=this.selector+'[data-target="'+b+'"],'+this.selector+'[href="'+b+'"]',d=a(c).parents("li").addClass("active");
d.parent(".dropdown-menu").length&&(d=d.closest("li.dropdown").addClass("active")),d.trigger("activate.bs.scrollspy")},b.prototype.clear=function(){a(this.selector).parentsUntil(this.options.target,".active").removeClass("active")};var d=a.fn.scrollspy;a.fn.scrollspy=c,a.fn.scrollspy.Constructor=b,a.fn.scrollspy.noConflict=function(){return a.fn.scrollspy=d,this},a(window).on("load.bs.scrollspy.data-api",function(){a('[data-spy="scroll"]').each(function(){var b=a(this);c.call(b,b.data())})})}(jQuery),+function(a){"use strict";function b(b){return this.each(function(){var d=a(this),e=d.data("bs.tab");e||d.data("bs.tab",e=new c(this)),"string"==typeof b&&e[b]()})}var c=function(b){this.element=a(b)};c.VERSION="3.3.6",c.TRANSITION_DURATION=150,c.prototype.show=function(){var b=this.element,c=b.closest("ul:not(.dropdown-menu)"),d=b.data("target");if(d||(d=b.attr("href"),d=d&&d.replace(/.*(?=#[^\s]*$)/,"")),!b.parent("li").hasClass("active")){var e=c.find(".active:last a"),f=a.Event("hide.bs.tab",{relatedTarget:b[0]}),g=a.Event("show.bs.tab",{relatedTarget:e[0]});if(e.trigger(f),b.trigger(g),!g.isDefaultPrevented()&&!f.isDefaultPrevented()){var h=a(d);this.activate(b.closest("li"),c),this.activate(h,h.parent(),function(){e.trigger({type:"hidden.bs.tab",relatedTarget:b[0]}),b.trigger({type:"shown.bs.tab",relatedTarget:e[0]})})}}},c.prototype.activate=function(b,d,e){function f(){g.removeClass("active").find("> .dropdown-menu > .active").removeClass("active").end().find('[data-toggle="tab"]').attr("aria-expanded",!1),b.addClass("active").find('[data-toggle="tab"]').attr("aria-expanded",!0),h?(b[0].offsetWidth,b.addClass("in")):b.removeClass("fade"),b.parent(".dropdown-menu").length&&b.closest("li.dropdown").addClass("active").end().find('[data-toggle="tab"]').attr("aria-expanded",!0),e&&e()}var g=d.find("> .active"),h=e&&a.support.transition&&(g.length&&g.hasClass("fade")||!!d.find("> .fade").length);g.length&&h?g.one("bsTransitionEnd",f).emulateTransitionEnd(c.TRANSITION_DURATION):f(),g.removeClass("in")};var d=a.fn.tab;a.fn.tab=b,a.fn.tab.Constructor=c,a.fn.tab.noConflict=function(){return a.fn.tab=d,this};var e=function(c){c.preventDefault(),b.call(a(this),"show")};a(document).on("click.bs.tab.data-api",'[data-toggle="tab"]',e).on("click.bs.tab.data-api",'[data-toggle="pill"]',e)}(jQuery),+function(a){"use strict";function b(b){return this.each(function(){var d=a(this),e=d.data("bs.affix"),f="object"==typeof b&&b;e||d.data("bs.affix",e=new c(this,f)),"string"==typeof b&&e[b]()})}var c=function(b,d){this.options=a.extend({},c.DEFAULTS,d),this.$target=a(this.options.target).on("scroll.bs.affix.data-api",a.proxy(this.checkPosition,this)).on("click.bs.affix.data-api",a.proxy(this.checkPositionWithEventLoop,this)),this.$element=a(b),this.affixed=null,this.unpin=null,this.pinnedOffset=null,this.checkPosition()};c.VERSION="3.3.6",c.RESET="affix affix-top affix-bottom",c.DEFAULTS={offset:0,target:window},c.prototype.getState=function(a,b,c,d){var e=this.$target.scrollTop(),f=this.$element.offset(),g=this.$target.height();if(null!=c&&"top"==this.affixed)return c>e?"top":!1;if("bottom"==this.affixed)return null!=c?e+this.unpin<=f.top?!1:"bottom":a-d>=e+g?!1:"bottom";var h=null==this.affixed,i=h?e:f.top,j=h?g:b;return null!=c&&c>=e?"top":null!=d&&i+j>=a-d?"bottom":!1},c.prototype.getPinnedOffset=function(){if(this.pinnedOffset)return this.pinnedOffset;this.$element.removeClass(c.RESET).addClass("affix");var a=this.$target.scrollTop(),b=this.$element.offset();return this.pinnedOffset=b.top-a},c.prototype.checkPositionWithEventLoop=function(){setTimeout(a.proxy(this.checkPosition,this),1)},c.prototype.checkPosition=function(){if(this.$element.is(":visible")){var b=this.$element.height(),d=this.options.offset,e=d.top,f=d.bottom,g=Math.max(a(document).height(),a(document.body).height());"object"!=typeof d&&(f=e=d),"function"==typeof e&&(e=d.top(this.$element)),"function"==typeof f&&(f=d.bottom(this.$element));var h=this.getState(g,b,e,f);if(this.affixed!=h){null!=this.unpin&&this.$element.css("top","");var i="affix"+(h?"-"+h:""),j=a.Event(i+".bs.affix");if(this.$element.trigger(j),j.isDefaultPrevented())return;this.affixed=h,this.unpin="bottom"==h?this.getPinnedOffset():null,this.$element.removeClass(c.RESET).addClass(i).trigger(i.replace("affix","affixed")+".bs.affix")}"bottom"==h&&this.$element.offset({top:g-b-f})}};var d=a.fn.affix;a.fn.affix=b,a.fn.affix.Constructor=c,a.fn.affix.noConflict=function(){return a.fn.affix=d,this},a(window).on("load",function(){a('[data-spy="affix"]').each(function(){var c=a(this),d=c.data();d.offset=d.offset||{},null!=d.offsetBottom&&(d.offset.bottom=d.offsetBottom),null!=d.offsetTop&&(d.offset.top=d.offsetTop),b.call(c,d)})})}(jQuery);
(function(t){var e=typeof self=="object"&&self.self===self&&self||typeof global=="object"&&global.global===global&&global;if(typeof define==="function"&&define.amd){define(["underscore","jquery","exports"],function(i,r,n){e.Backbone=t(e,n,i,r)})}else if(typeof exports!=="undefined"){var i=require("underscore"),r;try{r=require("jquery")}catch(n){}t(e,exports,i,r)}else{e.Backbone=t(e,{},e._,e.jQuery||e.Zepto||e.ender||e.$)}})(function(t,e,i,r){var n=t.Backbone;var s=Array.prototype.slice;e.VERSION="1.3.3";e.$=r;e.noConflict=function(){t.Backbone=n;return this};e.emulateHTTP=false;e.emulateJSON=false;var a=function(t,e,r){switch(t){case 1:return function(){return i[e](this[r])};case 2:return function(t){return i[e](this[r],t)};case 3:return function(t,n){return i[e](this[r],o(t,this),n)};case 4:return function(t,n,s){return i[e](this[r],o(t,this),n,s)};default:return function(){var t=s.call(arguments);t.unshift(this[r]);return i[e].apply(i,t)}}};var h=function(t,e,r){i.each(e,function(e,n){if(i[n])t.prototype[n]=a(e,n,r)})};var o=function(t,e){if(i.isFunction(t))return t;if(i.isObject(t)&&!e._isModel(t))return l(t);if(i.isString(t))return function(e){return e.get(t)};return t};var l=function(t){var e=i.matches(t);return function(t){return e(t.attributes)}};var u=e.Events={};var c=/\s+/;var f=function(t,e,r,n,s){var a=0,h;if(r&&typeof r==="object"){if(n!==void 0&&"context"in s&&s.context===void 0)s.context=n;for(h=i.keys(r);a<h.length;a++){e=f(t,e,h[a],r[h[a]],s)}}else if(r&&c.test(r)){for(h=r.split(c);a<h.length;a++){e=t(e,h[a],n,s)}}else{e=t(e,r,n,s)}return e};u.on=function(t,e,i){return d(this,t,e,i)};var d=function(t,e,i,r,n){t._events=f(v,t._events||{},e,i,{context:r,ctx:t,listening:n});if(n){var s=t._listeners||(t._listeners={});s[n.id]=n}return t};u.listenTo=function(t,e,r){if(!t)return this;var n=t._listenId||(t._listenId=i.uniqueId("l"));var s=this._listeningTo||(this._listeningTo={});var a=s[n];if(!a){var h=this._listenId||(this._listenId=i.uniqueId("l"));a=s[n]={obj:t,objId:n,id:h,listeningTo:s,count:0}}d(t,e,r,this,a);return this};var v=function(t,e,i,r){if(i){var n=t[e]||(t[e]=[]);var s=r.context,a=r.ctx,h=r.listening;if(h)h.count++;n.push({callback:i,context:s,ctx:s||a,listening:h})}return t};u.off=function(t,e,i){if(!this._events)return this;this._events=f(g,this._events,t,e,{context:i,listeners:this._listeners});return this};u.stopListening=function(t,e,r){var n=this._listeningTo;if(!n)return this;var s=t?[t._listenId]:i.keys(n);for(var a=0;a<s.length;a++){var h=n[s[a]];if(!h)break;h.obj.off(e,r,this)}return this};var g=function(t,e,r,n){if(!t)return;var s=0,a;var h=n.context,o=n.listeners;if(!e&&!r&&!h){var l=i.keys(o);for(;s<l.length;s++){a=o[l[s]];delete o[a.id];delete a.listeningTo[a.objId]}return}var u=e?[e]:i.keys(t);for(;s<u.length;s++){e=u[s];var c=t[e];if(!c)break;var f=[];for(var d=0;d<c.length;d++){var v=c[d];if(r&&r!==v.callback&&r!==v.callback._callback||h&&h!==v.context){f.push(v)}else{a=v.listening;if(a&&--a.count===0){delete o[a.id];delete a.listeningTo[a.objId]}}}if(f.length){t[e]=f}else{delete t[e]}}return t};u.once=function(t,e,r){var n=f(p,{},t,e,i.bind(this.off,this));if(typeof t==="string"&&r==null)e=void 0;return this.on(n,e,r)};u.listenToOnce=function(t,e,r){var n=f(p,{},e,r,i.bind(this.stopListening,this,t));return this.listenTo(t,n)};var p=function(t,e,r,n){if(r){var s=t[e]=i.once(function(){n(e,s);r.apply(this,arguments)});s._callback=r}return t};u.trigger=function(t){if(!this._events)return this;var e=Math.max(0,arguments.length-1);var i=Array(e);for(var r=0;r<e;r++)i[r]=arguments[r+1];f(m,this._events,t,void 0,i);return this};var m=function(t,e,i,r){if(t){var n=t[e];var s=t.all;if(n&&s)s=s.slice();if(n)_(n,r);if(s)_(s,[e].concat(r))}return t};var _=function(t,e){var i,r=-1,n=t.length,s=e[0],a=e[1],h=e[2];switch(e.length){case 0:while(++r<n)(i=t[r]).callback.call(i.ctx);return;case 1:while(++r<n)(i=t[r]).callback.call(i.ctx,s);return;case 2:while(++r<n)(i=t[r]).callback.call(i.ctx,s,a);return;case 3:while(++r<n)(i=t[r]).callback.call(i.ctx,s,a,h);return;default:while(++r<n)(i=t[r]).callback.apply(i.ctx,e);return}};u.bind=u.on;u.unbind=u.off;i.extend(e,u);var y=e.Model=function(t,e){var r=t||{};e||(e={});this.cid=i.uniqueId(this.cidPrefix);this.attributes={};if(e.collection)this.collection=e.collection;if(e.parse)r=this.parse(r,e)||{};var n=i.result(this,"defaults");r=i.defaults(i.extend({},n,r),n);this.set(r,e);this.changed={};this.initialize.apply(this,arguments)};i.extend(y.prototype,u,{changed:null,validationError:null,idAttribute:"id",cidPrefix:"c",initialize:function(){},toJSON:function(t){return i.clone(this.attributes)},sync:function(){return e.sync.apply(this,arguments)},get:function(t){return this.attributes[t]},escape:function(t){return i.escape(this.get(t))},has:function(t){return this.get(t)!=null},matches:function(t){return!!i.iteratee(t,this)(this.attributes)},set:function(t,e,r){if(t==null)return this;var n;if(typeof t==="object"){n=t;r=e}else{(n={})[t]=e}r||(r={});if(!this._validate(n,r))return false;var s=r.unset;var a=r.silent;var h=[];var o=this._changing;this._changing=true;if(!o){this._previousAttributes=i.clone(this.attributes);this.changed={}}var l=this.attributes;var u=this.changed;var c=this._previousAttributes;for(var f in n){e=n[f];if(!i.isEqual(l[f],e))h.push(f);if(!i.isEqual(c[f],e)){u[f]=e}else{delete u[f]}s?delete l[f]:l[f]=e}if(this.idAttribute in n)this.id=this.get(this.idAttribute);if(!a){if(h.length)this._pending=r;for(var d=0;d<h.length;d++){this.trigger("change:"+h[d],this,l[h[d]],r)}}if(o)return this;if(!a){while(this._pending){r=this._pending;this._pending=false;this.trigger("change",this,r)}}this._pending=false;this._changing=false;return this},unset:function(t,e){return this.set(t,void 0,i.extend({},e,{unset:true}))},clear:function(t){var e={};for(var r in this.attributes)e[r]=void 0;return this.set(e,i.extend({},t,{unset:true}))},hasChanged:function(t){if(t==null)return!i.isEmpty(this.changed);return i.has(this.changed,t)},changedAttributes:function(t){if(!t)return this.hasChanged()?i.clone(this.changed):false;var e=this._changing?this._previousAttributes:this.attributes;var r={};for(var n in t){var s=t[n];if(i.isEqual(e[n],s))continue;r[n]=s}return i.size(r)?r:false},previous:function(t){if(t==null||!this._previousAttributes)return null;return this._previousAttributes[t]},previousAttributes:function(){return i.clone(this._previousAttributes)},fetch:function(t){t=i.extend({parse:true},t);var e=this;var r=t.success;t.success=function(i){var n=t.parse?e.parse(i,t):i;if(!e.set(n,t))return false;if(r)r.call(t.context,e,i,t);e.trigger("sync",e,i,t)};B(this,t);return this.sync("read",this,t)},save:function(t,e,r){var n;if(t==null||typeof t==="object"){n=t;r=e}else{(n={})[t]=e}r=i.extend({validate:true,parse:true},r);var s=r.wait;if(n&&!s){if(!this.set(n,r))return false}else if(!this._validate(n,r)){return false}var a=this;var h=r.success;var o=this.attributes;r.success=function(t){a.attributes=o;var e=r.parse?a.parse(t,r):t;if(s)e=i.extend({},n,e);if(e&&!a.set(e,r))return false;if(h)h.call(r.context,a,t,r);a.trigger("sync",a,t,r)};B(this,r);if(n&&s)this.attributes=i.extend({},o,n);var l=this.isNew()?"create":r.patch?"patch":"update";if(l==="patch"&&!r.attrs)r.attrs=n;var u=this.sync(l,this,r);this.attributes=o;return u},destroy:function(t){t=t?i.clone(t):{};var e=this;var r=t.success;var n=t.wait;var s=function(){e.stopListening();e.trigger("destroy",e,e.collection,t)};t.success=function(i){if(n)s();if(r)r.call(t.context,e,i,t);if(!e.isNew())e.trigger("sync",e,i,t)};var a=false;if(this.isNew()){i.defer(t.success)}else{B(this,t);a=this.sync("delete",this,t)}if(!n)s();return a},url:function(){var t=i.result(this,"urlRoot")||i.result(this.collection,"url")||F();if(this.isNew())return t;var e=this.get(this.idAttribute);return t.replace(/[^\/]$/,"$&/")+encodeURIComponent(e)},parse:function(t,e){return t},clone:function(){return new this.constructor(this.attributes)},isNew:function(){return!this.has(this.idAttribute)},isValid:function(t){return this._validate({},i.extend({},t,{validate:true}))},_validate:function(t,e){if(!e.validate||!this.validate)return true;t=i.extend({},this.attributes,t);var r=this.validationError=this.validate(t,e)||null;if(!r)return true;this.trigger("invalid",this,r,i.extend(e,{validationError:r}));return false}});var b={keys:1,values:1,pairs:1,invert:1,pick:0,omit:0,chain:1,isEmpty:1};h(y,b,"attributes");var x=e.Collection=function(t,e){e||(e={});if(e.model)this.model=e.model;if(e.comparator!==void 0)this.comparator=e.comparator;this._reset();this.initialize.apply(this,arguments);if(t)this.reset(t,i.extend({silent:true},e))};var w={add:true,remove:true,merge:true};var E={add:true,remove:false};var I=function(t,e,i){i=Math.min(Math.max(i,0),t.length);var r=Array(t.length-i);var n=e.length;var s;for(s=0;s<r.length;s++)r[s]=t[s+i];for(s=0;s<n;s++)t[s+i]=e[s];for(s=0;s<r.length;s++)t[s+n+i]=r[s]};i.extend(x.prototype,u,{model:y,initialize:function(){},toJSON:function(t){return this.map(function(e){return e.toJSON(t)})},sync:function(){return e.sync.apply(this,arguments)},add:function(t,e){return this.set(t,i.extend({merge:false},e,E))},remove:function(t,e){e=i.extend({},e);var r=!i.isArray(t);t=r?[t]:t.slice();var n=this._removeModels(t,e);if(!e.silent&&n.length){e.changes={added:[],merged:[],removed:n};this.trigger("update",this,e)}return r?n[0]:n},set:function(t,e){if(t==null)return;e=i.extend({},w,e);if(e.parse&&!this._isModel(t)){t=this.parse(t,e)||[]}var r=!i.isArray(t);t=r?[t]:t.slice();var n=e.at;if(n!=null)n=+n;if(n>this.length)n=this.length;if(n<0)n+=this.length+1;var s=[];var a=[];var h=[];var o=[];var l={};var u=e.add;var c=e.merge;var f=e.remove;var d=false;var v=this.comparator&&n==null&&e.sort!==false;var g=i.isString(this.comparator)?this.comparator:null;var p,m;for(m=0;m<t.length;m++){p=t[m];var _=this.get(p);if(_){if(c&&p!==_){var y=this._isModel(p)?p.attributes:p;if(e.parse)y=_.parse(y,e);_.set(y,e);h.push(_);if(v&&!d)d=_.hasChanged(g)}if(!l[_.cid]){l[_.cid]=true;s.push(_)}t[m]=_}else if(u){p=t[m]=this._prepareModel(p,e);if(p){a.push(p);this._addReference(p,e);l[p.cid]=true;s.push(p)}}}if(f){for(m=0;m<this.length;m++){p=this.models[m];if(!l[p.cid])o.push(p)}if(o.length)this._removeModels(o,e)}var b=false;var x=!v&&u&&f;if(s.length&&x){b=this.length!==s.length||i.some(this.models,function(t,e){return t!==s[e]});this.models.length=0;I(this.models,s,0);this.length=this.models.length}else if(a.length){if(v)d=true;I(this.models,a,n==null?this.length:n);this.length=this.models.length}if(d)this.sort({silent:true});if(!e.silent){for(m=0;m<a.length;m++){if(n!=null)e.index=n+m;p=a[m];p.trigger("add",p,this,e)}if(d||b)this.trigger("sort",this,e);if(a.length||o.length||h.length){e.changes={added:a,removed:o,merged:h};this.trigger("update",this,e)}}return r?t[0]:t},reset:function(t,e){e=e?i.clone(e):{};for(var r=0;r<this.models.length;r++){this._removeReference(this.models[r],e)}e.previousModels=this.models;this._reset();t=this.add(t,i.extend({silent:true},e));if(!e.silent)this.trigger("reset",this,e);return t},push:function(t,e){return this.add(t,i.extend({at:this.length},e))},pop:function(t){var e=this.at(this.length-1);return this.remove(e,t)},unshift:function(t,e){return this.add(t,i.extend({at:0},e))},shift:function(t){var e=this.at(0);return this.remove(e,t)},slice:function(){return s.apply(this.models,arguments)},get:function(t){if(t==null)return void 0;return this._byId[t]||this._byId[this.modelId(t.attributes||t)]||t.cid&&this._byId[t.cid]},has:function(t){return this.get(t)!=null},at:function(t){if(t<0)t+=this.length;return this.models[t]},where:function(t,e){return this[e?"find":"filter"](t)},findWhere:function(t){return this.where(t,true)},sort:function(t){var e=this.comparator;if(!e)throw new Error("Cannot sort a set without a comparator");t||(t={});var r=e.length;if(i.isFunction(e))e=i.bind(e,this);if(r===1||i.isString(e)){this.models=this.sortBy(e)}else{this.models.sort(e)}if(!t.silent)this.trigger("sort",this,t);return this},pluck:function(t){return this.map(t+"")},fetch:function(t){t=i.extend({parse:true},t);var e=t.success;var r=this;t.success=function(i){var n=t.reset?"reset":"set";r[n](i,t);if(e)e.call(t.context,r,i,t);r.trigger("sync",r,i,t)};B(this,t);return this.sync("read",this,t)},create:function(t,e){e=e?i.clone(e):{};var r=e.wait;t=this._prepareModel(t,e);if(!t)return false;if(!r)this.add(t,e);var n=this;var s=e.success;e.success=function(t,e,i){if(r)n.add(t,i);if(s)s.call(i.context,t,e,i)};t.save(null,e);return t},parse:function(t,e){return t},clone:function(){return new this.constructor(this.models,{model:this.model,comparator:this.comparator})},modelId:function(t){return t[this.model.prototype.idAttribute||"id"]},_reset:function(){this.length=0;this.models=[];this._byId={}},_prepareModel:function(t,e){if(this._isModel(t)){if(!t.collection)t.collection=this;return t}e=e?i.clone(e):{};e.collection=this;var r=new this.model(t,e);if(!r.validationError)return r;this.trigger("invalid",this,r.validationError,e);return false},_removeModels:function(t,e){var i=[];for(var r=0;r<t.length;r++){var n=this.get(t[r]);if(!n)continue;var s=this.indexOf(n);this.models.splice(s,1);this.length--;delete this._byId[n.cid];var a=this.modelId(n.attributes);if(a!=null)delete this._byId[a];if(!e.silent){e.index=s;n.trigger("remove",n,this,e)}i.push(n);this._removeReference(n,e)}return i},_isModel:function(t){return t instanceof y},_addReference:function(t,e){this._byId[t.cid]=t;var i=this.modelId(t.attributes);if(i!=null)this._byId[i]=t;t.on("all",this._onModelEvent,this)},_removeReference:function(t,e){delete this._byId[t.cid];var i=this.modelId(t.attributes);if(i!=null)delete this._byId[i];if(this===t.collection)delete t.collection;t.off("all",this._onModelEvent,this)},_onModelEvent:function(t,e,i,r){if(e){if((t==="add"||t==="remove")&&i!==this)return;if(t==="destroy")this.remove(e,r);if(t==="change"){var n=this.modelId(e.previousAttributes());var s=this.modelId(e.attributes);if(n!==s){if(n!=null)delete this._byId[n];if(s!=null)this._byId[s]=e}}}this.trigger.apply(this,arguments)}});var S={forEach:3,each:3,map:3,collect:3,reduce:0,foldl:0,inject:0,reduceRight:0,foldr:0,find:3,detect:3,filter:3,select:3,reject:3,every:3,all:3,some:3,any:3,include:3,includes:3,contains:3,invoke:0,max:3,min:3,toArray:1,size:1,first:3,head:3,take:3,initial:3,rest:3,tail:3,drop:3,last:3,without:0,difference:0,indexOf:3,shuffle:1,lastIndexOf:3,isEmpty:1,chain:1,sample:3,partition:3,groupBy:3,countBy:3,sortBy:3,indexBy:3,findIndex:3,findLastIndex:3};h(x,S,"models");var k=e.View=function(t){this.cid=i.uniqueId("view");i.extend(this,i.pick(t,P));this._ensureElement();this.initialize.apply(this,arguments)};var T=/^(\S+)\s*(.*)$/;var P=["model","collection","el","id","attributes","className","tagName","events"];i.extend(k.prototype,u,{tagName:"div",$:function(t){return this.$el.find(t)},initialize:function(){},render:function(){return this},remove:function(){this._removeElement();this.stopListening();return this},_removeElement:function(){this.$el.remove()},setElement:function(t){this.undelegateEvents();this._setElement(t);this.delegateEvents();return this},_setElement:function(t){this.$el=t instanceof e.$?t:e.$(t);this.el=this.$el[0]},delegateEvents:function(t){t||(t=i.result(this,"events"));if(!t)return this;this.undelegateEvents();for(var e in t){var r=t[e];if(!i.isFunction(r))r=this[r];if(!r)continue;var n=e.match(T);this.delegate(n[1],n[2],i.bind(r,this))}return this},delegate:function(t,e,i){this.$el.on(t+".delegateEvents"+this.cid,e,i);return this},undelegateEvents:function(){if(this.$el)this.$el.off(".delegateEvents"+this.cid);return this},undelegate:function(t,e,i){this.$el.off(t+".delegateEvents"+this.cid,e,i);return this},_createElement:function(t){return document.createElement(t)},_ensureElement:function(){if(!this.el){var t=i.extend({},i.result(this,"attributes"));if(this.id)t.id=i.result(this,"id");if(this.className)t["class"]=i.result(this,"className");this.setElement(this._createElement(i.result(this,"tagName")));this._setAttributes(t)}else{this.setElement(i.result(this,"el"))}},_setAttributes:function(t){this.$el.attr(t)}});e.sync=function(t,r,n){var s=H[t];i.defaults(n||(n={}),{emulateHTTP:e.emulateHTTP,emulateJSON:e.emulateJSON});var a={type:s,dataType:"json"};if(!n.url){a.url=i.result(r,"url")||F()}if(n.data==null&&r&&(t==="create"||t==="update"||t==="patch")){a.contentType="application/json";a.data=JSON.stringify(n.attrs||r.toJSON(n))}if(n.emulateJSON){a.contentType="application/x-www-form-urlencoded";a.data=a.data?{model:a.data}:{}}if(n.emulateHTTP&&(s==="PUT"||s==="DELETE"||s==="PATCH")){a.type="POST";if(n.emulateJSON)a.data._method=s;var h=n.beforeSend;n.beforeSend=function(t){t.setRequestHeader("X-HTTP-Method-Override",s);if(h)return h.apply(this,arguments)}}if(a.type!=="GET"&&!n.emulateJSON){a.processData=false}var o=n.error;n.error=function(t,e,i){n.textStatus=e;n.errorThrown=i;if(o)o.call(n.context,t,e,i)};var l=n.xhr=e.ajax(i.extend(a,n));r.trigger("request",r,l,n);return l};var H={create:"POST",update:"PUT",patch:"PATCH","delete":"DELETE",read:"GET"};e.ajax=function(){return e.$.ajax.apply(e.$,arguments)};var $=e.Router=function(t){t||(t={});if(t.routes)this.routes=t.routes;this._bindRoutes();this.initialize.apply(this,arguments)};var A=/\((.*?)\)/g;var C=/(\(\?)?:\w+/g;var R=/\*\w+/g;var j=/[\-{}\[\]+?.,\\\^$|#\s]/g;i.extend($.prototype,u,{initialize:function(){},route:function(t,r,n){if(!i.isRegExp(t))t=this._routeToRegExp(t);if(i.isFunction(r)){n=r;r=""}if(!n)n=this[r];var s=this;e.history.route(t,function(i){var a=s._extractParameters(t,i);if(s.execute(n,a,r)!==false){s.trigger.apply(s,["route:"+r].concat(a));s.trigger("route",r,a);e.history.trigger("route",s,r,a)}});return this},execute:function(t,e,i){if(t)t.apply(this,e)},navigate:function(t,i){e.history.navigate(t,i);return this},_bindRoutes:function(){if(!this.routes)return;this.routes=i.result(this,"routes");var t,e=i.keys(this.routes);while((t=e.pop())!=null){this.route(t,this.routes[t])}},_routeToRegExp:function(t){t=t.replace(j,"\\$&").replace(A,"(?:$1)?").replace(C,function(t,e){return e?t:"([^/?]+)"}).replace(R,"([^?]*?)");return new RegExp("^"+t+"(?:\\?([\\s\\S]*))?$")},_extractParameters:function(t,e){var r=t.exec(e).slice(1);return i.map(r,function(t,e){if(e===r.length-1)return t||null;return t?decodeURIComponent(t):null})}});var N=e.History=function(){this.handlers=[];this.checkUrl=i.bind(this.checkUrl,this);if(typeof window!=="undefined"){this.location=window.location;this.history=window.history}};var M=/^[#\/]|\s+$/g;var O=/^\/+|\/+$/g;var U=/#.*$/;N.started=false;i.extend(N.prototype,u,{interval:50,atRoot:function(){var t=this.location.pathname.replace(/[^\/]$/,"$&/");return t===this.root&&!this.getSearch()},matchRoot:function(){var t=this.decodeFragment(this.location.pathname);var e=t.slice(0,this.root.length-1)+"/";return e===this.root},decodeFragment:function(t){return decodeURI(t.replace(/%25/g,"%2525"))},getSearch:function(){var t=this.location.href.replace(/#.*/,"").match(/\?.+/);return t?t[0]:""},getHash:function(t){var e=(t||this).location.href.match(/#(.*)$/);return e?e[1]:""},getPath:function(){var t=this.decodeFragment(this.location.pathname+this.getSearch()).slice(this.root.length-1);return t.charAt(0)==="/"?t.slice(1):t},getFragment:function(t){if(t==null){if(this._usePushState||!this._wantsHashChange){t=this.getPath()}else{t=this.getHash()}}return t.replace(M,"")},start:function(t){if(N.started)throw new Error("Backbone.history has already been started");N.started=true;this.options=i.extend({root:"/"},this.options,t);this.root=this.options.root;this._wantsHashChange=this.options.hashChange!==false;this._hasHashChange="onhashchange"in window&&(document.documentMode===void 0||document.documentMode>7);this._useHashChange=this._wantsHashChange&&this._hasHashChange;this._wantsPushState=!!this.options.pushState;this._hasPushState=!!(this.history&&this.history.pushState);this._usePushState=this._wantsPushState&&this._hasPushState;this.fragment=this.getFragment();this.root=("/"+this.root+"/").replace(O,"/");if(this._wantsHashChange&&this._wantsPushState){if(!this._hasPushState&&!this.atRoot()){var e=this.root.slice(0,-1)||"/";this.location.replace(e+"#"+this.getPath());return true}else if(this._hasPushState&&this.atRoot()){this.navigate(this.getHash(),{replace:true})}}if(!this._hasHashChange&&this._wantsHashChange&&!this._usePushState){this.iframe=document.createElement("iframe");this.iframe.src="javascript:0";this.iframe.style.display="none";this.iframe.tabIndex=-1;var r=document.body;var n=r.insertBefore(this.iframe,r.firstChild).contentWindow;n.document.open();n.document.close();n.location.hash="#"+this.fragment}var s=window.addEventListener||function(t,e){return attachEvent("on"+t,e)};if(this._usePushState){s("popstate",this.checkUrl,false)}else if(this._useHashChange&&!this.iframe){s("hashchange",this.checkUrl,false)}else if(this._wantsHashChange){this._checkUrlInterval=setInterval(this.checkUrl,this.interval)}if(!this.options.silent)return this.loadUrl()},stop:function(){var t=window.removeEventListener||function(t,e){return detachEvent("on"+t,e)};if(this._usePushState){t("popstate",this.checkUrl,false)}else if(this._useHashChange&&!this.iframe){t("hashchange",this.checkUrl,false)}if(this.iframe){document.body.removeChild(this.iframe);this.iframe=null}if(this._checkUrlInterval)clearInterval(this._checkUrlInterval);N.started=false},route:function(t,e){this.handlers.unshift({route:t,callback:e})},checkUrl:function(t){var e=this.getFragment();if(e===this.fragment&&this.iframe){e=this.getHash(this.iframe.contentWindow)}if(e===this.fragment)return false;if(this.iframe)this.navigate(e);this.loadUrl()},loadUrl:function(t){if(!this.matchRoot())return false;t=this.fragment=this.getFragment(t);return i.some(this.handlers,function(e){if(e.route.test(t)){e.callback(t);return true}})},navigate:function(t,e){if(!N.started)return false;if(!e||e===true)e={trigger:!!e};t=this.getFragment(t||"");var i=this.root;if(t===""||t.charAt(0)==="?"){i=i.slice(0,-1)||"/"}var r=i+t;t=this.decodeFragment(t.replace(U,""));if(this.fragment===t)return;this.fragment=t;if(this._usePushState){this.history[e.replace?"replaceState":"pushState"]({},document.title,r)}else if(this._wantsHashChange){this._updateHash(this.location,t,e.replace);if(this.iframe&&t!==this.getHash(this.iframe.contentWindow)){var n=this.iframe.contentWindow;if(!e.replace){n.document.open();n.document.close()}this._updateHash(n.location,t,e.replace)}}else{return this.location.assign(r)}if(e.trigger)return this.loadUrl(t)},_updateHash:function(t,e,i){if(i){var r=t.href.replace(/(javascript:|#).*$/,"");t.replace(r+"#"+e)}else{t.hash="#"+e}}});e.history=new N;var q=function(t,e){var r=this;var n;if(t&&i.has(t,"constructor")){n=t.constructor}else{n=function(){return r.apply(this,arguments)}}i.extend(n,r,e);n.prototype=i.create(r.prototype,t);n.prototype.constructor=n;n.__super__=r.prototype;return n};y.extend=x.extend=$.extend=k.extend=N.extend=q;var F=function(){throw new Error('A "url" property or function must be specified')};var B=function(t,e){var i=e.error;e.error=function(r){if(i)i.call(e.context,t,r,e);t.trigger("error",t,r,e)}};return e});
(function(root,factory){if(typeof define==='function'&&define.amd){define(['backbone','underscore'],function(Backbone,_){return(root.Marionette=factory(root,Backbone,_));});}else if(typeof exports!=='undefined'){var Backbone=require('backbone');var _=require('underscore');module.exports=factory(root,Backbone,_);}else{root.Marionette=factory(root,root.Backbone,root._);}}(this,function(root,Backbone,_){'use strict';(function(Backbone,_){"use strict";var previousChildViewContainer=Backbone.ChildViewContainer;Backbone.ChildViewContainer=function(Backbone,_){var Container=function(views){this._views={};this._indexByModel={};this._indexByCustom={};this._updateLength();_.each(views,this.add,this);};_.extend(Container.prototype,{add:function(view,customIndex){var viewCid=view.cid;this._views[viewCid]=view;if(view.model){this._indexByModel[view.model.cid]=viewCid;}
if(customIndex){this._indexByCustom[customIndex]=viewCid;}
this._updateLength();return this;},findByModel:function(model){return this.findByModelCid(model.cid);},findByModelCid:function(modelCid){var viewCid=this._indexByModel[modelCid];return this.findByCid(viewCid);},findByCustom:function(index){var viewCid=this._indexByCustom[index];return this.findByCid(viewCid);},findByIndex:function(index){return _.values(this._views)[index];},findByCid:function(cid){return this._views[cid];},remove:function(view){var viewCid=view.cid;if(view.model){delete this._indexByModel[view.model.cid];}
_.any(this._indexByCustom,function(cid,key){if(cid===viewCid){delete this._indexByCustom[key];return true;}},this);delete this._views[viewCid];this._updateLength();return this;},call:function(method){this.apply(method,_.tail(arguments));},apply:function(method,args){_.each(this._views,function(view){if(_.isFunction(view[method])){view[method].apply(view,args||[]);}});},_updateLength:function(){this.length=_.size(this._views);}});var methods=["forEach","each","map","find","detect","filter","select","reject","every","all","some","any","include","contains","invoke","toArray","first","initial","rest","last","without","isEmpty","pluck"];_.each(methods,function(method){Container.prototype[method]=function(){var views=_.values(this._views);var args=[views].concat(_.toArray(arguments));return _[method].apply(_,args);};});return Container;}(Backbone,_);Backbone.ChildViewContainer.VERSION="0.1.4";Backbone.ChildViewContainer.noConflict=function(){Backbone.ChildViewContainer=previousChildViewContainer;return this;};return Backbone.ChildViewContainer;})(Backbone,_);(function(Backbone,_){"use strict";var previousWreqr=Backbone.Wreqr;var Wreqr=Backbone.Wreqr={};Backbone.Wreqr.VERSION="1.3.1";Backbone.Wreqr.noConflict=function(){Backbone.Wreqr=previousWreqr;return this;};Wreqr.Handlers=function(Backbone,_){"use strict";var Handlers=function(options){this.options=options;this._wreqrHandlers={};if(_.isFunction(this.initialize)){this.initialize(options);}};Handlers.extend=Backbone.Model.extend;_.extend(Handlers.prototype,Backbone.Events,{setHandlers:function(handlers){_.each(handlers,function(handler,name){var context=null;if(_.isObject(handler)&&!_.isFunction(handler)){context=handler.context;handler=handler.callback;}
this.setHandler(name,handler,context);},this);},setHandler:function(name,handler,context){var config={callback:handler,context:context};this._wreqrHandlers[name]=config;this.trigger("handler:add",name,handler,context);},hasHandler:function(name){return!!this._wreqrHandlers[name];},getHandler:function(name){var config=this._wreqrHandlers[name];if(!config){return;}
return function(){var args=Array.prototype.slice.apply(arguments);return config.callback.apply(config.context,args);};},removeHandler:function(name){delete this._wreqrHandlers[name];},removeAllHandlers:function(){this._wreqrHandlers={};}});return Handlers;}(Backbone,_);Wreqr.CommandStorage=function(){"use strict";var CommandStorage=function(options){this.options=options;this._commands={};if(_.isFunction(this.initialize)){this.initialize(options);}};_.extend(CommandStorage.prototype,Backbone.Events,{getCommands:function(commandName){var commands=this._commands[commandName];if(!commands){commands={command:commandName,instances:[]};this._commands[commandName]=commands;}
return commands;},addCommand:function(commandName,args){var command=this.getCommands(commandName);command.instances.push(args);},clearCommands:function(commandName){var command=this.getCommands(commandName);command.instances=[];}});return CommandStorage;}();Wreqr.Commands=function(Wreqr){"use strict";return Wreqr.Handlers.extend({storageType:Wreqr.CommandStorage,constructor:function(options){this.options=options||{};this._initializeStorage(this.options);this.on("handler:add",this._executeCommands,this);var args=Array.prototype.slice.call(arguments);Wreqr.Handlers.prototype.constructor.apply(this,args);},execute:function(name,args){name=arguments[0];args=Array.prototype.slice.call(arguments,1);if(this.hasHandler(name)){this.getHandler(name).apply(this,args);}else{this.storage.addCommand(name,args);}},_executeCommands:function(name,handler,context){var command=this.storage.getCommands(name);_.each(command.instances,function(args){handler.apply(context,args);});this.storage.clearCommands(name);},_initializeStorage:function(options){var storage;var StorageType=options.storageType||this.storageType;if(_.isFunction(StorageType)){storage=new StorageType();}else{storage=StorageType;}
this.storage=storage;}});}(Wreqr);Wreqr.RequestResponse=function(Wreqr){"use strict";return Wreqr.Handlers.extend({request:function(){var name=arguments[0];var args=Array.prototype.slice.call(arguments,1);if(this.hasHandler(name)){return this.getHandler(name).apply(this,args);}}});}(Wreqr);Wreqr.EventAggregator=function(Backbone,_){"use strict";var EA=function(){};EA.extend=Backbone.Model.extend;_.extend(EA.prototype,Backbone.Events);return EA;}(Backbone,_);Wreqr.Channel=function(Wreqr){"use strict";var Channel=function(channelName){this.vent=new Backbone.Wreqr.EventAggregator();this.reqres=new Backbone.Wreqr.RequestResponse();this.commands=new Backbone.Wreqr.Commands();this.channelName=channelName;};_.extend(Channel.prototype,{reset:function(){this.vent.off();this.vent.stopListening();this.reqres.removeAllHandlers();this.commands.removeAllHandlers();return this;},connectEvents:function(hash,context){this._connect("vent",hash,context);return this;},connectCommands:function(hash,context){this._connect("commands",hash,context);return this;},connectRequests:function(hash,context){this._connect("reqres",hash,context);return this;},_connect:function(type,hash,context){if(!hash){return;}
context=context||this;var method=type==="vent"?"on":"setHandler";_.each(hash,function(fn,eventName){this[type][method](eventName,_.bind(fn,context));},this);}});return Channel;}(Wreqr);Wreqr.radio=function(Wreqr){"use strict";var Radio=function(){this._channels={};this.vent={};this.commands={};this.reqres={};this._proxyMethods();};_.extend(Radio.prototype,{channel:function(channelName){if(!channelName){throw new Error("Channel must receive a name");}
return this._getChannel(channelName);},_getChannel:function(channelName){var channel=this._channels[channelName];if(!channel){channel=new Wreqr.Channel(channelName);this._channels[channelName]=channel;}
return channel;},_proxyMethods:function(){_.each(["vent","commands","reqres"],function(system){_.each(messageSystems[system],function(method){this[system][method]=proxyMethod(this,system,method);},this);},this);}});var messageSystems={vent:["on","off","trigger","once","stopListening","listenTo","listenToOnce"],commands:["execute","setHandler","setHandlers","removeHandler","removeAllHandlers"],reqres:["request","setHandler","setHandlers","removeHandler","removeAllHandlers"]};var proxyMethod=function(radio,system,method){return function(channelName){var messageSystem=radio._getChannel(channelName)[system];var args=Array.prototype.slice.call(arguments,1);return messageSystem[method].apply(messageSystem,args);};};return new Radio();}(Wreqr);return Backbone.Wreqr;})(Backbone,_);var previousMarionette=root.Marionette;var Marionette=Backbone.Marionette={};Marionette.VERSION='2.2.0';Marionette.noConflict=function(){root.Marionette=previousMarionette;return this;};Backbone.Marionette=Marionette;Marionette.Deferred=Backbone.$.Deferred;var slice=Array.prototype.slice;Marionette.extend=Backbone.Model.extend;Marionette.getOption=function(target,optionName){if(!target||!optionName){return;}
var value;if(target.options&&(target.options[optionName]!==undefined)){value=target.options[optionName];}else{value=target[optionName];}
return value;};Marionette.proxyGetOption=function(optionName){return Marionette.getOption(this,optionName);};Marionette.normalizeMethods=function(hash){var normalizedHash={};_.each(hash,function(method,name){if(!_.isFunction(method)){method=this[method];}
if(!method){return;}
normalizedHash[name]=method;},this);return normalizedHash;};Marionette.normalizeUIString=function(uiString,ui){return uiString.replace(/@ui\.[a-zA-Z_$0-9]*/g,function(r){return ui[r.slice(4)];});};Marionette.normalizeUIKeys=function(hash,ui){if(typeof(hash)==='undefined'){return;}
hash=_.clone(hash);_.each(_.keys(hash),function(key){var normalizedKey=Marionette.normalizeUIString(key,ui);if(normalizedKey!==key){hash[normalizedKey]=hash[key];delete hash[key];}});return hash;};Marionette.normalizeUIValues=function(hash,ui){if(typeof(hash)==='undefined'){return;}
_.each(hash,function(val,key){if(_.isString(val)){hash[key]=Marionette.normalizeUIString(val,ui);}});return hash;};Marionette.actAsCollection=function(object,listProperty){var methods=['forEach','each','map','find','detect','filter','select','reject','every','all','some','any','include','contains','invoke','toArray','first','initial','rest','last','without','isEmpty','pluck'];_.each(methods,function(method){object[method]=function(){var list=_.values(_.result(this,listProperty));var args=[list].concat(_.toArray(arguments));return _[method].apply(_,args);};});};Marionette.triggerMethod=function(event){var splitter=/(^|:)(\w)/gi;function getEventName(match,prefix,eventName){return eventName.toUpperCase();}
var methodName='on'+ event.replace(splitter,getEventName);var method=this[methodName];var result;if(_.isFunction(method)){result=method.apply(this,_.tail(arguments));}
if(_.isFunction(this.trigger)){this.trigger.apply(this,arguments);}
return result;};Marionette.triggerMethodOn=function(context,event){var args=_.tail(arguments,2);var fnc;if(_.isFunction(context.triggerMethod)){fnc=context.triggerMethod;}else{fnc=Marionette.triggerMethod;}
return fnc.apply(context,[event].concat(args));};Marionette.MonitorDOMRefresh=(function(documentElement){function handleShow(view){view._isShown=true;triggerDOMRefresh(view);}
function handleRender(view){view._isRendered=true;triggerDOMRefresh(view);}
function triggerDOMRefresh(view){if(view._isShown&&view._isRendered&&isInDOM(view)){if(_.isFunction(view.triggerMethod)){view.triggerMethod('dom:refresh');}}}
function isInDOM(view){return Backbone.$.contains(documentElement,view.el);}
return function(view){view.listenTo(view,'show',function(){handleShow(view);});view.listenTo(view,'render',function(){handleRender(view);});};})(document.documentElement);(function(Marionette){'use strict';function bindFromStrings(target,entity,evt,methods){var methodNames=methods.split(/\s+/);_.each(methodNames,function(methodName){var method=target[methodName];if(!method){throw new Marionette.Error('Method "'+ methodName+'" was configured as an event handler, but does not exist.');}
target.listenTo(entity,evt,method);});}
function bindToFunction(target,entity,evt,method){target.listenTo(entity,evt,method);}
function unbindFromStrings(target,entity,evt,methods){var methodNames=methods.split(/\s+/);_.each(methodNames,function(methodName){var method=target[methodName];target.stopListening(entity,evt,method);});}
function unbindToFunction(target,entity,evt,method){target.stopListening(entity,evt,method);}
function iterateEvents(target,entity,bindings,functionCallback,stringCallback){if(!entity||!bindings){return;}
if(!_.isFunction(bindings)&&!_.isObject(bindings)){throw new Marionette.Error({message:'Bindings must be an object or function.',url:'marionette.functions.html#marionettebindentityevents'});}
if(_.isFunction(bindings)){bindings=bindings.call(target);}
_.each(bindings,function(methods,evt){if(_.isFunction(methods)){functionCallback(target,entity,evt,methods);}else{stringCallback(target,entity,evt,methods);}});}
Marionette.bindEntityEvents=function(target,entity,bindings){iterateEvents(target,entity,bindings,bindToFunction,bindFromStrings);};Marionette.unbindEntityEvents=function(target,entity,bindings){iterateEvents(target,entity,bindings,unbindToFunction,unbindFromStrings);};Marionette.proxyBindEntityEvents=function(entity,bindings){return Marionette.bindEntityEvents(this,entity,bindings);};Marionette.proxyUnbindEntityEvents=function(entity,bindings){return Marionette.unbindEntityEvents(this,entity,bindings);};})(Marionette);var errorProps=['description','fileName','lineNumber','name','message','number'];Marionette.Error=Marionette.extend.call(Error,{urlRoot:'http://marionettejs.com/docs/'+ Marionette.VERSION+'/',constructor:function(message,options){if(_.isObject(message)){options=message;message=options.message;}else if(!options){options={};}
var error=Error.call(this,message);_.extend(this,_.pick(error,errorProps),_.pick(options,errorProps));this.captureStackTrace();if(options.url){this.url=this.urlRoot+ options.url;}},captureStackTrace:function(){if(Error.captureStackTrace){Error.captureStackTrace(this,Marionette.Error);}},toString:function(){return this.name+': '+ this.message+(this.url?' See: '+ this.url:'');}});Marionette.Error.extend=Marionette.extend;Marionette.Callbacks=function(){this._deferred=Marionette.Deferred();this._callbacks=[];};_.extend(Marionette.Callbacks.prototype,{add:function(callback,contextOverride){var promise=_.result(this._deferred,'promise');this._callbacks.push({cb:callback,ctx:contextOverride});promise.then(function(args){if(contextOverride){args.context=contextOverride;}
callback.call(args.context,args.options);});},run:function(options,context){this._deferred.resolve({options:options,context:context});},reset:function(){var callbacks=this._callbacks;this._deferred=Marionette.Deferred();this._callbacks=[];_.each(callbacks,function(cb){this.add(cb.cb,cb.ctx);},this);}});Marionette.Controller=function(options){this.options=options||{};if(_.isFunction(this.initialize)){this.initialize(this.options);}};Marionette.Controller.extend=Marionette.extend;_.extend(Marionette.Controller.prototype,Backbone.Events,{destroy:function(){var args=slice.call(arguments);this.triggerMethod.apply(this,['before:destroy'].concat(args));this.triggerMethod.apply(this,['destroy'].concat(args));this.stopListening();this.off();return this;},triggerMethod:Marionette.triggerMethod,getOption:Marionette.proxyGetOption});Marionette.Object=function(options){this.options=_.extend({},_.result(this,'options'),options);this.initialize.apply(this,arguments);};Marionette.Object.extend=Marionette.extend;_.extend(Marionette.Object.prototype,{initialize:function(){},destroy:function(){this.triggerMethod('before:destroy');this.triggerMethod('destroy');this.stopListening();},triggerMethod:Marionette.triggerMethod,getOption:Marionette.proxyGetOption,bindEntityEvents:Marionette.proxyBindEntityEvents,unbindEntityEvents:Marionette.proxyUnbindEntityEvents});_.extend(Marionette.Object.prototype,Backbone.Events);Marionette.Region=function(options){this.options=options||{};this.el=this.getOption('el');this.el=this.el instanceof Backbone.$?this.el[0]:this.el;if(!this.el){throw new Marionette.Error({name:'NoElError',message:'An "el" must be specified for a region.'});}
this.$el=this.getEl(this.el);if(this.initialize){var args=slice.apply(arguments);this.initialize.apply(this,args);}};_.extend(Marionette.Region,{buildRegion:function(regionConfig,DefaultRegionClass){if(_.isString(regionConfig)){return this._buildRegionFromSelector(regionConfig,DefaultRegionClass);}
if(regionConfig.selector||regionConfig.el||regionConfig.regionClass){return this._buildRegionFromObject(regionConfig,DefaultRegionClass);}
if(_.isFunction(regionConfig)){return this._buildRegionFromRegionClass(regionConfig);}
throw new Marionette.Error({message:'Improper region configuration type.',url:'marionette.region.html#region-configuration-types'});},_buildRegionFromSelector:function(selector,DefaultRegionClass){return new DefaultRegionClass({el:selector});},_buildRegionFromObject:function(regionConfig,DefaultRegionClass){var RegionClass=regionConfig.regionClass||DefaultRegionClass;var options=_.omit(regionConfig,'selector','regionClass');if(regionConfig.selector&&!options.el){options.el=regionConfig.selector;}
var region=new RegionClass(options);if(regionConfig.parentEl){region.getEl=function(el){if(_.isObject(el)){return Backbone.$(el);}
var parentEl=regionConfig.parentEl;if(_.isFunction(parentEl)){parentEl=parentEl();}
return parentEl.find(el);};}
return region;},_buildRegionFromRegionClass:function(RegionClass){return new RegionClass();}});_.extend(Marionette.Region.prototype,Backbone.Events,{show:function(view,options){this._ensureElement();var showOptions=options||{};var isDifferentView=view!==this.currentView;var preventDestroy=!!showOptions.preventDestroy;var forceShow=!!showOptions.forceShow;var isChangingView=!!this.currentView;var _shouldDestroyView=!preventDestroy&&isDifferentView;var _shouldShowView=isDifferentView||forceShow;if(isChangingView){this.triggerMethod('before:swapOut',this.currentView);}
if(_shouldDestroyView){this.empty();}
if(_shouldShowView){view.once('destroy',_.bind(this.empty,this));view.render();if(isChangingView){this.triggerMethod('before:swap',view);}
this.triggerMethod('before:show',view);Marionette.triggerMethodOn(view,'before:show');if(isChangingView){this.triggerMethod('swapOut',this.currentView);}
this.attachHtml(view);this.currentView=view;if(isChangingView){this.triggerMethod('swap',view);}
this.triggerMethod('show',view);Marionette.triggerMethodOn(view,'show');return this;}
return this;},_ensureElement:function(){if(!_.isObject(this.el)){this.$el=this.getEl(this.el);this.el=this.$el[0];}
if(!this.$el||this.$el.length===0){throw new Marionette.Error('An "el" '+ this.$el.selector+' must exist in DOM');}},getEl:function(el){return Backbone.$(el);},attachHtml:function(view){this.el.innerHTML='';this.el.appendChild(view.el);},empty:function(){var view=this.currentView;if(!view){return;}
this.triggerMethod('before:empty',view);this._destroyView();this.triggerMethod('empty',view);delete this.currentView;return this;},_destroyView:function(){var view=this.currentView;if(view.destroy&&!view.isDestroyed){view.destroy();}else if(view.remove){view.remove();}},attachView:function(view){this.currentView=view;return this;},hasView:function(){return!!this.currentView;},reset:function(){this.empty();if(this.$el){this.el=this.$el.selector;}
delete this.$el;return this;},getOption:Marionette.proxyGetOption,triggerMethod:Marionette.triggerMethod});Marionette.Region.extend=Marionette.extend;Marionette.RegionManager=(function(Marionette){var RegionManager=Marionette.Controller.extend({constructor:function(options){this._regions={};Marionette.Controller.call(this,options);},addRegions:function(regionDefinitions,defaults){if(_.isFunction(regionDefinitions)){regionDefinitions=regionDefinitions.apply(this,arguments);}
var regions={};_.each(regionDefinitions,function(definition,name){if(_.isString(definition)){definition={selector:definition};}
if(definition.selector){definition=_.defaults({},definition,defaults);}
var region=this.addRegion(name,definition);regions[name]=region;},this);return regions;},addRegion:function(name,definition){var region;if(definition instanceof Marionette.Region){region=definition;}else{region=Marionette.Region.buildRegion(definition,Marionette.Region);}
this.triggerMethod('before:add:region',name,region);this._store(name,region);this.triggerMethod('add:region',name,region);return region;},get:function(name){return this._regions[name];},getRegions:function(){return _.clone(this._regions);},removeRegion:function(name){var region=this._regions[name];this._remove(name,region);return region;},removeRegions:function(){var regions=this.getRegions();_.each(this._regions,function(region,name){this._remove(name,region);},this);return regions;},emptyRegions:function(){var regions=this.getRegions();_.each(regions,function(region){region.empty();},this);return regions;},destroy:function(){this.removeRegions();return Marionette.Controller.prototype.destroy.apply(this,arguments);},_store:function(name,region){this._regions[name]=region;this._setLength();},_remove:function(name,region){this.triggerMethod('before:remove:region',name,region);region.empty();region.stopListening();delete this._regions[name];this._setLength();this.triggerMethod('remove:region',name,region);},_setLength:function(){this.length=_.size(this._regions);}});Marionette.actAsCollection(RegionManager.prototype,'_regions');return RegionManager;})(Marionette);Marionette.TemplateCache=function(templateId){this.templateId=templateId;};_.extend(Marionette.TemplateCache,{templateCaches:{},get:function(templateId){var cachedTemplate=this.templateCaches[templateId];if(!cachedTemplate){cachedTemplate=new Marionette.TemplateCache(templateId);this.templateCaches[templateId]=cachedTemplate;}
return cachedTemplate.load();},clear:function(){var i;var args=slice.call(arguments);var length=args.length;if(length>0){for(i=0;i<length;i++){delete this.templateCaches[args[i]];}}else{this.templateCaches={};}}});_.extend(Marionette.TemplateCache.prototype,{load:function(){if(this.compiledTemplate){return this.compiledTemplate;}
var template=this.loadTemplate(this.templateId);this.compiledTemplate=this.compileTemplate(template);return this.compiledTemplate;},loadTemplate:function(templateId){var template=Backbone.$(templateId).html();if(!template||template.length===0){throw new Marionette.Error({name:'NoTemplateError',message:'Could not find template: "'+ templateId+'"'});}
return template;},compileTemplate:function(rawTemplate){return _.template(rawTemplate);}});Marionette.Renderer={render:function(template,data){if(!template){throw new Marionette.Error({name:'TemplateNotFoundError',message:'Cannot render the template since its false, null or undefined.'});}
var templateFunc;if(typeof template==='function'){templateFunc=template;}else{templateFunc=Marionette.TemplateCache.get(template);}
return templateFunc(data);}};Marionette.View=Backbone.View.extend({constructor:function(options){_.bindAll(this,'render');this.options=_.extend({},_.result(this,'options'),_.isFunction(options)?options.call(this):options);this._behaviors=Marionette.Behaviors(this);Backbone.View.apply(this,arguments);Marionette.MonitorDOMRefresh(this);this.listenTo(this,'show',this.onShowCalled);},getTemplate:function(){return this.getOption('template');},serializeModel:function(model){return model.toJSON.apply(model,slice.call(arguments,1));},mixinTemplateHelpers:function(target){target=target||{};var templateHelpers=this.getOption('templateHelpers');if(_.isFunction(templateHelpers)){templateHelpers=templateHelpers.call(this);}
return _.extend(target,templateHelpers);},normalizeUIKeys:function(hash){var ui=_.result(this,'ui');var uiBindings=_.result(this,'_uiBindings');return Marionette.normalizeUIKeys(hash,uiBindings||ui);},normalizeUIValues:function(hash){var ui=_.result(this,'ui');var uiBindings=_.result(this,'_uiBindings');return Marionette.normalizeUIValues(hash,uiBindings||ui);},configureTriggers:function(){if(!this.triggers){return;}
var triggerEvents={};var triggers=this.normalizeUIKeys(_.result(this,'triggers'));_.each(triggers,function(value,key){triggerEvents[key]=this._buildViewTrigger(value);},this);return triggerEvents;},delegateEvents:function(events){this._delegateDOMEvents(events);this.bindEntityEvents(this.model,this.getOption('modelEvents'));this.bindEntityEvents(this.collection,this.getOption('collectionEvents'));_.each(this._behaviors,function(behavior){behavior.bindEntityEvents(this.model,behavior.getOption('modelEvents'));behavior.bindEntityEvents(this.collection,behavior.getOption('collectionEvents'));},this);return this;},_delegateDOMEvents:function(eventsArg){var events=eventsArg||this.events;if(_.isFunction(events)){events=events.call(this);}
events=this.normalizeUIKeys(events);if(_.isUndefined(eventsArg)){this.events=events;}
var combinedEvents={};var behaviorEvents=_.result(this,'behaviorEvents')||{};var triggers=this.configureTriggers();var behaviorTriggers=_.result(this,'behaviorTriggers')||{};_.extend(combinedEvents,behaviorEvents,events,triggers,behaviorTriggers);Backbone.View.prototype.delegateEvents.call(this,combinedEvents);},undelegateEvents:function(){var args=slice.call(arguments);Backbone.View.prototype.undelegateEvents.apply(this,args);this.unbindEntityEvents(this.model,this.getOption('modelEvents'));this.unbindEntityEvents(this.collection,this.getOption('collectionEvents'));_.each(this._behaviors,function(behavior){behavior.unbindEntityEvents(this.model,behavior.getOption('modelEvents'));behavior.unbindEntityEvents(this.collection,behavior.getOption('collectionEvents'));},this);return this;},onShowCalled:function(){},_ensureViewIsIntact:function(){if(this.isDestroyed){throw new Marionette.Error({name:'ViewDestroyedError',message:'View (cid: "'+ this.cid+'") has already been destroyed and cannot be used.'});}},destroy:function(){if(this.isDestroyed){return;}
var args=slice.call(arguments);this.triggerMethod.apply(this,['before:destroy'].concat(args));this.isDestroyed=true;this.triggerMethod.apply(this,['destroy'].concat(args));this.unbindUIElements();this.remove();_.invoke(this._behaviors,'destroy',args);return this;},bindUIElements:function(){this._bindUIElements();_.invoke(this._behaviors,this._bindUIElements);},_bindUIElements:function(){if(!this.ui){return;}
if(!this._uiBindings){this._uiBindings=this.ui;}
var bindings=_.result(this,'_uiBindings');this.ui={};_.each(_.keys(bindings),function(key){var selector=bindings[key];this.ui[key]=this.$(selector);},this);},unbindUIElements:function(){this._unbindUIElements();_.invoke(this._behaviors,this._unbindUIElements);},_unbindUIElements:function(){if(!this.ui||!this._uiBindings){return;}
_.each(this.ui,function($el,name){delete this.ui[name];},this);this.ui=this._uiBindings;delete this._uiBindings;},_buildViewTrigger:function(triggerDef){var hasOptions=_.isObject(triggerDef);var options=_.defaults({},(hasOptions?triggerDef:{}),{preventDefault:true,stopPropagation:true});var eventName=hasOptions?options.event:triggerDef;return function(e){if(e){if(e.preventDefault&&options.preventDefault){e.preventDefault();}
if(e.stopPropagation&&options.stopPropagation){e.stopPropagation();}}
var args={view:this,model:this.model,collection:this.collection};this.triggerMethod(eventName,args);};},setElement:function(){var ret=Backbone.View.prototype.setElement.apply(this,arguments);_.invoke(this._behaviors,'proxyViewProperties',this);return ret;},triggerMethod:function(){var args=arguments;var triggerMethod=Marionette.triggerMethod;var ret=triggerMethod.apply(this,args);_.each(this._behaviors,function(b){triggerMethod.apply(b,args);});return ret;},normalizeMethods:Marionette.normalizeMethods,getOption:Marionette.proxyGetOption,bindEntityEvents:Marionette.proxyBindEntityEvents,unbindEntityEvents:Marionette.proxyUnbindEntityEvents});Marionette.ItemView=Marionette.View.extend({constructor:function(){Marionette.View.apply(this,arguments);},serializeData:function(){var data={};if(this.model){data=_.partial(this.serializeModel,this.model).apply(this,arguments);}
else if(this.collection){data={items:_.partial(this.serializeCollection,this.collection).apply(this,arguments)};}
return data;},serializeCollection:function(collection){return collection.toJSON.apply(collection,slice.call(arguments,1));},render:function(){this._ensureViewIsIntact();this.triggerMethod('before:render',this);this._renderTemplate();this.bindUIElements();this.triggerMethod('render',this);return this;},_renderTemplate:function(){var template=this.getTemplate();if(template===false){return;}
if(!template){throw new Marionette.Error({name:'UndefinedTemplateError',message:'Cannot render the template since it is null or undefined.'});}
var data=this.serializeData();data=this.mixinTemplateHelpers(data);var html=Marionette.Renderer.render(template,data,this);this.attachElContent(html);return this;},attachElContent:function(html){this.$el.html(html);return this;},destroy:function(){if(this.isDestroyed){return;}
return Marionette.View.prototype.destroy.apply(this,arguments);}});Marionette.CollectionView=Marionette.View.extend({childViewEventPrefix:'childview',constructor:function(options){var initOptions=options||{};this.sort=_.isUndefined(initOptions.sort)?true:initOptions.sort;if(initOptions.collection&&!(initOptions.collection instanceof Backbone.Collection)){throw new Marionette.Error('The Collection option passed to this view needs to be an instance of a Backbone.Collection');}
this.once('render',this._initialEvents);this._initChildViewStorage();Marionette.View.apply(this,arguments);this.initRenderBuffer();},initRenderBuffer:function(){this.elBuffer=document.createDocumentFragment();this._bufferedChildren=[];},startBuffering:function(){this.initRenderBuffer();this.isBuffering=true;},endBuffering:function(){this.isBuffering=false;this._triggerBeforeShowBufferedChildren();this.attachBuffer(this,this.elBuffer);this._triggerShowBufferedChildren();this.initRenderBuffer();},_triggerBeforeShowBufferedChildren:function(){if(this._isShown){_.each(this._bufferedChildren,_.partial(this._triggerMethodOnChild,'before:show'));}},_triggerShowBufferedChildren:function(){if(this._isShown){_.each(this._bufferedChildren,_.partial(this._triggerMethodOnChild,'show'));this._bufferedChildren=[];}},_triggerMethodOnChild:function(event,childView){Marionette.triggerMethodOn(childView,event);},_initialEvents:function(){if(this.collection){this.listenTo(this.collection,'add',this._onCollectionAdd);this.listenTo(this.collection,'remove',this._onCollectionRemove);this.listenTo(this.collection,'reset',this.render);if(this.sort){this.listenTo(this.collection,'sort',this._sortViews);}}},_onCollectionAdd:function(child){this.destroyEmptyView();var ChildView=this.getChildView(child);var index=this.collection.indexOf(child);this.addChild(child,ChildView,index);},_onCollectionRemove:function(model){var view=this.children.findByModel(model);this.removeChildView(view);this.checkEmpty();},onShowCalled:function(){this.children.each(_.partial(this._triggerMethodOnChild,'show'));},render:function(){this._ensureViewIsIntact();this.triggerMethod('before:render',this);this._renderChildren();this.triggerMethod('render',this);return this;},resortView:function(){this.render();},_sortViews:function(){var orderChanged=this.collection.find(function(item,index){var view=this.children.findByModel(item);return!view||view._index!==index;},this);if(orderChanged){this.resortView();}},_renderChildren:function(){this.destroyEmptyView();this.destroyChildren();if(this.isEmpty(this.collection)){this.showEmptyView();}else{this.triggerMethod('before:render:collection',this);this.startBuffering();this.showCollection();this.endBuffering();this.triggerMethod('render:collection',this);}},showCollection:function(){var ChildView;this.collection.each(function(child,index){ChildView=this.getChildView(child);this.addChild(child,ChildView,index);},this);},showEmptyView:function(){var EmptyView=this.getEmptyView();if(EmptyView&&!this._showingEmptyView){this.triggerMethod('before:render:empty');this._showingEmptyView=true;var model=new Backbone.Model();this.addEmptyView(model,EmptyView);this.triggerMethod('render:empty');}},destroyEmptyView:function(){if(this._showingEmptyView){this.triggerMethod('before:remove:empty');this.destroyChildren();delete this._showingEmptyView;this.triggerMethod('remove:empty');}},getEmptyView:function(){return this.getOption('emptyView');},addEmptyView:function(child,EmptyView){var emptyViewOptions=this.getOption('emptyViewOptions')||this.getOption('childViewOptions');if(_.isFunction(emptyViewOptions)){emptyViewOptions=emptyViewOptions.call(this);}
var view=this.buildChildView(child,EmptyView,emptyViewOptions);this.proxyChildEvents(view);if(this._isShown){Marionette.triggerMethodOn(view,'before:show');}
this.children.add(view);this.renderChildView(view,-1);if(this._isShown){Marionette.triggerMethodOn(view,'show');}},getChildView:function(child){var childView=this.getOption('childView');if(!childView){throw new Marionette.Error({name:'NoChildViewError',message:'A "childView" must be specified'});}
return childView;},addChild:function(child,ChildView,index){var childViewOptions=this.getOption('childViewOptions');if(_.isFunction(childViewOptions)){childViewOptions=childViewOptions.call(this,child,index);}
var view=this.buildChildView(child,ChildView,childViewOptions);this._updateIndices(view,true,index);this._addChildView(view,index);return view;},_updateIndices:function(view,increment,index){if(!this.sort){return;}
if(increment){view._index=index;this.children.each(function(laterView){if(laterView._index>=view._index){laterView._index++;}});}
else{this.children.each(function(laterView){if(laterView._index>=view._index){laterView._index--;}});}},_addChildView:function(view,index){this.proxyChildEvents(view);this.triggerMethod('before:add:child',view);this.children.add(view);this.renderChildView(view,index);if(this._isShown&&!this.isBuffering){Marionette.triggerMethodOn(view,'show');}
this.triggerMethod('add:child',view);},renderChildView:function(view,index){view.render();this.attachHtml(this,view,index);return view;},buildChildView:function(child,ChildViewClass,childViewOptions){var options=_.extend({model:child},childViewOptions);return new ChildViewClass(options);},removeChildView:function(view){if(view){this.triggerMethod('before:remove:child',view);if(view.destroy){view.destroy();}
else if(view.remove){view.remove();}
this.stopListening(view);this.children.remove(view);this.triggerMethod('remove:child',view);this._updateIndices(view,false);}
return view;},isEmpty:function(){return!this.collection||this.collection.length===0;},checkEmpty:function(){if(this.isEmpty(this.collection)){this.showEmptyView();}},attachBuffer:function(collectionView,buffer){collectionView.$el.append(buffer);},attachHtml:function(collectionView,childView,index){if(collectionView.isBuffering){collectionView.elBuffer.appendChild(childView.el);collectionView._bufferedChildren.push(childView);}
else{if(!collectionView._insertBefore(childView,index)){collectionView._insertAfter(childView);}}},_insertBefore:function(childView,index){var currentView;var findPosition=this.sort&&(index<this.children.length- 1);if(findPosition){currentView=this.children.find(function(view){return view._index===index+ 1;});}
if(currentView){currentView.$el.before(childView.el);return true;}
return false;},_insertAfter:function(childView){this.$el.append(childView.el);},_initChildViewStorage:function(){this.children=new Backbone.ChildViewContainer();},destroy:function(){if(this.isDestroyed){return;}
this.triggerMethod('before:destroy:collection');this.destroyChildren();this.triggerMethod('destroy:collection');return Marionette.View.prototype.destroy.apply(this,arguments);},destroyChildren:function(){var childViews=this.children.map(_.identity);this.children.each(this.removeChildView,this);this.checkEmpty();return childViews;},proxyChildEvents:function(view){var prefix=this.getOption('childViewEventPrefix');this.listenTo(view,'all',function(){var args=slice.call(arguments);var rootEvent=args[0];var childEvents=this.normalizeMethods(_.result(this,'childEvents'));args[0]=prefix+':'+ rootEvent;args.splice(1,0,view);if(typeof childEvents!=='undefined'&&_.isFunction(childEvents[rootEvent])){childEvents[rootEvent].apply(this,args.slice(1));}
this.triggerMethod.apply(this,args);},this);}});Marionette.CompositeView=Marionette.CollectionView.extend({constructor:function(){Marionette.CollectionView.apply(this,arguments);},_initialEvents:function(){if(this.collection){this.listenTo(this.collection,'add',this._onCollectionAdd);this.listenTo(this.collection,'remove',this._onCollectionRemove);this.listenTo(this.collection,'reset',this._renderChildren);if(this.sort){this.listenTo(this.collection,'sort',this._sortViews);}}},getChildView:function(child){var childView=this.getOption('childView')||this.constructor;if(!childView){throw new Marionette.Error({name:'NoChildViewError',message:'A "childView" must be specified'});}
return childView;},serializeData:function(){var data={};if(this.model){data=_.partial(this.serializeModel,this.model).apply(this,arguments);}
return data;},render:function(){this._ensureViewIsIntact();this.isRendered=true;this.resetChildViewContainer();this.triggerMethod('before:render',this);this._renderTemplate();this._renderChildren();this.triggerMethod('render',this);return this;},_renderChildren:function(){if(this.isRendered){Marionette.CollectionView.prototype._renderChildren.call(this);}},_renderTemplate:function(){var data={};data=this.serializeData();data=this.mixinTemplateHelpers(data);this.triggerMethod('before:render:template');var template=this.getTemplate();var html=Marionette.Renderer.render(template,data,this);this.attachElContent(html);this.bindUIElements();this.triggerMethod('render:template');},attachElContent:function(html){this.$el.html(html);return this;},attachBuffer:function(compositeView,buffer){var $container=this.getChildViewContainer(compositeView);$container.append(buffer);},_insertAfter:function(childView){var $container=this.getChildViewContainer(this);$container.append(childView.el);},getChildViewContainer:function(containerView){if('$childViewContainer'in containerView){return containerView.$childViewContainer;}
var container;var childViewContainer=Marionette.getOption(containerView,'childViewContainer');if(childViewContainer){var selector=_.isFunction(childViewContainer)?childViewContainer.call(containerView):childViewContainer;if(selector.charAt(0)==='@'&&containerView.ui){container=containerView.ui[selector.substr(4)];}else{container=containerView.$(selector);}
if(container.length<=0){throw new Marionette.Error({name:'ChildViewContainerMissingError',message:'The specified "childViewContainer" was not found: '+ containerView.childViewContainer});}}else{container=containerView.$el;}
containerView.$childViewContainer=container;return container;},resetChildViewContainer:function(){if(this.$childViewContainer){delete this.$childViewContainer;}}});Marionette.LayoutView=Marionette.ItemView.extend({regionClass:Marionette.Region,constructor:function(options){options=options||{};this._firstRender=true;this._initializeRegions(options);Marionette.ItemView.call(this,options);},render:function(){this._ensureViewIsIntact();if(this._firstRender){this._firstRender=false;}else{this._reInitializeRegions();}
return Marionette.ItemView.prototype.render.apply(this,arguments);},destroy:function(){if(this.isDestroyed){return this;}
this.regionManager.destroy();return Marionette.ItemView.prototype.destroy.apply(this,arguments);},addRegion:function(name,definition){this.triggerMethod('before:region:add',name);var regions={};regions[name]=definition;return this._buildRegions(regions)[name];},addRegions:function(regions){this.regions=_.extend({},this.regions,regions);return this._buildRegions(regions);},removeRegion:function(name){this.triggerMethod('before:region:remove',name);delete this.regions[name];return this.regionManager.removeRegion(name);},getRegion:function(region){return this.regionManager.get(region);},getRegions:function(){return this.regionManager.getRegions();},_buildRegions:function(regions){var that=this;var defaults={regionClass:this.getOption('regionClass'),parentEl:function(){return that.$el;}};return this.regionManager.addRegions(regions,defaults);},_initializeRegions:function(options){var regions;this._initRegionManager();if(_.isFunction(this.regions)){regions=this.regions(options);}else{regions=this.regions||{};}
var regionOptions=this.getOption.call(options,'regions');if(_.isFunction(regionOptions)){regionOptions=regionOptions.call(this,options);}
_.extend(regions,regionOptions);regions=this.normalizeUIValues(regions);this.addRegions(regions);},_reInitializeRegions:function(){this.regionManager.emptyRegions();this.regionManager.each(function(region){region.reset();});},getRegionManager:function(){return new Marionette.RegionManager();},_initRegionManager:function(){this.regionManager=this.getRegionManager();this.listenTo(this.regionManager,'before:add:region',function(name){this.triggerMethod('before:add:region',name);});this.listenTo(this.regionManager,'add:region',function(name,region){this[name]=region;this.triggerMethod('add:region',name,region);});this.listenTo(this.regionManager,'before:remove:region',function(name){this.triggerMethod('before:remove:region',name);});this.listenTo(this.regionManager,'remove:region',function(name,region){delete this[name];this.triggerMethod('remove:region',name,region);});}});Marionette.Behavior=(function(_,Backbone){function Behavior(options,view){this.view=view;this.defaults=_.result(this,'defaults')||{};this.options=_.extend({},this.defaults,options);this.$=function(){return this.view.$.apply(this.view,arguments);};this.initialize.apply(this,arguments);}
_.extend(Behavior.prototype,Backbone.Events,{initialize:function(){},destroy:function(){this.stopListening();},proxyViewProperties:function(view){this.$el=view.$el;this.el=view.el;},triggerMethod:Marionette.triggerMethod,getOption:Marionette.proxyGetOption,bindEntityEvents:Marionette.proxyBindEntityEvents,unbindEntityEvents:Marionette.proxyUnbindEntityEvents});Behavior.extend=Marionette.extend;return Behavior;})(_,Backbone);Marionette.Behaviors=(function(Marionette,_){function Behaviors(view,behaviors){if(!_.isObject(view.behaviors)){return{};}
behaviors=Behaviors.parseBehaviors(view,behaviors||_.result(view,'behaviors'));Behaviors.wrap(view,behaviors,_.keys(methods));return behaviors;}
var methods={behaviorTriggers:function(behaviorTriggers,behaviors){var triggerBuilder=new BehaviorTriggersBuilder(this,behaviors);return triggerBuilder.buildBehaviorTriggers();},behaviorEvents:function(behaviorEvents,behaviors){var _behaviorsEvents={};var viewUI=_.result(this,'ui');_.each(behaviors,function(b,i){var _events={};var behaviorEvents=_.clone(_.result(b,'events'))||{};var behaviorUI=_.result(b,'ui');var ui=_.extend({},viewUI,behaviorUI);behaviorEvents=Marionette.normalizeUIKeys(behaviorEvents,ui);_.each(_.keys(behaviorEvents),function(key){var whitespace=(new Array(i+ 2)).join(' ');var eventKey=key+ whitespace;var handler=_.isFunction(behaviorEvents[key])?behaviorEvents[key]:b[behaviorEvents[key]];_events[eventKey]=_.bind(handler,b);});_behaviorsEvents=_.extend(_behaviorsEvents,_events);});return _behaviorsEvents;}};_.extend(Behaviors,{behaviorsLookup:function(){throw new Marionette.Error({message:'You must define where your behaviors are stored.',url:'marionette.behaviors.html#behaviorslookup'});},getBehaviorClass:function(options,key){if(options.behaviorClass){return options.behaviorClass;}
return _.isFunction(Behaviors.behaviorsLookup)?Behaviors.behaviorsLookup.apply(this,arguments)[key]:Behaviors.behaviorsLookup[key];},parseBehaviors:function(view,behaviors){return _.chain(behaviors).map(function(options,key){var BehaviorClass=Behaviors.getBehaviorClass(options,key);var behavior=new BehaviorClass(options,view);var nestedBehaviors=Behaviors.parseBehaviors(view,_.result(behavior,'behaviors'));return[behavior].concat(nestedBehaviors);}).flatten().value();},wrap:function(view,behaviors,methodNames){_.each(methodNames,function(methodName){view[methodName]=_.partial(methods[methodName],view[methodName],behaviors);});}});function BehaviorTriggersBuilder(view,behaviors){this._view=view;this._viewUI=_.result(view,'ui');this._behaviors=behaviors;this._triggers={};}
_.extend(BehaviorTriggersBuilder.prototype,{buildBehaviorTriggers:function(){_.each(this._behaviors,this._buildTriggerHandlersForBehavior,this);return this._triggers;},_buildTriggerHandlersForBehavior:function(behavior,i){var ui=_.extend({},this._viewUI,_.result(behavior,'ui'));var triggersHash=_.clone(_.result(behavior,'triggers'))||{};triggersHash=Marionette.normalizeUIKeys(triggersHash,ui);_.each(triggersHash,_.partial(this._setHandlerForBehavior,behavior,i),this);},_setHandlerForBehavior:function(behavior,i,eventName,trigger){var triggerKey=trigger.replace(/^\S+/,function(triggerName){return triggerName+'.'+'behaviortriggers'+ i;});this._triggers[triggerKey]=this._view._buildViewTrigger(eventName);}});return Behaviors;})(Marionette,_);Marionette.AppRouter=Backbone.Router.extend({constructor:function(options){Backbone.Router.apply(this,arguments);this.options=options||{};var appRoutes=this.getOption('appRoutes');var controller=this._getController();this.processAppRoutes(controller,appRoutes);this.on('route',this._processOnRoute,this);},appRoute:function(route,methodName){var controller=this._getController();this._addAppRoute(controller,route,methodName);},_processOnRoute:function(routeName,routeArgs){var routePath=_.invert(this.getOption('appRoutes'))[routeName];if(_.isFunction(this.onRoute)){this.onRoute(routeName,routePath,routeArgs);}},processAppRoutes:function(controller,appRoutes){if(!appRoutes){return;}
var routeNames=_.keys(appRoutes).reverse();_.each(routeNames,function(route){this._addAppRoute(controller,route,appRoutes[route]);},this);},_getController:function(){return this.getOption('controller');},_addAppRoute:function(controller,route,methodName){var method=controller[methodName];if(!method){throw new Marionette.Error('Method "'+ methodName+'" was not found on the controller');}
this.route(route,methodName,_.bind(method,controller));},getOption:Marionette.proxyGetOption});Marionette.Application=function(options){this.options=options;this._initializeRegions(options);this._initCallbacks=new Marionette.Callbacks();this.submodules={};_.extend(this,options);this._initChannel();this.initialize.apply(this,arguments);};_.extend(Marionette.Application.prototype,Backbone.Events,{initialize:function(){},execute:function(){this.commands.execute.apply(this.commands,arguments);},request:function(){return this.reqres.request.apply(this.reqres,arguments);},addInitializer:function(initializer){this._initCallbacks.add(initializer);},start:function(options){this.triggerMethod('before:start',options);this._initCallbacks.run(options,this);this.triggerMethod('start',options);},addRegions:function(regions){return this._regionManager.addRegions(regions);},emptyRegions:function(){return this._regionManager.emptyRegions();},removeRegion:function(region){return this._regionManager.removeRegion(region);},getRegion:function(region){return this._regionManager.get(region);},getRegions:function(){return this._regionManager.getRegions();},module:function(moduleNames,moduleDefinition){var ModuleClass=Marionette.Module.getClass(moduleDefinition);var args=slice.call(arguments);args.unshift(this);return ModuleClass.create.apply(ModuleClass,args);},getRegionManager:function(){return new Marionette.RegionManager();},_initializeRegions:function(options){var regions=_.isFunction(this.regions)?this.regions(options):this.regions||{};this._initRegionManager();var optionRegions=Marionette.getOption(options,'regions');if(_.isFunction(optionRegions)){optionRegions=optionRegions.call(this,options);}
_.extend(regions,optionRegions);this.addRegions(regions);return this;},_initRegionManager:function(){this._regionManager=this.getRegionManager();this.listenTo(this._regionManager,'before:add:region',function(name){this.triggerMethod('before:add:region',name);});this.listenTo(this._regionManager,'add:region',function(name,region){this[name]=region;this.triggerMethod('add:region',name,region);});this.listenTo(this._regionManager,'before:remove:region',function(name){this.triggerMethod('before:remove:region',name);});this.listenTo(this._regionManager,'remove:region',function(name,region){delete this[name];this.triggerMethod('remove:region',name,region);});},_initChannel:function(){this.channelName=_.result(this,'channelName')||'global';this.channel=_.result(this,'channel')||Backbone.Wreqr.radio.channel(this.channelName);this.vent=_.result(this,'vent')||this.channel.vent;this.commands=_.result(this,'commands')||this.channel.commands;this.reqres=_.result(this,'reqres')||this.channel.reqres;},triggerMethod:Marionette.triggerMethod,getOption:Marionette.proxyGetOption});Marionette.Application.extend=Marionette.extend;Marionette.Module=function(moduleName,app,options){this.moduleName=moduleName;this.options=_.extend({},this.options,options);this.initialize=options.initialize||this.initialize;this.submodules={};this._setupInitializersAndFinalizers();this.app=app;if(_.isFunction(this.initialize)){this.initialize(moduleName,app,this.options);}};Marionette.Module.extend=Marionette.extend;_.extend(Marionette.Module.prototype,Backbone.Events,{startWithParent:true,initialize:function(){},addInitializer:function(callback){this._initializerCallbacks.add(callback);},addFinalizer:function(callback){this._finalizerCallbacks.add(callback);},start:function(options){if(this._isInitialized){return;}
_.each(this.submodules,function(mod){if(mod.startWithParent){mod.start(options);}});this.triggerMethod('before:start',options);this._initializerCallbacks.run(options,this);this._isInitialized=true;this.triggerMethod('start',options);},stop:function(){if(!this._isInitialized){return;}
this._isInitialized=false;this.triggerMethod('before:stop');_.each(this.submodules,function(mod){mod.stop();});this._finalizerCallbacks.run(undefined,this);this._initializerCallbacks.reset();this._finalizerCallbacks.reset();this.triggerMethod('stop');},addDefinition:function(moduleDefinition,customArgs){this._runModuleDefinition(moduleDefinition,customArgs);},_runModuleDefinition:function(definition,customArgs){if(!definition){return;}
var args=_.flatten([this,this.app,Backbone,Marionette,Backbone.$,_,customArgs]);definition.apply(this,args);},_setupInitializersAndFinalizers:function(){this._initializerCallbacks=new Marionette.Callbacks();this._finalizerCallbacks=new Marionette.Callbacks();},triggerMethod:Marionette.triggerMethod});_.extend(Marionette.Module,{create:function(app,moduleNames,moduleDefinition){var module=app;var customArgs=slice.call(arguments);customArgs.splice(0,3);moduleNames=moduleNames.split('.');var length=moduleNames.length;var moduleDefinitions=[];moduleDefinitions[length- 1]=moduleDefinition;_.each(moduleNames,function(moduleName,i){var parentModule=module;module=this._getModule(parentModule,moduleName,app,moduleDefinition);this._addModuleDefinition(parentModule,module,moduleDefinitions[i],customArgs);},this);return module;},_getModule:function(parentModule,moduleName,app,def,args){var options=_.extend({},def);var ModuleClass=this.getClass(def);var module=parentModule[moduleName];if(!module){module=new ModuleClass(moduleName,app,options);parentModule[moduleName]=module;parentModule.submodules[moduleName]=module;}
return module;},getClass:function(moduleDefinition){var ModuleClass=Marionette.Module;if(!moduleDefinition){return ModuleClass;}
if(moduleDefinition.prototype instanceof ModuleClass){return moduleDefinition;}
return moduleDefinition.moduleClass||ModuleClass;},_addModuleDefinition:function(parentModule,module,def,args){var fn=this._getDefine(def);var startWithParent=this._getStartWithParent(def,module);if(fn){module.addDefinition(fn,args);}
this._addStartWithParent(parentModule,module,startWithParent);},_getStartWithParent:function(def,module){var swp;if(_.isFunction(def)&&(def.prototype instanceof Marionette.Module)){swp=module.constructor.prototype.startWithParent;return _.isUndefined(swp)?true:swp;}
if(_.isObject(def)){swp=def.startWithParent;return _.isUndefined(swp)?true:swp;}
return true;},_getDefine:function(def){if(_.isFunction(def)&&!(def.prototype instanceof Marionette.Module)){return def;}
if(_.isObject(def)){return def.define;}
return null;},_addStartWithParent:function(parentModule,module,startWithParent){module.startWithParent=module.startWithParent&&startWithParent;if(!module.startWithParent||!!module.startWithParentIsConfigured){return;}
module.startWithParentIsConfigured=true;parentModule.addInitializer(function(options){if(module.startWithParent){module.start(options);}});}});return Marionette;}));
// Backbone.Radio v2.0.0

(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory(require('underscore'), require('backbone')) :
  typeof define === 'function' && define.amd ? define(['underscore', 'backbone'], factory) :
  (global.Backbone = global.Backbone || {}, global.Backbone.Radio = factory(global._,global.Backbone));
}(this, function (_,Backbone) { 'use strict';

  _ = 'default' in _ ? _['default'] : _;
  Backbone = 'default' in Backbone ? Backbone['default'] : Backbone;

  var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && typeof Symbol === "function" && obj.constructor === Symbol ? "symbol" : typeof obj;
  };

  var previousRadio = Backbone.Radio;

  var Radio = Backbone.Radio = {};

  Radio.VERSION = '2.0.0';

  // This allows you to run multiple instances of Radio on the same
  // webapp. After loading the new version, call `noConflict()` to
  // get a reference to it. At the same time the old version will be
  // returned to Backbone.Radio.
  Radio.noConflict = function () {
    Backbone.Radio = previousRadio;
    return this;
  };

  // Whether or not we're in DEBUG mode or not. DEBUG mode helps you
  // get around the issues of lack of warnings when events are mis-typed.
  Radio.DEBUG = false;

  // Format debug text.
  Radio._debugText = function (warning, eventName, channelName) {
    return warning + (channelName ? ' on the ' + channelName + ' channel' : '') + ': "' + eventName + '"';
  };

  // This is the method that's called when an unregistered event was called.
  // By default, it logs warning to the console. By overriding this you could
  // make it throw an Error, for instance. This would make firing a nonexistent event
  // have the same consequence as firing a nonexistent method on an Object.
  Radio.debugLog = function (warning, eventName, channelName) {
    if (Radio.DEBUG && console && console.warn) {
      console.warn(Radio._debugText(warning, eventName, channelName));
    }
  };

  var eventSplitter = /\s+/;

  // An internal method used to handle Radio's method overloading for Requests.
  // It's borrowed from Backbone.Events. It differs from Backbone's overload
  // API (which is used in Backbone.Events) in that it doesn't support space-separated
  // event names.
  Radio._eventsApi = function (obj, action, name, rest) {
    if (!name) {
      return false;
    }

    var results = {};

    // Handle event maps.
    if ((typeof name === 'undefined' ? 'undefined' : _typeof(name)) === 'object') {
      for (var key in name) {
        var result = obj[action].apply(obj, [key, name[key]].concat(rest));
        eventSplitter.test(key) ? _.extend(results, result) : results[key] = result;
      }
      return results;
    }

    // Handle space separated event names.
    if (eventSplitter.test(name)) {
      var names = name.split(eventSplitter);
      for (var i = 0, l = names.length; i < l; i++) {
        results[names[i]] = obj[action].apply(obj, [names[i]].concat(rest));
      }
      return results;
    }

    return false;
  };

  // An optimized way to execute callbacks.
  Radio._callHandler = function (callback, context, args) {
    var a1 = args[0],
        a2 = args[1],
        a3 = args[2];
    switch (args.length) {
      case 0:
        return callback.call(context);
      case 1:
        return callback.call(context, a1);
      case 2:
        return callback.call(context, a1, a2);
      case 3:
        return callback.call(context, a1, a2, a3);
      default:
        return callback.apply(context, args);
    }
  };

  // A helper used by `off` methods to the handler from the store
  function removeHandler(store, name, callback, context) {
    var event = store[name];
    if ((!callback || callback === event.callback || callback === event.callback._callback) && (!context || context === event.context)) {
      delete store[name];
      return true;
    }
  }

  function removeHandlers(store, name, callback, context) {
    store || (store = {});
    var names = name ? [name] : _.keys(store);
    var matched = false;

    for (var i = 0, length = names.length; i < length; i++) {
      name = names[i];

      // If there's no event by this name, log it and continue
      // with the loop
      if (!store[name]) {
        continue;
      }

      if (removeHandler(store, name, callback, context)) {
        matched = true;
      }
    }

    return matched;
  }

  /*
   * tune-in
   * -------
   * Get console logs of a channel's activity
   *
   */

  var _logs = {};

  // This is to produce an identical function in both tuneIn and tuneOut,
  // so that Backbone.Events unregisters it.
  function _partial(channelName) {
    return _logs[channelName] || (_logs[channelName] = _.bind(Radio.log, Radio, channelName));
  }

  _.extend(Radio, {

    // Log information about the channel and event
    log: function log(channelName, eventName) {
      if (typeof console === 'undefined') {
        return;
      }
      var args = _.toArray(arguments).slice(2);
      console.log('[' + channelName + '] "' + eventName + '"', args);
    },

    // Logs all events on this channel to the console. It sets an
    // internal value on the channel telling it we're listening,
    // then sets a listener on the Backbone.Events
    tuneIn: function tuneIn(channelName) {
      var channel = Radio.channel(channelName);
      channel._tunedIn = true;
      channel.on('all', _partial(channelName));
      return this;
    },

    // Stop logging all of the activities on this channel to the console
    tuneOut: function tuneOut(channelName) {
      var channel = Radio.channel(channelName);
      channel._tunedIn = false;
      channel.off('all', _partial(channelName));
      delete _logs[channelName];
      return this;
    }
  });

  /*
   * Backbone.Radio.Requests
   * -----------------------
   * A messaging system for requesting data.
   *
   */

  function makeCallback(callback) {
    return _.isFunction(callback) ? callback : function () {
      return callback;
    };
  }

  Radio.Requests = {

    // Make a request
    request: function request(name) {
      var args = _.toArray(arguments).slice(1);
      var results = Radio._eventsApi(this, 'request', name, args);
      if (results) {
        return results;
      }
      var channelName = this.channelName;
      var requests = this._requests;

      // Check if we should log the request, and if so, do it
      if (channelName && this._tunedIn) {
        Radio.log.apply(this, [channelName, name].concat(args));
      }

      // If the request isn't handled, log it in DEBUG mode and exit
      if (requests && (requests[name] || requests['default'])) {
        var handler = requests[name] || requests['default'];
        args = requests[name] ? args : arguments;
        return Radio._callHandler(handler.callback, handler.context, args);
      } else {
        Radio.debugLog('An unhandled request was fired', name, channelName);
      }
    },

    // Set up a handler for a request
    reply: function reply(name, callback, context) {
      if (Radio._eventsApi(this, 'reply', name, [callback, context])) {
        return this;
      }

      this._requests || (this._requests = {});

      if (this._requests[name]) {
        Radio.debugLog('A request was overwritten', name, this.channelName);
      }

      this._requests[name] = {
        callback: makeCallback(callback),
        context: context || this
      };

      return this;
    },

    // Set up a handler that can only be requested once
    replyOnce: function replyOnce(name, callback, context) {
      if (Radio._eventsApi(this, 'replyOnce', name, [callback, context])) {
        return this;
      }

      var self = this;

      var once = _.once(function () {
        self.stopReplying(name);
        return makeCallback(callback).apply(this, arguments);
      });

      return this.reply(name, once, context);
    },

    // Remove handler(s)
    stopReplying: function stopReplying(name, callback, context) {
      if (Radio._eventsApi(this, 'stopReplying', name)) {
        return this;
      }

      // Remove everything if there are no arguments passed
      if (!name && !callback && !context) {
        delete this._requests;
      } else if (!removeHandlers(this._requests, name, callback, context)) {
        Radio.debugLog('Attempted to remove the unregistered request', name, this.channelName);
      }

      return this;
    }
  };

  /*
   * Backbone.Radio.channel
   * ----------------------
   * Get a reference to a channel by name.
   *
   */

  Radio._channels = {};

  Radio.channel = function (channelName) {
    if (!channelName) {
      throw new Error('You must provide a name for the channel.');
    }

    if (Radio._channels[channelName]) {
      return Radio._channels[channelName];
    } else {
      return Radio._channels[channelName] = new Radio.Channel(channelName);
    }
  };

  /*
   * Backbone.Radio.Channel
   * ----------------------
   * A Channel is an object that extends from Backbone.Events,
   * and Radio.Requests.
   *
   */

  Radio.Channel = function (channelName) {
    this.channelName = channelName;
  };

  _.extend(Radio.Channel.prototype, Backbone.Events, Radio.Requests, {

    // Remove all handlers from the messaging systems of this channel
    reset: function reset() {
      this.off();
      this.stopListening();
      this.stopReplying();
      return this;
    }
  });

  /*
   * Top-level API
   * -------------
   * Supplies the 'top-level API' for working with Channels directly
   * from Backbone.Radio.
   *
   */

  var channel;
  var args;
  var systems = [Backbone.Events, Radio.Requests];
  _.each(systems, function (system) {
    _.each(system, function (method, methodName) {
      Radio[methodName] = function (channelName) {
        args = _.toArray(arguments).slice(1);
        channel = this.channel(channelName);
        return channel[methodName].apply(channel, args);
      };
    });
  });

  Radio.reset = function (channelName) {
    var channels = !channelName ? this._channels : [this._channels[channelName]];
    _.each(channels, function (channel) {
      channel.reset();
    });
  };

  return Radio;

}));
/*
 * metismenu - v2.4.0
 * A jQuery menu plugin
 * https://github.com/onokumus/metisMenu#readme
 *
 * Made by Osman Nuri Okumuş <onokumus@gmail.com> (https://github.com/onokumus)
 * Under MIT License
 */


!function(a,b){"function"==typeof define&&define.amd?define(["jquery"],b):"object"==typeof exports?module.exports=b(require("jquery")):a.sortable=b(a.jQuery)}(this,function(a){"use strict";function b(){var a=document.createElement("mm"),b={WebkitTransition:"webkitTransitionEnd",MozTransition:"transitionend",OTransition:"oTransitionEnd otransitionend",transition:"transitionend"};for(var c in b)if(void 0!==a.style[c])return{end:b[c]};return!1}function c(b){return this.each(function(){var c=a(this),d=c.data("mm"),f=a.extend({},e.DEFAULTS,c.data(),"object"==typeof b&&b);d||c.data("mm",d=new e(this,f)),"string"==typeof b&&d[b]()})}a.fn.emulateTransitionEnd=function(b){var c=!1,e=this;a(this).one("mmTransitionEnd",function(){c=!0});var f=function(){c||a(e).trigger(d.end)};return setTimeout(f,b),this};var d=b();d&&(a.event.special.mmTransitionEnd={bindType:d.end,delegateType:d.end,handle:function(b){return a(b.target).is(this)?b.handleObj.handler.apply(this,arguments):void 0}});var e=function(b,c){this.$element=a(b),this.options=a.extend({},e.DEFAULTS,c),this.transitioning=null,this.init()};e.TRANSITION_DURATION=350,e.DEFAULTS={toggle:!0,doubleTapToGo:!1,preventDefault:!0,activeClass:"active",collapseClass:"collapse",collapseInClass:"in",collapsingClass:"collapsing",onTransitionStart:!1,onTransitionEnd:!1},e.prototype.init=function(){var b=this,c=this.options.activeClass,d=this.options.collapseClass,e=this.options.collapseInClass;this.$element.find("li."+c).has("ul").children("ul").attr("aria-expanded",!0).addClass(d+" "+e),this.$element.find("li").not("."+c).has("ul").children("ul").attr("aria-expanded",!1).addClass(d),this.options.doubleTapToGo&&this.$element.find("li."+c).has("ul").children("a").addClass("doubleTapToGo"),this.$element.find("li").has("ul").children("a").on("click.metisMenu",function(d){var e=a(this),f=e.parent("li"),g=f.children("ul");return b.options.preventDefault&&d.preventDefault(),"true"!==e.attr("aria-disabled")?(f.hasClass(c)&&!b.options.doubleTapToGo?(b.hide(g),e.attr("aria-expanded",!1)):(b.show(g),e.attr("aria-expanded",!0)),b.options.onTransitionStart&&b.options.onTransitionStart(),b.options.doubleTapToGo&&b.doubleTapToGo(e)&&"#"!==e.attr("href")&&""!==e.attr("href")?(d.stopPropagation(),void(document.location=e.attr("href"))):void 0):void 0})},e.prototype.doubleTapToGo=function(a){var b=this.$element;return a.hasClass("doubleTapToGo")?(a.removeClass("doubleTapToGo"),!0):a.parent().children("ul").length?(b.find(".doubleTapToGo").removeClass("doubleTapToGo"),a.addClass("doubleTapToGo"),!1):void 0},e.prototype.show=function(b){var c=this.options.activeClass,f=this.options.collapseClass,g=this.options.collapseInClass,h=this.options.collapsingClass,i=a(b),j=i.parent("li");if(!this.transitioning&&!i.hasClass(g)){j.addClass(c),this.options.toggle&&this.hide(j.siblings().children("ul."+g).attr("aria-expanded",!1)),i.removeClass(f).addClass(h).height(0),this.transitioning=1;var k=function(){this.transitioning&&this.options.onTransitionEnd&&this.options.onTransitionEnd(),i.removeClass(h).addClass(f+" "+g).height("").attr("aria-expanded",!0),this.transitioning=0};return d?void i.one("mmTransitionEnd",a.proxy(k,this)).emulateTransitionEnd(e.TRANSITION_DURATION).height(i[0].scrollHeight):k.call(this)}},e.prototype.hide=function(b){var c=this.options.activeClass,f=this.options.collapseClass,g=this.options.collapseInClass,h=this.options.collapsingClass,i=a(b);if(!this.transitioning&&i.hasClass(g)){i.parent("li").removeClass(c),i.height(i.height())[0].offsetHeight,i.addClass(h).removeClass(f).removeClass(g),this.transitioning=1;var j=function(){this.transitioning&&this.options.onTransitionEnd&&this.options.onTransitionEnd(),this.transitioning=0,i.removeClass(h).addClass(f).attr("aria-expanded",!1)};return d?void i.height(0).one("mmTransitionEnd",a.proxy(j,this)).emulateTransitionEnd(e.TRANSITION_DURATION):j.call(this)}};var f=a.fn.metisMenu;a.fn.metisMenu=c,a.fn.metisMenu.Constructor=e,a.fn.metisMenu.noConflict=function(){return a.fn.metisMenu=f,this}});
/**
*
* jquery.sparkline.js
*
* v2.1.2
* (c) Splunk, Inc
* Contact: Gareth Watts (gareth@splunk.com)
* http://omnipotent.net/jquery.sparkline/
*
* Generates inline sparkline charts from data supplied either to the method
* or inline in HTML
*
* Compatible with Internet Explorer 6.0+ and modern browsers equipped with the canvas tag
* (Firefox 2.0+, Safari, Opera, etc)
*
* License: New BSD License
*
* Copyright (c) 2012, Splunk Inc.
* All rights reserved.
*
* Redistribution and use in source and binary forms, with or without modification,
* are permitted provided that the following conditions are met:
*
*     * Redistributions of source code must retain the above copyright notice,
*       this list of conditions and the following disclaimer.
*     * Redistributions in binary form must reproduce the above copyright notice,
*       this list of conditions and the following disclaimer in the documentation
*       and/or other materials provided with the distribution.
*     * Neither the name of Splunk Inc nor the names of its contributors may
*       be used to endorse or promote products derived from this software without
*       specific prior written permission.
*
* THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY
* EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES
* OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT
* SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
* SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
* OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION)
* HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY,
* OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS
* SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*
*
* Usage:
*  $(selector).sparkline(values, options)
*
* If values is undefined or set to 'html' then the data values are read from the specified tag:
*   <p>Sparkline: <span class="sparkline">1,4,6,6,8,5,3,5</span></p>
*   $('.sparkline').sparkline();
* There must be no spaces in the enclosed data set
*
* Otherwise values must be an array of numbers or null values
*    <p>Sparkline: <span id="sparkline1">This text replaced if the browser is compatible</span></p>
*    $('#sparkline1').sparkline([1,4,6,6,8,5,3,5])
*    $('#sparkline2').sparkline([1,4,6,null,null,5,3,5])
*
* Values can also be specified in an HTML comment, or as a values attribute:
*    <p>Sparkline: <span class="sparkline"><!--1,4,6,6,8,5,3,5 --></span></p>
*    <p>Sparkline: <span class="sparkline" values="1,4,6,6,8,5,3,5"></span></p>
*    $('.sparkline').sparkline();
*
* For line charts, x values can also be specified:
*   <p>Sparkline: <span class="sparkline">1:1,2.7:4,3.4:6,5:6,6:8,8.7:5,9:3,10:5</span></p>
*    $('#sparkline1').sparkline([ [1,1], [2.7,4], [3.4,6], [5,6], [6,8], [8.7,5], [9,3], [10,5] ])
*
* By default, options should be passed in as teh second argument to the sparkline function:
*   $('.sparkline').sparkline([1,2,3,4], {type: 'bar'})
*
* Options can also be set by passing them on the tag itself.  This feature is disabled by default though
* as there's a slight performance overhead:
*   $('.sparkline').sparkline([1,2,3,4], {enableTagOptions: true})
*   <p>Sparkline: <span class="sparkline" sparkType="bar" sparkBarColor="red">loading</span></p>
* Prefix all options supplied as tag attribute with "spark" (configurable by setting tagOptionPrefix)
*
* Supported options:
*   lineColor - Color of the line used for the chart
*   fillColor - Color used to fill in the chart - Set to '' or false for a transparent chart
*   width - Width of the chart - Defaults to 3 times the number of values in pixels
*   height - Height of the chart - Defaults to the height of the containing element
*   chartRangeMin - Specify the minimum value to use for the Y range of the chart - Defaults to the minimum value supplied
*   chartRangeMax - Specify the maximum value to use for the Y range of the chart - Defaults to the maximum value supplied
*   chartRangeClip - Clip out of range values to the max/min specified by chartRangeMin and chartRangeMax
*   chartRangeMinX - Specify the minimum value to use for the X range of the chart - Defaults to the minimum value supplied
*   chartRangeMaxX - Specify the maximum value to use for the X range of the chart - Defaults to the maximum value supplied
*   composite - If true then don't erase any existing chart attached to the tag, but draw
*           another chart over the top - Note that width and height are ignored if an
*           existing chart is detected.
*   tagValuesAttribute - Name of tag attribute to check for data values - Defaults to 'values'
*   enableTagOptions - Whether to check tags for sparkline options
*   tagOptionPrefix - Prefix used for options supplied as tag attributes - Defaults to 'spark'
*   disableHiddenCheck - If set to true, then the plugin will assume that charts will never be drawn into a
*           hidden dom element, avoding a browser reflow
*   disableInteraction - If set to true then all mouseover/click interaction behaviour will be disabled,
*       making the plugin perform much like it did in 1.x
*   disableTooltips - If set to true then tooltips will be disabled - Defaults to false (tooltips enabled)
*   disableHighlight - If set to true then highlighting of selected chart elements on mouseover will be disabled
*       defaults to false (highlights enabled)
*   highlightLighten - Factor to lighten/darken highlighted chart values by - Defaults to 1.4 for a 40% increase
*   tooltipContainer - Specify which DOM element the tooltip should be rendered into - defaults to document.body
*   tooltipClassname - Optional CSS classname to apply to tooltips - If not specified then a default style will be applied
*   tooltipOffsetX - How many pixels away from the mouse pointer to render the tooltip on the X axis
*   tooltipOffsetY - How many pixels away from the mouse pointer to render the tooltip on the r axis
*   tooltipFormatter  - Optional callback that allows you to override the HTML displayed in the tooltip
*       callback is given arguments of (sparkline, options, fields)
*   tooltipChartTitle - If specified then the tooltip uses the string specified by this setting as a title
*   tooltipFormat - A format string or SPFormat object  (or an array thereof for multiple entries)
*       to control the format of the tooltip
*   tooltipPrefix - A string to prepend to each field displayed in a tooltip
*   tooltipSuffix - A string to append to each field displayed in a tooltip
*   tooltipSkipNull - If true then null values will not have a tooltip displayed (defaults to true)
*   tooltipValueLookups - An object or range map to map field values to tooltip strings
*       (eg. to map -1 to "Lost", 0 to "Draw", and 1 to "Win")
*   numberFormatter - Optional callback for formatting numbers in tooltips
*   numberDigitGroupSep - Character to use for group separator in numbers "1,234" - Defaults to ","
*   numberDecimalMark - Character to use for the decimal point when formatting numbers - Defaults to "."
*   numberDigitGroupCount - Number of digits between group separator - Defaults to 3
*
* There are 7 types of sparkline, selected by supplying a "type" option of 'line' (default),
* 'bar', 'tristate', 'bullet', 'discrete', 'pie' or 'box'
*    line - Line chart.  Options:
*       spotColor - Set to '' to not end each line in a circular spot
*       minSpotColor - If set, color of spot at minimum value
*       maxSpotColor - If set, color of spot at maximum value
*       spotRadius - Radius in pixels
*       lineWidth - Width of line in pixels
*       normalRangeMin
*       normalRangeMax - If set draws a filled horizontal bar between these two values marking the "normal"
*                      or expected range of values
*       normalRangeColor - Color to use for the above bar
*       drawNormalOnTop - Draw the normal range above the chart fill color if true
*       defaultPixelsPerValue - Defaults to 3 pixels of width for each value in the chart
*       highlightSpotColor - The color to use for drawing a highlight spot on mouseover - Set to null to disable
*       highlightLineColor - The color to use for drawing a highlight line on mouseover - Set to null to disable
*       valueSpots - Specify which points to draw spots on, and in which color.  Accepts a range map
*
*   bar - Bar chart.  Options:
*       barColor - Color of bars for postive values
*       negBarColor - Color of bars for negative values
*       zeroColor - Color of bars with zero values
*       nullColor - Color of bars with null values - Defaults to omitting the bar entirely
*       barWidth - Width of bars in pixels
*       colorMap - Optional mappnig of values to colors to override the *BarColor values above
*                  can be an Array of values to control the color of individual bars or a range map
*                  to specify colors for individual ranges of values
*       barSpacing - Gap between bars in pixels
*       zeroAxis - Centers the y-axis around zero if true
*
*   tristate - Charts values of win (>0), lose (<0) or draw (=0)
*       posBarColor - Color of win values
*       negBarColor - Color of lose values
*       zeroBarColor - Color of draw values
*       barWidth - Width of bars in pixels
*       barSpacing - Gap between bars in pixels
*       colorMap - Optional mappnig of values to colors to override the *BarColor values above
*                  can be an Array of values to control the color of individual bars or a range map
*                  to specify colors for individual ranges of values
*
*   discrete - Options:
*       lineHeight - Height of each line in pixels - Defaults to 30% of the graph height
*       thesholdValue - Values less than this value will be drawn using thresholdColor instead of lineColor
*       thresholdColor
*
*   bullet - Values for bullet graphs msut be in the order: target, performance, range1, range2, range3, ...
*       options:
*       targetColor - The color of the vertical target marker
*       targetWidth - The width of the target marker in pixels
*       performanceColor - The color of the performance measure horizontal bar
*       rangeColors - Colors to use for each qualitative range background color
*
*   pie - Pie chart. Options:
*       sliceColors - An array of colors to use for pie slices
*       offset - Angle in degrees to offset the first slice - Try -90 or +90
*       borderWidth - Width of border to draw around the pie chart, in pixels - Defaults to 0 (no border)
*       borderColor - Color to use for the pie chart border - Defaults to #000
*
*   box - Box plot. Options:
*       raw - Set to true to supply pre-computed plot points as values
*             values should be: low_outlier, low_whisker, q1, median, q3, high_whisker, high_outlier
*             When set to false you can supply any number of values and the box plot will
*             be computed for you.  Default is false.
*       showOutliers - Set to true (default) to display outliers as circles
*       outlierIQR - Interquartile range used to determine outliers.  Default 1.5
*       boxLineColor - Outline color of the box
*       boxFillColor - Fill color for the box
*       whiskerColor - Line color used for whiskers
*       outlierLineColor - Outline color of outlier circles
*       outlierFillColor - Fill color of the outlier circles
*       spotRadius - Radius of outlier circles
*       medianColor - Line color of the median line
*       target - Draw a target cross hair at the supplied value (default undefined)
*
*
*
*   Examples:
*   $('#sparkline1').sparkline(myvalues, { lineColor: '#f00', fillColor: false });
*   $('.barsparks').sparkline('html', { type:'bar', height:'40px', barWidth:5 });
*   $('#tristate').sparkline([1,1,-1,1,0,0,-1], { type:'tristate' }):
*   $('#discrete').sparkline([1,3,4,5,5,3,4,5], { type:'discrete' });
*   $('#bullet').sparkline([10,12,12,9,7], { type:'bullet' });
*   $('#pie').sparkline([1,1,2], { type:'pie' });
*/

/*jslint regexp: true, browser: true, jquery: true, white: true, nomen: false, plusplus: false, maxerr: 500, indent: 4 */


(function(document, Math, undefined) { // performance/minified-size optimization
(function(factory) {
    if(typeof define === 'function' && define.amd) {
        define(['jquery'], factory);
    } else if (jQuery && !jQuery.fn.sparkline) {
        factory(jQuery);
    }
}
(function($) {
    'use strict';

    var UNSET_OPTION = {},
        getDefaults, createClass, SPFormat, clipval, quartile, normalizeValue, normalizeValues,
        remove, isNumber, all, sum, addCSS, ensureArray, formatNumber, RangeMap,
        MouseHandler, Tooltip, barHighlightMixin,
        line, bar, tristate, discrete, bullet, pie, box, defaultStyles, initStyles,
        VShape, VCanvas_base, VCanvas_canvas, VCanvas_vml, pending, shapeCount = 0;

    /**
     * Default configuration settings
     */
    getDefaults = function () {
        return {
            // Settings common to most/all chart types
            common: {
                type: 'line',
                lineColor: '#00f',
                fillColor: '#cdf',
                defaultPixelsPerValue: 3,
                width: 'auto',
                height: 'auto',
                composite: false,
                tagValuesAttribute: 'values',
                tagOptionsPrefix: 'spark',
                enableTagOptions: false,
                enableHighlight: true,
                highlightLighten: 1.4,
                tooltipSkipNull: true,
                tooltipPrefix: '',
                tooltipSuffix: '',
                disableHiddenCheck: false,
                numberFormatter: false,
                numberDigitGroupCount: 3,
                numberDigitGroupSep: ',',
                numberDecimalMark: '.',
                disableTooltips: false,
                disableInteraction: false
            },
            // Defaults for line charts
            line: {
                spotColor: '#f80',
                highlightSpotColor: '#5f5',
                highlightLineColor: '#f22',
                spotRadius: 1.5,
                minSpotColor: '#f80',
                maxSpotColor: '#f80',
                lineWidth: 1,
                normalRangeMin: undefined,
                normalRangeMax: undefined,
                normalRangeColor: '#ccc',
                drawNormalOnTop: false,
                chartRangeMin: undefined,
                chartRangeMax: undefined,
                chartRangeMinX: undefined,
                chartRangeMaxX: undefined,
                tooltipFormat: new SPFormat('<span style="color: {{color}}">&#9679;</span> {{prefix}}{{y}}{{suffix}}')
            },
            // Defaults for bar charts
            bar: {
                barColor: '#3366cc',
                negBarColor: '#f44',
                stackedBarColor: ['#3366cc', '#dc3912', '#ff9900', '#109618', '#66aa00',
                    '#dd4477', '#0099c6', '#990099'],
                zeroColor: undefined,
                nullColor: undefined,
                zeroAxis: true,
                barWidth: 4,
                barSpacing: 1,
                chartRangeMax: undefined,
                chartRangeMin: undefined,
                chartRangeClip: false,
                colorMap: undefined,
                tooltipFormat: new SPFormat('<span style="color: {{color}}">&#9679;</span> {{prefix}}{{value}}{{suffix}}')
            },
            // Defaults for tristate charts
            tristate: {
                barWidth: 4,
                barSpacing: 1,
                posBarColor: '#6f6',
                negBarColor: '#f44',
                zeroBarColor: '#999',
                colorMap: {},
                tooltipFormat: new SPFormat('<span style="color: {{color}}">&#9679;</span> {{value:map}}'),
                tooltipValueLookups: { map: { '-1': 'Loss', '0': 'Draw', '1': 'Win' } }
            },
            // Defaults for discrete charts
            discrete: {
                lineHeight: 'auto',
                thresholdColor: undefined,
                thresholdValue: 0,
                chartRangeMax: undefined,
                chartRangeMin: undefined,
                chartRangeClip: false,
                tooltipFormat: new SPFormat('{{prefix}}{{value}}{{suffix}}')
            },
            // Defaults for bullet charts
            bullet: {
                targetColor: '#f33',
                targetWidth: 3, // width of the target bar in pixels
                performanceColor: '#33f',
                rangeColors: ['#d3dafe', '#a8b6ff', '#7f94ff'],
                base: undefined, // set this to a number to change the base start number
                tooltipFormat: new SPFormat('{{fieldkey:fields}} - {{value}}'),
                tooltipValueLookups: { fields: {r: 'Range', p: 'Performance', t: 'Target'} }
            },
            // Defaults for pie charts
            pie: {
                offset: 0,
                sliceColors: ['#3366cc', '#dc3912', '#ff9900', '#109618', '#66aa00',
                    '#dd4477', '#0099c6', '#990099'],
                borderWidth: 0,
                borderColor: '#000',
                tooltipFormat: new SPFormat('<span style="color: {{color}}">&#9679;</span> {{value}} ({{percent.1}}%)')
            },
            // Defaults for box plots
            box: {
                raw: false,
                boxLineColor: '#000',
                boxFillColor: '#cdf',
                whiskerColor: '#000',
                outlierLineColor: '#333',
                outlierFillColor: '#fff',
                medianColor: '#f00',
                showOutliers: true,
                outlierIQR: 1.5,
                spotRadius: 1.5,
                target: undefined,
                targetColor: '#4a2',
                chartRangeMax: undefined,
                chartRangeMin: undefined,
                tooltipFormat: new SPFormat('{{field:fields}}: {{value}}'),
                tooltipFormatFieldlistKey: 'field',
                tooltipValueLookups: { fields: { lq: 'Lower Quartile', med: 'Median',
                    uq: 'Upper Quartile', lo: 'Left Outlier', ro: 'Right Outlier',
                    lw: 'Left Whisker', rw: 'Right Whisker'} }
            }
        };
    };

    // You can have tooltips use a css class other than jqstooltip by specifying tooltipClassname
    defaultStyles = '.jqstooltip { ' +
            'position: absolute;' +
            'left: 0px;' +
            'top: 0px;' +
            'visibility: hidden;' +
            'background: rgb(0, 0, 0) transparent;' +
            'background-color: rgba(0,0,0,0.6);' +
            'filter:progid:DXImageTransform.Microsoft.gradient(startColorstr=#99000000, endColorstr=#99000000);' +
            '-ms-filter: "progid:DXImageTransform.Microsoft.gradient(startColorstr=#99000000, endColorstr=#99000000)";' +
            'color: white;' +
            'font: 10px arial, san serif;' +
            'text-align: left;' +
            'white-space: nowrap;' +
            'padding: 5px;' +
            'border: 1px solid white;' +
            'z-index: 10000;' +
            '}' +
            '.jqsfield { ' +
            'color: white;' +
            'font: 10px arial, san serif;' +
            'text-align: left;' +
            '}';

    /**
     * Utilities
     */

    createClass = function (/* [baseclass, [mixin, ...]], definition */) {
        var Class, args;
        Class = function () {
            this.init.apply(this, arguments);
        };
        if (arguments.length > 1) {
            if (arguments[0]) {
                Class.prototype = $.extend(new arguments[0](), arguments[arguments.length - 1]);
                Class._super = arguments[0].prototype;
            } else {
                Class.prototype = arguments[arguments.length - 1];
            }
            if (arguments.length > 2) {
                args = Array.prototype.slice.call(arguments, 1, -1);
                args.unshift(Class.prototype);
                $.extend.apply($, args);
            }
        } else {
            Class.prototype = arguments[0];
        }
        Class.prototype.cls = Class;
        return Class;
    };

    /**
     * Wraps a format string for tooltips
     * {{x}}
     * {{x.2}
     * {{x:months}}
     */
    $.SPFormatClass = SPFormat = createClass({
        fre: /\{\{([\w.]+?)(:(.+?))?\}\}/g,
        precre: /(\w+)\.(\d+)/,

        init: function (format, fclass) {
            this.format = format;
            this.fclass = fclass;
        },

        render: function (fieldset, lookups, options) {
            var self = this,
                fields = fieldset,
                match, token, lookupkey, fieldvalue, prec;
            return this.format.replace(this.fre, function () {
                var lookup;
                token = arguments[1];
                lookupkey = arguments[3];
                match = self.precre.exec(token);
                if (match) {
                    prec = match[2];
                    token = match[1];
                } else {
                    prec = false;
                }
                fieldvalue = fields[token];
                if (fieldvalue === undefined) {
                    return '';
                }
                if (lookupkey && lookups && lookups[lookupkey]) {
                    lookup = lookups[lookupkey];
                    if (lookup.get) { // RangeMap
                        return lookups[lookupkey].get(fieldvalue) || fieldvalue;
                    } else {
                        return lookups[lookupkey][fieldvalue] || fieldvalue;
                    }
                }
                if (isNumber(fieldvalue)) {
                    if (options.get('numberFormatter')) {
                        fieldvalue = options.get('numberFormatter')(fieldvalue);
                    } else {
                        fieldvalue = formatNumber(fieldvalue, prec,
                            options.get('numberDigitGroupCount'),
                            options.get('numberDigitGroupSep'),
                            options.get('numberDecimalMark'));
                    }
                }
                return fieldvalue;
            });
        }
    });

    // convience method to avoid needing the new operator
    $.spformat = function(format, fclass) {
        return new SPFormat(format, fclass);
    };

    clipval = function (val, min, max) {
        if (val < min) {
            return min;
        }
        if (val > max) {
            return max;
        }
        return val;
    };

    quartile = function (values, q) {
        var vl;
        if (q === 2) {
            vl = Math.floor(values.length / 2);
            return values.length % 2 ? values[vl] : (values[vl-1] + values[vl]) / 2;
        } else {
            if (values.length % 2 ) { // odd
                vl = (values.length * q + q) / 4;
                return vl % 1 ? (values[Math.floor(vl)] + values[Math.floor(vl) - 1]) / 2 : values[vl-1];
            } else { //even
                vl = (values.length * q + 2) / 4;
                return vl % 1 ? (values[Math.floor(vl)] + values[Math.floor(vl) - 1]) / 2 :  values[vl-1];

            }
        }
    };

    normalizeValue = function (val) {
        var nf;
        switch (val) {
            case 'undefined':
                val = undefined;
                break;
            case 'null':
                val = null;
                break;
            case 'true':
                val = true;
                break;
            case 'false':
                val = false;
                break;
            default:
                nf = parseFloat(val);
                if (val == nf) {
                    val = nf;
                }
        }
        return val;
    };

    normalizeValues = function (vals) {
        var i, result = [];
        for (i = vals.length; i--;) {
            result[i] = normalizeValue(vals[i]);
        }
        return result;
    };

    remove = function (vals, filter) {
        var i, vl, result = [];
        for (i = 0, vl = vals.length; i < vl; i++) {
            if (vals[i] !== filter) {
                result.push(vals[i]);
            }
        }
        return result;
    };

    isNumber = function (num) {
        return !isNaN(parseFloat(num)) && isFinite(num);
    };

    formatNumber = function (num, prec, groupsize, groupsep, decsep) {
        var p, i;
        num = (prec === false ? parseFloat(num).toString() : num.toFixed(prec)).split('');
        p = (p = $.inArray('.', num)) < 0 ? num.length : p;
        if (p < num.length) {
            num[p] = decsep;
        }
        for (i = p - groupsize; i > 0; i -= groupsize) {
            num.splice(i, 0, groupsep);
        }
        return num.join('');
    };

    // determine if all values of an array match a value
    // returns true if the array is empty
    all = function (val, arr, ignoreNull) {
        var i;
        for (i = arr.length; i--; ) {
            if (ignoreNull && arr[i] === null) continue;
            if (arr[i] !== val) {
                return false;
            }
        }
        return true;
    };

    // sums the numeric values in an array, ignoring other values
    sum = function (vals) {
        var total = 0, i;
        for (i = vals.length; i--;) {
            total += typeof vals[i] === 'number' ? vals[i] : 0;
        }
        return total;
    };

    ensureArray = function (val) {
        return $.isArray(val) ? val : [val];
    };

    // http://paulirish.com/2008/bookmarklet-inject-new-css-rules/
    addCSS = function(css) {
        var tag;
        //if ('\v' == 'v') /* ie only */ {
        if (document.createStyleSheet) {
            document.createStyleSheet().cssText = css;
        } else {
            tag = document.createElement('style');
            tag.type = 'text/css';
            document.getElementsByTagName('head')[0].appendChild(tag);
            tag[(typeof document.body.style.WebkitAppearance == 'string') /* webkit only */ ? 'innerText' : 'innerHTML'] = css;
        }
    };

    // Provide a cross-browser interface to a few simple drawing primitives
    $.fn.simpledraw = function (width, height, useExisting, interact) {
        var target, mhandler;
        if (useExisting && (target = this.data('_jqs_vcanvas'))) {
            return target;
        }

        if ($.fn.sparkline.canvas === false) {
            // We've already determined that neither Canvas nor VML are available
            return false;

        } else if ($.fn.sparkline.canvas === undefined) {
            // No function defined yet -- need to see if we support Canvas or VML
            var el = document.createElement('canvas');
            if (!!(el.getContext && el.getContext('2d'))) {
                // Canvas is available
                $.fn.sparkline.canvas = function(width, height, target, interact) {
                    return new VCanvas_canvas(width, height, target, interact);
                };
            } else if (document.namespaces && !document.namespaces.v) {
                // VML is available
                document.namespaces.add('v', 'urn:schemas-microsoft-com:vml', '#default#VML');
                $.fn.sparkline.canvas = function(width, height, target, interact) {
                    return new VCanvas_vml(width, height, target);
                };
            } else {
                // Neither Canvas nor VML are available
                $.fn.sparkline.canvas = false;
                return false;
            }
        }

        if (width === undefined) {
            width = $(this).innerWidth();
        }
        if (height === undefined) {
            height = $(this).innerHeight();
        }

        target = $.fn.sparkline.canvas(width, height, this, interact);

        mhandler = $(this).data('_jqs_mhandler');
        if (mhandler) {
            mhandler.registerCanvas(target);
        }
        return target;
    };

    $.fn.cleardraw = function () {
        var target = this.data('_jqs_vcanvas');
        if (target) {
            target.reset();
        }
    };

    $.RangeMapClass = RangeMap = createClass({
        init: function (map) {
            var key, range, rangelist = [];
            for (key in map) {
                if (map.hasOwnProperty(key) && typeof key === 'string' && key.indexOf(':') > -1) {
                    range = key.split(':');
                    range[0] = range[0].length === 0 ? -Infinity : parseFloat(range[0]);
                    range[1] = range[1].length === 0 ? Infinity : parseFloat(range[1]);
                    range[2] = map[key];
                    rangelist.push(range);
                }
            }
            this.map = map;
            this.rangelist = rangelist || false;
        },

        get: function (value) {
            var rangelist = this.rangelist,
                i, range, result;
            if ((result = this.map[value]) !== undefined) {
                return result;
            }
            if (rangelist) {
                for (i = rangelist.length; i--;) {
                    range = rangelist[i];
                    if (range[0] <= value && range[1] >= value) {
                        return range[2];
                    }
                }
            }
            return undefined;
        }
    });

    // Convenience function
    $.range_map = function(map) {
        return new RangeMap(map);
    };

    MouseHandler = createClass({
        init: function (el, options) {
            var $el = $(el);
            this.$el = $el;
            this.options = options;
            this.currentPageX = 0;
            this.currentPageY = 0;
            this.el = el;
            this.splist = [];
            this.tooltip = null;
            this.over = false;
            this.displayTooltips = !options.get('disableTooltips');
            this.highlightEnabled = !options.get('disableHighlight');
        },

        registerSparkline: function (sp) {
            this.splist.push(sp);
            if (this.over) {
                this.updateDisplay();
            }
        },

        registerCanvas: function (canvas) {
            var $canvas = $(canvas.canvas);
            this.canvas = canvas;
            this.$canvas = $canvas;
            $canvas.mouseenter($.proxy(this.mouseenter, this));
            $canvas.mouseleave($.proxy(this.mouseleave, this));
            $canvas.click($.proxy(this.mouseclick, this));
        },

        reset: function (removeTooltip) {
            this.splist = [];
            if (this.tooltip && removeTooltip) {
                this.tooltip.remove();
                this.tooltip = undefined;
            }
        },

        mouseclick: function (e) {
            var clickEvent = $.Event('sparklineClick');
            clickEvent.originalEvent = e;
            clickEvent.sparklines = this.splist;
            this.$el.trigger(clickEvent);
        },

        mouseenter: function (e) {
            $(document.body).unbind('mousemove.jqs');
            $(document.body).bind('mousemove.jqs', $.proxy(this.mousemove, this));
            this.over = true;
            this.currentPageX = e.pageX;
            this.currentPageY = e.pageY;
            this.currentEl = e.target;
            if (!this.tooltip && this.displayTooltips) {
                this.tooltip = new Tooltip(this.options);
                this.tooltip.updatePosition(e.pageX, e.pageY);
            }
            this.updateDisplay();
        },

        mouseleave: function () {
            $(document.body).unbind('mousemove.jqs');
            var splist = this.splist,
                 spcount = splist.length,
                 needsRefresh = false,
                 sp, i;
            this.over = false;
            this.currentEl = null;

            if (this.tooltip) {
                this.tooltip.remove();
                this.tooltip = null;
            }

            for (i = 0; i < spcount; i++) {
                sp = splist[i];
                if (sp.clearRegionHighlight()) {
                    needsRefresh = true;
                }
            }

            if (needsRefresh) {
                this.canvas.render();
            }
        },

        mousemove: function (e) {
            this.currentPageX = e.pageX;
            this.currentPageY = e.pageY;
            this.currentEl = e.target;
            if (this.tooltip) {
                this.tooltip.updatePosition(e.pageX, e.pageY);
            }
            this.updateDisplay();
        },

        updateDisplay: function () {
            var splist = this.splist,
                 spcount = splist.length,
                 needsRefresh = false,
                 offset = this.$canvas.offset(),
                 localX = this.currentPageX - offset.left,
                 localY = this.currentPageY - offset.top,
                 tooltiphtml, sp, i, result, changeEvent;
            if (!this.over) {
                return;
            }
            for (i = 0; i < spcount; i++) {
                sp = splist[i];
                result = sp.setRegionHighlight(this.currentEl, localX, localY);
                if (result) {
                    needsRefresh = true;
                }
            }
            if (needsRefresh) {
                changeEvent = $.Event('sparklineRegionChange');
                changeEvent.sparklines = this.splist;
                this.$el.trigger(changeEvent);
                if (this.tooltip) {
                    tooltiphtml = '';
                    for (i = 0; i < spcount; i++) {
                        sp = splist[i];
                        tooltiphtml += sp.getCurrentRegionTooltip();
                    }
                    this.tooltip.setContent(tooltiphtml);
                }
                if (!this.disableHighlight) {
                    this.canvas.render();
                }
            }
            if (result === null) {
                this.mouseleave();
            }
        }
    });


    Tooltip = createClass({
        sizeStyle: 'position: static !important;' +
            'display: block !important;' +
            'visibility: hidden !important;' +
            'float: left !important;',

        init: function (options) {
            var tooltipClassname = options.get('tooltipClassname', 'jqstooltip'),
                sizetipStyle = this.sizeStyle,
                offset;
            this.container = options.get('tooltipContainer') || document.body;
            this.tooltipOffsetX = options.get('tooltipOffsetX', 10);
            this.tooltipOffsetY = options.get('tooltipOffsetY', 12);
            // remove any previous lingering tooltip
            $('#jqssizetip').remove();
            $('#jqstooltip').remove();
            this.sizetip = $('<div/>', {
                id: 'jqssizetip',
                style: sizetipStyle,
                'class': tooltipClassname
            });
            this.tooltip = $('<div/>', {
                id: 'jqstooltip',
                'class': tooltipClassname
            }).appendTo(this.container);
            // account for the container's location
            offset = this.tooltip.offset();
            this.offsetLeft = offset.left;
            this.offsetTop = offset.top;
            this.hidden = true;
            $(window).unbind('resize.jqs scroll.jqs');
            $(window).bind('resize.jqs scroll.jqs', $.proxy(this.updateWindowDims, this));
            this.updateWindowDims();
        },

        updateWindowDims: function () {
            this.scrollTop = $(window).scrollTop();
            this.scrollLeft = $(window).scrollLeft();
            this.scrollRight = this.scrollLeft + $(window).width();
            this.updatePosition();
        },

        getSize: function (content) {
            this.sizetip.html(content).appendTo(this.container);
            this.width = this.sizetip.width() + 1;
            this.height = this.sizetip.height();
            this.sizetip.remove();
        },

        setContent: function (content) {
            if (!content) {
                this.tooltip.css('visibility', 'hidden');
                this.hidden = true;
                return;
            }
            this.getSize(content);
            this.tooltip.html(content)
                .css({
                    'width': this.width,
                    'height': this.height,
                    'visibility': 'visible'
                });
            if (this.hidden) {
                this.hidden = false;
                this.updatePosition();
            }
        },

        updatePosition: function (x, y) {
            if (x === undefined) {
                if (this.mousex === undefined) {
                    return;
                }
                x = this.mousex - this.offsetLeft;
                y = this.mousey - this.offsetTop;

            } else {
                this.mousex = x = x - this.offsetLeft;
                this.mousey = y = y - this.offsetTop;
            }
            if (!this.height || !this.width || this.hidden) {
                return;
            }

            y -= this.height + this.tooltipOffsetY;
            x += this.tooltipOffsetX;

            if (y < this.scrollTop) {
                y = this.scrollTop;
            }
            if (x < this.scrollLeft) {
                x = this.scrollLeft;
            } else if (x + this.width > this.scrollRight) {
                x = this.scrollRight - this.width;
            }

            this.tooltip.css({
                'left': x,
                'top': y
            });
        },

        remove: function () {
            this.tooltip.remove();
            this.sizetip.remove();
            this.sizetip = this.tooltip = undefined;
            $(window).unbind('resize.jqs scroll.jqs');
        }
    });

    initStyles = function() {
        addCSS(defaultStyles);
    };

    $(initStyles);

    pending = [];
    $.fn.sparkline = function (userValues, userOptions) {
        return this.each(function () {
            var options = new $.fn.sparkline.options(this, userOptions),
                 $this = $(this),
                 render, i;
            render = function () {
                var values, width, height, tmp, mhandler, sp, vals;
                if (userValues === 'html' || userValues === undefined) {
                    vals = this.getAttribute(options.get('tagValuesAttribute'));
                    if (vals === undefined || vals === null) {
                        vals = $this.html();
                    }
                    values = vals.replace(/(^\s*<!--)|(-->\s*$)|\s+/g, '').split(',');
                } else {
                    values = userValues;
                }

                width = options.get('width') === 'auto' ? values.length * options.get('defaultPixelsPerValue') : options.get('width');
                if (options.get('height') === 'auto') {
                    if (!options.get('composite') || !$.data(this, '_jqs_vcanvas')) {
                        // must be a better way to get the line height
                        tmp = document.createElement('span');
                        tmp.innerHTML = 'a';
                        $this.html(tmp);
                        height = $(tmp).innerHeight() || $(tmp).height();
                        $(tmp).remove();
                        tmp = null;
                    }
                } else {
                    height = options.get('height');
                }

                if (!options.get('disableInteraction')) {
                    mhandler = $.data(this, '_jqs_mhandler');
                    if (!mhandler) {
                        mhandler = new MouseHandler(this, options);
                        $.data(this, '_jqs_mhandler', mhandler);
                    } else if (!options.get('composite')) {
                        mhandler.reset();
                    }
                } else {
                    mhandler = false;
                }

                if (options.get('composite') && !$.data(this, '_jqs_vcanvas')) {
                    if (!$.data(this, '_jqs_errnotify')) {
                        alert('Attempted to attach a composite sparkline to an element with no existing sparkline');
                        $.data(this, '_jqs_errnotify', true);
                    }
                    return;
                }

                sp = new $.fn.sparkline[options.get('type')](this, values, options, width, height);

                sp.render();

                if (mhandler) {
                    mhandler.registerSparkline(sp);
                }
            };
            if (($(this).html() && !options.get('disableHiddenCheck') && $(this).is(':hidden')) || !$(this).parents('body').length) {
                if (!options.get('composite') && $.data(this, '_jqs_pending')) {
                    // remove any existing references to the element
                    for (i = pending.length; i; i--) {
                        if (pending[i - 1][0] == this) {
                            pending.splice(i - 1, 1);
                        }
                    }
                }
                pending.push([this, render]);
                $.data(this, '_jqs_pending', true);
            } else {
                render.call(this);
            }
        });
    };

    $.fn.sparkline.defaults = getDefaults();


    $.sparkline_display_visible = function () {
        var el, i, pl;
        var done = [];
        for (i = 0, pl = pending.length; i < pl; i++) {
            el = pending[i][0];
            if ($(el).is(':visible') && !$(el).parents().is(':hidden')) {
                pending[i][1].call(el);
                $.data(pending[i][0], '_jqs_pending', false);
                done.push(i);
            } else if (!$(el).closest('html').length && !$.data(el, '_jqs_pending')) {
                // element has been inserted and removed from the DOM
                // If it was not yet inserted into the dom then the .data request
                // will return true.
                // removing from the dom causes the data to be removed.
                $.data(pending[i][0], '_jqs_pending', false);
                done.push(i);
            }
        }
        for (i = done.length; i; i--) {
            pending.splice(done[i - 1], 1);
        }
    };


    /**
     * User option handler
     */
    $.fn.sparkline.options = createClass({
        init: function (tag, userOptions) {
            var extendedOptions, defaults, base, tagOptionType;
            this.userOptions = userOptions = userOptions || {};
            this.tag = tag;
            this.tagValCache = {};
            defaults = $.fn.sparkline.defaults;
            base = defaults.common;
            this.tagOptionsPrefix = userOptions.enableTagOptions && (userOptions.tagOptionsPrefix || base.tagOptionsPrefix);

            tagOptionType = this.getTagSetting('type');
            if (tagOptionType === UNSET_OPTION) {
                extendedOptions = defaults[userOptions.type || base.type];
            } else {
                extendedOptions = defaults[tagOptionType];
            }
            this.mergedOptions = $.extend({}, base, extendedOptions, userOptions);
        },


        getTagSetting: function (key) {
            var prefix = this.tagOptionsPrefix,
                val, i, pairs, keyval;
            if (prefix === false || prefix === undefined) {
                return UNSET_OPTION;
            }
            if (this.tagValCache.hasOwnProperty(key)) {
                val = this.tagValCache.key;
            } else {
                val = this.tag.getAttribute(prefix + key);
                if (val === undefined || val === null) {
                    val = UNSET_OPTION;
                } else if (val.substr(0, 1) === '[') {
                    val = val.substr(1, val.length - 2).split(',');
                    for (i = val.length; i--;) {
                        val[i] = normalizeValue(val[i].replace(/(^\s*)|(\s*$)/g, ''));
                    }
                } else if (val.substr(0, 1) === '{') {
                    pairs = val.substr(1, val.length - 2).split(',');
                    val = {};
                    for (i = pairs.length; i--;) {
                        keyval = pairs[i].split(':', 2);
                        val[keyval[0].replace(/(^\s*)|(\s*$)/g, '')] = normalizeValue(keyval[1].replace(/(^\s*)|(\s*$)/g, ''));
                    }
                } else {
                    val = normalizeValue(val);
                }
                this.tagValCache.key = val;
            }
            return val;
        },

        get: function (key, defaultval) {
            var tagOption = this.getTagSetting(key),
                result;
            if (tagOption !== UNSET_OPTION) {
                return tagOption;
            }
            return (result = this.mergedOptions[key]) === undefined ? defaultval : result;
        }
    });


    $.fn.sparkline._base = createClass({
        disabled: false,

        init: function (el, values, options, width, height) {
            this.el = el;
            this.$el = $(el);
            this.values = values;
            this.options = options;
            this.width = width;
            this.height = height;
            this.currentRegion = undefined;
        },

        /**
         * Setup the canvas
         */
        initTarget: function () {
            var interactive = !this.options.get('disableInteraction');
            if (!(this.target = this.$el.simpledraw(this.width, this.height, this.options.get('composite'), interactive))) {
                this.disabled = true;
            } else {
                this.canvasWidth = this.target.pixelWidth;
                this.canvasHeight = this.target.pixelHeight;
            }
        },

        /**
         * Actually render the chart to the canvas
         */
        render: function () {
            if (this.disabled) {
                this.el.innerHTML = '';
                return false;
            }
            return true;
        },

        /**
         * Return a region id for a given x/y co-ordinate
         */
        getRegion: function (x, y) {
        },

        /**
         * Highlight an item based on the moused-over x,y co-ordinate
         */
        setRegionHighlight: function (el, x, y) {
            var currentRegion = this.currentRegion,
                highlightEnabled = !this.options.get('disableHighlight'),
                newRegion;
            if (x > this.canvasWidth || y > this.canvasHeight || x < 0 || y < 0) {
                return null;
            }
            newRegion = this.getRegion(el, x, y);
            if (currentRegion !== newRegion) {
                if (currentRegion !== undefined && highlightEnabled) {
                    this.removeHighlight();
                }
                this.currentRegion = newRegion;
                if (newRegion !== undefined && highlightEnabled) {
                    this.renderHighlight();
                }
                return true;
            }
            return false;
        },

        /**
         * Reset any currently highlighted item
         */
        clearRegionHighlight: function () {
            if (this.currentRegion !== undefined) {
                this.removeHighlight();
                this.currentRegion = undefined;
                return true;
            }
            return false;
        },

        renderHighlight: function () {
            this.changeHighlight(true);
        },

        removeHighlight: function () {
            this.changeHighlight(false);
        },

        changeHighlight: function (highlight)  {},

        /**
         * Fetch the HTML to display as a tooltip
         */
        getCurrentRegionTooltip: function () {
            var options = this.options,
                header = '',
                entries = [],
                fields, formats, formatlen, fclass, text, i,
                showFields, showFieldsKey, newFields, fv,
                formatter, format, fieldlen, j;
            if (this.currentRegion === undefined) {
                return '';
            }
            fields = this.getCurrentRegionFields();
            formatter = options.get('tooltipFormatter');
            if (formatter) {
                return formatter(this, options, fields);
            }
            if (options.get('tooltipChartTitle')) {
                header += '<div class="jqs jqstitle">' + options.get('tooltipChartTitle') + '</div>\n';
            }
            formats = this.options.get('tooltipFormat');
            if (!formats) {
                return '';
            }
            if (!$.isArray(formats)) {
                formats = [formats];
            }
            if (!$.isArray(fields)) {
                fields = [fields];
            }
            showFields = this.options.get('tooltipFormatFieldlist');
            showFieldsKey = this.options.get('tooltipFormatFieldlistKey');
            if (showFields && showFieldsKey) {
                // user-selected ordering of fields
                newFields = [];
                for (i = fields.length; i--;) {
                    fv = fields[i][showFieldsKey];
                    if ((j = $.inArray(fv, showFields)) != -1) {
                        newFields[j] = fields[i];
                    }
                }
                fields = newFields;
            }
            formatlen = formats.length;
            fieldlen = fields.length;
            for (i = 0; i < formatlen; i++) {
                format = formats[i];
                if (typeof format === 'string') {
                    format = new SPFormat(format);
                }
                fclass = format.fclass || 'jqsfield';
                for (j = 0; j < fieldlen; j++) {
                    if (!fields[j].isNull || !options.get('tooltipSkipNull')) {
                        $.extend(fields[j], {
                            prefix: options.get('tooltipPrefix'),
                            suffix: options.get('tooltipSuffix')
                        });
                        text = format.render(fields[j], options.get('tooltipValueLookups'), options);
                        entries.push('<div class="' + fclass + '">' + text + '</div>');
                    }
                }
            }
            if (entries.length) {
                return header + entries.join('\n');
            }
            return '';
        },

        getCurrentRegionFields: function () {},

        calcHighlightColor: function (color, options) {
            var highlightColor = options.get('highlightColor'),
                lighten = options.get('highlightLighten'),
                parse, mult, rgbnew, i;
            if (highlightColor) {
                return highlightColor;
            }
            if (lighten) {
                // extract RGB values
                parse = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(color) || /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(color);
                if (parse) {
                    rgbnew = [];
                    mult = color.length === 4 ? 16 : 1;
                    for (i = 0; i < 3; i++) {
                        rgbnew[i] = clipval(Math.round(parseInt(parse[i + 1], 16) * mult * lighten), 0, 255);
                    }
                    return 'rgb(' + rgbnew.join(',') + ')';
                }

            }
            return color;
        }

    });

    barHighlightMixin = {
        changeHighlight: function (highlight) {
            var currentRegion = this.currentRegion,
                target = this.target,
                shapeids = this.regionShapes[currentRegion],
                newShapes;
            // will be null if the region value was null
            if (shapeids) {
                newShapes = this.renderRegion(currentRegion, highlight);
                if ($.isArray(newShapes) || $.isArray(shapeids)) {
                    target.replaceWithShapes(shapeids, newShapes);
                    this.regionShapes[currentRegion] = $.map(newShapes, function (newShape) {
                        return newShape.id;
                    });
                } else {
                    target.replaceWithShape(shapeids, newShapes);
                    this.regionShapes[currentRegion] = newShapes.id;
                }
            }
        },

        render: function () {
            var values = this.values,
                target = this.target,
                regionShapes = this.regionShapes,
                shapes, ids, i, j;

            if (!this.cls._super.render.call(this)) {
                return;
            }
            for (i = values.length; i--;) {
                shapes = this.renderRegion(i);
                if (shapes) {
                    if ($.isArray(shapes)) {
                        ids = [];
                        for (j = shapes.length; j--;) {
                            shapes[j].append();
                            ids.push(shapes[j].id);
                        }
                        regionShapes[i] = ids;
                    } else {
                        shapes.append();
                        regionShapes[i] = shapes.id; // store just the shapeid
                    }
                } else {
                    // null value
                    regionShapes[i] = null;
                }
            }
            target.render();
        }
    };

    /**
     * Line charts
     */
    $.fn.sparkline.line = line = createClass($.fn.sparkline._base, {
        type: 'line',

        init: function (el, values, options, width, height) {
            line._super.init.call(this, el, values, options, width, height);
            this.vertices = [];
            this.regionMap = [];
            this.xvalues = [];
            this.yvalues = [];
            this.yminmax = [];
            this.hightlightSpotId = null;
            this.lastShapeId = null;
            this.initTarget();
        },

        getRegion: function (el, x, y) {
            var i,
                regionMap = this.regionMap; // maps regions to value positions
            for (i = regionMap.length; i--;) {
                if (regionMap[i] !== null && x >= regionMap[i][0] && x <= regionMap[i][1]) {
                    return regionMap[i][2];
                }
            }
            return undefined;
        },

        getCurrentRegionFields: function () {
            var currentRegion = this.currentRegion;
            return {
                isNull: this.yvalues[currentRegion] === null,
                x: this.xvalues[currentRegion],
                y: this.yvalues[currentRegion],
                color: this.options.get('lineColor'),
                fillColor: this.options.get('fillColor'),
                offset: currentRegion
            };
        },

        renderHighlight: function () {
            var currentRegion = this.currentRegion,
                target = this.target,
                vertex = this.vertices[currentRegion],
                options = this.options,
                spotRadius = options.get('spotRadius'),
                highlightSpotColor = options.get('highlightSpotColor'),
                highlightLineColor = options.get('highlightLineColor'),
                highlightSpot, highlightLine;

            if (!vertex) {
                return;
            }
            if (spotRadius && highlightSpotColor) {
                highlightSpot = target.drawCircle(vertex[0], vertex[1],
                    spotRadius, undefined, highlightSpotColor);
                this.highlightSpotId = highlightSpot.id;
                target.insertAfterShape(this.lastShapeId, highlightSpot);
            }
            if (highlightLineColor) {
                highlightLine = target.drawLine(vertex[0], this.canvasTop, vertex[0],
                    this.canvasTop + this.canvasHeight, highlightLineColor);
                this.highlightLineId = highlightLine.id;
                target.insertAfterShape(this.lastShapeId, highlightLine);
            }
        },

        removeHighlight: function () {
            var target = this.target;
            if (this.highlightSpotId) {
                target.removeShapeId(this.highlightSpotId);
                this.highlightSpotId = null;
            }
            if (this.highlightLineId) {
                target.removeShapeId(this.highlightLineId);
                this.highlightLineId = null;
            }
        },

        scanValues: function () {
            var values = this.values,
                valcount = values.length,
                xvalues = this.xvalues,
                yvalues = this.yvalues,
                yminmax = this.yminmax,
                i, val, isStr, isArray, sp;
            for (i = 0; i < valcount; i++) {
                val = values[i];
                isStr = typeof(values[i]) === 'string';
                isArray = typeof(values[i]) === 'object' && values[i] instanceof Array;
                sp = isStr && values[i].split(':');
                if (isStr && sp.length === 2) { // x:y
                    xvalues.push(Number(sp[0]));
                    yvalues.push(Number(sp[1]));
                    yminmax.push(Number(sp[1]));
                } else if (isArray) {
                    xvalues.push(val[0]);
                    yvalues.push(val[1]);
                    yminmax.push(val[1]);
                } else {
                    xvalues.push(i);
                    if (values[i] === null || values[i] === 'null') {
                        yvalues.push(null);
                    } else {
                        yvalues.push(Number(val));
                        yminmax.push(Number(val));
                    }
                }
            }
            if (this.options.get('xvalues')) {
                xvalues = this.options.get('xvalues');
            }

            this.maxy = this.maxyorg = Math.max.apply(Math, yminmax);
            this.miny = this.minyorg = Math.min.apply(Math, yminmax);

            this.maxx = Math.max.apply(Math, xvalues);
            this.minx = Math.min.apply(Math, xvalues);

            this.xvalues = xvalues;
            this.yvalues = yvalues;
            this.yminmax = yminmax;

        },

        processRangeOptions: function () {
            var options = this.options,
                normalRangeMin = options.get('normalRangeMin'),
                normalRangeMax = options.get('normalRangeMax');

            if (normalRangeMin !== undefined) {
                if (normalRangeMin < this.miny) {
                    this.miny = normalRangeMin;
                }
                if (normalRangeMax > this.maxy) {
                    this.maxy = normalRangeMax;
                }
            }
            if (options.get('chartRangeMin') !== undefined && (options.get('chartRangeClip') || options.get('chartRangeMin') < this.miny)) {
                this.miny = options.get('chartRangeMin');
            }
            if (options.get('chartRangeMax') !== undefined && (options.get('chartRangeClip') || options.get('chartRangeMax') > this.maxy)) {
                this.maxy = options.get('chartRangeMax');
            }
            if (options.get('chartRangeMinX') !== undefined && (options.get('chartRangeClipX') || options.get('chartRangeMinX') < this.minx)) {
                this.minx = options.get('chartRangeMinX');
            }
            if (options.get('chartRangeMaxX') !== undefined && (options.get('chartRangeClipX') || options.get('chartRangeMaxX') > this.maxx)) {
                this.maxx = options.get('chartRangeMaxX');
            }

        },

        drawNormalRange: function (canvasLeft, canvasTop, canvasHeight, canvasWidth, rangey) {
            var normalRangeMin = this.options.get('normalRangeMin'),
                normalRangeMax = this.options.get('normalRangeMax'),
                ytop = canvasTop + Math.round(canvasHeight - (canvasHeight * ((normalRangeMax - this.miny) / rangey))),
                height = Math.round((canvasHeight * (normalRangeMax - normalRangeMin)) / rangey);
            this.target.drawRect(canvasLeft, ytop, canvasWidth, height, undefined, this.options.get('normalRangeColor')).append();
        },

        render: function () {
            var options = this.options,
                target = this.target,
                canvasWidth = this.canvasWidth,
                canvasHeight = this.canvasHeight,
                vertices = this.vertices,
                spotRadius = options.get('spotRadius'),
                regionMap = this.regionMap,
                rangex, rangey, yvallast,
                canvasTop, canvasLeft,
                vertex, path, paths, x, y, xnext, xpos, xposnext,
                last, next, yvalcount, lineShapes, fillShapes, plen,
                valueSpots, hlSpotsEnabled, color, xvalues, yvalues, i;

            if (!line._super.render.call(this)) {
                return;
            }

            this.scanValues();
            this.processRangeOptions();

            xvalues = this.xvalues;
            yvalues = this.yvalues;

            if (!this.yminmax.length || this.yvalues.length < 2) {
                // empty or all null valuess
                return;
            }

            canvasTop = canvasLeft = 0;

            rangex = this.maxx - this.minx === 0 ? 1 : this.maxx - this.minx;
            rangey = this.maxy - this.miny === 0 ? 1 : this.maxy - this.miny;
            yvallast = this.yvalues.length - 1;

            if (spotRadius && (canvasWidth < (spotRadius * 4) || canvasHeight < (spotRadius * 4))) {
                spotRadius = 0;
            }
            if (spotRadius) {
                // adjust the canvas size as required so that spots will fit
                hlSpotsEnabled = options.get('highlightSpotColor') &&  !options.get('disableInteraction');
                if (hlSpotsEnabled || options.get('minSpotColor') || (options.get('spotColor') && yvalues[yvallast] === this.miny)) {
                    canvasHeight -= Math.ceil(spotRadius);
                }
                if (hlSpotsEnabled || options.get('maxSpotColor') || (options.get('spotColor') && yvalues[yvallast] === this.maxy)) {
                    canvasHeight -= Math.ceil(spotRadius);
                    canvasTop += Math.ceil(spotRadius);
                }
                if (hlSpotsEnabled ||
                     ((options.get('minSpotColor') || options.get('maxSpotColor')) && (yvalues[0] === this.miny || yvalues[0] === this.maxy))) {
                    canvasLeft += Math.ceil(spotRadius);
                    canvasWidth -= Math.ceil(spotRadius);
                }
                if (hlSpotsEnabled || options.get('spotColor') ||
                    (options.get('minSpotColor') || options.get('maxSpotColor') &&
                        (yvalues[yvallast] === this.miny || yvalues[yvallast] === this.maxy))) {
                    canvasWidth -= Math.ceil(spotRadius);
                }
            }


            canvasHeight--;

            if (options.get('normalRangeMin') !== undefined && !options.get('drawNormalOnTop')) {
                this.drawNormalRange(canvasLeft, canvasTop, canvasHeight, canvasWidth, rangey);
            }

            path = [];
            paths = [path];
            last = next = null;
            yvalcount = yvalues.length;
            for (i = 0; i < yvalcount; i++) {
                x = xvalues[i];
                xnext = xvalues[i + 1];
                y = yvalues[i];
                xpos = canvasLeft + Math.round((x - this.minx) * (canvasWidth / rangex));
                xposnext = i < yvalcount - 1 ? canvasLeft + Math.round((xnext - this.minx) * (canvasWidth / rangex)) : canvasWidth;
                next = xpos + ((xposnext - xpos) / 2);
                regionMap[i] = [last || 0, next, i];
                last = next;
                if (y === null) {
                    if (i) {
                        if (yvalues[i - 1] !== null) {
                            path = [];
                            paths.push(path);
                        }
                        vertices.push(null);
                    }
                } else {
                    if (y < this.miny) {
                        y = this.miny;
                    }
                    if (y > this.maxy) {
                        y = this.maxy;
                    }
                    if (!path.length) {
                        // previous value was null
                        path.push([xpos, canvasTop + canvasHeight]);
                    }
                    vertex = [xpos, canvasTop + Math.round(canvasHeight - (canvasHeight * ((y - this.miny) / rangey)))];
                    path.push(vertex);
                    vertices.push(vertex);
                }
            }

            lineShapes = [];
            fillShapes = [];
            plen = paths.length;
            for (i = 0; i < plen; i++) {
                path = paths[i];
                if (path.length) {
                    if (options.get('fillColor')) {
                        path.push([path[path.length - 1][0], (canvasTop + canvasHeight)]);
                        fillShapes.push(path.slice(0));
                        path.pop();
                    }
                    // if there's only a single point in this path, then we want to display it
                    // as a vertical line which means we keep path[0]  as is
                    if (path.length > 2) {
                        // else we want the first value
                        path[0] = [path[0][0], path[1][1]];
                    }
                    lineShapes.push(path);
                }
            }

            // draw the fill first, then optionally the normal range, then the line on top of that
            plen = fillShapes.length;
            for (i = 0; i < plen; i++) {
                target.drawShape(fillShapes[i],
                    options.get('fillColor'), options.get('fillColor')).append();
            }

            if (options.get('normalRangeMin') !== undefined && options.get('drawNormalOnTop')) {
                this.drawNormalRange(canvasLeft, canvasTop, canvasHeight, canvasWidth, rangey);
            }

            plen = lineShapes.length;
            for (i = 0; i < plen; i++) {
                target.drawShape(lineShapes[i], options.get('lineColor'), undefined,
                    options.get('lineWidth')).append();
            }

            if (spotRadius && options.get('valueSpots')) {
                valueSpots = options.get('valueSpots');
                if (valueSpots.get === undefined) {
                    valueSpots = new RangeMap(valueSpots);
                }
                for (i = 0; i < yvalcount; i++) {
                    color = valueSpots.get(yvalues[i]);
                    if (color) {
                        target.drawCircle(canvasLeft + Math.round((xvalues[i] - this.minx) * (canvasWidth / rangex)),
                            canvasTop + Math.round(canvasHeight - (canvasHeight * ((yvalues[i] - this.miny) / rangey))),
                            spotRadius, undefined,
                            color).append();
                    }
                }

            }
            if (spotRadius && options.get('spotColor') && yvalues[yvallast] !== null) {
                target.drawCircle(canvasLeft + Math.round((xvalues[xvalues.length - 1] - this.minx) * (canvasWidth / rangex)),
                    canvasTop + Math.round(canvasHeight - (canvasHeight * ((yvalues[yvallast] - this.miny) / rangey))),
                    spotRadius, undefined,
                    options.get('spotColor')).append();
            }
            if (this.maxy !== this.minyorg) {
                if (spotRadius && options.get('minSpotColor')) {
                    x = xvalues[$.inArray(this.minyorg, yvalues)];
                    target.drawCircle(canvasLeft + Math.round((x - this.minx) * (canvasWidth / rangex)),
                        canvasTop + Math.round(canvasHeight - (canvasHeight * ((this.minyorg - this.miny) / rangey))),
                        spotRadius, undefined,
                        options.get('minSpotColor')).append();
                }
                if (spotRadius && options.get('maxSpotColor')) {
                    x = xvalues[$.inArray(this.maxyorg, yvalues)];
                    target.drawCircle(canvasLeft + Math.round((x - this.minx) * (canvasWidth / rangex)),
                        canvasTop + Math.round(canvasHeight - (canvasHeight * ((this.maxyorg - this.miny) / rangey))),
                        spotRadius, undefined,
                        options.get('maxSpotColor')).append();
                }
            }

            this.lastShapeId = target.getLastShapeId();
            this.canvasTop = canvasTop;
            target.render();
        }
    });

    /**
     * Bar charts
     */
    $.fn.sparkline.bar = bar = createClass($.fn.sparkline._base, barHighlightMixin, {
        type: 'bar',

        init: function (el, values, options, width, height) {
            var barWidth = parseInt(options.get('barWidth'), 10),
                barSpacing = parseInt(options.get('barSpacing'), 10),
                chartRangeMin = options.get('chartRangeMin'),
                chartRangeMax = options.get('chartRangeMax'),
                chartRangeClip = options.get('chartRangeClip'),
                stackMin = Infinity,
                stackMax = -Infinity,
                isStackString, groupMin, groupMax, stackRanges,
                numValues, i, vlen, range, zeroAxis, xaxisOffset, min, max, clipMin, clipMax,
                stacked, vlist, j, slen, svals, val, yoffset, yMaxCalc, canvasHeightEf;
            bar._super.init.call(this, el, values, options, width, height);

            // scan values to determine whether to stack bars
            for (i = 0, vlen = values.length; i < vlen; i++) {
                val = values[i];
                isStackString = typeof(val) === 'string' && val.indexOf(':') > -1;
                if (isStackString || $.isArray(val)) {
                    stacked = true;
                    if (isStackString) {
                        val = values[i] = normalizeValues(val.split(':'));
                    }
                    val = remove(val, null); // min/max will treat null as zero
                    groupMin = Math.min.apply(Math, val);
                    groupMax = Math.max.apply(Math, val);
                    if (groupMin < stackMin) {
                        stackMin = groupMin;
                    }
                    if (groupMax > stackMax) {
                        stackMax = groupMax;
                    }
                }
            }

            this.stacked = stacked;
            this.regionShapes = {};
            this.barWidth = barWidth;
            this.barSpacing = barSpacing;
            this.totalBarWidth = barWidth + barSpacing;
            this.width = width = (values.length * barWidth) + ((values.length - 1) * barSpacing);

            this.initTarget();

            if (chartRangeClip) {
                clipMin = chartRangeMin === undefined ? -Infinity : chartRangeMin;
                clipMax = chartRangeMax === undefined ? Infinity : chartRangeMax;
            }

            numValues = [];
            stackRanges = stacked ? [] : numValues;
            var stackTotals = [];
            var stackRangesNeg = [];
            for (i = 0, vlen = values.length; i < vlen; i++) {
                if (stacked) {
                    vlist = values[i];
                    values[i] = svals = [];
                    stackTotals[i] = 0;
                    stackRanges[i] = stackRangesNeg[i] = 0;
                    for (j = 0, slen = vlist.length; j < slen; j++) {
                        val = svals[j] = chartRangeClip ? clipval(vlist[j], clipMin, clipMax) : vlist[j];
                        if (val !== null) {
                            if (val > 0) {
                                stackTotals[i] += val;
                            }
                            if (stackMin < 0 && stackMax > 0) {
                                if (val < 0) {
                                    stackRangesNeg[i] += Math.abs(val);
                                } else {
                                    stackRanges[i] += val;
                                }
                            } else {
                                stackRanges[i] += Math.abs(val - (val < 0 ? stackMax : stackMin));
                            }
                            numValues.push(val);
                        }
                    }
                } else {
                    val = chartRangeClip ? clipval(values[i], clipMin, clipMax) : values[i];
                    val = values[i] = normalizeValue(val);
                    if (val !== null) {
                        numValues.push(val);
                    }
                }
            }
            this.max = max = Math.max.apply(Math, numValues);
            this.min = min = Math.min.apply(Math, numValues);
            this.stackMax = stackMax = stacked ? Math.max.apply(Math, stackTotals) : max;
            this.stackMin = stackMin = stacked ? Math.min.apply(Math, numValues) : min;

            if (options.get('chartRangeMin') !== undefined && (options.get('chartRangeClip') || options.get('chartRangeMin') < min)) {
                min = options.get('chartRangeMin');
            }
            if (options.get('chartRangeMax') !== undefined && (options.get('chartRangeClip') || options.get('chartRangeMax') > max)) {
                max = options.get('chartRangeMax');
            }

            this.zeroAxis = zeroAxis = options.get('zeroAxis', true);
            if (min <= 0 && max >= 0 && zeroAxis) {
                xaxisOffset = 0;
            } else if (zeroAxis == false) {
                xaxisOffset = min;
            } else if (min > 0) {
                xaxisOffset = min;
            } else {
                xaxisOffset = max;
            }
            this.xaxisOffset = xaxisOffset;

            range = stacked ? (Math.max.apply(Math, stackRanges) + Math.max.apply(Math, stackRangesNeg)) : max - min;

            // as we plot zero/min values a single pixel line, we add a pixel to all other
            // values - Reduce the effective canvas size to suit
            this.canvasHeightEf = (zeroAxis && min < 0) ? this.canvasHeight - 2 : this.canvasHeight - 1;

            if (min < xaxisOffset) {
                yMaxCalc = (stacked && max >= 0) ? stackMax : max;
                yoffset = (yMaxCalc - xaxisOffset) / range * this.canvasHeight;
                if (yoffset !== Math.ceil(yoffset)) {
                    this.canvasHeightEf -= 2;
                    yoffset = Math.ceil(yoffset);
                }
            } else {
                yoffset = this.canvasHeight;
            }
            this.yoffset = yoffset;

            if ($.isArray(options.get('colorMap'))) {
                this.colorMapByIndex = options.get('colorMap');
                this.colorMapByValue = null;
            } else {
                this.colorMapByIndex = null;
                this.colorMapByValue = options.get('colorMap');
                if (this.colorMapByValue && this.colorMapByValue.get === undefined) {
                    this.colorMapByValue = new RangeMap(this.colorMapByValue);
                }
            }

            this.range = range;
        },

        getRegion: function (el, x, y) {
            var result = Math.floor(x / this.totalBarWidth);
            return (result < 0 || result >= this.values.length) ? undefined : result;
        },

        getCurrentRegionFields: function () {
            var currentRegion = this.currentRegion,
                values = ensureArray(this.values[currentRegion]),
                result = [],
                value, i;
            for (i = values.length; i--;) {
                value = values[i];
                result.push({
                    isNull: value === null,
                    value: value,
                    color: this.calcColor(i, value, currentRegion),
                    offset: currentRegion
                });
            }
            return result;
        },

        calcColor: function (stacknum, value, valuenum) {
            var colorMapByIndex = this.colorMapByIndex,
                colorMapByValue = this.colorMapByValue,
                options = this.options,
                color, newColor;
            if (this.stacked) {
                color = options.get('stackedBarColor');
            } else {
                color = (value < 0) ? options.get('negBarColor') : options.get('barColor');
            }
            if (value === 0 && options.get('zeroColor') !== undefined) {
                color = options.get('zeroColor');
            }
            if (colorMapByValue && (newColor = colorMapByValue.get(value))) {
                color = newColor;
            } else if (colorMapByIndex && colorMapByIndex.length > valuenum) {
                color = colorMapByIndex[valuenum];
            }
            return $.isArray(color) ? color[stacknum % color.length] : color;
        },

        /**
         * Render bar(s) for a region
         */
        renderRegion: function (valuenum, highlight) {
            var vals = this.values[valuenum],
                options = this.options,
                xaxisOffset = this.xaxisOffset,
                result = [],
                range = this.range,
                stacked = this.stacked,
                target = this.target,
                x = valuenum * this.totalBarWidth,
                canvasHeightEf = this.canvasHeightEf,
                yoffset = this.yoffset,
                y, height, color, isNull, yoffsetNeg, i, valcount, val, minPlotted, allMin;

            vals = $.isArray(vals) ? vals : [vals];
            valcount = vals.length;
            val = vals[0];
            isNull = all(null, vals);
            allMin = all(xaxisOffset, vals, true);

            if (isNull) {
                if (options.get('nullColor')) {
                    color = highlight ? options.get('nullColor') : this.calcHighlightColor(options.get('nullColor'), options);
                    y = (yoffset > 0) ? yoffset - 1 : yoffset;
                    return target.drawRect(x, y, this.barWidth - 1, 0, color, color);
                } else {
                    return undefined;
                }
            }
            yoffsetNeg = yoffset;
            for (i = 0; i < valcount; i++) {
                val = vals[i];

                if (stacked && val === xaxisOffset) {
                    if (!allMin || minPlotted) {
                        continue;
                    }
                    minPlotted = true;
                }

                if (range > 0) {
                    height = Math.floor(canvasHeightEf * ((Math.abs(val - xaxisOffset) / range))) + 1;
                } else {
                    height = 1;
                }
                if (val < xaxisOffset || (val === xaxisOffset && yoffset === 0)) {
                    y = yoffsetNeg;
                    yoffsetNeg += height;
                } else {
                    y = yoffset - height;
                    yoffset -= height;
                }
                color = this.calcColor(i, val, valuenum);
                if (highlight) {
                    color = this.calcHighlightColor(color, options);
                }
                result.push(target.drawRect(x, y, this.barWidth - 1, height - 1, color, color));
            }
            if (result.length === 1) {
                return result[0];
            }
            return result;
        }
    });

    /**
     * Tristate charts
     */
    $.fn.sparkline.tristate = tristate = createClass($.fn.sparkline._base, barHighlightMixin, {
        type: 'tristate',

        init: function (el, values, options, width, height) {
            var barWidth = parseInt(options.get('barWidth'), 10),
                barSpacing = parseInt(options.get('barSpacing'), 10);
            tristate._super.init.call(this, el, values, options, width, height);

            this.regionShapes = {};
            this.barWidth = barWidth;
            this.barSpacing = barSpacing;
            this.totalBarWidth = barWidth + barSpacing;
            this.values = $.map(values, Number);
            this.width = width = (values.length * barWidth) + ((values.length - 1) * barSpacing);

            if ($.isArray(options.get('colorMap'))) {
                this.colorMapByIndex = options.get('colorMap');
                this.colorMapByValue = null;
            } else {
                this.colorMapByIndex = null;
                this.colorMapByValue = options.get('colorMap');
                if (this.colorMapByValue && this.colorMapByValue.get === undefined) {
                    this.colorMapByValue = new RangeMap(this.colorMapByValue);
                }
            }
            this.initTarget();
        },

        getRegion: function (el, x, y) {
            return Math.floor(x / this.totalBarWidth);
        },

        getCurrentRegionFields: function () {
            var currentRegion = this.currentRegion;
            return {
                isNull: this.values[currentRegion] === undefined,
                value: this.values[currentRegion],
                color: this.calcColor(this.values[currentRegion], currentRegion),
                offset: currentRegion
            };
        },

        calcColor: function (value, valuenum) {
            var values = this.values,
                options = this.options,
                colorMapByIndex = this.colorMapByIndex,
                colorMapByValue = this.colorMapByValue,
                color, newColor;

            if (colorMapByValue && (newColor = colorMapByValue.get(value))) {
                color = newColor;
            } else if (colorMapByIndex && colorMapByIndex.length > valuenum) {
                color = colorMapByIndex[valuenum];
            } else if (values[valuenum] < 0) {
                color = options.get('negBarColor');
            } else if (values[valuenum] > 0) {
                color = options.get('posBarColor');
            } else {
                color = options.get('zeroBarColor');
            }
            return color;
        },

        renderRegion: function (valuenum, highlight) {
            var values = this.values,
                options = this.options,
                target = this.target,
                canvasHeight, height, halfHeight,
                x, y, color;

            canvasHeight = target.pixelHeight;
            halfHeight = Math.round(canvasHeight / 2);

            x = valuenum * this.totalBarWidth;
            if (values[valuenum] < 0) {
                y = halfHeight;
                height = halfHeight - 1;
            } else if (values[valuenum] > 0) {
                y = 0;
                height = halfHeight - 1;
            } else {
                y = halfHeight - 1;
                height = 2;
            }
            color = this.calcColor(values[valuenum], valuenum);
            if (color === null) {
                return;
            }
            if (highlight) {
                color = this.calcHighlightColor(color, options);
            }
            return target.drawRect(x, y, this.barWidth - 1, height - 1, color, color);
        }
    });

    /**
     * Discrete charts
     */
    $.fn.sparkline.discrete = discrete = createClass($.fn.sparkline._base, barHighlightMixin, {
        type: 'discrete',

        init: function (el, values, options, width, height) {
            discrete._super.init.call(this, el, values, options, width, height);

            this.regionShapes = {};
            this.values = values = $.map(values, Number);
            this.min = Math.min.apply(Math, values);
            this.max = Math.max.apply(Math, values);
            this.range = this.max - this.min;
            this.width = width = options.get('width') === 'auto' ? values.length * 2 : this.width;
            this.interval = Math.floor(width / values.length);
            this.itemWidth = width / values.length;
            if (options.get('chartRangeMin') !== undefined && (options.get('chartRangeClip') || options.get('chartRangeMin') < this.min)) {
                this.min = options.get('chartRangeMin');
            }
            if (options.get('chartRangeMax') !== undefined && (options.get('chartRangeClip') || options.get('chartRangeMax') > this.max)) {
                this.max = options.get('chartRangeMax');
            }
            this.initTarget();
            if (this.target) {
                this.lineHeight = options.get('lineHeight') === 'auto' ? Math.round(this.canvasHeight * 0.3) : options.get('lineHeight');
            }
        },

        getRegion: function (el, x, y) {
            return Math.floor(x / this.itemWidth);
        },

        getCurrentRegionFields: function () {
            var currentRegion = this.currentRegion;
            return {
                isNull: this.values[currentRegion] === undefined,
                value: this.values[currentRegion],
                offset: currentRegion
            };
        },

        renderRegion: function (valuenum, highlight) {
            var values = this.values,
                options = this.options,
                min = this.min,
                max = this.max,
                range = this.range,
                interval = this.interval,
                target = this.target,
                canvasHeight = this.canvasHeight,
                lineHeight = this.lineHeight,
                pheight = canvasHeight - lineHeight,
                ytop, val, color, x;

            val = clipval(values[valuenum], min, max);
            x = valuenum * interval;
            ytop = Math.round(pheight - pheight * ((val - min) / range));
            color = (options.get('thresholdColor') && val < options.get('thresholdValue')) ? options.get('thresholdColor') : options.get('lineColor');
            if (highlight) {
                color = this.calcHighlightColor(color, options);
            }
            return target.drawLine(x, ytop, x, ytop + lineHeight, color);
        }
    });

    /**
     * Bullet charts
     */
    $.fn.sparkline.bullet = bullet = createClass($.fn.sparkline._base, {
        type: 'bullet',

        init: function (el, values, options, width, height) {
            var min, max, vals;
            bullet._super.init.call(this, el, values, options, width, height);

            // values: target, performance, range1, range2, range3
            this.values = values = normalizeValues(values);
            // target or performance could be null
            vals = values.slice();
            vals[0] = vals[0] === null ? vals[2] : vals[0];
            vals[1] = values[1] === null ? vals[2] : vals[1];
            min = Math.min.apply(Math, values);
            max = Math.max.apply(Math, values);
            if (options.get('base') === undefined) {
                min = min < 0 ? min : 0;
            } else {
                min = options.get('base');
            }
            this.min = min;
            this.max = max;
            this.range = max - min;
            this.shapes = {};
            this.valueShapes = {};
            this.regiondata = {};
            this.width = width = options.get('width') === 'auto' ? '4.0em' : width;
            this.target = this.$el.simpledraw(width, height, options.get('composite'));
            if (!values.length) {
                this.disabled = true;
            }
            this.initTarget();
        },

        getRegion: function (el, x, y) {
            var shapeid = this.target.getShapeAt(el, x, y);
            return (shapeid !== undefined && this.shapes[shapeid] !== undefined) ? this.shapes[shapeid] : undefined;
        },

        getCurrentRegionFields: function () {
            var currentRegion = this.currentRegion;
            return {
                fieldkey: currentRegion.substr(0, 1),
                value: this.values[currentRegion.substr(1)],
                region: currentRegion
            };
        },

        changeHighlight: function (highlight) {
            var currentRegion = this.currentRegion,
                shapeid = this.valueShapes[currentRegion],
                shape;
            delete this.shapes[shapeid];
            switch (currentRegion.substr(0, 1)) {
                case 'r':
                    shape = this.renderRange(currentRegion.substr(1), highlight);
                    break;
                case 'p':
                    shape = this.renderPerformance(highlight);
                    break;
                case 't':
                    shape = this.renderTarget(highlight);
                    break;
            }
            this.valueShapes[currentRegion] = shape.id;
            this.shapes[shape.id] = currentRegion;
            this.target.replaceWithShape(shapeid, shape);
        },

        renderRange: function (rn, highlight) {
            var rangeval = this.values[rn],
                rangewidth = Math.round(this.canvasWidth * ((rangeval - this.min) / this.range)),
                color = this.options.get('rangeColors')[rn - 2];
            if (highlight) {
                color = this.calcHighlightColor(color, this.options);
            }
            return this.target.drawRect(0, 0, rangewidth - 1, this.canvasHeight - 1, color, color);
        },

        renderPerformance: function (highlight) {
            var perfval = this.values[1],
                perfwidth = Math.round(this.canvasWidth * ((perfval - this.min) / this.range)),
                color = this.options.get('performanceColor');
            if (highlight) {
                color = this.calcHighlightColor(color, this.options);
            }
            return this.target.drawRect(0, Math.round(this.canvasHeight * 0.3), perfwidth - 1,
                Math.round(this.canvasHeight * 0.4) - 1, color, color);
        },

        renderTarget: function (highlight) {
            var targetval = this.values[0],
                x = Math.round(this.canvasWidth * ((targetval - this.min) / this.range) - (this.options.get('targetWidth') / 2)),
                targettop = Math.round(this.canvasHeight * 0.10),
                targetheight = this.canvasHeight - (targettop * 2),
                color = this.options.get('targetColor');
            if (highlight) {
                color = this.calcHighlightColor(color, this.options);
            }
            return this.target.drawRect(x, targettop, this.options.get('targetWidth') - 1, targetheight - 1, color, color);
        },

        render: function () {
            var vlen = this.values.length,
                target = this.target,
                i, shape;
            if (!bullet._super.render.call(this)) {
                return;
            }
            for (i = 2; i < vlen; i++) {
                shape = this.renderRange(i).append();
                this.shapes[shape.id] = 'r' + i;
                this.valueShapes['r' + i] = shape.id;
            }
            if (this.values[1] !== null) {
                shape = this.renderPerformance().append();
                this.shapes[shape.id] = 'p1';
                this.valueShapes.p1 = shape.id;
            }
            if (this.values[0] !== null) {
                shape = this.renderTarget().append();
                this.shapes[shape.id] = 't0';
                this.valueShapes.t0 = shape.id;
            }
            target.render();
        }
    });

    /**
     * Pie charts
     */
    $.fn.sparkline.pie = pie = createClass($.fn.sparkline._base, {
        type: 'pie',

        init: function (el, values, options, width, height) {
            var total = 0, i;

            pie._super.init.call(this, el, values, options, width, height);

            this.shapes = {}; // map shape ids to value offsets
            this.valueShapes = {}; // maps value offsets to shape ids
            this.values = values = $.map(values, Number);

            if (options.get('width') === 'auto') {
                this.width = this.height;
            }

            if (values.length > 0) {
                for (i = values.length; i--;) {
                    total += values[i];
                }
            }
            this.total = total;
            this.initTarget();
            this.radius = Math.floor(Math.min(this.canvasWidth, this.canvasHeight) / 2);
        },

        getRegion: function (el, x, y) {
            var shapeid = this.target.getShapeAt(el, x, y);
            return (shapeid !== undefined && this.shapes[shapeid] !== undefined) ? this.shapes[shapeid] : undefined;
        },

        getCurrentRegionFields: function () {
            var currentRegion = this.currentRegion;
            return {
                isNull: this.values[currentRegion] === undefined,
                value: this.values[currentRegion],
                percent: this.values[currentRegion] / this.total * 100,
                color: this.options.get('sliceColors')[currentRegion % this.options.get('sliceColors').length],
                offset: currentRegion
            };
        },

        changeHighlight: function (highlight) {
            var currentRegion = this.currentRegion,
                 newslice = this.renderSlice(currentRegion, highlight),
                 shapeid = this.valueShapes[currentRegion];
            delete this.shapes[shapeid];
            this.target.replaceWithShape(shapeid, newslice);
            this.valueShapes[currentRegion] = newslice.id;
            this.shapes[newslice.id] = currentRegion;
        },

        renderSlice: function (valuenum, highlight) {
            var target = this.target,
                options = this.options,
                radius = this.radius,
                borderWidth = options.get('borderWidth'),
                offset = options.get('offset'),
                circle = 2 * Math.PI,
                values = this.values,
                total = this.total,
                next = offset ? (2*Math.PI)*(offset/360) : 0,
                start, end, i, vlen, color;

            vlen = values.length;
            for (i = 0; i < vlen; i++) {
                start = next;
                end = next;
                if (total > 0) {  // avoid divide by zero
                    end = next + (circle * (values[i] / total));
                }
                if (valuenum === i) {
                    color = options.get('sliceColors')[i % options.get('sliceColors').length];
                    if (highlight) {
                        color = this.calcHighlightColor(color, options);
                    }

                    return target.drawPieSlice(radius, radius, radius - borderWidth, start, end, undefined, color);
                }
                next = end;
            }
        },

        render: function () {
            var target = this.target,
                values = this.values,
                options = this.options,
                radius = this.radius,
                borderWidth = options.get('borderWidth'),
                shape, i;

            if (!pie._super.render.call(this)) {
                return;
            }
            if (borderWidth) {
                target.drawCircle(radius, radius, Math.floor(radius - (borderWidth / 2)),
                    options.get('borderColor'), undefined, borderWidth).append();
            }
            for (i = values.length; i--;) {
                if (values[i]) { // don't render zero values
                    shape = this.renderSlice(i).append();
                    this.valueShapes[i] = shape.id; // store just the shapeid
                    this.shapes[shape.id] = i;
                }
            }
            target.render();
        }
    });

    /**
     * Box plots
     */
    $.fn.sparkline.box = box = createClass($.fn.sparkline._base, {
        type: 'box',

        init: function (el, values, options, width, height) {
            box._super.init.call(this, el, values, options, width, height);
            this.values = $.map(values, Number);
            this.width = options.get('width') === 'auto' ? '4.0em' : width;
            this.initTarget();
            if (!this.values.length) {
                this.disabled = 1;
            }
        },

        /**
         * Simulate a single region
         */
        getRegion: function () {
            return 1;
        },

        getCurrentRegionFields: function () {
            var result = [
                { field: 'lq', value: this.quartiles[0] },
                { field: 'med', value: this.quartiles[1] },
                { field: 'uq', value: this.quartiles[2] }
            ];
            if (this.loutlier !== undefined) {
                result.push({ field: 'lo', value: this.loutlier});
            }
            if (this.routlier !== undefined) {
                result.push({ field: 'ro', value: this.routlier});
            }
            if (this.lwhisker !== undefined) {
                result.push({ field: 'lw', value: this.lwhisker});
            }
            if (this.rwhisker !== undefined) {
                result.push({ field: 'rw', value: this.rwhisker});
            }
            return result;
        },

        render: function () {
            var target = this.target,
                values = this.values,
                vlen = values.length,
                options = this.options,
                canvasWidth = this.canvasWidth,
                canvasHeight = this.canvasHeight,
                minValue = options.get('chartRangeMin') === undefined ? Math.min.apply(Math, values) : options.get('chartRangeMin'),
                maxValue = options.get('chartRangeMax') === undefined ? Math.max.apply(Math, values) : options.get('chartRangeMax'),
                canvasLeft = 0,
                lwhisker, loutlier, iqr, q1, q2, q3, rwhisker, routlier, i,
                size, unitSize;

            if (!box._super.render.call(this)) {
                return;
            }

            if (options.get('raw')) {
                if (options.get('showOutliers') && values.length > 5) {
                    loutlier = values[0];
                    lwhisker = values[1];
                    q1 = values[2];
                    q2 = values[3];
                    q3 = values[4];
                    rwhisker = values[5];
                    routlier = values[6];
                } else {
                    lwhisker = values[0];
                    q1 = values[1];
                    q2 = values[2];
                    q3 = values[3];
                    rwhisker = values[4];
                }
            } else {
                values.sort(function (a, b) { return a - b; });
                q1 = quartile(values, 1);
                q2 = quartile(values, 2);
                q3 = quartile(values, 3);
                iqr = q3 - q1;
                if (options.get('showOutliers')) {
                    lwhisker = rwhisker = undefined;
                    for (i = 0; i < vlen; i++) {
                        if (lwhisker === undefined && values[i] > q1 - (iqr * options.get('outlierIQR'))) {
                            lwhisker = values[i];
                        }
                        if (values[i] < q3 + (iqr * options.get('outlierIQR'))) {
                            rwhisker = values[i];
                        }
                    }
                    loutlier = values[0];
                    routlier = values[vlen - 1];
                } else {
                    lwhisker = values[0];
                    rwhisker = values[vlen - 1];
                }
            }
            this.quartiles = [q1, q2, q3];
            this.lwhisker = lwhisker;
            this.rwhisker = rwhisker;
            this.loutlier = loutlier;
            this.routlier = routlier;

            unitSize = canvasWidth / (maxValue - minValue + 1);
            if (options.get('showOutliers')) {
                canvasLeft = Math.ceil(options.get('spotRadius'));
                canvasWidth -= 2 * Math.ceil(options.get('spotRadius'));
                unitSize = canvasWidth / (maxValue - minValue + 1);
                if (loutlier < lwhisker) {
                    target.drawCircle((loutlier - minValue) * unitSize + canvasLeft,
                        canvasHeight / 2,
                        options.get('spotRadius'),
                        options.get('outlierLineColor'),
                        options.get('outlierFillColor')).append();
                }
                if (routlier > rwhisker) {
                    target.drawCircle((routlier - minValue) * unitSize + canvasLeft,
                        canvasHeight / 2,
                        options.get('spotRadius'),
                        options.get('outlierLineColor'),
                        options.get('outlierFillColor')).append();
                }
            }

            // box
            target.drawRect(
                Math.round((q1 - minValue) * unitSize + canvasLeft),
                Math.round(canvasHeight * 0.1),
                Math.round((q3 - q1) * unitSize),
                Math.round(canvasHeight * 0.8),
                options.get('boxLineColor'),
                options.get('boxFillColor')).append();
            // left whisker
            target.drawLine(
                Math.round((lwhisker - minValue) * unitSize + canvasLeft),
                Math.round(canvasHeight / 2),
                Math.round((q1 - minValue) * unitSize + canvasLeft),
                Math.round(canvasHeight / 2),
                options.get('lineColor')).append();
            target.drawLine(
                Math.round((lwhisker - minValue) * unitSize + canvasLeft),
                Math.round(canvasHeight / 4),
                Math.round((lwhisker - minValue) * unitSize + canvasLeft),
                Math.round(canvasHeight - canvasHeight / 4),
                options.get('whiskerColor')).append();
            // right whisker
            target.drawLine(Math.round((rwhisker - minValue) * unitSize + canvasLeft),
                Math.round(canvasHeight / 2),
                Math.round((q3 - minValue) * unitSize + canvasLeft),
                Math.round(canvasHeight / 2),
                options.get('lineColor')).append();
            target.drawLine(
                Math.round((rwhisker - minValue) * unitSize + canvasLeft),
                Math.round(canvasHeight / 4),
                Math.round((rwhisker - minValue) * unitSize + canvasLeft),
                Math.round(canvasHeight - canvasHeight / 4),
                options.get('whiskerColor')).append();
            // median line
            target.drawLine(
                Math.round((q2 - minValue) * unitSize + canvasLeft),
                Math.round(canvasHeight * 0.1),
                Math.round((q2 - minValue) * unitSize + canvasLeft),
                Math.round(canvasHeight * 0.9),
                options.get('medianColor')).append();
            if (options.get('target')) {
                size = Math.ceil(options.get('spotRadius'));
                target.drawLine(
                    Math.round((options.get('target') - minValue) * unitSize + canvasLeft),
                    Math.round((canvasHeight / 2) - size),
                    Math.round((options.get('target') - minValue) * unitSize + canvasLeft),
                    Math.round((canvasHeight / 2) + size),
                    options.get('targetColor')).append();
                target.drawLine(
                    Math.round((options.get('target') - minValue) * unitSize + canvasLeft - size),
                    Math.round(canvasHeight / 2),
                    Math.round((options.get('target') - minValue) * unitSize + canvasLeft + size),
                    Math.round(canvasHeight / 2),
                    options.get('targetColor')).append();
            }
            target.render();
        }
    });

    // Setup a very simple "virtual canvas" to make drawing the few shapes we need easier
    // This is accessible as $(foo).simpledraw()

    VShape = createClass({
        init: function (target, id, type, args) {
            this.target = target;
            this.id = id;
            this.type = type;
            this.args = args;
        },
        append: function () {
            this.target.appendShape(this);
            return this;
        }
    });

    VCanvas_base = createClass({
        _pxregex: /(\d+)(px)?\s*$/i,

        init: function (width, height, target) {
            if (!width) {
                return;
            }
            this.width = width;
            this.height = height;
            this.target = target;
            this.lastShapeId = null;
            if (target[0]) {
                target = target[0];
            }
            $.data(target, '_jqs_vcanvas', this);
        },

        drawLine: function (x1, y1, x2, y2, lineColor, lineWidth) {
            return this.drawShape([[x1, y1], [x2, y2]], lineColor, lineWidth);
        },

        drawShape: function (path, lineColor, fillColor, lineWidth) {
            return this._genShape('Shape', [path, lineColor, fillColor, lineWidth]);
        },

        drawCircle: function (x, y, radius, lineColor, fillColor, lineWidth) {
            return this._genShape('Circle', [x, y, radius, lineColor, fillColor, lineWidth]);
        },

        drawPieSlice: function (x, y, radius, startAngle, endAngle, lineColor, fillColor) {
            return this._genShape('PieSlice', [x, y, radius, startAngle, endAngle, lineColor, fillColor]);
        },

        drawRect: function (x, y, width, height, lineColor, fillColor) {
            return this._genShape('Rect', [x, y, width, height, lineColor, fillColor]);
        },

        getElement: function () {
            return this.canvas;
        },

        /**
         * Return the most recently inserted shape id
         */
        getLastShapeId: function () {
            return this.lastShapeId;
        },

        /**
         * Clear and reset the canvas
         */
        reset: function () {
            alert('reset not implemented');
        },

        _insert: function (el, target) {
            $(target).html(el);
        },

        /**
         * Calculate the pixel dimensions of the canvas
         */
        _calculatePixelDims: function (width, height, canvas) {
            // XXX This should probably be a configurable option
            var match;
            match = this._pxregex.exec(height);
            if (match) {
                this.pixelHeight = match[1];
            } else {
                this.pixelHeight = $(canvas).height();
            }
            match = this._pxregex.exec(width);
            if (match) {
                this.pixelWidth = match[1];
            } else {
                this.pixelWidth = $(canvas).width();
            }
        },

        /**
         * Generate a shape object and id for later rendering
         */
        _genShape: function (shapetype, shapeargs) {
            var id = shapeCount++;
            shapeargs.unshift(id);
            return new VShape(this, id, shapetype, shapeargs);
        },

        /**
         * Add a shape to the end of the render queue
         */
        appendShape: function (shape) {
            alert('appendShape not implemented');
        },

        /**
         * Replace one shape with another
         */
        replaceWithShape: function (shapeid, shape) {
            alert('replaceWithShape not implemented');
        },

        /**
         * Insert one shape after another in the render queue
         */
        insertAfterShape: function (shapeid, shape) {
            alert('insertAfterShape not implemented');
        },

        /**
         * Remove a shape from the queue
         */
        removeShapeId: function (shapeid) {
            alert('removeShapeId not implemented');
        },

        /**
         * Find a shape at the specified x/y co-ordinates
         */
        getShapeAt: function (el, x, y) {
            alert('getShapeAt not implemented');
        },

        /**
         * Render all queued shapes onto the canvas
         */
        render: function () {
            alert('render not implemented');
        }
    });

    VCanvas_canvas = createClass(VCanvas_base, {
        init: function (width, height, target, interact) {
            VCanvas_canvas._super.init.call(this, width, height, target);
            this.canvas = document.createElement('canvas');
            if (target[0]) {
                target = target[0];
            }
            $.data(target, '_jqs_vcanvas', this);
            $(this.canvas).css({ display: 'inline-block', width: width, height: height, verticalAlign: 'top' });
            this._insert(this.canvas, target);
            this._calculatePixelDims(width, height, this.canvas);
            this.canvas.width = this.pixelWidth;
            this.canvas.height = this.pixelHeight;
            this.interact = interact;
            this.shapes = {};
            this.shapeseq = [];
            this.currentTargetShapeId = undefined;
            $(this.canvas).css({width: this.pixelWidth, height: this.pixelHeight});
        },

        _getContext: function (lineColor, fillColor, lineWidth) {
            var context = this.canvas.getContext('2d');
            if (lineColor !== undefined) {
                context.strokeStyle = lineColor;
            }
            context.lineWidth = lineWidth === undefined ? 1 : lineWidth;
            if (fillColor !== undefined) {
                context.fillStyle = fillColor;
            }
            return context;
        },

        reset: function () {
            var context = this._getContext();
            context.clearRect(0, 0, this.pixelWidth, this.pixelHeight);
            this.shapes = {};
            this.shapeseq = [];
            this.currentTargetShapeId = undefined;
        },

        _drawShape: function (shapeid, path, lineColor, fillColor, lineWidth) {
            var context = this._getContext(lineColor, fillColor, lineWidth),
                i, plen;
            context.beginPath();
            context.moveTo(path[0][0] + 0.5, path[0][1] + 0.5);
            for (i = 1, plen = path.length; i < plen; i++) {
                context.lineTo(path[i][0] + 0.5, path[i][1] + 0.5); // the 0.5 offset gives us crisp pixel-width lines
            }
            if (lineColor !== undefined) {
                context.stroke();
            }
            if (fillColor !== undefined) {
                context.fill();
            }
            if (this.targetX !== undefined && this.targetY !== undefined &&
                context.isPointInPath(this.targetX, this.targetY)) {
                this.currentTargetShapeId = shapeid;
            }
        },

        _drawCircle: function (shapeid, x, y, radius, lineColor, fillColor, lineWidth) {
            var context = this._getContext(lineColor, fillColor, lineWidth);
            context.beginPath();
            context.arc(x, y, radius, 0, 2 * Math.PI, false);
            if (this.targetX !== undefined && this.targetY !== undefined &&
                context.isPointInPath(this.targetX, this.targetY)) {
                this.currentTargetShapeId = shapeid;
            }
            if (lineColor !== undefined) {
                context.stroke();
            }
            if (fillColor !== undefined) {
                context.fill();
            }
        },

        _drawPieSlice: function (shapeid, x, y, radius, startAngle, endAngle, lineColor, fillColor) {
            var context = this._getContext(lineColor, fillColor);
            context.beginPath();
            context.moveTo(x, y);
            context.arc(x, y, radius, startAngle, endAngle, false);
            context.lineTo(x, y);
            context.closePath();
            if (lineColor !== undefined) {
                context.stroke();
            }
            if (fillColor) {
                context.fill();
            }
            if (this.targetX !== undefined && this.targetY !== undefined &&
                context.isPointInPath(this.targetX, this.targetY)) {
                this.currentTargetShapeId = shapeid;
            }
        },

        _drawRect: function (shapeid, x, y, width, height, lineColor, fillColor) {
            return this._drawShape(shapeid, [[x, y], [x + width, y], [x + width, y + height], [x, y + height], [x, y]], lineColor, fillColor);
        },

        appendShape: function (shape) {
            this.shapes[shape.id] = shape;
            this.shapeseq.push(shape.id);
            this.lastShapeId = shape.id;
            return shape.id;
        },

        replaceWithShape: function (shapeid, shape) {
            var shapeseq = this.shapeseq,
                i;
            this.shapes[shape.id] = shape;
            for (i = shapeseq.length; i--;) {
                if (shapeseq[i] == shapeid) {
                    shapeseq[i] = shape.id;
                }
            }
            delete this.shapes[shapeid];
        },

        replaceWithShapes: function (shapeids, shapes) {
            var shapeseq = this.shapeseq,
                shapemap = {},
                sid, i, first;

            for (i = shapeids.length; i--;) {
                shapemap[shapeids[i]] = true;
            }
            for (i = shapeseq.length; i--;) {
                sid = shapeseq[i];
                if (shapemap[sid]) {
                    shapeseq.splice(i, 1);
                    delete this.shapes[sid];
                    first = i;
                }
            }
            for (i = shapes.length; i--;) {
                shapeseq.splice(first, 0, shapes[i].id);
                this.shapes[shapes[i].id] = shapes[i];
            }

        },

        insertAfterShape: function (shapeid, shape) {
            var shapeseq = this.shapeseq,
                i;
            for (i = shapeseq.length; i--;) {
                if (shapeseq[i] === shapeid) {
                    shapeseq.splice(i + 1, 0, shape.id);
                    this.shapes[shape.id] = shape;
                    return;
                }
            }
        },

        removeShapeId: function (shapeid) {
            var shapeseq = this.shapeseq,
                i;
            for (i = shapeseq.length; i--;) {
                if (shapeseq[i] === shapeid) {
                    shapeseq.splice(i, 1);
                    break;
                }
            }
            delete this.shapes[shapeid];
        },

        getShapeAt: function (el, x, y) {
            this.targetX = x;
            this.targetY = y;
            this.render();
            return this.currentTargetShapeId;
        },

        render: function () {
            var shapeseq = this.shapeseq,
                shapes = this.shapes,
                shapeCount = shapeseq.length,
                context = this._getContext(),
                shapeid, shape, i;
            context.clearRect(0, 0, this.pixelWidth, this.pixelHeight);
            for (i = 0; i < shapeCount; i++) {
                shapeid = shapeseq[i];
                shape = shapes[shapeid];
                this['_draw' + shape.type].apply(this, shape.args);
            }
            if (!this.interact) {
                // not interactive so no need to keep the shapes array
                this.shapes = {};
                this.shapeseq = [];
            }
        }

    });

    VCanvas_vml = createClass(VCanvas_base, {
        init: function (width, height, target) {
            var groupel;
            VCanvas_vml._super.init.call(this, width, height, target);
            if (target[0]) {
                target = target[0];
            }
            $.data(target, '_jqs_vcanvas', this);
            this.canvas = document.createElement('span');
            $(this.canvas).css({ display: 'inline-block', position: 'relative', overflow: 'hidden', width: width, height: height, margin: '0px', padding: '0px', verticalAlign: 'top'});
            this._insert(this.canvas, target);
            this._calculatePixelDims(width, height, this.canvas);
            this.canvas.width = this.pixelWidth;
            this.canvas.height = this.pixelHeight;
            groupel = '<v:group coordorigin="0 0" coordsize="' + this.pixelWidth + ' ' + this.pixelHeight + '"' +
                    ' style="position:absolute;top:0;left:0;width:' + this.pixelWidth + 'px;height=' + this.pixelHeight + 'px;"></v:group>';
            this.canvas.insertAdjacentHTML('beforeEnd', groupel);
            this.group = $(this.canvas).children()[0];
            this.rendered = false;
            this.prerender = '';
        },

        _drawShape: function (shapeid, path, lineColor, fillColor, lineWidth) {
            var vpath = [],
                initial, stroke, fill, closed, vel, plen, i;
            for (i = 0, plen = path.length; i < plen; i++) {
                vpath[i] = '' + (path[i][0]) + ',' + (path[i][1]);
            }
            initial = vpath.splice(0, 1);
            lineWidth = lineWidth === undefined ? 1 : lineWidth;
            stroke = lineColor === undefined ? ' stroked="false" ' : ' strokeWeight="' + lineWidth + 'px" strokeColor="' + lineColor + '" ';
            fill = fillColor === undefined ? ' filled="false"' : ' fillColor="' + fillColor + '" filled="true" ';
            closed = vpath[0] === vpath[vpath.length - 1] ? 'x ' : '';
            vel = '<v:shape coordorigin="0 0" coordsize="' + this.pixelWidth + ' ' + this.pixelHeight + '" ' +
                 ' id="jqsshape' + shapeid + '" ' +
                 stroke +
                 fill +
                ' style="position:absolute;left:0px;top:0px;height:' + this.pixelHeight + 'px;width:' + this.pixelWidth + 'px;padding:0px;margin:0px;" ' +
                ' path="m ' + initial + ' l ' + vpath.join(', ') + ' ' + closed + 'e">' +
                ' </v:shape>';
            return vel;
        },

        _drawCircle: function (shapeid, x, y, radius, lineColor, fillColor, lineWidth) {
            var stroke, fill, vel;
            x -= radius;
            y -= radius;
            stroke = lineColor === undefined ? ' stroked="false" ' : ' strokeWeight="' + lineWidth + 'px" strokeColor="' + lineColor + '" ';
            fill = fillColor === undefined ? ' filled="false"' : ' fillColor="' + fillColor + '" filled="true" ';
            vel = '<v:oval ' +
                 ' id="jqsshape' + shapeid + '" ' +
                stroke +
                fill +
                ' style="position:absolute;top:' + y + 'px; left:' + x + 'px; width:' + (radius * 2) + 'px; height:' + (radius * 2) + 'px"></v:oval>';
            return vel;

        },

        _drawPieSlice: function (shapeid, x, y, radius, startAngle, endAngle, lineColor, fillColor) {
            var vpath, startx, starty, endx, endy, stroke, fill, vel;
            if (startAngle === endAngle) {
                return '';  // VML seems to have problem when start angle equals end angle.
            }
            if ((endAngle - startAngle) === (2 * Math.PI)) {
                startAngle = 0.0;  // VML seems to have a problem when drawing a full circle that doesn't start 0
                endAngle = (2 * Math.PI);
            }

            startx = x + Math.round(Math.cos(startAngle) * radius);
            starty = y + Math.round(Math.sin(startAngle) * radius);
            endx = x + Math.round(Math.cos(endAngle) * radius);
            endy = y + Math.round(Math.sin(endAngle) * radius);

            if (startx === endx && starty === endy) {
                if ((endAngle - startAngle) < Math.PI) {
                    // Prevent very small slices from being mistaken as a whole pie
                    return '';
                }
                // essentially going to be the entire circle, so ignore startAngle
                startx = endx = x + radius;
                starty = endy = y;
            }

            if (startx === endx && starty === endy && (endAngle - startAngle) < Math.PI) {
                return '';
            }

            vpath = [x - radius, y - radius, x + radius, y + radius, startx, starty, endx, endy];
            stroke = lineColor === undefined ? ' stroked="false" ' : ' strokeWeight="1px" strokeColor="' + lineColor + '" ';
            fill = fillColor === undefined ? ' filled="false"' : ' fillColor="' + fillColor + '" filled="true" ';
            vel = '<v:shape coordorigin="0 0" coordsize="' + this.pixelWidth + ' ' + this.pixelHeight + '" ' +
                 ' id="jqsshape' + shapeid + '" ' +
                 stroke +
                 fill +
                ' style="position:absolute;left:0px;top:0px;height:' + this.pixelHeight + 'px;width:' + this.pixelWidth + 'px;padding:0px;margin:0px;" ' +
                ' path="m ' + x + ',' + y + ' wa ' + vpath.join(', ') + ' x e">' +
                ' </v:shape>';
            return vel;
        },

        _drawRect: function (shapeid, x, y, width, height, lineColor, fillColor) {
            return this._drawShape(shapeid, [[x, y], [x, y + height], [x + width, y + height], [x + width, y], [x, y]], lineColor, fillColor);
        },

        reset: function () {
            this.group.innerHTML = '';
        },

        appendShape: function (shape) {
            var vel = this['_draw' + shape.type].apply(this, shape.args);
            if (this.rendered) {
                this.group.insertAdjacentHTML('beforeEnd', vel);
            } else {
                this.prerender += vel;
            }
            this.lastShapeId = shape.id;
            return shape.id;
        },

        replaceWithShape: function (shapeid, shape) {
            var existing = $('#jqsshape' + shapeid),
                vel = this['_draw' + shape.type].apply(this, shape.args);
            existing[0].outerHTML = vel;
        },

        replaceWithShapes: function (shapeids, shapes) {
            // replace the first shapeid with all the new shapes then toast the remaining old shapes
            var existing = $('#jqsshape' + shapeids[0]),
                replace = '',
                slen = shapes.length,
                i;
            for (i = 0; i < slen; i++) {
                replace += this['_draw' + shapes[i].type].apply(this, shapes[i].args);
            }
            existing[0].outerHTML = replace;
            for (i = 1; i < shapeids.length; i++) {
                $('#jqsshape' + shapeids[i]).remove();
            }
        },

        insertAfterShape: function (shapeid, shape) {
            var existing = $('#jqsshape' + shapeid),
                 vel = this['_draw' + shape.type].apply(this, shape.args);
            existing[0].insertAdjacentHTML('afterEnd', vel);
        },

        removeShapeId: function (shapeid) {
            var existing = $('#jqsshape' + shapeid);
            this.group.removeChild(existing[0]);
        },

        getShapeAt: function (el, x, y) {
            var shapeid = el.id.substr(8);
            return shapeid;
        },

        render: function () {
            if (!this.rendered) {
                // batch the intial render into a single repaint
                this.group.innerHTML = this.prerender;
                this.rendered = true;
            }
        }
    });

}))}(document, Math));
/*!
 * Datepicker for Bootstrap v1.5.0 (https://github.com/eternicode/bootstrap-datepicker)
 *
 * Copyright 2012 Stefan Petre
 * Improvements by Andrew Rowls
 * Licensed under the Apache License v2.0 (http://www.apache.org/licenses/LICENSE-2.0)
 */

!function(a,b){function c(){return new Date(Date.UTC.apply(Date,arguments))}function d(){var a=new Date;return c(a.getFullYear(),a.getMonth(),a.getDate())}function e(a,b){return a.getUTCFullYear()===b.getUTCFullYear()&&a.getUTCMonth()===b.getUTCMonth()&&a.getUTCDate()===b.getUTCDate()}function f(a){return function(){return this[a].apply(this,arguments)}}function g(b,c){function d(a,b){return b.toLowerCase()}var e,f=a(b).data(),g={},h=new RegExp("^"+c.toLowerCase()+"([A-Z])");c=new RegExp("^"+c.toLowerCase());for(var i in f)c.test(i)&&(e=i.replace(h,d),g[e]=f[i]);return g}function h(b){var c={};if(p[b]||(b=b.split("-")[0],p[b])){var d=p[b];return a.each(o,function(a,b){b in d&&(c[b]=d[b])}),c}}var i=function(){var b={get:function(a){return this.slice(a)[0]},contains:function(a){for(var b=a&&a.valueOf(),c=0,d=this.length;d>c;c++)if(this[c].valueOf()===b)return c;return-1},remove:function(a){this.splice(a,1)},replace:function(b){b&&(a.isArray(b)||(b=[b]),this.clear(),this.push.apply(this,b))},clear:function(){this.length=0},copy:function(){var a=new i;return a.replace(this),a}};return function(){var c=[];return c.push.apply(c,arguments),a.extend(c,b),c}}(),j=function(b,c){this._process_options(c),this.dates=new i,this.viewDate=this.o.defaultViewDate,this.focusDate=null,this.element=a(b),this.isInline=!1,this.isInput=this.element.is("input"),this.component=this.element.hasClass("date")?this.element.find(".add-on, .input-group-addon, .btn"):!1,this.hasInput=this.component&&this.element.find("input").length,this.component&&0===this.component.length&&(this.component=!1),this.picker=a(q.template),this._buildEvents(),this._attachEvents(),this.isInline?this.picker.addClass("datepicker-inline").appendTo(this.element):this.picker.addClass("datepicker-dropdown dropdown-menu"),this.o.rtl&&this.picker.addClass("datepicker-rtl"),this.viewMode=this.o.startView,this.o.calendarWeeks&&this.picker.find("tfoot .today, tfoot .clear").attr("colspan",function(a,b){return parseInt(b)+1}),this._allow_update=!1,this.setStartDate(this._o.startDate),this.setEndDate(this._o.endDate),this.setDaysOfWeekDisabled(this.o.daysOfWeekDisabled),this.setDatesDisabled(this.o.datesDisabled),this.fillDow(),this.fillMonths(),this._allow_update=!0,this.update(),this.showMode(),this.isInline&&this.show()};j.prototype={constructor:j,_process_options:function(e){this._o=a.extend({},this._o,e);var f=this.o=a.extend({},this._o),g=f.language;switch(p[g]||(g=g.split("-")[0],p[g]||(g=n.language)),f.language=g,f.startView){case 2:case"decade":f.startView=2;break;case 1:case"year":f.startView=1;break;default:f.startView=0}switch(f.minViewMode){case 1:case"months":f.minViewMode=1;break;case 2:case"years":f.minViewMode=2;break;default:f.minViewMode=0}f.startView=Math.max(f.startView,f.minViewMode),f.multidate!==!0&&(f.multidate=Number(f.multidate)||!1,f.multidate!==!1&&(f.multidate=Math.max(0,f.multidate))),f.multidateSeparator=String(f.multidateSeparator),f.weekStart%=7,f.weekEnd=(f.weekStart+6)%7;var h=q.parseFormat(f.format);if(f.startDate!==-1/0&&(f.startDate=f.startDate?f.startDate instanceof Date?this._local_to_utc(this._zero_time(f.startDate)):q.parseDate(f.startDate,h,f.language):-1/0),1/0!==f.endDate&&(f.endDate=f.endDate?f.endDate instanceof Date?this._local_to_utc(this._zero_time(f.endDate)):q.parseDate(f.endDate,h,f.language):1/0),f.daysOfWeekDisabled=f.daysOfWeekDisabled||[],a.isArray(f.daysOfWeekDisabled)||(f.daysOfWeekDisabled=f.daysOfWeekDisabled.split(/[,\s]*/)),f.daysOfWeekDisabled=a.map(f.daysOfWeekDisabled,function(a){return parseInt(a,10)}),f.datesDisabled=f.datesDisabled||[],!a.isArray(f.datesDisabled)){var i=[];i.push(q.parseDate(f.datesDisabled,h,f.language)),f.datesDisabled=i}f.datesDisabled=a.map(f.datesDisabled,function(a){return q.parseDate(a,h,f.language)});var j=String(f.orientation).toLowerCase().split(/\s+/g),k=f.orientation.toLowerCase();if(j=a.grep(j,function(a){return/^auto|left|right|top|bottom$/.test(a)}),f.orientation={x:"auto",y:"auto"},k&&"auto"!==k)if(1===j.length)switch(j[0]){case"top":case"bottom":f.orientation.y=j[0];break;case"left":case"right":f.orientation.x=j[0]}else k=a.grep(j,function(a){return/^left|right$/.test(a)}),f.orientation.x=k[0]||"auto",k=a.grep(j,function(a){return/^top|bottom$/.test(a)}),f.orientation.y=k[0]||"auto";else;if(f.defaultViewDate){var l=f.defaultViewDate.year||(new Date).getFullYear(),m=f.defaultViewDate.month||0,o=f.defaultViewDate.day||1;f.defaultViewDate=c(l,m,o)}else f.defaultViewDate=d();f.showOnFocus=f.showOnFocus!==b?f.showOnFocus:!0},_events:[],_secondaryEvents:[],_applyEvents:function(a){for(var c,d,e,f=0;f<a.length;f++)c=a[f][0],2===a[f].length?(d=b,e=a[f][1]):3===a[f].length&&(d=a[f][1],e=a[f][2]),c.on(e,d)},_unapplyEvents:function(a){for(var c,d,e,f=0;f<a.length;f++)c=a[f][0],2===a[f].length?(e=b,d=a[f][1]):3===a[f].length&&(e=a[f][1],d=a[f][2]),c.off(d,e)},_buildEvents:function(){var b={keyup:a.proxy(function(b){-1===a.inArray(b.keyCode,[27,37,39,38,40,32,13,9])&&this.update()},this),keydown:a.proxy(this.keydown,this)};this.o.showOnFocus===!0&&(b.focus=a.proxy(this.show,this)),this.isInput?this._events=[[this.element,b]]:this.component&&this.hasInput?this._events=[[this.element.find("input"),b],[this.component,{click:a.proxy(this.show,this)}]]:this.element.is("div")?this.isInline=!0:this._events=[[this.element,{click:a.proxy(this.show,this)}]],this._events.push([this.element,"*",{blur:a.proxy(function(a){this._focused_from=a.target},this)}],[this.element,{blur:a.proxy(function(a){this._focused_from=a.target},this)}]),this._secondaryEvents=[[this.picker,{click:a.proxy(this.click,this)}],[a(window),{resize:a.proxy(this.place,this)}],[a(document),{"mousedown touchstart":a.proxy(function(a){this.element.is(a.target)||this.element.find(a.target).length||this.picker.is(a.target)||this.picker.find(a.target).length||this.hide()},this)}]]},_attachEvents:function(){this._detachEvents(),this._applyEvents(this._events)},_detachEvents:function(){this._unapplyEvents(this._events)},_attachSecondaryEvents:function(){this._detachSecondaryEvents(),this._applyEvents(this._secondaryEvents)},_detachSecondaryEvents:function(){this._unapplyEvents(this._secondaryEvents)},_trigger:function(b,c){var d=c||this.dates.get(-1),e=this._utc_to_local(d);this.element.trigger({type:b,date:e,dates:a.map(this.dates,this._utc_to_local),format:a.proxy(function(a,b){0===arguments.length?(a=this.dates.length-1,b=this.o.format):"string"==typeof a&&(b=a,a=this.dates.length-1),b=b||this.o.format;var c=this.dates.get(a);return q.formatDate(c,b,this.o.language)},this)})},show:function(){return this.element.attr("readonly")&&this.o.enableOnReadonly===!1?void 0:(this.isInline||this.picker.appendTo(this.o.container),this.place(),this.picker.show(),this._attachSecondaryEvents(),this._trigger("show"),(window.navigator.msMaxTouchPoints||"ontouchstart"in document)&&this.o.disableTouchKeyboard&&a(this.element).blur(),this)},hide:function(){return this.isInline?this:this.picker.is(":visible")?(this.focusDate=null,this.picker.hide().detach(),this._detachSecondaryEvents(),this.viewMode=this.o.startView,this.showMode(),this.o.forceParse&&(this.isInput&&this.element.val()||this.hasInput&&this.element.find("input").val())&&this.setValue(),this._trigger("hide"),this):this},remove:function(){return this.hide(),this._detachEvents(),this._detachSecondaryEvents(),this.picker.remove(),delete this.element.data().datepicker,this.isInput||delete this.element.data().date,this},_utc_to_local:function(a){return a&&new Date(a.getTime()+6e4*a.getTimezoneOffset())},_local_to_utc:function(a){return a&&new Date(a.getTime()-6e4*a.getTimezoneOffset())},_zero_time:function(a){return a&&new Date(a.getFullYear(),a.getMonth(),a.getDate())},_zero_utc_time:function(a){return a&&new Date(Date.UTC(a.getUTCFullYear(),a.getUTCMonth(),a.getUTCDate()))},getDates:function(){return a.map(this.dates,this._utc_to_local)},getUTCDates:function(){return a.map(this.dates,function(a){return new Date(a)})},getDate:function(){return this._utc_to_local(this.getUTCDate())},getUTCDate:function(){var a=this.dates.get(-1);return"undefined"!=typeof a?new Date(a):null},clearDates:function(){var a;this.isInput?a=this.element:this.component&&(a=this.element.find("input")),a&&a.val("").change(),this.update(),this._trigger("changeDate"),this.o.autoclose&&this.hide()},setDates:function(){var b=a.isArray(arguments[0])?arguments[0]:arguments;return this.update.apply(this,b),this._trigger("changeDate"),this.setValue(),this},setUTCDates:function(){var b=a.isArray(arguments[0])?arguments[0]:arguments;return this.update.apply(this,a.map(b,this._utc_to_local)),this._trigger("changeDate"),this.setValue(),this},setDate:f("setDates"),setUTCDate:f("setUTCDates"),setValue:function(){var a=this.getFormattedDate();return this.isInput?this.element.val(a).change():this.component&&this.element.find("input").val(a).change(),this},getFormattedDate:function(c){c===b&&(c=this.o.format);var d=this.o.language;return a.map(this.dates,function(a){return q.formatDate(a,c,d)}).join(this.o.multidateSeparator)},setStartDate:function(a){return this._process_options({startDate:a}),this.update(),this.updateNavArrows(),this},setEndDate:function(a){return this._process_options({endDate:a}),this.update(),this.updateNavArrows(),this},setDaysOfWeekDisabled:function(a){return this._process_options({daysOfWeekDisabled:a}),this.update(),this.updateNavArrows(),this},setDatesDisabled:function(a){this._process_options({datesDisabled:a}),this.update(),this.updateNavArrows()},place:function(){if(this.isInline)return this;var b=this.picker.outerWidth(),c=this.picker.outerHeight(),d=10,e=a(this.o.container).width(),f=a(this.o.container).height(),g=a(this.o.container).scrollTop(),h=a(this.o.container).offset(),i=[];this.element.parents().each(function(){var b=a(this).css("z-index");"auto"!==b&&0!==b&&i.push(parseInt(b))});var j=Math.max.apply(Math,i)+10,k=this.component?this.component.parent().offset():this.element.offset(),l=this.component?this.component.outerHeight(!0):this.element.outerHeight(!1),m=this.component?this.component.outerWidth(!0):this.element.outerWidth(!1),n=k.left-h.left,o=k.top-h.top;this.picker.removeClass("datepicker-orient-top datepicker-orient-bottom datepicker-orient-right datepicker-orient-left"),"auto"!==this.o.orientation.x?(this.picker.addClass("datepicker-orient-"+this.o.orientation.x),"right"===this.o.orientation.x&&(n-=b-m)):k.left<0?(this.picker.addClass("datepicker-orient-left"),n-=k.left-d):n+b>e?(this.picker.addClass("datepicker-orient-right"),n=k.left+m-b):this.picker.addClass("datepicker-orient-left");var p,q,r=this.o.orientation.y;if("auto"===r&&(p=-g+o-c,q=g+f-(o+l+c),r=Math.max(p,q)===q?"top":"bottom"),this.picker.addClass("datepicker-orient-"+r),"top"===r?o+=l:o-=c+parseInt(this.picker.css("padding-top")),this.o.rtl){var s=e-(n+m);this.picker.css({top:o,right:s,zIndex:j})}else this.picker.css({top:o,left:n,zIndex:j});return this},_allow_update:!0,update:function(){if(!this._allow_update)return this;var b=this.dates.copy(),c=[],d=!1;return arguments.length?(a.each(arguments,a.proxy(function(a,b){b instanceof Date&&(b=this._local_to_utc(b)),c.push(b)},this)),d=!0):(c=this.isInput?this.element.val():this.element.data("date")||this.element.find("input").val(),c=c&&this.o.multidate?c.split(this.o.multidateSeparator):[c],delete this.element.data().date),c=a.map(c,a.proxy(function(a){return q.parseDate(a,this.o.format,this.o.language)},this)),c=a.grep(c,a.proxy(function(a){return a<this.o.startDate||a>this.o.endDate||!a},this),!0),this.dates.replace(c),this.dates.length?this.viewDate=new Date(this.dates.get(-1)):this.viewDate<this.o.startDate?this.viewDate=new Date(this.o.startDate):this.viewDate>this.o.endDate&&(this.viewDate=new Date(this.o.endDate)),d?this.setValue():c.length&&String(b)!==String(this.dates)&&this._trigger("changeDate"),!this.dates.length&&b.length&&this._trigger("clearDate"),this.fill(),this},fillDow:function(){var a=this.o.weekStart,b="<tr>";if(this.o.calendarWeeks){this.picker.find(".datepicker-days thead tr:first-child .datepicker-switch").attr("colspan",function(a,b){return parseInt(b)+1});var c='<th class="cw">&#160;</th>';b+=c}for(;a<this.o.weekStart+7;)b+='<th class="dow">'+p[this.o.language].daysMin[a++%7]+"</th>";b+="</tr>",this.picker.find(".datepicker-days thead").append(b)},fillMonths:function(){for(var a="",b=0;12>b;)a+='<span class="month">'+p[this.o.language].monthsShort[b++]+"</span>";this.picker.find(".datepicker-months td").html(a)},setRange:function(b){b&&b.length?this.range=a.map(b,function(a){return a.valueOf()}):delete this.range,this.fill()},getClassNames:function(b){var c=[],d=this.viewDate.getUTCFullYear(),f=this.viewDate.getUTCMonth(),g=new Date;return b.getUTCFullYear()<d||b.getUTCFullYear()===d&&b.getUTCMonth()<f?c.push("old"):(b.getUTCFullYear()>d||b.getUTCFullYear()===d&&b.getUTCMonth()>f)&&c.push("new"),this.focusDate&&b.valueOf()===this.focusDate.valueOf()&&c.push("focused"),this.o.todayHighlight&&b.getUTCFullYear()===g.getFullYear()&&b.getUTCMonth()===g.getMonth()&&b.getUTCDate()===g.getDate()&&c.push("today"),-1!==this.dates.contains(b)&&c.push("active"),(b.valueOf()<this.o.startDate||b.valueOf()>this.o.endDate||-1!==a.inArray(b.getUTCDay(),this.o.daysOfWeekDisabled))&&c.push("disabled"),this.o.datesDisabled.length>0&&a.grep(this.o.datesDisabled,function(a){return e(b,a)}).length>0&&c.push("disabled","disabled-date"),this.range&&(b>this.range[0]&&b<this.range[this.range.length-1]&&c.push("range"),-1!==a.inArray(b.valueOf(),this.range)&&c.push("selected")),c},fill:function(){var d,e=new Date(this.viewDate),f=e.getUTCFullYear(),g=e.getUTCMonth(),h=this.o.startDate!==-1/0?this.o.startDate.getUTCFullYear():-1/0,i=this.o.startDate!==-1/0?this.o.startDate.getUTCMonth():-1/0,j=1/0!==this.o.endDate?this.o.endDate.getUTCFullYear():1/0,k=1/0!==this.o.endDate?this.o.endDate.getUTCMonth():1/0,l=p[this.o.language].today||p.en.today||"",m=p[this.o.language].clear||p.en.clear||"";if(!isNaN(f)&&!isNaN(g)){this.picker.find(".datepicker-days thead .datepicker-switch").text(p[this.o.language].months[g]+" "+f),this.picker.find("tfoot .today").text(l).toggle(this.o.todayBtn!==!1),this.picker.find("tfoot .clear").text(m).toggle(this.o.clearBtn!==!1),this.updateNavArrows(),this.fillMonths();var n=c(f,g-1,28),o=q.getDaysInMonth(n.getUTCFullYear(),n.getUTCMonth());n.setUTCDate(o),n.setUTCDate(o-(n.getUTCDay()-this.o.weekStart+7)%7);var r=new Date(n);r.setUTCDate(r.getUTCDate()+42),r=r.valueOf();for(var s,t=[];n.valueOf()<r;){if(n.getUTCDay()===this.o.weekStart&&(t.push("<tr>"),this.o.calendarWeeks)){var u=new Date(+n+(this.o.weekStart-n.getUTCDay()-7)%7*864e5),v=new Date(Number(u)+(11-u.getUTCDay())%7*864e5),w=new Date(Number(w=c(v.getUTCFullYear(),0,1))+(11-w.getUTCDay())%7*864e5),x=(v-w)/864e5/7+1;t.push('<td class="cw">'+x+"</td>")}if(s=this.getClassNames(n),s.push("day"),this.o.beforeShowDay!==a.noop){var y=this.o.beforeShowDay(this._utc_to_local(n));y===b?y={}:"boolean"==typeof y?y={enabled:y}:"string"==typeof y&&(y={classes:y}),y.enabled===!1&&s.push("disabled"),y.classes&&(s=s.concat(y.classes.split(/\s+/))),y.tooltip&&(d=y.tooltip)}s=a.unique(s),t.push('<td class="'+s.join(" ")+'"'+(d?' title="'+d+'"':"")+">"+n.getUTCDate()+"</td>"),d=null,n.getUTCDay()===this.o.weekEnd&&t.push("</tr>"),n.setUTCDate(n.getUTCDate()+1)}this.picker.find(".datepicker-days tbody").empty().append(t.join(""));var z=this.picker.find(".datepicker-months").find("th:eq(1)").text(f).end().find("span").removeClass("active");if(a.each(this.dates,function(a,b){b.getUTCFullYear()===f&&z.eq(b.getUTCMonth()).addClass("active")}),(h>f||f>j)&&z.addClass("disabled"),f===h&&z.slice(0,i).addClass("disabled"),f===j&&z.slice(k+1).addClass("disabled"),this.o.beforeShowMonth!==a.noop){var A=this;a.each(z,function(b,c){if(!a(c).hasClass("disabled")){var d=new Date(f,b,1),e=A.o.beforeShowMonth(d);e===!1&&a(c).addClass("disabled")}})}t="",f=10*parseInt(f/10,10);var B=this.picker.find(".datepicker-years").find("th:eq(1)").text(f+"-"+(f+9)).end().find("td");f-=1;for(var C,D=a.map(this.dates,function(a){return a.getUTCFullYear()}),E=-1;11>E;E++)C=["year"],-1===E?C.push("old"):10===E&&C.push("new"),-1!==a.inArray(f,D)&&C.push("active"),(h>f||f>j)&&C.push("disabled"),t+='<span class="'+C.join(" ")+'">'+f+"</span>",f+=1;B.html(t)}},updateNavArrows:function(){if(this._allow_update){var a=new Date(this.viewDate),b=a.getUTCFullYear(),c=a.getUTCMonth();switch(this.viewMode){case 0:this.picker.find(".prev").css(this.o.startDate!==-1/0&&b<=this.o.startDate.getUTCFullYear()&&c<=this.o.startDate.getUTCMonth()?{visibility:"hidden"}:{visibility:"visible"}),this.picker.find(".next").css(1/0!==this.o.endDate&&b>=this.o.endDate.getUTCFullYear()&&c>=this.o.endDate.getUTCMonth()?{visibility:"hidden"}:{visibility:"visible"});break;case 1:case 2:this.picker.find(".prev").css(this.o.startDate!==-1/0&&b<=this.o.startDate.getUTCFullYear()?{visibility:"hidden"}:{visibility:"visible"}),this.picker.find(".next").css(1/0!==this.o.endDate&&b>=this.o.endDate.getUTCFullYear()?{visibility:"hidden"}:{visibility:"visible"})}}},click:function(b){b.preventDefault();var d,e,f,g=a(b.target).closest("span, td, th");if(1===g.length)switch(g[0].nodeName.toLowerCase()){case"th":switch(g[0].className){case"datepicker-switch":this.showMode(1);break;case"prev":case"next":var h=q.modes[this.viewMode].navStep*("prev"===g[0].className?-1:1);switch(this.viewMode){case 0:this.viewDate=this.moveMonth(this.viewDate,h),this._trigger("changeMonth",this.viewDate);break;case 1:case 2:this.viewDate=this.moveYear(this.viewDate,h),1===this.viewMode&&this._trigger("changeYear",this.viewDate)}this.fill();break;case"today":var i=new Date;i=c(i.getFullYear(),i.getMonth(),i.getDate(),0,0,0),this.showMode(-2);var j="linked"===this.o.todayBtn?null:"view";this._setDate(i,j);break;case"clear":this.clearDates()}break;case"span":g.hasClass("disabled")||(this.viewDate.setUTCDate(1),g.hasClass("month")?(f=1,e=g.parent().find("span").index(g),d=this.viewDate.getUTCFullYear(),this.viewDate.setUTCMonth(e),this._trigger("changeMonth",this.viewDate),1===this.o.minViewMode&&this._setDate(c(d,e,f))):(f=1,e=0,d=parseInt(g.text(),10)||0,this.viewDate.setUTCFullYear(d),this._trigger("changeYear",this.viewDate),2===this.o.minViewMode&&this._setDate(c(d,e,f))),this.showMode(-1),this.fill());break;case"td":g.hasClass("day")&&!g.hasClass("disabled")&&(f=parseInt(g.text(),10)||1,d=this.viewDate.getUTCFullYear(),e=this.viewDate.getUTCMonth(),g.hasClass("old")?0===e?(e=11,d-=1):e-=1:g.hasClass("new")&&(11===e?(e=0,d+=1):e+=1),this._setDate(c(d,e,f)))}this.picker.is(":visible")&&this._focused_from&&a(this._focused_from).focus(),delete this._focused_from},_toggle_multidate:function(a){var b=this.dates.contains(a);if(a||this.dates.clear(),-1!==b?(this.o.multidate===!0||this.o.multidate>1||this.o.toggleActive)&&this.dates.remove(b):this.o.multidate===!1?(this.dates.clear(),this.dates.push(a)):this.dates.push(a),"number"==typeof this.o.multidate)for(;this.dates.length>this.o.multidate;)this.dates.remove(0)},_setDate:function(a,b){b&&"date"!==b||this._toggle_multidate(a&&new Date(a)),b&&"view"!==b||(this.viewDate=a&&new Date(a)),this.fill(),this.setValue(),b&&"view"===b||this._trigger("changeDate");var c;this.isInput?c=this.element:this.component&&(c=this.element.find("input")),c&&c.change(),!this.o.autoclose||b&&"date"!==b||this.hide()},moveMonth:function(a,c){if(!a)return b;if(!c)return a;var d,e,f=new Date(a.valueOf()),g=f.getUTCDate(),h=f.getUTCMonth(),i=Math.abs(c);if(c=c>0?1:-1,1===i)e=-1===c?function(){return f.getUTCMonth()===h}:function(){return f.getUTCMonth()!==d},d=h+c,f.setUTCMonth(d),(0>d||d>11)&&(d=(d+12)%12);else{for(var j=0;i>j;j++)f=this.moveMonth(f,c);d=f.getUTCMonth(),f.setUTCDate(g),e=function(){return d!==f.getUTCMonth()}}for(;e();)f.setUTCDate(--g),f.setUTCMonth(d);return f},moveYear:function(a,b){return this.moveMonth(a,12*b)},dateWithinRange:function(a){return a>=this.o.startDate&&a<=this.o.endDate},keydown:function(a){if(!this.picker.is(":visible"))return void(27===a.keyCode&&this.show());var b,c,e,f=!1,g=this.focusDate||this.viewDate;switch(a.keyCode){case 27:this.focusDate?(this.focusDate=null,this.viewDate=this.dates.get(-1)||this.viewDate,this.fill()):this.hide(),a.preventDefault();break;case 37:case 39:if(!this.o.keyboardNavigation)break;b=37===a.keyCode?-1:1,a.ctrlKey?(c=this.moveYear(this.dates.get(-1)||d(),b),e=this.moveYear(g,b),this._trigger("changeYear",this.viewDate)):a.shiftKey?(c=this.moveMonth(this.dates.get(-1)||d(),b),e=this.moveMonth(g,b),this._trigger("changeMonth",this.viewDate)):(c=new Date(this.dates.get(-1)||d()),c.setUTCDate(c.getUTCDate()+b),e=new Date(g),e.setUTCDate(g.getUTCDate()+b)),this.dateWithinRange(e)&&(this.focusDate=this.viewDate=e,this.setValue(),this.fill(),a.preventDefault());break;case 38:case 40:if(!this.o.keyboardNavigation)break;b=38===a.keyCode?-1:1,a.ctrlKey?(c=this.moveYear(this.dates.get(-1)||d(),b),e=this.moveYear(g,b),this._trigger("changeYear",this.viewDate)):a.shiftKey?(c=this.moveMonth(this.dates.get(-1)||d(),b),e=this.moveMonth(g,b),this._trigger("changeMonth",this.viewDate)):(c=new Date(this.dates.get(-1)||d()),c.setUTCDate(c.getUTCDate()+7*b),e=new Date(g),e.setUTCDate(g.getUTCDate()+7*b)),this.dateWithinRange(e)&&(this.focusDate=this.viewDate=e,this.setValue(),this.fill(),a.preventDefault());break;case 32:break;case 13:g=this.focusDate||this.dates.get(-1)||this.viewDate,this.o.keyboardNavigation&&(this._toggle_multidate(g),f=!0),this.focusDate=null,this.viewDate=this.dates.get(-1)||this.viewDate,this.setValue(),this.fill(),this.picker.is(":visible")&&(a.preventDefault(),"function"==typeof a.stopPropagation?a.stopPropagation():a.cancelBubble=!0,this.o.autoclose&&this.hide());break;case 9:this.focusDate=null,this.viewDate=this.dates.get(-1)||this.viewDate,this.fill(),this.hide()}if(f){this._trigger(this.dates.length?"changeDate":"clearDate");var h;this.isInput?h=this.element:this.component&&(h=this.element.find("input")),h&&h.change()}},showMode:function(a){a&&(this.viewMode=Math.max(this.o.minViewMode,Math.min(2,this.viewMode+a))),this.picker.children("div").hide().filter(".datepicker-"+q.modes[this.viewMode].clsName).css("display","block"),this.updateNavArrows()}};var k=function(b,c){this.element=a(b),this.inputs=a.map(c.inputs,function(a){return a.jquery?a[0]:a}),delete c.inputs,m.call(a(this.inputs),c).bind("changeDate",a.proxy(this.dateUpdated,this)),this.pickers=a.map(this.inputs,function(b){return a(b).data("datepicker")}),this.updateDates()};k.prototype={updateDates:function(){this.dates=a.map(this.pickers,function(a){return a.getUTCDate()}),this.updateRanges()},updateRanges:function(){var b=a.map(this.dates,function(a){return a.valueOf()});a.each(this.pickers,function(a,c){c.setRange(b)})},dateUpdated:function(b){if(!this.updating){this.updating=!0;var c=a(b.target).data("datepicker"),d=c.getUTCDate(),e=a.inArray(b.target,this.inputs),f=e-1,g=e+1,h=this.inputs.length;if(-1!==e){if(a.each(this.pickers,function(a,b){b.getUTCDate()||b.setUTCDate(d)}),d<this.dates[f])for(;f>=0&&d<this.dates[f];)this.pickers[f--].setUTCDate(d);else if(d>this.dates[g])for(;h>g&&d>this.dates[g];)this.pickers[g++].setUTCDate(d);this.updateDates(),delete this.updating}}},remove:function(){a.map(this.pickers,function(a){a.remove()}),delete this.element.data().datepicker}};var l=a.fn.datepicker,m=function(c){var d=Array.apply(null,arguments);d.shift();var e;return this.each(function(){var f=a(this),i=f.data("datepicker"),l="object"==typeof c&&c;if(!i){var m=g(this,"date"),o=a.extend({},n,m,l),p=h(o.language),q=a.extend({},n,p,m,l);if(f.hasClass("input-daterange")||q.inputs){var r={inputs:q.inputs||f.find("input").toArray()};f.data("datepicker",i=new k(this,a.extend(q,r)))}else f.data("datepicker",i=new j(this,q))}return"string"==typeof c&&"function"==typeof i[c]&&(e=i[c].apply(i,d),e!==b)?!1:void 0}),e!==b?e:this};a.fn.datepicker=m;var n=a.fn.datepicker.defaults={autoclose:!1,beforeShowDay:a.noop,beforeShowMonth:a.noop,calendarWeeks:!1,clearBtn:!1,toggleActive:!1,daysOfWeekDisabled:[],datesDisabled:[],endDate:1/0,forceParse:!0,format:"mm/dd/yyyy",keyboardNavigation:!0,language:"en",minViewMode:0,multidate:!1,multidateSeparator:",",orientation:"auto",rtl:!1,startDate:-1/0,startView:0,todayBtn:!1,todayHighlight:!1,weekStart:0,disableTouchKeyboard:!1,enableOnReadonly:!0,container:"body"},o=a.fn.datepicker.locale_opts=["format","rtl","weekStart"];a.fn.datepicker.Constructor=j;var p=a.fn.datepicker.dates={en:{days:["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],daysShort:["Sun","Mon","Tue","Wed","Thu","Fri","Sat","Sun"],daysMin:["Su","Mo","Tu","We","Th","Fr","Sa","Su"],months:["January","February","March","April","May","June","July","August","September","October","November","December"],monthsShort:["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],today:"Today",clear:"Clear"}},q={modes:[{clsName:"days",navFnc:"Month",navStep:1},{clsName:"months",navFnc:"FullYear",navStep:1},{clsName:"years",navFnc:"FullYear",navStep:10}],isLeapYear:function(a){return a%4===0&&a%100!==0||a%400===0},getDaysInMonth:function(a,b){return[31,q.isLeapYear(a)?29:28,31,30,31,30,31,31,30,31,30,31][b]},validParts:/dd?|DD?|mm?|MM?|yy(?:yy)?/g,nonpunctuation:/[^ -\/:-@\[\u3400-\u9fff-`{-~\t\n\r]+/g,parseFormat:function(a){var b=a.replace(this.validParts,"\x00").split("\x00"),c=a.match(this.validParts);if(!b||!b.length||!c||0===c.length)throw new Error("Invalid date format.");return{separators:b,parts:c}},parseDate:function(d,e,f){function g(){var a=this.slice(0,m[k].length),b=m[k].slice(0,a.length);return a.toLowerCase()===b.toLowerCase()}if(!d)return b;if(d instanceof Date)return d;"string"==typeof e&&(e=q.parseFormat(e));var h,i,k,l=/([\-+]\d+)([dmwy])/,m=d.match(/([\-+]\d+)([dmwy])/g);if(/^[\-+]\d+[dmwy]([\s,]+[\-+]\d+[dmwy])*$/.test(d)){for(d=new Date,k=0;k<m.length;k++)switch(h=l.exec(m[k]),i=parseInt(h[1]),h[2]){case"d":d.setUTCDate(d.getUTCDate()+i);break;case"m":d=j.prototype.moveMonth.call(j.prototype,d,i);break;case"w":d.setUTCDate(d.getUTCDate()+7*i);break;case"y":d=j.prototype.moveYear.call(j.prototype,d,i)}return c(d.getUTCFullYear(),d.getUTCMonth(),d.getUTCDate(),0,0,0)}m=d&&d.match(this.nonpunctuation)||[],d=new Date;var n,o,r={},s=["yyyy","yy","M","MM","m","mm","d","dd"],t={yyyy:function(a,b){return a.setUTCFullYear(b)},yy:function(a,b){return a.setUTCFullYear(2e3+b)},m:function(a,b){if(isNaN(a))return a;for(b-=1;0>b;)b+=12;for(b%=12,a.setUTCMonth(b);a.getUTCMonth()!==b;)a.setUTCDate(a.getUTCDate()-1);return a},d:function(a,b){return a.setUTCDate(b)}};t.M=t.MM=t.mm=t.m,t.dd=t.d,d=c(d.getFullYear(),d.getMonth(),d.getDate(),0,0,0);var u=e.parts.slice();if(m.length!==u.length&&(u=a(u).filter(function(b,c){return-1!==a.inArray(c,s)}).toArray()),m.length===u.length){var v;for(k=0,v=u.length;v>k;k++){if(n=parseInt(m[k],10),h=u[k],isNaN(n))switch(h){case"MM":o=a(p[f].months).filter(g),n=a.inArray(o[0],p[f].months)+1;break;case"M":o=a(p[f].monthsShort).filter(g),n=a.inArray(o[0],p[f].monthsShort)+1}r[h]=n}var w,x;for(k=0;k<s.length;k++)x=s[k],x in r&&!isNaN(r[x])&&(w=new Date(d),t[x](w,r[x]),isNaN(w)||(d=w))}return d},formatDate:function(b,c,d){if(!b)return"";"string"==typeof c&&(c=q.parseFormat(c));var e={d:b.getUTCDate(),D:p[d].daysShort[b.getUTCDay()],DD:p[d].days[b.getUTCDay()],m:b.getUTCMonth()+1,M:p[d].monthsShort[b.getUTCMonth()],MM:p[d].months[b.getUTCMonth()],yy:b.getUTCFullYear().toString().substring(2),yyyy:b.getUTCFullYear()};e.dd=(e.d<10?"0":"")+e.d,e.mm=(e.m<10?"0":"")+e.m,b=[];for(var f=a.extend([],c.separators),g=0,h=c.parts.length;h>=g;g++)f.length&&b.push(f.shift()),b.push(e[c.parts[g]]);return b.join("")},headTemplate:'<thead><tr><th class="prev">&#171;</th><th colspan="5" class="datepicker-switch"></th><th class="next">&#187;</th></tr></thead>',contTemplate:'<tbody><tr><td colspan="7"></td></tr></tbody>',footTemplate:'<tfoot><tr><th colspan="7" class="today"></th></tr><tr><th colspan="7" class="clear"></th></tr></tfoot>'};q.template='<div class="datepicker"><div class="datepicker-days"><table class=" table-condensed">'+q.headTemplate+"<tbody></tbody>"+q.footTemplate+'</table></div><div class="datepicker-months"><table class="table-condensed">'+q.headTemplate+q.contTemplate+q.footTemplate+'</table></div><div class="datepicker-years"><table class="table-condensed">'+q.headTemplate+q.contTemplate+q.footTemplate+"</table></div></div>",a.fn.datepicker.DPGlobal=q,a.fn.datepicker.noConflict=function(){return a.fn.datepicker=l,this},a.fn.datepicker.version="1.5.0",a(document).on("focus.datepicker.data-api click.datepicker.data-api",'[data-provide="datepicker"]',function(b){var c=a(this);c.data("datepicker")||(b.preventDefault(),m.call(c,"show"))}),a(function(){m.call(a('[data-provide="datepicker-inline"]'))})}(window.jQuery);
(function(a){if(typeof define==="function"&&define.amd){define(["jquery"],a)}else{if(typeof exports==="object"){a(require("jquery"))}else{a(jQuery)}}}(function(d,f){if(!("indexOf" in Array.prototype)){Array.prototype.indexOf=function(k,j){if(j===f){j=0}if(j<0){j+=this.length}if(j<0){j=0}for(var l=this.length;j<l;j++){if(j in this&&this[j]===k){return j}}return -1}}function a(){var q,k,p,l,j,n,m,o;k=(new Date()).toString();p=((m=k.split("(")[1])!=null?m.slice(0,-1):0)||k.split(" ");if(p instanceof Array){n=[];for(var l=0,j=p.length;l<j;l++){o=p[l];if((q=(m=o.match(/\b[A-Z]+\b/))!==null)?m[0]:0){n.push(q)}}p=n.pop()}return p}function h(){return new Date(Date.UTC.apply(Date,arguments))}var g=function(k,j){var m=this;this.element=d(k);this.container=j.container||"body";this.language=j.language||this.element.data("date-language")||"en";this.language=this.language in e?this.language:this.language.split("-")[0];this.language=this.language in e?this.language:"en";this.isRTL=e[this.language].rtl||false;this.formatType=j.formatType||this.element.data("format-type")||"standard";this.format=c.parseFormat(j.format||this.element.data("date-format")||e[this.language].format||c.getDefaultFormat(this.formatType,"input"),this.formatType);this.isInline=false;this.isVisible=false;this.isInput=this.element.is("input");this.fontAwesome=j.fontAwesome||this.element.data("font-awesome")||false;this.bootcssVer=j.bootcssVer||(this.isInput?(this.element.is(".form-control")?3:2):(this.bootcssVer=this.element.is(".input-group")?3:2));this.component=this.element.is(".date")?(this.bootcssVer===3?this.element.find(".input-group-addon .glyphicon-th, .input-group-addon .glyphicon-time, .input-group-addon .glyphicon-remove, .input-group-addon .glyphicon-calendar, .input-group-addon .fa-calendar, .input-group-addon .fa-clock-o").parent():this.element.find(".add-on .icon-th, .add-on .icon-time, .add-on .icon-calendar, .add-on .fa-calendar, .add-on .fa-clock-o").parent()):false;this.componentReset=this.element.is(".date")?(this.bootcssVer===3?this.element.find(".input-group-addon .glyphicon-remove, .input-group-addon .fa-times").parent():this.element.find(".add-on .icon-remove, .add-on .fa-times").parent()):false;this.hasInput=this.component&&this.element.find("input").length;if(this.component&&this.component.length===0){this.component=false}this.linkField=j.linkField||this.element.data("link-field")||false;this.linkFormat=c.parseFormat(j.linkFormat||this.element.data("link-format")||c.getDefaultFormat(this.formatType,"link"),this.formatType);this.minuteStep=j.minuteStep||this.element.data("minute-step")||5;this.pickerPosition=j.pickerPosition||this.element.data("picker-position")||"bottom-right";this.showMeridian=j.showMeridian||this.element.data("show-meridian")||false;this.initialDate=j.initialDate||new Date();this.zIndex=j.zIndex||this.element.data("z-index")||f;this.title=typeof j.title==="undefined"?false:j.title;this.timezone=j.timezone||a();this.icons={leftArrow:this.fontAwesome?"fa-arrow-left":(this.bootcssVer===3?"glyphicon-arrow-left":"icon-arrow-left"),rightArrow:this.fontAwesome?"fa-arrow-right":(this.bootcssVer===3?"glyphicon-arrow-right":"icon-arrow-right")};this.icontype=this.fontAwesome?"fa":"glyphicon";this._attachEvents();this.clickedOutside=function(n){if(d(n.target).closest(".datetimepicker").length===0){m.hide()}};this.formatViewType="datetime";if("formatViewType" in j){this.formatViewType=j.formatViewType}else{if("formatViewType" in this.element.data()){this.formatViewType=this.element.data("formatViewType")}}this.minView=0;if("minView" in j){this.minView=j.minView}else{if("minView" in this.element.data()){this.minView=this.element.data("min-view")}}this.minView=c.convertViewMode(this.minView);this.maxView=c.modes.length-1;if("maxView" in j){this.maxView=j.maxView}else{if("maxView" in this.element.data()){this.maxView=this.element.data("max-view")}}this.maxView=c.convertViewMode(this.maxView);this.wheelViewModeNavigation=false;if("wheelViewModeNavigation" in j){this.wheelViewModeNavigation=j.wheelViewModeNavigation}else{if("wheelViewModeNavigation" in this.element.data()){this.wheelViewModeNavigation=this.element.data("view-mode-wheel-navigation")}}this.wheelViewModeNavigationInverseDirection=false;if("wheelViewModeNavigationInverseDirection" in j){this.wheelViewModeNavigationInverseDirection=j.wheelViewModeNavigationInverseDirection}else{if("wheelViewModeNavigationInverseDirection" in this.element.data()){this.wheelViewModeNavigationInverseDirection=this.element.data("view-mode-wheel-navigation-inverse-dir")}}this.wheelViewModeNavigationDelay=100;if("wheelViewModeNavigationDelay" in j){this.wheelViewModeNavigationDelay=j.wheelViewModeNavigationDelay}else{if("wheelViewModeNavigationDelay" in this.element.data()){this.wheelViewModeNavigationDelay=this.element.data("view-mode-wheel-navigation-delay")}}this.startViewMode=2;if("startView" in j){this.startViewMode=j.startView}else{if("startView" in this.element.data()){this.startViewMode=this.element.data("start-view")}}this.startViewMode=c.convertViewMode(this.startViewMode);this.viewMode=this.startViewMode;this.viewSelect=this.minView;if("viewSelect" in j){this.viewSelect=j.viewSelect}else{if("viewSelect" in this.element.data()){this.viewSelect=this.element.data("view-select")}}this.viewSelect=c.convertViewMode(this.viewSelect);this.forceParse=true;if("forceParse" in j){this.forceParse=j.forceParse}else{if("dateForceParse" in this.element.data()){this.forceParse=this.element.data("date-force-parse")}}var l=this.bootcssVer===3?c.templateV3:c.template;while(l.indexOf("{iconType}")!==-1){l=l.replace("{iconType}",this.icontype)}while(l.indexOf("{leftArrow}")!==-1){l=l.replace("{leftArrow}",this.icons.leftArrow)}while(l.indexOf("{rightArrow}")!==-1){l=l.replace("{rightArrow}",this.icons.rightArrow)}this.picker=d(l).appendTo(this.isInline?this.element:this.container).on({click:d.proxy(this.click,this),mousedown:d.proxy(this.mousedown,this)});if(this.wheelViewModeNavigation){if(d.fn.mousewheel){this.picker.on({mousewheel:d.proxy(this.mousewheel,this)})}else{console.log("Mouse Wheel event is not supported. Please include the jQuery Mouse Wheel plugin before enabling this option")}}if(this.isInline){this.picker.addClass("datetimepicker-inline")}else{this.picker.addClass("datetimepicker-dropdown-"+this.pickerPosition+" dropdown-menu")}if(this.isRTL){this.picker.addClass("datetimepicker-rtl");var i=this.bootcssVer===3?".prev span, .next span":".prev i, .next i";this.picker.find(i).toggleClass(this.icons.leftArrow+" "+this.icons.rightArrow)}d(document).on("mousedown touchend",this.clickedOutside);this.autoclose=false;if("autoclose" in j){this.autoclose=j.autoclose}else{if("dateAutoclose" in this.element.data()){this.autoclose=this.element.data("date-autoclose")}}this.keyboardNavigation=true;if("keyboardNavigation" in j){this.keyboardNavigation=j.keyboardNavigation}else{if("dateKeyboardNavigation" in this.element.data()){this.keyboardNavigation=this.element.data("date-keyboard-navigation")}}this.todayBtn=(j.todayBtn||this.element.data("date-today-btn")||false);this.clearBtn=(j.clearBtn||this.element.data("date-clear-btn")||false);this.todayHighlight=(j.todayHighlight||this.element.data("date-today-highlight")||false);this.weekStart=0;if(typeof j.weekStart!=="undefined"){this.weekStart=j.weekStart}else{if(typeof this.element.data("date-weekstart")!=="undefined"){this.weekStart=this.element.data("date-weekstart")}else{if(typeof e[this.language].weekStart!=="undefined"){this.weekStart=e[this.language].weekStart}}}this.weekStart=this.weekStart%7;this.weekEnd=((this.weekStart+6)%7);this.onRenderDay=function(n){var p=(j.onRenderDay||function(){return[]})(n);if(typeof p==="string"){p=[p]}var o=["day"];return o.concat((p?p:[]))};this.onRenderHour=function(n){var p=(j.onRenderHour||function(){return[]})(n);var o=["hour"];if(typeof p==="string"){p=[p]}return o.concat((p?p:[]))};this.onRenderMinute=function(n){var p=(j.onRenderMinute||function(){return[]})(n);var o=["minute"];if(typeof p==="string"){p=[p]}if(n<this.startDate||n>this.endDate){o.push("disabled")}else{if(Math.floor(this.date.getUTCMinutes()/this.minuteStep)===Math.floor(n.getUTCMinutes()/this.minuteStep)){o.push("active")}}return o.concat((p?p:[]))};this.onRenderYear=function(o){var q=(j.onRenderYear||function(){return[]})(o);var p=["year"];if(typeof q==="string"){q=[q]}if(this.date.getUTCFullYear()===o.getUTCFullYear()){p.push("active")}var n=o.getUTCFullYear();var r=this.endDate.getUTCFullYear();if(o<this.startDate||n>r){p.push("disabled")}return p.concat((q?q:[]))};this.onRenderMonth=function(n){var p=(j.onRenderMonth||function(){return[]})(n);var o=["month"];if(typeof p==="string"){p=[p]}return o.concat((p?p:[]))};this.startDate=new Date(-8639968443048000);this.endDate=new Date(8639968443048000);this.datesDisabled=[];this.daysOfWeekDisabled=[];this.setStartDate(j.startDate||this.element.data("date-startdate"));this.setEndDate(j.endDate||this.element.data("date-enddate"));this.setDatesDisabled(j.datesDisabled||this.element.data("date-dates-disabled"));this.setDaysOfWeekDisabled(j.daysOfWeekDisabled||this.element.data("date-days-of-week-disabled"));this.setMinutesDisabled(j.minutesDisabled||this.element.data("date-minute-disabled"));this.setHoursDisabled(j.hoursDisabled||this.element.data("date-hour-disabled"));this.fillDow();this.fillMonths();this.update();this.showMode();if(this.isInline){this.show()}};g.prototype={constructor:g,_events:[],_attachEvents:function(){this._detachEvents();if(this.isInput){this._events=[[this.element,{focus:d.proxy(this.show,this),keyup:d.proxy(this.update,this),keydown:d.proxy(this.keydown,this)}]]}else{if(this.component&&this.hasInput){this._events=[[this.element.find("input"),{focus:d.proxy(this.show,this),keyup:d.proxy(this.update,this),keydown:d.proxy(this.keydown,this)}],[this.component,{click:d.proxy(this.show,this)}]];if(this.componentReset){this._events.push([this.componentReset,{click:d.proxy(this.reset,this)}])}}else{if(this.element.is("div")){this.isInline=true}else{this._events=[[this.element,{click:d.proxy(this.show,this)}]]}}}for(var j=0,k,l;j<this._events.length;j++){k=this._events[j][0];l=this._events[j][1];k.on(l)}},_detachEvents:function(){for(var j=0,k,l;j<this._events.length;j++){k=this._events[j][0];l=this._events[j][1];k.off(l)}this._events=[]},show:function(i){this.picker.show();this.height=this.component?this.component.outerHeight():this.element.outerHeight();if(this.forceParse){this.update()}this.place();d(window).on("resize",d.proxy(this.place,this));if(i){i.stopPropagation();i.preventDefault()}this.isVisible=true;this.element.trigger({type:"show",date:this.date})},hide:function(){if(!this.isVisible){return}if(this.isInline){return}this.picker.hide();d(window).off("resize",this.place);this.viewMode=this.startViewMode;this.showMode();if(!this.isInput){d(document).off("mousedown",this.hide)}if(this.forceParse&&(this.isInput&&this.element.val()||this.hasInput&&this.element.find("input").val())){this.setValue()}this.isVisible=false;this.element.trigger({type:"hide",date:this.date})},remove:function(){this._detachEvents();d(document).off("mousedown",this.clickedOutside);this.picker.remove();delete this.picker;delete this.element.data().datetimepicker},getDate:function(){var i=this.getUTCDate();if(i===null){return null}return new Date(i.getTime()+(i.getTimezoneOffset()*60000))},getUTCDate:function(){return this.date},getInitialDate:function(){return this.initialDate},setInitialDate:function(i){this.initialDate=i},setDate:function(i){this.setUTCDate(new Date(i.getTime()-(i.getTimezoneOffset()*60000)))},setUTCDate:function(i){if(i>=this.startDate&&i<=this.endDate){this.date=i;this.setValue();this.viewDate=this.date;this.fill()}else{this.element.trigger({type:"outOfRange",date:i,startDate:this.startDate,endDate:this.endDate})}},setFormat:function(j){this.format=c.parseFormat(j,this.formatType);var i;if(this.isInput){i=this.element}else{if(this.component){i=this.element.find("input")}}if(i&&i.val()){this.setValue()}},setValue:function(){var i=this.getFormattedDate();if(!this.isInput){if(this.component){this.element.find("input").val(i)}this.element.data("date",i)}else{this.element.val(i)}if(this.linkField){d("#"+this.linkField).val(this.getFormattedDate(this.linkFormat))}},getFormattedDate:function(i){i=i||this.format;return c.formatDate(this.date,i,this.language,this.formatType,this.timezone)},setStartDate:function(i){this.startDate=i||this.startDate;if(this.startDate.valueOf()!==8639968443048000){this.startDate=c.parseDate(this.startDate,this.format,this.language,this.formatType,this.timezone)}this.update();this.updateNavArrows()},setEndDate:function(i){this.endDate=i||this.endDate;if(this.endDate.valueOf()!==8639968443048000){this.endDate=c.parseDate(this.endDate,this.format,this.language,this.formatType,this.timezone)}this.update();this.updateNavArrows()},setDatesDisabled:function(j){this.datesDisabled=j||[];if(!d.isArray(this.datesDisabled)){this.datesDisabled=this.datesDisabled.split(/,\s*/)}var i=this;this.datesDisabled=d.map(this.datesDisabled,function(k){return c.parseDate(k,i.format,i.language,i.formatType,i.timezone).toDateString()});this.update();this.updateNavArrows()},setTitle:function(i,j){return this.picker.find(i).find("th:eq(1)").text(this.title===false?j:this.title)},setDaysOfWeekDisabled:function(i){this.daysOfWeekDisabled=i||[];if(!d.isArray(this.daysOfWeekDisabled)){this.daysOfWeekDisabled=this.daysOfWeekDisabled.split(/,\s*/)}this.daysOfWeekDisabled=d.map(this.daysOfWeekDisabled,function(j){return parseInt(j,10)});this.update();this.updateNavArrows()},setMinutesDisabled:function(i){this.minutesDisabled=i||[];if(!d.isArray(this.minutesDisabled)){this.minutesDisabled=this.minutesDisabled.split(/,\s*/)}this.minutesDisabled=d.map(this.minutesDisabled,function(j){return parseInt(j,10)});this.update();this.updateNavArrows()},setHoursDisabled:function(i){this.hoursDisabled=i||[];if(!d.isArray(this.hoursDisabled)){this.hoursDisabled=this.hoursDisabled.split(/,\s*/)}this.hoursDisabled=d.map(this.hoursDisabled,function(j){return parseInt(j,10)});this.update();this.updateNavArrows()},place:function(){if(this.isInline){return}if(!this.zIndex){var j=0;d("div").each(function(){var o=parseInt(d(this).css("zIndex"),10);if(o>j){j=o}});this.zIndex=j+10}var n,m,l,k;if(this.container instanceof d){k=this.container.offset()}else{k=d(this.container).offset()}if(this.component){n=this.component.offset();l=n.left;if(this.pickerPosition==="bottom-left"||this.pickerPosition==="top-left"){l+=this.component.outerWidth()-this.picker.outerWidth()}}else{n=this.element.offset();l=n.left;if(this.pickerPosition==="bottom-left"||this.pickerPosition==="top-left"){l+=this.element.outerWidth()-this.picker.outerWidth()}}var i=document.body.clientWidth||window.innerWidth;if(l+220>i){l=i-220}if(this.pickerPosition==="top-left"||this.pickerPosition==="top-right"){m=n.top-this.picker.outerHeight()}else{m=n.top+this.height}m=m-k.top;l=l-k.left;this.picker.css({top:m,left:l,zIndex:this.zIndex})},hour_minute:"^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]",update:function(){var i,j=false;if(arguments&&arguments.length&&(typeof arguments[0]==="string"||arguments[0] instanceof Date)){i=arguments[0];j=true}else{i=(this.isInput?this.element.val():this.element.find("input").val())||this.element.data("date")||this.initialDate;if(typeof i==="string"){i=i.replace(/^\s+|\s+$/g,"")}}if(!i){i=new Date();j=false}if(typeof i==="string"){if(new RegExp(this.hour_minute).test(i)||new RegExp(this.hour_minute+":[0-5][0-9]").test(i)){i=this.getDate()}}this.date=c.parseDate(i,this.format,this.language,this.formatType,this.timezone);if(j){this.setValue()}if(this.date<this.startDate){this.viewDate=new Date(this.startDate)}else{if(this.date>this.endDate){this.viewDate=new Date(this.endDate)}else{this.viewDate=new Date(this.date)}}this.fill()},fillDow:function(){var i=this.weekStart,j="<tr>";while(i<this.weekStart+7){j+='<th class="dow">'+e[this.language].daysMin[(i++)%7]+"</th>"}j+="</tr>";this.picker.find(".datetimepicker-days thead").append(j)},fillMonths:function(){var l="";var m=new Date(this.viewDate);for(var k=0;k<12;k++){m.setUTCMonth(k);var j=this.onRenderMonth(m);l+='<span class="'+j.join(" ")+'">'+e[this.language].monthsShort[k]+"</span>"}this.picker.find(".datetimepicker-months td").html(l)},fill:function(){if(!this.date||!this.viewDate){return}var E=new Date(this.viewDate),t=E.getUTCFullYear(),G=E.getUTCMonth(),n=E.getUTCDate(),A=E.getUTCHours(),w=this.startDate.getUTCFullYear(),B=this.startDate.getUTCMonth(),p=this.endDate.getUTCFullYear(),x=this.endDate.getUTCMonth()+1,q=(new h(this.date.getUTCFullYear(),this.date.getUTCMonth(),this.date.getUTCDate())).valueOf(),D=new Date();this.setTitle(".datetimepicker-days",e[this.language].months[G]+" "+t);if(this.formatViewType==="time"){var k=this.getFormattedDate();this.setTitle(".datetimepicker-hours",k);this.setTitle(".datetimepicker-minutes",k)}else{this.setTitle(".datetimepicker-hours",n+" "+e[this.language].months[G]+" "+t);this.setTitle(".datetimepicker-minutes",n+" "+e[this.language].months[G]+" "+t)}this.picker.find("tfoot th.today").text(e[this.language].today||e.en.today).toggle(this.todayBtn!==false);this.picker.find("tfoot th.clear").text(e[this.language].clear||e.en.clear).toggle(this.clearBtn!==false);this.updateNavArrows();this.fillMonths();var I=h(t,G-1,28,0,0,0,0),z=c.getDaysInMonth(I.getUTCFullYear(),I.getUTCMonth());I.setUTCDate(z);I.setUTCDate(z-(I.getUTCDay()-this.weekStart+7)%7);var j=new Date(I);j.setUTCDate(j.getUTCDate()+42);j=j.valueOf();var r=[];var F;while(I.valueOf()<j){if(I.getUTCDay()===this.weekStart){r.push("<tr>")}F=this.onRenderDay(I);if(I.getUTCFullYear()<t||(I.getUTCFullYear()===t&&I.getUTCMonth()<G)){F.push("old")}else{if(I.getUTCFullYear()>t||(I.getUTCFullYear()===t&&I.getUTCMonth()>G)){F.push("new")}}if(this.todayHighlight&&I.getUTCFullYear()===D.getFullYear()&&I.getUTCMonth()===D.getMonth()&&I.getUTCDate()===D.getDate()){F.push("today")}if(I.valueOf()===q){F.push("active")}if((I.valueOf()+86400000)<=this.startDate||I.valueOf()>this.endDate||d.inArray(I.getUTCDay(),this.daysOfWeekDisabled)!==-1||d.inArray(I.toDateString(),this.datesDisabled)!==-1){F.push("disabled")}r.push('<td class="'+F.join(" ")+'">'+I.getUTCDate()+"</td>");if(I.getUTCDay()===this.weekEnd){r.push("</tr>")}I.setUTCDate(I.getUTCDate()+1)}this.picker.find(".datetimepicker-days tbody").empty().append(r.join(""));r=[];var u="",C="",s="";var l=this.hoursDisabled||[];E=new Date(this.viewDate);for(var y=0;y<24;y++){E.setUTCHours(y);F=this.onRenderHour(E);if(l.indexOf(y)!==-1){F.push("disabled")}var v=h(t,G,n,y);if((v.valueOf()+3600000)<=this.startDate||v.valueOf()>this.endDate){F.push("disabled")}else{if(A===y){F.push("active")}}if(this.showMeridian&&e[this.language].meridiem.length===2){C=(y<12?e[this.language].meridiem[0]:e[this.language].meridiem[1]);if(C!==s){if(s!==""){r.push("</fieldset>")}r.push('<fieldset class="hour"><legend>'+C.toUpperCase()+"</legend>")}s=C;u=(y%12?y%12:12);if(y<12){F.push("hour_am")}else{F.push("hour_pm")}r.push('<span class="'+F.join(" ")+'">'+u+"</span>");if(y===23){r.push("</fieldset>")}}else{u=y+":00";r.push('<span class="'+F.join(" ")+'">'+u+"</span>")}}this.picker.find(".datetimepicker-hours td").html(r.join(""));r=[];u="";C="";s="";var m=this.minutesDisabled||[];E=new Date(this.viewDate);for(var y=0;y<60;y+=this.minuteStep){if(m.indexOf(y)!==-1){continue}E.setUTCMinutes(y);E.setUTCSeconds(0);F=this.onRenderMinute(E);if(this.showMeridian&&e[this.language].meridiem.length===2){C=(A<12?e[this.language].meridiem[0]:e[this.language].meridiem[1]);if(C!==s){if(s!==""){r.push("</fieldset>")}r.push('<fieldset class="minute"><legend>'+C.toUpperCase()+"</legend>")}s=C;u=(A%12?A%12:12);r.push('<span class="'+F.join(" ")+'">'+u+":"+(y<10?"0"+y:y)+"</span>");if(y===59){r.push("</fieldset>")}}else{u=y+":00";r.push('<span class="'+F.join(" ")+'">'+A+":"+(y<10?"0"+y:y)+"</span>")}}this.picker.find(".datetimepicker-minutes td").html(r.join(""));var J=this.date.getUTCFullYear();var o=this.setTitle(".datetimepicker-months",t).end().find(".month").removeClass("active");if(J===t){o.eq(this.date.getUTCMonth()).addClass("active")}if(t<w||t>p){o.addClass("disabled")}if(t===w){o.slice(0,B).addClass("disabled")}if(t===p){o.slice(x).addClass("disabled")}r="";t=parseInt(t/10,10)*10;var H=this.setTitle(".datetimepicker-years",t+"-"+(t+9)).end().find("td");t-=1;E=new Date(this.viewDate);for(var y=-1;y<11;y++){E.setUTCFullYear(t);F=this.onRenderYear(E);if(y===-1||y===10){F.push(b)}r+='<span class="'+F.join(" ")+'">'+t+"</span>";t+=1}H.html(r);this.place()},updateNavArrows:function(){var m=new Date(this.viewDate),k=m.getUTCFullYear(),l=m.getUTCMonth(),j=m.getUTCDate(),i=m.getUTCHours();switch(this.viewMode){case 0:if(k<=this.startDate.getUTCFullYear()&&l<=this.startDate.getUTCMonth()&&j<=this.startDate.getUTCDate()&&i<=this.startDate.getUTCHours()){this.picker.find(".prev").css({visibility:"hidden"})}else{this.picker.find(".prev").css({visibility:"visible"})}if(k>=this.endDate.getUTCFullYear()&&l>=this.endDate.getUTCMonth()&&j>=this.endDate.getUTCDate()&&i>=this.endDate.getUTCHours()){this.picker.find(".next").css({visibility:"hidden"})}else{this.picker.find(".next").css({visibility:"visible"})}break;case 1:if(k<=this.startDate.getUTCFullYear()&&l<=this.startDate.getUTCMonth()&&j<=this.startDate.getUTCDate()){this.picker.find(".prev").css({visibility:"hidden"})}else{this.picker.find(".prev").css({visibility:"visible"})}if(k>=this.endDate.getUTCFullYear()&&l>=this.endDate.getUTCMonth()&&j>=this.endDate.getUTCDate()){this.picker.find(".next").css({visibility:"hidden"})}else{this.picker.find(".next").css({visibility:"visible"})}break;case 2:if(k<=this.startDate.getUTCFullYear()&&l<=this.startDate.getUTCMonth()){this.picker.find(".prev").css({visibility:"hidden"})}else{this.picker.find(".prev").css({visibility:"visible"})}if(k>=this.endDate.getUTCFullYear()&&l>=this.endDate.getUTCMonth()){this.picker.find(".next").css({visibility:"hidden"})}else{this.picker.find(".next").css({visibility:"visible"})}break;case 3:case 4:if(k<=this.startDate.getUTCFullYear()){this.picker.find(".prev").css({visibility:"hidden"})}else{this.picker.find(".prev").css({visibility:"visible"})}if(k>=this.endDate.getUTCFullYear()){this.picker.find(".next").css({visibility:"hidden"})}else{this.picker.find(".next").css({visibility:"visible"})}break}},mousewheel:function(j){j.preventDefault();j.stopPropagation();if(this.wheelPause){return}this.wheelPause=true;var i=j.originalEvent;var l=i.wheelDelta;var k=l>0?1:(l===0)?0:-1;if(this.wheelViewModeNavigationInverseDirection){k=-k}this.showMode(k);setTimeout(d.proxy(function(){this.wheelPause=false},this),this.wheelViewModeNavigationDelay)},click:function(m){m.stopPropagation();m.preventDefault();var n=d(m.target).closest("span, td, th, legend");if(n.is("."+this.icontype)){n=d(n).parent().closest("span, td, th, legend")}if(n.length===1){if(n.is(".disabled")){this.element.trigger({type:"outOfRange",date:this.viewDate,startDate:this.startDate,endDate:this.endDate});return}switch(n[0].nodeName.toLowerCase()){case"th":switch(n[0].className){case"switch":this.showMode(1);break;case"prev":case"next":var i=c.modes[this.viewMode].navStep*(n[0].className==="prev"?-1:1);switch(this.viewMode){case 0:this.viewDate=this.moveHour(this.viewDate,i);break;case 1:this.viewDate=this.moveDate(this.viewDate,i);break;case 2:this.viewDate=this.moveMonth(this.viewDate,i);break;case 3:case 4:this.viewDate=this.moveYear(this.viewDate,i);break}this.fill();this.element.trigger({type:n[0].className+":"+this.convertViewModeText(this.viewMode),date:this.viewDate,startDate:this.startDate,endDate:this.endDate});break;case"clear":this.reset();if(this.autoclose){this.hide()}break;case"today":var j=new Date();j=h(j.getFullYear(),j.getMonth(),j.getDate(),j.getHours(),j.getMinutes(),j.getSeconds(),0);if(j<this.startDate){j=this.startDate}else{if(j>this.endDate){j=this.endDate}}this.viewMode=this.startViewMode;this.showMode(0);this._setDate(j);this.fill();if(this.autoclose){this.hide()}break}break;case"span":if(!n.is(".disabled")){var p=this.viewDate.getUTCFullYear(),o=this.viewDate.getUTCMonth(),q=this.viewDate.getUTCDate(),r=this.viewDate.getUTCHours(),k=this.viewDate.getUTCMinutes(),s=this.viewDate.getUTCSeconds();if(n.is(".month")){this.viewDate.setUTCDate(1);o=n.parent().find("span").index(n);q=this.viewDate.getUTCDate();this.viewDate.setUTCMonth(o);this.element.trigger({type:"changeMonth",date:this.viewDate});if(this.viewSelect>=3){this._setDate(h(p,o,q,r,k,s,0))}}else{if(n.is(".year")){this.viewDate.setUTCDate(1);p=parseInt(n.text(),10)||0;this.viewDate.setUTCFullYear(p);this.element.trigger({type:"changeYear",date:this.viewDate});if(this.viewSelect>=4){this._setDate(h(p,o,q,r,k,s,0))}}else{if(n.is(".hour")){r=parseInt(n.text(),10)||0;if(n.hasClass("hour_am")||n.hasClass("hour_pm")){if(r===12&&n.hasClass("hour_am")){r=0}else{if(r!==12&&n.hasClass("hour_pm")){r+=12}}}this.viewDate.setUTCHours(r);this.element.trigger({type:"changeHour",date:this.viewDate});if(this.viewSelect>=1){this._setDate(h(p,o,q,r,k,s,0))}}else{if(n.is(".minute")){k=parseInt(n.text().substr(n.text().indexOf(":")+1),10)||0;this.viewDate.setUTCMinutes(k);this.element.trigger({type:"changeMinute",date:this.viewDate});if(this.viewSelect>=0){this._setDate(h(p,o,q,r,k,s,0))}}}}}if(this.viewMode!==0){var l=this.viewMode;this.showMode(-1);this.fill();if(l===this.viewMode&&this.autoclose){this.hide()}}else{this.fill();if(this.autoclose){this.hide()}}}break;case"td":if(n.is(".day")&&!n.is(".disabled")){var q=parseInt(n.text(),10)||1;var p=this.viewDate.getUTCFullYear(),o=this.viewDate.getUTCMonth(),r=this.viewDate.getUTCHours(),k=this.viewDate.getUTCMinutes(),s=this.viewDate.getUTCSeconds();if(n.is(".old")){if(o===0){o=11;p-=1}else{o-=1}}else{if(n.is(".new")){if(o===11){o=0;p+=1}else{o+=1}}}this.viewDate.setUTCFullYear(p);this.viewDate.setUTCMonth(o,q);this.element.trigger({type:"changeDay",date:this.viewDate});if(this.viewSelect>=2){this._setDate(h(p,o,q,r,k,s,0))}}var l=this.viewMode;this.showMode(-1);this.fill();if(l===this.viewMode&&this.autoclose){this.hide()}break}}},_setDate:function(i,k){if(!k||k==="date"){this.date=i}if(!k||k==="view"){this.viewDate=i}this.fill();this.setValue();var j;if(this.isInput){j=this.element}else{if(this.component){j=this.element.find("input")}}if(j){j.change()}this.element.trigger({type:"changeDate",date:this.getDate()});if(i===null){this.date=this.viewDate}},moveMinute:function(j,i){if(!i){return j}var k=new Date(j.valueOf());k.setUTCMinutes(k.getUTCMinutes()+(i*this.minuteStep));return k},moveHour:function(j,i){if(!i){return j}var k=new Date(j.valueOf());k.setUTCHours(k.getUTCHours()+i);return k},moveDate:function(j,i){if(!i){return j}var k=new Date(j.valueOf());k.setUTCDate(k.getUTCDate()+i);return k},moveMonth:function(j,k){if(!k){return j}var n=new Date(j.valueOf()),r=n.getUTCDate(),o=n.getUTCMonth(),m=Math.abs(k),q,p;k=k>0?1:-1;if(m===1){p=k===-1?function(){return n.getUTCMonth()===o}:function(){return n.getUTCMonth()!==q};q=o+k;n.setUTCMonth(q);if(q<0||q>11){q=(q+12)%12}}else{for(var l=0;l<m;l++){n=this.moveMonth(n,k)}q=n.getUTCMonth();n.setUTCDate(r);p=function(){return q!==n.getUTCMonth()}}while(p()){n.setUTCDate(--r);n.setUTCMonth(q)}return n},moveYear:function(j,i){return this.moveMonth(j,i*12)},dateWithinRange:function(i){return i>=this.startDate&&i<=this.endDate},keydown:function(o){if(this.picker.is(":not(:visible)")){if(o.keyCode===27){this.show()}return}var k=false,j,i,n;switch(o.keyCode){case 27:this.hide();o.preventDefault();break;case 37:case 39:if(!this.keyboardNavigation){break}j=o.keyCode===37?-1:1;var m=this.viewMode;if(o.ctrlKey){m+=2}else{if(o.shiftKey){m+=1}}if(m===4){i=this.moveYear(this.date,j);n=this.moveYear(this.viewDate,j)}else{if(m===3){i=this.moveMonth(this.date,j);n=this.moveMonth(this.viewDate,j)}else{if(m===2){i=this.moveDate(this.date,j);n=this.moveDate(this.viewDate,j)}else{if(m===1){i=this.moveHour(this.date,j);n=this.moveHour(this.viewDate,j)}else{if(m===0){i=this.moveMinute(this.date,j);n=this.moveMinute(this.viewDate,j)}}}}}if(this.dateWithinRange(i)){this.date=i;this.viewDate=n;this.setValue();this.update();o.preventDefault();k=true}break;case 38:case 40:if(!this.keyboardNavigation){break}j=o.keyCode===38?-1:1;m=this.viewMode;if(o.ctrlKey){m+=2}else{if(o.shiftKey){m+=1}}if(m===4){i=this.moveYear(this.date,j);n=this.moveYear(this.viewDate,j)}else{if(m===3){i=this.moveMonth(this.date,j);n=this.moveMonth(this.viewDate,j)}else{if(m===2){i=this.moveDate(this.date,j*7);n=this.moveDate(this.viewDate,j*7)}else{if(m===1){if(this.showMeridian){i=this.moveHour(this.date,j*6);n=this.moveHour(this.viewDate,j*6)}else{i=this.moveHour(this.date,j*4);n=this.moveHour(this.viewDate,j*4)}}else{if(m===0){i=this.moveMinute(this.date,j*4);n=this.moveMinute(this.viewDate,j*4)}}}}}if(this.dateWithinRange(i)){this.date=i;this.viewDate=n;this.setValue();this.update();o.preventDefault();k=true}break;case 13:if(this.viewMode!==0){var p=this.viewMode;this.showMode(-1);this.fill();if(p===this.viewMode&&this.autoclose){this.hide()}}else{this.fill();if(this.autoclose){this.hide()}}o.preventDefault();break;case 9:this.hide();break}if(k){var l;if(this.isInput){l=this.element}else{if(this.component){l=this.element.find("input")}}if(l){l.change()}this.element.trigger({type:"changeDate",date:this.getDate()})}},showMode:function(i){if(i){var j=Math.max(0,Math.min(c.modes.length-1,this.viewMode+i));if(j>=this.minView&&j<=this.maxView){this.element.trigger({type:"changeMode",date:this.viewDate,oldViewMode:this.viewMode,newViewMode:j});this.viewMode=j}}this.picker.find(">div").hide().filter(".datetimepicker-"+c.modes[this.viewMode].clsName).css("display","block");this.updateNavArrows()},reset:function(){this._setDate(null,"date")},convertViewModeText:function(i){switch(i){case 4:return"decade";case 3:return"year";case 2:return"month";case 1:return"day";case 0:return"hour"}}};var b=d.fn.datetimepicker;d.fn.datetimepicker=function(k){var i=Array.apply(null,arguments);i.shift();var j;this.each(function(){var n=d(this),m=n.data("datetimepicker"),l=typeof k==="object"&&k;if(!m){n.data("datetimepicker",(m=new g(this,d.extend({},d.fn.datetimepicker.defaults,l))))}if(typeof k==="string"&&typeof m[k]==="function"){j=m[k].apply(m,i);if(j!==f){return false}}});if(j!==f){return j}else{return this}};d.fn.datetimepicker.defaults={};d.fn.datetimepicker.Constructor=g;var e=d.fn.datetimepicker.dates={en:{days:["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],daysShort:["Sun","Mon","Tue","Wed","Thu","Fri","Sat","Sun"],daysMin:["Su","Mo","Tu","We","Th","Fr","Sa","Su"],months:["January","February","March","April","May","June","July","August","September","October","November","December"],monthsShort:["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],meridiem:["am","pm"],suffix:["st","nd","rd","th"],today:"Today",clear:"Clear"}};var c={modes:[{clsName:"minutes",navFnc:"Hours",navStep:1},{clsName:"hours",navFnc:"Date",navStep:1},{clsName:"days",navFnc:"Month",navStep:1},{clsName:"months",navFnc:"FullYear",navStep:1},{clsName:"years",navFnc:"FullYear",navStep:10}],isLeapYear:function(i){return(((i%4===0)&&(i%100!==0))||(i%400===0))},getDaysInMonth:function(i,j){return[31,(c.isLeapYear(i)?29:28),31,30,31,30,31,31,30,31,30,31][j]},getDefaultFormat:function(i,j){if(i==="standard"){if(j==="input"){return"yyyy-mm-dd hh:ii"}else{return"yyyy-mm-dd hh:ii:ss"}}else{if(i==="php"){if(j==="input"){return"Y-m-d H:i"}else{return"Y-m-d H:i:s"}}else{throw new Error("Invalid format type.")}}},validParts:function(i){if(i==="standard"){return/t|hh?|HH?|p|P|z|Z|ii?|ss?|dd?|DD?|mm?|MM?|yy(?:yy)?/g}else{if(i==="php"){return/[dDjlNwzFmMnStyYaABgGhHis]/g}else{throw new Error("Invalid format type.")}}},nonpunctuation:/[^ -\/:-@\[-`{-~\t\n\rTZ]+/g,parseFormat:function(l,j){var i=l.replace(this.validParts(j),"\0").split("\0"),k=l.match(this.validParts(j));if(!i||!i.length||!k||k.length===0){throw new Error("Invalid date format.")}return{separators:i,parts:k}},parseDate:function(A,y,v,j,r){if(A instanceof Date){var u=new Date(A.valueOf()-A.getTimezoneOffset()*60000);u.setMilliseconds(0);return u}if(/^\d{4}\-\d{1,2}\-\d{1,2}$/.test(A)){y=this.parseFormat("yyyy-mm-dd",j)}if(/^\d{4}\-\d{1,2}\-\d{1,2}[T ]\d{1,2}\:\d{1,2}$/.test(A)){y=this.parseFormat("yyyy-mm-dd hh:ii",j)}if(/^\d{4}\-\d{1,2}\-\d{1,2}[T ]\d{1,2}\:\d{1,2}\:\d{1,2}[Z]{0,1}$/.test(A)){y=this.parseFormat("yyyy-mm-dd hh:ii:ss",j)}if(/^[-+]\d+[dmwy]([\s,]+[-+]\d+[dmwy])*$/.test(A)){var l=/([-+]\d+)([dmwy])/,q=A.match(/([-+]\d+)([dmwy])/g),t,p;A=new Date();for(var x=0;x<q.length;x++){t=l.exec(q[x]);p=parseInt(t[1]);switch(t[2]){case"d":A.setUTCDate(A.getUTCDate()+p);break;case"m":A=g.prototype.moveMonth.call(g.prototype,A,p);break;case"w":A.setUTCDate(A.getUTCDate()+p*7);break;case"y":A=g.prototype.moveYear.call(g.prototype,A,p);break}}return h(A.getUTCFullYear(),A.getUTCMonth(),A.getUTCDate(),A.getUTCHours(),A.getUTCMinutes(),A.getUTCSeconds(),0)}var q=A&&A.toString().match(this.nonpunctuation)||[],A=new Date(0,0,0,0,0,0,0),m={},z=["hh","h","ii","i","ss","s","yyyy","yy","M","MM","m","mm","D","DD","d","dd","H","HH","p","P","z","Z"],o={hh:function(s,i){return s.setUTCHours(i)},h:function(s,i){return s.setUTCHours(i)},HH:function(s,i){return s.setUTCHours(i===12?0:i)},H:function(s,i){return s.setUTCHours(i===12?0:i)},ii:function(s,i){return s.setUTCMinutes(i)},i:function(s,i){return s.setUTCMinutes(i)},ss:function(s,i){return s.setUTCSeconds(i)},s:function(s,i){return s.setUTCSeconds(i)},yyyy:function(s,i){return s.setUTCFullYear(i)},yy:function(s,i){return s.setUTCFullYear(2000+i)},m:function(s,i){i-=1;while(i<0){i+=12}i%=12;s.setUTCMonth(i);while(s.getUTCMonth()!==i){if(isNaN(s.getUTCMonth())){return s}else{s.setUTCDate(s.getUTCDate()-1)}}return s},d:function(s,i){return s.setUTCDate(i)},p:function(s,i){return s.setUTCHours(i===1?s.getUTCHours()+12:s.getUTCHours())},z:function(){return r}},B,k,t;o.M=o.MM=o.mm=o.m;o.dd=o.d;o.P=o.p;o.Z=o.z;A=h(A.getFullYear(),A.getMonth(),A.getDate(),A.getHours(),A.getMinutes(),A.getSeconds());if(q.length===y.parts.length){for(var x=0,w=y.parts.length;x<w;x++){B=parseInt(q[x],10);t=y.parts[x];if(isNaN(B)){switch(t){case"MM":k=d(e[v].months).filter(function(){var i=this.slice(0,q[x].length),s=q[x].slice(0,i.length);return i===s});B=d.inArray(k[0],e[v].months)+1;break;case"M":k=d(e[v].monthsShort).filter(function(){var i=this.slice(0,q[x].length),s=q[x].slice(0,i.length);return i.toLowerCase()===s.toLowerCase()});B=d.inArray(k[0],e[v].monthsShort)+1;break;case"p":case"P":B=d.inArray(q[x].toLowerCase(),e[v].meridiem);break;case"z":case"Z":r;break}}m[t]=B}for(var x=0,n;x<z.length;x++){n=z[x];if(n in m&&!isNaN(m[n])){o[n](A,m[n])}}}return A},formatDate:function(l,q,m,p,o){if(l===null){return""}var k;if(p==="standard"){k={t:l.getTime(),yy:l.getUTCFullYear().toString().substring(2),yyyy:l.getUTCFullYear(),m:l.getUTCMonth()+1,M:e[m].monthsShort[l.getUTCMonth()],MM:e[m].months[l.getUTCMonth()],d:l.getUTCDate(),D:e[m].daysShort[l.getUTCDay()],DD:e[m].days[l.getUTCDay()],p:(e[m].meridiem.length===2?e[m].meridiem[l.getUTCHours()<12?0:1]:""),h:l.getUTCHours(),i:l.getUTCMinutes(),s:l.getUTCSeconds(),z:o};if(e[m].meridiem.length===2){k.H=(k.h%12===0?12:k.h%12)}else{k.H=k.h}k.HH=(k.H<10?"0":"")+k.H;k.P=k.p.toUpperCase();k.Z=k.z;k.hh=(k.h<10?"0":"")+k.h;k.ii=(k.i<10?"0":"")+k.i;k.ss=(k.s<10?"0":"")+k.s;k.dd=(k.d<10?"0":"")+k.d;k.mm=(k.m<10?"0":"")+k.m}else{if(p==="php"){k={y:l.getUTCFullYear().toString().substring(2),Y:l.getUTCFullYear(),F:e[m].months[l.getUTCMonth()],M:e[m].monthsShort[l.getUTCMonth()],n:l.getUTCMonth()+1,t:c.getDaysInMonth(l.getUTCFullYear(),l.getUTCMonth()),j:l.getUTCDate(),l:e[m].days[l.getUTCDay()],D:e[m].daysShort[l.getUTCDay()],w:l.getUTCDay(),N:(l.getUTCDay()===0?7:l.getUTCDay()),S:(l.getUTCDate()%10<=e[m].suffix.length?e[m].suffix[l.getUTCDate()%10-1]:""),a:(e[m].meridiem.length===2?e[m].meridiem[l.getUTCHours()<12?0:1]:""),g:(l.getUTCHours()%12===0?12:l.getUTCHours()%12),G:l.getUTCHours(),i:l.getUTCMinutes(),s:l.getUTCSeconds()};k.m=(k.n<10?"0":"")+k.n;k.d=(k.j<10?"0":"")+k.j;k.A=k.a.toString().toUpperCase();k.h=(k.g<10?"0":"")+k.g;k.H=(k.G<10?"0":"")+k.G;k.i=(k.i<10?"0":"")+k.i;k.s=(k.s<10?"0":"")+k.s}else{throw new Error("Invalid format type.")}}var l=[],r=d.extend([],q.separators);for(var n=0,j=q.parts.length;n<j;n++){if(r.length){l.push(r.shift())}l.push(k[q.parts[n]])}if(r.length){l.push(r.shift())}return l.join("")},convertViewMode:function(i){switch(i){case 4:case"decade":i=4;break;case 3:case"year":i=3;break;case 2:case"month":i=2;break;case 1:case"day":i=1;break;case 0:case"hour":i=0;break}return i},headTemplate:'<thead><tr><th class="prev"><i class="{iconType} {leftArrow}"/></th><th colspan="5" class="switch"></th><th class="next"><i class="{iconType} {rightArrow}"/></th></tr></thead>',headTemplateV3:'<thead><tr><th class="prev"><span class="{iconType} {leftArrow}"></span> </th><th colspan="5" class="switch"></th><th class="next"><span class="{iconType} {rightArrow}"></span> </th></tr></thead>',contTemplate:'<tbody><tr><td colspan="7"></td></tr></tbody>',footTemplate:'<tfoot><tr><th colspan="7" class="today"></th></tr><tr><th colspan="7" class="clear"></th></tr></tfoot>'};c.template='<div class="datetimepicker"><div class="datetimepicker-minutes"><table class=" table-condensed">'+c.headTemplate+c.contTemplate+c.footTemplate+'</table></div><div class="datetimepicker-hours"><table class=" table-condensed">'+c.headTemplate+c.contTemplate+c.footTemplate+'</table></div><div class="datetimepicker-days"><table class=" table-condensed">'+c.headTemplate+"<tbody></tbody>"+c.footTemplate+'</table></div><div class="datetimepicker-months"><table class="table-condensed">'+c.headTemplate+c.contTemplate+c.footTemplate+'</table></div><div class="datetimepicker-years"><table class="table-condensed">'+c.headTemplate+c.contTemplate+c.footTemplate+"</table></div></div>";c.templateV3='<div class="datetimepicker"><div class="datetimepicker-minutes"><table class=" table-condensed">'+c.headTemplateV3+c.contTemplate+c.footTemplate+'</table></div><div class="datetimepicker-hours"><table class=" table-condensed">'+c.headTemplateV3+c.contTemplate+c.footTemplate+'</table></div><div class="datetimepicker-days"><table class=" table-condensed">'+c.headTemplateV3+"<tbody></tbody>"+c.footTemplate+'</table></div><div class="datetimepicker-months"><table class="table-condensed">'+c.headTemplateV3+c.contTemplate+c.footTemplate+'</table></div><div class="datetimepicker-years"><table class="table-condensed">'+c.headTemplateV3+c.contTemplate+c.footTemplate+"</table></div></div>";d.fn.datetimepicker.DPGlobal=c;d.fn.datetimepicker.noConflict=function(){d.fn.datetimepicker=b;return this};d(document).on("focus.datetimepicker.data-api click.datetimepicker.data-api",'[data-provide="datetimepicker"]',function(j){var i=d(this);if(i.data("datetimepicker")){return}j.preventDefault();i.datetimepicker("show")});d(function(){d('[data-provide="datetimepicker-inline"]').datetimepicker()})}));
/* ========================================================================
 * bootstrap-tour - v0.10.1
 * http://bootstraptour.com
 * ========================================================================
 * Copyright 2012-2013 Ulrich Sossou
 *
 * ========================================================================
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * ========================================================================
 */


!function(t,e){var o,n;return n=e.document,o=function(){function o(o){var n;try{n=e.localStorage}catch(i){n=!1}this._options=t.extend({name:"tour",steps:[],container:"body",autoscroll:!0,keyboard:!0,storage:n,debug:!1,backdrop:!1,backdropPadding:0,redirect:!0,orphan:!1,duration:!1,delay:!1,basePath:"",template:'<div class="popover" role="tooltip"> <div class="arrow"></div> <h3 class="popover-title"></h3> <div class="popover-content"></div> <div class="popover-navigation"> <div class="btn-group"> <button class="btn btn-sm btn-default" data-role="prev">&laquo; Prev</button> <button class="btn btn-sm btn-default" data-role="next">Next &raquo;</button> <button class="btn btn-sm btn-default" data-role="pause-resume" data-pause-text="Pause" data-resume-text="Resume">Pause</button> </div> <button class="btn btn-sm btn-default" data-role="end">End tour</button> </div> </div>',afterSetState:function(){},afterGetState:function(){},afterRemoveState:function(){},onStart:function(){},onEnd:function(){},onShow:function(){},onShown:function(){},onHide:function(){},onHidden:function(){},onNext:function(){},onPrev:function(){},onPause:function(){},onResume:function(){}},o),this._force=!1,this._inited=!1,this.backdrop={overlay:null,$element:null,$background:null,backgroundShown:!1,overlayElementShown:!1}}return o.prototype.addSteps=function(t){var e,o,n;for(o=0,n=t.length;n>o;o++)e=t[o],this.addStep(e);return this},o.prototype.addStep=function(t){return this._options.steps.push(t),this},o.prototype.getStep=function(e){return null!=this._options.steps[e]?t.extend({id:"step-"+e,path:"",placement:"right",title:"",content:"<p></p>",next:e===this._options.steps.length-1?-1:e+1,prev:e-1,animation:!0,container:this._options.container,autoscroll:this._options.autoscroll,backdrop:this._options.backdrop,backdropPadding:this._options.backdropPadding,redirect:this._options.redirect,orphan:this._options.orphan,duration:this._options.duration,delay:this._options.delay,template:this._options.template,onShow:this._options.onShow,onShown:this._options.onShown,onHide:this._options.onHide,onHidden:this._options.onHidden,onNext:this._options.onNext,onPrev:this._options.onPrev,onPause:this._options.onPause,onResume:this._options.onResume},this._options.steps[e]):void 0},o.prototype.init=function(t){return this._force=t,this.ended()?(this._debug("Tour ended, init prevented."),this):(this.setCurrentStep(),this._initMouseNavigation(),this._initKeyboardNavigation(),this._onResize(function(t){return function(){return t.showStep(t._current)}}(this)),null!==this._current&&this.showStep(this._current),this._inited=!0,this)},o.prototype.start=function(t){var e;return null==t&&(t=!1),this._inited||this.init(t),null===this._current&&(e=this._makePromise(null!=this._options.onStart?this._options.onStart(this):void 0),this._callOnPromiseDone(e,this.showStep,0)),this},o.prototype.next=function(){var t;return t=this.hideStep(this._current),this._callOnPromiseDone(t,this._showNextStep)},o.prototype.prev=function(){var t;return t=this.hideStep(this._current),this._callOnPromiseDone(t,this._showPrevStep)},o.prototype.goTo=function(t){var e;return e=this.hideStep(this._current),this._callOnPromiseDone(e,this.showStep,t)},o.prototype.end=function(){var o,i;return o=function(o){return function(){return t(n).off("click.tour-"+o._options.name),t(n).off("keyup.tour-"+o._options.name),t(e).off("resize.tour-"+o._options.name),o._setState("end","yes"),o._inited=!1,o._force=!1,o._clearTimer(),null!=o._options.onEnd?o._options.onEnd(o):void 0}}(this),i=this.hideStep(this._current),this._callOnPromiseDone(i,o)},o.prototype.ended=function(){return!this._force&&!!this._getState("end")},o.prototype.restart=function(){return this._removeState("current_step"),this._removeState("end"),this.start()},o.prototype.pause=function(){var t;return t=this.getStep(this._current),t&&t.duration?(this._paused=!0,this._duration-=(new Date).getTime()-this._start,e.clearTimeout(this._timer),this._debug("Paused/Stopped step "+(this._current+1)+" timer ("+this._duration+" remaining)."),null!=t.onPause?t.onPause(this,this._duration):void 0):this},o.prototype.resume=function(){var t;return t=this.getStep(this._current),t&&t.duration?(this._paused=!1,this._start=(new Date).getTime(),this._duration=this._duration||t.duration,this._timer=e.setTimeout(function(t){return function(){return t._isLast()?t.next():t.end()}}(this),this._duration),this._debug("Started step "+(this._current+1)+" timer with duration "+this._duration),null!=t.onResume&&this._duration!==t.duration?t.onResume(this,this._duration):void 0):this},o.prototype.hideStep=function(e){var o,n,i;return(i=this.getStep(e))?(this._clearTimer(),n=this._makePromise(null!=i.onHide?i.onHide(this,e):void 0),o=function(o){return function(){var n;return n=t(i.element),n.data("bs.popover")||n.data("popover")||(n=t("body")),n.popover("destroy").removeClass("tour-"+o._options.name+"-element tour-"+o._options.name+"-"+e+"-element"),i.reflex&&n.removeClass("tour-step-element-reflex").off(""+o._reflexEvent(i.reflex)+".tour-"+o._options.name),i.backdrop&&o._hideBackdrop(),null!=i.onHidden?i.onHidden(o):void 0}}(this),this._callOnPromiseDone(n,o),n):void 0},o.prototype.showStep=function(t){var o,i,r,s;return this.ended()?(this._debug("Tour ended, showStep prevented."),this):(s=this.getStep(t))?(r=t<this._current,o=this._makePromise(null!=s.onShow?s.onShow(this,t):void 0),i=function(e){return function(){var o,i,a;if(e.setCurrentStep(t),i=function(){switch({}.toString.call(s.path)){case"[object Function]":return s.path();case"[object String]":return this._options.basePath+s.path;default:return s.path}}.call(e),o=[n.location.pathname,n.location.hash].join(""),e._isRedirect(i,o))return e._redirect(s,i),void 0;if(e._isOrphan(s)){if(!s.orphan)return e._debug("Skip the orphan step "+(e._current+1)+".\nOrphan option is false and the element does not exist or is hidden."),r?e._showPrevStep():e._showNextStep(),void 0;e._debug("Show the orphan step "+(e._current+1)+". Orphans option is true.")}return s.backdrop&&e._showBackdrop(e._isOrphan(s)?void 0:s.element),a=function(){return e.getCurrentStep()===t?(null!=s.element&&s.backdrop&&e._showOverlayElement(s),e._showPopover(s,t),null!=s.onShown&&s.onShown(e),e._debug("Step "+(e._current+1)+" of "+e._options.steps.length)):void 0},s.autoscroll?e._scrollIntoView(s.element,a):a(),s.duration?e.resume():void 0}}(this),s.delay?(this._debug("Wait "+s.delay+" milliseconds to show the step "+(this._current+1)),e.setTimeout(function(t){return function(){return t._callOnPromiseDone(o,i)}}(this),s.delay)):this._callOnPromiseDone(o,i),o):void 0},o.prototype.getCurrentStep=function(){return this._current},o.prototype.setCurrentStep=function(t){return null!=t?(this._current=t,this._setState("current_step",t)):(this._current=this._getState("current_step"),this._current=null===this._current?null:parseInt(this._current,10)),this},o.prototype._setState=function(t,e){var o,n;if(this._options.storage){n=""+this._options.name+"_"+t;try{this._options.storage.setItem(n,e)}catch(i){o=i,o.code===DOMException.QUOTA_EXCEEDED_ERR&&this._debug("LocalStorage quota exceeded. State storage failed.")}return this._options.afterSetState(n,e)}return null==this._state&&(this._state={}),this._state[t]=e},o.prototype._removeState=function(t){var e;return this._options.storage?(e=""+this._options.name+"_"+t,this._options.storage.removeItem(e),this._options.afterRemoveState(e)):null!=this._state?delete this._state[t]:void 0},o.prototype._getState=function(t){var e,o;return this._options.storage?(e=""+this._options.name+"_"+t,o=this._options.storage.getItem(e)):null!=this._state&&(o=this._state[t]),(void 0===o||"null"===o)&&(o=null),this._options.afterGetState(t,o),o},o.prototype._showNextStep=function(){var t,e,o;return o=this.getStep(this._current),e=function(t){return function(){return t.showStep(o.next)}}(this),t=this._makePromise(null!=o.onNext?o.onNext(this):void 0),this._callOnPromiseDone(t,e)},o.prototype._showPrevStep=function(){var t,e,o;return o=this.getStep(this._current),e=function(t){return function(){return t.showStep(o.prev)}}(this),t=this._makePromise(null!=o.onPrev?o.onPrev(this):void 0),this._callOnPromiseDone(t,e)},o.prototype._debug=function(t){return this._options.debug?e.console.log("Bootstrap Tour '"+this._options.name+"' | "+t):void 0},o.prototype._isRedirect=function(t,e){return null!=t&&""!==t&&("[object RegExp]"==={}.toString.call(t)&&!t.test(e)||"[object String]"==={}.toString.call(t)&&t.replace(/\?.*$/,"").replace(/\/?$/,"")!==e.replace(/\/?$/,""))},o.prototype._redirect=function(e,o){return t.isFunction(e.redirect)?e.redirect.call(this,o):e.redirect===!0?(this._debug("Redirect to "+o),n.location.href=o):void 0},o.prototype._isOrphan=function(e){return null==e.element||!t(e.element).length||t(e.element).is(":hidden")&&"http://www.w3.org/2000/svg"!==t(e.element)[0].namespaceURI},o.prototype._isLast=function(){return this._current<this._options.steps.length-1},o.prototype._showPopover=function(e,o){var n,i,r,s;return t(".tour-"+this._options.name).remove(),s=t.extend({},this._options),r=this._isOrphan(e),e.template=this._template(e,o),r&&(e.element="body",e.placement="top"),n=t(e.element),n.addClass("tour-"+this._options.name+"-element tour-"+this._options.name+"-"+o+"-element"),e.options&&t.extend(s,e.options),e.reflex&&!r&&(n.addClass("tour-step-element-reflex"),n.off(""+this._reflexEvent(e.reflex)+".tour-"+this._options.name),n.on(""+this._reflexEvent(e.reflex)+".tour-"+this._options.name,function(t){return function(){return t._isLast()?t.next():t.end()}}(this))),n.popover({placement:e.placement,trigger:"manual",title:e.title,content:e.content,html:!0,animation:e.animation,container:e.container,template:e.template,selector:e.element}).popover("show"),i=n.data("bs.popover")?n.data("bs.popover").tip():n.data("popover").tip(),i.attr("id",e.id),this._reposition(i,e),r?this._center(i):void 0},o.prototype._template=function(e,o){var n,i,r,s,a;return a=t.isFunction(e.template)?t(e.template(o,e)):t(e.template),n=a.find(".popover-navigation"),r=n.find('[data-role="prev"]'),i=n.find('[data-role="next"]'),s=n.find('[data-role="pause-resume"]'),this._isOrphan(e)&&a.addClass("orphan"),a.addClass("tour-"+this._options.name+" tour-"+this._options.name+"-"+o),e.prev<0&&r.addClass("disabled"),e.next<0&&i.addClass("disabled"),e.duration||s.remove(),a.clone().wrap("<div>").parent().html()},o.prototype._reflexEvent=function(t){return"[object Boolean]"==={}.toString.call(t)?"click":t},o.prototype._reposition=function(e,o){var i,r,s,a,p,u,h;if(a=e[0].offsetWidth,r=e[0].offsetHeight,h=e.offset(),p=h.left,u=h.top,i=t(n).outerHeight()-h.top-e.outerHeight(),0>i&&(h.top=h.top+i),s=t("html").outerWidth()-h.left-e.outerWidth(),0>s&&(h.left=h.left+s),h.top<0&&(h.top=0),h.left<0&&(h.left=0),e.offset(h),"bottom"===o.placement||"top"===o.placement){if(p!==h.left)return this._replaceArrow(e,2*(h.left-p),a,"left")}else if(u!==h.top)return this._replaceArrow(e,2*(h.top-u),r,"top")},o.prototype._center=function(o){return o.css("top",t(e).outerHeight()/2-o.outerHeight()/2)},o.prototype._replaceArrow=function(t,e,o,n){return t.find(".arrow").css(n,e?50*(1-e/o)+"%":"")},o.prototype._scrollIntoView=function(o,n){var i,r,s,a,p,u;return i=t(o),i.length?(r=t(e),a=i.offset().top,u=r.height(),p=Math.max(0,a-u/2),this._debug("Scroll into view. ScrollTop: "+p+". Element offset: "+a+". Window height: "+u+"."),s=0,t("body, html").stop(!0,!0).animate({scrollTop:Math.ceil(p)},function(t){return function(){return 2===++s?(n(),t._debug("Scroll into view.\nAnimation end element offset: "+i.offset().top+".\nWindow height: "+r.height()+".")):void 0}}(this))):n()},o.prototype._onResize=function(o,n){return t(e).on("resize.tour-"+this._options.name,function(){return clearTimeout(n),n=setTimeout(o,100)})},o.prototype._initMouseNavigation=function(){var e;return e=this,t(n).off("click.tour-"+this._options.name,".popover.tour-"+this._options.name+" *[data-role='prev']").off("click.tour-"+this._options.name,".popover.tour-"+this._options.name+" *[data-role='next']").off("click.tour-"+this._options.name,".popover.tour-"+this._options.name+" *[data-role='end']").off("click.tour-"+this._options.name,".popover.tour-"+this._options.name+" *[data-role='pause-resume']").on("click.tour-"+this._options.name,".popover.tour-"+this._options.name+" *[data-role='next']",function(t){return function(e){return e.preventDefault(),t.next()}}(this)).on("click.tour-"+this._options.name,".popover.tour-"+this._options.name+" *[data-role='prev']",function(t){return function(e){return e.preventDefault(),t.prev()}}(this)).on("click.tour-"+this._options.name,".popover.tour-"+this._options.name+" *[data-role='end']",function(t){return function(e){return e.preventDefault(),t.end()}}(this)).on("click.tour-"+this._options.name,".popover.tour-"+this._options.name+" *[data-role='pause-resume']",function(o){var n;return o.preventDefault(),n=t(this),n.text(e._paused?n.data("pause-text"):n.data("resume-text")),e._paused?e.resume():e.pause()})},o.prototype._initKeyboardNavigation=function(){return this._options.keyboard?t(n).on("keyup.tour-"+this._options.name,function(t){return function(e){if(e.which)switch(e.which){case 39:return e.preventDefault(),t._isLast()?t.next():t.end();case 37:if(e.preventDefault(),t._current>0)return t.prev();break;case 27:return e.preventDefault(),t.end()}}}(this)):void 0},o.prototype._makePromise=function(e){return e&&t.isFunction(e.then)?e:null},o.prototype._callOnPromiseDone=function(t,e,o){return t?t.then(function(t){return function(){return e.call(t,o)}}(this)):e.call(this,o)},o.prototype._showBackdrop=function(){return this.backdrop.backgroundShown?void 0:(this.backdrop=t("<div>",{"class":"tour-backdrop"}),this.backdrop.backgroundShown=!0,t("body").append(this.backdrop))},o.prototype._hideBackdrop=function(){return this._hideOverlayElement(),this._hideBackground()},o.prototype._hideBackground=function(){return this.backdrop?(this.backdrop.remove(),this.backdrop.overlay=null,this.backdrop.backgroundShown=!1):void 0},o.prototype._showOverlayElement=function(e){var o,n;return o=t(e.element),o&&0!==o.length&&!this.backdrop.overlayElementShown?(this.backdrop.overlayElementShown=!0,this.backdrop.$element=o.addClass("tour-step-backdrop"),this.backdrop.$background=t("<div>",{"class":"tour-step-background"}),n={width:o.innerWidth(),height:o.innerHeight(),offset:o.offset()},this.backdrop.$background.appendTo("body"),e.backdropPadding&&(n=this._applyBackdropPadding(e.backdropPadding,n)),this.backdrop.$background.width(n.width).height(n.height).offset(n.offset)):void 0},o.prototype._hideOverlayElement=function(){return this.backdrop.overlayElementShown?(this.backdrop.$element.removeClass("tour-step-backdrop"),this.backdrop.$background.remove(),this.backdrop.$element=null,this.backdrop.$background=null,this.backdrop.overlayElementShown=!1):void 0},o.prototype._applyBackdropPadding=function(t,e){return"object"==typeof t?(null==t.top&&(t.top=0),null==t.right&&(t.right=0),null==t.bottom&&(t.bottom=0),null==t.left&&(t.left=0),e.offset.top=e.offset.top-t.top,e.offset.left=e.offset.left-t.left,e.width=e.width+t.left+t.right,e.height=e.height+t.top+t.bottom):(e.offset.top=e.offset.top-t,e.offset.left=e.offset.left-t,e.width=e.width+2*t,e.height=e.height+2*t),e},o.prototype._clearTimer=function(){return e.clearTimeout(this._timer),this._timer=null,this._duration=null},o}(),e.Tour=o}(jQuery,window);
/*! X-editable - v1.5.1 
* In-place editing with Twitter Bootstrap, jQuery UI or pure jQuery
* http://github.com/vitalets/x-editable
* Copyright (c) 2013 Vitaliy Potapov; Licensed MIT */

!function(a){"use strict";var b=function(b,c){this.options=a.extend({},a.fn.editableform.defaults,c),this.$div=a(b),this.options.scope||(this.options.scope=this)};b.prototype={constructor:b,initInput:function(){this.input=this.options.input,this.value=this.input.str2value(this.options.value),this.input.prerender()},initTemplate:function(){this.$form=a(a.fn.editableform.template)},initButtons:function(){var b=this.$form.find(".editable-buttons");b.append(a.fn.editableform.buttons),"bottom"===this.options.showbuttons&&b.addClass("editable-buttons-bottom")},render:function(){this.$loading=a(a.fn.editableform.loading),this.$div.empty().append(this.$loading),this.initTemplate(),this.options.showbuttons?this.initButtons():this.$form.find(".editable-buttons").remove(),this.showLoading(),this.isSaving=!1,this.$div.triggerHandler("rendering"),this.initInput(),this.$form.find("div.editable-input").append(this.input.$tpl),this.$div.append(this.$form),a.when(this.input.render()).then(a.proxy(function(){if(this.options.showbuttons||this.input.autosubmit(),this.$form.find(".editable-cancel").click(a.proxy(this.cancel,this)),this.input.error)this.error(this.input.error),this.$form.find(".editable-submit").attr("disabled",!0),this.input.$input.attr("disabled",!0),this.$form.submit(function(a){a.preventDefault()});else{this.error(!1),this.input.$input.removeAttr("disabled"),this.$form.find(".editable-submit").removeAttr("disabled");var b=null===this.value||void 0===this.value||""===this.value?this.options.defaultValue:this.value;this.input.value2input(b),this.$form.submit(a.proxy(this.submit,this))}this.$div.triggerHandler("rendered"),this.showForm(),this.input.postrender&&this.input.postrender()},this))},cancel:function(){this.$div.triggerHandler("cancel")},showLoading:function(){var a,b;this.$form?(a=this.$form.outerWidth(),b=this.$form.outerHeight(),a&&this.$loading.width(a),b&&this.$loading.height(b),this.$form.hide()):(a=this.$loading.parent().width(),a&&this.$loading.width(a)),this.$loading.show()},showForm:function(a){this.$loading.hide(),this.$form.show(),a!==!1&&this.input.activate(),this.$div.triggerHandler("show")},error:function(b){var c,d=this.$form.find(".control-group"),e=this.$form.find(".editable-error-block");if(b===!1)d.removeClass(a.fn.editableform.errorGroupClass),e.removeClass(a.fn.editableform.errorBlockClass).empty().hide();else{if(b){c=(""+b).split("\n");for(var f=0;f<c.length;f++)c[f]=a("<div>").text(c[f]).html();b=c.join("<br>")}d.addClass(a.fn.editableform.errorGroupClass),e.addClass(a.fn.editableform.errorBlockClass).html(b).show()}},submit:function(b){b.stopPropagation(),b.preventDefault();var c=this.input.input2value(),d=this.validate(c);if("object"===a.type(d)&&void 0!==d.newValue){if(c=d.newValue,this.input.value2input(c),"string"==typeof d.msg)return this.error(d.msg),this.showForm(),void 0}else if(d)return this.error(d),this.showForm(),void 0;if(!this.options.savenochange&&this.input.value2str(c)==this.input.value2str(this.value))return this.$div.triggerHandler("nochange"),void 0;var e=this.input.value2submit(c);this.isSaving=!0,a.when(this.save(e)).done(a.proxy(function(a){this.isSaving=!1;var b="function"==typeof this.options.success?this.options.success.call(this.options.scope,a,c):null;return b===!1?(this.error(!1),this.showForm(!1),void 0):"string"==typeof b?(this.error(b),this.showForm(),void 0):(b&&"object"==typeof b&&b.hasOwnProperty("newValue")&&(c=b.newValue),this.error(!1),this.value=c,this.$div.triggerHandler("save",{newValue:c,submitValue:e,response:a}),void 0)},this)).fail(a.proxy(function(a){this.isSaving=!1;var b;b="function"==typeof this.options.error?this.options.error.call(this.options.scope,a,c):"string"==typeof a?a:a.responseText||a.statusText||"Unknown error!",this.error(b),this.showForm()},this))},save:function(b){this.options.pk=a.fn.editableutils.tryParseJson(this.options.pk,!0);var c,d="function"==typeof this.options.pk?this.options.pk.call(this.options.scope):this.options.pk,e=!!("function"==typeof this.options.url||this.options.url&&("always"===this.options.send||"auto"===this.options.send&&null!==d&&void 0!==d));return e?(this.showLoading(),c={name:this.options.name||"",value:b,pk:d},"function"==typeof this.options.params?c=this.options.params.call(this.options.scope,c):(this.options.params=a.fn.editableutils.tryParseJson(this.options.params,!0),a.extend(c,this.options.params)),"function"==typeof this.options.url?this.options.url.call(this.options.scope,c):a.ajax(a.extend({url:this.options.url,data:c,type:"POST"},this.options.ajaxOptions))):void 0},validate:function(a){return void 0===a&&(a=this.value),"function"==typeof this.options.validate?this.options.validate.call(this.options.scope,a):void 0},option:function(a,b){a in this.options&&(this.options[a]=b),"value"===a&&this.setValue(b)},setValue:function(a,b){this.value=b?this.input.str2value(a):a,this.$form&&this.$form.is(":visible")&&this.input.value2input(this.value)}},a.fn.editableform=function(c){var d=arguments;return this.each(function(){var e=a(this),f=e.data("editableform"),g="object"==typeof c&&c;f||e.data("editableform",f=new b(this,g)),"string"==typeof c&&f[c].apply(f,Array.prototype.slice.call(d,1))})},a.fn.editableform.Constructor=b,a.fn.editableform.defaults={type:"text",url:null,params:null,name:null,pk:null,value:null,defaultValue:null,send:"auto",validate:null,success:null,error:null,ajaxOptions:null,showbuttons:!0,scope:null,savenochange:!1},a.fn.editableform.template='<form class="form-inline editableform"><div class="control-group"><div><div class="editable-input"></div><div class="editable-buttons"></div></div><div class="editable-error-block"></div></div></form>',a.fn.editableform.loading='<div class="editableform-loading"></div>',a.fn.editableform.buttons='<button type="submit" class="editable-submit">ok</button><button type="button" class="editable-cancel">cancel</button>',a.fn.editableform.errorGroupClass=null,a.fn.editableform.errorBlockClass="editable-error",a.fn.editableform.engine="jquery"}(window.jQuery),function(a){"use strict";a.fn.editableutils={inherit:function(a,b){var c=function(){};c.prototype=b.prototype,a.prototype=new c,a.prototype.constructor=a,a.superclass=b.prototype},setCursorPosition:function(a,b){if(a.setSelectionRange)a.setSelectionRange(b,b);else if(a.createTextRange){var c=a.createTextRange();c.collapse(!0),c.moveEnd("character",b),c.moveStart("character",b),c.select()}},tryParseJson:function(a,b){if("string"==typeof a&&a.length&&a.match(/^[\{\[].*[\}\]]$/))if(b)try{a=new Function("return "+a)()}catch(c){}finally{return a}else a=new Function("return "+a)();return a},sliceObj:function(b,c,d){var e,f,g={};if(!a.isArray(c)||!c.length)return g;for(var h=0;h<c.length;h++)e=c[h],b.hasOwnProperty(e)&&(g[e]=b[e]),d!==!0&&(f=e.toLowerCase(),b.hasOwnProperty(f)&&(g[e]=b[f]));return g},getConfigData:function(b){var c={};return a.each(b.data(),function(a,b){("object"!=typeof b||b&&"object"==typeof b&&(b.constructor===Object||b.constructor===Array))&&(c[a]=b)}),c},objectKeys:function(a){if(Object.keys)return Object.keys(a);if(a!==Object(a))throw new TypeError("Object.keys called on a non-object");var b,c=[];for(b in a)Object.prototype.hasOwnProperty.call(a,b)&&c.push(b);return c},escape:function(b){return a("<div>").text(b).html()},itemsByValue:function(b,c,d){if(!c||null===b)return[];if("function"!=typeof d){var e=d||"value";d=function(a){return a[e]}}var f=a.isArray(b),g=[],h=this;return a.each(c,function(c,e){if(e.children)g=g.concat(h.itemsByValue(b,e.children,d));else if(f)a.grep(b,function(a){return a==(e&&"object"==typeof e?d(e):e)}).length&&g.push(e);else{var i=e&&"object"==typeof e?d(e):e;b==i&&g.push(e)}}),g},createInput:function(b){var c,d,e,f=b.type;return"date"===f&&("inline"===b.mode?a.fn.editabletypes.datefield?f="datefield":a.fn.editabletypes.dateuifield&&(f="dateuifield"):a.fn.editabletypes.date?f="date":a.fn.editabletypes.dateui&&(f="dateui"),"date"!==f||a.fn.editabletypes.date||(f="combodate")),"datetime"===f&&"inline"===b.mode&&(f="datetimefield"),"wysihtml5"!==f||a.fn.editabletypes[f]||(f="textarea"),"function"==typeof a.fn.editabletypes[f]?(c=a.fn.editabletypes[f],d=this.sliceObj(b,this.objectKeys(c.defaults)),e=new c(d)):(a.error("Unknown type: "+f),!1)},supportsTransitions:function(){var a=document.body||document.documentElement,b=a.style,c="transition",d=["Moz","Webkit","Khtml","O","ms"];if("string"==typeof b[c])return!0;c=c.charAt(0).toUpperCase()+c.substr(1);for(var e=0;e<d.length;e++)if("string"==typeof b[d[e]+c])return!0;return!1}}}(window.jQuery),function(a){"use strict";var b=function(a,b){this.init(a,b)},c=function(a,b){this.init(a,b)};b.prototype={containerName:null,containerDataName:null,innerCss:null,containerClass:"editable-container editable-popup",defaults:{},init:function(c,d){this.$element=a(c),this.options=a.extend({},a.fn.editableContainer.defaults,d),this.splitOptions(),this.formOptions.scope=this.$element[0],this.initContainer(),this.delayedHide=!1,this.$element.on("destroyed",a.proxy(function(){this.destroy()},this)),a(document).data("editable-handlers-attached")||(a(document).on("keyup.editable",function(b){27===b.which&&a(".editable-open").editableContainer("hide")}),a(document).on("click.editable",function(c){var d,e=a(c.target),f=[".editable-container",".ui-datepicker-header",".datepicker",".modal-backdrop",".bootstrap-wysihtml5-insert-image-modal",".bootstrap-wysihtml5-insert-link-modal"];if(a.contains(document.documentElement,c.target)&&!e.is(document)){for(d=0;d<f.length;d++)if(e.is(f[d])||e.parents(f[d]).length)return;b.prototype.closeOthers(c.target)}}),a(document).data("editable-handlers-attached",!0))},splitOptions:function(){if(this.containerOptions={},this.formOptions={},!a.fn[this.containerName])throw new Error(this.containerName+" not found. Have you included corresponding js file?");for(var b in this.options)b in this.defaults?this.containerOptions[b]=this.options[b]:this.formOptions[b]=this.options[b]},tip:function(){return this.container()?this.container().$tip:null},container:function(){var a;return this.containerDataName&&(a=this.$element.data(this.containerDataName))?a:a=this.$element.data(this.containerName)},call:function(){this.$element[this.containerName].apply(this.$element,arguments)},initContainer:function(){this.call(this.containerOptions)},renderForm:function(){this.$form.editableform(this.formOptions).on({save:a.proxy(this.save,this),nochange:a.proxy(function(){this.hide("nochange")},this),cancel:a.proxy(function(){this.hide("cancel")},this),show:a.proxy(function(){this.delayedHide?(this.hide(this.delayedHide.reason),this.delayedHide=!1):this.setPosition()},this),rendering:a.proxy(this.setPosition,this),resize:a.proxy(this.setPosition,this),rendered:a.proxy(function(){this.$element.triggerHandler("shown",a(this.options.scope).data("editable"))},this)}).editableform("render")},show:function(b){this.$element.addClass("editable-open"),b!==!1&&this.closeOthers(this.$element[0]),this.innerShow(),this.tip().addClass(this.containerClass),this.$form,this.$form=a("<div>"),this.tip().is(this.innerCss)?this.tip().append(this.$form):this.tip().find(this.innerCss).append(this.$form),this.renderForm()},hide:function(a){if(this.tip()&&this.tip().is(":visible")&&this.$element.hasClass("editable-open")){if(this.$form.data("editableform").isSaving)return this.delayedHide={reason:a},void 0;this.delayedHide=!1,this.$element.removeClass("editable-open"),this.innerHide(),this.$element.triggerHandler("hidden",a||"manual")}},innerShow:function(){},innerHide:function(){},toggle:function(a){this.container()&&this.tip()&&this.tip().is(":visible")?this.hide():this.show(a)},setPosition:function(){},save:function(a,b){this.$element.triggerHandler("save",b),this.hide("save")},option:function(a,b){this.options[a]=b,a in this.containerOptions?(this.containerOptions[a]=b,this.setContainerOption(a,b)):(this.formOptions[a]=b,this.$form&&this.$form.editableform("option",a,b))},setContainerOption:function(a,b){this.call("option",a,b)},destroy:function(){this.hide(),this.innerDestroy(),this.$element.off("destroyed"),this.$element.removeData("editableContainer")},innerDestroy:function(){},closeOthers:function(b){a(".editable-open").each(function(c,d){if(d!==b&&!a(d).find(b).length){var e=a(d),f=e.data("editableContainer");f&&("cancel"===f.options.onblur?e.data("editableContainer").hide("onblur"):"submit"===f.options.onblur&&e.data("editableContainer").tip().find("form").submit())}})},activate:function(){this.tip&&this.tip().is(":visible")&&this.$form&&this.$form.data("editableform").input.activate()}},a.fn.editableContainer=function(d){var e=arguments;return this.each(function(){var f=a(this),g="editableContainer",h=f.data(g),i="object"==typeof d&&d,j="inline"===i.mode?c:b;h||f.data(g,h=new j(this,i)),"string"==typeof d&&h[d].apply(h,Array.prototype.slice.call(e,1))})},a.fn.editableContainer.Popup=b,a.fn.editableContainer.Inline=c,a.fn.editableContainer.defaults={value:null,placement:"top",autohide:!0,onblur:"cancel",anim:!1,mode:"popup"},jQuery.event.special.destroyed={remove:function(a){a.handler&&a.handler()}}}(window.jQuery),function(a){"use strict";a.extend(a.fn.editableContainer.Inline.prototype,a.fn.editableContainer.Popup.prototype,{containerName:"editableform",innerCss:".editable-inline",containerClass:"editable-container editable-inline",initContainer:function(){this.$tip=a("<span></span>"),this.options.anim||(this.options.anim=0)},splitOptions:function(){this.containerOptions={},this.formOptions=this.options},tip:function(){return this.$tip},innerShow:function(){this.$element.hide(),this.tip().insertAfter(this.$element).show()},innerHide:function(){this.$tip.hide(this.options.anim,a.proxy(function(){this.$element.show(),this.innerDestroy()},this))},innerDestroy:function(){this.tip()&&this.tip().empty().remove()}})}(window.jQuery),function(a){"use strict";var b=function(b,c){this.$element=a(b),this.options=a.extend({},a.fn.editable.defaults,c,a.fn.editableutils.getConfigData(this.$element)),this.options.selector?this.initLive():this.init(),this.options.highlight&&!a.fn.editableutils.supportsTransitions()&&(this.options.highlight=!1)};b.prototype={constructor:b,init:function(){var b,c=!1;if(this.options.name=this.options.name||this.$element.attr("id"),this.options.scope=this.$element[0],this.input=a.fn.editableutils.createInput(this.options),this.input){switch(void 0===this.options.value||null===this.options.value?(this.value=this.input.html2value(a.trim(this.$element.html())),c=!0):(this.options.value=a.fn.editableutils.tryParseJson(this.options.value,!0),this.value="string"==typeof this.options.value?this.input.str2value(this.options.value):this.options.value),this.$element.addClass("editable"),"textarea"===this.input.type&&this.$element.addClass("editable-pre-wrapped"),"manual"!==this.options.toggle?(this.$element.addClass("editable-click"),this.$element.on(this.options.toggle+".editable",a.proxy(function(a){if(this.options.disabled||a.preventDefault(),"mouseenter"===this.options.toggle)this.show();else{var b="click"!==this.options.toggle;this.toggle(b)}},this))):this.$element.attr("tabindex",-1),"function"==typeof this.options.display&&(this.options.autotext="always"),this.options.autotext){case"always":b=!0;break;case"auto":b=!a.trim(this.$element.text()).length&&null!==this.value&&void 0!==this.value&&!c;break;default:b=!1}a.when(b?this.render():!0).then(a.proxy(function(){this.options.disabled?this.disable():this.enable(),this.$element.triggerHandler("init",this)},this))}},initLive:function(){var b=this.options.selector;this.options.selector=!1,this.options.autotext="never",this.$element.on(this.options.toggle+".editable",b,a.proxy(function(b){var c=a(b.target);c.data("editable")||(c.hasClass(this.options.emptyclass)&&c.empty(),c.editable(this.options).trigger(b))},this))},render:function(a){return this.options.display!==!1?this.input.value2htmlFinal?this.input.value2html(this.value,this.$element[0],this.options.display,a):"function"==typeof this.options.display?this.options.display.call(this.$element[0],this.value,a):this.input.value2html(this.value,this.$element[0]):void 0},enable:function(){this.options.disabled=!1,this.$element.removeClass("editable-disabled"),this.handleEmpty(this.isEmpty),"manual"!==this.options.toggle&&"-1"===this.$element.attr("tabindex")&&this.$element.removeAttr("tabindex")},disable:function(){this.options.disabled=!0,this.hide(),this.$element.addClass("editable-disabled"),this.handleEmpty(this.isEmpty),this.$element.attr("tabindex",-1)},toggleDisabled:function(){this.options.disabled?this.enable():this.disable()},option:function(b,c){return b&&"object"==typeof b?(a.each(b,a.proxy(function(b,c){this.option(a.trim(b),c)},this)),void 0):(this.options[b]=c,"disabled"===b?c?this.disable():this.enable():("value"===b&&this.setValue(c),this.container&&this.container.option(b,c),this.input.option&&this.input.option(b,c),void 0))},handleEmpty:function(b){this.options.display!==!1&&(this.isEmpty=void 0!==b?b:"function"==typeof this.input.isEmpty?this.input.isEmpty(this.$element):""===a.trim(this.$element.html()),this.options.disabled?this.isEmpty&&(this.$element.empty(),this.options.emptyclass&&this.$element.removeClass(this.options.emptyclass)):this.isEmpty?(this.$element.html(this.options.emptytext),this.options.emptyclass&&this.$element.addClass(this.options.emptyclass)):this.options.emptyclass&&this.$element.removeClass(this.options.emptyclass))},show:function(b){if(!this.options.disabled){if(this.container){if(this.container.tip().is(":visible"))return}else{var c=a.extend({},this.options,{value:this.value,input:this.input});this.$element.editableContainer(c),this.$element.on("save.internal",a.proxy(this.save,this)),this.container=this.$element.data("editableContainer")}this.container.show(b)}},hide:function(){this.container&&this.container.hide()},toggle:function(a){this.container&&this.container.tip().is(":visible")?this.hide():this.show(a)},save:function(a,b){if(this.options.unsavedclass){var c=!1;c=c||"function"==typeof this.options.url,c=c||this.options.display===!1,c=c||void 0!==b.response,c=c||this.options.savenochange&&this.input.value2str(this.value)!==this.input.value2str(b.newValue),c?this.$element.removeClass(this.options.unsavedclass):this.$element.addClass(this.options.unsavedclass)}if(this.options.highlight){var d=this.$element,e=d.css("background-color");d.css("background-color",this.options.highlight),setTimeout(function(){"transparent"===e&&(e=""),d.css("background-color",e),d.addClass("editable-bg-transition"),setTimeout(function(){d.removeClass("editable-bg-transition")},1700)},10)}this.setValue(b.newValue,!1,b.response)},validate:function(){return"function"==typeof this.options.validate?this.options.validate.call(this,this.value):void 0},setValue:function(b,c,d){this.value=c?this.input.str2value(b):b,this.container&&this.container.option("value",this.value),a.when(this.render(d)).then(a.proxy(function(){this.handleEmpty()},this))},activate:function(){this.container&&this.container.activate()},destroy:function(){this.disable(),this.container&&this.container.destroy(),this.input.destroy(),"manual"!==this.options.toggle&&(this.$element.removeClass("editable-click"),this.$element.off(this.options.toggle+".editable")),this.$element.off("save.internal"),this.$element.removeClass("editable editable-open editable-disabled"),this.$element.removeData("editable")}},a.fn.editable=function(c){var d={},e=arguments,f="editable";switch(c){case"validate":return this.each(function(){var b,c=a(this),e=c.data(f);e&&(b=e.validate())&&(d[e.options.name]=b)}),d;case"getValue":return 2===arguments.length&&arguments[1]===!0?d=this.eq(0).data(f).value:this.each(function(){var b=a(this),c=b.data(f);c&&void 0!==c.value&&null!==c.value&&(d[c.options.name]=c.input.value2submit(c.value))}),d;case"submit":var g=arguments[1]||{},h=this,i=this.editable("validate");if(a.isEmptyObject(i)){var j={};if(1===h.length){var k=h.data("editable"),l={name:k.options.name||"",value:k.input.value2submit(k.value),pk:"function"==typeof k.options.pk?k.options.pk.call(k.options.scope):k.options.pk};"function"==typeof k.options.params?l=k.options.params.call(k.options.scope,l):(k.options.params=a.fn.editableutils.tryParseJson(k.options.params,!0),a.extend(l,k.options.params)),j={url:k.options.url,data:l,type:"POST"},g.success=g.success||k.options.success,g.error=g.error||k.options.error}else{var m=this.editable("getValue");j={url:g.url,data:m,type:"POST"}}j.success="function"==typeof g.success?function(a){g.success.call(h,a,g)}:a.noop,j.error="function"==typeof g.error?function(){g.error.apply(h,arguments)}:a.noop,g.ajaxOptions&&a.extend(j,g.ajaxOptions),g.data&&a.extend(j.data,g.data),a.ajax(j)}else"function"==typeof g.error&&g.error.call(h,i);return this}return this.each(function(){var d=a(this),g=d.data(f),h="object"==typeof c&&c;return h&&h.selector?(g=new b(this,h),void 0):(g||d.data(f,g=new b(this,h)),"string"==typeof c&&g[c].apply(g,Array.prototype.slice.call(e,1)),void 0)})},a.fn.editable.defaults={type:"text",disabled:!1,toggle:"click",emptytext:"Empty",autotext:"auto",value:null,display:null,emptyclass:"editable-empty",unsavedclass:"editable-unsaved",selector:null,highlight:"#FFFF80"}}(window.jQuery),function(a){"use strict";a.fn.editabletypes={};var b=function(){};b.prototype={init:function(b,c,d){this.type=b,this.options=a.extend({},d,c)},prerender:function(){this.$tpl=a(this.options.tpl),this.$input=this.$tpl,this.$clear=null,this.error=null},render:function(){},value2html:function(b,c){a(c)[this.options.escape?"text":"html"](a.trim(b))},html2value:function(b){return a("<div>").html(b).text()},value2str:function(a){return a},str2value:function(a){return a},value2submit:function(a){return a},value2input:function(a){this.$input.val(a)},input2value:function(){return this.$input.val()},activate:function(){this.$input.is(":visible")&&this.$input.focus()},clear:function(){this.$input.val(null)},escape:function(b){return a("<div>").text(b).html()},autosubmit:function(){},destroy:function(){},setClass:function(){this.options.inputclass&&this.$input.addClass(this.options.inputclass)},setAttr:function(a){void 0!==this.options[a]&&null!==this.options[a]&&this.$input.attr(a,this.options[a])},option:function(a,b){this.options[a]=b}},b.defaults={tpl:"",inputclass:null,escape:!0,scope:null,showbuttons:!0},a.extend(a.fn.editabletypes,{abstractinput:b})}(window.jQuery),function(a){"use strict";var b=function(){};a.fn.editableutils.inherit(b,a.fn.editabletypes.abstractinput),a.extend(b.prototype,{render:function(){var b=a.Deferred();return this.error=null,this.onSourceReady(function(){this.renderList(),b.resolve()},function(){this.error=this.options.sourceError,b.resolve()}),b.promise()},html2value:function(){return null},value2html:function(b,c,d,e){var f=a.Deferred(),g=function(){"function"==typeof d?d.call(c,b,this.sourceData,e):this.value2htmlFinal(b,c),f.resolve()};return null===b?g.call(this):this.onSourceReady(g,function(){f.resolve()}),f.promise()},onSourceReady:function(b,c){var d;if(a.isFunction(this.options.source)?(d=this.options.source.call(this.options.scope),this.sourceData=null):d=this.options.source,this.options.sourceCache&&a.isArray(this.sourceData))return b.call(this),void 0;try{d=a.fn.editableutils.tryParseJson(d,!1)}catch(e){return c.call(this),void 0}if("string"==typeof d){if(this.options.sourceCache){var f,g=d;if(a(document).data(g)||a(document).data(g,{}),f=a(document).data(g),f.loading===!1&&f.sourceData)return this.sourceData=f.sourceData,this.doPrepend(),b.call(this),void 0;if(f.loading===!0)return f.callbacks.push(a.proxy(function(){this.sourceData=f.sourceData,this.doPrepend(),b.call(this)},this)),f.err_callbacks.push(a.proxy(c,this)),void 0;f.loading=!0,f.callbacks=[],f.err_callbacks=[]}var h=a.extend({url:d,type:"get",cache:!1,dataType:"json",success:a.proxy(function(d){f&&(f.loading=!1),this.sourceData=this.makeArray(d),a.isArray(this.sourceData)?(f&&(f.sourceData=this.sourceData,a.each(f.callbacks,function(){this.call()})),this.doPrepend(),b.call(this)):(c.call(this),f&&a.each(f.err_callbacks,function(){this.call()}))},this),error:a.proxy(function(){c.call(this),f&&(f.loading=!1,a.each(f.err_callbacks,function(){this.call()}))},this)},this.options.sourceOptions);a.ajax(h)}else this.sourceData=this.makeArray(d),a.isArray(this.sourceData)?(this.doPrepend(),b.call(this)):c.call(this)},doPrepend:function(){null!==this.options.prepend&&void 0!==this.options.prepend&&(a.isArray(this.prependData)||(a.isFunction(this.options.prepend)&&(this.options.prepend=this.options.prepend.call(this.options.scope)),this.options.prepend=a.fn.editableutils.tryParseJson(this.options.prepend,!0),"string"==typeof this.options.prepend&&(this.options.prepend={"":this.options.prepend}),this.prependData=this.makeArray(this.options.prepend)),a.isArray(this.prependData)&&a.isArray(this.sourceData)&&(this.sourceData=this.prependData.concat(this.sourceData)))},renderList:function(){},value2htmlFinal:function(){},makeArray:function(b){var c,d,e,f,g=[];if(!b||"string"==typeof b)return null;if(a.isArray(b)){f=function(a,b){return d={value:a,text:b},c++>=2?!1:void 0};for(var h=0;h<b.length;h++)e=b[h],"object"==typeof e?(c=0,a.each(e,f),1===c?g.push(d):c>1&&(e.children&&(e.children=this.makeArray(e.children)),g.push(e))):g.push({value:e,text:e})}else a.each(b,function(a,b){g.push({value:a,text:b})});return g},option:function(a,b){this.options[a]=b,"source"===a&&(this.sourceData=null),"prepend"===a&&(this.prependData=null)}}),b.defaults=a.extend({},a.fn.editabletypes.abstractinput.defaults,{source:null,prepend:!1,sourceError:"Error when loading list",sourceCache:!0,sourceOptions:null}),a.fn.editabletypes.list=b}(window.jQuery),function(a){"use strict";var b=function(a){this.init("text",a,b.defaults)};a.fn.editableutils.inherit(b,a.fn.editabletypes.abstractinput),a.extend(b.prototype,{render:function(){this.renderClear(),this.setClass(),this.setAttr("placeholder")},activate:function(){this.$input.is(":visible")&&(this.$input.focus(),a.fn.editableutils.setCursorPosition(this.$input.get(0),this.$input.val().length),this.toggleClear&&this.toggleClear())},renderClear:function(){this.options.clear&&(this.$clear=a('<span class="editable-clear-x"></span>'),this.$input.after(this.$clear).css("padding-right",24).keyup(a.proxy(function(b){if(!~a.inArray(b.keyCode,[40,38,9,13,27])){clearTimeout(this.t);var c=this;this.t=setTimeout(function(){c.toggleClear(b)},100)}},this)).parent().css("position","relative"),this.$clear.click(a.proxy(this.clear,this)))},postrender:function(){},toggleClear:function(){if(this.$clear){var a=this.$input.val().length,b=this.$clear.is(":visible");a&&!b&&this.$clear.show(),!a&&b&&this.$clear.hide()}},clear:function(){this.$clear.hide(),this.$input.val("").focus()}}),b.defaults=a.extend({},a.fn.editabletypes.abstractinput.defaults,{tpl:'<input type="text">',placeholder:null,clear:!0}),a.fn.editabletypes.text=b}(window.jQuery),function(a){"use strict";var b=function(a){this.init("textarea",a,b.defaults)};a.fn.editableutils.inherit(b,a.fn.editabletypes.abstractinput),a.extend(b.prototype,{render:function(){this.setClass(),this.setAttr("placeholder"),this.setAttr("rows"),this.$input.keydown(function(b){b.ctrlKey&&13===b.which&&a(this).closest("form").submit()})},activate:function(){a.fn.editabletypes.text.prototype.activate.call(this)}}),b.defaults=a.extend({},a.fn.editabletypes.abstractinput.defaults,{tpl:"<textarea></textarea>",inputclass:"input-large",placeholder:null,rows:7}),a.fn.editabletypes.textarea=b}(window.jQuery),function(a){"use strict";var b=function(a){this.init("select",a,b.defaults)};a.fn.editableutils.inherit(b,a.fn.editabletypes.list),a.extend(b.prototype,{renderList:function(){this.$input.empty();var b=function(c,d){var e;if(a.isArray(d))for(var f=0;f<d.length;f++)e={},d[f].children?(e.label=d[f].text,c.append(b(a("<optgroup>",e),d[f].children))):(e.value=d[f].value,d[f].disabled&&(e.disabled=!0),c.append(a("<option>",e).text(d[f].text)));return c};b(this.$input,this.sourceData),this.setClass(),this.$input.on("keydown.editable",function(b){13===b.which&&a(this).closest("form").submit()})},value2htmlFinal:function(b,c){var d="",e=a.fn.editableutils.itemsByValue(b,this.sourceData);e.length&&(d=e[0].text),a.fn.editabletypes.abstractinput.prototype.value2html.call(this,d,c)},autosubmit:function(){this.$input.off("keydown.editable").on("change.editable",function(){a(this).closest("form").submit()})}}),b.defaults=a.extend({},a.fn.editabletypes.list.defaults,{tpl:"<select></select>"}),a.fn.editabletypes.select=b}(window.jQuery),function(a){"use strict";var b=function(a){this.init("checklist",a,b.defaults)};a.fn.editableutils.inherit(b,a.fn.editabletypes.list),a.extend(b.prototype,{renderList:function(){var b;if(this.$tpl.empty(),a.isArray(this.sourceData)){for(var c=0;c<this.sourceData.length;c++)b=a("<label>").append(a("<input>",{type:"checkbox",value:this.sourceData[c].value})).append(a("<span>").text(" "+this.sourceData[c].text)),a("<div>").append(b).appendTo(this.$tpl);this.$input=this.$tpl.find('input[type="checkbox"]'),this.setClass()}},value2str:function(b){return a.isArray(b)?b.sort().join(a.trim(this.options.separator)):""},str2value:function(b){var c,d=null;return"string"==typeof b&&b.length?(c=new RegExp("\\s*"+a.trim(this.options.separator)+"\\s*"),d=b.split(c)):d=a.isArray(b)?b:[b],d},value2input:function(b){this.$input.prop("checked",!1),a.isArray(b)&&b.length&&this.$input.each(function(c,d){var e=a(d);a.each(b,function(a,b){e.val()==b&&e.prop("checked",!0)})})},input2value:function(){var b=[];return this.$input.filter(":checked").each(function(c,d){b.push(a(d).val())}),b},value2htmlFinal:function(b,c){var d=[],e=a.fn.editableutils.itemsByValue(b,this.sourceData),f=this.options.escape;e.length?(a.each(e,function(b,c){var e=f?a.fn.editableutils.escape(c.text):c.text;d.push(e)}),a(c).html(d.join("<br>"))):a(c).empty()},activate:function(){this.$input.first().focus()},autosubmit:function(){this.$input.on("keydown",function(b){13===b.which&&a(this).closest("form").submit()})}}),b.defaults=a.extend({},a.fn.editabletypes.list.defaults,{tpl:'<div class="editable-checklist"></div>',inputclass:null,separator:","}),a.fn.editabletypes.checklist=b}(window.jQuery),function(a){"use strict";var b=function(a){this.init("password",a,b.defaults)};a.fn.editableutils.inherit(b,a.fn.editabletypes.text),a.extend(b.prototype,{value2html:function(b,c){b?a(c).text("[hidden]"):a(c).empty()},html2value:function(){return null}}),b.defaults=a.extend({},a.fn.editabletypes.text.defaults,{tpl:'<input type="password">'}),a.fn.editabletypes.password=b}(window.jQuery),function(a){"use strict";var b=function(a){this.init("email",a,b.defaults)};a.fn.editableutils.inherit(b,a.fn.editabletypes.text),b.defaults=a.extend({},a.fn.editabletypes.text.defaults,{tpl:'<input type="email">'}),a.fn.editabletypes.email=b}(window.jQuery),function(a){"use strict";var b=function(a){this.init("url",a,b.defaults)};a.fn.editableutils.inherit(b,a.fn.editabletypes.text),b.defaults=a.extend({},a.fn.editabletypes.text.defaults,{tpl:'<input type="url">'}),a.fn.editabletypes.url=b}(window.jQuery),function(a){"use strict";var b=function(a){this.init("tel",a,b.defaults)};a.fn.editableutils.inherit(b,a.fn.editabletypes.text),b.defaults=a.extend({},a.fn.editabletypes.text.defaults,{tpl:'<input type="tel">'}),a.fn.editabletypes.tel=b}(window.jQuery),function(a){"use strict";var b=function(a){this.init("number",a,b.defaults)};a.fn.editableutils.inherit(b,a.fn.editabletypes.text),a.extend(b.prototype,{render:function(){b.superclass.render.call(this),this.setAttr("min"),this.setAttr("max"),this.setAttr("step")},postrender:function(){this.$clear&&this.$clear.css({right:24})}}),b.defaults=a.extend({},a.fn.editabletypes.text.defaults,{tpl:'<input type="number">',inputclass:"input-mini",min:null,max:null,step:null}),a.fn.editabletypes.number=b}(window.jQuery),function(a){"use strict";
var b=function(a){this.init("range",a,b.defaults)};a.fn.editableutils.inherit(b,a.fn.editabletypes.number),a.extend(b.prototype,{render:function(){this.$input=this.$tpl.filter("input"),this.setClass(),this.setAttr("min"),this.setAttr("max"),this.setAttr("step"),this.$input.on("input",function(){a(this).siblings("output").text(a(this).val())})},activate:function(){this.$input.focus()}}),b.defaults=a.extend({},a.fn.editabletypes.number.defaults,{tpl:'<input type="range"><output style="width: 30px; display: inline-block"></output>',inputclass:"input-medium"}),a.fn.editabletypes.range=b}(window.jQuery),function(a){"use strict";var b=function(a){this.init("time",a,b.defaults)};a.fn.editableutils.inherit(b,a.fn.editabletypes.abstractinput),a.extend(b.prototype,{render:function(){this.setClass()}}),b.defaults=a.extend({},a.fn.editabletypes.abstractinput.defaults,{tpl:'<input type="time">'}),a.fn.editabletypes.time=b}(window.jQuery),function(a){"use strict";var b=function(c){if(this.init("select2",c,b.defaults),c.select2=c.select2||{},this.sourceData=null,c.placeholder&&(c.select2.placeholder=c.placeholder),!c.select2.tags&&c.source){var d=c.source;a.isFunction(c.source)&&(d=c.source.call(c.scope)),"string"==typeof d?(c.select2.ajax=c.select2.ajax||{},c.select2.ajax.data||(c.select2.ajax.data=function(a){return{query:a}}),c.select2.ajax.results||(c.select2.ajax.results=function(a){return{results:a}}),c.select2.ajax.url=d):(this.sourceData=this.convertSource(d),c.select2.data=this.sourceData)}if(this.options.select2=a.extend({},b.defaults.select2,c.select2),this.isMultiple=this.options.select2.tags||this.options.select2.multiple,this.isRemote="ajax"in this.options.select2,this.idFunc=this.options.select2.id,"function"!=typeof this.idFunc){var e=this.idFunc||"id";this.idFunc=function(a){return a[e]}}this.formatSelection=this.options.select2.formatSelection,"function"!=typeof this.formatSelection&&(this.formatSelection=function(a){return a.text})};a.fn.editableutils.inherit(b,a.fn.editabletypes.abstractinput),a.extend(b.prototype,{render:function(){this.setClass(),this.isRemote&&this.$input.on("select2-loaded",a.proxy(function(a){this.sourceData=a.items.results},this)),this.isMultiple&&this.$input.on("change",function(){a(this).closest("form").parent().triggerHandler("resize")})},value2html:function(c,d){var e,f="",g=this;this.options.select2.tags?e=c:this.sourceData&&(e=a.fn.editableutils.itemsByValue(c,this.sourceData,this.idFunc)),a.isArray(e)?(f=[],a.each(e,function(a,b){f.push(b&&"object"==typeof b?g.formatSelection(b):b)})):e&&(f=g.formatSelection(e)),f=a.isArray(f)?f.join(this.options.viewseparator):f,b.superclass.value2html.call(this,f,d)},html2value:function(a){return this.options.select2.tags?this.str2value(a,this.options.viewseparator):null},value2input:function(b){if(a.isArray(b)&&(b=b.join(this.getSeparator())),this.$input.data("select2")?this.$input.val(b).trigger("change",!0):(this.$input.val(b),this.$input.select2(this.options.select2)),this.isRemote&&!this.isMultiple&&!this.options.select2.initSelection){var c=this.options.select2.id,d=this.options.select2.formatSelection;if(!c&&!d){var e=a(this.options.scope);if(!e.data("editable").isEmpty){var f={id:b,text:e.text()};this.$input.select2("data",f)}}}},input2value:function(){return this.$input.select2("val")},str2value:function(b,c){if("string"!=typeof b||!this.isMultiple)return b;c=c||this.getSeparator();var d,e,f;if(null===b||b.length<1)return null;for(d=b.split(c),e=0,f=d.length;f>e;e+=1)d[e]=a.trim(d[e]);return d},autosubmit:function(){this.$input.on("change",function(b,c){c||a(this).closest("form").submit()})},getSeparator:function(){return this.options.select2.separator||a.fn.select2.defaults.separator},convertSource:function(b){if(a.isArray(b)&&b.length&&void 0!==b[0].value)for(var c=0;c<b.length;c++)void 0!==b[c].value&&(b[c].id=b[c].value,delete b[c].value);return b},destroy:function(){this.$input.data("select2")&&this.$input.select2("destroy")}}),b.defaults=a.extend({},a.fn.editabletypes.abstractinput.defaults,{tpl:'<input type="hidden">',select2:null,placeholder:null,source:null,viewseparator:", "}),a.fn.editabletypes.select2=b}(window.jQuery),function(a){var b=function(b,c){return this.$element=a(b),this.$element.is("input")?(this.options=a.extend({},a.fn.combodate.defaults,c,this.$element.data()),this.init(),void 0):(a.error("Combodate should be applied to INPUT element"),void 0)};b.prototype={constructor:b,init:function(){this.map={day:["D","date"],month:["M","month"],year:["Y","year"],hour:["[Hh]","hours"],minute:["m","minutes"],second:["s","seconds"],ampm:["[Aa]",""]},this.$widget=a('<span class="combodate"></span>').html(this.getTemplate()),this.initCombos(),this.$widget.on("change","select",a.proxy(function(b){this.$element.val(this.getValue()).change(),this.options.smartDays&&(a(b.target).is(".month")||a(b.target).is(".year"))&&this.fillCombo("day")},this)),this.$widget.find("select").css("width","auto"),this.$element.hide().after(this.$widget),this.setValue(this.$element.val()||this.options.value)},getTemplate:function(){var b=this.options.template;return a.each(this.map,function(a,c){c=c[0];var d=new RegExp(c+"+"),e=c.length>1?c.substring(1,2):c;b=b.replace(d,"{"+e+"}")}),b=b.replace(/ /g,"&nbsp;"),a.each(this.map,function(a,c){c=c[0];var d=c.length>1?c.substring(1,2):c;b=b.replace("{"+d+"}",'<select class="'+a+'"></select>')}),b},initCombos:function(){for(var a in this.map){var b=this.$widget.find("."+a);this["$"+a]=b.length?b:null,this.fillCombo(a)}},fillCombo:function(a){var b=this["$"+a];if(b){var c="fill"+a.charAt(0).toUpperCase()+a.slice(1),d=this[c](),e=b.val();b.empty();for(var f=0;f<d.length;f++)b.append('<option value="'+d[f][0]+'">'+d[f][1]+"</option>");b.val(e)}},fillCommon:function(a){var b,c=[];if("name"===this.options.firstItem){b=moment.relativeTime||moment.langData()._relativeTime;var d="function"==typeof b[a]?b[a](1,!0,a,!1):b[a];d=d.split(" ").reverse()[0],c.push(["",d])}else"empty"===this.options.firstItem&&c.push(["",""]);return c},fillDay:function(){var a,b,c=this.fillCommon("d"),d=-1!==this.options.template.indexOf("DD"),e=31;if(this.options.smartDays&&this.$month&&this.$year){var f=parseInt(this.$month.val(),10),g=parseInt(this.$year.val(),10);isNaN(f)||isNaN(g)||(e=moment([g,f]).daysInMonth())}for(b=1;e>=b;b++)a=d?this.leadZero(b):b,c.push([b,a]);return c},fillMonth:function(){var a,b,c=this.fillCommon("M"),d=-1!==this.options.template.indexOf("MMMM"),e=-1!==this.options.template.indexOf("MMM"),f=-1!==this.options.template.indexOf("MM");for(b=0;11>=b;b++)a=d?moment().date(1).month(b).format("MMMM"):e?moment().date(1).month(b).format("MMM"):f?this.leadZero(b+1):b+1,c.push([b,a]);return c},fillYear:function(){var a,b,c=[],d=-1!==this.options.template.indexOf("YYYY");for(b=this.options.maxYear;b>=this.options.minYear;b--)a=d?b:(b+"").substring(2),c[this.options.yearDescending?"push":"unshift"]([b,a]);return c=this.fillCommon("y").concat(c)},fillHour:function(){var a,b,c=this.fillCommon("h"),d=-1!==this.options.template.indexOf("h"),e=(-1!==this.options.template.indexOf("H"),-1!==this.options.template.toLowerCase().indexOf("hh")),f=d?1:0,g=d?12:23;for(b=f;g>=b;b++)a=e?this.leadZero(b):b,c.push([b,a]);return c},fillMinute:function(){var a,b,c=this.fillCommon("m"),d=-1!==this.options.template.indexOf("mm");for(b=0;59>=b;b+=this.options.minuteStep)a=d?this.leadZero(b):b,c.push([b,a]);return c},fillSecond:function(){var a,b,c=this.fillCommon("s"),d=-1!==this.options.template.indexOf("ss");for(b=0;59>=b;b+=this.options.secondStep)a=d?this.leadZero(b):b,c.push([b,a]);return c},fillAmpm:function(){var a=-1!==this.options.template.indexOf("a"),b=(-1!==this.options.template.indexOf("A"),[["am",a?"am":"AM"],["pm",a?"pm":"PM"]]);return b},getValue:function(b){var c,d={},e=this,f=!1;return a.each(this.map,function(a){if("ampm"!==a){var b="day"===a?1:0;return d[a]=e["$"+a]?parseInt(e["$"+a].val(),10):b,isNaN(d[a])?(f=!0,!1):void 0}}),f?"":(this.$ampm&&(d.hour=12===d.hour?"am"===this.$ampm.val()?0:12:"am"===this.$ampm.val()?d.hour:d.hour+12),c=moment([d.year,d.month,d.day,d.hour,d.minute,d.second]),this.highlight(c),b=void 0===b?this.options.format:b,null===b?c.isValid()?c:null:c.isValid()?c.format(b):"")},setValue:function(b){function c(b,c){var d={};return b.children("option").each(function(b,e){var f,g=a(e).attr("value");""!==g&&(f=Math.abs(g-c),("undefined"==typeof d.distance||f<d.distance)&&(d={value:g,distance:f}))}),d.value}if(b){var d="string"==typeof b?moment(b,this.options.format):moment(b),e=this,f={};d.isValid()&&(a.each(this.map,function(a,b){"ampm"!==a&&(f[a]=d[b[1]]())}),this.$ampm&&(f.hour>=12?(f.ampm="pm",f.hour>12&&(f.hour-=12)):(f.ampm="am",0===f.hour&&(f.hour=12))),a.each(f,function(a,b){e["$"+a]&&("minute"===a&&e.options.minuteStep>1&&e.options.roundTime&&(b=c(e["$"+a],b)),"second"===a&&e.options.secondStep>1&&e.options.roundTime&&(b=c(e["$"+a],b)),e["$"+a].val(b))}),this.options.smartDays&&this.fillCombo("day"),this.$element.val(d.format(this.options.format)).change())}},highlight:function(a){a.isValid()?this.options.errorClass?this.$widget.removeClass(this.options.errorClass):this.$widget.find("select").css("border-color",this.borderColor):this.options.errorClass?this.$widget.addClass(this.options.errorClass):(this.borderColor||(this.borderColor=this.$widget.find("select").css("border-color")),this.$widget.find("select").css("border-color","red"))},leadZero:function(a){return 9>=a?"0"+a:a},destroy:function(){this.$widget.remove(),this.$element.removeData("combodate").show()}},a.fn.combodate=function(c){var d,e=Array.apply(null,arguments);return e.shift(),"getValue"===c&&this.length&&(d=this.eq(0).data("combodate"))?d.getValue.apply(d,e):this.each(function(){var d=a(this),f=d.data("combodate"),g="object"==typeof c&&c;f||d.data("combodate",f=new b(this,g)),"string"==typeof c&&"function"==typeof f[c]&&f[c].apply(f,e)})},a.fn.combodate.defaults={format:"DD-MM-YYYY HH:mm",template:"D / MMM / YYYY   H : mm",value:null,minYear:1970,maxYear:2015,yearDescending:!0,minuteStep:5,secondStep:1,firstItem:"empty",errorClass:null,roundTime:!0,smartDays:!1}}(window.jQuery),function(a){"use strict";var b=function(c){this.init("combodate",c,b.defaults),this.options.viewformat||(this.options.viewformat=this.options.format),c.combodate=a.fn.editableutils.tryParseJson(c.combodate,!0),this.options.combodate=a.extend({},b.defaults.combodate,c.combodate,{format:this.options.format,template:this.options.template})};a.fn.editableutils.inherit(b,a.fn.editabletypes.abstractinput),a.extend(b.prototype,{render:function(){this.$input.combodate(this.options.combodate),"bs3"===a.fn.editableform.engine&&this.$input.siblings().find("select").addClass("form-control"),this.options.inputclass&&this.$input.siblings().find("select").addClass(this.options.inputclass)},value2html:function(a,c){var d=a?a.format(this.options.viewformat):"";b.superclass.value2html.call(this,d,c)},html2value:function(a){return a?moment(a,this.options.viewformat):null},value2str:function(a){return a?a.format(this.options.format):""},str2value:function(a){return a?moment(a,this.options.format):null},value2submit:function(a){return this.value2str(a)},value2input:function(a){this.$input.combodate("setValue",a)},input2value:function(){return this.$input.combodate("getValue",null)},activate:function(){this.$input.siblings(".combodate").find("select").eq(0).focus()},autosubmit:function(){}}),b.defaults=a.extend({},a.fn.editabletypes.abstractinput.defaults,{tpl:'<input type="text">',inputclass:null,format:"YYYY-MM-DD",viewformat:null,template:"D / MMM / YYYY",combodate:null}),a.fn.editabletypes.combodate=b}(window.jQuery),function(a){"use strict";var b=a.fn.editableform.Constructor.prototype.initInput;a.extend(a.fn.editableform.Constructor.prototype,{initTemplate:function(){this.$form=a(a.fn.editableform.template),this.$form.find(".control-group").addClass("form-group"),this.$form.find(".editable-error-block").addClass("help-block")},initInput:function(){b.apply(this);var c=null===this.input.options.inputclass||this.input.options.inputclass===!1,d="input-sm",e="text,select,textarea,password,email,url,tel,number,range,time,typeaheadjs".split(",");~a.inArray(this.input.type,e)&&(this.input.$input.addClass("form-control"),c&&(this.input.options.inputclass=d,this.input.$input.addClass(d)));for(var f=this.$form.find(".editable-buttons"),g=c?[d]:this.input.options.inputclass.split(" "),h=0;h<g.length;h++)"input-lg"===g[h].toLowerCase()&&f.find("button").removeClass("btn-sm").addClass("btn-lg")}}),a.fn.editableform.buttons='<button type="submit" class="btn btn-primary btn-sm editable-submit"><i class="glyphicon glyphicon-ok"></i></button><button type="button" class="btn btn-default btn-sm editable-cancel"><i class="glyphicon glyphicon-remove"></i></button>',a.fn.editableform.errorGroupClass="has-error",a.fn.editableform.errorBlockClass=null,a.fn.editableform.engine="bs3"}(window.jQuery),function(a){"use strict";a.extend(a.fn.editableContainer.Popup.prototype,{containerName:"popover",containerDataName:"bs.popover",innerCss:".popover-content",defaults:a.fn.popover.Constructor.DEFAULTS,initContainer:function(){a.extend(this.containerOptions,{trigger:"manual",selector:!1,content:" ",template:this.defaults.template});var b;this.$element.data("template")&&(b=this.$element.data("template"),this.$element.removeData("template")),this.call(this.containerOptions),b&&this.$element.data("template",b)},innerShow:function(){this.call("show")},innerHide:function(){this.call("hide")},innerDestroy:function(){this.call("destroy")},setContainerOption:function(a,b){this.container().options[a]=b},setPosition:function(){!function(){var a=this.tip(),b="function"==typeof this.options.placement?this.options.placement.call(this,a[0],this.$element[0]):this.options.placement,c=/\s?auto?\s?/i,d=c.test(b);d&&(b=b.replace(c,"")||"top");var e=this.getPosition(),f=a[0].offsetWidth,g=a[0].offsetHeight;if(d){var h=this.$element.parent(),i=b,j=document.documentElement.scrollTop||document.body.scrollTop,k="body"==this.options.container?window.innerWidth:h.outerWidth(),l="body"==this.options.container?window.innerHeight:h.outerHeight(),m="body"==this.options.container?0:h.offset().left;b="bottom"==b&&e.top+e.height+g-j>l?"top":"top"==b&&e.top-j-g<0?"bottom":"right"==b&&e.right+f>k?"left":"left"==b&&e.left-f<m?"right":b,a.removeClass(i).addClass(b)}var n=this.getCalculatedOffset(b,e,f,g);this.applyPlacement(n,b)}.call(this.container())}})}(window.jQuery),function(a){function b(){return new Date(Date.UTC.apply(Date,arguments))}function c(b,c){var d,e=a(b).data(),f={},g=new RegExp("^"+c.toLowerCase()+"([A-Z])"),c=new RegExp("^"+c.toLowerCase());for(var h in e)c.test(h)&&(d=h.replace(g,function(a,b){return b.toLowerCase()}),f[d]=e[h]);return f}function d(b){var c={};if(k[b]||(b=b.split("-")[0],k[b])){var d=k[b];return a.each(j,function(a,b){b in d&&(c[b]=d[b])}),c}}var e=function(b,c){this._process_options(c),this.element=a(b),this.isInline=!1,this.isInput=this.element.is("input"),this.component=this.element.is(".date")?this.element.find(".add-on, .btn"):!1,this.hasInput=this.component&&this.element.find("input").length,this.component&&0===this.component.length&&(this.component=!1),this.picker=a(l.template),this._buildEvents(),this._attachEvents(),this.isInline?this.picker.addClass("datepicker-inline").appendTo(this.element):this.picker.addClass("datepicker-dropdown dropdown-menu"),this.o.rtl&&(this.picker.addClass("datepicker-rtl"),this.picker.find(".prev i, .next i").toggleClass("icon-arrow-left icon-arrow-right")),this.viewMode=this.o.startView,this.o.calendarWeeks&&this.picker.find("tfoot th.today").attr("colspan",function(a,b){return parseInt(b)+1}),this._allow_update=!1,this.setStartDate(this.o.startDate),this.setEndDate(this.o.endDate),this.setDaysOfWeekDisabled(this.o.daysOfWeekDisabled),this.fillDow(),this.fillMonths(),this._allow_update=!0,this.update(),this.showMode(),this.isInline&&this.show()};e.prototype={constructor:e,_process_options:function(b){this._o=a.extend({},this._o,b);var c=this.o=a.extend({},this._o),d=c.language;switch(k[d]||(d=d.split("-")[0],k[d]||(d=i.language)),c.language=d,c.startView){case 2:case"decade":c.startView=2;break;case 1:case"year":c.startView=1;break;default:c.startView=0}switch(c.minViewMode){case 1:case"months":c.minViewMode=1;break;case 2:case"years":c.minViewMode=2;break;default:c.minViewMode=0}c.startView=Math.max(c.startView,c.minViewMode),c.weekStart%=7,c.weekEnd=(c.weekStart+6)%7;var e=l.parseFormat(c.format);c.startDate!==-1/0&&(c.startDate=l.parseDate(c.startDate,e,c.language)),1/0!==c.endDate&&(c.endDate=l.parseDate(c.endDate,e,c.language)),c.daysOfWeekDisabled=c.daysOfWeekDisabled||[],a.isArray(c.daysOfWeekDisabled)||(c.daysOfWeekDisabled=c.daysOfWeekDisabled.split(/[,\s]*/)),c.daysOfWeekDisabled=a.map(c.daysOfWeekDisabled,function(a){return parseInt(a,10)})},_events:[],_secondaryEvents:[],_applyEvents:function(a){for(var b,c,d=0;d<a.length;d++)b=a[d][0],c=a[d][1],b.on(c)},_unapplyEvents:function(a){for(var b,c,d=0;d<a.length;d++)b=a[d][0],c=a[d][1],b.off(c)},_buildEvents:function(){this.isInput?this._events=[[this.element,{focus:a.proxy(this.show,this),keyup:a.proxy(this.update,this),keydown:a.proxy(this.keydown,this)}]]:this.component&&this.hasInput?this._events=[[this.element.find("input"),{focus:a.proxy(this.show,this),keyup:a.proxy(this.update,this),keydown:a.proxy(this.keydown,this)}],[this.component,{click:a.proxy(this.show,this)}]]:this.element.is("div")?this.isInline=!0:this._events=[[this.element,{click:a.proxy(this.show,this)}]],this._secondaryEvents=[[this.picker,{click:a.proxy(this.click,this)}],[a(window),{resize:a.proxy(this.place,this)}],[a(document),{mousedown:a.proxy(function(a){this.element.is(a.target)||this.element.find(a.target).size()||this.picker.is(a.target)||this.picker.find(a.target).size()||this.hide()},this)}]]},_attachEvents:function(){this._detachEvents(),this._applyEvents(this._events)},_detachEvents:function(){this._unapplyEvents(this._events)},_attachSecondaryEvents:function(){this._detachSecondaryEvents(),this._applyEvents(this._secondaryEvents)},_detachSecondaryEvents:function(){this._unapplyEvents(this._secondaryEvents)},_trigger:function(b,c){var d=c||this.date,e=new Date(d.getTime()+6e4*d.getTimezoneOffset());this.element.trigger({type:b,date:e,format:a.proxy(function(a){var b=a||this.o.format;return l.formatDate(d,b,this.o.language)},this)})},show:function(a){this.isInline||this.picker.appendTo("body"),this.picker.show(),this.height=this.component?this.component.outerHeight():this.element.outerHeight(),this.place(),this._attachSecondaryEvents(),a&&a.preventDefault(),this._trigger("show")},hide:function(){this.isInline||this.picker.is(":visible")&&(this.picker.hide().detach(),this._detachSecondaryEvents(),this.viewMode=this.o.startView,this.showMode(),this.o.forceParse&&(this.isInput&&this.element.val()||this.hasInput&&this.element.find("input").val())&&this.setValue(),this._trigger("hide"))},remove:function(){this.hide(),this._detachEvents(),this._detachSecondaryEvents(),this.picker.remove(),delete this.element.data().datepicker,this.isInput||delete this.element.data().date},getDate:function(){var a=this.getUTCDate();return new Date(a.getTime()+6e4*a.getTimezoneOffset())},getUTCDate:function(){return this.date},setDate:function(a){this.setUTCDate(new Date(a.getTime()-6e4*a.getTimezoneOffset()))},setUTCDate:function(a){this.date=a,this.setValue()},setValue:function(){var a=this.getFormattedDate();this.isInput?this.element.val(a):this.component&&this.element.find("input").val(a)},getFormattedDate:function(a){return void 0===a&&(a=this.o.format),l.formatDate(this.date,a,this.o.language)},setStartDate:function(a){this._process_options({startDate:a}),this.update(),this.updateNavArrows()},setEndDate:function(a){this._process_options({endDate:a}),this.update(),this.updateNavArrows()},setDaysOfWeekDisabled:function(a){this._process_options({daysOfWeekDisabled:a}),this.update(),this.updateNavArrows()},place:function(){if(!this.isInline){var b=parseInt(this.element.parents().filter(function(){return"auto"!=a(this).css("z-index")}).first().css("z-index"))+10,c=this.component?this.component.parent().offset():this.element.offset(),d=this.component?this.component.outerHeight(!0):this.element.outerHeight(!0);this.picker.css({top:c.top+d,left:c.left,zIndex:b})}},_allow_update:!0,update:function(){if(this._allow_update){var a,b=!1;arguments&&arguments.length&&("string"==typeof arguments[0]||arguments[0]instanceof Date)?(a=arguments[0],b=!0):(a=this.isInput?this.element.val():this.element.data("date")||this.element.find("input").val(),delete this.element.data().date),this.date=l.parseDate(a,this.o.format,this.o.language),b&&this.setValue(),this.viewDate=this.date<this.o.startDate?new Date(this.o.startDate):this.date>this.o.endDate?new Date(this.o.endDate):new Date(this.date),this.fill()}},fillDow:function(){var a=this.o.weekStart,b="<tr>";if(this.o.calendarWeeks){var c='<th class="cw">&nbsp;</th>';b+=c,this.picker.find(".datepicker-days thead tr:first-child").prepend(c)}for(;a<this.o.weekStart+7;)b+='<th class="dow">'+k[this.o.language].daysMin[a++%7]+"</th>";b+="</tr>",this.picker.find(".datepicker-days thead").append(b)},fillMonths:function(){for(var a="",b=0;12>b;)a+='<span class="month">'+k[this.o.language].monthsShort[b++]+"</span>";this.picker.find(".datepicker-months td").html(a)},setRange:function(b){b&&b.length?this.range=a.map(b,function(a){return a.valueOf()}):delete this.range,this.fill()},getClassNames:function(b){var c=[],d=this.viewDate.getUTCFullYear(),e=this.viewDate.getUTCMonth(),f=this.date.valueOf(),g=new Date;return b.getUTCFullYear()<d||b.getUTCFullYear()==d&&b.getUTCMonth()<e?c.push("old"):(b.getUTCFullYear()>d||b.getUTCFullYear()==d&&b.getUTCMonth()>e)&&c.push("new"),this.o.todayHighlight&&b.getUTCFullYear()==g.getFullYear()&&b.getUTCMonth()==g.getMonth()&&b.getUTCDate()==g.getDate()&&c.push("today"),f&&b.valueOf()==f&&c.push("active"),(b.valueOf()<this.o.startDate||b.valueOf()>this.o.endDate||-1!==a.inArray(b.getUTCDay(),this.o.daysOfWeekDisabled))&&c.push("disabled"),this.range&&(b>this.range[0]&&b<this.range[this.range.length-1]&&c.push("range"),-1!=a.inArray(b.valueOf(),this.range)&&c.push("selected")),c},fill:function(){var c,d=new Date(this.viewDate),e=d.getUTCFullYear(),f=d.getUTCMonth(),g=this.o.startDate!==-1/0?this.o.startDate.getUTCFullYear():-1/0,h=this.o.startDate!==-1/0?this.o.startDate.getUTCMonth():-1/0,i=1/0!==this.o.endDate?this.o.endDate.getUTCFullYear():1/0,j=1/0!==this.o.endDate?this.o.endDate.getUTCMonth():1/0;this.date&&this.date.valueOf(),this.picker.find(".datepicker-days thead th.datepicker-switch").text(k[this.o.language].months[f]+" "+e),this.picker.find("tfoot th.today").text(k[this.o.language].today).toggle(this.o.todayBtn!==!1),this.picker.find("tfoot th.clear").text(k[this.o.language].clear).toggle(this.o.clearBtn!==!1),this.updateNavArrows(),this.fillMonths();var m=b(e,f-1,28,0,0,0,0),n=l.getDaysInMonth(m.getUTCFullYear(),m.getUTCMonth());m.setUTCDate(n),m.setUTCDate(n-(m.getUTCDay()-this.o.weekStart+7)%7);var o=new Date(m);o.setUTCDate(o.getUTCDate()+42),o=o.valueOf();for(var p,q=[];m.valueOf()<o;){if(m.getUTCDay()==this.o.weekStart&&(q.push("<tr>"),this.o.calendarWeeks)){var r=new Date(+m+864e5*((this.o.weekStart-m.getUTCDay()-7)%7)),s=new Date(+r+864e5*((11-r.getUTCDay())%7)),t=new Date(+(t=b(s.getUTCFullYear(),0,1))+864e5*((11-t.getUTCDay())%7)),u=(s-t)/864e5/7+1;q.push('<td class="cw">'+u+"</td>")}p=this.getClassNames(m),p.push("day");var v=this.o.beforeShowDay(m);void 0===v?v={}:"boolean"==typeof v?v={enabled:v}:"string"==typeof v&&(v={classes:v}),v.enabled===!1&&p.push("disabled"),v.classes&&(p=p.concat(v.classes.split(/\s+/))),v.tooltip&&(c=v.tooltip),p=a.unique(p),q.push('<td class="'+p.join(" ")+'"'+(c?' title="'+c+'"':"")+">"+m.getUTCDate()+"</td>"),m.getUTCDay()==this.o.weekEnd&&q.push("</tr>"),m.setUTCDate(m.getUTCDate()+1)}this.picker.find(".datepicker-days tbody").empty().append(q.join(""));var w=this.date&&this.date.getUTCFullYear(),x=this.picker.find(".datepicker-months").find("th:eq(1)").text(e).end().find("span").removeClass("active");w&&w==e&&x.eq(this.date.getUTCMonth()).addClass("active"),(g>e||e>i)&&x.addClass("disabled"),e==g&&x.slice(0,h).addClass("disabled"),e==i&&x.slice(j+1).addClass("disabled"),q="",e=10*parseInt(e/10,10);var y=this.picker.find(".datepicker-years").find("th:eq(1)").text(e+"-"+(e+9)).end().find("td");e-=1;for(var z=-1;11>z;z++)q+='<span class="year'+(-1==z?" old":10==z?" new":"")+(w==e?" active":"")+(g>e||e>i?" disabled":"")+'">'+e+"</span>",e+=1;y.html(q)},updateNavArrows:function(){if(this._allow_update){var a=new Date(this.viewDate),b=a.getUTCFullYear(),c=a.getUTCMonth();switch(this.viewMode){case 0:this.o.startDate!==-1/0&&b<=this.o.startDate.getUTCFullYear()&&c<=this.o.startDate.getUTCMonth()?this.picker.find(".prev").css({visibility:"hidden"}):this.picker.find(".prev").css({visibility:"visible"}),1/0!==this.o.endDate&&b>=this.o.endDate.getUTCFullYear()&&c>=this.o.endDate.getUTCMonth()?this.picker.find(".next").css({visibility:"hidden"}):this.picker.find(".next").css({visibility:"visible"});break;case 1:case 2:this.o.startDate!==-1/0&&b<=this.o.startDate.getUTCFullYear()?this.picker.find(".prev").css({visibility:"hidden"}):this.picker.find(".prev").css({visibility:"visible"}),1/0!==this.o.endDate&&b>=this.o.endDate.getUTCFullYear()?this.picker.find(".next").css({visibility:"hidden"}):this.picker.find(".next").css({visibility:"visible"})}}},click:function(c){c.preventDefault();var d=a(c.target).closest("span, td, th");if(1==d.length)switch(d[0].nodeName.toLowerCase()){case"th":switch(d[0].className){case"datepicker-switch":this.showMode(1);break;case"prev":case"next":var e=l.modes[this.viewMode].navStep*("prev"==d[0].className?-1:1);switch(this.viewMode){case 0:this.viewDate=this.moveMonth(this.viewDate,e);break;case 1:case 2:this.viewDate=this.moveYear(this.viewDate,e)}this.fill();break;case"today":var f=new Date;f=b(f.getFullYear(),f.getMonth(),f.getDate(),0,0,0),this.showMode(-2);var g="linked"==this.o.todayBtn?null:"view";this._setDate(f,g);break;case"clear":var h;this.isInput?h=this.element:this.component&&(h=this.element.find("input")),h&&h.val("").change(),this._trigger("changeDate"),this.update(),this.o.autoclose&&this.hide()}break;case"span":if(!d.is(".disabled")){if(this.viewDate.setUTCDate(1),d.is(".month")){var i=1,j=d.parent().find("span").index(d),k=this.viewDate.getUTCFullYear();this.viewDate.setUTCMonth(j),this._trigger("changeMonth",this.viewDate),1===this.o.minViewMode&&this._setDate(b(k,j,i,0,0,0,0))}else{var k=parseInt(d.text(),10)||0,i=1,j=0;this.viewDate.setUTCFullYear(k),this._trigger("changeYear",this.viewDate),2===this.o.minViewMode&&this._setDate(b(k,j,i,0,0,0,0))}this.showMode(-1),this.fill()}break;case"td":if(d.is(".day")&&!d.is(".disabled")){var i=parseInt(d.text(),10)||1,k=this.viewDate.getUTCFullYear(),j=this.viewDate.getUTCMonth();d.is(".old")?0===j?(j=11,k-=1):j-=1:d.is(".new")&&(11==j?(j=0,k+=1):j+=1),this._setDate(b(k,j,i,0,0,0,0))}}},_setDate:function(a,b){b&&"date"!=b||(this.date=new Date(a)),b&&"view"!=b||(this.viewDate=new Date(a)),this.fill(),this.setValue(),this._trigger("changeDate");var c;this.isInput?c=this.element:this.component&&(c=this.element.find("input")),c&&(c.change(),!this.o.autoclose||b&&"date"!=b||this.hide())},moveMonth:function(a,b){if(!b)return a;var c,d,e=new Date(a.valueOf()),f=e.getUTCDate(),g=e.getUTCMonth(),h=Math.abs(b);if(b=b>0?1:-1,1==h)d=-1==b?function(){return e.getUTCMonth()==g}:function(){return e.getUTCMonth()!=c},c=g+b,e.setUTCMonth(c),(0>c||c>11)&&(c=(c+12)%12);else{for(var i=0;h>i;i++)e=this.moveMonth(e,b);c=e.getUTCMonth(),e.setUTCDate(f),d=function(){return c!=e.getUTCMonth()}}for(;d();)e.setUTCDate(--f),e.setUTCMonth(c);return e},moveYear:function(a,b){return this.moveMonth(a,12*b)},dateWithinRange:function(a){return a>=this.o.startDate&&a<=this.o.endDate},keydown:function(a){if(this.picker.is(":not(:visible)"))return 27==a.keyCode&&this.show(),void 0;var b,c,d,e=!1;switch(a.keyCode){case 27:this.hide(),a.preventDefault();break;case 37:case 39:if(!this.o.keyboardNavigation)break;b=37==a.keyCode?-1:1,a.ctrlKey?(c=this.moveYear(this.date,b),d=this.moveYear(this.viewDate,b)):a.shiftKey?(c=this.moveMonth(this.date,b),d=this.moveMonth(this.viewDate,b)):(c=new Date(this.date),c.setUTCDate(this.date.getUTCDate()+b),d=new Date(this.viewDate),d.setUTCDate(this.viewDate.getUTCDate()+b)),this.dateWithinRange(c)&&(this.date=c,this.viewDate=d,this.setValue(),this.update(),a.preventDefault(),e=!0);break;case 38:case 40:if(!this.o.keyboardNavigation)break;b=38==a.keyCode?-1:1,a.ctrlKey?(c=this.moveYear(this.date,b),d=this.moveYear(this.viewDate,b)):a.shiftKey?(c=this.moveMonth(this.date,b),d=this.moveMonth(this.viewDate,b)):(c=new Date(this.date),c.setUTCDate(this.date.getUTCDate()+7*b),d=new Date(this.viewDate),d.setUTCDate(this.viewDate.getUTCDate()+7*b)),this.dateWithinRange(c)&&(this.date=c,this.viewDate=d,this.setValue(),this.update(),a.preventDefault(),e=!0);break;case 13:this.hide(),a.preventDefault();break;case 9:this.hide()}if(e){this._trigger("changeDate");var f;this.isInput?f=this.element:this.component&&(f=this.element.find("input")),f&&f.change()}},showMode:function(a){a&&(this.viewMode=Math.max(this.o.minViewMode,Math.min(2,this.viewMode+a))),this.picker.find(">div").hide().filter(".datepicker-"+l.modes[this.viewMode].clsName).css("display","block"),this.updateNavArrows()}};var f=function(b,c){this.element=a(b),this.inputs=a.map(c.inputs,function(a){return a.jquery?a[0]:a}),delete c.inputs,a(this.inputs).datepicker(c).bind("changeDate",a.proxy(this.dateUpdated,this)),this.pickers=a.map(this.inputs,function(b){return a(b).data("datepicker")}),this.updateDates()};f.prototype={updateDates:function(){this.dates=a.map(this.pickers,function(a){return a.date}),this.updateRanges()},updateRanges:function(){var b=a.map(this.dates,function(a){return a.valueOf()});a.each(this.pickers,function(a,c){c.setRange(b)})},dateUpdated:function(b){var c=a(b.target).data("datepicker"),d=c.getUTCDate(),e=a.inArray(b.target,this.inputs),f=this.inputs.length;if(-1!=e){if(d<this.dates[e])for(;e>=0&&d<this.dates[e];)this.pickers[e--].setUTCDate(d);else if(d>this.dates[e])for(;f>e&&d>this.dates[e];)this.pickers[e++].setUTCDate(d);this.updateDates()}},remove:function(){a.map(this.pickers,function(a){a.remove()}),delete this.element.data().datepicker}};var g=a.fn.datepicker,h=a.fn.datepicker=function(b){var g=Array.apply(null,arguments);g.shift();var h;return this.each(function(){var j=a(this),k=j.data("datepicker"),l="object"==typeof b&&b;if(!k){var m=c(this,"date"),n=a.extend({},i,m,l),o=d(n.language),p=a.extend({},i,o,m,l);if(j.is(".input-daterange")||p.inputs){var q={inputs:p.inputs||j.find("input").toArray()};j.data("datepicker",k=new f(this,a.extend(p,q)))}else j.data("datepicker",k=new e(this,p))}return"string"==typeof b&&"function"==typeof k[b]&&(h=k[b].apply(k,g),void 0!==h)?!1:void 0}),void 0!==h?h:this},i=a.fn.datepicker.defaults={autoclose:!1,beforeShowDay:a.noop,calendarWeeks:!1,clearBtn:!1,daysOfWeekDisabled:[],endDate:1/0,forceParse:!0,format:"mm/dd/yyyy",keyboardNavigation:!0,language:"en",minViewMode:0,rtl:!1,startDate:-1/0,startView:0,todayBtn:!1,todayHighlight:!1,weekStart:0},j=a.fn.datepicker.locale_opts=["format","rtl","weekStart"];a.fn.datepicker.Constructor=e;var k=a.fn.datepicker.dates={en:{days:["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],daysShort:["Sun","Mon","Tue","Wed","Thu","Fri","Sat","Sun"],daysMin:["Su","Mo","Tu","We","Th","Fr","Sa","Su"],months:["January","February","March","April","May","June","July","August","September","October","November","December"],monthsShort:["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],today:"Today",clear:"Clear"}},l={modes:[{clsName:"days",navFnc:"Month",navStep:1},{clsName:"months",navFnc:"FullYear",navStep:1},{clsName:"years",navFnc:"FullYear",navStep:10}],isLeapYear:function(a){return 0===a%4&&0!==a%100||0===a%400
},getDaysInMonth:function(a,b){return[31,l.isLeapYear(a)?29:28,31,30,31,30,31,31,30,31,30,31][b]},validParts:/dd?|DD?|mm?|MM?|yy(?:yy)?/g,nonpunctuation:/[^ -\/:-@\[\u3400-\u9fff-`{-~\t\n\r]+/g,parseFormat:function(a){var b=a.replace(this.validParts,"\0").split("\0"),c=a.match(this.validParts);if(!b||!b.length||!c||0===c.length)throw new Error("Invalid date format.");return{separators:b,parts:c}},parseDate:function(c,d,f){if(c instanceof Date)return c;if("string"==typeof d&&(d=l.parseFormat(d)),/^[\-+]\d+[dmwy]([\s,]+[\-+]\d+[dmwy])*$/.test(c)){var g,h,i=/([\-+]\d+)([dmwy])/,j=c.match(/([\-+]\d+)([dmwy])/g);c=new Date;for(var m=0;m<j.length;m++)switch(g=i.exec(j[m]),h=parseInt(g[1]),g[2]){case"d":c.setUTCDate(c.getUTCDate()+h);break;case"m":c=e.prototype.moveMonth.call(e.prototype,c,h);break;case"w":c.setUTCDate(c.getUTCDate()+7*h);break;case"y":c=e.prototype.moveYear.call(e.prototype,c,h)}return b(c.getUTCFullYear(),c.getUTCMonth(),c.getUTCDate(),0,0,0)}var n,o,g,j=c&&c.match(this.nonpunctuation)||[],c=new Date,p={},q=["yyyy","yy","M","MM","m","mm","d","dd"],r={yyyy:function(a,b){return a.setUTCFullYear(b)},yy:function(a,b){return a.setUTCFullYear(2e3+b)},m:function(a,b){for(b-=1;0>b;)b+=12;for(b%=12,a.setUTCMonth(b);a.getUTCMonth()!=b;)a.setUTCDate(a.getUTCDate()-1);return a},d:function(a,b){return a.setUTCDate(b)}};r.M=r.MM=r.mm=r.m,r.dd=r.d,c=b(c.getFullYear(),c.getMonth(),c.getDate(),0,0,0);var s=d.parts.slice();if(j.length!=s.length&&(s=a(s).filter(function(b,c){return-1!==a.inArray(c,q)}).toArray()),j.length==s.length){for(var m=0,t=s.length;t>m;m++){if(n=parseInt(j[m],10),g=s[m],isNaN(n))switch(g){case"MM":o=a(k[f].months).filter(function(){var a=this.slice(0,j[m].length),b=j[m].slice(0,a.length);return a==b}),n=a.inArray(o[0],k[f].months)+1;break;case"M":o=a(k[f].monthsShort).filter(function(){var a=this.slice(0,j[m].length),b=j[m].slice(0,a.length);return a==b}),n=a.inArray(o[0],k[f].monthsShort)+1}p[g]=n}for(var u,m=0;m<q.length;m++)u=q[m],u in p&&!isNaN(p[u])&&r[u](c,p[u])}return c},formatDate:function(b,c,d){"string"==typeof c&&(c=l.parseFormat(c));var e={d:b.getUTCDate(),D:k[d].daysShort[b.getUTCDay()],DD:k[d].days[b.getUTCDay()],m:b.getUTCMonth()+1,M:k[d].monthsShort[b.getUTCMonth()],MM:k[d].months[b.getUTCMonth()],yy:b.getUTCFullYear().toString().substring(2),yyyy:b.getUTCFullYear()};e.dd=(e.d<10?"0":"")+e.d,e.mm=(e.m<10?"0":"")+e.m;for(var b=[],f=a.extend([],c.separators),g=0,h=c.parts.length;h>=g;g++)f.length&&b.push(f.shift()),b.push(e[c.parts[g]]);return b.join("")},headTemplate:'<thead><tr><th class="prev"><i class="icon-arrow-left"/></th><th colspan="5" class="datepicker-switch"></th><th class="next"><i class="icon-arrow-right"/></th></tr></thead>',contTemplate:'<tbody><tr><td colspan="7"></td></tr></tbody>',footTemplate:'<tfoot><tr><th colspan="7" class="today"></th></tr><tr><th colspan="7" class="clear"></th></tr></tfoot>'};l.template='<div class="datepicker"><div class="datepicker-days"><table class=" table-condensed">'+l.headTemplate+"<tbody></tbody>"+l.footTemplate+"</table>"+"</div>"+'<div class="datepicker-months">'+'<table class="table-condensed">'+l.headTemplate+l.contTemplate+l.footTemplate+"</table>"+"</div>"+'<div class="datepicker-years">'+'<table class="table-condensed">'+l.headTemplate+l.contTemplate+l.footTemplate+"</table>"+"</div>"+"</div>",a.fn.datepicker.DPGlobal=l,a.fn.datepicker.noConflict=function(){return a.fn.datepicker=g,this},a(document).on("focus.datepicker.data-api click.datepicker.data-api",'[data-provide="datepicker"]',function(b){var c=a(this);c.data("datepicker")||(b.preventDefault(),h.call(c,"show"))}),a(function(){h.call(a('[data-provide="datepicker-inline"]'))})}(window.jQuery),function(a){"use strict";a.fn.bdatepicker=a.fn.datepicker.noConflict(),a.fn.datepicker||(a.fn.datepicker=a.fn.bdatepicker);var b=function(a){this.init("date",a,b.defaults),this.initPicker(a,b.defaults)};a.fn.editableutils.inherit(b,a.fn.editabletypes.abstractinput),a.extend(b.prototype,{initPicker:function(b,c){this.options.viewformat||(this.options.viewformat=this.options.format),b.datepicker=a.fn.editableutils.tryParseJson(b.datepicker,!0),this.options.datepicker=a.extend({},c.datepicker,b.datepicker,{format:this.options.viewformat}),this.options.datepicker.language=this.options.datepicker.language||"en",this.dpg=a.fn.bdatepicker.DPGlobal,this.parsedFormat=this.dpg.parseFormat(this.options.format),this.parsedViewFormat=this.dpg.parseFormat(this.options.viewformat)},render:function(){this.$input.bdatepicker(this.options.datepicker),this.options.clear&&(this.$clear=a('<a href="#"></a>').html(this.options.clear).click(a.proxy(function(a){a.preventDefault(),a.stopPropagation(),this.clear()},this)),this.$tpl.parent().append(a('<div class="editable-clear">').append(this.$clear)))},value2html:function(a,c){var d=a?this.dpg.formatDate(a,this.parsedViewFormat,this.options.datepicker.language):"";b.superclass.value2html.call(this,d,c)},html2value:function(a){return this.parseDate(a,this.parsedViewFormat)},value2str:function(a){return a?this.dpg.formatDate(a,this.parsedFormat,this.options.datepicker.language):""},str2value:function(a){return this.parseDate(a,this.parsedFormat)},value2submit:function(a){return this.value2str(a)},value2input:function(a){this.$input.bdatepicker("update",a)},input2value:function(){return this.$input.data("datepicker").date},activate:function(){},clear:function(){this.$input.data("datepicker").date=null,this.$input.find(".active").removeClass("active"),this.options.showbuttons||this.$input.closest("form").submit()},autosubmit:function(){this.$input.on("mouseup",".day",function(b){if(!a(b.currentTarget).is(".old")&&!a(b.currentTarget).is(".new")){var c=a(this).closest("form");setTimeout(function(){c.submit()},200)}})},parseDate:function(a,b){var c,d=null;return a&&(d=this.dpg.parseDate(a,b,this.options.datepicker.language),"string"==typeof a&&(c=this.dpg.formatDate(d,b,this.options.datepicker.language),a!==c&&(d=null))),d}}),b.defaults=a.extend({},a.fn.editabletypes.abstractinput.defaults,{tpl:'<div class="editable-date well"></div>',inputclass:null,format:"yyyy-mm-dd",viewformat:null,datepicker:{weekStart:0,startView:0,minViewMode:0,autoclose:!1},clear:"&times; clear"}),a.fn.editabletypes.date=b}(window.jQuery),function(a){"use strict";var b=function(a){this.init("datefield",a,b.defaults),this.initPicker(a,b.defaults)};a.fn.editableutils.inherit(b,a.fn.editabletypes.date),a.extend(b.prototype,{render:function(){this.$input=this.$tpl.find("input"),this.setClass(),this.setAttr("placeholder"),this.$tpl.bdatepicker(this.options.datepicker),this.$input.off("focus keydown"),this.$input.keyup(a.proxy(function(){this.$tpl.removeData("date"),this.$tpl.bdatepicker("update")},this))},value2input:function(a){this.$input.val(a?this.dpg.formatDate(a,this.parsedViewFormat,this.options.datepicker.language):""),this.$tpl.bdatepicker("update")},input2value:function(){return this.html2value(this.$input.val())},activate:function(){a.fn.editabletypes.text.prototype.activate.call(this)},autosubmit:function(){}}),b.defaults=a.extend({},a.fn.editabletypes.date.defaults,{tpl:'<div class="input-append date"><input type="text"/><span class="add-on"><i class="icon-th"></i></span></div>',inputclass:"input-small",datepicker:{weekStart:0,startView:0,minViewMode:0,autoclose:!0}}),a.fn.editabletypes.datefield=b}(window.jQuery),function(a){"use strict";var b=function(a){this.init("datetime",a,b.defaults),this.initPicker(a,b.defaults)};a.fn.editableutils.inherit(b,a.fn.editabletypes.abstractinput),a.extend(b.prototype,{initPicker:function(b,c){this.options.viewformat||(this.options.viewformat=this.options.format),b.datetimepicker=a.fn.editableutils.tryParseJson(b.datetimepicker,!0),this.options.datetimepicker=a.extend({},c.datetimepicker,b.datetimepicker,{format:this.options.viewformat}),this.options.datetimepicker.language=this.options.datetimepicker.language||"en",this.dpg=a.fn.datetimepicker.DPGlobal,this.parsedFormat=this.dpg.parseFormat(this.options.format,this.options.formatType),this.parsedViewFormat=this.dpg.parseFormat(this.options.viewformat,this.options.formatType)},render:function(){this.$input.datetimepicker(this.options.datetimepicker),this.$input.on("changeMode",function(){var b=a(this).closest("form").parent();setTimeout(function(){b.triggerHandler("resize")},0)}),this.options.clear&&(this.$clear=a('<a href="#"></a>').html(this.options.clear).click(a.proxy(function(a){a.preventDefault(),a.stopPropagation(),this.clear()},this)),this.$tpl.parent().append(a('<div class="editable-clear">').append(this.$clear)))},value2html:function(a,c){var d=a?this.dpg.formatDate(this.toUTC(a),this.parsedViewFormat,this.options.datetimepicker.language,this.options.formatType):"";return c?(b.superclass.value2html.call(this,d,c),void 0):d},html2value:function(a){var b=this.parseDate(a,this.parsedViewFormat);return b?this.fromUTC(b):null},value2str:function(a){return a?this.dpg.formatDate(this.toUTC(a),this.parsedFormat,this.options.datetimepicker.language,this.options.formatType):""},str2value:function(a){var b=this.parseDate(a,this.parsedFormat);return b?this.fromUTC(b):null},value2submit:function(a){return this.value2str(a)},value2input:function(a){a&&this.$input.data("datetimepicker").setDate(a)},input2value:function(){var a=this.$input.data("datetimepicker");return a.date?a.getDate():null},activate:function(){},clear:function(){this.$input.data("datetimepicker").date=null,this.$input.find(".active").removeClass("active"),this.options.showbuttons||this.$input.closest("form").submit()},autosubmit:function(){this.$input.on("mouseup",".minute",function(){var b=a(this).closest("form");setTimeout(function(){b.submit()},200)})},toUTC:function(a){return a?new Date(a.valueOf()-6e4*a.getTimezoneOffset()):a},fromUTC:function(a){return a?new Date(a.valueOf()+6e4*a.getTimezoneOffset()):a},parseDate:function(a,b){var c,d=null;return a&&(d=this.dpg.parseDate(a,b,this.options.datetimepicker.language,this.options.formatType),"string"==typeof a&&(c=this.dpg.formatDate(d,b,this.options.datetimepicker.language,this.options.formatType),a!==c&&(d=null))),d}}),b.defaults=a.extend({},a.fn.editabletypes.abstractinput.defaults,{tpl:'<div class="editable-date well"></div>',inputclass:null,format:"yyyy-mm-dd hh:ii",formatType:"standard",viewformat:null,datetimepicker:{todayHighlight:!1,autoclose:!1},clear:"&times; clear"}),a.fn.editabletypes.datetime=b}(window.jQuery),function(a){"use strict";var b=function(a){this.init("datetimefield",a,b.defaults),this.initPicker(a,b.defaults)};a.fn.editableutils.inherit(b,a.fn.editabletypes.datetime),a.extend(b.prototype,{render:function(){this.$input=this.$tpl.find("input"),this.setClass(),this.setAttr("placeholder"),this.$tpl.datetimepicker(this.options.datetimepicker),this.$input.off("focus keydown"),this.$input.keyup(a.proxy(function(){this.$tpl.removeData("date"),this.$tpl.datetimepicker("update")},this))},value2input:function(a){this.$input.val(this.value2html(a)),this.$tpl.datetimepicker("update")},input2value:function(){return this.html2value(this.$input.val())},activate:function(){a.fn.editabletypes.text.prototype.activate.call(this)},autosubmit:function(){}}),b.defaults=a.extend({},a.fn.editabletypes.datetime.defaults,{tpl:'<div class="input-append date"><input type="text"/><span class="add-on"><i class="icon-th"></i></span></div>',inputclass:"input-medium",datetimepicker:{todayHighlight:!1,autoclose:!0}}),a.fn.editabletypes.datetimefield=b}(window.jQuery);
/*!
 * Select2 4.0.3
 * https://select2.github.io
 *
 * Released under the MIT license
 * https://github.com/select2/select2/blob/master/LICENSE.md
 */

(function (factory) {
  if (typeof define === 'function' && define.amd) {
    // AMD. Register as an anonymous module.
    define(['jquery'], factory);
  } else if (typeof exports === 'object') {
    // Node/CommonJS
    factory(require('jquery'));
  } else {
    // Browser globals
    factory(jQuery);
  }
}(function (jQuery) {
  // This is needed so we can catch the AMD loader configuration and use it
  // The inner file should be wrapped (by `banner.start.js`) in a function that
  // returns the AMD loader references.
  var S2 =
(function () {
  // Restore the Select2 AMD loader so it can be used
  // Needed mostly in the language files, where the loader is not inserted
  if (jQuery && jQuery.fn && jQuery.fn.select2 && jQuery.fn.select2.amd) {
    var S2 = jQuery.fn.select2.amd;
  }
var S2;(function () { if (!S2 || !S2.requirejs) {
if (!S2) { S2 = {}; } else { require = S2; }
/**
 * @license almond 0.3.1 Copyright (c) 2011-2014, The Dojo Foundation All Rights Reserved.
 * Available via the MIT or new BSD license.
 * see: http://github.com/jrburke/almond for details
 */
//Going sloppy to avoid 'use strict' string cost, but strict practices should
//be followed.
/*jslint sloppy: true */
/*global setTimeout: false */

var requirejs, require, define;
(function (undef) {
    var main, req, makeMap, handlers,
        defined = {},
        waiting = {},
        config = {},
        defining = {},
        hasOwn = Object.prototype.hasOwnProperty,
        aps = [].slice,
        jsSuffixRegExp = /\.js$/;

    function hasProp(obj, prop) {
        return hasOwn.call(obj, prop);
    }

    /**
     * Given a relative module name, like ./something, normalize it to
     * a real name that can be mapped to a path.
     * @param {String} name the relative name
     * @param {String} baseName a real name that the name arg is relative
     * to.
     * @returns {String} normalized name
     */
    function normalize(name, baseName) {
        var nameParts, nameSegment, mapValue, foundMap, lastIndex,
            foundI, foundStarMap, starI, i, j, part,
            baseParts = baseName && baseName.split("/"),
            map = config.map,
            starMap = (map && map['*']) || {};

        //Adjust any relative paths.
        if (name && name.charAt(0) === ".") {
            //If have a base name, try to normalize against it,
            //otherwise, assume it is a top-level require that will
            //be relative to baseUrl in the end.
            if (baseName) {
                name = name.split('/');
                lastIndex = name.length - 1;

                // Node .js allowance:
                if (config.nodeIdCompat && jsSuffixRegExp.test(name[lastIndex])) {
                    name[lastIndex] = name[lastIndex].replace(jsSuffixRegExp, '');
                }

                //Lop off the last part of baseParts, so that . matches the
                //"directory" and not name of the baseName's module. For instance,
                //baseName of "one/two/three", maps to "one/two/three.js", but we
                //want the directory, "one/two" for this normalization.
                name = baseParts.slice(0, baseParts.length - 1).concat(name);

                //start trimDots
                for (i = 0; i < name.length; i += 1) {
                    part = name[i];
                    if (part === ".") {
                        name.splice(i, 1);
                        i -= 1;
                    } else if (part === "..") {
                        if (i === 1 && (name[2] === '..' || name[0] === '..')) {
                            //End of the line. Keep at least one non-dot
                            //path segment at the front so it can be mapped
                            //correctly to disk. Otherwise, there is likely
                            //no path mapping for a path starting with '..'.
                            //This can still fail, but catches the most reasonable
                            //uses of ..
                            break;
                        } else if (i > 0) {
                            name.splice(i - 1, 2);
                            i -= 2;
                        }
                    }
                }
                //end trimDots

                name = name.join("/");
            } else if (name.indexOf('./') === 0) {
                // No baseName, so this is ID is resolved relative
                // to baseUrl, pull off the leading dot.
                name = name.substring(2);
            }
        }

        //Apply map config if available.
        if ((baseParts || starMap) && map) {
            nameParts = name.split('/');

            for (i = nameParts.length; i > 0; i -= 1) {
                nameSegment = nameParts.slice(0, i).join("/");

                if (baseParts) {
                    //Find the longest baseName segment match in the config.
                    //So, do joins on the biggest to smallest lengths of baseParts.
                    for (j = baseParts.length; j > 0; j -= 1) {
                        mapValue = map[baseParts.slice(0, j).join('/')];

                        //baseName segment has  config, find if it has one for
                        //this name.
                        if (mapValue) {
                            mapValue = mapValue[nameSegment];
                            if (mapValue) {
                                //Match, update name to the new value.
                                foundMap = mapValue;
                                foundI = i;
                                break;
                            }
                        }
                    }
                }

                if (foundMap) {
                    break;
                }

                //Check for a star map match, but just hold on to it,
                //if there is a shorter segment match later in a matching
                //config, then favor over this star map.
                if (!foundStarMap && starMap && starMap[nameSegment]) {
                    foundStarMap = starMap[nameSegment];
                    starI = i;
                }
            }

            if (!foundMap && foundStarMap) {
                foundMap = foundStarMap;
                foundI = starI;
            }

            if (foundMap) {
                nameParts.splice(0, foundI, foundMap);
                name = nameParts.join('/');
            }
        }

        return name;
    }

    function makeRequire(relName, forceSync) {
        return function () {
            //A version of a require function that passes a moduleName
            //value for items that may need to
            //look up paths relative to the moduleName
            var args = aps.call(arguments, 0);

            //If first arg is not require('string'), and there is only
            //one arg, it is the array form without a callback. Insert
            //a null so that the following concat is correct.
            if (typeof args[0] !== 'string' && args.length === 1) {
                args.push(null);
            }
            return req.apply(undef, args.concat([relName, forceSync]));
        };
    }

    function makeNormalize(relName) {
        return function (name) {
            return normalize(name, relName);
        };
    }

    function makeLoad(depName) {
        return function (value) {
            defined[depName] = value;
        };
    }

    function callDep(name) {
        if (hasProp(waiting, name)) {
            var args = waiting[name];
            delete waiting[name];
            defining[name] = true;
            main.apply(undef, args);
        }

        if (!hasProp(defined, name) && !hasProp(defining, name)) {
            throw new Error('No ' + name);
        }
        return defined[name];
    }

    //Turns a plugin!resource to [plugin, resource]
    //with the plugin being undefined if the name
    //did not have a plugin prefix.
    function splitPrefix(name) {
        var prefix,
            index = name ? name.indexOf('!') : -1;
        if (index > -1) {
            prefix = name.substring(0, index);
            name = name.substring(index + 1, name.length);
        }
        return [prefix, name];
    }

    /**
     * Makes a name map, normalizing the name, and using a plugin
     * for normalization if necessary. Grabs a ref to plugin
     * too, as an optimization.
     */
    makeMap = function (name, relName) {
        var plugin,
            parts = splitPrefix(name),
            prefix = parts[0];

        name = parts[1];

        if (prefix) {
            prefix = normalize(prefix, relName);
            plugin = callDep(prefix);
        }

        //Normalize according
        if (prefix) {
            if (plugin && plugin.normalize) {
                name = plugin.normalize(name, makeNormalize(relName));
            } else {
                name = normalize(name, relName);
            }
        } else {
            name = normalize(name, relName);
            parts = splitPrefix(name);
            prefix = parts[0];
            name = parts[1];
            if (prefix) {
                plugin = callDep(prefix);
            }
        }

        //Using ridiculous property names for space reasons
        return {
            f: prefix ? prefix + '!' + name : name, //fullName
            n: name,
            pr: prefix,
            p: plugin
        };
    };

    function makeConfig(name) {
        return function () {
            return (config && config.config && config.config[name]) || {};
        };
    }

    handlers = {
        require: function (name) {
            return makeRequire(name);
        },
        exports: function (name) {
            var e = defined[name];
            if (typeof e !== 'undefined') {
                return e;
            } else {
                return (defined[name] = {});
            }
        },
        module: function (name) {
            return {
                id: name,
                uri: '',
                exports: defined[name],
                config: makeConfig(name)
            };
        }
    };

    main = function (name, deps, callback, relName) {
        var cjsModule, depName, ret, map, i,
            args = [],
            callbackType = typeof callback,
            usingExports;

        //Use name if no relName
        relName = relName || name;

        //Call the callback to define the module, if necessary.
        if (callbackType === 'undefined' || callbackType === 'function') {
            //Pull out the defined dependencies and pass the ordered
            //values to the callback.
            //Default to [require, exports, module] if no deps
            deps = !deps.length && callback.length ? ['require', 'exports', 'module'] : deps;
            for (i = 0; i < deps.length; i += 1) {
                map = makeMap(deps[i], relName);
                depName = map.f;

                //Fast path CommonJS standard dependencies.
                if (depName === "require") {
                    args[i] = handlers.require(name);
                } else if (depName === "exports") {
                    //CommonJS module spec 1.1
                    args[i] = handlers.exports(name);
                    usingExports = true;
                } else if (depName === "module") {
                    //CommonJS module spec 1.1
                    cjsModule = args[i] = handlers.module(name);
                } else if (hasProp(defined, depName) ||
                           hasProp(waiting, depName) ||
                           hasProp(defining, depName)) {
                    args[i] = callDep(depName);
                } else if (map.p) {
                    map.p.load(map.n, makeRequire(relName, true), makeLoad(depName), {});
                    args[i] = defined[depName];
                } else {
                    throw new Error(name + ' missing ' + depName);
                }
            }

            ret = callback ? callback.apply(defined[name], args) : undefined;

            if (name) {
                //If setting exports via "module" is in play,
                //favor that over return value and exports. After that,
                //favor a non-undefined return value over exports use.
                if (cjsModule && cjsModule.exports !== undef &&
                        cjsModule.exports !== defined[name]) {
                    defined[name] = cjsModule.exports;
                } else if (ret !== undef || !usingExports) {
                    //Use the return value from the function.
                    defined[name] = ret;
                }
            }
        } else if (name) {
            //May just be an object definition for the module. Only
            //worry about defining if have a module name.
            defined[name] = callback;
        }
    };

    requirejs = require = req = function (deps, callback, relName, forceSync, alt) {
        if (typeof deps === "string") {
            if (handlers[deps]) {
                //callback in this case is really relName
                return handlers[deps](callback);
            }
            //Just return the module wanted. In this scenario, the
            //deps arg is the module name, and second arg (if passed)
            //is just the relName.
            //Normalize module name, if it contains . or ..
            return callDep(makeMap(deps, callback).f);
        } else if (!deps.splice) {
            //deps is a config object, not an array.
            config = deps;
            if (config.deps) {
                req(config.deps, config.callback);
            }
            if (!callback) {
                return;
            }

            if (callback.splice) {
                //callback is an array, which means it is a dependency list.
                //Adjust args if there are dependencies
                deps = callback;
                callback = relName;
                relName = null;
            } else {
                deps = undef;
            }
        }

        //Support require(['a'])
        callback = callback || function () {};

        //If relName is a function, it is an errback handler,
        //so remove it.
        if (typeof relName === 'function') {
            relName = forceSync;
            forceSync = alt;
        }

        //Simulate async callback;
        if (forceSync) {
            main(undef, deps, callback, relName);
        } else {
            //Using a non-zero value because of concern for what old browsers
            //do, and latest browsers "upgrade" to 4 if lower value is used:
            //http://www.whatwg.org/specs/web-apps/current-work/multipage/timers.html#dom-windowtimers-settimeout:
            //If want a value immediately, use require('id') instead -- something
            //that works in almond on the global level, but not guaranteed and
            //unlikely to work in other AMD implementations.
            setTimeout(function () {
                main(undef, deps, callback, relName);
            }, 4);
        }

        return req;
    };

    /**
     * Just drops the config on the floor, but returns req in case
     * the config return value is used.
     */
    req.config = function (cfg) {
        return req(cfg);
    };

    /**
     * Expose module registry for debugging and tooling
     */
    requirejs._defined = defined;

    define = function (name, deps, callback) {
        if (typeof name !== 'string') {
            throw new Error('See almond README: incorrect module build, no module name');
        }

        //This module may not have dependencies
        if (!deps.splice) {
            //deps is not an array, so probably means
            //an object literal or factory function for
            //the value. Adjust args.
            callback = deps;
            deps = [];
        }

        if (!hasProp(defined, name) && !hasProp(waiting, name)) {
            waiting[name] = [name, deps, callback];
        }
    };

    define.amd = {
        jQuery: true
    };
}());

S2.requirejs = requirejs;S2.require = require;S2.define = define;
}
}());
S2.define("almond", function(){});

/* global jQuery:false, $:false */
S2.define('jquery',[],function () {
  var _$ = jQuery || $;

  if (_$ == null && console && console.error) {
    console.error(
      'Select2: An instance of jQuery or a jQuery-compatible library was not ' +
      'found. Make sure that you are including jQuery before Select2 on your ' +
      'web page.'
    );
  }

  return _$;
});

S2.define('select2/utils',[
  'jquery'
], function ($) {
  var Utils = {};

  Utils.Extend = function (ChildClass, SuperClass) {
    var __hasProp = {}.hasOwnProperty;

    function BaseConstructor () {
      this.constructor = ChildClass;
    }

    for (var key in SuperClass) {
      if (__hasProp.call(SuperClass, key)) {
        ChildClass[key] = SuperClass[key];
      }
    }

    BaseConstructor.prototype = SuperClass.prototype;
    ChildClass.prototype = new BaseConstructor();
    ChildClass.__super__ = SuperClass.prototype;

    return ChildClass;
  };

  function getMethods (theClass) {
    var proto = theClass.prototype;

    var methods = [];

    for (var methodName in proto) {
      var m = proto[methodName];

      if (typeof m !== 'function') {
        continue;
      }

      if (methodName === 'constructor') {
        continue;
      }

      methods.push(methodName);
    }

    return methods;
  }

  Utils.Decorate = function (SuperClass, DecoratorClass) {
    var decoratedMethods = getMethods(DecoratorClass);
    var superMethods = getMethods(SuperClass);

    function DecoratedClass () {
      var unshift = Array.prototype.unshift;

      var argCount = DecoratorClass.prototype.constructor.length;

      var calledConstructor = SuperClass.prototype.constructor;

      if (argCount > 0) {
        unshift.call(arguments, SuperClass.prototype.constructor);

        calledConstructor = DecoratorClass.prototype.constructor;
      }

      calledConstructor.apply(this, arguments);
    }

    DecoratorClass.displayName = SuperClass.displayName;

    function ctr () {
      this.constructor = DecoratedClass;
    }

    DecoratedClass.prototype = new ctr();

    for (var m = 0; m < superMethods.length; m++) {
        var superMethod = superMethods[m];

        DecoratedClass.prototype[superMethod] =
          SuperClass.prototype[superMethod];
    }

    var calledMethod = function (methodName) {
      // Stub out the original method if it's not decorating an actual method
      var originalMethod = function () {};

      if (methodName in DecoratedClass.prototype) {
        originalMethod = DecoratedClass.prototype[methodName];
      }

      var decoratedMethod = DecoratorClass.prototype[methodName];

      return function () {
        var unshift = Array.prototype.unshift;

        unshift.call(arguments, originalMethod);

        return decoratedMethod.apply(this, arguments);
      };
    };

    for (var d = 0; d < decoratedMethods.length; d++) {
      var decoratedMethod = decoratedMethods[d];

      DecoratedClass.prototype[decoratedMethod] = calledMethod(decoratedMethod);
    }

    return DecoratedClass;
  };

  var Observable = function () {
    this.listeners = {};
  };

  Observable.prototype.on = function (event, callback) {
    this.listeners = this.listeners || {};

    if (event in this.listeners) {
      this.listeners[event].push(callback);
    } else {
      this.listeners[event] = [callback];
    }
  };

  Observable.prototype.trigger = function (event) {
    var slice = Array.prototype.slice;
    var params = slice.call(arguments, 1);

    this.listeners = this.listeners || {};

    // Params should always come in as an array
    if (params == null) {
      params = [];
    }

    // If there are no arguments to the event, use a temporary object
    if (params.length === 0) {
      params.push({});
    }

    // Set the `_type` of the first object to the event
    params[0]._type = event;

    if (event in this.listeners) {
      this.invoke(this.listeners[event], slice.call(arguments, 1));
    }

    if ('*' in this.listeners) {
      this.invoke(this.listeners['*'], arguments);
    }
  };

  Observable.prototype.invoke = function (listeners, params) {
    for (var i = 0, len = listeners.length; i < len; i++) {
      listeners[i].apply(this, params);
    }
  };

  Utils.Observable = Observable;

  Utils.generateChars = function (length) {
    var chars = '';

    for (var i = 0; i < length; i++) {
      var randomChar = Math.floor(Math.random() * 36);
      chars += randomChar.toString(36);
    }

    return chars;
  };

  Utils.bind = function (func, context) {
    return function () {
      func.apply(context, arguments);
    };
  };

  Utils._convertData = function (data) {
    for (var originalKey in data) {
      var keys = originalKey.split('-');

      var dataLevel = data;

      if (keys.length === 1) {
        continue;
      }

      for (var k = 0; k < keys.length; k++) {
        var key = keys[k];

        // Lowercase the first letter
        // By default, dash-separated becomes camelCase
        key = key.substring(0, 1).toLowerCase() + key.substring(1);

        if (!(key in dataLevel)) {
          dataLevel[key] = {};
        }

        if (k == keys.length - 1) {
          dataLevel[key] = data[originalKey];
        }

        dataLevel = dataLevel[key];
      }

      delete data[originalKey];
    }

    return data;
  };

  Utils.hasScroll = function (index, el) {
    // Adapted from the function created by @ShadowScripter
    // and adapted by @BillBarry on the Stack Exchange Code Review website.
    // The original code can be found at
    // http://codereview.stackexchange.com/q/13338
    // and was designed to be used with the Sizzle selector engine.

    var $el = $(el);
    var overflowX = el.style.overflowX;
    var overflowY = el.style.overflowY;

    //Check both x and y declarations
    if (overflowX === overflowY &&
        (overflowY === 'hidden' || overflowY === 'visible')) {
      return false;
    }

    if (overflowX === 'scroll' || overflowY === 'scroll') {
      return true;
    }

    return ($el.innerHeight() < el.scrollHeight ||
      $el.innerWidth() < el.scrollWidth);
  };

  Utils.escapeMarkup = function (markup) {
    var replaceMap = {
      '\\': '&#92;',
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      '\'': '&#39;',
      '/': '&#47;'
    };

    // Do not try to escape the markup if it's not a string
    if (typeof markup !== 'string') {
      return markup;
    }

    return String(markup).replace(/[&<>"'\/\\]/g, function (match) {
      return replaceMap[match];
    });
  };

  // Append an array of jQuery nodes to a given element.
  Utils.appendMany = function ($element, $nodes) {
    // jQuery 1.7.x does not support $.fn.append() with an array
    // Fall back to a jQuery object collection using $.fn.add()
    if ($.fn.jquery.substr(0, 3) === '1.7') {
      var $jqNodes = $();

      $.map($nodes, function (node) {
        $jqNodes = $jqNodes.add(node);
      });

      $nodes = $jqNodes;
    }

    $element.append($nodes);
  };

  return Utils;
});

S2.define('select2/results',[
  'jquery',
  './utils'
], function ($, Utils) {
  function Results ($element, options, dataAdapter) {
    this.$element = $element;
    this.data = dataAdapter;
    this.options = options;

    Results.__super__.constructor.call(this);
  }

  Utils.Extend(Results, Utils.Observable);

  Results.prototype.render = function () {
    var $results = $(
      '<ul class="select2-results__options" role="tree"></ul>'
    );

    if (this.options.get('multiple')) {
      $results.attr('aria-multiselectable', 'true');
    }

    this.$results = $results;

    return $results;
  };

  Results.prototype.clear = function () {
    this.$results.empty();
  };

  Results.prototype.displayMessage = function (params) {
    var escapeMarkup = this.options.get('escapeMarkup');

    this.clear();
    this.hideLoading();

    var $message = $(
      '<li role="treeitem" aria-live="assertive"' +
      ' class="select2-results__option"></li>'
    );

    var message = this.options.get('translations').get(params.message);

    $message.append(
      escapeMarkup(
        message(params.args)
      )
    );

    $message[0].className += ' select2-results__message';

    this.$results.append($message);
  };

  Results.prototype.hideMessages = function () {
    this.$results.find('.select2-results__message').remove();
  };

  Results.prototype.append = function (data) {
    this.hideLoading();

    var $options = [];

    if (data.results == null || data.results.length === 0) {
      if (this.$results.children().length === 0) {
        this.trigger('results:message', {
          message: 'noResults'
        });
      }

      return;
    }

    data.results = this.sort(data.results);

    for (var d = 0; d < data.results.length; d++) {
      var item = data.results[d];

      var $option = this.option(item);

      $options.push($option);
    }

    this.$results.append($options);
  };

  Results.prototype.position = function ($results, $dropdown) {
    var $resultsContainer = $dropdown.find('.select2-results');
    $resultsContainer.append($results);
  };

  Results.prototype.sort = function (data) {
    var sorter = this.options.get('sorter');

    return sorter(data);
  };

  Results.prototype.highlightFirstItem = function () {
    var $options = this.$results
      .find('.select2-results__option[aria-selected]');

    var $selected = $options.filter('[aria-selected=true]');

    // Check if there are any selected options
    if ($selected.length > 0) {
      // If there are selected options, highlight the first
      $selected.first().trigger('mouseenter');
    } else {
      // If there are no selected options, highlight the first option
      // in the dropdown
      $options.first().trigger('mouseenter');
    }

    this.ensureHighlightVisible();
  };

  Results.prototype.setClasses = function () {
    var self = this;

    this.data.current(function (selected) {
      var selectedIds = $.map(selected, function (s) {
        return s.id.toString();
      });

      var $options = self.$results
        .find('.select2-results__option[aria-selected]');

      $options.each(function () {
        var $option = $(this);

        var item = $.data(this, 'data');

        // id needs to be converted to a string when comparing
        var id = '' + item.id;

        if ((item.element != null && item.element.selected) ||
            (item.element == null && $.inArray(id, selectedIds) > -1)) {
          $option.attr('aria-selected', 'true');
        } else {
          $option.attr('aria-selected', 'false');
        }
      });

    });
  };

  Results.prototype.showLoading = function (params) {
    this.hideLoading();

    var loadingMore = this.options.get('translations').get('searching');

    var loading = {
      disabled: true,
      loading: true,
      text: loadingMore(params)
    };
    var $loading = this.option(loading);
    $loading.className += ' loading-results';

    this.$results.prepend($loading);
  };

  Results.prototype.hideLoading = function () {
    this.$results.find('.loading-results').remove();
  };

  Results.prototype.option = function (data) {
    var option = document.createElement('li');
    option.className = 'select2-results__option';

    var attrs = {
      'role': 'treeitem',
      'aria-selected': 'false'
    };

    if (data.disabled) {
      delete attrs['aria-selected'];
      attrs['aria-disabled'] = 'true';
    }

    if (data.id == null) {
      delete attrs['aria-selected'];
    }

    if (data._resultId != null) {
      option.id = data._resultId;
    }

    if (data.title) {
      option.title = data.title;
    }

    if (data.children) {
      attrs.role = 'group';
      attrs['aria-label'] = data.text;
      delete attrs['aria-selected'];
    }

    for (var attr in attrs) {
      var val = attrs[attr];

      option.setAttribute(attr, val);
    }

    if (data.children) {
      var $option = $(option);

      var label = document.createElement('strong');
      label.className = 'select2-results__group';

      var $label = $(label);
      this.template(data, label);

      var $children = [];

      for (var c = 0; c < data.children.length; c++) {
        var child = data.children[c];

        var $child = this.option(child);

        $children.push($child);
      }

      var $childrenContainer = $('<ul></ul>', {
        'class': 'select2-results__options select2-results__options--nested'
      });

      $childrenContainer.append($children);

      $option.append(label);
      $option.append($childrenContainer);
    } else {
      this.template(data, option);
    }

    $.data(option, 'data', data);

    return option;
  };

  Results.prototype.bind = function (container, $container) {
    var self = this;

    var id = container.id + '-results';

    this.$results.attr('id', id);

    container.on('results:all', function (params) {
      self.clear();
      self.append(params.data);

      if (container.isOpen()) {
        self.setClasses();
        self.highlightFirstItem();
      }
    });

    container.on('results:append', function (params) {
      self.append(params.data);

      if (container.isOpen()) {
        self.setClasses();
      }
    });

    container.on('query', function (params) {
      self.hideMessages();
      self.showLoading(params);
    });

    container.on('select', function () {
      if (!container.isOpen()) {
        return;
      }

      self.setClasses();
      self.highlightFirstItem();
    });

    container.on('unselect', function () {
      if (!container.isOpen()) {
        return;
      }

      self.setClasses();
      self.highlightFirstItem();
    });

    container.on('open', function () {
      // When the dropdown is open, aria-expended="true"
      self.$results.attr('aria-expanded', 'true');
      self.$results.attr('aria-hidden', 'false');

      self.setClasses();
      self.ensureHighlightVisible();
    });

    container.on('close', function () {
      // When the dropdown is closed, aria-expended="false"
      self.$results.attr('aria-expanded', 'false');
      self.$results.attr('aria-hidden', 'true');
      self.$results.removeAttr('aria-activedescendant');
    });

    container.on('results:toggle', function () {
      var $highlighted = self.getHighlightedResults();

      if ($highlighted.length === 0) {
        return;
      }

      $highlighted.trigger('mouseup');
    });

    container.on('results:select', function () {
      var $highlighted = self.getHighlightedResults();

      if ($highlighted.length === 0) {
        return;
      }

      var data = $highlighted.data('data');

      if ($highlighted.attr('aria-selected') == 'true') {
        self.trigger('close', {});
      } else {
        self.trigger('select', {
          data: data
        });
      }
    });

    container.on('results:previous', function () {
      var $highlighted = self.getHighlightedResults();

      var $options = self.$results.find('[aria-selected]');

      var currentIndex = $options.index($highlighted);

      // If we are already at te top, don't move further
      if (currentIndex === 0) {
        return;
      }

      var nextIndex = currentIndex - 1;

      // If none are highlighted, highlight the first
      if ($highlighted.length === 0) {
        nextIndex = 0;
      }

      var $next = $options.eq(nextIndex);

      $next.trigger('mouseenter');

      var currentOffset = self.$results.offset().top;
      var nextTop = $next.offset().top;
      var nextOffset = self.$results.scrollTop() + (nextTop - currentOffset);

      if (nextIndex === 0) {
        self.$results.scrollTop(0);
      } else if (nextTop - currentOffset < 0) {
        self.$results.scrollTop(nextOffset);
      }
    });

    container.on('results:next', function () {
      var $highlighted = self.getHighlightedResults();

      var $options = self.$results.find('[aria-selected]');

      var currentIndex = $options.index($highlighted);

      var nextIndex = currentIndex + 1;

      // If we are at the last option, stay there
      if (nextIndex >= $options.length) {
        return;
      }

      var $next = $options.eq(nextIndex);

      $next.trigger('mouseenter');

      var currentOffset = self.$results.offset().top +
        self.$results.outerHeight(false);
      var nextBottom = $next.offset().top + $next.outerHeight(false);
      var nextOffset = self.$results.scrollTop() + nextBottom - currentOffset;

      if (nextIndex === 0) {
        self.$results.scrollTop(0);
      } else if (nextBottom > currentOffset) {
        self.$results.scrollTop(nextOffset);
      }
    });

    container.on('results:focus', function (params) {
      params.element.addClass('select2-results__option--highlighted');
    });

    container.on('results:message', function (params) {
      self.displayMessage(params);
    });

    if ($.fn.mousewheel) {
      this.$results.on('mousewheel', function (e) {
        var top = self.$results.scrollTop();

        var bottom = self.$results.get(0).scrollHeight - top + e.deltaY;

        var isAtTop = e.deltaY > 0 && top - e.deltaY <= 0;
        var isAtBottom = e.deltaY < 0 && bottom <= self.$results.height();

        if (isAtTop) {
          self.$results.scrollTop(0);

          e.preventDefault();
          e.stopPropagation();
        } else if (isAtBottom) {
          self.$results.scrollTop(
            self.$results.get(0).scrollHeight - self.$results.height()
          );

          e.preventDefault();
          e.stopPropagation();
        }
      });
    }

    this.$results.on('mouseup', '.select2-results__option[aria-selected]',
      function (evt) {
      var $this = $(this);

      var data = $this.data('data');

      if ($this.attr('aria-selected') === 'true') {
        if (self.options.get('multiple')) {
          self.trigger('unselect', {
            originalEvent: evt,
            data: data
          });
        } else {
          self.trigger('close', {});
        }

        return;
      }

      self.trigger('select', {
        originalEvent: evt,
        data: data
      });
    });

    this.$results.on('mouseenter', '.select2-results__option[aria-selected]',
      function (evt) {
      var data = $(this).data('data');

      self.getHighlightedResults()
          .removeClass('select2-results__option--highlighted');

      self.trigger('results:focus', {
        data: data,
        element: $(this)
      });
    });
  };

  Results.prototype.getHighlightedResults = function () {
    var $highlighted = this.$results
    .find('.select2-results__option--highlighted');

    return $highlighted;
  };

  Results.prototype.destroy = function () {
    this.$results.remove();
  };

  Results.prototype.ensureHighlightVisible = function () {
    var $highlighted = this.getHighlightedResults();

    if ($highlighted.length === 0) {
      return;
    }

    var $options = this.$results.find('[aria-selected]');

    var currentIndex = $options.index($highlighted);

    var currentOffset = this.$results.offset().top;
    var nextTop = $highlighted.offset().top;
    var nextOffset = this.$results.scrollTop() + (nextTop - currentOffset);

    var offsetDelta = nextTop - currentOffset;
    nextOffset -= $highlighted.outerHeight(false) * 2;

    if (currentIndex <= 2) {
      this.$results.scrollTop(0);
    } else if (offsetDelta > this.$results.outerHeight() || offsetDelta < 0) {
      this.$results.scrollTop(nextOffset);
    }
  };

  Results.prototype.template = function (result, container) {
    var template = this.options.get('templateResult');
    var escapeMarkup = this.options.get('escapeMarkup');

    var content = template(result, container);

    if (content == null) {
      container.style.display = 'none';
    } else if (typeof content === 'string') {
      container.innerHTML = escapeMarkup(content);
    } else {
      $(container).append(content);
    }
  };

  return Results;
});

S2.define('select2/keys',[

], function () {
  var KEYS = {
    BACKSPACE: 8,
    TAB: 9,
    ENTER: 13,
    SHIFT: 16,
    CTRL: 17,
    ALT: 18,
    ESC: 27,
    SPACE: 32,
    PAGE_UP: 33,
    PAGE_DOWN: 34,
    END: 35,
    HOME: 36,
    LEFT: 37,
    UP: 38,
    RIGHT: 39,
    DOWN: 40,
    DELETE: 46
  };

  return KEYS;
});

S2.define('select2/selection/base',[
  'jquery',
  '../utils',
  '../keys'
], function ($, Utils, KEYS) {
  function BaseSelection ($element, options) {
    this.$element = $element;
    this.options = options;

    BaseSelection.__super__.constructor.call(this);
  }

  Utils.Extend(BaseSelection, Utils.Observable);

  BaseSelection.prototype.render = function () {
    var $selection = $(
      '<span class="select2-selection" role="combobox" ' +
      ' aria-haspopup="true" aria-expanded="false">' +
      '</span>'
    );

    this._tabindex = 0;

    if (this.$element.data('old-tabindex') != null) {
      this._tabindex = this.$element.data('old-tabindex');
    } else if (this.$element.attr('tabindex') != null) {
      this._tabindex = this.$element.attr('tabindex');
    }

    $selection.attr('title', this.$element.attr('title'));
    $selection.attr('tabindex', this._tabindex);

    this.$selection = $selection;

    return $selection;
  };

  BaseSelection.prototype.bind = function (container, $container) {
    var self = this;

    var id = container.id + '-container';
    var resultsId = container.id + '-results';

    this.container = container;

    this.$selection.on('focus', function (evt) {
      self.trigger('focus', evt);
    });

    this.$selection.on('blur', function (evt) {
      self._handleBlur(evt);
    });

    this.$selection.on('keydown', function (evt) {
      self.trigger('keypress', evt);

      if (evt.which === KEYS.SPACE) {
        evt.preventDefault();
      }
    });

    container.on('results:focus', function (params) {
      self.$selection.attr('aria-activedescendant', params.data._resultId);
    });

    container.on('selection:update', function (params) {
      self.update(params.data);
    });

    container.on('open', function () {
      // When the dropdown is open, aria-expanded="true"
      self.$selection.attr('aria-expanded', 'true');
      self.$selection.attr('aria-owns', resultsId);

      self._attachCloseHandler(container);
    });

    container.on('close', function () {
      // When the dropdown is closed, aria-expanded="false"
      self.$selection.attr('aria-expanded', 'false');
      self.$selection.removeAttr('aria-activedescendant');
      self.$selection.removeAttr('aria-owns');

      self.$selection.focus();

      self._detachCloseHandler(container);
    });

    container.on('enable', function () {
      self.$selection.attr('tabindex', self._tabindex);
    });

    container.on('disable', function () {
      self.$selection.attr('tabindex', '-1');
    });
  };

  BaseSelection.prototype._handleBlur = function (evt) {
    var self = this;

    // This needs to be delayed as the active element is the body when the tab
    // key is pressed, possibly along with others.
    window.setTimeout(function () {
      // Don't trigger `blur` if the focus is still in the selection
      if (
        (document.activeElement == self.$selection[0]) ||
        ($.contains(self.$selection[0], document.activeElement))
      ) {
        return;
      }

      self.trigger('blur', evt);
    }, 1);
  };

  BaseSelection.prototype._attachCloseHandler = function (container) {
    var self = this;

    $(document.body).on('mousedown.select2.' + container.id, function (e) {
      var $target = $(e.target);

      var $select = $target.closest('.select2');

      var $all = $('.select2.select2-container--open');

      $all.each(function () {
        var $this = $(this);

        if (this == $select[0]) {
          return;
        }

        var $element = $this.data('element');

        $element.select2('close');
      });
    });
  };

  BaseSelection.prototype._detachCloseHandler = function (container) {
    $(document.body).off('mousedown.select2.' + container.id);
  };

  BaseSelection.prototype.position = function ($selection, $container) {
    var $selectionContainer = $container.find('.selection');
    $selectionContainer.append($selection);
  };

  BaseSelection.prototype.destroy = function () {
    this._detachCloseHandler(this.container);
  };

  BaseSelection.prototype.update = function (data) {
    throw new Error('The `update` method must be defined in child classes.');
  };

  return BaseSelection;
});

S2.define('select2/selection/single',[
  'jquery',
  './base',
  '../utils',
  '../keys'
], function ($, BaseSelection, Utils, KEYS) {
  function SingleSelection () {
    SingleSelection.__super__.constructor.apply(this, arguments);
  }

  Utils.Extend(SingleSelection, BaseSelection);

  SingleSelection.prototype.render = function () {
    var $selection = SingleSelection.__super__.render.call(this);

    $selection.addClass('select2-selection--single');

    $selection.html(
      '<span class="select2-selection__rendered"></span>' +
      '<span class="select2-selection__arrow" role="presentation">' +
        '<b role="presentation"></b>' +
      '</span>'
    );

    return $selection;
  };

  SingleSelection.prototype.bind = function (container, $container) {
    var self = this;

    SingleSelection.__super__.bind.apply(this, arguments);

    var id = container.id + '-container';

    this.$selection.find('.select2-selection__rendered').attr('id', id);
    this.$selection.attr('aria-labelledby', id);

    this.$selection.on('mousedown', function (evt) {
      // Only respond to left clicks
      if (evt.which !== 1) {
        return;
      }

      self.trigger('toggle', {
        originalEvent: evt
      });
    });

    this.$selection.on('focus', function (evt) {
      // User focuses on the container
    });

    this.$selection.on('blur', function (evt) {
      // User exits the container
    });

    container.on('focus', function (evt) {
      if (!container.isOpen()) {
        self.$selection.focus();
      }
    });

    container.on('selection:update', function (params) {
      self.update(params.data);
    });
  };

  SingleSelection.prototype.clear = function () {
    this.$selection.find('.select2-selection__rendered').empty();
  };

  SingleSelection.prototype.display = function (data, container) {
    var template = this.options.get('templateSelection');
    var escapeMarkup = this.options.get('escapeMarkup');

    return escapeMarkup(template(data, container));
  };

  SingleSelection.prototype.selectionContainer = function () {
    return $('<span></span>');
  };

  SingleSelection.prototype.update = function (data) {
    if (data.length === 0) {
      this.clear();
      return;
    }

    var selection = data[0];

    var $rendered = this.$selection.find('.select2-selection__rendered');
    var formatted = this.display(selection, $rendered);

    $rendered.empty().append(formatted);
    $rendered.prop('title', selection.title || selection.text);
  };

  return SingleSelection;
});

S2.define('select2/selection/multiple',[
  'jquery',
  './base',
  '../utils'
], function ($, BaseSelection, Utils) {
  function MultipleSelection ($element, options) {
    MultipleSelection.__super__.constructor.apply(this, arguments);
  }

  Utils.Extend(MultipleSelection, BaseSelection);

  MultipleSelection.prototype.render = function () {
    var $selection = MultipleSelection.__super__.render.call(this);

    $selection.addClass('select2-selection--multiple');

    $selection.html(
      '<ul class="select2-selection__rendered"></ul>'
    );

    return $selection;
  };

  MultipleSelection.prototype.bind = function (container, $container) {
    var self = this;

    MultipleSelection.__super__.bind.apply(this, arguments);

    this.$selection.on('click', function (evt) {
      self.trigger('toggle', {
        originalEvent: evt
      });
    });

    this.$selection.on(
      'click',
      '.select2-selection__choice__remove',
      function (evt) {
        // Ignore the event if it is disabled
        if (self.options.get('disabled')) {
          return;
        }

        var $remove = $(this);
        var $selection = $remove.parent();

        var data = $selection.data('data');

        self.trigger('unselect', {
          originalEvent: evt,
          data: data
        });
      }
    );
  };

  MultipleSelection.prototype.clear = function () {
    this.$selection.find('.select2-selection__rendered').empty();
  };

  MultipleSelection.prototype.display = function (data, container) {
    var template = this.options.get('templateSelection');
    var escapeMarkup = this.options.get('escapeMarkup');

    return escapeMarkup(template(data, container));
  };

  MultipleSelection.prototype.selectionContainer = function () {
    var $container = $(
      '<li class="select2-selection__choice">' +
        '<span class="select2-selection__choice__remove" role="presentation">' +
          '&times;' +
        '</span>' +
      '</li>'
    );

    return $container;
  };

  MultipleSelection.prototype.update = function (data) {
    this.clear();

    if (data.length === 0) {
      return;
    }

    var $selections = [];

    for (var d = 0; d < data.length; d++) {
      var selection = data[d];

      var $selection = this.selectionContainer();
      var formatted = this.display(selection, $selection);

      $selection.append(formatted);
      $selection.prop('title', selection.title || selection.text);

      $selection.data('data', selection);

      $selections.push($selection);
    }

    var $rendered = this.$selection.find('.select2-selection__rendered');

    Utils.appendMany($rendered, $selections);
  };

  return MultipleSelection;
});

S2.define('select2/selection/placeholder',[
  '../utils'
], function (Utils) {
  function Placeholder (decorated, $element, options) {
    this.placeholder = this.normalizePlaceholder(options.get('placeholder'));

    decorated.call(this, $element, options);
  }

  Placeholder.prototype.normalizePlaceholder = function (_, placeholder) {
    if (typeof placeholder === 'string') {
      placeholder = {
        id: '',
        text: placeholder
      };
    }

    return placeholder;
  };

  Placeholder.prototype.createPlaceholder = function (decorated, placeholder) {
    var $placeholder = this.selectionContainer();

    $placeholder.html(this.display(placeholder));
    $placeholder.addClass('select2-selection__placeholder')
                .removeClass('select2-selection__choice');

    return $placeholder;
  };

  Placeholder.prototype.update = function (decorated, data) {
    var singlePlaceholder = (
      data.length == 1 && data[0].id != this.placeholder.id
    );
    var multipleSelections = data.length > 1;

    if (multipleSelections || singlePlaceholder) {
      return decorated.call(this, data);
    }

    this.clear();

    var $placeholder = this.createPlaceholder(this.placeholder);

    this.$selection.find('.select2-selection__rendered').append($placeholder);
  };

  return Placeholder;
});

S2.define('select2/selection/allowClear',[
  'jquery',
  '../keys'
], function ($, KEYS) {
  function AllowClear () { }

  AllowClear.prototype.bind = function (decorated, container, $container) {
    var self = this;

    decorated.call(this, container, $container);

    if (this.placeholder == null) {
      if (this.options.get('debug') && window.console && console.error) {
        console.error(
          'Select2: The `allowClear` option should be used in combination ' +
          'with the `placeholder` option.'
        );
      }
    }

    this.$selection.on('mousedown', '.select2-selection__clear',
      function (evt) {
        self._handleClear(evt);
    });

    container.on('keypress', function (evt) {
      self._handleKeyboardClear(evt, container);
    });
  };

  AllowClear.prototype._handleClear = function (_, evt) {
    // Ignore the event if it is disabled
    if (this.options.get('disabled')) {
      return;
    }

    var $clear = this.$selection.find('.select2-selection__clear');

    // Ignore the event if nothing has been selected
    if ($clear.length === 0) {
      return;
    }

    evt.stopPropagation();

    var data = $clear.data('data');

    for (var d = 0; d < data.length; d++) {
      var unselectData = {
        data: data[d]
      };

      // Trigger the `unselect` event, so people can prevent it from being
      // cleared.
      this.trigger('unselect', unselectData);

      // If the event was prevented, don't clear it out.
      if (unselectData.prevented) {
        return;
      }
    }

    this.$element.val(this.placeholder.id).trigger('change');

    this.trigger('toggle', {});
  };

  AllowClear.prototype._handleKeyboardClear = function (_, evt, container) {
    if (container.isOpen()) {
      return;
    }

    if (evt.which == KEYS.DELETE || evt.which == KEYS.BACKSPACE) {
      this._handleClear(evt);
    }
  };

  AllowClear.prototype.update = function (decorated, data) {
    decorated.call(this, data);

    if (this.$selection.find('.select2-selection__placeholder').length > 0 ||
        data.length === 0) {
      return;
    }

    var $remove = $(
      '<span class="select2-selection__clear">' +
        '&times;' +
      '</span>'
    );
    $remove.data('data', data);

    this.$selection.find('.select2-selection__rendered').prepend($remove);
  };

  return AllowClear;
});

S2.define('select2/selection/search',[
  'jquery',
  '../utils',
  '../keys'
], function ($, Utils, KEYS) {
  function Search (decorated, $element, options) {
    decorated.call(this, $element, options);
  }

  Search.prototype.render = function (decorated) {
    var $search = $(
      '<li class="select2-search select2-search--inline">' +
        '<input class="select2-search__field" type="search" tabindex="-1"' +
        ' autocomplete="off" autocorrect="off" autocapitalize="off"' +
        ' spellcheck="false" role="textbox" aria-autocomplete="list" />' +
      '</li>'
    );

    this.$searchContainer = $search;
    this.$search = $search.find('input');

    var $rendered = decorated.call(this);

    this._transferTabIndex();

    return $rendered;
  };

  Search.prototype.bind = function (decorated, container, $container) {
    var self = this;

    decorated.call(this, container, $container);

    container.on('open', function () {
      self.$search.trigger('focus');
    });

    container.on('close', function () {
      self.$search.val('');
      self.$search.removeAttr('aria-activedescendant');
      self.$search.trigger('focus');
    });

    container.on('enable', function () {
      self.$search.prop('disabled', false);

      self._transferTabIndex();
    });

    container.on('disable', function () {
      self.$search.prop('disabled', true);
    });

    container.on('focus', function (evt) {
      self.$search.trigger('focus');
    });

    container.on('results:focus', function (params) {
      self.$search.attr('aria-activedescendant', params.id);
    });

    this.$selection.on('focusin', '.select2-search--inline', function (evt) {
      self.trigger('focus', evt);
    });

    this.$selection.on('focusout', '.select2-search--inline', function (evt) {
      self._handleBlur(evt);
    });

    this.$selection.on('keydown', '.select2-search--inline', function (evt) {
      evt.stopPropagation();

      self.trigger('keypress', evt);

      self._keyUpPrevented = evt.isDefaultPrevented();

      var key = evt.which;

      if (key === KEYS.BACKSPACE && self.$search.val() === '') {
        var $previousChoice = self.$searchContainer
          .prev('.select2-selection__choice');

        if ($previousChoice.length > 0) {
          var item = $previousChoice.data('data');

          self.searchRemoveChoice(item);

          evt.preventDefault();
        }
      }
    });

    // Try to detect the IE version should the `documentMode` property that
    // is stored on the document. This is only implemented in IE and is
    // slightly cleaner than doing a user agent check.
    // This property is not available in Edge, but Edge also doesn't have
    // this bug.
    var msie = document.documentMode;
    var disableInputEvents = msie && msie <= 11;

    // Workaround for browsers which do not support the `input` event
    // This will prevent double-triggering of events for browsers which support
    // both the `keyup` and `input` events.
    this.$selection.on(
      'input.searchcheck',
      '.select2-search--inline',
      function (evt) {
        // IE will trigger the `input` event when a placeholder is used on a
        // search box. To get around this issue, we are forced to ignore all
        // `input` events in IE and keep using `keyup`.
        if (disableInputEvents) {
          self.$selection.off('input.search input.searchcheck');
          return;
        }

        // Unbind the duplicated `keyup` event
        self.$selection.off('keyup.search');
      }
    );

    this.$selection.on(
      'keyup.search input.search',
      '.select2-search--inline',
      function (evt) {
        // IE will trigger the `input` event when a placeholder is used on a
        // search box. To get around this issue, we are forced to ignore all
        // `input` events in IE and keep using `keyup`.
        if (disableInputEvents && evt.type === 'input') {
          self.$selection.off('input.search input.searchcheck');
          return;
        }

        var key = evt.which;

        // We can freely ignore events from modifier keys
        if (key == KEYS.SHIFT || key == KEYS.CTRL || key == KEYS.ALT) {
          return;
        }

        // Tabbing will be handled during the `keydown` phase
        if (key == KEYS.TAB) {
          return;
        }

        self.handleSearch(evt);
      }
    );
  };

  /**
   * This method will transfer the tabindex attribute from the rendered
   * selection to the search box. This allows for the search box to be used as
   * the primary focus instead of the selection container.
   *
   * @private
   */
  Search.prototype._transferTabIndex = function (decorated) {
    this.$search.attr('tabindex', this.$selection.attr('tabindex'));
    this.$selection.attr('tabindex', '-1');
  };

  Search.prototype.createPlaceholder = function (decorated, placeholder) {
    this.$search.attr('placeholder', placeholder.text);
  };

  Search.prototype.update = function (decorated, data) {
    var searchHadFocus = this.$search[0] == document.activeElement;

    this.$search.attr('placeholder', '');

    decorated.call(this, data);

    this.$selection.find('.select2-selection__rendered')
                   .append(this.$searchContainer);

    this.resizeSearch();
    if (searchHadFocus) {
      this.$search.focus();
    }
  };

  Search.prototype.handleSearch = function () {
    this.resizeSearch();

    if (!this._keyUpPrevented) {
      var input = this.$search.val();

      this.trigger('query', {
        term: input
      });
    }

    this._keyUpPrevented = false;
  };

  Search.prototype.searchRemoveChoice = function (decorated, item) {
    this.trigger('unselect', {
      data: item
    });

    this.$search.val(item.text);
    this.handleSearch();
  };

  Search.prototype.resizeSearch = function () {
    this.$search.css('width', '25px');

    var width = '';

    if (this.$search.attr('placeholder') !== '') {
      width = this.$selection.find('.select2-selection__rendered').innerWidth();
    } else {
      var minimumWidth = this.$search.val().length + 1;

      width = (minimumWidth * 0.75) + 'em';
    }

    this.$search.css('width', width);
  };

  return Search;
});

S2.define('select2/selection/eventRelay',[
  'jquery'
], function ($) {
  function EventRelay () { }

  EventRelay.prototype.bind = function (decorated, container, $container) {
    var self = this;
    var relayEvents = [
      'open', 'opening',
      'close', 'closing',
      'select', 'selecting',
      'unselect', 'unselecting'
    ];

    var preventableEvents = ['opening', 'closing', 'selecting', 'unselecting'];

    decorated.call(this, container, $container);

    container.on('*', function (name, params) {
      // Ignore events that should not be relayed
      if ($.inArray(name, relayEvents) === -1) {
        return;
      }

      // The parameters should always be an object
      params = params || {};

      // Generate the jQuery event for the Select2 event
      var evt = $.Event('select2:' + name, {
        params: params
      });

      self.$element.trigger(evt);

      // Only handle preventable events if it was one
      if ($.inArray(name, preventableEvents) === -1) {
        return;
      }

      params.prevented = evt.isDefaultPrevented();
    });
  };

  return EventRelay;
});

S2.define('select2/translation',[
  'jquery',
  'require'
], function ($, require) {
  function Translation (dict) {
    this.dict = dict || {};
  }

  Translation.prototype.all = function () {
    return this.dict;
  };

  Translation.prototype.get = function (key) {
    return this.dict[key];
  };

  Translation.prototype.extend = function (translation) {
    this.dict = $.extend({}, translation.all(), this.dict);
  };

  // Static functions

  Translation._cache = {};

  Translation.loadPath = function (path) {
    if (!(path in Translation._cache)) {
      var translations = require(path);

      Translation._cache[path] = translations;
    }

    return new Translation(Translation._cache[path]);
  };

  return Translation;
});

S2.define('select2/diacritics',[

], function () {
  var diacritics = {
    '\u24B6': 'A',
    '\uFF21': 'A',
    '\u00C0': 'A',
    '\u00C1': 'A',
    '\u00C2': 'A',
    '\u1EA6': 'A',
    '\u1EA4': 'A',
    '\u1EAA': 'A',
    '\u1EA8': 'A',
    '\u00C3': 'A',
    '\u0100': 'A',
    '\u0102': 'A',
    '\u1EB0': 'A',
    '\u1EAE': 'A',
    '\u1EB4': 'A',
    '\u1EB2': 'A',
    '\u0226': 'A',
    '\u01E0': 'A',
    '\u00C4': 'A',
    '\u01DE': 'A',
    '\u1EA2': 'A',
    '\u00C5': 'A',
    '\u01FA': 'A',
    '\u01CD': 'A',
    '\u0200': 'A',
    '\u0202': 'A',
    '\u1EA0': 'A',
    '\u1EAC': 'A',
    '\u1EB6': 'A',
    '\u1E00': 'A',
    '\u0104': 'A',
    '\u023A': 'A',
    '\u2C6F': 'A',
    '\uA732': 'AA',
    '\u00C6': 'AE',
    '\u01FC': 'AE',
    '\u01E2': 'AE',
    '\uA734': 'AO',
    '\uA736': 'AU',
    '\uA738': 'AV',
    '\uA73A': 'AV',
    '\uA73C': 'AY',
    '\u24B7': 'B',
    '\uFF22': 'B',
    '\u1E02': 'B',
    '\u1E04': 'B',
    '\u1E06': 'B',
    '\u0243': 'B',
    '\u0182': 'B',
    '\u0181': 'B',
    '\u24B8': 'C',
    '\uFF23': 'C',
    '\u0106': 'C',
    '\u0108': 'C',
    '\u010A': 'C',
    '\u010C': 'C',
    '\u00C7': 'C',
    '\u1E08': 'C',
    '\u0187': 'C',
    '\u023B': 'C',
    '\uA73E': 'C',
    '\u24B9': 'D',
    '\uFF24': 'D',
    '\u1E0A': 'D',
    '\u010E': 'D',
    '\u1E0C': 'D',
    '\u1E10': 'D',
    '\u1E12': 'D',
    '\u1E0E': 'D',
    '\u0110': 'D',
    '\u018B': 'D',
    '\u018A': 'D',
    '\u0189': 'D',
    '\uA779': 'D',
    '\u01F1': 'DZ',
    '\u01C4': 'DZ',
    '\u01F2': 'Dz',
    '\u01C5': 'Dz',
    '\u24BA': 'E',
    '\uFF25': 'E',
    '\u00C8': 'E',
    '\u00C9': 'E',
    '\u00CA': 'E',
    '\u1EC0': 'E',
    '\u1EBE': 'E',
    '\u1EC4': 'E',
    '\u1EC2': 'E',
    '\u1EBC': 'E',
    '\u0112': 'E',
    '\u1E14': 'E',
    '\u1E16': 'E',
    '\u0114': 'E',
    '\u0116': 'E',
    '\u00CB': 'E',
    '\u1EBA': 'E',
    '\u011A': 'E',
    '\u0204': 'E',
    '\u0206': 'E',
    '\u1EB8': 'E',
    '\u1EC6': 'E',
    '\u0228': 'E',
    '\u1E1C': 'E',
    '\u0118': 'E',
    '\u1E18': 'E',
    '\u1E1A': 'E',
    '\u0190': 'E',
    '\u018E': 'E',
    '\u24BB': 'F',
    '\uFF26': 'F',
    '\u1E1E': 'F',
    '\u0191': 'F',
    '\uA77B': 'F',
    '\u24BC': 'G',
    '\uFF27': 'G',
    '\u01F4': 'G',
    '\u011C': 'G',
    '\u1E20': 'G',
    '\u011E': 'G',
    '\u0120': 'G',
    '\u01E6': 'G',
    '\u0122': 'G',
    '\u01E4': 'G',
    '\u0193': 'G',
    '\uA7A0': 'G',
    '\uA77D': 'G',
    '\uA77E': 'G',
    '\u24BD': 'H',
    '\uFF28': 'H',
    '\u0124': 'H',
    '\u1E22': 'H',
    '\u1E26': 'H',
    '\u021E': 'H',
    '\u1E24': 'H',
    '\u1E28': 'H',
    '\u1E2A': 'H',
    '\u0126': 'H',
    '\u2C67': 'H',
    '\u2C75': 'H',
    '\uA78D': 'H',
    '\u24BE': 'I',
    '\uFF29': 'I',
    '\u00CC': 'I',
    '\u00CD': 'I',
    '\u00CE': 'I',
    '\u0128': 'I',
    '\u012A': 'I',
    '\u012C': 'I',
    '\u0130': 'I',
    '\u00CF': 'I',
    '\u1E2E': 'I',
    '\u1EC8': 'I',
    '\u01CF': 'I',
    '\u0208': 'I',
    '\u020A': 'I',
    '\u1ECA': 'I',
    '\u012E': 'I',
    '\u1E2C': 'I',
    '\u0197': 'I',
    '\u24BF': 'J',
    '\uFF2A': 'J',
    '\u0134': 'J',
    '\u0248': 'J',
    '\u24C0': 'K',
    '\uFF2B': 'K',
    '\u1E30': 'K',
    '\u01E8': 'K',
    '\u1E32': 'K',
    '\u0136': 'K',
    '\u1E34': 'K',
    '\u0198': 'K',
    '\u2C69': 'K',
    '\uA740': 'K',
    '\uA742': 'K',
    '\uA744': 'K',
    '\uA7A2': 'K',
    '\u24C1': 'L',
    '\uFF2C': 'L',
    '\u013F': 'L',
    '\u0139': 'L',
    '\u013D': 'L',
    '\u1E36': 'L',
    '\u1E38': 'L',
    '\u013B': 'L',
    '\u1E3C': 'L',
    '\u1E3A': 'L',
    '\u0141': 'L',
    '\u023D': 'L',
    '\u2C62': 'L',
    '\u2C60': 'L',
    '\uA748': 'L',
    '\uA746': 'L',
    '\uA780': 'L',
    '\u01C7': 'LJ',
    '\u01C8': 'Lj',
    '\u24C2': 'M',
    '\uFF2D': 'M',
    '\u1E3E': 'M',
    '\u1E40': 'M',
    '\u1E42': 'M',
    '\u2C6E': 'M',
    '\u019C': 'M',
    '\u24C3': 'N',
    '\uFF2E': 'N',
    '\u01F8': 'N',
    '\u0143': 'N',
    '\u00D1': 'N',
    '\u1E44': 'N',
    '\u0147': 'N',
    '\u1E46': 'N',
    '\u0145': 'N',
    '\u1E4A': 'N',
    '\u1E48': 'N',
    '\u0220': 'N',
    '\u019D': 'N',
    '\uA790': 'N',
    '\uA7A4': 'N',
    '\u01CA': 'NJ',
    '\u01CB': 'Nj',
    '\u24C4': 'O',
    '\uFF2F': 'O',
    '\u00D2': 'O',
    '\u00D3': 'O',
    '\u00D4': 'O',
    '\u1ED2': 'O',
    '\u1ED0': 'O',
    '\u1ED6': 'O',
    '\u1ED4': 'O',
    '\u00D5': 'O',
    '\u1E4C': 'O',
    '\u022C': 'O',
    '\u1E4E': 'O',
    '\u014C': 'O',
    '\u1E50': 'O',
    '\u1E52': 'O',
    '\u014E': 'O',
    '\u022E': 'O',
    '\u0230': 'O',
    '\u00D6': 'O',
    '\u022A': 'O',
    '\u1ECE': 'O',
    '\u0150': 'O',
    '\u01D1': 'O',
    '\u020C': 'O',
    '\u020E': 'O',
    '\u01A0': 'O',
    '\u1EDC': 'O',
    '\u1EDA': 'O',
    '\u1EE0': 'O',
    '\u1EDE': 'O',
    '\u1EE2': 'O',
    '\u1ECC': 'O',
    '\u1ED8': 'O',
    '\u01EA': 'O',
    '\u01EC': 'O',
    '\u00D8': 'O',
    '\u01FE': 'O',
    '\u0186': 'O',
    '\u019F': 'O',
    '\uA74A': 'O',
    '\uA74C': 'O',
    '\u01A2': 'OI',
    '\uA74E': 'OO',
    '\u0222': 'OU',
    '\u24C5': 'P',
    '\uFF30': 'P',
    '\u1E54': 'P',
    '\u1E56': 'P',
    '\u01A4': 'P',
    '\u2C63': 'P',
    '\uA750': 'P',
    '\uA752': 'P',
    '\uA754': 'P',
    '\u24C6': 'Q',
    '\uFF31': 'Q',
    '\uA756': 'Q',
    '\uA758': 'Q',
    '\u024A': 'Q',
    '\u24C7': 'R',
    '\uFF32': 'R',
    '\u0154': 'R',
    '\u1E58': 'R',
    '\u0158': 'R',
    '\u0210': 'R',
    '\u0212': 'R',
    '\u1E5A': 'R',
    '\u1E5C': 'R',
    '\u0156': 'R',
    '\u1E5E': 'R',
    '\u024C': 'R',
    '\u2C64': 'R',
    '\uA75A': 'R',
    '\uA7A6': 'R',
    '\uA782': 'R',
    '\u24C8': 'S',
    '\uFF33': 'S',
    '\u1E9E': 'S',
    '\u015A': 'S',
    '\u1E64': 'S',
    '\u015C': 'S',
    '\u1E60': 'S',
    '\u0160': 'S',
    '\u1E66': 'S',
    '\u1E62': 'S',
    '\u1E68': 'S',
    '\u0218': 'S',
    '\u015E': 'S',
    '\u2C7E': 'S',
    '\uA7A8': 'S',
    '\uA784': 'S',
    '\u24C9': 'T',
    '\uFF34': 'T',
    '\u1E6A': 'T',
    '\u0164': 'T',
    '\u1E6C': 'T',
    '\u021A': 'T',
    '\u0162': 'T',
    '\u1E70': 'T',
    '\u1E6E': 'T',
    '\u0166': 'T',
    '\u01AC': 'T',
    '\u01AE': 'T',
    '\u023E': 'T',
    '\uA786': 'T',
    '\uA728': 'TZ',
    '\u24CA': 'U',
    '\uFF35': 'U',
    '\u00D9': 'U',
    '\u00DA': 'U',
    '\u00DB': 'U',
    '\u0168': 'U',
    '\u1E78': 'U',
    '\u016A': 'U',
    '\u1E7A': 'U',
    '\u016C': 'U',
    '\u00DC': 'U',
    '\u01DB': 'U',
    '\u01D7': 'U',
    '\u01D5': 'U',
    '\u01D9': 'U',
    '\u1EE6': 'U',
    '\u016E': 'U',
    '\u0170': 'U',
    '\u01D3': 'U',
    '\u0214': 'U',
    '\u0216': 'U',
    '\u01AF': 'U',
    '\u1EEA': 'U',
    '\u1EE8': 'U',
    '\u1EEE': 'U',
    '\u1EEC': 'U',
    '\u1EF0': 'U',
    '\u1EE4': 'U',
    '\u1E72': 'U',
    '\u0172': 'U',
    '\u1E76': 'U',
    '\u1E74': 'U',
    '\u0244': 'U',
    '\u24CB': 'V',
    '\uFF36': 'V',
    '\u1E7C': 'V',
    '\u1E7E': 'V',
    '\u01B2': 'V',
    '\uA75E': 'V',
    '\u0245': 'V',
    '\uA760': 'VY',
    '\u24CC': 'W',
    '\uFF37': 'W',
    '\u1E80': 'W',
    '\u1E82': 'W',
    '\u0174': 'W',
    '\u1E86': 'W',
    '\u1E84': 'W',
    '\u1E88': 'W',
    '\u2C72': 'W',
    '\u24CD': 'X',
    '\uFF38': 'X',
    '\u1E8A': 'X',
    '\u1E8C': 'X',
    '\u24CE': 'Y',
    '\uFF39': 'Y',
    '\u1EF2': 'Y',
    '\u00DD': 'Y',
    '\u0176': 'Y',
    '\u1EF8': 'Y',
    '\u0232': 'Y',
    '\u1E8E': 'Y',
    '\u0178': 'Y',
    '\u1EF6': 'Y',
    '\u1EF4': 'Y',
    '\u01B3': 'Y',
    '\u024E': 'Y',
    '\u1EFE': 'Y',
    '\u24CF': 'Z',
    '\uFF3A': 'Z',
    '\u0179': 'Z',
    '\u1E90': 'Z',
    '\u017B': 'Z',
    '\u017D': 'Z',
    '\u1E92': 'Z',
    '\u1E94': 'Z',
    '\u01B5': 'Z',
    '\u0224': 'Z',
    '\u2C7F': 'Z',
    '\u2C6B': 'Z',
    '\uA762': 'Z',
    '\u24D0': 'a',
    '\uFF41': 'a',
    '\u1E9A': 'a',
    '\u00E0': 'a',
    '\u00E1': 'a',
    '\u00E2': 'a',
    '\u1EA7': 'a',
    '\u1EA5': 'a',
    '\u1EAB': 'a',
    '\u1EA9': 'a',
    '\u00E3': 'a',
    '\u0101': 'a',
    '\u0103': 'a',
    '\u1EB1': 'a',
    '\u1EAF': 'a',
    '\u1EB5': 'a',
    '\u1EB3': 'a',
    '\u0227': 'a',
    '\u01E1': 'a',
    '\u00E4': 'a',
    '\u01DF': 'a',
    '\u1EA3': 'a',
    '\u00E5': 'a',
    '\u01FB': 'a',
    '\u01CE': 'a',
    '\u0201': 'a',
    '\u0203': 'a',
    '\u1EA1': 'a',
    '\u1EAD': 'a',
    '\u1EB7': 'a',
    '\u1E01': 'a',
    '\u0105': 'a',
    '\u2C65': 'a',
    '\u0250': 'a',
    '\uA733': 'aa',
    '\u00E6': 'ae',
    '\u01FD': 'ae',
    '\u01E3': 'ae',
    '\uA735': 'ao',
    '\uA737': 'au',
    '\uA739': 'av',
    '\uA73B': 'av',
    '\uA73D': 'ay',
    '\u24D1': 'b',
    '\uFF42': 'b',
    '\u1E03': 'b',
    '\u1E05': 'b',
    '\u1E07': 'b',
    '\u0180': 'b',
    '\u0183': 'b',
    '\u0253': 'b',
    '\u24D2': 'c',
    '\uFF43': 'c',
    '\u0107': 'c',
    '\u0109': 'c',
    '\u010B': 'c',
    '\u010D': 'c',
    '\u00E7': 'c',
    '\u1E09': 'c',
    '\u0188': 'c',
    '\u023C': 'c',
    '\uA73F': 'c',
    '\u2184': 'c',
    '\u24D3': 'd',
    '\uFF44': 'd',
    '\u1E0B': 'd',
    '\u010F': 'd',
    '\u1E0D': 'd',
    '\u1E11': 'd',
    '\u1E13': 'd',
    '\u1E0F': 'd',
    '\u0111': 'd',
    '\u018C': 'd',
    '\u0256': 'd',
    '\u0257': 'd',
    '\uA77A': 'd',
    '\u01F3': 'dz',
    '\u01C6': 'dz',
    '\u24D4': 'e',
    '\uFF45': 'e',
    '\u00E8': 'e',
    '\u00E9': 'e',
    '\u00EA': 'e',
    '\u1EC1': 'e',
    '\u1EBF': 'e',
    '\u1EC5': 'e',
    '\u1EC3': 'e',
    '\u1EBD': 'e',
    '\u0113': 'e',
    '\u1E15': 'e',
    '\u1E17': 'e',
    '\u0115': 'e',
    '\u0117': 'e',
    '\u00EB': 'e',
    '\u1EBB': 'e',
    '\u011B': 'e',
    '\u0205': 'e',
    '\u0207': 'e',
    '\u1EB9': 'e',
    '\u1EC7': 'e',
    '\u0229': 'e',
    '\u1E1D': 'e',
    '\u0119': 'e',
    '\u1E19': 'e',
    '\u1E1B': 'e',
    '\u0247': 'e',
    '\u025B': 'e',
    '\u01DD': 'e',
    '\u24D5': 'f',
    '\uFF46': 'f',
    '\u1E1F': 'f',
    '\u0192': 'f',
    '\uA77C': 'f',
    '\u24D6': 'g',
    '\uFF47': 'g',
    '\u01F5': 'g',
    '\u011D': 'g',
    '\u1E21': 'g',
    '\u011F': 'g',
    '\u0121': 'g',
    '\u01E7': 'g',
    '\u0123': 'g',
    '\u01E5': 'g',
    '\u0260': 'g',
    '\uA7A1': 'g',
    '\u1D79': 'g',
    '\uA77F': 'g',
    '\u24D7': 'h',
    '\uFF48': 'h',
    '\u0125': 'h',
    '\u1E23': 'h',
    '\u1E27': 'h',
    '\u021F': 'h',
    '\u1E25': 'h',
    '\u1E29': 'h',
    '\u1E2B': 'h',
    '\u1E96': 'h',
    '\u0127': 'h',
    '\u2C68': 'h',
    '\u2C76': 'h',
    '\u0265': 'h',
    '\u0195': 'hv',
    '\u24D8': 'i',
    '\uFF49': 'i',
    '\u00EC': 'i',
    '\u00ED': 'i',
    '\u00EE': 'i',
    '\u0129': 'i',
    '\u012B': 'i',
    '\u012D': 'i',
    '\u00EF': 'i',
    '\u1E2F': 'i',
    '\u1EC9': 'i',
    '\u01D0': 'i',
    '\u0209': 'i',
    '\u020B': 'i',
    '\u1ECB': 'i',
    '\u012F': 'i',
    '\u1E2D': 'i',
    '\u0268': 'i',
    '\u0131': 'i',
    '\u24D9': 'j',
    '\uFF4A': 'j',
    '\u0135': 'j',
    '\u01F0': 'j',
    '\u0249': 'j',
    '\u24DA': 'k',
    '\uFF4B': 'k',
    '\u1E31': 'k',
    '\u01E9': 'k',
    '\u1E33': 'k',
    '\u0137': 'k',
    '\u1E35': 'k',
    '\u0199': 'k',
    '\u2C6A': 'k',
    '\uA741': 'k',
    '\uA743': 'k',
    '\uA745': 'k',
    '\uA7A3': 'k',
    '\u24DB': 'l',
    '\uFF4C': 'l',
    '\u0140': 'l',
    '\u013A': 'l',
    '\u013E': 'l',
    '\u1E37': 'l',
    '\u1E39': 'l',
    '\u013C': 'l',
    '\u1E3D': 'l',
    '\u1E3B': 'l',
    '\u017F': 'l',
    '\u0142': 'l',
    '\u019A': 'l',
    '\u026B': 'l',
    '\u2C61': 'l',
    '\uA749': 'l',
    '\uA781': 'l',
    '\uA747': 'l',
    '\u01C9': 'lj',
    '\u24DC': 'm',
    '\uFF4D': 'm',
    '\u1E3F': 'm',
    '\u1E41': 'm',
    '\u1E43': 'm',
    '\u0271': 'm',
    '\u026F': 'm',
    '\u24DD': 'n',
    '\uFF4E': 'n',
    '\u01F9': 'n',
    '\u0144': 'n',
    '\u00F1': 'n',
    '\u1E45': 'n',
    '\u0148': 'n',
    '\u1E47': 'n',
    '\u0146': 'n',
    '\u1E4B': 'n',
    '\u1E49': 'n',
    '\u019E': 'n',
    '\u0272': 'n',
    '\u0149': 'n',
    '\uA791': 'n',
    '\uA7A5': 'n',
    '\u01CC': 'nj',
    '\u24DE': 'o',
    '\uFF4F': 'o',
    '\u00F2': 'o',
    '\u00F3': 'o',
    '\u00F4': 'o',
    '\u1ED3': 'o',
    '\u1ED1': 'o',
    '\u1ED7': 'o',
    '\u1ED5': 'o',
    '\u00F5': 'o',
    '\u1E4D': 'o',
    '\u022D': 'o',
    '\u1E4F': 'o',
    '\u014D': 'o',
    '\u1E51': 'o',
    '\u1E53': 'o',
    '\u014F': 'o',
    '\u022F': 'o',
    '\u0231': 'o',
    '\u00F6': 'o',
    '\u022B': 'o',
    '\u1ECF': 'o',
    '\u0151': 'o',
    '\u01D2': 'o',
    '\u020D': 'o',
    '\u020F': 'o',
    '\u01A1': 'o',
    '\u1EDD': 'o',
    '\u1EDB': 'o',
    '\u1EE1': 'o',
    '\u1EDF': 'o',
    '\u1EE3': 'o',
    '\u1ECD': 'o',
    '\u1ED9': 'o',
    '\u01EB': 'o',
    '\u01ED': 'o',
    '\u00F8': 'o',
    '\u01FF': 'o',
    '\u0254': 'o',
    '\uA74B': 'o',
    '\uA74D': 'o',
    '\u0275': 'o',
    '\u01A3': 'oi',
    '\u0223': 'ou',
    '\uA74F': 'oo',
    '\u24DF': 'p',
    '\uFF50': 'p',
    '\u1E55': 'p',
    '\u1E57': 'p',
    '\u01A5': 'p',
    '\u1D7D': 'p',
    '\uA751': 'p',
    '\uA753': 'p',
    '\uA755': 'p',
    '\u24E0': 'q',
    '\uFF51': 'q',
    '\u024B': 'q',
    '\uA757': 'q',
    '\uA759': 'q',
    '\u24E1': 'r',
    '\uFF52': 'r',
    '\u0155': 'r',
    '\u1E59': 'r',
    '\u0159': 'r',
    '\u0211': 'r',
    '\u0213': 'r',
    '\u1E5B': 'r',
    '\u1E5D': 'r',
    '\u0157': 'r',
    '\u1E5F': 'r',
    '\u024D': 'r',
    '\u027D': 'r',
    '\uA75B': 'r',
    '\uA7A7': 'r',
    '\uA783': 'r',
    '\u24E2': 's',
    '\uFF53': 's',
    '\u00DF': 's',
    '\u015B': 's',
    '\u1E65': 's',
    '\u015D': 's',
    '\u1E61': 's',
    '\u0161': 's',
    '\u1E67': 's',
    '\u1E63': 's',
    '\u1E69': 's',
    '\u0219': 's',
    '\u015F': 's',
    '\u023F': 's',
    '\uA7A9': 's',
    '\uA785': 's',
    '\u1E9B': 's',
    '\u24E3': 't',
    '\uFF54': 't',
    '\u1E6B': 't',
    '\u1E97': 't',
    '\u0165': 't',
    '\u1E6D': 't',
    '\u021B': 't',
    '\u0163': 't',
    '\u1E71': 't',
    '\u1E6F': 't',
    '\u0167': 't',
    '\u01AD': 't',
    '\u0288': 't',
    '\u2C66': 't',
    '\uA787': 't',
    '\uA729': 'tz',
    '\u24E4': 'u',
    '\uFF55': 'u',
    '\u00F9': 'u',
    '\u00FA': 'u',
    '\u00FB': 'u',
    '\u0169': 'u',
    '\u1E79': 'u',
    '\u016B': 'u',
    '\u1E7B': 'u',
    '\u016D': 'u',
    '\u00FC': 'u',
    '\u01DC': 'u',
    '\u01D8': 'u',
    '\u01D6': 'u',
    '\u01DA': 'u',
    '\u1EE7': 'u',
    '\u016F': 'u',
    '\u0171': 'u',
    '\u01D4': 'u',
    '\u0215': 'u',
    '\u0217': 'u',
    '\u01B0': 'u',
    '\u1EEB': 'u',
    '\u1EE9': 'u',
    '\u1EEF': 'u',
    '\u1EED': 'u',
    '\u1EF1': 'u',
    '\u1EE5': 'u',
    '\u1E73': 'u',
    '\u0173': 'u',
    '\u1E77': 'u',
    '\u1E75': 'u',
    '\u0289': 'u',
    '\u24E5': 'v',
    '\uFF56': 'v',
    '\u1E7D': 'v',
    '\u1E7F': 'v',
    '\u028B': 'v',
    '\uA75F': 'v',
    '\u028C': 'v',
    '\uA761': 'vy',
    '\u24E6': 'w',
    '\uFF57': 'w',
    '\u1E81': 'w',
    '\u1E83': 'w',
    '\u0175': 'w',
    '\u1E87': 'w',
    '\u1E85': 'w',
    '\u1E98': 'w',
    '\u1E89': 'w',
    '\u2C73': 'w',
    '\u24E7': 'x',
    '\uFF58': 'x',
    '\u1E8B': 'x',
    '\u1E8D': 'x',
    '\u24E8': 'y',
    '\uFF59': 'y',
    '\u1EF3': 'y',
    '\u00FD': 'y',
    '\u0177': 'y',
    '\u1EF9': 'y',
    '\u0233': 'y',
    '\u1E8F': 'y',
    '\u00FF': 'y',
    '\u1EF7': 'y',
    '\u1E99': 'y',
    '\u1EF5': 'y',
    '\u01B4': 'y',
    '\u024F': 'y',
    '\u1EFF': 'y',
    '\u24E9': 'z',
    '\uFF5A': 'z',
    '\u017A': 'z',
    '\u1E91': 'z',
    '\u017C': 'z',
    '\u017E': 'z',
    '\u1E93': 'z',
    '\u1E95': 'z',
    '\u01B6': 'z',
    '\u0225': 'z',
    '\u0240': 'z',
    '\u2C6C': 'z',
    '\uA763': 'z',
    '\u0386': '\u0391',
    '\u0388': '\u0395',
    '\u0389': '\u0397',
    '\u038A': '\u0399',
    '\u03AA': '\u0399',
    '\u038C': '\u039F',
    '\u038E': '\u03A5',
    '\u03AB': '\u03A5',
    '\u038F': '\u03A9',
    '\u03AC': '\u03B1',
    '\u03AD': '\u03B5',
    '\u03AE': '\u03B7',
    '\u03AF': '\u03B9',
    '\u03CA': '\u03B9',
    '\u0390': '\u03B9',
    '\u03CC': '\u03BF',
    '\u03CD': '\u03C5',
    '\u03CB': '\u03C5',
    '\u03B0': '\u03C5',
    '\u03C9': '\u03C9',
    '\u03C2': '\u03C3'
  };

  return diacritics;
});

S2.define('select2/data/base',[
  '../utils'
], function (Utils) {
  function BaseAdapter ($element, options) {
    BaseAdapter.__super__.constructor.call(this);
  }

  Utils.Extend(BaseAdapter, Utils.Observable);

  BaseAdapter.prototype.current = function (callback) {
    throw new Error('The `current` method must be defined in child classes.');
  };

  BaseAdapter.prototype.query = function (params, callback) {
    throw new Error('The `query` method must be defined in child classes.');
  };

  BaseAdapter.prototype.bind = function (container, $container) {
    // Can be implemented in subclasses
  };

  BaseAdapter.prototype.destroy = function () {
    // Can be implemented in subclasses
  };

  BaseAdapter.prototype.generateResultId = function (container, data) {
    var id = container.id + '-result-';

    id += Utils.generateChars(4);

    if (data.id != null) {
      id += '-' + data.id.toString();
    } else {
      id += '-' + Utils.generateChars(4);
    }
    return id;
  };

  return BaseAdapter;
});

S2.define('select2/data/select',[
  './base',
  '../utils',
  'jquery'
], function (BaseAdapter, Utils, $) {
  function SelectAdapter ($element, options) {
    this.$element = $element;
    this.options = options;

    SelectAdapter.__super__.constructor.call(this);
  }

  Utils.Extend(SelectAdapter, BaseAdapter);

  SelectAdapter.prototype.current = function (callback) {
    var data = [];
    var self = this;

    this.$element.find(':selected').each(function () {
      var $option = $(this);

      var option = self.item($option);

      data.push(option);
    });

    callback(data);
  };

  SelectAdapter.prototype.select = function (data) {
    var self = this;

    data.selected = true;

    // If data.element is a DOM node, use it instead
    if ($(data.element).is('option')) {
      data.element.selected = true;

      this.$element.trigger('change');

      return;
    }

    if (this.$element.prop('multiple')) {
      this.current(function (currentData) {
        var val = [];

        data = [data];
        data.push.apply(data, currentData);

        for (var d = 0; d < data.length; d++) {
          var id = data[d].id;

          if ($.inArray(id, val) === -1) {
            val.push(id);
          }
        }

        self.$element.val(val);
        self.$element.trigger('change');
      });
    } else {
      var val = data.id;

      this.$element.val(val);
      this.$element.trigger('change');
    }
  };

  SelectAdapter.prototype.unselect = function (data) {
    var self = this;

    if (!this.$element.prop('multiple')) {
      return;
    }

    data.selected = false;

    if ($(data.element).is('option')) {
      data.element.selected = false;

      this.$element.trigger('change');

      return;
    }

    this.current(function (currentData) {
      var val = [];

      for (var d = 0; d < currentData.length; d++) {
        var id = currentData[d].id;

        if (id !== data.id && $.inArray(id, val) === -1) {
          val.push(id);
        }
      }

      self.$element.val(val);

      self.$element.trigger('change');
    });
  };

  SelectAdapter.prototype.bind = function (container, $container) {
    var self = this;

    this.container = container;

    container.on('select', function (params) {
      self.select(params.data);
    });

    container.on('unselect', function (params) {
      self.unselect(params.data);
    });
  };

  SelectAdapter.prototype.destroy = function () {
    // Remove anything added to child elements
    this.$element.find('*').each(function () {
      // Remove any custom data set by Select2
      $.removeData(this, 'data');
    });
  };

  SelectAdapter.prototype.query = function (params, callback) {
    var data = [];
    var self = this;

    var $options = this.$element.children();

    $options.each(function () {
      var $option = $(this);

      if (!$option.is('option') && !$option.is('optgroup')) {
        return;
      }

      var option = self.item($option);

      var matches = self.matches(params, option);

      if (matches !== null) {
        data.push(matches);
      }
    });

    callback({
      results: data
    });
  };

  SelectAdapter.prototype.addOptions = function ($options) {
    Utils.appendMany(this.$element, $options);
  };

  SelectAdapter.prototype.option = function (data) {
    var option;

    if (data.children) {
      option = document.createElement('optgroup');
      option.label = data.text;
    } else {
      option = document.createElement('option');

      if (option.textContent !== undefined) {
        option.textContent = data.text;
      } else {
        option.innerText = data.text;
      }
    }

    if (data.id) {
      option.value = data.id;
    }

    if (data.disabled) {
      option.disabled = true;
    }

    if (data.selected) {
      option.selected = true;
    }

    if (data.title) {
      option.title = data.title;
    }

    var $option = $(option);

    var normalizedData = this._normalizeItem(data);
    normalizedData.element = option;

    // Override the option's data with the combined data
    $.data(option, 'data', normalizedData);

    return $option;
  };

  SelectAdapter.prototype.item = function ($option) {
    var data = {};

    data = $.data($option[0], 'data');

    if (data != null) {
      return data;
    }

    if ($option.is('option')) {
      data = {
        id: $option.val(),
        text: $option.text(),
        disabled: $option.prop('disabled'),
        selected: $option.prop('selected'),
        title: $option.prop('title')
      };
    } else if ($option.is('optgroup')) {
      data = {
        text: $option.prop('label'),
        children: [],
        title: $option.prop('title')
      };

      var $children = $option.children('option');
      var children = [];

      for (var c = 0; c < $children.length; c++) {
        var $child = $($children[c]);

        var child = this.item($child);

        children.push(child);
      }

      data.children = children;
    }

    data = this._normalizeItem(data);
    data.element = $option[0];

    $.data($option[0], 'data', data);

    return data;
  };

  SelectAdapter.prototype._normalizeItem = function (item) {
    if (!$.isPlainObject(item)) {
      item = {
        id: item,
        text: item
      };
    }

    item = $.extend({}, {
      text: ''
    }, item);

    var defaults = {
      selected: false,
      disabled: false
    };

    if (item.id != null) {
      item.id = item.id.toString();
    }

    if (item.text != null) {
      item.text = item.text.toString();
    }

    if (item._resultId == null && item.id && this.container != null) {
      item._resultId = this.generateResultId(this.container, item);
    }

    return $.extend({}, defaults, item);
  };

  SelectAdapter.prototype.matches = function (params, data) {
    var matcher = this.options.get('matcher');

    return matcher(params, data);
  };

  return SelectAdapter;
});

S2.define('select2/data/array',[
  './select',
  '../utils',
  'jquery'
], function (SelectAdapter, Utils, $) {
  function ArrayAdapter ($element, options) {
    var data = options.get('data') || [];

    ArrayAdapter.__super__.constructor.call(this, $element, options);

    this.addOptions(this.convertToOptions(data));
  }

  Utils.Extend(ArrayAdapter, SelectAdapter);

  ArrayAdapter.prototype.select = function (data) {
    var $option = this.$element.find('option').filter(function (i, elm) {
      return elm.value == data.id.toString();
    });

    if ($option.length === 0) {
      $option = this.option(data);

      this.addOptions($option);
    }

    ArrayAdapter.__super__.select.call(this, data);
  };

  ArrayAdapter.prototype.convertToOptions = function (data) {
    var self = this;

    var $existing = this.$element.find('option');
    var existingIds = $existing.map(function () {
      return self.item($(this)).id;
    }).get();

    var $options = [];

    // Filter out all items except for the one passed in the argument
    function onlyItem (item) {
      return function () {
        return $(this).val() == item.id;
      };
    }

    for (var d = 0; d < data.length; d++) {
      var item = this._normalizeItem(data[d]);

      // Skip items which were pre-loaded, only merge the data
      if ($.inArray(item.id, existingIds) >= 0) {
        var $existingOption = $existing.filter(onlyItem(item));

        var existingData = this.item($existingOption);
        var newData = $.extend(true, {}, item, existingData);

        var $newOption = this.option(newData);

        $existingOption.replaceWith($newOption);

        continue;
      }

      var $option = this.option(item);

      if (item.children) {
        var $children = this.convertToOptions(item.children);

        Utils.appendMany($option, $children);
      }

      $options.push($option);
    }

    return $options;
  };

  return ArrayAdapter;
});

S2.define('select2/data/ajax',[
  './array',
  '../utils',
  'jquery'
], function (ArrayAdapter, Utils, $) {
  function AjaxAdapter ($element, options) {
    this.ajaxOptions = this._applyDefaults(options.get('ajax'));

    if (this.ajaxOptions.processResults != null) {
      this.processResults = this.ajaxOptions.processResults;
    }

    AjaxAdapter.__super__.constructor.call(this, $element, options);
  }

  Utils.Extend(AjaxAdapter, ArrayAdapter);

  AjaxAdapter.prototype._applyDefaults = function (options) {
    var defaults = {
      data: function (params) {
        return $.extend({}, params, {
          q: params.term
        });
      },
      transport: function (params, success, failure) {
        var $request = $.ajax(params);

        $request.then(success);
        $request.fail(failure);

        return $request;
      }
    };

    return $.extend({}, defaults, options, true);
  };

  AjaxAdapter.prototype.processResults = function (results) {
    return results;
  };

  AjaxAdapter.prototype.query = function (params, callback) {
    var matches = [];
    var self = this;

    if (this._request != null) {
      // JSONP requests cannot always be aborted
      if ($.isFunction(this._request.abort)) {
        this._request.abort();
      }

      this._request = null;
    }

    var options = $.extend({
      type: 'GET'
    }, this.ajaxOptions);

    if (typeof options.url === 'function') {
      options.url = options.url.call(this.$element, params);
    }

    if (typeof options.data === 'function') {
      options.data = options.data.call(this.$element, params);
    }

    function request () {
      var $request = options.transport(options, function (data) {
        var results = self.processResults(data, params);

        if (self.options.get('debug') && window.console && console.error) {
          // Check to make sure that the response included a `results` key.
          if (!results || !results.results || !$.isArray(results.results)) {
            console.error(
              'Select2: The AJAX results did not return an array in the ' +
              '`results` key of the response.'
            );
          }
        }

        callback(results);
      }, function () {
        // Attempt to detect if a request was aborted
        // Only works if the transport exposes a status property
        if ($request.status && $request.status === '0') {
          return;
        }

        self.trigger('results:message', {
          message: 'errorLoading'
        });
      });

      self._request = $request;
    }

    if (this.ajaxOptions.delay && params.term != null) {
      if (this._queryTimeout) {
        window.clearTimeout(this._queryTimeout);
      }

      this._queryTimeout = window.setTimeout(request, this.ajaxOptions.delay);
    } else {
      request();
    }
  };

  return AjaxAdapter;
});

S2.define('select2/data/tags',[
  'jquery'
], function ($) {
  function Tags (decorated, $element, options) {
    var tags = options.get('tags');

    var createTag = options.get('createTag');

    if (createTag !== undefined) {
      this.createTag = createTag;
    }

    var insertTag = options.get('insertTag');

    if (insertTag !== undefined) {
        this.insertTag = insertTag;
    }

    decorated.call(this, $element, options);

    if ($.isArray(tags)) {
      for (var t = 0; t < tags.length; t++) {
        var tag = tags[t];
        var item = this._normalizeItem(tag);

        var $option = this.option(item);

        this.$element.append($option);
      }
    }
  }

  Tags.prototype.query = function (decorated, params, callback) {
    var self = this;

    this._removeOldTags();

    if (params.term == null || params.page != null) {
      decorated.call(this, params, callback);
      return;
    }

    function wrapper (obj, child) {
      var data = obj.results;

      for (var i = 0; i < data.length; i++) {
        var option = data[i];

        var checkChildren = (
          option.children != null &&
          !wrapper({
            results: option.children
          }, true)
        );

        var checkText = option.text === params.term;

        if (checkText || checkChildren) {
          if (child) {
            return false;
          }

          obj.data = data;
          callback(obj);

          return;
        }
      }

      if (child) {
        return true;
      }

      var tag = self.createTag(params);

      if (tag != null) {
        var $option = self.option(tag);
        $option.attr('data-select2-tag', true);

        self.addOptions([$option]);

        self.insertTag(data, tag);
      }

      obj.results = data;

      callback(obj);
    }

    decorated.call(this, params, wrapper);
  };

  Tags.prototype.createTag = function (decorated, params) {
    var term = $.trim(params.term);

    if (term === '') {
      return null;
    }

    return {
      id: term,
      text: term
    };
  };

  Tags.prototype.insertTag = function (_, data, tag) {
    data.unshift(tag);
  };

  Tags.prototype._removeOldTags = function (_) {
    var tag = this._lastTag;

    var $options = this.$element.find('option[data-select2-tag]');

    $options.each(function () {
      if (this.selected) {
        return;
      }

      $(this).remove();
    });
  };

  return Tags;
});

S2.define('select2/data/tokenizer',[
  'jquery'
], function ($) {
  function Tokenizer (decorated, $element, options) {
    var tokenizer = options.get('tokenizer');

    if (tokenizer !== undefined) {
      this.tokenizer = tokenizer;
    }

    decorated.call(this, $element, options);
  }

  Tokenizer.prototype.bind = function (decorated, container, $container) {
    decorated.call(this, container, $container);

    this.$search =  container.dropdown.$search || container.selection.$search ||
      $container.find('.select2-search__field');
  };

  Tokenizer.prototype.query = function (decorated, params, callback) {
    var self = this;

    function createAndSelect (data) {
      // Normalize the data object so we can use it for checks
      var item = self._normalizeItem(data);

      // Check if the data object already exists as a tag
      // Select it if it doesn't
      var $existingOptions = self.$element.find('option').filter(function () {
        return $(this).val() === item.id;
      });

      // If an existing option wasn't found for it, create the option
      if (!$existingOptions.length) {
        var $option = self.option(item);
        $option.attr('data-select2-tag', true);

        self._removeOldTags();
        self.addOptions([$option]);
      }

      // Select the item, now that we know there is an option for it
      select(item);
    }

    function select (data) {
      self.trigger('select', {
        data: data
      });
    }

    params.term = params.term || '';

    var tokenData = this.tokenizer(params, this.options, createAndSelect);

    if (tokenData.term !== params.term) {
      // Replace the search term if we have the search box
      if (this.$search.length) {
        this.$search.val(tokenData.term);
        this.$search.focus();
      }

      params.term = tokenData.term;
    }

    decorated.call(this, params, callback);
  };

  Tokenizer.prototype.tokenizer = function (_, params, options, callback) {
    var separators = options.get('tokenSeparators') || [];
    var term = params.term;
    var i = 0;

    var createTag = this.createTag || function (params) {
      return {
        id: params.term,
        text: params.term
      };
    };

    while (i < term.length) {
      var termChar = term[i];

      if ($.inArray(termChar, separators) === -1) {
        i++;

        continue;
      }

      var part = term.substr(0, i);
      var partParams = $.extend({}, params, {
        term: part
      });

      var data = createTag(partParams);

      if (data == null) {
        i++;
        continue;
      }

      callback(data);

      // Reset the term to not include the tokenized portion
      term = term.substr(i + 1) || '';
      i = 0;
    }

    return {
      term: term
    };
  };

  return Tokenizer;
});

S2.define('select2/data/minimumInputLength',[

], function () {
  function MinimumInputLength (decorated, $e, options) {
    this.minimumInputLength = options.get('minimumInputLength');

    decorated.call(this, $e, options);
  }

  MinimumInputLength.prototype.query = function (decorated, params, callback) {
    params.term = params.term || '';

    if (params.term.length < this.minimumInputLength) {
      this.trigger('results:message', {
        message: 'inputTooShort',
        args: {
          minimum: this.minimumInputLength,
          input: params.term,
          params: params
        }
      });

      return;
    }

    decorated.call(this, params, callback);
  };

  return MinimumInputLength;
});

S2.define('select2/data/maximumInputLength',[

], function () {
  function MaximumInputLength (decorated, $e, options) {
    this.maximumInputLength = options.get('maximumInputLength');

    decorated.call(this, $e, options);
  }

  MaximumInputLength.prototype.query = function (decorated, params, callback) {
    params.term = params.term || '';

    if (this.maximumInputLength > 0 &&
        params.term.length > this.maximumInputLength) {
      this.trigger('results:message', {
        message: 'inputTooLong',
        args: {
          maximum: this.maximumInputLength,
          input: params.term,
          params: params
        }
      });

      return;
    }

    decorated.call(this, params, callback);
  };

  return MaximumInputLength;
});

S2.define('select2/data/maximumSelectionLength',[

], function (){
  function MaximumSelectionLength (decorated, $e, options) {
    this.maximumSelectionLength = options.get('maximumSelectionLength');

    decorated.call(this, $e, options);
  }

  MaximumSelectionLength.prototype.query =
    function (decorated, params, callback) {
      var self = this;

      this.current(function (currentData) {
        var count = currentData != null ? currentData.length : 0;
        if (self.maximumSelectionLength > 0 &&
          count >= self.maximumSelectionLength) {
          self.trigger('results:message', {
            message: 'maximumSelected',
            args: {
              maximum: self.maximumSelectionLength
            }
          });
          return;
        }
        decorated.call(self, params, callback);
      });
  };

  return MaximumSelectionLength;
});

S2.define('select2/dropdown',[
  'jquery',
  './utils'
], function ($, Utils) {
  function Dropdown ($element, options) {
    this.$element = $element;
    this.options = options;

    Dropdown.__super__.constructor.call(this);
  }

  Utils.Extend(Dropdown, Utils.Observable);

  Dropdown.prototype.render = function () {
    var $dropdown = $(
      '<span class="select2-dropdown">' +
        '<span class="select2-results"></span>' +
      '</span>'
    );

    $dropdown.attr('dir', this.options.get('dir'));

    this.$dropdown = $dropdown;

    return $dropdown;
  };

  Dropdown.prototype.bind = function () {
    // Should be implemented in subclasses
  };

  Dropdown.prototype.position = function ($dropdown, $container) {
    // Should be implmented in subclasses
  };

  Dropdown.prototype.destroy = function () {
    // Remove the dropdown from the DOM
    this.$dropdown.remove();
  };

  return Dropdown;
});

S2.define('select2/dropdown/search',[
  'jquery',
  '../utils'
], function ($, Utils) {
  function Search () { }

  Search.prototype.render = function (decorated) {
    var $rendered = decorated.call(this);

    var $search = $(
      '<span class="select2-search select2-search--dropdown">' +
        '<input class="select2-search__field" type="search" tabindex="-1"' +
        ' autocomplete="off" autocorrect="off" autocapitalize="off"' +
        ' spellcheck="false" role="textbox" />' +
      '</span>'
    );

    this.$searchContainer = $search;
    this.$search = $search.find('input');

    $rendered.prepend($search);

    return $rendered;
  };

  Search.prototype.bind = function (decorated, container, $container) {
    var self = this;

    decorated.call(this, container, $container);

    this.$search.on('keydown', function (evt) {
      self.trigger('keypress', evt);

      self._keyUpPrevented = evt.isDefaultPrevented();
    });

    // Workaround for browsers which do not support the `input` event
    // This will prevent double-triggering of events for browsers which support
    // both the `keyup` and `input` events.
    this.$search.on('input', function (evt) {
      // Unbind the duplicated `keyup` event
      $(this).off('keyup');
    });

    this.$search.on('keyup input', function (evt) {
      self.handleSearch(evt);
    });

    container.on('open', function () {
      self.$search.attr('tabindex', 0);

      self.$search.focus();

      window.setTimeout(function () {
        self.$search.focus();
      }, 0);
    });

    container.on('close', function () {
      self.$search.attr('tabindex', -1);

      self.$search.val('');
    });

    container.on('focus', function () {
      if (container.isOpen()) {
        self.$search.focus();
      }
    });

    container.on('results:all', function (params) {
      if (params.query.term == null || params.query.term === '') {
        var showSearch = self.showSearch(params);

        if (showSearch) {
          self.$searchContainer.removeClass('select2-search--hide');
        } else {
          self.$searchContainer.addClass('select2-search--hide');
        }
      }
    });
  };

  Search.prototype.handleSearch = function (evt) {
    if (!this._keyUpPrevented) {
      var input = this.$search.val();

      this.trigger('query', {
        term: input
      });
    }

    this._keyUpPrevented = false;
  };

  Search.prototype.showSearch = function (_, params) {
    return true;
  };

  return Search;
});

S2.define('select2/dropdown/hidePlaceholder',[

], function () {
  function HidePlaceholder (decorated, $element, options, dataAdapter) {
    this.placeholder = this.normalizePlaceholder(options.get('placeholder'));

    decorated.call(this, $element, options, dataAdapter);
  }

  HidePlaceholder.prototype.append = function (decorated, data) {
    data.results = this.removePlaceholder(data.results);

    decorated.call(this, data);
  };

  HidePlaceholder.prototype.normalizePlaceholder = function (_, placeholder) {
    if (typeof placeholder === 'string') {
      placeholder = {
        id: '',
        text: placeholder
      };
    }

    return placeholder;
  };

  HidePlaceholder.prototype.removePlaceholder = function (_, data) {
    var modifiedData = data.slice(0);

    for (var d = data.length - 1; d >= 0; d--) {
      var item = data[d];

      if (this.placeholder.id === item.id) {
        modifiedData.splice(d, 1);
      }
    }

    return modifiedData;
  };

  return HidePlaceholder;
});

S2.define('select2/dropdown/infiniteScroll',[
  'jquery'
], function ($) {
  function InfiniteScroll (decorated, $element, options, dataAdapter) {
    this.lastParams = {};

    decorated.call(this, $element, options, dataAdapter);

    this.$loadingMore = this.createLoadingMore();
    this.loading = false;
  }

  InfiniteScroll.prototype.append = function (decorated, data) {
    this.$loadingMore.remove();
    this.loading = false;

    decorated.call(this, data);

    if (this.showLoadingMore(data)) {
      this.$results.append(this.$loadingMore);
    }
  };

  InfiniteScroll.prototype.bind = function (decorated, container, $container) {
    var self = this;

    decorated.call(this, container, $container);

    container.on('query', function (params) {
      self.lastParams = params;
      self.loading = true;
    });

    container.on('query:append', function (params) {
      self.lastParams = params;
      self.loading = true;
    });

    this.$results.on('scroll', function () {
      var isLoadMoreVisible = $.contains(
        document.documentElement,
        self.$loadingMore[0]
      );

      if (self.loading || !isLoadMoreVisible) {
        return;
      }

      var currentOffset = self.$results.offset().top +
        self.$results.outerHeight(false);
      var loadingMoreOffset = self.$loadingMore.offset().top +
        self.$loadingMore.outerHeight(false);

      if (currentOffset + 50 >= loadingMoreOffset) {
        self.loadMore();
      }
    });
  };

  InfiniteScroll.prototype.loadMore = function () {
    this.loading = true;

    var params = $.extend({}, {page: 1}, this.lastParams);

    params.page++;

    this.trigger('query:append', params);
  };

  InfiniteScroll.prototype.showLoadingMore = function (_, data) {
    return data.pagination && data.pagination.more;
  };

  InfiniteScroll.prototype.createLoadingMore = function () {
    var $option = $(
      '<li ' +
      'class="select2-results__option select2-results__option--load-more"' +
      'role="treeitem" aria-disabled="true"></li>'
    );

    var message = this.options.get('translations').get('loadingMore');

    $option.html(message(this.lastParams));

    return $option;
  };

  return InfiniteScroll;
});

S2.define('select2/dropdown/attachBody',[
  'jquery',
  '../utils'
], function ($, Utils) {
  function AttachBody (decorated, $element, options) {
    this.$dropdownParent = options.get('dropdownParent') || $(document.body);

    decorated.call(this, $element, options);
  }

  AttachBody.prototype.bind = function (decorated, container, $container) {
    var self = this;

    var setupResultsEvents = false;

    decorated.call(this, container, $container);

    container.on('open', function () {
      self._showDropdown();
      self._attachPositioningHandler(container);

      if (!setupResultsEvents) {
        setupResultsEvents = true;

        container.on('results:all', function () {
          self._positionDropdown();
          self._resizeDropdown();
        });

        container.on('results:append', function () {
          self._positionDropdown();
          self._resizeDropdown();
        });
      }
    });

    container.on('close', function () {
      self._hideDropdown();
      self._detachPositioningHandler(container);
    });

    this.$dropdownContainer.on('mousedown', function (evt) {
      evt.stopPropagation();
    });
  };

  AttachBody.prototype.destroy = function (decorated) {
    decorated.call(this);

    this.$dropdownContainer.remove();
  };

  AttachBody.prototype.position = function (decorated, $dropdown, $container) {
    // Clone all of the container classes
    $dropdown.attr('class', $container.attr('class'));

    $dropdown.removeClass('select2');
    $dropdown.addClass('select2-container--open');

    $dropdown.css({
      position: 'absolute',
      top: -999999
    });

    this.$container = $container;
  };

  AttachBody.prototype.render = function (decorated) {
    var $container = $('<span></span>');

    var $dropdown = decorated.call(this);
    $container.append($dropdown);

    this.$dropdownContainer = $container;

    return $container;
  };

  AttachBody.prototype._hideDropdown = function (decorated) {
    this.$dropdownContainer.detach();
  };

  AttachBody.prototype._attachPositioningHandler =
      function (decorated, container) {
    var self = this;

    var scrollEvent = 'scroll.select2.' + container.id;
    var resizeEvent = 'resize.select2.' + container.id;
    var orientationEvent = 'orientationchange.select2.' + container.id;

    var $watchers = this.$container.parents().filter(Utils.hasScroll);
    $watchers.each(function () {
      $(this).data('select2-scroll-position', {
        x: $(this).scrollLeft(),
        y: $(this).scrollTop()
      });
    });

    $watchers.on(scrollEvent, function (ev) {
      var position = $(this).data('select2-scroll-position');
      $(this).scrollTop(position.y);
    });

    $(window).on(scrollEvent + ' ' + resizeEvent + ' ' + orientationEvent,
      function (e) {
      self._positionDropdown();
      self._resizeDropdown();
    });
  };

  AttachBody.prototype._detachPositioningHandler =
      function (decorated, container) {
    var scrollEvent = 'scroll.select2.' + container.id;
    var resizeEvent = 'resize.select2.' + container.id;
    var orientationEvent = 'orientationchange.select2.' + container.id;

    var $watchers = this.$container.parents().filter(Utils.hasScroll);
    $watchers.off(scrollEvent);

    $(window).off(scrollEvent + ' ' + resizeEvent + ' ' + orientationEvent);
  };

  AttachBody.prototype._positionDropdown = function () {
    var $window = $(window);

    var isCurrentlyAbove = this.$dropdown.hasClass('select2-dropdown--above');
    var isCurrentlyBelow = this.$dropdown.hasClass('select2-dropdown--below');

    var newDirection = null;

    var offset = this.$container.offset();

    offset.bottom = offset.top + this.$container.outerHeight(false);

    var container = {
      height: this.$container.outerHeight(false)
    };

    container.top = offset.top;
    container.bottom = offset.top + container.height;

    var dropdown = {
      height: this.$dropdown.outerHeight(false)
    };

    var viewport = {
      top: $window.scrollTop(),
      bottom: $window.scrollTop() + $window.height()
    };

    var enoughRoomAbove = viewport.top < (offset.top - dropdown.height);
    var enoughRoomBelow = viewport.bottom > (offset.bottom + dropdown.height);

    var css = {
      left: offset.left,
      top: container.bottom
    };

    // Determine what the parent element is to use for calciulating the offset
    var $offsetParent = this.$dropdownParent;

    // For statically positoned elements, we need to get the element
    // that is determining the offset
    if ($offsetParent.css('position') === 'static') {
      $offsetParent = $offsetParent.offsetParent();
    }

    var parentOffset = $offsetParent.offset();

    css.top -= parentOffset.top;
    css.left -= parentOffset.left;

    if (!isCurrentlyAbove && !isCurrentlyBelow) {
      newDirection = 'below';
    }

    if (!enoughRoomBelow && enoughRoomAbove && !isCurrentlyAbove) {
      newDirection = 'above';
    } else if (!enoughRoomAbove && enoughRoomBelow && isCurrentlyAbove) {
      newDirection = 'below';
    }

    if (newDirection == 'above' ||
      (isCurrentlyAbove && newDirection !== 'below')) {
      css.top = container.top - parentOffset.top - dropdown.height;
    }

    if (newDirection != null) {
      this.$dropdown
        .removeClass('select2-dropdown--below select2-dropdown--above')
        .addClass('select2-dropdown--' + newDirection);
      this.$container
        .removeClass('select2-container--below select2-container--above')
        .addClass('select2-container--' + newDirection);
    }

    this.$dropdownContainer.css(css);
  };

  AttachBody.prototype._resizeDropdown = function () {
    var css = {
      width: this.$container.outerWidth(false) + 'px'
    };

    if (this.options.get('dropdownAutoWidth')) {
      css.minWidth = css.width;
      css.position = 'relative';
      css.width = 'auto';
    }

    this.$dropdown.css(css);
  };

  AttachBody.prototype._showDropdown = function (decorated) {
    this.$dropdownContainer.appendTo(this.$dropdownParent);

    this._positionDropdown();
    this._resizeDropdown();
  };

  return AttachBody;
});

S2.define('select2/dropdown/minimumResultsForSearch',[

], function () {
  function countResults (data) {
    var count = 0;

    for (var d = 0; d < data.length; d++) {
      var item = data[d];

      if (item.children) {
        count += countResults(item.children);
      } else {
        count++;
      }
    }

    return count;
  }

  function MinimumResultsForSearch (decorated, $element, options, dataAdapter) {
    this.minimumResultsForSearch = options.get('minimumResultsForSearch');

    if (this.minimumResultsForSearch < 0) {
      this.minimumResultsForSearch = Infinity;
    }

    decorated.call(this, $element, options, dataAdapter);
  }

  MinimumResultsForSearch.prototype.showSearch = function (decorated, params) {
    if (countResults(params.data.results) < this.minimumResultsForSearch) {
      return false;
    }

    return decorated.call(this, params);
  };

  return MinimumResultsForSearch;
});

S2.define('select2/dropdown/selectOnClose',[

], function () {
  function SelectOnClose () { }

  SelectOnClose.prototype.bind = function (decorated, container, $container) {
    var self = this;

    decorated.call(this, container, $container);

    container.on('close', function (params) {
      self._handleSelectOnClose(params);
    });
  };

  SelectOnClose.prototype._handleSelectOnClose = function (_, params) {
    if (params && params.originalSelect2Event != null) {
      var event = params.originalSelect2Event;

      // Don't select an item if the close event was triggered from a select or
      // unselect event
      if (event._type === 'select' || event._type === 'unselect') {
        return;
      }
    }

    var $highlightedResults = this.getHighlightedResults();

    // Only select highlighted results
    if ($highlightedResults.length < 1) {
      return;
    }

    var data = $highlightedResults.data('data');

    // Don't re-select already selected resulte
    if (
      (data.element != null && data.element.selected) ||
      (data.element == null && data.selected)
    ) {
      return;
    }

    this.trigger('select', {
        data: data
    });
  };

  return SelectOnClose;
});

S2.define('select2/dropdown/closeOnSelect',[

], function () {
  function CloseOnSelect () { }

  CloseOnSelect.prototype.bind = function (decorated, container, $container) {
    var self = this;

    decorated.call(this, container, $container);

    container.on('select', function (evt) {
      self._selectTriggered(evt);
    });

    container.on('unselect', function (evt) {
      self._selectTriggered(evt);
    });
  };

  CloseOnSelect.prototype._selectTriggered = function (_, evt) {
    var originalEvent = evt.originalEvent;

    // Don't close if the control key is being held
    if (originalEvent && originalEvent.ctrlKey) {
      return;
    }

    this.trigger('close', {
      originalEvent: originalEvent,
      originalSelect2Event: evt
    });
  };

  return CloseOnSelect;
});

S2.define('select2/i18n/en',[],function () {
  // English
  return {
    errorLoading: function () {
      return 'The results could not be loaded.';
    },
    inputTooLong: function (args) {
      var overChars = args.input.length - args.maximum;

      var message = 'Please delete ' + overChars + ' character';

      if (overChars != 1) {
        message += 's';
      }

      return message;
    },
    inputTooShort: function (args) {
      var remainingChars = args.minimum - args.input.length;

      var message = 'Please enter ' + remainingChars + ' or more characters';

      return message;
    },
    loadingMore: function () {
      return 'Loading more results…';
    },
    maximumSelected: function (args) {
      var message = 'You can only select ' + args.maximum + ' item';

      if (args.maximum != 1) {
        message += 's';
      }

      return message;
    },
    noResults: function () {
      return 'No results found';
    },
    searching: function () {
      return 'Searching…';
    }
  };
});

S2.define('select2/defaults',[
  'jquery',
  'require',

  './results',

  './selection/single',
  './selection/multiple',
  './selection/placeholder',
  './selection/allowClear',
  './selection/search',
  './selection/eventRelay',

  './utils',
  './translation',
  './diacritics',

  './data/select',
  './data/array',
  './data/ajax',
  './data/tags',
  './data/tokenizer',
  './data/minimumInputLength',
  './data/maximumInputLength',
  './data/maximumSelectionLength',

  './dropdown',
  './dropdown/search',
  './dropdown/hidePlaceholder',
  './dropdown/infiniteScroll',
  './dropdown/attachBody',
  './dropdown/minimumResultsForSearch',
  './dropdown/selectOnClose',
  './dropdown/closeOnSelect',

  './i18n/en'
], function ($, require,

             ResultsList,

             SingleSelection, MultipleSelection, Placeholder, AllowClear,
             SelectionSearch, EventRelay,

             Utils, Translation, DIACRITICS,

             SelectData, ArrayData, AjaxData, Tags, Tokenizer,
             MinimumInputLength, MaximumInputLength, MaximumSelectionLength,

             Dropdown, DropdownSearch, HidePlaceholder, InfiniteScroll,
             AttachBody, MinimumResultsForSearch, SelectOnClose, CloseOnSelect,

             EnglishTranslation) {
  function Defaults () {
    this.reset();
  }

  Defaults.prototype.apply = function (options) {
    options = $.extend(true, {}, this.defaults, options);

    if (options.dataAdapter == null) {
      if (options.ajax != null) {
        options.dataAdapter = AjaxData;
      } else if (options.data != null) {
        options.dataAdapter = ArrayData;
      } else {
        options.dataAdapter = SelectData;
      }

      if (options.minimumInputLength > 0) {
        options.dataAdapter = Utils.Decorate(
          options.dataAdapter,
          MinimumInputLength
        );
      }

      if (options.maximumInputLength > 0) {
        options.dataAdapter = Utils.Decorate(
          options.dataAdapter,
          MaximumInputLength
        );
      }

      if (options.maximumSelectionLength > 0) {
        options.dataAdapter = Utils.Decorate(
          options.dataAdapter,
          MaximumSelectionLength
        );
      }

      if (options.tags) {
        options.dataAdapter = Utils.Decorate(options.dataAdapter, Tags);
      }

      if (options.tokenSeparators != null || options.tokenizer != null) {
        options.dataAdapter = Utils.Decorate(
          options.dataAdapter,
          Tokenizer
        );
      }

      if (options.query != null) {
        var Query = require(options.amdBase + 'compat/query');

        options.dataAdapter = Utils.Decorate(
          options.dataAdapter,
          Query
        );
      }

      if (options.initSelection != null) {
        var InitSelection = require(options.amdBase + 'compat/initSelection');

        options.dataAdapter = Utils.Decorate(
          options.dataAdapter,
          InitSelection
        );
      }
    }

    if (options.resultsAdapter == null) {
      options.resultsAdapter = ResultsList;

      if (options.ajax != null) {
        options.resultsAdapter = Utils.Decorate(
          options.resultsAdapter,
          InfiniteScroll
        );
      }

      if (options.placeholder != null) {
        options.resultsAdapter = Utils.Decorate(
          options.resultsAdapter,
          HidePlaceholder
        );
      }

      if (options.selectOnClose) {
        options.resultsAdapter = Utils.Decorate(
          options.resultsAdapter,
          SelectOnClose
        );
      }
    }

    if (options.dropdownAdapter == null) {
      if (options.multiple) {
        options.dropdownAdapter = Dropdown;
      } else {
        var SearchableDropdown = Utils.Decorate(Dropdown, DropdownSearch);

        options.dropdownAdapter = SearchableDropdown;
      }

      if (options.minimumResultsForSearch !== 0) {
        options.dropdownAdapter = Utils.Decorate(
          options.dropdownAdapter,
          MinimumResultsForSearch
        );
      }

      if (options.closeOnSelect) {
        options.dropdownAdapter = Utils.Decorate(
          options.dropdownAdapter,
          CloseOnSelect
        );
      }

      if (
        options.dropdownCssClass != null ||
        options.dropdownCss != null ||
        options.adaptDropdownCssClass != null
      ) {
        var DropdownCSS = require(options.amdBase + 'compat/dropdownCss');

        options.dropdownAdapter = Utils.Decorate(
          options.dropdownAdapter,
          DropdownCSS
        );
      }

      options.dropdownAdapter = Utils.Decorate(
        options.dropdownAdapter,
        AttachBody
      );
    }

    if (options.selectionAdapter == null) {
      if (options.multiple) {
        options.selectionAdapter = MultipleSelection;
      } else {
        options.selectionAdapter = SingleSelection;
      }

      // Add the placeholder mixin if a placeholder was specified
      if (options.placeholder != null) {
        options.selectionAdapter = Utils.Decorate(
          options.selectionAdapter,
          Placeholder
        );
      }

      if (options.allowClear) {
        options.selectionAdapter = Utils.Decorate(
          options.selectionAdapter,
          AllowClear
        );
      }

      if (options.multiple) {
        options.selectionAdapter = Utils.Decorate(
          options.selectionAdapter,
          SelectionSearch
        );
      }

      if (
        options.containerCssClass != null ||
        options.containerCss != null ||
        options.adaptContainerCssClass != null
      ) {
        var ContainerCSS = require(options.amdBase + 'compat/containerCss');

        options.selectionAdapter = Utils.Decorate(
          options.selectionAdapter,
          ContainerCSS
        );
      }

      options.selectionAdapter = Utils.Decorate(
        options.selectionAdapter,
        EventRelay
      );
    }

    if (typeof options.language === 'string') {
      // Check if the language is specified with a region
      if (options.language.indexOf('-') > 0) {
        // Extract the region information if it is included
        var languageParts = options.language.split('-');
        var baseLanguage = languageParts[0];

        options.language = [options.language, baseLanguage];
      } else {
        options.language = [options.language];
      }
    }

    if ($.isArray(options.language)) {
      var languages = new Translation();
      options.language.push('en');

      var languageNames = options.language;

      for (var l = 0; l < languageNames.length; l++) {
        var name = languageNames[l];
        var language = {};

        try {
          // Try to load it with the original name
          language = Translation.loadPath(name);
        } catch (e) {
          try {
            // If we couldn't load it, check if it wasn't the full path
            name = this.defaults.amdLanguageBase + name;
            language = Translation.loadPath(name);
          } catch (ex) {
            // The translation could not be loaded at all. Sometimes this is
            // because of a configuration problem, other times this can be
            // because of how Select2 helps load all possible translation files.
            if (options.debug && window.console && console.warn) {
              console.warn(
                'Select2: The language file for "' + name + '" could not be ' +
                'automatically loaded. A fallback will be used instead.'
              );
            }

            continue;
          }
        }

        languages.extend(language);
      }

      options.translations = languages;
    } else {
      var baseTranslation = Translation.loadPath(
        this.defaults.amdLanguageBase + 'en'
      );
      var customTranslation = new Translation(options.language);

      customTranslation.extend(baseTranslation);

      options.translations = customTranslation;
    }

    return options;
  };

  Defaults.prototype.reset = function () {
    function stripDiacritics (text) {
      // Used 'uni range + named function' from http://jsperf.com/diacritics/18
      function match(a) {
        return DIACRITICS[a] || a;
      }

      return text.replace(/[^\u0000-\u007E]/g, match);
    }

    function matcher (params, data) {
      // Always return the object if there is nothing to compare
      if ($.trim(params.term) === '') {
        return data;
      }

      // Do a recursive check for options with children
      if (data.children && data.children.length > 0) {
        // Clone the data object if there are children
        // This is required as we modify the object to remove any non-matches
        var match = $.extend(true, {}, data);

        // Check each child of the option
        for (var c = data.children.length - 1; c >= 0; c--) {
          var child = data.children[c];

          var matches = matcher(params, child);

          // If there wasn't a match, remove the object in the array
          if (matches == null) {
            match.children.splice(c, 1);
          }
        }

        // If any children matched, return the new object
        if (match.children.length > 0) {
          return match;
        }

        // If there were no matching children, check just the plain object
        return matcher(params, match);
      }

      var original = stripDiacritics(data.text).toUpperCase();
      var term = stripDiacritics(params.term).toUpperCase();

      // Check if the text contains the term
      if (original.indexOf(term) > -1) {
        return data;
      }

      // If it doesn't contain the term, don't return anything
      return null;
    }

    this.defaults = {
      amdBase: './',
      amdLanguageBase: './i18n/',
      closeOnSelect: true,
      debug: false,
      dropdownAutoWidth: false,
      escapeMarkup: Utils.escapeMarkup,
      language: EnglishTranslation,
      matcher: matcher,
      minimumInputLength: 0,
      maximumInputLength: 0,
      maximumSelectionLength: 0,
      minimumResultsForSearch: 0,
      selectOnClose: false,
      sorter: function (data) {
        return data;
      },
      templateResult: function (result) {
        return result.text;
      },
      templateSelection: function (selection) {
        return selection.text;
      },
      theme: 'default',
      width: 'resolve'
    };
  };

  Defaults.prototype.set = function (key, value) {
    var camelKey = $.camelCase(key);

    var data = {};
    data[camelKey] = value;

    var convertedData = Utils._convertData(data);

    $.extend(this.defaults, convertedData);
  };

  var defaults = new Defaults();

  return defaults;
});

S2.define('select2/options',[
  'require',
  'jquery',
  './defaults',
  './utils'
], function (require, $, Defaults, Utils) {
  function Options (options, $element) {
    this.options = options;

    if ($element != null) {
      this.fromElement($element);
    }

    this.options = Defaults.apply(this.options);

    if ($element && $element.is('input')) {
      var InputCompat = require(this.get('amdBase') + 'compat/inputData');

      this.options.dataAdapter = Utils.Decorate(
        this.options.dataAdapter,
        InputCompat
      );
    }
  }

  Options.prototype.fromElement = function ($e) {
    var excludedData = ['select2'];

    if (this.options.multiple == null) {
      this.options.multiple = $e.prop('multiple');
    }

    if (this.options.disabled == null) {
      this.options.disabled = $e.prop('disabled');
    }

    if (this.options.language == null) {
      if ($e.prop('lang')) {
        this.options.language = $e.prop('lang').toLowerCase();
      } else if ($e.closest('[lang]').prop('lang')) {
        this.options.language = $e.closest('[lang]').prop('lang');
      }
    }

    if (this.options.dir == null) {
      if ($e.prop('dir')) {
        this.options.dir = $e.prop('dir');
      } else if ($e.closest('[dir]').prop('dir')) {
        this.options.dir = $e.closest('[dir]').prop('dir');
      } else {
        this.options.dir = 'ltr';
      }
    }

    $e.prop('disabled', this.options.disabled);
    $e.prop('multiple', this.options.multiple);

    if ($e.data('select2Tags')) {
      if (this.options.debug && window.console && console.warn) {
        console.warn(
          'Select2: The `data-select2-tags` attribute has been changed to ' +
          'use the `data-data` and `data-tags="true"` attributes and will be ' +
          'removed in future versions of Select2.'
        );
      }

      $e.data('data', $e.data('select2Tags'));
      $e.data('tags', true);
    }

    if ($e.data('ajaxUrl')) {
      if (this.options.debug && window.console && console.warn) {
        console.warn(
          'Select2: The `data-ajax-url` attribute has been changed to ' +
          '`data-ajax--url` and support for the old attribute will be removed' +
          ' in future versions of Select2.'
        );
      }

      $e.attr('ajax--url', $e.data('ajaxUrl'));
      $e.data('ajax--url', $e.data('ajaxUrl'));
    }

    var dataset = {};

    // Prefer the element's `dataset` attribute if it exists
    // jQuery 1.x does not correctly handle data attributes with multiple dashes
    if ($.fn.jquery && $.fn.jquery.substr(0, 2) == '1.' && $e[0].dataset) {
      dataset = $.extend(true, {}, $e[0].dataset, $e.data());
    } else {
      dataset = $e.data();
    }

    var data = $.extend(true, {}, dataset);

    data = Utils._convertData(data);

    for (var key in data) {
      if ($.inArray(key, excludedData) > -1) {
        continue;
      }

      if ($.isPlainObject(this.options[key])) {
        $.extend(this.options[key], data[key]);
      } else {
        this.options[key] = data[key];
      }
    }

    return this;
  };

  Options.prototype.get = function (key) {
    return this.options[key];
  };

  Options.prototype.set = function (key, val) {
    this.options[key] = val;
  };

  return Options;
});

S2.define('select2/core',[
  'jquery',
  './options',
  './utils',
  './keys'
], function ($, Options, Utils, KEYS) {
  var Select2 = function ($element, options) {
    if ($element.data('select2') != null) {
      $element.data('select2').destroy();
    }

    this.$element = $element;

    this.id = this._generateId($element);

    options = options || {};

    this.options = new Options(options, $element);

    Select2.__super__.constructor.call(this);

    // Set up the tabindex

    var tabindex = $element.attr('tabindex') || 0;
    $element.data('old-tabindex', tabindex);
    $element.attr('tabindex', '-1');

    // Set up containers and adapters

    var DataAdapter = this.options.get('dataAdapter');
    this.dataAdapter = new DataAdapter($element, this.options);

    var $container = this.render();

    this._placeContainer($container);

    var SelectionAdapter = this.options.get('selectionAdapter');
    this.selection = new SelectionAdapter($element, this.options);
    this.$selection = this.selection.render();

    this.selection.position(this.$selection, $container);

    var DropdownAdapter = this.options.get('dropdownAdapter');
    this.dropdown = new DropdownAdapter($element, this.options);
    this.$dropdown = this.dropdown.render();

    this.dropdown.position(this.$dropdown, $container);

    var ResultsAdapter = this.options.get('resultsAdapter');
    this.results = new ResultsAdapter($element, this.options, this.dataAdapter);
    this.$results = this.results.render();

    this.results.position(this.$results, this.$dropdown);

    // Bind events

    var self = this;

    // Bind the container to all of the adapters
    this._bindAdapters();

    // Register any DOM event handlers
    this._registerDomEvents();

    // Register any internal event handlers
    this._registerDataEvents();
    this._registerSelectionEvents();
    this._registerDropdownEvents();
    this._registerResultsEvents();
    this._registerEvents();

    // Set the initial state
    this.dataAdapter.current(function (initialData) {
      self.trigger('selection:update', {
        data: initialData
      });
    });

    // Hide the original select
    $element.addClass('select2-hidden-accessible');
    $element.attr('aria-hidden', 'true');

    // Synchronize any monitored attributes
    this._syncAttributes();

    $element.data('select2', this);
  };

  Utils.Extend(Select2, Utils.Observable);

  Select2.prototype._generateId = function ($element) {
    var id = '';

    if ($element.attr('id') != null) {
      id = $element.attr('id');
    } else if ($element.attr('name') != null) {
      id = $element.attr('name') + '-' + Utils.generateChars(2);
    } else {
      id = Utils.generateChars(4);
    }

    id = id.replace(/(:|\.|\[|\]|,)/g, '');
    id = 'select2-' + id;

    return id;
  };

  Select2.prototype._placeContainer = function ($container) {
    $container.insertAfter(this.$element);

    var width = this._resolveWidth(this.$element, this.options.get('width'));

    if (width != null) {
      $container.css('width', width);
    }
  };

  Select2.prototype._resolveWidth = function ($element, method) {
    var WIDTH = /^width:(([-+]?([0-9]*\.)?[0-9]+)(px|em|ex|%|in|cm|mm|pt|pc))/i;

    if (method == 'resolve') {
      var styleWidth = this._resolveWidth($element, 'style');

      if (styleWidth != null) {
        return styleWidth;
      }

      return this._resolveWidth($element, 'element');
    }

    if (method == 'element') {
      var elementWidth = $element.outerWidth(false);

      if (elementWidth <= 0) {
        return 'auto';
      }

      return elementWidth + 'px';
    }

    if (method == 'style') {
      var style = $element.attr('style');

      if (typeof(style) !== 'string') {
        return null;
      }

      var attrs = style.split(';');

      for (var i = 0, l = attrs.length; i < l; i = i + 1) {
        var attr = attrs[i].replace(/\s/g, '');
        var matches = attr.match(WIDTH);

        if (matches !== null && matches.length >= 1) {
          return matches[1];
        }
      }

      return null;
    }

    return method;
  };

  Select2.prototype._bindAdapters = function () {
    this.dataAdapter.bind(this, this.$container);
    this.selection.bind(this, this.$container);

    this.dropdown.bind(this, this.$container);
    this.results.bind(this, this.$container);
  };

  Select2.prototype._registerDomEvents = function () {
    var self = this;

    this.$element.on('change.select2', function () {
      self.dataAdapter.current(function (data) {
        self.trigger('selection:update', {
          data: data
        });
      });
    });

    this.$element.on('focus.select2', function (evt) {
      self.trigger('focus', evt);
    });

    this._syncA = Utils.bind(this._syncAttributes, this);
    this._syncS = Utils.bind(this._syncSubtree, this);

    if (this.$element[0].attachEvent) {
      this.$element[0].attachEvent('onpropertychange', this._syncA);
    }

    var observer = window.MutationObserver ||
      window.WebKitMutationObserver ||
      window.MozMutationObserver
    ;

    if (observer != null) {
      this._observer = new observer(function (mutations) {
        $.each(mutations, self._syncA);
        $.each(mutations, self._syncS);
      });
      this._observer.observe(this.$element[0], {
        attributes: true,
        childList: true,
        subtree: false
      });
    } else if (this.$element[0].addEventListener) {
      this.$element[0].addEventListener(
        'DOMAttrModified',
        self._syncA,
        false
      );
      this.$element[0].addEventListener(
        'DOMNodeInserted',
        self._syncS,
        false
      );
      this.$element[0].addEventListener(
        'DOMNodeRemoved',
        self._syncS,
        false
      );
    }
  };

  Select2.prototype._registerDataEvents = function () {
    var self = this;

    this.dataAdapter.on('*', function (name, params) {
      self.trigger(name, params);
    });
  };

  Select2.prototype._registerSelectionEvents = function () {
    var self = this;
    var nonRelayEvents = ['toggle', 'focus'];

    this.selection.on('toggle', function () {
      self.toggleDropdown();
    });

    this.selection.on('focus', function (params) {
      self.focus(params);
    });

    this.selection.on('*', function (name, params) {
      if ($.inArray(name, nonRelayEvents) !== -1) {
        return;
      }

      self.trigger(name, params);
    });
  };

  Select2.prototype._registerDropdownEvents = function () {
    var self = this;

    this.dropdown.on('*', function (name, params) {
      self.trigger(name, params);
    });
  };

  Select2.prototype._registerResultsEvents = function () {
    var self = this;

    this.results.on('*', function (name, params) {
      self.trigger(name, params);
    });
  };

  Select2.prototype._registerEvents = function () {
    var self = this;

    this.on('open', function () {
      self.$container.addClass('select2-container--open');
    });

    this.on('close', function () {
      self.$container.removeClass('select2-container--open');
    });

    this.on('enable', function () {
      self.$container.removeClass('select2-container--disabled');
    });

    this.on('disable', function () {
      self.$container.addClass('select2-container--disabled');
    });

    this.on('blur', function () {
      self.$container.removeClass('select2-container--focus');
    });

    this.on('query', function (params) {
      if (!self.isOpen()) {
        self.trigger('open', {});
      }

      this.dataAdapter.query(params, function (data) {
        self.trigger('results:all', {
          data: data,
          query: params
        });
      });
    });

    this.on('query:append', function (params) {
      this.dataAdapter.query(params, function (data) {
        self.trigger('results:append', {
          data: data,
          query: params
        });
      });
    });

    this.on('keypress', function (evt) {
      var key = evt.which;

      if (self.isOpen()) {
        if (key === KEYS.ESC || key === KEYS.TAB ||
            (key === KEYS.UP && evt.altKey)) {
          self.close();

          evt.preventDefault();
        } else if (key === KEYS.ENTER) {
          self.trigger('results:select', {});

          evt.preventDefault();
        } else if ((key === KEYS.SPACE && evt.ctrlKey)) {
          self.trigger('results:toggle', {});

          evt.preventDefault();
        } else if (key === KEYS.UP) {
          self.trigger('results:previous', {});

          evt.preventDefault();
        } else if (key === KEYS.DOWN) {
          self.trigger('results:next', {});

          evt.preventDefault();
        }
      } else {
        if (key === KEYS.ENTER || key === KEYS.SPACE ||
            (key === KEYS.DOWN && evt.altKey)) {
          self.open();

          evt.preventDefault();
        }
      }
    });
  };

  Select2.prototype._syncAttributes = function () {
    this.options.set('disabled', this.$element.prop('disabled'));

    if (this.options.get('disabled')) {
      if (this.isOpen()) {
        this.close();
      }

      this.trigger('disable', {});
    } else {
      this.trigger('enable', {});
    }
  };

  Select2.prototype._syncSubtree = function (evt, mutations) {
    var changed = false;
    var self = this;

    // Ignore any mutation events raised for elements that aren't options or
    // optgroups. This handles the case when the select element is destroyed
    if (
      evt && evt.target && (
        evt.target.nodeName !== 'OPTION' && evt.target.nodeName !== 'OPTGROUP'
      )
    ) {
      return;
    }

    if (!mutations) {
      // If mutation events aren't supported, then we can only assume that the
      // change affected the selections
      changed = true;
    } else if (mutations.addedNodes && mutations.addedNodes.length > 0) {
      for (var n = 0; n < mutations.addedNodes.length; n++) {
        var node = mutations.addedNodes[n];

        if (node.selected) {
          changed = true;
        }
      }
    } else if (mutations.removedNodes && mutations.removedNodes.length > 0) {
      changed = true;
    }

    // Only re-pull the data if we think there is a change
    if (changed) {
      this.dataAdapter.current(function (currentData) {
        self.trigger('selection:update', {
          data: currentData
        });
      });
    }
  };

  /**
   * Override the trigger method to automatically trigger pre-events when
   * there are events that can be prevented.
   */
  Select2.prototype.trigger = function (name, args) {
    var actualTrigger = Select2.__super__.trigger;
    var preTriggerMap = {
      'open': 'opening',
      'close': 'closing',
      'select': 'selecting',
      'unselect': 'unselecting'
    };

    if (args === undefined) {
      args = {};
    }

    if (name in preTriggerMap) {
      var preTriggerName = preTriggerMap[name];
      var preTriggerArgs = {
        prevented: false,
        name: name,
        args: args
      };

      actualTrigger.call(this, preTriggerName, preTriggerArgs);

      if (preTriggerArgs.prevented) {
        args.prevented = true;

        return;
      }
    }

    actualTrigger.call(this, name, args);
  };

  Select2.prototype.toggleDropdown = function () {
    if (this.options.get('disabled')) {
      return;
    }

    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  };

  Select2.prototype.open = function () {
    if (this.isOpen()) {
      return;
    }

    this.trigger('query', {});
  };

  Select2.prototype.close = function () {
    if (!this.isOpen()) {
      return;
    }

    this.trigger('close', {});
  };

  Select2.prototype.isOpen = function () {
    return this.$container.hasClass('select2-container--open');
  };

  Select2.prototype.hasFocus = function () {
    return this.$container.hasClass('select2-container--focus');
  };

  Select2.prototype.focus = function (data) {
    // No need to re-trigger focus events if we are already focused
    if (this.hasFocus()) {
      return;
    }

    this.$container.addClass('select2-container--focus');
    this.trigger('focus', {});
  };

  Select2.prototype.enable = function (args) {
    if (this.options.get('debug') && window.console && console.warn) {
      console.warn(
        'Select2: The `select2("enable")` method has been deprecated and will' +
        ' be removed in later Select2 versions. Use $element.prop("disabled")' +
        ' instead.'
      );
    }

    if (args == null || args.length === 0) {
      args = [true];
    }

    var disabled = !args[0];

    this.$element.prop('disabled', disabled);
  };

  Select2.prototype.data = function () {
    if (this.options.get('debug') &&
        arguments.length > 0 && window.console && console.warn) {
      console.warn(
        'Select2: Data can no longer be set using `select2("data")`. You ' +
        'should consider setting the value instead using `$element.val()`.'
      );
    }

    var data = [];

    this.dataAdapter.current(function (currentData) {
      data = currentData;
    });

    return data;
  };

  Select2.prototype.val = function (args) {
    if (this.options.get('debug') && window.console && console.warn) {
      console.warn(
        'Select2: The `select2("val")` method has been deprecated and will be' +
        ' removed in later Select2 versions. Use $element.val() instead.'
      );
    }

    if (args == null || args.length === 0) {
      return this.$element.val();
    }

    var newVal = args[0];

    if ($.isArray(newVal)) {
      newVal = $.map(newVal, function (obj) {
        return obj.toString();
      });
    }

    this.$element.val(newVal).trigger('change');
  };

  Select2.prototype.destroy = function () {
    this.$container.remove();

    if (this.$element[0].detachEvent) {
      this.$element[0].detachEvent('onpropertychange', this._syncA);
    }

    if (this._observer != null) {
      this._observer.disconnect();
      this._observer = null;
    } else if (this.$element[0].removeEventListener) {
      this.$element[0]
        .removeEventListener('DOMAttrModified', this._syncA, false);
      this.$element[0]
        .removeEventListener('DOMNodeInserted', this._syncS, false);
      this.$element[0]
        .removeEventListener('DOMNodeRemoved', this._syncS, false);
    }

    this._syncA = null;
    this._syncS = null;

    this.$element.off('.select2');
    this.$element.attr('tabindex', this.$element.data('old-tabindex'));

    this.$element.removeClass('select2-hidden-accessible');
    this.$element.attr('aria-hidden', 'false');
    this.$element.removeData('select2');

    this.dataAdapter.destroy();
    this.selection.destroy();
    this.dropdown.destroy();
    this.results.destroy();

    this.dataAdapter = null;
    this.selection = null;
    this.dropdown = null;
    this.results = null;
  };

  Select2.prototype.render = function () {
    var $container = $(
      '<span class="select2 select2-container">' +
        '<span class="selection"></span>' +
        '<span class="dropdown-wrapper" aria-hidden="true"></span>' +
      '</span>'
    );

    $container.attr('dir', this.options.get('dir'));

    this.$container = $container;

    this.$container.addClass('select2-container--' + this.options.get('theme'));

    $container.data('element', this.$element);

    return $container;
  };

  return Select2;
});

S2.define('jquery-mousewheel',[
  'jquery'
], function ($) {
  // Used to shim jQuery.mousewheel for non-full builds.
  return $;
});

S2.define('jquery.select2',[
  'jquery',
  'jquery-mousewheel',

  './select2/core',
  './select2/defaults'
], function ($, _, Select2, Defaults) {
  if ($.fn.select2 == null) {
    // All methods that should return the element
    var thisMethods = ['open', 'close', 'destroy'];

    $.fn.select2 = function (options) {
      options = options || {};

      if (typeof options === 'object') {
        this.each(function () {
          var instanceOptions = $.extend(true, {}, options);

          var instance = new Select2($(this), instanceOptions);
        });

        return this;
      } else if (typeof options === 'string') {
        var ret;
        var args = Array.prototype.slice.call(arguments, 1);

        this.each(function () {
          var instance = $(this).data('select2');

          if (instance == null && window.console && console.error) {
            console.error(
              'The select2(\'' + options + '\') method was called on an ' +
              'element that is not using Select2.'
            );
          }

          ret = instance[options].apply(instance, args);
        });

        // Check if we should be returning `this`
        if ($.inArray(options, thisMethods) > -1) {
          return this;
        }

        return ret;
      } else {
        throw new Error('Invalid arguments for Select2: ' + options);
      }
    };
  }

  if ($.fn.select2.defaults == null) {
    $.fn.select2.defaults = Defaults;
  }

  return Select2;
});

  // Return the AMD loader configuration so it can be used outside of this file
  return {
    define: S2.define,
    require: S2.require
  };
}());

  // Autoload the jQuery bindings
  // We know that all of the modules exist above this, so we're safe
  var select2 = S2.require('jquery.select2');

  // Hold the AMD module references on the jQuery function that was just loaded
  // This allows Select2 to use the internal loader outside of this file, such
  // as in the language files.
  jQuery.fn.select2.amd = S2;

  // Return the Select2 instance for anyone who is importing it.
  return select2;
}));
/*! iCheck v1.0.2 by Damir Sultanov, http://git.io/arlzeA, MIT Licensed */

(function(f){function A(a,b,d){var c=a[0],g=/er/.test(d)?_indeterminate:/bl/.test(d)?n:k,e=d==_update?{checked:c[k],disabled:c[n],indeterminate:"true"==a.attr(_indeterminate)||"false"==a.attr(_determinate)}:c[g];if(/^(ch|di|in)/.test(d)&&!e)x(a,g);else if(/^(un|en|de)/.test(d)&&e)q(a,g);else if(d==_update)for(var f in e)e[f]?x(a,f,!0):q(a,f,!0);else if(!b||"toggle"==d){if(!b)a[_callback]("ifClicked");e?c[_type]!==r&&q(a,g):x(a,g)}}function x(a,b,d){var c=a[0],g=a.parent(),e=b==k,u=b==_indeterminate,
v=b==n,s=u?_determinate:e?y:"enabled",F=l(a,s+t(c[_type])),B=l(a,b+t(c[_type]));if(!0!==c[b]){if(!d&&b==k&&c[_type]==r&&c.name){var w=a.closest("form"),p='input[name="'+c.name+'"]',p=w.length?w.find(p):f(p);p.each(function(){this!==c&&f(this).data(m)&&q(f(this),b)})}u?(c[b]=!0,c[k]&&q(a,k,"force")):(d||(c[b]=!0),e&&c[_indeterminate]&&q(a,_indeterminate,!1));D(a,e,b,d)}c[n]&&l(a,_cursor,!0)&&g.find("."+C).css(_cursor,"default");g[_add](B||l(a,b)||"");g.attr("role")&&!u&&g.attr("aria-"+(v?n:k),"true");
g[_remove](F||l(a,s)||"")}function q(a,b,d){var c=a[0],g=a.parent(),e=b==k,f=b==_indeterminate,m=b==n,s=f?_determinate:e?y:"enabled",q=l(a,s+t(c[_type])),r=l(a,b+t(c[_type]));if(!1!==c[b]){if(f||!d||"force"==d)c[b]=!1;D(a,e,s,d)}!c[n]&&l(a,_cursor,!0)&&g.find("."+C).css(_cursor,"pointer");g[_remove](r||l(a,b)||"");g.attr("role")&&!f&&g.attr("aria-"+(m?n:k),"false");g[_add](q||l(a,s)||"")}function E(a,b){if(a.data(m)){a.parent().html(a.attr("style",a.data(m).s||""));if(b)a[_callback](b);a.off(".i").unwrap();
f(_label+'[for="'+a[0].id+'"]').add(a.closest(_label)).off(".i")}}function l(a,b,f){if(a.data(m))return a.data(m).o[b+(f?"":"Class")]}function t(a){return a.charAt(0).toUpperCase()+a.slice(1)}function D(a,b,f,c){if(!c){if(b)a[_callback]("ifToggled");a[_callback]("ifChanged")[_callback]("if"+t(f))}}var m="iCheck",C=m+"-helper",r="radio",k="checked",y="un"+k,n="disabled";_determinate="determinate";_indeterminate="in"+_determinate;_update="update";_type="type";_click="click";_touch="touchbegin.i touchend.i";
_add="addClass";_remove="removeClass";_callback="trigger";_label="label";_cursor="cursor";_mobile=/ipad|iphone|ipod|android|blackberry|windows phone|opera mini|silk/i.test(navigator.userAgent);f.fn[m]=function(a,b){var d='input[type="checkbox"], input[type="'+r+'"]',c=f(),g=function(a){a.each(function(){var a=f(this);c=a.is(d)?c.add(a):c.add(a.find(d))})};if(/^(check|uncheck|toggle|indeterminate|determinate|disable|enable|update|destroy)$/i.test(a))return a=a.toLowerCase(),g(this),c.each(function(){var c=
f(this);"destroy"==a?E(c,"ifDestroyed"):A(c,!0,a);f.isFunction(b)&&b()});if("object"!=typeof a&&a)return this;var e=f.extend({checkedClass:k,disabledClass:n,indeterminateClass:_indeterminate,labelHover:!0},a),l=e.handle,v=e.hoverClass||"hover",s=e.focusClass||"focus",t=e.activeClass||"active",B=!!e.labelHover,w=e.labelHoverClass||"hover",p=(""+e.increaseArea).replace("%","")|0;if("checkbox"==l||l==r)d='input[type="'+l+'"]';-50>p&&(p=-50);g(this);return c.each(function(){var a=f(this);E(a);var c=this,
b=c.id,g=-p+"%",d=100+2*p+"%",d={position:"absolute",top:g,left:g,display:"block",width:d,height:d,margin:0,padding:0,background:"#fff",border:0,opacity:0},g=_mobile?{position:"absolute",visibility:"hidden"}:p?d:{position:"absolute",opacity:0},l="checkbox"==c[_type]?e.checkboxClass||"icheckbox":e.radioClass||"i"+r,z=f(_label+'[for="'+b+'"]').add(a.closest(_label)),u=!!e.aria,y=m+"-"+Math.random().toString(36).substr(2,6),h='<div class="'+l+'" '+(u?'role="'+c[_type]+'" ':"");u&&z.each(function(){h+=
'aria-labelledby="';this.id?h+=this.id:(this.id=y,h+=y);h+='"'});h=a.wrap(h+"/>")[_callback]("ifCreated").parent().append(e.insert);d=f('<ins class="'+C+'"/>').css(d).appendTo(h);a.data(m,{o:e,s:a.attr("style")}).css(g);e.inheritClass&&h[_add](c.className||"");e.inheritID&&b&&h.attr("id",m+"-"+b);"static"==h.css("position")&&h.css("position","relative");A(a,!0,_update);if(z.length)z.on(_click+".i mouseover.i mouseout.i "+_touch,function(b){var d=b[_type],e=f(this);if(!c[n]){if(d==_click){if(f(b.target).is("a"))return;
A(a,!1,!0)}else B&&(/ut|nd/.test(d)?(h[_remove](v),e[_remove](w)):(h[_add](v),e[_add](w)));if(_mobile)b.stopPropagation();else return!1}});a.on(_click+".i focus.i blur.i keyup.i keydown.i keypress.i",function(b){var d=b[_type];b=b.keyCode;if(d==_click)return!1;if("keydown"==d&&32==b)return c[_type]==r&&c[k]||(c[k]?q(a,k):x(a,k)),!1;if("keyup"==d&&c[_type]==r)!c[k]&&x(a,k);else if(/us|ur/.test(d))h["blur"==d?_remove:_add](s)});d.on(_click+" mousedown mouseup mouseover mouseout "+_touch,function(b){var d=
b[_type],e=/wn|up/.test(d)?t:v;if(!c[n]){if(d==_click)A(a,!1,!0);else{if(/wn|er|in/.test(d))h[_add](e);else h[_remove](e+" "+t);if(z.length&&B&&e==v)z[/ut|nd/.test(d)?_remove:_add](w)}if(_mobile)b.stopPropagation();else return!1}})})}})(window.jQuery||window.Zepto);
/*!
 * Chart.js
 * http://chartjs.org/
 * Version: 1.0.1-beta.3
 *
 * Copyright 2014 Nick Downie
 * Released under the MIT license
 * https://github.com/nnnick/Chart.js/blob/master/LICENSE.md
 */

(function(){"use strict";var t=this,i=t.Chart,e=function(t){this.canvas=t.canvas,this.ctx=t;this.width=t.canvas.width,this.height=t.canvas.height;return this.aspectRatio=this.width/this.height,s.retinaScale(this),this};e.defaults={global:{animation:!0,animationSteps:60,animationEasing:"easeOutQuart",showScale:!0,scaleOverride:!1,scaleSteps:null,scaleStepWidth:null,scaleStartValue:null,scaleLineColor:"rgba(0,0,0,.1)",scaleLineWidth:1,scaleShowLabels:!0,scaleLabel:"<%=value%>",scaleIntegersOnly:!0,scaleBeginAtZero:!1,scaleFontFamily:"'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",scaleFontSize:12,scaleFontStyle:"normal",scaleFontColor:"#666",responsive:!1,maintainAspectRatio:!0,showTooltips:!0,tooltipEvents:["mousemove","touchstart","touchmove","mouseout"],tooltipFillColor:"rgba(0,0,0,0.8)",tooltipFontFamily:"'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",tooltipFontSize:14,tooltipFontStyle:"normal",tooltipFontColor:"#fff",tooltipTitleFontFamily:"'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",tooltipTitleFontSize:14,tooltipTitleFontStyle:"bold",tooltipTitleFontColor:"#fff",tooltipYPadding:6,tooltipXPadding:6,tooltipCaretSize:8,tooltipCornerRadius:6,tooltipXOffset:10,tooltipTemplate:"<%if (label){%><%=label%>: <%}%><%= value %>",multiTooltipTemplate:"<%= value %>",multiTooltipKeyBackground:"#fff",onAnimationProgress:function(){},onAnimationComplete:function(){}}},e.types={};var s=e.helpers={},n=s.each=function(t,i,e){var s=Array.prototype.slice.call(arguments,3);if(t)if(t.length===+t.length){var n;for(n=0;n<t.length;n++)i.apply(e,[t[n],n].concat(s))}else for(var o in t)i.apply(e,[t[o],o].concat(s))},o=s.clone=function(t){var i={};return n(t,function(e,s){t.hasOwnProperty(s)&&(i[s]=e)}),i},a=s.extend=function(t){return n(Array.prototype.slice.call(arguments,1),function(i){n(i,function(e,s){i.hasOwnProperty(s)&&(t[s]=e)})}),t},h=s.merge=function(){var t=Array.prototype.slice.call(arguments,0);return t.unshift({}),a.apply(null,t)},l=s.indexOf=function(t,i){if(Array.prototype.indexOf)return t.indexOf(i);for(var e=0;e<t.length;e++)if(t[e]===i)return e;return-1},r=s.inherits=function(t){var i=this,e=t&&t.hasOwnProperty("constructor")?t.constructor:function(){return i.apply(this,arguments)},s=function(){this.constructor=e};return s.prototype=i.prototype,e.prototype=new s,e.extend=r,t&&a(e.prototype,t),e.__super__=i.prototype,e},c=s.noop=function(){},u=s.uid=function(){var t=0;return function(){return"chart-"+t++}}(),d=s.warn=function(t){window.console&&"function"==typeof window.console.warn&&console.warn(t)},p=s.amd="function"==typeof t.define&&t.define.amd,f=s.isNumber=function(t){return!isNaN(parseFloat(t))&&isFinite(t)},g=s.max=function(t){return Math.max.apply(Math,t)},m=s.min=function(t){return Math.min.apply(Math,t)},v=(s.cap=function(t,i,e){if(f(i)){if(t>i)return i}else if(f(e)&&e>t)return e;return t},s.getDecimalPlaces=function(t){return t%1!==0&&f(t)?t.toString().split(".")[1].length:0}),x=s.radians=function(t){return t*(Math.PI/180)},S=(s.getAngleFromPoint=function(t,i){var e=i.x-t.x,s=i.y-t.y,n=Math.sqrt(e*e+s*s),o=2*Math.PI+Math.atan2(s,e);return 0>e&&0>s&&(o+=2*Math.PI),{angle:o,distance:n}},s.aliasPixel=function(t){return t%2===0?0:.5}),C=(s.splineCurve=function(t,i,e,s){var n=Math.sqrt(Math.pow(i.x-t.x,2)+Math.pow(i.y-t.y,2)),o=Math.sqrt(Math.pow(e.x-i.x,2)+Math.pow(e.y-i.y,2)),a=s*n/(n+o),h=s*o/(n+o);return{inner:{x:i.x-a*(e.x-t.x),y:i.y-a*(e.y-t.y)},outer:{x:i.x+h*(e.x-t.x),y:i.y+h*(e.y-t.y)}}},s.calculateOrderOfMagnitude=function(t){return Math.floor(Math.log(t)/Math.LN10)}),y=(s.calculateScaleRange=function(t,i,e,s,n){var o=2,a=Math.floor(i/(1.5*e)),h=o>=a,l=g(t),r=m(t);l===r&&(l+=.5,r>=.5&&!s?r-=.5:l+=.5);for(var c=Math.abs(l-r),u=C(c),d=Math.ceil(l/(1*Math.pow(10,u)))*Math.pow(10,u),p=s?0:Math.floor(r/(1*Math.pow(10,u)))*Math.pow(10,u),f=d-p,v=Math.pow(10,u),x=Math.round(f/v);(x>a||a>2*x)&&!h;)if(x>a)v*=2,x=Math.round(f/v),x%1!==0&&(h=!0);else if(n&&u>=0){if(v/2%1!==0)break;v/=2,x=Math.round(f/v)}else v/=2,x=Math.round(f/v);return h&&(x=o,v=f/x),{steps:x,stepValue:v,min:p,max:p+x*v}},s.template=function(t,i){function e(t,i){var e=/\W/.test(t)?new Function("obj","var p=[],print=function(){p.push.apply(p,arguments);};with(obj){p.push('"+t.replace(/[\r\t\n]/g," ").split("<%").join("	").replace(/((^|%>)[^\t]*)'/g,"$1\r").replace(/\t=(.*?)%>/g,"',$1,'").split("	").join("');").split("%>").join("p.push('").split("\r").join("\\'")+"');}return p.join('');"):s[t]=s[t];return i?e(i):e}if(t instanceof Function)return t(i);var s={};return e(t,i)}),b=(s.generateLabels=function(t,i,e,s){var o=new Array(i);return labelTemplateString&&n(o,function(i,n){o[n]=y(t,{value:e+s*(n+1)})}),o},s.easingEffects={linear:function(t){return t},easeInQuad:function(t){return t*t},easeOutQuad:function(t){return-1*t*(t-2)},easeInOutQuad:function(t){return(t/=.5)<1?.5*t*t:-0.5*(--t*(t-2)-1)},easeInCubic:function(t){return t*t*t},easeOutCubic:function(t){return 1*((t=t/1-1)*t*t+1)},easeInOutCubic:function(t){return(t/=.5)<1?.5*t*t*t:.5*((t-=2)*t*t+2)},easeInQuart:function(t){return t*t*t*t},easeOutQuart:function(t){return-1*((t=t/1-1)*t*t*t-1)},easeInOutQuart:function(t){return(t/=.5)<1?.5*t*t*t*t:-0.5*((t-=2)*t*t*t-2)},easeInQuint:function(t){return 1*(t/=1)*t*t*t*t},easeOutQuint:function(t){return 1*((t=t/1-1)*t*t*t*t+1)},easeInOutQuint:function(t){return(t/=.5)<1?.5*t*t*t*t*t:.5*((t-=2)*t*t*t*t+2)},easeInSine:function(t){return-1*Math.cos(t/1*(Math.PI/2))+1},easeOutSine:function(t){return 1*Math.sin(t/1*(Math.PI/2))},easeInOutSine:function(t){return-0.5*(Math.cos(Math.PI*t/1)-1)},easeInExpo:function(t){return 0===t?1:1*Math.pow(2,10*(t/1-1))},easeOutExpo:function(t){return 1===t?1:1*(-Math.pow(2,-10*t/1)+1)},easeInOutExpo:function(t){return 0===t?0:1===t?1:(t/=.5)<1?.5*Math.pow(2,10*(t-1)):.5*(-Math.pow(2,-10*--t)+2)},easeInCirc:function(t){return t>=1?t:-1*(Math.sqrt(1-(t/=1)*t)-1)},easeOutCirc:function(t){return 1*Math.sqrt(1-(t=t/1-1)*t)},easeInOutCirc:function(t){return(t/=.5)<1?-0.5*(Math.sqrt(1-t*t)-1):.5*(Math.sqrt(1-(t-=2)*t)+1)},easeInElastic:function(t){var i=1.70158,e=0,s=1;return 0===t?0:1==(t/=1)?1:(e||(e=.3),s<Math.abs(1)?(s=1,i=e/4):i=e/(2*Math.PI)*Math.asin(1/s),-(s*Math.pow(2,10*(t-=1))*Math.sin(2*(1*t-i)*Math.PI/e)))},easeOutElastic:function(t){var i=1.70158,e=0,s=1;return 0===t?0:1==(t/=1)?1:(e||(e=.3),s<Math.abs(1)?(s=1,i=e/4):i=e/(2*Math.PI)*Math.asin(1/s),s*Math.pow(2,-10*t)*Math.sin(2*(1*t-i)*Math.PI/e)+1)},easeInOutElastic:function(t){var i=1.70158,e=0,s=1;return 0===t?0:2==(t/=.5)?1:(e||(e=.3*1.5),s<Math.abs(1)?(s=1,i=e/4):i=e/(2*Math.PI)*Math.asin(1/s),1>t?-.5*s*Math.pow(2,10*(t-=1))*Math.sin(2*(1*t-i)*Math.PI/e):s*Math.pow(2,-10*(t-=1))*Math.sin(2*(1*t-i)*Math.PI/e)*.5+1)},easeInBack:function(t){var i=1.70158;return 1*(t/=1)*t*((i+1)*t-i)},easeOutBack:function(t){var i=1.70158;return 1*((t=t/1-1)*t*((i+1)*t+i)+1)},easeInOutBack:function(t){var i=1.70158;return(t/=.5)<1?.5*t*t*(((i*=1.525)+1)*t-i):.5*((t-=2)*t*(((i*=1.525)+1)*t+i)+2)},easeInBounce:function(t){return 1-b.easeOutBounce(1-t)},easeOutBounce:function(t){return(t/=1)<1/2.75?7.5625*t*t:2/2.75>t?1*(7.5625*(t-=1.5/2.75)*t+.75):2.5/2.75>t?1*(7.5625*(t-=2.25/2.75)*t+.9375):1*(7.5625*(t-=2.625/2.75)*t+.984375)},easeInOutBounce:function(t){return.5>t?.5*b.easeInBounce(2*t):.5*b.easeOutBounce(2*t-1)+.5}}),w=s.requestAnimFrame=function(){return window.requestAnimationFrame||window.webkitRequestAnimationFrame||window.mozRequestAnimationFrame||window.oRequestAnimationFrame||window.msRequestAnimationFrame||function(t){return window.setTimeout(t,1e3/60)}}(),P=(s.cancelAnimFrame=function(){return window.cancelAnimationFrame||window.webkitCancelAnimationFrame||window.mozCancelAnimationFrame||window.oCancelAnimationFrame||window.msCancelAnimationFrame||function(t){return window.clearTimeout(t,1e3/60)}}(),s.animationLoop=function(t,i,e,s,n,o){var a=0,h=b[e]||b.linear,l=function(){a++;var e=a/i,r=h(e);t.call(o,r,e,a),s.call(o,r,e),i>a?o.animationFrame=w(l):n.apply(o)};w(l)},s.getRelativePosition=function(t){var i,e,s=t.originalEvent||t,n=t.currentTarget||t.srcElement,o=n.getBoundingClientRect();return s.touches?(i=s.touches[0].clientX-o.left,e=s.touches[0].clientY-o.top):(i=s.clientX-o.left,e=s.clientY-o.top),{x:i,y:e}},s.addEvent=function(t,i,e){t.addEventListener?t.addEventListener(i,e):t.attachEvent?t.attachEvent("on"+i,e):t["on"+i]=e}),L=s.removeEvent=function(t,i,e){t.removeEventListener?t.removeEventListener(i,e,!1):t.detachEvent?t.detachEvent("on"+i,e):t["on"+i]=c},k=(s.bindEvents=function(t,i,e){t.events||(t.events={}),n(i,function(i){t.events[i]=function(){e.apply(t,arguments)},P(t.chart.canvas,i,t.events[i])})},s.unbindEvents=function(t,i){n(i,function(i,e){L(t.chart.canvas,e,i)})}),F=s.getMaximumWidth=function(t){var i=t.parentNode;return i.clientWidth},R=s.getMaximumHeight=function(t){var i=t.parentNode;return i.clientHeight},A=(s.getMaximumSize=s.getMaximumWidth,s.retinaScale=function(t){var i=t.ctx,e=t.canvas.width,s=t.canvas.height;window.devicePixelRatio&&(i.canvas.style.width=e+"px",i.canvas.style.height=s+"px",i.canvas.height=s*window.devicePixelRatio,i.canvas.width=e*window.devicePixelRatio,i.scale(window.devicePixelRatio,window.devicePixelRatio))}),T=s.clear=function(t){t.ctx.clearRect(0,0,t.width,t.height)},M=s.fontString=function(t,i,e){return i+" "+t+"px "+e},W=s.longestText=function(t,i,e){t.font=i;var s=0;return n(e,function(i){var e=t.measureText(i).width;s=e>s?e:s}),s},z=s.drawRoundedRectangle=function(t,i,e,s,n,o){t.beginPath(),t.moveTo(i+o,e),t.lineTo(i+s-o,e),t.quadraticCurveTo(i+s,e,i+s,e+o),t.lineTo(i+s,e+n-o),t.quadraticCurveTo(i+s,e+n,i+s-o,e+n),t.lineTo(i+o,e+n),t.quadraticCurveTo(i,e+n,i,e+n-o),t.lineTo(i,e+o),t.quadraticCurveTo(i,e,i+o,e),t.closePath()};e.instances={},e.Type=function(t,i,s){this.options=i,this.chart=s,this.id=u(),e.instances[this.id]=this,i.responsive&&this.resize(),this.initialize.call(this,t)},a(e.Type.prototype,{initialize:function(){return this},clear:function(){return T(this.chart),this},stop:function(){return s.cancelAnimFrame.call(t,this.animationFrame),this},resize:function(t){this.stop();var i=this.chart.canvas,e=F(this.chart.canvas),s=this.options.maintainAspectRatio?e/this.chart.aspectRatio:R(this.chart.canvas);return i.width=this.chart.width=e,i.height=this.chart.height=s,A(this.chart),"function"==typeof t&&t.apply(this,Array.prototype.slice.call(arguments,1)),this},reflow:c,render:function(t){return t&&this.reflow(),this.options.animation&&!t?s.animationLoop(this.draw,this.options.animationSteps,this.options.animationEasing,this.options.onAnimationProgress,this.options.onAnimationComplete,this):(this.draw(),this.options.onAnimationComplete.call(this)),this},generateLegend:function(){return y(this.options.legendTemplate,this)},destroy:function(){this.clear(),k(this,this.events),delete e.instances[this.id]},showTooltip:function(t,i){"undefined"==typeof this.activeElements&&(this.activeElements=[]);var o=function(t){var i=!1;return t.length!==this.activeElements.length?i=!0:(n(t,function(t,e){t!==this.activeElements[e]&&(i=!0)},this),i)}.call(this,t);if(o||i){if(this.activeElements=t,this.draw(),t.length>0)if(this.datasets&&this.datasets.length>1){for(var a,h,r=this.datasets.length-1;r>=0&&(a=this.datasets[r].points||this.datasets[r].bars||this.datasets[r].segments,h=l(a,t[0]),-1===h);r--);var c=[],u=[],d=function(){var t,i,e,n,o,a=[],l=[],r=[];return s.each(this.datasets,function(i){t=i.points||i.bars||i.segments,t[h]&&a.push(t[h])}),s.each(a,function(t){l.push(t.x),r.push(t.y),c.push(s.template(this.options.multiTooltipTemplate,t)),u.push({fill:t._saved.fillColor||t.fillColor,stroke:t._saved.strokeColor||t.strokeColor})},this),o=m(r),e=g(r),n=m(l),i=g(l),{x:n>this.chart.width/2?n:i,y:(o+e)/2}}.call(this,h);new e.MultiTooltip({x:d.x,y:d.y,xPadding:this.options.tooltipXPadding,yPadding:this.options.tooltipYPadding,xOffset:this.options.tooltipXOffset,fillColor:this.options.tooltipFillColor,textColor:this.options.tooltipFontColor,fontFamily:this.options.tooltipFontFamily,fontStyle:this.options.tooltipFontStyle,fontSize:this.options.tooltipFontSize,titleTextColor:this.options.tooltipTitleFontColor,titleFontFamily:this.options.tooltipTitleFontFamily,titleFontStyle:this.options.tooltipTitleFontStyle,titleFontSize:this.options.tooltipTitleFontSize,cornerRadius:this.options.tooltipCornerRadius,labels:c,legendColors:u,legendColorBackground:this.options.multiTooltipKeyBackground,title:t[0].label,chart:this.chart,ctx:this.chart.ctx}).draw()}else n(t,function(t){var i=t.tooltipPosition();new e.Tooltip({x:Math.round(i.x),y:Math.round(i.y),xPadding:this.options.tooltipXPadding,yPadding:this.options.tooltipYPadding,fillColor:this.options.tooltipFillColor,textColor:this.options.tooltipFontColor,fontFamily:this.options.tooltipFontFamily,fontStyle:this.options.tooltipFontStyle,fontSize:this.options.tooltipFontSize,caretHeight:this.options.tooltipCaretSize,cornerRadius:this.options.tooltipCornerRadius,text:y(this.options.tooltipTemplate,t),chart:this.chart}).draw()},this);return this}},toBase64Image:function(){return this.chart.canvas.toDataURL.apply(this.chart.canvas,arguments)}}),e.Type.extend=function(t){var i=this,s=function(){return i.apply(this,arguments)};if(s.prototype=o(i.prototype),a(s.prototype,t),s.extend=e.Type.extend,t.name||i.prototype.name){var n=t.name||i.prototype.name,l=e.defaults[i.prototype.name]?o(e.defaults[i.prototype.name]):{};e.defaults[n]=a(l,t.defaults),e.types[n]=s,e.prototype[n]=function(t,i){var o=h(e.defaults.global,e.defaults[n],i||{});return new s(t,o,this)}}else d("Name not provided for this chart, so it hasn't been registered");return i},e.Element=function(t){a(this,t),this.initialize.apply(this,arguments),this.save()},a(e.Element.prototype,{initialize:function(){},restore:function(t){return t?n(t,function(t){this[t]=this._saved[t]},this):a(this,this._saved),this},save:function(){return this._saved=o(this),delete this._saved._saved,this},update:function(t){return n(t,function(t,i){this._saved[i]=this[i],this[i]=t},this),this},transition:function(t,i){return n(t,function(t,e){this[e]=(t-this._saved[e])*i+this._saved[e]},this),this},tooltipPosition:function(){return{x:this.x,y:this.y}}}),e.Element.extend=r,e.Point=e.Element.extend({display:!0,inRange:function(t,i){var e=this.hitDetectionRadius+this.radius;return Math.pow(t-this.x,2)+Math.pow(i-this.y,2)<Math.pow(e,2)},draw:function(){if(this.display){var t=this.ctx;t.beginPath(),t.arc(this.x,this.y,this.radius,0,2*Math.PI),t.closePath(),t.strokeStyle=this.strokeColor,t.lineWidth=this.strokeWidth,t.fillStyle=this.fillColor,t.fill(),t.stroke()}}}),e.Arc=e.Element.extend({inRange:function(t,i){var e=s.getAngleFromPoint(this,{x:t,y:i}),n=e.angle>=this.startAngle&&e.angle<=this.endAngle,o=e.distance>=this.innerRadius&&e.distance<=this.outerRadius;return n&&o},tooltipPosition:function(){var t=this.startAngle+(this.endAngle-this.startAngle)/2,i=(this.outerRadius-this.innerRadius)/2+this.innerRadius;return{x:this.x+Math.cos(t)*i,y:this.y+Math.sin(t)*i}},draw:function(t){var i=this.ctx;i.beginPath(),i.arc(this.x,this.y,this.outerRadius,this.startAngle,this.endAngle),i.arc(this.x,this.y,this.innerRadius,this.endAngle,this.startAngle,!0),i.closePath(),i.strokeStyle=this.strokeColor,i.lineWidth=this.strokeWidth,i.fillStyle=this.fillColor,i.fill(),i.lineJoin="bevel",this.showStroke&&i.stroke()}}),e.Rectangle=e.Element.extend({draw:function(){var t=this.ctx,i=this.width/2,e=this.x-i,s=this.x+i,n=this.base-(this.base-this.y),o=this.strokeWidth/2;this.showStroke&&(e+=o,s-=o,n+=o),t.beginPath(),t.fillStyle=this.fillColor,t.strokeStyle=this.strokeColor,t.lineWidth=this.strokeWidth,t.moveTo(e,this.base),t.lineTo(e,n),t.lineTo(s,n),t.lineTo(s,this.base),t.fill(),this.showStroke&&t.stroke()},height:function(){return this.base-this.y},inRange:function(t,i){return t>=this.x-this.width/2&&t<=this.x+this.width/2&&i>=this.y&&i<=this.base}}),e.Tooltip=e.Element.extend({draw:function(){var t=this.chart.ctx;t.font=M(this.fontSize,this.fontStyle,this.fontFamily),this.xAlign="center",this.yAlign="above";var i=2,e=t.measureText(this.text).width+2*this.xPadding,s=this.fontSize+2*this.yPadding,n=s+this.caretHeight+i;this.x+e/2>this.chart.width?this.xAlign="left":this.x-e/2<0&&(this.xAlign="right"),this.y-n<0&&(this.yAlign="below");var o=this.x-e/2,a=this.y-n;switch(t.fillStyle=this.fillColor,this.yAlign){case"above":t.beginPath(),t.moveTo(this.x,this.y-i),t.lineTo(this.x+this.caretHeight,this.y-(i+this.caretHeight)),t.lineTo(this.x-this.caretHeight,this.y-(i+this.caretHeight)),t.closePath(),t.fill();break;case"below":a=this.y+i+this.caretHeight,t.beginPath(),t.moveTo(this.x,this.y+i),t.lineTo(this.x+this.caretHeight,this.y+i+this.caretHeight),t.lineTo(this.x-this.caretHeight,this.y+i+this.caretHeight),t.closePath(),t.fill()}switch(this.xAlign){case"left":o=this.x-e+(this.cornerRadius+this.caretHeight);break;case"right":o=this.x-(this.cornerRadius+this.caretHeight)}z(t,o,a,e,s,this.cornerRadius),t.fill(),t.fillStyle=this.textColor,t.textAlign="center",t.textBaseline="middle",t.fillText(this.text,o+e/2,a+s/2)}}),e.MultiTooltip=e.Element.extend({initialize:function(){this.font=M(this.fontSize,this.fontStyle,this.fontFamily),this.titleFont=M(this.titleFontSize,this.titleFontStyle,this.titleFontFamily),this.height=this.labels.length*this.fontSize+(this.labels.length-1)*(this.fontSize/2)+2*this.yPadding+1.5*this.titleFontSize,this.ctx.font=this.titleFont;var t=this.ctx.measureText(this.title).width,i=W(this.ctx,this.font,this.labels)+this.fontSize+3,e=g([i,t]);this.width=e+2*this.xPadding;var s=this.height/2;this.y-s<0?this.y=s:this.y+s>this.chart.height&&(this.y=this.chart.height-s),this.x>this.chart.width/2?this.x-=this.xOffset+this.width:this.x+=this.xOffset},getLineHeight:function(t){var i=this.y-this.height/2+this.yPadding,e=t-1;return 0===t?i+this.titleFontSize/2:i+(1.5*this.fontSize*e+this.fontSize/2)+1.5*this.titleFontSize},draw:function(){z(this.ctx,this.x,this.y-this.height/2,this.width,this.height,this.cornerRadius);var t=this.ctx;t.fillStyle=this.fillColor,t.fill(),t.closePath(),t.textAlign="left",t.textBaseline="middle",t.fillStyle=this.titleTextColor,t.font=this.titleFont,t.fillText(this.title,this.x+this.xPadding,this.getLineHeight(0)),t.font=this.font,s.each(this.labels,function(i,e){t.fillStyle=this.textColor,t.fillText(i,this.x+this.xPadding+this.fontSize+3,this.getLineHeight(e+1)),t.fillStyle=this.legendColorBackground,t.fillRect(this.x+this.xPadding,this.getLineHeight(e+1)-this.fontSize/2,this.fontSize,this.fontSize),t.fillStyle=this.legendColors[e].fill,t.fillRect(this.x+this.xPadding,this.getLineHeight(e+1)-this.fontSize/2,this.fontSize,this.fontSize)},this)}}),e.Scale=e.Element.extend({initialize:function(){this.fit()},buildYLabels:function(){this.yLabels=[];for(var t=v(this.stepValue),i=0;i<=this.steps;i++)this.yLabels.push(y(this.templateString,{value:(this.min+i*this.stepValue).toFixed(t)}));this.yLabelWidth=this.display&&this.showLabels?W(this.ctx,this.font,this.yLabels):0},addXLabel:function(t){this.xLabels.push(t),this.valuesCount++,this.fit()},removeXLabel:function(){this.xLabels.shift(),this.valuesCount--,this.fit()},fit:function(){this.startPoint=this.display?this.fontSize:0,this.endPoint=this.display?this.height-1.5*this.fontSize-5:this.height,this.startPoint+=this.padding,this.endPoint-=this.padding;var t,i=this.endPoint-this.startPoint;for(this.calculateYRange(i),this.buildYLabels(),this.calculateXLabelRotation();i>this.endPoint-this.startPoint;)i=this.endPoint-this.startPoint,t=this.yLabelWidth,this.calculateYRange(i),this.buildYLabels(),t<this.yLabelWidth&&this.calculateXLabelRotation()},calculateXLabelRotation:function(){this.ctx.font=this.font;var t,i,e=this.ctx.measureText(this.xLabels[0]).width,s=this.ctx.measureText(this.xLabels[this.xLabels.length-1]).width;if(this.xScalePaddingRight=s/2+3,this.xScalePaddingLeft=e/2>this.yLabelWidth+10?e/2:this.yLabelWidth+10,this.xLabelRotation=0,this.display){var n,o=W(this.ctx,this.font,this.xLabels);this.xLabelWidth=o;for(var a=Math.floor(this.calculateX(1)-this.calculateX(0))-6;this.xLabelWidth>a&&0===this.xLabelRotation||this.xLabelWidth>a&&this.xLabelRotation<=90&&this.xLabelRotation>0;)n=Math.cos(x(this.xLabelRotation)),t=n*e,i=n*s,t+this.fontSize/2>this.yLabelWidth+8&&(this.xScalePaddingLeft=t+this.fontSize/2),this.xScalePaddingRight=this.fontSize/2,this.xLabelRotation++,this.xLabelWidth=n*o;this.xLabelRotation>0&&(this.endPoint-=Math.sin(x(this.xLabelRotation))*o+3)}else this.xLabelWidth=0,this.xScalePaddingRight=this.padding,this.xScalePaddingLeft=this.padding},calculateYRange:c,drawingArea:function(){return this.startPoint-this.endPoint},calculateY:function(t){var i=this.drawingArea()/(this.min-this.max);return this.endPoint-i*(t-this.min)},calculateX:function(t){var i=(this.xLabelRotation>0,this.width-(this.xScalePaddingLeft+this.xScalePaddingRight)),e=i/(this.valuesCount-(this.offsetGridLines?0:1)),s=e*t+this.xScalePaddingLeft;return this.offsetGridLines&&(s+=e/2),Math.round(s)},update:function(t){s.extend(this,t),this.fit()},draw:function(){var t=this.ctx,i=(this.endPoint-this.startPoint)/this.steps,e=Math.round(this.xScalePaddingLeft);this.display&&(t.fillStyle=this.textColor,t.font=this.font,n(this.yLabels,function(n,o){var a=this.endPoint-i*o,h=Math.round(a);t.textAlign="right",t.textBaseline="middle",this.showLabels&&t.fillText(n,e-10,a),t.beginPath(),o>0?(t.lineWidth=this.gridLineWidth,t.strokeStyle=this.gridLineColor):(t.lineWidth=this.lineWidth,t.strokeStyle=this.lineColor),h+=s.aliasPixel(t.lineWidth),t.moveTo(e,h),t.lineTo(this.width,h),t.stroke(),t.closePath(),t.lineWidth=this.lineWidth,t.strokeStyle=this.lineColor,t.beginPath(),t.moveTo(e-5,h),t.lineTo(e,h),t.stroke(),t.closePath()},this),n(this.xLabels,function(i,e){var s=this.calculateX(e)+S(this.lineWidth),n=this.calculateX(e-(this.offsetGridLines?.5:0))+S(this.lineWidth),o=this.xLabelRotation>0;t.beginPath(),e>0?(t.lineWidth=this.gridLineWidth,t.strokeStyle=this.gridLineColor):(t.lineWidth=this.lineWidth,t.strokeStyle=this.lineColor),t.moveTo(n,this.endPoint),t.lineTo(n,this.startPoint-3),t.stroke(),t.closePath(),t.lineWidth=this.lineWidth,t.strokeStyle=this.lineColor,t.beginPath(),t.moveTo(n,this.endPoint),t.lineTo(n,this.endPoint+5),t.stroke(),t.closePath(),t.save(),t.translate(s,o?this.endPoint+12:this.endPoint+8),t.rotate(-1*x(this.xLabelRotation)),t.font=this.font,t.textAlign=o?"right":"center",t.textBaseline=o?"middle":"top",t.fillText(i,0,0),t.restore()},this))}}),e.RadialScale=e.Element.extend({initialize:function(){this.size=m([this.height,this.width]),this.drawingArea=this.display?this.size/2-(this.fontSize/2+this.backdropPaddingY):this.size/2},calculateCenterOffset:function(t){var i=this.drawingArea/(this.max-this.min);return(t-this.min)*i},update:function(){this.lineArc?this.drawingArea=this.display?this.size/2-(this.fontSize/2+this.backdropPaddingY):this.size/2:this.setScaleSize(),this.buildYLabels()},buildYLabels:function(){this.yLabels=[];for(var t=v(this.stepValue),i=0;i<=this.steps;i++)this.yLabels.push(y(this.templateString,{value:(this.min+i*this.stepValue).toFixed(t)}))},getCircumference:function(){return 2*Math.PI/this.valuesCount},setScaleSize:function(){var t,i,e,s,n,o,a,h,l,r,c,u,d=m([this.height/2-this.pointLabelFontSize-5,this.width/2]),p=this.width,g=0;for(this.ctx.font=M(this.pointLabelFontSize,this.pointLabelFontStyle,this.pointLabelFontFamily),i=0;i<this.valuesCount;i++)t=this.getPointPosition(i,d),e=this.ctx.measureText(y(this.templateString,{value:this.labels[i]})).width+5,0===i||i===this.valuesCount/2?(s=e/2,t.x+s>p&&(p=t.x+s,n=i),t.x-s<g&&(g=t.x-s,a=i)):i<this.valuesCount/2?t.x+e>p&&(p=t.x+e,n=i):i>this.valuesCount/2&&t.x-e<g&&(g=t.x-e,a=i);l=g,r=Math.ceil(p-this.width),o=this.getIndexAngle(n),h=this.getIndexAngle(a),c=r/Math.sin(o+Math.PI/2),u=l/Math.sin(h+Math.PI/2),c=f(c)?c:0,u=f(u)?u:0,this.drawingArea=d-(u+c)/2,this.setCenterPoint(u,c)},setCenterPoint:function(t,i){var e=this.width-i-this.drawingArea,s=t+this.drawingArea;this.xCenter=(s+e)/2,this.yCenter=this.height/2},getIndexAngle:function(t){var i=2*Math.PI/this.valuesCount;return t*i-Math.PI/2},getPointPosition:function(t,i){var e=this.getIndexAngle(t);return{x:Math.cos(e)*i+this.xCenter,y:Math.sin(e)*i+this.yCenter}},draw:function(){if(this.display){var t=this.ctx;if(n(this.yLabels,function(i,e){if(e>0){var s,n=e*(this.drawingArea/this.steps),o=this.yCenter-n;if(this.lineWidth>0)if(t.strokeStyle=this.lineColor,t.lineWidth=this.lineWidth,this.lineArc)t.beginPath(),t.arc(this.xCenter,this.yCenter,n,0,2*Math.PI),t.closePath(),t.stroke();else{t.beginPath();for(var a=0;a<this.valuesCount;a++)s=this.getPointPosition(a,this.calculateCenterOffset(this.min+e*this.stepValue)),0===a?t.moveTo(s.x,s.y):t.lineTo(s.x,s.y);t.closePath(),t.stroke()}if(this.showLabels){if(t.font=M(this.fontSize,this.fontStyle,this.fontFamily),this.showLabelBackdrop){var h=t.measureText(i).width;t.fillStyle=this.backdropColor,t.fillRect(this.xCenter-h/2-this.backdropPaddingX,o-this.fontSize/2-this.backdropPaddingY,h+2*this.backdropPaddingX,this.fontSize+2*this.backdropPaddingY)}t.textAlign="center",t.textBaseline="middle",t.fillStyle=this.fontColor,t.fillText(i,this.xCenter,o)}}},this),!this.lineArc){t.lineWidth=this.angleLineWidth,t.strokeStyle=this.angleLineColor;for(var i=this.valuesCount-1;i>=0;i--){if(this.angleLineWidth>0){var e=this.getPointPosition(i,this.calculateCenterOffset(this.max));t.beginPath(),t.moveTo(this.xCenter,this.yCenter),t.lineTo(e.x,e.y),t.stroke(),t.closePath()}var s=this.getPointPosition(i,this.calculateCenterOffset(this.max)+5);t.font=M(this.pointLabelFontSize,this.pointLabelFontStyle,this.pointLabelFontFamily),t.fillStyle=this.pointLabelFontColor;var o=this.labels.length,a=this.labels.length/2,h=a/2,l=h>i||i>o-h,r=i===h||i===o-h;t.textAlign=0===i?"center":i===a?"center":a>i?"left":"right",t.textBaseline=r?"middle":l?"bottom":"top",t.fillText(this.labels[i],s.x,s.y)}}}}}),s.addEvent(window,"resize",function(){var t;return function(){clearTimeout(t),t=setTimeout(function(){n(e.instances,function(t){t.options.responsive&&t.resize(t.render,!0)})},50)}}()),p?define(function(){return e}):"object"==typeof module&&module.exports&&(module.exports=e),t.Chart=e,e.noConflict=function(){return t.Chart=i,e}}).call(this),function(){"use strict";var t=this,i=t.Chart,e=i.helpers,s={scaleBeginAtZero:!0,scaleShowGridLines:!0,scaleGridLineColor:"rgba(0,0,0,.05)",scaleGridLineWidth:1,barShowStroke:!0,barStrokeWidth:2,barValueSpacing:5,barDatasetSpacing:1,legendTemplate:'<ul class="<%=name.toLowerCase()%>-legend"><% for (var i=0; i<datasets.length; i++){%><li><span style="background-color:<%=datasets[i].fillColor%>"></span><%if(datasets[i].label){%><%=datasets[i].label%><%}%></li><%}%></ul>'};i.Type.extend({name:"Bar",defaults:s,initialize:function(t){var s=this.options;this.ScaleClass=i.Scale.extend({offsetGridLines:!0,calculateBarX:function(t,i,e){var n=this.calculateBaseWidth(),o=this.calculateX(e)-n/2,a=this.calculateBarWidth(t);return o+a*i+i*s.barDatasetSpacing+a/2},calculateBaseWidth:function(){return this.calculateX(1)-this.calculateX(0)-2*s.barValueSpacing},calculateBarWidth:function(t){var i=this.calculateBaseWidth()-(t-1)*s.barDatasetSpacing;return i/t}}),this.datasets=[],this.options.showTooltips&&e.bindEvents(this,this.options.tooltipEvents,function(t){var i="mouseout"!==t.type?this.getBarsAtEvent(t):[];this.eachBars(function(t){t.restore(["fillColor","strokeColor"])}),e.each(i,function(t){t.fillColor=t.highlightFill,t.strokeColor=t.highlightStroke}),this.showTooltip(i)}),this.BarClass=i.Rectangle.extend({strokeWidth:this.options.barStrokeWidth,showStroke:this.options.barShowStroke,ctx:this.chart.ctx}),e.each(t.datasets,function(i){var s={label:i.label||null,fillColor:i.fillColor,strokeColor:i.strokeColor,bars:[]};this.datasets.push(s),e.each(i.data,function(n,o){e.isNumber(n)&&s.bars.push(new this.BarClass({value:n,label:t.labels[o],datasetLabel:i.label,strokeColor:i.strokeColor,fillColor:i.fillColor,highlightFill:i.highlightFill||i.fillColor,highlightStroke:i.highlightStroke||i.strokeColor}))},this)},this),this.buildScale(t.labels),this.BarClass.prototype.base=this.scale.endPoint,this.eachBars(function(t,i,s){e.extend(t,{width:this.scale.calculateBarWidth(this.datasets.length),x:this.scale.calculateBarX(this.datasets.length,s,i),y:this.scale.endPoint}),t.save()},this),this.render()},update:function(){this.scale.update(),e.each(this.activeElements,function(t){t.restore(["fillColor","strokeColor"])}),this.eachBars(function(t){t.save()}),this.render()},eachBars:function(t){e.each(this.datasets,function(i,s){e.each(i.bars,t,this,s)},this)},getBarsAtEvent:function(t){for(var i,s=[],n=e.getRelativePosition(t),o=function(t){s.push(t.bars[i])},a=0;a<this.datasets.length;a++)for(i=0;i<this.datasets[a].bars.length;i++)if(this.datasets[a].bars[i].inRange(n.x,n.y))return e.each(this.datasets,o),s;return s},buildScale:function(t){var i=this,s=function(){var t=[];return i.eachBars(function(i){t.push(i.value)}),t},n={templateString:this.options.scaleLabel,height:this.chart.height,width:this.chart.width,ctx:this.chart.ctx,textColor:this.options.scaleFontColor,fontSize:this.options.scaleFontSize,fontStyle:this.options.scaleFontStyle,fontFamily:this.options.scaleFontFamily,valuesCount:t.length,beginAtZero:this.options.scaleBeginAtZero,integersOnly:this.options.scaleIntegersOnly,calculateYRange:function(t){var i=e.calculateScaleRange(s(),t,this.fontSize,this.beginAtZero,this.integersOnly);e.extend(this,i)},xLabels:t,font:e.fontString(this.options.scaleFontSize,this.options.scaleFontStyle,this.options.scaleFontFamily),lineWidth:this.options.scaleLineWidth,lineColor:this.options.scaleLineColor,gridLineWidth:this.options.scaleShowGridLines?this.options.scaleGridLineWidth:0,gridLineColor:this.options.scaleShowGridLines?this.options.scaleGridLineColor:"rgba(0,0,0,0)",padding:this.options.showScale?0:this.options.barShowStroke?this.options.barStrokeWidth:0,showLabels:this.options.scaleShowLabels,display:this.options.showScale};this.options.scaleOverride&&e.extend(n,{calculateYRange:e.noop,steps:this.options.scaleSteps,stepValue:this.options.scaleStepWidth,min:this.options.scaleStartValue,max:this.options.scaleStartValue+this.options.scaleSteps*this.options.scaleStepWidth}),this.scale=new this.ScaleClass(n)},addData:function(t,i){e.each(t,function(t,s){e.isNumber(t)&&this.datasets[s].bars.push(new this.BarClass({value:t,label:i,x:this.scale.calculateBarX(this.datasets.length,s,this.scale.valuesCount+1),y:this.scale.endPoint,width:this.scale.calculateBarWidth(this.datasets.length),base:this.scale.endPoint,strokeColor:this.datasets[s].strokeColor,fillColor:this.datasets[s].fillColor}))},this),this.scale.addXLabel(i),this.update()},removeData:function(){this.scale.removeXLabel(),e.each(this.datasets,function(t){t.bars.shift()},this),this.update()},reflow:function(){e.extend(this.BarClass.prototype,{y:this.scale.endPoint,base:this.scale.endPoint});var t=e.extend({height:this.chart.height,width:this.chart.width});this.scale.update(t)},draw:function(t){var i=t||1;this.clear();this.chart.ctx;this.scale.draw(i),e.each(this.datasets,function(t,s){e.each(t.bars,function(t,e){t.base=this.scale.endPoint,t.transition({x:this.scale.calculateBarX(this.datasets.length,s,e),y:this.scale.calculateY(t.value),width:this.scale.calculateBarWidth(this.datasets.length)},i).draw()},this)},this)}})}.call(this),function(){"use strict";var t=this,i=t.Chart,e=i.helpers,s={segmentShowStroke:!0,segmentStrokeColor:"#fff",segmentStrokeWidth:2,percentageInnerCutout:50,animationSteps:100,animationEasing:"easeOutBounce",animateRotate:!0,animateScale:!1,legendTemplate:'<ul class="<%=name.toLowerCase()%>-legend"><% for (var i=0; i<segments.length; i++){%><li><span style="background-color:<%=segments[i].fillColor%>"></span><%if(segments[i].label){%><%=segments[i].label%><%}%></li><%}%></ul>'};i.Type.extend({name:"Doughnut",defaults:s,initialize:function(t){this.segments=[],this.outerRadius=(e.min([this.chart.width,this.chart.height])-this.options.segmentStrokeWidth/2)/2,this.SegmentArc=i.Arc.extend({ctx:this.chart.ctx,x:this.chart.width/2,y:this.chart.height/2}),this.options.showTooltips&&e.bindEvents(this,this.options.tooltipEvents,function(t){var i="mouseout"!==t.type?this.getSegmentsAtEvent(t):[];
e.each(this.segments,function(t){t.restore(["fillColor"])}),e.each(i,function(t){t.fillColor=t.highlightColor}),this.showTooltip(i)}),this.calculateTotal(t),e.each(t,function(t,i){this.addData(t,i,!0)},this),this.render()},getSegmentsAtEvent:function(t){var i=[],s=e.getRelativePosition(t);return e.each(this.segments,function(t){t.inRange(s.x,s.y)&&i.push(t)},this),i},addData:function(t,i,e){var s=i||this.segments.length;this.segments.splice(s,0,new this.SegmentArc({value:t.value,outerRadius:this.options.animateScale?0:this.outerRadius,innerRadius:this.options.animateScale?0:this.outerRadius/100*this.options.percentageInnerCutout,fillColor:t.color,highlightColor:t.highlight||t.color,showStroke:this.options.segmentShowStroke,strokeWidth:this.options.segmentStrokeWidth,strokeColor:this.options.segmentStrokeColor,startAngle:1.5*Math.PI,circumference:this.options.animateRotate?0:this.calculateCircumference(t.value),label:t.label})),e||(this.reflow(),this.update())},calculateCircumference:function(t){return 2*Math.PI*(t/this.total)},calculateTotal:function(t){this.total=0,e.each(t,function(t){this.total+=t.value},this)},update:function(){this.calculateTotal(this.segments),e.each(this.activeElements,function(t){t.restore(["fillColor"])}),e.each(this.segments,function(t){t.save()}),this.render()},removeData:function(t){var i=e.isNumber(t)?t:this.segments.length-1;this.segments.splice(i,1),this.reflow(),this.update()},reflow:function(){e.extend(this.SegmentArc.prototype,{x:this.chart.width/2,y:this.chart.height/2}),this.outerRadius=(e.min([this.chart.width,this.chart.height])-this.options.segmentStrokeWidth/2)/2,e.each(this.segments,function(t){t.update({outerRadius:this.outerRadius,innerRadius:this.outerRadius/100*this.options.percentageInnerCutout})},this)},draw:function(t){var i=t?t:1;this.clear(),e.each(this.segments,function(t,e){t.transition({circumference:this.calculateCircumference(t.value),outerRadius:this.outerRadius,innerRadius:this.outerRadius/100*this.options.percentageInnerCutout},i),t.endAngle=t.startAngle+t.circumference,t.draw(),0===e&&(t.startAngle=1.5*Math.PI),e<this.segments.length-1&&(this.segments[e+1].startAngle=t.endAngle)},this)}}),i.types.Doughnut.extend({name:"Pie",defaults:e.merge(s,{percentageInnerCutout:0})})}.call(this),function(){"use strict";var t=this,i=t.Chart,e=i.helpers,s={scaleShowGridLines:!0,scaleGridLineColor:"rgba(0,0,0,.05)",scaleGridLineWidth:1,bezierCurve:!0,bezierCurveTension:.4,pointDot:!0,pointDotRadius:4,pointDotStrokeWidth:1,pointHitDetectionRadius:20,datasetStroke:!0,datasetStrokeWidth:2,datasetFill:!0,legendTemplate:'<ul class="<%=name.toLowerCase()%>-legend"><% for (var i=0; i<datasets.length; i++){%><li><span style="background-color:<%=datasets[i].strokeColor%>"></span><%if(datasets[i].label){%><%=datasets[i].label%><%}%></li><%}%></ul>'};i.Type.extend({name:"Line",defaults:s,initialize:function(t){this.PointClass=i.Point.extend({strokeWidth:this.options.pointDotStrokeWidth,radius:this.options.pointDotRadius,display:this.options.pointDot,hitDetectionRadius:this.options.pointHitDetectionRadius,ctx:this.chart.ctx,inRange:function(t){return Math.pow(t-this.x,2)<Math.pow(this.radius+this.hitDetectionRadius,2)}}),this.datasets=[],this.options.showTooltips&&e.bindEvents(this,this.options.tooltipEvents,function(t){var i="mouseout"!==t.type?this.getPointsAtEvent(t):[];this.eachPoints(function(t){t.restore(["fillColor","strokeColor"])}),e.each(i,function(t){t.fillColor=t.highlightFill,t.strokeColor=t.highlightStroke}),this.showTooltip(i)}),e.each(t.datasets,function(i){var s={label:i.label||null,fillColor:i.fillColor,strokeColor:i.strokeColor,pointColor:i.pointColor,pointStrokeColor:i.pointStrokeColor,points:[]};this.datasets.push(s),e.each(i.data,function(n,o){e.isNumber(n)&&s.points.push(new this.PointClass({value:n,label:t.labels[o],datasetLabel:i.label,strokeColor:i.pointStrokeColor,fillColor:i.pointColor,highlightFill:i.pointHighlightFill||i.pointColor,highlightStroke:i.pointHighlightStroke||i.pointStrokeColor}))},this),this.buildScale(t.labels),this.eachPoints(function(t,i){e.extend(t,{x:this.scale.calculateX(i),y:this.scale.endPoint}),t.save()},this)},this),this.render()},update:function(){this.scale.update(),e.each(this.activeElements,function(t){t.restore(["fillColor","strokeColor"])}),this.eachPoints(function(t){t.save()}),this.render()},eachPoints:function(t){e.each(this.datasets,function(i){e.each(i.points,t,this)},this)},getPointsAtEvent:function(t){var i=[],s=e.getRelativePosition(t);return e.each(this.datasets,function(t){e.each(t.points,function(t){t.inRange(s.x,s.y)&&i.push(t)})},this),i},buildScale:function(t){var s=this,n=function(){var t=[];return s.eachPoints(function(i){t.push(i.value)}),t},o={templateString:this.options.scaleLabel,height:this.chart.height,width:this.chart.width,ctx:this.chart.ctx,textColor:this.options.scaleFontColor,fontSize:this.options.scaleFontSize,fontStyle:this.options.scaleFontStyle,fontFamily:this.options.scaleFontFamily,valuesCount:t.length,beginAtZero:this.options.scaleBeginAtZero,integersOnly:this.options.scaleIntegersOnly,calculateYRange:function(t){var i=e.calculateScaleRange(n(),t,this.fontSize,this.beginAtZero,this.integersOnly);e.extend(this,i)},xLabels:t,font:e.fontString(this.options.scaleFontSize,this.options.scaleFontStyle,this.options.scaleFontFamily),lineWidth:this.options.scaleLineWidth,lineColor:this.options.scaleLineColor,gridLineWidth:this.options.scaleShowGridLines?this.options.scaleGridLineWidth:0,gridLineColor:this.options.scaleShowGridLines?this.options.scaleGridLineColor:"rgba(0,0,0,0)",padding:this.options.showScale?0:this.options.pointDotRadius+this.options.pointDotStrokeWidth,showLabels:this.options.scaleShowLabels,display:this.options.showScale};this.options.scaleOverride&&e.extend(o,{calculateYRange:e.noop,steps:this.options.scaleSteps,stepValue:this.options.scaleStepWidth,min:this.options.scaleStartValue,max:this.options.scaleStartValue+this.options.scaleSteps*this.options.scaleStepWidth}),this.scale=new i.Scale(o)},addData:function(t,i){e.each(t,function(t,s){e.isNumber(t)&&this.datasets[s].points.push(new this.PointClass({value:t,label:i,x:this.scale.calculateX(this.scale.valuesCount+1),y:this.scale.endPoint,strokeColor:this.datasets[s].pointStrokeColor,fillColor:this.datasets[s].pointColor}))},this),this.scale.addXLabel(i),this.update()},removeData:function(){this.scale.removeXLabel(),e.each(this.datasets,function(t){t.points.shift()},this),this.update()},reflow:function(){var t=e.extend({height:this.chart.height,width:this.chart.width});this.scale.update(t)},draw:function(t){var i=t||1;this.clear();var s=this.chart.ctx;this.scale.draw(i),e.each(this.datasets,function(t){e.each(t.points,function(t,e){t.transition({y:this.scale.calculateY(t.value),x:this.scale.calculateX(e)},i)},this),this.options.bezierCurve&&e.each(t.points,function(i,s){i.controlPoints=0===s?e.splineCurve(i,i,t.points[s+1],0):s>=t.points.length-1?e.splineCurve(t.points[s-1],i,i,0):e.splineCurve(t.points[s-1],i,t.points[s+1],this.options.bezierCurveTension)},this),s.lineWidth=this.options.datasetStrokeWidth,s.strokeStyle=t.strokeColor,s.beginPath(),e.each(t.points,function(i,e){e>0?this.options.bezierCurve?s.bezierCurveTo(t.points[e-1].controlPoints.outer.x,t.points[e-1].controlPoints.outer.y,i.controlPoints.inner.x,i.controlPoints.inner.y,i.x,i.y):s.lineTo(i.x,i.y):s.moveTo(i.x,i.y)},this),s.stroke(),this.options.datasetFill&&(s.lineTo(t.points[t.points.length-1].x,this.scale.endPoint),s.lineTo(this.scale.calculateX(0),this.scale.endPoint),s.fillStyle=t.fillColor,s.closePath(),s.fill()),e.each(t.points,function(t){t.draw()})},this)}})}.call(this),function(){"use strict";var t=this,i=t.Chart,e=i.helpers,s={scaleShowLabelBackdrop:!0,scaleBackdropColor:"rgba(255,255,255,0.75)",scaleBeginAtZero:!0,scaleBackdropPaddingY:2,scaleBackdropPaddingX:2,scaleShowLine:!0,segmentShowStroke:!0,segmentStrokeColor:"#fff",segmentStrokeWidth:2,animationSteps:100,animationEasing:"easeOutBounce",animateRotate:!0,animateScale:!1,legendTemplate:'<ul class="<%=name.toLowerCase()%>-legend"><% for (var i=0; i<segments.length; i++){%><li><span style="background-color:<%=segments[i].fillColor%>"></span><%if(segments[i].label){%><%=segments[i].label%><%}%></li><%}%></ul>'};i.Type.extend({name:"PolarArea",defaults:s,initialize:function(t){this.segments=[],this.SegmentArc=i.Arc.extend({showStroke:this.options.segmentShowStroke,strokeWidth:this.options.segmentStrokeWidth,strokeColor:this.options.segmentStrokeColor,ctx:this.chart.ctx,innerRadius:0,x:this.chart.width/2,y:this.chart.height/2}),this.scale=new i.RadialScale({display:this.options.showScale,fontStyle:this.options.scaleFontStyle,fontSize:this.options.scaleFontSize,fontFamily:this.options.scaleFontFamily,fontColor:this.options.scaleFontColor,showLabels:this.options.scaleShowLabels,showLabelBackdrop:this.options.scaleShowLabelBackdrop,backdropColor:this.options.scaleBackdropColor,backdropPaddingY:this.options.scaleBackdropPaddingY,backdropPaddingX:this.options.scaleBackdropPaddingX,lineWidth:this.options.scaleShowLine?this.options.scaleLineWidth:0,lineColor:this.options.scaleLineColor,lineArc:!0,width:this.chart.width,height:this.chart.height,xCenter:this.chart.width/2,yCenter:this.chart.height/2,ctx:this.chart.ctx,templateString:this.options.scaleLabel,valuesCount:t.length}),this.updateScaleRange(t),this.scale.update(),e.each(t,function(t,i){this.addData(t,i,!0)},this),this.options.showTooltips&&e.bindEvents(this,this.options.tooltipEvents,function(t){var i="mouseout"!==t.type?this.getSegmentsAtEvent(t):[];e.each(this.segments,function(t){t.restore(["fillColor"])}),e.each(i,function(t){t.fillColor=t.highlightColor}),this.showTooltip(i)}),this.render()},getSegmentsAtEvent:function(t){var i=[],s=e.getRelativePosition(t);return e.each(this.segments,function(t){t.inRange(s.x,s.y)&&i.push(t)},this),i},addData:function(t,i,e){var s=i||this.segments.length;this.segments.splice(s,0,new this.SegmentArc({fillColor:t.color,highlightColor:t.highlight||t.color,label:t.label,value:t.value,outerRadius:this.options.animateScale?0:this.scale.calculateCenterOffset(t.value),circumference:this.options.animateRotate?0:this.scale.getCircumference(),startAngle:1.5*Math.PI})),e||(this.reflow(),this.update())},removeData:function(t){var i=e.isNumber(t)?t:this.segments.length-1;this.segments.splice(i,1),this.reflow(),this.update()},calculateTotal:function(t){this.total=0,e.each(t,function(t){this.total+=t.value},this),this.scale.valuesCount=this.segments.length},updateScaleRange:function(t){var i=[];e.each(t,function(t){i.push(t.value)});var s=this.options.scaleOverride?{steps:this.options.scaleSteps,stepValue:this.options.scaleStepWidth,min:this.options.scaleStartValue,max:this.options.scaleStartValue+this.options.scaleSteps*this.options.scaleStepWidth}:e.calculateScaleRange(i,e.min([this.chart.width,this.chart.height])/2,this.options.scaleFontSize,this.options.scaleBeginAtZero,this.options.scaleIntegersOnly);e.extend(this.scale,s,{size:e.min([this.chart.width,this.chart.height]),xCenter:this.chart.width/2,yCenter:this.chart.height/2})},update:function(){this.calculateTotal(this.segments),e.each(this.segments,function(t){t.save()}),this.render()},reflow:function(){e.extend(this.SegmentArc.prototype,{x:this.chart.width/2,y:this.chart.height/2}),this.updateScaleRange(this.segments),this.scale.update(),e.extend(this.scale,{xCenter:this.chart.width/2,yCenter:this.chart.height/2}),e.each(this.segments,function(t){t.update({outerRadius:this.scale.calculateCenterOffset(t.value)})},this)},draw:function(t){var i=t||1;this.clear(),e.each(this.segments,function(t,e){t.transition({circumference:this.scale.getCircumference(),outerRadius:this.scale.calculateCenterOffset(t.value)},i),t.endAngle=t.startAngle+t.circumference,0===e&&(t.startAngle=1.5*Math.PI),e<this.segments.length-1&&(this.segments[e+1].startAngle=t.endAngle),t.draw()},this),this.scale.draw()}})}.call(this),function(){"use strict";var t=this,i=t.Chart,e=i.helpers;i.Type.extend({name:"Radar",defaults:{scaleShowLine:!0,angleShowLineOut:!0,scaleShowLabels:!1,scaleBeginAtZero:!0,angleLineColor:"rgba(0,0,0,.1)",angleLineWidth:1,pointLabelFontFamily:"'Arial'",pointLabelFontStyle:"normal",pointLabelFontSize:10,pointLabelFontColor:"#666",pointDot:!0,pointDotRadius:3,pointDotStrokeWidth:1,pointHitDetectionRadius:20,datasetStroke:!0,datasetStrokeWidth:2,datasetFill:!0,legendTemplate:'<ul class="<%=name.toLowerCase()%>-legend"><% for (var i=0; i<datasets.length; i++){%><li><span style="background-color:<%=datasets[i].strokeColor%>"></span><%if(datasets[i].label){%><%=datasets[i].label%><%}%></li><%}%></ul>'},initialize:function(t){this.PointClass=i.Point.extend({strokeWidth:this.options.pointDotStrokeWidth,radius:this.options.pointDotRadius,display:this.options.pointDot,hitDetectionRadius:this.options.pointHitDetectionRadius,ctx:this.chart.ctx}),this.datasets=[],this.buildScale(t),this.options.showTooltips&&e.bindEvents(this,this.options.tooltipEvents,function(t){var i="mouseout"!==t.type?this.getPointsAtEvent(t):[];this.eachPoints(function(t){t.restore(["fillColor","strokeColor"])}),e.each(i,function(t){t.fillColor=t.highlightFill,t.strokeColor=t.highlightStroke}),this.showTooltip(i)}),e.each(t.datasets,function(i){var s={label:i.label||null,fillColor:i.fillColor,strokeColor:i.strokeColor,pointColor:i.pointColor,pointStrokeColor:i.pointStrokeColor,points:[]};this.datasets.push(s),e.each(i.data,function(n,o){if(e.isNumber(n)){var a;this.scale.animation||(a=this.scale.getPointPosition(o,this.scale.calculateCenterOffset(n))),s.points.push(new this.PointClass({value:n,label:t.labels[o],datasetLabel:i.label,x:this.options.animation?this.scale.xCenter:a.x,y:this.options.animation?this.scale.yCenter:a.y,strokeColor:i.pointStrokeColor,fillColor:i.pointColor,highlightFill:i.pointHighlightFill||i.pointColor,highlightStroke:i.pointHighlightStroke||i.pointStrokeColor}))}},this)},this),this.render()},eachPoints:function(t){e.each(this.datasets,function(i){e.each(i.points,t,this)},this)},getPointsAtEvent:function(t){var i=e.getRelativePosition(t),s=e.getAngleFromPoint({x:this.scale.xCenter,y:this.scale.yCenter},i),n=2*Math.PI/this.scale.valuesCount,o=Math.round((s.angle-1.5*Math.PI)/n),a=[];return(o>=this.scale.valuesCount||0>o)&&(o=0),s.distance<=this.scale.drawingArea&&e.each(this.datasets,function(t){a.push(t.points[o])}),a},buildScale:function(t){this.scale=new i.RadialScale({display:this.options.showScale,fontStyle:this.options.scaleFontStyle,fontSize:this.options.scaleFontSize,fontFamily:this.options.scaleFontFamily,fontColor:this.options.scaleFontColor,showLabels:this.options.scaleShowLabels,showLabelBackdrop:this.options.scaleShowLabelBackdrop,backdropColor:this.options.scaleBackdropColor,backdropPaddingY:this.options.scaleBackdropPaddingY,backdropPaddingX:this.options.scaleBackdropPaddingX,lineWidth:this.options.scaleShowLine?this.options.scaleLineWidth:0,lineColor:this.options.scaleLineColor,angleLineColor:this.options.angleLineColor,angleLineWidth:this.options.angleShowLineOut?this.options.angleLineWidth:0,pointLabelFontColor:this.options.pointLabelFontColor,pointLabelFontSize:this.options.pointLabelFontSize,pointLabelFontFamily:this.options.pointLabelFontFamily,pointLabelFontStyle:this.options.pointLabelFontStyle,height:this.chart.height,width:this.chart.width,xCenter:this.chart.width/2,yCenter:this.chart.height/2,ctx:this.chart.ctx,templateString:this.options.scaleLabel,labels:t.labels,valuesCount:t.datasets[0].data.length}),this.scale.setScaleSize(),this.updateScaleRange(t.datasets),this.scale.buildYLabels()},updateScaleRange:function(t){var i=function(){var i=[];return e.each(t,function(t){t.data?i=i.concat(t.data):e.each(t.points,function(t){i.push(t.value)})}),i}(),s=this.options.scaleOverride?{steps:this.options.scaleSteps,stepValue:this.options.scaleStepWidth,min:this.options.scaleStartValue,max:this.options.scaleStartValue+this.options.scaleSteps*this.options.scaleStepWidth}:e.calculateScaleRange(i,e.min([this.chart.width,this.chart.height])/2,this.options.scaleFontSize,this.options.scaleBeginAtZero,this.options.scaleIntegersOnly);e.extend(this.scale,s)},addData:function(t,i){this.scale.valuesCount++,e.each(t,function(t,s){if(e.isNumber(t)){var n=this.scale.getPointPosition(this.scale.valuesCount,this.scale.calculateCenterOffset(t));this.datasets[s].points.push(new this.PointClass({value:t,label:i,x:n.x,y:n.y,strokeColor:this.datasets[s].pointStrokeColor,fillColor:this.datasets[s].pointColor}))}},this),this.scale.labels.push(i),this.reflow(),this.update()},removeData:function(){this.scale.valuesCount--,this.scale.labels.shift(),e.each(this.datasets,function(t){t.points.shift()},this),this.reflow(),this.update()},update:function(){this.eachPoints(function(t){t.save()}),this.reflow(),this.render()},reflow:function(){e.extend(this.scale,{width:this.chart.width,height:this.chart.height,size:e.min([this.chart.width,this.chart.height]),xCenter:this.chart.width/2,yCenter:this.chart.height/2}),this.updateScaleRange(this.datasets),this.scale.setScaleSize(),this.scale.buildYLabels()},draw:function(t){var i=t||1,s=this.chart.ctx;this.clear(),this.scale.draw(),e.each(this.datasets,function(t){e.each(t.points,function(t,e){t.transition(this.scale.getPointPosition(e,this.scale.calculateCenterOffset(t.value)),i)},this),s.lineWidth=this.options.datasetStrokeWidth,s.strokeStyle=t.strokeColor,s.beginPath(),e.each(t.points,function(t,i){0===i?s.moveTo(t.x,t.y):s.lineTo(t.x,t.y)},this),s.closePath(),s.stroke(),s.fillStyle=t.fillColor,s.fill(),e.each(t.points,function(t){t.draw()})},this)}})}.call(this);
/*
 *  Bootstrap TouchSpin - v3.0.1
 *  A mobile and touch friendly input spinner component for Bootstrap 3.
 *  http://www.virtuosoft.eu/code/bootstrap-touchspin/
 *
 *  Made by István Ujj-Mészáros
 *  Under Apache License v2.0 License
 */

!function(a){"use strict";function b(a,b){return a+".touchspin_"+b}function c(c,d){return a.map(c,function(a){return b(a,d)})}var d=0;a.fn.TouchSpin=function(b){if("destroy"===b)return void this.each(function(){var b=a(this),d=b.data();a(document).off(c(["mouseup","touchend","touchcancel","mousemove","touchmove","scroll","scrollstart"],d.spinnerid).join(" "))});var e={min:0,max:100,initval:"",step:1,decimals:0,stepinterval:100,forcestepdivisibility:"round",stepintervaldelay:500,verticalbuttons:!1,verticalupclass:"glyphicon glyphicon-chevron-up",verticaldownclass:"glyphicon glyphicon-chevron-down",prefix:"",postfix:"",prefix_extraclass:"",postfix_extraclass:"",booster:!0,boostat:10,maxboostedstep:!1,mousewheel:!0,buttondown_class:"btn btn-default",buttonup_class:"btn btn-default"},f={min:"min",max:"max",initval:"init-val",step:"step",decimals:"decimals",stepinterval:"step-interval",verticalbuttons:"vertical-buttons",verticalupclass:"vertical-up-class",verticaldownclass:"vertical-down-class",forcestepdivisibility:"force-step-divisibility",stepintervaldelay:"step-interval-delay",prefix:"prefix",postfix:"postfix",prefix_extraclass:"prefix-extra-class",postfix_extraclass:"postfix-extra-class",booster:"booster",boostat:"boostat",maxboostedstep:"max-boosted-step",mousewheel:"mouse-wheel",buttondown_class:"button-down-class",buttonup_class:"button-up-class"};return this.each(function(){function g(){if(!J.data("alreadyinitialized")){if(J.data("alreadyinitialized",!0),d+=1,J.data("spinnerid",d),!J.is("input"))return void console.log("Must be an input.");j(),h(),u(),m(),p(),q(),r(),s(),D.input.css("display","block")}}function h(){""!==B.initval&&""===J.val()&&J.val(B.initval)}function i(a){l(a),u();var b=D.input.val();""!==b&&(b=Number(D.input.val()),D.input.val(b.toFixed(B.decimals)))}function j(){B=a.extend({},e,K,k(),b)}function k(){var b={};return a.each(f,function(a,c){var d="bts-"+c;J.is("[data-"+d+"]")&&(b[a]=J.data(d))}),b}function l(b){B=a.extend({},B,b)}function m(){var a=J.val(),b=J.parent();""!==a&&(a=Number(a).toFixed(B.decimals)),J.data("initvalue",a).val(a),J.addClass("form-control"),b.hasClass("input-group")?n(b):o()}function n(b){b.addClass("bootstrap-touchspin");var c,d,e=J.prev(),f=J.next(),g='<span class="input-group-addon bootstrap-touchspin-prefix">'+B.prefix+"</span>",h='<span class="input-group-addon bootstrap-touchspin-postfix">'+B.postfix+"</span>";e.hasClass("input-group-btn")?(c='<button class="'+B.buttondown_class+' bootstrap-touchspin-down" type="button">-</button>',e.append(c)):(c='<span class="input-group-btn"><button class="'+B.buttondown_class+' bootstrap-touchspin-down" type="button">-</button></span>',a(c).insertBefore(J)),f.hasClass("input-group-btn")?(d='<button class="'+B.buttonup_class+' bootstrap-touchspin-up" type="button">+</button>',f.prepend(d)):(d='<span class="input-group-btn"><button class="'+B.buttonup_class+' bootstrap-touchspin-up" type="button">+</button></span>',a(d).insertAfter(J)),a(g).insertBefore(J),a(h).insertAfter(J),C=b}function o(){var b;b=B.verticalbuttons?'<div class="input-group bootstrap-touchspin"><span class="input-group-addon bootstrap-touchspin-prefix">'+B.prefix+'</span><span class="input-group-addon bootstrap-touchspin-postfix">'+B.postfix+'</span><span class="input-group-btn-vertical"><button class="'+B.buttondown_class+' bootstrap-touchspin-up" type="button"><i class="'+B.verticalupclass+'"></i></button><button class="'+B.buttonup_class+' bootstrap-touchspin-down" type="button"><i class="'+B.verticaldownclass+'"></i></button></span></div>':'<div class="input-group bootstrap-touchspin"><span class="input-group-btn"><button class="'+B.buttondown_class+' bootstrap-touchspin-down" type="button">-</button></span><span class="input-group-addon bootstrap-touchspin-prefix">'+B.prefix+'</span><span class="input-group-addon bootstrap-touchspin-postfix">'+B.postfix+'</span><span class="input-group-btn"><button class="'+B.buttonup_class+' bootstrap-touchspin-up" type="button">+</button></span></div>',C=a(b).insertBefore(J),a(".bootstrap-touchspin-prefix",C).after(J),J.hasClass("input-sm")?C.addClass("input-group-sm"):J.hasClass("input-lg")&&C.addClass("input-group-lg")}function p(){D={down:a(".bootstrap-touchspin-down",C),up:a(".bootstrap-touchspin-up",C),input:a("input",C),prefix:a(".bootstrap-touchspin-prefix",C).addClass(B.prefix_extraclass),postfix:a(".bootstrap-touchspin-postfix",C).addClass(B.postfix_extraclass)}}function q(){""===B.prefix&&D.prefix.hide(),""===B.postfix&&D.postfix.hide()}function r(){J.on("keydown",function(a){var b=a.keyCode||a.which;38===b?("up"!==M&&(w(),z()),a.preventDefault()):40===b&&("down"!==M&&(x(),y()),a.preventDefault())}),J.on("keyup",function(a){var b=a.keyCode||a.which;38===b?A():40===b&&A()}),J.on("blur",function(){u()}),D.down.on("keydown",function(a){var b=a.keyCode||a.which;(32===b||13===b)&&("down"!==M&&(x(),y()),a.preventDefault())}),D.down.on("keyup",function(a){var b=a.keyCode||a.which;(32===b||13===b)&&A()}),D.up.on("keydown",function(a){var b=a.keyCode||a.which;(32===b||13===b)&&("up"!==M&&(w(),z()),a.preventDefault())}),D.up.on("keyup",function(a){var b=a.keyCode||a.which;(32===b||13===b)&&A()}),D.down.on("mousedown.touchspin",function(a){D.down.off("touchstart.touchspin"),J.is(":disabled")||(x(),y(),a.preventDefault(),a.stopPropagation())}),D.down.on("touchstart.touchspin",function(a){D.down.off("mousedown.touchspin"),J.is(":disabled")||(x(),y(),a.preventDefault(),a.stopPropagation())}),D.up.on("mousedown.touchspin",function(a){D.up.off("touchstart.touchspin"),J.is(":disabled")||(w(),z(),a.preventDefault(),a.stopPropagation())}),D.up.on("touchstart.touchspin",function(a){D.up.off("mousedown.touchspin"),J.is(":disabled")||(w(),z(),a.preventDefault(),a.stopPropagation())}),D.up.on("mouseout touchleave touchend touchcancel",function(a){M&&(a.stopPropagation(),A())}),D.down.on("mouseout touchleave touchend touchcancel",function(a){M&&(a.stopPropagation(),A())}),D.down.on("mousemove touchmove",function(a){M&&(a.stopPropagation(),a.preventDefault())}),D.up.on("mousemove touchmove",function(a){M&&(a.stopPropagation(),a.preventDefault())}),a(document).on(c(["mouseup","touchend","touchcancel"],d).join(" "),function(a){M&&(a.preventDefault(),A())}),a(document).on(c(["mousemove","touchmove","scroll","scrollstart"],d).join(" "),function(a){M&&(a.preventDefault(),A())}),J.on("mousewheel DOMMouseScroll",function(a){if(B.mousewheel&&J.is(":focus")){var b=a.originalEvent.wheelDelta||-a.originalEvent.deltaY||-a.originalEvent.detail;a.stopPropagation(),a.preventDefault(),0>b?x():w()}})}function s(){J.on("touchspin.uponce",function(){A(),w()}),J.on("touchspin.downonce",function(){A(),x()}),J.on("touchspin.startupspin",function(){z()}),J.on("touchspin.startdownspin",function(){y()}),J.on("touchspin.stopspin",function(){A()}),J.on("touchspin.updatesettings",function(a,b){i(b)})}function t(a){switch(B.forcestepdivisibility){case"round":return(Math.round(a/B.step)*B.step).toFixed(B.decimals);case"floor":return(Math.floor(a/B.step)*B.step).toFixed(B.decimals);case"ceil":return(Math.ceil(a/B.step)*B.step).toFixed(B.decimals);default:return a}}function u(){var a,b,c;a=J.val(),""!==a&&(B.decimals>0&&"."===a||(b=parseFloat(a),isNaN(b)&&(b=0),c=b,b.toString()!==a&&(c=b),b<B.min&&(c=B.min),b>B.max&&(c=B.max),c=t(c),Number(a).toString()!==c.toString()&&(J.val(c),J.trigger("change"))))}function v(){if(B.booster){var a=Math.pow(2,Math.floor(L/B.boostat))*B.step;return B.maxboostedstep&&a>B.maxboostedstep&&(a=B.maxboostedstep,E=Math.round(E/a)*a),Math.max(B.step,a)}return B.step}function w(){u(),E=parseFloat(D.input.val()),isNaN(E)&&(E=0);var a=E,b=v();E+=b,E>B.max&&(E=B.max,J.trigger("touchspin.on.max"),A()),D.input.val(Number(E).toFixed(B.decimals)),a!==E&&J.trigger("change")}function x(){u(),E=parseFloat(D.input.val()),isNaN(E)&&(E=0);var a=E,b=v();E-=b,E<B.min&&(E=B.min,J.trigger("touchspin.on.min"),A()),D.input.val(E.toFixed(B.decimals)),a!==E&&J.trigger("change")}function y(){A(),L=0,M="down",J.trigger("touchspin.on.startspin"),J.trigger("touchspin.on.startdownspin"),H=setTimeout(function(){F=setInterval(function(){L++,x()},B.stepinterval)},B.stepintervaldelay)}function z(){A(),L=0,M="up",J.trigger("touchspin.on.startspin"),J.trigger("touchspin.on.startupspin"),I=setTimeout(function(){G=setInterval(function(){L++,w()},B.stepinterval)},B.stepintervaldelay)}function A(){switch(clearTimeout(H),clearTimeout(I),clearInterval(F),clearInterval(G),M){case"up":J.trigger("touchspin.on.stopupspin"),J.trigger("touchspin.on.stopspin");break;case"down":J.trigger("touchspin.on.stopdownspin"),J.trigger("touchspin.on.stopspin")}L=0,M=!1}var B,C,D,E,F,G,H,I,J=a(this),K=J.data(),L=0,M=!1;g()})}}(jQuery);
//! moment.js
//! version : 2.9.0
//! authors : Tim Wood, Iskren Chernev, Moment.js contributors
//! license : MIT
//! momentjs.com
(function(a){function b(a,b,c){switch(arguments.length){case 2:return null!=a?a:b;case 3:return null!=a?a:null!=b?b:c;default:throw new Error("Implement me")}}function c(a,b){return Bb.call(a,b)}function d(){return{empty:!1,unusedTokens:[],unusedInput:[],overflow:-2,charsLeftOver:0,nullInput:!1,invalidMonth:null,invalidFormat:!1,userInvalidated:!1,iso:!1}}function e(a){vb.suppressDeprecationWarnings===!1&&"undefined"!=typeof console&&console.warn&&console.warn("Deprecation warning: "+a)}function f(a,b){var c=!0;return o(function(){return c&&(e(a),c=!1),b.apply(this,arguments)},b)}function g(a,b){sc[a]||(e(b),sc[a]=!0)}function h(a,b){return function(c){return r(a.call(this,c),b)}}function i(a,b){return function(c){return this.localeData().ordinal(a.call(this,c),b)}}function j(a,b){var c,d,e=12*(b.year()-a.year())+(b.month()-a.month()),f=a.clone().add(e,"months");return 0>b-f?(c=a.clone().add(e-1,"months"),d=(b-f)/(f-c)):(c=a.clone().add(e+1,"months"),d=(b-f)/(c-f)),-(e+d)}function k(a,b,c){var d;return null==c?b:null!=a.meridiemHour?a.meridiemHour(b,c):null!=a.isPM?(d=a.isPM(c),d&&12>b&&(b+=12),d||12!==b||(b=0),b):b}function l(){}function m(a,b){b!==!1&&H(a),p(this,a),this._d=new Date(+a._d),uc===!1&&(uc=!0,vb.updateOffset(this),uc=!1)}function n(a){var b=A(a),c=b.year||0,d=b.quarter||0,e=b.month||0,f=b.week||0,g=b.day||0,h=b.hour||0,i=b.minute||0,j=b.second||0,k=b.millisecond||0;this._milliseconds=+k+1e3*j+6e4*i+36e5*h,this._days=+g+7*f,this._months=+e+3*d+12*c,this._data={},this._locale=vb.localeData(),this._bubble()}function o(a,b){for(var d in b)c(b,d)&&(a[d]=b[d]);return c(b,"toString")&&(a.toString=b.toString),c(b,"valueOf")&&(a.valueOf=b.valueOf),a}function p(a,b){var c,d,e;if("undefined"!=typeof b._isAMomentObject&&(a._isAMomentObject=b._isAMomentObject),"undefined"!=typeof b._i&&(a._i=b._i),"undefined"!=typeof b._f&&(a._f=b._f),"undefined"!=typeof b._l&&(a._l=b._l),"undefined"!=typeof b._strict&&(a._strict=b._strict),"undefined"!=typeof b._tzm&&(a._tzm=b._tzm),"undefined"!=typeof b._isUTC&&(a._isUTC=b._isUTC),"undefined"!=typeof b._offset&&(a._offset=b._offset),"undefined"!=typeof b._pf&&(a._pf=b._pf),"undefined"!=typeof b._locale&&(a._locale=b._locale),Kb.length>0)for(c in Kb)d=Kb[c],e=b[d],"undefined"!=typeof e&&(a[d]=e);return a}function q(a){return 0>a?Math.ceil(a):Math.floor(a)}function r(a,b,c){for(var d=""+Math.abs(a),e=a>=0;d.length<b;)d="0"+d;return(e?c?"+":"":"-")+d}function s(a,b){var c={milliseconds:0,months:0};return c.months=b.month()-a.month()+12*(b.year()-a.year()),a.clone().add(c.months,"M").isAfter(b)&&--c.months,c.milliseconds=+b-+a.clone().add(c.months,"M"),c}function t(a,b){var c;return b=M(b,a),a.isBefore(b)?c=s(a,b):(c=s(b,a),c.milliseconds=-c.milliseconds,c.months=-c.months),c}function u(a,b){return function(c,d){var e,f;return null===d||isNaN(+d)||(g(b,"moment()."+b+"(period, number) is deprecated. Please use moment()."+b+"(number, period)."),f=c,c=d,d=f),c="string"==typeof c?+c:c,e=vb.duration(c,d),v(this,e,a),this}}function v(a,b,c,d){var e=b._milliseconds,f=b._days,g=b._months;d=null==d?!0:d,e&&a._d.setTime(+a._d+e*c),f&&pb(a,"Date",ob(a,"Date")+f*c),g&&nb(a,ob(a,"Month")+g*c),d&&vb.updateOffset(a,f||g)}function w(a){return"[object Array]"===Object.prototype.toString.call(a)}function x(a){return"[object Date]"===Object.prototype.toString.call(a)||a instanceof Date}function y(a,b,c){var d,e=Math.min(a.length,b.length),f=Math.abs(a.length-b.length),g=0;for(d=0;e>d;d++)(c&&a[d]!==b[d]||!c&&C(a[d])!==C(b[d]))&&g++;return g+f}function z(a){if(a){var b=a.toLowerCase().replace(/(.)s$/,"$1");a=lc[a]||mc[b]||b}return a}function A(a){var b,d,e={};for(d in a)c(a,d)&&(b=z(d),b&&(e[b]=a[d]));return e}function B(b){var c,d;if(0===b.indexOf("week"))c=7,d="day";else{if(0!==b.indexOf("month"))return;c=12,d="month"}vb[b]=function(e,f){var g,h,i=vb._locale[b],j=[];if("number"==typeof e&&(f=e,e=a),h=function(a){var b=vb().utc().set(d,a);return i.call(vb._locale,b,e||"")},null!=f)return h(f);for(g=0;c>g;g++)j.push(h(g));return j}}function C(a){var b=+a,c=0;return 0!==b&&isFinite(b)&&(c=b>=0?Math.floor(b):Math.ceil(b)),c}function D(a,b){return new Date(Date.UTC(a,b+1,0)).getUTCDate()}function E(a,b,c){return jb(vb([a,11,31+b-c]),b,c).week}function F(a){return G(a)?366:365}function G(a){return a%4===0&&a%100!==0||a%400===0}function H(a){var b;a._a&&-2===a._pf.overflow&&(b=a._a[Db]<0||a._a[Db]>11?Db:a._a[Eb]<1||a._a[Eb]>D(a._a[Cb],a._a[Db])?Eb:a._a[Fb]<0||a._a[Fb]>24||24===a._a[Fb]&&(0!==a._a[Gb]||0!==a._a[Hb]||0!==a._a[Ib])?Fb:a._a[Gb]<0||a._a[Gb]>59?Gb:a._a[Hb]<0||a._a[Hb]>59?Hb:a._a[Ib]<0||a._a[Ib]>999?Ib:-1,a._pf._overflowDayOfYear&&(Cb>b||b>Eb)&&(b=Eb),a._pf.overflow=b)}function I(b){return null==b._isValid&&(b._isValid=!isNaN(b._d.getTime())&&b._pf.overflow<0&&!b._pf.empty&&!b._pf.invalidMonth&&!b._pf.nullInput&&!b._pf.invalidFormat&&!b._pf.userInvalidated,b._strict&&(b._isValid=b._isValid&&0===b._pf.charsLeftOver&&0===b._pf.unusedTokens.length&&b._pf.bigHour===a)),b._isValid}function J(a){return a?a.toLowerCase().replace("_","-"):a}function K(a){for(var b,c,d,e,f=0;f<a.length;){for(e=J(a[f]).split("-"),b=e.length,c=J(a[f+1]),c=c?c.split("-"):null;b>0;){if(d=L(e.slice(0,b).join("-")))return d;if(c&&c.length>=b&&y(e,c,!0)>=b-1)break;b--}f++}return null}function L(a){var b=null;if(!Jb[a]&&Lb)try{b=vb.locale(),require("./locale/"+a),vb.locale(b)}catch(c){}return Jb[a]}function M(a,b){var c,d;return b._isUTC?(c=b.clone(),d=(vb.isMoment(a)||x(a)?+a:+vb(a))-+c,c._d.setTime(+c._d+d),vb.updateOffset(c,!1),c):vb(a).local()}function N(a){return a.match(/\[[\s\S]/)?a.replace(/^\[|\]$/g,""):a.replace(/\\/g,"")}function O(a){var b,c,d=a.match(Pb);for(b=0,c=d.length;c>b;b++)d[b]=rc[d[b]]?rc[d[b]]:N(d[b]);return function(e){var f="";for(b=0;c>b;b++)f+=d[b]instanceof Function?d[b].call(e,a):d[b];return f}}function P(a,b){return a.isValid()?(b=Q(b,a.localeData()),nc[b]||(nc[b]=O(b)),nc[b](a)):a.localeData().invalidDate()}function Q(a,b){function c(a){return b.longDateFormat(a)||a}var d=5;for(Qb.lastIndex=0;d>=0&&Qb.test(a);)a=a.replace(Qb,c),Qb.lastIndex=0,d-=1;return a}function R(a,b){var c,d=b._strict;switch(a){case"Q":return _b;case"DDDD":return bc;case"YYYY":case"GGGG":case"gggg":return d?cc:Tb;case"Y":case"G":case"g":return ec;case"YYYYYY":case"YYYYY":case"GGGGG":case"ggggg":return d?dc:Ub;case"S":if(d)return _b;case"SS":if(d)return ac;case"SSS":if(d)return bc;case"DDD":return Sb;case"MMM":case"MMMM":case"dd":case"ddd":case"dddd":return Wb;case"a":case"A":return b._locale._meridiemParse;case"x":return Zb;case"X":return $b;case"Z":case"ZZ":return Xb;case"T":return Yb;case"SSSS":return Vb;case"MM":case"DD":case"YY":case"GG":case"gg":case"HH":case"hh":case"mm":case"ss":case"ww":case"WW":return d?ac:Rb;case"M":case"D":case"d":case"H":case"h":case"m":case"s":case"w":case"W":case"e":case"E":return Rb;case"Do":return d?b._locale._ordinalParse:b._locale._ordinalParseLenient;default:return c=new RegExp($(Z(a.replace("\\","")),"i"))}}function S(a){a=a||"";var b=a.match(Xb)||[],c=b[b.length-1]||[],d=(c+"").match(jc)||["-",0,0],e=+(60*d[1])+C(d[2]);return"+"===d[0]?e:-e}function T(a,b,c){var d,e=c._a;switch(a){case"Q":null!=b&&(e[Db]=3*(C(b)-1));break;case"M":case"MM":null!=b&&(e[Db]=C(b)-1);break;case"MMM":case"MMMM":d=c._locale.monthsParse(b,a,c._strict),null!=d?e[Db]=d:c._pf.invalidMonth=b;break;case"D":case"DD":null!=b&&(e[Eb]=C(b));break;case"Do":null!=b&&(e[Eb]=C(parseInt(b.match(/\d{1,2}/)[0],10)));break;case"DDD":case"DDDD":null!=b&&(c._dayOfYear=C(b));break;case"YY":e[Cb]=vb.parseTwoDigitYear(b);break;case"YYYY":case"YYYYY":case"YYYYYY":e[Cb]=C(b);break;case"a":case"A":c._meridiem=b;break;case"h":case"hh":c._pf.bigHour=!0;case"H":case"HH":e[Fb]=C(b);break;case"m":case"mm":e[Gb]=C(b);break;case"s":case"ss":e[Hb]=C(b);break;case"S":case"SS":case"SSS":case"SSSS":e[Ib]=C(1e3*("0."+b));break;case"x":c._d=new Date(C(b));break;case"X":c._d=new Date(1e3*parseFloat(b));break;case"Z":case"ZZ":c._useUTC=!0,c._tzm=S(b);break;case"dd":case"ddd":case"dddd":d=c._locale.weekdaysParse(b),null!=d?(c._w=c._w||{},c._w.d=d):c._pf.invalidWeekday=b;break;case"w":case"ww":case"W":case"WW":case"d":case"e":case"E":a=a.substr(0,1);case"gggg":case"GGGG":case"GGGGG":a=a.substr(0,2),b&&(c._w=c._w||{},c._w[a]=C(b));break;case"gg":case"GG":c._w=c._w||{},c._w[a]=vb.parseTwoDigitYear(b)}}function U(a){var c,d,e,f,g,h,i;c=a._w,null!=c.GG||null!=c.W||null!=c.E?(g=1,h=4,d=b(c.GG,a._a[Cb],jb(vb(),1,4).year),e=b(c.W,1),f=b(c.E,1)):(g=a._locale._week.dow,h=a._locale._week.doy,d=b(c.gg,a._a[Cb],jb(vb(),g,h).year),e=b(c.w,1),null!=c.d?(f=c.d,g>f&&++e):f=null!=c.e?c.e+g:g),i=kb(d,e,f,h,g),a._a[Cb]=i.year,a._dayOfYear=i.dayOfYear}function V(a){var c,d,e,f,g=[];if(!a._d){for(e=X(a),a._w&&null==a._a[Eb]&&null==a._a[Db]&&U(a),a._dayOfYear&&(f=b(a._a[Cb],e[Cb]),a._dayOfYear>F(f)&&(a._pf._overflowDayOfYear=!0),d=fb(f,0,a._dayOfYear),a._a[Db]=d.getUTCMonth(),a._a[Eb]=d.getUTCDate()),c=0;3>c&&null==a._a[c];++c)a._a[c]=g[c]=e[c];for(;7>c;c++)a._a[c]=g[c]=null==a._a[c]?2===c?1:0:a._a[c];24===a._a[Fb]&&0===a._a[Gb]&&0===a._a[Hb]&&0===a._a[Ib]&&(a._nextDay=!0,a._a[Fb]=0),a._d=(a._useUTC?fb:eb).apply(null,g),null!=a._tzm&&a._d.setUTCMinutes(a._d.getUTCMinutes()-a._tzm),a._nextDay&&(a._a[Fb]=24)}}function W(a){var b;a._d||(b=A(a._i),a._a=[b.year,b.month,b.day||b.date,b.hour,b.minute,b.second,b.millisecond],V(a))}function X(a){var b=new Date;return a._useUTC?[b.getUTCFullYear(),b.getUTCMonth(),b.getUTCDate()]:[b.getFullYear(),b.getMonth(),b.getDate()]}function Y(b){if(b._f===vb.ISO_8601)return void ab(b);b._a=[],b._pf.empty=!0;var c,d,e,f,g,h=""+b._i,i=h.length,j=0;for(e=Q(b._f,b._locale).match(Pb)||[],c=0;c<e.length;c++)f=e[c],d=(h.match(R(f,b))||[])[0],d&&(g=h.substr(0,h.indexOf(d)),g.length>0&&b._pf.unusedInput.push(g),h=h.slice(h.indexOf(d)+d.length),j+=d.length),rc[f]?(d?b._pf.empty=!1:b._pf.unusedTokens.push(f),T(f,d,b)):b._strict&&!d&&b._pf.unusedTokens.push(f);b._pf.charsLeftOver=i-j,h.length>0&&b._pf.unusedInput.push(h),b._pf.bigHour===!0&&b._a[Fb]<=12&&(b._pf.bigHour=a),b._a[Fb]=k(b._locale,b._a[Fb],b._meridiem),V(b),H(b)}function Z(a){return a.replace(/\\(\[)|\\(\])|\[([^\]\[]*)\]|\\(.)/g,function(a,b,c,d,e){return b||c||d||e})}function $(a){return a.replace(/[-\/\\^$*+?.()|[\]{}]/g,"\\$&")}function _(a){var b,c,e,f,g;if(0===a._f.length)return a._pf.invalidFormat=!0,void(a._d=new Date(0/0));for(f=0;f<a._f.length;f++)g=0,b=p({},a),null!=a._useUTC&&(b._useUTC=a._useUTC),b._pf=d(),b._f=a._f[f],Y(b),I(b)&&(g+=b._pf.charsLeftOver,g+=10*b._pf.unusedTokens.length,b._pf.score=g,(null==e||e>g)&&(e=g,c=b));o(a,c||b)}function ab(a){var b,c,d=a._i,e=fc.exec(d);if(e){for(a._pf.iso=!0,b=0,c=hc.length;c>b;b++)if(hc[b][1].exec(d)){a._f=hc[b][0]+(e[6]||" ");break}for(b=0,c=ic.length;c>b;b++)if(ic[b][1].exec(d)){a._f+=ic[b][0];break}d.match(Xb)&&(a._f+="Z"),Y(a)}else a._isValid=!1}function bb(a){ab(a),a._isValid===!1&&(delete a._isValid,vb.createFromInputFallback(a))}function cb(a,b){var c,d=[];for(c=0;c<a.length;++c)d.push(b(a[c],c));return d}function db(b){var c,d=b._i;d===a?b._d=new Date:x(d)?b._d=new Date(+d):null!==(c=Mb.exec(d))?b._d=new Date(+c[1]):"string"==typeof d?bb(b):w(d)?(b._a=cb(d.slice(0),function(a){return parseInt(a,10)}),V(b)):"object"==typeof d?W(b):"number"==typeof d?b._d=new Date(d):vb.createFromInputFallback(b)}function eb(a,b,c,d,e,f,g){var h=new Date(a,b,c,d,e,f,g);return 1970>a&&h.setFullYear(a),h}function fb(a){var b=new Date(Date.UTC.apply(null,arguments));return 1970>a&&b.setUTCFullYear(a),b}function gb(a,b){if("string"==typeof a)if(isNaN(a)){if(a=b.weekdaysParse(a),"number"!=typeof a)return null}else a=parseInt(a,10);return a}function hb(a,b,c,d,e){return e.relativeTime(b||1,!!c,a,d)}function ib(a,b,c){var d=vb.duration(a).abs(),e=Ab(d.as("s")),f=Ab(d.as("m")),g=Ab(d.as("h")),h=Ab(d.as("d")),i=Ab(d.as("M")),j=Ab(d.as("y")),k=e<oc.s&&["s",e]||1===f&&["m"]||f<oc.m&&["mm",f]||1===g&&["h"]||g<oc.h&&["hh",g]||1===h&&["d"]||h<oc.d&&["dd",h]||1===i&&["M"]||i<oc.M&&["MM",i]||1===j&&["y"]||["yy",j];return k[2]=b,k[3]=+a>0,k[4]=c,hb.apply({},k)}function jb(a,b,c){var d,e=c-b,f=c-a.day();return f>e&&(f-=7),e-7>f&&(f+=7),d=vb(a).add(f,"d"),{week:Math.ceil(d.dayOfYear()/7),year:d.year()}}function kb(a,b,c,d,e){var f,g,h=fb(a,0,1).getUTCDay();return h=0===h?7:h,c=null!=c?c:e,f=e-h+(h>d?7:0)-(e>h?7:0),g=7*(b-1)+(c-e)+f+1,{year:g>0?a:a-1,dayOfYear:g>0?g:F(a-1)+g}}function lb(b){var c,d=b._i,e=b._f;return b._locale=b._locale||vb.localeData(b._l),null===d||e===a&&""===d?vb.invalid({nullInput:!0}):("string"==typeof d&&(b._i=d=b._locale.preparse(d)),vb.isMoment(d)?new m(d,!0):(e?w(e)?_(b):Y(b):db(b),c=new m(b),c._nextDay&&(c.add(1,"d"),c._nextDay=a),c))}function mb(a,b){var c,d;if(1===b.length&&w(b[0])&&(b=b[0]),!b.length)return vb();for(c=b[0],d=1;d<b.length;++d)b[d][a](c)&&(c=b[d]);return c}function nb(a,b){var c;return"string"==typeof b&&(b=a.localeData().monthsParse(b),"number"!=typeof b)?a:(c=Math.min(a.date(),D(a.year(),b)),a._d["set"+(a._isUTC?"UTC":"")+"Month"](b,c),a)}function ob(a,b){return a._d["get"+(a._isUTC?"UTC":"")+b]()}function pb(a,b,c){return"Month"===b?nb(a,c):a._d["set"+(a._isUTC?"UTC":"")+b](c)}function qb(a,b){return function(c){return null!=c?(pb(this,a,c),vb.updateOffset(this,b),this):ob(this,a)}}function rb(a){return 400*a/146097}function sb(a){return 146097*a/400}function tb(a){vb.duration.fn[a]=function(){return this._data[a]}}function ub(a){"undefined"==typeof ender&&(wb=zb.moment,zb.moment=a?f("Accessing Moment through the global scope is deprecated, and will be removed in an upcoming release.",vb):vb)}for(var vb,wb,xb,yb="2.9.0",zb="undefined"==typeof global||"undefined"!=typeof window&&window!==global.window?this:global,Ab=Math.round,Bb=Object.prototype.hasOwnProperty,Cb=0,Db=1,Eb=2,Fb=3,Gb=4,Hb=5,Ib=6,Jb={},Kb=[],Lb="undefined"!=typeof module&&module&&module.exports,Mb=/^\/?Date\((\-?\d+)/i,Nb=/(\-)?(?:(\d*)\.)?(\d+)\:(\d+)(?:\:(\d+)\.?(\d{3})?)?/,Ob=/^(-)?P(?:(?:([0-9,.]*)Y)?(?:([0-9,.]*)M)?(?:([0-9,.]*)D)?(?:T(?:([0-9,.]*)H)?(?:([0-9,.]*)M)?(?:([0-9,.]*)S)?)?|([0-9,.]*)W)$/,Pb=/(\[[^\[]*\])|(\\)?(Mo|MM?M?M?|Do|DDDo|DD?D?D?|ddd?d?|do?|w[o|w]?|W[o|W]?|Q|YYYYYY|YYYYY|YYYY|YY|gg(ggg?)?|GG(GGG?)?|e|E|a|A|hh?|HH?|mm?|ss?|S{1,4}|x|X|zz?|ZZ?|.)/g,Qb=/(\[[^\[]*\])|(\\)?(LTS|LT|LL?L?L?|l{1,4})/g,Rb=/\d\d?/,Sb=/\d{1,3}/,Tb=/\d{1,4}/,Ub=/[+\-]?\d{1,6}/,Vb=/\d+/,Wb=/[0-9]*['a-z\u00A0-\u05FF\u0700-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+|[\u0600-\u06FF\/]+(\s*?[\u0600-\u06FF]+){1,2}/i,Xb=/Z|[\+\-]\d\d:?\d\d/gi,Yb=/T/i,Zb=/[\+\-]?\d+/,$b=/[\+\-]?\d+(\.\d{1,3})?/,_b=/\d/,ac=/\d\d/,bc=/\d{3}/,cc=/\d{4}/,dc=/[+-]?\d{6}/,ec=/[+-]?\d+/,fc=/^\s*(?:[+-]\d{6}|\d{4})-(?:(\d\d-\d\d)|(W\d\d$)|(W\d\d-\d)|(\d\d\d))((T| )(\d\d(:\d\d(:\d\d(\.\d+)?)?)?)?([\+\-]\d\d(?::?\d\d)?|\s*Z)?)?$/,gc="YYYY-MM-DDTHH:mm:ssZ",hc=[["YYYYYY-MM-DD",/[+-]\d{6}-\d{2}-\d{2}/],["YYYY-MM-DD",/\d{4}-\d{2}-\d{2}/],["GGGG-[W]WW-E",/\d{4}-W\d{2}-\d/],["GGGG-[W]WW",/\d{4}-W\d{2}/],["YYYY-DDD",/\d{4}-\d{3}/]],ic=[["HH:mm:ss.SSSS",/(T| )\d\d:\d\d:\d\d\.\d+/],["HH:mm:ss",/(T| )\d\d:\d\d:\d\d/],["HH:mm",/(T| )\d\d:\d\d/],["HH",/(T| )\d\d/]],jc=/([\+\-]|\d\d)/gi,kc=("Date|Hours|Minutes|Seconds|Milliseconds".split("|"),{Milliseconds:1,Seconds:1e3,Minutes:6e4,Hours:36e5,Days:864e5,Months:2592e6,Years:31536e6}),lc={ms:"millisecond",s:"second",m:"minute",h:"hour",d:"day",D:"date",w:"week",W:"isoWeek",M:"month",Q:"quarter",y:"year",DDD:"dayOfYear",e:"weekday",E:"isoWeekday",gg:"weekYear",GG:"isoWeekYear"},mc={dayofyear:"dayOfYear",isoweekday:"isoWeekday",isoweek:"isoWeek",weekyear:"weekYear",isoweekyear:"isoWeekYear"},nc={},oc={s:45,m:45,h:22,d:26,M:11},pc="DDD w W M D d".split(" "),qc="M D H h m s w W".split(" "),rc={M:function(){return this.month()+1},MMM:function(a){return this.localeData().monthsShort(this,a)},MMMM:function(a){return this.localeData().months(this,a)},D:function(){return this.date()},DDD:function(){return this.dayOfYear()},d:function(){return this.day()},dd:function(a){return this.localeData().weekdaysMin(this,a)},ddd:function(a){return this.localeData().weekdaysShort(this,a)},dddd:function(a){return this.localeData().weekdays(this,a)},w:function(){return this.week()},W:function(){return this.isoWeek()},YY:function(){return r(this.year()%100,2)},YYYY:function(){return r(this.year(),4)},YYYYY:function(){return r(this.year(),5)},YYYYYY:function(){var a=this.year(),b=a>=0?"+":"-";return b+r(Math.abs(a),6)},gg:function(){return r(this.weekYear()%100,2)},gggg:function(){return r(this.weekYear(),4)},ggggg:function(){return r(this.weekYear(),5)},GG:function(){return r(this.isoWeekYear()%100,2)},GGGG:function(){return r(this.isoWeekYear(),4)},GGGGG:function(){return r(this.isoWeekYear(),5)},e:function(){return this.weekday()},E:function(){return this.isoWeekday()},a:function(){return this.localeData().meridiem(this.hours(),this.minutes(),!0)},A:function(){return this.localeData().meridiem(this.hours(),this.minutes(),!1)},H:function(){return this.hours()},h:function(){return this.hours()%12||12},m:function(){return this.minutes()},s:function(){return this.seconds()},S:function(){return C(this.milliseconds()/100)},SS:function(){return r(C(this.milliseconds()/10),2)},SSS:function(){return r(this.milliseconds(),3)},SSSS:function(){return r(this.milliseconds(),3)},Z:function(){var a=this.utcOffset(),b="+";return 0>a&&(a=-a,b="-"),b+r(C(a/60),2)+":"+r(C(a)%60,2)},ZZ:function(){var a=this.utcOffset(),b="+";return 0>a&&(a=-a,b="-"),b+r(C(a/60),2)+r(C(a)%60,2)},z:function(){return this.zoneAbbr()},zz:function(){return this.zoneName()},x:function(){return this.valueOf()},X:function(){return this.unix()},Q:function(){return this.quarter()}},sc={},tc=["months","monthsShort","weekdays","weekdaysShort","weekdaysMin"],uc=!1;pc.length;)xb=pc.pop(),rc[xb+"o"]=i(rc[xb],xb);for(;qc.length;)xb=qc.pop(),rc[xb+xb]=h(rc[xb],2);rc.DDDD=h(rc.DDD,3),o(l.prototype,{set:function(a){var b,c;for(c in a)b=a[c],"function"==typeof b?this[c]=b:this["_"+c]=b;this._ordinalParseLenient=new RegExp(this._ordinalParse.source+"|"+/\d{1,2}/.source)},_months:"January_February_March_April_May_June_July_August_September_October_November_December".split("_"),months:function(a){return this._months[a.month()]},_monthsShort:"Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec".split("_"),monthsShort:function(a){return this._monthsShort[a.month()]},monthsParse:function(a,b,c){var d,e,f;for(this._monthsParse||(this._monthsParse=[],this._longMonthsParse=[],this._shortMonthsParse=[]),d=0;12>d;d++){if(e=vb.utc([2e3,d]),c&&!this._longMonthsParse[d]&&(this._longMonthsParse[d]=new RegExp("^"+this.months(e,"").replace(".","")+"$","i"),this._shortMonthsParse[d]=new RegExp("^"+this.monthsShort(e,"").replace(".","")+"$","i")),c||this._monthsParse[d]||(f="^"+this.months(e,"")+"|^"+this.monthsShort(e,""),this._monthsParse[d]=new RegExp(f.replace(".",""),"i")),c&&"MMMM"===b&&this._longMonthsParse[d].test(a))return d;if(c&&"MMM"===b&&this._shortMonthsParse[d].test(a))return d;if(!c&&this._monthsParse[d].test(a))return d}},_weekdays:"Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"),weekdays:function(a){return this._weekdays[a.day()]},_weekdaysShort:"Sun_Mon_Tue_Wed_Thu_Fri_Sat".split("_"),weekdaysShort:function(a){return this._weekdaysShort[a.day()]},_weekdaysMin:"Su_Mo_Tu_We_Th_Fr_Sa".split("_"),weekdaysMin:function(a){return this._weekdaysMin[a.day()]},weekdaysParse:function(a){var b,c,d;for(this._weekdaysParse||(this._weekdaysParse=[]),b=0;7>b;b++)if(this._weekdaysParse[b]||(c=vb([2e3,1]).day(b),d="^"+this.weekdays(c,"")+"|^"+this.weekdaysShort(c,"")+"|^"+this.weekdaysMin(c,""),this._weekdaysParse[b]=new RegExp(d.replace(".",""),"i")),this._weekdaysParse[b].test(a))return b},_longDateFormat:{LTS:"h:mm:ss A",LT:"h:mm A",L:"MM/DD/YYYY",LL:"MMMM D, YYYY",LLL:"MMMM D, YYYY LT",LLLL:"dddd, MMMM D, YYYY LT"},longDateFormat:function(a){var b=this._longDateFormat[a];return!b&&this._longDateFormat[a.toUpperCase()]&&(b=this._longDateFormat[a.toUpperCase()].replace(/MMMM|MM|DD|dddd/g,function(a){return a.slice(1)}),this._longDateFormat[a]=b),b},isPM:function(a){return"p"===(a+"").toLowerCase().charAt(0)},_meridiemParse:/[ap]\.?m?\.?/i,meridiem:function(a,b,c){return a>11?c?"pm":"PM":c?"am":"AM"},_calendar:{sameDay:"[Today at] LT",nextDay:"[Tomorrow at] LT",nextWeek:"dddd [at] LT",lastDay:"[Yesterday at] LT",lastWeek:"[Last] dddd [at] LT",sameElse:"L"},calendar:function(a,b,c){var d=this._calendar[a];return"function"==typeof d?d.apply(b,[c]):d},_relativeTime:{future:"in %s",past:"%s ago",s:"a few seconds",m:"a minute",mm:"%d minutes",h:"an hour",hh:"%d hours",d:"a day",dd:"%d days",M:"a month",MM:"%d months",y:"a year",yy:"%d years"},relativeTime:function(a,b,c,d){var e=this._relativeTime[c];return"function"==typeof e?e(a,b,c,d):e.replace(/%d/i,a)},pastFuture:function(a,b){var c=this._relativeTime[a>0?"future":"past"];return"function"==typeof c?c(b):c.replace(/%s/i,b)},ordinal:function(a){return this._ordinal.replace("%d",a)},_ordinal:"%d",_ordinalParse:/\d{1,2}/,preparse:function(a){return a},postformat:function(a){return a},week:function(a){return jb(a,this._week.dow,this._week.doy).week},_week:{dow:0,doy:6},firstDayOfWeek:function(){return this._week.dow},firstDayOfYear:function(){return this._week.doy},_invalidDate:"Invalid date",invalidDate:function(){return this._invalidDate}}),vb=function(b,c,e,f){var g;return"boolean"==typeof e&&(f=e,e=a),g={},g._isAMomentObject=!0,g._i=b,g._f=c,g._l=e,g._strict=f,g._isUTC=!1,g._pf=d(),lb(g)},vb.suppressDeprecationWarnings=!1,vb.createFromInputFallback=f("moment construction falls back to js Date. This is discouraged and will be removed in upcoming major release. Please refer to https://github.com/moment/moment/issues/1407 for more info.",function(a){a._d=new Date(a._i+(a._useUTC?" UTC":""))}),vb.min=function(){var a=[].slice.call(arguments,0);return mb("isBefore",a)},vb.max=function(){var a=[].slice.call(arguments,0);return mb("isAfter",a)},vb.utc=function(b,c,e,f){var g;return"boolean"==typeof e&&(f=e,e=a),g={},g._isAMomentObject=!0,g._useUTC=!0,g._isUTC=!0,g._l=e,g._i=b,g._f=c,g._strict=f,g._pf=d(),lb(g).utc()},vb.unix=function(a){return vb(1e3*a)},vb.duration=function(a,b){var d,e,f,g,h=a,i=null;return vb.isDuration(a)?h={ms:a._milliseconds,d:a._days,M:a._months}:"number"==typeof a?(h={},b?h[b]=a:h.milliseconds=a):(i=Nb.exec(a))?(d="-"===i[1]?-1:1,h={y:0,d:C(i[Eb])*d,h:C(i[Fb])*d,m:C(i[Gb])*d,s:C(i[Hb])*d,ms:C(i[Ib])*d}):(i=Ob.exec(a))?(d="-"===i[1]?-1:1,f=function(a){var b=a&&parseFloat(a.replace(",","."));return(isNaN(b)?0:b)*d},h={y:f(i[2]),M:f(i[3]),d:f(i[4]),h:f(i[5]),m:f(i[6]),s:f(i[7]),w:f(i[8])}):null==h?h={}:"object"==typeof h&&("from"in h||"to"in h)&&(g=t(vb(h.from),vb(h.to)),h={},h.ms=g.milliseconds,h.M=g.months),e=new n(h),vb.isDuration(a)&&c(a,"_locale")&&(e._locale=a._locale),e},vb.version=yb,vb.defaultFormat=gc,vb.ISO_8601=function(){},vb.momentProperties=Kb,vb.updateOffset=function(){},vb.relativeTimeThreshold=function(b,c){return oc[b]===a?!1:c===a?oc[b]:(oc[b]=c,!0)},vb.lang=f("moment.lang is deprecated. Use moment.locale instead.",function(a,b){return vb.locale(a,b)}),vb.locale=function(a,b){var c;return a&&(c="undefined"!=typeof b?vb.defineLocale(a,b):vb.localeData(a),c&&(vb.duration._locale=vb._locale=c)),vb._locale._abbr},vb.defineLocale=function(a,b){return null!==b?(b.abbr=a,Jb[a]||(Jb[a]=new l),Jb[a].set(b),vb.locale(a),Jb[a]):(delete Jb[a],null)},vb.langData=f("moment.langData is deprecated. Use moment.localeData instead.",function(a){return vb.localeData(a)}),vb.localeData=function(a){var b;if(a&&a._locale&&a._locale._abbr&&(a=a._locale._abbr),!a)return vb._locale;if(!w(a)){if(b=L(a))return b;a=[a]}return K(a)},vb.isMoment=function(a){return a instanceof m||null!=a&&c(a,"_isAMomentObject")},vb.isDuration=function(a){return a instanceof n};for(xb=tc.length-1;xb>=0;--xb)B(tc[xb]);vb.normalizeUnits=function(a){return z(a)},vb.invalid=function(a){var b=vb.utc(0/0);return null!=a?o(b._pf,a):b._pf.userInvalidated=!0,b},vb.parseZone=function(){return vb.apply(null,arguments).parseZone()},vb.parseTwoDigitYear=function(a){return C(a)+(C(a)>68?1900:2e3)},vb.isDate=x,o(vb.fn=m.prototype,{clone:function(){return vb(this)},valueOf:function(){return+this._d-6e4*(this._offset||0)},unix:function(){return Math.floor(+this/1e3)},toString:function(){return this.clone().locale("en").format("ddd MMM DD YYYY HH:mm:ss [GMT]ZZ")},toDate:function(){return this._offset?new Date(+this):this._d},toISOString:function(){var a=vb(this).utc();return 0<a.year()&&a.year()<=9999?"function"==typeof Date.prototype.toISOString?this.toDate().toISOString():P(a,"YYYY-MM-DD[T]HH:mm:ss.SSS[Z]"):P(a,"YYYYYY-MM-DD[T]HH:mm:ss.SSS[Z]")},toArray:function(){var a=this;return[a.year(),a.month(),a.date(),a.hours(),a.minutes(),a.seconds(),a.milliseconds()]},isValid:function(){return I(this)},isDSTShifted:function(){return this._a?this.isValid()&&y(this._a,(this._isUTC?vb.utc(this._a):vb(this._a)).toArray())>0:!1},parsingFlags:function(){return o({},this._pf)},invalidAt:function(){return this._pf.overflow},utc:function(a){return this.utcOffset(0,a)},local:function(a){return this._isUTC&&(this.utcOffset(0,a),this._isUTC=!1,a&&this.subtract(this._dateUtcOffset(),"m")),this},format:function(a){var b=P(this,a||vb.defaultFormat);return this.localeData().postformat(b)},add:u(1,"add"),subtract:u(-1,"subtract"),diff:function(a,b,c){var d,e,f=M(a,this),g=6e4*(f.utcOffset()-this.utcOffset());return b=z(b),"year"===b||"month"===b||"quarter"===b?(e=j(this,f),"quarter"===b?e/=3:"year"===b&&(e/=12)):(d=this-f,e="second"===b?d/1e3:"minute"===b?d/6e4:"hour"===b?d/36e5:"day"===b?(d-g)/864e5:"week"===b?(d-g)/6048e5:d),c?e:q(e)},from:function(a,b){return vb.duration({to:this,from:a}).locale(this.locale()).humanize(!b)},fromNow:function(a){return this.from(vb(),a)},calendar:function(a){var b=a||vb(),c=M(b,this).startOf("day"),d=this.diff(c,"days",!0),e=-6>d?"sameElse":-1>d?"lastWeek":0>d?"lastDay":1>d?"sameDay":2>d?"nextDay":7>d?"nextWeek":"sameElse";return this.format(this.localeData().calendar(e,this,vb(b)))},isLeapYear:function(){return G(this.year())},isDST:function(){return this.utcOffset()>this.clone().month(0).utcOffset()||this.utcOffset()>this.clone().month(5).utcOffset()},day:function(a){var b=this._isUTC?this._d.getUTCDay():this._d.getDay();return null!=a?(a=gb(a,this.localeData()),this.add(a-b,"d")):b},month:qb("Month",!0),startOf:function(a){switch(a=z(a)){case"year":this.month(0);case"quarter":case"month":this.date(1);case"week":case"isoWeek":case"day":this.hours(0);case"hour":this.minutes(0);case"minute":this.seconds(0);case"second":this.milliseconds(0)}return"week"===a?this.weekday(0):"isoWeek"===a&&this.isoWeekday(1),"quarter"===a&&this.month(3*Math.floor(this.month()/3)),this},endOf:function(b){return b=z(b),b===a||"millisecond"===b?this:this.startOf(b).add(1,"isoWeek"===b?"week":b).subtract(1,"ms")},isAfter:function(a,b){var c;return b=z("undefined"!=typeof b?b:"millisecond"),"millisecond"===b?(a=vb.isMoment(a)?a:vb(a),+this>+a):(c=vb.isMoment(a)?+a:+vb(a),c<+this.clone().startOf(b))},isBefore:function(a,b){var c;return b=z("undefined"!=typeof b?b:"millisecond"),"millisecond"===b?(a=vb.isMoment(a)?a:vb(a),+a>+this):(c=vb.isMoment(a)?+a:+vb(a),+this.clone().endOf(b)<c)},isBetween:function(a,b,c){return this.isAfter(a,c)&&this.isBefore(b,c)},isSame:function(a,b){var c;return b=z(b||"millisecond"),"millisecond"===b?(a=vb.isMoment(a)?a:vb(a),+this===+a):(c=+vb(a),+this.clone().startOf(b)<=c&&c<=+this.clone().endOf(b))},min:f("moment().min is deprecated, use moment.min instead. https://github.com/moment/moment/issues/1548",function(a){return a=vb.apply(null,arguments),this>a?this:a}),max:f("moment().max is deprecated, use moment.max instead. https://github.com/moment/moment/issues/1548",function(a){return a=vb.apply(null,arguments),a>this?this:a}),zone:f("moment().zone is deprecated, use moment().utcOffset instead. https://github.com/moment/moment/issues/1779",function(a,b){return null!=a?("string"!=typeof a&&(a=-a),this.utcOffset(a,b),this):-this.utcOffset()}),utcOffset:function(a,b){var c,d=this._offset||0;return null!=a?("string"==typeof a&&(a=S(a)),Math.abs(a)<16&&(a=60*a),!this._isUTC&&b&&(c=this._dateUtcOffset()),this._offset=a,this._isUTC=!0,null!=c&&this.add(c,"m"),d!==a&&(!b||this._changeInProgress?v(this,vb.duration(a-d,"m"),1,!1):this._changeInProgress||(this._changeInProgress=!0,vb.updateOffset(this,!0),this._changeInProgress=null)),this):this._isUTC?d:this._dateUtcOffset()},isLocal:function(){return!this._isUTC},isUtcOffset:function(){return this._isUTC},isUtc:function(){return this._isUTC&&0===this._offset},zoneAbbr:function(){return this._isUTC?"UTC":""},zoneName:function(){return this._isUTC?"Coordinated Universal Time":""},parseZone:function(){return this._tzm?this.utcOffset(this._tzm):"string"==typeof this._i&&this.utcOffset(S(this._i)),this},hasAlignedHourOffset:function(a){return a=a?vb(a).utcOffset():0,(this.utcOffset()-a)%60===0},daysInMonth:function(){return D(this.year(),this.month())},dayOfYear:function(a){var b=Ab((vb(this).startOf("day")-vb(this).startOf("year"))/864e5)+1;return null==a?b:this.add(a-b,"d")},quarter:function(a){return null==a?Math.ceil((this.month()+1)/3):this.month(3*(a-1)+this.month()%3)},weekYear:function(a){var b=jb(this,this.localeData()._week.dow,this.localeData()._week.doy).year;return null==a?b:this.add(a-b,"y")},isoWeekYear:function(a){var b=jb(this,1,4).year;return null==a?b:this.add(a-b,"y")},week:function(a){var b=this.localeData().week(this);return null==a?b:this.add(7*(a-b),"d")},isoWeek:function(a){var b=jb(this,1,4).week;return null==a?b:this.add(7*(a-b),"d")},weekday:function(a){var b=(this.day()+7-this.localeData()._week.dow)%7;return null==a?b:this.add(a-b,"d")},isoWeekday:function(a){return null==a?this.day()||7:this.day(this.day()%7?a:a-7)},isoWeeksInYear:function(){return E(this.year(),1,4)},weeksInYear:function(){var a=this.localeData()._week;return E(this.year(),a.dow,a.doy)},get:function(a){return a=z(a),this[a]()},set:function(a,b){var c;if("object"==typeof a)for(c in a)this.set(c,a[c]);else a=z(a),"function"==typeof this[a]&&this[a](b);return this},locale:function(b){var c;return b===a?this._locale._abbr:(c=vb.localeData(b),null!=c&&(this._locale=c),this)},lang:f("moment().lang() is deprecated. Instead, use moment().localeData() to get the language configuration. Use moment().locale() to change languages.",function(b){return b===a?this.localeData():this.locale(b)}),localeData:function(){return this._locale},_dateUtcOffset:function(){return 15*-Math.round(this._d.getTimezoneOffset()/15)}}),vb.fn.millisecond=vb.fn.milliseconds=qb("Milliseconds",!1),vb.fn.second=vb.fn.seconds=qb("Seconds",!1),vb.fn.minute=vb.fn.minutes=qb("Minutes",!1),vb.fn.hour=vb.fn.hours=qb("Hours",!0),vb.fn.date=qb("Date",!0),vb.fn.dates=f("dates accessor is deprecated. Use date instead.",qb("Date",!0)),vb.fn.year=qb("FullYear",!0),vb.fn.years=f("years accessor is deprecated. Use year instead.",qb("FullYear",!0)),vb.fn.days=vb.fn.day,vb.fn.months=vb.fn.month,vb.fn.weeks=vb.fn.week,vb.fn.isoWeeks=vb.fn.isoWeek,vb.fn.quarters=vb.fn.quarter,vb.fn.toJSON=vb.fn.toISOString,vb.fn.isUTC=vb.fn.isUtc,o(vb.duration.fn=n.prototype,{_bubble:function(){var a,b,c,d=this._milliseconds,e=this._days,f=this._months,g=this._data,h=0;g.milliseconds=d%1e3,a=q(d/1e3),g.seconds=a%60,b=q(a/60),g.minutes=b%60,c=q(b/60),g.hours=c%24,e+=q(c/24),h=q(rb(e)),e-=q(sb(h)),f+=q(e/30),e%=30,h+=q(f/12),f%=12,g.days=e,g.months=f,g.years=h},abs:function(){return this._milliseconds=Math.abs(this._milliseconds),this._days=Math.abs(this._days),this._months=Math.abs(this._months),this._data.milliseconds=Math.abs(this._data.milliseconds),this._data.seconds=Math.abs(this._data.seconds),this._data.minutes=Math.abs(this._data.minutes),this._data.hours=Math.abs(this._data.hours),this._data.months=Math.abs(this._data.months),this._data.years=Math.abs(this._data.years),this},weeks:function(){return q(this.days()/7)},valueOf:function(){return this._milliseconds+864e5*this._days+this._months%12*2592e6+31536e6*C(this._months/12)
},humanize:function(a){var b=ib(this,!a,this.localeData());return a&&(b=this.localeData().pastFuture(+this,b)),this.localeData().postformat(b)},add:function(a,b){var c=vb.duration(a,b);return this._milliseconds+=c._milliseconds,this._days+=c._days,this._months+=c._months,this._bubble(),this},subtract:function(a,b){var c=vb.duration(a,b);return this._milliseconds-=c._milliseconds,this._days-=c._days,this._months-=c._months,this._bubble(),this},get:function(a){return a=z(a),this[a.toLowerCase()+"s"]()},as:function(a){var b,c;if(a=z(a),"month"===a||"year"===a)return b=this._days+this._milliseconds/864e5,c=this._months+12*rb(b),"month"===a?c:c/12;switch(b=this._days+Math.round(sb(this._months/12)),a){case"week":return b/7+this._milliseconds/6048e5;case"day":return b+this._milliseconds/864e5;case"hour":return 24*b+this._milliseconds/36e5;case"minute":return 24*b*60+this._milliseconds/6e4;case"second":return 24*b*60*60+this._milliseconds/1e3;case"millisecond":return Math.floor(24*b*60*60*1e3)+this._milliseconds;default:throw new Error("Unknown unit "+a)}},lang:vb.fn.lang,locale:vb.fn.locale,toIsoString:f("toIsoString() is deprecated. Please use toISOString() instead (notice the capitals)",function(){return this.toISOString()}),toISOString:function(){var a=Math.abs(this.years()),b=Math.abs(this.months()),c=Math.abs(this.days()),d=Math.abs(this.hours()),e=Math.abs(this.minutes()),f=Math.abs(this.seconds()+this.milliseconds()/1e3);return this.asSeconds()?(this.asSeconds()<0?"-":"")+"P"+(a?a+"Y":"")+(b?b+"M":"")+(c?c+"D":"")+(d||e||f?"T":"")+(d?d+"H":"")+(e?e+"M":"")+(f?f+"S":""):"P0D"},localeData:function(){return this._locale},toJSON:function(){return this.toISOString()}}),vb.duration.fn.toString=vb.duration.fn.toISOString;for(xb in kc)c(kc,xb)&&tb(xb.toLowerCase());vb.duration.fn.asMilliseconds=function(){return this.as("ms")},vb.duration.fn.asSeconds=function(){return this.as("s")},vb.duration.fn.asMinutes=function(){return this.as("m")},vb.duration.fn.asHours=function(){return this.as("h")},vb.duration.fn.asDays=function(){return this.as("d")},vb.duration.fn.asWeeks=function(){return this.as("weeks")},vb.duration.fn.asMonths=function(){return this.as("M")},vb.duration.fn.asYears=function(){return this.as("y")},vb.locale("en",{ordinalParse:/\d{1,2}(th|st|nd|rd)/,ordinal:function(a){var b=a%10,c=1===C(a%100/10)?"th":1===b?"st":2===b?"nd":3===b?"rd":"th";return a+c}}),Lb?module.exports=vb:"function"==typeof define&&define.amd?(define(function(a,b,c){return c.config&&c.config()&&c.config().noGlobal===!0&&(zb.moment=wb),vb}),ub(!0)):ub()}).call(this);
/*! offline-js 0.7.13 */

(function(){var a,b,c,d,e,f,g;d=function(a,b){var c,d,e,f;e=[];for(d in b.prototype)try{f=b.prototype[d],null==a[d]&&"function"!=typeof f?e.push(a[d]=f):e.push(void 0)}catch(g){c=g}return e},a={},null==a.options&&(a.options={}),c={checks:{xhr:{url:function(){return"/favicon.ico?_="+Math.floor(1e9*Math.random())},timeout:5e3},image:{url:function(){return"/favicon.ico?_="+Math.floor(1e9*Math.random())}},active:"xhr"},checkOnLoad:!1,interceptRequests:!0,reconnect:!0},e=function(a,b){var c,d,e,f,g,h;for(c=a,h=b.split("."),d=e=0,f=h.length;f>e&&(g=h[d],c=c[g],"object"==typeof c);d=++e);return d===h.length-1?c:void 0},a.getOption=function(b){var d,f;return f=null!=(d=e(a.options,b))?d:e(c,b),"function"==typeof f?f():f},"function"==typeof window.addEventListener&&window.addEventListener("online",function(){return setTimeout(a.confirmUp,100)},!1),"function"==typeof window.addEventListener&&window.addEventListener("offline",function(){return a.confirmDown()},!1),a.state="up",a.markUp=function(){return a.trigger("confirmed-up"),"up"!==a.state?(a.state="up",a.trigger("up")):void 0},a.markDown=function(){return a.trigger("confirmed-down"),"down"!==a.state?(a.state="down",a.trigger("down")):void 0},f={},a.on=function(b,c,d){var e,g,h,i,j;if(g=b.split(" "),g.length>1){for(j=[],h=0,i=g.length;i>h;h++)e=g[h],j.push(a.on(e,c,d));return j}return null==f[b]&&(f[b]=[]),f[b].push([d,c])},a.off=function(a,b){var c,d,e,g,h;if(null!=f[a]){if(b){for(e=0,h=[];e<f[a].length;)g=f[a][e],d=g[0],c=g[1],c===b?h.push(f[a].splice(e,1)):h.push(e++);return h}return f[a]=[]}},a.trigger=function(a){var b,c,d,e,g,h,i;if(null!=f[a]){for(g=f[a],i=[],d=0,e=g.length;e>d;d++)h=g[d],b=h[0],c=h[1],i.push(c.call(b));return i}},b=function(a,b,c){var d,e,f,g,h;return h=function(){return a.status&&a.status<12e3?b():c()},null===a.onprogress?(d=a.onerror,a.onerror=function(){return c(),"function"==typeof d?d.apply(null,arguments):void 0},g=a.ontimeout,a.ontimeout=function(){return c(),"function"==typeof g?g.apply(null,arguments):void 0},e=a.onload,a.onload=function(){return h(),"function"==typeof e?e.apply(null,arguments):void 0}):(f=a.onreadystatechange,a.onreadystatechange=function(){return 4===a.readyState?h():0===a.readyState&&c(),"function"==typeof f?f.apply(null,arguments):void 0})},a.checks={},a.checks.xhr=function(){var c,d;d=new XMLHttpRequest,d.offline=!1,d.open("HEAD",a.getOption("checks.xhr.url"),!0),null!=d.timeout&&(d.timeout=a.getOption("checks.xhr.timeout")),b(d,a.markUp,a.markDown);try{d.send()}catch(e){c=e,a.markDown()}return d},a.checks.image=function(){var b;return b=document.createElement("img"),b.onerror=a.markDown,b.onload=a.markUp,void(b.src=a.getOption("checks.image.url"))},a.checks.down=a.markDown,a.checks.up=a.markUp,a.check=function(){return a.trigger("checking"),a.checks[a.getOption("checks.active")]()},a.confirmUp=a.confirmDown=a.check,a.onXHR=function(a){var b,c,e;return e=function(b,c){var d;return d=b.open,b.open=function(e,f,g,h,i){return a({type:e,url:f,async:g,flags:c,user:h,password:i,xhr:b}),d.apply(b,arguments)}},c=window.XMLHttpRequest,window.XMLHttpRequest=function(a){var b,d,f;return f=new c(a),e(f,a),d=f.setRequestHeader,f.headers={},f.setRequestHeader=function(a,b){return f.headers[a]=b,d.call(f,a,b)},b=f.overrideMimeType,f.overrideMimeType=function(a){return f.mimeType=a,b.call(f,a)},f},d(window.XMLHttpRequest,c),null!=window.XDomainRequest?(b=window.XDomainRequest,window.XDomainRequest=function(){var a;return a=new b,e(a),a},d(window.XDomainRequest,b)):void 0},g=function(){return a.getOption("interceptRequests")&&a.onXHR(function(c){var d;return d=c.xhr,d.offline!==!1?b(d,a.markUp,a.confirmDown):void 0}),a.getOption("checkOnLoad")?a.check():void 0},setTimeout(g,0),window.Offline=a}).call(this),function(){var a,b,c,d,e,f,g,h,i;if(!window.Offline)throw new Error("Offline Reconnect brought in without offline.js");d=Offline.reconnect={},f=null,e=function(){var a;return null!=d.state&&"inactive"!==d.state&&Offline.trigger("reconnect:stopped"),d.state="inactive",d.remaining=d.delay=null!=(a=Offline.getOption("reconnect.initialDelay"))?a:3},b=function(){var a,b;return a=null!=(b=Offline.getOption("reconnect.delay"))?b:Math.min(Math.ceil(1.5*d.delay),3600),d.remaining=d.delay=a},g=function(){return"connecting"!==d.state?(d.remaining-=1,Offline.trigger("reconnect:tick"),0===d.remaining?h():void 0):void 0},h=function(){return"waiting"===d.state?(Offline.trigger("reconnect:connecting"),d.state="connecting",Offline.check()):void 0},a=function(){return Offline.getOption("reconnect")?(e(),d.state="waiting",Offline.trigger("reconnect:started"),f=setInterval(g,1e3)):void 0},i=function(){return null!=f&&clearInterval(f),e()},c=function(){return Offline.getOption("reconnect")&&"connecting"===d.state?(Offline.trigger("reconnect:failure"),d.state="waiting",b()):void 0},d.tryNow=h,e(),Offline.on("down",a),Offline.on("confirmed-down",c),Offline.on("up",i)}.call(this),function(){var a,b,c,d,e,f;if(!window.Offline)throw new Error("Requests module brought in without offline.js");c=[],f=!1,d=function(a){return Offline.trigger("requests:capture"),"down"!==Offline.state&&(f=!0),c.push(a)},e=function(a){var b,c,d,e,f,g,h,i,j;j=a.xhr,g=a.url,f=a.type,h=a.user,d=a.password,b=a.body,j.abort(),j.open(f,g,!0,h,d),e=j.headers;for(c in e)i=e[c],j.setRequestHeader(c,i);return j.mimeType&&j.overrideMimeType(j.mimeType),j.send(b)},a=function(){return c=[]},b=function(){var b,d,f,g,h,i;for(Offline.trigger("requests:flush"),h={},b=0,f=c.length;f>b;b++)g=c[b],i=g.url.replace(/(\?|&)_=[0-9]+/,function(a,b){return"?"===b?b:""}),h[g.type.toUpperCase()+" - "+i]=g;for(d in h)g=h[d],e(g);return a()},setTimeout(function(){return Offline.getOption("requests")!==!1?(Offline.on("confirmed-up",function(){return f?(f=!1,a()):void 0}),Offline.on("up",b),Offline.on("down",function(){return f=!1}),Offline.onXHR(function(a){var b,c,e,f,g;return g=a.xhr,e=a.async,g.offline!==!1&&(f=function(){return d(a)},c=g.send,g.send=function(b){return a.body=b,c.apply(g,arguments)},e)?null===g.onprogress?(g.addEventListener("error",f,!1),g.addEventListener("timeout",f,!1)):(b=g.onreadystatechange,g.onreadystatechange=function(){return 0===g.readyState?f():4===g.readyState&&(0===g.status||g.status>=12e3)&&f(),"function"==typeof b?b.apply(null,arguments):void 0}):void 0}),Offline.requests={flush:b,clear:a}):void 0},0)}.call(this),function(){var a,b,c,d,e;if(!Offline)throw new Error("Offline simulate brought in without offline.js");for(d=["up","down"],b=0,c=d.length;c>b;b++)e=d[b],(document.querySelector("script[data-simulate='"+e+"']")||localStorage.OFFLINE_SIMULATE===e)&&(null==Offline.options&&(Offline.options={}),null==(a=Offline.options).checks&&(a.checks={}),Offline.options.checks.active=e)}.call(this),function(){var a,b,c,d,e,f,g,h,i,j,k,l,m;if(!window.Offline)throw new Error("Offline UI brought in without offline.js");b='<div class="offline-ui"><div class="offline-ui-content"></div></div>',a='<a href class="offline-ui-retry"></a>',f=function(a){var b;return b=document.createElement("div"),b.innerHTML=a,b.children[0]},g=e=null,d=function(a){return k(a),g.className+=" "+a},k=function(a){return g.className=g.className.replace(new RegExp("(^| )"+a.split(" ").join("|")+"( |$)","gi")," ")},i={},h=function(a,b){return d(a),null!=i[a]&&clearTimeout(i[a]),i[a]=setTimeout(function(){return k(a),delete i[a]},1e3*b)},m=function(a){var b,c,d,e;d={day:86400,hour:3600,minute:60,second:1};for(c in d)if(b=d[c],a>=b)return e=Math.floor(a/b),[e,c];return["now",""]},l=function(){var c,h;return g=f(b),document.body.appendChild(g),null!=Offline.reconnect&&Offline.getOption("reconnect")&&(g.appendChild(f(a)),c=g.querySelector(".offline-ui-retry"),h=function(a){return a.preventDefault(),Offline.reconnect.tryNow()},null!=c.addEventListener?c.addEventListener("click",h,!1):c.attachEvent("click",h)),d("offline-ui-"+Offline.state),e=g.querySelector(".offline-ui-content")},j=function(){return l(),Offline.on("up",function(){return k("offline-ui-down"),d("offline-ui-up"),h("offline-ui-up-2s",2),h("offline-ui-up-5s",5)}),Offline.on("down",function(){return k("offline-ui-up"),d("offline-ui-down"),h("offline-ui-down-2s",2),h("offline-ui-down-5s",5)}),Offline.on("reconnect:connecting",function(){return d("offline-ui-connecting"),k("offline-ui-waiting")}),Offline.on("reconnect:tick",function(){var a,b,c;return d("offline-ui-waiting"),k("offline-ui-connecting"),a=m(Offline.reconnect.remaining),b=a[0],c=a[1],e.setAttribute("data-retry-in-value",b),e.setAttribute("data-retry-in-unit",c)}),Offline.on("reconnect:stopped",function(){return k("offline-ui-connecting offline-ui-waiting"),e.setAttribute("data-retry-in-value",null),e.setAttribute("data-retry-in-unit",null)}),Offline.on("reconnect:failure",function(){return h("offline-ui-reconnect-failed-2s",2),h("offline-ui-reconnect-failed-5s",5)}),Offline.on("reconnect:success",function(){return h("offline-ui-reconnect-succeeded-2s",2),h("offline-ui-reconnect-succeeded-5s",5)})},"complete"===document.readyState?j():null!=document.addEventListener?document.addEventListener("DOMContentLoaded",j,!1):(c=document.onreadystatechange,document.onreadystatechange=function(){return"complete"===document.readyState&&j(),"function"==typeof c?c.apply(null,arguments):void 0})}.call(this);
(function() {
  $(document).ready(function() {
    return $(document).on('submit', '#new_user, #new_employee, .new_employee', function(e) {
      return Tipmetric.Controllers.spinner.show();
    });
  });

}).call(this);
/*! jQuery Validation Plugin - v1.14.0 - 6/30/2015
 * http://jqueryvalidation.org/
 * Copyright (c) 2015 Jörn Zaefferer; Licensed MIT */

!function(a){"function"==typeof define&&define.amd?define(["jquery"],a):a(jQuery)}(function(a){a.extend(a.fn,{validate:function(b){if(!this.length)return void(b&&b.debug&&window.console&&console.warn("Nothing selected, can't validate, returning nothing."));var c=a.data(this[0],"validator");return c?c:(this.attr("novalidate","novalidate"),c=new a.validator(b,this[0]),a.data(this[0],"validator",c),c.settings.onsubmit&&(this.on("click.validate",":submit",function(b){c.settings.submitHandler&&(c.submitButton=b.target),a(this).hasClass("cancel")&&(c.cancelSubmit=!0),void 0!==a(this).attr("formnovalidate")&&(c.cancelSubmit=!0)}),this.on("submit.validate",function(b){function d(){var d,e;return c.settings.submitHandler?(c.submitButton&&(d=a("<input type='hidden'/>").attr("name",c.submitButton.name).val(a(c.submitButton).val()).appendTo(c.currentForm)),e=c.settings.submitHandler.call(c,c.currentForm,b),c.submitButton&&d.remove(),void 0!==e?e:!1):!0}return c.settings.debug&&b.preventDefault(),c.cancelSubmit?(c.cancelSubmit=!1,d()):c.form()?c.pendingRequest?(c.formSubmitted=!0,!1):d():(c.focusInvalid(),!1)})),c)},valid:function(){var b,c,d;return a(this[0]).is("form")?b=this.validate().form():(d=[],b=!0,c=a(this[0].form).validate(),this.each(function(){b=c.element(this)&&b,d=d.concat(c.errorList)}),c.errorList=d),b},rules:function(b,c){var d,e,f,g,h,i,j=this[0];if(b)switch(d=a.data(j.form,"validator").settings,e=d.rules,f=a.validator.staticRules(j),b){case"add":a.extend(f,a.validator.normalizeRule(c)),delete f.messages,e[j.name]=f,c.messages&&(d.messages[j.name]=a.extend(d.messages[j.name],c.messages));break;case"remove":return c?(i={},a.each(c.split(/\s/),function(b,c){i[c]=f[c],delete f[c],"required"===c&&a(j).removeAttr("aria-required")}),i):(delete e[j.name],f)}return g=a.validator.normalizeRules(a.extend({},a.validator.classRules(j),a.validator.attributeRules(j),a.validator.dataRules(j),a.validator.staticRules(j)),j),g.required&&(h=g.required,delete g.required,g=a.extend({required:h},g),a(j).attr("aria-required","true")),g.remote&&(h=g.remote,delete g.remote,g=a.extend(g,{remote:h})),g}}),a.extend(a.expr[":"],{blank:function(b){return!a.trim(""+a(b).val())},filled:function(b){return!!a.trim(""+a(b).val())},unchecked:function(b){return!a(b).prop("checked")}}),a.validator=function(b,c){this.settings=a.extend(!0,{},a.validator.defaults,b),this.currentForm=c,this.init()},a.validator.format=function(b,c){return 1===arguments.length?function(){var c=a.makeArray(arguments);return c.unshift(b),a.validator.format.apply(this,c)}:(arguments.length>2&&c.constructor!==Array&&(c=a.makeArray(arguments).slice(1)),c.constructor!==Array&&(c=[c]),a.each(c,function(a,c){b=b.replace(new RegExp("\\{"+a+"\\}","g"),function(){return c})}),b)},a.extend(a.validator,{defaults:{messages:{},groups:{},rules:{},errorClass:"error",validClass:"valid",errorElement:"label",focusCleanup:!1,focusInvalid:!0,errorContainer:a([]),errorLabelContainer:a([]),onsubmit:!0,ignore:":hidden",ignoreTitle:!1,onfocusin:function(a){this.lastActive=a,this.settings.focusCleanup&&(this.settings.unhighlight&&this.settings.unhighlight.call(this,a,this.settings.errorClass,this.settings.validClass),this.hideThese(this.errorsFor(a)))},onfocusout:function(a){this.checkable(a)||!(a.name in this.submitted)&&this.optional(a)||this.element(a)},onkeyup:function(b,c){var d=[16,17,18,20,35,36,37,38,39,40,45,144,225];9===c.which&&""===this.elementValue(b)||-1!==a.inArray(c.keyCode,d)||(b.name in this.submitted||b===this.lastElement)&&this.element(b)},onclick:function(a){a.name in this.submitted?this.element(a):a.parentNode.name in this.submitted&&this.element(a.parentNode)},highlight:function(b,c,d){"radio"===b.type?this.findByName(b.name).addClass(c).removeClass(d):a(b).addClass(c).removeClass(d)},unhighlight:function(b,c,d){"radio"===b.type?this.findByName(b.name).removeClass(c).addClass(d):a(b).removeClass(c).addClass(d)}},setDefaults:function(b){a.extend(a.validator.defaults,b)},messages:{required:"This field is required.",remote:"Please fix this field.",email:"Please enter a valid email address.",url:"Please enter a valid URL.",date:"Please enter a valid date.",dateISO:"Please enter a valid date ( ISO ).",number:"Please enter a valid number.",digits:"Please enter only digits.",creditcard:"Please enter a valid credit card number.",equalTo:"Please enter the same value again.",maxlength:a.validator.format("Please enter no more than {0} characters."),minlength:a.validator.format("Please enter at least {0} characters."),rangelength:a.validator.format("Please enter a value between {0} and {1} characters long."),range:a.validator.format("Please enter a value between {0} and {1}."),max:a.validator.format("Please enter a value less than or equal to {0}."),min:a.validator.format("Please enter a value greater than or equal to {0}.")},autoCreateRanges:!1,prototype:{init:function(){function b(b){var c=a.data(this.form,"validator"),d="on"+b.type.replace(/^validate/,""),e=c.settings;e[d]&&!a(this).is(e.ignore)&&e[d].call(c,this,b)}this.labelContainer=a(this.settings.errorLabelContainer),this.errorContext=this.labelContainer.length&&this.labelContainer||a(this.currentForm),this.containers=a(this.settings.errorContainer).add(this.settings.errorLabelContainer),this.submitted={},this.valueCache={},this.pendingRequest=0,this.pending={},this.invalid={},this.reset();var c,d=this.groups={};a.each(this.settings.groups,function(b,c){"string"==typeof c&&(c=c.split(/\s/)),a.each(c,function(a,c){d[c]=b})}),c=this.settings.rules,a.each(c,function(b,d){c[b]=a.validator.normalizeRule(d)}),a(this.currentForm).on("focusin.validate focusout.validate keyup.validate",":text, [type='password'], [type='file'], select, textarea, [type='number'], [type='search'], [type='tel'], [type='url'], [type='email'], [type='datetime'], [type='date'], [type='month'], [type='week'], [type='time'], [type='datetime-local'], [type='range'], [type='color'], [type='radio'], [type='checkbox']",b).on("click.validate","select, option, [type='radio'], [type='checkbox']",b),this.settings.invalidHandler&&a(this.currentForm).on("invalid-form.validate",this.settings.invalidHandler),a(this.currentForm).find("[required], [data-rule-required], .required").attr("aria-required","true")},form:function(){return this.checkForm(),a.extend(this.submitted,this.errorMap),this.invalid=a.extend({},this.errorMap),this.valid()||a(this.currentForm).triggerHandler("invalid-form",[this]),this.showErrors(),this.valid()},checkForm:function(){this.prepareForm();for(var a=0,b=this.currentElements=this.elements();b[a];a++)this.check(b[a]);return this.valid()},element:function(b){var c=this.clean(b),d=this.validationTargetFor(c),e=!0;return this.lastElement=d,void 0===d?delete this.invalid[c.name]:(this.prepareElement(d),this.currentElements=a(d),e=this.check(d)!==!1,e?delete this.invalid[d.name]:this.invalid[d.name]=!0),a(b).attr("aria-invalid",!e),this.numberOfInvalids()||(this.toHide=this.toHide.add(this.containers)),this.showErrors(),e},showErrors:function(b){if(b){a.extend(this.errorMap,b),this.errorList=[];for(var c in b)this.errorList.push({message:b[c],element:this.findByName(c)[0]});this.successList=a.grep(this.successList,function(a){return!(a.name in b)})}this.settings.showErrors?this.settings.showErrors.call(this,this.errorMap,this.errorList):this.defaultShowErrors()},resetForm:function(){a.fn.resetForm&&a(this.currentForm).resetForm(),this.submitted={},this.lastElement=null,this.prepareForm(),this.hideErrors();var b,c=this.elements().removeData("previousValue").removeAttr("aria-invalid");if(this.settings.unhighlight)for(b=0;c[b];b++)this.settings.unhighlight.call(this,c[b],this.settings.errorClass,"");else c.removeClass(this.settings.errorClass)},numberOfInvalids:function(){return this.objectLength(this.invalid)},objectLength:function(a){var b,c=0;for(b in a)c++;return c},hideErrors:function(){this.hideThese(this.toHide)},hideThese:function(a){a.not(this.containers).text(""),this.addWrapper(a).hide()},valid:function(){return 0===this.size()},size:function(){return this.errorList.length},focusInvalid:function(){if(this.settings.focusInvalid)try{a(this.findLastActive()||this.errorList.length&&this.errorList[0].element||[]).filter(":visible").focus().trigger("focusin")}catch(b){}},findLastActive:function(){var b=this.lastActive;return b&&1===a.grep(this.errorList,function(a){return a.element.name===b.name}).length&&b},elements:function(){var b=this,c={};return a(this.currentForm).find("input, select, textarea").not(":submit, :reset, :image, :disabled").not(this.settings.ignore).filter(function(){return!this.name&&b.settings.debug&&window.console&&console.error("%o has no name assigned",this),this.name in c||!b.objectLength(a(this).rules())?!1:(c[this.name]=!0,!0)})},clean:function(b){return a(b)[0]},errors:function(){var b=this.settings.errorClass.split(" ").join(".");return a(this.settings.errorElement+"."+b,this.errorContext)},reset:function(){this.successList=[],this.errorList=[],this.errorMap={},this.toShow=a([]),this.toHide=a([]),this.currentElements=a([])},prepareForm:function(){this.reset(),this.toHide=this.errors().add(this.containers)},prepareElement:function(a){this.reset(),this.toHide=this.errorsFor(a)},elementValue:function(b){var c,d=a(b),e=b.type;return"radio"===e||"checkbox"===e?this.findByName(b.name).filter(":checked").val():"number"===e&&"undefined"!=typeof b.validity?b.validity.badInput?!1:d.val():(c=d.val(),"string"==typeof c?c.replace(/\r/g,""):c)},check:function(b){b=this.validationTargetFor(this.clean(b));var c,d,e,f=a(b).rules(),g=a.map(f,function(a,b){return b}).length,h=!1,i=this.elementValue(b);for(d in f){e={method:d,parameters:f[d]};try{if(c=a.validator.methods[d].call(this,i,b,e.parameters),"dependency-mismatch"===c&&1===g){h=!0;continue}if(h=!1,"pending"===c)return void(this.toHide=this.toHide.not(this.errorsFor(b)));if(!c)return this.formatAndAdd(b,e),!1}catch(j){throw this.settings.debug&&window.console&&console.log("Exception occurred when checking element "+b.id+", check the '"+e.method+"' method.",j),j instanceof TypeError&&(j.message+=".  Exception occurred when checking element "+b.id+", check the '"+e.method+"' method."),j}}if(!h)return this.objectLength(f)&&this.successList.push(b),!0},customDataMessage:function(b,c){return a(b).data("msg"+c.charAt(0).toUpperCase()+c.substring(1).toLowerCase())||a(b).data("msg")},customMessage:function(a,b){var c=this.settings.messages[a];return c&&(c.constructor===String?c:c[b])},findDefined:function(){for(var a=0;a<arguments.length;a++)if(void 0!==arguments[a])return arguments[a];return void 0},defaultMessage:function(b,c){return this.findDefined(this.customMessage(b.name,c),this.customDataMessage(b,c),!this.settings.ignoreTitle&&b.title||void 0,a.validator.messages[c],"<strong>Warning: No message defined for "+b.name+"</strong>")},formatAndAdd:function(b,c){var d=this.defaultMessage(b,c.method),e=/\$?\{(\d+)\}/g;"function"==typeof d?d=d.call(this,c.parameters,b):e.test(d)&&(d=a.validator.format(d.replace(e,"{$1}"),c.parameters)),this.errorList.push({message:d,element:b,method:c.method}),this.errorMap[b.name]=d,this.submitted[b.name]=d},addWrapper:function(a){return this.settings.wrapper&&(a=a.add(a.parent(this.settings.wrapper))),a},defaultShowErrors:function(){var a,b,c;for(a=0;this.errorList[a];a++)c=this.errorList[a],this.settings.highlight&&this.settings.highlight.call(this,c.element,this.settings.errorClass,this.settings.validClass),this.showLabel(c.element,c.message);if(this.errorList.length&&(this.toShow=this.toShow.add(this.containers)),this.settings.success)for(a=0;this.successList[a];a++)this.showLabel(this.successList[a]);if(this.settings.unhighlight)for(a=0,b=this.validElements();b[a];a++)this.settings.unhighlight.call(this,b[a],this.settings.errorClass,this.settings.validClass);this.toHide=this.toHide.not(this.toShow),this.hideErrors(),this.addWrapper(this.toShow).show()},validElements:function(){return this.currentElements.not(this.invalidElements())},invalidElements:function(){return a(this.errorList).map(function(){return this.element})},showLabel:function(b,c){var d,e,f,g=this.errorsFor(b),h=this.idOrName(b),i=a(b).attr("aria-describedby");g.length?(g.removeClass(this.settings.validClass).addClass(this.settings.errorClass),g.html(c)):(g=a("<"+this.settings.errorElement+">").attr("id",h+"-error").addClass(this.settings.errorClass).html(c||""),d=g,this.settings.wrapper&&(d=g.hide().show().wrap("<"+this.settings.wrapper+"/>").parent()),this.labelContainer.length?this.labelContainer.append(d):this.settings.errorPlacement?this.settings.errorPlacement(d,a(b)):d.insertAfter(b),g.is("label")?g.attr("for",h):0===g.parents("label[for='"+h+"']").length&&(f=g.attr("id").replace(/(:|\.|\[|\]|\$)/g,"\\$1"),i?i.match(new RegExp("\\b"+f+"\\b"))||(i+=" "+f):i=f,a(b).attr("aria-describedby",i),e=this.groups[b.name],e&&a.each(this.groups,function(b,c){c===e&&a("[name='"+b+"']",this.currentForm).attr("aria-describedby",g.attr("id"))}))),!c&&this.settings.success&&(g.text(""),"string"==typeof this.settings.success?g.addClass(this.settings.success):this.settings.success(g,b)),this.toShow=this.toShow.add(g)},errorsFor:function(b){var c=this.idOrName(b),d=a(b).attr("aria-describedby"),e="label[for='"+c+"'], label[for='"+c+"'] *";return d&&(e=e+", #"+d.replace(/\s+/g,", #")),this.errors().filter(e)},idOrName:function(a){return this.groups[a.name]||(this.checkable(a)?a.name:a.id||a.name)},validationTargetFor:function(b){return this.checkable(b)&&(b=this.findByName(b.name)),a(b).not(this.settings.ignore)[0]},checkable:function(a){return/radio|checkbox/i.test(a.type)},findByName:function(b){return a(this.currentForm).find("[name='"+b+"']")},getLength:function(b,c){switch(c.nodeName.toLowerCase()){case"select":return a("option:selected",c).length;case"input":if(this.checkable(c))return this.findByName(c.name).filter(":checked").length}return b.length},depend:function(a,b){return this.dependTypes[typeof a]?this.dependTypes[typeof a](a,b):!0},dependTypes:{"boolean":function(a){return a},string:function(b,c){return!!a(b,c.form).length},"function":function(a,b){return a(b)}},optional:function(b){var c=this.elementValue(b);return!a.validator.methods.required.call(this,c,b)&&"dependency-mismatch"},startRequest:function(a){this.pending[a.name]||(this.pendingRequest++,this.pending[a.name]=!0)},stopRequest:function(b,c){this.pendingRequest--,this.pendingRequest<0&&(this.pendingRequest=0),delete this.pending[b.name],c&&0===this.pendingRequest&&this.formSubmitted&&this.form()?(a(this.currentForm).submit(),this.formSubmitted=!1):!c&&0===this.pendingRequest&&this.formSubmitted&&(a(this.currentForm).triggerHandler("invalid-form",[this]),this.formSubmitted=!1)},previousValue:function(b){return a.data(b,"previousValue")||a.data(b,"previousValue",{old:null,valid:!0,message:this.defaultMessage(b,"remote")})},destroy:function(){this.resetForm(),a(this.currentForm).off(".validate").removeData("validator")}},classRuleSettings:{required:{required:!0},email:{email:!0},url:{url:!0},date:{date:!0},dateISO:{dateISO:!0},number:{number:!0},digits:{digits:!0},creditcard:{creditcard:!0}},addClassRules:function(b,c){b.constructor===String?this.classRuleSettings[b]=c:a.extend(this.classRuleSettings,b)},classRules:function(b){var c={},d=a(b).attr("class");return d&&a.each(d.split(" "),function(){this in a.validator.classRuleSettings&&a.extend(c,a.validator.classRuleSettings[this])}),c},normalizeAttributeRule:function(a,b,c,d){/min|max/.test(c)&&(null===b||/number|range|text/.test(b))&&(d=Number(d),isNaN(d)&&(d=void 0)),d||0===d?a[c]=d:b===c&&"range"!==b&&(a[c]=!0)},attributeRules:function(b){var c,d,e={},f=a(b),g=b.getAttribute("type");for(c in a.validator.methods)"required"===c?(d=b.getAttribute(c),""===d&&(d=!0),d=!!d):d=f.attr(c),this.normalizeAttributeRule(e,g,c,d);return e.maxlength&&/-1|2147483647|524288/.test(e.maxlength)&&delete e.maxlength,e},dataRules:function(b){var c,d,e={},f=a(b),g=b.getAttribute("type");for(c in a.validator.methods)d=f.data("rule"+c.charAt(0).toUpperCase()+c.substring(1).toLowerCase()),this.normalizeAttributeRule(e,g,c,d);return e},staticRules:function(b){var c={},d=a.data(b.form,"validator");return d.settings.rules&&(c=a.validator.normalizeRule(d.settings.rules[b.name])||{}),c},normalizeRules:function(b,c){return a.each(b,function(d,e){if(e===!1)return void delete b[d];if(e.param||e.depends){var f=!0;switch(typeof e.depends){case"string":f=!!a(e.depends,c.form).length;break;case"function":f=e.depends.call(c,c)}f?b[d]=void 0!==e.param?e.param:!0:delete b[d]}}),a.each(b,function(d,e){b[d]=a.isFunction(e)?e(c):e}),a.each(["minlength","maxlength"],function(){b[this]&&(b[this]=Number(b[this]))}),a.each(["rangelength","range"],function(){var c;b[this]&&(a.isArray(b[this])?b[this]=[Number(b[this][0]),Number(b[this][1])]:"string"==typeof b[this]&&(c=b[this].replace(/[\[\]]/g,"").split(/[\s,]+/),b[this]=[Number(c[0]),Number(c[1])]))}),a.validator.autoCreateRanges&&(null!=b.min&&null!=b.max&&(b.range=[b.min,b.max],delete b.min,delete b.max),null!=b.minlength&&null!=b.maxlength&&(b.rangelength=[b.minlength,b.maxlength],delete b.minlength,delete b.maxlength)),b},normalizeRule:function(b){if("string"==typeof b){var c={};a.each(b.split(/\s/),function(){c[this]=!0}),b=c}return b},addMethod:function(b,c,d){a.validator.methods[b]=c,a.validator.messages[b]=void 0!==d?d:a.validator.messages[b],c.length<3&&a.validator.addClassRules(b,a.validator.normalizeRule(b))},methods:{required:function(b,c,d){if(!this.depend(d,c))return"dependency-mismatch";if("select"===c.nodeName.toLowerCase()){var e=a(c).val();return e&&e.length>0}return this.checkable(c)?this.getLength(b,c)>0:b.length>0},email:function(a,b){return this.optional(b)||/^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(a)},url:function(a,b){return this.optional(b)||/^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})).?)(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(a)},date:function(a,b){return this.optional(b)||!/Invalid|NaN/.test(new Date(a).toString())},dateISO:function(a,b){return this.optional(b)||/^\d{4}[\/\-](0?[1-9]|1[012])[\/\-](0?[1-9]|[12][0-9]|3[01])$/.test(a)},number:function(a,b){return this.optional(b)||/^(?:-?\d+|-?\d{1,3}(?:,\d{3})+)?(?:\.\d+)?$/.test(a)},digits:function(a,b){return this.optional(b)||/^\d+$/.test(a)},creditcard:function(a,b){if(this.optional(b))return"dependency-mismatch";if(/[^0-9 \-]+/.test(a))return!1;var c,d,e=0,f=0,g=!1;if(a=a.replace(/\D/g,""),a.length<13||a.length>19)return!1;for(c=a.length-1;c>=0;c--)d=a.charAt(c),f=parseInt(d,10),g&&(f*=2)>9&&(f-=9),e+=f,g=!g;return e%10===0},minlength:function(b,c,d){var e=a.isArray(b)?b.length:this.getLength(b,c);return this.optional(c)||e>=d},maxlength:function(b,c,d){var e=a.isArray(b)?b.length:this.getLength(b,c);return this.optional(c)||d>=e},rangelength:function(b,c,d){var e=a.isArray(b)?b.length:this.getLength(b,c);return this.optional(c)||e>=d[0]&&e<=d[1]},min:function(a,b,c){return this.optional(b)||a>=c},max:function(a,b,c){return this.optional(b)||c>=a},range:function(a,b,c){return this.optional(b)||a>=c[0]&&a<=c[1]},equalTo:function(b,c,d){var e=a(d);return this.settings.onfocusout&&e.off(".validate-equalTo").on("blur.validate-equalTo",function(){a(c).valid()}),b===e.val()},remote:function(b,c,d){if(this.optional(c))return"dependency-mismatch";var e,f,g=this.previousValue(c);return this.settings.messages[c.name]||(this.settings.messages[c.name]={}),g.originalMessage=this.settings.messages[c.name].remote,this.settings.messages[c.name].remote=g.message,d="string"==typeof d&&{url:d}||d,g.old===b?g.valid:(g.old=b,e=this,this.startRequest(c),f={},f[c.name]=b,a.ajax(a.extend(!0,{mode:"abort",port:"validate"+c.name,dataType:"json",data:f,context:e.currentForm,success:function(d){var f,h,i,j=d===!0||"true"===d;e.settings.messages[c.name].remote=g.originalMessage,j?(i=e.formSubmitted,e.prepareElement(c),e.formSubmitted=i,e.successList.push(c),delete e.invalid[c.name],e.showErrors()):(f={},h=d||e.defaultMessage(c,"remote"),f[c.name]=g.message=a.isFunction(h)?h(b):h,e.invalid[c.name]=!0,e.showErrors(f)),g.valid=j,e.stopRequest(c,j)}},d)),"pending")}}});var b,c={};a.ajaxPrefilter?a.ajaxPrefilter(function(a,b,d){var e=a.port;"abort"===a.mode&&(c[e]&&c[e].abort(),c[e]=d)}):(b=a.ajax,a.ajax=function(d){var e=("mode"in d?d:a.ajaxSettings).mode,f=("port"in d?d:a.ajaxSettings).port;return"abort"===e?(c[f]&&c[f].abort(),c[f]=b.apply(this,arguments),c[f]):b.apply(this,arguments)})});
RegExp.escape=function(r){return r.replace(/[-\/\\^$*+?.()|[\]{}]/g,"\\$&")},function(){"use strict";var r;(r="undefined"!=typeof jQuery&&jQuery?jQuery:{}).csv={defaults:{separator:",",delimiter:'"',headers:!0},hooks:{castToScalar:function(r,e){if(isNaN(r))return r;if(/\./.test(r))return parseFloat(r);var a=parseInt(r);return isNaN(a)?null:a}},parsers:{parse:function(r,e){var a=e.separator,t=e.delimiter;e.state.rowNum||(e.state.rowNum=1),e.state.colNum||(e.state.colNum=1);var s=[],o=[],n=0,i="",l=!1;function c(){if(n=0,i="",e.start&&e.state.rowNum<e.start)return o=[],e.state.rowNum++,void(e.state.colNum=1);if(void 0===e.onParseEntry)s.push(o);else{var r=e.onParseEntry(o,e.state);!1!==r&&s.push(r)}o=[],e.end&&e.state.rowNum>=e.end&&(l=!0),e.state.rowNum++,e.state.colNum=1}function u(){if(void 0===e.onParseValue)o.push(i);else{var r=e.onParseValue(i,e.state);!1!==r&&o.push(r)}i="",n=0,e.state.colNum++}var f=RegExp.escape(a),d=RegExp.escape(t),m=/(D|S|\r\n|\n|\r|[^DS\r\n]+)/,p=m.source;return p=(p=p.replace(/S/g,f)).replace(/D/g,d),m=new RegExp(p,"gm"),r.replace(m,function(r){if(!l)switch(n){case 0:if(r===a){i+="",u();break}if(r===t){n=1;break}if(/^(\r\n|\n|\r)$/.test(r)){u(),c();break}i+=r,n=3;break;case 1:if(r===t){n=2;break}i+=r,n=1;break;case 2:if(r===t){i+=r,n=1;break}if(r===a){u();break}if(/^(\r\n|\n|\r)$/.test(r)){u(),c();break}throw new Error("CSVDataError: Illegal State [Row:"+e.state.rowNum+"][Col:"+e.state.colNum+"]");case 3:if(r===a){u();break}if(/^(\r\n|\n|\r)$/.test(r)){u(),c();break}if(r===t)throw new Error("CSVDataError: Illegal Quote [Row:"+e.state.rowNum+"][Col:"+e.state.colNum+"]");throw new Error("CSVDataError: Illegal Data [Row:"+e.state.rowNum+"][Col:"+e.state.colNum+"]");default:throw new Error("CSVDataError: Unknown State [Row:"+e.state.rowNum+"][Col:"+e.state.colNum+"]")}}),0!==o.length&&(u(),c()),s},splitLines:function(e,a){if(e){var t=(a=a||{}).separator||r.csv.defaults.separator,s=a.delimiter||r.csv.defaults.delimiter;a.state=a.state||{},a.state.rowNum||(a.state.rowNum=1);var o=[],n=0,i="",l=!1,c=RegExp.escape(t),u=RegExp.escape(s),f=/(D|S|\n|\r|[^DS\r\n]+)/,d=f.source;return d=(d=d.replace(/S/g,c)).replace(/D/g,u),f=new RegExp(d,"gm"),e.replace(f,function(r){if(!l)switch(n){case 0:if(r===t){i+=r,n=0;break}if(r===s){i+=r,n=1;break}if("\n"===r){m();break}if(/^\r$/.test(r))break;i+=r,n=3;break;case 1:if(r===s){i+=r,n=2;break}i+=r,n=1;break;case 2:var e=i.substr(i.length-1);if(r===s&&e===s){i+=r,n=1;break}if(r===t){i+=r,n=0;break}if("\n"===r){m();break}if("\r"===r)break;throw new Error("CSVDataError: Illegal state [Row:"+a.state.rowNum+"]");case 3:if(r===t){i+=r,n=0;break}if("\n"===r){m();break}if("\r"===r)break;if(r===s)throw new Error("CSVDataError: Illegal quote [Row:"+a.state.rowNum+"]");throw new Error("CSVDataError: Illegal state [Row:"+a.state.rowNum+"]");default:throw new Error("CSVDataError: Unknown state [Row:"+a.state.rowNum+"]")}}),""!==i&&m(),o}function m(){if(n=0,a.start&&a.state.rowNum<a.start)return i="",void a.state.rowNum++;if(void 0===a.onParseEntry)o.push(i);else{var r=a.onParseEntry(i,a.state);!1!==r&&o.push(r)}i="",a.end&&a.state.rowNum>=a.end&&(l=!0),a.state.rowNum++}},parseEntry:function(r,e){var a=e.separator,t=e.delimiter;e.state.rowNum||(e.state.rowNum=1),e.state.colNum||(e.state.colNum=1);var s=[],o=0,n="";function i(){if(void 0===e.onParseValue)s.push(n);else{var r=e.onParseValue(n,e.state);!1!==r&&s.push(r)}n="",o=0,e.state.colNum++}if(!e.match){var l=RegExp.escape(a),c=RegExp.escape(t),u=/(D|S|\n|\r|[^DS\r\n]+)/.source;u=(u=u.replace(/S/g,l)).replace(/D/g,c),e.match=new RegExp(u,"gm")}return r.replace(e.match,function(r){switch(o){case 0:if(r===a){n+="",i();break}if(r===t){o=1;break}if("\n"===r||"\r"===r)break;n+=r,o=3;break;case 1:if(r===t){o=2;break}n+=r,o=1;break;case 2:if(r===t){n+=r,o=1;break}if(r===a){i();break}if("\n"===r||"\r"===r)break;throw new Error("CSVDataError: Illegal State [Row:"+e.state.rowNum+"][Col:"+e.state.colNum+"]");case 3:if(r===a){i();break}if("\n"===r||"\r"===r)break;if(r===t)throw new Error("CSVDataError: Illegal Quote [Row:"+e.state.rowNum+"][Col:"+e.state.colNum+"]");throw new Error("CSVDataError: Illegal Data [Row:"+e.state.rowNum+"][Col:"+e.state.colNum+"]");default:throw new Error("CSVDataError: Unknown State [Row:"+e.state.rowNum+"][Col:"+e.state.colNum+"]")}}),i(),s}},helpers:{collectPropertyNames:function(r){var e=[],a=[],t=[];for(e in r)for(a in r[e])r[e].hasOwnProperty(a)&&t.indexOf(a)<0&&"function"!=typeof r[e][a]&&t.push(a);return t}},toArray:function(e,a,t){a=void 0!==a?a:{};var s={};s.callback=void 0!==t&&"function"==typeof t&&t,s.separator="separator"in a?a.separator:r.csv.defaults.separator,s.delimiter="delimiter"in a?a.delimiter:r.csv.defaults.delimiter;var o=void 0!==a.state?a.state:{};a={delimiter:s.delimiter,separator:s.separator,onParseEntry:a.onParseEntry,onParseValue:a.onParseValue,state:o};var n=r.csv.parsers.parseEntry(e,a);if(!s.callback)return n;s.callback("",n)},toArrays:function(e,a,t){a=void 0!==a?a:{};var s={};s.callback=void 0!==t&&"function"==typeof t&&t,s.separator="separator"in a?a.separator:r.csv.defaults.separator,s.delimiter="delimiter"in a?a.delimiter:r.csv.defaults.delimiter;var o;if(void 0!==(a={delimiter:s.delimiter,separator:s.separator,onPreParse:a.onPreParse,onParseEntry:a.onParseEntry,onParseValue:a.onParseValue,onPostParse:a.onPostParse,start:a.start,end:a.end,state:{rowNum:1,colNum:1}}).onPreParse&&a.onPreParse(e,a.state),o=r.csv.parsers.parse(e,a),void 0!==a.onPostParse&&a.onPostParse(o,a.state),!s.callback)return o;s.callback("",o)},toObjects:function(e,a,t){a=void 0!==a?a:{};var s={};s.callback=void 0!==t&&"function"==typeof t&&t,s.separator="separator"in a?a.separator:r.csv.defaults.separator,s.delimiter="delimiter"in a?a.delimiter:r.csv.defaults.delimiter,s.headers="headers"in a?a.headers:r.csv.defaults.headers,a.start="start"in a?a.start:1,s.headers&&a.start++,a.end&&s.headers&&a.end++;var o,n=[];a={delimiter:s.delimiter,separator:s.separator,onPreParse:a.onPreParse,onParseEntry:a.onParseEntry,onParseValue:a.onParseValue,onPostParse:a.onPostParse,start:a.start,end:a.end,state:{rowNum:1,colNum:1},match:!1,transform:a.transform};var i={delimiter:s.delimiter,separator:s.separator,start:1,end:1,state:{rowNum:1,colNum:1}};void 0!==a.onPreParse&&a.onPreParse(e,a.state);var l=r.csv.parsers.splitLines(e,i),c=r.csv.toArray(l[0],a);o=r.csv.parsers.splitLines(e,a),a.state.colNum=1,a.state.rowNum=c?2:1;for(var u=0,f=o.length;u<f;u++){for(var d=r.csv.toArray(o[u],a),m={},p=0;p<c.length;p++)m[c[p]]=d[p];void 0!==a.transform?n.push(a.transform.call(void 0,m)):n.push(m),a.state.rowNum++}if(void 0!==a.onPostParse&&a.onPostParse(n,a.state),!s.callback)return n;s.callback("",n)},fromArrays:function(e,a,t){a=void 0!==a?a:{};var s={};s.callback=void 0!==t&&"function"==typeof t&&t,s.separator="separator"in a?a.separator:r.csv.defaults.separator,s.delimiter="delimiter"in a?a.delimiter:r.csv.defaults.delimiter;var o,n,i,l,c="";for(i=0;i<e.length;i++){for(o=e[i],n=[],l=0;l<o.length;l++){var u=void 0===o[l]||null===o[l]?"":o[l].toString();u.indexOf(s.delimiter)>-1&&(u=u.replace(new RegExp(s.delimiter,"g"),s.delimiter+s.delimiter));var f="\n|\r|S|D";f=(f=f.replace("S",s.separator)).replace("D",s.delimiter),u.search(f)>-1&&(u=s.delimiter+u+s.delimiter),n.push(u)}c+=n.join(s.separator)+"\r\n"}if(!s.callback)return c;s.callback("",c)},fromObjects:function(e,a,t){a=void 0!==a?a:{};var s={};if(s.callback=void 0!==t&&"function"==typeof t&&t,s.separator="separator"in a?a.separator:r.csv.defaults.separator,s.delimiter="delimiter"in a?a.delimiter:r.csv.defaults.delimiter,s.headers="headers"in a?a.headers:r.csv.defaults.headers,s.sortOrder="sortOrder"in a?a.sortOrder:"declare",s.manualOrder="manualOrder"in a?a.manualOrder:[],s.transform=a.transform,"string"==typeof s.manualOrder&&(s.manualOrder=r.csv.toArray(s.manualOrder,s)),void 0!==s.transform){var o,n=e;for(e=[],o=0;o<n.length;o++)e.push(s.transform.call(void 0,n[o]))}var i=r.csv.helpers.collectPropertyNames(e);if("alpha"===s.sortOrder&&i.sort(),s.manualOrder.length>0){var l=[].concat(s.manualOrder);for(u=0;u<i.length;u++)l.indexOf(i[u])<0&&l.push(i[u]);i=l}var c,u,f,d,m=[];for(s.headers&&m.push(i),c=0;c<e.length;c++){for(f=[],u=0;u<i.length;u++)(d=i[u])in e[c]&&"function"!=typeof e[c][d]?f.push(e[c][d]):f.push("");m.push(f)}return r.csv.fromArrays(m,a,s.callback)}},r.csvEntry2Array=r.csv.toArray,r.csv2Array=r.csv.toArrays,r.csv2Dictionary=r.csv.toObjects,"undefined"!=typeof module&&module.exports&&(module.exports=r.csv)}.call(this);
!function(e,n){"object"==typeof exports&&"undefined"!=typeof module?module.exports=n():"function"==typeof define&&define.amd?define(n):e.Promise=n()}(this,function(){"use strict";function e(){}function n(e){if(!(this instanceof n))throw new TypeError("Promises must be constructed via new");if("function"!=typeof e)throw new TypeError("not a function");this._state=0,this._handled=!1,this._value=undefined,this._deferreds=[],f(e,this)}function t(e,t){for(;3===e._state;)e=e._value;0!==e._state?(e._handled=!0,n._immediateFn(function(){var n=1===e._state?t.onFulfilled:t.onRejected;if(null!==n){var i;try{i=n(e._value)}catch(f){return void r(t.promise,f)}o(t.promise,i)}else(1===e._state?o:r)(t.promise,e._value)})):e._deferreds.push(t)}function o(e,t){try{if(t===e)throw new TypeError("A promise cannot be resolved with itself.");if(t&&("object"==typeof t||"function"==typeof t)){var o=t.then;if(t instanceof n)return e._state=3,e._value=t,void i(e);if("function"==typeof o)return void f(function(e,n){return function(){e.apply(n,arguments)}}(o,t),e)}e._state=1,e._value=t,i(e)}catch(u){r(e,u)}}function r(e,n){e._state=2,e._value=n,i(e)}function i(e){2===e._state&&0===e._deferreds.length&&n._immediateFn(function(){e._handled||n._unhandledRejectionFn(e._value)});for(var o=0,r=e._deferreds.length;r>o;o++)t(e,e._deferreds[o]);e._deferreds=null}function f(e,n){var t=!1;try{e(function(e){t||(t=!0,o(n,e))},function(e){t||(t=!0,r(n,e))})}catch(i){if(t)return;t=!0,r(n,i)}}var u=setTimeout;return n.prototype["catch"]=function(e){return this.then(null,e)},n.prototype.then=function(n,o){var r=new this.constructor(e);return t(this,new function(e,n,t){this.onFulfilled="function"==typeof e?e:null,this.onRejected="function"==typeof n?n:null,this.promise=t}(n,o,r)),r},n.prototype["finally"]=function(e){var n=this.constructor;return this.then(function(t){return n.resolve(e()).then(function(){return t})},function(t){return n.resolve(e()).then(function(){return n.reject(t)})})},n.all=function(e){return new n(function(n,t){function o(e,f){try{if(f&&("object"==typeof f||"function"==typeof f)){var u=f.then;if("function"==typeof u)return void u.call(f,function(n){o(e,n)},t)}r[e]=f,0==--i&&n(r)}catch(c){t(c)}}if(!e||"undefined"==typeof e.length)throw new TypeError("Promise.all accepts an array");var r=Array.prototype.slice.call(e);if(0===r.length)return n([]);for(var i=r.length,f=0;r.length>f;f++)o(f,r[f])})},n.resolve=function(e){return e&&"object"==typeof e&&e.constructor===n?e:new n(function(n){n(e)})},n.reject=function(e){return new n(function(n,t){t(e)})},n.race=function(e){return new n(function(n,t){for(var o=0,r=e.length;r>o;o++)e[o].then(n,t)})},n._immediateFn="function"==typeof setImmediate&&function(e){setImmediate(e)}||function(e){u(e,0)},n._unhandledRejectionFn=function(e){void 0!==console&&console&&console.warn("Possible Unhandled Promise Rejection:",e)},n});
/*!
* sweetalert2 v7.22.0
* Released under the MIT License.
*/

(function (global, factory) {
	typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
	typeof define === 'function' && define.amd ? define(factory) :
	(global.Sweetalert2 = factory());
}(this, (function () { 'use strict';

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) {
  return typeof obj;
} : function (obj) {
  return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
};

var classCallCheck = function (instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};

var createClass = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }

  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();

var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

var get = function get(object, property, receiver) {
  if (object === null) object = Function.prototype;
  var desc = Object.getOwnPropertyDescriptor(object, property);

  if (desc === undefined) {
    var parent = Object.getPrototypeOf(object);

    if (parent === null) {
      return undefined;
    } else {
      return get(parent, property, receiver);
    }
  } else if ("value" in desc) {
    return desc.value;
  } else {
    var getter = desc.get;

    if (getter === undefined) {
      return undefined;
    }

    return getter.call(receiver);
  }
};

var inherits = function (subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function, not " + typeof superClass);
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      enumerable: false,
      writable: true,
      configurable: true
    }
  });
  if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass;
};

var possibleConstructorReturn = function (self, call) {
  if (!self) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return call && (typeof call === "object" || typeof call === "function") ? call : self;
};

var slicedToArray = function () {
  function sliceIterator(arr, i) {
    var _arr = [];
    var _n = true;
    var _d = false;
    var _e = undefined;

    try {
      for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
        _arr.push(_s.value);

        if (i && _arr.length === i) break;
      }
    } catch (err) {
      _d = true;
      _e = err;
    } finally {
      try {
        if (!_n && _i["return"]) _i["return"]();
      } finally {
        if (_d) throw _e;
      }
    }

    return _arr;
  }

  return function (arr, i) {
    if (Array.isArray(arr)) {
      return arr;
    } else if (Symbol.iterator in Object(arr)) {
      return sliceIterator(arr, i);
    } else {
      throw new TypeError("Invalid attempt to destructure non-iterable instance");
    }
  };
}();

var consolePrefix = 'SweetAlert2:';

/**
 * Filter the unique values into a new array
 * @param arr
 */
var uniqueArray = function uniqueArray(arr) {
  var result = [];
  for (var i = 0; i < arr.length; i++) {
    if (result.indexOf(arr[i]) === -1) {
      result.push(arr[i]);
    }
  }
  return result;
};

/**
 * Converts `inputOptions` into an array of `[value, label]`s
 * @param inputOptions
 */
var formatInputOptions = function formatInputOptions(inputOptions) {
  var result = [];
  if (typeof Map !== 'undefined' && inputOptions instanceof Map) {
    inputOptions.forEach(function (value, key) {
      result.push([key, value]);
    });
  } else {
    Object.keys(inputOptions).forEach(function (key) {
      result.push([key, inputOptions[key]]);
    });
  }
  return result;
};

/**
 * Standardise console warnings
 * @param message
 */
var warn = function warn(message) {
  console.warn(consolePrefix + ' ' + message);
};

/**
 * Standardise console errors
 * @param message
 */
var error = function error(message) {
  console.error(consolePrefix + ' ' + message);
};

/**
 * Private global state for `warnOnce`
 * @type {Array}
 * @private
 */
var previousWarnOnceMessages = [];

/**
 * Show a console warning, but only if it hasn't already been shown
 * @param message
 */
var warnOnce = function warnOnce(message) {
  if (!(previousWarnOnceMessages.indexOf(message) !== -1)) {
    previousWarnOnceMessages.push(message);
    warn(message);
  }
};

/**
 * If `arg` is a function, call it (with no arguments or context) and return the result.
 * Otherwise, just pass the value through
 * @param arg
 */
var callIfFunction = function callIfFunction(arg) {
  return typeof arg === 'function' ? arg() : arg;
};

var isThenable = function isThenable(arg) {
  return (typeof arg === 'undefined' ? 'undefined' : _typeof(arg)) === 'object' && typeof arg.then === 'function';
};

var DismissReason = Object.freeze({
  cancel: 'cancel',
  backdrop: 'overlay',
  close: 'close',
  esc: 'esc',
  timer: 'timer'
});

var version = "7.22.0";

var argsToParams = function argsToParams(args) {
  var params = {};
  switch (_typeof(args[0])) {
    case 'string':
      ['title', 'html', 'type'].forEach(function (name, index) {
        switch (_typeof(args[index])) {
          case 'string':
            params[name] = args[index];
            break;
          case 'undefined':
            break;
          default:
            error('Unexpected type of ' + name + '! Expected "string", got ' + _typeof(args[index]));
        }
      });
      break;

    case 'object':
      _extends(params, args[0]);
      break;

    default:
      error('Unexpected type of argument! Expected "string" or "object", got ' + _typeof(args[0]));
      return false;
  }
  return params;
};

/**
 * Adapt a legacy inputValidator for use with expectRejections=false
 */
var adaptInputValidator = function adaptInputValidator(legacyValidator) {
  return function adaptedInputValidator(inputValue, extraParams) {
    return legacyValidator.call(this, inputValue, extraParams).then(function () {
      return undefined;
    }, function (validationError) {
      return validationError;
    });
  };
};

var swalPrefix = 'swal2-';

var prefix = function prefix(items) {
  var result = {};
  for (var i in items) {
    result[items[i]] = swalPrefix + items[i];
  }
  return result;
};

var swalClasses = prefix(['container', 'shown', 'height-auto', 'iosfix', 'popup', 'modal', 'no-backdrop', 'toast', 'toast-shown', 'fade', 'show', 'hide', 'noanimation', 'close', 'title', 'header', 'content', 'actions', 'confirm', 'cancel', 'footer', 'icon', 'icon-text', 'image', 'input', 'has-input', 'file', 'range', 'select', 'radio', 'checkbox', 'textarea', 'inputerror', 'validationerror', 'progresssteps', 'activeprogressstep', 'progresscircle', 'progressline', 'loading', 'styled', 'top', 'top-start', 'top-end', 'top-left', 'top-right', 'center', 'center-start', 'center-end', 'center-left', 'center-right', 'bottom', 'bottom-start', 'bottom-end', 'bottom-left', 'bottom-right', 'grow-row', 'grow-column', 'grow-fullscreen']);

var iconTypes = prefix(['success', 'warning', 'info', 'question', 'error']);

// Remember state in cases where opening and handling a modal will fiddle with it.
var states = {
  previousBodyPadding: null
};

var hasClass = function hasClass(elem, className) {
  if (elem.classList) {
    return elem.classList.contains(className);
  }
  return false;
};

var focusInput = function focusInput(input) {
  input.focus();

  // place cursor at end of text in text input
  if (input.type !== 'file') {
    // http://stackoverflow.com/a/2345915/1331425
    var val = input.value;
    input.value = '';
    input.value = val;
  }
};

var addOrRemoveClass = function addOrRemoveClass(target, classList, add) {
  if (!target || !classList) {
    return;
  }
  if (typeof classList === 'string') {
    classList = classList.split(/\s+/).filter(Boolean);
  }
  classList.forEach(function (className) {
    if (target.forEach) {
      target.forEach(function (elem) {
        add ? elem.classList.add(className) : elem.classList.remove(className);
      });
    } else {
      add ? target.classList.add(className) : target.classList.remove(className);
    }
  });
};

var addClass = function addClass(target, classList) {
  addOrRemoveClass(target, classList, true);
};

var removeClass = function removeClass(target, classList) {
  addOrRemoveClass(target, classList, false);
};

var getChildByClass = function getChildByClass(elem, className) {
  for (var i = 0; i < elem.childNodes.length; i++) {
    if (hasClass(elem.childNodes[i], className)) {
      return elem.childNodes[i];
    }
  }
};

var show = function show(elem) {
  elem.style.opacity = '';
  elem.style.display = elem.id === swalClasses.content ? 'block' : 'flex';
};

var hide = function hide(elem) {
  elem.style.opacity = '';
  elem.style.display = 'none';
};

var empty = function empty(elem) {
  while (elem.firstChild) {
    elem.removeChild(elem.firstChild);
  }
};

// borrowed from jquery $(elem).is(':visible') implementation
var isVisible = function isVisible(elem) {
  return elem && (elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length);
};

var removeStyleProperty = function removeStyleProperty(elem, property) {
  if (elem.style.removeProperty) {
    elem.style.removeProperty(property);
  } else {
    elem.style.removeAttribute(property);
  }
};

var getContainer = function getContainer() {
  return document.body.querySelector('.' + swalClasses.container);
};

var elementByClass = function elementByClass(className) {
  var container = getContainer();
  return container ? container.querySelector('.' + className) : null;
};

var getPopup = function getPopup() {
  return elementByClass(swalClasses.popup);
};

var getIcons = function getIcons() {
  var popup = getPopup();
  return popup.querySelectorAll('.' + swalClasses.icon);
};

var getTitle = function getTitle() {
  return elementByClass(swalClasses.title);
};

var getContent = function getContent() {
  return elementByClass(swalClasses.content);
};

var getImage = function getImage() {
  return elementByClass(swalClasses.image);
};

var getProgressSteps = function getProgressSteps() {
  return elementByClass(swalClasses.progresssteps);
};

var getValidationError = function getValidationError() {
  return elementByClass(swalClasses.validationerror);
};

var getConfirmButton = function getConfirmButton() {
  return elementByClass(swalClasses.confirm);
};

var getCancelButton = function getCancelButton() {
  return elementByClass(swalClasses.cancel);
};

var getButtonsWrapper = function getButtonsWrapper() {
  warnOnce('swal.getButtonsWrapper() is deprecated and will be removed in the next major release, use swal.getActions() instead');
  return elementByClass(swalClasses.actions);
};

var getActions = function getActions() {
  return elementByClass(swalClasses.actions);
};

var getFooter = function getFooter() {
  return elementByClass(swalClasses.footer);
};

var getCloseButton = function getCloseButton() {
  return elementByClass(swalClasses.close);
};

var getFocusableElements = function getFocusableElements() {
  var focusableElementsWithTabindex = Array.prototype.slice.call(getPopup().querySelectorAll('[tabindex]:not([tabindex="-1"]):not([tabindex="0"])'))
  // sort according to tabindex
  .sort(function (a, b) {
    a = parseInt(a.getAttribute('tabindex'));
    b = parseInt(b.getAttribute('tabindex'));
    if (a > b) {
      return 1;
    } else if (a < b) {
      return -1;
    }
    return 0;
  });

  // https://github.com/jkup/focusable/blob/master/index.js
  var otherFocusableElements = Array.prototype.slice.call(getPopup().querySelectorAll('a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex="0"], [contenteditable], audio[controls], video[controls]'));

  return uniqueArray(focusableElementsWithTabindex.concat(otherFocusableElements));
};

var isModal = function isModal() {
  return !document.body.classList.contains(swalClasses['toast-shown']);
};

var isToast = function isToast() {
  return document.body.classList.contains(swalClasses['toast-shown']);
};

var isLoading = function isLoading() {
  return getPopup().hasAttribute('data-loading');
};

// Detect Node env
var isNodeEnv = function isNodeEnv() {
  return typeof window === 'undefined' || typeof document === 'undefined';
};

var sweetHTML = ('\n <div aria-labelledby="' + swalClasses.title + '" aria-describedby="' + swalClasses.content + '" class="' + swalClasses.popup + '" tabindex="-1">\n   <div class="' + swalClasses.header + '">\n     <ul class="' + swalClasses.progresssteps + '"></ul>\n     <div class="' + swalClasses.icon + ' ' + iconTypes.error + '">\n       <span class="swal2-x-mark"><span class="swal2-x-mark-line-left"></span><span class="swal2-x-mark-line-right"></span></span>\n     </div>\n     <div class="' + swalClasses.icon + ' ' + iconTypes.question + '">\n       <span class="' + swalClasses['icon-text'] + '">?</span>\n      </div>\n     <div class="' + swalClasses.icon + ' ' + iconTypes.warning + '">\n       <span class="' + swalClasses['icon-text'] + '">!</span>\n      </div>\n     <div class="' + swalClasses.icon + ' ' + iconTypes.info + '">\n       <span class="' + swalClasses['icon-text'] + '">i</span>\n      </div>\n     <div class="' + swalClasses.icon + ' ' + iconTypes.success + '">\n       <div class="swal2-success-circular-line-left"></div>\n       <span class="swal2-success-line-tip"></span> <span class="swal2-success-line-long"></span>\n       <div class="swal2-success-ring"></div> <div class="swal2-success-fix"></div>\n       <div class="swal2-success-circular-line-right"></div>\n     </div>\n     <img class="' + swalClasses.image + '" />\n     <h2 class="' + swalClasses.title + '" id="' + swalClasses.title + '"></h2>\n     <button type="button" class="' + swalClasses.close + '">\xD7</button>\n   </div>\n   <div class="' + swalClasses.content + '">\n     <div id="' + swalClasses.content + '"></div>\n     <input class="' + swalClasses.input + '" />\n     <input type="file" class="' + swalClasses.file + '" />\n     <div class="' + swalClasses.range + '">\n       <input type="range" />\n       <output></output>\n     </div>\n     <select class="' + swalClasses.select + '"></select>\n     <div class="' + swalClasses.radio + '"></div>\n     <label for="' + swalClasses.checkbox + '" class="' + swalClasses.checkbox + '">\n       <input type="checkbox" />\n     </label>\n     <textarea class="' + swalClasses.textarea + '"></textarea>\n     <div class="' + swalClasses.validationerror + '" id="' + swalClasses.validationerror + '"></div>\n   </div>\n   <div class="' + swalClasses.actions + '">\n     <button type="button" class="' + swalClasses.confirm + '">OK</button>\n     <button type="button" class="' + swalClasses.cancel + '">Cancel</button>\n   </div>\n   <div class="' + swalClasses.footer + '">\n   </div>\n </div>\n').replace(/(^|\n)\s*/g, '');

/*
 * Add modal + backdrop to DOM
 */
var init = function init(params) {
  // Clean up the old popup if it exists
  var c = getContainer();
  if (c) {
    c.parentNode.removeChild(c);
    removeClass([document.documentElement, document.body], [swalClasses['no-backdrop'], swalClasses['has-input'], swalClasses['toast-shown']]);
  }

  if (isNodeEnv()) {
    error('SweetAlert2 requires document to initialize');
    return;
  }

  var container = document.createElement('div');
  container.className = swalClasses.container;
  container.innerHTML = sweetHTML;

  var targetElement = typeof params.target === 'string' ? document.querySelector(params.target) : params.target;
  targetElement.appendChild(container);

  var popup = getPopup();
  var content = getContent();
  var input = getChildByClass(content, swalClasses.input);
  var file = getChildByClass(content, swalClasses.file);
  var range = content.querySelector('.' + swalClasses.range + ' input');
  var rangeOutput = content.querySelector('.' + swalClasses.range + ' output');
  var select = getChildByClass(content, swalClasses.select);
  var checkbox = content.querySelector('.' + swalClasses.checkbox + ' input');
  var textarea = getChildByClass(content, swalClasses.textarea);

  // a11y
  popup.setAttribute('role', params.toast ? 'alert' : 'dialog');
  popup.setAttribute('aria-live', params.toast ? 'polite' : 'assertive');
  if (!params.toast) {
    popup.setAttribute('aria-modal', 'true');
  }

  var oldInputVal = void 0; // IE11 workaround, see #1109 for details
  var resetValidationError = function resetValidationError(e) {
    if (Swal.isVisible() && oldInputVal !== e.target.value) {
      Swal.resetValidationError();
    }
    oldInputVal = e.target.value;
  };

  input.oninput = resetValidationError;
  file.onchange = resetValidationError;
  select.onchange = resetValidationError;
  checkbox.onchange = resetValidationError;
  textarea.oninput = resetValidationError;

  range.oninput = function (e) {
    resetValidationError(e);
    rangeOutput.value = range.value;
  };

  range.onchange = function (e) {
    resetValidationError(e);
    range.nextSibling.value = range.value;
  };

  return popup;
};

var parseHtmlToContainer = function parseHtmlToContainer(param, target) {
  if (!param) {
    return hide(target);
  }

  if ((typeof param === 'undefined' ? 'undefined' : _typeof(param)) === 'object') {
    target.innerHTML = '';
    if (0 in param) {
      for (var i = 0; i in param; i++) {
        target.appendChild(param[i].cloneNode(true));
      }
    } else {
      target.appendChild(param.cloneNode(true));
    }
  } else if (param) {
    target.innerHTML = param;
  } else {}
  show(target);
};

var animationEndEvent = function () {
  // Prevent run in Node env
  if (isNodeEnv()) {
    return false;
  }

  var testEl = document.createElement('div');
  var transEndEventNames = {
    'WebkitAnimation': 'webkitAnimationEnd',
    'OAnimation': 'oAnimationEnd oanimationend',
    'animation': 'animationend'
  };
  for (var i in transEndEventNames) {
    if (transEndEventNames.hasOwnProperty(i) && typeof testEl.style[i] !== 'undefined') {
      return transEndEventNames[i];
    }
  }

  return false;
}();

// Measure width of scrollbar
// https://github.com/twbs/bootstrap/blob/master/js/modal.js#L279-L286
var measureScrollbar = function measureScrollbar() {
  var supportsTouch = 'ontouchstart' in window || navigator.msMaxTouchPoints;
  if (supportsTouch) {
    return 0;
  }
  var scrollDiv = document.createElement('div');
  scrollDiv.style.width = '50px';
  scrollDiv.style.height = '50px';
  scrollDiv.style.overflow = 'scroll';
  document.body.appendChild(scrollDiv);
  var scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
  document.body.removeChild(scrollDiv);
  return scrollbarWidth;
};

var fixScrollbar = function fixScrollbar() {
  // for queues, do not do this more than once
  if (states.previousBodyPadding !== null) {
    return;
  }
  // if the body has overflow
  if (document.body.scrollHeight > window.innerHeight) {
    // add padding so the content doesn't shift after removal of scrollbar
    states.previousBodyPadding = parseInt(window.getComputedStyle(document.body).getPropertyValue('padding-right'));
    document.body.style.paddingRight = states.previousBodyPadding + measureScrollbar() + 'px';
  }
};

var undoScrollbar = function undoScrollbar() {
  if (states.previousBodyPadding !== null) {
    document.body.style.paddingRight = states.previousBodyPadding;
    states.previousBodyPadding = null;
  }
};

// Fix iOS scrolling http://stackoverflow.com/q/39626302/1331425
var iOSfix = function iOSfix() {
  var iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  if (iOS && !hasClass(document.body, swalClasses.iosfix)) {
    var offset = document.body.scrollTop;
    document.body.style.top = offset * -1 + 'px';
    addClass(document.body, swalClasses.iosfix);
  }
};

var undoIOSfix = function undoIOSfix() {
  if (hasClass(document.body, swalClasses.iosfix)) {
    var offset = parseInt(document.body.style.top, 10);
    removeClass(document.body, swalClasses.iosfix);
    document.body.style.top = '';
    document.body.scrollTop = offset * -1;
  }
};

var globalState = {};

// Restore previous active (focused) element
var restoreActiveElement = function restoreActiveElement() {
  if (globalState.previousActiveElement && globalState.previousActiveElement.focus) {
    var previousActiveElement = globalState.previousActiveElement;
    globalState.previousActiveElement = null;
    var x = window.scrollX;
    var y = window.scrollY;
    setTimeout(function () {
      previousActiveElement.focus && previousActiveElement.focus();
    }, 100); // issues/900
    if (typeof x !== 'undefined' && typeof y !== 'undefined') {
      // IE doesn't have scrollX/scrollY support
      window.scrollTo(x, y);
    }
  }
};

/*
 * Global function to close sweetAlert
 */
var close = function close(onClose, onAfterClose) {
  var container = getContainer();
  var popup = getPopup();
  if (!popup) {
    return;
  }

  if (onClose !== null && typeof onClose === 'function') {
    onClose(popup);
  }

  removeClass(popup, swalClasses.show);
  addClass(popup, swalClasses.hide);

  var removePopupAndResetState = function removePopupAndResetState() {
    if (!isToast()) {
      restoreActiveElement();
      window.removeEventListener('keydown', globalState.keydownHandler, { capture: true });
      globalState.keydownHandlerAdded = false;
    }

    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
    removeClass([document.documentElement, document.body], [swalClasses.shown, swalClasses['height-auto'], swalClasses['no-backdrop'], swalClasses['has-input'], swalClasses['toast-shown']]);

    if (isModal()) {
      undoScrollbar();
      undoIOSfix();
    }

    if (onAfterClose !== null && typeof onAfterClose === 'function') {
      setTimeout(function () {
        onAfterClose();
      });
    }
  };

  // If animation is supported, animate
  if (animationEndEvent && !hasClass(popup, swalClasses.noanimation)) {
    popup.addEventListener(animationEndEvent, function swalCloseEventFinished() {
      popup.removeEventListener(animationEndEvent, swalCloseEventFinished);
      if (hasClass(popup, swalClasses.hide)) {
        removePopupAndResetState();
      }
    });
  } else {
    // Otherwise, remove immediately
    removePopupAndResetState();
  }
};

/*
 * Global function to determine if swal2 popup is shown
 */
var isVisible$1 = function isVisible() {
  return !!getPopup();
};

/*
 * Global function to click 'Confirm' button
 */
var clickConfirm = function clickConfirm() {
  return getConfirmButton().click();
};

/*
 * Global function to click 'Cancel' button
 */
var clickCancel = function clickCancel() {
  return getCancelButton().click();
};

function fire() {
  var Swal = this;

  for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  return new (Function.prototype.bind.apply(Swal, [null].concat(args)))();
}

/**
 * Extends a Swal class making it able to be instantiated without the `new` keyword (and thus without `Swal.fire`)
 * @param ParentSwal
 * @returns {NoNewKeywordSwal}
 */
function withNoNewKeyword(ParentSwal) {
  var NoNewKeywordSwal = function NoNewKeywordSwal() {
    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (!(this instanceof NoNewKeywordSwal)) {
      return new (Function.prototype.bind.apply(NoNewKeywordSwal, [null].concat(args)))();
    }
    Object.getPrototypeOf(NoNewKeywordSwal).apply(this, args);
  };
  NoNewKeywordSwal.prototype = _extends(Object.create(ParentSwal.prototype), { constructor: NoNewKeywordSwal });

  if (typeof Object.setPrototypeOf === 'function') {
    Object.setPrototypeOf(NoNewKeywordSwal, ParentSwal);
  } else {
    // Android 4.4
    // eslint-disable-next-line
    NoNewKeywordSwal.__proto__ = ParentSwal;
  }
  return NoNewKeywordSwal;
}

var defaultParams = {
  title: '',
  titleText: '',
  text: '',
  html: '',
  footer: '',
  type: null,
  toast: false,
  customClass: '',
  target: 'body',
  backdrop: true,
  animation: true,
  heightAuto: true,
  allowOutsideClick: true,
  allowEscapeKey: true,
  allowEnterKey: true,
  showConfirmButton: true,
  showCancelButton: false,
  preConfirm: null,
  confirmButtonText: 'OK',
  confirmButtonAriaLabel: '',
  confirmButtonColor: null,
  confirmButtonClass: null,
  cancelButtonText: 'Cancel',
  cancelButtonAriaLabel: '',
  cancelButtonColor: null,
  cancelButtonClass: null,
  buttonsStyling: true,
  reverseButtons: true,
  focusConfirm: true,
  focusCancel: false,
  showCloseButton: false,
  closeButtonAriaLabel: 'Close this dialog',
  showLoaderOnConfirm: false,
  imageUrl: null,
  imageWidth: null,
  imageHeight: null,
  imageAlt: '',
  imageClass: null,
  timer: null,
  width: null,
  padding: null,
  background: null,
  input: null,
  inputPlaceholder: '',
  inputValue: '',
  inputOptions: {},
  inputAutoTrim: true,
  inputClass: null,
  inputAttributes: {},
  inputValidator: null,
  grow: false,
  position: 'center',
  progressSteps: [],
  currentProgressStep: null,
  progressStepsDistance: null,
  onBeforeOpen: null,
  onAfterClose: null,
  onOpen: null,
  onClose: null,
  useRejections: false,
  expectRejections: false,
  modalIdentifier: ''
};

var deprecatedParams = ['useRejections', 'expectRejections'];

/**
 * Is valid parameter
 * @param {String} paramName
 */
var isValidParameter = function isValidParameter(paramName) {
  return defaultParams.hasOwnProperty(paramName) || paramName === 'extraParams';
};

/**
 * Is deprecated parameter
 * @param {String} paramName
 */
var isDeprecatedParameter = function isDeprecatedParameter(paramName) {
  return deprecatedParams.indexOf(paramName) !== -1;
};

/**
 * Show relevant warnings for given params
 *
 * @param params
 */
var showWarningsForParams = function showWarningsForParams(params) {
  for (var param in params) {
    if (!isValidParameter(param)) {
      warn('Unknown parameter "' + param + '"');
    }
    if (isDeprecatedParameter(param)) {
      warnOnce('The parameter "' + param + '" is deprecated and will be removed in the next major release.');
    }
  }
};

var deprecationWarning = '"setDefaults" & "resetDefaults" methods are deprecated in favor of "mixin" method and will be removed in the next major release. For new projects, use "mixin". For past projects already using "setDefaults", support will be provided through an additional package.';
var defaults$1 = {};

function withGlobalDefaults(ParentSwal) {
  var SwalWithGlobalDefaults = function (_ParentSwal) {
    inherits(SwalWithGlobalDefaults, _ParentSwal);

    function SwalWithGlobalDefaults() {
      classCallCheck(this, SwalWithGlobalDefaults);
      return possibleConstructorReturn(this, (SwalWithGlobalDefaults.__proto__ || Object.getPrototypeOf(SwalWithGlobalDefaults)).apply(this, arguments));
    }

    createClass(SwalWithGlobalDefaults, [{
      key: '_main',
      value: function _main(params) {
        return get(SwalWithGlobalDefaults.prototype.__proto__ || Object.getPrototypeOf(SwalWithGlobalDefaults.prototype), '_main', this).call(this, _extends({}, defaults$1, params));
      }
    }], [{
      key: 'setDefaults',
      value: function setDefaults(params) {
        warnOnce(deprecationWarning);
        if (!params || (typeof params === 'undefined' ? 'undefined' : _typeof(params)) !== 'object') {
          throw new TypeError('SweetAlert2: The argument for setDefaults() is required and has to be a object');
        }
        showWarningsForParams(params);
        // assign valid params from `params` to `defaults`
        Object.keys(params).forEach(function (param) {
          if (ParentSwal.isValidParameter(param)) {
            defaults$1[param] = params[param];
          }
        });
      }
    }, {
      key: 'resetDefaults',
      value: function resetDefaults() {
        warnOnce(deprecationWarning);
        defaults$1 = {};
      }
    }]);
    return SwalWithGlobalDefaults;
  }(ParentSwal);

  // Set default params if `window._swalDefaults` is an object


  if (typeof window !== 'undefined' && _typeof(window._swalDefaults) === 'object') {
    SwalWithGlobalDefaults.setDefaults(window._swalDefaults);
  }

  return SwalWithGlobalDefaults;
}

/**
 * Returns an extended version of `Swal` containing `params` as defaults.
 * Useful for reusing Swal configuration.
 *
 * For example:
 *
 * Before:
 * const textPromptOptions = { input: 'text', showCancelButton: true }
 * const {value: firstName} = await Swal({ ...textPromptOptions, title: 'What is your first name?' })
 * const {value: lastName} = await Swal({ ...textPromptOptions, title: 'What is your last name?' })
 *
 * After:
 * const TextPrompt = Swal.mixin({ input: 'text', showCancelButton: true })
 * const {value: firstName} = await TextPrompt('What is your first name?')
 * const {value: lastName} = await TextPrompt('What is your last name?')
 *
 * @param mixinParams
 */
function mixin(mixinParams) {
  var Swal = this;
  return withNoNewKeyword(function (_Swal) {
    inherits(MixinSwal, _Swal);

    function MixinSwal() {
      classCallCheck(this, MixinSwal);
      return possibleConstructorReturn(this, (MixinSwal.__proto__ || Object.getPrototypeOf(MixinSwal)).apply(this, arguments));
    }

    createClass(MixinSwal, [{
      key: '_main',
      value: function _main(params) {
        return get(MixinSwal.prototype.__proto__ || Object.getPrototypeOf(MixinSwal.prototype), '_main', this).call(this, _extends({}, mixinParams, params));
      }
    }]);
    return MixinSwal;
  }(Swal));
}

// private global state for the queue feature
var currentSteps = [];

/*
 * Global function for chaining sweetAlert popups
 */
var queue = function queue(steps) {
  var swal = this;
  currentSteps = steps;
  var resetQueue = function resetQueue() {
    currentSteps = [];
    document.body.removeAttribute('data-swal2-queue-step');
  };
  var queueResult = [];
  return new Promise(function (resolve, reject) {
    (function step(i, callback) {
      if (i < currentSteps.length) {
        document.body.setAttribute('data-swal2-queue-step', i);

        swal(currentSteps[i]).then(function (result) {
          if (typeof result.value !== 'undefined') {
            queueResult.push(result.value);
            step(i + 1, callback);
          } else {
            resetQueue();
            resolve({ dismiss: result.dismiss });
          }
        });
      } else {
        resetQueue();
        resolve({ value: queueResult });
      }
    })(0);
  });
};

/*
 * Global function for getting the index of current popup in queue
 */
var getQueueStep = function getQueueStep() {
  return document.body.getAttribute('data-swal2-queue-step');
};

/*
 * Global function for inserting a popup to the queue
 */
var insertQueueStep = function insertQueueStep(step, index) {
  if (index && index < currentSteps.length) {
    return currentSteps.splice(index, 0, step);
  }
  return currentSteps.push(step);
};

/*
 * Global function for deleting a popup from the queue
 */
var deleteQueueStep = function deleteQueueStep(index) {
  if (typeof currentSteps[index] !== 'undefined') {
    currentSteps.splice(index, 1);
  }
};

/**
 * Show spinner instead of Confirm button and disable Cancel button
 */
var showLoading = function showLoading() {
  var popup = getPopup();
  if (!popup) {
    Swal('');
  }
  popup = getPopup();
  var actions = getActions();
  var confirmButton = getConfirmButton();
  var cancelButton = getCancelButton();

  show(actions);
  show(confirmButton);
  addClass([popup, actions], swalClasses.loading);
  confirmButton.disabled = true;
  cancelButton.disabled = true;

  popup.setAttribute('data-loading', true);
  popup.setAttribute('aria-busy', true);
  popup.focus();
};

/**
 * Show spinner instead of Confirm button and disable Cancel button
 */
var getTimerLeft = function getTimerLeft() {
  return globalState.timeout && globalState.timeout.getTimerLeft();
};



var staticMethods = Object.freeze({
	isValidParameter: isValidParameter,
	isDeprecatedParameter: isDeprecatedParameter,
	argsToParams: argsToParams,
	adaptInputValidator: adaptInputValidator,
	close: close,
	closePopup: close,
	closeModal: close,
	closeToast: close,
	isVisible: isVisible$1,
	clickConfirm: clickConfirm,
	clickCancel: clickCancel,
	getPopup: getPopup,
	getTitle: getTitle,
	getContent: getContent,
	getImage: getImage,
	getButtonsWrapper: getButtonsWrapper,
	getActions: getActions,
	getConfirmButton: getConfirmButton,
	getCancelButton: getCancelButton,
	getFooter: getFooter,
	isLoading: isLoading,
	fire: fire,
	mixin: mixin,
	queue: queue,
	getQueueStep: getQueueStep,
	insertQueueStep: insertQueueStep,
	deleteQueueStep: deleteQueueStep,
	showLoading: showLoading,
	enableLoading: showLoading,
	getTimerLeft: getTimerLeft
});

/**
 * This module containts `WeakMap`s for each effectively-"private  property" that a `swal` has.
 * For example, to set the private property "foo" of `this` to "bar", you can `privateProps.foo.set(this, 'bar')`
 * This is the approach that Babel will probably take to implement private methods/fields
 *   https://github.com/tc39/proposal-private-methods
 *   https://github.com/babel/babel/pull/7555
 * Once we have the changes from that PR in Babel, and our core class fits reasonable in *one module*
 *   then we can use that language feature.
 */

// WeakMap polyfill, needed for Android 4.4
// Related issue: https://github.com/sweetalert2/sweetalert2/issues/1071
if (typeof window !== 'undefined' && typeof window.WeakMap !== 'function') {
  // https://github.com/Riim/symbol-polyfill/blob/master/index.js
  var idCounter = 0;
  window.Symbol = function _Symbol(key) {
    return '__' + key + '_' + Math.floor(Math.random() * 1e9) + '_' + ++idCounter + '__';
  };
  Symbol.iterator = Symbol('Symbol.iterator');

  // http://webreflection.blogspot.fi/2015/04/a-weakmap-polyfill-in-20-lines-of-code.html
  window.WeakMap = function (s, dP, hOP) {
    function WeakMap() {
      dP(this, s, { value: Symbol('WeakMap') });
    }
    WeakMap.prototype = {
      'delete': function del(o) {
        delete o[this[s]];
      },
      get: function get(o) {
        return o[this[s]];
      },
      has: function has(o) {
        return hOP.call(o, this[s]);
      },
      set: function set(o, v) {
        dP(o, this[s], { configurable: true, value: v });
      }
    };
    return WeakMap;
  }(Symbol('WeakMap'), Object.defineProperty, {}.hasOwnProperty);
}

var privateProps = {
  promise: new WeakMap(),
  innerParams: new WeakMap(),
  domCache: new WeakMap()
};

/**
 * Show spinner instead of Confirm button and disable Cancel button
 */
function hideLoading() {
  var innerParams = privateProps.innerParams.get(this);
  var domCache = privateProps.domCache.get(this);
  if (!innerParams.showConfirmButton) {
    hide(domCache.confirmButton);
    if (!innerParams.showCancelButton) {
      hide(domCache.actions);
    }
  }
  removeClass([domCache.popup, domCache.actions], swalClasses.loading);
  domCache.popup.removeAttribute('aria-busy');
  domCache.popup.removeAttribute('data-loading');
  domCache.confirmButton.disabled = false;
  domCache.cancelButton.disabled = false;
}

// Get input element by specified type or, if type isn't specified, by params.input
function getInput(inputType) {
  var innerParams = privateProps.innerParams.get(this);
  var domCache = privateProps.domCache.get(this);
  inputType = inputType || innerParams.input;
  if (!inputType) {
    return null;
  }
  switch (inputType) {
    case 'select':
    case 'textarea':
    case 'file':
      return getChildByClass(domCache.content, swalClasses[inputType]);
    case 'checkbox':
      return domCache.popup.querySelector('.' + swalClasses.checkbox + ' input');
    case 'radio':
      return domCache.popup.querySelector('.' + swalClasses.radio + ' input:checked') || domCache.popup.querySelector('.' + swalClasses.radio + ' input:first-child');
    case 'range':
      return domCache.popup.querySelector('.' + swalClasses.range + ' input');
    default:
      return getChildByClass(domCache.content, swalClasses.input);
  }
}

function enableButtons() {
  var domCache = privateProps.domCache.get(this);
  domCache.confirmButton.disabled = false;
  domCache.cancelButton.disabled = false;
}

function disableButtons() {
  var domCache = privateProps.domCache.get(this);
  domCache.confirmButton.disabled = true;
  domCache.cancelButton.disabled = true;
}

function enableConfirmButton() {
  var domCache = privateProps.domCache.get(this);
  domCache.confirmButton.disabled = false;
}

function disableConfirmButton() {
  var domCache = privateProps.domCache.get(this);
  domCache.confirmButton.disabled = true;
}

function enableInput() {
  var input = this.getInput();
  if (!input) {
    return false;
  }
  if (input.type === 'radio') {
    var radiosContainer = input.parentNode.parentNode;
    var radios = radiosContainer.querySelectorAll('input');
    for (var i = 0; i < radios.length; i++) {
      radios[i].disabled = false;
    }
  } else {
    input.disabled = false;
  }
}

function disableInput() {
  var input = this.getInput();
  if (!input) {
    return false;
  }
  if (input && input.type === 'radio') {
    var radiosContainer = input.parentNode.parentNode;
    var radios = radiosContainer.querySelectorAll('input');
    for (var i = 0; i < radios.length; i++) {
      radios[i].disabled = true;
    }
  } else {
    input.disabled = true;
  }
}

// Show block with validation error
function showValidationError(error) {
  var domCache = privateProps.domCache.get(this);
  domCache.validationError.innerHTML = error;
  var popupComputedStyle = window.getComputedStyle(domCache.popup);
  domCache.validationError.style.marginLeft = '-' + popupComputedStyle.getPropertyValue('padding-left');
  domCache.validationError.style.marginRight = '-' + popupComputedStyle.getPropertyValue('padding-right');
  show(domCache.validationError);

  var input = this.getInput();
  if (input) {
    input.setAttribute('aria-invalid', true);
    input.setAttribute('aria-describedBy', swalClasses.validationerror);
    focusInput(input);
    addClass(input, swalClasses.inputerror);
  }
}

// Hide block with validation error
function resetValidationError() {
  var domCache = privateProps.domCache.get(this);
  if (domCache.validationError) {
    hide(domCache.validationError);
  }

  var input = this.getInput();
  if (input) {
    input.removeAttribute('aria-invalid');
    input.removeAttribute('aria-describedBy');
    removeClass(input, swalClasses.inputerror);
  }
}

var Timer = function Timer(callback, delay) {
  classCallCheck(this, Timer);

  var id, started, running;
  var remaining = delay;
  this.start = function () {
    running = true;
    started = new Date();
    id = setTimeout(callback, remaining);
  };
  this.stop = function () {
    running = false;
    clearTimeout(id);
    remaining -= new Date() - started;
  };
  this.getTimerLeft = function () {
    if (running) {
      this.stop();
      this.start();
    }
    return remaining;
  };
  this.getStateRunning = function () {
    return running;
  };
  this.start();
};

var defaultInputValidators = {
  email: function email(string, extraParams) {
    return (/^[a-zA-Z0-9.+_-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9-]{2,24}$/.test(string) ? Promise.resolve() : Promise.reject(extraParams && extraParams.validationMessage ? extraParams.validationMessage : 'Invalid email address')
    );
  },
  url: function url(string, extraParams) {
    // taken from https://stackoverflow.com/a/3809435/1331425
    return (/^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$/.test(string) ? Promise.resolve() : Promise.reject(extraParams && extraParams.validationMessage ? extraParams.validationMessage : 'Invalid URL')
    );
  }
};

/**
 * Set type, text and actions on popup
 *
 * @param params
 * @returns {boolean}
 */
function setParameters(params) {
  // Use default `inputValidator` for supported input types if not provided
  if (!params.inputValidator) {
    Object.keys(defaultInputValidators).forEach(function (key) {
      if (params.input === key) {
        params.inputValidator = params.expectRejections ? defaultInputValidators[key] : Swal.adaptInputValidator(defaultInputValidators[key]);
      }
    });
  }

  // Determine if the custom target element is valid
  if (!params.target || typeof params.target === 'string' && !document.querySelector(params.target) || typeof params.target !== 'string' && !params.target.appendChild) {
    warn('Target parameter is not valid, defaulting to "body"');
    params.target = 'body';
  }

  var popup = void 0;
  var oldPopup = getPopup();
  var targetElement = typeof params.target === 'string' ? document.querySelector(params.target) : params.target;
  // If the model target has changed, refresh the popup
  if (oldPopup && targetElement && oldPopup.parentNode !== targetElement.parentNode) {
    popup = init(params);
  } else {
    popup = oldPopup || init(params);
  }

  // Set popup width
  if (params.width) {
    popup.style.width = typeof params.width === 'number' ? params.width + 'px' : params.width;
  }

  // Set popup padding
  if (params.padding) {
    popup.style.padding = typeof params.padding === 'number' ? params.padding + 'px' : params.padding;
  }

  // Set popup background
  if (params.background) {
    popup.style.background = params.background;
  }
  var popupBackgroundColor = window.getComputedStyle(popup).getPropertyValue('background-color');
  var successIconParts = popup.querySelectorAll('[class^=swal2-success-circular-line], .swal2-success-fix');
  for (var i = 0; i < successIconParts.length; i++) {
    successIconParts[i].style.backgroundColor = popupBackgroundColor;
  }

  var container = getContainer();
  var title = getTitle();
  var content = getContent().querySelector('#' + swalClasses.content);
  var actions = getActions();
  var confirmButton = getConfirmButton();
  var cancelButton = getCancelButton();
  var closeButton = getCloseButton();
  var footer = getFooter();

  // Title
  if (params.titleText) {
    title.innerText = params.titleText;
  } else if (params.title) {
    title.innerHTML = params.title.split('\n').join('<br />');
  }

  if (typeof params.backdrop === 'string') {
    getContainer().style.background = params.backdrop;
  } else if (!params.backdrop) {
    addClass([document.documentElement, document.body], swalClasses['no-backdrop']);
  }

  // Content as HTML
  if (params.html) {
    parseHtmlToContainer(params.html, content);

    // Content as plain text
  } else if (params.text) {
    content.textContent = params.text;
    show(content);
  } else {
    hide(content);
  }

  // Position
  if (params.position in swalClasses) {
    addClass(container, swalClasses[params.position]);
  } else {
    warn('The "position" parameter is not valid, defaulting to "center"');
    addClass(container, swalClasses.center);
  }

  // Grow
  if (params.grow && typeof params.grow === 'string') {
    var growClass = 'grow-' + params.grow;
    if (growClass in swalClasses) {
      addClass(container, swalClasses[growClass]);
    }
  }

  // Animation
  if (typeof params.animation === 'function') {
    params.animation = params.animation.call();
  }

  // Close button
  if (params.showCloseButton) {
    closeButton.setAttribute('aria-label', params.closeButtonAriaLabel);
    show(closeButton);
  } else {
    hide(closeButton);
  }

  // Default Class
  popup.className = swalClasses.popup;
  if (params.toast) {
    addClass([document.documentElement, document.body], swalClasses['toast-shown']);
    addClass(popup, swalClasses.toast);
  } else {
    addClass(popup, swalClasses.modal);
  }

  // Custom Class
  if (params.customClass) {
    addClass(popup, params.customClass);
  }

  // Progress steps
  var progressStepsContainer = getProgressSteps();
  var currentProgressStep = parseInt(params.currentProgressStep === null ? Swal.getQueueStep() : params.currentProgressStep, 10);
  if (params.progressSteps && params.progressSteps.length) {
    show(progressStepsContainer);
    empty(progressStepsContainer);
    if (currentProgressStep >= params.progressSteps.length) {
      warn('Invalid currentProgressStep parameter, it should be less than progressSteps.length ' + '(currentProgressStep like JS arrays starts from 0)');
    }
    params.progressSteps.forEach(function (step, index) {
      var circle = document.createElement('li');
      addClass(circle, swalClasses.progresscircle);
      circle.innerHTML = step;
      if (index === currentProgressStep) {
        addClass(circle, swalClasses.activeprogressstep);
      }
      progressStepsContainer.appendChild(circle);
      if (index !== params.progressSteps.length - 1) {
        var line = document.createElement('li');
        addClass(line, swalClasses.progressline);
        if (params.progressStepsDistance) {
          line.style.width = params.progressStepsDistance;
        }
        progressStepsContainer.appendChild(line);
      }
    });
  } else {
    hide(progressStepsContainer);
  }

  // Icon
  var icons = getIcons();
  for (var _i = 0; _i < icons.length; _i++) {
    hide(icons[_i]);
  }
  if (params.type) {
    var validType = false;
    for (var iconType in iconTypes) {
      if (params.type === iconType) {
        validType = true;
        break;
      }
    }
    if (!validType) {
      error('Unknown alert type: ' + params.type);
      return false;
    }
    var icon = popup.querySelector('.' + swalClasses.icon + '.' + iconTypes[params.type]);
    show(icon);

    // Animate icon
    if (params.animation) {
      addClass(icon, 'swal2-animate-' + params.type + '-icon');
    }
  }

  if(params.modalIdentifier){
    popup = getPopup()
    popup.setAttribute('data-modal-dentifier', params.modalIdentifier);
  }
  // Custom image
  var image = getImage();
  if (params.imageUrl) {
    image.setAttribute('src', params.imageUrl);
    image.setAttribute('alt', params.imageAlt);
    show(image);

    if (params.imageWidth) {
      image.setAttribute('width', params.imageWidth);
    } else {
      image.removeAttribute('width');
    }

    if (params.imageHeight) {
      image.setAttribute('height', params.imageHeight);
    } else {
      image.removeAttribute('height');
    }

    image.className = swalClasses.image;
    if (params.imageClass) {
      addClass(image, params.imageClass);
    }
  } else {
    hide(image);
  }

  // Cancel button
  if (params.showCancelButton) {
    cancelButton.style.display = 'inline-block';
  } else {
    hide(cancelButton);
  }

  // Confirm button
  if (params.showConfirmButton) {
    removeStyleProperty(confirmButton, 'display');
  } else {
    hide(confirmButton);
  }

  // Actions (buttons) wrapper
  if (!params.showConfirmButton && !params.showCancelButton) {
    hide(actions);
  } else {
    show(actions);
  }

  // Edit text on confirm and cancel buttons
  confirmButton.innerHTML = params.confirmButtonText;
  cancelButton.innerHTML = params.cancelButtonText;

  // ARIA labels for confirm and cancel buttons
  confirmButton.setAttribute('aria-label', params.confirmButtonAriaLabel);
  cancelButton.setAttribute('aria-label', params.cancelButtonAriaLabel);

  // Add buttons custom classes
  confirmButton.className = swalClasses.confirm;
  addClass(confirmButton, params.confirmButtonClass);
  cancelButton.className = swalClasses.cancel;
  addClass(cancelButton, params.cancelButtonClass);

  // Buttons styling
  if (params.buttonsStyling) {
    addClass([confirmButton, cancelButton], swalClasses.styled);

    // Buttons background colors
    if (params.confirmButtonColor) {
      confirmButton.style.backgroundColor = params.confirmButtonColor;
    }
    if (params.cancelButtonColor) {
      cancelButton.style.backgroundColor = params.cancelButtonColor;
    }

    // Loading state
    var confirmButtonBackgroundColor = window.getComputedStyle(confirmButton).getPropertyValue('background-color');
    confirmButton.style.borderLeftColor = confirmButtonBackgroundColor;
    confirmButton.style.borderRightColor = confirmButtonBackgroundColor;
  } else {
    removeClass([confirmButton, cancelButton], swalClasses.styled);

    confirmButton.style.backgroundColor = confirmButton.style.borderLeftColor = confirmButton.style.borderRightColor = '';
    cancelButton.style.backgroundColor = cancelButton.style.borderLeftColor = cancelButton.style.borderRightColor = '';
  }

  // Footer
  parseHtmlToContainer(params.footer, footer);

  // CSS animation
  if (params.animation === true) {
    removeClass(popup, swalClasses.noanimation);
  } else {
    addClass(popup, swalClasses.noanimation);
  }

  // showLoaderOnConfirm && preConfirm
  if (params.showLoaderOnConfirm && !params.preConfirm) {
    warn('showLoaderOnConfirm is set to true, but preConfirm is not defined.\n' + 'showLoaderOnConfirm should be used together with preConfirm, see usage example:\n' + 'https://sweetalert2.github.io/#ajax-request');
  }
}

/**
 * Open popup, add necessary classes and styles, fix scrollbar
 *
 * @param {Array} params
 */
var openPopup = function openPopup(params) {
  var container = getContainer();
  var popup = getPopup();

  if (params.onBeforeOpen !== null && typeof params.onBeforeOpen === 'function') {
    params.onBeforeOpen(popup);
  }

  if (params.animation) {
    addClass(popup, swalClasses.show);
    addClass(container, swalClasses.fade);
    removeClass(popup, swalClasses.hide);
  } else {
    removeClass(popup, swalClasses.fade);
  }
  show(popup);

  // scrolling is 'hidden' until animation is done, after that 'auto'
  container.style.overflowY = 'hidden';
  if (animationEndEvent && !hasClass(popup, swalClasses.noanimation)) {
    popup.addEventListener(animationEndEvent, function swalCloseEventFinished() {
      popup.removeEventListener(animationEndEvent, swalCloseEventFinished);
      container.style.overflowY = 'auto';
    });
  } else {
    container.style.overflowY = 'auto';
  }

  addClass([document.documentElement, document.body, container], swalClasses.shown);
  if (params.heightAuto && params.backdrop && !params.toast) {
    addClass([document.documentElement, document.body], swalClasses['height-auto']);
  }

  if (isModal()) {
    fixScrollbar();
    iOSfix();
  }
  if (!globalState.previousActiveElement) {
    globalState.previousActiveElement = document.activeElement;
  }
  if (params.onOpen !== null && typeof params.onOpen === 'function') {
    setTimeout(function () {
      params.onOpen(popup);
    });
  }
};

function _main(userParams) {
  var _this = this;

  showWarningsForParams(userParams);

  var innerParams = _extends({}, defaultParams, userParams);
  setParameters(innerParams);
  Object.freeze(innerParams);
  privateProps.innerParams.set(this, innerParams);

  // clear the previous timer
  if (globalState.timeout) {
    globalState.timeout.stop();
    delete globalState.timeout;
  }

  var domCache = {
    popup: getPopup(),
    container: getContainer(),
    content: getContent(),
    actions: getActions(),
    confirmButton: getConfirmButton(),
    cancelButton: getCancelButton(),
    closeButton: getCloseButton(),
    validationError: getValidationError(),
    progressSteps: getProgressSteps()
  };
  privateProps.domCache.set(this, domCache);

  var constructor = this.constructor;

  return new Promise(function (resolve, reject) {
    // functions to handle all resolving/rejecting/settling
    var succeedWith = function succeedWith(value) {
      constructor.closePopup(innerParams.onClose, innerParams.onAfterClose); // TODO: make closePopup an *instance* method
      if (innerParams.useRejections) {
        resolve(value);
      } else {
        resolve({ value: value });
      }
    };
    var dismissWith = function dismissWith(dismiss) {
      constructor.closePopup(innerParams.onClose, innerParams.onAfterClose);
      if (innerParams.useRejections) {
        reject(dismiss);
      } else {
        resolve({ dismiss: dismiss });
      }
    };
    var errorWith = function errorWith(error$$1) {
      constructor.closePopup(innerParams.onClose, innerParams.onAfterClose);
      reject(error$$1);
    };

    // Close on timer
    if (innerParams.timer) {
      globalState.timeout = new Timer(function () {
        dismissWith('timer');
        delete globalState.timeout;
      }, innerParams.timer);
    }

    // Get the value of the popup input
    var getInputValue = function getInputValue() {
      var input = _this.getInput();
      if (!input) {
        return null;
      }
      switch (innerParams.input) {
        case 'checkbox':
          return input.checked ? 1 : 0;
        case 'radio':
          return input.checked ? input.value : null;
        case 'file':
          return input.files.length ? input.files[0] : null;
        default:
          return innerParams.inputAutoTrim ? input.value.trim() : input.value;
      }
    };

    // input autofocus
    if (innerParams.input) {
      setTimeout(function () {
        var input = _this.getInput();
        if (input) {
          focusInput(input);
        }
      }, 0);
    }

    var confirm = function confirm(value) {
      if (innerParams.showLoaderOnConfirm) {
        constructor.showLoading(); // TODO: make showLoading an *instance* method
      }

      if (innerParams.preConfirm) {
        _this.resetValidationError();
        var preConfirmPromise = Promise.resolve().then(function () {
          return innerParams.preConfirm(value, innerParams.extraParams);
        });
        if (innerParams.expectRejections) {
          preConfirmPromise.then(function (preConfirmValue) {
            return succeedWith(preConfirmValue || value);
          }, function (validationError) {
            _this.hideLoading();
            if (validationError) {
              _this.showValidationError(validationError);
            }
          });
        } else {
          preConfirmPromise.then(function (preConfirmValue) {
            if (isVisible(domCache.validationError) || preConfirmValue === false) {
              _this.hideLoading();
            } else {
              succeedWith(preConfirmValue || value);
            }
          }, function (error$$1) {
            return errorWith(error$$1);
          });
        }
      } else {
        succeedWith(value);
      }
    };

    // Mouse interactions
    var onButtonEvent = function onButtonEvent(event) {
      var e = event || window.event;
      var target = e.target || e.srcElement;
      var confirmButton = domCache.confirmButton,
          cancelButton = domCache.cancelButton;

      var targetedConfirm = confirmButton && (confirmButton === target || confirmButton.contains(target));
      var targetedCancel = cancelButton && (cancelButton === target || cancelButton.contains(target));

      switch (e.type) {
        case 'click':
          // Clicked 'confirm'
          if (targetedConfirm && constructor.isVisible()) {
            _this.disableButtons();
            if (innerParams.input) {
              var inputValue = getInputValue();

              if (innerParams.inputValidator) {
                _this.disableInput();
                var validationPromise = Promise.resolve().then(function () {
                  return innerParams.inputValidator(inputValue, innerParams.extraParams);
                });
                if (innerParams.expectRejections) {
                  validationPromise.then(function () {
                    _this.enableButtons();
                    _this.enableInput();
                    confirm(inputValue);
                  }, function (validationError) {
                    _this.enableButtons();
                    _this.enableInput();
                    if (validationError) {
                      _this.showValidationError(validationError);
                    }
                  });
                } else {
                  validationPromise.then(function (validationError) {
                    _this.enableButtons();
                    _this.enableInput();
                    if (validationError) {
                      _this.showValidationError(validationError);
                    } else {
                      confirm(inputValue);
                    }
                  }, function (error$$1) {
                    return errorWith(error$$1);
                  });
                }
              } else {
                confirm(inputValue);
              }
            } else {
              confirm(true);
            }

            // Clicked 'cancel'
          } else if (targetedCancel && constructor.isVisible()) {
            _this.disableButtons();
            dismissWith(constructor.DismissReason.cancel);
          }
          break;
        default:
      }
    };

    var buttons = domCache.popup.querySelectorAll('button');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].onclick = onButtonEvent;
      buttons[i].onmouseover = onButtonEvent;
      buttons[i].onmouseout = onButtonEvent;
      buttons[i].onmousedown = onButtonEvent;
    }

    // Closing popup by close button
    domCache.closeButton.onclick = function () {
      dismissWith(constructor.DismissReason.close);
    };

    if (innerParams.toast) {
      // Closing popup by internal click
      domCache.popup.onclick = function (e) {
        if (innerParams.showConfirmButton || innerParams.showCancelButton || innerParams.showCloseButton || innerParams.input) {
          return;
        }
        constructor.closePopup(innerParams.onClose, innerParams.onAfterClose);
        dismissWith(constructor.DismissReason.close);
      };
    } else {
      var ignoreOutsideClick = false;

      // Ignore click events that had mousedown on the popup but mouseup on the container
      // This can happen when the user drags a slider
      domCache.popup.onmousedown = function () {
        domCache.container.onmouseup = function (e) {
          domCache.container.onmouseup = undefined;
          // We only check if the mouseup target is the container because usually it doesn't
          // have any other direct children aside of the popup
          if (e.target === domCache.container) {
            ignoreOutsideClick = true;
          }
        };
      };

      // Ignore click events that had mousedown on the container but mouseup on the popup
      domCache.container.onmousedown = function () {
        domCache.popup.onmouseup = function (e) {
          domCache.popup.onmouseup = undefined;
          // We also need to check if the mouseup target is a child of the popup
          if (e.target === domCache.popup || domCache.popup.contains(e.target)) {
            ignoreOutsideClick = true;
          }
        };
      };

      domCache.container.onclick = function (e) {
        if (ignoreOutsideClick) {
          ignoreOutsideClick = false;
          return;
        }
        if (e.target !== domCache.container) {
          return;
        }
        if (callIfFunction(innerParams.allowOutsideClick)) {
          dismissWith(constructor.DismissReason.backdrop);
        }
      };
    }

    // Reverse buttons (Confirm on the right side)
    if (innerParams.reverseButtons) {
      domCache.confirmButton.parentNode.insertBefore(domCache.cancelButton, domCache.confirmButton);
    } else {
      domCache.confirmButton.parentNode.insertBefore(domCache.confirmButton, domCache.cancelButton);
    }

    // Focus handling
    var setFocus = function setFocus(index, increment) {
      var focusableElements = getFocusableElements(innerParams.focusCancel);
      // search for visible elements and select the next possible match
      for (var _i = 0; _i < focusableElements.length; _i++) {
        index = index + increment;

        // rollover to first item
        if (index === focusableElements.length) {
          index = 0;

          // go to last item
        } else if (index === -1) {
          index = focusableElements.length - 1;
        }

        // determine if element is visible
        var el = focusableElements[index];
        if (isVisible(el)) {
          return el.focus();
        }
      }
      // no visible focusable elements, focus the popup
      domCache.popup.focus();
    };

    var keydownHandler = function keydownHandler(e, innerParams) {
      e.stopPropagation();

      var arrowKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Left', 'Right', 'Up', 'Down' // IE11
      ];

      if (e.key === 'Enter' && !e.isComposing) {
        if (e.target && _this.getInput() && e.target.outerHTML === _this.getInput().outerHTML) {
          if (['textarea', 'file'].indexOf(innerParams.input) !== -1) {
            return; // do not submit
          }

          constructor.clickConfirm();
          e.preventDefault();
        }

        // TAB
      } else if (e.key === 'Tab') {
        var targetElement = e.target || e.srcElement;

        var focusableElements = getFocusableElements(innerParams.focusCancel);
        var btnIndex = -1; // Find the button - note, this is a nodelist, not an array.
        for (var _i2 = 0; _i2 < focusableElements.length; _i2++) {
          if (targetElement === focusableElements[_i2]) {
            btnIndex = _i2;
            break;
          }
        }

        if (!e.shiftKey) {
          // Cycle to the next button
          setFocus(btnIndex, 1);
        } else {
          // Cycle to the prev button
          setFocus(btnIndex, -1);
        }
        e.stopPropagation();
        e.preventDefault();

        // ARROWS - switch focus between buttons
      } else if (arrowKeys.indexOf(e.key) !== -1) {
        // focus Cancel button if Confirm button is currently focused
        if (document.activeElement === domCache.confirmButton && isVisible(domCache.cancelButton)) {
          domCache.cancelButton.focus();
          // and vice versa
        } else if (document.activeElement === domCache.cancelButton && isVisible(domCache.confirmButton)) {
          domCache.confirmButton.focus();
        }

        // ESC
      } else if ((e.key === 'Escape' || e.key === 'Esc') && callIfFunction(innerParams.allowEscapeKey) === true) {
        dismissWith(constructor.DismissReason.esc);
      }
    };

    if (globalState.keydownHandlerAdded) {
      window.removeEventListener('keydown', globalState.keydownHandler, { capture: true });
      globalState.keydownHandlerAdded = false;
    }

    if (!innerParams.toast) {
      globalState.keydownHandler = function (e) {
        return keydownHandler(e, innerParams);
      };
      window.addEventListener('keydown', globalState.keydownHandler, { capture: true });
      globalState.keydownHandlerAdded = true;
    }

    _this.enableButtons();
    _this.hideLoading();
    _this.resetValidationError();

    if (innerParams.input) {
      addClass(document.body, swalClasses['has-input']);
    }

    // inputs
    var inputTypes = ['input', 'file', 'range', 'select', 'radio', 'checkbox', 'textarea'];
    var input = void 0;
    for (var _i3 = 0; _i3 < inputTypes.length; _i3++) {
      var inputClass = swalClasses[inputTypes[_i3]];
      var inputContainer = getChildByClass(domCache.content, inputClass);
      input = _this.getInput(inputTypes[_i3]);

      // set attributes
      if (input) {
        for (var j in input.attributes) {
          if (input.attributes.hasOwnProperty(j)) {
            var attrName = input.attributes[j].name;
            if (attrName !== 'type' && attrName !== 'value') {
              input.removeAttribute(attrName);
            }
          }
        }
        for (var attr in innerParams.inputAttributes) {
          input.setAttribute(attr, innerParams.inputAttributes[attr]);
        }
      }

      // set class
      inputContainer.className = inputClass;
      if (innerParams.inputClass) {
        addClass(inputContainer, innerParams.inputClass);
      }

      hide(inputContainer);
    }

    var populateInputOptions = void 0;
    switch (innerParams.input) {
      case 'text':
      case 'email':
      case 'password':
      case 'number':
      case 'tel':
      case 'url':
        input = getChildByClass(domCache.content, swalClasses.input);
        input.value = innerParams.inputValue;
        input.placeholder = innerParams.inputPlaceholder;
        input.type = innerParams.input;
        show(input);
        break;
      case 'file':
        input = getChildByClass(domCache.content, swalClasses.file);
        input.placeholder = innerParams.inputPlaceholder;
        input.type = innerParams.input;
        show(input);
        break;
      case 'range':
        var range = getChildByClass(domCache.content, swalClasses.range);
        var rangeInput = range.querySelector('input');
        var rangeOutput = range.querySelector('output');
        rangeInput.value = innerParams.inputValue;
        rangeInput.type = innerParams.input;
        rangeOutput.value = innerParams.inputValue;
        show(range);
        break;
      case 'select':
        var select = getChildByClass(domCache.content, swalClasses.select);
        select.innerHTML = '';
        if (innerParams.inputPlaceholder) {
          var placeholder = document.createElement('option');
          placeholder.innerHTML = innerParams.inputPlaceholder;
          placeholder.value = '';
          placeholder.disabled = true;
          placeholder.selected = true;
          select.appendChild(placeholder);
        }
        populateInputOptions = function populateInputOptions(inputOptions) {
          inputOptions.forEach(function (_ref) {
            var _ref2 = slicedToArray(_ref, 2),
                optionValue = _ref2[0],
                optionLabel = _ref2[1];

            var option = document.createElement('option');
            option.value = optionValue;
            option.innerHTML = optionLabel;
            if (innerParams.inputValue.toString() === optionValue.toString()) {
              option.selected = true;
            }
            select.appendChild(option);
          });
          show(select);
          select.focus();
        };
        break;
      case 'radio':
        var radio = getChildByClass(domCache.content, swalClasses.radio);
        radio.innerHTML = '';
        populateInputOptions = function populateInputOptions(inputOptions) {
          inputOptions.forEach(function (_ref3) {
            var _ref4 = slicedToArray(_ref3, 2),
                radioValue = _ref4[0],
                radioLabel = _ref4[1];

            var radioInput = document.createElement('input');
            var radioLabelElement = document.createElement('label');
            radioInput.type = 'radio';
            radioInput.name = swalClasses.radio;
            radioInput.value = radioValue;
            if (innerParams.inputValue.toString() === radioValue.toString()) {
              radioInput.checked = true;
            }
            radioLabelElement.innerHTML = radioLabel;
            radioLabelElement.insertBefore(radioInput, radioLabelElement.firstChild);
            radio.appendChild(radioLabelElement);
          });
          show(radio);
          var radios = radio.querySelectorAll('input');
          if (radios.length) {
            radios[0].focus();
          }
        };
        break;
      case 'checkbox':
        var checkbox = getChildByClass(domCache.content, swalClasses.checkbox);
        var checkboxInput = _this.getInput('checkbox');
        checkboxInput.type = 'checkbox';
        checkboxInput.value = 1;
        checkboxInput.id = swalClasses.checkbox;
        checkboxInput.checked = Boolean(innerParams.inputValue);
        var label = checkbox.getElementsByTagName('span');
        if (label.length) {
          checkbox.removeChild(label[0]);
        }
        label = document.createElement('span');
        label.innerHTML = innerParams.inputPlaceholder;
        checkbox.appendChild(label);
        show(checkbox);
        break;
      case 'textarea':
        var textarea = getChildByClass(domCache.content, swalClasses.textarea);
        textarea.value = innerParams.inputValue;
        textarea.placeholder = innerParams.inputPlaceholder;
        show(textarea);
        break;
      case null:
        break;
      default:
        error('Unexpected type of input! Expected "text", "email", "password", "number", "tel", "select", "radio", "checkbox", "textarea", "file" or "url", got "' + innerParams.input + '"');
        break;
    }

    if (innerParams.input === 'select' || innerParams.input === 'radio') {
      var processInputOptions = function processInputOptions(inputOptions) {
        return populateInputOptions(formatInputOptions(inputOptions));
      };
      if (isThenable(innerParams.inputOptions)) {
        constructor.showLoading();
        innerParams.inputOptions.then(function (inputOptions) {
          _this.hideLoading();
          processInputOptions(inputOptions);
        });
      } else if (_typeof(innerParams.inputOptions) === 'object') {
        processInputOptions(innerParams.inputOptions);
      } else {
        error('Unexpected type of inputOptions! Expected object, Map or Promise, got ' + _typeof(innerParams.inputOptions));
      }
    } else if (['text', 'email', 'number', 'tel', 'textarea'].indexOf(innerParams.input) !== -1 && isThenable(innerParams.inputValue)) {
      constructor.showLoading();
      hide(input);
      innerParams.inputValue.then(function (inputValue) {
        input.value = innerParams.input === 'number' ? parseFloat(inputValue) || 0 : inputValue + '';
        show(input);
        _this.hideLoading();
      }).catch(function (err) {
        error('Error in inputValue promise: ' + err);
        input.value = '';
        show(input);
        _this.hideLoading();
      });
    }

    openPopup(innerParams);

    if (!innerParams.toast) {
      if (!callIfFunction(innerParams.allowEnterKey)) {
        if (document.activeElement) {
          document.activeElement.blur();
        }
      } else if (innerParams.focusCancel && isVisible(domCache.cancelButton)) {
        domCache.cancelButton.focus();
      } else if (innerParams.focusConfirm && isVisible(domCache.confirmButton)) {
        domCache.confirmButton.focus();
      } else {
        setFocus(-1, 1);
      }
    }

    // fix scroll
    domCache.container.scrollTop = 0;
  });
}



var instanceMethods = Object.freeze({
	hideLoading: hideLoading,
	disableLoading: hideLoading,
	getInput: getInput,
	enableButtons: enableButtons,
	disableButtons: disableButtons,
	enableConfirmButton: enableConfirmButton,
	disableConfirmButton: disableConfirmButton,
	enableInput: enableInput,
	disableInput: disableInput,
	showValidationError: showValidationError,
	resetValidationError: resetValidationError,
	_main: _main
});

var currentInstance = void 0;

// SweetAlert constructor
function SweetAlert() {
  // Prevent run in Node env
  if (typeof window === 'undefined') {
    return;
  }

  // Check for the existence of Promise
  if (typeof Promise === 'undefined') {
    error('This package requires a Promise library, please include a shim to enable it in this browser (See: https://github.com/sweetalert2/sweetalert2/wiki/Migration-from-SweetAlert-to-SweetAlert2#1-ie-support)');
  }

  for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  if (typeof args[0] === 'undefined') {
    error('SweetAlert2 expects at least 1 attribute!');
    return false;
  }

  currentInstance = this;

  var outerParams = Object.freeze(this.constructor.argsToParams(args));

  Object.defineProperties(this, {
    params: {
      value: outerParams,
      writable: false,
      enumerable: true
    }
  });

  var promise = this._main(this.params);
  privateProps.promise.set(this, promise);
}

// `catch` cannot be the name of a module export, so we define our thenable methods here instead
SweetAlert.prototype.then = function (onFulfilled, onRejected) {
  var promise = privateProps.promise.get(this);
  return promise.then(onFulfilled, onRejected);
};
SweetAlert.prototype.catch = function (onRejected) {
  var promise = privateProps.promise.get(this);
  return promise.catch(onRejected);
};
SweetAlert.prototype.finally = function (onFinally) {
  var promise = privateProps.promise.get(this);
  return promise.finally(onFinally);
};

// Assign instance methods from src/instanceMethods/*.js to prototype
_extends(SweetAlert.prototype, instanceMethods);

// Assign static methods from src/staticMethods/*.js to constructor
_extends(SweetAlert, staticMethods);

// Proxy to instance methods to constructor, for now, for backwards compatibility
Object.keys(instanceMethods).forEach(function (key) {
  SweetAlert[key] = function () {
    if (currentInstance) {
      var _currentInstance;

      return (_currentInstance = currentInstance)[key].apply(_currentInstance, arguments);
    }
  };
});

SweetAlert.DismissReason = DismissReason;

SweetAlert.noop = function () {};

SweetAlert.version = version;

var Swal = withNoNewKeyword(withGlobalDefaults(SweetAlert));
Swal.default = Swal;

return Swal;

})));
if (typeof window !== 'undefined' && window.Sweetalert2){  window.swal = window.sweetAlert = window.Swal = window.SweetAlert = window.Sweetalert2}
;
/*! DataTables 1.10.10
 * ©2008-2015 SpryMedia Ltd - datatables.net/license
 */

/**
 * @summary     DataTables
 * @description Paginate, search and order HTML tables
 * @version     1.10.10
 * @file        jquery.dataTables.js
 * @author      SpryMedia Ltd (www.sprymedia.co.uk)
 * @contact     www.sprymedia.co.uk/contact
 * @copyright   Copyright 2008-2015 SpryMedia Ltd.
 *
 * This source file is free software, available under the following license:
 *   MIT license - http://datatables.net/license
 *
 * This source file is distributed in the hope that it will be useful, but
 * WITHOUT ANY WARRANTY; without even the implied warranty of MERCHANTABILITY
 * or FITNESS FOR A PARTICULAR PURPOSE. See the license files for details.
 *
 * For details please refer to: http://www.datatables.net
 */

/*jslint evil: true, undef: true, browser: true */
/*globals $,require,jQuery,define,_selector_run,_selector_opts,_selector_first,_selector_row_indexes,_ext,_Api,_api_register,_api_registerPlural,_re_new_lines,_re_html,_re_formatted_numeric,_re_escape_regex,_empty,_intVal,_numToDecimal,_isNumber,_isHtml,_htmlNumeric,_pluck,_pluck_order,_range,_stripHtml,_unique,_fnBuildAjax,_fnAjaxUpdate,_fnAjaxParameters,_fnAjaxUpdateDraw,_fnAjaxDataSrc,_fnAddColumn,_fnColumnOptions,_fnAdjustColumnSizing,_fnVisibleToColumnIndex,_fnColumnIndexToVisible,_fnVisbleColumns,_fnGetColumns,_fnColumnTypes,_fnApplyColumnDefs,_fnHungarianMap,_fnCamelToHungarian,_fnLanguageCompat,_fnBrowserDetect,_fnAddData,_fnAddTr,_fnNodeToDataIndex,_fnNodeToColumnIndex,_fnGetCellData,_fnSetCellData,_fnSplitObjNotation,_fnGetObjectDataFn,_fnSetObjectDataFn,_fnGetDataMaster,_fnClearTable,_fnDeleteIndex,_fnInvalidate,_fnGetRowElements,_fnCreateTr,_fnBuildHead,_fnDrawHead,_fnDraw,_fnReDraw,_fnAddOptionsHtml,_fnDetectHeader,_fnGetUniqueThs,_fnFeatureHtmlFilter,_fnFilterComplete,_fnFilterCustom,_fnFilterColumn,_fnFilter,_fnFilterCreateSearch,_fnEscapeRegex,_fnFilterData,_fnFeatureHtmlInfo,_fnUpdateInfo,_fnInfoMacros,_fnInitialise,_fnInitComplete,_fnLengthChange,_fnFeatureHtmlLength,_fnFeatureHtmlPaginate,_fnPageChange,_fnFeatureHtmlProcessing,_fnProcessingDisplay,_fnFeatureHtmlTable,_fnScrollDraw,_fnApplyToChildren,_fnCalculateColumnWidths,_fnThrottle,_fnConvertToWidth,_fnGetWidestNode,_fnGetMaxLenString,_fnStringToCss,_fnSortFlatten,_fnSort,_fnSortAria,_fnSortListener,_fnSortAttachListener,_fnSortingClasses,_fnSortData,_fnSaveState,_fnLoadState,_fnSettingsFromNode,_fnLog,_fnMap,_fnBindAction,_fnCallbackReg,_fnCallbackFire,_fnLengthOverflow,_fnRenderer,_fnDataSource,_fnRowAttributes*/


(function( factory ) {
	"use strict";

	if ( typeof define === 'function' && define.amd ) {
		// AMD
		define( ['jquery'], function ( $ ) {
			return factory( $, window, document );
		} );
	}
	else if ( typeof exports === 'object' ) {
		// CommonJS
		module.exports = function (root, $) {
			if ( ! root ) {
				// CommonJS environments without a window global must pass a
				// root. This will give an error otherwise
				root = window;
			}

			if ( ! $ ) {
				$ = typeof window !== 'undefined' ? // jQuery's factory checks for a global window
					require('jquery') :
					require('jquery')( root );
			}

			return factory( $, root, root.document );
		};
	}
	else {
		// Browser
		factory( jQuery, window, document );
	}
}
(function( $, window, document, undefined ) {
	"use strict";

	/**
	 * DataTables is a plug-in for the jQuery Javascript library. It is a highly
	 * flexible tool, based upon the foundations of progressive enhancement,
	 * which will add advanced interaction controls to any HTML table. For a
	 * full list of features please refer to
	 * [DataTables.net](href="http://datatables.net).
	 *
	 * Note that the `DataTable` object is not a global variable but is aliased
	 * to `jQuery.fn.DataTable` and `jQuery.fn.dataTable` through which it may
	 * be  accessed.
	 *
	 *  @class
	 *  @param {object} [init={}] Configuration object for DataTables. Options
	 *    are defined by {@link DataTable.defaults}
	 *  @requires jQuery 1.7+
	 *
	 *  @example
	 *    // Basic initialisation
	 *    $(document).ready( function {
	 *      $('#example').dataTable();
	 *    } );
	 *
	 *  @example
	 *    // Initialisation with configuration options - in this case, disable
	 *    // pagination and sorting.
	 *    $(document).ready( function {
	 *      $('#example').dataTable( {
	 *        "paginate": false,
	 *        "sort": false
	 *      } );
	 *    } );
	 */
	var DataTable;

	
	/*
	 * It is useful to have variables which are scoped locally so only the
	 * DataTables functions can access them and they don't leak into global space.
	 * At the same time these functions are often useful over multiple files in the
	 * core and API, so we list, or at least document, all variables which are used
	 * by DataTables as private variables here. This also ensures that there is no
	 * clashing of variable names and that they can easily referenced for reuse.
	 */
	
	
	// Defined else where
	//  _selector_run
	//  _selector_opts
	//  _selector_first
	//  _selector_row_indexes
	
	var _ext; // DataTable.ext
	var _Api; // DataTable.Api
	var _api_register; // DataTable.Api.register
	var _api_registerPlural; // DataTable.Api.registerPlural
	
	var _re_dic = {};
	var _re_new_lines = /[\r\n]/g;
	var _re_html = /<.*?>/g;
	var _re_date_start = /^[\w\+\-]/;
	var _re_date_end = /[\w\+\-]$/;
	
	// Escape regular expression special characters
	var _re_escape_regex = new RegExp( '(\\' + [ '/', '.', '*', '+', '?', '|', '(', ')', '[', ']', '{', '}', '\\', '$', '^', '-' ].join('|\\') + ')', 'g' );
	
	// http://en.wikipedia.org/wiki/Foreign_exchange_market
	// - \u20BD - Russian ruble.
	// - \u20a9 - South Korean Won
	// - \u20BA - Turkish Lira
	// - \u20B9 - Indian Rupee
	// - R - Brazil (R$) and South Africa
	// - fr - Swiss Franc
	// - kr - Swedish krona, Norwegian krone and Danish krone
	// - \u2009 is thin space and \u202F is narrow no-break space, both used in many
	//   standards as thousands separators.
	var _re_formatted_numeric = /[',$£€¥%\u2009\u202F\u20BD\u20a9\u20BArfk]/gi;
	
	
	var _empty = function ( d ) {
		return !d || d === true || d === '-' ? true : false;
	};
	
	
	var _intVal = function ( s ) {
		var integer = parseInt( s, 10 );
		return !isNaN(integer) && isFinite(s) ? integer : null;
	};
	
	// Convert from a formatted number with characters other than `.` as the
	// decimal place, to a Javascript number
	var _numToDecimal = function ( num, decimalPoint ) {
		// Cache created regular expressions for speed as this function is called often
		if ( ! _re_dic[ decimalPoint ] ) {
			_re_dic[ decimalPoint ] = new RegExp( _fnEscapeRegex( decimalPoint ), 'g' );
		}
		return typeof num === 'string' && decimalPoint !== '.' ?
			num.replace( /\./g, '' ).replace( _re_dic[ decimalPoint ], '.' ) :
			num;
	};
	
	
	var _isNumber = function ( d, decimalPoint, formatted ) {
		var strType = typeof d === 'string';
	
		// If empty return immediately so there must be a number if it is a
		// formatted string (this stops the string "k", or "kr", etc being detected
		// as a formatted number for currency
		if ( _empty( d ) ) {
			return true;
		}
	
		if ( decimalPoint && strType ) {
			d = _numToDecimal( d, decimalPoint );
		}
	
		if ( formatted && strType ) {
			d = d.replace( _re_formatted_numeric, '' );
		}
	
		return !isNaN( parseFloat(d) ) && isFinite( d );
	};
	
	
	// A string without HTML in it can be considered to be HTML still
	var _isHtml = function ( d ) {
		return _empty( d ) || typeof d === 'string';
	};
	
	
	var _htmlNumeric = function ( d, decimalPoint, formatted ) {
		if ( _empty( d ) ) {
			return true;
		}
	
		var html = _isHtml( d );
		return ! html ?
			null :
			_isNumber( _stripHtml( d ), decimalPoint, formatted ) ?
				true :
				null;
	};
	
	
	var _pluck = function ( a, prop, prop2 ) {
		var out = [];
		var i=0, ien=a.length;
	
		// Could have the test in the loop for slightly smaller code, but speed
		// is essential here
		if ( prop2 !== undefined ) {
			for ( ; i<ien ; i++ ) {
				if ( a[i] && a[i][ prop ] ) {
					out.push( a[i][ prop ][ prop2 ] );
				}
			}
		}
		else {
			for ( ; i<ien ; i++ ) {
				if ( a[i] ) {
					out.push( a[i][ prop ] );
				}
			}
		}
	
		return out;
	};
	
	
	// Basically the same as _pluck, but rather than looping over `a` we use `order`
	// as the indexes to pick from `a`
	var _pluck_order = function ( a, order, prop, prop2 )
	{
		var out = [];
		var i=0, ien=order.length;
	
		// Could have the test in the loop for slightly smaller code, but speed
		// is essential here
		if ( prop2 !== undefined ) {
			for ( ; i<ien ; i++ ) {
				if ( a[ order[i] ][ prop ] ) {
					out.push( a[ order[i] ][ prop ][ prop2 ] );
				}
			}
		}
		else {
			for ( ; i<ien ; i++ ) {
				out.push( a[ order[i] ][ prop ] );
			}
		}
	
		return out;
	};
	
	
	var _range = function ( len, start )
	{
		var out = [];
		var end;
	
		if ( start === undefined ) {
			start = 0;
			end = len;
		}
		else {
			end = start;
			start = len;
		}
	
		for ( var i=start ; i<end ; i++ ) {
			out.push( i );
		}
	
		return out;
	};
	
	
	var _removeEmpty = function ( a )
	{
		var out = [];
	
		for ( var i=0, ien=a.length ; i<ien ; i++ ) {
			if ( a[i] ) { // careful - will remove all falsy values!
				out.push( a[i] );
			}
		}
	
		return out;
	};
	
	
	var _stripHtml = function ( d ) {
		return d.replace( _re_html, '' );
	};
	
	
	/**
	 * Find the unique elements in a source array.
	 *
	 * @param  {array} src Source array
	 * @return {array} Array of unique items
	 * @ignore
	 */
	var _unique = function ( src )
	{
		// A faster unique method is to use object keys to identify used values,
		// but this doesn't work with arrays or objects, which we must also
		// consider. See jsperf.com/compare-array-unique-versions/4 for more
		// information.
		var
			out = [],
			val,
			i, ien=src.length,
			j, k=0;
	
		again: for ( i=0 ; i<ien ; i++ ) {
			val = src[i];
	
			for ( j=0 ; j<k ; j++ ) {
				if ( out[j] === val ) {
					continue again;
				}
			}
	
			out.push( val );
			k++;
		}
	
		return out;
	};
	
	
	
	/**
	 * Create a mapping object that allows camel case parameters to be looked up
	 * for their Hungarian counterparts. The mapping is stored in a private
	 * parameter called `_hungarianMap` which can be accessed on the source object.
	 *  @param {object} o
	 *  @memberof DataTable#oApi
	 */
	function _fnHungarianMap ( o )
	{
		var
			hungarian = 'a aa ai ao as b fn i m o s ',
			match,
			newKey,
			map = {};
	
		$.each( o, function (key, val) {
			match = key.match(/^([^A-Z]+?)([A-Z])/);
	
			if ( match && hungarian.indexOf(match[1]+' ') !== -1 )
			{
				newKey = key.replace( match[0], match[2].toLowerCase() );
				map[ newKey ] = key;
	
				if ( match[1] === 'o' )
				{
					_fnHungarianMap( o[key] );
				}
			}
		} );
	
		o._hungarianMap = map;
	}
	
	
	/**
	 * Convert from camel case parameters to Hungarian, based on a Hungarian map
	 * created by _fnHungarianMap.
	 *  @param {object} src The model object which holds all parameters that can be
	 *    mapped.
	 *  @param {object} user The object to convert from camel case to Hungarian.
	 *  @param {boolean} force When set to `true`, properties which already have a
	 *    Hungarian value in the `user` object will be overwritten. Otherwise they
	 *    won't be.
	 *  @memberof DataTable#oApi
	 */
	function _fnCamelToHungarian ( src, user, force )
	{
		if ( ! src._hungarianMap ) {
			_fnHungarianMap( src );
		}
	
		var hungarianKey;
	
		$.each( user, function (key, val) {
			hungarianKey = src._hungarianMap[ key ];
	
			if ( hungarianKey !== undefined && (force || user[hungarianKey] === undefined) )
			{
				// For objects, we need to buzz down into the object to copy parameters
				if ( hungarianKey.charAt(0) === 'o' )
				{
					// Copy the camelCase options over to the hungarian
					if ( ! user[ hungarianKey ] ) {
						user[ hungarianKey ] = {};
					}
					$.extend( true, user[hungarianKey], user[key] );
	
					_fnCamelToHungarian( src[hungarianKey], user[hungarianKey], force );
				}
				else {
					user[hungarianKey] = user[ key ];
				}
			}
		} );
	}
	
	
	/**
	 * Language compatibility - when certain options are given, and others aren't, we
	 * need to duplicate the values over, in order to provide backwards compatibility
	 * with older language files.
	 *  @param {object} oSettings dataTables settings object
	 *  @memberof DataTable#oApi
	 */
	function _fnLanguageCompat( lang )
	{
		var defaults = DataTable.defaults.oLanguage;
		var zeroRecords = lang.sZeroRecords;
	
		/* Backwards compatibility - if there is no sEmptyTable given, then use the same as
		 * sZeroRecords - assuming that is given.
		 */
		if ( ! lang.sEmptyTable && zeroRecords &&
			defaults.sEmptyTable === "No data available in table" )
		{
			_fnMap( lang, lang, 'sZeroRecords', 'sEmptyTable' );
		}
	
		/* Likewise with loading records */
		if ( ! lang.sLoadingRecords && zeroRecords &&
			defaults.sLoadingRecords === "Loading..." )
		{
			_fnMap( lang, lang, 'sZeroRecords', 'sLoadingRecords' );
		}
	
		// Old parameter name of the thousands separator mapped onto the new
		if ( lang.sInfoThousands ) {
			lang.sThousands = lang.sInfoThousands;
		}
	
		var decimal = lang.sDecimal;
		if ( decimal ) {
			_addNumericSort( decimal );
		}
	}
	
	
	/**
	 * Map one parameter onto another
	 *  @param {object} o Object to map
	 *  @param {*} knew The new parameter name
	 *  @param {*} old The old parameter name
	 */
	var _fnCompatMap = function ( o, knew, old ) {
		if ( o[ knew ] !== undefined ) {
			o[ old ] = o[ knew ];
		}
	};
	
	
	/**
	 * Provide backwards compatibility for the main DT options. Note that the new
	 * options are mapped onto the old parameters, so this is an external interface
	 * change only.
	 *  @param {object} init Object to map
	 */
	function _fnCompatOpts ( init )
	{
		_fnCompatMap( init, 'ordering',      'bSort' );
		_fnCompatMap( init, 'orderMulti',    'bSortMulti' );
		_fnCompatMap( init, 'orderClasses',  'bSortClasses' );
		_fnCompatMap( init, 'orderCellsTop', 'bSortCellsTop' );
		_fnCompatMap( init, 'order',         'aaSorting' );
		_fnCompatMap( init, 'orderFixed',    'aaSortingFixed' );
		_fnCompatMap( init, 'paging',        'bPaginate' );
		_fnCompatMap( init, 'pagingType',    'sPaginationType' );
		_fnCompatMap( init, 'pageLength',    'iDisplayLength' );
		_fnCompatMap( init, 'searching',     'bFilter' );
	
		// Boolean initialisation of x-scrolling
		if ( typeof init.sScrollX === 'boolean' ) {
			init.sScrollX = init.sScrollX ? '100%' : '';
		}
		if ( typeof init.scrollX === 'boolean' ) {
			init.scrollX = init.scrollX ? '100%' : '';
		}
	
		// Column search objects are in an array, so it needs to be converted
		// element by element
		var searchCols = init.aoSearchCols;
	
		if ( searchCols ) {
			for ( var i=0, ien=searchCols.length ; i<ien ; i++ ) {
				if ( searchCols[i] ) {
					_fnCamelToHungarian( DataTable.models.oSearch, searchCols[i] );
				}
			}
		}
	}
	
	
	/**
	 * Provide backwards compatibility for column options. Note that the new options
	 * are mapped onto the old parameters, so this is an external interface change
	 * only.
	 *  @param {object} init Object to map
	 */
	function _fnCompatCols ( init )
	{
		_fnCompatMap( init, 'orderable',     'bSortable' );
		_fnCompatMap( init, 'orderData',     'aDataSort' );
		_fnCompatMap( init, 'orderSequence', 'asSorting' );
		_fnCompatMap( init, 'orderDataType', 'sortDataType' );
	
		// orderData can be given as an integer
		var dataSort = init.aDataSort;
		if ( dataSort && ! $.isArray( dataSort ) ) {
			init.aDataSort = [ dataSort ];
		}
	}
	
	
	/**
	 * Browser feature detection for capabilities, quirks
	 *  @param {object} settings dataTables settings object
	 *  @memberof DataTable#oApi
	 */
	function _fnBrowserDetect( settings )
	{
		// We don't need to do this every time DataTables is constructed, the values
		// calculated are specific to the browser and OS configuration which we
		// don't expect to change between initialisations
		if ( ! DataTable.__browser ) {
			var browser = {};
			DataTable.__browser = browser;
	
			// Scrolling feature / quirks detection
			var n = $('<div/>')
				.css( {
					position: 'fixed',
					top: 0,
					left: 0,
					height: 1,
					width: 1,
					overflow: 'hidden'
				} )
				.append(
					$('<div/>')
						.css( {
							position: 'absolute',
							top: 1,
							left: 1,
							width: 100,
							overflow: 'scroll'
						} )
						.append(
							$('<div/>')
								.css( {
									width: '100%',
									height: 10
								} )
						)
				)
				.appendTo( 'body' );
	
			var outer = n.children();
			var inner = outer.children();
	
			// Numbers below, in order, are:
			// inner.offsetWidth, inner.clientWidth, outer.offsetWidth, outer.clientWidth
			//
			// IE6 XP:                           100 100 100  83
			// IE7 Vista:                        100 100 100  83
			// IE 8+ Windows:                     83  83 100  83
			// Evergreen Windows:                 83  83 100  83
			// Evergreen Mac with scrollbars:     85  85 100  85
			// Evergreen Mac without scrollbars: 100 100 100 100
	
			// Get scrollbar width
			browser.barWidth = outer[0].offsetWidth - outer[0].clientWidth;
	
			// IE6/7 will oversize a width 100% element inside a scrolling element, to
			// include the width of the scrollbar, while other browsers ensure the inner
			// element is contained without forcing scrolling
			browser.bScrollOversize = inner[0].offsetWidth === 100 && outer[0].clientWidth !== 100;
	
			// In rtl text layout, some browsers (most, but not all) will place the
			// scrollbar on the left, rather than the right.
			browser.bScrollbarLeft = Math.round( inner.offset().left ) !== 1;
	
			// IE8- don't provide height and width for getBoundingClientRect
			browser.bBounding = n[0].getBoundingClientRect().width ? true : false;
	
			n.remove();
		}
	
		$.extend( settings.oBrowser, DataTable.__browser );
		settings.oScroll.iBarWidth = DataTable.__browser.barWidth;
	}
	
	
	/**
	 * Array.prototype reduce[Right] method, used for browsers which don't support
	 * JS 1.6. Done this way to reduce code size, since we iterate either way
	 *  @param {object} settings dataTables settings object
	 *  @memberof DataTable#oApi
	 */
	function _fnReduce ( that, fn, init, start, end, inc )
	{
		var
			i = start,
			value,
			isSet = false;
	
		if ( init !== undefined ) {
			value = init;
			isSet = true;
		}
	
		while ( i !== end ) {
			if ( ! that.hasOwnProperty(i) ) {
				continue;
			}
	
			value = isSet ?
				fn( value, that[i], i, that ) :
				that[i];
	
			isSet = true;
			i += inc;
		}
	
		return value;
	}
	
	/**
	 * Add a column to the list used for the table with default values
	 *  @param {object} oSettings dataTables settings object
	 *  @param {node} nTh The th element for this column
	 *  @memberof DataTable#oApi
	 */
	function _fnAddColumn( oSettings, nTh )
	{
		// Add column to aoColumns array
		var oDefaults = DataTable.defaults.column;
		var iCol = oSettings.aoColumns.length;
		var oCol = $.extend( {}, DataTable.models.oColumn, oDefaults, {
			"nTh": nTh ? nTh : document.createElement('th'),
			"sTitle":    oDefaults.sTitle    ? oDefaults.sTitle    : nTh ? nTh.innerHTML : '',
			"aDataSort": oDefaults.aDataSort ? oDefaults.aDataSort : [iCol],
			"mData": oDefaults.mData ? oDefaults.mData : iCol,
			idx: iCol
		} );
		oSettings.aoColumns.push( oCol );
	
		// Add search object for column specific search. Note that the `searchCols[ iCol ]`
		// passed into extend can be undefined. This allows the user to give a default
		// with only some of the parameters defined, and also not give a default
		var searchCols = oSettings.aoPreSearchCols;
		searchCols[ iCol ] = $.extend( {}, DataTable.models.oSearch, searchCols[ iCol ] );
	
		// Use the default column options function to initialise classes etc
		_fnColumnOptions( oSettings, iCol, $(nTh).data() );
	}
	
	
	/**
	 * Apply options for a column
	 *  @param {object} oSettings dataTables settings object
	 *  @param {int} iCol column index to consider
	 *  @param {object} oOptions object with sType, bVisible and bSearchable etc
	 *  @memberof DataTable#oApi
	 */
	function _fnColumnOptions( oSettings, iCol, oOptions )
	{
		var oCol = oSettings.aoColumns[ iCol ];
		var oClasses = oSettings.oClasses;
		var th = $(oCol.nTh);
	
		// Try to get width information from the DOM. We can't get it from CSS
		// as we'd need to parse the CSS stylesheet. `width` option can override
		if ( ! oCol.sWidthOrig ) {
			// Width attribute
			oCol.sWidthOrig = th.attr('width') || null;
	
			// Style attribute
			var t = (th.attr('style') || '').match(/width:\s*(\d+[pxem%]+)/);
			if ( t ) {
				oCol.sWidthOrig = t[1];
			}
		}
	
		/* User specified column options */
		if ( oOptions !== undefined && oOptions !== null )
		{
			// Backwards compatibility
			_fnCompatCols( oOptions );
	
			// Map camel case parameters to their Hungarian counterparts
			_fnCamelToHungarian( DataTable.defaults.column, oOptions );
	
			/* Backwards compatibility for mDataProp */
			if ( oOptions.mDataProp !== undefined && !oOptions.mData )
			{
				oOptions.mData = oOptions.mDataProp;
			}
	
			if ( oOptions.sType )
			{
				oCol._sManualType = oOptions.sType;
			}
	
			// `class` is a reserved word in Javascript, so we need to provide
			// the ability to use a valid name for the camel case input
			if ( oOptions.className && ! oOptions.sClass )
			{
				oOptions.sClass = oOptions.className;
			}
	
			$.extend( oCol, oOptions );
			_fnMap( oCol, oOptions, "sWidth", "sWidthOrig" );
	
			/* iDataSort to be applied (backwards compatibility), but aDataSort will take
			 * priority if defined
			 */
			if ( oOptions.iDataSort !== undefined )
			{
				oCol.aDataSort = [ oOptions.iDataSort ];
			}
			_fnMap( oCol, oOptions, "aDataSort" );
		}
	
		/* Cache the data get and set functions for speed */
		var mDataSrc = oCol.mData;
		var mData = _fnGetObjectDataFn( mDataSrc );
		var mRender = oCol.mRender ? _fnGetObjectDataFn( oCol.mRender ) : null;
	
		var attrTest = function( src ) {
			return typeof src === 'string' && src.indexOf('@') !== -1;
		};
		oCol._bAttrSrc = $.isPlainObject( mDataSrc ) && (
			attrTest(mDataSrc.sort) || attrTest(mDataSrc.type) || attrTest(mDataSrc.filter)
		);
	
		oCol.fnGetData = function (rowData, type, meta) {
			var innerData = mData( rowData, type, undefined, meta );
	
			return mRender && type ?
				mRender( innerData, type, rowData, meta ) :
				innerData;
		};
		oCol.fnSetData = function ( rowData, val, meta ) {
			return _fnSetObjectDataFn( mDataSrc )( rowData, val, meta );
		};
	
		// Indicate if DataTables should read DOM data as an object or array
		// Used in _fnGetRowElements
		if ( typeof mDataSrc !== 'number' ) {
			oSettings._rowReadObject = true;
		}
	
		/* Feature sorting overrides column specific when off */
		if ( !oSettings.oFeatures.bSort )
		{
			oCol.bSortable = false;
			th.addClass( oClasses.sSortableNone ); // Have to add class here as order event isn't called
		}
	
		/* Check that the class assignment is correct for sorting */
		var bAsc = $.inArray('asc', oCol.asSorting) !== -1;
		var bDesc = $.inArray('desc', oCol.asSorting) !== -1;
		if ( !oCol.bSortable || (!bAsc && !bDesc) )
		{
			oCol.sSortingClass = oClasses.sSortableNone;
			oCol.sSortingClassJUI = "";
		}
		else if ( bAsc && !bDesc )
		{
			oCol.sSortingClass = oClasses.sSortableAsc;
			oCol.sSortingClassJUI = oClasses.sSortJUIAscAllowed;
		}
		else if ( !bAsc && bDesc )
		{
			oCol.sSortingClass = oClasses.sSortableDesc;
			oCol.sSortingClassJUI = oClasses.sSortJUIDescAllowed;
		}
		else
		{
			oCol.sSortingClass = oClasses.sSortable;
			oCol.sSortingClassJUI = oClasses.sSortJUI;
		}
	}
	
	
	/**
	 * Adjust the table column widths for new data. Note: you would probably want to
	 * do a redraw after calling this function!
	 *  @param {object} settings dataTables settings object
	 *  @memberof DataTable#oApi
	 */
	function _fnAdjustColumnSizing ( settings )
	{
		/* Not interested in doing column width calculation if auto-width is disabled */
		if ( settings.oFeatures.bAutoWidth !== false )
		{
			var columns = settings.aoColumns;
	
			_fnCalculateColumnWidths( settings );
			for ( var i=0 , iLen=columns.length ; i<iLen ; i++ )
			{
				columns[i].nTh.style.width = columns[i].sWidth;
			}
		}
	
		var scroll = settings.oScroll;
		if ( scroll.sY !== '' || scroll.sX !== '')
		{
			_fnScrollDraw( settings );
		}
	
		_fnCallbackFire( settings, null, 'column-sizing', [settings] );
	}
	
	
	/**
	 * Covert the index of a visible column to the index in the data array (take account
	 * of hidden columns)
	 *  @param {object} oSettings dataTables settings object
	 *  @param {int} iMatch Visible column index to lookup
	 *  @returns {int} i the data index
	 *  @memberof DataTable#oApi
	 */
	function _fnVisibleToColumnIndex( oSettings, iMatch )
	{
		var aiVis = _fnGetColumns( oSettings, 'bVisible' );
	
		return typeof aiVis[iMatch] === 'number' ?
			aiVis[iMatch] :
			null;
	}
	
	
	/**
	 * Covert the index of an index in the data array and convert it to the visible
	 *   column index (take account of hidden columns)
	 *  @param {int} iMatch Column index to lookup
	 *  @param {object} oSettings dataTables settings object
	 *  @returns {int} i the data index
	 *  @memberof DataTable#oApi
	 */
	function _fnColumnIndexToVisible( oSettings, iMatch )
	{
		var aiVis = _fnGetColumns( oSettings, 'bVisible' );
		var iPos = $.inArray( iMatch, aiVis );
	
		return iPos !== -1 ? iPos : null;
	}
	
	
	/**
	 * Get the number of visible columns
	 *  @param {object} oSettings dataTables settings object
	 *  @returns {int} i the number of visible columns
	 *  @memberof DataTable#oApi
	 */
	function _fnVisbleColumns( oSettings )
	{
		return _fnGetColumns( oSettings, 'bVisible' ).length;
	}
	
	
	/**
	 * Get an array of column indexes that match a given property
	 *  @param {object} oSettings dataTables settings object
	 *  @param {string} sParam Parameter in aoColumns to look for - typically
	 *    bVisible or bSearchable
	 *  @returns {array} Array of indexes with matched properties
	 *  @memberof DataTable#oApi
	 */
	function _fnGetColumns( oSettings, sParam )
	{
		var a = [];
	
		$.map( oSettings.aoColumns, function(val, i) {
			if ( val[sParam] ) {
				a.push( i );
			}
		} );
	
		return a;
	}
	
	
	/**
	 * Calculate the 'type' of a column
	 *  @param {object} settings dataTables settings object
	 *  @memberof DataTable#oApi
	 */
	function _fnColumnTypes ( settings )
	{
		var columns = settings.aoColumns;
		var data = settings.aoData;
		var types = DataTable.ext.type.detect;
		var i, ien, j, jen, k, ken;
		var col, cell, detectedType, cache;
	
		// For each column, spin over the 
		for ( i=0, ien=columns.length ; i<ien ; i++ ) {
			col = columns[i];
			cache = [];
	
			if ( ! col.sType && col._sManualType ) {
				col.sType = col._sManualType;
			}
			else if ( ! col.sType ) {
				for ( j=0, jen=types.length ; j<jen ; j++ ) {
					for ( k=0, ken=data.length ; k<ken ; k++ ) {
						// Use a cache array so we only need to get the type data
						// from the formatter once (when using multiple detectors)
						if ( cache[k] === undefined ) {
							cache[k] = _fnGetCellData( settings, k, i, 'type' );
						}
	
						detectedType = types[j]( cache[k], settings );
	
						// If null, then this type can't apply to this column, so
						// rather than testing all cells, break out. There is an
						// exception for the last type which is `html`. We need to
						// scan all rows since it is possible to mix string and HTML
						// types
						if ( ! detectedType && j !== types.length-1 ) {
							break;
						}
	
						// Only a single match is needed for html type since it is
						// bottom of the pile and very similar to string
						if ( detectedType === 'html' ) {
							break;
						}
					}
	
					// Type is valid for all data points in the column - use this
					// type
					if ( detectedType ) {
						col.sType = detectedType;
						break;
					}
				}
	
				// Fall back - if no type was detected, always use string
				if ( ! col.sType ) {
					col.sType = 'string';
				}
			}
		}
	}
	
	
	/**
	 * Take the column definitions and static columns arrays and calculate how
	 * they relate to column indexes. The callback function will then apply the
	 * definition found for a column to a suitable configuration object.
	 *  @param {object} oSettings dataTables settings object
	 *  @param {array} aoColDefs The aoColumnDefs array that is to be applied
	 *  @param {array} aoCols The aoColumns array that defines columns individually
	 *  @param {function} fn Callback function - takes two parameters, the calculated
	 *    column index and the definition for that column.
	 *  @memberof DataTable#oApi
	 */
	function _fnApplyColumnDefs( oSettings, aoColDefs, aoCols, fn )
	{
		var i, iLen, j, jLen, k, kLen, def;
		var columns = oSettings.aoColumns;
	
		// Column definitions with aTargets
		if ( aoColDefs )
		{
			/* Loop over the definitions array - loop in reverse so first instance has priority */
			for ( i=aoColDefs.length-1 ; i>=0 ; i-- )
			{
				def = aoColDefs[i];
	
				/* Each definition can target multiple columns, as it is an array */
				var aTargets = def.targets !== undefined ?
					def.targets :
					def.aTargets;
	
				if ( ! $.isArray( aTargets ) )
				{
					aTargets = [ aTargets ];
				}
	
				for ( j=0, jLen=aTargets.length ; j<jLen ; j++ )
				{
					if ( typeof aTargets[j] === 'number' && aTargets[j] >= 0 )
					{
						/* Add columns that we don't yet know about */
						while( columns.length <= aTargets[j] )
						{
							_fnAddColumn( oSettings );
						}
	
						/* Integer, basic index */
						fn( aTargets[j], def );
					}
					else if ( typeof aTargets[j] === 'number' && aTargets[j] < 0 )
					{
						/* Negative integer, right to left column counting */
						fn( columns.length+aTargets[j], def );
					}
					else if ( typeof aTargets[j] === 'string' )
					{
						/* Class name matching on TH element */
						for ( k=0, kLen=columns.length ; k<kLen ; k++ )
						{
							if ( aTargets[j] == "_all" ||
							     $(columns[k].nTh).hasClass( aTargets[j] ) )
							{
								fn( k, def );
							}
						}
					}
				}
			}
		}
	
		// Statically defined columns array
		if ( aoCols )
		{
			for ( i=0, iLen=aoCols.length ; i<iLen ; i++ )
			{
				fn( i, aoCols[i] );
			}
		}
	}
	
	/**
	 * Add a data array to the table, creating DOM node etc. This is the parallel to
	 * _fnGatherData, but for adding rows from a Javascript source, rather than a
	 * DOM source.
	 *  @param {object} oSettings dataTables settings object
	 *  @param {array} aData data array to be added
	 *  @param {node} [nTr] TR element to add to the table - optional. If not given,
	 *    DataTables will create a row automatically
	 *  @param {array} [anTds] Array of TD|TH elements for the row - must be given
	 *    if nTr is.
	 *  @returns {int} >=0 if successful (index of new aoData entry), -1 if failed
	 *  @memberof DataTable#oApi
	 */
	function _fnAddData ( oSettings, aDataIn, nTr, anTds )
	{
		/* Create the object for storing information about this new row */
		var iRow = oSettings.aoData.length;
		var oData = $.extend( true, {}, DataTable.models.oRow, {
			src: nTr ? 'dom' : 'data',
			idx: iRow
		} );
	
		oData._aData = aDataIn;
		oSettings.aoData.push( oData );
	
		/* Create the cells */
		var nTd, sThisType;
		var columns = oSettings.aoColumns;
	
		// Invalidate the column types as the new data needs to be revalidated
		for ( var i=0, iLen=columns.length ; i<iLen ; i++ )
		{
			columns[i].sType = null;
		}
	
		/* Add to the display array */
		oSettings.aiDisplayMaster.push( iRow );
	
		var id = oSettings.rowIdFn( aDataIn );
		if ( id !== undefined ) {
			oSettings.aIds[ id ] = oData;
		}
	
		/* Create the DOM information, or register it if already present */
		if ( nTr || ! oSettings.oFeatures.bDeferRender )
		{
			_fnCreateTr( oSettings, iRow, nTr, anTds );
		}
	
		return iRow;
	}
	
	
	/**
	 * Add one or more TR elements to the table. Generally we'd expect to
	 * use this for reading data from a DOM sourced table, but it could be
	 * used for an TR element. Note that if a TR is given, it is used (i.e.
	 * it is not cloned).
	 *  @param {object} settings dataTables settings object
	 *  @param {array|node|jQuery} trs The TR element(s) to add to the table
	 *  @returns {array} Array of indexes for the added rows
	 *  @memberof DataTable#oApi
	 */
	function _fnAddTr( settings, trs )
	{
		var row;
	
		// Allow an individual node to be passed in
		if ( ! (trs instanceof $) ) {
			trs = $(trs);
		}
	
		return trs.map( function (i, el) {
			row = _fnGetRowElements( settings, el );
			return _fnAddData( settings, row.data, el, row.cells );
		} );
	}
	
	
	/**
	 * Take a TR element and convert it to an index in aoData
	 *  @param {object} oSettings dataTables settings object
	 *  @param {node} n the TR element to find
	 *  @returns {int} index if the node is found, null if not
	 *  @memberof DataTable#oApi
	 */
	function _fnNodeToDataIndex( oSettings, n )
	{
		return (n._DT_RowIndex!==undefined) ? n._DT_RowIndex : null;
	}
	
	
	/**
	 * Take a TD element and convert it into a column data index (not the visible index)
	 *  @param {object} oSettings dataTables settings object
	 *  @param {int} iRow The row number the TD/TH can be found in
	 *  @param {node} n The TD/TH element to find
	 *  @returns {int} index if the node is found, -1 if not
	 *  @memberof DataTable#oApi
	 */
	function _fnNodeToColumnIndex( oSettings, iRow, n )
	{
		return $.inArray( n, oSettings.aoData[ iRow ].anCells );
	}
	
	
	/**
	 * Get the data for a given cell from the internal cache, taking into account data mapping
	 *  @param {object} settings dataTables settings object
	 *  @param {int} rowIdx aoData row id
	 *  @param {int} colIdx Column index
	 *  @param {string} type data get type ('display', 'type' 'filter' 'sort')
	 *  @returns {*} Cell data
	 *  @memberof DataTable#oApi
	 */
	function _fnGetCellData( settings, rowIdx, colIdx, type )
	{
		var draw           = settings.iDraw;
		var col            = settings.aoColumns[colIdx];
		var rowData        = settings.aoData[rowIdx]._aData;
		var defaultContent = col.sDefaultContent;
		var cellData       = col.fnGetData( rowData, type, {
			settings: settings,
			row:      rowIdx,
			col:      colIdx
		} );
	
		if ( cellData === undefined ) {
			if ( settings.iDrawError != draw && defaultContent === null ) {
				_fnLog( settings, 0, "Requested unknown parameter "+
					(typeof col.mData=='function' ? '{function}' : "'"+col.mData+"'")+
					" for row "+rowIdx+", column "+colIdx, 4 );
				settings.iDrawError = draw;
			}
			return defaultContent;
		}
	
		/* When the data source is null, we can use default column data */
		if ( (cellData === rowData || cellData === null) && defaultContent !== null ) {
			cellData = defaultContent;
		}
		else if ( typeof cellData === 'function' ) {
			// If the data source is a function, then we run it and use the return,
			// executing in the scope of the data object (for instances)
			return cellData.call( rowData );
		}
	
		if ( cellData === null && type == 'display' ) {
			return '';
		}
		return cellData;
	}
	
	
	/**
	 * Set the value for a specific cell, into the internal data cache
	 *  @param {object} settings dataTables settings object
	 *  @param {int} rowIdx aoData row id
	 *  @param {int} colIdx Column index
	 *  @param {*} val Value to set
	 *  @memberof DataTable#oApi
	 */
	function _fnSetCellData( settings, rowIdx, colIdx, val )
	{
		var col     = settings.aoColumns[colIdx];
		var rowData = settings.aoData[rowIdx]._aData;
	
		col.fnSetData( rowData, val, {
			settings: settings,
			row:      rowIdx,
			col:      colIdx
		}  );
	}
	
	
	// Private variable that is used to match action syntax in the data property object
	var __reArray = /\[.*?\]$/;
	var __reFn = /\(\)$/;
	
	/**
	 * Split string on periods, taking into account escaped periods
	 * @param  {string} str String to split
	 * @return {array} Split string
	 */
	function _fnSplitObjNotation( str )
	{
		return $.map( str.match(/(\\.|[^\.])+/g) || [''], function ( s ) {
			return s.replace(/\\./g, '.');
		} );
	}
	
	
	/**
	 * Return a function that can be used to get data from a source object, taking
	 * into account the ability to use nested objects as a source
	 *  @param {string|int|function} mSource The data source for the object
	 *  @returns {function} Data get function
	 *  @memberof DataTable#oApi
	 */
	function _fnGetObjectDataFn( mSource )
	{
		if ( $.isPlainObject( mSource ) )
		{
			/* Build an object of get functions, and wrap them in a single call */
			var o = {};
			$.each( mSource, function (key, val) {
				if ( val ) {
					o[key] = _fnGetObjectDataFn( val );
				}
			} );
	
			return function (data, type, row, meta) {
				var t = o[type] || o._;
				return t !== undefined ?
					t(data, type, row, meta) :
					data;
			};
		}
		else if ( mSource === null )
		{
			/* Give an empty string for rendering / sorting etc */
			return function (data) { // type, row and meta also passed, but not used
				return data;
			};
		}
		else if ( typeof mSource === 'function' )
		{
			return function (data, type, row, meta) {
				return mSource( data, type, row, meta );
			};
		}
		else if ( typeof mSource === 'string' && (mSource.indexOf('.') !== -1 ||
			      mSource.indexOf('[') !== -1 || mSource.indexOf('(') !== -1) )
		{
			/* If there is a . in the source string then the data source is in a
			 * nested object so we loop over the data for each level to get the next
			 * level down. On each loop we test for undefined, and if found immediately
			 * return. This allows entire objects to be missing and sDefaultContent to
			 * be used if defined, rather than throwing an error
			 */
			var fetchData = function (data, type, src) {
				var arrayNotation, funcNotation, out, innerSrc;
	
				if ( src !== "" )
				{
					var a = _fnSplitObjNotation( src );
	
					for ( var i=0, iLen=a.length ; i<iLen ; i++ )
					{
						// Check if we are dealing with special notation
						arrayNotation = a[i].match(__reArray);
						funcNotation = a[i].match(__reFn);
	
						if ( arrayNotation )
						{
							// Array notation
							a[i] = a[i].replace(__reArray, '');
	
							// Condition allows simply [] to be passed in
							if ( a[i] !== "" ) {
								data = data[ a[i] ];
							}
							out = [];
	
							// Get the remainder of the nested object to get
							a.splice( 0, i+1 );
							innerSrc = a.join('.');
	
							// Traverse each entry in the array getting the properties requested
							if ( $.isArray( data ) ) {
								for ( var j=0, jLen=data.length ; j<jLen ; j++ ) {
									out.push( fetchData( data[j], type, innerSrc ) );
								}
							}
	
							// If a string is given in between the array notation indicators, that
							// is used to join the strings together, otherwise an array is returned
							var join = arrayNotation[0].substring(1, arrayNotation[0].length-1);
							data = (join==="") ? out : out.join(join);
	
							// The inner call to fetchData has already traversed through the remainder
							// of the source requested, so we exit from the loop
							break;
						}
						else if ( funcNotation )
						{
							// Function call
							a[i] = a[i].replace(__reFn, '');
							data = data[ a[i] ]();
							continue;
						}
	
						if ( data === null || data[ a[i] ] === undefined )
						{
							return undefined;
						}
						data = data[ a[i] ];
					}
				}
	
				return data;
			};
	
			return function (data, type) { // row and meta also passed, but not used
				return fetchData( data, type, mSource );
			};
		}
		else
		{
			/* Array or flat object mapping */
			return function (data, type) { // row and meta also passed, but not used
				return data[mSource];
			};
		}
	}
	
	
	/**
	 * Return a function that can be used to set data from a source object, taking
	 * into account the ability to use nested objects as a source
	 *  @param {string|int|function} mSource The data source for the object
	 *  @returns {function} Data set function
	 *  @memberof DataTable#oApi
	 */
	function _fnSetObjectDataFn( mSource )
	{
		if ( $.isPlainObject( mSource ) )
		{
			/* Unlike get, only the underscore (global) option is used for for
			 * setting data since we don't know the type here. This is why an object
			 * option is not documented for `mData` (which is read/write), but it is
			 * for `mRender` which is read only.
			 */
			return _fnSetObjectDataFn( mSource._ );
		}
		else if ( mSource === null )
		{
			/* Nothing to do when the data source is null */
			return function () {};
		}
		else if ( typeof mSource === 'function' )
		{
			return function (data, val, meta) {
				mSource( data, 'set', val, meta );
			};
		}
		else if ( typeof mSource === 'string' && (mSource.indexOf('.') !== -1 ||
			      mSource.indexOf('[') !== -1 || mSource.indexOf('(') !== -1) )
		{
			/* Like the get, we need to get data from a nested object */
			var setData = function (data, val, src) {
				var a = _fnSplitObjNotation( src ), b;
				var aLast = a[a.length-1];
				var arrayNotation, funcNotation, o, innerSrc;
	
				for ( var i=0, iLen=a.length-1 ; i<iLen ; i++ )
				{
					// Check if we are dealing with an array notation request
					arrayNotation = a[i].match(__reArray);
					funcNotation = a[i].match(__reFn);
	
					if ( arrayNotation )
					{
						a[i] = a[i].replace(__reArray, '');
						data[ a[i] ] = [];
	
						// Get the remainder of the nested object to set so we can recurse
						b = a.slice();
						b.splice( 0, i+1 );
						innerSrc = b.join('.');
	
						// Traverse each entry in the array setting the properties requested
						if ( $.isArray( val ) )
						{
							for ( var j=0, jLen=val.length ; j<jLen ; j++ )
							{
								o = {};
								setData( o, val[j], innerSrc );
								data[ a[i] ].push( o );
							}
						}
						else
						{
							// We've been asked to save data to an array, but it
							// isn't array data to be saved. Best that can be done
							// is to just save the value.
							data[ a[i] ] = val;
						}
	
						// The inner call to setData has already traversed through the remainder
						// of the source and has set the data, thus we can exit here
						return;
					}
					else if ( funcNotation )
					{
						// Function call
						a[i] = a[i].replace(__reFn, '');
						data = data[ a[i] ]( val );
					}
	
					// If the nested object doesn't currently exist - since we are
					// trying to set the value - create it
					if ( data[ a[i] ] === null || data[ a[i] ] === undefined )
					{
						data[ a[i] ] = {};
					}
					data = data[ a[i] ];
				}
	
				// Last item in the input - i.e, the actual set
				if ( aLast.match(__reFn ) )
				{
					// Function call
					data = data[ aLast.replace(__reFn, '') ]( val );
				}
				else
				{
					// If array notation is used, we just want to strip it and use the property name
					// and assign the value. If it isn't used, then we get the result we want anyway
					data[ aLast.replace(__reArray, '') ] = val;
				}
			};
	
			return function (data, val) { // meta is also passed in, but not used
				return setData( data, val, mSource );
			};
		}
		else
		{
			/* Array or flat object mapping */
			return function (data, val) { // meta is also passed in, but not used
				data[mSource] = val;
			};
		}
	}
	
	
	/**
	 * Return an array with the full table data
	 *  @param {object} oSettings dataTables settings object
	 *  @returns array {array} aData Master data array
	 *  @memberof DataTable#oApi
	 */
	function _fnGetDataMaster ( settings )
	{
		return _pluck( settings.aoData, '_aData' );
	}
	
	
	/**
	 * Nuke the table
	 *  @param {object} oSettings dataTables settings object
	 *  @memberof DataTable#oApi
	 */
	function _fnClearTable( settings )
	{
		settings.aoData.length = 0;
		settings.aiDisplayMaster.length = 0;
		settings.aiDisplay.length = 0;
		settings.aIds = {};
	}
	
	
	 /**
	 * Take an array of integers (index array) and remove a target integer (value - not
	 * the key!)
	 *  @param {array} a Index array to target
	 *  @param {int} iTarget value to find
	 *  @memberof DataTable#oApi
	 */
	function _fnDeleteIndex( a, iTarget, splice )
	{
		var iTargetIndex = -1;
	
		for ( var i=0, iLen=a.length ; i<iLen ; i++ )
		{
			if ( a[i] == iTarget )
			{
				iTargetIndex = i;
			}
			else if ( a[i] > iTarget )
			{
				a[i]--;
			}
		}
	
		if ( iTargetIndex != -1 && splice === undefined )
		{
			a.splice( iTargetIndex, 1 );
		}
	}
	
	
	/**
	 * Mark cached data as invalid such that a re-read of the data will occur when
	 * the cached data is next requested. Also update from the data source object.
	 *
	 * @param {object} settings DataTables settings object
	 * @param {int}    rowIdx   Row index to invalidate
	 * @param {string} [src]    Source to invalidate from: undefined, 'auto', 'dom'
	 *     or 'data'
	 * @param {int}    [colIdx] Column index to invalidate. If undefined the whole
	 *     row will be invalidated
	 * @memberof DataTable#oApi
	 *
	 * @todo For the modularisation of v1.11 this will need to become a callback, so
	 *   the sort and filter methods can subscribe to it. That will required
	 *   initialisation options for sorting, which is why it is not already baked in
	 */
	function _fnInvalidate( settings, rowIdx, src, colIdx )
	{
		var row = settings.aoData[ rowIdx ];
		var i, ien;
		var cellWrite = function ( cell, col ) {
			// This is very frustrating, but in IE if you just write directly
			// to innerHTML, and elements that are overwritten are GC'ed,
			// even if there is a reference to them elsewhere
			while ( cell.childNodes.length ) {
				cell.removeChild( cell.firstChild );
			}
	
			cell.innerHTML = _fnGetCellData( settings, rowIdx, col, 'display' );
		};
	
		// Are we reading last data from DOM or the data object?
		if ( src === 'dom' || ((! src || src === 'auto') && row.src === 'dom') ) {
			// Read the data from the DOM
			row._aData = _fnGetRowElements(
					settings, row, colIdx, colIdx === undefined ? undefined : row._aData
				)
				.data;
		}
		else {
			// Reading from data object, update the DOM
			var cells = row.anCells;
	
			if ( cells ) {
				if ( colIdx !== undefined ) {
					cellWrite( cells[colIdx], colIdx );
				}
				else {
					for ( i=0, ien=cells.length ; i<ien ; i++ ) {
						cellWrite( cells[i], i );
					}
				}
			}
		}
	
		// For both row and cell invalidation, the cached data for sorting and
		// filtering is nulled out
		row._aSortData = null;
		row._aFilterData = null;
	
		// Invalidate the type for a specific column (if given) or all columns since
		// the data might have changed
		var cols = settings.aoColumns;
		if ( colIdx !== undefined ) {
			cols[ colIdx ].sType = null;
		}
		else {
			for ( i=0, ien=cols.length ; i<ien ; i++ ) {
				cols[i].sType = null;
			}
	
			// Update DataTables special `DT_*` attributes for the row
			_fnRowAttributes( settings, row );
		}
	}
	
	
	/**
	 * Build a data source object from an HTML row, reading the contents of the
	 * cells that are in the row.
	 *
	 * @param {object} settings DataTables settings object
	 * @param {node|object} TR element from which to read data or existing row
	 *   object from which to re-read the data from the cells
	 * @param {int} [colIdx] Optional column index
	 * @param {array|object} [d] Data source object. If `colIdx` is given then this
	 *   parameter should also be given and will be used to write the data into.
	 *   Only the column in question will be written
	 * @returns {object} Object with two parameters: `data` the data read, in
	 *   document order, and `cells` and array of nodes (they can be useful to the
	 *   caller, so rather than needing a second traversal to get them, just return
	 *   them from here).
	 * @memberof DataTable#oApi
	 */
	function _fnGetRowElements( settings, row, colIdx, d )
	{
		var
			tds = [],
			td = row.firstChild,
			name, col, o, i=0, contents,
			columns = settings.aoColumns,
			objectRead = settings._rowReadObject;
	
		// Allow the data object to be passed in, or construct
		d = d !== undefined ?
			d :
			objectRead ?
				{} :
				[];
	
		var attr = function ( str, td  ) {
			if ( typeof str === 'string' ) {
				var idx = str.indexOf('@');
	
				if ( idx !== -1 ) {
					var attr = str.substring( idx+1 );
					var setter = _fnSetObjectDataFn( str );
					setter( d, td.getAttribute( attr ) );
				}
			}
		};
	
		// Read data from a cell and store into the data object
		var cellProcess = function ( cell ) {
			if ( colIdx === undefined || colIdx === i ) {
				col = columns[i];
				contents = $.trim(cell.innerHTML);
	
				if ( col && col._bAttrSrc ) {
					var setter = _fnSetObjectDataFn( col.mData._ );
					setter( d, contents );
	
					attr( col.mData.sort, cell );
					attr( col.mData.type, cell );
					attr( col.mData.filter, cell );
				}
				else {
					// Depending on the `data` option for the columns the data can
					// be read to either an object or an array.
					if ( objectRead ) {
						if ( ! col._setter ) {
							// Cache the setter function
							col._setter = _fnSetObjectDataFn( col.mData );
						}
						col._setter( d, contents );
					}
					else {
						d[i] = contents;
					}
				}
			}
	
			i++;
		};
	
		if ( td ) {
			// `tr` element was passed in
			while ( td ) {
				name = td.nodeName.toUpperCase();
	
				if ( name == "TD" || name == "TH" ) {
					cellProcess( td );
					tds.push( td );
				}
	
				td = td.nextSibling;
			}
		}
		else {
			// Existing row object passed in
			tds = row.anCells;
	
			for ( var j=0, jen=tds.length ; j<jen ; j++ ) {
				cellProcess( tds[j] );
			}
		}
	
		// Read the ID from the DOM if present
		var rowNode = row.firstChild ? row : row.nTr;
	
		if ( rowNode ) {
			var id = rowNode.getAttribute( 'id' );
	
			if ( id ) {
				_fnSetObjectDataFn( settings.rowId )( d, id );
			}
		}
	
		return {
			data: d,
			cells: tds
		};
	}
	/**
	 * Create a new TR element (and it's TD children) for a row
	 *  @param {object} oSettings dataTables settings object
	 *  @param {int} iRow Row to consider
	 *  @param {node} [nTrIn] TR element to add to the table - optional. If not given,
	 *    DataTables will create a row automatically
	 *  @param {array} [anTds] Array of TD|TH elements for the row - must be given
	 *    if nTr is.
	 *  @memberof DataTable#oApi
	 */
	function _fnCreateTr ( oSettings, iRow, nTrIn, anTds )
	{
		var
			row = oSettings.aoData[iRow],
			rowData = row._aData,
			cells = [],
			nTr, nTd, oCol,
			i, iLen;
	
		if ( row.nTr === null )
		{
			nTr = nTrIn || document.createElement('tr');
	
			row.nTr = nTr;
			row.anCells = cells;
	
			/* Use a private property on the node to allow reserve mapping from the node
			 * to the aoData array for fast look up
			 */
			nTr._DT_RowIndex = iRow;
	
			/* Special parameters can be given by the data source to be used on the row */
			_fnRowAttributes( oSettings, row );
	
			/* Process each column */
			for ( i=0, iLen=oSettings.aoColumns.length ; i<iLen ; i++ )
			{
				oCol = oSettings.aoColumns[i];
	
				nTd = nTrIn ? anTds[i] : document.createElement( oCol.sCellType );
				nTd._DT_CellIndex = {
					row: iRow,
					column: i
				};
				
				cells.push( nTd );
	
				// Need to create the HTML if new, or if a rendering function is defined
				if ( !nTrIn || oCol.mRender || oCol.mData !== i )
				{
					nTd.innerHTML = _fnGetCellData( oSettings, iRow, i, 'display' );
				}
	
				/* Add user defined class */
				if ( oCol.sClass )
				{
					nTd.className += ' '+oCol.sClass;
				}
	
				// Visibility - add or remove as required
				if ( oCol.bVisible && ! nTrIn )
				{
					nTr.appendChild( nTd );
				}
				else if ( ! oCol.bVisible && nTrIn )
				{
					nTd.parentNode.removeChild( nTd );
				}
	
				if ( oCol.fnCreatedCell )
				{
					oCol.fnCreatedCell.call( oSettings.oInstance,
						nTd, _fnGetCellData( oSettings, iRow, i ), rowData, iRow, i
					);
				}
			}
	
			_fnCallbackFire( oSettings, 'aoRowCreatedCallback', null, [nTr, rowData, iRow] );
		}
	
		// Remove once webkit bug 131819 and Chromium bug 365619 have been resolved
		// and deployed
		row.nTr.setAttribute( 'role', 'row' );
	}
	
	
	/**
	 * Add attributes to a row based on the special `DT_*` parameters in a data
	 * source object.
	 *  @param {object} settings DataTables settings object
	 *  @param {object} DataTables row object for the row to be modified
	 *  @memberof DataTable#oApi
	 */
	function _fnRowAttributes( settings, row )
	{
		var tr = row.nTr;
		var data = row._aData;
	
		if ( tr ) {
			var id = settings.rowIdFn( data );
	
			if ( id ) {
				tr.id = id;
			}
	
			if ( data.DT_RowClass ) {
				// Remove any classes added by DT_RowClass before
				var a = data.DT_RowClass.split(' ');
				row.__rowc = row.__rowc ?
					_unique( row.__rowc.concat( a ) ) :
					a;
	
				$(tr)
					.removeClass( row.__rowc.join(' ') )
					.addClass( data.DT_RowClass );
			}
	
			if ( data.DT_RowAttr ) {
				$(tr).attr( data.DT_RowAttr );
			}
	
			if ( data.DT_RowData ) {
				$(tr).data( data.DT_RowData );
			}
		}
	}
	
	
	/**
	 * Create the HTML header for the table
	 *  @param {object} oSettings dataTables settings object
	 *  @memberof DataTable#oApi
	 */
	function _fnBuildHead( oSettings )
	{
		var i, ien, cell, row, column;
		var thead = oSettings.nTHead;
		var tfoot = oSettings.nTFoot;
		var createHeader = $('th, td', thead).length === 0;
		var classes = oSettings.oClasses;
		var columns = oSettings.aoColumns;
	
		if ( createHeader ) {
			row = $('<tr/>').appendTo( thead );
		}
	
		for ( i=0, ien=columns.length ; i<ien ; i++ ) {
			column = columns[i];
			cell = $( column.nTh ).addClass( column.sClass );
	
			if ( createHeader ) {
				cell.appendTo( row );
			}
	
			// 1.11 move into sorting
			if ( oSettings.oFeatures.bSort ) {
				cell.addClass( column.sSortingClass );
	
				if ( column.bSortable !== false ) {
					cell
						.attr( 'tabindex', oSettings.iTabIndex )
						.attr( 'aria-controls', oSettings.sTableId );
	
					_fnSortAttachListener( oSettings, column.nTh, i );
				}
			}
	
			if ( column.sTitle != cell[0].innerHTML ) {
				cell.html( column.sTitle );
			}
	
			_fnRenderer( oSettings, 'header' )(
				oSettings, cell, column, classes
			);
		}
	
		if ( createHeader ) {
			_fnDetectHeader( oSettings.aoHeader, thead );
		}
		
		/* ARIA role for the rows */
	 	$(thead).find('>tr').attr('role', 'row');
	
		/* Deal with the footer - add classes if required */
		$(thead).find('>tr>th, >tr>td').addClass( classes.sHeaderTH );
		$(tfoot).find('>tr>th, >tr>td').addClass( classes.sFooterTH );
	
		// Cache the footer cells. Note that we only take the cells from the first
		// row in the footer. If there is more than one row the user wants to
		// interact with, they need to use the table().foot() method. Note also this
		// allows cells to be used for multiple columns using colspan
		if ( tfoot !== null ) {
			var cells = oSettings.aoFooter[0];
	
			for ( i=0, ien=cells.length ; i<ien ; i++ ) {
				column = columns[i];
				column.nTf = cells[i].cell;
	
				if ( column.sClass ) {
					$(column.nTf).addClass( column.sClass );
				}
			}
		}
	}
	
	
	/**
	 * Draw the header (or footer) element based on the column visibility states. The
	 * methodology here is to use the layout array from _fnDetectHeader, modified for
	 * the instantaneous column visibility, to construct the new layout. The grid is
	 * traversed over cell at a time in a rows x columns grid fashion, although each
	 * cell insert can cover multiple elements in the grid - which is tracks using the
	 * aApplied array. Cell inserts in the grid will only occur where there isn't
	 * already a cell in that position.
	 *  @param {object} oSettings dataTables settings object
	 *  @param array {objects} aoSource Layout array from _fnDetectHeader
	 *  @param {boolean} [bIncludeHidden=false] If true then include the hidden columns in the calc,
	 *  @memberof DataTable#oApi
	 */
	function _fnDrawHead( oSettings, aoSource, bIncludeHidden )
	{
		var i, iLen, j, jLen, k, kLen, n, nLocalTr;
		var aoLocal = [];
		var aApplied = [];
		var iColumns = oSettings.aoColumns.length;
		var iRowspan, iColspan;
	
		if ( ! aoSource )
		{
			return;
		}
	
		if (  bIncludeHidden === undefined )
		{
			bIncludeHidden = false;
		}
	
		/* Make a copy of the master layout array, but without the visible columns in it */
		for ( i=0, iLen=aoSource.length ; i<iLen ; i++ )
		{
			aoLocal[i] = aoSource[i].slice();
			aoLocal[i].nTr = aoSource[i].nTr;
	
			/* Remove any columns which are currently hidden */
			for ( j=iColumns-1 ; j>=0 ; j-- )
			{
				if ( !oSettings.aoColumns[j].bVisible && !bIncludeHidden )
				{
					aoLocal[i].splice( j, 1 );
				}
			}
	
			/* Prep the applied array - it needs an element for each row */
			aApplied.push( [] );
		}
	
		for ( i=0, iLen=aoLocal.length ; i<iLen ; i++ )
		{
			nLocalTr = aoLocal[i].nTr;
	
			/* All cells are going to be replaced, so empty out the row */
			if ( nLocalTr )
			{
				while( (n = nLocalTr.firstChild) )
				{
					nLocalTr.removeChild( n );
				}
			}
	
			for ( j=0, jLen=aoLocal[i].length ; j<jLen ; j++ )
			{
				iRowspan = 1;
				iColspan = 1;
	
				/* Check to see if there is already a cell (row/colspan) covering our target
				 * insert point. If there is, then there is nothing to do.
				 */
				if ( aApplied[i][j] === undefined )
				{
					nLocalTr.appendChild( aoLocal[i][j].cell );
					aApplied[i][j] = 1;
	
					/* Expand the cell to cover as many rows as needed */
					while ( aoLocal[i+iRowspan] !== undefined &&
					        aoLocal[i][j].cell == aoLocal[i+iRowspan][j].cell )
					{
						aApplied[i+iRowspan][j] = 1;
						iRowspan++;
					}
	
					/* Expand the cell to cover as many columns as needed */
					while ( aoLocal[i][j+iColspan] !== undefined &&
					        aoLocal[i][j].cell == aoLocal[i][j+iColspan].cell )
					{
						/* Must update the applied array over the rows for the columns */
						for ( k=0 ; k<iRowspan ; k++ )
						{
							aApplied[i+k][j+iColspan] = 1;
						}
						iColspan++;
					}
	
					/* Do the actual expansion in the DOM */
					$(aoLocal[i][j].cell)
						.attr('rowspan', iRowspan)
						.attr('colspan', iColspan);
				}
			}
		}
	}
	
	
	/**
	 * Insert the required TR nodes into the table for display
	 *  @param {object} oSettings dataTables settings object
	 *  @memberof DataTable#oApi
	 */
	function _fnDraw( oSettings )
	{
		/* Provide a pre-callback function which can be used to cancel the draw is false is returned */
		var aPreDraw = _fnCallbackFire( oSettings, 'aoPreDrawCallback', 'preDraw', [oSettings] );
		if ( $.inArray( false, aPreDraw ) !== -1 )
		{
			_fnProcessingDisplay( oSettings, false );
			return;
		}
	
		var i, iLen, n;
		var anRows = [];
		var iRowCount = 0;
		var asStripeClasses = oSettings.asStripeClasses;
		var iStripes = asStripeClasses.length;
		var iOpenRows = oSettings.aoOpenRows.length;
		var oLang = oSettings.oLanguage;
		var iInitDisplayStart = oSettings.iInitDisplayStart;
		var bServerSide = _fnDataSource( oSettings ) == 'ssp';
		var aiDisplay = oSettings.aiDisplay;
	
		oSettings.bDrawing = true;
	
		/* Check and see if we have an initial draw position from state saving */
		if ( iInitDisplayStart !== undefined && iInitDisplayStart !== -1 )
		{
			oSettings._iDisplayStart = bServerSide ?
				iInitDisplayStart :
				iInitDisplayStart >= oSettings.fnRecordsDisplay() ?
					0 :
					iInitDisplayStart;
	
			oSettings.iInitDisplayStart = -1;
		}
	
		var iDisplayStart = oSettings._iDisplayStart;
		var iDisplayEnd = oSettings.fnDisplayEnd();
	
		/* Server-side processing draw intercept */
		if ( oSettings.bDeferLoading )
		{
			oSettings.bDeferLoading = false;
			oSettings.iDraw++;
			_fnProcessingDisplay( oSettings, false );
		}
		else if ( !bServerSide )
		{
			oSettings.iDraw++;
		}
		else if ( !oSettings.bDestroying && !_fnAjaxUpdate( oSettings ) )
		{
			return;
		}
	
		if ( aiDisplay.length !== 0 )
		{
			var iStart = bServerSide ? 0 : iDisplayStart;
			var iEnd = bServerSide ? oSettings.aoData.length : iDisplayEnd;
	
			for ( var j=iStart ; j<iEnd ; j++ )
			{
				var iDataIndex = aiDisplay[j];
				var aoData = oSettings.aoData[ iDataIndex ];
				if ( aoData.nTr === null )
				{
					_fnCreateTr( oSettings, iDataIndex );
				}
	
				var nRow = aoData.nTr;
	
				/* Remove the old striping classes and then add the new one */
				if ( iStripes !== 0 )
				{
					var sStripe = asStripeClasses[ iRowCount % iStripes ];
					if ( aoData._sRowStripe != sStripe )
					{
						$(nRow).removeClass( aoData._sRowStripe ).addClass( sStripe );
						aoData._sRowStripe = sStripe;
					}
				}
	
				// Row callback functions - might want to manipulate the row
				// iRowCount and j are not currently documented. Are they at all
				// useful?
				_fnCallbackFire( oSettings, 'aoRowCallback', null,
					[nRow, aoData._aData, iRowCount, j] );
	
				anRows.push( nRow );
				iRowCount++;
			}
		}
		else
		{
			/* Table is empty - create a row with an empty message in it */
			var sZero = oLang.sZeroRecords;
			if ( oSettings.iDraw == 1 &&  _fnDataSource( oSettings ) == 'ajax' )
			{
				sZero = oLang.sLoadingRecords;
			}
			else if ( oLang.sEmptyTable && oSettings.fnRecordsTotal() === 0 )
			{
				sZero = oLang.sEmptyTable;
			}
	
			anRows[ 0 ] = $( '<tr/>', { 'class': iStripes ? asStripeClasses[0] : '' } )
				.append( $('<td />', {
					'valign':  'top',
					'colSpan': _fnVisbleColumns( oSettings ),
					'class':   oSettings.oClasses.sRowEmpty
				} ).html( sZero ) )[0];
		}
	
		/* Header and footer callbacks */
		_fnCallbackFire( oSettings, 'aoHeaderCallback', 'header', [ $(oSettings.nTHead).children('tr')[0],
			_fnGetDataMaster( oSettings ), iDisplayStart, iDisplayEnd, aiDisplay ] );
	
		_fnCallbackFire( oSettings, 'aoFooterCallback', 'footer', [ $(oSettings.nTFoot).children('tr')[0],
			_fnGetDataMaster( oSettings ), iDisplayStart, iDisplayEnd, aiDisplay ] );
	
		var body = $(oSettings.nTBody);
	
		body.children().detach();
		body.append( $(anRows) );
	
		/* Call all required callback functions for the end of a draw */
		_fnCallbackFire( oSettings, 'aoDrawCallback', 'draw', [oSettings] );
	
		/* Draw is complete, sorting and filtering must be as well */
		oSettings.bSorted = false;
		oSettings.bFiltered = false;
		oSettings.bDrawing = false;
	}
	
	
	/**
	 * Redraw the table - taking account of the various features which are enabled
	 *  @param {object} oSettings dataTables settings object
	 *  @param {boolean} [holdPosition] Keep the current paging position. By default
	 *    the paging is reset to the first page
	 *  @memberof DataTable#oApi
	 */
	function _fnReDraw( settings, holdPosition )
	{
		var
			features = settings.oFeatures,
			sort     = features.bSort,
			filter   = features.bFilter;
	
		if ( sort ) {
			_fnSort( settings );
		}
	
		if ( filter ) {
			_fnFilterComplete( settings, settings.oPreviousSearch );
		}
		else {
			// No filtering, so we want to just use the display master
			settings.aiDisplay = settings.aiDisplayMaster.slice();
		}
	
		if ( holdPosition !== true ) {
			settings._iDisplayStart = 0;
		}
	
		// Let any modules know about the draw hold position state (used by
		// scrolling internally)
		settings._drawHold = holdPosition;
	
		_fnDraw( settings );
	
		settings._drawHold = false;
	}
	
	
	/**
	 * Add the options to the page HTML for the table
	 *  @param {object} oSettings dataTables settings object
	 *  @memberof DataTable#oApi
	 */
	function _fnAddOptionsHtml ( oSettings )
	{
		var classes = oSettings.oClasses;
		var table = $(oSettings.nTable);
		var holding = $('<div/>').insertBefore( table ); // Holding element for speed
		var features = oSettings.oFeatures;
	
		// All DataTables are wrapped in a div
		var insert = $('<div/>', {
			id:      oSettings.sTableId+'_wrapper',
			'class': classes.sWrapper + (oSettings.nTFoot ? '' : ' '+classes.sNoFooter)
		} );
	
		oSettings.nHolding = holding[0];
		oSettings.nTableWrapper = insert[0];
		oSettings.nTableReinsertBefore = oSettings.nTable.nextSibling;
	
		/* Loop over the user set positioning and place the elements as needed */
		var aDom = oSettings.sDom.split('');
		var featureNode, cOption, nNewNode, cNext, sAttr, j;
		for ( var i=0 ; i<aDom.length ; i++ )
		{
			featureNode = null;
			cOption = aDom[i];
	
			if ( cOption == '<' )
			{
				/* New container div */
				nNewNode = $('<div/>')[0];
	
				/* Check to see if we should append an id and/or a class name to the container */
				cNext = aDom[i+1];
				if ( cNext == "'" || cNext == '"' )
				{
					sAttr = "";
					j = 2;
					while ( aDom[i+j] != cNext )
					{
						sAttr += aDom[i+j];
						j++;
					}
	
					/* Replace jQuery UI constants @todo depreciated */
					if ( sAttr == "H" )
					{
						sAttr = classes.sJUIHeader;
					}
					else if ( sAttr == "F" )
					{
						sAttr = classes.sJUIFooter;
					}
	
					/* The attribute can be in the format of "#id.class", "#id" or "class" This logic
					 * breaks the string into parts and applies them as needed
					 */
					if ( sAttr.indexOf('.') != -1 )
					{
						var aSplit = sAttr.split('.');
						nNewNode.id = aSplit[0].substr(1, aSplit[0].length-1);
						nNewNode.className = aSplit[1];
					}
					else if ( sAttr.charAt(0) == "#" )
					{
						nNewNode.id = sAttr.substr(1, sAttr.length-1);
					}
					else
					{
						nNewNode.className = sAttr;
					}
	
					i += j; /* Move along the position array */
				}
	
				insert.append( nNewNode );
				insert = $(nNewNode);
			}
			else if ( cOption == '>' )
			{
				/* End container div */
				insert = insert.parent();
			}
			// @todo Move options into their own plugins?
			else if ( cOption == 'l' && features.bPaginate && features.bLengthChange )
			{
				/* Length */
				featureNode = _fnFeatureHtmlLength( oSettings );
			}
			else if ( cOption == 'f' && features.bFilter )
			{
				/* Filter */
				featureNode = _fnFeatureHtmlFilter( oSettings );
			}
			else if ( cOption == 'r' && features.bProcessing )
			{
				/* pRocessing */
				featureNode = _fnFeatureHtmlProcessing( oSettings );
			}
			else if ( cOption == 't' )
			{
				/* Table */
				featureNode = _fnFeatureHtmlTable( oSettings );
			}
			else if ( cOption ==  'i' && features.bInfo )
			{
				/* Info */
				featureNode = _fnFeatureHtmlInfo( oSettings );
			}
			else if ( cOption == 'p' && features.bPaginate )
			{
				/* Pagination */
				featureNode = _fnFeatureHtmlPaginate( oSettings );
			}
			else if ( DataTable.ext.feature.length !== 0 )
			{
				/* Plug-in features */
				var aoFeatures = DataTable.ext.feature;
				for ( var k=0, kLen=aoFeatures.length ; k<kLen ; k++ )
				{
					if ( cOption == aoFeatures[k].cFeature )
					{
						featureNode = aoFeatures[k].fnInit( oSettings );
						break;
					}
				}
			}
	
			/* Add to the 2D features array */
			if ( featureNode )
			{
				var aanFeatures = oSettings.aanFeatures;
	
				if ( ! aanFeatures[cOption] )
				{
					aanFeatures[cOption] = [];
				}
	
				aanFeatures[cOption].push( featureNode );
				insert.append( featureNode );
			}
		}
	
		/* Built our DOM structure - replace the holding div with what we want */
		holding.replaceWith( insert );
		oSettings.nHolding = null;
	}
	
	
	/**
	 * Use the DOM source to create up an array of header cells. The idea here is to
	 * create a layout grid (array) of rows x columns, which contains a reference
	 * to the cell that that point in the grid (regardless of col/rowspan), such that
	 * any column / row could be removed and the new grid constructed
	 *  @param array {object} aLayout Array to store the calculated layout in
	 *  @param {node} nThead The header/footer element for the table
	 *  @memberof DataTable#oApi
	 */
	function _fnDetectHeader ( aLayout, nThead )
	{
		var nTrs = $(nThead).children('tr');
		var nTr, nCell;
		var i, k, l, iLen, jLen, iColShifted, iColumn, iColspan, iRowspan;
		var bUnique;
		var fnShiftCol = function ( a, i, j ) {
			var k = a[i];
	                while ( k[j] ) {
				j++;
			}
			return j;
		};
	
		aLayout.splice( 0, aLayout.length );
	
		/* We know how many rows there are in the layout - so prep it */
		for ( i=0, iLen=nTrs.length ; i<iLen ; i++ )
		{
			aLayout.push( [] );
		}
	
		/* Calculate a layout array */
		for ( i=0, iLen=nTrs.length ; i<iLen ; i++ )
		{
			nTr = nTrs[i];
			iColumn = 0;
	
			/* For every cell in the row... */
			nCell = nTr.firstChild;
			while ( nCell ) {
				if ( nCell.nodeName.toUpperCase() == "TD" ||
				     nCell.nodeName.toUpperCase() == "TH" )
				{
					/* Get the col and rowspan attributes from the DOM and sanitise them */
					iColspan = nCell.getAttribute('colspan') * 1;
					iRowspan = nCell.getAttribute('rowspan') * 1;
					iColspan = (!iColspan || iColspan===0 || iColspan===1) ? 1 : iColspan;
					iRowspan = (!iRowspan || iRowspan===0 || iRowspan===1) ? 1 : iRowspan;
	
					/* There might be colspan cells already in this row, so shift our target
					 * accordingly
					 */
					iColShifted = fnShiftCol( aLayout, i, iColumn );
	
					/* Cache calculation for unique columns */
					bUnique = iColspan === 1 ? true : false;
	
					/* If there is col / rowspan, copy the information into the layout grid */
					for ( l=0 ; l<iColspan ; l++ )
					{
						for ( k=0 ; k<iRowspan ; k++ )
						{
							aLayout[i+k][iColShifted+l] = {
								"cell": nCell,
								"unique": bUnique
							};
							aLayout[i+k].nTr = nTr;
						}
					}
				}
				nCell = nCell.nextSibling;
			}
		}
	}
	
	
	/**
	 * Get an array of unique th elements, one for each column
	 *  @param {object} oSettings dataTables settings object
	 *  @param {node} nHeader automatically detect the layout from this node - optional
	 *  @param {array} aLayout thead/tfoot layout from _fnDetectHeader - optional
	 *  @returns array {node} aReturn list of unique th's
	 *  @memberof DataTable#oApi
	 */
	function _fnGetUniqueThs ( oSettings, nHeader, aLayout )
	{
		var aReturn = [];
		if ( !aLayout )
		{
			aLayout = oSettings.aoHeader;
			if ( nHeader )
			{
				aLayout = [];
				_fnDetectHeader( aLayout, nHeader );
			}
		}
	
		for ( var i=0, iLen=aLayout.length ; i<iLen ; i++ )
		{
			for ( var j=0, jLen=aLayout[i].length ; j<jLen ; j++ )
			{
				if ( aLayout[i][j].unique &&
					 (!aReturn[j] || !oSettings.bSortCellsTop) )
				{
					aReturn[j] = aLayout[i][j].cell;
				}
			}
		}
	
		return aReturn;
	}
	
	/**
	 * Create an Ajax call based on the table's settings, taking into account that
	 * parameters can have multiple forms, and backwards compatibility.
	 *
	 * @param {object} oSettings dataTables settings object
	 * @param {array} data Data to send to the server, required by
	 *     DataTables - may be augmented by developer callbacks
	 * @param {function} fn Callback function to run when data is obtained
	 */
	function _fnBuildAjax( oSettings, data, fn )
	{
		// Compatibility with 1.9-, allow fnServerData and event to manipulate
		_fnCallbackFire( oSettings, 'aoServerParams', 'serverParams', [data] );
	
		// Convert to object based for 1.10+ if using the old array scheme which can
		// come from server-side processing or serverParams
		if ( data && $.isArray(data) ) {
			var tmp = {};
			var rbracket = /(.*?)\[\]$/;
	
			$.each( data, function (key, val) {
				var match = val.name.match(rbracket);
	
				if ( match ) {
					// Support for arrays
					var name = match[0];
	
					if ( ! tmp[ name ] ) {
						tmp[ name ] = [];
					}
					tmp[ name ].push( val.value );
				}
				else {
					tmp[val.name] = val.value;
				}
			} );
			data = tmp;
		}
	
		var ajaxData;
		var ajax = oSettings.ajax;
		var instance = oSettings.oInstance;
		var callback = function ( json ) {
			_fnCallbackFire( oSettings, null, 'xhr', [oSettings, json, oSettings.jqXHR] );
			fn( json );
		};
	
		if ( $.isPlainObject( ajax ) && ajax.data )
		{
			ajaxData = ajax.data;
	
			var newData = $.isFunction( ajaxData ) ?
				ajaxData( data, oSettings ) :  // fn can manipulate data or return
				ajaxData;                      // an object object or array to merge
	
			// If the function returned something, use that alone
			data = $.isFunction( ajaxData ) && newData ?
				newData :
				$.extend( true, data, newData );
	
			// Remove the data property as we've resolved it already and don't want
			// jQuery to do it again (it is restored at the end of the function)
			delete ajax.data;
		}
	
		var baseAjax = {
			"data": data,
			"success": function (json) {
				var error = json.error || json.sError;
				if ( error ) {
					_fnLog( oSettings, 0, error );
				}
	
				oSettings.json = json;
				callback( json );
			},
			"dataType": "json",
			"cache": false,
			"type": oSettings.sServerMethod,
			"error": function (xhr, error, thrown) {
				var ret = _fnCallbackFire( oSettings, null, 'xhr', [oSettings, null, oSettings.jqXHR] );
	
				if ( $.inArray( true, ret ) === -1 ) {
					if ( error == "parsererror" ) {
						_fnLog( oSettings, 0, 'Invalid JSON response', 1 );
					}
					else if ( xhr.readyState === 4 ) {
						_fnLog( oSettings, 0, 'Ajax error', 7 );
					}
				}
	
				_fnProcessingDisplay( oSettings, false );
			}
		};
	
		// Store the data submitted for the API
		oSettings.oAjaxData = data;
	
		// Allow plug-ins and external processes to modify the data
		_fnCallbackFire( oSettings, null, 'preXhr', [oSettings, data] );
	
		if ( oSettings.fnServerData )
		{
			// DataTables 1.9- compatibility
			oSettings.fnServerData.call( instance,
				oSettings.sAjaxSource,
				$.map( data, function (val, key) { // Need to convert back to 1.9 trad format
					return { name: key, value: val };
				} ),
				callback,
				oSettings
			);
		}
		else if ( oSettings.sAjaxSource || typeof ajax === 'string' )
		{
			// DataTables 1.9- compatibility
			oSettings.jqXHR = $.ajax( $.extend( baseAjax, {
				url: ajax || oSettings.sAjaxSource
			} ) );
		}
		else if ( $.isFunction( ajax ) )
		{
			// Is a function - let the caller define what needs to be done
			oSettings.jqXHR = ajax.call( instance, data, callback, oSettings );
		}
		else
		{
			// Object to extend the base settings
			oSettings.jqXHR = $.ajax( $.extend( baseAjax, ajax ) );
	
			// Restore for next time around
			ajax.data = ajaxData;
		}
	}
	
	
	/**
	 * Update the table using an Ajax call
	 *  @param {object} settings dataTables settings object
	 *  @returns {boolean} Block the table drawing or not
	 *  @memberof DataTable#oApi
	 */
	function _fnAjaxUpdate( settings )
	{
		if ( settings.bAjaxDataGet ) {
			settings.iDraw++;
			_fnProcessingDisplay( settings, true );
	
			_fnBuildAjax(
				settings,
				_fnAjaxParameters( settings ),
				function(json) {
					_fnAjaxUpdateDraw( settings, json );
				}
			);
	
			return false;
		}
		return true;
	}
	
	
	/**
	 * Build up the parameters in an object needed for a server-side processing
	 * request. Note that this is basically done twice, is different ways - a modern
	 * method which is used by default in DataTables 1.10 which uses objects and
	 * arrays, or the 1.9- method with is name / value pairs. 1.9 method is used if
	 * the sAjaxSource option is used in the initialisation, or the legacyAjax
	 * option is set.
	 *  @param {object} oSettings dataTables settings object
	 *  @returns {bool} block the table drawing or not
	 *  @memberof DataTable#oApi
	 */
	function _fnAjaxParameters( settings )
	{
		var
			columns = settings.aoColumns,
			columnCount = columns.length,
			features = settings.oFeatures,
			preSearch = settings.oPreviousSearch,
			preColSearch = settings.aoPreSearchCols,
			i, data = [], dataProp, column, columnSearch,
			sort = _fnSortFlatten( settings ),
			displayStart = settings._iDisplayStart,
			displayLength = features.bPaginate !== false ?
				settings._iDisplayLength :
				-1;
	
		var param = function ( name, value ) {
			data.push( { 'name': name, 'value': value } );
		};
	
		// DataTables 1.9- compatible method
		param( 'sEcho',          settings.iDraw );
		param( 'iColumns',       columnCount );
		param( 'sColumns',       _pluck( columns, 'sName' ).join(',') );
		param( 'iDisplayStart',  displayStart );
		param( 'iDisplayLength', displayLength );
	
		// DataTables 1.10+ method
		var d = {
			draw:    settings.iDraw,
			columns: [],
			order:   [],
			start:   displayStart,
			length:  displayLength,
			search:  {
				value: preSearch.sSearch,
				regex: preSearch.bRegex
			}
		};
	
		for ( i=0 ; i<columnCount ; i++ ) {
			column = columns[i];
			columnSearch = preColSearch[i];
			dataProp = typeof column.mData=="function" ? 'function' : column.mData ;
	
			d.columns.push( {
				data:       dataProp,
				name:       column.sName,
				searchable: column.bSearchable,
				orderable:  column.bSortable,
				search:     {
					value: columnSearch.sSearch,
					regex: columnSearch.bRegex
				}
			} );
	
			param( "mDataProp_"+i, dataProp );
	
			if ( features.bFilter ) {
				param( 'sSearch_'+i,     columnSearch.sSearch );
				param( 'bRegex_'+i,      columnSearch.bRegex );
				param( 'bSearchable_'+i, column.bSearchable );
			}
	
			if ( features.bSort ) {
				param( 'bSortable_'+i, column.bSortable );
			}
		}
	
		if ( features.bFilter ) {
			param( 'sSearch', preSearch.sSearch );
			param( 'bRegex', preSearch.bRegex );
		}
	
		if ( features.bSort ) {
			$.each( sort, function ( i, val ) {
				d.order.push( { column: val.col, dir: val.dir } );
	
				param( 'iSortCol_'+i, val.col );
				param( 'sSortDir_'+i, val.dir );
			} );
	
			param( 'iSortingCols', sort.length );
		}
	
		// If the legacy.ajax parameter is null, then we automatically decide which
		// form to use, based on sAjaxSource
		var legacy = DataTable.ext.legacy.ajax;
		if ( legacy === null ) {
			return settings.sAjaxSource ? data : d;
		}
	
		// Otherwise, if legacy has been specified then we use that to decide on the
		// form
		return legacy ? data : d;
	}
	
	
	/**
	 * Data the data from the server (nuking the old) and redraw the table
	 *  @param {object} oSettings dataTables settings object
	 *  @param {object} json json data return from the server.
	 *  @param {string} json.sEcho Tracking flag for DataTables to match requests
	 *  @param {int} json.iTotalRecords Number of records in the data set, not accounting for filtering
	 *  @param {int} json.iTotalDisplayRecords Number of records in the data set, accounting for filtering
	 *  @param {array} json.aaData The data to display on this page
	 *  @param {string} [json.sColumns] Column ordering (sName, comma separated)
	 *  @memberof DataTable#oApi
	 */
	function _fnAjaxUpdateDraw ( settings, json )
	{
		// v1.10 uses camelCase variables, while 1.9 uses Hungarian notation.
		// Support both
		var compat = function ( old, modern ) {
			return json[old] !== undefined ? json[old] : json[modern];
		};
	
		var data = _fnAjaxDataSrc( settings, json );
		var draw            = compat( 'sEcho',                'draw' );
		var recordsTotal    = compat( 'iTotalRecords',        'recordsTotal' );
		var recordsFiltered = compat( 'iTotalDisplayRecords', 'recordsFiltered' );
	
		if ( draw ) {
			// Protect against out of sequence returns
			if ( draw*1 < settings.iDraw ) {
				return;
			}
			settings.iDraw = draw * 1;
		}
	
		_fnClearTable( settings );
		settings._iRecordsTotal   = parseInt(recordsTotal, 10);
		settings._iRecordsDisplay = parseInt(recordsFiltered, 10);
	
		for ( var i=0, ien=data.length ; i<ien ; i++ ) {
			_fnAddData( settings, data[i] );
		}
		settings.aiDisplay = settings.aiDisplayMaster.slice();
	
		settings.bAjaxDataGet = false;
		_fnDraw( settings );
	
		if ( ! settings._bInitComplete ) {
			_fnInitComplete( settings, json );
		}
	
		settings.bAjaxDataGet = true;
		_fnProcessingDisplay( settings, false );
	}
	
	
	/**
	 * Get the data from the JSON data source to use for drawing a table. Using
	 * `_fnGetObjectDataFn` allows the data to be sourced from a property of the
	 * source object, or from a processing function.
	 *  @param {object} oSettings dataTables settings object
	 *  @param  {object} json Data source object / array from the server
	 *  @return {array} Array of data to use
	 */
	function _fnAjaxDataSrc ( oSettings, json )
	{
		var dataSrc = $.isPlainObject( oSettings.ajax ) && oSettings.ajax.dataSrc !== undefined ?
			oSettings.ajax.dataSrc :
			oSettings.sAjaxDataProp; // Compatibility with 1.9-.
	
		// Compatibility with 1.9-. In order to read from aaData, check if the
		// default has been changed, if not, check for aaData
		if ( dataSrc === 'data' ) {
			return json.aaData || json[dataSrc];
		}
	
		return dataSrc !== "" ?
			_fnGetObjectDataFn( dataSrc )( json ) :
			json;
	}
	
	/**
	 * Generate the node required for filtering text
	 *  @returns {node} Filter control element
	 *  @param {object} oSettings dataTables settings object
	 *  @memberof DataTable#oApi
	 */
	function _fnFeatureHtmlFilter ( settings )
	{
		var classes = settings.oClasses;
		var tableId = settings.sTableId;
		var language = settings.oLanguage;
		var previousSearch = settings.oPreviousSearch;
		var features = settings.aanFeatures;
		var input = '<input type="search" class="'+classes.sFilterInput+'"/>';
	
		var str = language.sSearch;
		str = str.match(/_INPUT_/) ?
			str.replace('_INPUT_', input) :
			str+input;
	
		var filter = $('<div/>', {
				'id': ! features.f ? tableId+'_filter' : null,
				'class': classes.sFilter
			} )
			.append( $('<label/>' ).append( str ) );
	
		var searchFn = function() {
			/* Update all other filter input elements for the new display */
			var n = features.f;
			var val = !this.value ? "" : this.value; // mental IE8 fix :-(
	
			/* Now do the filter */
			if ( val != previousSearch.sSearch ) {
				_fnFilterComplete( settings, {
					"sSearch": val,
					"bRegex": previousSearch.bRegex,
					"bSmart": previousSearch.bSmart ,
					"bCaseInsensitive": previousSearch.bCaseInsensitive
				} );
	
				// Need to redraw, without resorting
				settings._iDisplayStart = 0;
				_fnDraw( settings );
			}
		};
	
		var searchDelay = settings.searchDelay !== null ?
			settings.searchDelay :
			_fnDataSource( settings ) === 'ssp' ?
				400 :
				0;
	
		var jqFilter = $('input', filter)
			.val( previousSearch.sSearch )
			.attr( 'placeholder', language.sSearchPlaceholder )
			.bind(
				'keyup.DT search.DT input.DT paste.DT cut.DT',
				searchDelay ?
					_fnThrottle( searchFn, searchDelay ) :
					searchFn
			)
			.bind( 'keypress.DT', function(e) {
				/* Prevent form submission */
				if ( e.keyCode == 13 ) {
					return false;
				}
			} )
			.attr('aria-controls', tableId);
	
		// Update the input elements whenever the table is filtered
		$(settings.nTable).on( 'search.dt.DT', function ( ev, s ) {
			if ( settings === s ) {
				// IE9 throws an 'unknown error' if document.activeElement is used
				// inside an iframe or frame...
				try {
					if ( jqFilter[0] !== document.activeElement ) {
						jqFilter.val( previousSearch.sSearch );
					}
				}
				catch ( e ) {}
			}
		} );
	
		return filter[0];
	}
	
	
	/**
	 * Filter the table using both the global filter and column based filtering
	 *  @param {object} oSettings dataTables settings object
	 *  @param {object} oSearch search information
	 *  @param {int} [iForce] force a research of the master array (1) or not (undefined or 0)
	 *  @memberof DataTable#oApi
	 */
	function _fnFilterComplete ( oSettings, oInput, iForce )
	{
		var oPrevSearch = oSettings.oPreviousSearch;
		var aoPrevSearch = oSettings.aoPreSearchCols;
		var fnSaveFilter = function ( oFilter ) {
			/* Save the filtering values */
			oPrevSearch.sSearch = oFilter.sSearch;
			oPrevSearch.bRegex = oFilter.bRegex;
			oPrevSearch.bSmart = oFilter.bSmart;
			oPrevSearch.bCaseInsensitive = oFilter.bCaseInsensitive;
		};
		var fnRegex = function ( o ) {
			// Backwards compatibility with the bEscapeRegex option
			return o.bEscapeRegex !== undefined ? !o.bEscapeRegex : o.bRegex;
		};
	
		// Resolve any column types that are unknown due to addition or invalidation
		// @todo As per sort - can this be moved into an event handler?
		_fnColumnTypes( oSettings );
	
		/* In server-side processing all filtering is done by the server, so no point hanging around here */
		if ( _fnDataSource( oSettings ) != 'ssp' )
		{
			/* Global filter */
			_fnFilter( oSettings, oInput.sSearch, iForce, fnRegex(oInput), oInput.bSmart, oInput.bCaseInsensitive );
			fnSaveFilter( oInput );
	
			/* Now do the individual column filter */
			for ( var i=0 ; i<aoPrevSearch.length ; i++ )
			{
				_fnFilterColumn( oSettings, aoPrevSearch[i].sSearch, i, fnRegex(aoPrevSearch[i]),
					aoPrevSearch[i].bSmart, aoPrevSearch[i].bCaseInsensitive );
			}
	
			/* Custom filtering */
			_fnFilterCustom( oSettings );
		}
		else
		{
			fnSaveFilter( oInput );
		}
	
		/* Tell the draw function we have been filtering */
		oSettings.bFiltered = true;
		_fnCallbackFire( oSettings, null, 'search', [oSettings] );
	}
	
	
	/**
	 * Apply custom filtering functions
	 *  @param {object} oSettings dataTables settings object
	 *  @memberof DataTable#oApi
	 */
	function _fnFilterCustom( settings )
	{
		var filters = DataTable.ext.search;
		var displayRows = settings.aiDisplay;
		var row, rowIdx;
	
		for ( var i=0, ien=filters.length ; i<ien ; i++ ) {
			var rows = [];
	
			// Loop over each row and see if it should be included
			for ( var j=0, jen=displayRows.length ; j<jen ; j++ ) {
				rowIdx = displayRows[ j ];
				row = settings.aoData[ rowIdx ];
	
				if ( filters[i]( settings, row._aFilterData, rowIdx, row._aData, j ) ) {
					rows.push( rowIdx );
				}
			}
	
			// So the array reference doesn't break set the results into the
			// existing array
			displayRows.length = 0;
			$.merge( displayRows, rows );
		}
	}
	
	
	/**
	 * Filter the table on a per-column basis
	 *  @param {object} oSettings dataTables settings object
	 *  @param {string} sInput string to filter on
	 *  @param {int} iColumn column to filter
	 *  @param {bool} bRegex treat search string as a regular expression or not
	 *  @param {bool} bSmart use smart filtering or not
	 *  @param {bool} bCaseInsensitive Do case insenstive matching or not
	 *  @memberof DataTable#oApi
	 */
	function _fnFilterColumn ( settings, searchStr, colIdx, regex, smart, caseInsensitive )
	{
		if ( searchStr === '' ) {
			return;
		}
	
		var data;
		var display = settings.aiDisplay;
		var rpSearch = _fnFilterCreateSearch( searchStr, regex, smart, caseInsensitive );
	
		for ( var i=display.length-1 ; i>=0 ; i-- ) {
			data = settings.aoData[ display[i] ]._aFilterData[ colIdx ];
	
			if ( ! rpSearch.test( data ) ) {
				display.splice( i, 1 );
			}
		}
	}
	
	
	/**
	 * Filter the data table based on user input and draw the table
	 *  @param {object} settings dataTables settings object
	 *  @param {string} input string to filter on
	 *  @param {int} force optional - force a research of the master array (1) or not (undefined or 0)
	 *  @param {bool} regex treat as a regular expression or not
	 *  @param {bool} smart perform smart filtering or not
	 *  @param {bool} caseInsensitive Do case insenstive matching or not
	 *  @memberof DataTable#oApi
	 */
	function _fnFilter( settings, input, force, regex, smart, caseInsensitive )
	{
		var rpSearch = _fnFilterCreateSearch( input, regex, smart, caseInsensitive );
		var prevSearch = settings.oPreviousSearch.sSearch;
		var displayMaster = settings.aiDisplayMaster;
		var display, invalidated, i;
	
		// Need to take account of custom filtering functions - always filter
		if ( DataTable.ext.search.length !== 0 ) {
			force = true;
		}
	
		// Check if any of the rows were invalidated
		invalidated = _fnFilterData( settings );
	
		// If the input is blank - we just want the full data set
		if ( input.length <= 0 ) {
			settings.aiDisplay = displayMaster.slice();
		}
		else {
			// New search - start from the master array
			if ( invalidated ||
				 force ||
				 prevSearch.length > input.length ||
				 input.indexOf(prevSearch) !== 0 ||
				 settings.bSorted // On resort, the display master needs to be
				                  // re-filtered since indexes will have changed
			) {
				settings.aiDisplay = displayMaster.slice();
			}
	
			// Search the display array
			display = settings.aiDisplay;
	
			for ( i=display.length-1 ; i>=0 ; i-- ) {
				if ( ! rpSearch.test( settings.aoData[ display[i] ]._sFilterRow ) ) {
					display.splice( i, 1 );
				}
			}
		}
	}
	
	
	/**
	 * Build a regular expression object suitable for searching a table
	 *  @param {string} sSearch string to search for
	 *  @param {bool} bRegex treat as a regular expression or not
	 *  @param {bool} bSmart perform smart filtering or not
	 *  @param {bool} bCaseInsensitive Do case insensitive matching or not
	 *  @returns {RegExp} constructed object
	 *  @memberof DataTable#oApi
	 */
	function _fnFilterCreateSearch( search, regex, smart, caseInsensitive )
	{
		search = regex ?
			search :
			_fnEscapeRegex( search );
		
		if ( smart ) {
			/* For smart filtering we want to allow the search to work regardless of
			 * word order. We also want double quoted text to be preserved, so word
			 * order is important - a la google. So this is what we want to
			 * generate:
			 * 
			 * ^(?=.*?\bone\b)(?=.*?\btwo three\b)(?=.*?\bfour\b).*$
			 */
			var a = $.map( search.match( /"[^"]+"|[^ ]+/g ) || [''], function ( word ) {
				if ( word.charAt(0) === '"' ) {
					var m = word.match( /^"(.*)"$/ );
					word = m ? m[1] : word;
				}
	
				return word.replace('"', '');
			} );
	
			search = '^(?=.*?'+a.join( ')(?=.*?' )+').*$';
		}
	
		return new RegExp( search, caseInsensitive ? 'i' : '' );
	}
	
	
	/**
	 * Escape a string such that it can be used in a regular expression
	 *  @param {string} sVal string to escape
	 *  @returns {string} escaped string
	 *  @memberof DataTable#oApi
	 */
	function _fnEscapeRegex ( sVal )
	{
		return sVal.replace( _re_escape_regex, '\\$1' );
	}
	
	
	
	var __filter_div = $('<div>')[0];
	var __filter_div_textContent = __filter_div.textContent !== undefined;
	
	// Update the filtering data for each row if needed (by invalidation or first run)
	function _fnFilterData ( settings )
	{
		var columns = settings.aoColumns;
		var column;
		var i, j, ien, jen, filterData, cellData, row;
		var fomatters = DataTable.ext.type.search;
		var wasInvalidated = false;
	
		for ( i=0, ien=settings.aoData.length ; i<ien ; i++ ) {
			row = settings.aoData[i];
	
			if ( ! row._aFilterData ) {
				filterData = [];
	
				for ( j=0, jen=columns.length ; j<jen ; j++ ) {
					column = columns[j];
	
					if ( column.bSearchable ) {
						cellData = _fnGetCellData( settings, i, j, 'filter' );
	
						if ( fomatters[ column.sType ] ) {
							cellData = fomatters[ column.sType ]( cellData );
						}
	
						// Search in DataTables 1.10 is string based. In 1.11 this
						// should be altered to also allow strict type checking.
						if ( cellData === null ) {
							cellData = '';
						}
	
						if ( typeof cellData !== 'string' && cellData.toString ) {
							cellData = cellData.toString();
						}
					}
					else {
						cellData = '';
					}
	
					// If it looks like there is an HTML entity in the string,
					// attempt to decode it so sorting works as expected. Note that
					// we could use a single line of jQuery to do this, but the DOM
					// method used here is much faster http://jsperf.com/html-decode
					if ( cellData.indexOf && cellData.indexOf('&') !== -1 ) {
						__filter_div.innerHTML = cellData;
						cellData = __filter_div_textContent ?
							__filter_div.textContent :
							__filter_div.innerText;
					}
	
					if ( cellData.replace ) {
						cellData = cellData.replace(/[\r\n]/g, '');
					}
	
					filterData.push( cellData );
				}
	
				row._aFilterData = filterData;
				row._sFilterRow = filterData.join('  ');
				wasInvalidated = true;
			}
		}
	
		return wasInvalidated;
	}
	
	
	/**
	 * Convert from the internal Hungarian notation to camelCase for external
	 * interaction
	 *  @param {object} obj Object to convert
	 *  @returns {object} Inverted object
	 *  @memberof DataTable#oApi
	 */
	function _fnSearchToCamel ( obj )
	{
		return {
			search:          obj.sSearch,
			smart:           obj.bSmart,
			regex:           obj.bRegex,
			caseInsensitive: obj.bCaseInsensitive
		};
	}
	
	
	
	/**
	 * Convert from camelCase notation to the internal Hungarian. We could use the
	 * Hungarian convert function here, but this is cleaner
	 *  @param {object} obj Object to convert
	 *  @returns {object} Inverted object
	 *  @memberof DataTable#oApi
	 */
	function _fnSearchToHung ( obj )
	{
		return {
			sSearch:          obj.search,
			bSmart:           obj.smart,
			bRegex:           obj.regex,
			bCaseInsensitive: obj.caseInsensitive
		};
	}
	
	/**
	 * Generate the node required for the info display
	 *  @param {object} oSettings dataTables settings object
	 *  @returns {node} Information element
	 *  @memberof DataTable#oApi
	 */
	function _fnFeatureHtmlInfo ( settings )
	{
		var
			tid = settings.sTableId,
			nodes = settings.aanFeatures.i,
			n = $('<div/>', {
				'class': settings.oClasses.sInfo,
				'id': ! nodes ? tid+'_info' : null
			} );
	
		if ( ! nodes ) {
			// Update display on each draw
			settings.aoDrawCallback.push( {
				"fn": _fnUpdateInfo,
				"sName": "information"
			} );
	
			n
				.attr( 'role', 'status' )
				.attr( 'aria-live', 'polite' );
	
			// Table is described by our info div
			$(settings.nTable).attr( 'aria-describedby', tid+'_info' );
		}
	
		return n[0];
	}
	
	
	/**
	 * Update the information elements in the display
	 *  @param {object} settings dataTables settings object
	 *  @memberof DataTable#oApi
	 */
	function _fnUpdateInfo ( settings )
	{
		/* Show information about the table */
		var nodes = settings.aanFeatures.i;
		if ( nodes.length === 0 ) {
			return;
		}
	
		var
			lang  = settings.oLanguage,
			start = settings._iDisplayStart+1,
			end   = settings.fnDisplayEnd(),
			max   = settings.fnRecordsTotal(),
			total = settings.fnRecordsDisplay(),
			out   = total ?
				lang.sInfo :
				lang.sInfoEmpty;
	
		if ( total !== max ) {
			/* Record set after filtering */
			out += ' ' + lang.sInfoFiltered;
		}
	
		// Convert the macros
		out += lang.sInfoPostFix;
		out = _fnInfoMacros( settings, out );
	
		var callback = lang.fnInfoCallback;
		if ( callback !== null ) {
			out = callback.call( settings.oInstance,
				settings, start, end, max, total, out
			);
		}
	
		$(nodes).html( out );
	}
	
	
	function _fnInfoMacros ( settings, str )
	{
		// When infinite scrolling, we are always starting at 1. _iDisplayStart is used only
		// internally
		var
			formatter  = settings.fnFormatNumber,
			start      = settings._iDisplayStart+1,
			len        = settings._iDisplayLength,
			vis        = settings.fnRecordsDisplay(),
			all        = len === -1;
	
		return str.
			replace(/_START_/g, formatter.call( settings, start ) ).
			replace(/_END_/g,   formatter.call( settings, settings.fnDisplayEnd() ) ).
			replace(/_MAX_/g,   formatter.call( settings, settings.fnRecordsTotal() ) ).
			replace(/_TOTAL_/g, formatter.call( settings, vis ) ).
			replace(/_PAGE_/g,  formatter.call( settings, all ? 1 : Math.ceil( start / len ) ) ).
			replace(/_PAGES_/g, formatter.call( settings, all ? 1 : Math.ceil( vis / len ) ) );
	}
	
	
	
	/**
	 * Draw the table for the first time, adding all required features
	 *  @param {object} settings dataTables settings object
	 *  @memberof DataTable#oApi
	 */
	function _fnInitialise ( settings )
	{
		var i, iLen, iAjaxStart=settings.iInitDisplayStart;
		var columns = settings.aoColumns, column;
		var features = settings.oFeatures;
		var deferLoading = settings.bDeferLoading; // value modified by the draw
	
		/* Ensure that the table data is fully initialised */
		if ( ! settings.bInitialised ) {
			setTimeout( function(){ _fnInitialise( settings ); }, 200 );
			return;
		}
	
		/* Show the display HTML options */
		_fnAddOptionsHtml( settings );
	
		/* Build and draw the header / footer for the table */
		_fnBuildHead( settings );
		_fnDrawHead( settings, settings.aoHeader );
		_fnDrawHead( settings, settings.aoFooter );
	
		/* Okay to show that something is going on now */
		_fnProcessingDisplay( settings, true );
	
		/* Calculate sizes for columns */
		if ( features.bAutoWidth ) {
			_fnCalculateColumnWidths( settings );
		}
	
		for ( i=0, iLen=columns.length ; i<iLen ; i++ ) {
			column = columns[i];
	
			if ( column.sWidth ) {
				column.nTh.style.width = _fnStringToCss( column.sWidth );
			}
		}
	
		_fnCallbackFire( settings, null, 'preInit', [settings] );
	
		// If there is default sorting required - let's do it. The sort function
		// will do the drawing for us. Otherwise we draw the table regardless of the
		// Ajax source - this allows the table to look initialised for Ajax sourcing
		// data (show 'loading' message possibly)
		_fnReDraw( settings );
	
		// Server-side processing init complete is done by _fnAjaxUpdateDraw
		var dataSrc = _fnDataSource( settings );
		if ( dataSrc != 'ssp' || deferLoading ) {
			// if there is an ajax source load the data
			if ( dataSrc == 'ajax' ) {
				_fnBuildAjax( settings, [], function(json) {
					var aData = _fnAjaxDataSrc( settings, json );
	
					// Got the data - add it to the table
					for ( i=0 ; i<aData.length ; i++ ) {
						_fnAddData( settings, aData[i] );
					}
	
					// Reset the init display for cookie saving. We've already done
					// a filter, and therefore cleared it before. So we need to make
					// it appear 'fresh'
					settings.iInitDisplayStart = iAjaxStart;
	
					_fnReDraw( settings );
	
					_fnProcessingDisplay( settings, false );
					_fnInitComplete( settings, json );
				}, settings );
			}
			else {
				_fnProcessingDisplay( settings, false );
				_fnInitComplete( settings );
			}
		}
	}
	
	
	/**
	 * Draw the table for the first time, adding all required features
	 *  @param {object} oSettings dataTables settings object
	 *  @param {object} [json] JSON from the server that completed the table, if using Ajax source
	 *    with client-side processing (optional)
	 *  @memberof DataTable#oApi
	 */
	function _fnInitComplete ( settings, json )
	{
		settings._bInitComplete = true;
	
		// When data was added after the initialisation (data or Ajax) we need to
		// calculate the column sizing
		if ( json || settings.oInit.aaData ) {
			_fnAdjustColumnSizing( settings );
		}
	
		_fnCallbackFire( settings, null, 'plugin-init', [settings, json] );
		_fnCallbackFire( settings, 'aoInitComplete', 'init', [settings, json] );
	}
	
	
	function _fnLengthChange ( settings, val )
	{
		var len = parseInt( val, 10 );
		settings._iDisplayLength = len;
	
		_fnLengthOverflow( settings );
	
		// Fire length change event
		_fnCallbackFire( settings, null, 'length', [settings, len] );
	}
	
	
	/**
	 * Generate the node required for user display length changing
	 *  @param {object} settings dataTables settings object
	 *  @returns {node} Display length feature node
	 *  @memberof DataTable#oApi
	 */
	function _fnFeatureHtmlLength ( settings )
	{
		var
			classes  = settings.oClasses,
			tableId  = settings.sTableId,
			menu     = settings.aLengthMenu,
			d2       = $.isArray( menu[0] ),
			lengths  = d2 ? menu[0] : menu,
			language = d2 ? menu[1] : menu;
	
		var select = $('<select/>', {
			'name':          tableId+'_length',
			'aria-controls': tableId,
			'class':         classes.sLengthSelect
		} );
	
		for ( var i=0, ien=lengths.length ; i<ien ; i++ ) {
			select[0][ i ] = new Option( language[i], lengths[i] );
		}
	
		var div = $('<div><label/></div>').addClass( classes.sLength );
		if ( ! settings.aanFeatures.l ) {
			div[0].id = tableId+'_length';
		}
	
		div.children().append(
			settings.oLanguage.sLengthMenu.replace( '_MENU_', select[0].outerHTML )
		);
	
		// Can't use `select` variable as user might provide their own and the
		// reference is broken by the use of outerHTML
		$('select', div)
			.val( settings._iDisplayLength )
			.bind( 'change.DT', function(e) {
				_fnLengthChange( settings, $(this).val() );
				_fnDraw( settings );
			} );
	
		// Update node value whenever anything changes the table's length
		$(settings.nTable).bind( 'length.dt.DT', function (e, s, len) {
			if ( settings === s ) {
				$('select', div).val( len );
			}
		} );
	
		return div[0];
	}
	
	
	
	/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
	 * Note that most of the paging logic is done in
	 * DataTable.ext.pager
	 */
	
	/**
	 * Generate the node required for default pagination
	 *  @param {object} oSettings dataTables settings object
	 *  @returns {node} Pagination feature node
	 *  @memberof DataTable#oApi
	 */
	function _fnFeatureHtmlPaginate ( settings )
	{
		var
			type   = settings.sPaginationType,
			plugin = DataTable.ext.pager[ type ],
			modern = typeof plugin === 'function',
			redraw = function( settings ) {
				_fnDraw( settings );
			},
			node = $('<div/>').addClass( settings.oClasses.sPaging + type )[0],
			features = settings.aanFeatures;
	
		if ( ! modern ) {
			plugin.fnInit( settings, node, redraw );
		}
	
		/* Add a draw callback for the pagination on first instance, to update the paging display */
		if ( ! features.p )
		{
			node.id = settings.sTableId+'_paginate';
	
			settings.aoDrawCallback.push( {
				"fn": function( settings ) {
					if ( modern ) {
						var
							start      = settings._iDisplayStart,
							len        = settings._iDisplayLength,
							visRecords = settings.fnRecordsDisplay(),
							all        = len === -1,
							page = all ? 0 : Math.ceil( start / len ),
							pages = all ? 1 : Math.ceil( visRecords / len ),
							buttons = plugin(page, pages),
							i, ien;
	
						for ( i=0, ien=features.p.length ; i<ien ; i++ ) {
							_fnRenderer( settings, 'pageButton' )(
								settings, features.p[i], i, buttons, page, pages
							);
						}
					}
					else {
						plugin.fnUpdate( settings, redraw );
					}
				},
				"sName": "pagination"
			} );
		}
	
		return node;
	}
	
	
	/**
	 * Alter the display settings to change the page
	 *  @param {object} settings DataTables settings object
	 *  @param {string|int} action Paging action to take: "first", "previous",
	 *    "next" or "last" or page number to jump to (integer)
	 *  @param [bool] redraw Automatically draw the update or not
	 *  @returns {bool} true page has changed, false - no change
	 *  @memberof DataTable#oApi
	 */
	function _fnPageChange ( settings, action, redraw )
	{
		var
			start     = settings._iDisplayStart,
			len       = settings._iDisplayLength,
			records   = settings.fnRecordsDisplay();
	
		if ( records === 0 || len === -1 )
		{
			start = 0;
		}
		else if ( typeof action === "number" )
		{
			start = action * len;
	
			if ( start > records )
			{
				start = 0;
			}
		}
		else if ( action == "first" )
		{
			start = 0;
		}
		else if ( action == "previous" )
		{
			start = len >= 0 ?
				start - len :
				0;
	
			if ( start < 0 )
			{
			  start = 0;
			}
		}
		else if ( action == "next" )
		{
			if ( start + len < records )
			{
				start += len;
			}
		}
		else if ( action == "last" )
		{
			start = Math.floor( (records-1) / len) * len;
		}
		else
		{
			_fnLog( settings, 0, "Unknown paging action: "+action, 5 );
		}
	
		var changed = settings._iDisplayStart !== start;
		settings._iDisplayStart = start;
	
		if ( changed ) {
			_fnCallbackFire( settings, null, 'page', [settings] );
	
			if ( redraw ) {
				_fnDraw( settings );
			}
		}
	
		return changed;
	}
	
	
	
	/**
	 * Generate the node required for the processing node
	 *  @param {object} settings dataTables settings object
	 *  @returns {node} Processing element
	 *  @memberof DataTable#oApi
	 */
	function _fnFeatureHtmlProcessing ( settings )
	{
		return $('<div/>', {
				'id': ! settings.aanFeatures.r ? settings.sTableId+'_processing' : null,
				'class': settings.oClasses.sProcessing
			} )
			.html( settings.oLanguage.sProcessing )
			.insertBefore( settings.nTable )[0];
	}
	
	
	/**
	 * Display or hide the processing indicator
	 *  @param {object} settings dataTables settings object
	 *  @param {bool} show Show the processing indicator (true) or not (false)
	 *  @memberof DataTable#oApi
	 */
	function _fnProcessingDisplay ( settings, show )
	{
		if ( settings.oFeatures.bProcessing ) {
			$(settings.aanFeatures.r).css( 'display', show ? 'block' : 'none' );
		}
	
		_fnCallbackFire( settings, null, 'processing', [settings, show] );
	}
	
	/**
	 * Add any control elements for the table - specifically scrolling
	 *  @param {object} settings dataTables settings object
	 *  @returns {node} Node to add to the DOM
	 *  @memberof DataTable#oApi
	 */
	function _fnFeatureHtmlTable ( settings )
	{
		var table = $(settings.nTable);
	
		// Add the ARIA grid role to the table
		table.attr( 'role', 'grid' );
	
		// Scrolling from here on in
		var scroll = settings.oScroll;
	
		if ( scroll.sX === '' && scroll.sY === '' ) {
			return settings.nTable;
		}
	
		var scrollX = scroll.sX;
		var scrollY = scroll.sY;
		var classes = settings.oClasses;
		var caption = table.children('caption');
		var captionSide = caption.length ? caption[0]._captionSide : null;
		var headerClone = $( table[0].cloneNode(false) );
		var footerClone = $( table[0].cloneNode(false) );
		var footer = table.children('tfoot');
		var _div = '<div/>';
		var size = function ( s ) {
			return !s ? null : _fnStringToCss( s );
		};
	
		if ( ! footer.length ) {
			footer = null;
		}
	
		/*
		 * The HTML structure that we want to generate in this function is:
		 *  div - scroller
		 *    div - scroll head
		 *      div - scroll head inner
		 *        table - scroll head table
		 *          thead - thead
		 *    div - scroll body
		 *      table - table (master table)
		 *        thead - thead clone for sizing
		 *        tbody - tbody
		 *    div - scroll foot
		 *      div - scroll foot inner
		 *        table - scroll foot table
		 *          tfoot - tfoot
		 */
		var scroller = $( _div, { 'class': classes.sScrollWrapper } )
			.append(
				$(_div, { 'class': classes.sScrollHead } )
					.css( {
						overflow: 'hidden',
						position: 'relative',
						border: 0,
						width: scrollX ? size(scrollX) : '100%'
					} )
					.append(
						$(_div, { 'class': classes.sScrollHeadInner } )
							.css( {
								'box-sizing': 'content-box',
								width: scroll.sXInner || '100%'
							} )
							.append(
								headerClone
									.removeAttr('id')
									.css( 'margin-left', 0 )
									.append( captionSide === 'top' ? caption : null )
									.append(
										table.children('thead')
									)
							)
					)
			)
			.append(
				$(_div, { 'class': classes.sScrollBody } )
					.css( {
						position: 'relative',
						overflow: 'auto',
						width: size( scrollX )
					} )
					.append( table )
			);
	
		if ( footer ) {
			scroller.append(
				$(_div, { 'class': classes.sScrollFoot } )
					.css( {
						overflow: 'hidden',
						border: 0,
						width: scrollX ? size(scrollX) : '100%'
					} )
					.append(
						$(_div, { 'class': classes.sScrollFootInner } )
							.append(
								footerClone
									.removeAttr('id')
									.css( 'margin-left', 0 )
									.append( captionSide === 'bottom' ? caption : null )
									.append(
										table.children('tfoot')
									)
							)
					)
			);
		}
	
		var children = scroller.children();
		var scrollHead = children[0];
		var scrollBody = children[1];
		var scrollFoot = footer ? children[2] : null;
	
		// When the body is scrolled, then we also want to scroll the headers
		if ( scrollX ) {
			$(scrollBody).on( 'scroll.DT', function (e) {
				var scrollLeft = this.scrollLeft;
	
				scrollHead.scrollLeft = scrollLeft;
	
				if ( footer ) {
					scrollFoot.scrollLeft = scrollLeft;
				}
			} );
		}
	
		$(scrollBody).css(
			scrollY && scroll.bCollapse ? 'max-height' : 'height', 
			scrollY
		);
	
		settings.nScrollHead = scrollHead;
		settings.nScrollBody = scrollBody;
		settings.nScrollFoot = scrollFoot;
	
		// On redraw - align columns
		settings.aoDrawCallback.push( {
			"fn": _fnScrollDraw,
			"sName": "scrolling"
		} );
	
		return scroller[0];
	}
	
	
	
	/**
	 * Update the header, footer and body tables for resizing - i.e. column
	 * alignment.
	 *
	 * Welcome to the most horrible function DataTables. The process that this
	 * function follows is basically:
	 *   1. Re-create the table inside the scrolling div
	 *   2. Take live measurements from the DOM
	 *   3. Apply the measurements to align the columns
	 *   4. Clean up
	 *
	 *  @param {object} settings dataTables settings object
	 *  @memberof DataTable#oApi
	 */
	function _fnScrollDraw ( settings )
	{
		// Given that this is such a monster function, a lot of variables are use
		// to try and keep the minimised size as small as possible
		var
			scroll         = settings.oScroll,
			scrollX        = scroll.sX,
			scrollXInner   = scroll.sXInner,
			scrollY        = scroll.sY,
			barWidth       = scroll.iBarWidth,
			divHeader      = $(settings.nScrollHead),
			divHeaderStyle = divHeader[0].style,
			divHeaderInner = divHeader.children('div'),
			divHeaderInnerStyle = divHeaderInner[0].style,
			divHeaderTable = divHeaderInner.children('table'),
			divBodyEl      = settings.nScrollBody,
			divBody        = $(divBodyEl),
			divBodyStyle   = divBodyEl.style,
			divFooter      = $(settings.nScrollFoot),
			divFooterInner = divFooter.children('div'),
			divFooterTable = divFooterInner.children('table'),
			header         = $(settings.nTHead),
			table          = $(settings.nTable),
			tableEl        = table[0],
			tableStyle     = tableEl.style,
			footer         = settings.nTFoot ? $(settings.nTFoot) : null,
			browser        = settings.oBrowser,
			ie67           = browser.bScrollOversize,
			headerTrgEls, footerTrgEls,
			headerSrcEls, footerSrcEls,
			headerCopy, footerCopy,
			headerWidths=[], footerWidths=[],
			headerContent=[],
			idx, correction, sanityWidth,
			zeroOut = function(nSizer) {
				var style = nSizer.style;
				style.paddingTop = "0";
				style.paddingBottom = "0";
				style.borderTopWidth = "0";
				style.borderBottomWidth = "0";
				style.height = 0;
			};
	
		// If the scrollbar visibility has changed from the last draw, we need to
		// adjust the column sizes as the table width will have changed to account
		// for the scrollbar
		var scrollBarVis = divBodyEl.scrollHeight > divBodyEl.clientHeight;
		
		if ( settings.scrollBarVis !== scrollBarVis && settings.scrollBarVis !== undefined ) {
			settings.scrollBarVis = scrollBarVis;
			_fnAdjustColumnSizing( settings );
			return; // adjust column sizing will call this function again
		}
		else {
			settings.scrollBarVis = scrollBarVis;
		}
	
		/*
		 * 1. Re-create the table inside the scrolling div
		 */
	
		// Remove the old minimised thead and tfoot elements in the inner table
		table.children('thead, tfoot').remove();
	
		// Clone the current header and footer elements and then place it into the inner table
		headerCopy = header.clone().prependTo( table );
		headerTrgEls = header.find('tr'); // original header is in its own table
		headerSrcEls = headerCopy.find('tr');
		headerCopy.find('th, td').removeAttr('tabindex');
	
		if ( footer ) {
			footerCopy = footer.clone().prependTo( table );
			footerTrgEls = footer.find('tr'); // the original tfoot is in its own table and must be sized
			footerSrcEls = footerCopy.find('tr');
		}
	
	
		/*
		 * 2. Take live measurements from the DOM - do not alter the DOM itself!
		 */
	
		// Remove old sizing and apply the calculated column widths
		// Get the unique column headers in the newly created (cloned) header. We want to apply the
		// calculated sizes to this header
		if ( ! scrollX )
		{
			divBodyStyle.width = '100%';
			divHeader[0].style.width = '100%';
		}
	
		$.each( _fnGetUniqueThs( settings, headerCopy ), function ( i, el ) {
			idx = _fnVisibleToColumnIndex( settings, i );
			el.style.width = settings.aoColumns[idx].sWidth;
		} );
	
		if ( footer ) {
			_fnApplyToChildren( function(n) {
				n.style.width = "";
			}, footerSrcEls );
		}
	
		// Size the table as a whole
		sanityWidth = table.outerWidth();
		if ( scrollX === "" ) {
			// No x scrolling
			tableStyle.width = "100%";
	
			// IE7 will make the width of the table when 100% include the scrollbar
			// - which is shouldn't. When there is a scrollbar we need to take this
			// into account.
			if ( ie67 && (table.find('tbody').height() > divBodyEl.offsetHeight ||
				divBody.css('overflow-y') == "scroll")
			) {
				tableStyle.width = _fnStringToCss( table.outerWidth() - barWidth);
			}
	
			// Recalculate the sanity width
			sanityWidth = table.outerWidth();
		}
		else if ( scrollXInner !== "" ) {
			// legacy x scroll inner has been given - use it
			tableStyle.width = _fnStringToCss(scrollXInner);
	
			// Recalculate the sanity width
			sanityWidth = table.outerWidth();
		}
	
		// Hidden header should have zero height, so remove padding and borders. Then
		// set the width based on the real headers
	
		// Apply all styles in one pass
		_fnApplyToChildren( zeroOut, headerSrcEls );
	
		// Read all widths in next pass
		_fnApplyToChildren( function(nSizer) {
			headerContent.push( nSizer.innerHTML );
			headerWidths.push( _fnStringToCss( $(nSizer).css('width') ) );
		}, headerSrcEls );
	
		// Apply all widths in final pass
		_fnApplyToChildren( function(nToSize, i) {
			nToSize.style.width = headerWidths[i];
		}, headerTrgEls );
	
		$(headerSrcEls).height(0);
	
		/* Same again with the footer if we have one */
		if ( footer )
		{
			_fnApplyToChildren( zeroOut, footerSrcEls );
	
			_fnApplyToChildren( function(nSizer) {
				footerWidths.push( _fnStringToCss( $(nSizer).css('width') ) );
			}, footerSrcEls );
	
			_fnApplyToChildren( function(nToSize, i) {
				nToSize.style.width = footerWidths[i];
			}, footerTrgEls );
	
			$(footerSrcEls).height(0);
		}
	
	
		/*
		 * 3. Apply the measurements
		 */
	
		// "Hide" the header and footer that we used for the sizing. We need to keep
		// the content of the cell so that the width applied to the header and body
		// both match, but we want to hide it completely. We want to also fix their
		// width to what they currently are
		_fnApplyToChildren( function(nSizer, i) {
			nSizer.innerHTML = '<div class="dataTables_sizing" style="height:0;overflow:hidden;">'+headerContent[i]+'</div>';
			nSizer.style.width = headerWidths[i];
		}, headerSrcEls );
	
		if ( footer )
		{
			_fnApplyToChildren( function(nSizer, i) {
				nSizer.innerHTML = "";
				nSizer.style.width = footerWidths[i];
			}, footerSrcEls );
		}
	
		// Sanity check that the table is of a sensible width. If not then we are going to get
		// misalignment - try to prevent this by not allowing the table to shrink below its min width
		if ( table.outerWidth() < sanityWidth )
		{
			// The min width depends upon if we have a vertical scrollbar visible or not */
			correction = ((divBodyEl.scrollHeight > divBodyEl.offsetHeight ||
				divBody.css('overflow-y') == "scroll")) ?
					sanityWidth+barWidth :
					sanityWidth;
	
			// IE6/7 are a law unto themselves...
			if ( ie67 && (divBodyEl.scrollHeight >
				divBodyEl.offsetHeight || divBody.css('overflow-y') == "scroll")
			) {
				tableStyle.width = _fnStringToCss( correction-barWidth );
			}
	
			// And give the user a warning that we've stopped the table getting too small
			if ( scrollX === "" || scrollXInner !== "" ) {
				_fnLog( settings, 1, 'Possible column misalignment', 6 );
			}
		}
		else
		{
			correction = '100%';
		}
	
		// Apply to the container elements
		divBodyStyle.width = _fnStringToCss( correction );
		divHeaderStyle.width = _fnStringToCss( correction );
	
		if ( footer ) {
			settings.nScrollFoot.style.width = _fnStringToCss( correction );
		}
	
	
		/*
		 * 4. Clean up
		 */
		if ( ! scrollY ) {
			/* IE7< puts a vertical scrollbar in place (when it shouldn't be) due to subtracting
			 * the scrollbar height from the visible display, rather than adding it on. We need to
			 * set the height in order to sort this. Don't want to do it in any other browsers.
			 */
			if ( ie67 ) {
				divBodyStyle.height = _fnStringToCss( tableEl.offsetHeight+barWidth );
			}
		}
	
		/* Finally set the width's of the header and footer tables */
		var iOuterWidth = table.outerWidth();
		divHeaderTable[0].style.width = _fnStringToCss( iOuterWidth );
		divHeaderInnerStyle.width = _fnStringToCss( iOuterWidth );
	
		// Figure out if there are scrollbar present - if so then we need a the header and footer to
		// provide a bit more space to allow "overflow" scrolling (i.e. past the scrollbar)
		var bScrolling = table.height() > divBodyEl.clientHeight || divBody.css('overflow-y') == "scroll";
		var padding = 'padding' + (browser.bScrollbarLeft ? 'Left' : 'Right' );
		divHeaderInnerStyle[ padding ] = bScrolling ? barWidth+"px" : "0px";
	
		if ( footer ) {
			divFooterTable[0].style.width = _fnStringToCss( iOuterWidth );
			divFooterInner[0].style.width = _fnStringToCss( iOuterWidth );
			divFooterInner[0].style[padding] = bScrolling ? barWidth+"px" : "0px";
		}
	
		/* Adjust the position of the header in case we loose the y-scrollbar */
		divBody.scroll();
	
		// If sorting or filtering has occurred, jump the scrolling back to the top
		// only if we aren't holding the position
		if ( (settings.bSorted || settings.bFiltered) && ! settings._drawHold ) {
			divBodyEl.scrollTop = 0;
		}
	}
	
	
	
	/**
	 * Apply a given function to the display child nodes of an element array (typically
	 * TD children of TR rows
	 *  @param {function} fn Method to apply to the objects
	 *  @param array {nodes} an1 List of elements to look through for display children
	 *  @param array {nodes} an2 Another list (identical structure to the first) - optional
	 *  @memberof DataTable#oApi
	 */
	function _fnApplyToChildren( fn, an1, an2 )
	{
		var index=0, i=0, iLen=an1.length;
		var nNode1, nNode2;
	
		while ( i < iLen ) {
			nNode1 = an1[i].firstChild;
			nNode2 = an2 ? an2[i].firstChild : null;
	
			while ( nNode1 ) {
				if ( nNode1.nodeType === 1 ) {
					if ( an2 ) {
						fn( nNode1, nNode2, index );
					}
					else {
						fn( nNode1, index );
					}
	
					index++;
				}
	
				nNode1 = nNode1.nextSibling;
				nNode2 = an2 ? nNode2.nextSibling : null;
			}
	
			i++;
		}
	}
	
	
	
	var __re_html_remove = /<.*?>/g;
	
	
	/**
	 * Calculate the width of columns for the table
	 *  @param {object} oSettings dataTables settings object
	 *  @memberof DataTable#oApi
	 */
	function _fnCalculateColumnWidths ( oSettings )
	{
		var
			table = oSettings.nTable,
			columns = oSettings.aoColumns,
			scroll = oSettings.oScroll,
			scrollY = scroll.sY,
			scrollX = scroll.sX,
			scrollXInner = scroll.sXInner,
			columnCount = columns.length,
			visibleColumns = _fnGetColumns( oSettings, 'bVisible' ),
			headerCells = $('th', oSettings.nTHead),
			tableWidthAttr = table.getAttribute('width'), // from DOM element
			tableContainer = table.parentNode,
			userInputs = false,
			i, column, columnIdx, width, outerWidth,
			browser = oSettings.oBrowser,
			ie67 = browser.bScrollOversize;
	
		var styleWidth = table.style.width;
		if ( styleWidth && styleWidth.indexOf('%') !== -1 ) {
			tableWidthAttr = styleWidth;
		}
	
		/* Convert any user input sizes into pixel sizes */
		for ( i=0 ; i<visibleColumns.length ; i++ ) {
			column = columns[ visibleColumns[i] ];
	
			if ( column.sWidth !== null ) {
				column.sWidth = _fnConvertToWidth( column.sWidthOrig, tableContainer );
	
				userInputs = true;
			}
		}
	
		/* If the number of columns in the DOM equals the number that we have to
		 * process in DataTables, then we can use the offsets that are created by
		 * the web- browser. No custom sizes can be set in order for this to happen,
		 * nor scrolling used
		 */
		if ( ie67 || ! userInputs && ! scrollX && ! scrollY &&
		     columnCount == _fnVisbleColumns( oSettings ) &&
		     columnCount == headerCells.length
		) {
			for ( i=0 ; i<columnCount ; i++ ) {
				var colIdx = _fnVisibleToColumnIndex( oSettings, i );
	
				if ( colIdx !== null ) {
					columns[ colIdx ].sWidth = _fnStringToCss( headerCells.eq(i).width() );
				}
			}
		}
		else
		{
			// Otherwise construct a single row, worst case, table with the widest
			// node in the data, assign any user defined widths, then insert it into
			// the DOM and allow the browser to do all the hard work of calculating
			// table widths
			var tmpTable = $(table).clone() // don't use cloneNode - IE8 will remove events on the main table
				.css( 'visibility', 'hidden' )
				.removeAttr( 'id' );
	
			// Clean up the table body
			tmpTable.find('tbody tr').remove();
			var tr = $('<tr/>').appendTo( tmpTable.find('tbody') );
	
			// Clone the table header and footer - we can't use the header / footer
			// from the cloned table, since if scrolling is active, the table's
			// real header and footer are contained in different table tags
			tmpTable.find('thead, tfoot').remove();
			tmpTable
				.append( $(oSettings.nTHead).clone() )
				.append( $(oSettings.nTFoot).clone() );
	
			// Remove any assigned widths from the footer (from scrolling)
			tmpTable.find('tfoot th, tfoot td').css('width', '');
	
			// Apply custom sizing to the cloned header
			headerCells = _fnGetUniqueThs( oSettings, tmpTable.find('thead')[0] );
	
			for ( i=0 ; i<visibleColumns.length ; i++ ) {
				column = columns[ visibleColumns[i] ];
	
				headerCells[i].style.width = column.sWidthOrig !== null && column.sWidthOrig !== '' ?
					_fnStringToCss( column.sWidthOrig ) :
					'';
	
				// For scrollX we need to force the column width otherwise the
				// browser will collapse it. If this width is smaller than the
				// width the column requires, then it will have no effect
				if ( column.sWidthOrig && scrollX ) {
					$( headerCells[i] ).append( $('<div/>').css( {
						width: column.sWidthOrig,
						margin: 0,
						padding: 0,
						border: 0,
						height: 1
					} ) );
				}
			}
	
			// Find the widest cell for each column and put it into the table
			if ( oSettings.aoData.length ) {
				for ( i=0 ; i<visibleColumns.length ; i++ ) {
					columnIdx = visibleColumns[i];
					column = columns[ columnIdx ];
	
					$( _fnGetWidestNode( oSettings, columnIdx ) )
						.clone( false )
						.append( column.sContentPadding )
						.appendTo( tr );
				}
			}
	
			// Table has been built, attach to the document so we can work with it.
			// A holding element is used, positioned at the top of the container
			// with minimal height, so it has no effect on if the container scrolls
			// or not. Otherwise it might trigger scrolling when it actually isn't
			// needed
			var holder = $('<div/>').css( scrollX || scrollY ?
					{
						position: 'absolute',
						top: 0,
						left: 0,
						height: 1,
						right: 0,
						overflow: 'hidden'
					} :
					{}
				)
				.append( tmpTable )
				.appendTo( tableContainer );
	
			// When scrolling (X or Y) we want to set the width of the table as 
			// appropriate. However, when not scrolling leave the table width as it
			// is. This results in slightly different, but I think correct behaviour
			if ( scrollX && scrollXInner ) {
				tmpTable.width( scrollXInner );
			}
			else if ( scrollX ) {
				tmpTable.css( 'width', 'auto' );
				tmpTable.removeAttr('width');
	
				// If there is no width attribute or style, then allow the table to
				// collapse
				if ( tmpTable.width() < tableContainer.clientWidth && tableWidthAttr ) {
					tmpTable.width( tableContainer.clientWidth );
				}
			}
			else if ( scrollY ) {
				tmpTable.width( tableContainer.clientWidth );
			}
			else if ( tableWidthAttr ) {
				tmpTable.width( tableWidthAttr );
			}
	
			// Get the width of each column in the constructed table - we need to
			// know the inner width (so it can be assigned to the other table's
			// cells) and the outer width so we can calculate the full width of the
			// table. This is safe since DataTables requires a unique cell for each
			// column, but if ever a header can span multiple columns, this will
			// need to be modified.
			var total = 0;
			for ( i=0 ; i<visibleColumns.length ; i++ ) {
				var cell = $(headerCells[i]);
				var border = cell.outerWidth() - cell.width();
	
				// Use getBounding... where possible (not IE8-) because it can give
				// sub-pixel accuracy, which we then want to round up!
				var bounding = browser.bBounding ?
					Math.ceil( headerCells[i].getBoundingClientRect().width ) :
					cell.outerWidth();
	
				// Total is tracked to remove any sub-pixel errors as the outerWidth
				// of the table might not equal the total given here (IE!).
				total += bounding;
	
				// Width for each column to use
				columns[ visibleColumns[i] ].sWidth = _fnStringToCss( bounding - border );
			}
	
			table.style.width = _fnStringToCss( total );
	
			// Finished with the table - ditch it
			holder.remove();
		}
	
		// If there is a width attr, we want to attach an event listener which
		// allows the table sizing to automatically adjust when the window is
		// resized. Use the width attr rather than CSS, since we can't know if the
		// CSS is a relative value or absolute - DOM read is always px.
		if ( tableWidthAttr ) {
			table.style.width = _fnStringToCss( tableWidthAttr );
		}
	
		if ( (tableWidthAttr || scrollX) && ! oSettings._reszEvt ) {
			var bindResize = function () {
				$(window).bind('resize.DT-'+oSettings.sInstance, _fnThrottle( function () {
					_fnAdjustColumnSizing( oSettings );
				} ) );
			};
	
			// IE6/7 will crash if we bind a resize event handler on page load.
			// To be removed in 1.11 which drops IE6/7 support
			if ( ie67 ) {
				setTimeout( bindResize, 1000 );
			}
			else {
				bindResize();
			}
	
			oSettings._reszEvt = true;
		}
	}
	
	
	/**
	 * Throttle the calls to a function. Arguments and context are maintained for
	 * the throttled function
	 *  @param {function} fn Function to be called
	 *  @param {int} [freq=200] call frequency in mS
	 *  @returns {function} wrapped function
	 *  @memberof DataTable#oApi
	 */
	function _fnThrottle( fn, freq ) {
		var
			frequency = freq !== undefined ? freq : 200,
			last,
			timer;
	
		return function () {
			var
				that = this,
				now  = +new Date(),
				args = arguments;
	
			if ( last && now < last + frequency ) {
				clearTimeout( timer );
	
				timer = setTimeout( function () {
					last = undefined;
					fn.apply( that, args );
				}, frequency );
			}
			else {
				last = now;
				fn.apply( that, args );
			}
		};
	}
	
	
	/**
	 * Convert a CSS unit width to pixels (e.g. 2em)
	 *  @param {string} width width to be converted
	 *  @param {node} parent parent to get the with for (required for relative widths) - optional
	 *  @returns {int} width in pixels
	 *  @memberof DataTable#oApi
	 */
	function _fnConvertToWidth ( width, parent )
	{
		if ( ! width ) {
			return 0;
		}
	
		var n = $('<div/>')
			.css( 'width', _fnStringToCss( width ) )
			.appendTo( parent || document.body );
	
		var val = n[0].offsetWidth;
		n.remove();
	
		return val;
	}
	
	
	/**
	 * Get the widest node
	 *  @param {object} settings dataTables settings object
	 *  @param {int} colIdx column of interest
	 *  @returns {node} widest table node
	 *  @memberof DataTable#oApi
	 */
	function _fnGetWidestNode( settings, colIdx )
	{
		var idx = _fnGetMaxLenString( settings, colIdx );
		if ( idx < 0 ) {
			return null;
		}
	
		var data = settings.aoData[ idx ];
		return ! data.nTr ? // Might not have been created when deferred rendering
			$('<td/>').html( _fnGetCellData( settings, idx, colIdx, 'display' ) )[0] :
			data.anCells[ colIdx ];
	}
	
	
	/**
	 * Get the maximum strlen for each data column
	 *  @param {object} settings dataTables settings object
	 *  @param {int} colIdx column of interest
	 *  @returns {string} max string length for each column
	 *  @memberof DataTable#oApi
	 */
	function _fnGetMaxLenString( settings, colIdx )
	{
		var s, max=-1, maxIdx = -1;
	
		for ( var i=0, ien=settings.aoData.length ; i<ien ; i++ ) {
			s = _fnGetCellData( settings, i, colIdx, 'display' )+'';
			s = s.replace( __re_html_remove, '' );
			s = s.replace( /&nbsp;/g, ' ' );
	
			if ( s.length > max ) {
				max = s.length;
				maxIdx = i;
			}
		}
	
		return maxIdx;
	}
	
	
	/**
	 * Append a CSS unit (only if required) to a string
	 *  @param {string} value to css-ify
	 *  @returns {string} value with css unit
	 *  @memberof DataTable#oApi
	 */
	function _fnStringToCss( s )
	{
		if ( s === null ) {
			return '0px';
		}
	
		if ( typeof s == 'number' ) {
			return s < 0 ?
				'0px' :
				s+'px';
		}
	
		// Check it has a unit character already
		return s.match(/\d$/) ?
			s+'px' :
			s;
	}
	
	
	
	function _fnSortFlatten ( settings )
	{
		var
			i, iLen, k, kLen,
			aSort = [],
			aiOrig = [],
			aoColumns = settings.aoColumns,
			aDataSort, iCol, sType, srcCol,
			fixed = settings.aaSortingFixed,
			fixedObj = $.isPlainObject( fixed ),
			nestedSort = [],
			add = function ( a ) {
				if ( a.length && ! $.isArray( a[0] ) ) {
					// 1D array
					nestedSort.push( a );
				}
				else {
					// 2D array
					$.merge( nestedSort, a );
				}
			};
	
		// Build the sort array, with pre-fix and post-fix options if they have been
		// specified
		if ( $.isArray( fixed ) ) {
			add( fixed );
		}
	
		if ( fixedObj && fixed.pre ) {
			add( fixed.pre );
		}
	
		add( settings.aaSorting );
	
		if (fixedObj && fixed.post ) {
			add( fixed.post );
		}
	
		for ( i=0 ; i<nestedSort.length ; i++ )
		{
			srcCol = nestedSort[i][0];
			aDataSort = aoColumns[ srcCol ].aDataSort;
	
			for ( k=0, kLen=aDataSort.length ; k<kLen ; k++ )
			{
				iCol = aDataSort[k];
				sType = aoColumns[ iCol ].sType || 'string';
	
				if ( nestedSort[i]._idx === undefined ) {
					nestedSort[i]._idx = $.inArray( nestedSort[i][1], aoColumns[iCol].asSorting );
				}
	
				aSort.push( {
					src:       srcCol,
					col:       iCol,
					dir:       nestedSort[i][1],
					index:     nestedSort[i]._idx,
					type:      sType,
					formatter: DataTable.ext.type.order[ sType+"-pre" ]
				} );
			}
		}
	
		return aSort;
	}
	
	/**
	 * Change the order of the table
	 *  @param {object} oSettings dataTables settings object
	 *  @memberof DataTable#oApi
	 *  @todo This really needs split up!
	 */
	function _fnSort ( oSettings )
	{
		var
			i, ien, iLen, j, jLen, k, kLen,
			sDataType, nTh,
			aiOrig = [],
			oExtSort = DataTable.ext.type.order,
			aoData = oSettings.aoData,
			aoColumns = oSettings.aoColumns,
			aDataSort, data, iCol, sType, oSort,
			formatters = 0,
			sortCol,
			displayMaster = oSettings.aiDisplayMaster,
			aSort;
	
		// Resolve any column types that are unknown due to addition or invalidation
		// @todo Can this be moved into a 'data-ready' handler which is called when
		//   data is going to be used in the table?
		_fnColumnTypes( oSettings );
	
		aSort = _fnSortFlatten( oSettings );
	
		for ( i=0, ien=aSort.length ; i<ien ; i++ ) {
			sortCol = aSort[i];
	
			// Track if we can use the fast sort algorithm
			if ( sortCol.formatter ) {
				formatters++;
			}
	
			// Load the data needed for the sort, for each cell
			_fnSortData( oSettings, sortCol.col );
		}
	
		/* No sorting required if server-side or no sorting array */
		if ( _fnDataSource( oSettings ) != 'ssp' && aSort.length !== 0 )
		{
			// Create a value - key array of the current row positions such that we can use their
			// current position during the sort, if values match, in order to perform stable sorting
			for ( i=0, iLen=displayMaster.length ; i<iLen ; i++ ) {
				aiOrig[ displayMaster[i] ] = i;
			}
	
			/* Do the sort - here we want multi-column sorting based on a given data source (column)
			 * and sorting function (from oSort) in a certain direction. It's reasonably complex to
			 * follow on it's own, but this is what we want (example two column sorting):
			 *  fnLocalSorting = function(a,b){
			 *    var iTest;
			 *    iTest = oSort['string-asc']('data11', 'data12');
			 *      if (iTest !== 0)
			 *        return iTest;
			 *    iTest = oSort['numeric-desc']('data21', 'data22');
			 *    if (iTest !== 0)
			 *      return iTest;
			 *    return oSort['numeric-asc']( aiOrig[a], aiOrig[b] );
			 *  }
			 * Basically we have a test for each sorting column, if the data in that column is equal,
			 * test the next column. If all columns match, then we use a numeric sort on the row
			 * positions in the original data array to provide a stable sort.
			 *
			 * Note - I know it seems excessive to have two sorting methods, but the first is around
			 * 15% faster, so the second is only maintained for backwards compatibility with sorting
			 * methods which do not have a pre-sort formatting function.
			 */
			if ( formatters === aSort.length ) {
				// All sort types have formatting functions
				displayMaster.sort( function ( a, b ) {
					var
						x, y, k, test, sort,
						len=aSort.length,
						dataA = aoData[a]._aSortData,
						dataB = aoData[b]._aSortData;
	
					for ( k=0 ; k<len ; k++ ) {
						sort = aSort[k];
	
						x = dataA[ sort.col ];
						y = dataB[ sort.col ];
	
						test = x<y ? -1 : x>y ? 1 : 0;
						if ( test !== 0 ) {
							return sort.dir === 'asc' ? test : -test;
						}
					}
	
					x = aiOrig[a];
					y = aiOrig[b];
					return x<y ? -1 : x>y ? 1 : 0;
				} );
			}
			else {
				// Depreciated - remove in 1.11 (providing a plug-in option)
				// Not all sort types have formatting methods, so we have to call their sorting
				// methods.
				displayMaster.sort( function ( a, b ) {
					var
						x, y, k, l, test, sort, fn,
						len=aSort.length,
						dataA = aoData[a]._aSortData,
						dataB = aoData[b]._aSortData;
	
					for ( k=0 ; k<len ; k++ ) {
						sort = aSort[k];
	
						x = dataA[ sort.col ];
						y = dataB[ sort.col ];
	
						fn = oExtSort[ sort.type+"-"+sort.dir ] || oExtSort[ "string-"+sort.dir ];
						test = fn( x, y );
						if ( test !== 0 ) {
							return test;
						}
					}
	
					x = aiOrig[a];
					y = aiOrig[b];
					return x<y ? -1 : x>y ? 1 : 0;
				} );
			}
		}
	
		/* Tell the draw function that we have sorted the data */
		oSettings.bSorted = true;
	}
	
	
	function _fnSortAria ( settings )
	{
		var label;
		var nextSort;
		var columns = settings.aoColumns;
		var aSort = _fnSortFlatten( settings );
		var oAria = settings.oLanguage.oAria;
	
		// ARIA attributes - need to loop all columns, to update all (removing old
		// attributes as needed)
		for ( var i=0, iLen=columns.length ; i<iLen ; i++ )
		{
			var col = columns[i];
			var asSorting = col.asSorting;
			var sTitle = col.sTitle.replace( /<.*?>/g, "" );
			var th = col.nTh;
	
			// IE7 is throwing an error when setting these properties with jQuery's
			// attr() and removeAttr() methods...
			th.removeAttribute('aria-sort');
	
			/* In ARIA only the first sorting column can be marked as sorting - no multi-sort option */
			if ( col.bSortable ) {
				if ( aSort.length > 0 && aSort[0].col == i ) {
					th.setAttribute('aria-sort', aSort[0].dir=="asc" ? "ascending" : "descending" );
					nextSort = asSorting[ aSort[0].index+1 ] || asSorting[0];
				}
				else {
					nextSort = asSorting[0];
				}
	
				label = sTitle + ( nextSort === "asc" ?
					oAria.sSortAscending :
					oAria.sSortDescending
				);
			}
			else {
				label = sTitle;
			}
	
			th.setAttribute('aria-label', label);
		}
	}
	
	
	/**
	 * Function to run on user sort request
	 *  @param {object} settings dataTables settings object
	 *  @param {node} attachTo node to attach the handler to
	 *  @param {int} colIdx column sorting index
	 *  @param {boolean} [append=false] Append the requested sort to the existing
	 *    sort if true (i.e. multi-column sort)
	 *  @param {function} [callback] callback function
	 *  @memberof DataTable#oApi
	 */
	function _fnSortListener ( settings, colIdx, append, callback )
	{
		var col = settings.aoColumns[ colIdx ];
		var sorting = settings.aaSorting;
		var asSorting = col.asSorting;
		var nextSortIdx;
		var next = function ( a, overflow ) {
			var idx = a._idx;
			if ( idx === undefined ) {
				idx = $.inArray( a[1], asSorting );
			}
	
			return idx+1 < asSorting.length ?
				idx+1 :
				overflow ?
					null :
					0;
		};
	
		// Convert to 2D array if needed
		if ( typeof sorting[0] === 'number' ) {
			sorting = settings.aaSorting = [ sorting ];
		}
	
		// If appending the sort then we are multi-column sorting
		if ( append && settings.oFeatures.bSortMulti ) {
			// Are we already doing some kind of sort on this column?
			var sortIdx = $.inArray( colIdx, _pluck(sorting, '0') );
	
			if ( sortIdx !== -1 ) {
				// Yes, modify the sort
				nextSortIdx = next( sorting[sortIdx], true );
	
				if ( nextSortIdx === null && sorting.length === 1 ) {
					nextSortIdx = 0; // can't remove sorting completely
				}
	
				if ( nextSortIdx === null ) {
					sorting.splice( sortIdx, 1 );
				}
				else {
					sorting[sortIdx][1] = asSorting[ nextSortIdx ];
					sorting[sortIdx]._idx = nextSortIdx;
				}
			}
			else {
				// No sort on this column yet
				sorting.push( [ colIdx, asSorting[0], 0 ] );
				sorting[sorting.length-1]._idx = 0;
			}
		}
		else if ( sorting.length && sorting[0][0] == colIdx ) {
			// Single column - already sorting on this column, modify the sort
			nextSortIdx = next( sorting[0] );
	
			sorting.length = 1;
			sorting[0][1] = asSorting[ nextSortIdx ];
			sorting[0]._idx = nextSortIdx;
		}
		else {
			// Single column - sort only on this column
			sorting.length = 0;
			sorting.push( [ colIdx, asSorting[0] ] );
			sorting[0]._idx = 0;
		}
	
		// Run the sort by calling a full redraw
		_fnReDraw( settings );
	
		// callback used for async user interaction
		if ( typeof callback == 'function' ) {
			callback( settings );
		}
	}
	
	
	/**
	 * Attach a sort handler (click) to a node
	 *  @param {object} settings dataTables settings object
	 *  @param {node} attachTo node to attach the handler to
	 *  @param {int} colIdx column sorting index
	 *  @param {function} [callback] callback function
	 *  @memberof DataTable#oApi
	 */
	function _fnSortAttachListener ( settings, attachTo, colIdx, callback )
	{
		var col = settings.aoColumns[ colIdx ];
	
		_fnBindAction( attachTo, {}, function (e) {
			/* If the column is not sortable - don't to anything */
			if ( col.bSortable === false ) {
				return;
			}
	
			// If processing is enabled use a timeout to allow the processing
			// display to be shown - otherwise to it synchronously
			if ( settings.oFeatures.bProcessing ) {
				_fnProcessingDisplay( settings, true );
	
				setTimeout( function() {
					_fnSortListener( settings, colIdx, e.shiftKey, callback );
	
					// In server-side processing, the draw callback will remove the
					// processing display
					if ( _fnDataSource( settings ) !== 'ssp' ) {
						_fnProcessingDisplay( settings, false );
					}
				}, 0 );
			}
			else {
				_fnSortListener( settings, colIdx, e.shiftKey, callback );
			}
		} );
	}
	
	
	/**
	 * Set the sorting classes on table's body, Note: it is safe to call this function
	 * when bSort and bSortClasses are false
	 *  @param {object} oSettings dataTables settings object
	 *  @memberof DataTable#oApi
	 */
	function _fnSortingClasses( settings )
	{
		var oldSort = settings.aLastSort;
		var sortClass = settings.oClasses.sSortColumn;
		var sort = _fnSortFlatten( settings );
		var features = settings.oFeatures;
		var i, ien, colIdx;
	
		if ( features.bSort && features.bSortClasses ) {
			// Remove old sorting classes
			for ( i=0, ien=oldSort.length ; i<ien ; i++ ) {
				colIdx = oldSort[i].src;
	
				// Remove column sorting
				$( _pluck( settings.aoData, 'anCells', colIdx ) )
					.removeClass( sortClass + (i<2 ? i+1 : 3) );
			}
	
			// Add new column sorting
			for ( i=0, ien=sort.length ; i<ien ; i++ ) {
				colIdx = sort[i].src;
	
				$( _pluck( settings.aoData, 'anCells', colIdx ) )
					.addClass( sortClass + (i<2 ? i+1 : 3) );
			}
		}
	
		settings.aLastSort = sort;
	}
	
	
	// Get the data to sort a column, be it from cache, fresh (populating the
	// cache), or from a sort formatter
	function _fnSortData( settings, idx )
	{
		// Custom sorting function - provided by the sort data type
		var column = settings.aoColumns[ idx ];
		var customSort = DataTable.ext.order[ column.sSortDataType ];
		var customData;
	
		if ( customSort ) {
			customData = customSort.call( settings.oInstance, settings, idx,
				_fnColumnIndexToVisible( settings, idx )
			);
		}
	
		// Use / populate cache
		var row, cellData;
		var formatter = DataTable.ext.type.order[ column.sType+"-pre" ];
	
		for ( var i=0, ien=settings.aoData.length ; i<ien ; i++ ) {
			row = settings.aoData[i];
	
			if ( ! row._aSortData ) {
				row._aSortData = [];
			}
	
			if ( ! row._aSortData[idx] || customSort ) {
				cellData = customSort ?
					customData[i] : // If there was a custom sort function, use data from there
					_fnGetCellData( settings, i, idx, 'sort' );
	
				row._aSortData[ idx ] = formatter ?
					formatter( cellData ) :
					cellData;
			}
		}
	}
	
	
	
	/**
	 * Save the state of a table
	 *  @param {object} oSettings dataTables settings object
	 *  @memberof DataTable#oApi
	 */
	function _fnSaveState ( settings )
	{
		if ( !settings.oFeatures.bStateSave || settings.bDestroying )
		{
			return;
		}
	
		/* Store the interesting variables */
		var state = {
			time:    +new Date(),
			start:   settings._iDisplayStart,
			length:  settings._iDisplayLength,
			order:   $.extend( true, [], settings.aaSorting ),
			search:  _fnSearchToCamel( settings.oPreviousSearch ),
			columns: $.map( settings.aoColumns, function ( col, i ) {
				return {
					visible: col.bVisible,
					search: _fnSearchToCamel( settings.aoPreSearchCols[i] )
				};
			} )
		};
	
		_fnCallbackFire( settings, "aoStateSaveParams", 'stateSaveParams', [settings, state] );
	
		settings.oSavedState = state;
		settings.fnStateSaveCallback.call( settings.oInstance, settings, state );
	}
	
	
	/**
	 * Attempt to load a saved table state
	 *  @param {object} oSettings dataTables settings object
	 *  @param {object} oInit DataTables init object so we can override settings
	 *  @memberof DataTable#oApi
	 */
	function _fnLoadState ( settings, oInit )
	{
		var i, ien;
		var columns = settings.aoColumns;
	
		if ( ! settings.oFeatures.bStateSave ) {
			return;
		}
	
		var state = settings.fnStateLoadCallback.call( settings.oInstance, settings );
		if ( ! state || ! state.time ) {
			return;
		}
	
		/* Allow custom and plug-in manipulation functions to alter the saved data set and
		 * cancelling of loading by returning false
		 */
		var abStateLoad = _fnCallbackFire( settings, 'aoStateLoadParams', 'stateLoadParams', [settings, state] );
		if ( $.inArray( false, abStateLoad ) !== -1 ) {
			return;
		}
	
		/* Reject old data */
		var duration = settings.iStateDuration;
		if ( duration > 0 && state.time < +new Date() - (duration*1000) ) {
			return;
		}
	
		// Number of columns have changed - all bets are off, no restore of settings
		if ( columns.length !== state.columns.length ) {
			return;
		}
	
		// Store the saved state so it might be accessed at any time
		settings.oLoadedState = $.extend( true, {}, state );
	
		// Restore key features - todo - for 1.11 this needs to be done by
		// subscribed events
		if ( state.start !== undefined ) {
			settings._iDisplayStart    = state.start;
			settings.iInitDisplayStart = state.start;
		}
		if ( state.length !== undefined ) {
			settings._iDisplayLength   = state.length;
		}
	
		// Order
		if ( state.order !== undefined ) {
			settings.aaSorting = [];
			$.each( state.order, function ( i, col ) {
				settings.aaSorting.push( col[0] >= columns.length ?
					[ 0, col[1] ] :
					col
				);
			} );
		}
	
		// Search
		if ( state.search !== undefined ) {
			$.extend( settings.oPreviousSearch, _fnSearchToHung( state.search ) );
		}
	
		// Columns
		for ( i=0, ien=state.columns.length ; i<ien ; i++ ) {
			var col = state.columns[i];
	
			// Visibility
			if ( col.visible !== undefined ) {
				columns[i].bVisible = col.visible;
			}
	
			// Search
			if ( col.search !== undefined ) {
				$.extend( settings.aoPreSearchCols[i], _fnSearchToHung( col.search ) );
			}
		}
	
		_fnCallbackFire( settings, 'aoStateLoaded', 'stateLoaded', [settings, state] );
	}
	
	
	/**
	 * Return the settings object for a particular table
	 *  @param {node} table table we are using as a dataTable
	 *  @returns {object} Settings object - or null if not found
	 *  @memberof DataTable#oApi
	 */
	function _fnSettingsFromNode ( table )
	{
		var settings = DataTable.settings;
		var idx = $.inArray( table, _pluck( settings, 'nTable' ) );
	
		return idx !== -1 ?
			settings[ idx ] :
			null;
	}
	
	
	/**
	 * Log an error message
	 *  @param {object} settings dataTables settings object
	 *  @param {int} level log error messages, or display them to the user
	 *  @param {string} msg error message
	 *  @param {int} tn Technical note id to get more information about the error.
	 *  @memberof DataTable#oApi
	 */
	function _fnLog( settings, level, msg, tn )
	{
		msg = 'DataTables warning: '+
			(settings ? 'table id='+settings.sTableId+' - ' : '')+msg;
	
		if ( tn ) {
			msg += '. For more information about this error, please see '+
			'http://datatables.net/tn/'+tn;
		}
	
		if ( ! level  ) {
			// Backwards compatibility pre 1.10
			var ext = DataTable.ext;
			var type = ext.sErrMode || ext.errMode;
	
			if ( settings ) {
				_fnCallbackFire( settings, null, 'error', [ settings, tn, msg ] );
			}
	
			if ( type == 'alert' ) {
				alert( msg );
			}
			else if ( type == 'throw' ) {
				throw new Error(msg);
			}
			else if ( typeof type == 'function' ) {
				type( settings, tn, msg );
			}
		}
		else if ( window.console && console.log ) {
			console.log( msg );
		}
	}
	
	
	/**
	 * See if a property is defined on one object, if so assign it to the other object
	 *  @param {object} ret target object
	 *  @param {object} src source object
	 *  @param {string} name property
	 *  @param {string} [mappedName] name to map too - optional, name used if not given
	 *  @memberof DataTable#oApi
	 */
	function _fnMap( ret, src, name, mappedName )
	{
		if ( $.isArray( name ) ) {
			$.each( name, function (i, val) {
				if ( $.isArray( val ) ) {
					_fnMap( ret, src, val[0], val[1] );
				}
				else {
					_fnMap( ret, src, val );
				}
			} );
	
			return;
		}
	
		if ( mappedName === undefined ) {
			mappedName = name;
		}
	
		if ( src[name] !== undefined ) {
			ret[mappedName] = src[name];
		}
	}
	
	
	/**
	 * Extend objects - very similar to jQuery.extend, but deep copy objects, and
	 * shallow copy arrays. The reason we need to do this, is that we don't want to
	 * deep copy array init values (such as aaSorting) since the dev wouldn't be
	 * able to override them, but we do want to deep copy arrays.
	 *  @param {object} out Object to extend
	 *  @param {object} extender Object from which the properties will be applied to
	 *      out
	 *  @param {boolean} breakRefs If true, then arrays will be sliced to take an
	 *      independent copy with the exception of the `data` or `aaData` parameters
	 *      if they are present. This is so you can pass in a collection to
	 *      DataTables and have that used as your data source without breaking the
	 *      references
	 *  @returns {object} out Reference, just for convenience - out === the return.
	 *  @memberof DataTable#oApi
	 *  @todo This doesn't take account of arrays inside the deep copied objects.
	 */
	function _fnExtend( out, extender, breakRefs )
	{
		var val;
	
		for ( var prop in extender ) {
			if ( extender.hasOwnProperty(prop) ) {
				val = extender[prop];
	
				if ( $.isPlainObject( val ) ) {
					if ( ! $.isPlainObject( out[prop] ) ) {
						out[prop] = {};
					}
					$.extend( true, out[prop], val );
				}
				else if ( breakRefs && prop !== 'data' && prop !== 'aaData' && $.isArray(val) ) {
					out[prop] = val.slice();
				}
				else {
					out[prop] = val;
				}
			}
		}
	
		return out;
	}
	
	
	/**
	 * Bind an event handers to allow a click or return key to activate the callback.
	 * This is good for accessibility since a return on the keyboard will have the
	 * same effect as a click, if the element has focus.
	 *  @param {element} n Element to bind the action to
	 *  @param {object} oData Data object to pass to the triggered function
	 *  @param {function} fn Callback function for when the event is triggered
	 *  @memberof DataTable#oApi
	 */
	function _fnBindAction( n, oData, fn )
	{
		$(n)
			.bind( 'click.DT', oData, function (e) {
					n.blur(); // Remove focus outline for mouse users
					fn(e);
				} )
			.bind( 'keypress.DT', oData, function (e){
					if ( e.which === 13 ) {
						e.preventDefault();
						fn(e);
					}
				} )
			.bind( 'selectstart.DT', function () {
					/* Take the brutal approach to cancelling text selection */
					return false;
				} );
	}
	
	
	/**
	 * Register a callback function. Easily allows a callback function to be added to
	 * an array store of callback functions that can then all be called together.
	 *  @param {object} oSettings dataTables settings object
	 *  @param {string} sStore Name of the array storage for the callbacks in oSettings
	 *  @param {function} fn Function to be called back
	 *  @param {string} sName Identifying name for the callback (i.e. a label)
	 *  @memberof DataTable#oApi
	 */
	function _fnCallbackReg( oSettings, sStore, fn, sName )
	{
		if ( fn )
		{
			oSettings[sStore].push( {
				"fn": fn,
				"sName": sName
			} );
		}
	}
	
	
	/**
	 * Fire callback functions and trigger events. Note that the loop over the
	 * callback array store is done backwards! Further note that you do not want to
	 * fire off triggers in time sensitive applications (for example cell creation)
	 * as its slow.
	 *  @param {object} settings dataTables settings object
	 *  @param {string} callbackArr Name of the array storage for the callbacks in
	 *      oSettings
	 *  @param {string} eventName Name of the jQuery custom event to trigger. If
	 *      null no trigger is fired
	 *  @param {array} args Array of arguments to pass to the callback function /
	 *      trigger
	 *  @memberof DataTable#oApi
	 */
	function _fnCallbackFire( settings, callbackArr, eventName, args )
	{
		var ret = [];
	
		if ( callbackArr ) {
			ret = $.map( settings[callbackArr].slice().reverse(), function (val, i) {
				return val.fn.apply( settings.oInstance, args );
			} );
		}
	
		if ( eventName !== null ) {
			var e = $.Event( eventName+'.dt' );
	
			$(settings.nTable).trigger( e, args );
	
			ret.push( e.result );
		}
	
		return ret;
	}
	
	
	function _fnLengthOverflow ( settings )
	{
		var
			start = settings._iDisplayStart,
			end = settings.fnDisplayEnd(),
			len = settings._iDisplayLength;
	
		/* If we have space to show extra rows (backing up from the end point - then do so */
		if ( start >= end )
		{
			start = end - len;
		}
	
		// Keep the start record on the current page
		start -= (start % len);
	
		if ( len === -1 || start < 0 )
		{
			start = 0;
		}
	
		settings._iDisplayStart = start;
	}
	
	
	function _fnRenderer( settings, type )
	{
		var renderer = settings.renderer;
		var host = DataTable.ext.renderer[type];
	
		if ( $.isPlainObject( renderer ) && renderer[type] ) {
			// Specific renderer for this type. If available use it, otherwise use
			// the default.
			return host[renderer[type]] || host._;
		}
		else if ( typeof renderer === 'string' ) {
			// Common renderer - if there is one available for this type use it,
			// otherwise use the default
			return host[renderer] || host._;
		}
	
		// Use the default
		return host._;
	}
	
	
	/**
	 * Detect the data source being used for the table. Used to simplify the code
	 * a little (ajax) and to make it compress a little smaller.
	 *
	 *  @param {object} settings dataTables settings object
	 *  @returns {string} Data source
	 *  @memberof DataTable#oApi
	 */
	function _fnDataSource ( settings )
	{
		if ( settings.oFeatures.bServerSide ) {
			return 'ssp';
		}
		else if ( settings.ajax || settings.sAjaxSource ) {
			return 'ajax';
		}
		return 'dom';
	}
	

	DataTable = function( options )
	{
		/**
		 * Perform a jQuery selector action on the table's TR elements (from the tbody) and
		 * return the resulting jQuery object.
		 *  @param {string|node|jQuery} sSelector jQuery selector or node collection to act on
		 *  @param {object} [oOpts] Optional parameters for modifying the rows to be included
		 *  @param {string} [oOpts.filter=none] Select TR elements that meet the current filter
		 *    criterion ("applied") or all TR elements (i.e. no filter).
		 *  @param {string} [oOpts.order=current] Order of the TR elements in the processed array.
		 *    Can be either 'current', whereby the current sorting of the table is used, or
		 *    'original' whereby the original order the data was read into the table is used.
		 *  @param {string} [oOpts.page=all] Limit the selection to the currently displayed page
		 *    ("current") or not ("all"). If 'current' is given, then order is assumed to be
		 *    'current' and filter is 'applied', regardless of what they might be given as.
		 *  @returns {object} jQuery object, filtered by the given selector.
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable = $('#example').dataTable();
		 *
		 *      // Highlight every second row
		 *      oTable.$('tr:odd').css('backgroundColor', 'blue');
		 *    } );
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable = $('#example').dataTable();
		 *
		 *      // Filter to rows with 'Webkit' in them, add a background colour and then
		 *      // remove the filter, thus highlighting the 'Webkit' rows only.
		 *      oTable.fnFilter('Webkit');
		 *      oTable.$('tr', {"search": "applied"}).css('backgroundColor', 'blue');
		 *      oTable.fnFilter('');
		 *    } );
		 */
		this.$ = function ( sSelector, oOpts )
		{
			return this.api(true).$( sSelector, oOpts );
		};
		
		
		/**
		 * Almost identical to $ in operation, but in this case returns the data for the matched
		 * rows - as such, the jQuery selector used should match TR row nodes or TD/TH cell nodes
		 * rather than any descendants, so the data can be obtained for the row/cell. If matching
		 * rows are found, the data returned is the original data array/object that was used to
		 * create the row (or a generated array if from a DOM source).
		 *
		 * This method is often useful in-combination with $ where both functions are given the
		 * same parameters and the array indexes will match identically.
		 *  @param {string|node|jQuery} sSelector jQuery selector or node collection to act on
		 *  @param {object} [oOpts] Optional parameters for modifying the rows to be included
		 *  @param {string} [oOpts.filter=none] Select elements that meet the current filter
		 *    criterion ("applied") or all elements (i.e. no filter).
		 *  @param {string} [oOpts.order=current] Order of the data in the processed array.
		 *    Can be either 'current', whereby the current sorting of the table is used, or
		 *    'original' whereby the original order the data was read into the table is used.
		 *  @param {string} [oOpts.page=all] Limit the selection to the currently displayed page
		 *    ("current") or not ("all"). If 'current' is given, then order is assumed to be
		 *    'current' and filter is 'applied', regardless of what they might be given as.
		 *  @returns {array} Data for the matched elements. If any elements, as a result of the
		 *    selector, were not TR, TD or TH elements in the DataTable, they will have a null
		 *    entry in the array.
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable = $('#example').dataTable();
		 *
		 *      // Get the data from the first row in the table
		 *      var data = oTable._('tr:first');
		 *
		 *      // Do something useful with the data
		 *      alert( "First cell is: "+data[0] );
		 *    } );
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable = $('#example').dataTable();
		 *
		 *      // Filter to 'Webkit' and get all data for
		 *      oTable.fnFilter('Webkit');
		 *      var data = oTable._('tr', {"search": "applied"});
		 *
		 *      // Do something with the data
		 *      alert( data.length+" rows matched the search" );
		 *    } );
		 */
		this._ = function ( sSelector, oOpts )
		{
			return this.api(true).rows( sSelector, oOpts ).data();
		};
		
		
		/**
		 * Create a DataTables Api instance, with the currently selected tables for
		 * the Api's context.
		 * @param {boolean} [traditional=false] Set the API instance's context to be
		 *   only the table referred to by the `DataTable.ext.iApiIndex` option, as was
		 *   used in the API presented by DataTables 1.9- (i.e. the traditional mode),
		 *   or if all tables captured in the jQuery object should be used.
		 * @return {DataTables.Api}
		 */
		this.api = function ( traditional )
		{
			return traditional ?
				new _Api(
					_fnSettingsFromNode( this[ _ext.iApiIndex ] )
				) :
				new _Api( this );
		};
		
		
		/**
		 * Add a single new row or multiple rows of data to the table. Please note
		 * that this is suitable for client-side processing only - if you are using
		 * server-side processing (i.e. "bServerSide": true), then to add data, you
		 * must add it to the data source, i.e. the server-side, through an Ajax call.
		 *  @param {array|object} data The data to be added to the table. This can be:
		 *    <ul>
		 *      <li>1D array of data - add a single row with the data provided</li>
		 *      <li>2D array of arrays - add multiple rows in a single call</li>
		 *      <li>object - data object when using <i>mData</i></li>
		 *      <li>array of objects - multiple data objects when using <i>mData</i></li>
		 *    </ul>
		 *  @param {bool} [redraw=true] redraw the table or not
		 *  @returns {array} An array of integers, representing the list of indexes in
		 *    <i>aoData</i> ({@link DataTable.models.oSettings}) that have been added to
		 *    the table.
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    // Global var for counter
		 *    var giCount = 2;
		 *
		 *    $(document).ready(function() {
		 *      $('#example').dataTable();
		 *    } );
		 *
		 *    function fnClickAddRow() {
		 *      $('#example').dataTable().fnAddData( [
		 *        giCount+".1",
		 *        giCount+".2",
		 *        giCount+".3",
		 *        giCount+".4" ]
		 *      );
		 *
		 *      giCount++;
		 *    }
		 */
		this.fnAddData = function( data, redraw )
		{
			var api = this.api( true );
		
			/* Check if we want to add multiple rows or not */
			var rows = $.isArray(data) && ( $.isArray(data[0]) || $.isPlainObject(data[0]) ) ?
				api.rows.add( data ) :
				api.row.add( data );
		
			if ( redraw === undefined || redraw ) {
				api.draw();
			}
		
			return rows.flatten().toArray();
		};
		
		
		/**
		 * This function will make DataTables recalculate the column sizes, based on the data
		 * contained in the table and the sizes applied to the columns (in the DOM, CSS or
		 * through the sWidth parameter). This can be useful when the width of the table's
		 * parent element changes (for example a window resize).
		 *  @param {boolean} [bRedraw=true] Redraw the table or not, you will typically want to
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable = $('#example').dataTable( {
		 *        "sScrollY": "200px",
		 *        "bPaginate": false
		 *      } );
		 *
		 *      $(window).bind('resize', function () {
		 *        oTable.fnAdjustColumnSizing();
		 *      } );
		 *    } );
		 */
		this.fnAdjustColumnSizing = function ( bRedraw )
		{
			var api = this.api( true ).columns.adjust();
			var settings = api.settings()[0];
			var scroll = settings.oScroll;
		
			if ( bRedraw === undefined || bRedraw ) {
				api.draw( false );
			}
			else if ( scroll.sX !== "" || scroll.sY !== "" ) {
				/* If not redrawing, but scrolling, we want to apply the new column sizes anyway */
				_fnScrollDraw( settings );
			}
		};
		
		
		/**
		 * Quickly and simply clear a table
		 *  @param {bool} [bRedraw=true] redraw the table or not
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable = $('#example').dataTable();
		 *
		 *      // Immediately 'nuke' the current rows (perhaps waiting for an Ajax callback...)
		 *      oTable.fnClearTable();
		 *    } );
		 */
		this.fnClearTable = function( bRedraw )
		{
			var api = this.api( true ).clear();
		
			if ( bRedraw === undefined || bRedraw ) {
				api.draw();
			}
		};
		
		
		/**
		 * The exact opposite of 'opening' a row, this function will close any rows which
		 * are currently 'open'.
		 *  @param {node} nTr the table row to 'close'
		 *  @returns {int} 0 on success, or 1 if failed (can't find the row)
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable;
		 *
		 *      // 'open' an information row when a row is clicked on
		 *      $('#example tbody tr').click( function () {
		 *        if ( oTable.fnIsOpen(this) ) {
		 *          oTable.fnClose( this );
		 *        } else {
		 *          oTable.fnOpen( this, "Temporary row opened", "info_row" );
		 *        }
		 *      } );
		 *
		 *      oTable = $('#example').dataTable();
		 *    } );
		 */
		this.fnClose = function( nTr )
		{
			this.api( true ).row( nTr ).child.hide();
		};
		
		
		/**
		 * Remove a row for the table
		 *  @param {mixed} target The index of the row from aoData to be deleted, or
		 *    the TR element you want to delete
		 *  @param {function|null} [callBack] Callback function
		 *  @param {bool} [redraw=true] Redraw the table or not
		 *  @returns {array} The row that was deleted
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable = $('#example').dataTable();
		 *
		 *      // Immediately remove the first row
		 *      oTable.fnDeleteRow( 0 );
		 *    } );
		 */
		this.fnDeleteRow = function( target, callback, redraw )
		{
			var api = this.api( true );
			var rows = api.rows( target );
			var settings = rows.settings()[0];
			var data = settings.aoData[ rows[0][0] ];
		
			rows.remove();
		
			if ( callback ) {
				callback.call( this, settings, data );
			}
		
			if ( redraw === undefined || redraw ) {
				api.draw();
			}
		
			return data;
		};
		
		
		/**
		 * Restore the table to it's original state in the DOM by removing all of DataTables
		 * enhancements, alterations to the DOM structure of the table and event listeners.
		 *  @param {boolean} [remove=false] Completely remove the table from the DOM
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      // This example is fairly pointless in reality, but shows how fnDestroy can be used
		 *      var oTable = $('#example').dataTable();
		 *      oTable.fnDestroy();
		 *    } );
		 */
		this.fnDestroy = function ( remove )
		{
			this.api( true ).destroy( remove );
		};
		
		
		/**
		 * Redraw the table
		 *  @param {bool} [complete=true] Re-filter and resort (if enabled) the table before the draw.
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable = $('#example').dataTable();
		 *
		 *      // Re-draw the table - you wouldn't want to do it here, but it's an example :-)
		 *      oTable.fnDraw();
		 *    } );
		 */
		this.fnDraw = function( complete )
		{
			// Note that this isn't an exact match to the old call to _fnDraw - it takes
			// into account the new data, but can hold position.
			this.api( true ).draw( complete );
		};
		
		
		/**
		 * Filter the input based on data
		 *  @param {string} sInput String to filter the table on
		 *  @param {int|null} [iColumn] Column to limit filtering to
		 *  @param {bool} [bRegex=false] Treat as regular expression or not
		 *  @param {bool} [bSmart=true] Perform smart filtering or not
		 *  @param {bool} [bShowGlobal=true] Show the input global filter in it's input box(es)
		 *  @param {bool} [bCaseInsensitive=true] Do case-insensitive matching (true) or not (false)
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable = $('#example').dataTable();
		 *
		 *      // Sometime later - filter...
		 *      oTable.fnFilter( 'test string' );
		 *    } );
		 */
		this.fnFilter = function( sInput, iColumn, bRegex, bSmart, bShowGlobal, bCaseInsensitive )
		{
			var api = this.api( true );
		
			if ( iColumn === null || iColumn === undefined ) {
				api.search( sInput, bRegex, bSmart, bCaseInsensitive );
			}
			else {
				api.column( iColumn ).search( sInput, bRegex, bSmart, bCaseInsensitive );
			}
		
			api.draw();
		};
		
		
		/**
		 * Get the data for the whole table, an individual row or an individual cell based on the
		 * provided parameters.
		 *  @param {int|node} [src] A TR row node, TD/TH cell node or an integer. If given as
		 *    a TR node then the data source for the whole row will be returned. If given as a
		 *    TD/TH cell node then iCol will be automatically calculated and the data for the
		 *    cell returned. If given as an integer, then this is treated as the aoData internal
		 *    data index for the row (see fnGetPosition) and the data for that row used.
		 *  @param {int} [col] Optional column index that you want the data of.
		 *  @returns {array|object|string} If mRow is undefined, then the data for all rows is
		 *    returned. If mRow is defined, just data for that row, and is iCol is
		 *    defined, only data for the designated cell is returned.
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    // Row data
		 *    $(document).ready(function() {
		 *      oTable = $('#example').dataTable();
		 *
		 *      oTable.$('tr').click( function () {
		 *        var data = oTable.fnGetData( this );
		 *        // ... do something with the array / object of data for the row
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // Individual cell data
		 *    $(document).ready(function() {
		 *      oTable = $('#example').dataTable();
		 *
		 *      oTable.$('td').click( function () {
		 *        var sData = oTable.fnGetData( this );
		 *        alert( 'The cell clicked on had the value of '+sData );
		 *      } );
		 *    } );
		 */
		this.fnGetData = function( src, col )
		{
			var api = this.api( true );
		
			if ( src !== undefined ) {
				var type = src.nodeName ? src.nodeName.toLowerCase() : '';
		
				return col !== undefined || type == 'td' || type == 'th' ?
					api.cell( src, col ).data() :
					api.row( src ).data() || null;
			}
		
			return api.data().toArray();
		};
		
		
		/**
		 * Get an array of the TR nodes that are used in the table's body. Note that you will
		 * typically want to use the '$' API method in preference to this as it is more
		 * flexible.
		 *  @param {int} [iRow] Optional row index for the TR element you want
		 *  @returns {array|node} If iRow is undefined, returns an array of all TR elements
		 *    in the table's body, or iRow is defined, just the TR element requested.
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable = $('#example').dataTable();
		 *
		 *      // Get the nodes from the table
		 *      var nNodes = oTable.fnGetNodes( );
		 *    } );
		 */
		this.fnGetNodes = function( iRow )
		{
			var api = this.api( true );
		
			return iRow !== undefined ?
				api.row( iRow ).node() :
				api.rows().nodes().flatten().toArray();
		};
		
		
		/**
		 * Get the array indexes of a particular cell from it's DOM element
		 * and column index including hidden columns
		 *  @param {node} node this can either be a TR, TD or TH in the table's body
		 *  @returns {int} If nNode is given as a TR, then a single index is returned, or
		 *    if given as a cell, an array of [row index, column index (visible),
		 *    column index (all)] is given.
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      $('#example tbody td').click( function () {
		 *        // Get the position of the current data from the node
		 *        var aPos = oTable.fnGetPosition( this );
		 *
		 *        // Get the data array for this row
		 *        var aData = oTable.fnGetData( aPos[0] );
		 *
		 *        // Update the data array and return the value
		 *        aData[ aPos[1] ] = 'clicked';
		 *        this.innerHTML = 'clicked';
		 *      } );
		 *
		 *      // Init DataTables
		 *      oTable = $('#example').dataTable();
		 *    } );
		 */
		this.fnGetPosition = function( node )
		{
			var api = this.api( true );
			var nodeName = node.nodeName.toUpperCase();
		
			if ( nodeName == 'TR' ) {
				return api.row( node ).index();
			}
			else if ( nodeName == 'TD' || nodeName == 'TH' ) {
				var cell = api.cell( node ).index();
		
				return [
					cell.row,
					cell.columnVisible,
					cell.column
				];
			}
			return null;
		};
		
		
		/**
		 * Check to see if a row is 'open' or not.
		 *  @param {node} nTr the table row to check
		 *  @returns {boolean} true if the row is currently open, false otherwise
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable;
		 *
		 *      // 'open' an information row when a row is clicked on
		 *      $('#example tbody tr').click( function () {
		 *        if ( oTable.fnIsOpen(this) ) {
		 *          oTable.fnClose( this );
		 *        } else {
		 *          oTable.fnOpen( this, "Temporary row opened", "info_row" );
		 *        }
		 *      } );
		 *
		 *      oTable = $('#example').dataTable();
		 *    } );
		 */
		this.fnIsOpen = function( nTr )
		{
			return this.api( true ).row( nTr ).child.isShown();
		};
		
		
		/**
		 * This function will place a new row directly after a row which is currently
		 * on display on the page, with the HTML contents that is passed into the
		 * function. This can be used, for example, to ask for confirmation that a
		 * particular record should be deleted.
		 *  @param {node} nTr The table row to 'open'
		 *  @param {string|node|jQuery} mHtml The HTML to put into the row
		 *  @param {string} sClass Class to give the new TD cell
		 *  @returns {node} The row opened. Note that if the table row passed in as the
		 *    first parameter, is not found in the table, this method will silently
		 *    return.
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable;
		 *
		 *      // 'open' an information row when a row is clicked on
		 *      $('#example tbody tr').click( function () {
		 *        if ( oTable.fnIsOpen(this) ) {
		 *          oTable.fnClose( this );
		 *        } else {
		 *          oTable.fnOpen( this, "Temporary row opened", "info_row" );
		 *        }
		 *      } );
		 *
		 *      oTable = $('#example').dataTable();
		 *    } );
		 */
		this.fnOpen = function( nTr, mHtml, sClass )
		{
			return this.api( true )
				.row( nTr )
				.child( mHtml, sClass )
				.show()
				.child()[0];
		};
		
		
		/**
		 * Change the pagination - provides the internal logic for pagination in a simple API
		 * function. With this function you can have a DataTables table go to the next,
		 * previous, first or last pages.
		 *  @param {string|int} mAction Paging action to take: "first", "previous", "next" or "last"
		 *    or page number to jump to (integer), note that page 0 is the first page.
		 *  @param {bool} [bRedraw=true] Redraw the table or not
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable = $('#example').dataTable();
		 *      oTable.fnPageChange( 'next' );
		 *    } );
		 */
		this.fnPageChange = function ( mAction, bRedraw )
		{
			var api = this.api( true ).page( mAction );
		
			if ( bRedraw === undefined || bRedraw ) {
				api.draw(false);
			}
		};
		
		
		/**
		 * Show a particular column
		 *  @param {int} iCol The column whose display should be changed
		 *  @param {bool} bShow Show (true) or hide (false) the column
		 *  @param {bool} [bRedraw=true] Redraw the table or not
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable = $('#example').dataTable();
		 *
		 *      // Hide the second column after initialisation
		 *      oTable.fnSetColumnVis( 1, false );
		 *    } );
		 */
		this.fnSetColumnVis = function ( iCol, bShow, bRedraw )
		{
			var api = this.api( true ).column( iCol ).visible( bShow );
		
			if ( bRedraw === undefined || bRedraw ) {
				api.columns.adjust().draw();
			}
		};
		
		
		/**
		 * Get the settings for a particular table for external manipulation
		 *  @returns {object} DataTables settings object. See
		 *    {@link DataTable.models.oSettings}
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable = $('#example').dataTable();
		 *      var oSettings = oTable.fnSettings();
		 *
		 *      // Show an example parameter from the settings
		 *      alert( oSettings._iDisplayStart );
		 *    } );
		 */
		this.fnSettings = function()
		{
			return _fnSettingsFromNode( this[_ext.iApiIndex] );
		};
		
		
		/**
		 * Sort the table by a particular column
		 *  @param {int} iCol the data index to sort on. Note that this will not match the
		 *    'display index' if you have hidden data entries
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable = $('#example').dataTable();
		 *
		 *      // Sort immediately with columns 0 and 1
		 *      oTable.fnSort( [ [0,'asc'], [1,'asc'] ] );
		 *    } );
		 */
		this.fnSort = function( aaSort )
		{
			this.api( true ).order( aaSort ).draw();
		};
		
		
		/**
		 * Attach a sort listener to an element for a given column
		 *  @param {node} nNode the element to attach the sort listener to
		 *  @param {int} iColumn the column that a click on this node will sort on
		 *  @param {function} [fnCallback] callback function when sort is run
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable = $('#example').dataTable();
		 *
		 *      // Sort on column 1, when 'sorter' is clicked on
		 *      oTable.fnSortListener( document.getElementById('sorter'), 1 );
		 *    } );
		 */
		this.fnSortListener = function( nNode, iColumn, fnCallback )
		{
			this.api( true ).order.listener( nNode, iColumn, fnCallback );
		};
		
		
		/**
		 * Update a table cell or row - this method will accept either a single value to
		 * update the cell with, an array of values with one element for each column or
		 * an object in the same format as the original data source. The function is
		 * self-referencing in order to make the multi column updates easier.
		 *  @param {object|array|string} mData Data to update the cell/row with
		 *  @param {node|int} mRow TR element you want to update or the aoData index
		 *  @param {int} [iColumn] The column to update, give as null or undefined to
		 *    update a whole row.
		 *  @param {bool} [bRedraw=true] Redraw the table or not
		 *  @param {bool} [bAction=true] Perform pre-draw actions or not
		 *  @returns {int} 0 on success, 1 on error
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable = $('#example').dataTable();
		 *      oTable.fnUpdate( 'Example update', 0, 0 ); // Single cell
		 *      oTable.fnUpdate( ['a', 'b', 'c', 'd', 'e'], $('tbody tr')[0] ); // Row
		 *    } );
		 */
		this.fnUpdate = function( mData, mRow, iColumn, bRedraw, bAction )
		{
			var api = this.api( true );
		
			if ( iColumn === undefined || iColumn === null ) {
				api.row( mRow ).data( mData );
			}
			else {
				api.cell( mRow, iColumn ).data( mData );
			}
		
			if ( bAction === undefined || bAction ) {
				api.columns.adjust();
			}
		
			if ( bRedraw === undefined || bRedraw ) {
				api.draw();
			}
			return 0;
		};
		
		
		/**
		 * Provide a common method for plug-ins to check the version of DataTables being used, in order
		 * to ensure compatibility.
		 *  @param {string} sVersion Version string to check for, in the format "X.Y.Z". Note that the
		 *    formats "X" and "X.Y" are also acceptable.
		 *  @returns {boolean} true if this version of DataTables is greater or equal to the required
		 *    version, or false if this version of DataTales is not suitable
		 *  @method
		 *  @dtopt API
		 *  @deprecated Since v1.10
		 *
		 *  @example
		 *    $(document).ready(function() {
		 *      var oTable = $('#example').dataTable();
		 *      alert( oTable.fnVersionCheck( '1.9.0' ) );
		 *    } );
		 */
		this.fnVersionCheck = _ext.fnVersionCheck;
		

		var _that = this;
		var emptyInit = options === undefined;
		var len = this.length;

		if ( emptyInit ) {
			options = {};
		}

		this.oApi = this.internal = _ext.internal;

		// Extend with old style plug-in API methods
		for ( var fn in DataTable.ext.internal ) {
			if ( fn ) {
				this[fn] = _fnExternApiFunc(fn);
			}
		}

		this.each(function() {
			// For each initialisation we want to give it a clean initialisation
			// object that can be bashed around
			var o = {};
			var oInit = len > 1 ? // optimisation for single table case
				_fnExtend( o, options, true ) :
				options;

			/*global oInit,_that,emptyInit*/
			var i=0, iLen, j, jLen, k, kLen;
			var sId = this.getAttribute( 'id' );
			var bInitHandedOff = false;
			var defaults = DataTable.defaults;
			var $this = $(this);
			
			
			/* Sanity check */
			if ( this.nodeName.toLowerCase() != 'table' )
			{
				_fnLog( null, 0, 'Non-table node initialisation ('+this.nodeName+')', 2 );
				return;
			}
			
			/* Backwards compatibility for the defaults */
			_fnCompatOpts( defaults );
			_fnCompatCols( defaults.column );
			
			/* Convert the camel-case defaults to Hungarian */
			_fnCamelToHungarian( defaults, defaults, true );
			_fnCamelToHungarian( defaults.column, defaults.column, true );
			
			/* Setting up the initialisation object */
			_fnCamelToHungarian( defaults, $.extend( oInit, $this.data() ) );
			
			
			
			/* Check to see if we are re-initialising a table */
			var allSettings = DataTable.settings;
			for ( i=0, iLen=allSettings.length ; i<iLen ; i++ )
			{
				var s = allSettings[i];
			
				/* Base check on table node */
				if ( s.nTable == this || s.nTHead.parentNode == this || (s.nTFoot && s.nTFoot.parentNode == this) )
				{
					var bRetrieve = oInit.bRetrieve !== undefined ? oInit.bRetrieve : defaults.bRetrieve;
					var bDestroy = oInit.bDestroy !== undefined ? oInit.bDestroy : defaults.bDestroy;
			
					if ( emptyInit || bRetrieve )
					{
						return s.oInstance;
					}
					else if ( bDestroy )
					{
						s.oInstance.fnDestroy();
						break;
					}
					else
					{
						_fnLog( s, 0, 'Cannot reinitialise DataTable', 3 );
						return;
					}
				}
			
				/* If the element we are initialising has the same ID as a table which was previously
				 * initialised, but the table nodes don't match (from before) then we destroy the old
				 * instance by simply deleting it. This is under the assumption that the table has been
				 * destroyed by other methods. Anyone using non-id selectors will need to do this manually
				 */
				if ( s.sTableId == this.id )
				{
					allSettings.splice( i, 1 );
					break;
				}
			}
			
			/* Ensure the table has an ID - required for accessibility */
			if ( sId === null || sId === "" )
			{
				sId = "DataTables_Table_"+(DataTable.ext._unique++);
				this.id = sId;
			}
			
			/* Create the settings object for this table and set some of the default parameters */
			var oSettings = $.extend( true, {}, DataTable.models.oSettings, {
				"sDestroyWidth": $this[0].style.width,
				"sInstance":     sId,
				"sTableId":      sId
			} );
			oSettings.nTable = this;
			oSettings.oApi   = _that.internal;
			oSettings.oInit  = oInit;
			
			allSettings.push( oSettings );
			
			// Need to add the instance after the instance after the settings object has been added
			// to the settings array, so we can self reference the table instance if more than one
			oSettings.oInstance = (_that.length===1) ? _that : $this.dataTable();
			
			// Backwards compatibility, before we apply all the defaults
			_fnCompatOpts( oInit );
			
			if ( oInit.oLanguage )
			{
				_fnLanguageCompat( oInit.oLanguage );
			}
			
			// If the length menu is given, but the init display length is not, use the length menu
			if ( oInit.aLengthMenu && ! oInit.iDisplayLength )
			{
				oInit.iDisplayLength = $.isArray( oInit.aLengthMenu[0] ) ?
					oInit.aLengthMenu[0][0] : oInit.aLengthMenu[0];
			}
			
			// Apply the defaults and init options to make a single init object will all
			// options defined from defaults and instance options.
			oInit = _fnExtend( $.extend( true, {}, defaults ), oInit );
			
			
			// Map the initialisation options onto the settings object
			_fnMap( oSettings.oFeatures, oInit, [
				"bPaginate",
				"bLengthChange",
				"bFilter",
				"bSort",
				"bSortMulti",
				"bInfo",
				"bProcessing",
				"bAutoWidth",
				"bSortClasses",
				"bServerSide",
				"bDeferRender"
			] );
			_fnMap( oSettings, oInit, [
				"asStripeClasses",
				"ajax",
				"fnServerData",
				"fnFormatNumber",
				"sServerMethod",
				"aaSorting",
				"aaSortingFixed",
				"aLengthMenu",
				"sPaginationType",
				"sAjaxSource",
				"sAjaxDataProp",
				"iStateDuration",
				"sDom",
				"bSortCellsTop",
				"iTabIndex",
				"fnStateLoadCallback",
				"fnStateSaveCallback",
				"renderer",
				"searchDelay",
				"rowId",
				[ "iCookieDuration", "iStateDuration" ], // backwards compat
				[ "oSearch", "oPreviousSearch" ],
				[ "aoSearchCols", "aoPreSearchCols" ],
				[ "iDisplayLength", "_iDisplayLength" ],
				[ "bJQueryUI", "bJUI" ]
			] );
			_fnMap( oSettings.oScroll, oInit, [
				[ "sScrollX", "sX" ],
				[ "sScrollXInner", "sXInner" ],
				[ "sScrollY", "sY" ],
				[ "bScrollCollapse", "bCollapse" ]
			] );
			_fnMap( oSettings.oLanguage, oInit, "fnInfoCallback" );
			
			/* Callback functions which are array driven */
			_fnCallbackReg( oSettings, 'aoDrawCallback',       oInit.fnDrawCallback,      'user' );
			_fnCallbackReg( oSettings, 'aoServerParams',       oInit.fnServerParams,      'user' );
			_fnCallbackReg( oSettings, 'aoStateSaveParams',    oInit.fnStateSaveParams,   'user' );
			_fnCallbackReg( oSettings, 'aoStateLoadParams',    oInit.fnStateLoadParams,   'user' );
			_fnCallbackReg( oSettings, 'aoStateLoaded',        oInit.fnStateLoaded,       'user' );
			_fnCallbackReg( oSettings, 'aoRowCallback',        oInit.fnRowCallback,       'user' );
			_fnCallbackReg( oSettings, 'aoRowCreatedCallback', oInit.fnCreatedRow,        'user' );
			_fnCallbackReg( oSettings, 'aoHeaderCallback',     oInit.fnHeaderCallback,    'user' );
			_fnCallbackReg( oSettings, 'aoFooterCallback',     oInit.fnFooterCallback,    'user' );
			_fnCallbackReg( oSettings, 'aoInitComplete',       oInit.fnInitComplete,      'user' );
			_fnCallbackReg( oSettings, 'aoPreDrawCallback',    oInit.fnPreDrawCallback,   'user' );
			
			oSettings.rowIdFn = _fnGetObjectDataFn( oInit.rowId );
			
			/* Browser support detection */
			_fnBrowserDetect( oSettings );
			
			var oClasses = oSettings.oClasses;
			
			// @todo Remove in 1.11
			if ( oInit.bJQueryUI )
			{
				/* Use the JUI classes object for display. You could clone the oStdClasses object if
				 * you want to have multiple tables with multiple independent classes
				 */
				$.extend( oClasses, DataTable.ext.oJUIClasses, oInit.oClasses );
			
				if ( oInit.sDom === defaults.sDom && defaults.sDom === "lfrtip" )
				{
					/* Set the DOM to use a layout suitable for jQuery UI's theming */
					oSettings.sDom = '<"H"lfr>t<"F"ip>';
				}
			
				if ( ! oSettings.renderer ) {
					oSettings.renderer = 'jqueryui';
				}
				else if ( $.isPlainObject( oSettings.renderer ) && ! oSettings.renderer.header ) {
					oSettings.renderer.header = 'jqueryui';
				}
			}
			else
			{
				$.extend( oClasses, DataTable.ext.classes, oInit.oClasses );
			}
			$this.addClass( oClasses.sTable );
			
			
			if ( oSettings.iInitDisplayStart === undefined )
			{
				/* Display start point, taking into account the save saving */
				oSettings.iInitDisplayStart = oInit.iDisplayStart;
				oSettings._iDisplayStart = oInit.iDisplayStart;
			}
			
			if ( oInit.iDeferLoading !== null )
			{
				oSettings.bDeferLoading = true;
				var tmp = $.isArray( oInit.iDeferLoading );
				oSettings._iRecordsDisplay = tmp ? oInit.iDeferLoading[0] : oInit.iDeferLoading;
				oSettings._iRecordsTotal = tmp ? oInit.iDeferLoading[1] : oInit.iDeferLoading;
			}
			
			/* Language definitions */
			var oLanguage = oSettings.oLanguage;
			$.extend( true, oLanguage, oInit.oLanguage );
			
			if ( oLanguage.sUrl !== "" )
			{
				/* Get the language definitions from a file - because this Ajax call makes the language
				 * get async to the remainder of this function we use bInitHandedOff to indicate that
				 * _fnInitialise will be fired by the returned Ajax handler, rather than the constructor
				 */
				$.ajax( {
					dataType: 'json',
					url: oLanguage.sUrl,
					success: function ( json ) {
						_fnLanguageCompat( json );
						_fnCamelToHungarian( defaults.oLanguage, json );
						$.extend( true, oLanguage, json );
						_fnInitialise( oSettings );
					},
					error: function () {
						// Error occurred loading language file, continue on as best we can
						_fnInitialise( oSettings );
					}
				} );
				bInitHandedOff = true;
			}
			
			/*
			 * Stripes
			 */
			if ( oInit.asStripeClasses === null )
			{
				oSettings.asStripeClasses =[
					oClasses.sStripeOdd,
					oClasses.sStripeEven
				];
			}
			
			/* Remove row stripe classes if they are already on the table row */
			var stripeClasses = oSettings.asStripeClasses;
			var rowOne = $this.children('tbody').find('tr').eq(0);
			if ( $.inArray( true, $.map( stripeClasses, function(el, i) {
				return rowOne.hasClass(el);
			} ) ) !== -1 ) {
				$('tbody tr', this).removeClass( stripeClasses.join(' ') );
				oSettings.asDestroyStripes = stripeClasses.slice();
			}
			
			/*
			 * Columns
			 * See if we should load columns automatically or use defined ones
			 */
			var anThs = [];
			var aoColumnsInit;
			var nThead = this.getElementsByTagName('thead');
			if ( nThead.length !== 0 )
			{
				_fnDetectHeader( oSettings.aoHeader, nThead[0] );
				anThs = _fnGetUniqueThs( oSettings );
			}
			
			/* If not given a column array, generate one with nulls */
			if ( oInit.aoColumns === null )
			{
				aoColumnsInit = [];
				for ( i=0, iLen=anThs.length ; i<iLen ; i++ )
				{
					aoColumnsInit.push( null );
				}
			}
			else
			{
				aoColumnsInit = oInit.aoColumns;
			}
			
			/* Add the columns */
			for ( i=0, iLen=aoColumnsInit.length ; i<iLen ; i++ )
			{
				_fnAddColumn( oSettings, anThs ? anThs[i] : null );
			}
			
			/* Apply the column definitions */
			_fnApplyColumnDefs( oSettings, oInit.aoColumnDefs, aoColumnsInit, function (iCol, oDef) {
				_fnColumnOptions( oSettings, iCol, oDef );
			} );
			
			/* HTML5 attribute detection - build an mData object automatically if the
			 * attributes are found
			 */
			if ( rowOne.length ) {
				var a = function ( cell, name ) {
					return cell.getAttribute( 'data-'+name ) !== null ? name : null;
				};
			
				$( rowOne[0] ).children('th, td').each( function (i, cell) {
					var col = oSettings.aoColumns[i];
			
					if ( col.mData === i ) {
						var sort = a( cell, 'sort' ) || a( cell, 'order' );
						var filter = a( cell, 'filter' ) || a( cell, 'search' );
			
						if ( sort !== null || filter !== null ) {
							col.mData = {
								_:      i+'.display',
								sort:   sort !== null   ? i+'.@data-'+sort   : undefined,
								type:   sort !== null   ? i+'.@data-'+sort   : undefined,
								filter: filter !== null ? i+'.@data-'+filter : undefined
							};
			
							_fnColumnOptions( oSettings, i );
						}
					}
				} );
			}
			
			var features = oSettings.oFeatures;
			
			/* Must be done after everything which can be overridden by the state saving! */
			if ( oInit.bStateSave )
			{
				features.bStateSave = true;
				_fnLoadState( oSettings, oInit );
				_fnCallbackReg( oSettings, 'aoDrawCallback', _fnSaveState, 'state_save' );
			}
			
			
			/*
			 * Sorting
			 * @todo For modularisation (1.11) this needs to do into a sort start up handler
			 */
			
			// If aaSorting is not defined, then we use the first indicator in asSorting
			// in case that has been altered, so the default sort reflects that option
			if ( oInit.aaSorting === undefined )
			{
				var sorting = oSettings.aaSorting;
				for ( i=0, iLen=sorting.length ; i<iLen ; i++ )
				{
					sorting[i][1] = oSettings.aoColumns[ i ].asSorting[0];
				}
			}
			
			/* Do a first pass on the sorting classes (allows any size changes to be taken into
			 * account, and also will apply sorting disabled classes if disabled
			 */
			_fnSortingClasses( oSettings );
			
			if ( features.bSort )
			{
				_fnCallbackReg( oSettings, 'aoDrawCallback', function () {
					if ( oSettings.bSorted ) {
						var aSort = _fnSortFlatten( oSettings );
						var sortedColumns = {};
			
						$.each( aSort, function (i, val) {
							sortedColumns[ val.src ] = val.dir;
						} );
			
						_fnCallbackFire( oSettings, null, 'order', [oSettings, aSort, sortedColumns] );
						_fnSortAria( oSettings );
					}
				} );
			}
			
			_fnCallbackReg( oSettings, 'aoDrawCallback', function () {
				if ( oSettings.bSorted || _fnDataSource( oSettings ) === 'ssp' || features.bDeferRender ) {
					_fnSortingClasses( oSettings );
				}
			}, 'sc' );
			
			
			/*
			 * Final init
			 * Cache the header, body and footer as required, creating them if needed
			 */
			
			// Work around for Webkit bug 83867 - store the caption-side before removing from doc
			var captions = $this.children('caption').each( function () {
				this._captionSide = $this.css('caption-side');
			} );
			
			var thead = $this.children('thead');
			if ( thead.length === 0 )
			{
				thead = $('<thead/>').appendTo(this);
			}
			oSettings.nTHead = thead[0];
			
			var tbody = $this.children('tbody');
			if ( tbody.length === 0 )
			{
				tbody = $('<tbody/>').appendTo(this);
			}
			oSettings.nTBody = tbody[0];
			
			var tfoot = $this.children('tfoot');
			if ( tfoot.length === 0 && captions.length > 0 && (oSettings.oScroll.sX !== "" || oSettings.oScroll.sY !== "") )
			{
				// If we are a scrolling table, and no footer has been given, then we need to create
				// a tfoot element for the caption element to be appended to
				tfoot = $('<tfoot/>').appendTo(this);
			}
			
			if ( tfoot.length === 0 || tfoot.children().length === 0 ) {
				$this.addClass( oClasses.sNoFooter );
			}
			else if ( tfoot.length > 0 ) {
				oSettings.nTFoot = tfoot[0];
				_fnDetectHeader( oSettings.aoFooter, oSettings.nTFoot );
			}
			
			/* Check if there is data passing into the constructor */
			if ( oInit.aaData )
			{
				for ( i=0 ; i<oInit.aaData.length ; i++ )
				{
					_fnAddData( oSettings, oInit.aaData[ i ] );
				}
			}
			else if ( oSettings.bDeferLoading || _fnDataSource( oSettings ) == 'dom' )
			{
				/* Grab the data from the page - only do this when deferred loading or no Ajax
				 * source since there is no point in reading the DOM data if we are then going
				 * to replace it with Ajax data
				 */
				_fnAddTr( oSettings, $(oSettings.nTBody).children('tr') );
			}
			
			/* Copy the data index array */
			oSettings.aiDisplay = oSettings.aiDisplayMaster.slice();
			
			/* Initialisation complete - table can be drawn */
			oSettings.bInitialised = true;
			
			/* Check if we need to initialise the table (it might not have been handed off to the
			 * language processor)
			 */
			if ( bInitHandedOff === false )
			{
				_fnInitialise( oSettings );
			}
		} );
		_that = null;
		return this;
	};

	
	
	/**
	 * Computed structure of the DataTables API, defined by the options passed to
	 * `DataTable.Api.register()` when building the API.
	 *
	 * The structure is built in order to speed creation and extension of the Api
	 * objects since the extensions are effectively pre-parsed.
	 *
	 * The array is an array of objects with the following structure, where this
	 * base array represents the Api prototype base:
	 *
	 *     [
	 *       {
	 *         name:      'data'                -- string   - Property name
	 *         val:       function () {},       -- function - Api method (or undefined if just an object
	 *         methodExt: [ ... ],              -- array    - Array of Api object definitions to extend the method result
	 *         propExt:   [ ... ]               -- array    - Array of Api object definitions to extend the property
	 *       },
	 *       {
	 *         name:     'row'
	 *         val:       {},
	 *         methodExt: [ ... ],
	 *         propExt:   [
	 *           {
	 *             name:      'data'
	 *             val:       function () {},
	 *             methodExt: [ ... ],
	 *             propExt:   [ ... ]
	 *           },
	 *           ...
	 *         ]
	 *       }
	 *     ]
	 *
	 * @type {Array}
	 * @ignore
	 */
	var __apiStruct = [];
	
	
	/**
	 * `Array.prototype` reference.
	 *
	 * @type object
	 * @ignore
	 */
	var __arrayProto = Array.prototype;
	
	
	/**
	 * Abstraction for `context` parameter of the `Api` constructor to allow it to
	 * take several different forms for ease of use.
	 *
	 * Each of the input parameter types will be converted to a DataTables settings
	 * object where possible.
	 *
	 * @param  {string|node|jQuery|object} mixed DataTable identifier. Can be one
	 *   of:
	 *
	 *   * `string` - jQuery selector. Any DataTables' matching the given selector
	 *     with be found and used.
	 *   * `node` - `TABLE` node which has already been formed into a DataTable.
	 *   * `jQuery` - A jQuery object of `TABLE` nodes.
	 *   * `object` - DataTables settings object
	 *   * `DataTables.Api` - API instance
	 * @return {array|null} Matching DataTables settings objects. `null` or
	 *   `undefined` is returned if no matching DataTable is found.
	 * @ignore
	 */
	var _toSettings = function ( mixed )
	{
		var idx, jq;
		var settings = DataTable.settings;
		var tables = $.map( settings, function (el, i) {
			return el.nTable;
		} );
	
		if ( ! mixed ) {
			return [];
		}
		else if ( mixed.nTable && mixed.oApi ) {
			// DataTables settings object
			return [ mixed ];
		}
		else if ( mixed.nodeName && mixed.nodeName.toLowerCase() === 'table' ) {
			// Table node
			idx = $.inArray( mixed, tables );
			return idx !== -1 ? [ settings[idx] ] : null;
		}
		else if ( mixed && typeof mixed.settings === 'function' ) {
			return mixed.settings().toArray();
		}
		else if ( typeof mixed === 'string' ) {
			// jQuery selector
			jq = $(mixed);
		}
		else if ( mixed instanceof $ ) {
			// jQuery object (also DataTables instance)
			jq = mixed;
		}
	
		if ( jq ) {
			return jq.map( function(i) {
				idx = $.inArray( this, tables );
				return idx !== -1 ? settings[idx] : null;
			} ).toArray();
		}
	};
	
	
	/**
	 * DataTables API class - used to control and interface with  one or more
	 * DataTables enhanced tables.
	 *
	 * The API class is heavily based on jQuery, presenting a chainable interface
	 * that you can use to interact with tables. Each instance of the API class has
	 * a "context" - i.e. the tables that it will operate on. This could be a single
	 * table, all tables on a page or a sub-set thereof.
	 *
	 * Additionally the API is designed to allow you to easily work with the data in
	 * the tables, retrieving and manipulating it as required. This is done by
	 * presenting the API class as an array like interface. The contents of the
	 * array depend upon the actions requested by each method (for example
	 * `rows().nodes()` will return an array of nodes, while `rows().data()` will
	 * return an array of objects or arrays depending upon your table's
	 * configuration). The API object has a number of array like methods (`push`,
	 * `pop`, `reverse` etc) as well as additional helper methods (`each`, `pluck`,
	 * `unique` etc) to assist your working with the data held in a table.
	 *
	 * Most methods (those which return an Api instance) are chainable, which means
	 * the return from a method call also has all of the methods available that the
	 * top level object had. For example, these two calls are equivalent:
	 *
	 *     // Not chained
	 *     api.row.add( {...} );
	 *     api.draw();
	 *
	 *     // Chained
	 *     api.row.add( {...} ).draw();
	 *
	 * @class DataTable.Api
	 * @param {array|object|string|jQuery} context DataTable identifier. This is
	 *   used to define which DataTables enhanced tables this API will operate on.
	 *   Can be one of:
	 *
	 *   * `string` - jQuery selector. Any DataTables' matching the given selector
	 *     with be found and used.
	 *   * `node` - `TABLE` node which has already been formed into a DataTable.
	 *   * `jQuery` - A jQuery object of `TABLE` nodes.
	 *   * `object` - DataTables settings object
	 * @param {array} [data] Data to initialise the Api instance with.
	 *
	 * @example
	 *   // Direct initialisation during DataTables construction
	 *   var api = $('#example').DataTable();
	 *
	 * @example
	 *   // Initialisation using a DataTables jQuery object
	 *   var api = $('#example').dataTable().api();
	 *
	 * @example
	 *   // Initialisation as a constructor
	 *   var api = new $.fn.DataTable.Api( 'table.dataTable' );
	 */
	_Api = function ( context, data )
	{
		if ( ! (this instanceof _Api) ) {
			return new _Api( context, data );
		}
	
		var settings = [];
		var ctxSettings = function ( o ) {
			var a = _toSettings( o );
			if ( a ) {
				settings = settings.concat( a );
			}
		};
	
		if ( $.isArray( context ) ) {
			for ( var i=0, ien=context.length ; i<ien ; i++ ) {
				ctxSettings( context[i] );
			}
		}
		else {
			ctxSettings( context );
		}
	
		// Remove duplicates
		this.context = _unique( settings );
	
		// Initial data
		if ( data ) {
			$.merge( this, data );
		}
	
		// selector
		this.selector = {
			rows: null,
			cols: null,
			opts: null
		};
	
		_Api.extend( this, this, __apiStruct );
	};
	
	DataTable.Api = _Api;
	
	// Don't destroy the existing prototype, just extend it. Required for jQuery 2's
	// isPlainObject.
	$.extend( _Api.prototype, {
		any: function ()
		{
			return this.count() !== 0;
		},
	
	
		concat:  __arrayProto.concat,
	
	
		context: [], // array of table settings objects
	
	
		count: function ()
		{
			return this.flatten().length;
		},
	
	
		each: function ( fn )
		{
			for ( var i=0, ien=this.length ; i<ien; i++ ) {
				fn.call( this, this[i], i, this );
			}
	
			return this;
		},
	
	
		eq: function ( idx )
		{
			var ctx = this.context;
	
			return ctx.length > idx ?
				new _Api( ctx[idx], this[idx] ) :
				null;
		},
	
	
		filter: function ( fn )
		{
			var a = [];
	
			if ( __arrayProto.filter ) {
				a = __arrayProto.filter.call( this, fn, this );
			}
			else {
				// Compatibility for browsers without EMCA-252-5 (JS 1.6)
				for ( var i=0, ien=this.length ; i<ien ; i++ ) {
					if ( fn.call( this, this[i], i, this ) ) {
						a.push( this[i] );
					}
				}
			}
	
			return new _Api( this.context, a );
		},
	
	
		flatten: function ()
		{
			var a = [];
			return new _Api( this.context, a.concat.apply( a, this.toArray() ) );
		},
	
	
		join:    __arrayProto.join,
	
	
		indexOf: __arrayProto.indexOf || function (obj, start)
		{
			for ( var i=(start || 0), ien=this.length ; i<ien ; i++ ) {
				if ( this[i] === obj ) {
					return i;
				}
			}
			return -1;
		},
	
		iterator: function ( flatten, type, fn, alwaysNew ) {
			var
				a = [], ret,
				i, ien, j, jen,
				context = this.context,
				rows, items, item,
				selector = this.selector;
	
			// Argument shifting
			if ( typeof flatten === 'string' ) {
				alwaysNew = fn;
				fn = type;
				type = flatten;
				flatten = false;
			}
	
			for ( i=0, ien=context.length ; i<ien ; i++ ) {
				var apiInst = new _Api( context[i] );
	
				if ( type === 'table' ) {
					ret = fn.call( apiInst, context[i], i );
	
					if ( ret !== undefined ) {
						a.push( ret );
					}
				}
				else if ( type === 'columns' || type === 'rows' ) {
					// this has same length as context - one entry for each table
					ret = fn.call( apiInst, context[i], this[i], i );
	
					if ( ret !== undefined ) {
						a.push( ret );
					}
				}
				else if ( type === 'column' || type === 'column-rows' || type === 'row' || type === 'cell' ) {
					// columns and rows share the same structure.
					// 'this' is an array of column indexes for each context
					items = this[i];
	
					if ( type === 'column-rows' ) {
						rows = _selector_row_indexes( context[i], selector.opts );
					}
	
					for ( j=0, jen=items.length ; j<jen ; j++ ) {
						item = items[j];
	
						if ( type === 'cell' ) {
							ret = fn.call( apiInst, context[i], item.row, item.column, i, j );
						}
						else {
							ret = fn.call( apiInst, context[i], item, i, j, rows );
						}
	
						if ( ret !== undefined ) {
							a.push( ret );
						}
					}
				}
			}
	
			if ( a.length || alwaysNew ) {
				var api = new _Api( context, flatten ? a.concat.apply( [], a ) : a );
				var apiSelector = api.selector;
				apiSelector.rows = selector.rows;
				apiSelector.cols = selector.cols;
				apiSelector.opts = selector.opts;
				return api;
			}
			return this;
		},
	
	
		lastIndexOf: __arrayProto.lastIndexOf || function (obj, start)
		{
			// Bit cheeky...
			return this.indexOf.apply( this.toArray.reverse(), arguments );
		},
	
	
		length:  0,
	
	
		map: function ( fn )
		{
			var a = [];
	
			if ( __arrayProto.map ) {
				a = __arrayProto.map.call( this, fn, this );
			}
			else {
				// Compatibility for browsers without EMCA-252-5 (JS 1.6)
				for ( var i=0, ien=this.length ; i<ien ; i++ ) {
					a.push( fn.call( this, this[i], i ) );
				}
			}
	
			return new _Api( this.context, a );
		},
	
	
		pluck: function ( prop )
		{
			return this.map( function ( el ) {
				return el[ prop ];
			} );
		},
	
		pop:     __arrayProto.pop,
	
	
		push:    __arrayProto.push,
	
	
		// Does not return an API instance
		reduce: __arrayProto.reduce || function ( fn, init )
		{
			return _fnReduce( this, fn, init, 0, this.length, 1 );
		},
	
	
		reduceRight: __arrayProto.reduceRight || function ( fn, init )
		{
			return _fnReduce( this, fn, init, this.length-1, -1, -1 );
		},
	
	
		reverse: __arrayProto.reverse,
	
	
		// Object with rows, columns and opts
		selector: null,
	
	
		shift:   __arrayProto.shift,
	
	
		sort:    __arrayProto.sort, // ? name - order?
	
	
		splice:  __arrayProto.splice,
	
	
		toArray: function ()
		{
			return __arrayProto.slice.call( this );
		},
	
	
		to$: function ()
		{
			return $( this );
		},
	
	
		toJQuery: function ()
		{
			return $( this );
		},
	
	
		unique: function ()
		{
			return new _Api( this.context, _unique(this) );
		},
	
	
		unshift: __arrayProto.unshift
	} );
	
	
	_Api.extend = function ( scope, obj, ext )
	{
		// Only extend API instances and static properties of the API
		if ( ! ext.length || ! obj || ( ! (obj instanceof _Api) && ! obj.__dt_wrapper ) ) {
			return;
		}
	
		var
			i, ien,
			j, jen,
			struct, inner,
			methodScoping = function ( scope, fn, struc ) {
				return function () {
					var ret = fn.apply( scope, arguments );
	
					// Method extension
					_Api.extend( ret, ret, struc.methodExt );
					return ret;
				};
			};
	
		for ( i=0, ien=ext.length ; i<ien ; i++ ) {
			struct = ext[i];
	
			// Value
			obj[ struct.name ] = typeof struct.val === 'function' ?
				methodScoping( scope, struct.val, struct ) :
				$.isPlainObject( struct.val ) ?
					{} :
					struct.val;
	
			obj[ struct.name ].__dt_wrapper = true;
	
			// Property extension
			_Api.extend( scope, obj[ struct.name ], struct.propExt );
		}
	};
	
	
	// @todo - Is there need for an augment function?
	// _Api.augment = function ( inst, name )
	// {
	// 	// Find src object in the structure from the name
	// 	var parts = name.split('.');
	
	// 	_Api.extend( inst, obj );
	// };
	
	
	//     [
	//       {
	//         name:      'data'                -- string   - Property name
	//         val:       function () {},       -- function - Api method (or undefined if just an object
	//         methodExt: [ ... ],              -- array    - Array of Api object definitions to extend the method result
	//         propExt:   [ ... ]               -- array    - Array of Api object definitions to extend the property
	//       },
	//       {
	//         name:     'row'
	//         val:       {},
	//         methodExt: [ ... ],
	//         propExt:   [
	//           {
	//             name:      'data'
	//             val:       function () {},
	//             methodExt: [ ... ],
	//             propExt:   [ ... ]
	//           },
	//           ...
	//         ]
	//       }
	//     ]
	
	_Api.register = _api_register = function ( name, val )
	{
		if ( $.isArray( name ) ) {
			for ( var j=0, jen=name.length ; j<jen ; j++ ) {
				_Api.register( name[j], val );
			}
			return;
		}
	
		var
			i, ien,
			heir = name.split('.'),
			struct = __apiStruct,
			key, method;
	
		var find = function ( src, name ) {
			for ( var i=0, ien=src.length ; i<ien ; i++ ) {
				if ( src[i].name === name ) {
					return src[i];
				}
			}
			return null;
		};
	
		for ( i=0, ien=heir.length ; i<ien ; i++ ) {
			method = heir[i].indexOf('()') !== -1;
			key = method ?
				heir[i].replace('()', '') :
				heir[i];
	
			var src = find( struct, key );
			if ( ! src ) {
				src = {
					name:      key,
					val:       {},
					methodExt: [],
					propExt:   []
				};
				struct.push( src );
			}
	
			if ( i === ien-1 ) {
				src.val = val;
			}
			else {
				struct = method ?
					src.methodExt :
					src.propExt;
			}
		}
	};
	
	
	_Api.registerPlural = _api_registerPlural = function ( pluralName, singularName, val ) {
		_Api.register( pluralName, val );
	
		_Api.register( singularName, function () {
			var ret = val.apply( this, arguments );
	
			if ( ret === this ) {
				// Returned item is the API instance that was passed in, return it
				return this;
			}
			else if ( ret instanceof _Api ) {
				// New API instance returned, want the value from the first item
				// in the returned array for the singular result.
				return ret.length ?
					$.isArray( ret[0] ) ?
						new _Api( ret.context, ret[0] ) : // Array results are 'enhanced'
						ret[0] :
					undefined;
			}
	
			// Non-API return - just fire it back
			return ret;
		} );
	};
	
	
	/**
	 * Selector for HTML tables. Apply the given selector to the give array of
	 * DataTables settings objects.
	 *
	 * @param {string|integer} [selector] jQuery selector string or integer
	 * @param  {array} Array of DataTables settings objects to be filtered
	 * @return {array}
	 * @ignore
	 */
	var __table_selector = function ( selector, a )
	{
		// Integer is used to pick out a table by index
		if ( typeof selector === 'number' ) {
			return [ a[ selector ] ];
		}
	
		// Perform a jQuery selector on the table nodes
		var nodes = $.map( a, function (el, i) {
			return el.nTable;
		} );
	
		return $(nodes)
			.filter( selector )
			.map( function (i) {
				// Need to translate back from the table node to the settings
				var idx = $.inArray( this, nodes );
				return a[ idx ];
			} )
			.toArray();
	};
	
	
	
	/**
	 * Context selector for the API's context (i.e. the tables the API instance
	 * refers to.
	 *
	 * @name    DataTable.Api#tables
	 * @param {string|integer} [selector] Selector to pick which tables the iterator
	 *   should operate on. If not given, all tables in the current context are
	 *   used. This can be given as a jQuery selector (for example `':gt(0)'`) to
	 *   select multiple tables or as an integer to select a single table.
	 * @returns {DataTable.Api} Returns a new API instance if a selector is given.
	 */
	_api_register( 'tables()', function ( selector ) {
		// A new instance is created if there was a selector specified
		return selector ?
			new _Api( __table_selector( selector, this.context ) ) :
			this;
	} );
	
	
	_api_register( 'table()', function ( selector ) {
		var tables = this.tables( selector );
		var ctx = tables.context;
	
		// Truncate to the first matched table
		return ctx.length ?
			new _Api( ctx[0] ) :
			tables;
	} );
	
	
	_api_registerPlural( 'tables().nodes()', 'table().node()' , function () {
		return this.iterator( 'table', function ( ctx ) {
			return ctx.nTable;
		}, 1 );
	} );
	
	
	_api_registerPlural( 'tables().body()', 'table().body()' , function () {
		return this.iterator( 'table', function ( ctx ) {
			return ctx.nTBody;
		}, 1 );
	} );
	
	
	_api_registerPlural( 'tables().header()', 'table().header()' , function () {
		return this.iterator( 'table', function ( ctx ) {
			return ctx.nTHead;
		}, 1 );
	} );
	
	
	_api_registerPlural( 'tables().footer()', 'table().footer()' , function () {
		return this.iterator( 'table', function ( ctx ) {
			return ctx.nTFoot;
		}, 1 );
	} );
	
	
	_api_registerPlural( 'tables().containers()', 'table().container()' , function () {
		return this.iterator( 'table', function ( ctx ) {
			return ctx.nTableWrapper;
		}, 1 );
	} );
	
	
	
	/**
	 * Redraw the tables in the current context.
	 */
	_api_register( 'draw()', function ( paging ) {
		return this.iterator( 'table', function ( settings ) {
			if ( paging === 'page' ) {
				_fnDraw( settings );
			}
			else {
				if ( typeof paging === 'string' ) {
					paging = paging === 'full-hold' ?
						false :
						true;
				}
	
				_fnReDraw( settings, paging===false );
			}
		} );
	} );
	
	
	
	/**
	 * Get the current page index.
	 *
	 * @return {integer} Current page index (zero based)
	 *//**
	 * Set the current page.
	 *
	 * Note that if you attempt to show a page which does not exist, DataTables will
	 * not throw an error, but rather reset the paging.
	 *
	 * @param {integer|string} action The paging action to take. This can be one of:
	 *  * `integer` - The page index to jump to
	 *  * `string` - An action to take:
	 *    * `first` - Jump to first page.
	 *    * `next` - Jump to the next page
	 *    * `previous` - Jump to previous page
	 *    * `last` - Jump to the last page.
	 * @returns {DataTables.Api} this
	 */
	_api_register( 'page()', function ( action ) {
		if ( action === undefined ) {
			return this.page.info().page; // not an expensive call
		}
	
		// else, have an action to take on all tables
		return this.iterator( 'table', function ( settings ) {
			_fnPageChange( settings, action );
		} );
	} );
	
	
	/**
	 * Paging information for the first table in the current context.
	 *
	 * If you require paging information for another table, use the `table()` method
	 * with a suitable selector.
	 *
	 * @return {object} Object with the following properties set:
	 *  * `page` - Current page index (zero based - i.e. the first page is `0`)
	 *  * `pages` - Total number of pages
	 *  * `start` - Display index for the first record shown on the current page
	 *  * `end` - Display index for the last record shown on the current page
	 *  * `length` - Display length (number of records). Note that generally `start
	 *    + length = end`, but this is not always true, for example if there are
	 *    only 2 records to show on the final page, with a length of 10.
	 *  * `recordsTotal` - Full data set length
	 *  * `recordsDisplay` - Data set length once the current filtering criterion
	 *    are applied.
	 */
	_api_register( 'page.info()', function ( action ) {
		if ( this.context.length === 0 ) {
			return undefined;
		}
	
		var
			settings   = this.context[0],
			start      = settings._iDisplayStart,
			len        = settings.oFeatures.bPaginate ? settings._iDisplayLength : -1,
			visRecords = settings.fnRecordsDisplay(),
			all        = len === -1;
	
		return {
			"page":           all ? 0 : Math.floor( start / len ),
			"pages":          all ? 1 : Math.ceil( visRecords / len ),
			"start":          start,
			"end":            settings.fnDisplayEnd(),
			"length":         len,
			"recordsTotal":   settings.fnRecordsTotal(),
			"recordsDisplay": visRecords,
			"serverSide":     _fnDataSource( settings ) === 'ssp'
		};
	} );
	
	
	/**
	 * Get the current page length.
	 *
	 * @return {integer} Current page length. Note `-1` indicates that all records
	 *   are to be shown.
	 *//**
	 * Set the current page length.
	 *
	 * @param {integer} Page length to set. Use `-1` to show all records.
	 * @returns {DataTables.Api} this
	 */
	_api_register( 'page.len()', function ( len ) {
		// Note that we can't call this function 'length()' because `length`
		// is a Javascript property of functions which defines how many arguments
		// the function expects.
		if ( len === undefined ) {
			return this.context.length !== 0 ?
				this.context[0]._iDisplayLength :
				undefined;
		}
	
		// else, set the page length
		return this.iterator( 'table', function ( settings ) {
			_fnLengthChange( settings, len );
		} );
	} );
	
	
	
	var __reload = function ( settings, holdPosition, callback ) {
		// Use the draw event to trigger a callback
		if ( callback ) {
			var api = new _Api( settings );
	
			api.one( 'draw', function () {
				callback( api.ajax.json() );
			} );
		}
	
		if ( _fnDataSource( settings ) == 'ssp' ) {
			_fnReDraw( settings, holdPosition );
		}
		else {
			_fnProcessingDisplay( settings, true );
	
			// Cancel an existing request
			var xhr = settings.jqXHR;
			if ( xhr && xhr.readyState !== 4 ) {
				xhr.abort();
			}
	
			// Trigger xhr
			_fnBuildAjax( settings, [], function( json ) {
				_fnClearTable( settings );
	
				var data = _fnAjaxDataSrc( settings, json );
				for ( var i=0, ien=data.length ; i<ien ; i++ ) {
					_fnAddData( settings, data[i] );
				}
	
				_fnReDraw( settings, holdPosition );
				_fnProcessingDisplay( settings, false );
			} );
		}
	};
	
	
	/**
	 * Get the JSON response from the last Ajax request that DataTables made to the
	 * server. Note that this returns the JSON from the first table in the current
	 * context.
	 *
	 * @return {object} JSON received from the server.
	 */
	_api_register( 'ajax.json()', function () {
		var ctx = this.context;
	
		if ( ctx.length > 0 ) {
			return ctx[0].json;
		}
	
		// else return undefined;
	} );
	
	
	/**
	 * Get the data submitted in the last Ajax request
	 */
	_api_register( 'ajax.params()', function () {
		var ctx = this.context;
	
		if ( ctx.length > 0 ) {
			return ctx[0].oAjaxData;
		}
	
		// else return undefined;
	} );
	
	
	/**
	 * Reload tables from the Ajax data source. Note that this function will
	 * automatically re-draw the table when the remote data has been loaded.
	 *
	 * @param {boolean} [reset=true] Reset (default) or hold the current paging
	 *   position. A full re-sort and re-filter is performed when this method is
	 *   called, which is why the pagination reset is the default action.
	 * @returns {DataTables.Api} this
	 */
	_api_register( 'ajax.reload()', function ( callback, resetPaging ) {
		return this.iterator( 'table', function (settings) {
			__reload( settings, resetPaging===false, callback );
		} );
	} );
	
	
	/**
	 * Get the current Ajax URL. Note that this returns the URL from the first
	 * table in the current context.
	 *
	 * @return {string} Current Ajax source URL
	 *//**
	 * Set the Ajax URL. Note that this will set the URL for all tables in the
	 * current context.
	 *
	 * @param {string} url URL to set.
	 * @returns {DataTables.Api} this
	 */
	_api_register( 'ajax.url()', function ( url ) {
		var ctx = this.context;
	
		if ( url === undefined ) {
			// get
			if ( ctx.length === 0 ) {
				return undefined;
			}
			ctx = ctx[0];
	
			return ctx.ajax ?
				$.isPlainObject( ctx.ajax ) ?
					ctx.ajax.url :
					ctx.ajax :
				ctx.sAjaxSource;
		}
	
		// set
		return this.iterator( 'table', function ( settings ) {
			if ( $.isPlainObject( settings.ajax ) ) {
				settings.ajax.url = url;
			}
			else {
				settings.ajax = url;
			}
			// No need to consider sAjaxSource here since DataTables gives priority
			// to `ajax` over `sAjaxSource`. So setting `ajax` here, renders any
			// value of `sAjaxSource` redundant.
		} );
	} );
	
	
	/**
	 * Load data from the newly set Ajax URL. Note that this method is only
	 * available when `ajax.url()` is used to set a URL. Additionally, this method
	 * has the same effect as calling `ajax.reload()` but is provided for
	 * convenience when setting a new URL. Like `ajax.reload()` it will
	 * automatically redraw the table once the remote data has been loaded.
	 *
	 * @returns {DataTables.Api} this
	 */
	_api_register( 'ajax.url().load()', function ( callback, resetPaging ) {
		// Same as a reload, but makes sense to present it for easy access after a
		// url change
		return this.iterator( 'table', function ( ctx ) {
			__reload( ctx, resetPaging===false, callback );
		} );
	} );
	
	
	
	
	var _selector_run = function ( type, selector, selectFn, settings, opts )
	{
		var
			out = [], res,
			a, i, ien, j, jen,
			selectorType = typeof selector;
	
		// Can't just check for isArray here, as an API or jQuery instance might be
		// given with their array like look
		if ( ! selector || selectorType === 'string' || selectorType === 'function' || selector.length === undefined ) {
			selector = [ selector ];
		}
	
		for ( i=0, ien=selector.length ; i<ien ; i++ ) {
			a = selector[i] && selector[i].split ?
				selector[i].split(',') :
				[ selector[i] ];
	
			for ( j=0, jen=a.length ; j<jen ; j++ ) {
				res = selectFn( typeof a[j] === 'string' ? $.trim(a[j]) : a[j] );
	
				if ( res && res.length ) {
					out = out.concat( res );
				}
			}
		}
	
		// selector extensions
		var ext = _ext.selector[ type ];
		if ( ext.length ) {
			for ( i=0, ien=ext.length ; i<ien ; i++ ) {
				out = ext[i]( settings, opts, out );
			}
		}
	
		return _unique( out );
	};
	
	
	var _selector_opts = function ( opts )
	{
		if ( ! opts ) {
			opts = {};
		}
	
		// Backwards compatibility for 1.9- which used the terminology filter rather
		// than search
		if ( opts.filter && opts.search === undefined ) {
			opts.search = opts.filter;
		}
	
		return $.extend( {
			search: 'none',
			order: 'current',
			page: 'all'
		}, opts );
	};
	
	
	var _selector_first = function ( inst )
	{
		// Reduce the API instance to the first item found
		for ( var i=0, ien=inst.length ; i<ien ; i++ ) {
			if ( inst[i].length > 0 ) {
				// Assign the first element to the first item in the instance
				// and truncate the instance and context
				inst[0] = inst[i];
				inst[0].length = 1;
				inst.length = 1;
				inst.context = [ inst.context[i] ];
	
				return inst;
			}
		}
	
		// Not found - return an empty instance
		inst.length = 0;
		return inst;
	};
	
	
	var _selector_row_indexes = function ( settings, opts )
	{
		var
			i, ien, tmp, a=[],
			displayFiltered = settings.aiDisplay,
			displayMaster = settings.aiDisplayMaster;
	
		var
			search = opts.search,  // none, applied, removed
			order  = opts.order,   // applied, current, index (original - compatibility with 1.9)
			page   = opts.page;    // all, current
	
		if ( _fnDataSource( settings ) == 'ssp' ) {
			// In server-side processing mode, most options are irrelevant since
			// rows not shown don't exist and the index order is the applied order
			// Removed is a special case - for consistency just return an empty
			// array
			return search === 'removed' ?
				[] :
				_range( 0, displayMaster.length );
		}
		else if ( page == 'current' ) {
			// Current page implies that order=current and fitler=applied, since it is
			// fairly senseless otherwise, regardless of what order and search actually
			// are
			for ( i=settings._iDisplayStart, ien=settings.fnDisplayEnd() ; i<ien ; i++ ) {
				a.push( displayFiltered[i] );
			}
		}
		else if ( order == 'current' || order == 'applied' ) {
			a = search == 'none' ?
				displayMaster.slice() :                      // no search
				search == 'applied' ?
					displayFiltered.slice() :                // applied search
					$.map( displayMaster, function (el, i) { // removed search
						return $.inArray( el, displayFiltered ) === -1 ? el : null;
					} );
		}
		else if ( order == 'index' || order == 'original' ) {
			for ( i=0, ien=settings.aoData.length ; i<ien ; i++ ) {
				if ( search == 'none' ) {
					a.push( i );
				}
				else { // applied | removed
					tmp = $.inArray( i, displayFiltered );
	
					if ((tmp === -1 && search == 'removed') ||
						(tmp >= 0   && search == 'applied') )
					{
						a.push( i );
					}
				}
			}
		}
	
		return a;
	};
	
	
	/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
	 * Rows
	 *
	 * {}          - no selector - use all available rows
	 * {integer}   - row aoData index
	 * {node}      - TR node
	 * {string}    - jQuery selector to apply to the TR elements
	 * {array}     - jQuery array of nodes, or simply an array of TR nodes
	 *
	 */
	
	
	var __row_selector = function ( settings, selector, opts )
	{
		var run = function ( sel ) {
			var selInt = _intVal( sel );
			var i, ien;
	
			// Short cut - selector is a number and no options provided (default is
			// all records, so no need to check if the index is in there, since it
			// must be - dev error if the index doesn't exist).
			if ( selInt !== null && ! opts ) {
				return [ selInt ];
			}
	
			var rows = _selector_row_indexes( settings, opts );
	
			if ( selInt !== null && $.inArray( selInt, rows ) !== -1 ) {
				// Selector - integer
				return [ selInt ];
			}
			else if ( ! sel ) {
				// Selector - none
				return rows;
			}
	
			// Selector - function
			if ( typeof sel === 'function' ) {
				return $.map( rows, function (idx) {
					var row = settings.aoData[ idx ];
					return sel( idx, row._aData, row.nTr ) ? idx : null;
				} );
			}
	
			// Get nodes in the order from the `rows` array with null values removed
			var nodes = _removeEmpty(
				_pluck_order( settings.aoData, rows, 'nTr' )
			);
	
			// Selector - node
			if ( sel.nodeName ) {
				if ( $.inArray( sel, nodes ) !== -1 ) {
					return [ sel._DT_RowIndex ]; // sel is a TR node that is in the table
					                             // and DataTables adds a prop for fast lookup
				}
			}
	
			// ID selector. Want to always be able to select rows by id, regardless
			// of if the tr element has been created or not, so can't rely upon
			// jQuery here - hence a custom implementation. This does not match
			// Sizzle's fast selector or HTML4 - in HTML5 the ID can be anything,
			// but to select it using a CSS selector engine (like Sizzle or
			// querySelect) it would need to need to be escaped for some characters.
			// DataTables simplifies this for row selectors since you can select
			// only a row. A # indicates an id any anything that follows is the id -
			// unescaped.
			if ( typeof sel === 'string' && sel.charAt(0) === '#' ) {
				// get row index from id
				var rowObj = settings.aIds[ sel.replace( /^#/, '' ) ];
				if ( rowObj !== undefined ) {
					return [ rowObj.idx ];
				}
	
				// need to fall through to jQuery in case there is DOM id that
				// matches
			}
	
			// Selector - jQuery selector string, array of nodes or jQuery object/
			// As jQuery's .filter() allows jQuery objects to be passed in filter,
			// it also allows arrays, so this will cope with all three options
			return $(nodes)
				.filter( sel )
				.map( function () {
					return this._DT_RowIndex;
				} )
				.toArray();
		};
	
		return _selector_run( 'row', selector, run, settings, opts );
	};
	
	
	_api_register( 'rows()', function ( selector, opts ) {
		// argument shifting
		if ( selector === undefined ) {
			selector = '';
		}
		else if ( $.isPlainObject( selector ) ) {
			opts = selector;
			selector = '';
		}
	
		opts = _selector_opts( opts );
	
		var inst = this.iterator( 'table', function ( settings ) {
			return __row_selector( settings, selector, opts );
		}, 1 );
	
		// Want argument shifting here and in __row_selector?
		inst.selector.rows = selector;
		inst.selector.opts = opts;
	
		return inst;
	} );
	
	_api_register( 'rows().nodes()', function () {
		return this.iterator( 'row', function ( settings, row ) {
			return settings.aoData[ row ].nTr || undefined;
		}, 1 );
	} );
	
	_api_register( 'rows().data()', function () {
		return this.iterator( true, 'rows', function ( settings, rows ) {
			return _pluck_order( settings.aoData, rows, '_aData' );
		}, 1 );
	} );
	
	_api_registerPlural( 'rows().cache()', 'row().cache()', function ( type ) {
		return this.iterator( 'row', function ( settings, row ) {
			var r = settings.aoData[ row ];
			return type === 'search' ? r._aFilterData : r._aSortData;
		}, 1 );
	} );
	
	_api_registerPlural( 'rows().invalidate()', 'row().invalidate()', function ( src ) {
		return this.iterator( 'row', function ( settings, row ) {
			_fnInvalidate( settings, row, src );
		} );
	} );
	
	_api_registerPlural( 'rows().indexes()', 'row().index()', function () {
		return this.iterator( 'row', function ( settings, row ) {
			return row;
		}, 1 );
	} );
	
	_api_registerPlural( 'rows().ids()', 'row().id()', function ( hash ) {
		var a = [];
		var context = this.context;
	
		// `iterator` will drop undefined values, but in this case we want them
		for ( var i=0, ien=context.length ; i<ien ; i++ ) {
			for ( var j=0, jen=this[i].length ; j<jen ; j++ ) {
				var id = context[i].rowIdFn( context[i].aoData[ this[i][j] ]._aData );
				a.push( (hash === true ? '#' : '' )+ id );
			}
		}
	
		return new _Api( context, a );
	} );
	
	_api_registerPlural( 'rows().remove()', 'row().remove()', function () {
		var that = this;
	
		this.iterator( 'row', function ( settings, row, thatIdx ) {
			var data = settings.aoData;
			var rowData = data[ row ];
			var i, ien, j, jen;
			var loopRow, loopCells;
	
			data.splice( row, 1 );
	
			// Update the cached indexes
			for ( i=0, ien=data.length ; i<ien ; i++ ) {
				loopRow = data[i];
				loopCells = loopRow.anCells;
	
				// Rows
				if ( loopRow.nTr !== null ) {
					loopRow.nTr._DT_RowIndex = i;
				}
	
				// Cells
				if ( loopCells !== null ) {
					for ( j=0, jen=loopCells.length ; j<jen ; j++ ) {
						loopCells[j]._DT_CellIndex.row = i;
					}
				}
			}
	
			// Delete from the display arrays
			_fnDeleteIndex( settings.aiDisplayMaster, row );
			_fnDeleteIndex( settings.aiDisplay, row );
			_fnDeleteIndex( that[ thatIdx ], row, false ); // maintain local indexes
	
			// Check for an 'overflow' they case for displaying the table
			_fnLengthOverflow( settings );
	
			// Remove the row's ID reference if there is one
			var id = settings.rowIdFn( rowData._aData );
			if ( id !== undefined ) {
				delete settings.aIds[ id ];
			}
		} );
	
		this.iterator( 'table', function ( settings ) {
			for ( var i=0, ien=settings.aoData.length ; i<ien ; i++ ) {
				settings.aoData[i].idx = i;
			}
		} );
	
		return this;
	} );
	
	
	_api_register( 'rows.add()', function ( rows ) {
		var newRows = this.iterator( 'table', function ( settings ) {
				var row, i, ien;
				var out = [];
	
				for ( i=0, ien=rows.length ; i<ien ; i++ ) {
					row = rows[i];
	
					if ( row.nodeName && row.nodeName.toUpperCase() === 'TR' ) {
						out.push( _fnAddTr( settings, row )[0] );
					}
					else {
						out.push( _fnAddData( settings, row ) );
					}
				}
	
				return out;
			}, 1 );
	
		// Return an Api.rows() extended instance, so rows().nodes() etc can be used
		var modRows = this.rows( -1 );
		modRows.pop();
		$.merge( modRows, newRows );
	
		return modRows;
	} );
	
	
	
	
	
	/**
	 *
	 */
	_api_register( 'row()', function ( selector, opts ) {
		return _selector_first( this.rows( selector, opts ) );
	} );
	
	
	_api_register( 'row().data()', function ( data ) {
		var ctx = this.context;
	
		if ( data === undefined ) {
			// Get
			return ctx.length && this.length ?
				ctx[0].aoData[ this[0] ]._aData :
				undefined;
		}
	
		// Set
		ctx[0].aoData[ this[0] ]._aData = data;
	
		// Automatically invalidate
		_fnInvalidate( ctx[0], this[0], 'data' );
	
		return this;
	} );
	
	
	_api_register( 'row().node()', function () {
		var ctx = this.context;
	
		return ctx.length && this.length ?
			ctx[0].aoData[ this[0] ].nTr || null :
			null;
	} );
	
	
	_api_register( 'row.add()', function ( row ) {
		// Allow a jQuery object to be passed in - only a single row is added from
		// it though - the first element in the set
		if ( row instanceof $ && row.length ) {
			row = row[0];
		}
	
		var rows = this.iterator( 'table', function ( settings ) {
			if ( row.nodeName && row.nodeName.toUpperCase() === 'TR' ) {
				return _fnAddTr( settings, row )[0];
			}
			return _fnAddData( settings, row );
		} );
	
		// Return an Api.rows() extended instance, with the newly added row selected
		return this.row( rows[0] );
	} );
	
	
	
	var __details_add = function ( ctx, row, data, klass )
	{
		// Convert to array of TR elements
		var rows = [];
		var addRow = function ( r, k ) {
			// Recursion to allow for arrays of jQuery objects
			if ( $.isArray( r ) || r instanceof $ ) {
				for ( var i=0, ien=r.length ; i<ien ; i++ ) {
					addRow( r[i], k );
				}
				return;
			}
	
			// If we get a TR element, then just add it directly - up to the dev
			// to add the correct number of columns etc
			if ( r.nodeName && r.nodeName.toLowerCase() === 'tr' ) {
				rows.push( r );
			}
			else {
				// Otherwise create a row with a wrapper
				var created = $('<tr><td/></tr>').addClass( k );
				$('td', created)
					.addClass( k )
					.html( r )
					[0].colSpan = _fnVisbleColumns( ctx );
	
				rows.push( created[0] );
			}
		};
	
		addRow( data, klass );
	
		if ( row._details ) {
			row._details.remove();
		}
	
		row._details = $(rows);
	
		// If the children were already shown, that state should be retained
		if ( row._detailsShow ) {
			row._details.insertAfter( row.nTr );
		}
	};
	
	
	var __details_remove = function ( api, idx )
	{
		var ctx = api.context;
	
		if ( ctx.length ) {
			var row = ctx[0].aoData[ idx !== undefined ? idx : api[0] ];
	
			if ( row && row._details ) {
				row._details.remove();
	
				row._detailsShow = undefined;
				row._details = undefined;
			}
		}
	};
	
	
	var __details_display = function ( api, show ) {
		var ctx = api.context;
	
		if ( ctx.length && api.length ) {
			var row = ctx[0].aoData[ api[0] ];
	
			if ( row._details ) {
				row._detailsShow = show;
	
				if ( show ) {
					row._details.insertAfter( row.nTr );
				}
				else {
					row._details.detach();
				}
	
				__details_events( ctx[0] );
			}
		}
	};
	
	
	var __details_events = function ( settings )
	{
		var api = new _Api( settings );
		var namespace = '.dt.DT_details';
		var drawEvent = 'draw'+namespace;
		var colvisEvent = 'column-visibility'+namespace;
		var destroyEvent = 'destroy'+namespace;
		var data = settings.aoData;
	
		api.off( drawEvent +' '+ colvisEvent +' '+ destroyEvent );
	
		if ( _pluck( data, '_details' ).length > 0 ) {
			// On each draw, insert the required elements into the document
			api.on( drawEvent, function ( e, ctx ) {
				if ( settings !== ctx ) {
					return;
				}
	
				api.rows( {page:'current'} ).eq(0).each( function (idx) {
					// Internal data grab
					var row = data[ idx ];
	
					if ( row._detailsShow ) {
						row._details.insertAfter( row.nTr );
					}
				} );
			} );
	
			// Column visibility change - update the colspan
			api.on( colvisEvent, function ( e, ctx, idx, vis ) {
				if ( settings !== ctx ) {
					return;
				}
	
				// Update the colspan for the details rows (note, only if it already has
				// a colspan)
				var row, visible = _fnVisbleColumns( ctx );
	
				for ( var i=0, ien=data.length ; i<ien ; i++ ) {
					row = data[i];
	
					if ( row._details ) {
						row._details.children('td[colspan]').attr('colspan', visible );
					}
				}
			} );
	
			// Table destroyed - nuke any child rows
			api.on( destroyEvent, function ( e, ctx ) {
				if ( settings !== ctx ) {
					return;
				}
	
				for ( var i=0, ien=data.length ; i<ien ; i++ ) {
					if ( data[i]._details ) {
						__details_remove( api, i );
					}
				}
			} );
		}
	};
	
	// Strings for the method names to help minification
	var _emp = '';
	var _child_obj = _emp+'row().child';
	var _child_mth = _child_obj+'()';
	
	// data can be:
	//  tr
	//  string
	//  jQuery or array of any of the above
	_api_register( _child_mth, function ( data, klass ) {
		var ctx = this.context;
	
		if ( data === undefined ) {
			// get
			return ctx.length && this.length ?
				ctx[0].aoData[ this[0] ]._details :
				undefined;
		}
		else if ( data === true ) {
			// show
			this.child.show();
		}
		else if ( data === false ) {
			// remove
			__details_remove( this );
		}
		else if ( ctx.length && this.length ) {
			// set
			__details_add( ctx[0], ctx[0].aoData[ this[0] ], data, klass );
		}
	
		return this;
	} );
	
	
	_api_register( [
		_child_obj+'.show()',
		_child_mth+'.show()' // only when `child()` was called with parameters (without
	], function ( show ) {   // it returns an object and this method is not executed)
		__details_display( this, true );
		return this;
	} );
	
	
	_api_register( [
		_child_obj+'.hide()',
		_child_mth+'.hide()' // only when `child()` was called with parameters (without
	], function () {         // it returns an object and this method is not executed)
		__details_display( this, false );
		return this;
	} );
	
	
	_api_register( [
		_child_obj+'.remove()',
		_child_mth+'.remove()' // only when `child()` was called with parameters (without
	], function () {           // it returns an object and this method is not executed)
		__details_remove( this );
		return this;
	} );
	
	
	_api_register( _child_obj+'.isShown()', function () {
		var ctx = this.context;
	
		if ( ctx.length && this.length ) {
			// _detailsShown as false or undefined will fall through to return false
			return ctx[0].aoData[ this[0] ]._detailsShow || false;
		}
		return false;
	} );
	
	
	
	/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
	 * Columns
	 *
	 * {integer}           - column index (>=0 count from left, <0 count from right)
	 * "{integer}:visIdx"  - visible column index (i.e. translate to column index)  (>=0 count from left, <0 count from right)
	 * "{integer}:visible" - alias for {integer}:visIdx  (>=0 count from left, <0 count from right)
	 * "{string}:name"     - column name
	 * "{string}"          - jQuery selector on column header nodes
	 *
	 */
	
	// can be an array of these items, comma separated list, or an array of comma
	// separated lists
	
	var __re_column_selector = /^(.+):(name|visIdx|visible)$/;
	
	
	// r1 and r2 are redundant - but it means that the parameters match for the
	// iterator callback in columns().data()
	var __columnData = function ( settings, column, r1, r2, rows ) {
		var a = [];
		for ( var row=0, ien=rows.length ; row<ien ; row++ ) {
			a.push( _fnGetCellData( settings, rows[row], column ) );
		}
		return a;
	};
	
	
	var __column_selector = function ( settings, selector, opts )
	{
		var
			columns = settings.aoColumns,
			names = _pluck( columns, 'sName' ),
			nodes = _pluck( columns, 'nTh' );
	
		var run = function ( s ) {
			var selInt = _intVal( s );
	
			// Selector - all
			if ( s === '' ) {
				return _range( columns.length );
			}
			
			// Selector - index
			if ( selInt !== null ) {
				return [ selInt >= 0 ?
					selInt : // Count from left
					columns.length + selInt // Count from right (+ because its a negative value)
				];
			}
			
			// Selector = function
			if ( typeof s === 'function' ) {
				var rows = _selector_row_indexes( settings, opts );
	
				return $.map( columns, function (col, idx) {
					return s(
							idx,
							__columnData( settings, idx, 0, 0, rows ),
							nodes[ idx ]
						) ? idx : null;
				} );
			}
	
			// jQuery or string selector
			var match = typeof s === 'string' ?
				s.match( __re_column_selector ) :
				'';
	
			if ( match ) {
				switch( match[2] ) {
					case 'visIdx':
					case 'visible':
						var idx = parseInt( match[1], 10 );
						// Visible index given, convert to column index
						if ( idx < 0 ) {
							// Counting from the right
							var visColumns = $.map( columns, function (col,i) {
								return col.bVisible ? i : null;
							} );
							return [ visColumns[ visColumns.length + idx ] ];
						}
						// Counting from the left
						return [ _fnVisibleToColumnIndex( settings, idx ) ];
	
					case 'name':
						// match by name. `names` is column index complete and in order
						return $.map( names, function (name, i) {
							return name === match[1] ? i : null;
						} );
				}
			}
			else {
				// jQuery selector on the TH elements for the columns
				return $( nodes )
					.filter( s )
					.map( function () {
						return $.inArray( this, nodes ); // `nodes` is column index complete and in order
					} )
					.toArray();
			}
		};
	
		return _selector_run( 'column', selector, run, settings, opts );
	};
	
	
	var __setColumnVis = function ( settings, column, vis, recalc ) {
		var
			cols = settings.aoColumns,
			col  = cols[ column ],
			data = settings.aoData,
			row, cells, i, ien, tr;
	
		// Get
		if ( vis === undefined ) {
			return col.bVisible;
		}
	
		// Set
		// No change
		if ( col.bVisible === vis ) {
			return;
		}
	
		if ( vis ) {
			// Insert column
			// Need to decide if we should use appendChild or insertBefore
			var insertBefore = $.inArray( true, _pluck(cols, 'bVisible'), column+1 );
	
			for ( i=0, ien=data.length ; i<ien ; i++ ) {
				tr = data[i].nTr;
				cells = data[i].anCells;
	
				if ( tr ) {
					// insertBefore can act like appendChild if 2nd arg is null
					tr.insertBefore( cells[ column ], cells[ insertBefore ] || null );
				}
			}
		}
		else {
			// Remove column
			$( _pluck( settings.aoData, 'anCells', column ) ).detach();
		}
	
		// Common actions
		col.bVisible = vis;
		_fnDrawHead( settings, settings.aoHeader );
		_fnDrawHead( settings, settings.aoFooter );
	
		if ( recalc === undefined || recalc ) {
			// Automatically adjust column sizing
			_fnAdjustColumnSizing( settings );
	
			// Realign columns for scrolling
			if ( settings.oScroll.sX || settings.oScroll.sY ) {
				_fnScrollDraw( settings );
			}
		}
	
		_fnCallbackFire( settings, null, 'column-visibility', [settings, column, vis, recalc] );
	
		_fnSaveState( settings );
	};
	
	
	_api_register( 'columns()', function ( selector, opts ) {
		// argument shifting
		if ( selector === undefined ) {
			selector = '';
		}
		else if ( $.isPlainObject( selector ) ) {
			opts = selector;
			selector = '';
		}
	
		opts = _selector_opts( opts );
	
		var inst = this.iterator( 'table', function ( settings ) {
			return __column_selector( settings, selector, opts );
		}, 1 );
	
		// Want argument shifting here and in _row_selector?
		inst.selector.cols = selector;
		inst.selector.opts = opts;
	
		return inst;
	} );
	
	_api_registerPlural( 'columns().header()', 'column().header()', function ( selector, opts ) {
		return this.iterator( 'column', function ( settings, column ) {
			return settings.aoColumns[column].nTh;
		}, 1 );
	} );
	
	_api_registerPlural( 'columns().footer()', 'column().footer()', function ( selector, opts ) {
		return this.iterator( 'column', function ( settings, column ) {
			return settings.aoColumns[column].nTf;
		}, 1 );
	} );
	
	_api_registerPlural( 'columns().data()', 'column().data()', function () {
		return this.iterator( 'column-rows', __columnData, 1 );
	} );
	
	_api_registerPlural( 'columns().dataSrc()', 'column().dataSrc()', function () {
		return this.iterator( 'column', function ( settings, column ) {
			return settings.aoColumns[column].mData;
		}, 1 );
	} );
	
	_api_registerPlural( 'columns().cache()', 'column().cache()', function ( type ) {
		return this.iterator( 'column-rows', function ( settings, column, i, j, rows ) {
			return _pluck_order( settings.aoData, rows,
				type === 'search' ? '_aFilterData' : '_aSortData', column
			);
		}, 1 );
	} );
	
	_api_registerPlural( 'columns().nodes()', 'column().nodes()', function () {
		return this.iterator( 'column-rows', function ( settings, column, i, j, rows ) {
			return _pluck_order( settings.aoData, rows, 'anCells', column ) ;
		}, 1 );
	} );
	
	_api_registerPlural( 'columns().visible()', 'column().visible()', function ( vis, calc ) {
		return this.iterator( 'column', function ( settings, column ) {
			if ( vis === undefined ) {
				return settings.aoColumns[ column ].bVisible;
			} // else
			__setColumnVis( settings, column, vis, calc );
		} );
	} );
	
	_api_registerPlural( 'columns().indexes()', 'column().index()', function ( type ) {
		return this.iterator( 'column', function ( settings, column ) {
			return type === 'visible' ?
				_fnColumnIndexToVisible( settings, column ) :
				column;
		}, 1 );
	} );
	
	_api_register( 'columns.adjust()', function () {
		return this.iterator( 'table', function ( settings ) {
			_fnAdjustColumnSizing( settings );
		}, 1 );
	} );
	
	_api_register( 'column.index()', function ( type, idx ) {
		if ( this.context.length !== 0 ) {
			var ctx = this.context[0];
	
			if ( type === 'fromVisible' || type === 'toData' ) {
				return _fnVisibleToColumnIndex( ctx, idx );
			}
			else if ( type === 'fromData' || type === 'toVisible' ) {
				return _fnColumnIndexToVisible( ctx, idx );
			}
		}
	} );
	
	_api_register( 'column()', function ( selector, opts ) {
		return _selector_first( this.columns( selector, opts ) );
	} );
	
	
	
	
	var __cell_selector = function ( settings, selector, opts )
	{
		var data = settings.aoData;
		var rows = _selector_row_indexes( settings, opts );
		var cells = _removeEmpty( _pluck_order( data, rows, 'anCells' ) );
		var allCells = $( [].concat.apply([], cells) );
		var row;
		var columns = settings.aoColumns.length;
		var a, i, ien, j, o, host;
	
		var run = function ( s ) {
			var fnSelector = typeof s === 'function';
	
			if ( s === null || s === undefined || fnSelector ) {
				// All cells and function selectors
				a = [];
	
				for ( i=0, ien=rows.length ; i<ien ; i++ ) {
					row = rows[i];
	
					for ( j=0 ; j<columns ; j++ ) {
						o = {
							row: row,
							column: j
						};
	
						if ( fnSelector ) {
							// Selector - function
							host = data[ row ];
	
							if ( s( o, _fnGetCellData(settings, row, j), host.anCells ? host.anCells[j] : null ) ) {
								a.push( o );
							}
						}
						else {
							// Selector - all
							a.push( o );
						}
					}
				}
	
				return a;
			}
			
			// Selector - index
			if ( $.isPlainObject( s ) ) {
				return [s];
			}
	
			// Selector - jQuery filtered cells
			return allCells
				.filter( s )
				.map( function (i, el) {
					return { // use a new object, in case someone changes the values
						row:    el._DT_CellIndex.row,
						column: el._DT_CellIndex.column
	 				};
				} )
				.toArray();
		};
	
		return _selector_run( 'cell', selector, run, settings, opts );
	};
	
	
	
	
	_api_register( 'cells()', function ( rowSelector, columnSelector, opts ) {
		// Argument shifting
		if ( $.isPlainObject( rowSelector ) ) {
			// Indexes
			if ( rowSelector.row === undefined ) {
				// Selector options in first parameter
				opts = rowSelector;
				rowSelector = null;
			}
			else {
				// Cell index objects in first parameter
				opts = columnSelector;
				columnSelector = null;
			}
		}
		if ( $.isPlainObject( columnSelector ) ) {
			opts = columnSelector;
			columnSelector = null;
		}
	
		// Cell selector
		if ( columnSelector === null || columnSelector === undefined ) {
			return this.iterator( 'table', function ( settings ) {
				return __cell_selector( settings, rowSelector, _selector_opts( opts ) );
			} );
		}
	
		// Row + column selector
		var columns = this.columns( columnSelector, opts );
		var rows = this.rows( rowSelector, opts );
		var a, i, ien, j, jen;
	
		var cells = this.iterator( 'table', function ( settings, idx ) {
			a = [];
	
			for ( i=0, ien=rows[idx].length ; i<ien ; i++ ) {
				for ( j=0, jen=columns[idx].length ; j<jen ; j++ ) {
					a.push( {
						row:    rows[idx][i],
						column: columns[idx][j]
					} );
				}
			}
	
			return a;
		}, 1 );
	
		$.extend( cells.selector, {
			cols: columnSelector,
			rows: rowSelector,
			opts: opts
		} );
	
		return cells;
	} );
	
	
	_api_registerPlural( 'cells().nodes()', 'cell().node()', function () {
		return this.iterator( 'cell', function ( settings, row, column ) {
			var cells = settings.aoData[ row ].anCells;
			return cells ?
				cells[ column ] :
				undefined;
		}, 1 );
	} );
	
	
	_api_register( 'cells().data()', function () {
		return this.iterator( 'cell', function ( settings, row, column ) {
			return _fnGetCellData( settings, row, column );
		}, 1 );
	} );
	
	
	_api_registerPlural( 'cells().cache()', 'cell().cache()', function ( type ) {
		type = type === 'search' ? '_aFilterData' : '_aSortData';
	
		return this.iterator( 'cell', function ( settings, row, column ) {
			return settings.aoData[ row ][ type ][ column ];
		}, 1 );
	} );
	
	
	_api_registerPlural( 'cells().render()', 'cell().render()', function ( type ) {
		return this.iterator( 'cell', function ( settings, row, column ) {
			return _fnGetCellData( settings, row, column, type );
		}, 1 );
	} );
	
	
	_api_registerPlural( 'cells().indexes()', 'cell().index()', function () {
		return this.iterator( 'cell', function ( settings, row, column ) {
			return {
				row: row,
				column: column,
				columnVisible: _fnColumnIndexToVisible( settings, column )
			};
		}, 1 );
	} );
	
	
	_api_registerPlural( 'cells().invalidate()', 'cell().invalidate()', function ( src ) {
		return this.iterator( 'cell', function ( settings, row, column ) {
			_fnInvalidate( settings, row, src, column );
		} );
	} );
	
	
	
	_api_register( 'cell()', function ( rowSelector, columnSelector, opts ) {
		return _selector_first( this.cells( rowSelector, columnSelector, opts ) );
	} );
	
	
	_api_register( 'cell().data()', function ( data ) {
		var ctx = this.context;
		var cell = this[0];
	
		if ( data === undefined ) {
			// Get
			return ctx.length && cell.length ?
				_fnGetCellData( ctx[0], cell[0].row, cell[0].column ) :
				undefined;
		}
	
		// Set
		_fnSetCellData( ctx[0], cell[0].row, cell[0].column, data );
		_fnInvalidate( ctx[0], cell[0].row, 'data', cell[0].column );
	
		return this;
	} );
	
	
	
	/**
	 * Get current ordering (sorting) that has been applied to the table.
	 *
	 * @returns {array} 2D array containing the sorting information for the first
	 *   table in the current context. Each element in the parent array represents
	 *   a column being sorted upon (i.e. multi-sorting with two columns would have
	 *   2 inner arrays). The inner arrays may have 2 or 3 elements. The first is
	 *   the column index that the sorting condition applies to, the second is the
	 *   direction of the sort (`desc` or `asc`) and, optionally, the third is the
	 *   index of the sorting order from the `column.sorting` initialisation array.
	 *//**
	 * Set the ordering for the table.
	 *
	 * @param {integer} order Column index to sort upon.
	 * @param {string} direction Direction of the sort to be applied (`asc` or `desc`)
	 * @returns {DataTables.Api} this
	 *//**
	 * Set the ordering for the table.
	 *
	 * @param {array} order 1D array of sorting information to be applied.
	 * @param {array} [...] Optional additional sorting conditions
	 * @returns {DataTables.Api} this
	 *//**
	 * Set the ordering for the table.
	 *
	 * @param {array} order 2D array of sorting information to be applied.
	 * @returns {DataTables.Api} this
	 */
	_api_register( 'order()', function ( order, dir ) {
		var ctx = this.context;
	
		if ( order === undefined ) {
			// get
			return ctx.length !== 0 ?
				ctx[0].aaSorting :
				undefined;
		}
	
		// set
		if ( typeof order === 'number' ) {
			// Simple column / direction passed in
			order = [ [ order, dir ] ];
		}
		else if ( ! $.isArray( order[0] ) ) {
			// Arguments passed in (list of 1D arrays)
			order = Array.prototype.slice.call( arguments );
		}
		// otherwise a 2D array was passed in
	
		return this.iterator( 'table', function ( settings ) {
			settings.aaSorting = order.slice();
		} );
	} );
	
	
	/**
	 * Attach a sort listener to an element for a given column
	 *
	 * @param {node|jQuery|string} node Identifier for the element(s) to attach the
	 *   listener to. This can take the form of a single DOM node, a jQuery
	 *   collection of nodes or a jQuery selector which will identify the node(s).
	 * @param {integer} column the column that a click on this node will sort on
	 * @param {function} [callback] callback function when sort is run
	 * @returns {DataTables.Api} this
	 */
	_api_register( 'order.listener()', function ( node, column, callback ) {
		return this.iterator( 'table', function ( settings ) {
			_fnSortAttachListener( settings, node, column, callback );
		} );
	} );
	
	
	_api_register( 'order.fixed()', function ( set ) {
		if ( ! set ) {
			var ctx = this.context;
			var fixed = ctx.length ?
				ctx[0].aaSortingFixed :
				undefined;
	
			return $.isArray( fixed ) ?
				{ pre: fixed } :
				fixed;
		}
	
		return this.iterator( 'table', function ( settings ) {
			settings.aaSortingFixed = $.extend( true, {}, set );
		} );
	} );
	
	
	// Order by the selected column(s)
	_api_register( [
		'columns().order()',
		'column().order()'
	], function ( dir ) {
		var that = this;
	
		return this.iterator( 'table', function ( settings, i ) {
			var sort = [];
	
			$.each( that[i], function (j, col) {
				sort.push( [ col, dir ] );
			} );
	
			settings.aaSorting = sort;
		} );
	} );
	
	
	
	_api_register( 'search()', function ( input, regex, smart, caseInsen ) {
		var ctx = this.context;
	
		if ( input === undefined ) {
			// get
			return ctx.length !== 0 ?
				ctx[0].oPreviousSearch.sSearch :
				undefined;
		}
	
		// set
		return this.iterator( 'table', function ( settings ) {
			if ( ! settings.oFeatures.bFilter ) {
				return;
			}
	
			_fnFilterComplete( settings, $.extend( {}, settings.oPreviousSearch, {
				"sSearch": input+"",
				"bRegex":  regex === null ? false : regex,
				"bSmart":  smart === null ? true  : smart,
				"bCaseInsensitive": caseInsen === null ? true : caseInsen
			} ), 1 );
		} );
	} );
	
	
	_api_registerPlural(
		'columns().search()',
		'column().search()',
		function ( input, regex, smart, caseInsen ) {
			return this.iterator( 'column', function ( settings, column ) {
				var preSearch = settings.aoPreSearchCols;
	
				if ( input === undefined ) {
					// get
					return preSearch[ column ].sSearch;
				}
	
				// set
				if ( ! settings.oFeatures.bFilter ) {
					return;
				}
	
				$.extend( preSearch[ column ], {
					"sSearch": input+"",
					"bRegex":  regex === null ? false : regex,
					"bSmart":  smart === null ? true  : smart,
					"bCaseInsensitive": caseInsen === null ? true : caseInsen
				} );
	
				_fnFilterComplete( settings, settings.oPreviousSearch, 1 );
			} );
		}
	);
	
	/*
	 * State API methods
	 */
	
	_api_register( 'state()', function () {
		return this.context.length ?
			this.context[0].oSavedState :
			null;
	} );
	
	
	_api_register( 'state.clear()', function () {
		return this.iterator( 'table', function ( settings ) {
			// Save an empty object
			settings.fnStateSaveCallback.call( settings.oInstance, settings, {} );
		} );
	} );
	
	
	_api_register( 'state.loaded()', function () {
		return this.context.length ?
			this.context[0].oLoadedState :
			null;
	} );
	
	
	_api_register( 'state.save()', function () {
		return this.iterator( 'table', function ( settings ) {
			_fnSaveState( settings );
		} );
	} );
	
	
	
	/**
	 * Provide a common method for plug-ins to check the version of DataTables being
	 * used, in order to ensure compatibility.
	 *
	 *  @param {string} version Version string to check for, in the format "X.Y.Z".
	 *    Note that the formats "X" and "X.Y" are also acceptable.
	 *  @returns {boolean} true if this version of DataTables is greater or equal to
	 *    the required version, or false if this version of DataTales is not
	 *    suitable
	 *  @static
	 *  @dtopt API-Static
	 *
	 *  @example
	 *    alert( $.fn.dataTable.versionCheck( '1.9.0' ) );
	 */
	DataTable.versionCheck = DataTable.fnVersionCheck = function( version )
	{
		var aThis = DataTable.version.split('.');
		var aThat = version.split('.');
		var iThis, iThat;
	
		for ( var i=0, iLen=aThat.length ; i<iLen ; i++ ) {
			iThis = parseInt( aThis[i], 10 ) || 0;
			iThat = parseInt( aThat[i], 10 ) || 0;
	
			// Parts are the same, keep comparing
			if (iThis === iThat) {
				continue;
			}
	
			// Parts are different, return immediately
			return iThis > iThat;
		}
	
		return true;
	};
	
	
	/**
	 * Check if a `<table>` node is a DataTable table already or not.
	 *
	 *  @param {node|jquery|string} table Table node, jQuery object or jQuery
	 *      selector for the table to test. Note that if more than more than one
	 *      table is passed on, only the first will be checked
	 *  @returns {boolean} true the table given is a DataTable, or false otherwise
	 *  @static
	 *  @dtopt API-Static
	 *
	 *  @example
	 *    if ( ! $.fn.DataTable.isDataTable( '#example' ) ) {
	 *      $('#example').dataTable();
	 *    }
	 */
	DataTable.isDataTable = DataTable.fnIsDataTable = function ( table )
	{
		var t = $(table).get(0);
		var is = false;
	
		$.each( DataTable.settings, function (i, o) {
			var head = o.nScrollHead ? $('table', o.nScrollHead)[0] : null;
			var foot = o.nScrollFoot ? $('table', o.nScrollFoot)[0] : null;
	
			if ( o.nTable === t || head === t || foot === t ) {
				is = true;
			}
		} );
	
		return is;
	};
	
	
	/**
	 * Get all DataTable tables that have been initialised - optionally you can
	 * select to get only currently visible tables.
	 *
	 *  @param {boolean} [visible=false] Flag to indicate if you want all (default)
	 *    or visible tables only.
	 *  @returns {array} Array of `table` nodes (not DataTable instances) which are
	 *    DataTables
	 *  @static
	 *  @dtopt API-Static
	 *
	 *  @example
	 *    $.each( $.fn.dataTable.tables(true), function () {
	 *      $(table).DataTable().columns.adjust();
	 *    } );
	 */
	DataTable.tables = DataTable.fnTables = function ( visible )
	{
		var api = false;
	
		if ( $.isPlainObject( visible ) ) {
			api = visible.api;
			visible = visible.visible;
		}
	
		var a = $.map( DataTable.settings, function (o) {
			if ( !visible || (visible && $(o.nTable).is(':visible')) ) {
				return o.nTable;
			}
		} );
	
		return api ?
			new _Api( a ) :
			a;
	};
	
	
	/**
	 * DataTables utility methods
	 * 
	 * This namespace provides helper methods that DataTables uses internally to
	 * create a DataTable, but which are not exclusively used only for DataTables.
	 * These methods can be used by extension authors to save the duplication of
	 * code.
	 *
	 *  @namespace
	 */
	DataTable.util = {
		/**
		 * Throttle the calls to a function. Arguments and context are maintained
		 * for the throttled function.
		 *
		 * @param {function} fn Function to be called
		 * @param {integer} freq Call frequency in mS
		 * @return {function} Wrapped function
		 */
		throttle: _fnThrottle,
	
	
		/**
		 * Escape a string such that it can be used in a regular expression
		 *
		 *  @param {string} sVal string to escape
		 *  @returns {string} escaped string
		 */
		escapeRegex: _fnEscapeRegex
	};
	
	
	/**
	 * Convert from camel case parameters to Hungarian notation. This is made public
	 * for the extensions to provide the same ability as DataTables core to accept
	 * either the 1.9 style Hungarian notation, or the 1.10+ style camelCase
	 * parameters.
	 *
	 *  @param {object} src The model object which holds all parameters that can be
	 *    mapped.
	 *  @param {object} user The object to convert from camel case to Hungarian.
	 *  @param {boolean} force When set to `true`, properties which already have a
	 *    Hungarian value in the `user` object will be overwritten. Otherwise they
	 *    won't be.
	 */
	DataTable.camelToHungarian = _fnCamelToHungarian;
	
	
	
	/**
	 *
	 */
	_api_register( '$()', function ( selector, opts ) {
		var
			rows   = this.rows( opts ).nodes(), // Get all rows
			jqRows = $(rows);
	
		return $( [].concat(
			jqRows.filter( selector ).toArray(),
			jqRows.find( selector ).toArray()
		) );
	} );
	
	
	// jQuery functions to operate on the tables
	$.each( [ 'on', 'one', 'off' ], function (i, key) {
		_api_register( key+'()', function ( /* event, handler */ ) {
			var args = Array.prototype.slice.call(arguments);
	
			// Add the `dt` namespace automatically if it isn't already present
			if ( ! args[0].match(/\.dt\b/) ) {
				args[0] += '.dt';
			}
	
			var inst = $( this.tables().nodes() );
			inst[key].apply( inst, args );
			return this;
		} );
	} );
	
	
	_api_register( 'clear()', function () {
		return this.iterator( 'table', function ( settings ) {
			_fnClearTable( settings );
		} );
	} );
	
	
	_api_register( 'settings()', function () {
		return new _Api( this.context, this.context );
	} );
	
	
	_api_register( 'init()', function () {
		var ctx = this.context;
		return ctx.length ? ctx[0].oInit : null;
	} );
	
	
	_api_register( 'data()', function () {
		return this.iterator( 'table', function ( settings ) {
			return _pluck( settings.aoData, '_aData' );
		} ).flatten();
	} );
	
	
	_api_register( 'destroy()', function ( remove ) {
		remove = remove || false;
	
		return this.iterator( 'table', function ( settings ) {
			var orig      = settings.nTableWrapper.parentNode;
			var classes   = settings.oClasses;
			var table     = settings.nTable;
			var tbody     = settings.nTBody;
			var thead     = settings.nTHead;
			var tfoot     = settings.nTFoot;
			var jqTable   = $(table);
			var jqTbody   = $(tbody);
			var jqWrapper = $(settings.nTableWrapper);
			var rows      = $.map( settings.aoData, function (r) { return r.nTr; } );
			var i, ien;
	
			// Flag to note that the table is currently being destroyed - no action
			// should be taken
			settings.bDestroying = true;
	
			// Fire off the destroy callbacks for plug-ins etc
			_fnCallbackFire( settings, "aoDestroyCallback", "destroy", [settings] );
	
			// If not being removed from the document, make all columns visible
			if ( ! remove ) {
				new _Api( settings ).columns().visible( true );
			}
	
			// Blitz all `DT` namespaced events (these are internal events, the
			// lowercase, `dt` events are user subscribed and they are responsible
			// for removing them
			jqWrapper.unbind('.DT').find(':not(tbody *)').unbind('.DT');
			$(window).unbind('.DT-'+settings.sInstance);
	
			// When scrolling we had to break the table up - restore it
			if ( table != thead.parentNode ) {
				jqTable.children('thead').detach();
				jqTable.append( thead );
			}
	
			if ( tfoot && table != tfoot.parentNode ) {
				jqTable.children('tfoot').detach();
				jqTable.append( tfoot );
			}
	
			settings.aaSorting = [];
			settings.aaSortingFixed = [];
			_fnSortingClasses( settings );
	
			$( rows ).removeClass( settings.asStripeClasses.join(' ') );
	
			$('th, td', thead).removeClass( classes.sSortable+' '+
				classes.sSortableAsc+' '+classes.sSortableDesc+' '+classes.sSortableNone
			);
	
			if ( settings.bJUI ) {
				$('th span.'+classes.sSortIcon+ ', td span.'+classes.sSortIcon, thead).detach();
				$('th, td', thead).each( function () {
					var wrapper = $('div.'+classes.sSortJUIWrapper, this);
					$(this).append( wrapper.contents() );
					wrapper.detach();
				} );
			}
	
			// Add the TR elements back into the table in their original order
			jqTbody.children().detach();
			jqTbody.append( rows );
	
			// Remove the DataTables generated nodes, events and classes
			var removedMethod = remove ? 'remove' : 'detach';
			jqTable[ removedMethod ]();
			jqWrapper[ removedMethod ]();
	
			// If we need to reattach the table to the document
			if ( ! remove && orig ) {
				// insertBefore acts like appendChild if !arg[1]
				orig.insertBefore( table, settings.nTableReinsertBefore );
	
				// Restore the width of the original table - was read from the style property,
				// so we can restore directly to that
				jqTable
					.css( 'width', settings.sDestroyWidth )
					.removeClass( classes.sTable );
	
				// If the were originally stripe classes - then we add them back here.
				// Note this is not fool proof (for example if not all rows had stripe
				// classes - but it's a good effort without getting carried away
				ien = settings.asDestroyStripes.length;
	
				if ( ien ) {
					jqTbody.children().each( function (i) {
						$(this).addClass( settings.asDestroyStripes[i % ien] );
					} );
				}
			}
	
			/* Remove the settings object from the settings array */
			var idx = $.inArray( settings, DataTable.settings );
			if ( idx !== -1 ) {
				DataTable.settings.splice( idx, 1 );
			}
		} );
	} );
	
	
	// Add the `every()` method for rows, columns and cells in a compact form
	$.each( [ 'column', 'row', 'cell' ], function ( i, type ) {
		_api_register( type+'s().every()', function ( fn ) {
			var opts = this.selector.opts;
			var api = this;
	
			return this.iterator( type, function ( settings, arg1, arg2, arg3, arg4 ) {
				// Rows and columns:
				//  arg1 - index
				//  arg2 - table counter
				//  arg3 - loop counter
				//  arg4 - undefined
				// Cells:
				//  arg1 - row index
				//  arg2 - column index
				//  arg3 - table counter
				//  arg4 - loop counter
				fn.call(
					api[ type ](
						arg1,
						type==='cell' ? arg2 : opts,
						type==='cell' ? opts : undefined
					),
					arg1, arg2, arg3, arg4
				);
			} );
		} );
	} );
	
	
	// i18n method for extensions to be able to use the language object from the
	// DataTable
	_api_register( 'i18n()', function ( token, def, plural ) {
		var ctx = this.context[0];
		var resolved = _fnGetObjectDataFn( token )( ctx.oLanguage );
	
		if ( resolved === undefined ) {
			resolved = def;
		}
	
		if ( plural !== undefined && $.isPlainObject( resolved ) ) {
			resolved = resolved[ plural ] !== undefined ?
				resolved[ plural ] :
				resolved._;
		}
	
		return resolved.replace( '%d', plural ); // nb: plural might be undefined,
	} );

	/**
	 * Version string for plug-ins to check compatibility. Allowed format is
	 * `a.b.c-d` where: a:int, b:int, c:int, d:string(dev|beta|alpha). `d` is used
	 * only for non-release builds. See http://semver.org/ for more information.
	 *  @member
	 *  @type string
	 *  @default Version number
	 */
	DataTable.version = "1.10.10";

	/**
	 * Private data store, containing all of the settings objects that are
	 * created for the tables on a given page.
	 *
	 * Note that the `DataTable.settings` object is aliased to
	 * `jQuery.fn.dataTableExt` through which it may be accessed and
	 * manipulated, or `jQuery.fn.dataTable.settings`.
	 *  @member
	 *  @type array
	 *  @default []
	 *  @private
	 */
	DataTable.settings = [];

	/**
	 * Object models container, for the various models that DataTables has
	 * available to it. These models define the objects that are used to hold
	 * the active state and configuration of the table.
	 *  @namespace
	 */
	DataTable.models = {};
	
	
	
	/**
	 * Template object for the way in which DataTables holds information about
	 * search information for the global filter and individual column filters.
	 *  @namespace
	 */
	DataTable.models.oSearch = {
		/**
		 * Flag to indicate if the filtering should be case insensitive or not
		 *  @type boolean
		 *  @default true
		 */
		"bCaseInsensitive": true,
	
		/**
		 * Applied search term
		 *  @type string
		 *  @default <i>Empty string</i>
		 */
		"sSearch": "",
	
		/**
		 * Flag to indicate if the search term should be interpreted as a
		 * regular expression (true) or not (false) and therefore and special
		 * regex characters escaped.
		 *  @type boolean
		 *  @default false
		 */
		"bRegex": false,
	
		/**
		 * Flag to indicate if DataTables is to use its smart filtering or not.
		 *  @type boolean
		 *  @default true
		 */
		"bSmart": true
	};
	
	
	
	
	/**
	 * Template object for the way in which DataTables holds information about
	 * each individual row. This is the object format used for the settings
	 * aoData array.
	 *  @namespace
	 */
	DataTable.models.oRow = {
		/**
		 * TR element for the row
		 *  @type node
		 *  @default null
		 */
		"nTr": null,
	
		/**
		 * Array of TD elements for each row. This is null until the row has been
		 * created.
		 *  @type array nodes
		 *  @default []
		 */
		"anCells": null,
	
		/**
		 * Data object from the original data source for the row. This is either
		 * an array if using the traditional form of DataTables, or an object if
		 * using mData options. The exact type will depend on the passed in
		 * data from the data source, or will be an array if using DOM a data
		 * source.
		 *  @type array|object
		 *  @default []
		 */
		"_aData": [],
	
		/**
		 * Sorting data cache - this array is ostensibly the same length as the
		 * number of columns (although each index is generated only as it is
		 * needed), and holds the data that is used for sorting each column in the
		 * row. We do this cache generation at the start of the sort in order that
		 * the formatting of the sort data need be done only once for each cell
		 * per sort. This array should not be read from or written to by anything
		 * other than the master sorting methods.
		 *  @type array
		 *  @default null
		 *  @private
		 */
		"_aSortData": null,
	
		/**
		 * Per cell filtering data cache. As per the sort data cache, used to
		 * increase the performance of the filtering in DataTables
		 *  @type array
		 *  @default null
		 *  @private
		 */
		"_aFilterData": null,
	
		/**
		 * Filtering data cache. This is the same as the cell filtering cache, but
		 * in this case a string rather than an array. This is easily computed with
		 * a join on `_aFilterData`, but is provided as a cache so the join isn't
		 * needed on every search (memory traded for performance)
		 *  @type array
		 *  @default null
		 *  @private
		 */
		"_sFilterRow": null,
	
		/**
		 * Cache of the class name that DataTables has applied to the row, so we
		 * can quickly look at this variable rather than needing to do a DOM check
		 * on className for the nTr property.
		 *  @type string
		 *  @default <i>Empty string</i>
		 *  @private
		 */
		"_sRowStripe": "",
	
		/**
		 * Denote if the original data source was from the DOM, or the data source
		 * object. This is used for invalidating data, so DataTables can
		 * automatically read data from the original source, unless uninstructed
		 * otherwise.
		 *  @type string
		 *  @default null
		 *  @private
		 */
		"src": null,
	
		/**
		 * Index in the aoData array. This saves an indexOf lookup when we have the
		 * object, but want to know the index
		 *  @type integer
		 *  @default -1
		 *  @private
		 */
		"idx": -1
	};
	
	
	/**
	 * Template object for the column information object in DataTables. This object
	 * is held in the settings aoColumns array and contains all the information that
	 * DataTables needs about each individual column.
	 *
	 * Note that this object is related to {@link DataTable.defaults.column}
	 * but this one is the internal data store for DataTables's cache of columns.
	 * It should NOT be manipulated outside of DataTables. Any configuration should
	 * be done through the initialisation options.
	 *  @namespace
	 */
	DataTable.models.oColumn = {
		/**
		 * Column index. This could be worked out on-the-fly with $.inArray, but it
		 * is faster to just hold it as a variable
		 *  @type integer
		 *  @default null
		 */
		"idx": null,
	
		/**
		 * A list of the columns that sorting should occur on when this column
		 * is sorted. That this property is an array allows multi-column sorting
		 * to be defined for a column (for example first name / last name columns
		 * would benefit from this). The values are integers pointing to the
		 * columns to be sorted on (typically it will be a single integer pointing
		 * at itself, but that doesn't need to be the case).
		 *  @type array
		 */
		"aDataSort": null,
	
		/**
		 * Define the sorting directions that are applied to the column, in sequence
		 * as the column is repeatedly sorted upon - i.e. the first value is used
		 * as the sorting direction when the column if first sorted (clicked on).
		 * Sort it again (click again) and it will move on to the next index.
		 * Repeat until loop.
		 *  @type array
		 */
		"asSorting": null,
	
		/**
		 * Flag to indicate if the column is searchable, and thus should be included
		 * in the filtering or not.
		 *  @type boolean
		 */
		"bSearchable": null,
	
		/**
		 * Flag to indicate if the column is sortable or not.
		 *  @type boolean
		 */
		"bSortable": null,
	
		/**
		 * Flag to indicate if the column is currently visible in the table or not
		 *  @type boolean
		 */
		"bVisible": null,
	
		/**
		 * Store for manual type assignment using the `column.type` option. This
		 * is held in store so we can manipulate the column's `sType` property.
		 *  @type string
		 *  @default null
		 *  @private
		 */
		"_sManualType": null,
	
		/**
		 * Flag to indicate if HTML5 data attributes should be used as the data
		 * source for filtering or sorting. True is either are.
		 *  @type boolean
		 *  @default false
		 *  @private
		 */
		"_bAttrSrc": false,
	
		/**
		 * Developer definable function that is called whenever a cell is created (Ajax source,
		 * etc) or processed for input (DOM source). This can be used as a compliment to mRender
		 * allowing you to modify the DOM element (add background colour for example) when the
		 * element is available.
		 *  @type function
		 *  @param {element} nTd The TD node that has been created
		 *  @param {*} sData The Data for the cell
		 *  @param {array|object} oData The data for the whole row
		 *  @param {int} iRow The row index for the aoData data store
		 *  @default null
		 */
		"fnCreatedCell": null,
	
		/**
		 * Function to get data from a cell in a column. You should <b>never</b>
		 * access data directly through _aData internally in DataTables - always use
		 * the method attached to this property. It allows mData to function as
		 * required. This function is automatically assigned by the column
		 * initialisation method
		 *  @type function
		 *  @param {array|object} oData The data array/object for the array
		 *    (i.e. aoData[]._aData)
		 *  @param {string} sSpecific The specific data type you want to get -
		 *    'display', 'type' 'filter' 'sort'
		 *  @returns {*} The data for the cell from the given row's data
		 *  @default null
		 */
		"fnGetData": null,
	
		/**
		 * Function to set data for a cell in the column. You should <b>never</b>
		 * set the data directly to _aData internally in DataTables - always use
		 * this method. It allows mData to function as required. This function
		 * is automatically assigned by the column initialisation method
		 *  @type function
		 *  @param {array|object} oData The data array/object for the array
		 *    (i.e. aoData[]._aData)
		 *  @param {*} sValue Value to set
		 *  @default null
		 */
		"fnSetData": null,
	
		/**
		 * Property to read the value for the cells in the column from the data
		 * source array / object. If null, then the default content is used, if a
		 * function is given then the return from the function is used.
		 *  @type function|int|string|null
		 *  @default null
		 */
		"mData": null,
	
		/**
		 * Partner property to mData which is used (only when defined) to get
		 * the data - i.e. it is basically the same as mData, but without the
		 * 'set' option, and also the data fed to it is the result from mData.
		 * This is the rendering method to match the data method of mData.
		 *  @type function|int|string|null
		 *  @default null
		 */
		"mRender": null,
	
		/**
		 * Unique header TH/TD element for this column - this is what the sorting
		 * listener is attached to (if sorting is enabled.)
		 *  @type node
		 *  @default null
		 */
		"nTh": null,
	
		/**
		 * Unique footer TH/TD element for this column (if there is one). Not used
		 * in DataTables as such, but can be used for plug-ins to reference the
		 * footer for each column.
		 *  @type node
		 *  @default null
		 */
		"nTf": null,
	
		/**
		 * The class to apply to all TD elements in the table's TBODY for the column
		 *  @type string
		 *  @default null
		 */
		"sClass": null,
	
		/**
		 * When DataTables calculates the column widths to assign to each column,
		 * it finds the longest string in each column and then constructs a
		 * temporary table and reads the widths from that. The problem with this
		 * is that "mmm" is much wider then "iiii", but the latter is a longer
		 * string - thus the calculation can go wrong (doing it properly and putting
		 * it into an DOM object and measuring that is horribly(!) slow). Thus as
		 * a "work around" we provide this option. It will append its value to the
		 * text that is found to be the longest string for the column - i.e. padding.
		 *  @type string
		 */
		"sContentPadding": null,
	
		/**
		 * Allows a default value to be given for a column's data, and will be used
		 * whenever a null data source is encountered (this can be because mData
		 * is set to null, or because the data source itself is null).
		 *  @type string
		 *  @default null
		 */
		"sDefaultContent": null,
	
		/**
		 * Name for the column, allowing reference to the column by name as well as
		 * by index (needs a lookup to work by name).
		 *  @type string
		 */
		"sName": null,
	
		/**
		 * Custom sorting data type - defines which of the available plug-ins in
		 * afnSortData the custom sorting will use - if any is defined.
		 *  @type string
		 *  @default std
		 */
		"sSortDataType": 'std',
	
		/**
		 * Class to be applied to the header element when sorting on this column
		 *  @type string
		 *  @default null
		 */
		"sSortingClass": null,
	
		/**
		 * Class to be applied to the header element when sorting on this column -
		 * when jQuery UI theming is used.
		 *  @type string
		 *  @default null
		 */
		"sSortingClassJUI": null,
	
		/**
		 * Title of the column - what is seen in the TH element (nTh).
		 *  @type string
		 */
		"sTitle": null,
	
		/**
		 * Column sorting and filtering type
		 *  @type string
		 *  @default null
		 */
		"sType": null,
	
		/**
		 * Width of the column
		 *  @type string
		 *  @default null
		 */
		"sWidth": null,
	
		/**
		 * Width of the column when it was first "encountered"
		 *  @type string
		 *  @default null
		 */
		"sWidthOrig": null
	};
	
	
	/*
	 * Developer note: The properties of the object below are given in Hungarian
	 * notation, that was used as the interface for DataTables prior to v1.10, however
	 * from v1.10 onwards the primary interface is camel case. In order to avoid
	 * breaking backwards compatibility utterly with this change, the Hungarian
	 * version is still, internally the primary interface, but is is not documented
	 * - hence the @name tags in each doc comment. This allows a Javascript function
	 * to create a map from Hungarian notation to camel case (going the other direction
	 * would require each property to be listed, which would at around 3K to the size
	 * of DataTables, while this method is about a 0.5K hit.
	 *
	 * Ultimately this does pave the way for Hungarian notation to be dropped
	 * completely, but that is a massive amount of work and will break current
	 * installs (therefore is on-hold until v2).
	 */
	
	/**
	 * Initialisation options that can be given to DataTables at initialisation
	 * time.
	 *  @namespace
	 */
	DataTable.defaults = {
		/**
		 * An array of data to use for the table, passed in at initialisation which
		 * will be used in preference to any data which is already in the DOM. This is
		 * particularly useful for constructing tables purely in Javascript, for
		 * example with a custom Ajax call.
		 *  @type array
		 *  @default null
		 *
		 *  @dtopt Option
		 *  @name DataTable.defaults.data
		 *
		 *  @example
		 *    // Using a 2D array data source
		 *    $(document).ready( function () {
		 *      $('#example').dataTable( {
		 *        "data": [
		 *          ['Trident', 'Internet Explorer 4.0', 'Win 95+', 4, 'X'],
		 *          ['Trident', 'Internet Explorer 5.0', 'Win 95+', 5, 'C'],
		 *        ],
		 *        "columns": [
		 *          { "title": "Engine" },
		 *          { "title": "Browser" },
		 *          { "title": "Platform" },
		 *          { "title": "Version" },
		 *          { "title": "Grade" }
		 *        ]
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // Using an array of objects as a data source (`data`)
		 *    $(document).ready( function () {
		 *      $('#example').dataTable( {
		 *        "data": [
		 *          {
		 *            "engine":   "Trident",
		 *            "browser":  "Internet Explorer 4.0",
		 *            "platform": "Win 95+",
		 *            "version":  4,
		 *            "grade":    "X"
		 *          },
		 *          {
		 *            "engine":   "Trident",
		 *            "browser":  "Internet Explorer 5.0",
		 *            "platform": "Win 95+",
		 *            "version":  5,
		 *            "grade":    "C"
		 *          }
		 *        ],
		 *        "columns": [
		 *          { "title": "Engine",   "data": "engine" },
		 *          { "title": "Browser",  "data": "browser" },
		 *          { "title": "Platform", "data": "platform" },
		 *          { "title": "Version",  "data": "version" },
		 *          { "title": "Grade",    "data": "grade" }
		 *        ]
		 *      } );
		 *    } );
		 */
		"aaData": null,
	
	
		/**
		 * If ordering is enabled, then DataTables will perform a first pass sort on
		 * initialisation. You can define which column(s) the sort is performed
		 * upon, and the sorting direction, with this variable. The `sorting` array
		 * should contain an array for each column to be sorted initially containing
		 * the column's index and a direction string ('asc' or 'desc').
		 *  @type array
		 *  @default [[0,'asc']]
		 *
		 *  @dtopt Option
		 *  @name DataTable.defaults.order
		 *
		 *  @example
		 *    // Sort by 3rd column first, and then 4th column
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "order": [[2,'asc'], [3,'desc']]
		 *      } );
		 *    } );
		 *
		 *    // No initial sorting
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "order": []
		 *      } );
		 *    } );
		 */
		"aaSorting": [[0,'asc']],
	
	
		/**
		 * This parameter is basically identical to the `sorting` parameter, but
		 * cannot be overridden by user interaction with the table. What this means
		 * is that you could have a column (visible or hidden) which the sorting
		 * will always be forced on first - any sorting after that (from the user)
		 * will then be performed as required. This can be useful for grouping rows
		 * together.
		 *  @type array
		 *  @default null
		 *
		 *  @dtopt Option
		 *  @name DataTable.defaults.orderFixed
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "orderFixed": [[0,'asc']]
		 *      } );
		 *    } )
		 */
		"aaSortingFixed": [],
	
	
		/**
		 * DataTables can be instructed to load data to display in the table from a
		 * Ajax source. This option defines how that Ajax call is made and where to.
		 *
		 * The `ajax` property has three different modes of operation, depending on
		 * how it is defined. These are:
		 *
		 * * `string` - Set the URL from where the data should be loaded from.
		 * * `object` - Define properties for `jQuery.ajax`.
		 * * `function` - Custom data get function
		 *
		 * `string`
		 * --------
		 *
		 * As a string, the `ajax` property simply defines the URL from which
		 * DataTables will load data.
		 *
		 * `object`
		 * --------
		 *
		 * As an object, the parameters in the object are passed to
		 * [jQuery.ajax](http://api.jquery.com/jQuery.ajax/) allowing fine control
		 * of the Ajax request. DataTables has a number of default parameters which
		 * you can override using this option. Please refer to the jQuery
		 * documentation for a full description of the options available, although
		 * the following parameters provide additional options in DataTables or
		 * require special consideration:
		 *
		 * * `data` - As with jQuery, `data` can be provided as an object, but it
		 *   can also be used as a function to manipulate the data DataTables sends
		 *   to the server. The function takes a single parameter, an object of
		 *   parameters with the values that DataTables has readied for sending. An
		 *   object may be returned which will be merged into the DataTables
		 *   defaults, or you can add the items to the object that was passed in and
		 *   not return anything from the function. This supersedes `fnServerParams`
		 *   from DataTables 1.9-.
		 *
		 * * `dataSrc` - By default DataTables will look for the property `data` (or
		 *   `aaData` for compatibility with DataTables 1.9-) when obtaining data
		 *   from an Ajax source or for server-side processing - this parameter
		 *   allows that property to be changed. You can use Javascript dotted
		 *   object notation to get a data source for multiple levels of nesting, or
		 *   it my be used as a function. As a function it takes a single parameter,
		 *   the JSON returned from the server, which can be manipulated as
		 *   required, with the returned value being that used by DataTables as the
		 *   data source for the table. This supersedes `sAjaxDataProp` from
		 *   DataTables 1.9-.
		 *
		 * * `success` - Should not be overridden it is used internally in
		 *   DataTables. To manipulate / transform the data returned by the server
		 *   use `ajax.dataSrc`, or use `ajax` as a function (see below).
		 *
		 * `function`
		 * ----------
		 *
		 * As a function, making the Ajax call is left up to yourself allowing
		 * complete control of the Ajax request. Indeed, if desired, a method other
		 * than Ajax could be used to obtain the required data, such as Web storage
		 * or an AIR database.
		 *
		 * The function is given four parameters and no return is required. The
		 * parameters are:
		 *
		 * 1. _object_ - Data to send to the server
		 * 2. _function_ - Callback function that must be executed when the required
		 *    data has been obtained. That data should be passed into the callback
		 *    as the only parameter
		 * 3. _object_ - DataTables settings object for the table
		 *
		 * Note that this supersedes `fnServerData` from DataTables 1.9-.
		 *
		 *  @type string|object|function
		 *  @default null
		 *
		 *  @dtopt Option
		 *  @name DataTable.defaults.ajax
		 *  @since 1.10.0
		 *
		 * @example
		 *   // Get JSON data from a file via Ajax.
		 *   // Note DataTables expects data in the form `{ data: [ ...data... ] }` by default).
		 *   $('#example').dataTable( {
		 *     "ajax": "data.json"
		 *   } );
		 *
		 * @example
		 *   // Get JSON data from a file via Ajax, using `dataSrc` to change
		 *   // `data` to `tableData` (i.e. `{ tableData: [ ...data... ] }`)
		 *   $('#example').dataTable( {
		 *     "ajax": {
		 *       "url": "data.json",
		 *       "dataSrc": "tableData"
		 *     }
		 *   } );
		 *
		 * @example
		 *   // Get JSON data from a file via Ajax, using `dataSrc` to read data
		 *   // from a plain array rather than an array in an object
		 *   $('#example').dataTable( {
		 *     "ajax": {
		 *       "url": "data.json",
		 *       "dataSrc": ""
		 *     }
		 *   } );
		 *
		 * @example
		 *   // Manipulate the data returned from the server - add a link to data
		 *   // (note this can, should, be done using `render` for the column - this
		 *   // is just a simple example of how the data can be manipulated).
		 *   $('#example').dataTable( {
		 *     "ajax": {
		 *       "url": "data.json",
		 *       "dataSrc": function ( json ) {
		 *         for ( var i=0, ien=json.length ; i<ien ; i++ ) {
		 *           json[i][0] = '<a href="/message/'+json[i][0]+'>View message</a>';
		 *         }
		 *         return json;
		 *       }
		 *     }
		 *   } );
		 *
		 * @example
		 *   // Add data to the request
		 *   $('#example').dataTable( {
		 *     "ajax": {
		 *       "url": "data.json",
		 *       "data": function ( d ) {
		 *         return {
		 *           "extra_search": $('#extra').val()
		 *         };
		 *       }
		 *     }
		 *   } );
		 *
		 * @example
		 *   // Send request as POST
		 *   $('#example').dataTable( {
		 *     "ajax": {
		 *       "url": "data.json",
		 *       "type": "POST"
		 *     }
		 *   } );
		 *
		 * @example
		 *   // Get the data from localStorage (could interface with a form for
		 *   // adding, editing and removing rows).
		 *   $('#example').dataTable( {
		 *     "ajax": function (data, callback, settings) {
		 *       callback(
		 *         JSON.parse( localStorage.getItem('dataTablesData') )
		 *       );
		 *     }
		 *   } );
		 */
		"ajax": null,
	
	
		/**
		 * This parameter allows you to readily specify the entries in the length drop
		 * down menu that DataTables shows when pagination is enabled. It can be
		 * either a 1D array of options which will be used for both the displayed
		 * option and the value, or a 2D array which will use the array in the first
		 * position as the value, and the array in the second position as the
		 * displayed options (useful for language strings such as 'All').
		 *
		 * Note that the `pageLength` property will be automatically set to the
		 * first value given in this array, unless `pageLength` is also provided.
		 *  @type array
		 *  @default [ 10, 25, 50, 100 ]
		 *
		 *  @dtopt Option
		 *  @name DataTable.defaults.lengthMenu
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "lengthMenu": [[10, 25, 50, -1], [10, 25, 50, "All"]]
		 *      } );
		 *    } );
		 */
		"aLengthMenu": [ 10, 25, 50, 100 ],
	
	
		/**
		 * The `columns` option in the initialisation parameter allows you to define
		 * details about the way individual columns behave. For a full list of
		 * column options that can be set, please see
		 * {@link DataTable.defaults.column}. Note that if you use `columns` to
		 * define your columns, you must have an entry in the array for every single
		 * column that you have in your table (these can be null if you don't which
		 * to specify any options).
		 *  @member
		 *
		 *  @name DataTable.defaults.column
		 */
		"aoColumns": null,
	
		/**
		 * Very similar to `columns`, `columnDefs` allows you to target a specific
		 * column, multiple columns, or all columns, using the `targets` property of
		 * each object in the array. This allows great flexibility when creating
		 * tables, as the `columnDefs` arrays can be of any length, targeting the
		 * columns you specifically want. `columnDefs` may use any of the column
		 * options available: {@link DataTable.defaults.column}, but it _must_
		 * have `targets` defined in each object in the array. Values in the `targets`
		 * array may be:
		 *   <ul>
		 *     <li>a string - class name will be matched on the TH for the column</li>
		 *     <li>0 or a positive integer - column index counting from the left</li>
		 *     <li>a negative integer - column index counting from the right</li>
		 *     <li>the string "_all" - all columns (i.e. assign a default)</li>
		 *   </ul>
		 *  @member
		 *
		 *  @name DataTable.defaults.columnDefs
		 */
		"aoColumnDefs": null,
	
	
		/**
		 * Basically the same as `search`, this parameter defines the individual column
		 * filtering state at initialisation time. The array must be of the same size
		 * as the number of columns, and each element be an object with the parameters
		 * `search` and `escapeRegex` (the latter is optional). 'null' is also
		 * accepted and the default will be used.
		 *  @type array
		 *  @default []
		 *
		 *  @dtopt Option
		 *  @name DataTable.defaults.searchCols
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "searchCols": [
		 *          null,
		 *          { "search": "My filter" },
		 *          null,
		 *          { "search": "^[0-9]", "escapeRegex": false }
		 *        ]
		 *      } );
		 *    } )
		 */
		"aoSearchCols": [],
	
	
		/**
		 * An array of CSS classes that should be applied to displayed rows. This
		 * array may be of any length, and DataTables will apply each class
		 * sequentially, looping when required.
		 *  @type array
		 *  @default null <i>Will take the values determined by the `oClasses.stripe*`
		 *    options</i>
		 *
		 *  @dtopt Option
		 *  @name DataTable.defaults.stripeClasses
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "stripeClasses": [ 'strip1', 'strip2', 'strip3' ]
		 *      } );
		 *    } )
		 */
		"asStripeClasses": null,
	
	
		/**
		 * Enable or disable automatic column width calculation. This can be disabled
		 * as an optimisation (it takes some time to calculate the widths) if the
		 * tables widths are passed in using `columns`.
		 *  @type boolean
		 *  @default true
		 *
		 *  @dtopt Features
		 *  @name DataTable.defaults.autoWidth
		 *
		 *  @example
		 *    $(document).ready( function () {
		 *      $('#example').dataTable( {
		 *        "autoWidth": false
		 *      } );
		 *    } );
		 */
		"bAutoWidth": true,
	
	
		/**
		 * Deferred rendering can provide DataTables with a huge speed boost when you
		 * are using an Ajax or JS data source for the table. This option, when set to
		 * true, will cause DataTables to defer the creation of the table elements for
		 * each row until they are needed for a draw - saving a significant amount of
		 * time.
		 *  @type boolean
		 *  @default false
		 *
		 *  @dtopt Features
		 *  @name DataTable.defaults.deferRender
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "ajax": "sources/arrays.txt",
		 *        "deferRender": true
		 *      } );
		 *    } );
		 */
		"bDeferRender": false,
	
	
		/**
		 * Replace a DataTable which matches the given selector and replace it with
		 * one which has the properties of the new initialisation object passed. If no
		 * table matches the selector, then the new DataTable will be constructed as
		 * per normal.
		 *  @type boolean
		 *  @default false
		 *
		 *  @dtopt Options
		 *  @name DataTable.defaults.destroy
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "srollY": "200px",
		 *        "paginate": false
		 *      } );
		 *
		 *      // Some time later....
		 *      $('#example').dataTable( {
		 *        "filter": false,
		 *        "destroy": true
		 *      } );
		 *    } );
		 */
		"bDestroy": false,
	
	
		/**
		 * Enable or disable filtering of data. Filtering in DataTables is "smart" in
		 * that it allows the end user to input multiple words (space separated) and
		 * will match a row containing those words, even if not in the order that was
		 * specified (this allow matching across multiple columns). Note that if you
		 * wish to use filtering in DataTables this must remain 'true' - to remove the
		 * default filtering input box and retain filtering abilities, please use
		 * {@link DataTable.defaults.dom}.
		 *  @type boolean
		 *  @default true
		 *
		 *  @dtopt Features
		 *  @name DataTable.defaults.searching
		 *
		 *  @example
		 *    $(document).ready( function () {
		 *      $('#example').dataTable( {
		 *        "searching": false
		 *      } );
		 *    } );
		 */
		"bFilter": true,
	
	
		/**
		 * Enable or disable the table information display. This shows information
		 * about the data that is currently visible on the page, including information
		 * about filtered data if that action is being performed.
		 *  @type boolean
		 *  @default true
		 *
		 *  @dtopt Features
		 *  @name DataTable.defaults.info
		 *
		 *  @example
		 *    $(document).ready( function () {
		 *      $('#example').dataTable( {
		 *        "info": false
		 *      } );
		 *    } );
		 */
		"bInfo": true,
	
	
		/**
		 * Enable jQuery UI ThemeRoller support (required as ThemeRoller requires some
		 * slightly different and additional mark-up from what DataTables has
		 * traditionally used).
		 *  @type boolean
		 *  @default false
		 *
		 *  @dtopt Features
		 *  @name DataTable.defaults.jQueryUI
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "jQueryUI": true
		 *      } );
		 *    } );
		 */
		"bJQueryUI": false,
	
	
		/**
		 * Allows the end user to select the size of a formatted page from a select
		 * menu (sizes are 10, 25, 50 and 100). Requires pagination (`paginate`).
		 *  @type boolean
		 *  @default true
		 *
		 *  @dtopt Features
		 *  @name DataTable.defaults.lengthChange
		 *
		 *  @example
		 *    $(document).ready( function () {
		 *      $('#example').dataTable( {
		 *        "lengthChange": false
		 *      } );
		 *    } );
		 */
		"bLengthChange": true,
	
	
		/**
		 * Enable or disable pagination.
		 *  @type boolean
		 *  @default true
		 *
		 *  @dtopt Features
		 *  @name DataTable.defaults.paging
		 *
		 *  @example
		 *    $(document).ready( function () {
		 *      $('#example').dataTable( {
		 *        "paging": false
		 *      } );
		 *    } );
		 */
		"bPaginate": true,
	
	
		/**
		 * Enable or disable the display of a 'processing' indicator when the table is
		 * being processed (e.g. a sort). This is particularly useful for tables with
		 * large amounts of data where it can take a noticeable amount of time to sort
		 * the entries.
		 *  @type boolean
		 *  @default false
		 *
		 *  @dtopt Features
		 *  @name DataTable.defaults.processing
		 *
		 *  @example
		 *    $(document).ready( function () {
		 *      $('#example').dataTable( {
		 *        "processing": true
		 *      } );
		 *    } );
		 */
		"bProcessing": false,
	
	
		/**
		 * Retrieve the DataTables object for the given selector. Note that if the
		 * table has already been initialised, this parameter will cause DataTables
		 * to simply return the object that has already been set up - it will not take
		 * account of any changes you might have made to the initialisation object
		 * passed to DataTables (setting this parameter to true is an acknowledgement
		 * that you understand this). `destroy` can be used to reinitialise a table if
		 * you need.
		 *  @type boolean
		 *  @default false
		 *
		 *  @dtopt Options
		 *  @name DataTable.defaults.retrieve
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      initTable();
		 *      tableActions();
		 *    } );
		 *
		 *    function initTable ()
		 *    {
		 *      return $('#example').dataTable( {
		 *        "scrollY": "200px",
		 *        "paginate": false,
		 *        "retrieve": true
		 *      } );
		 *    }
		 *
		 *    function tableActions ()
		 *    {
		 *      var table = initTable();
		 *      // perform API operations with oTable
		 *    }
		 */
		"bRetrieve": false,
	
	
		/**
		 * When vertical (y) scrolling is enabled, DataTables will force the height of
		 * the table's viewport to the given height at all times (useful for layout).
		 * However, this can look odd when filtering data down to a small data set,
		 * and the footer is left "floating" further down. This parameter (when
		 * enabled) will cause DataTables to collapse the table's viewport down when
		 * the result set will fit within the given Y height.
		 *  @type boolean
		 *  @default false
		 *
		 *  @dtopt Options
		 *  @name DataTable.defaults.scrollCollapse
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "scrollY": "200",
		 *        "scrollCollapse": true
		 *      } );
		 *    } );
		 */
		"bScrollCollapse": false,
	
	
		/**
		 * Configure DataTables to use server-side processing. Note that the
		 * `ajax` parameter must also be given in order to give DataTables a
		 * source to obtain the required data for each draw.
		 *  @type boolean
		 *  @default false
		 *
		 *  @dtopt Features
		 *  @dtopt Server-side
		 *  @name DataTable.defaults.serverSide
		 *
		 *  @example
		 *    $(document).ready( function () {
		 *      $('#example').dataTable( {
		 *        "serverSide": true,
		 *        "ajax": "xhr.php"
		 *      } );
		 *    } );
		 */
		"bServerSide": false,
	
	
		/**
		 * Enable or disable sorting of columns. Sorting of individual columns can be
		 * disabled by the `sortable` option for each column.
		 *  @type boolean
		 *  @default true
		 *
		 *  @dtopt Features
		 *  @name DataTable.defaults.ordering
		 *
		 *  @example
		 *    $(document).ready( function () {
		 *      $('#example').dataTable( {
		 *        "ordering": false
		 *      } );
		 *    } );
		 */
		"bSort": true,
	
	
		/**
		 * Enable or display DataTables' ability to sort multiple columns at the
		 * same time (activated by shift-click by the user).
		 *  @type boolean
		 *  @default true
		 *
		 *  @dtopt Options
		 *  @name DataTable.defaults.orderMulti
		 *
		 *  @example
		 *    // Disable multiple column sorting ability
		 *    $(document).ready( function () {
		 *      $('#example').dataTable( {
		 *        "orderMulti": false
		 *      } );
		 *    } );
		 */
		"bSortMulti": true,
	
	
		/**
		 * Allows control over whether DataTables should use the top (true) unique
		 * cell that is found for a single column, or the bottom (false - default).
		 * This is useful when using complex headers.
		 *  @type boolean
		 *  @default false
		 *
		 *  @dtopt Options
		 *  @name DataTable.defaults.orderCellsTop
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "orderCellsTop": true
		 *      } );
		 *    } );
		 */
		"bSortCellsTop": false,
	
	
		/**
		 * Enable or disable the addition of the classes `sorting\_1`, `sorting\_2` and
		 * `sorting\_3` to the columns which are currently being sorted on. This is
		 * presented as a feature switch as it can increase processing time (while
		 * classes are removed and added) so for large data sets you might want to
		 * turn this off.
		 *  @type boolean
		 *  @default true
		 *
		 *  @dtopt Features
		 *  @name DataTable.defaults.orderClasses
		 *
		 *  @example
		 *    $(document).ready( function () {
		 *      $('#example').dataTable( {
		 *        "orderClasses": false
		 *      } );
		 *    } );
		 */
		"bSortClasses": true,
	
	
		/**
		 * Enable or disable state saving. When enabled HTML5 `localStorage` will be
		 * used to save table display information such as pagination information,
		 * display length, filtering and sorting. As such when the end user reloads
		 * the page the display display will match what thy had previously set up.
		 *
		 * Due to the use of `localStorage` the default state saving is not supported
		 * in IE6 or 7. If state saving is required in those browsers, use
		 * `stateSaveCallback` to provide a storage solution such as cookies.
		 *  @type boolean
		 *  @default false
		 *
		 *  @dtopt Features
		 *  @name DataTable.defaults.stateSave
		 *
		 *  @example
		 *    $(document).ready( function () {
		 *      $('#example').dataTable( {
		 *        "stateSave": true
		 *      } );
		 *    } );
		 */
		"bStateSave": false,
	
	
		/**
		 * This function is called when a TR element is created (and all TD child
		 * elements have been inserted), or registered if using a DOM source, allowing
		 * manipulation of the TR element (adding classes etc).
		 *  @type function
		 *  @param {node} row "TR" element for the current row
		 *  @param {array} data Raw data array for this row
		 *  @param {int} dataIndex The index of this row in the internal aoData array
		 *
		 *  @dtopt Callbacks
		 *  @name DataTable.defaults.createdRow
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "createdRow": function( row, data, dataIndex ) {
		 *          // Bold the grade for all 'A' grade browsers
		 *          if ( data[4] == "A" )
		 *          {
		 *            $('td:eq(4)', row).html( '<b>A</b>' );
		 *          }
		 *        }
		 *      } );
		 *    } );
		 */
		"fnCreatedRow": null,
	
	
		/**
		 * This function is called on every 'draw' event, and allows you to
		 * dynamically modify any aspect you want about the created DOM.
		 *  @type function
		 *  @param {object} settings DataTables settings object
		 *
		 *  @dtopt Callbacks
		 *  @name DataTable.defaults.drawCallback
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "drawCallback": function( settings ) {
		 *          alert( 'DataTables has redrawn the table' );
		 *        }
		 *      } );
		 *    } );
		 */
		"fnDrawCallback": null,
	
	
		/**
		 * Identical to fnHeaderCallback() but for the table footer this function
		 * allows you to modify the table footer on every 'draw' event.
		 *  @type function
		 *  @param {node} foot "TR" element for the footer
		 *  @param {array} data Full table data (as derived from the original HTML)
		 *  @param {int} start Index for the current display starting point in the
		 *    display array
		 *  @param {int} end Index for the current display ending point in the
		 *    display array
		 *  @param {array int} display Index array to translate the visual position
		 *    to the full data array
		 *
		 *  @dtopt Callbacks
		 *  @name DataTable.defaults.footerCallback
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "footerCallback": function( tfoot, data, start, end, display ) {
		 *          tfoot.getElementsByTagName('th')[0].innerHTML = "Starting index is "+start;
		 *        }
		 *      } );
		 *    } )
		 */
		"fnFooterCallback": null,
	
	
		/**
		 * When rendering large numbers in the information element for the table
		 * (i.e. "Showing 1 to 10 of 57 entries") DataTables will render large numbers
		 * to have a comma separator for the 'thousands' units (e.g. 1 million is
		 * rendered as "1,000,000") to help readability for the end user. This
		 * function will override the default method DataTables uses.
		 *  @type function
		 *  @member
		 *  @param {int} toFormat number to be formatted
		 *  @returns {string} formatted string for DataTables to show the number
		 *
		 *  @dtopt Callbacks
		 *  @name DataTable.defaults.formatNumber
		 *
		 *  @example
		 *    // Format a number using a single quote for the separator (note that
		 *    // this can also be done with the language.thousands option)
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "formatNumber": function ( toFormat ) {
		 *          return toFormat.toString().replace(
		 *            /\B(?=(\d{3})+(?!\d))/g, "'"
		 *          );
		 *        };
		 *      } );
		 *    } );
		 */
		"fnFormatNumber": function ( toFormat ) {
			return toFormat.toString().replace(
				/\B(?=(\d{3})+(?!\d))/g,
				this.oLanguage.sThousands
			);
		},
	
	
		/**
		 * This function is called on every 'draw' event, and allows you to
		 * dynamically modify the header row. This can be used to calculate and
		 * display useful information about the table.
		 *  @type function
		 *  @param {node} head "TR" element for the header
		 *  @param {array} data Full table data (as derived from the original HTML)
		 *  @param {int} start Index for the current display starting point in the
		 *    display array
		 *  @param {int} end Index for the current display ending point in the
		 *    display array
		 *  @param {array int} display Index array to translate the visual position
		 *    to the full data array
		 *
		 *  @dtopt Callbacks
		 *  @name DataTable.defaults.headerCallback
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "fheaderCallback": function( head, data, start, end, display ) {
		 *          head.getElementsByTagName('th')[0].innerHTML = "Displaying "+(end-start)+" records";
		 *        }
		 *      } );
		 *    } )
		 */
		"fnHeaderCallback": null,
	
	
		/**
		 * The information element can be used to convey information about the current
		 * state of the table. Although the internationalisation options presented by
		 * DataTables are quite capable of dealing with most customisations, there may
		 * be times where you wish to customise the string further. This callback
		 * allows you to do exactly that.
		 *  @type function
		 *  @param {object} oSettings DataTables settings object
		 *  @param {int} start Starting position in data for the draw
		 *  @param {int} end End position in data for the draw
		 *  @param {int} max Total number of rows in the table (regardless of
		 *    filtering)
		 *  @param {int} total Total number of rows in the data set, after filtering
		 *  @param {string} pre The string that DataTables has formatted using it's
		 *    own rules
		 *  @returns {string} The string to be displayed in the information element.
		 *
		 *  @dtopt Callbacks
		 *  @name DataTable.defaults.infoCallback
		 *
		 *  @example
		 *    $('#example').dataTable( {
		 *      "infoCallback": function( settings, start, end, max, total, pre ) {
		 *        return start +" to "+ end;
		 *      }
		 *    } );
		 */
		"fnInfoCallback": null,
	
	
		/**
		 * Called when the table has been initialised. Normally DataTables will
		 * initialise sequentially and there will be no need for this function,
		 * however, this does not hold true when using external language information
		 * since that is obtained using an async XHR call.
		 *  @type function
		 *  @param {object} settings DataTables settings object
		 *  @param {object} json The JSON object request from the server - only
		 *    present if client-side Ajax sourced data is used
		 *
		 *  @dtopt Callbacks
		 *  @name DataTable.defaults.initComplete
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "initComplete": function(settings, json) {
		 *          alert( 'DataTables has finished its initialisation.' );
		 *        }
		 *      } );
		 *    } )
		 */
		"fnInitComplete": null,
	
	
		/**
		 * Called at the very start of each table draw and can be used to cancel the
		 * draw by returning false, any other return (including undefined) results in
		 * the full draw occurring).
		 *  @type function
		 *  @param {object} settings DataTables settings object
		 *  @returns {boolean} False will cancel the draw, anything else (including no
		 *    return) will allow it to complete.
		 *
		 *  @dtopt Callbacks
		 *  @name DataTable.defaults.preDrawCallback
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "preDrawCallback": function( settings ) {
		 *          if ( $('#test').val() == 1 ) {
		 *            return false;
		 *          }
		 *        }
		 *      } );
		 *    } );
		 */
		"fnPreDrawCallback": null,
	
	
		/**
		 * This function allows you to 'post process' each row after it have been
		 * generated for each table draw, but before it is rendered on screen. This
		 * function might be used for setting the row class name etc.
		 *  @type function
		 *  @param {node} row "TR" element for the current row
		 *  @param {array} data Raw data array for this row
		 *  @param {int} displayIndex The display index for the current table draw
		 *  @param {int} displayIndexFull The index of the data in the full list of
		 *    rows (after filtering)
		 *
		 *  @dtopt Callbacks
		 *  @name DataTable.defaults.rowCallback
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "rowCallback": function( row, data, displayIndex, displayIndexFull ) {
		 *          // Bold the grade for all 'A' grade browsers
		 *          if ( data[4] == "A" ) {
		 *            $('td:eq(4)', row).html( '<b>A</b>' );
		 *          }
		 *        }
		 *      } );
		 *    } );
		 */
		"fnRowCallback": null,
	
	
		/**
		 * __Deprecated__ The functionality provided by this parameter has now been
		 * superseded by that provided through `ajax`, which should be used instead.
		 *
		 * This parameter allows you to override the default function which obtains
		 * the data from the server so something more suitable for your application.
		 * For example you could use POST data, or pull information from a Gears or
		 * AIR database.
		 *  @type function
		 *  @member
		 *  @param {string} source HTTP source to obtain the data from (`ajax`)
		 *  @param {array} data A key/value pair object containing the data to send
		 *    to the server
		 *  @param {function} callback to be called on completion of the data get
		 *    process that will draw the data on the page.
		 *  @param {object} settings DataTables settings object
		 *
		 *  @dtopt Callbacks
		 *  @dtopt Server-side
		 *  @name DataTable.defaults.serverData
		 *
		 *  @deprecated 1.10. Please use `ajax` for this functionality now.
		 */
		"fnServerData": null,
	
	
		/**
		 * __Deprecated__ The functionality provided by this parameter has now been
		 * superseded by that provided through `ajax`, which should be used instead.
		 *
		 *  It is often useful to send extra data to the server when making an Ajax
		 * request - for example custom filtering information, and this callback
		 * function makes it trivial to send extra information to the server. The
		 * passed in parameter is the data set that has been constructed by
		 * DataTables, and you can add to this or modify it as you require.
		 *  @type function
		 *  @param {array} data Data array (array of objects which are name/value
		 *    pairs) that has been constructed by DataTables and will be sent to the
		 *    server. In the case of Ajax sourced data with server-side processing
		 *    this will be an empty array, for server-side processing there will be a
		 *    significant number of parameters!
		 *  @returns {undefined} Ensure that you modify the data array passed in,
		 *    as this is passed by reference.
		 *
		 *  @dtopt Callbacks
		 *  @dtopt Server-side
		 *  @name DataTable.defaults.serverParams
		 *
		 *  @deprecated 1.10. Please use `ajax` for this functionality now.
		 */
		"fnServerParams": null,
	
	
		/**
		 * Load the table state. With this function you can define from where, and how, the
		 * state of a table is loaded. By default DataTables will load from `localStorage`
		 * but you might wish to use a server-side database or cookies.
		 *  @type function
		 *  @member
		 *  @param {object} settings DataTables settings object
		 *  @return {object} The DataTables state object to be loaded
		 *
		 *  @dtopt Callbacks
		 *  @name DataTable.defaults.stateLoadCallback
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "stateSave": true,
		 *        "stateLoadCallback": function (settings) {
		 *          var o;
		 *
		 *          // Send an Ajax request to the server to get the data. Note that
		 *          // this is a synchronous request.
		 *          $.ajax( {
		 *            "url": "/state_load",
		 *            "async": false,
		 *            "dataType": "json",
		 *            "success": function (json) {
		 *              o = json;
		 *            }
		 *          } );
		 *
		 *          return o;
		 *        }
		 *      } );
		 *    } );
		 */
		"fnStateLoadCallback": function ( settings ) {
			try {
				return JSON.parse(
					(settings.iStateDuration === -1 ? sessionStorage : localStorage).getItem(
						'DataTables_'+settings.sInstance+'_'+location.pathname
					)
				);
			} catch (e) {}
		},
	
	
		/**
		 * Callback which allows modification of the saved state prior to loading that state.
		 * This callback is called when the table is loading state from the stored data, but
		 * prior to the settings object being modified by the saved state. Note that for
		 * plug-in authors, you should use the `stateLoadParams` event to load parameters for
		 * a plug-in.
		 *  @type function
		 *  @param {object} settings DataTables settings object
		 *  @param {object} data The state object that is to be loaded
		 *
		 *  @dtopt Callbacks
		 *  @name DataTable.defaults.stateLoadParams
		 *
		 *  @example
		 *    // Remove a saved filter, so filtering is never loaded
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "stateSave": true,
		 *        "stateLoadParams": function (settings, data) {
		 *          data.oSearch.sSearch = "";
		 *        }
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // Disallow state loading by returning false
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "stateSave": true,
		 *        "stateLoadParams": function (settings, data) {
		 *          return false;
		 *        }
		 *      } );
		 *    } );
		 */
		"fnStateLoadParams": null,
	
	
		/**
		 * Callback that is called when the state has been loaded from the state saving method
		 * and the DataTables settings object has been modified as a result of the loaded state.
		 *  @type function
		 *  @param {object} settings DataTables settings object
		 *  @param {object} data The state object that was loaded
		 *
		 *  @dtopt Callbacks
		 *  @name DataTable.defaults.stateLoaded
		 *
		 *  @example
		 *    // Show an alert with the filtering value that was saved
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "stateSave": true,
		 *        "stateLoaded": function (settings, data) {
		 *          alert( 'Saved filter was: '+data.oSearch.sSearch );
		 *        }
		 *      } );
		 *    } );
		 */
		"fnStateLoaded": null,
	
	
		/**
		 * Save the table state. This function allows you to define where and how the state
		 * information for the table is stored By default DataTables will use `localStorage`
		 * but you might wish to use a server-side database or cookies.
		 *  @type function
		 *  @member
		 *  @param {object} settings DataTables settings object
		 *  @param {object} data The state object to be saved
		 *
		 *  @dtopt Callbacks
		 *  @name DataTable.defaults.stateSaveCallback
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "stateSave": true,
		 *        "stateSaveCallback": function (settings, data) {
		 *          // Send an Ajax request to the server with the state object
		 *          $.ajax( {
		 *            "url": "/state_save",
		 *            "data": data,
		 *            "dataType": "json",
		 *            "method": "POST"
		 *            "success": function () {}
		 *          } );
		 *        }
		 *      } );
		 *    } );
		 */
		"fnStateSaveCallback": function ( settings, data ) {
			try {
				(settings.iStateDuration === -1 ? sessionStorage : localStorage).setItem(
					'DataTables_'+settings.sInstance+'_'+location.pathname,
					JSON.stringify( data )
				);
			} catch (e) {}
		},
	
	
		/**
		 * Callback which allows modification of the state to be saved. Called when the table
		 * has changed state a new state save is required. This method allows modification of
		 * the state saving object prior to actually doing the save, including addition or
		 * other state properties or modification. Note that for plug-in authors, you should
		 * use the `stateSaveParams` event to save parameters for a plug-in.
		 *  @type function
		 *  @param {object} settings DataTables settings object
		 *  @param {object} data The state object to be saved
		 *
		 *  @dtopt Callbacks
		 *  @name DataTable.defaults.stateSaveParams
		 *
		 *  @example
		 *    // Remove a saved filter, so filtering is never saved
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "stateSave": true,
		 *        "stateSaveParams": function (settings, data) {
		 *          data.oSearch.sSearch = "";
		 *        }
		 *      } );
		 *    } );
		 */
		"fnStateSaveParams": null,
	
	
		/**
		 * Duration for which the saved state information is considered valid. After this period
		 * has elapsed the state will be returned to the default.
		 * Value is given in seconds.
		 *  @type int
		 *  @default 7200 <i>(2 hours)</i>
		 *
		 *  @dtopt Options
		 *  @name DataTable.defaults.stateDuration
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "stateDuration": 60*60*24; // 1 day
		 *      } );
		 *    } )
		 */
		"iStateDuration": 7200,
	
	
		/**
		 * When enabled DataTables will not make a request to the server for the first
		 * page draw - rather it will use the data already on the page (no sorting etc
		 * will be applied to it), thus saving on an XHR at load time. `deferLoading`
		 * is used to indicate that deferred loading is required, but it is also used
		 * to tell DataTables how many records there are in the full table (allowing
		 * the information element and pagination to be displayed correctly). In the case
		 * where a filtering is applied to the table on initial load, this can be
		 * indicated by giving the parameter as an array, where the first element is
		 * the number of records available after filtering and the second element is the
		 * number of records without filtering (allowing the table information element
		 * to be shown correctly).
		 *  @type int | array
		 *  @default null
		 *
		 *  @dtopt Options
		 *  @name DataTable.defaults.deferLoading
		 *
		 *  @example
		 *    // 57 records available in the table, no filtering applied
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "serverSide": true,
		 *        "ajax": "scripts/server_processing.php",
		 *        "deferLoading": 57
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // 57 records after filtering, 100 without filtering (an initial filter applied)
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "serverSide": true,
		 *        "ajax": "scripts/server_processing.php",
		 *        "deferLoading": [ 57, 100 ],
		 *        "search": {
		 *          "search": "my_filter"
		 *        }
		 *      } );
		 *    } );
		 */
		"iDeferLoading": null,
	
	
		/**
		 * Number of rows to display on a single page when using pagination. If
		 * feature enabled (`lengthChange`) then the end user will be able to override
		 * this to a custom setting using a pop-up menu.
		 *  @type int
		 *  @default 10
		 *
		 *  @dtopt Options
		 *  @name DataTable.defaults.pageLength
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "pageLength": 50
		 *      } );
		 *    } )
		 */
		"iDisplayLength": 10,
	
	
		/**
		 * Define the starting point for data display when using DataTables with
		 * pagination. Note that this parameter is the number of records, rather than
		 * the page number, so if you have 10 records per page and want to start on
		 * the third page, it should be "20".
		 *  @type int
		 *  @default 0
		 *
		 *  @dtopt Options
		 *  @name DataTable.defaults.displayStart
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "displayStart": 20
		 *      } );
		 *    } )
		 */
		"iDisplayStart": 0,
	
	
		/**
		 * By default DataTables allows keyboard navigation of the table (sorting, paging,
		 * and filtering) by adding a `tabindex` attribute to the required elements. This
		 * allows you to tab through the controls and press the enter key to activate them.
		 * The tabindex is default 0, meaning that the tab follows the flow of the document.
		 * You can overrule this using this parameter if you wish. Use a value of -1 to
		 * disable built-in keyboard navigation.
		 *  @type int
		 *  @default 0
		 *
		 *  @dtopt Options
		 *  @name DataTable.defaults.tabIndex
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "tabIndex": 1
		 *      } );
		 *    } );
		 */
		"iTabIndex": 0,
	
	
		/**
		 * Classes that DataTables assigns to the various components and features
		 * that it adds to the HTML table. This allows classes to be configured
		 * during initialisation in addition to through the static
		 * {@link DataTable.ext.oStdClasses} object).
		 *  @namespace
		 *  @name DataTable.defaults.classes
		 */
		"oClasses": {},
	
	
		/**
		 * All strings that DataTables uses in the user interface that it creates
		 * are defined in this object, allowing you to modified them individually or
		 * completely replace them all as required.
		 *  @namespace
		 *  @name DataTable.defaults.language
		 */
		"oLanguage": {
			/**
			 * Strings that are used for WAI-ARIA labels and controls only (these are not
			 * actually visible on the page, but will be read by screenreaders, and thus
			 * must be internationalised as well).
			 *  @namespace
			 *  @name DataTable.defaults.language.aria
			 */
			"oAria": {
				/**
				 * ARIA label that is added to the table headers when the column may be
				 * sorted ascending by activing the column (click or return when focused).
				 * Note that the column header is prefixed to this string.
				 *  @type string
				 *  @default : activate to sort column ascending
				 *
				 *  @dtopt Language
				 *  @name DataTable.defaults.language.aria.sortAscending
				 *
				 *  @example
				 *    $(document).ready( function() {
				 *      $('#example').dataTable( {
				 *        "language": {
				 *          "aria": {
				 *            "sortAscending": " - click/return to sort ascending"
				 *          }
				 *        }
				 *      } );
				 *    } );
				 */
				"sSortAscending": ": activate to sort column ascending",
	
				/**
				 * ARIA label that is added to the table headers when the column may be
				 * sorted descending by activing the column (click or return when focused).
				 * Note that the column header is prefixed to this string.
				 *  @type string
				 *  @default : activate to sort column ascending
				 *
				 *  @dtopt Language
				 *  @name DataTable.defaults.language.aria.sortDescending
				 *
				 *  @example
				 *    $(document).ready( function() {
				 *      $('#example').dataTable( {
				 *        "language": {
				 *          "aria": {
				 *            "sortDescending": " - click/return to sort descending"
				 *          }
				 *        }
				 *      } );
				 *    } );
				 */
				"sSortDescending": ": activate to sort column descending"
			},
	
			/**
			 * Pagination string used by DataTables for the built-in pagination
			 * control types.
			 *  @namespace
			 *  @name DataTable.defaults.language.paginate
			 */
			"oPaginate": {
				/**
				 * Text to use when using the 'full_numbers' type of pagination for the
				 * button to take the user to the first page.
				 *  @type string
				 *  @default First
				 *
				 *  @dtopt Language
				 *  @name DataTable.defaults.language.paginate.first
				 *
				 *  @example
				 *    $(document).ready( function() {
				 *      $('#example').dataTable( {
				 *        "language": {
				 *          "paginate": {
				 *            "first": "First page"
				 *          }
				 *        }
				 *      } );
				 *    } );
				 */
				"sFirst": "First",
	
	
				/**
				 * Text to use when using the 'full_numbers' type of pagination for the
				 * button to take the user to the last page.
				 *  @type string
				 *  @default Last
				 *
				 *  @dtopt Language
				 *  @name DataTable.defaults.language.paginate.last
				 *
				 *  @example
				 *    $(document).ready( function() {
				 *      $('#example').dataTable( {
				 *        "language": {
				 *          "paginate": {
				 *            "last": "Last page"
				 *          }
				 *        }
				 *      } );
				 *    } );
				 */
				"sLast": "Last",
	
	
				/**
				 * Text to use for the 'next' pagination button (to take the user to the
				 * next page).
				 *  @type string
				 *  @default Next
				 *
				 *  @dtopt Language
				 *  @name DataTable.defaults.language.paginate.next
				 *
				 *  @example
				 *    $(document).ready( function() {
				 *      $('#example').dataTable( {
				 *        "language": {
				 *          "paginate": {
				 *            "next": "Next page"
				 *          }
				 *        }
				 *      } );
				 *    } );
				 */
				"sNext": "Next",
	
	
				/**
				 * Text to use for the 'previous' pagination button (to take the user to
				 * the previous page).
				 *  @type string
				 *  @default Previous
				 *
				 *  @dtopt Language
				 *  @name DataTable.defaults.language.paginate.previous
				 *
				 *  @example
				 *    $(document).ready( function() {
				 *      $('#example').dataTable( {
				 *        "language": {
				 *          "paginate": {
				 *            "previous": "Previous page"
				 *          }
				 *        }
				 *      } );
				 *    } );
				 */
				"sPrevious": "Previous"
			},
	
			/**
			 * This string is shown in preference to `zeroRecords` when the table is
			 * empty of data (regardless of filtering). Note that this is an optional
			 * parameter - if it is not given, the value of `zeroRecords` will be used
			 * instead (either the default or given value).
			 *  @type string
			 *  @default No data available in table
			 *
			 *  @dtopt Language
			 *  @name DataTable.defaults.language.emptyTable
			 *
			 *  @example
			 *    $(document).ready( function() {
			 *      $('#example').dataTable( {
			 *        "language": {
			 *          "emptyTable": "No data available in table"
			 *        }
			 *      } );
			 *    } );
			 */
			"sEmptyTable": "No data available in table",
	
	
			/**
			 * This string gives information to the end user about the information
			 * that is current on display on the page. The following tokens can be
			 * used in the string and will be dynamically replaced as the table
			 * display updates. This tokens can be placed anywhere in the string, or
			 * removed as needed by the language requires:
			 *
			 * * `\_START\_` - Display index of the first record on the current page
			 * * `\_END\_` - Display index of the last record on the current page
			 * * `\_TOTAL\_` - Number of records in the table after filtering
			 * * `\_MAX\_` - Number of records in the table without filtering
			 * * `\_PAGE\_` - Current page number
			 * * `\_PAGES\_` - Total number of pages of data in the table
			 *
			 *  @type string
			 *  @default Showing _START_ to _END_ of _TOTAL_ entries
			 *
			 *  @dtopt Language
			 *  @name DataTable.defaults.language.info
			 *
			 *  @example
			 *    $(document).ready( function() {
			 *      $('#example').dataTable( {
			 *        "language": {
			 *          "info": "Showing page _PAGE_ of _PAGES_"
			 *        }
			 *      } );
			 *    } );
			 */
			"sInfo": "Showing _START_ to _END_ of _TOTAL_ entries",
	
	
			/**
			 * Display information string for when the table is empty. Typically the
			 * format of this string should match `info`.
			 *  @type string
			 *  @default Showing 0 to 0 of 0 entries
			 *
			 *  @dtopt Language
			 *  @name DataTable.defaults.language.infoEmpty
			 *
			 *  @example
			 *    $(document).ready( function() {
			 *      $('#example').dataTable( {
			 *        "language": {
			 *          "infoEmpty": "No entries to show"
			 *        }
			 *      } );
			 *    } );
			 */
			"sInfoEmpty": "Showing 0 to 0 of 0 entries",
	
	
			/**
			 * When a user filters the information in a table, this string is appended
			 * to the information (`info`) to give an idea of how strong the filtering
			 * is. The variable _MAX_ is dynamically updated.
			 *  @type string
			 *  @default (filtered from _MAX_ total entries)
			 *
			 *  @dtopt Language
			 *  @name DataTable.defaults.language.infoFiltered
			 *
			 *  @example
			 *    $(document).ready( function() {
			 *      $('#example').dataTable( {
			 *        "language": {
			 *          "infoFiltered": " - filtering from _MAX_ records"
			 *        }
			 *      } );
			 *    } );
			 */
			"sInfoFiltered": "(filtered from _MAX_ total entries)",
	
	
			/**
			 * If can be useful to append extra information to the info string at times,
			 * and this variable does exactly that. This information will be appended to
			 * the `info` (`infoEmpty` and `infoFiltered` in whatever combination they are
			 * being used) at all times.
			 *  @type string
			 *  @default <i>Empty string</i>
			 *
			 *  @dtopt Language
			 *  @name DataTable.defaults.language.infoPostFix
			 *
			 *  @example
			 *    $(document).ready( function() {
			 *      $('#example').dataTable( {
			 *        "language": {
			 *          "infoPostFix": "All records shown are derived from real information."
			 *        }
			 *      } );
			 *    } );
			 */
			"sInfoPostFix": "",
	
	
			/**
			 * This decimal place operator is a little different from the other
			 * language options since DataTables doesn't output floating point
			 * numbers, so it won't ever use this for display of a number. Rather,
			 * what this parameter does is modify the sort methods of the table so
			 * that numbers which are in a format which has a character other than
			 * a period (`.`) as a decimal place will be sorted numerically.
			 *
			 * Note that numbers with different decimal places cannot be shown in
			 * the same table and still be sortable, the table must be consistent.
			 * However, multiple different tables on the page can use different
			 * decimal place characters.
			 *  @type string
			 *  @default 
			 *
			 *  @dtopt Language
			 *  @name DataTable.defaults.language.decimal
			 *
			 *  @example
			 *    $(document).ready( function() {
			 *      $('#example').dataTable( {
			 *        "language": {
			 *          "decimal": ","
			 *          "thousands": "."
			 *        }
			 *      } );
			 *    } );
			 */
			"sDecimal": "",
	
	
			/**
			 * DataTables has a build in number formatter (`formatNumber`) which is
			 * used to format large numbers that are used in the table information.
			 * By default a comma is used, but this can be trivially changed to any
			 * character you wish with this parameter.
			 *  @type string
			 *  @default ,
			 *
			 *  @dtopt Language
			 *  @name DataTable.defaults.language.thousands
			 *
			 *  @example
			 *    $(document).ready( function() {
			 *      $('#example').dataTable( {
			 *        "language": {
			 *          "thousands": "'"
			 *        }
			 *      } );
			 *    } );
			 */
			"sThousands": ",",
	
	
			/**
			 * Detail the action that will be taken when the drop down menu for the
			 * pagination length option is changed. The '_MENU_' variable is replaced
			 * with a default select list of 10, 25, 50 and 100, and can be replaced
			 * with a custom select box if required.
			 *  @type string
			 *  @default Show _MENU_ entries
			 *
			 *  @dtopt Language
			 *  @name DataTable.defaults.language.lengthMenu
			 *
			 *  @example
			 *    // Language change only
			 *    $(document).ready( function() {
			 *      $('#example').dataTable( {
			 *        "language": {
			 *          "lengthMenu": "Display _MENU_ records"
			 *        }
			 *      } );
			 *    } );
			 *
			 *  @example
			 *    // Language and options change
			 *    $(document).ready( function() {
			 *      $('#example').dataTable( {
			 *        "language": {
			 *          "lengthMenu": 'Display <select>'+
			 *            '<option value="10">10</option>'+
			 *            '<option value="20">20</option>'+
			 *            '<option value="30">30</option>'+
			 *            '<option value="40">40</option>'+
			 *            '<option value="50">50</option>'+
			 *            '<option value="-1">All</option>'+
			 *            '</select> records'
			 *        }
			 *      } );
			 *    } );
			 */
			"sLengthMenu": "Show _MENU_ entries",
	
	
			/**
			 * When using Ajax sourced data and during the first draw when DataTables is
			 * gathering the data, this message is shown in an empty row in the table to
			 * indicate to the end user the the data is being loaded. Note that this
			 * parameter is not used when loading data by server-side processing, just
			 * Ajax sourced data with client-side processing.
			 *  @type string
			 *  @default Loading...
			 *
			 *  @dtopt Language
			 *  @name DataTable.defaults.language.loadingRecords
			 *
			 *  @example
			 *    $(document).ready( function() {
			 *      $('#example').dataTable( {
			 *        "language": {
			 *          "loadingRecords": "Please wait - loading..."
			 *        }
			 *      } );
			 *    } );
			 */
			"sLoadingRecords": "Loading...",
	
	
			/**
			 * Text which is displayed when the table is processing a user action
			 * (usually a sort command or similar).
			 *  @type string
			 *  @default Processing...
			 *
			 *  @dtopt Language
			 *  @name DataTable.defaults.language.processing
			 *
			 *  @example
			 *    $(document).ready( function() {
			 *      $('#example').dataTable( {
			 *        "language": {
			 *          "processing": "DataTables is currently busy"
			 *        }
			 *      } );
			 *    } );
			 */
			"sProcessing": "Processing...",
	
	
			/**
			 * Details the actions that will be taken when the user types into the
			 * filtering input text box. The variable "_INPUT_", if used in the string,
			 * is replaced with the HTML text box for the filtering input allowing
			 * control over where it appears in the string. If "_INPUT_" is not given
			 * then the input box is appended to the string automatically.
			 *  @type string
			 *  @default Search:
			 *
			 *  @dtopt Language
			 *  @name DataTable.defaults.language.search
			 *
			 *  @example
			 *    // Input text box will be appended at the end automatically
			 *    $(document).ready( function() {
			 *      $('#example').dataTable( {
			 *        "language": {
			 *          "search": "Filter records:"
			 *        }
			 *      } );
			 *    } );
			 *
			 *  @example
			 *    // Specify where the filter should appear
			 *    $(document).ready( function() {
			 *      $('#example').dataTable( {
			 *        "language": {
			 *          "search": "Apply filter _INPUT_ to table"
			 *        }
			 *      } );
			 *    } );
			 */
			"sSearch": "Search:",
	
	
			/**
			 * Assign a `placeholder` attribute to the search `input` element
			 *  @type string
			 *  @default 
			 *
			 *  @dtopt Language
			 *  @name DataTable.defaults.language.searchPlaceholder
			 */
			"sSearchPlaceholder": "",
	
	
			/**
			 * All of the language information can be stored in a file on the
			 * server-side, which DataTables will look up if this parameter is passed.
			 * It must store the URL of the language file, which is in a JSON format,
			 * and the object has the same properties as the oLanguage object in the
			 * initialiser object (i.e. the above parameters). Please refer to one of
			 * the example language files to see how this works in action.
			 *  @type string
			 *  @default <i>Empty string - i.e. disabled</i>
			 *
			 *  @dtopt Language
			 *  @name DataTable.defaults.language.url
			 *
			 *  @example
			 *    $(document).ready( function() {
			 *      $('#example').dataTable( {
			 *        "language": {
			 *          "url": "http://www.sprymedia.co.uk/dataTables/lang.txt"
			 *        }
			 *      } );
			 *    } );
			 */
			"sUrl": "",
	
	
			/**
			 * Text shown inside the table records when the is no information to be
			 * displayed after filtering. `emptyTable` is shown when there is simply no
			 * information in the table at all (regardless of filtering).
			 *  @type string
			 *  @default No matching records found
			 *
			 *  @dtopt Language
			 *  @name DataTable.defaults.language.zeroRecords
			 *
			 *  @example
			 *    $(document).ready( function() {
			 *      $('#example').dataTable( {
			 *        "language": {
			 *          "zeroRecords": "No records to display"
			 *        }
			 *      } );
			 *    } );
			 */
			"sZeroRecords": "No matching records found"
		},
	
	
		/**
		 * This parameter allows you to have define the global filtering state at
		 * initialisation time. As an object the `search` parameter must be
		 * defined, but all other parameters are optional. When `regex` is true,
		 * the search string will be treated as a regular expression, when false
		 * (default) it will be treated as a straight string. When `smart`
		 * DataTables will use it's smart filtering methods (to word match at
		 * any point in the data), when false this will not be done.
		 *  @namespace
		 *  @extends DataTable.models.oSearch
		 *
		 *  @dtopt Options
		 *  @name DataTable.defaults.search
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "search": {"search": "Initial search"}
		 *      } );
		 *    } )
		 */
		"oSearch": $.extend( {}, DataTable.models.oSearch ),
	
	
		/**
		 * __Deprecated__ The functionality provided by this parameter has now been
		 * superseded by that provided through `ajax`, which should be used instead.
		 *
		 * By default DataTables will look for the property `data` (or `aaData` for
		 * compatibility with DataTables 1.9-) when obtaining data from an Ajax
		 * source or for server-side processing - this parameter allows that
		 * property to be changed. You can use Javascript dotted object notation to
		 * get a data source for multiple levels of nesting.
		 *  @type string
		 *  @default data
		 *
		 *  @dtopt Options
		 *  @dtopt Server-side
		 *  @name DataTable.defaults.ajaxDataProp
		 *
		 *  @deprecated 1.10. Please use `ajax` for this functionality now.
		 */
		"sAjaxDataProp": "data",
	
	
		/**
		 * __Deprecated__ The functionality provided by this parameter has now been
		 * superseded by that provided through `ajax`, which should be used instead.
		 *
		 * You can instruct DataTables to load data from an external
		 * source using this parameter (use aData if you want to pass data in you
		 * already have). Simply provide a url a JSON object can be obtained from.
		 *  @type string
		 *  @default null
		 *
		 *  @dtopt Options
		 *  @dtopt Server-side
		 *  @name DataTable.defaults.ajaxSource
		 *
		 *  @deprecated 1.10. Please use `ajax` for this functionality now.
		 */
		"sAjaxSource": null,
	
	
		/**
		 * This initialisation variable allows you to specify exactly where in the
		 * DOM you want DataTables to inject the various controls it adds to the page
		 * (for example you might want the pagination controls at the top of the
		 * table). DIV elements (with or without a custom class) can also be added to
		 * aid styling. The follow syntax is used:
		 *   <ul>
		 *     <li>The following options are allowed:
		 *       <ul>
		 *         <li>'l' - Length changing</li>
		 *         <li>'f' - Filtering input</li>
		 *         <li>'t' - The table!</li>
		 *         <li>'i' - Information</li>
		 *         <li>'p' - Pagination</li>
		 *         <li>'r' - pRocessing</li>
		 *       </ul>
		 *     </li>
		 *     <li>The following constants are allowed:
		 *       <ul>
		 *         <li>'H' - jQueryUI theme "header" classes ('fg-toolbar ui-widget-header ui-corner-tl ui-corner-tr ui-helper-clearfix')</li>
		 *         <li>'F' - jQueryUI theme "footer" classes ('fg-toolbar ui-widget-header ui-corner-bl ui-corner-br ui-helper-clearfix')</li>
		 *       </ul>
		 *     </li>
		 *     <li>The following syntax is expected:
		 *       <ul>
		 *         <li>'&lt;' and '&gt;' - div elements</li>
		 *         <li>'&lt;"class" and '&gt;' - div with a class</li>
		 *         <li>'&lt;"#id" and '&gt;' - div with an ID</li>
		 *       </ul>
		 *     </li>
		 *     <li>Examples:
		 *       <ul>
		 *         <li>'&lt;"wrapper"flipt&gt;'</li>
		 *         <li>'&lt;lf&lt;t&gt;ip&gt;'</li>
		 *       </ul>
		 *     </li>
		 *   </ul>
		 *  @type string
		 *  @default lfrtip <i>(when `jQueryUI` is false)</i> <b>or</b>
		 *    <"H"lfr>t<"F"ip> <i>(when `jQueryUI` is true)</i>
		 *
		 *  @dtopt Options
		 *  @name DataTable.defaults.dom
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "dom": '&lt;"top"i&gt;rt&lt;"bottom"flp&gt;&lt;"clear"&gt;'
		 *      } );
		 *    } );
		 */
		"sDom": "lfrtip",
	
	
		/**
		 * Search delay option. This will throttle full table searches that use the
		 * DataTables provided search input element (it does not effect calls to
		 * `dt-api search()`, providing a delay before the search is made.
		 *  @type integer
		 *  @default 0
		 *
		 *  @dtopt Options
		 *  @name DataTable.defaults.searchDelay
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "searchDelay": 200
		 *      } );
		 *    } )
		 */
		"searchDelay": null,
	
	
		/**
		 * DataTables features four different built-in options for the buttons to
		 * display for pagination control:
		 *
		 * * `simple` - 'Previous' and 'Next' buttons only
		 * * 'simple_numbers` - 'Previous' and 'Next' buttons, plus page numbers
		 * * `full` - 'First', 'Previous', 'Next' and 'Last' buttons
		 * * `full_numbers` - 'First', 'Previous', 'Next' and 'Last' buttons, plus
		 *   page numbers
		 *  
		 * Further methods can be added using {@link DataTable.ext.oPagination}.
		 *  @type string
		 *  @default simple_numbers
		 *
		 *  @dtopt Options
		 *  @name DataTable.defaults.pagingType
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "pagingType": "full_numbers"
		 *      } );
		 *    } )
		 */
		"sPaginationType": "simple_numbers",
	
	
		/**
		 * Enable horizontal scrolling. When a table is too wide to fit into a
		 * certain layout, or you have a large number of columns in the table, you
		 * can enable x-scrolling to show the table in a viewport, which can be
		 * scrolled. This property can be `true` which will allow the table to
		 * scroll horizontally when needed, or any CSS unit, or a number (in which
		 * case it will be treated as a pixel measurement). Setting as simply `true`
		 * is recommended.
		 *  @type boolean|string
		 *  @default <i>blank string - i.e. disabled</i>
		 *
		 *  @dtopt Features
		 *  @name DataTable.defaults.scrollX
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "scrollX": true,
		 *        "scrollCollapse": true
		 *      } );
		 *    } );
		 */
		"sScrollX": "",
	
	
		/**
		 * This property can be used to force a DataTable to use more width than it
		 * might otherwise do when x-scrolling is enabled. For example if you have a
		 * table which requires to be well spaced, this parameter is useful for
		 * "over-sizing" the table, and thus forcing scrolling. This property can by
		 * any CSS unit, or a number (in which case it will be treated as a pixel
		 * measurement).
		 *  @type string
		 *  @default <i>blank string - i.e. disabled</i>
		 *
		 *  @dtopt Options
		 *  @name DataTable.defaults.scrollXInner
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "scrollX": "100%",
		 *        "scrollXInner": "110%"
		 *      } );
		 *    } );
		 */
		"sScrollXInner": "",
	
	
		/**
		 * Enable vertical scrolling. Vertical scrolling will constrain the DataTable
		 * to the given height, and enable scrolling for any data which overflows the
		 * current viewport. This can be used as an alternative to paging to display
		 * a lot of data in a small area (although paging and scrolling can both be
		 * enabled at the same time). This property can be any CSS unit, or a number
		 * (in which case it will be treated as a pixel measurement).
		 *  @type string
		 *  @default <i>blank string - i.e. disabled</i>
		 *
		 *  @dtopt Features
		 *  @name DataTable.defaults.scrollY
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "scrollY": "200px",
		 *        "paginate": false
		 *      } );
		 *    } );
		 */
		"sScrollY": "",
	
	
		/**
		 * __Deprecated__ The functionality provided by this parameter has now been
		 * superseded by that provided through `ajax`, which should be used instead.
		 *
		 * Set the HTTP method that is used to make the Ajax call for server-side
		 * processing or Ajax sourced data.
		 *  @type string
		 *  @default GET
		 *
		 *  @dtopt Options
		 *  @dtopt Server-side
		 *  @name DataTable.defaults.serverMethod
		 *
		 *  @deprecated 1.10. Please use `ajax` for this functionality now.
		 */
		"sServerMethod": "GET",
	
	
		/**
		 * DataTables makes use of renderers when displaying HTML elements for
		 * a table. These renderers can be added or modified by plug-ins to
		 * generate suitable mark-up for a site. For example the Bootstrap
		 * integration plug-in for DataTables uses a paging button renderer to
		 * display pagination buttons in the mark-up required by Bootstrap.
		 *
		 * For further information about the renderers available see
		 * DataTable.ext.renderer
		 *  @type string|object
		 *  @default null
		 *
		 *  @name DataTable.defaults.renderer
		 *
		 */
		"renderer": null,
	
	
		/**
		 * Set the data property name that DataTables should use to get a row's id
		 * to set as the `id` property in the node.
		 *  @type string
		 *  @default DT_RowId
		 *
		 *  @name DataTable.defaults.rowId
		 */
		"rowId": "DT_RowId"
	};
	
	_fnHungarianMap( DataTable.defaults );
	
	
	
	/*
	 * Developer note - See note in model.defaults.js about the use of Hungarian
	 * notation and camel case.
	 */
	
	/**
	 * Column options that can be given to DataTables at initialisation time.
	 *  @namespace
	 */
	DataTable.defaults.column = {
		/**
		 * Define which column(s) an order will occur on for this column. This
		 * allows a column's ordering to take multiple columns into account when
		 * doing a sort or use the data from a different column. For example first
		 * name / last name columns make sense to do a multi-column sort over the
		 * two columns.
		 *  @type array|int
		 *  @default null <i>Takes the value of the column index automatically</i>
		 *
		 *  @name DataTable.defaults.column.orderData
		 *  @dtopt Columns
		 *
		 *  @example
		 *    // Using `columnDefs`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [
		 *          { "orderData": [ 0, 1 ], "targets": [ 0 ] },
		 *          { "orderData": [ 1, 0 ], "targets": [ 1 ] },
		 *          { "orderData": 2, "targets": [ 2 ] }
		 *        ]
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // Using `columns`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columns": [
		 *          { "orderData": [ 0, 1 ] },
		 *          { "orderData": [ 1, 0 ] },
		 *          { "orderData": 2 },
		 *          null,
		 *          null
		 *        ]
		 *      } );
		 *    } );
		 */
		"aDataSort": null,
		"iDataSort": -1,
	
	
		/**
		 * You can control the default ordering direction, and even alter the
		 * behaviour of the sort handler (i.e. only allow ascending ordering etc)
		 * using this parameter.
		 *  @type array
		 *  @default [ 'asc', 'desc' ]
		 *
		 *  @name DataTable.defaults.column.orderSequence
		 *  @dtopt Columns
		 *
		 *  @example
		 *    // Using `columnDefs`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [
		 *          { "orderSequence": [ "asc" ], "targets": [ 1 ] },
		 *          { "orderSequence": [ "desc", "asc", "asc" ], "targets": [ 2 ] },
		 *          { "orderSequence": [ "desc" ], "targets": [ 3 ] }
		 *        ]
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // Using `columns`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columns": [
		 *          null,
		 *          { "orderSequence": [ "asc" ] },
		 *          { "orderSequence": [ "desc", "asc", "asc" ] },
		 *          { "orderSequence": [ "desc" ] },
		 *          null
		 *        ]
		 *      } );
		 *    } );
		 */
		"asSorting": [ 'asc', 'desc' ],
	
	
		/**
		 * Enable or disable filtering on the data in this column.
		 *  @type boolean
		 *  @default true
		 *
		 *  @name DataTable.defaults.column.searchable
		 *  @dtopt Columns
		 *
		 *  @example
		 *    // Using `columnDefs`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [
		 *          { "searchable": false, "targets": [ 0 ] }
		 *        ] } );
		 *    } );
		 *
		 *  @example
		 *    // Using `columns`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columns": [
		 *          { "searchable": false },
		 *          null,
		 *          null,
		 *          null,
		 *          null
		 *        ] } );
		 *    } );
		 */
		"bSearchable": true,
	
	
		/**
		 * Enable or disable ordering on this column.
		 *  @type boolean
		 *  @default true
		 *
		 *  @name DataTable.defaults.column.orderable
		 *  @dtopt Columns
		 *
		 *  @example
		 *    // Using `columnDefs`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [
		 *          { "orderable": false, "targets": [ 0 ] }
		 *        ] } );
		 *    } );
		 *
		 *  @example
		 *    // Using `columns`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columns": [
		 *          { "orderable": false },
		 *          null,
		 *          null,
		 *          null,
		 *          null
		 *        ] } );
		 *    } );
		 */
		"bSortable": true,
	
	
		/**
		 * Enable or disable the display of this column.
		 *  @type boolean
		 *  @default true
		 *
		 *  @name DataTable.defaults.column.visible
		 *  @dtopt Columns
		 *
		 *  @example
		 *    // Using `columnDefs`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [
		 *          { "visible": false, "targets": [ 0 ] }
		 *        ] } );
		 *    } );
		 *
		 *  @example
		 *    // Using `columns`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columns": [
		 *          { "visible": false },
		 *          null,
		 *          null,
		 *          null,
		 *          null
		 *        ] } );
		 *    } );
		 */
		"bVisible": true,
	
	
		/**
		 * Developer definable function that is called whenever a cell is created (Ajax source,
		 * etc) or processed for input (DOM source). This can be used as a compliment to mRender
		 * allowing you to modify the DOM element (add background colour for example) when the
		 * element is available.
		 *  @type function
		 *  @param {element} td The TD node that has been created
		 *  @param {*} cellData The Data for the cell
		 *  @param {array|object} rowData The data for the whole row
		 *  @param {int} row The row index for the aoData data store
		 *  @param {int} col The column index for aoColumns
		 *
		 *  @name DataTable.defaults.column.createdCell
		 *  @dtopt Columns
		 *
		 *  @example
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [ {
		 *          "targets": [3],
		 *          "createdCell": function (td, cellData, rowData, row, col) {
		 *            if ( cellData == "1.7" ) {
		 *              $(td).css('color', 'blue')
		 *            }
		 *          }
		 *        } ]
		 *      });
		 *    } );
		 */
		"fnCreatedCell": null,
	
	
		/**
		 * This parameter has been replaced by `data` in DataTables to ensure naming
		 * consistency. `dataProp` can still be used, as there is backwards
		 * compatibility in DataTables for this option, but it is strongly
		 * recommended that you use `data` in preference to `dataProp`.
		 *  @name DataTable.defaults.column.dataProp
		 */
	
	
		/**
		 * This property can be used to read data from any data source property,
		 * including deeply nested objects / properties. `data` can be given in a
		 * number of different ways which effect its behaviour:
		 *
		 * * `integer` - treated as an array index for the data source. This is the
		 *   default that DataTables uses (incrementally increased for each column).
		 * * `string` - read an object property from the data source. There are
		 *   three 'special' options that can be used in the string to alter how
		 *   DataTables reads the data from the source object:
		 *    * `.` - Dotted Javascript notation. Just as you use a `.` in
		 *      Javascript to read from nested objects, so to can the options
		 *      specified in `data`. For example: `browser.version` or
		 *      `browser.name`. If your object parameter name contains a period, use
		 *      `\\` to escape it - i.e. `first\\.name`.
		 *    * `[]` - Array notation. DataTables can automatically combine data
		 *      from and array source, joining the data with the characters provided
		 *      between the two brackets. For example: `name[, ]` would provide a
		 *      comma-space separated list from the source array. If no characters
		 *      are provided between the brackets, the original array source is
		 *      returned.
		 *    * `()` - Function notation. Adding `()` to the end of a parameter will
		 *      execute a function of the name given. For example: `browser()` for a
		 *      simple function on the data source, `browser.version()` for a
		 *      function in a nested property or even `browser().version` to get an
		 *      object property if the function called returns an object. Note that
		 *      function notation is recommended for use in `render` rather than
		 *      `data` as it is much simpler to use as a renderer.
		 * * `null` - use the original data source for the row rather than plucking
		 *   data directly from it. This action has effects on two other
		 *   initialisation options:
		 *    * `defaultContent` - When null is given as the `data` option and
		 *      `defaultContent` is specified for the column, the value defined by
		 *      `defaultContent` will be used for the cell.
		 *    * `render` - When null is used for the `data` option and the `render`
		 *      option is specified for the column, the whole data source for the
		 *      row is used for the renderer.
		 * * `function` - the function given will be executed whenever DataTables
		 *   needs to set or get the data for a cell in the column. The function
		 *   takes three parameters:
		 *    * Parameters:
		 *      * `{array|object}` The data source for the row
		 *      * `{string}` The type call data requested - this will be 'set' when
		 *        setting data or 'filter', 'display', 'type', 'sort' or undefined
		 *        when gathering data. Note that when `undefined` is given for the
		 *        type DataTables expects to get the raw data for the object back<
		 *      * `{*}` Data to set when the second parameter is 'set'.
		 *    * Return:
		 *      * The return value from the function is not required when 'set' is
		 *        the type of call, but otherwise the return is what will be used
		 *        for the data requested.
		 *
		 * Note that `data` is a getter and setter option. If you just require
		 * formatting of data for output, you will likely want to use `render` which
		 * is simply a getter and thus simpler to use.
		 *
		 * Note that prior to DataTables 1.9.2 `data` was called `mDataProp`. The
		 * name change reflects the flexibility of this property and is consistent
		 * with the naming of mRender. If 'mDataProp' is given, then it will still
		 * be used by DataTables, as it automatically maps the old name to the new
		 * if required.
		 *
		 *  @type string|int|function|null
		 *  @default null <i>Use automatically calculated column index</i>
		 *
		 *  @name DataTable.defaults.column.data
		 *  @dtopt Columns
		 *
		 *  @example
		 *    // Read table data from objects
		 *    // JSON structure for each row:
		 *    //   {
		 *    //      "engine": {value},
		 *    //      "browser": {value},
		 *    //      "platform": {value},
		 *    //      "version": {value},
		 *    //      "grade": {value}
		 *    //   }
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "ajaxSource": "sources/objects.txt",
		 *        "columns": [
		 *          { "data": "engine" },
		 *          { "data": "browser" },
		 *          { "data": "platform" },
		 *          { "data": "version" },
		 *          { "data": "grade" }
		 *        ]
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // Read information from deeply nested objects
		 *    // JSON structure for each row:
		 *    //   {
		 *    //      "engine": {value},
		 *    //      "browser": {value},
		 *    //      "platform": {
		 *    //         "inner": {value}
		 *    //      },
		 *    //      "details": [
		 *    //         {value}, {value}
		 *    //      ]
		 *    //   }
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "ajaxSource": "sources/deep.txt",
		 *        "columns": [
		 *          { "data": "engine" },
		 *          { "data": "browser" },
		 *          { "data": "platform.inner" },
		 *          { "data": "platform.details.0" },
		 *          { "data": "platform.details.1" }
		 *        ]
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // Using `data` as a function to provide different information for
		 *    // sorting, filtering and display. In this case, currency (price)
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [ {
		 *          "targets": [ 0 ],
		 *          "data": function ( source, type, val ) {
		 *            if (type === 'set') {
		 *              source.price = val;
		 *              // Store the computed dislay and filter values for efficiency
		 *              source.price_display = val=="" ? "" : "$"+numberFormat(val);
		 *              source.price_filter  = val=="" ? "" : "$"+numberFormat(val)+" "+val;
		 *              return;
		 *            }
		 *            else if (type === 'display') {
		 *              return source.price_display;
		 *            }
		 *            else if (type === 'filter') {
		 *              return source.price_filter;
		 *            }
		 *            // 'sort', 'type' and undefined all just use the integer
		 *            return source.price;
		 *          }
		 *        } ]
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // Using default content
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [ {
		 *          "targets": [ 0 ],
		 *          "data": null,
		 *          "defaultContent": "Click to edit"
		 *        } ]
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // Using array notation - outputting a list from an array
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [ {
		 *          "targets": [ 0 ],
		 *          "data": "name[, ]"
		 *        } ]
		 *      } );
		 *    } );
		 *
		 */
		"mData": null,
	
	
		/**
		 * This property is the rendering partner to `data` and it is suggested that
		 * when you want to manipulate data for display (including filtering,
		 * sorting etc) without altering the underlying data for the table, use this
		 * property. `render` can be considered to be the the read only companion to
		 * `data` which is read / write (then as such more complex). Like `data`
		 * this option can be given in a number of different ways to effect its
		 * behaviour:
		 *
		 * * `integer` - treated as an array index for the data source. This is the
		 *   default that DataTables uses (incrementally increased for each column).
		 * * `string` - read an object property from the data source. There are
		 *   three 'special' options that can be used in the string to alter how
		 *   DataTables reads the data from the source object:
		 *    * `.` - Dotted Javascript notation. Just as you use a `.` in
		 *      Javascript to read from nested objects, so to can the options
		 *      specified in `data`. For example: `browser.version` or
		 *      `browser.name`. If your object parameter name contains a period, use
		 *      `\\` to escape it - i.e. `first\\.name`.
		 *    * `[]` - Array notation. DataTables can automatically combine data
		 *      from and array source, joining the data with the characters provided
		 *      between the two brackets. For example: `name[, ]` would provide a
		 *      comma-space separated list from the source array. If no characters
		 *      are provided between the brackets, the original array source is
		 *      returned.
		 *    * `()` - Function notation. Adding `()` to the end of a parameter will
		 *      execute a function of the name given. For example: `browser()` for a
		 *      simple function on the data source, `browser.version()` for a
		 *      function in a nested property or even `browser().version` to get an
		 *      object property if the function called returns an object.
		 * * `object` - use different data for the different data types requested by
		 *   DataTables ('filter', 'display', 'type' or 'sort'). The property names
		 *   of the object is the data type the property refers to and the value can
		 *   defined using an integer, string or function using the same rules as
		 *   `render` normally does. Note that an `_` option _must_ be specified.
		 *   This is the default value to use if you haven't specified a value for
		 *   the data type requested by DataTables.
		 * * `function` - the function given will be executed whenever DataTables
		 *   needs to set or get the data for a cell in the column. The function
		 *   takes three parameters:
		 *    * Parameters:
		 *      * {array|object} The data source for the row (based on `data`)
		 *      * {string} The type call data requested - this will be 'filter',
		 *        'display', 'type' or 'sort'.
		 *      * {array|object} The full data source for the row (not based on
		 *        `data`)
		 *    * Return:
		 *      * The return value from the function is what will be used for the
		 *        data requested.
		 *
		 *  @type string|int|function|object|null
		 *  @default null Use the data source value.
		 *
		 *  @name DataTable.defaults.column.render
		 *  @dtopt Columns
		 *
		 *  @example
		 *    // Create a comma separated list from an array of objects
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "ajaxSource": "sources/deep.txt",
		 *        "columns": [
		 *          { "data": "engine" },
		 *          { "data": "browser" },
		 *          {
		 *            "data": "platform",
		 *            "render": "[, ].name"
		 *          }
		 *        ]
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // Execute a function to obtain data
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [ {
		 *          "targets": [ 0 ],
		 *          "data": null, // Use the full data source object for the renderer's source
		 *          "render": "browserName()"
		 *        } ]
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // As an object, extracting different data for the different types
		 *    // This would be used with a data source such as:
		 *    //   { "phone": 5552368, "phone_filter": "5552368 555-2368", "phone_display": "555-2368" }
		 *    // Here the `phone` integer is used for sorting and type detection, while `phone_filter`
		 *    // (which has both forms) is used for filtering for if a user inputs either format, while
		 *    // the formatted phone number is the one that is shown in the table.
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [ {
		 *          "targets": [ 0 ],
		 *          "data": null, // Use the full data source object for the renderer's source
		 *          "render": {
		 *            "_": "phone",
		 *            "filter": "phone_filter",
		 *            "display": "phone_display"
		 *          }
		 *        } ]
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // Use as a function to create a link from the data source
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [ {
		 *          "targets": [ 0 ],
		 *          "data": "download_link",
		 *          "render": function ( data, type, full ) {
		 *            return '<a href="'+data+'">Download</a>';
		 *          }
		 *        } ]
		 *      } );
		 *    } );
		 */
		"mRender": null,
	
	
		/**
		 * Change the cell type created for the column - either TD cells or TH cells. This
		 * can be useful as TH cells have semantic meaning in the table body, allowing them
		 * to act as a header for a row (you may wish to add scope='row' to the TH elements).
		 *  @type string
		 *  @default td
		 *
		 *  @name DataTable.defaults.column.cellType
		 *  @dtopt Columns
		 *
		 *  @example
		 *    // Make the first column use TH cells
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [ {
		 *          "targets": [ 0 ],
		 *          "cellType": "th"
		 *        } ]
		 *      } );
		 *    } );
		 */
		"sCellType": "td",
	
	
		/**
		 * Class to give to each cell in this column.
		 *  @type string
		 *  @default <i>Empty string</i>
		 *
		 *  @name DataTable.defaults.column.class
		 *  @dtopt Columns
		 *
		 *  @example
		 *    // Using `columnDefs`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [
		 *          { "class": "my_class", "targets": [ 0 ] }
		 *        ]
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // Using `columns`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columns": [
		 *          { "class": "my_class" },
		 *          null,
		 *          null,
		 *          null,
		 *          null
		 *        ]
		 *      } );
		 *    } );
		 */
		"sClass": "",
	
		/**
		 * When DataTables calculates the column widths to assign to each column,
		 * it finds the longest string in each column and then constructs a
		 * temporary table and reads the widths from that. The problem with this
		 * is that "mmm" is much wider then "iiii", but the latter is a longer
		 * string - thus the calculation can go wrong (doing it properly and putting
		 * it into an DOM object and measuring that is horribly(!) slow). Thus as
		 * a "work around" we provide this option. It will append its value to the
		 * text that is found to be the longest string for the column - i.e. padding.
		 * Generally you shouldn't need this!
		 *  @type string
		 *  @default <i>Empty string<i>
		 *
		 *  @name DataTable.defaults.column.contentPadding
		 *  @dtopt Columns
		 *
		 *  @example
		 *    // Using `columns`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columns": [
		 *          null,
		 *          null,
		 *          null,
		 *          {
		 *            "contentPadding": "mmm"
		 *          }
		 *        ]
		 *      } );
		 *    } );
		 */
		"sContentPadding": "",
	
	
		/**
		 * Allows a default value to be given for a column's data, and will be used
		 * whenever a null data source is encountered (this can be because `data`
		 * is set to null, or because the data source itself is null).
		 *  @type string
		 *  @default null
		 *
		 *  @name DataTable.defaults.column.defaultContent
		 *  @dtopt Columns
		 *
		 *  @example
		 *    // Using `columnDefs`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [
		 *          {
		 *            "data": null,
		 *            "defaultContent": "Edit",
		 *            "targets": [ -1 ]
		 *          }
		 *        ]
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // Using `columns`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columns": [
		 *          null,
		 *          null,
		 *          null,
		 *          {
		 *            "data": null,
		 *            "defaultContent": "Edit"
		 *          }
		 *        ]
		 *      } );
		 *    } );
		 */
		"sDefaultContent": null,
	
	
		/**
		 * This parameter is only used in DataTables' server-side processing. It can
		 * be exceptionally useful to know what columns are being displayed on the
		 * client side, and to map these to database fields. When defined, the names
		 * also allow DataTables to reorder information from the server if it comes
		 * back in an unexpected order (i.e. if you switch your columns around on the
		 * client-side, your server-side code does not also need updating).
		 *  @type string
		 *  @default <i>Empty string</i>
		 *
		 *  @name DataTable.defaults.column.name
		 *  @dtopt Columns
		 *
		 *  @example
		 *    // Using `columnDefs`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [
		 *          { "name": "engine", "targets": [ 0 ] },
		 *          { "name": "browser", "targets": [ 1 ] },
		 *          { "name": "platform", "targets": [ 2 ] },
		 *          { "name": "version", "targets": [ 3 ] },
		 *          { "name": "grade", "targets": [ 4 ] }
		 *        ]
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // Using `columns`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columns": [
		 *          { "name": "engine" },
		 *          { "name": "browser" },
		 *          { "name": "platform" },
		 *          { "name": "version" },
		 *          { "name": "grade" }
		 *        ]
		 *      } );
		 *    } );
		 */
		"sName": "",
	
	
		/**
		 * Defines a data source type for the ordering which can be used to read
		 * real-time information from the table (updating the internally cached
		 * version) prior to ordering. This allows ordering to occur on user
		 * editable elements such as form inputs.
		 *  @type string
		 *  @default std
		 *
		 *  @name DataTable.defaults.column.orderDataType
		 *  @dtopt Columns
		 *
		 *  @example
		 *    // Using `columnDefs`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [
		 *          { "orderDataType": "dom-text", "targets": [ 2, 3 ] },
		 *          { "type": "numeric", "targets": [ 3 ] },
		 *          { "orderDataType": "dom-select", "targets": [ 4 ] },
		 *          { "orderDataType": "dom-checkbox", "targets": [ 5 ] }
		 *        ]
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // Using `columns`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columns": [
		 *          null,
		 *          null,
		 *          { "orderDataType": "dom-text" },
		 *          { "orderDataType": "dom-text", "type": "numeric" },
		 *          { "orderDataType": "dom-select" },
		 *          { "orderDataType": "dom-checkbox" }
		 *        ]
		 *      } );
		 *    } );
		 */
		"sSortDataType": "std",
	
	
		/**
		 * The title of this column.
		 *  @type string
		 *  @default null <i>Derived from the 'TH' value for this column in the
		 *    original HTML table.</i>
		 *
		 *  @name DataTable.defaults.column.title
		 *  @dtopt Columns
		 *
		 *  @example
		 *    // Using `columnDefs`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [
		 *          { "title": "My column title", "targets": [ 0 ] }
		 *        ]
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // Using `columns`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columns": [
		 *          { "title": "My column title" },
		 *          null,
		 *          null,
		 *          null,
		 *          null
		 *        ]
		 *      } );
		 *    } );
		 */
		"sTitle": null,
	
	
		/**
		 * The type allows you to specify how the data for this column will be
		 * ordered. Four types (string, numeric, date and html (which will strip
		 * HTML tags before ordering)) are currently available. Note that only date
		 * formats understood by Javascript's Date() object will be accepted as type
		 * date. For example: "Mar 26, 2008 5:03 PM". May take the values: 'string',
		 * 'numeric', 'date' or 'html' (by default). Further types can be adding
		 * through plug-ins.
		 *  @type string
		 *  @default null <i>Auto-detected from raw data</i>
		 *
		 *  @name DataTable.defaults.column.type
		 *  @dtopt Columns
		 *
		 *  @example
		 *    // Using `columnDefs`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [
		 *          { "type": "html", "targets": [ 0 ] }
		 *        ]
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // Using `columns`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columns": [
		 *          { "type": "html" },
		 *          null,
		 *          null,
		 *          null,
		 *          null
		 *        ]
		 *      } );
		 *    } );
		 */
		"sType": null,
	
	
		/**
		 * Defining the width of the column, this parameter may take any CSS value
		 * (3em, 20px etc). DataTables applies 'smart' widths to columns which have not
		 * been given a specific width through this interface ensuring that the table
		 * remains readable.
		 *  @type string
		 *  @default null <i>Automatic</i>
		 *
		 *  @name DataTable.defaults.column.width
		 *  @dtopt Columns
		 *
		 *  @example
		 *    // Using `columnDefs`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columnDefs": [
		 *          { "width": "20%", "targets": [ 0 ] }
		 *        ]
		 *      } );
		 *    } );
		 *
		 *  @example
		 *    // Using `columns`
		 *    $(document).ready( function() {
		 *      $('#example').dataTable( {
		 *        "columns": [
		 *          { "width": "20%" },
		 *          null,
		 *          null,
		 *          null,
		 *          null
		 *        ]
		 *      } );
		 *    } );
		 */
		"sWidth": null
	};
	
	_fnHungarianMap( DataTable.defaults.column );
	
	
	
	/**
	 * DataTables settings object - this holds all the information needed for a
	 * given table, including configuration, data and current application of the
	 * table options. DataTables does not have a single instance for each DataTable
	 * with the settings attached to that instance, but rather instances of the
	 * DataTable "class" are created on-the-fly as needed (typically by a
	 * $().dataTable() call) and the settings object is then applied to that
	 * instance.
	 *
	 * Note that this object is related to {@link DataTable.defaults} but this
	 * one is the internal data store for DataTables's cache of columns. It should
	 * NOT be manipulated outside of DataTables. Any configuration should be done
	 * through the initialisation options.
	 *  @namespace
	 *  @todo Really should attach the settings object to individual instances so we
	 *    don't need to create new instances on each $().dataTable() call (if the
	 *    table already exists). It would also save passing oSettings around and
	 *    into every single function. However, this is a very significant
	 *    architecture change for DataTables and will almost certainly break
	 *    backwards compatibility with older installations. This is something that
	 *    will be done in 2.0.
	 */
	DataTable.models.oSettings = {
		/**
		 * Primary features of DataTables and their enablement state.
		 *  @namespace
		 */
		"oFeatures": {
	
			/**
			 * Flag to say if DataTables should automatically try to calculate the
			 * optimum table and columns widths (true) or not (false).
			 * Note that this parameter will be set by the initialisation routine. To
			 * set a default use {@link DataTable.defaults}.
			 *  @type boolean
			 */
			"bAutoWidth": null,
	
			/**
			 * Delay the creation of TR and TD elements until they are actually
			 * needed by a driven page draw. This can give a significant speed
			 * increase for Ajax source and Javascript source data, but makes no
			 * difference at all fro DOM and server-side processing tables.
			 * Note that this parameter will be set by the initialisation routine. To
			 * set a default use {@link DataTable.defaults}.
			 *  @type boolean
			 */
			"bDeferRender": null,
	
			/**
			 * Enable filtering on the table or not. Note that if this is disabled
			 * then there is no filtering at all on the table, including fnFilter.
			 * To just remove the filtering input use sDom and remove the 'f' option.
			 * Note that this parameter will be set by the initialisation routine. To
			 * set a default use {@link DataTable.defaults}.
			 *  @type boolean
			 */
			"bFilter": null,
	
			/**
			 * Table information element (the 'Showing x of y records' div) enable
			 * flag.
			 * Note that this parameter will be set by the initialisation routine. To
			 * set a default use {@link DataTable.defaults}.
			 *  @type boolean
			 */
			"bInfo": null,
	
			/**
			 * Present a user control allowing the end user to change the page size
			 * when pagination is enabled.
			 * Note that this parameter will be set by the initialisation routine. To
			 * set a default use {@link DataTable.defaults}.
			 *  @type boolean
			 */
			"bLengthChange": null,
	
			/**
			 * Pagination enabled or not. Note that if this is disabled then length
			 * changing must also be disabled.
			 * Note that this parameter will be set by the initialisation routine. To
			 * set a default use {@link DataTable.defaults}.
			 *  @type boolean
			 */
			"bPaginate": null,
	
			/**
			 * Processing indicator enable flag whenever DataTables is enacting a
			 * user request - typically an Ajax request for server-side processing.
			 * Note that this parameter will be set by the initialisation routine. To
			 * set a default use {@link DataTable.defaults}.
			 *  @type boolean
			 */
			"bProcessing": null,
	
			/**
			 * Server-side processing enabled flag - when enabled DataTables will
			 * get all data from the server for every draw - there is no filtering,
			 * sorting or paging done on the client-side.
			 * Note that this parameter will be set by the initialisation routine. To
			 * set a default use {@link DataTable.defaults}.
			 *  @type boolean
			 */
			"bServerSide": null,
	
			/**
			 * Sorting enablement flag.
			 * Note that this parameter will be set by the initialisation routine. To
			 * set a default use {@link DataTable.defaults}.
			 *  @type boolean
			 */
			"bSort": null,
	
			/**
			 * Multi-column sorting
			 * Note that this parameter will be set by the initialisation routine. To
			 * set a default use {@link DataTable.defaults}.
			 *  @type boolean
			 */
			"bSortMulti": null,
	
			/**
			 * Apply a class to the columns which are being sorted to provide a
			 * visual highlight or not. This can slow things down when enabled since
			 * there is a lot of DOM interaction.
			 * Note that this parameter will be set by the initialisation routine. To
			 * set a default use {@link DataTable.defaults}.
			 *  @type boolean
			 */
			"bSortClasses": null,
	
			/**
			 * State saving enablement flag.
			 * Note that this parameter will be set by the initialisation routine. To
			 * set a default use {@link DataTable.defaults}.
			 *  @type boolean
			 */
			"bStateSave": null
		},
	
	
		/**
		 * Scrolling settings for a table.
		 *  @namespace
		 */
		"oScroll": {
			/**
			 * When the table is shorter in height than sScrollY, collapse the
			 * table container down to the height of the table (when true).
			 * Note that this parameter will be set by the initialisation routine. To
			 * set a default use {@link DataTable.defaults}.
			 *  @type boolean
			 */
			"bCollapse": null,
	
			/**
			 * Width of the scrollbar for the web-browser's platform. Calculated
			 * during table initialisation.
			 *  @type int
			 *  @default 0
			 */
			"iBarWidth": 0,
	
			/**
			 * Viewport width for horizontal scrolling. Horizontal scrolling is
			 * disabled if an empty string.
			 * Note that this parameter will be set by the initialisation routine. To
			 * set a default use {@link DataTable.defaults}.
			 *  @type string
			 */
			"sX": null,
	
			/**
			 * Width to expand the table to when using x-scrolling. Typically you
			 * should not need to use this.
			 * Note that this parameter will be set by the initialisation routine. To
			 * set a default use {@link DataTable.defaults}.
			 *  @type string
			 *  @deprecated
			 */
			"sXInner": null,
	
			/**
			 * Viewport height for vertical scrolling. Vertical scrolling is disabled
			 * if an empty string.
			 * Note that this parameter will be set by the initialisation routine. To
			 * set a default use {@link DataTable.defaults}.
			 *  @type string
			 */
			"sY": null
		},
	
		/**
		 * Language information for the table.
		 *  @namespace
		 *  @extends DataTable.defaults.oLanguage
		 */
		"oLanguage": {
			/**
			 * Information callback function. See
			 * {@link DataTable.defaults.fnInfoCallback}
			 *  @type function
			 *  @default null
			 */
			"fnInfoCallback": null
		},
	
		/**
		 * Browser support parameters
		 *  @namespace
		 */
		"oBrowser": {
			/**
			 * Indicate if the browser incorrectly calculates width:100% inside a
			 * scrolling element (IE6/7)
			 *  @type boolean
			 *  @default false
			 */
			"bScrollOversize": false,
	
			/**
			 * Determine if the vertical scrollbar is on the right or left of the
			 * scrolling container - needed for rtl language layout, although not
			 * all browsers move the scrollbar (Safari).
			 *  @type boolean
			 *  @default false
			 */
			"bScrollbarLeft": false,
	
			/**
			 * Flag for if `getBoundingClientRect` is fully supported or not
			 *  @type boolean
			 *  @default false
			 */
			"bBounding": false,
	
			/**
			 * Browser scrollbar width
			 *  @type integer
			 *  @default 0
			 */
			"barWidth": 0
		},
	
	
		"ajax": null,
	
	
		/**
		 * Array referencing the nodes which are used for the features. The
		 * parameters of this object match what is allowed by sDom - i.e.
		 *   <ul>
		 *     <li>'l' - Length changing</li>
		 *     <li>'f' - Filtering input</li>
		 *     <li>'t' - The table!</li>
		 *     <li>'i' - Information</li>
		 *     <li>'p' - Pagination</li>
		 *     <li>'r' - pRocessing</li>
		 *   </ul>
		 *  @type array
		 *  @default []
		 */
		"aanFeatures": [],
	
		/**
		 * Store data information - see {@link DataTable.models.oRow} for detailed
		 * information.
		 *  @type array
		 *  @default []
		 */
		"aoData": [],
	
		/**
		 * Array of indexes which are in the current display (after filtering etc)
		 *  @type array
		 *  @default []
		 */
		"aiDisplay": [],
	
		/**
		 * Array of indexes for display - no filtering
		 *  @type array
		 *  @default []
		 */
		"aiDisplayMaster": [],
	
		/**
		 * Map of row ids to data indexes
		 *  @type object
		 *  @default {}
		 */
		"aIds": {},
	
		/**
		 * Store information about each column that is in use
		 *  @type array
		 *  @default []
		 */
		"aoColumns": [],
	
		/**
		 * Store information about the table's header
		 *  @type array
		 *  @default []
		 */
		"aoHeader": [],
	
		/**
		 * Store information about the table's footer
		 *  @type array
		 *  @default []
		 */
		"aoFooter": [],
	
		/**
		 * Store the applied global search information in case we want to force a
		 * research or compare the old search to a new one.
		 * Note that this parameter will be set by the initialisation routine. To
		 * set a default use {@link DataTable.defaults}.
		 *  @namespace
		 *  @extends DataTable.models.oSearch
		 */
		"oPreviousSearch": {},
	
		/**
		 * Store the applied search for each column - see
		 * {@link DataTable.models.oSearch} for the format that is used for the
		 * filtering information for each column.
		 *  @type array
		 *  @default []
		 */
		"aoPreSearchCols": [],
	
		/**
		 * Sorting that is applied to the table. Note that the inner arrays are
		 * used in the following manner:
		 * <ul>
		 *   <li>Index 0 - column number</li>
		 *   <li>Index 1 - current sorting direction</li>
		 * </ul>
		 * Note that this parameter will be set by the initialisation routine. To
		 * set a default use {@link DataTable.defaults}.
		 *  @type array
		 *  @todo These inner arrays should really be objects
		 */
		"aaSorting": null,
	
		/**
		 * Sorting that is always applied to the table (i.e. prefixed in front of
		 * aaSorting).
		 * Note that this parameter will be set by the initialisation routine. To
		 * set a default use {@link DataTable.defaults}.
		 *  @type array
		 *  @default []
		 */
		"aaSortingFixed": [],
	
		/**
		 * Classes to use for the striping of a table.
		 * Note that this parameter will be set by the initialisation routine. To
		 * set a default use {@link DataTable.defaults}.
		 *  @type array
		 *  @default []
		 */
		"asStripeClasses": null,
	
		/**
		 * If restoring a table - we should restore its striping classes as well
		 *  @type array
		 *  @default []
		 */
		"asDestroyStripes": [],
	
		/**
		 * If restoring a table - we should restore its width
		 *  @type int
		 *  @default 0
		 */
		"sDestroyWidth": 0,
	
		/**
		 * Callback functions array for every time a row is inserted (i.e. on a draw).
		 *  @type array
		 *  @default []
		 */
		"aoRowCallback": [],
	
		/**
		 * Callback functions for the header on each draw.
		 *  @type array
		 *  @default []
		 */
		"aoHeaderCallback": [],
	
		/**
		 * Callback function for the footer on each draw.
		 *  @type array
		 *  @default []
		 */
		"aoFooterCallback": [],
	
		/**
		 * Array of callback functions for draw callback functions
		 *  @type array
		 *  @default []
		 */
		"aoDrawCallback": [],
	
		/**
		 * Array of callback functions for row created function
		 *  @type array
		 *  @default []
		 */
		"aoRowCreatedCallback": [],
	
		/**
		 * Callback functions for just before the table is redrawn. A return of
		 * false will be used to cancel the draw.
		 *  @type array
		 *  @default []
		 */
		"aoPreDrawCallback": [],
	
		/**
		 * Callback functions for when the table has been initialised.
		 *  @type array
		 *  @default []
		 */
		"aoInitComplete": [],
	
	
		/**
		 * Callbacks for modifying the settings to be stored for state saving, prior to
		 * saving state.
		 *  @type array
		 *  @default []
		 */
		"aoStateSaveParams": [],
	
		/**
		 * Callbacks for modifying the settings that have been stored for state saving
		 * prior to using the stored values to restore the state.
		 *  @type array
		 *  @default []
		 */
		"aoStateLoadParams": [],
	
		/**
		 * Callbacks for operating on the settings object once the saved state has been
		 * loaded
		 *  @type array
		 *  @default []
		 */
		"aoStateLoaded": [],
	
		/**
		 * Cache the table ID for quick access
		 *  @type string
		 *  @default <i>Empty string</i>
		 */
		"sTableId": "",
	
		/**
		 * The TABLE node for the main table
		 *  @type node
		 *  @default null
		 */
		"nTable": null,
	
		/**
		 * Permanent ref to the thead element
		 *  @type node
		 *  @default null
		 */
		"nTHead": null,
	
		/**
		 * Permanent ref to the tfoot element - if it exists
		 *  @type node
		 *  @default null
		 */
		"nTFoot": null,
	
		/**
		 * Permanent ref to the tbody element
		 *  @type node
		 *  @default null
		 */
		"nTBody": null,
	
		/**
		 * Cache the wrapper node (contains all DataTables controlled elements)
		 *  @type node
		 *  @default null
		 */
		"nTableWrapper": null,
	
		/**
		 * Indicate if when using server-side processing the loading of data
		 * should be deferred until the second draw.
		 * Note that this parameter will be set by the initialisation routine. To
		 * set a default use {@link DataTable.defaults}.
		 *  @type boolean
		 *  @default false
		 */
		"bDeferLoading": false,
	
		/**
		 * Indicate if all required information has been read in
		 *  @type boolean
		 *  @default false
		 */
		"bInitialised": false,
	
		/**
		 * Information about open rows. Each object in the array has the parameters
		 * 'nTr' and 'nParent'
		 *  @type array
		 *  @default []
		 */
		"aoOpenRows": [],
	
		/**
		 * Dictate the positioning of DataTables' control elements - see
		 * {@link DataTable.model.oInit.sDom}.
		 * Note that this parameter will be set by the initialisation routine. To
		 * set a default use {@link DataTable.defaults}.
		 *  @type string
		 *  @default null
		 */
		"sDom": null,
	
		/**
		 * Search delay (in mS)
		 *  @type integer
		 *  @default null
		 */
		"searchDelay": null,
	
		/**
		 * Which type of pagination should be used.
		 * Note that this parameter will be set by the initialisation routine. To
		 * set a default use {@link DataTable.defaults}.
		 *  @type string
		 *  @default two_button
		 */
		"sPaginationType": "two_button",
	
		/**
		 * The state duration (for `stateSave`) in seconds.
		 * Note that this parameter will be set by the initialisation routine. To
		 * set a default use {@link DataTable.defaults}.
		 *  @type int
		 *  @default 0
		 */
		"iStateDuration": 0,
	
		/**
		 * Array of callback functions for state saving. Each array element is an
		 * object with the following parameters:
		 *   <ul>
		 *     <li>function:fn - function to call. Takes two parameters, oSettings
		 *       and the JSON string to save that has been thus far created. Returns
		 *       a JSON string to be inserted into a json object
		 *       (i.e. '"param": [ 0, 1, 2]')</li>
		 *     <li>string:sName - name of callback</li>
		 *   </ul>
		 *  @type array
		 *  @default []
		 */
		"aoStateSave": [],
	
		/**
		 * Array of callback functions for state loading. Each array element is an
		 * object with the following parameters:
		 *   <ul>
		 *     <li>function:fn - function to call. Takes two parameters, oSettings
		 *       and the object stored. May return false to cancel state loading</li>
		 *     <li>string:sName - name of callback</li>
		 *   </ul>
		 *  @type array
		 *  @default []
		 */
		"aoStateLoad": [],
	
		/**
		 * State that was saved. Useful for back reference
		 *  @type object
		 *  @default null
		 */
		"oSavedState": null,
	
		/**
		 * State that was loaded. Useful for back reference
		 *  @type object
		 *  @default null
		 */
		"oLoadedState": null,
	
		/**
		 * Source url for AJAX data for the table.
		 * Note that this parameter will be set by the initialisation routine. To
		 * set a default use {@link DataTable.defaults}.
		 *  @type string
		 *  @default null
		 */
		"sAjaxSource": null,
	
		/**
		 * Property from a given object from which to read the table data from. This
		 * can be an empty string (when not server-side processing), in which case
		 * it is  assumed an an array is given directly.
		 * Note that this parameter will be set by the initialisation routine. To
		 * set a default use {@link DataTable.defaults}.
		 *  @type string
		 */
		"sAjaxDataProp": null,
	
		/**
		 * Note if draw should be blocked while getting data
		 *  @type boolean
		 *  @default true
		 */
		"bAjaxDataGet": true,
	
		/**
		 * The last jQuery XHR object that was used for server-side data gathering.
		 * This can be used for working with the XHR information in one of the
		 * callbacks
		 *  @type object
		 *  @default null
		 */
		"jqXHR": null,
	
		/**
		 * JSON returned from the server in the last Ajax request
		 *  @type object
		 *  @default undefined
		 */
		"json": undefined,
	
		/**
		 * Data submitted as part of the last Ajax request
		 *  @type object
		 *  @default undefined
		 */
		"oAjaxData": undefined,
	
		/**
		 * Function to get the server-side data.
		 * Note that this parameter will be set by the initialisation routine. To
		 * set a default use {@link DataTable.defaults}.
		 *  @type function
		 */
		"fnServerData": null,
	
		/**
		 * Functions which are called prior to sending an Ajax request so extra
		 * parameters can easily be sent to the server
		 *  @type array
		 *  @default []
		 */
		"aoServerParams": [],
	
		/**
		 * Send the XHR HTTP method - GET or POST (could be PUT or DELETE if
		 * required).
		 * Note that this parameter will be set by the initialisation routine. To
		 * set a default use {@link DataTable.defaults}.
		 *  @type string
		 */
		"sServerMethod": null,
	
		/**
		 * Format numbers for display.
		 * Note that this parameter will be set by the initialisation routine. To
		 * set a default use {@link DataTable.defaults}.
		 *  @type function
		 */
		"fnFormatNumber": null,
	
		/**
		 * List of options that can be used for the user selectable length menu.
		 * Note that this parameter will be set by the initialisation routine. To
		 * set a default use {@link DataTable.defaults}.
		 *  @type array
		 *  @default []
		 */
		"aLengthMenu": null,
	
		/**
		 * Counter for the draws that the table does. Also used as a tracker for
		 * server-side processing
		 *  @type int
		 *  @default 0
		 */
		"iDraw": 0,
	
		/**
		 * Indicate if a redraw is being done - useful for Ajax
		 *  @type boolean
		 *  @default false
		 */
		"bDrawing": false,
	
		/**
		 * Draw index (iDraw) of the last error when parsing the returned data
		 *  @type int
		 *  @default -1
		 */
		"iDrawError": -1,
	
		/**
		 * Paging display length
		 *  @type int
		 *  @default 10
		 */
		"_iDisplayLength": 10,
	
		/**
		 * Paging start point - aiDisplay index
		 *  @type int
		 *  @default 0
		 */
		"_iDisplayStart": 0,
	
		/**
		 * Server-side processing - number of records in the result set
		 * (i.e. before filtering), Use fnRecordsTotal rather than
		 * this property to get the value of the number of records, regardless of
		 * the server-side processing setting.
		 *  @type int
		 *  @default 0
		 *  @private
		 */
		"_iRecordsTotal": 0,
	
		/**
		 * Server-side processing - number of records in the current display set
		 * (i.e. after filtering). Use fnRecordsDisplay rather than
		 * this property to get the value of the number of records, regardless of
		 * the server-side processing setting.
		 *  @type boolean
		 *  @default 0
		 *  @private
		 */
		"_iRecordsDisplay": 0,
	
		/**
		 * Flag to indicate if jQuery UI marking and classes should be used.
		 * Note that this parameter will be set by the initialisation routine. To
		 * set a default use {@link DataTable.defaults}.
		 *  @type boolean
		 */
		"bJUI": null,
	
		/**
		 * The classes to use for the table
		 *  @type object
		 *  @default {}
		 */
		"oClasses": {},
	
		/**
		 * Flag attached to the settings object so you can check in the draw
		 * callback if filtering has been done in the draw. Deprecated in favour of
		 * events.
		 *  @type boolean
		 *  @default false
		 *  @deprecated
		 */
		"bFiltered": false,
	
		/**
		 * Flag attached to the settings object so you can check in the draw
		 * callback if sorting has been done in the draw. Deprecated in favour of
		 * events.
		 *  @type boolean
		 *  @default false
		 *  @deprecated
		 */
		"bSorted": false,
	
		/**
		 * Indicate that if multiple rows are in the header and there is more than
		 * one unique cell per column, if the top one (true) or bottom one (false)
		 * should be used for sorting / title by DataTables.
		 * Note that this parameter will be set by the initialisation routine. To
		 * set a default use {@link DataTable.defaults}.
		 *  @type boolean
		 */
		"bSortCellsTop": null,
	
		/**
		 * Initialisation object that is used for the table
		 *  @type object
		 *  @default null
		 */
		"oInit": null,
	
		/**
		 * Destroy callback functions - for plug-ins to attach themselves to the
		 * destroy so they can clean up markup and events.
		 *  @type array
		 *  @default []
		 */
		"aoDestroyCallback": [],
	
	
		/**
		 * Get the number of records in the current record set, before filtering
		 *  @type function
		 */
		"fnRecordsTotal": function ()
		{
			return _fnDataSource( this ) == 'ssp' ?
				this._iRecordsTotal * 1 :
				this.aiDisplayMaster.length;
		},
	
		/**
		 * Get the number of records in the current record set, after filtering
		 *  @type function
		 */
		"fnRecordsDisplay": function ()
		{
			return _fnDataSource( this ) == 'ssp' ?
				this._iRecordsDisplay * 1 :
				this.aiDisplay.length;
		},
	
		/**
		 * Get the display end point - aiDisplay index
		 *  @type function
		 */
		"fnDisplayEnd": function ()
		{
			var
				len      = this._iDisplayLength,
				start    = this._iDisplayStart,
				calc     = start + len,
				records  = this.aiDisplay.length,
				features = this.oFeatures,
				paginate = features.bPaginate;
	
			if ( features.bServerSide ) {
				return paginate === false || len === -1 ?
					start + records :
					Math.min( start+len, this._iRecordsDisplay );
			}
			else {
				return ! paginate || calc>records || len===-1 ?
					records :
					calc;
			}
		},
	
		/**
		 * The DataTables object for this table
		 *  @type object
		 *  @default null
		 */
		"oInstance": null,
	
		/**
		 * Unique identifier for each instance of the DataTables object. If there
		 * is an ID on the table node, then it takes that value, otherwise an
		 * incrementing internal counter is used.
		 *  @type string
		 *  @default null
		 */
		"sInstance": null,
	
		/**
		 * tabindex attribute value that is added to DataTables control elements, allowing
		 * keyboard navigation of the table and its controls.
		 */
		"iTabIndex": 0,
	
		/**
		 * DIV container for the footer scrolling table if scrolling
		 */
		"nScrollHead": null,
	
		/**
		 * DIV container for the footer scrolling table if scrolling
		 */
		"nScrollFoot": null,
	
		/**
		 * Last applied sort
		 *  @type array
		 *  @default []
		 */
		"aLastSort": [],
	
		/**
		 * Stored plug-in instances
		 *  @type object
		 *  @default {}
		 */
		"oPlugins": {},
	
		/**
		 * Function used to get a row's id from the row's data
		 *  @type function
		 *  @default null
		 */
		"rowIdFn": null,
	
		/**
		 * Data location where to store a row's id
		 *  @type string
		 *  @default null
		 */
		"rowId": null
	};

	/**
	 * Extension object for DataTables that is used to provide all extension
	 * options.
	 *
	 * Note that the `DataTable.ext` object is available through
	 * `jQuery.fn.dataTable.ext` where it may be accessed and manipulated. It is
	 * also aliased to `jQuery.fn.dataTableExt` for historic reasons.
	 *  @namespace
	 *  @extends DataTable.models.ext
	 */
	
	
	/**
	 * DataTables extensions
	 * 
	 * This namespace acts as a collection area for plug-ins that can be used to
	 * extend DataTables capabilities. Indeed many of the build in methods
	 * use this method to provide their own capabilities (sorting methods for
	 * example).
	 *
	 * Note that this namespace is aliased to `jQuery.fn.dataTableExt` for legacy
	 * reasons
	 *
	 *  @namespace
	 */
	DataTable.ext = _ext = {
		/**
		 * Buttons. For use with the Buttons extension for DataTables. This is
		 * defined here so other extensions can define buttons regardless of load
		 * order. It is _not_ used by DataTables core.
		 *
		 *  @type object
		 *  @default {}
		 */
		buttons: {},
	
	
		/**
		 * Element class names
		 *
		 *  @type object
		 *  @default {}
		 */
		classes: {},
	
	
		/**
		 * DataTables build type (expanded by the download builder)
		 *
		 *  @type string
		 */
		builder: "-source-",
	
	
		/**
		 * Error reporting.
		 * 
		 * How should DataTables report an error. Can take the value 'alert',
		 * 'throw', 'none' or a function.
		 *
		 *  @type string|function
		 *  @default alert
		 */
		errMode: "alert",
	
	
		/**
		 * Feature plug-ins.
		 * 
		 * This is an array of objects which describe the feature plug-ins that are
		 * available to DataTables. These feature plug-ins are then available for
		 * use through the `dom` initialisation option.
		 * 
		 * Each feature plug-in is described by an object which must have the
		 * following properties:
		 * 
		 * * `fnInit` - function that is used to initialise the plug-in,
		 * * `cFeature` - a character so the feature can be enabled by the `dom`
		 *   instillation option. This is case sensitive.
		 *
		 * The `fnInit` function has the following input parameters:
		 *
		 * 1. `{object}` DataTables settings object: see
		 *    {@link DataTable.models.oSettings}
		 *
		 * And the following return is expected:
		 * 
		 * * {node|null} The element which contains your feature. Note that the
		 *   return may also be void if your plug-in does not require to inject any
		 *   DOM elements into DataTables control (`dom`) - for example this might
		 *   be useful when developing a plug-in which allows table control via
		 *   keyboard entry
		 *
		 *  @type array
		 *
		 *  @example
		 *    $.fn.dataTable.ext.features.push( {
		 *      "fnInit": function( oSettings ) {
		 *        return new TableTools( { "oDTSettings": oSettings } );
		 *      },
		 *      "cFeature": "T"
		 *    } );
		 */
		feature: [],
	
	
		/**
		 * Row searching.
		 * 
		 * This method of searching is complimentary to the default type based
		 * searching, and a lot more comprehensive as it allows you complete control
		 * over the searching logic. Each element in this array is a function
		 * (parameters described below) that is called for every row in the table,
		 * and your logic decides if it should be included in the searching data set
		 * or not.
		 *
		 * Searching functions have the following input parameters:
		 *
		 * 1. `{object}` DataTables settings object: see
		 *    {@link DataTable.models.oSettings}
		 * 2. `{array|object}` Data for the row to be processed (same as the
		 *    original format that was passed in as the data source, or an array
		 *    from a DOM data source
		 * 3. `{int}` Row index ({@link DataTable.models.oSettings.aoData}), which
		 *    can be useful to retrieve the `TR` element if you need DOM interaction.
		 *
		 * And the following return is expected:
		 *
		 * * {boolean} Include the row in the searched result set (true) or not
		 *   (false)
		 *
		 * Note that as with the main search ability in DataTables, technically this
		 * is "filtering", since it is subtractive. However, for consistency in
		 * naming we call it searching here.
		 *
		 *  @type array
		 *  @default []
		 *
		 *  @example
		 *    // The following example shows custom search being applied to the
		 *    // fourth column (i.e. the data[3] index) based on two input values
		 *    // from the end-user, matching the data in a certain range.
		 *    $.fn.dataTable.ext.search.push(
		 *      function( settings, data, dataIndex ) {
		 *        var min = document.getElementById('min').value * 1;
		 *        var max = document.getElementById('max').value * 1;
		 *        var version = data[3] == "-" ? 0 : data[3]*1;
		 *
		 *        if ( min == "" && max == "" ) {
		 *          return true;
		 *        }
		 *        else if ( min == "" && version < max ) {
		 *          return true;
		 *        }
		 *        else if ( min < version && "" == max ) {
		 *          return true;
		 *        }
		 *        else if ( min < version && version < max ) {
		 *          return true;
		 *        }
		 *        return false;
		 *      }
		 *    );
		 */
		search: [],
	
	
		/**
		 * Selector extensions
		 *
		 * The `selector` option can be used to extend the options available for the
		 * selector modifier options (`selector-modifier` object data type) that
		 * each of the three built in selector types offer (row, column and cell +
		 * their plural counterparts). For example the Select extension uses this
		 * mechanism to provide an option to select only rows, columns and cells
		 * that have been marked as selected by the end user (`{selected: true}`),
		 * which can be used in conjunction with the existing built in selector
		 * options.
		 *
		 * Each property is an array to which functions can be pushed. The functions
		 * take three attributes:
		 *
		 * * Settings object for the host table
		 * * Options object (`selector-modifier` object type)
		 * * Array of selected item indexes
		 *
		 * The return is an array of the resulting item indexes after the custom
		 * selector has been applied.
		 *
		 *  @type object
		 */
		selector: {
			cell: [],
			column: [],
			row: []
		},
	
	
		/**
		 * Internal functions, exposed for used in plug-ins.
		 * 
		 * Please note that you should not need to use the internal methods for
		 * anything other than a plug-in (and even then, try to avoid if possible).
		 * The internal function may change between releases.
		 *
		 *  @type object
		 *  @default {}
		 */
		internal: {},
	
	
		/**
		 * Legacy configuration options. Enable and disable legacy options that
		 * are available in DataTables.
		 *
		 *  @type object
		 */
		legacy: {
			/**
			 * Enable / disable DataTables 1.9 compatible server-side processing
			 * requests
			 *
			 *  @type boolean
			 *  @default null
			 */
			ajax: null
		},
	
	
		/**
		 * Pagination plug-in methods.
		 * 
		 * Each entry in this object is a function and defines which buttons should
		 * be shown by the pagination rendering method that is used for the table:
		 * {@link DataTable.ext.renderer.pageButton}. The renderer addresses how the
		 * buttons are displayed in the document, while the functions here tell it
		 * what buttons to display. This is done by returning an array of button
		 * descriptions (what each button will do).
		 *
		 * Pagination types (the four built in options and any additional plug-in
		 * options defined here) can be used through the `paginationType`
		 * initialisation parameter.
		 *
		 * The functions defined take two parameters:
		 *
		 * 1. `{int} page` The current page index
		 * 2. `{int} pages` The number of pages in the table
		 *
		 * Each function is expected to return an array where each element of the
		 * array can be one of:
		 *
		 * * `first` - Jump to first page when activated
		 * * `last` - Jump to last page when activated
		 * * `previous` - Show previous page when activated
		 * * `next` - Show next page when activated
		 * * `{int}` - Show page of the index given
		 * * `{array}` - A nested array containing the above elements to add a
		 *   containing 'DIV' element (might be useful for styling).
		 *
		 * Note that DataTables v1.9- used this object slightly differently whereby
		 * an object with two functions would be defined for each plug-in. That
		 * ability is still supported by DataTables 1.10+ to provide backwards
		 * compatibility, but this option of use is now decremented and no longer
		 * documented in DataTables 1.10+.
		 *
		 *  @type object
		 *  @default {}
		 *
		 *  @example
		 *    // Show previous, next and current page buttons only
		 *    $.fn.dataTableExt.oPagination.current = function ( page, pages ) {
		 *      return [ 'previous', page, 'next' ];
		 *    };
		 */
		pager: {},
	
	
		renderer: {
			pageButton: {},
			header: {}
		},
	
	
		/**
		 * Ordering plug-ins - custom data source
		 * 
		 * The extension options for ordering of data available here is complimentary
		 * to the default type based ordering that DataTables typically uses. It
		 * allows much greater control over the the data that is being used to
		 * order a column, but is necessarily therefore more complex.
		 * 
		 * This type of ordering is useful if you want to do ordering based on data
		 * live from the DOM (for example the contents of an 'input' element) rather
		 * than just the static string that DataTables knows of.
		 * 
		 * The way these plug-ins work is that you create an array of the values you
		 * wish to be ordering for the column in question and then return that
		 * array. The data in the array much be in the index order of the rows in
		 * the table (not the currently ordering order!). Which order data gathering
		 * function is run here depends on the `dt-init columns.orderDataType`
		 * parameter that is used for the column (if any).
		 *
		 * The functions defined take two parameters:
		 *
		 * 1. `{object}` DataTables settings object: see
		 *    {@link DataTable.models.oSettings}
		 * 2. `{int}` Target column index
		 *
		 * Each function is expected to return an array:
		 *
		 * * `{array}` Data for the column to be ordering upon
		 *
		 *  @type array
		 *
		 *  @example
		 *    // Ordering using `input` node values
		 *    $.fn.dataTable.ext.order['dom-text'] = function  ( settings, col )
		 *    {
		 *      return this.api().column( col, {order:'index'} ).nodes().map( function ( td, i ) {
		 *        return $('input', td).val();
		 *      } );
		 *    }
		 */
		order: {},
	
	
		/**
		 * Type based plug-ins.
		 *
		 * Each column in DataTables has a type assigned to it, either by automatic
		 * detection or by direct assignment using the `type` option for the column.
		 * The type of a column will effect how it is ordering and search (plug-ins
		 * can also make use of the column type if required).
		 *
		 * @namespace
		 */
		type: {
			/**
			 * Type detection functions.
			 *
			 * The functions defined in this object are used to automatically detect
			 * a column's type, making initialisation of DataTables super easy, even
			 * when complex data is in the table.
			 *
			 * The functions defined take two parameters:
			 *
		     *  1. `{*}` Data from the column cell to be analysed
		     *  2. `{settings}` DataTables settings object. This can be used to
		     *     perform context specific type detection - for example detection
		     *     based on language settings such as using a comma for a decimal
		     *     place. Generally speaking the options from the settings will not
		     *     be required
			 *
			 * Each function is expected to return:
			 *
			 * * `{string|null}` Data type detected, or null if unknown (and thus
			 *   pass it on to the other type detection functions.
			 *
			 *  @type array
			 *
			 *  @example
			 *    // Currency type detection plug-in:
			 *    $.fn.dataTable.ext.type.detect.push(
			 *      function ( data, settings ) {
			 *        // Check the numeric part
			 *        if ( ! $.isNumeric( data.substring(1) ) ) {
			 *          return null;
			 *        }
			 *
			 *        // Check prefixed by currency
			 *        if ( data.charAt(0) == '$' || data.charAt(0) == '&pound;' ) {
			 *          return 'currency';
			 *        }
			 *        return null;
			 *      }
			 *    );
			 */
			detect: [],
	
	
			/**
			 * Type based search formatting.
			 *
			 * The type based searching functions can be used to pre-format the
			 * data to be search on. For example, it can be used to strip HTML
			 * tags or to de-format telephone numbers for numeric only searching.
			 *
			 * Note that is a search is not defined for a column of a given type,
			 * no search formatting will be performed.
			 * 
			 * Pre-processing of searching data plug-ins - When you assign the sType
			 * for a column (or have it automatically detected for you by DataTables
			 * or a type detection plug-in), you will typically be using this for
			 * custom sorting, but it can also be used to provide custom searching
			 * by allowing you to pre-processing the data and returning the data in
			 * the format that should be searched upon. This is done by adding
			 * functions this object with a parameter name which matches the sType
			 * for that target column. This is the corollary of <i>afnSortData</i>
			 * for searching data.
			 *
			 * The functions defined take a single parameter:
			 *
		     *  1. `{*}` Data from the column cell to be prepared for searching
			 *
			 * Each function is expected to return:
			 *
			 * * `{string|null}` Formatted string that will be used for the searching.
			 *
			 *  @type object
			 *  @default {}
			 *
			 *  @example
			 *    $.fn.dataTable.ext.type.search['title-numeric'] = function ( d ) {
			 *      return d.replace(/\n/g," ").replace( /<.*?>/g, "" );
			 *    }
			 */
			search: {},
	
	
			/**
			 * Type based ordering.
			 *
			 * The column type tells DataTables what ordering to apply to the table
			 * when a column is sorted upon. The order for each type that is defined,
			 * is defined by the functions available in this object.
			 *
			 * Each ordering option can be described by three properties added to
			 * this object:
			 *
			 * * `{type}-pre` - Pre-formatting function
			 * * `{type}-asc` - Ascending order function
			 * * `{type}-desc` - Descending order function
			 *
			 * All three can be used together, only `{type}-pre` or only
			 * `{type}-asc` and `{type}-desc` together. It is generally recommended
			 * that only `{type}-pre` is used, as this provides the optimal
			 * implementation in terms of speed, although the others are provided
			 * for compatibility with existing Javascript sort functions.
			 *
			 * `{type}-pre`: Functions defined take a single parameter:
			 *
		     *  1. `{*}` Data from the column cell to be prepared for ordering
			 *
			 * And return:
			 *
			 * * `{*}` Data to be sorted upon
			 *
			 * `{type}-asc` and `{type}-desc`: Functions are typical Javascript sort
			 * functions, taking two parameters:
			 *
		     *  1. `{*}` Data to compare to the second parameter
		     *  2. `{*}` Data to compare to the first parameter
			 *
			 * And returning:
			 *
			 * * `{*}` Ordering match: <0 if first parameter should be sorted lower
			 *   than the second parameter, ===0 if the two parameters are equal and
			 *   >0 if the first parameter should be sorted height than the second
			 *   parameter.
			 * 
			 *  @type object
			 *  @default {}
			 *
			 *  @example
			 *    // Numeric ordering of formatted numbers with a pre-formatter
			 *    $.extend( $.fn.dataTable.ext.type.order, {
			 *      "string-pre": function(x) {
			 *        a = (a === "-" || a === "") ? 0 : a.replace( /[^\d\-\.]/g, "" );
			 *        return parseFloat( a );
			 *      }
			 *    } );
			 *
			 *  @example
			 *    // Case-sensitive string ordering, with no pre-formatting method
			 *    $.extend( $.fn.dataTable.ext.order, {
			 *      "string-case-asc": function(x,y) {
			 *        return ((x < y) ? -1 : ((x > y) ? 1 : 0));
			 *      },
			 *      "string-case-desc": function(x,y) {
			 *        return ((x < y) ? 1 : ((x > y) ? -1 : 0));
			 *      }
			 *    } );
			 */
			order: {}
		},
	
		/**
		 * Unique DataTables instance counter
		 *
		 * @type int
		 * @private
		 */
		_unique: 0,
	
	
		//
		// Depreciated
		// The following properties are retained for backwards compatiblity only.
		// The should not be used in new projects and will be removed in a future
		// version
		//
	
		/**
		 * Version check function.
		 *  @type function
		 *  @depreciated Since 1.10
		 */
		fnVersionCheck: DataTable.fnVersionCheck,
	
	
		/**
		 * Index for what 'this' index API functions should use
		 *  @type int
		 *  @deprecated Since v1.10
		 */
		iApiIndex: 0,
	
	
		/**
		 * jQuery UI class container
		 *  @type object
		 *  @deprecated Since v1.10
		 */
		oJUIClasses: {},
	
	
		/**
		 * Software version
		 *  @type string
		 *  @deprecated Since v1.10
		 */
		sVersion: DataTable.version
	};
	
	
	//
	// Backwards compatibility. Alias to pre 1.10 Hungarian notation counter parts
	//
	$.extend( _ext, {
		afnFiltering: _ext.search,
		aTypes:       _ext.type.detect,
		ofnSearch:    _ext.type.search,
		oSort:        _ext.type.order,
		afnSortData:  _ext.order,
		aoFeatures:   _ext.feature,
		oApi:         _ext.internal,
		oStdClasses:  _ext.classes,
		oPagination:  _ext.pager
	} );
	
	
	$.extend( DataTable.ext.classes, {
		"sTable": "dataTable",
		"sNoFooter": "no-footer",
	
		/* Paging buttons */
		"sPageButton": "paginate_button",
		"sPageButtonActive": "current",
		"sPageButtonDisabled": "disabled",
	
		/* Striping classes */
		"sStripeOdd": "odd",
		"sStripeEven": "even",
	
		/* Empty row */
		"sRowEmpty": "dataTables_empty",
	
		/* Features */
		"sWrapper": "dataTables_wrapper",
		"sFilter": "dataTables_filter",
		"sInfo": "dataTables_info",
		"sPaging": "dataTables_paginate paging_", /* Note that the type is postfixed */
		"sLength": "dataTables_length",
		"sProcessing": "dataTables_processing",
	
		/* Sorting */
		"sSortAsc": "sorting_asc",
		"sSortDesc": "sorting_desc",
		"sSortable": "sorting", /* Sortable in both directions */
		"sSortableAsc": "sorting_asc_disabled",
		"sSortableDesc": "sorting_desc_disabled",
		"sSortableNone": "sorting_disabled",
		"sSortColumn": "sorting_", /* Note that an int is postfixed for the sorting order */
	
		/* Filtering */
		"sFilterInput": "",
	
		/* Page length */
		"sLengthSelect": "",
	
		/* Scrolling */
		"sScrollWrapper": "dataTables_scroll",
		"sScrollHead": "dataTables_scrollHead",
		"sScrollHeadInner": "dataTables_scrollHeadInner",
		"sScrollBody": "dataTables_scrollBody",
		"sScrollFoot": "dataTables_scrollFoot",
		"sScrollFootInner": "dataTables_scrollFootInner",
	
		/* Misc */
		"sHeaderTH": "",
		"sFooterTH": "",
	
		// Deprecated
		"sSortJUIAsc": "",
		"sSortJUIDesc": "",
		"sSortJUI": "",
		"sSortJUIAscAllowed": "",
		"sSortJUIDescAllowed": "",
		"sSortJUIWrapper": "",
		"sSortIcon": "",
		"sJUIHeader": "",
		"sJUIFooter": ""
	} );
	
	
	(function() {
	
	// Reused strings for better compression. Closure compiler appears to have a
	// weird edge case where it is trying to expand strings rather than use the
	// variable version. This results in about 200 bytes being added, for very
	// little preference benefit since it this run on script load only.
	var _empty = '';
	_empty = '';
	
	var _stateDefault = _empty + 'ui-state-default';
	var _sortIcon     = _empty + 'css_right ui-icon ui-icon-';
	var _headerFooter = _empty + 'fg-toolbar ui-toolbar ui-widget-header ui-helper-clearfix';
	
	$.extend( DataTable.ext.oJUIClasses, DataTable.ext.classes, {
		/* Full numbers paging buttons */
		"sPageButton":         "fg-button ui-button "+_stateDefault,
		"sPageButtonActive":   "ui-state-disabled",
		"sPageButtonDisabled": "ui-state-disabled",
	
		/* Features */
		"sPaging": "dataTables_paginate fg-buttonset ui-buttonset fg-buttonset-multi "+
			"ui-buttonset-multi paging_", /* Note that the type is postfixed */
	
		/* Sorting */
		"sSortAsc":            _stateDefault+" sorting_asc",
		"sSortDesc":           _stateDefault+" sorting_desc",
		"sSortable":           _stateDefault+" sorting",
		"sSortableAsc":        _stateDefault+" sorting_asc_disabled",
		"sSortableDesc":       _stateDefault+" sorting_desc_disabled",
		"sSortableNone":       _stateDefault+" sorting_disabled",
		"sSortJUIAsc":         _sortIcon+"triangle-1-n",
		"sSortJUIDesc":        _sortIcon+"triangle-1-s",
		"sSortJUI":            _sortIcon+"carat-2-n-s",
		"sSortJUIAscAllowed":  _sortIcon+"carat-1-n",
		"sSortJUIDescAllowed": _sortIcon+"carat-1-s",
		"sSortJUIWrapper":     "DataTables_sort_wrapper",
		"sSortIcon":           "DataTables_sort_icon",
	
		/* Scrolling */
		"sScrollHead": "dataTables_scrollHead "+_stateDefault,
		"sScrollFoot": "dataTables_scrollFoot "+_stateDefault,
	
		/* Misc */
		"sHeaderTH":  _stateDefault,
		"sFooterTH":  _stateDefault,
		"sJUIHeader": _headerFooter+" ui-corner-tl ui-corner-tr",
		"sJUIFooter": _headerFooter+" ui-corner-bl ui-corner-br"
	} );
	
	}());
	
	
	
	var extPagination = DataTable.ext.pager;
	
	function _numbers ( page, pages ) {
		var
			numbers = [],
			buttons = extPagination.numbers_length,
			half = Math.floor( buttons / 2 ),
			i = 1;
	
		if ( pages <= buttons ) {
			numbers = _range( 0, pages );
		}
		else if ( page <= half ) {
			numbers = _range( 0, buttons-2 );
			numbers.push( 'ellipsis' );
			numbers.push( pages-1 );
		}
		else if ( page >= pages - 1 - half ) {
			numbers = _range( pages-(buttons-2), pages );
			numbers.splice( 0, 0, 'ellipsis' ); // no unshift in ie6
			numbers.splice( 0, 0, 0 );
		}
		else {
			numbers = _range( page-half+2, page+half-1 );
			numbers.push( 'ellipsis' );
			numbers.push( pages-1 );
			numbers.splice( 0, 0, 'ellipsis' );
			numbers.splice( 0, 0, 0 );
		}
	
		numbers.DT_el = 'span';
		return numbers;
	}
	
	
	$.extend( extPagination, {
		simple: function ( page, pages ) {
			return [ 'previous', 'next' ];
		},
	
		full: function ( page, pages ) {
			return [  'first', 'previous', 'next', 'last' ];
		},
	
		numbers: function ( page, pages ) {
			return [ _numbers(page, pages) ];
		},
	
		simple_numbers: function ( page, pages ) {
			return [ 'previous', _numbers(page, pages), 'next' ];
		},
	
		full_numbers: function ( page, pages ) {
			return [ 'first', 'previous', _numbers(page, pages), 'next', 'last' ];
		},
	
		// For testing and plug-ins to use
		_numbers: _numbers,
	
		// Number of number buttons (including ellipsis) to show. _Must be odd!_
		numbers_length: 7
	} );
	
	
	$.extend( true, DataTable.ext.renderer, {
		pageButton: {
			_: function ( settings, host, idx, buttons, page, pages ) {
				var classes = settings.oClasses;
				var lang = settings.oLanguage.oPaginate;
				var aria = settings.oLanguage.oAria.paginate || {};
				var btnDisplay, btnClass, counter=0;
	
				var attach = function( container, buttons ) {
					var i, ien, node, button;
					var clickHandler = function ( e ) {
						_fnPageChange( settings, e.data.action, true );
					};
	
					for ( i=0, ien=buttons.length ; i<ien ; i++ ) {
						button = buttons[i];
	
						if ( $.isArray( button ) ) {
							var inner = $( '<'+(button.DT_el || 'div')+'/>' )
								.appendTo( container );
							attach( inner, button );
						}
						else {
							btnDisplay = null;
							btnClass = '';
	
							switch ( button ) {
								case 'ellipsis':
									container.append('<span class="ellipsis">&#x2026;</span>');
									break;
	
								case 'first':
									btnDisplay = lang.sFirst;
									btnClass = button + (page > 0 ?
										'' : ' '+classes.sPageButtonDisabled);
									break;
	
								case 'previous':
									btnDisplay = lang.sPrevious;
									btnClass = button + (page > 0 ?
										'' : ' '+classes.sPageButtonDisabled);
									break;
	
								case 'next':
									btnDisplay = lang.sNext;
									btnClass = button + (page < pages-1 ?
										'' : ' '+classes.sPageButtonDisabled);
									break;
	
								case 'last':
									btnDisplay = lang.sLast;
									btnClass = button + (page < pages-1 ?
										'' : ' '+classes.sPageButtonDisabled);
									break;
	
								default:
									btnDisplay = button + 1;
									btnClass = page === button ?
										classes.sPageButtonActive : '';
									break;
							}
	
							if ( btnDisplay !== null ) {
								node = $('<a>', {
										'class': classes.sPageButton+' '+btnClass,
										'aria-controls': settings.sTableId,
										'aria-label': aria[ button ],
										'data-dt-idx': counter,
										'tabindex': settings.iTabIndex,
										'id': idx === 0 && typeof button === 'string' ?
											settings.sTableId +'_'+ button :
											null
									} )
									.html( btnDisplay )
									.appendTo( container );
	
								_fnBindAction(
									node, {action: button}, clickHandler
								);
	
								counter++;
							}
						}
					}
				};
	
				// IE9 throws an 'unknown error' if document.activeElement is used
				// inside an iframe or frame. Try / catch the error. Not good for
				// accessibility, but neither are frames.
				var activeEl;
	
				try {
					// Because this approach is destroying and recreating the paging
					// elements, focus is lost on the select button which is bad for
					// accessibility. So we want to restore focus once the draw has
					// completed
					activeEl = $(host).find(document.activeElement).data('dt-idx');
				}
				catch (e) {}
	
				attach( $(host).empty(), buttons );
	
				if ( activeEl ) {
					$(host).find( '[data-dt-idx='+activeEl+']' ).focus();
				}
			}
		}
	} );
	
	
	
	// Built in type detection. See model.ext.aTypes for information about
	// what is required from this methods.
	$.extend( DataTable.ext.type.detect, [
		// Plain numbers - first since V8 detects some plain numbers as dates
		// e.g. Date.parse('55') (but not all, e.g. Date.parse('22')...).
		function ( d, settings )
		{
			var decimal = settings.oLanguage.sDecimal;
			return _isNumber( d, decimal ) ? 'num'+decimal : null;
		},
	
		// Dates (only those recognised by the browser's Date.parse)
		function ( d, settings )
		{
			// V8 will remove any unknown characters at the start and end of the
			// expression, leading to false matches such as `$245.12` or `10%` being
			// a valid date. See forum thread 18941 for detail.
			if ( d && !(d instanceof Date) && ( ! _re_date_start.test(d) || ! _re_date_end.test(d) ) ) {
				return null;
			}
			var parsed = Date.parse(d);
			return (parsed !== null && !isNaN(parsed)) || _empty(d) ? 'date' : null;
		},
	
		// Formatted numbers
		function ( d, settings )
		{
			var decimal = settings.oLanguage.sDecimal;
			return _isNumber( d, decimal, true ) ? 'num-fmt'+decimal : null;
		},
	
		// HTML numeric
		function ( d, settings )
		{
			var decimal = settings.oLanguage.sDecimal;
			return _htmlNumeric( d, decimal ) ? 'html-num'+decimal : null;
		},
	
		// HTML numeric, formatted
		function ( d, settings )
		{
			var decimal = settings.oLanguage.sDecimal;
			return _htmlNumeric( d, decimal, true ) ? 'html-num-fmt'+decimal : null;
		},
	
		// HTML (this is strict checking - there must be html)
		function ( d, settings )
		{
			return _empty( d ) || (typeof d === 'string' && d.indexOf('<') !== -1) ?
				'html' : null;
		}
	] );
	
	
	
	// Filter formatting functions. See model.ext.ofnSearch for information about
	// what is required from these methods.
	// 
	// Note that additional search methods are added for the html numbers and
	// html formatted numbers by `_addNumericSort()` when we know what the decimal
	// place is
	
	
	$.extend( DataTable.ext.type.search, {
		html: function ( data ) {
			return _empty(data) ?
				data :
				typeof data === 'string' ?
					data
						.replace( _re_new_lines, " " )
						.replace( _re_html, "" ) :
					'';
		},
	
		string: function ( data ) {
			return _empty(data) ?
				data :
				typeof data === 'string' ?
					data.replace( _re_new_lines, " " ) :
					data;
		}
	} );
	
	
	
	var __numericReplace = function ( d, decimalPlace, re1, re2 ) {
		if ( d !== 0 && (!d || d === '-') ) {
			return -Infinity;
		}
	
		// If a decimal place other than `.` is used, it needs to be given to the
		// function so we can detect it and replace with a `.` which is the only
		// decimal place Javascript recognises - it is not locale aware.
		if ( decimalPlace ) {
			d = _numToDecimal( d, decimalPlace );
		}
	
		if ( d.replace ) {
			if ( re1 ) {
				d = d.replace( re1, '' );
			}
	
			if ( re2 ) {
				d = d.replace( re2, '' );
			}
		}
	
		return d * 1;
	};
	
	
	// Add the numeric 'deformatting' functions for sorting and search. This is done
	// in a function to provide an easy ability for the language options to add
	// additional methods if a non-period decimal place is used.
	function _addNumericSort ( decimalPlace ) {
		$.each(
			{
				// Plain numbers
				"num": function ( d ) {
					return __numericReplace( d, decimalPlace );
				},
	
				// Formatted numbers
				"num-fmt": function ( d ) {
					return __numericReplace( d, decimalPlace, _re_formatted_numeric );
				},
	
				// HTML numeric
				"html-num": function ( d ) {
					return __numericReplace( d, decimalPlace, _re_html );
				},
	
				// HTML numeric, formatted
				"html-num-fmt": function ( d ) {
					return __numericReplace( d, decimalPlace, _re_html, _re_formatted_numeric );
				}
			},
			function ( key, fn ) {
				// Add the ordering method
				_ext.type.order[ key+decimalPlace+'-pre' ] = fn;
	
				// For HTML types add a search formatter that will strip the HTML
				if ( key.match(/^html\-/) ) {
					_ext.type.search[ key+decimalPlace ] = _ext.type.search.html;
				}
			}
		);
	}
	
	
	// Default sort methods
	$.extend( _ext.type.order, {
		// Dates
		"date-pre": function ( d ) {
			return Date.parse( d ) || 0;
		},
	
		// html
		"html-pre": function ( a ) {
			return _empty(a) ?
				'' :
				a.replace ?
					a.replace( /<.*?>/g, "" ).toLowerCase() :
					a+'';
		},
	
		// string
		"string-pre": function ( a ) {
			// This is a little complex, but faster than always calling toString,
			// http://jsperf.com/tostring-v-check
			return _empty(a) ?
				'' :
				typeof a === 'string' ?
					a.toLowerCase() :
					! a.toString ?
						'' :
						a.toString();
		},
	
		// string-asc and -desc are retained only for compatibility with the old
		// sort methods
		"string-asc": function ( x, y ) {
			return ((x < y) ? -1 : ((x > y) ? 1 : 0));
		},
	
		"string-desc": function ( x, y ) {
			return ((x < y) ? 1 : ((x > y) ? -1 : 0));
		}
	} );
	
	
	// Numeric sorting types - order doesn't matter here
	_addNumericSort( '' );
	
	
	$.extend( true, DataTable.ext.renderer, {
		header: {
			_: function ( settings, cell, column, classes ) {
				// No additional mark-up required
				// Attach a sort listener to update on sort - note that using the
				// `DT` namespace will allow the event to be removed automatically
				// on destroy, while the `dt` namespaced event is the one we are
				// listening for
				$(settings.nTable).on( 'order.dt.DT', function ( e, ctx, sorting, columns ) {
					if ( settings !== ctx ) { // need to check this this is the host
						return;               // table, not a nested one
					}
	
					var colIdx = column.idx;
	
					cell
						.removeClass(
							column.sSortingClass +' '+
							classes.sSortAsc +' '+
							classes.sSortDesc
						)
						.addClass( columns[ colIdx ] == 'asc' ?
							classes.sSortAsc : columns[ colIdx ] == 'desc' ?
								classes.sSortDesc :
								column.sSortingClass
						);
				} );
			},
	
			jqueryui: function ( settings, cell, column, classes ) {
				$('<div/>')
					.addClass( classes.sSortJUIWrapper )
					.append( cell.contents() )
					.append( $('<span/>')
						.addClass( classes.sSortIcon+' '+column.sSortingClassJUI )
					)
					.appendTo( cell );
	
				// Attach a sort listener to update on sort
				$(settings.nTable).on( 'order.dt.DT', function ( e, ctx, sorting, columns ) {
					if ( settings !== ctx ) {
						return;
					}
	
					var colIdx = column.idx;
	
					cell
						.removeClass( classes.sSortAsc +" "+classes.sSortDesc )
						.addClass( columns[ colIdx ] == 'asc' ?
							classes.sSortAsc : columns[ colIdx ] == 'desc' ?
								classes.sSortDesc :
								column.sSortingClass
						);
	
					cell
						.find( 'span.'+classes.sSortIcon )
						.removeClass(
							classes.sSortJUIAsc +" "+
							classes.sSortJUIDesc +" "+
							classes.sSortJUI +" "+
							classes.sSortJUIAscAllowed +" "+
							classes.sSortJUIDescAllowed
						)
						.addClass( columns[ colIdx ] == 'asc' ?
							classes.sSortJUIAsc : columns[ colIdx ] == 'desc' ?
								classes.sSortJUIDesc :
								column.sSortingClassJUI
						);
				} );
			}
		}
	} );
	
	/*
	 * Public helper functions. These aren't used internally by DataTables, or
	 * called by any of the options passed into DataTables, but they can be used
	 * externally by developers working with DataTables. They are helper functions
	 * to make working with DataTables a little bit easier.
	 */
	
	/**
	 * Helpers for `columns.render`.
	 *
	 * The options defined here can be used with the `columns.render` initialisation
	 * option to provide a display renderer. The following functions are defined:
	 *
	 * * `number` - Will format numeric data (defined by `columns.data`) for
	 *   display, retaining the original unformatted data for sorting and filtering.
	 *   It takes 5 parameters:
	 *   * `string` - Thousands grouping separator
	 *   * `string` - Decimal point indicator
	 *   * `integer` - Number of decimal points to show
	 *   * `string` (optional) - Prefix.
	 *   * `string` (optional) - Postfix (/suffix).
	 * * `text` - Escape HTML to help prevent XSS attacks. It has no optional
	 *   parameters.
	 *
	 * @example
	 *   // Column definition using the number renderer
	 *   {
	 *     data: "salary",
	 *     render: $.fn.dataTable.render.number( '\'', '.', 0, '$' )
	 *   }
	 *
	 * @namespace
	 */
	DataTable.render = {
		number: function ( thousands, decimal, precision, prefix, postfix ) {
			return {
				display: function ( d ) {
					if ( typeof d !== 'number' && typeof d !== 'string' ) {
						return d;
					}
	
					var negative = d < 0 ? '-' : '';
					var flo = parseFloat( d );
	
					// If NaN then there isn't much formatting that we can do - just
					// return immediately
					if ( isNaN( flo ) ) {
						return d;
					}
	
					d = Math.abs( flo );
	
					var intPart = parseInt( d, 10 );
					var floatPart = precision ?
						decimal+(d - intPart).toFixed( precision ).substring( 2 ):
						'';
	
					return negative + (prefix||'') +
						intPart.toString().replace(
							/\B(?=(\d{3})+(?!\d))/g, thousands
						) +
						floatPart +
						(postfix||'');
				}
			};
		},
	
		text: function () {
			return {
				display: function ( d ) {
					return typeof d === 'string' ?
						d.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;') :
						d;
				}
			};
		}
	};
	
	
	/*
	 * This is really a good bit rubbish this method of exposing the internal methods
	 * publicly... - To be fixed in 2.0 using methods on the prototype
	 */
	
	
	/**
	 * Create a wrapper function for exporting an internal functions to an external API.
	 *  @param {string} fn API function name
	 *  @returns {function} wrapped function
	 *  @memberof DataTable#internal
	 */
	function _fnExternApiFunc (fn)
	{
		return function() {
			var args = [_fnSettingsFromNode( this[DataTable.ext.iApiIndex] )].concat(
				Array.prototype.slice.call(arguments)
			);
			return DataTable.ext.internal[fn].apply( this, args );
		};
	}
	
	
	/**
	 * Reference to internal functions for use by plug-in developers. Note that
	 * these methods are references to internal functions and are considered to be
	 * private. If you use these methods, be aware that they are liable to change
	 * between versions.
	 *  @namespace
	 */
	$.extend( DataTable.ext.internal, {
		_fnExternApiFunc: _fnExternApiFunc,
		_fnBuildAjax: _fnBuildAjax,
		_fnAjaxUpdate: _fnAjaxUpdate,
		_fnAjaxParameters: _fnAjaxParameters,
		_fnAjaxUpdateDraw: _fnAjaxUpdateDraw,
		_fnAjaxDataSrc: _fnAjaxDataSrc,
		_fnAddColumn: _fnAddColumn,
		_fnColumnOptions: _fnColumnOptions,
		_fnAdjustColumnSizing: _fnAdjustColumnSizing,
		_fnVisibleToColumnIndex: _fnVisibleToColumnIndex,
		_fnColumnIndexToVisible: _fnColumnIndexToVisible,
		_fnVisbleColumns: _fnVisbleColumns,
		_fnGetColumns: _fnGetColumns,
		_fnColumnTypes: _fnColumnTypes,
		_fnApplyColumnDefs: _fnApplyColumnDefs,
		_fnHungarianMap: _fnHungarianMap,
		_fnCamelToHungarian: _fnCamelToHungarian,
		_fnLanguageCompat: _fnLanguageCompat,
		_fnBrowserDetect: _fnBrowserDetect,
		_fnAddData: _fnAddData,
		_fnAddTr: _fnAddTr,
		_fnNodeToDataIndex: _fnNodeToDataIndex,
		_fnNodeToColumnIndex: _fnNodeToColumnIndex,
		_fnGetCellData: _fnGetCellData,
		_fnSetCellData: _fnSetCellData,
		_fnSplitObjNotation: _fnSplitObjNotation,
		_fnGetObjectDataFn: _fnGetObjectDataFn,
		_fnSetObjectDataFn: _fnSetObjectDataFn,
		_fnGetDataMaster: _fnGetDataMaster,
		_fnClearTable: _fnClearTable,
		_fnDeleteIndex: _fnDeleteIndex,
		_fnInvalidate: _fnInvalidate,
		_fnGetRowElements: _fnGetRowElements,
		_fnCreateTr: _fnCreateTr,
		_fnBuildHead: _fnBuildHead,
		_fnDrawHead: _fnDrawHead,
		_fnDraw: _fnDraw,
		_fnReDraw: _fnReDraw,
		_fnAddOptionsHtml: _fnAddOptionsHtml,
		_fnDetectHeader: _fnDetectHeader,
		_fnGetUniqueThs: _fnGetUniqueThs,
		_fnFeatureHtmlFilter: _fnFeatureHtmlFilter,
		_fnFilterComplete: _fnFilterComplete,
		_fnFilterCustom: _fnFilterCustom,
		_fnFilterColumn: _fnFilterColumn,
		_fnFilter: _fnFilter,
		_fnFilterCreateSearch: _fnFilterCreateSearch,
		_fnEscapeRegex: _fnEscapeRegex,
		_fnFilterData: _fnFilterData,
		_fnFeatureHtmlInfo: _fnFeatureHtmlInfo,
		_fnUpdateInfo: _fnUpdateInfo,
		_fnInfoMacros: _fnInfoMacros,
		_fnInitialise: _fnInitialise,
		_fnInitComplete: _fnInitComplete,
		_fnLengthChange: _fnLengthChange,
		_fnFeatureHtmlLength: _fnFeatureHtmlLength,
		_fnFeatureHtmlPaginate: _fnFeatureHtmlPaginate,
		_fnPageChange: _fnPageChange,
		_fnFeatureHtmlProcessing: _fnFeatureHtmlProcessing,
		_fnProcessingDisplay: _fnProcessingDisplay,
		_fnFeatureHtmlTable: _fnFeatureHtmlTable,
		_fnScrollDraw: _fnScrollDraw,
		_fnApplyToChildren: _fnApplyToChildren,
		_fnCalculateColumnWidths: _fnCalculateColumnWidths,
		_fnThrottle: _fnThrottle,
		_fnConvertToWidth: _fnConvertToWidth,
		_fnGetWidestNode: _fnGetWidestNode,
		_fnGetMaxLenString: _fnGetMaxLenString,
		_fnStringToCss: _fnStringToCss,
		_fnSortFlatten: _fnSortFlatten,
		_fnSort: _fnSort,
		_fnSortAria: _fnSortAria,
		_fnSortListener: _fnSortListener,
		_fnSortAttachListener: _fnSortAttachListener,
		_fnSortingClasses: _fnSortingClasses,
		_fnSortData: _fnSortData,
		_fnSaveState: _fnSaveState,
		_fnLoadState: _fnLoadState,
		_fnSettingsFromNode: _fnSettingsFromNode,
		_fnLog: _fnLog,
		_fnMap: _fnMap,
		_fnBindAction: _fnBindAction,
		_fnCallbackReg: _fnCallbackReg,
		_fnCallbackFire: _fnCallbackFire,
		_fnLengthOverflow: _fnLengthOverflow,
		_fnRenderer: _fnRenderer,
		_fnDataSource: _fnDataSource,
		_fnRowAttributes: _fnRowAttributes,
		_fnCalculateEnd: function () {} // Used by a lot of plug-ins, but redundant
		                                // in 1.10, so this dead-end function is
		                                // added to prevent errors
	} );
	

	// jQuery access
	$.fn.dataTable = DataTable;

	// Provide access to the host jQuery object (circular reference)
	DataTable.$ = $;

	// Legacy aliases
	$.fn.dataTableSettings = DataTable.settings;
	$.fn.dataTableExt = DataTable.ext;

	// With a capital `D` we return a DataTables API instance rather than a
	// jQuery object
	$.fn.DataTable = function ( opts ) {
		return $(this).dataTable( opts ).api();
	};

	// All properties that are available to $.fn.dataTable should also be
	// available on $.fn.DataTable
	$.each( DataTable, function ( prop, val ) {
		$.fn.DataTable[ prop ] = val;
	} );


	// Information about events fired by DataTables - for documentation.
	/**
	 * Draw event, fired whenever the table is redrawn on the page, at the same
	 * point as fnDrawCallback. This may be useful for binding events or
	 * performing calculations when the table is altered at all.
	 *  @name DataTable#draw.dt
	 *  @event
	 *  @param {event} e jQuery event object
	 *  @param {object} o DataTables settings object {@link DataTable.models.oSettings}
	 */

	/**
	 * Search event, fired when the searching applied to the table (using the
	 * built-in global search, or column filters) is altered.
	 *  @name DataTable#search.dt
	 *  @event
	 *  @param {event} e jQuery event object
	 *  @param {object} o DataTables settings object {@link DataTable.models.oSettings}
	 */

	/**
	 * Page change event, fired when the paging of the table is altered.
	 *  @name DataTable#page.dt
	 *  @event
	 *  @param {event} e jQuery event object
	 *  @param {object} o DataTables settings object {@link DataTable.models.oSettings}
	 */

	/**
	 * Order event, fired when the ordering applied to the table is altered.
	 *  @name DataTable#order.dt
	 *  @event
	 *  @param {event} e jQuery event object
	 *  @param {object} o DataTables settings object {@link DataTable.models.oSettings}
	 */

	/**
	 * DataTables initialisation complete event, fired when the table is fully
	 * drawn, including Ajax data loaded, if Ajax data is required.
	 *  @name DataTable#init.dt
	 *  @event
	 *  @param {event} e jQuery event object
	 *  @param {object} oSettings DataTables settings object
	 *  @param {object} json The JSON object request from the server - only
	 *    present if client-side Ajax sourced data is used</li></ol>
	 */

	/**
	 * State save event, fired when the table has changed state a new state save
	 * is required. This event allows modification of the state saving object
	 * prior to actually doing the save, including addition or other state
	 * properties (for plug-ins) or modification of a DataTables core property.
	 *  @name DataTable#stateSaveParams.dt
	 *  @event
	 *  @param {event} e jQuery event object
	 *  @param {object} oSettings DataTables settings object
	 *  @param {object} json The state information to be saved
	 */

	/**
	 * State load event, fired when the table is loading state from the stored
	 * data, but prior to the settings object being modified by the saved state
	 * - allowing modification of the saved state is required or loading of
	 * state for a plug-in.
	 *  @name DataTable#stateLoadParams.dt
	 *  @event
	 *  @param {event} e jQuery event object
	 *  @param {object} oSettings DataTables settings object
	 *  @param {object} json The saved state information
	 */

	/**
	 * State loaded event, fired when state has been loaded from stored data and
	 * the settings object has been modified by the loaded data.
	 *  @name DataTable#stateLoaded.dt
	 *  @event
	 *  @param {event} e jQuery event object
	 *  @param {object} oSettings DataTables settings object
	 *  @param {object} json The saved state information
	 */

	/**
	 * Processing event, fired when DataTables is doing some kind of processing
	 * (be it, order, searcg or anything else). It can be used to indicate to
	 * the end user that there is something happening, or that something has
	 * finished.
	 *  @name DataTable#processing.dt
	 *  @event
	 *  @param {event} e jQuery event object
	 *  @param {object} oSettings DataTables settings object
	 *  @param {boolean} bShow Flag for if DataTables is doing processing or not
	 */

	/**
	 * Ajax (XHR) event, fired whenever an Ajax request is completed from a
	 * request to made to the server for new data. This event is called before
	 * DataTables processed the returned data, so it can also be used to pre-
	 * process the data returned from the server, if needed.
	 *
	 * Note that this trigger is called in `fnServerData`, if you override
	 * `fnServerData` and which to use this event, you need to trigger it in you
	 * success function.
	 *  @name DataTable#xhr.dt
	 *  @event
	 *  @param {event} e jQuery event object
	 *  @param {object} o DataTables settings object {@link DataTable.models.oSettings}
	 *  @param {object} json JSON returned from the server
	 *
	 *  @example
	 *     // Use a custom property returned from the server in another DOM element
	 *     $('#table').dataTable().on('xhr.dt', function (e, settings, json) {
	 *       $('#status').html( json.status );
	 *     } );
	 *
	 *  @example
	 *     // Pre-process the data returned from the server
	 *     $('#table').dataTable().on('xhr.dt', function (e, settings, json) {
	 *       for ( var i=0, ien=json.aaData.length ; i<ien ; i++ ) {
	 *         json.aaData[i].sum = json.aaData[i].one + json.aaData[i].two;
	 *       }
	 *       // Note no return - manipulate the data directly in the JSON object.
	 *     } );
	 */

	/**
	 * Destroy event, fired when the DataTable is destroyed by calling fnDestroy
	 * or passing the bDestroy:true parameter in the initialisation object. This
	 * can be used to remove bound events, added DOM nodes, etc.
	 *  @name DataTable#destroy.dt
	 *  @event
	 *  @param {event} e jQuery event object
	 *  @param {object} o DataTables settings object {@link DataTable.models.oSettings}
	 */

	/**
	 * Page length change event, fired when number of records to show on each
	 * page (the length) is changed.
	 *  @name DataTable#length.dt
	 *  @event
	 *  @param {event} e jQuery event object
	 *  @param {object} o DataTables settings object {@link DataTable.models.oSettings}
	 *  @param {integer} len New length
	 */

	/**
	 * Column sizing has changed.
	 *  @name DataTable#column-sizing.dt
	 *  @event
	 *  @param {event} e jQuery event object
	 *  @param {object} o DataTables settings object {@link DataTable.models.oSettings}
	 */

	/**
	 * Column visibility has changed.
	 *  @name DataTable#column-visibility.dt
	 *  @event
	 *  @param {event} e jQuery event object
	 *  @param {object} o DataTables settings object {@link DataTable.models.oSettings}
	 *  @param {int} column Column index
	 *  @param {bool} vis `false` if column now hidden, or `true` if visible
	 */

	return $.fn.dataTable;
}));
/**
 * HOMER - Responsive Admin Theme
 * version 1.9
 *
 */


$(document).ready(function () {

    // Add special class to minimalize page elements when screen is less than 768px
    setBodySmall();

    // Handle minimalize sidebar menu
    $('.hide-menu').on('click', function(event){
        event.preventDefault();
        if ($(window).width() < 769) {
            $("body").toggleClass("show-sidebar");
        } else {
            $("body").toggleClass("hide-sidebar");
        }
    });

    // Initialize metsiMenu plugin to sidebar menu
    $('#side-menu').metisMenu();


    // Initialize animate panel function
    $('.animate-panel').animatePanel();

    // Function for collapse hpanel
    $('.showhide').on('click', function (event) {
        event.preventDefault();
        var hpanel = $(this).closest('div.hpanel');
        var icon = $(this).find('i:first');
        var body = hpanel.find('div.panel-body');
        var footer = hpanel.find('div.panel-footer');
        body.slideToggle(300);
        footer.slideToggle(200);

        // Toggle icon from up to down
        icon.toggleClass('fa-chevron-up').toggleClass('fa-chevron-down');
        hpanel.toggleClass('').toggleClass('panel-collapse');
        setTimeout(function () {
            hpanel.resize();
            hpanel.find('[id^=map-]').resize();
        }, 50);
    });

    // Function for close hpanel
    $('.closebox').on('click', function (event) {
        event.preventDefault();
        var hpanel = $(this).closest('div.hpanel');
        hpanel.remove();
        if($('body').hasClass('fullscreen-panel-mode')) { $('body').removeClass('fullscreen-panel-mode');}
    });

    // Fullscreen for fullscreen hpanel
    $('.fullscreen').on('click', function() {
        var hpanel = $(this).closest('div.hpanel');
        var icon = $(this).find('i:first');
        $('body').toggleClass('fullscreen-panel-mode');
        icon.toggleClass('fa-expand').toggleClass('fa-compress');
        hpanel.toggleClass('fullscreen');
        setTimeout(function() {
            $(window).trigger('resize');
        }, 100);
    });

    // Open close right sidebar
    $('.right-sidebar-toggle').on('click', function () {
        $('#right-sidebar').toggleClass('sidebar-open');
    });

    // Function for small header
    $('.small-header-action').on('click', function(event){
        event.preventDefault();
        var icon = $(this).find('i:first');
        var breadcrumb  = $(this).parent().find('#hbreadcrumb');
        $(this).parent().parent().parent().toggleClass('small-header');
        breadcrumb.toggleClass('m-t-lg');
        icon.toggleClass('fa-arrow-up').toggleClass('fa-arrow-down');
    });

    // Set minimal height of #wrapper to fit the window
    setTimeout(function () {
        fixWrapperHeight();
    });


    // Initialize tooltips
    $('.tooltip-demo').tooltip({
        selector: "[data-toggle=tooltip]"
    });

    // Initialize popover
    $("[data-toggle=popover]").popover();

    // Move modal to body
    // Fix Bootstrap backdrop issu with animation.css
    $('.modal').appendTo("body")

});

$(window).bind("load", function () {
    // Remove splash screen after load
    $('.splash').css('display', 'none')
});

$(window).bind("resize click", function () {

    // Add special class to minimalize page elements when screen is less than 768px
    setBodySmall();

    // Waint until metsiMenu, collapse and other effect finish and set wrapper height
    setTimeout(function () {
        fixWrapperHeight();
    }, 300);
});

function fixWrapperHeight() {

    // Get and set current height
    var headerH = 62;
    var navigationH = $("#navigation").height();
    var contentH = $(".content").height();

    // Set new height when contnet height is less then navigation
    if (contentH < navigationH) {
        $("#wrapper").css("min-height", navigationH + 'px');
    }

    // Set new height when contnet height is less then navigation and navigation is less then window
    if (contentH < navigationH && navigationH < $(window).height()) {
        $("#wrapper").css("min-height", $(window).height() - headerH  + 'px');
    }

    // Set new height when contnet is higher then navigation but less then window
    if (contentH > navigationH && contentH < $(window).height()) {
        $("#wrapper").css("min-height", $(window).height() - headerH + 'px');
    }
}


function setBodySmall() {
    if ($(this).width() < 769) {
        $('body').addClass('page-small');
    } else {
        $('body').removeClass('page-small');
        $('body').removeClass('show-sidebar');
    }
}

// Animate panel function
$.fn['animatePanel'] = function() {

    var element = $(this);
    var effect = $(this).data('effect');
    var delay = $(this).data('delay');
    var child = $(this).data('child');

    // Set default values for attrs
    if(!effect) { effect = 'zoomIn'}
    if(!delay) { delay = 0.06 } else { delay = delay / 10 }
    if(!child) { child = '.row > div'} else {child = "." + child}

    //Set defaul values for start animation and delay
    var startAnimation = 0;
    var start = Math.abs(delay) + startAnimation;

    // Get all visible element and set opacity to 0
    var panel = element.find(child);
    panel.addClass('opacity-0');

    // Get all elements and add effect class
    panel = element.find(child);
    panel.addClass('stagger').addClass('animated-panel').addClass(effect);

    var panelsCount = panel.length + 10;
    var animateTime = (panelsCount * delay * 10000) / 10;

    // Add delay for each child elements
    panel.each(function (i, elm) {
        start += delay;
        var rounded = Math.round(start * 10) / 10;
        $(elm).css('animation-delay', rounded + 's');
        // Remove opacity 0 after finish
        $(elm).removeClass('opacity-0');
    });

    // Clear animation after finish
    setTimeout(function(){
        $('.stagger').css('animation', '');
        $('.stagger').removeClass(effect).removeClass('animated-panel').removeClass('stagger');
    }, animateTime)

};
!function(e){e(["jquery"],function(e){return function(){function t(e,t,n){return f({type:O.error,iconClass:g().iconClasses.error,message:e,optionsOverride:n,title:t})}function n(t,n){return t||(t=g()),v=e("#"+t.containerId),v.length?v:(n&&(v=c(t)),v)}function i(e,t,n){return f({type:O.info,iconClass:g().iconClasses.info,message:e,optionsOverride:n,title:t})}function o(e){w=e}function s(e,t,n){return f({type:O.success,iconClass:g().iconClasses.success,message:e,optionsOverride:n,title:t})}function a(e,t,n){return f({type:O.warning,iconClass:g().iconClasses.warning,message:e,optionsOverride:n,title:t})}function r(e){var t=g();v||n(t),l(e,t)||u(t)}function d(t){var i=g();return v||n(i),t&&0===e(":focus",t).length?void h(t):void(v.children().length&&v.remove())}function u(t){for(var n=v.children(),i=n.length-1;i>=0;i--)l(e(n[i]),t)}function l(t,n){return t&&0===e(":focus",t).length?(t[n.hideMethod]({duration:n.hideDuration,easing:n.hideEasing,complete:function(){h(t)}}),!0):!1}function c(t){return v=e("<div/>").attr("id",t.containerId).addClass(t.positionClass).attr("aria-live","polite").attr("role","alert"),v.appendTo(e(t.target)),v}function p(){return{tapToDismiss:!0,toastClass:"toast",containerId:"toast-container",debug:!1,showMethod:"fadeIn",showDuration:300,showEasing:"swing",onShown:void 0,hideMethod:"fadeOut",hideDuration:1e3,hideEasing:"swing",onHidden:void 0,extendedTimeOut:1e3,iconClasses:{error:"toast-error",info:"toast-info",success:"toast-success",warning:"toast-warning"},iconClass:"toast-info",positionClass:"toast-top-right",timeOut:5e3,titleClass:"toast-title",messageClass:"toast-message",target:"body",closeHtml:'<button type="button">&times;</button>',newestOnTop:!0,preventDuplicates:!1,progressBar:!1}}function m(e){w&&w(e)}function f(t){function i(t){return!e(":focus",l).length||t?(clearTimeout(O.intervalId),l[r.hideMethod]({duration:r.hideDuration,easing:r.hideEasing,complete:function(){h(l),r.onHidden&&"hidden"!==b.state&&r.onHidden(),b.state="hidden",b.endTime=new Date,m(b)}})):void 0}function o(){(r.timeOut>0||r.extendedTimeOut>0)&&(u=setTimeout(i,r.extendedTimeOut),O.maxHideTime=parseFloat(r.extendedTimeOut),O.hideEta=(new Date).getTime()+O.maxHideTime)}function s(){clearTimeout(u),O.hideEta=0,l.stop(!0,!0)[r.showMethod]({duration:r.showDuration,easing:r.showEasing})}function a(){var e=(O.hideEta-(new Date).getTime())/O.maxHideTime*100;f.width(e+"%")}var r=g(),d=t.iconClass||r.iconClass;if("undefined"!=typeof t.optionsOverride&&(r=e.extend(r,t.optionsOverride),d=t.optionsOverride.iconClass||d),r.preventDuplicates){if(t.message===C)return;C=t.message}T++,v=n(r,!0);var u=null,l=e("<div/>"),c=e("<div/>"),p=e("<div/>"),f=e("<div/>"),w=e(r.closeHtml),O={intervalId:null,hideEta:null,maxHideTime:null},b={toastId:T,state:"visible",startTime:new Date,options:r,map:t};return t.iconClass&&l.addClass(r.toastClass).addClass(d),t.title&&(c.append(t.title).addClass(r.titleClass),l.append(c)),t.message&&(p.append(t.message).addClass(r.messageClass),l.append(p)),r.closeButton&&(w.addClass("toast-close-button").attr("role","button"),l.prepend(w)),r.progressBar&&(f.addClass("toast-progress"),l.prepend(f)),l.hide(),r.newestOnTop?v.prepend(l):v.append(l),l[r.showMethod]({duration:r.showDuration,easing:r.showEasing,complete:r.onShown}),r.timeOut>0&&(u=setTimeout(i,r.timeOut),O.maxHideTime=parseFloat(r.timeOut),O.hideEta=(new Date).getTime()+O.maxHideTime,r.progressBar&&(O.intervalId=setInterval(a,10))),l.hover(s,o),!r.onclick&&r.tapToDismiss&&l.click(i),r.closeButton&&w&&w.click(function(e){e.stopPropagation?e.stopPropagation():void 0!==e.cancelBubble&&e.cancelBubble!==!0&&(e.cancelBubble=!0),i(!0)}),r.onclick&&l.click(function(){r.onclick(),i()}),m(b),r.debug&&console&&console.log(b),l}function g(){return e.extend({},p(),b.options)}function h(e){v||(v=n()),e.is(":visible")||(e.remove(),e=null,0===v.children().length&&(v.remove(),C=void 0))}var v,w,C,T=0,O={error:"error",info:"info",success:"success",warning:"warning"},b={clear:r,remove:d,error:t,getContainer:n,info:i,options:{},subscribe:o,success:s,version:"2.1.0",warning:a};return b}()})}("function"==typeof define&&define.amd?define:function(e,t){"undefined"!=typeof module&&module.exports?module.exports=t(require("jquery")):window.toastr=t(window.jQuery)});
//# sourceMappingURL=toastr.js.map
;
(function() {
  $(document).ready(function() {
    'use strict';
    $('.field-input').focus(function() {
      $(this).parent().addClass('is-focused has-label');
    });
    $('.field-input').each(function() {
      if ($(this).val() !== '') {
        $(this).parent().addClass('has-label');
      }
    });
    $('.field-input').blur(function() {
      var $parent;
      $parent = $(this).parent();
      if ($(this).val() === '') {
        $parent.removeClass('has-label');
      }
      $parent.removeClass('is-focused');
    });
    return $(".send-btn").click(function() {
      return Tipmetric.Controllers.spinner.show();
    });
  });

}).call(this);
//     keymaster.js
//     (c) 2011-2013 Thomas Fuchs
//     keymaster.js may be freely distributed under the MIT license.

;(function(global){
  var k,
    _handlers = {},
    _mods = { 16: false, 18: false, 17: false, 91: false },
    _scope = 'all',
    // modifier keys
    _MODIFIERS = {
      '⇧': 16, shift: 16,
      '⌥': 18, alt: 18, option: 18,
      '⌃': 17, ctrl: 17, control: 17,
      '⌘': 91, command: 91
    },
    // special keys
    _MAP = {
      backspace: 8, tab: 9, clear: 12,
      enter: 13, 'return': 13,
      esc: 27, escape: 27, space: 32,
      left: 37, up: 38,
      right: 39, down: 40,
      del: 46, 'delete': 46,
      home: 36, end: 35,
      pageup: 33, pagedown: 34,
      ',': 188, '.': 190, '/': 191,
      '`': 192, '-': 189, '=': 187,
      ';': 186, '\'': 222,
      '[': 219, ']': 221, '\\': 220
    },
    code = function(x){
      return _MAP[x] || x.toUpperCase().charCodeAt(0);
    },
    _downKeys = [];

  for(k=1;k<20;k++) _MAP['f'+k] = 111+k;

  // IE doesn't support Array#indexOf, so have a simple replacement
  function index(array, item){
    var i = array.length;
    while(i--) if(array[i]===item) return i;
    return -1;
  }

  // for comparing mods before unassignment
  function compareArray(a1, a2) {
    if (a1.length != a2.length) return false;
    for (var i = 0; i < a1.length; i++) {
        if (a1[i] !== a2[i]) return false;
    }
    return true;
  }

  var modifierMap = {
      16:'shiftKey',
      18:'altKey',
      17:'ctrlKey',
      91:'metaKey'
  };
  function updateModifierKey(event) {
      for(k in _mods) _mods[k] = event[modifierMap[k]];
  };

  // handle keydown event
  function dispatch(event) {
    var key, handler, k, i, modifiersMatch, scope;
    key = event.keyCode;

    if (index(_downKeys, key) == -1) {
        _downKeys.push(key);
    }

    // if a modifier key, set the key.<modifierkeyname> property to true and return
    if(key == 93 || key == 224) key = 91; // right command on webkit, command on Gecko
    if(key in _mods) {
      _mods[key] = true;
      // 'assignKey' from inside this closure is exported to window.key
      for(k in _MODIFIERS) if(_MODIFIERS[k] == key) assignKey[k] = true;
      return;
    }
    updateModifierKey(event);

    // see if we need to ignore the keypress (filter() can can be overridden)
    // by default ignore key presses if a select, textarea, or input is focused
    if(!assignKey.filter.call(this, event)) return;

    // abort if no potentially matching shortcuts found
    if (!(key in _handlers)) return;

    scope = getScope();

    // for each potential shortcut
    for (i = 0; i < _handlers[key].length; i++) {
      handler = _handlers[key][i];

      // see if it's in the current scope
      if(handler.scope == scope || handler.scope == 'all'){
        // check if modifiers match if any
        modifiersMatch = handler.mods.length > 0;
        for(k in _mods)
          if((!_mods[k] && index(handler.mods, +k) > -1) ||
            (_mods[k] && index(handler.mods, +k) == -1)) modifiersMatch = false;
        // call the handler and stop the event if neccessary
        if((handler.mods.length == 0 && !_mods[16] && !_mods[18] && !_mods[17] && !_mods[91]) || modifiersMatch){
          if(handler.method(event, handler)===false){
            if(event.preventDefault) event.preventDefault();
              else event.returnValue = false;
            if(event.stopPropagation) event.stopPropagation();
            if(event.cancelBubble) event.cancelBubble = true;
          }
        }
      }
    }
  };

  // unset modifier keys on keyup
  function clearModifier(event){
    var key = event.keyCode, k,
        i = index(_downKeys, key);

    // remove key from _downKeys
    if (i >= 0) {
        _downKeys.splice(i, 1);
    }

    if(key == 93 || key == 224) key = 91;
    if(key in _mods) {
      _mods[key] = false;
      for(k in _MODIFIERS) if(_MODIFIERS[k] == key) assignKey[k] = false;
    }
  };

  function resetModifiers() {
    for(k in _mods) _mods[k] = false;
    for(k in _MODIFIERS) assignKey[k] = false;
  };

  // parse and assign shortcut
  function assignKey(key, scope, method){
    var keys, mods;
    keys = getKeys(key);
    if (method === undefined) {
      method = scope;
      scope = 'all';
    }

    // for each shortcut
    for (var i = 0; i < keys.length; i++) {
      // set modifier keys if any
      mods = [];
      key = keys[i].split('+');
      if (key.length > 1){
        mods = getMods(key);
        key = [key[key.length-1]];
      }
      // convert to keycode and...
      key = key[0]
      key = code(key);
      // ...store handler
      if (!(key in _handlers)) _handlers[key] = [];
      _handlers[key].push({ shortcut: keys[i], scope: scope, method: method, key: keys[i], mods: mods });
    }
  };

  // unbind all handlers for given key in current scope
  function unbindKey(key, scope) {
    var multipleKeys, keys,
      mods = [],
      i, j, obj;

    multipleKeys = getKeys(key);

    for (j = 0; j < multipleKeys.length; j++) {
      keys = multipleKeys[j].split('+');

      if (keys.length > 1) {
        mods = getMods(keys);
      }

      key = keys[keys.length - 1];
      key = code(key);

      if (scope === undefined) {
        scope = getScope();
      }
      if (!_handlers[key]) {
        return;
      }
      for (i = 0; i < _handlers[key].length; i++) {
        obj = _handlers[key][i];
        // only clear handlers if correct scope and mods match
        if (obj.scope === scope && compareArray(obj.mods, mods)) {
          _handlers[key][i] = {};
        }
      }
    }
  };

  // Returns true if the key with code 'keyCode' is currently down
  // Converts strings into key codes.
  function isPressed(keyCode) {
      if (typeof(keyCode)=='string') {
        keyCode = code(keyCode);
      }
      return index(_downKeys, keyCode) != -1;
  }

  function getPressedKeyCodes() {
      return _downKeys.slice(0);
  }

  function filter(event){
    var tagName = (event.target || event.srcElement).tagName;
    // ignore keypressed in any elements that support keyboard data input
    return !(tagName == 'INPUT' || tagName == 'SELECT' || tagName == 'TEXTAREA');
  }

  // initialize key.<modifier> to false
  for(k in _MODIFIERS) assignKey[k] = false;

  // set current scope (default 'all')
  function setScope(scope){ _scope = scope || 'all' };
  function getScope(){ return _scope || 'all' };

  // delete all handlers for a given scope
  function deleteScope(scope){
    var key, handlers, i;

    for (key in _handlers) {
      handlers = _handlers[key];
      for (i = 0; i < handlers.length; ) {
        if (handlers[i].scope === scope) handlers.splice(i, 1);
        else i++;
      }
    }
  };

  // abstract key logic for assign and unassign
  function getKeys(key) {
    var keys;
    key = key.replace(/\s/g, '');
    keys = key.split(',');
    if ((keys[keys.length - 1]) == '') {
      keys[keys.length - 2] += ',';
    }
    return keys;
  }

  // abstract mods logic for assign and unassign
  function getMods(key) {
    var mods = key.slice(0, key.length - 1);
    for (var mi = 0; mi < mods.length; mi++)
    mods[mi] = _MODIFIERS[mods[mi]];
    return mods;
  }

  // cross-browser events
  function addEvent(object, event, method) {
    if (object.addEventListener)
      object.addEventListener(event, method, false);
    else if(object.attachEvent)
      object.attachEvent('on'+event, function(){ method(window.event) });
  };

  // set the handlers globally on document
  addEvent(document, 'keydown', function(event) { dispatch(event) }); // Passing _scope to a callback to ensure it remains the same by execution. Fixes #48
  addEvent(document, 'keyup', clearModifier);

  // reset modifiers to false whenever the window is (re)focused.
  addEvent(window, 'focus', resetModifiers);

  // store previously defined key
  var previousKey = global.key;

  // restore previously defined key and return reference to our key object
  function noConflict() {
    var k = global.key;
    global.key = previousKey;
    return k;
  }

  // set window.key and window.key.set/get/deleteScope, and the default filter
  global.key = assignKey;
  global.key.setScope = setScope;
  global.key.getScope = getScope;
  global.key.deleteScope = deleteScope;
  global.key.filter = filter;
  global.key.isPressed = isPressed;
  global.key.getPressedKeyCodes = getPressedKeyCodes;
  global.key.noConflict = noConflict;
  global.key.unbind = unbindKey;

  if(typeof module !== 'undefined') module.exports = assignKey;

})(this);
(function() {
  this.Tipmetric = (function(Backbone, Marionette) {
    var App;
    App = new Backbone.Marionette.Application;
    App.Controllers || (App.Controllers = {});
    App.Modules || (App.Modules = {});
    App.Config || (App.Config = {});
    if (!_.isUndefined(window.gon)) {
      App.Config.env = gon.config.environment;
    }
    App.Helpers || (App.Helpers = {});
    App.Data || (App.Data = {});
    App.TestSupport || (App.TestSupport = {
      displayErrors: function(text) {
        if (Tipmetric.Config.env === 'test') {
          return $('#test-wrapper').html("<p class='errors'>Errors: " + text + "</p>");
        }
      }
    });
    App.addRegions({
      contentRegion: "#wrapper",
      headerRegion: "#header",
      leftMenuRegion: "#menu",
      historyContentRegion: "#history-page-days-wrapper"
    });
    App.on("start", function(options) {
      if (Backbone.history) {
        return Backbone.history.start({
          pushState: true
        });
      }
    });
    return App;
  })(Backbone, Marionette);

  $(function() {
    return Tipmetric.start();
  });

}).call(this);
(function() {
  Backbone.Marionette.Renderer.render = function(path, data) {
    var template;
    template = JST[path];
    if (!template) {
      throw "Template " + path + " not found!";
    }
    return template(data);
  };

}).call(this);
(function() {
  toastr.options = {
    "closeButton": true,
    "debug": false,
    "newestOnTop": false,
    "progressBar": false,
    "positionClass": "toast-top-center",
    "preventDuplicates": false,
    "onclick": null,
    "showDuration": "300",
    "hideDuration": "1000",
    "timeOut": "3000",
    "extendedTimeOut": "1000",
    "showEasing": "swing",
    "hideEasing": "linear",
    "showMethod": "fadeIn",
    "hideMethod": "fadeOut"
  };

}).call(this);
(function() {
  $.fn.updateSelectInputOptions = function(options) {
    var html, i, len, option, savedVal;
    savedVal = $(this).val();
    if (_.isEmpty(savedVal)) {
      if (_.some(options) && !_.isEmpty(_.pick(options[0], 'id'))) {
        savedVal = options[0].id;
      } else {
        savedVal = "";
      }
    }
    html = "";
    for (i = 0, len = options.length; i < len; i++) {
      option = options[i];
      html += "<option value='" + option.id + "'>" + option.name + "</option>";
    }
    $(this).html(html);
    return $(this).find("option[value='" + savedVal + "']").attr("selected", true);
  };

  String.prototype.capitalizeFirstLetter = function() {
    return this.charAt(0).toUpperCase() + this.slice(1);
  };

}).call(this);
(function() {
  var base, base1;

  this.Tipmetric || (this.Tipmetric = {});

  (base = this.Tipmetric).Controllers || (base.Controllers = {});

  (base1 = this.Tipmetric).Modules || (base1.Modules = {});

  this.Tipmetric.Controllers.helpers = {
    randomNumber: function() {
      return (Math.random() * (1000000000 - 0) + 0).toString().replace(".", "");
    },
    buildSmartData: function() {
      var area, area_id, area_ids, area_name, area_types_active_ids, areas, data, day, day_area_shifts, day_area_shifts_area_ids, day_area_shifts_by_area, day_area_shifts_by_area_shift_ids, day_area_shifts_selected, first_position_type_ids, i, j, k, l, len, len1, len2, len3, len4, m, position_employees, position_ids, position_type, position_type_active_ids, position_type_id, position_type_name, positions, ref, ref1, shift, shift_id, shift_ids, shift_name, shift_types_active_ids, shifts, sourceData;
      sourceData = gon.restaurant_inheritance_data_raw;
      data = {};
      data.scope = sourceData.scope;
      ref = sourceData.days;
      for (i = 0, len = ref.length; i < len; i++) {
        day = ref[i];
        day_area_shifts = _.filter(sourceData.area_shifts, function(f) {
          return _.contains(f.days, day);
        });
        day_area_shifts_area_ids = _.map(day_area_shifts, function(f) {
          return f.area_type_id.$oid;
        });
        area_types_active_ids = _.map(sourceData.area_types_active, function(f) {
          return f._id.$oid;
        });
        area_ids = _.intersection(day_area_shifts_area_ids, area_types_active_ids);
        areas = _.filter(sourceData.area_types_active, function(f) {
          return _.contains(area_ids, f._id.$oid);
        });
        position_employees = {};
        ref1 = sourceData.position_types_active;
        for (j = 0, len1 = ref1.length; j < len1; j++) {
          position_type = ref1[j];
          position_type_id = position_type._id.$oid;
          position_employees[position_type_id] = _.filter(sourceData.employees_active, function(f) {
            return _.contains(_.map(f.position_type_ids, function(p) {
              return p.$oid;
            }), position_type_id);
          });
        }
        data[day] = {};
        data[day].areas = {};
        for (k = 0, len2 = areas.length; k < len2; k++) {
          area = areas[k];
          area_id = area._id.$oid;
          area_name = area.name;
          data[day].areas[area_name] = {
            id: area_id,
            name: area_name
          };
          day_area_shifts_by_area = _.filter(day_area_shifts, function(f) {
            return f.area_type_id.$oid === area_id;
          });
          day_area_shifts_by_area_shift_ids = _.map(day_area_shifts_by_area, function(f) {
            return f.shift_type_id.$oid;
          });
          shift_types_active_ids = _.map(sourceData.shift_types_active, function(f) {
            return f._id.$oid;
          });
          shift_ids = _.intersection(day_area_shifts_by_area_shift_ids, shift_types_active_ids);
          shifts = _.filter(sourceData.shift_types_active, function(f) {
            return _.contains(shift_ids, f._id.$oid);
          });
          data[day].areas[area_name].shifts = {};
          for (l = 0, len3 = shifts.length; l < len3; l++) {
            shift = shifts[l];
            shift_id = shift._id.$oid;
            shift_name = shift.name;
            data[day].areas[area_name].shifts[shift_name] = {
              id: shift_id,
              name: shift_name,
              start_time: shift.start_time,
              end_time: shift.end_time
            };
            data[day].areas[area_name].shifts[shift_name].positions = {};
            day_area_shifts_selected = _.filter(sourceData.area_shifts, function(f) {
              return f.shift_type_id.$oid === shift_id && f.area_type_id.$oid === area_id;
            });
            first_position_type_ids = _.map(day_area_shifts_selected[0].position_type_ids, function(f) {
              return f.$oid;
            });
            position_type_active_ids = _.map(sourceData.position_types_active, function(f) {
              return f._id.$oid;
            });
            position_ids = _.intersection(first_position_type_ids, position_type_active_ids);
            positions = _.filter(sourceData.position_types_active, function(f) {
              return _.contains(position_ids, f._id.$oid);
            });
            for (m = 0, len4 = positions.length; m < len4; m++) {
              position_type = positions[m];
              position_type_id = position_type._id.$oid;
              position_type_name = position_type.name;
              data[day].areas[area_name].shifts[shift_name].positions[position_type_name] = {
                id: position_type_id,
                name: position_type_name,
                employees: position_employees[position_type_id]
              };
            }
          }
        }
      }
      return data;
    },
    filterImportDataByPosition: function(tableElement, columnNumber) {
      var filteredNames, filteredNamesLine, i, j, len, len1, line, ref, ref1, results, results1, tdValues, text, valuesThatShouldBeFiltered;
      filteredNames = $("#filtered_roles").val();
      if (_.isEmpty(filteredNames)) {
        return;
      }
      filteredNamesLine = filteredNames.replace(/ /g, "").toLowerCase().split(",");
      if ($("body").hasClass("csv-import-view")) {
        ref = $(tableElement).find("tbody tr");
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          line = ref[i];
          tdValues = _.compact(_.map($(line).find("td"), function(e) {
            return $(e).text().replace(/ /g, "").toLowerCase();
          }));
          valuesThatShouldBeFiltered = _.filter(tdValues, function(e) {
            return _.contains(filteredNamesLine, e);
          });
          if (valuesThatShouldBeFiltered.length > 0) {
            results.push($(line).remove());
          } else {
            results.push(void 0);
          }
        }
        return results;
      } else {
        ref1 = $(tableElement).find("tbody tr");
        results1 = [];
        for (j = 0, len1 = ref1.length; j < len1; j++) {
          line = ref1[j];
          text = $(line).find("td[data-column-number='" + columnNumber + "']").text().replace(/ /g, "").toLowerCase();
          if (_.contains(filteredNamesLine, text)) {
            results1.push($(line).remove());
          } else {
            results1.push(void 0);
          }
        }
        return results1;
      }
    },
    zeroHoursForFilteredRoles: function(tableElement, columnNumber) {
      var beginnings, endings, hoursWorked, hoursWorkedTd, i, len, line, positionName, ref, regexp, removeListArray, removeListString, results, zeroedPositionItems, zeroedPositions;
      if (!($("body").hasClass("brink-import-view") || $("body").hasClass("focus-import-view") || $("body").hasClass("toast-import-view"))) {
        return false;
      }
      removeListString = $("#filtered_roles").val();
      removeListArray = removeListString.split(",");
      regexp = /zerohours=[^,]+/;
      beginnings = /^\s*/;
      endings = /\s*$/;
      zeroedPositionItems = _.filter(removeListArray, function(f) {
        return !_.isEmpty(f.match(regexp));
      });
      zeroedPositions = _.map(zeroedPositionItems, function(f) {
        return f.replace("zerohours=", "").replace(beginnings, "").replace(endings, "").replace(/ /g, "").toLowerCase();
      });
      ref = $(tableElement).find("tbody tr");
      results = [];
      for (i = 0, len = ref.length; i < len; i++) {
        line = ref[i];
        positionName = $(line).find("td[data-column-number='" + columnNumber + "']").text().replace(/ /g, "").toLowerCase();
        if (_.contains(zeroedPositions, positionName)) {
          hoursWorkedTd = $(line).find("td[data-tm-field-name=':hours_worked']");
          hoursWorked = hoursWorkedTd.text();
          results.push($(line).find("td[data-tm-field-name=':hours_worked']").text("0"));
        } else {
          results.push(void 0);
        }
      }
      return results;
    },
    filterImportDataByEmployeeId: function(tableElement, columnNumber) {
      var filteredDataString, filteredElements, filteredEmployeeIds, filteredEmployeeIdsElements, i, len, line, ref, results, text;
      if (!($("body").hasClass("brink-import-view") || $("body").hasClass("focus-import-view") || $("body").hasClass("toast-import-view"))) {
        return false;
      }
      filteredDataString = $("#filtered_roles").val();
      filteredElements = filteredDataString.replace(/ /g, "").toLowerCase().split(",");
      filteredEmployeeIdsElements = _.filter(filteredElements, function(e) {
        return e.match(/empid=.+/);
      });
      filteredEmployeeIds = _.map(filteredEmployeeIdsElements, function(e) {
        return e.replace(/empid=/g, "");
      });
      ref = $(tableElement).find("tbody tr");
      results = [];
      for (i = 0, len = ref.length; i < len; i++) {
        line = ref[i];
        text = $(line).find("td[data-column-number='" + columnNumber + "']").text().replace(/ /g, "").toLowerCase();
        if (_.contains(filteredEmployeeIds, text)) {
          results.push($(line).remove());
        } else {
          results.push(void 0);
        }
      }
      return results;
    },
    bindFilteredRulesSaving: function(columnNumber, rebuildAction) {
      var filteredNames, filteredNamesLine, filteredRolesInput;
      filteredRolesInput = $("#filtered_roles");
      $(filteredRolesInput).attr("db-value", $(filteredRolesInput).val());
      filteredNames = $("#filtered_roles").val();
      filteredNamesLine = filteredNames.replace(/ /g, "").toLowerCase().split(",");
      return $("[data-action='save-filtered-roles']").click((function(_this) {
        return function(e) {
          var newValues, oldValues;
          e.preventDefault();
          oldValues = $("#filtered_roles").attr("db-value").replace(/ /g, "").toLowerCase().split(",");
          newValues = $("#filtered_roles").val().replace(/ /g, "").toLowerCase().split(",");
          if (_.some(_.difference(oldValues, newValues))) {
            return swal({
              title: "",
              text: "Updating remove roles list after table is built requires rebuilding the table. Are sure you want to continue?",
              type: "info",
              confirmButtonColor: "green",
              confirmButtonText: "Continue"
            }).then(function(isConfirmed) {
              if (isConfirmed.value) {
                return _this.sendUpdateRequest(columnNumber, rebuildAction);
              }
            });
          } else {
            return _this.sendUpdateRequest(columnNumber, false);
          }
        };
      })(this));
    },
    updateFilteredRolesData: function() {
      return $("#filtered_roles").attr("db-value", $("#filtered_roles").val());
    },
    sendUpdateRequest: function(columnNumber, rebuildAction) {
      var json;
      json = {};
      json[$("#filtered_roles").attr("data-filtered-roles-field")] = $("#filtered_roles").val();
      return $.ajax({
        url: "api/restaurants/1",
        method: "put",
        id: "1",
        data: {
          restaurant: json
        },
        success: (function(_this) {
          return function(response) {
            toastr["success"]("Roles updated.", "Success");
            if (rebuildAction) {
              rebuildAction();
            }
            Tipmetric.Controllers.helpers.filterImportDataByPosition($("#built-import-table"), columnNumber);
            Tipmetric.Controllers.helpers.filterImportDataByEmployeeId($("#built-import-table"), 2);
            Tipmetric.Controllers.helpers.zeroHoursForFilteredRoles($("#built-import-table"), 5);
            return _this.updateFilteredRolesData();
          };
        })(this),
        error: function(response) {
          return toastr["error"](response.responseText, "Error");
        }
      });
    },
    firstRaisingErrorMethod: function() {
      var e;
      console.log('starting first error script');
      try {
        throw new Error('Something went wrong - first method');
      } catch (error) {
        e = error;
        return Rollbar.error(e);
      }
    },
    secondRaisingErrorMethod: function() {
      console.log('starting second error script');
      return Rollbar.error('Something went wrong - second method');
    },
    thirdRaisingErrorMethod: function() {
      console.log('starting third error script');
      return null['error'];
    }
  };

}).call(this);
(function() {
  var base, base1;

  this.Tipmetric || (this.Tipmetric = {});

  (base = this.Tipmetric).Controllers || (base.Controllers = {});

  (base1 = this.Tipmetric).Modules || (base1.Modules = {});

  this.Tipmetric.Controllers.modalWrapper = {
    prepareWrapper: function(params) {
      var html;
      if ($("#" + params.wrapperId).length === 0) {
        html = JST["shared/modal_wrapper"](params);
        return $("#" + params.afterElementId).after(html);
      }
    }
  };

}).call(this);
(function() {
  var base, base1;

  this.Tipmetric || (this.Tipmetric = {});

  (base = this.Tipmetric).Controllers || (base.Controllers = {});

  (base1 = this.Tipmetric).Modules || (base1.Modules = {});

  this.Tipmetric.Controllers.notifier = {
    alert: function(title, message, buttonText, showCancel, callback) {
      var options;
      options = {
        title: title,
        text: message,
        type: "error",
        confirmButtonColor: "#BB473E",
        confirmButtonText: buttonText || "Ok"
      };
      if (showCancel) {
        options = _.extend(options, {
          showCancelButton: true,
          cancelButtonText: "Cancel"
        });
      }
      return swal(options, (function(_this) {
        return function(isConfirm) {
          if (callback && isConfirm) {
            callback();
          }
          return true;
        };
      })(this)).then((function(_this) {
        return function(isConfirm) {
          if (callback && isConfirm.value) {
            callback();
          }
          return true;
        };
      })(this));
    },
    info: function(title, message, buttonText, showCancel, callback) {
      var options;
      options = {
        title: title,
        text: message,
        type: "info",
        confirmButtonColor: "#28C256",
        confirmButtonText: buttonText || "Ok"
      };
      if (showCancel) {
        options = _.extend(options, {
          showCancelButton: true,
          cancelButtonText: "Cancel"
        });
      }
      return swal(options, (function(_this) {
        return function(isConfirm) {
          if (callback && isConfirm) {
            callback();
          }
          return true;
        };
      })(this)).then((function(_this) {
        return function(isConfirm) {
          if (callback && isConfirm.value) {
            callback();
          }
          return true;
        };
      })(this));
    }
  };

}).call(this);
(function() {
  window.bindSelect2 = function() {
    return $(".select-2").select2({
      theme: "bootstrap"
    });
  };

  window.bindSelectTo = function(element) {
    return $(element).select2({
      theme: 'bootstrap'
    });
  };

  window.bindMultipleSelectTo = function(element) {
    return $(element).select2({
      theme: 'bootstrap',
      multiple: true
    });
  };

  window.bindSelectWrappedBy = function(wrapper) {
    return $(wrapper).find(".select-2").select2({
      theme: 'bootstrap'
    });
  };

  window.bindPercentSelect = function() {
    var decimals;
    $(".touch-spin:not(.percentage-point)").TouchSpin({
      postfix: "<span data-distribution-type-elements='percents'>%</span> <span data-distribution-type-elements='points'>pts</span>",
      postfix_extraclass: "btn btn-default btn-xs",
      decimals: 2,
      step: 0.01
    });
    decimals = 2;
    if (typeof gon !== 'undefined' && gon && gon.restaurant && gon.restaurant.decimals_value) {
      decimals = gon.restaurant.decimals_value;
    }
    return $(".touch-spin.percentage-point").TouchSpin({
      postfix: "<span data-distribution-type-elements='percents'>%</span> <span data-distribution-type-elements='points'>pts</span>",
      postfix_extraclass: "btn btn-default btn-xs",
      decimals: decimals,
      step: 0.001
    });
  };

  window.bindCheckboxes = function() {
    return $('body.setup input[type=checkbox], ' + 'body.dashboard input[type=checkbox], ' + 'body.reports input[type=checkbox], ' + 'body.csv-import-view input[type=checkbox], ' + 'body.square-import-view input[type=checkbox], ' + 'body.toast-import-view input[type=checkbox], ' + 'body.focus-import-view input[type=checkbox]').iCheck({
      checkboxClass: 'icheckbox_square-green',
      radioClass: 'iradio_square-green'
    });
  };

  window.bindCheckboxesScoped = function(scope) {
    return $(scope).iCheck({
      checkboxClass: 'icheckbox_square-green',
      radioClass: 'iradio_square-green'
    });
  };

  window.setBrowserSupport = function() {
    if (navigator.userAgent.match(/OS X.*Safari/) && !navigator.userAgent.match(/Chrome/)) {
      return document.body.className += ' safari';
    }
  };

  window.formatMinutesToFloatHours = function(minutes) {
    var pad;
    pad = function(num) {
      if (num < 10) {
        return '0' + num;
      } else {
        return '' + num;
      }
    };
    return parseFloat([pad(Math.floor(minutes / 60) % 60), (pad(minutes % 60) * 100 / 60).toFixed(0)].join('.'));
  };

  window.formatMinutesToTimeFormat = function(minutes) {
    var pad;
    pad = function(num) {
      if (num < 10) {
        return '0' + num;
      } else {
        return '' + num;
      }
    };
    return [pad(Math.floor(minutes / 60) % 60), (pad(minutes % 60) * 100 / 60).toFixed(0)].join(':');
  };

  window.convertTimeFormatToFloat = function(time) {
    var hours, minutes, minutesFloated, pad;
    pad = function(num) {
      if (num < 10) {
        return '0' + num;
      } else {
        return '' + num;
      }
    };
    hours = time.split(":")[0];
    minutes = time.split(":")[1];
    minutesFloated = pad((parseFloat(minutes) * 100 / 60).toFixed(0));
    return hours + "." + minutesFloated;
  };

  window.summTimes = function(timeArray, i, totalsumm) {
    var formatTime, pad, time1, time2, timestrToMin;
    if (i == null) {
      i = 0;
    }
    if (totalsumm == null) {
      totalsumm = '00:00';
    }
    timestrToMin = function(timestr) {
      var parts;
      parts = timestr.split(':');
      return parseInt(parts[0] * 60) + parseInt(parts[1]);
    };
    pad = function(num) {
      if (num < 10) {
        return '0' + num;
      } else {
        return '' + num;
      }
    };
    formatTime = function(minutes) {
      return [pad(Math.floor(minutes / 60) % 60), pad(minutes % 60)].join(':');
    };
    time1 = totalsumm;
    time2 = timeArray[i];
    totalsumm = formatTime(timestrToMin(time1) + timestrToMin(time2));
    if (i + 1 === timeArray.length) {
      return totalsumm;
    } else {
      return window.summTimes(timeArray, i + 1, totalsumm);
    }
  };

  window.summTimesWithSeconds = function(timeArray, i, totalsumm) {
    var formatTime, make2digits, time1, time2, timestrToSeconds;
    if (i == null) {
      i = 0;
    }
    if (totalsumm == null) {
      totalsumm = '00:00:00';
    }
    timestrToSeconds = function(timestr) {
      var parts;
      parts = timestr.split(':');
      return parseInt(parts[0]) * 60 * 60 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
    };
    make2digits = function(num) {
      if (num < 10) {
        return '0' + num;
      } else {
        return '' + num;
      }
    };
    formatTime = function(seconds) {
      return [make2digits(Math.floor(seconds / (60 * 60)) % 60), make2digits(Math.floor(seconds / 60) % 60), make2digits(seconds % 60)].join(':');
    };
    time1 = totalsumm;
    time2 = timeArray[i];
    totalsumm = formatTime(timestrToSeconds(time1) + timestrToSeconds(time2));
    if (i + 1 === timeArray.length) {
      return totalsumm;
    } else {
      return window.summTimesWithSeconds(timeArray, i + 1, totalsumm);
    }
  };

  window.timeDifference = function(timeStart, timeEnd) {
    var hours, minutes, seconds;
    hours = moment(timeEnd).diff(moment(timeStart), 'hours');
    minutes = parseInt(moment(timeEnd).diff(moment(timeStart), 'minutes')) - 60 * hours;
    seconds = parseInt(moment(timeEnd).diff(moment(timeStart), 'seconds')) - 60 * 60 * hours - 60 * minutes;
    if (hours < 10) {
      hours = "0" + hours;
    }
    if (minutes < 10) {
      minutes = "0" + minutes;
    }
    if (seconds < 10) {
      seconds = "0" + seconds;
    }
    return hours + ":" + minutes + ":" + seconds;
  };

  String.prototype.cleanup = function() {
    return this.replace(/[^a-zA-Z0-9]+/g, "");
  };

  (function() {
    $.fn.editAndFocus = function(e) {
      this.attr("contenteditable", e);
      if (e === true) {
        this.removeAttr("disabled");
        return this.focus();
      } else {
        this.attr("disabled", !e);
        return this.blur();
      }
    };
    $.fn.saveData = function() {
      var el, j, len, ref;
      ref = $(this);
      for (j = 0, len = ref.length; j < len; j++) {
        el = ref[j];
        $(el).attr("data-original-value", $(el).val());
      }
      return this;
    };
    $.fn.restoreData = function(field) {
      var el, j, len, ref;
      ref = $(this);
      for (j = 0, len = ref.length; j < len; j++) {
        el = ref[j];
        $(el).val($(el).attr("data-original-value"));
      }
      return this.clearCashedData();
    };
    $.fn.clearCashedData = function(field) {
      var el, j, len, ref;
      ref = $(this);
      for (j = 0, len = ref.length; j < len; j++) {
        el = ref[j];
        $(el).removeAttr("data-original-value");
      }
      return this;
    };
    $.fn.disable = function(field) {
      var el, j, len, ref, results;
      ref = $(this);
      results = [];
      for (j = 0, len = ref.length; j < len; j++) {
        el = ref[j];
        results.push($(el).attr("disabled", "disabled"));
      }
      return results;
    };
    $.fn.enable = function(field) {
      var el, j, len, ref, results;
      ref = $(this);
      results = [];
      for (j = 0, len = ref.length; j < len; j++) {
        el = ref[j];
        results.push($(el).removeAttr("disabled"));
      }
      return results;
    };
    return $.fn.isDisabled = function(field) {
      return $(this).attr("disabled") === "disabled";
    };
  })();

  $(document).ready(function() {
    if (typeof gon !== "undefined" && gon !== null) {
      $("#profileChart").sparkline(gon.total_collected_money_data, {
        type: 'bar',
        barWidth: 7,
        height: '30px',
        barColor: '#62cb31',
        negBarColor: '#53ac2a'
      });
    }
    window.bindSelect2();
    window.bindPercentSelect();
    window.bindCheckboxes();
    return window.setBrowserSupport();
  });

}).call(this);
(function() {
  if (typeof gon !== "undefined" && typeof Rollbar !== "undefined" && typeof Rollbar.options !== "undefined") {
    if (gon.current_user) {
      Rollbar.options.payload.current_user = gon.current_user;
    }
    if (gon.calculation_params) {
      Rollbar.options.payload.calculation_params = gon.calculation_params;
    }
  }

}).call(this);
(function() {
  var base, base1;

  this.Tipmetric || (this.Tipmetric = {});

  (base = this.Tipmetric).Controllers || (base.Controllers = {});

  (base1 = this.Tipmetric).Modules || (base1.Modules = {});

  this.Tipmetric.Controllers.spinner = {
    show: function() {
      return $(".loader-spinner").show();
    },
    hide: function() {
      return $(".loader-spinner").hide();
    }
  };

}).call(this);
(function() {
  window.bindReportFormSubmit = function(form) {
    return $(form).on('submit', (function(_this) {
      return function(e) {
        var formData, format, xhr;
        format = $(form).attr("format");
        if (format !== "html") {
          e.preventDefault();
          Tipmetric.Controllers.spinner.show();
          xhr = new XMLHttpRequest();
          xhr.open('POST', form.attr('action'));
          xhr.responseType = 'blob';
          formData = new FormData($(form)[0]);
          xhr.send(formData);
          return xhr.onload = function(evt) {
            var a, blob, name, url;
            if (this.status === 200) {
              name = xhr.getResponseHeader("Content-Filename");
              if (format === "csv") {
                blob = new Blob([this.response], {
                  type: 'text/csv',
                  encoding: 'base64'
                });
              } else if (format === "xlsx") {
                blob = new Blob([this.response], {
                  type: 'Mime::XLSX',
                  encoding: 'base64'
                });
              } else if (format === "xls") {
                blob = new Blob([this.response], {
                  type: 'Mime::XLS',
                  encoding: 'base64'
                });
              }
              a = document.createElement("a");
              a.style = "display: none";
              document.body.appendChild(a);
              url = window.URL.createObjectURL(blob);
              a.href = url;
              a.download = name + "." + format;
              a.click();
              window.URL.revokeObjectURL(url);
            } else if (this.status === 403) {
              window.toastr["error"]("No data found for that range.", "Error");
            } else {
              window.toastr["error"]("Looks like something went wrong.", "Error");
            }
            return Tipmetric.Controllers.spinner.hide();
          };
        }
      };
    })(this));
  };

}).call(this);
(function() {
  var extend = function(child, parent) { for (var key in parent) { if (hasProp.call(parent, key)) child[key] = parent[key]; } function ctor() { this.constructor = child; } ctor.prototype = parent.prototype; child.prototype = new ctor(); child.__super__ = parent.prototype; return child; },
    hasProp = {}.hasOwnProperty;

  this.Tipmetric.module("AdminPanel", function(AdminPanel, App, Backbone, Marionette, $, _) {
    AdminPanel.Router = (function(superClass) {
      extend(Router, superClass);

      function Router() {
        return Router.__super__.constructor.apply(this, arguments);
      }

      Router.prototype.appRoutes = {
        "history": "loadHistory",
        "tip_sharings": "loadTipSharings",
        "employees/history": "loadHistory"
      };

      return Router;

    })(Marionette.AppRouter);
    return App.addInitializer(function() {
      return new AdminPanel.Router({
        controller: AdminPanel.Controller
      });
    });
  });

  this.Tipmetric.module("AdminPanel", function(AdminPanel, App, Backbone, Marionette, $, _) {
    AdminPanel.Controller = {
      loadHistory: function(e) {
        var historyLayoutView;
        historyLayoutView = new AdminPanel.HistoryLayoutView;
        return this.updateHistory(e);
      },
      loadTipSharings: function(e) {
        var tipSharingsHistoryLayoutView;
        return tipSharingsHistoryLayoutView = new Tipmetric.Calculation.HistoryView({
          el: "body"
        });
      },
      updateHistory: function(e) {
        if (e) {
          e.preventDefault();
        }
        $(".day-calculation-block").remove();
        App.Data.history_data = [];
        $(".load-next-days").attr("data-page", 0);
        return this.loadAdditionalHistoryData();
      },
      checkIfNextButtonIsNeeded: function(data) {
        if (data.length < 89) {
          return $(".load-next-days").hide();
        } else {
          return $(".load-next-days").show();
        }
      },
      loadAdditionalHistoryData: function() {
        var data, url;
        if (!_.isEmpty(gon.initial_history_date_data)) {
          data = _.pairs(gon.initial_history_date_data);
          this.checkIfNextButtonIsNeeded(data);
          gon.initial_history_date_data = [];
          this.seedHistoryData(data);
          return;
        }
        Tipmetric.Controllers.spinner.show();
        url = gon.current_user.workposition !== 'staff' ? "/api/calculations" : "/employees/api/calculations";
        return $.ajax({
          url: url,
          method: "get",
          data: {
            year: $("#year").val(),
            page: $(".load-next-days").attr("data-page")
          },
          success: (function(_this) {
            return function(response) {
              _this.checkIfNextButtonIsNeeded(_.pairs(response.calculations));
              if (_.isEmpty(App.Data.history_data)) {
                data = _.pairs(response.calculations);
              } else {
                data = _.union(_.pairs(response.calculations), App.Data.history_data);
              }
              return _this.seedHistoryData(data);
            };
          })(this)
        });
      },
      seedHistoryData: function(data) {
        var lastBlockHeight, lastBlockOfftop, nextPage, page, scrollToLastBlock;
        App.Data.history_data = (_.sortBy(data, function(f) {
          return f[0];
        })).reverse();
        if ($(".day-calculation-block").length > 0) {
          scrollToLastBlock = true;
          lastBlockHeight = $(".day-calculation-block").last().height();
          lastBlockOfftop = $(".day-calculation-block").last().offset().top;
        }
        App.historyContentRegion.show(new Tipmetric.Calculation.HistoryView());
        Tipmetric.Controllers.spinner.hide();
        if (scrollToLastBlock) {
          $(window).scrollTop(lastBlockOfftop + lastBlockHeight);
        }
        page = parseInt($(".load-next-days").attr("data-page"));
        nextPage = page + 1;
        return $(".load-next-days").attr("data-page", nextPage);
      }
    };
    return AdminPanel.HistoryLayoutView = (function(superClass) {
      extend(HistoryLayoutView, superClass);

      function HistoryLayoutView() {
        return HistoryLayoutView.__super__.constructor.apply(this, arguments);
      }

      HistoryLayoutView.prototype.template = _.noop;

      HistoryLayoutView.prototype.el = "body.history";

      HistoryLayoutView.prototype.events = {
        "change .history-year-select-wrapper select": "updateHistoryData",
        "click .load-next-days": "loadAdditionalHistoryData"
      };

      HistoryLayoutView.prototype.updateHistoryData = function(e) {
        return Tipmetric.AdminPanel.Controller.updateHistory(e);
      };

      HistoryLayoutView.prototype.loadAdditionalHistoryData = function(e) {
        e.preventDefault();
        return Tipmetric.AdminPanel.Controller.loadAdditionalHistoryData();
      };

      return HistoryLayoutView;

    })(Marionette.LayoutView);
  });

}).call(this);
(function() {
  $(document).ready(function() {
    $(".add-assistent .fa").click(function() {
      return $(".add-assistent-form").show();
    });
    return $(".assistent-email-check").blur(function(e) {
      var scope;
      scope = $(e.currentTarget).closest("form");
      return $.ajax({
        url: "/managers/assistents/check_existance",
        method: "get",
        data: {
          email: $(e.currentTarget).val()
        },
        success: function(response) {
          return scope.find(".email-belongs-to-payroll").hide();
        },
        error: function(response) {
          scope.find(".email-belongs-to-manager, .add-existing-payroll").hide();
          if (response.responseJSON.error_code === 1) {
            swal({
              title: "",
              text: response.responseJSON.user_info.email + " is associated with " + response.responseJSON.user_info.restaurants + "... Would you like to link " + gon.restaurant.name + "?",
              type: "info",
              confirmButtonColor: "#28C256",
              confirmButtonText: "Yes",
              showCancelButton: true,
              cancelButtonText: "Cancel"
            }).then((function(_this) {
              return function(isConfirmed) {
                if (isConfirmed.value) {
                  scope.find("input[name='name']").val(response.responseJSON.user_info.name);
                  scope.find("input[name='password']").removeAttr("required");
                  scope.find("input[name='password_confirmation']").removeAttr("required");
                  scope.find("input[name='add_restaurant']").val("true");
                  return scope.submit();
                } else {
                  scope.find(".assistent-email-check").val("");
                  scope.find("input[name='password']").attr("required", "required");
                  scope.find("input[name='password_confirmation']").attr("required", "required");
                  return scope.find("input[name='add_restaurant']").val("");
                }
              };
            })(this));
          }
          if (response.responseJSON.error_code === 2) {
            scope.find(".assistent-email-check").val("").focus();
            scope.find(".email-belongs-to-manager").show();
          }
          if (response.responseJSON.error_code === 3) {
            scope.find(".assistent-email-check").val("").focus();
            return scope.find(".email-belongs-to-payroll").show();
          }
        }
      });
    });
  });

}).call(this);
(function() {
  (function() {
    var BillingView;
    BillingView = Backbone.View.extend({
      el: "body.billing",
      container: "#billing-view",
      data: {},
      events: {
        'click .card .make-default': 'makeDefaultCard',
        'click .card .delete': 'deleteCard',
        'click .plan-wrapper .cancel': 'cancel',
        "change .period_editable": 'updatePlanPrice',
        "change .use-card": 'chooseCard',
        'click .toggle-billing-pages': 'toggleBillingPages'
      },
      initialize: function() {
        this.stripe = Stripe(gon.stripe_key);
        this.elements = this.stripe.elements();
        return this.renderSubscriptionBlocks();
      },
      oldCardChoosen: function() {
        return $(".use-card:checked").attr("data-card-id");
      },
      chooseCard: function(e) {
        var card, currentCardCheckbox, i, isChecked, len, ref, results;
        currentCardCheckbox = $(e.currentTarget);
        isChecked = $(currentCardCheckbox).is(":checked");
        if (isChecked) {
          ref = $(".use-card");
          results = [];
          for (i = 0, len = ref.length; i < len; i++) {
            card = ref[i];
            if ($(card).attr("data-card-id") !== $(currentCardCheckbox).attr("data-card-id")) {
              results.push($(card).prop('checked', false));
            } else {
              results.push(void 0);
            }
          }
          return results;
        }
      },
      toggleBillingPages: function() {
        if ($('body').attr('data-page-rendered') === 'billing') {
          return this.renderStatisticsBlocks();
        } else {
          return this.renderSubscriptionBlocks();
        }
      },
      renderSubscriptionBlocks: function() {
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          url: "/billing_user",
          method: "get",
          success: (function(_this) {
            return function(response) {
              var source;
              Tipmetric.Controllers.spinner.hide();
              gon.user_charges = response.user_charges;
              gon.stripe_current_user = response.stripe_current_user;
              source = _.findWhere(gon.stripe_current_user.sources.data, {
                id: gon.stripe_current_user.default_source
              });
              if (source) {
                if (source.object === 'source') {
                  gon.default_card = source.card;
                } else {
                  gon.default_card = source;
                }
              }
              $('body').attr('data-page-rendered', 'billing');
              _this.renderPage('billing');
              _this.createCard(gon);
              _this.bindSubmit(gon);
              _this.generateCards(gon);
              return _this.updatePlanPrice(gon);
            };
          })(this),
          error: function(response) {
            return Tipmetric.Controllers.spinner.hide();
          }
        });
      },
      renderPage: function(page) {
        var html;
        html = JST["billing/" + page](gon);
        return $(this.container).html(html);
      },
      subscribe: function(e) {
        var billingPeriod, cardChoosen, companySize, planName;
        companySize = $("[name='company_size']").val();
        billingPeriod = $("[name='billing_period']:checked").val();
        if (gon.stripe_plans.periods[billingPeriod] && _.any(_.findWhere(gon.stripe_plans.periods[billingPeriod].plans, {
          company_size: companySize
        }))) {
          planName = this.data.planSelected;
          Tipmetric.Controllers.spinner.show();
          cardChoosen = this.oldCardChoosen() || this.cardChoosen;
          return $.ajax({
            url: "/api/subscriptions",
            method: "post",
            data: {
              subscription_plan: planName,
              cardChoosen: cardChoosen
            },
            success: (function(_this) {
              return function(response) {
                toastr["success"]("Subscription created", "Success");
                Tipmetric.Controllers.spinner.hide();
                return _this.clearBillingForm();
              };
            })(this),
            error: function(response) {
              toastr["error"](response.responseText, "Error");
              return Tipmetric.Controllers.spinner.hide();
            }
          });
        } else {
          return toastr["error"]("Please choose subscription plan.", "Error");
        }
      },
      clearBillingForm: function() {
        Tipmetric.Controllers.billingView.card.clear();
        $(".use-card").prop('checked', false);
        $(".period_editable").prop("checked", false);
        return this.updatePlanPrice();
      },
      updatePlanPrice: function(gon) {
        var billingPeriod, companySize, planId;
        companySize = $("[name='company_size']").val();
        billingPeriod = $("[name='billing_period']:checked").val();
        planId = companySize + "-" + billingPeriod;
        $(".prices-wrapper").attr("data-plan-selected", planId);
        return this.data.planSelected = planId;
      },
      createCard: function(gon) {
        var style;
        style = {
          base: {
            fontSize: '16px',
            lineHeight: '24px'
          }
        };
        this.card = this.card || this.elements.create('card', {
          style: style
        });
        this.card.mount('#card-element');
        return this.card.addEventListener('change', function(event) {
          var displayError;
          displayError = document.getElementById('card-errors');
          if (event.error) {
            return displayError.textContent = event.error.message;
          } else {
            return displayError.textContent = '';
          }
        });
      },
      bindSubmit: function(gon) {
        this.form = document.getElementById('payment-form');
        return this.form.addEventListener('submit', (function(_this) {
          return function(event) {
            event.preventDefault();
            if (_.any(gon.stripe_current_user.subscriptions.data)) {
              return swal({
                title: "",
                text: "You already have subscriptions. Are you sure you want to add another one?",
                type: "info",
                confirmButtonColor: "#28C256",
                confirmButtonText: "Yes",
                showCancelButton: true,
                cancelButtonText: "Cancel"
              }, function(isConfirmed) {
                if (isConfirmed) {
                  return _this.startSubscriptionProcess();
                }
              }).then(function(isConfirmed) {
                if (isConfirmed.value) {
                  return _this.startSubscriptionProcess();
                }
              });
            } else {
              return _this.startSubscriptionProcess();
            }
          };
        })(this));
      },
      startSubscriptionProcess: function() {
        if (this.oldCardChoosen()) {
          return this.subscribe();
        } else {
          return this.stripe.createToken(this.card).then((function(_this) {
            return function(result) {
              var errorElement;
              if (result.error) {
                errorElement = document.getElementById('card-errors');
                return errorElement.textContent = result.error.message;
              } else {
                return _this.stripeTokenHandler(result.token, true, false);
              }
            };
          })(this));
        }
      },
      stripeTokenHandler: function(response, startSubscribing, showNoticeAfterCardCreated) {
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          url: "/api/cards",
          method: "post",
          data: {
            token: response.id
          },
          success: (function(_this) {
            return function(card_response) {
              if (showNoticeAfterCardCreated) {
                if (_.any($(".cards-list div[data-card-id='" + card_response.id + "']"))) {
                  toastr["error"]("This card is already added", "Error");
                } else {
                  toastr["success"]("Card successfully saved", "Success");
                }
              }
              _this.generateHtmlForCard(card_response);
              Tipmetric.Controllers.spinner.hide();
              _this.cardChoosen = card_response.id;
              if (startSubscribing) {
                return _this.subscribe();
              }
            };
          })(this),
          error: function(card_response) {
            toastr["error"](card_response.responseText, "Error");
            return Tipmetric.Controllers.spinner.hide();
          }
        });
      },
      renderStatisticsBlocks: function() {
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          url: "/billing_user",
          method: "get",
          success: (function(_this) {
            return function(response) {
              Tipmetric.Controllers.spinner.hide();
              gon.user_charges = response.user_charges;
              gon.stripe_current_user = response.stripe_current_user;
              $('body').attr('data-page-rendered', 'subscription');
              _this.renderPage('subscriptions');
              _this.createCard(gon);
              _this.bindCardCreation();
              _this.generateCards(gon);
              _this.generateCharges(gon);
              return _this.generateSubscriptions(gon);
            };
          })(this),
          error: function(response) {
            return Tipmetric.Controllers.spinner.hide();
          }
        });
      },
      bindCardCreation: function(gon) {
        return $(".add-card").on('click', (function(_this) {
          return function(event) {
            event.preventDefault();
            return _this.createNewCard();
          };
        })(this));
      },
      createNewCard: function(e) {
        return this.stripe.createToken(this.card).then((function(_this) {
          return function(result) {
            var errorElement;
            if (result.error) {
              errorElement = document.getElementById('card-errors');
              return errorElement.textContent = result.error.message;
            } else {
              return _this.stripeTokenHandler(result.token, false, true);
            }
          };
        })(this));
      },
      cancel: function(e) {
        var planName;
        planName = $(e.currentTarget).closest(".plan-wrapper").attr("data-plan-name");
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          url: "/api/subscriptions",
          method: "delete",
          data: {
            subscription_plan: planName
          },
          success: function(response) {
            $(e.currentTarget).closest(".plan-wrapper").remove();
            toastr["success"]("Subscription canceled", "Success");
            return Tipmetric.Controllers.spinner.hide();
          },
          error: function(response) {
            toastr["error"](response.responseText, "Error");
            return Tipmetric.Controllers.spinner.hide();
          }
        });
      },
      generateCards: function(gon) {
        var card, i, len, ref, results;
        ref = gon.stripe_current_user.sources.data;
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          card = ref[i];
          if (gon.stripe_current_user.default_source === card.id) {
            card.is_default = true;
          } else {
            card.is_default = false;
          }
          results.push(this.generateHtmlForCard(card));
        }
        return results;
      },
      generateSubscriptions: function() {
        var html, i, len, ref, results, subscription;
        ref = gon.stripe_current_user.subscriptions.data;
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          subscription = ref[i];
          html = JST["billing/subscription"](subscription);
          results.push($(".subscriptions-list").append(html));
        }
        return results;
      },
      generateCharges: function(gondata) {
        var charge, html, i, len, ref, results;
        ref = gondata.user_charges.data;
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          charge = ref[i];
          if (!_.contains(gon.hidden_charges, charge.id)) {
            html = JST["billing/charge"](charge);
            results.push($(".charges-list").append(html));
          } else {
            results.push(void 0);
          }
        }
        return results;
      },
      generateHtmlForCard: function(params) {
        var html;
        if (!_.any($(".cards-list div[data-card-id='" + params.id + "']"))) {
          html = JST["billing/card"](params);
          return $(".cards-list").append(html);
        }
      },
      makeDefaultCard: function(e) {
        var card_id;
        e.preventDefault();
        card_id = $(e.currentTarget).closest(".card").attr('data-card-id');
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          url: "/api/cards",
          method: "patch",
          data: {
            card_id: card_id
          },
          success: function(response) {
            var card, i, len, ref;
            ref = $(".card");
            for (i = 0, len = ref.length; i < len; i++) {
              card = ref[i];
              $(card).attr('data-card-is-default', false);
            }
            $(".card[data-card-id='" + card_id + "']").attr('data-card-is-default', true);
            toastr["success"]("Card successfully updated", "Success");
            return Tipmetric.Controllers.spinner.hide();
          },
          error: function(response) {
            toastr["error"](response.responseText, "Error");
            return Tipmetric.Controllers.spinner.hide();
          }
        });
      },
      deleteCard: function(e) {
        var card_div, card_id;
        e.preventDefault();
        card_div = $(e.currentTarget).closest(".card");
        card_id = $(e.currentTarget).closest(".card").attr('data-card-id');
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          url: "/api/cards",
          method: "delete",
          data: {
            card_id: card_id
          },
          success: function(response) {
            toastr["success"]("Card successfully removed", "Success");
            card_div.remove();
            return Tipmetric.Controllers.spinner.hide();
          },
          error: function(response) {
            toastr["error"](response.responseText, "Error");
            return Tipmetric.Controllers.spinner.hide();
          }
        });
      },
      showPlan: function(companySize) {
        if (gon.restaurant.id === "58be0c824c75e96b50000001") {
          return true;
        }
        if (companySize === 'test' || companySize === 'ruby-discount' || companySize === 'ruby-discount-10' || companySize === 'gold-discount' || companySize === 'gold-discount-10' || companySize === 'small-notax' || companySize === 'large-notax' || companySize === 'custom-gaspar') {
          if ((companySize === 'small-notax' || companySize === 'large-notax') && (gon.restaurant.id === "5d7a49c7c86de25f3fe245c4")) {
            return true;
          }
          if (companySize === 'test' && gon.current_user_testing_payments) {
            return true;
          }
          if ((companySize === 'ruby-discount-10' || companySize === 'gold-discount-10') && (gon.restaurant.id === "5a46833d4c75e91ca0000135" || gon.restaurant.id === "5c630ac3c86de26fe864cd97" || gon.restaurant.id === "5c63188cc86de26feb6f646e" || gon.restaurant.id === "5c632713c86de26fe54bfd1b" || gon.restaurant.id === "5d1e7e36c86de2496e4b3a53" || gon.restaurant.id === "5d31edaec86de25e7d0afb16" || gon.restaurant.id === "5d161d39c86de24790b3d612" || gon.restaurant.id === "5d2e7c8cc86de22e503fcf15" || gon.restaurant.id === "5d2e17fec86de272965c2134" || gon.restaurant.id === "5d2a0043c86de24971077c10" || gon.restaurant.id === "5d2e8a0ec86de22e503fcf5e" || gon.restaurant.id === "5c6477f2c86de26fe864d258")) {
            return true;
          }
          if (companySize === 'ruby-discount' && (gon.restaurant.name === "Vintry Wine & Whiskey" || gon.restaurant.name === "Bathtub Gin" || gon.restaurant.name === "Bar a Vin" || gon.restaurant.id === "5c41e9e9c86de27fdb8006f5" || gon.restaurant.id === "5e3c5f94c86de264970a8f36")) {
            return true;
          }
          if (companySize === 'gold-discount' && (gon.restaurant.name === "Ulysses" || gon.restaurant.name === "Harrys Cafe & Steak")) {
            return true;
          }
          if (companySize === 'custom-gaspar' && gon.restaurant.name === "gaspar") {
            return true;
          }
          if (companySize === 'ruby-discount' && gon.restaurant.id === "5dc430a2c86de23f5f0ceea9") {
            return true;
          }
          if (companySize === 'ruby-discount' && gon.restaurant.id === "5e20a8a5c86de23bb5d23ec2") {
            return true;
          }
          return false;
        } else {
          if ((companySize === 'small' || companySize === 'large') && (gon.restaurant.id === "5d7a49c7c86de25f3fe245c4")) {
            return false;
          }
          return true;
        }
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("billing")) {
        return Tipmetric.Controllers.billingView = new BillingView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var BrinkImportView;
    BrinkImportView = Backbone.View.extend({
      el: "#brink-import-view",
      tm_data_fields: [":hours_worked", ":net_sales", ":tip_amount", ":declared", ":food_summ", ":beverage_summ"],
      events: {
        "click #change-brink-config": "changeBrinkConfig",
        "change #area_type_id": "areaUpdated",
        "change #shift_type_id": "shiftUpdated",
        "click #run": "getData",
        "click #import": "sendImportData",
        "click [data-action='show-brink-config']": "toggleTokenBlock",
        "click [data-action='remove-row-inline']": "removeRowInline",
        "click [data-action='show-brink-sto-rules']": "showBrinkStoRules",
        "click .import-errors [data-action='remove']": "removeRow",
        "click .import-errors [data-action='add']": "addEmployee",
        "click .import-errors [data-action='create']": "createEmployee",
        "click .import-errors [data-action='remove-match']": "removeMatchEmployee",
        "click .import-errors [data-action='select-position']": "selectPosition",
        "click .import-errors [data-action='merge-duplications']": "mergeDuplications",
        "click .import-errors [data-action='remove-duplicated-line']": "removeDuplication",
        "click [data-action='get-report-group-ids']": "getReportGroupIds",
        "click [data-action='add-report-group']": "addGroupToInput",
        "click [data-action='remove-report-group']": "removeGroupToInput",
        "click [data-action='close-report-group-popup']": "closeReportGroupPopup",
        "blur #position_tip_sharings [data-recalculate='true']": "setPercents",
        "change [data-required-field='true']": "checkIfDataValid"
      },
      getReportGroupIds: function(e) {
        e.preventDefault();
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          url: "/api/brink/get_report_groups",
          method: "get",
          success: (function(_this) {
            return function(response) {
              var reportGroupsJson;
              console.log(response);
              reportGroupsJson = {};
              reportGroupsJson['currentValuesList'] = $("#brink_sales_beverage_categories_ids").val().replace(/ /g, "").split(",");
              reportGroupsJson['allRevenueCenterValues'] = response;
              _this.buildReportIdsPopup(reportGroupsJson);
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this)
        });
      },
      closeReportGroupPopup: function() {
        $("#report-group-ids-popup .list").html('');
        return $("#report-group-ids-popup-wrapper").hide();
      },
      addGroupToInput: function(e) {
        var currentValuesList, id, newList;
        id = $(e.currentTarget).closest("div").attr("data-group-id");
        $(e.currentTarget).closest("div").attr("data-added-to-list", true);
        currentValuesList = $("#brink_sales_beverage_categories_ids").val().split(",");
        newList = _.uniq(_.compact(_.union(currentValuesList, [id])));
        console.log("newList " + newList);
        return $("#brink_sales_beverage_categories_ids").val(newList.join(",").replace(/ /g, ""));
      },
      removeGroupToInput: function(e) {
        var currentValuesList, id, newList;
        id = $(e.currentTarget).closest("div").attr("data-group-id").toString();
        $(e.currentTarget).closest("div").attr("data-added-to-list", false);
        currentValuesList = $("#brink_sales_beverage_categories_ids").val().replace(/ /g, "").split(",");
        console.log(id);
        console.log(currentValuesList);
        newList = _.uniq(_.compact(_.without(currentValuesList, id)));
        console.log("newList " + newList);
        return $("#brink_sales_beverage_categories_ids").val(newList.join(","));
      },
      buildReportIdsPopup: function(reportGroupsJson) {
        var html;
        html = JST["brink_import/report_groups_popup"](reportGroupsJson);
        $("#report-group-ids-popup .list").html(html);
        return $("#report-group-ids-popup-wrapper").show();
      },
      checkIfDataValid: function(e) {
        var blankFields, fields;
        fields = $("#calculation-form-wrapper").find("input[name='date'], select[name='area_type_id'], select[name='shift_type_id'], select[id='source_position_ids']");
        blankFields = _.filter(fields, function(e) {
          return $(e).val() === null || $(e).val().length === 0 || $(e).val().length === void 0;
        }) || [];
        if (blankFields.length === 0) {
          return $("#run").removeAttr("disabled");
        } else {
          return $("#run").attr("disabled", "disabled");
        }
      },
      removeDuplication: function(e) {
        var currentParagraph, duplicationBlock, lineNumber, tr;
        duplicationBlock = $(e.currentTarget).closest(".employee-is-duplicated");
        currentParagraph = $(e.currentTarget).closest(".employee-duplication-line");
        lineNumber = $(e.currentTarget).attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        $(tr).remove();
        $(currentParagraph).remove();
        if ($(duplicationBlock).find(".employee-duplication-line").length === 0) {
          $(duplicationBlock).remove();
        }
        return this.setPercents();
      },
      mergeDuplications: function(e) {
        var dataType, duplicationBlock, empId, finalAddedNumbers, finalFilledLine, i, j, k, len, len1, len2, lineNumbers, lineToRemove, lines, otherLines, summ, tableRows, td, tm_data_fields_to_add, tr, values;
        duplicationBlock = $(e.currentTarget).closest(".employee-is-duplicated");
        lines = $(duplicationBlock).find(".employee-duplication-line");
        lineNumbers = _.collect(lines, function(line) {
          return $(line).attr("data-line-number");
        });
        empId = $(duplicationBlock).attr("data-emp-id");
        tm_data_fields_to_add = this.tm_data_fields;
        finalAddedNumbers = {};
        tableRows = _.filter($(".brink-import-data tr[data-emp-id='" + empId + "']"), function(f) {
          return _.contains(lineNumbers, $(f).attr("data-row-number"));
        });
        for (i = 0, len = tm_data_fields_to_add.length; i < len; i++) {
          dataType = tm_data_fields_to_add[i];
          values = _.map($(tableRows).find("td[data-tm-field-name='" + dataType + "']"), function(f) {
            return parseFloat($(f).text()) || 0;
          });
          if (!_.isEmpty(values)) {
            summ = _.reduce(values, function(memo, num) {
              return memo + num;
            }) || 0;
            finalAddedNumbers[dataType] = summ.toFixed(2);
          }
        }
        finalFilledLine = lineNumbers[0];
        for (j = 0, len1 = tm_data_fields_to_add.length; j < len1; j++) {
          dataType = tm_data_fields_to_add[j];
          if (finalAddedNumbers[dataType]) {
            td = $(".brink-import-data tr[data-emp-id='" + empId + "'][data-row-number='" + finalFilledLine + "'] td[data-tm-field-name='" + dataType + "'] ");
            $(td).attr("data-value", finalAddedNumbers[dataType]);
            $(td).text(finalAddedNumbers[dataType]);
          }
        }
        otherLines = _.filter(lineNumbers, function(n) {
          return n !== finalFilledLine;
        });
        for (k = 0, len2 = otherLines.length; k < len2; k++) {
          lineToRemove = otherLines[k];
          tr = this.$el.find("tr[data-emp-id='" + empId + "'][data-row-number='" + lineToRemove + "']");
          tr.remove();
        }
        $(duplicationBlock).remove();
        return this.setPercents();
      },
      selectPosition: function(e) {
        var currentParagraph, lineNumber, positionId, tr;
        currentParagraph = $(e.currentTarget).closest(".employee-in-multiple-positions-wrapper");
        positionId = $(currentParagraph).find("select.position-type-select option:selected").val();
        lineNumber = $(e.currentTarget).closest(".employee-in-multiple-positions-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        tr.attr("data-position-type-id", positionId);
        $(currentParagraph).remove();
        return this.setPercents();
      },
      removeMatchEmployee: function(e) {
        var currentParagraph, lineNumber, tr;
        currentParagraph = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper, .employees-doesnt-belong-to-any-position-wrapper");
        lineNumber = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper, .employees-doesnt-belong-to-any-position-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        $(tr).remove();
        $(currentParagraph).remove();
        return this.setPercents();
      },
      addEmployee: function(e) {
        var currentParagraph;
        currentParagraph = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        return currentParagraph.find(".doesnt-exist-warning-form").show();
      },
      createEmployee: function(e) {
        var brinkId, currentParagraph, empId, firstName, lastName, positionId;
        e.preventDefault();
        currentParagraph = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        positionId = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper").find("select.position-type-select option:selected").val();
        empId = $(currentParagraph).find("input[name='emp_id']").val();
        brinkId = $(currentParagraph).attr("data-brink-id");
        firstName = $(currentParagraph).find("input[name='first_name']").val();
        lastName = $(currentParagraph).find("input[name='last_name']").val();
        return $.ajax({
          url: "api/employees",
          method: "post",
          data: {
            employee: {
              emp_id: empId,
              first_name: firstName,
              brink_id: brinkId,
              last_name: lastName,
              position_type_ids: [positionId]
            }
          },
          success: (function(_this) {
            return function(response) {
              $(currentParagraph).remove();
              toastr["success"]("Emloyee has been created.", "Success");
              return _this.setPercents();
            };
          })(this),
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      removeRow: function(e) {
        var row, rowNumber;
        rowNumber = $(e.currentTarget).attr("data-row-number");
        row = this.$el.find("tr[data-row-number='" + rowNumber + "']");
        row.remove();
        $(e.currentTarget).closest(".doesnt-exist-warning-wrapper").remove();
        return this.setPercents();
      },
      initialize: function() {
        gon.restaurant_inheritance_data = Tipmetric.Controllers.helpers.buildSmartData();
        $.fn.editable.defaults.mode = 'inline';
        $.fn.editable.defaults.ajaxOptions = {
          type: "PUT"
        };
        $.fn.editableform.buttons = "<button type=\"submit\" class=\"btn btn-sm btn-success editable-submit\">SAVE</button> <button type=\"button\" class=\"btn btn-sm btn-default editable-cancel\">CANCEL</button>";
        $('#brink-access-token, #brink-location-token').editable({
          type: 'text',
          url: (function(_this) {
            return function(params) {
              var obj;
              return $.ajax({
                url: "api/restaurants/1",
                method: "put",
                id: "1",
                data: {
                  restaurant: (
                    obj = {},
                    obj["" + (params.name.replace(/-/g, "_"))] = params.value,
                    obj
                  )
                },
                success: function(response) {
                  $("#" + params.name).removeAttr("contenteditable");
                  $("#access-token-wrapper").attr("token-form-status", "locked");
                  return toastr["success"]("Access Token updated.", "Success");
                },
                error: function(response) {
                  return toastr["error"](response.responseText, "Error");
                }
              });
            };
          })(this)
        });
        if (this.tokenAdded()) {
          this.bindLocationsSelect();
        }
        $('.datapicker').datepicker().on("changeDate", (function(_this) {
          return function() {
            return _this.dateUpdated();
          };
        })(this)).on("show", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').addClass('open');
          };
        })(this)).on("hide", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').removeClass('open');
          };
        })(this));
        this.dateUpdated();
        this.areaUpdated();
        this.shiftUpdated();
        this.bindLBWCategoryIdsSaving();
        return Tipmetric.Controllers.helpers.bindFilteredRulesSaving(5, function() {
          return Tipmetric.Controllers.brinkImportView.buildTable();
        });
      },
      removeRowInline: function(e) {
        $(e.currentTarget).closest('tr').remove();
        return this.setPercents();
      },
      tokenAdded: function() {
        var accessAdded, locationAdded;
        accessAdded = $("#brink-access-token").text().replace(/ /g, '').replace(/\n/g, '').length > 30;
        locationAdded = $("#brink-location-token").text().replace(/ /g, '').replace(/\n/g, '').length > 30;
        return accessAdded && locationAdded;
      },
      toggleTokenBlock: function(e) {
        return $("#access-token-wrapper-block").toggle();
      },
      showBrinkStoRules: function(e) {
        if (e) {
          e.preventDefault();
        }
        return $(".brink-sto-rules-table-wrapper").toggle();
      },
      changeBrinkConfig: function(e) {
        $(".location-id").attr("contenteditable", "true");
        $("#access-token-wrapper").attr("token-form-status", "editing");
        return e.preventDefault();
      },
      setPercents: function() {
        var amBartenderId, barbackHoursValues, barbackHoursValuesSumm, barbackId, beverageSalesValues, beverageSalesValuesSumm, bohId, counterKitchenId, netSalesValues, netSalesValuesSumm, percent, pmBartenderId, tr, trs, virtualPoolId;
        if (gon.restaurant.id === "5f04b425c86de264f39b6ac5") {
          virtualPoolId = "5f297a3fc86de24e92d6a0cb";
          counterKitchenId = "5f04b4f7c86de264f17bdf4f";
          amBartenderId = "5f297980c86de24e8fbbd65f";
          pmBartenderId = "5f29798bc86de24e9899fef7";
          barbackId = "5f04b503c86de264eb92faba";
        } else if (gon.restaurant.id === "5f594c6ac86de2163e85b547") {
          virtualPoolId = "5f594ccfc86de2163e85b548";
          counterKitchenId = "5f594cadc86de216411e6c8e";
          amBartenderId = "5f594cbac86de21646e9b2aa";
          pmBartenderId = "5f594cc0c86de216411e6c8f";
          barbackId = "5f594cb3c86de21646e9b2a9";
        } else if (gon.restaurant.id === "5f5be24ec86de2241c3e3597") {
          virtualPoolId = "5f5be2c2c86de2241c3e359b";
          counterKitchenId = "5f5be29bc86de2241fef5d3d";
          amBartenderId = "5f5be2aac86de2241c3e3599";
          pmBartenderId = "5f5be2b0c86de2241c3e359a";
          barbackId = "5f5be2a1c86de2241c3e3598";
        } else if (gon.restaurant.id === "5f5be337c86de22419ecdd7e") {
          virtualPoolId = "5f5be3cbc86de2241fef5d3e";
          counterKitchenId = "5f5be3acc86de2241c3e370a";
          amBartenderId = "5f5be3b8c86de22422f32fc6";
          pmBartenderId = "5f5be3c0c86de22422f32fc7";
          barbackId = "5f5be3b2c86de22422f32fc4";
        } else if (gon.restaurant.id === "5f5be474c86de22422f32fc9") {
          virtualPoolId = "5f5be4bfc86de2241c3e3881";
          counterKitchenId = "5f5be4a0c86de2241c3e3880";
          amBartenderId = "5f5be4aec86de22422f32fcb";
          pmBartenderId = "5f5be4b4c86de22419ecdeee";
          barbackId = "5f5be4a6c86de22422f32fca";
        } else if (gon.restaurant.id === "5f5be4f6c86de22422f32fcd") {
          virtualPoolId = "5f5be556c86de2241fef5d40";
          counterKitchenId = "5f5be531c86de22419ece05f";
          amBartenderId = "5f5be53ec86de22422f33146";
          pmBartenderId = "5f5be547c86de22422f33147";
          barbackId = "5f5be538c86de22422f33145";
          bohId = "5f5fb35ec86de25954b13f3c";
        } else if (gon.restaurant.id === "5fac3174c86de20e17513204") {
          virtualPoolId = "5fac32cec86de20e17513205";
          amBartenderId = "5fac32b6c86de20e0e402d9b";
          pmBartenderId = "5fac32bdc86de20e1438f595";
          counterKitchenId = "5fac32a7c86de20e0e402d9a";
          barbackId = "5fac32aec86de20e116dbde7";
        } else if (gon.restaurant.id === "62019bb3c86de2646c07240a") {
          virtualPoolId = "620c04eac86de274a3c266ff";
          counterKitchenId = "620bf38ac86de274a626f3fb";
          amBartenderId = "62019c63c86de26467654336";
          pmBartenderId = "620c3cb3c86de274a99a7b88";
          barbackId = "62019c73c86de264644119d5";
        } else {
          return false;
        }
        netSalesValues = _.map($(".brink-import-data tr[data-identifier='eod_till']").find("td[data-tm-field-name=':net_sales']"), function(f) {
          return parseFloat($(f).text()) || 0;
        });
        netSalesValuesSumm = (_.reduce(netSalesValues, function(sum, num) {
          return sum + num;
        }) || 0).toFixed(2);
        beverageSalesValues = _.map($(".brink-import-data tr[data-identifier='eod_till']").find("td[data-tm-field-name=':beverage_summ']"), function(f) {
          return parseFloat($(f).text()) || 0;
        });
        beverageSalesValuesSumm = (_.reduce(beverageSalesValues, function(sum, num) {
          return sum + num;
        }) || 0).toFixed(2);
        if (gon.restaurant.id === "5f5be4f6c86de22422f32fcd") {
          percent = (((netSalesValuesSumm - beverageSalesValuesSumm) / netSalesValuesSumm) * 100).toFixed(2);
        } else {
          percent = (((netSalesValuesSumm - beverageSalesValuesSumm) / netSalesValuesSumm) * 100).toFixed(2);
        }
        tr = $("tr.position-tip-sharing[data-source-position-id='" + virtualPoolId + "'][data-target-position-id='" + counterKitchenId + "'][data-source-type='cc']");
        tr.find("input.percentage").val(percent).trigger("change");
        if (gon.restaurant.id === "5f5be4f6c86de22422f32fcd") {
          percent = (((netSalesValuesSumm - beverageSalesValuesSumm) / netSalesValuesSumm) * 0).toFixed(2);
          tr = $("tr.position-tip-sharing[data-source-position-id='" + virtualPoolId + "'][data-target-position-id='" + bohId + "'][data-source-type='cc']");
          tr.find("input.percentage").val(percent).trigger("change");
        }
        if (gon.restaurant.id !== "62019bb3c86de2646c07240a") {
          trs = _.filter($(".brink-import-data").find("tr"), function(f) {
            return $(f).find("td[data-tm-field-name=':job_code']").text().toLowerCase() === "barback";
          });
          barbackHoursValues = _.map(trs, function(f) {
            return parseFloat($(f).find("td[data-tm-field-name=':hours_worked']").text()) || 0;
          });
          barbackHoursValuesSumm = _.reduce(barbackHoursValues, function(sum, num) {
            return sum + num;
          }) || 0;
          percent = (0.375 * barbackHoursValuesSumm).toFixed(2);
          tr = $("tr.position-tip-sharing[data-source-position-id='" + amBartenderId + "'][data-target-position-id='" + barbackId + "'][data-source-type='foodAndBeverageSalesSumm']");
          tr.find("input.percentage").val(percent).trigger("change");
          tr = $("tr.position-tip-sharing[data-source-position-id='" + pmBartenderId + "'][data-target-position-id='" + barbackId + "'][data-source-type='foodAndBeverageSalesSumm']");
          return tr.find("input.percentage").val(percent).trigger("change");
        }
      },
      setEditablePercents: function() {
        var am_barback_tm_id, am_busser_tm_id, am_foodRunner_tm_id, barback_job_id, barback_tm_id, busser_job_id, busser_tm_id, foodRunner_job_id, foodRunner_tm_id, i, id, j, k, l, len, len1, len2, len3, len4, len5, m, o, percent, ref, ref1, ref2, ref3, ref4, ref5, results, results1, tr, trs;
        gon.updateTikiBartenderToTikiBarbackRule = true;
        if (gon.restaurant.id === "5f5be24ec86de2241c3e3597") {
          tr = $("tr.position-tip-sharing[data-source-position-id='5f88763cc86de2777868a2e8'][data-target-position-id='60b6514ec86de22e9a9d263d'][data-source-type='cc']");
          if (_.isEmpty(_.findWhere(Tipmetric.Controllers.brinkImportView.lastFetchedData.default_employees, {
            job_id: "640496834"
          }))) {
            percent = 0;
            tr.find("input.percentage").val(percent).trigger("change");
            gon.updateTikiBartenderToTikiBarbackRule = false;
          } else {
            percent = $(tr).attr("data-original-percentage");
            tr.find("input.percentage").val(percent).trigger("change");
          }
        }
        gon.forbiddenUpdateRulesList = [""];
        gon.settingCalculationRulesToZeroList = [""];
        if (gon.restaurant.id === "62019bb3c86de2646c07240a") {
          foodRunner_tm_id = "620c50cbc86de274a3c270fd";
          barback_tm_id = "620c50e4c86de274a99a7bf9";
          busser_tm_id = "620c50ddc86de274a3c270fe";
          am_foodRunner_tm_id = "62019c67c86de26467654337";
          am_barback_tm_id = "62019c73c86de264644119d5";
          am_busser_tm_id = "62019c6fc86de26467654338";
          foodRunner_job_id = "640450778";
          barback_job_id = "640464375";
          busser_job_id = "4013188";
          if (_.isEmpty(_.findWhere(Tipmetric.Controllers.brinkImportView.lastFetchedData.default_employees, {
            job_id: busser_job_id
          }))) {
            trs = $("tr.position-tip-sharing[data-source-position-id='" + busser_tm_id + "'], tr.position-tip-sharing[data-target-position-id='" + busser_tm_id + "'], tr.position-tip-sharing[data-source-position-id='" + am_busser_tm_id + "'], tr.position-tip-sharing[data-target-position-id='" + am_busser_tm_id + "']");
            ref = $(trs);
            for (i = 0, len = ref.length; i < len; i++) {
              tr = ref[i];
              id = $(tr).attr("data-position-tip-sharing-id");
              $(tr).find("input.percentage").val(0).trigger("change");
              gon.forbiddenUpdateRulesList.push(id);
              gon.settingCalculationRulesToZeroList.push(id);
            }
          } else {
            trs = $("tr.position-tip-sharing[data-source-position-id='" + busser_tm_id + "'], tr.position-tip-sharing[data-target-position-id='" + busser_tm_id + "'], tr.position-tip-sharing[data-source-position-id='" + am_busser_tm_id + "'], tr.position-tip-sharing[data-target-position-id='" + am_busser_tm_id + "']");
            ref1 = $(trs);
            for (j = 0, len1 = ref1.length; j < len1; j++) {
              tr = ref1[j];
              percent = $(tr).attr("data-original-percentage");
              $(tr).find("input.percentage").val(percent).trigger("change");
            }
          }
          if (_.isEmpty(_.findWhere(Tipmetric.Controllers.brinkImportView.lastFetchedData.default_employees, {
            job_id: foodRunner_job_id
          }))) {
            trs = $("tr.position-tip-sharing[data-source-position-id='" + foodRunner_tm_id + "'], tr.position-tip-sharing[data-target-position-id='" + foodRunner_tm_id + "'], tr.position-tip-sharing[data-source-position-id='" + am_foodRunner_tm_id + "'], tr.position-tip-sharing[data-target-position-id='" + am_foodRunner_tm_id + "']");
            ref2 = $(trs);
            for (k = 0, len2 = ref2.length; k < len2; k++) {
              tr = ref2[k];
              id = $(tr).attr("data-position-tip-sharing-id");
              $(tr).find("input.percentage").val(0).trigger("change");
              gon.forbiddenUpdateRulesList.push(id);
              gon.settingCalculationRulesToZeroList.push(id);
            }
          } else {
            trs = $("tr.position-tip-sharing[data-source-position-id='" + foodRunner_tm_id + "'], tr.position-tip-sharing[data-target-position-id='" + foodRunner_tm_id + "'], tr.position-tip-sharing[data-source-position-id='" + am_foodRunner_tm_id + "'], tr.position-tip-sharing[data-target-position-id='" + am_foodRunner_tm_id + "']");
            ref3 = $(trs);
            for (l = 0, len3 = ref3.length; l < len3; l++) {
              tr = ref3[l];
              percent = $(tr).attr("data-original-percentage");
              $(tr).find("input.percentage").val(percent).trigger("change");
            }
          }
          if (_.isEmpty(_.findWhere(Tipmetric.Controllers.brinkImportView.lastFetchedData.default_employees, {
            job_id: barback_job_id
          }))) {
            trs = $("tr.position-tip-sharing[data-source-position-id='" + barback_tm_id + "'], tr.position-tip-sharing[data-target-position-id='" + barback_tm_id + "'], tr.position-tip-sharing[data-source-position-id='" + am_barback_tm_id + "'], tr.position-tip-sharing[data-target-position-id='" + am_barback_tm_id + "']");
            ref4 = $(trs);
            results = [];
            for (m = 0, len4 = ref4.length; m < len4; m++) {
              tr = ref4[m];
              id = $(tr).attr("data-position-tip-sharing-id");
              $(tr).find("input.percentage").val(0).trigger("change");
              gon.forbiddenUpdateRulesList.push(id);
              results.push(gon.settingCalculationRulesToZeroList.push(id));
            }
            return results;
          } else {
            trs = $("tr.position-tip-sharing[data-source-position-id='" + barback_tm_id + "'], tr.position-tip-sharing[data-target-position-id='" + barback_tm_id + "'], tr.position-tip-sharing[data-source-position-id='" + am_barback_tm_id + "'], tr.position-tip-sharing[data-target-position-id='" + am_barback_tm_id + "']");
            ref5 = $(trs);
            results1 = [];
            for (o = 0, len5 = ref5.length; o < len5; o++) {
              tr = ref5[o];
              percent = $(tr).attr("data-original-percentage");
              results1.push($(tr).find("input.percentage").val(percent).trigger("change"));
            }
            return results1;
          }
        }
      },
      bindLBWCategoryIdsSaving: function(rebuildAction) {
        var LBWCategoryIdsInput, filteredNames, filteredNamesLine;
        LBWCategoryIdsInput = $("#brink_sales_beverage_categories_ids");
        $(LBWCategoryIdsInput).attr("db-value", $(LBWCategoryIdsInput).val());
        filteredNames = $("#brink_sales_beverage_categories_ids").val();
        filteredNamesLine = filteredNames.replace(/ /g, "").toLowerCase().split(",");
        return $("[data-action='save-brink-sales-beverage-categories-ids']").click((function(_this) {
          return function(e) {
            var newValues, oldValues;
            e.preventDefault();
            oldValues = $("#brink_sales_beverage_categories_ids").attr("db-value").replace(/ /g, "").toLowerCase().split(",");
            newValues = $("#brink_sales_beverage_categories_ids").val().replace(/ /g, "").toLowerCase().split(",");
            gon.restaurant.brink_sales_beverage_categories_ids = newValues;
            if (_.some(_.difference(oldValues, newValues))) {
              return swal({
                title: "",
                text: "Updating beverage categories ids list after table is built requires rebuilding the table. Are sure you want to continue?",
                type: "info",
                confirmButtonColor: "green",
                confirmButtonText: "Continue"
              }).then(function(isConfirmed) {
                if (isConfirmed.value) {
                  return _this.sendLBWCategoryIdsUpdateRequest(newValues, rebuildAction);
                }
              });
            } else {
              return _this.sendLBWCategoryIdsUpdateRequest(newValues, false);
            }
          };
        })(this));
      },
      sendLBWCategoryIdsUpdateRequest: function(newValues, rebuildAction) {
        var json;
        json = {};
        json["brink_sales_beverage_categories_ids"] = newValues;
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: {
            restaurant: json
          },
          success: (function(_this) {
            return function(response) {
              toastr["success"]("Sales Beverage Category ids updated. Please press RUN button again.", "Success");
              return _this.updateLBWCategoriesIdsData();
            };
          })(this),
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      updateLBWCategoriesIdsData: function() {
        return $("#brink_sales_beverage_categories_ids").attr("db-value", $("#brink_sales_beverage_categories_ids").val());
      },
      sendImportData: function(e) {
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return false;
        }
        e.preventDefault();
        window.brink_json = this.buildImportJson();
        return this.sendCheckBrinkImportDataRequest(false, void 0);
      },
      sendCheckBrinkImportDataRequest: function(allowUpdate, e) {
        var data;
        if (e && $(e.currentTarget).attr("disabled") !== void 0) {
          return false;
        }
        data = this.buildImportJson();
        data.allowUpdate = allowUpdate;
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          method: "post",
          url: "/api/import/brink/check_data",
          data: data,
          success: (function(_this) {
            return function(response) {
              _this.matchIdentifiedemployees(response);
              _this.$el.find(".import-errors > *").html('');
              return _this.sendImportBrinkRequest(allowUpdate);
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              var duplications, employee, employeeEmpId, html1, html3, html4, html5, html6, i, j, k, l, len, len1, len2, len3, len4, m, ref, ref1, ref2, ref3, ref4;
              console.log('error');
              window.response = response;
              _this.matchIdentifiedemployees(response.responseJSON);
              Tipmetric.Controllers.spinner.hide();
              Tipmetric.TestSupport.displayErrors(response.responseJSON.errors);
              if (response.responseJSON.critical_error) {
                swal({
                  title: "",
                  text: response.responseJSON.critical_error_message,
                  type: "error",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "OK",
                  showCancelButton: false
                });
                return;
              }
              if (response.responseJSON.calculation_exists === true && allowUpdate === false) {
                return swal({
                  title: "",
                  text: response.responseJSON.errors,
                  type: "info",
                  confirmButtonColor: "#c30",
                  confirmButtonText: "Continue",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirmed) {
                  if (isConfirmed.value) {
                    return _this.sendCheckBrinkImportDataRequest(true, e);
                  }
                });
              } else {
                if (response.responseJSON.errors) {
                  toastr["error"](response.responseJSON.errors, "Error");
                  return;
                }
                html1 = '';
                ref = response.responseJSON.employees_doesnt_exist;
                for (i = 0, len = ref.length; i < len; i++) {
                  employee = ref[i];
                  html1 += JST["csv_import/does_not_exist"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-exist").html(html1);
                html3 = '';
                ref1 = response.responseJSON.name_duplication;
                for (j = 0, len1 = ref1.length; j < len1; j++) {
                  employee = ref1[j];
                  html3 += JST["csv_import/last_name_duplication"](employee);
                }
                _this.$el.find(".import-errors .last-name-duplication").html(html3);
                html4 = '';
                ref2 = response.responseJSON.employee_in_multiple_positions;
                for (k = 0, len2 = ref2.length; k < len2; k++) {
                  employee = ref2[k];
                  html4 += JST["csv_import/employee_in_multiple_positions"](employee);
                }
                _this.$el.find(".import-errors .employee-in-multiple-positions").html(html4);
                html5 = '';
                ref3 = response.responseJSON.employees_doesnt_belong_to_any_position;
                for (l = 0, len3 = ref3.length; l < len3; l++) {
                  employee = ref3[l];
                  html5 += JST["csv_import/employees_doesnt_belong_to_any_position"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-belong-to-any-position").html(html5);
                html6 = '';
                ref4 = _.keys(response.responseJSON.duplicated_employees_grouped);
                for (m = 0, len4 = ref4.length; m < len4; m++) {
                  employeeEmpId = ref4[m];
                  duplications = _.map(response.responseJSON.duplicated_employees_grouped[employeeEmpId], function(f) {
                    f.filledFields = _this.filledFields(f);
                    return f;
                  });
                  html6 += JST["csv_import/duplicated_employee"](duplications);
                }
                _this.$el.find(".import-errors .employees-are-duplicated").html(html6);
                return toastr["error"]("Looks like import was not successfull.", "Error");
              }
            };
          })(this)
        });
      },
      filledFields: function(data) {
        return data[':hours_worked'] + " (" + ':hours_worked' + ")";
      },
      matchIdentifiedemployees: function(response) {
        var employee, i, len, ref, results, tr;
        window.response = response;
        ref = response.identified_employees;
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          employee = ref[i];
          tr = this.$el.find(".brink-import-data tr[data-row-number='" + employee.row_number + "']");
          results.push(tr.attr("data-emp-id", employee.employee_data.emp_id));
        }
        return results;
      },
      sendImportBrinkRequest: function(allowUpdate) {
        this.buildImportJson();
        this.data.allowUpdate = allowUpdate;
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          method: "post",
          url: "/api/import/brink/send_data",
          data: this.data,
          success: (function(_this) {
            return function(response) {
              toastr["success"]("Data has been imported.", "Success");
              Tipmetric.Controllers.spinner.hide();
              return _this.resetImportData();
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              Tipmetric.Controllers.spinner.hide();
              if (response.responseJSON.calculation_exists === true) {
                return swal({
                  title: "",
                  text: response.responseJSON.errors,
                  type: "info",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "Yes",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirmed) {
                  if (isConfirmed.value) {
                    return _this.sendImportBrinkRequest(true);
                  }
                });
              } else {
                return toastr["error"]("Data is not imported.", "Error");
              }
            };
          })(this)
        });
      },
      buildImportJson: function() {
        var employeeData, employeeTr, fieldName, i, id, j, len, len1, position_type_id, ref, ref1, rowNumber, td, value;
        this.data = {
          employeesData: [],
          frontend_restaurant_id: gon.restaurant.id,
          update_tikiBartender_to_tiki_barback_rule: gon.updateTikiBartenderToTikiBarbackRule,
          forbiddenUpdateRulesList: gon.forbiddenUpdateRulesList,
          settingCalculationRulesToZeroList: gon.settingCalculationRulesToZeroList
        };
        this.buildSTORules();
        this.data.calcualtionParams = {
          date: this.$el.find("#date").val(),
          area_type_id: this.$el.find("select#area_type_id").val(),
          shift_type_id: this.$el.find("select#shift_type_id").val(),
          source_position_type_ids: this.$el.find("select#source_position_ids").val(),
          teams_quantity: 1
        };
        ref = $(".brink-import-data tr");
        for (i = 0, len = ref.length; i < len; i++) {
          employeeTr = ref[i];
          employeeData = {};
          id = $(employeeTr).attr("data-emp-id");
          if (!_.isEmpty(id)) {
            employeeData[":emp_id"] = id;
          }
          id = $(employeeTr).attr("data-brink-id");
          if (!_.isEmpty(id)) {
            employeeData[":brink_id"] = id;
          }
          position_type_id = $(employeeTr).attr("data-position-type-id");
          if (!_.isEmpty(position_type_id)) {
            employeeData["position_type_id"] = position_type_id;
          }
          rowNumber = $(employeeTr).attr("data-row-number");
          employeeData["row_number"] = rowNumber;
          ref1 = $(employeeTr).find("td");
          for (j = 0, len1 = ref1.length; j < len1; j++) {
            td = ref1[j];
            fieldName = $(td).attr("data-tm-field-name");
            value = $(td).text();
            if (!_.isEmpty(fieldName) && !_.isEmpty(value)) {
              employeeData[fieldName] = value;
            }
          }
          this.data.employeesData.push(employeeData);
        }
        return this.data;
      },
      buildSTORules: function() {
        var i, len, positionsThatReceiveTipSharingIds, ref, results, tr;
        this.data.sourceTipSharings = _.map($("#position_tip_sharings .position-tip-sharing"), (function(_this) {
          return function(tr) {
            var json;
            json = {
              positionTipSharingId: $(tr).attr("data-position-tip-sharing-id"),
              sourcePositionId: $(tr).find("select.source-position-select").val(),
              percentage: parseFloat($(tr).find("input.percentage").val()),
              sourceType: $(tr).find("select.source-type-select").val(),
              targetPositionId: $(tr).find("select.target-position-select").val(),
              tempId: $(tr).attr("data-temp-id")
            };
            if (gon.restaurant.sto_condition_rules_enabled) {
              json.conditionFirst = $(tr).find("select.condition-first-select").val();
              json.conditionType = $(tr).find("select.condition-type-select").val();
              json.conditionSecond = parseFloat($(tr).find("input.condition-second-input").val());
            }
            return json;
          };
        })(this));
        positionsThatReceiveTipSharingIds = _.uniq(_.map($("#position_tip_sharings select.target-position-select"), function(f) {
          return $(f).val();
        }));
        this.data.sourceTipSharingsHash = {};
        ref = _.map($("#position_tip_sharings .position-tip-sharing"));
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          tr = ref[i];
          this.data.sourceTipSharingsHash[$(tr).attr("data-temp-id")] = {
            positionTipSharingId: $(tr).attr("data-position-tip-sharing-id"),
            sourcePositionId: $(tr).find("select.source-position-select").val(),
            sourcePositionName: $(tr).find("select.source-position-select option:selected").text(),
            percentage: parseFloat($(tr).find("input.percentage").val()),
            sourceType: $(tr).find("select.source-type-select").val(),
            targetPositionId: $(tr).find("select.target-position-select").val(),
            tempId: $(tr).attr("data-temp-id"),
            employeesAppliedTo: []
          };
          if (gon.restaurant.sto_condition_rules_enabled) {
            this.data.sourceTipSharingsHash[$(tr).attr("data-temp-id")].conditionEnabled = true;
            this.data.sourceTipSharingsHash[$(tr).attr("data-temp-id")].conditionFirst = $(tr).find("select.condition-first-select").val();
            this.data.sourceTipSharingsHash[$(tr).attr("data-temp-id")].conditionType = $(tr).find("select.condition-type-select").val();
            results.push(this.data.sourceTipSharingsHash[$(tr).attr("data-temp-id")].conditionSecond = parseFloat($(tr).find("input.condition-second-input").val()));
          } else {
            results.push(void 0);
          }
        }
        return results;
      },
      resetImportData: function(e) {
        this.$el.find("#built-import-table").remove();
        return this.$el.find(".import-errors > *").html("");
      },
      buildTable: function() {
        var html;
        html = JST["brink_import/employees"](this.lastFetchedData);
        this.$el.find("#employees-wrapper").html(html);
        this.setPercents();
        Tipmetric.Controllers.helpers.zeroHoursForFilteredRoles($("#built-import-table"), 5);
        Tipmetric.Controllers.helpers.filterImportDataByPosition($("#built-import-table"), 5);
        return Tipmetric.Controllers.helpers.filterImportDataByEmployeeId($("#built-import-table"), 2);
      },
      activePositionIds: function() {
        return _.map($("#source_position_ids option"), function(e) {
          return $(e).attr("value");
        });
      },
      dateUpdated: function() {
        var area, areas, dayName, i, len, options, savedVal;
        if (!_.isEmpty(this.$el.find("#date").val())) {
          this.$el.find("#date").attr("data-saved-date", this.$el.find("#date").val());
        }
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        if (gon.restaurant_inheritance_data[dayName]) {
          areas = _.map(gon.restaurant_inheritance_data[dayName].areas, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          options = "";
          options += "<option value=''>Area</option>";
          for (i = 0, len = areas.length; i < len; i++) {
            area = areas[i];
            options += "<option value='" + area.id + "'>" + area.name + "</option>";
          }
          savedVal = this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          this.$el.find("#calculation-form-wrapper").find("select#area_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val(savedVal).trigger("change");
        } else {
          this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val("").trigger("change").html("<option value=''>Area</option>");
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return this.$el.find("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      areaUpdated: function() {
        var areaName, dayName, i, len, options, savedVal, shift, shifts;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option[value='" + (this.$el.find("select#area_type_id").val()) + "']").text();
        if (this.areaEmpty()) {
          return false;
        }
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas[areaName]) {
          shifts = _.map(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts, function(e) {
            return {
              id: e.id,
              name: e.name,
              start_time: e.start_time,
              end_time: e.end_time
            };
          });
          options = "";
          options += "<option value=''>Shift</option>";
          for (i = 0, len = shifts.length; i < len; i++) {
            shift = shifts[i];
            options += "<option value='" + shift.id + "'>" + shift.name + "</option>";
          }
          savedVal = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val(savedVal).trigger("change");
        } else {
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return this.$el.find("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      availablePositions: function() {
        var areaName, dayName, shift, shiftId, shiftName;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option:selected").text();
        shiftId = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id option:selected").val();
        shift = _.find(_.pairs(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts), function(f) {
          return f[1].id === shiftId;
        });
        if (shift) {
          shiftName = shift[0];
        }
        if (gon.restaurant_inheritance_data[dayName].areas[areaName] && shiftName && gon.restaurant_inheritance_data[dayName].areas[areaName].shifts[shiftName]) {
          return gon.restaurant_inheritance_data[dayName].areas[areaName].shifts[shiftName].positions;
        } else {
          return false;
        }
      },
      areaEmpty: function() {
        return _.isEmpty(this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val());
      },
      shiftUpdated: function() {
        var areaData, areaName, dayName, firstPosition, i, len, options, position, positions, savedVal, shift, shiftId, shiftName;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option[value='" + ($('select#area_type_id').val()) + "']").text();
        if (this.areaEmpty()) {
          return false;
        }
        shiftId = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id option:selected").val();
        shift = _.find(_.pairs(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts), function(f) {
          return f[1].id === shiftId;
        });
        if (shift) {
          shiftName = shift[0];
        }
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas && areaName) {
          areaData = gon.restaurant_inheritance_data[dayName].areas[areaName];
        }
        if (areaData && shift && areaData.shifts[shiftName]) {
          positions = _.map(areaData.shifts[shiftName].positions, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          if (_.some(positions)) {
            firstPosition = positions[0].id;
          } else {
            firstPosition = "";
          }
          options = "";
          for (i = 0, len = positions.length; i < len; i++) {
            position = positions[i];
            options += "<option value='" + position.id + "'>" + position.name + "</option>";
          }
          savedVal = this.$el.find("select#source_position_ids").val();
          if (!savedVal) {
            savedVal = firstPosition;
          }
          this.$el.find("select#source_position_ids").html(options);
          return this.$el.find("select#source_position_ids").val(savedVal).trigger("change");
        } else {
          return this.$el.find("select#source_position_ids").val("").trigger("change");
        }
      },
      getData: function(e) {
        var data;
        e.preventDefault();
        data = {
          date: moment(this.$el.find("#date").val()).format("YYYY-MM-DD"),
          area_type_id: this.$el.find("#area_type_id").val(),
          shift_type_id: this.$el.find("#shift_type_id").val(),
          position_type: this.$el.find("#source_position_ids").val()
        };
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          url: "/api/brink/get_data",
          method: "get",
          data: data,
          success: (function(_this) {
            return function(response) {
              _this.lastFetchedData = response;
              _this.buildTable();
              $("#import").removeAttr("disabled");
              Tipmetric.Controllers.spinner.hide();
              return _this.setEditablePercents();
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              Tipmetric.Controllers.spinner.hide();
              return swal({
                title: "",
                text: response.responseJSON.error_message,
                type: "error",
                confirmButtonColor: "green",
                confirmButtonText: "Continue"
              });
            };
          })(this)
        });
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("brink-import-view")) {
        return Tipmetric.Controllers.brinkImportView = new BrinkImportView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var DistributionsView;
    DistributionsView = Backbone.View.extend({
      el: "#distributions-list-wrapper",
      events: {
        "click [data-action='add-employee']": "addEmployee",
        "click [data-action='edit-employee'], [data-action='edit-employee-cancel']": "editEmployee",
        "click [data-action='remove-employee-distribution']": "removeEmployee",
        "blur .employee-distribution-line input": "setBlankInput",
        "blur .employee-distribution-line .number-hrs": "transferHoursFormat",
        "blur .employee-distribution-line .number-hrs": "markBlankField",
        "ifChanged .toggle-tip-outs": "toggleTipOuts",
        "ifChanged .toggle-sales": "toggleSales",
        "ifChanged .toggle-food-sales": "toggleFoodSales",
        "ifChanged .toggle-ag-svc": "toggleAgSvc",
        "ifChanged .toggle-fee": "toggleFee"
      },
      initialize: function(options) {
        this.scope = options.scope;
        this.checkSalesIsShown();
        this.removeRequestIsInProcess = false;
        this.filterEmployeeInfo();
        this.lockInspectorPanel();
        return this.bindSelectsForEmployees();
      },
      bindSelectsForEmployees: function() {
        return $(".select-2-ed").select2({
          theme: "bootstrap",
          templateResult: this.select2Template,
          templateSelection: this.select2Template
        });
      },
      select2Template: function(state) {
        var $state, calc_date, employee_position_status, hidden_from, isActive, position_type_id, position_type_name;
        position_type_id = $(state.element).closest(".position-block").attr("data-position-block-id");
        position_type_name = $(state.element).closest(".position-block").attr("data-position-block-name");
        if (!gon.restaurant.hidden_from_enabled) {
          isActive = true;
        } else if (!_.isEmpty(_.findWhere(gon.restaurant_inheritance_data_raw.employee_position_statuses, {
          employee_id: state.id,
          position_type_id: position_type_id
        }))) {
          employee_position_status = _.findWhere(gon.restaurant_inheritance_data_raw.employee_position_statuses, {
            employee_id: state.id,
            position_type_id: position_type_id
          });
          hidden_from = moment(employee_position_status.hidden_from);
          calc_date = moment($("#calculation-form-wrapper").find("input#date").val());
          if (calc_date >= hidden_from) {
            isActive = false;
          } else {
            isActive = true;
          }
        } else {
          isActive = true;
        }
        if (!state.id) {
          return state.text;
        }
        $state = $(("<span data-position-enabled='" + isActive + "'>") + state.text + "</span>");
        return $state;
      },
      lockInspectorPanel: function() {
        if ($("body").attr("restrict-access") === "true" && $("body").hasClass("calculation")) {
          $("body").attr("oncontextmenu", "return false;");
          key('ctrl+r,ctrl+shift+i, ctrl+shift+j, ctrl+u, f12', function() {
            return false;
          });
          return key('⌘+alt+i, ⌘+alt+c, ⌘+alt+k, ⌘+alt+z, ⌘+alt+e, shift+f7, shift+f5, shift+f9, shift+f12, ⌘+shift+j, ⌘+u, ⌘+i, f12', function() {
            return false;
          });
        }
      },
      filterEmployeeInfo: function() {
        var currentEmployeeId, currentEmployeePositionblocks, distributionLine, i, len, positionBlock, ref, results;
        if ($("body").attr("data-role") !== "staff") {
          return;
        }
        if ($("body").attr("data-employee-history-is-filtered") !== "true") {
          return;
        }
        if ($("body").attr("data-calculation-record-action") !== "look") {
          return;
        }
        currentEmployeeId = $("body").attr("data-current-employee-id");
        currentEmployeePositionblocks = [];
        ref = $(".position-block");
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          positionBlock = ref[i];
          if ($(positionBlock).find(".employee-distribution-line[data-employee-id='" + currentEmployeeId + "']").length > 0) {
            $(positionBlock).attr("data-filtered", false);
            results.push((function() {
              var j, len1, ref1, results1;
              ref1 = $(positionBlock).find(".employee-distribution-line");
              results1 = [];
              for (j = 0, len1 = ref1.length; j < len1; j++) {
                distributionLine = ref1[j];
                if ($(distributionLine).attr("data-employee-id") === currentEmployeeId) {
                  results1.push($(distributionLine).attr("data-filtered", false));
                } else {
                  results1.push($(distributionLine).attr("data-filtered", true));
                }
              }
              return results1;
            })());
          } else {
            results.push($(positionBlock).attr("data-filtered", true));
          }
        }
        return results;
      },
      markBlankField: function(e) {
        if ($(e.currentTarget).val() === "" || parseFloat($(e.currentTarget).val()) === 0) {
          return $(e.currentTarget).addClass("blank-hours");
        } else {
          return $(e.currentTarget).removeClass("blank-hours");
        }
      },
      transferHoursFormat: function(e) {
        var electronicHourFormat, finalHoursText, hour, input, minutes, minutesToDecimal, minutesToDecimalText, text;
        if (!gon.restaurant.transfer_hours_format_enabled) {
          return true;
        }
        electronicHourFormat = /\d*:\d*/;
        input = $(e.currentTarget);
        text = $(input).val();
        if (electronicHourFormat.test(text)) {
          hour = text.split(":")[0];
          minutes = text.split(":")[1];
          minutesToDecimal = (minutes * 100 / 60).toFixed(0);
          if (minutesToDecimal < 10) {
            minutesToDecimalText = "0" + minutesToDecimal;
          } else {
            minutesToDecimalText = minutesToDecimal;
          }
          finalHoursText = hour + "." + minutesToDecimalText;
          $(input).val(finalHoursText);
          return this.scope.calculate();
        }
      },
      setBlankInput: function(e) {
        if ($(e.currentTarget).val() === "") {
          $(e.currentTarget).val($(e.currentTarget).attr("placeholder"));
          return this.scope.calculate();
        }
      },
      checkSalesIsShown: function() {
        if ($(".toggle-sales").length > 0) {
          return $("#distributions-list").attr("data-sales-shown", $(".toggle-sales")[0].checked);
        }
      },
      addMissingTables: function(source_positions, teams_quantity) {
        var data;
        data = Tipmetric.Controllers.calculationFormView.getNewPositionsAndTeams();
        return _.each(data.positions, (function(_this) {
          return function(position) {
            var positionBlock;
            positionBlock = $("[data-position-block-name=\"" + position.position_type_name + "\"]");
            $(positionBlock).attr("data-position-is-a-source", true);
            return _.each(_.range(1, data.team_count + 1), function(teamNumber) {
              var html, positionTable, string, teamTableBlock, teamTableHeader;
              string = position.position_type_name + " " + teamNumber;
              positionTable = positionBlock.find("[data-position-table=\"" + position.position_type_name + "\"][data-team='" + teamNumber + "']");
              if (positionTable.length === 0) {
                console.log(string + " missing");
                html = _this.generateHtmlForTeam({
                  position_type_name: position.position_type_name,
                  position_type_id: position.position_type_id,
                  position_type_is_a_source: true,
                  team_no: teamNumber,
                  team_count: data.team_count
                });
                return positionBlock.append(html);
              } else {
                $(positionTable).attr("data-position-is-a-source", true);
                teamTableBlock = $(positionTable).closest(".team-table-block");
                return teamTableHeader = teamTableBlock.find(".team-table-name").text(position.position_type_name.toUpperCase() + (" (Team #" + teamNumber + ")"));
              }
            });
          };
        })(this));
      },
      removeDestroyedTeamsAndPositions: function() {
        var data, new_source_position_ids, oldSourcePositionIds, removedSourcePositionIds, teamsArray;
        data = Tipmetric.Controllers.calculationFormView.getNewPositionsAndTeams();
        teamsArray = _.range(1, data.team_count + 1);
        _.each($(".team-table"), function(teamTable) {
          var teamNumber;
          teamNumber = parseInt($(teamTable).attr("data-team"));
          if (!_.contains(teamsArray, teamNumber)) {
            return $(".team-table[data-team='" + teamNumber + "']").closest(".team-table-block").remove();
          }
        });
        new_source_position_ids = _.map(data.positions, function(e) {
          return e.position_type_id;
        });
        oldSourcePositionIds = gon.calculation_params.source_position_ids;
        removedSourcePositionIds = _.difference(oldSourcePositionIds, new_source_position_ids);
        return _.each(removedSourcePositionIds, function(position_id) {
          var position_name;
          position_name = _.findWhere(gon.all_related_employees, {
            position_type_id: position_id
          }).position_type_name;
          $("[data-position-block-name=\"" + position_name + "\"] table").attr("data-position-is-a-source", false);
          return $("[data-position-block-name=\"" + position_name + "\"] .employee-distribution-line").remove();
        });
      },
      generateHtmlForTeam: function(params) {
        return JST["calculation/distributions/distributions_team_table"](params);
      },
      toggleTipOuts: function() {
        return $("#distributions-list").attr("data-tip-outs-shown", $(".toggle-tip-outs")[0].checked);
      },
      toggleSales: function() {
        if ($("body").attr("data-role") === "staff") {
          $("#distributions-list").attr("data-sales-shown", $(".toggle-sales")[0].checked);
          return;
        }
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: {
            restaurant: {
              sales_is_shown_by_default: $(".toggle-sales")[0].checked
            }
          },
          success: (function(_this) {
            return function(response) {
              return $("#distributions-list").attr("data-sales-shown", $(".toggle-sales")[0].checked);
            };
          })(this),
          error: function() {
            return toastr["error"]("Something went wrong.", "Error");
          }
        });
      },
      toggleFoodSales: function() {
        return $("#distributions-list").attr("data-food-sales-shown", $(".toggle-food-sales")[0].checked);
      },
      toggleAgSvc: function() {
        return $("#distributions-list").attr("data-ag-svc-shown", $(".toggle-ag-svc")[0].checked);
      },
      toggleFee: function() {
        return $("#distributions-list").attr("data-fee-shown", $(".toggle-fee")[0].checked);
      },
      addEmployee: function(e) {
        var emp, employeeListForPosition, existedEmployeeIds, i, len, moneyInFields, position, ref, table, team_td, tr_example;
        position = $(e.currentTarget).attr("data-position");
        $("[data-action='edit-employee-cancel'][data-position=\"" + position + "\"]").addClass("hidden");
        $("[data-action='edit-employee'][data-position=\"" + position + "\"]").removeClass("hidden");
        $("table[data-position-table=\"" + position + "\"]").find(".actions").addClass("hidden");
        table = $("table[data-position-table=\"" + ($(e.currentTarget).attr('data-position')) + "\"][data-team='" + ($(e.currentTarget).attr('data-team')) + "']").first();
        team_td = "<td class='team-number'>" + $(e.currentTarget).attr('data-team') + "</td>";
        moneyInFields = "<td class='notactive ag-svc-in source-only-fields ag-svc-cell'> <div class='input-group m-b'><span class='input-group-addon'>$</span> <input type='text' placeholder='0.00' class='form-control' value='0.00' data-recalculate='true'></div> </td> <td class='notactive fee-in source-only-fields fee-cell'> <div class='input-group m-b'><span class='input-group-addon'>$</span> <input type='text' placeholder='0.00' class='form-control' value='0.00' data-recalculate='true'></div> </td> <td class='notactive sales-cc source-only-fields sales-cell'> <div class='input-group m-b'><span class='input-group-addon'>$</span> <input type='text' placeholder='0.00' class='form-control' value='0.00' data-recalculate='true'></div> </td> <td class='notactive sales-cash source-only-fields sales-cell'> <div class='input-group m-b'><span class='input-group-addon'>$</span> <input type='text' placeholder='0.00' class='form-control' value='0.00' data-recalculate='true'></div> </td> <td class='notactive food-sales source-only-fields food-and-beverage-cell'> <div class='input-group m-b'><span class='input-group-addon'>$</span> <input type='text' placeholder='0.00' class='form-control' value='0.00' data-recalculate='true'></div> </td> <td class='notactive beverage-sales source-only-fields food-and-beverage-cell'> <div class='input-group m-b'><span class='input-group-addon'>$</span> <input type='text' placeholder='0.00' class='form-control' value='0.00' data-recalculate='true'></div> </td> <td class='notactive cc-in source-only-fields'> <div class='input-group m-b'><span class='input-group-addon'>$</span> <input type='text' placeholder='0.00' class='form-control' value='0.00' data-recalculate='true'></div> </td> <td class='notactive cash-in source-only-fields'> <div class='input-group m-b'><span class='input-group-addon'>$</span> <input type='text' placeholder='0.00' class='form-control' value='0.00' data-recalculate='true'></div> </td>";
        existedEmployeeIds = _.map(table.find("select.employee-id"), function(e) {
          return $(e).val();
        });
        employeeListForPosition = "";
        ref = gon.related_employees[$(e.currentTarget).attr('data-position')].employees;
        for (i = 0, len = ref.length; i < len; i++) {
          emp = ref[i];
          if (!_.contains(existedEmployeeIds, emp.id)) {
            employeeListForPosition += "<option value='" + emp.id + "'>" + emp.name + "</option>";
          }
        }
        if (employeeListForPosition.length > 0) {
          tr_example = "<tr class='employee-distribution-line' data-emp-distribution-status='new'> <td> <select class='select-2-ed employee-id' style='width: 100%'>" + employeeListForPosition + "</select> </td>" + team_td + "<td class='number-hrs-td'><input class='number-hrs form-control' type='text'  name='number-hrs' value='0.00' data-recalculate='true'></td>" + moneyInFields + ("<td class='notactive before-tip-out-td cc-out'> - </td> <td class='notactive before-tip-out-td cash-out'> - </td> <td class='notactive before-tip-out-td ag-svc-out ag-svc-cell'> - </td> <td class='notactive before-tip-out-td fee-out fee-cell'> - </td> <td class='notactive tip-outs tip-outs-given-cc'> 0 </td> <td class='notactive tip-outs tip-outs-given-cash'> 0 </td> <td class='notactive tip-outs tip-outs-given-ag-svc ag-svc-cell'> 0 </td> <td class='notactive tip-outs tip-outs-given-fee fee-cell'> 0 </td> <td class='notactive tip-outs tip-outs-received-cc'> 0 </td> <td class='notactive tip-outs tip-outs-received-cash'> 0 </td> <td class='notactive tip-outs tip-outs-received-ag-svc ag-svc-cell'> 0 </td> <td class='notactive tip-outs tip-outs-received-fee fee-cell'> 0 </td> <td class='active final-tips-distributed-cc'> 0 </td> <td class='active final-tips-distributed-cash'> 0 </td> <td class='active final-distributed-ag-svc ag-svc-cell'> 0 </td> <td class='active final-distributed-fee fee-cell'> 0 </td> <td class='active final-tips-column'> 0 </td> <td class='active hourly-rate-column'> 0 </td> <td class='active final-with-overtips-cc overtips-cell'> 0 </td> <td class='active final-with-overtips-cash overtips-cell'> 0 </td> <td class='actions hidden'> <button class='btn btn-danger' type='button' data-action='remove-employee-distribution' data-position=\"" + ($(e.currentTarget).attr('data-position')) + "\" data-team='" + ($(e.currentTarget).attr('data-team')) + "'><i class='fa fa-minus-circle'></i></button> </td> </tr>");
          $(table).find("tbody tr.info").before(tr_example);
          $(".employee-distribution-line .select-2-ed").select2({
            theme: "bootstrap",
            templateResult: this.select2Template,
            templateSelection: this.select2Template
          });
          this.updateEmployeeLinesIds();
          this.scope.calculate();
        } else {
          toastr["info"]("You have no more active employees to add to this position. Go to Employee Page to add more.", "No employees");
        }
        this.updateSelectBoxesIn($(table));
        return Tipmetric.Controllers.calculationView.disableInputs();
      },
      updateEmployeeLinesIds: function(e) {
        var tr;
        _.each($(".employee-distribution-line"), (function(_this) {
          return function(line) {
            var id;
            id = $(line).find("select.employee-id").val();
            return $(line).attr("data-employee-id", id);
          };
        })(this));
        if (e) {
          tr = $(e.currentTarget).closest("tr");
          if (tr.attr("data-emp-distribution-status") === 'persisted' && tr.attr("data-emp-distribution-id")) {
            return $.ajax({
              url: "/api/employee_distributions/change_employee",
              method: "patch",
              data: {
                calculation_id: gon.calculation_id,
                employee_distribution_id: tr.attr("data-emp-distribution-id"),
                employee_id: tr.attr("data-employee-id")
              },
              success: (function(_this) {
                return function(event) {
                  console.log("Employee distribution is updated");
                  return _this.updateSelectBoxesIn($(e.currentTarget).closest("table"));
                };
              })(this),
              error: function(e) {
                return console.log("Employee distribution is not updated");
              }
            });
          } else {
            return this.updateSelectBoxesIn($(e.currentTarget).closest("table"));
          }
        }
      },
      updateTableSelectBoxes: function(table) {
        return _.each($("table[data-table-type='employee-distribution']"), (function(_this) {
          return function(table) {
            return _this.updateSelectBoxesIn($(table));
          };
        })(this));
      },
      updateSelectBoxesIn: function(table) {
        var busyEmployeeIds, employees, freeEmployees, position;
        position = table.attr("data-position-table");
        employees = gon.all_related_employees[position].employees;
        busyEmployeeIds = _.map(table.find("select.employee-id"), function(e) {
          return $(e).val();
        });
        freeEmployees = _.filter(employees, function(employee) {
          return !_.contains(busyEmployeeIds, employee.id);
        });
        return _.each(table.find("select"), function(select) {
          var employee, i, len, options, selectedOption, selectedOptionText;
          selectedOption = $(select).val();
          selectedOptionText = _.filter(employees, function(employee) {
            return employee.id === selectedOption;
          });
          selectedOptionText = selectedOptionText[0].full_info;
          options = "";
          options += "<option value='" + selectedOption + "'>" + selectedOptionText + "</option>";
          for (i = 0, len = freeEmployees.length; i < len; i++) {
            employee = freeEmployees[i];
            options += "<option value='" + employee.id + "'>" + employee.full_info + "</option>";
          }
          return $(select).html(options);
        });
      },
      removeEmployee: function(e) {
        var table, tr;
        tr = $(e.currentTarget).closest("tr");
        table = $(e.currentTarget).closest("table");
        if (tr.attr("data-emp-distribution-status") === "new") {
          tr.remove();
          this.scope.calculate();
          return this.updateSelectBoxesIn(table);
        } else if (tr.attr("data-emp-distribution-status") === "persisted") {
          if (this.removeRequestIsInProcess === true) {
            return true;
          }
          this.removeRequestIsInProcess = true;
          return $.ajax({
            url: "/api/employee_distributions/remove_distribution",
            method: "delete",
            data: {
              calculation_id: gon.calculation_id,
              position_type_id: table.attr("data-position-type-id"),
              employee_id: tr.attr("data-employee-id")
            },
            success: (function(_this) {
              return function(e) {
                _this.removeRequestIsInProcess = false;
                toastr["success"]("Employee distribution removed", "Success");
                tr.remove();
                _this.scope.calculate();
                return _this.updateSelectBoxesIn(table);
              };
            })(this),
            error: (function(_this) {
              return function(response) {
                _this.removeRequestIsInProcess = false;
                return toastr["error"](response.responseText, "Error");
              };
            })(this)
          });
        }
      },
      editEmployee: function(e) {
        var action, position, table;
        position = $(e.currentTarget).attr("data-position");
        action = $("[data-action='edit-employee'][data-position=\"" + position + "\"], [data-action='edit-employee-cancel'][data-position=\"" + position + "\"]").toggleClass("hidden");
        return table = $("table[data-position-table=\"" + position + "\"]").find(".actions").toggleClass("hidden");
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("calculation") && gon.calculation_id !== void 0) {
        return Tipmetric.Controllers.DistributionsViewPrototype = DistributionsView;
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var TipOutView;
    TipOutView = Backbone.View.extend({
      el: "#food-tip-outs",
      events: {
        "keyup [data-recalculate='true']": function() {
          return this.scope.updateTotalNumbers();
        },
        "change [data-recalculate='true']": function() {
          return this.scope.updateTotalNumbers();
        },
        "keyup [data-recalculate-total-numbers='true']": function() {
          return this.scope.updateTotalNumbers();
        },
        "change [data-recalculate-total-numbers='true']": function() {
          return this.scope.updateTotalNumbers();
        },
        "change select#position_type_id": "updatePositionSelects",
        "change select#area_type_id": "updateAreaSelects",
        "change select#shift_type_id": "updateTipOutShift",
        "change select#subtype": "updateSubtype",
        "click [data-action='add-area']": "addTipout",
        "click [data-action='edit-tip-outs']": "editTipouts",
        "click [data-action='edit-tip-outs-cancel']": "editTipouts",
        "click [data-action='remove-tip-out-distribution']": "removeTipOutDistribution"
      },
      initialize: function(options) {
        this.scope = options.scope;
        if (!_.isEmpty($("#date").val())) {
          this.updateAreaSelects();
          this.updateAllPositionSelects();
        }
        return this.renderFoodTipOuts();
      },
      renderFoodTipOuts: function() {
        var foodTipOuts, html, i, len, tipOut;
        foodTipOuts = _.filter(gon.calculation_sender_tip_outs, function(to) {
          return to.tip_out_type === "food";
        });
        for (i = 0, len = foodTipOuts.length; i < len; i++) {
          tipOut = foodTipOuts[i];
          html = JST["calculation/tip_outs/existing_tip_out"](tipOut);
          $(this.el).find("#given-tipouts-table tbody").prepend(html);
        }
        window.bindSelectWrappedBy("#food-tip-outs");
        window.bindPercentSelect();
        this.updateTipOutArea();
        return this.updateTipOutShift();
      },
      updateAllPositionSelects: function(e) {
        _.each($("#food-tip-outs #given-tipouts-table .tip-out-line #position_type_id"), function(select) {
          var tipOutType, val;
          val = $(select).val();
          if (val === "all") {
            tipOutType = "area";
          } else {
            tipOutType = "position";
          }
          $(select).closest(".tip-out-line").attr("data-tip-out-type", tipOutType);
          return $(select).closest(".tip-out-line").attr("data-tip-out-position-id", val);
        });
        this.scope.updateTotalNumbers();
        return this.scope.calculate();
      },
      updatePositionSelects: function(e) {
        var tipOutType, val;
        val = $(e.currentTarget).val();
        if (val === "all") {
          tipOutType = "area";
        } else {
          tipOutType = "position";
        }
        $(e.currentTarget).closest(".tip-out-line").attr("data-tip-out-type", tipOutType);
        $(e.currentTarget).closest(".tip-out-line").attr("data-tip-out-position-id", val);
        this.scope.updateTotalNumbers();
        return this.scope.calculate();
      },
      updateTipOutArea: function(e) {
        return _.each($("#food-tip-outs #given-tipouts-table .tip-out-line #area_type_id"), function(select) {
          var areaName, tr, val;
          val = $(select).val();
          areaName = $(select).closest("tr").find("option[value='" + val + "']").text();
          return tr = $(select).closest("tr").attr("data-area-name", areaName);
        });
      },
      updateTipOutShift: function(e) {
        return _.each($("#food-tip-outs #given-tipouts-table .tip-out-line #shift_type_id"), function(shiftSelect) {
          var shiftName, tr, val;
          val = $(shiftSelect).val();
          shiftName = $(shiftSelect).closest("tr").find("option[value='" + val + "']").text();
          return tr = $(shiftSelect).closest("tr").attr("data-shift-type-name", shiftName);
        });
      },
      updateSubtype: function(e) {
        var tr;
        tr = $(e.currentTarget).closest("tr").attr("data-tip-out-food-subtype", $(e.currentTarget).val());
        return this.scope.calculate();
      },
      updateTipOutShiftForTipOutByArea: function(areaSelect) {
        var shiftSelect, tipOutLine;
        tipOutLine = $(areaSelect).closest(".tip-out-line");
        shiftSelect = $(tipOutLine).find("#area_type_id");
        return this.updateTipOutShiftForTipOut(shiftSelect);
      },
      updateTipOutShiftForTipOut: function(shiftSelect) {
        var areaId, areaName, areaSelect, dayName, optionsHtmlArray, shiftName, shiftSelectVal, shifts, tipOutLine;
        dayName = moment($("#date").val()).format('dddd').toLowerCase();
        tipOutLine = $(shiftSelect).closest(".tip-out-line");
        areaSelect = $(tipOutLine).find("#area_type_id");
        areaId = $(areaSelect).val();
        areaName = $(areaSelect).find("option[value='" + areaId + "']")[0].text;
        shiftSelect = $(shiftSelect).closest(".tip-out-line").find("#shift_type_id");
        shiftSelectVal = $(shiftSelect).val();
        shiftName = $(shiftSelect).find("option[value='" + shiftSelectVal + "']")[0].text;
        shifts = gon.restaurant_inheritance_data[dayName].areas[areaName].shifts;
        optionsHtmlArray = (_.map(shifts, function(item) {
          if (item.id === shiftSelectVal) {
            return "<option value='" + item.id + "' selected='selected'>" + item.name + "</option>";
          } else {
            return "<option value='" + item.id + "'>" + item.name + "</option>";
          }
        })).join();
        return $(shiftSelect).html(optionsHtmlArray);
      },
      availablePositionsForTipOuts: function() {
        return _.values(gon.calculation_source_positions);
      },
      upadteAllPositionTipoutsOptions: function() {
        var availablePositions;
        availablePositions = _.union(this.availablePositionsForTipOuts(), [
          {
            id: "all",
            name: 'all'
          }
        ]);
        return _.each($("#food-tip-outs #given-tipouts-table tbody tr.tip-out-line"), (function(_this) {
          return function(tipOutLine) {
            var finalHtml, id, name, select;
            select = $(tipOutLine).find("select#position_type_id");
            id = $(select).val();
            name = $(select).find("option[value='" + id + "']").text();
            finalHtml = (_.map(availablePositions, function(item) {
              if (item.id === id) {
                return "<option value='" + item.id + "' selected='selected'>" + item.name + "</option>";
              } else {
                return "<option value='" + item.id + "'>" + item.name + "</option>";
              }
            })).join();
            return $(select).html(finalHtml);
          };
        })(this));
      },
      availableAreasForTipOuts: function() {
        var allValues, dayName;
        dayName = moment($("#date").val()).format('dddd').toLowerCase();
        if (typeof Rollbar !== "undefined" && typeof Rollbar.options !== "undefined" && typeof Rollbar.options.payload !== "undefined") {
          Rollbar.options.payload.formParams = {
            date: $("#date").val(),
            day: dayName
          };
        }
        allValues = _.values(gon.restaurant_inheritance_data[dayName].areas);
        allValues = _.filter(allValues, function(item) {
          return !_.isEmpty(item.shifts);
        });
        if (!gon.restaurant.shifted_tip_outs_enabled) {
          allValues = _.filter(allValues, function(item) {
            return _.findWhere(item.shifts, {
              id: gon.calculation_params.shift_type_id
            });
          });
        }
        return allValues;
      },
      usedPositionTipOuts: function(data) {
        var positions;
        positions = _.map($("#food-tip-outs #given-tipouts-table tbody tr.tip-out-line[data-tip-out-type='position']"), (function(_this) {
          return function(tipOutLine) {
            return $(tipOutLine).attr("data-tip-out-position-id");
          };
        })(this));
        return positions;
      },
      updateAreaSelects: function(e) {
        var allValues;
        allValues = this.availableAreasForTipOuts();
        _.each($("#food-tip-outs given-tipouts-table .tip-out-line #area_type_id"), (function(_this) {
          return function(select) {
            var finalHtml, freeItems, id, name, otherAreaSelects, usedValues;
            if (gon.restaurant.shifted_tip_outs_enabled) {
              usedValues = [];
            } else {
              otherAreaSelects = _.filter($("#food-tip-outs #given-tipouts-table .tip-out-line #area_type_id"), function(e) {
                return $(e).get(0) !== $(select).get(0);
              });
              usedValues = _.map(otherAreaSelects, function(e) {
                return $(e).val();
              });
            }
            freeItems = otherAreaSelects;
            id = $(select).val();
            name = $(select).find("option[value='" + id + "']").text();
            finalHtml = (_.map(freeItems, function(item) {
              if (item.id === id) {
                return "<option value='" + item.id + "' selected='selected'>" + item.name + "</option>";
              } else {
                return "<option value='" + item.id + "'>" + item.name + "</option>";
              }
            })).join();
            $(select).html(finalHtml);
            if (gon.restaurant.shifted_tip_outs_enabled) {
              return _this.updateTipOutShiftForTipOutByArea(select);
            }
          };
        })(this));
        return this.updateTipOutArea(e);
      },
      editTipouts: function() {
        $(this.el).find(".actions").toggleClass("hidden");
        $(this.el).find("[data-action='edit-tip-outs-cancel']").toggleClass("hidden");
        return $(this.el).find("[data-action='edit-tip-outs']").toggleClass("hidden");
      },
      syncTipOutsWithData: function(data) {
        data = _.filter(data, function(d) {
          return d.tip_out_type === "food";
        });
        return _.each($("#food-tip-outs #given-tipouts-table .tip-out-line[data-tip-out-status='new']"), (function(_this) {
          return function(line) {
            var areaId, options, shiftTypeId, tipOutData;
            areaId = $(line).find("select#area_type_id").val();
            if (gon.restaurant.shifted_tip_outs_enabled) {
              shiftTypeId = $(line).find("select#shift_type_id").val();
              options = {
                area_type_id: areaId,
                shift_type_id: shiftTypeId
              };
            } else {
              options = {
                area_type_id: areaId
              };
            }
            tipOutData = _.findWhere(data, options);
            $(line).attr("data-tip-out-status", "persisted");
            return $(line).attr("data-tip-out-id", tipOutData.id);
          };
        })(this));
      },
      removeTipOutDistribution: function(e) {
        var destroyButton, table, tr;
        tr = $(e.currentTarget).closest("tr");
        table = $(e.currentTarget).closest("table");
        destroyButton = $(e.currentTarget);
        if (destroyButton.isDisabled()) {
          return true;
        }
        destroyButton.disable();
        if (tr.attr("data-tip-out-status") === "new") {
          tr.remove();
          this.scope.calculate();
          this.updateAreaSelects();
          this.scope.updateTotalNumbers();
          return destroyButton.enable();
        } else if (tr.attr("data-tip-out-status") === "persisted") {
          Tipmetric.Controllers.spinner.show();
          return $.ajax({
            url: "/api/tip_outs/" + tr.attr("data-tip-out-id"),
            method: "delete",
            data: {
              calculation_id: gon.calculation_id
            },
            success: (function(_this) {
              return function(e) {
                toastr["success"]("Tip out removed", "Success");
                tr.remove();
                _this.scope.calculate();
                _this.updateAreaSelects();
                _this.scope.updateTotalNumbers();
                Tipmetric.Controllers.spinner.hide();
                return destroyButton.enable();
              };
            })(this),
            error: (function(_this) {
              return function(e) {
                toastr["error"]("Tip out was not removed", "Error");
                Tipmetric.Controllers.spinner.hide();
                return destroyButton.enable();
              };
            })(this)
          });
        }
      },
      addTipout: function(e) {
        var freeAreaItems, freePositionItems, html, table, tipOut, usedValues;
        usedValues = _.map($("#food-tip-outs #given-tipouts-table .tip-out-line #area_type_id"), function(e) {
          return $(e).val();
        });
        freeAreaItems = this.availableAreasForTipOuts();
        freePositionItems = this.availablePositionsForTipOuts();
        if (freeAreaItems.length === 0) {
          return toastr["error"]("There are no areas for making tip out.", "Error");
        } else {
          table = $("#food-tip-outs table#given-tipouts-table");
          tipOut = {
            partialType: "food",
            tipOutType: "food",
            freeAreaItems: freeAreaItems,
            freePositionItems: freePositionItems,
            shiftItems: gon.shifts
          };
          html = JST["calculation/tip_outs/tip_out"](tipOut);
          $(table).find("tbody tr.total").before(html);
          window.bindSelectWrappedBy("#food-tip-outs");
          window.bindPercentSelect();
          this.updateAreaSelects(e);
          return this.updateTipOutShift();
        }
      }
    });
    return Tipmetric.Controllers.FoodTipOutViewPrototype = TipOutView;
  })();

}).call(this);
(function() {
  (function() {
    var CalculationFormView;
    CalculationFormView = Backbone.View.extend({
      el: "body.calculation",
      events: {
        "change #area_type_id": "areaUpdated",
        "change #shift_type_id": "shiftUpdated",
        "change [data-required-field='true']": "checkIfDataValid",
        "change .changeble-data select": "calculationChanged",
        "blur #date": "dateBlured",
        "click .show-calculation": "showCalculation",
        "click button[data-calculation-action]": "chooseCalculationVariant",
        "click .date-selection-arrow b": "showDatePicker"
      },
      initialize: function() {
        gon.restaurant_inheritance_data = Tipmetric.Controllers.helpers.buildSmartData();
        $('.datapicker').datepicker({
          todayHighlight: true
        }).on("changeDate", (function(_this) {
          return function() {
            return _this.dateUpdated();
          };
        })(this)).on("show", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').addClass('open');
          };
        })(this)).on("hide", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').removeClass('open');
          };
        })(this));
        this.checkIfDataValid();
        this.dateUpdated();
        this.areaUpdated();
        this.shiftUpdated();
        return this.setTeamsQuantity();
      },
      hidePopover: function() {
        return $("[data-toggle='popover']").popover('hide');
      },
      setTeamsQuantity: function() {
        if ($("select#teams_quantity option").length === 3) {
          return $("select#teams_quantity").val(1).trigger("change");
        }
      },
      showCalculation: function(e) {
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return false;
        }
        e.preventDefault();
        if (Tipmetric.Controllers.calculationView && Tipmetric.Controllers.calculationView.locked) {
          return false;
        }
        if ($(".changeble-data").hasClass("changed")) {
          return this.updateCalculation(e);
        } else {
          return this.checkCalculation();
        }
      },
      showDatePicker: function(e) {
        e.preventDefault();
        return $('#date').focus();
      },
      updateCalculation: function(e) {
        if (Tipmetric.Controllers.calculationView && Tipmetric.Controllers.calculationView.locked) {
          e.preventDefault();
          return false;
        }
        if ($(".show-calculation").attr("disabled") === "disabled") {
          return false;
        }
        if (this.tipOutpositionRemovedFromSources()) {
          return Tipmetric.Controllers.notifier.alert("", "You removed the position that is used in position tip outs. Please remove the tip out first.", false, false, (function(_this) {
            return function() {
              return true;
            };
          })(this));
        } else if (this.pendingDistributionPositionRemoveAttempt()) {
          Tipmetric.Controllers.notifier.alert("", "You are trying to remove the position that is used in pending distribution. Please approve or decline it first.", false, false, (function(_this) {
            return function() {
              return true;
            };
          })(this));
          return Tipmetric.Controllers.calculationFormView.cancelSourcePositions();
        } else if (this.isTeamsQuantityReduced()) {
          return Tipmetric.Controllers.notifier.alert("", "All information for removed teams will be deleted and is not reversible. Are you sure you want to continue?", false, true, (function(_this) {
            return function() {
              return _this.sendUpdateRequest();
            };
          })(this));
        } else if (this.isInitialSourcesQuantityReduced()) {
          if (gon.is_sources_quantity_reducing_allowed) {
            return Tipmetric.Controllers.notifier.alert("", "All information for removed position will be deleted and is not reversible. Are you sure you want to continue?", false, true, (function(_this) {
              return function() {
                return _this.sendUpdateRequest();
              };
            })(this));
          } else {
            return Tipmetric.Controllers.notifier.alert("", "Removing source positions is not available for that distribution logic type.", false, false, (function(_this) {
              return function() {
                return true;
              };
            })(this));
          }
        } else {
          return this.sendUpdateRequest();
        }
      },
      tipOutpositionRemovedFromSources: function() {
        var calculationSourceIds, positionTipOutPositionIds, removedPositionIds;
        calculationSourceIds = $("#calculation-form-wrapper").find("select#source_position_ids").val();
        positionTipOutPositionIds = Tipmetric.Controllers.calculationView.tipOutView.usedPositionTipOuts();
        removedPositionIds = _.difference(positionTipOutPositionIds, calculationSourceIds);
        return _.some(removedPositionIds);
      },
      removedPositionIds: function() {
        var currentCalculationSourceIds, oldSourcePositionIds, removedSourcePositionIds;
        oldSourcePositionIds = gon.calculation_params.source_position_ids;
        currentCalculationSourceIds = $("#calculation-form-wrapper").find("select#source_position_ids").val();
        removedSourcePositionIds = _.difference(oldSourcePositionIds, currentCalculationSourceIds);
        return removedSourcePositionIds;
      },
      pendingDistributionPositionRemoveAttempt: function() {
        var pendingDistributionsPositionIds, removedPendingPositions, removedPositionIds;
        pendingDistributionsPositionIds = _.map($("#pending-distributions-view").find(".pending-distribution-line"), function(e) {
          return $(e).attr("data-position-type-id");
        });
        removedPositionIds = this.removedPositionIds();
        removedPendingPositions = _.intersection(pendingDistributionsPositionIds, removedPositionIds);
        return _.some(removedPendingPositions);
      },
      sendUpdateRequest: function() {
        var params;
        Tipmetric.Controllers.calculationView.distributionsView.addMissingTables();
        Tipmetric.Controllers.calculationView.distributionsView.removeDestroyedTeamsAndPositions();
        params = {
          updateCalculationRequired: true
        };
        return Tipmetric.Controllers.calculationView.checkCalculationExistance(params);
      },
      addSourcePositionById: function(options) {
        var ids;
        ids = _.union(Tipmetric.Controllers.tipSharingToggleView.getSourcePositionIds(), [options.id]);
        $("select#source_position_ids").val(ids).trigger("change");
        if (options.forceUpdate) {
          return this.updateCalculation();
        }
      },
      addSourcePositionByIds: function(options) {
        var ids;
        ids = _.union(Tipmetric.Controllers.tipSharingToggleView.getSourcePositionIds(), options.ids);
        $("select#source_position_ids").val(ids).trigger("change");
        if (options.forceUpdate) {
          return this.updateCalculation();
        }
      },
      changeTeamQuantityTo: function(options) {
        $("select#teams_quantity").val(options.teamNumber).trigger("change");
        if (options.forceUpdate) {
          return this.updateCalculation();
        }
      },
      getNewPositionsAndTeams: function() {
        var positions_ids;
        positions_ids = $("#calculation-form-wrapper").find("select#source_position_ids").val();
        return {
          positions: _.map(positions_ids, function(id) {
            return {
              position_type_id: id,
              position_type_name: _.findWhere(gon.all_related_employees, {
                position_type_id: id
              }).position_type_name
            };
          }),
          team_count: parseInt($("#calculation-form-wrapper").find("select#teams_quantity").val())
        };
      },
      chooseCalculationVariant: function(isDelete) {
        var method;
        method = "show-old";
        $("input#existed_calculation_method").val(method);
        return $(".show-calculation-request").click();
      },
      dateBlured: function(e) {
        if (_.isEmpty($("#date").val())) {
          return $("#date").val($("#date").attr("data-saved-date"));
        }
      },
      dateUpdated: function() {
        var area, areas, dayName, i, len, options, savedVal;
        if (!_.isEmpty($("#date").val())) {
          $("#date").attr("data-saved-date", $("#date").val());
        }
        dayName = moment($("#date").val()).format('dddd').toLowerCase();
        if (gon.restaurant_inheritance_data[dayName]) {
          areas = _.map(gon.restaurant_inheritance_data[dayName].areas, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          options = "";
          options += "<option value=''>Area</option>";
          for (i = 0, len = areas.length; i < len; i++) {
            area = areas[i];
            options += "<option value='" + area.id + "'>" + area.name + "</option>";
          }
          savedVal = $("#calculation-form-wrapper").find("select#area_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          $("#calculation-form-wrapper").find("select#area_type_id").html(options).select2({
            theme: "bootstrap"
          });
          $("#calculation-form-wrapper").find("select#area_type_id").val(savedVal).trigger("change");
        } else {
          $("#calculation-form-wrapper").find("select#area_type_id").val("").trigger("change").html("<option value=''>Area</option>");
          $("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          $("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
        if (Tipmetric.Controllers.calculationView && Tipmetric.Controllers.calculationView.distributionsView) {
          return Tipmetric.Controllers.calculationView.distributionsView.bindSelectsForEmployees();
        }
      },
      areaUpdated: function() {
        var areaName, dayName, i, len, options, savedVal, shift, shifts;
        dayName = moment($("#date").val()).format('dddd').toLowerCase();
        areaName = $("#calculation-form-wrapper").find("select#area_type_id option[value='" + ($("select#area_type_id").val()) + "']").text();
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas[areaName]) {
          shifts = _.map(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          options = "";
          options += "<option value=''>Shift</option>";
          for (i = 0, len = shifts.length; i < len; i++) {
            shift = shifts[i];
            options += "<option value='" + shift.id + "'>" + shift.name + "</option>";
          }
          savedVal = $("#calculation-form-wrapper").find("select#shift_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          $("#calculation-form-wrapper").find("select#shift_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return $("#calculation-form-wrapper").find("select#shift_type_id").val(savedVal).trigger("change");
        } else {
          $("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return $("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      shiftUpdated: function() {
        var areaData, areaName, dayName, i, len, options, position, positions, savedVal, shiftName;
        dayName = moment($("#date").val()).format('dddd').toLowerCase();
        areaName = $("#calculation-form-wrapper").find("select#area_type_id option[value='" + ($('select#area_type_id').val()) + "']").text();
        shiftName = $("#calculation-form-wrapper").find("select#shift_type_id option[value='" + ($("select#shift_type_id").val()) + "']").text();
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas) {
          areaData = gon.restaurant_inheritance_data[dayName].areas[areaName];
        }
        if (areaData && areaData.shifts[shiftName]) {
          positions = _.map(areaData.shifts[shiftName].positions, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          options = "";
          for (i = 0, len = positions.length; i < len; i++) {
            position = positions[i];
            options += "<option value='" + position.id + "'>" + position.name + "</option>";
          }
          savedVal = $("select#source_position_ids").val();
          $("select#source_position_ids").html(options).select2({
            theme: "bootstrap",
            multiple: true
          });
          return $("select#source_position_ids").val(savedVal).trigger("change");
        } else {
          return $("select#source_position_ids").val("").trigger("change");
        }
      },
      updatableParamsChanged: function() {
        var sources1, sources2, teams1, teams2;
        sources1 = _.sortBy($("#calculation-form-wrapper").find("select#source_position_ids").val(), function(e) {
          return e;
        });
        sources2 = _.sortBy(gon.calculation_params.source_position_ids, function(e) {
          return e;
        });
        teams1 = parseInt($("#calculation-form-wrapper").find("select#teams_quantity").val());
        teams2 = parseInt(gon.calculation_params.teams_quantity);
        return !_.isEqual(sources1, sources2) || !_.isEqual(teams1, teams2);
      },
      lockedParamsChanged: function() {
        var area_type_id1, area_type_id2, date1, date2, shift_type_id1, shift_type_id2;
        date1 = $("#calculation-form-wrapper").find("input#date").val();
        date2 = gon.calculation_params.date;
        area_type_id1 = $("#calculation-form-wrapper").find("select#area_type_id").val();
        area_type_id2 = gon.calculation_params.area_type_id;
        shift_type_id1 = $("#calculation-form-wrapper").find("select#shift_type_id").val();
        shift_type_id2 = gon.calculation_params.shift_type_id;
        return !_.isEqual(date1, date2) || !_.isEqual(area_type_id1, area_type_id2) || !_.isEqual(shift_type_id1, shift_type_id2);
      },
      isTeamsQuantityReduced: function(e) {
        var teamsInitially, teamsNow;
        teamsNow = parseInt($("#calculation-form-wrapper").find("select#teams_quantity").val());
        teamsInitially = parseInt(gon.calculation_params.teams_quantity);
        return teamsNow < teamsInitially;
      },
      isInitialSourcesQuantityReduced: function(e) {
        var commonPositionIds, sourcesInitially, sourcesNow;
        sourcesNow = $("#calculation-form-wrapper").find("select#source_position_ids").val();
        sourcesInitially = gon.calculation_params.source_position_ids;
        commonPositionIds = _.intersection(sourcesNow, sourcesInitially);
        return commonPositionIds.length < sourcesInitially.length;
      },
      cancelSourcePositions: function() {
        var previousSourcePositionIds;
        previousSourcePositionIds = gon.calculation_params.source_position_ids;
        $("#calculation-form-wrapper").find("select#source_position_ids").val(previousSourcePositionIds);
        return $("#calculation-form-wrapper").find("select#source_position_ids").trigger("change");
      },
      calculationChanged: function(e) {
        if (!gon.calculation_id) {
          return true;
        }
        if (this.updatableParamsChanged() && !this.lockedParamsChanged()) {
          return $(".changeble-data").addClass("changed");
        } else {
          return $(".changeble-data").removeClass("changed");
        }
      },
      checkCalculation: function(e) {
        var areaName, areaVal, dateVal, shiftName, shiftVal;
        dateVal = $("#calculation-form-wrapper").find("input#date").val();
        areaVal = $("#calculation-form-wrapper").find("select#area_type_id").val();
        areaName = $("#calculation-form-wrapper").find("select#area_type_id option[value='" + areaVal + "']").text();
        shiftVal = $("#calculation-form-wrapper").find("select#shift_type_id").val();
        shiftName = $("#calculation-form-wrapper").find("select#shift_type_id option[value='" + shiftVal + "']").text();
        return $.ajax({
          url: "/api/calculations/check_calculation",
          method: "get",
          data: {
            area_type_id: areaVal,
            shift_type_id: shiftVal,
            date: dateVal
          },
          success: (function(_this) {
            return function(response) {
              if (response.persisted === true) {
                return swal({
                  title: "",
                  text: "Tip Record for " + dateVal + " " + areaName + " + " + shiftName + " already exists. Would you like to edit it?",
                  type: "info",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "Show Tip Record",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }, function(isDelete) {
                  if (isDelete) {
                    return _this.chooseCalculationVariant(isDelete);
                  }
                }).then(function(confirmed) {
                  if (confirmed.value) {
                    return _this.chooseCalculationVariant(confirmed);
                  }
                });
              } else {
                return $(".show-calculation-request").click();
              }
            };
          })(this),
          error: function(response) {
            if (response.responseJSON.locked || response.responseJSON.has_no_access_to_area) {
              return toastr["error"](response.responseJSON.errors, "Error");
            }
          }
        });
      },
      checkIfDataValid: function(e) {
        var blankFields, fields;
        fields = $("#calculation-form-wrapper").find("input[name='date'], select[name='area_type_id'], select[name='shift_type_id'], select[id='source_position_ids'], select[name='teams_quantity']");
        blankFields = _.filter(fields, function(e) {
          return $(e).val() === null || $(e).val().length === 0 || $(e).val().length === void 0;
        }) || [];
        if (blankFields.length === 0) {
          return $(".show-calculation").removeAttr("disabled");
        } else {
          return $(".show-calculation").attr("disabled", "disabled");
        }
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("calculation")) {
        return Tipmetric.Controllers.calculationFormView = new CalculationFormView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var CalculationPercentsHelper;
    CalculationPercentsHelper = (function() {
      function CalculationPercentsHelper() {}

      CalculationPercentsHelper.prototype.processPoints = function(data, scope) {
        var totalCalculationHourSumm, totalCalculationHoursMap, totalPointsSumm;
        this.data = data;
        totalPointsSumm = scope.totalPoints();
        totalCalculationHoursMap = _.map($(".employee-distribution-line .number-hrs"), function(num) {
          return parseFloat($(num).val());
        });
        totalCalculationHourSumm = _.reduce(totalCalculationHoursMap, function(sum, num) {
          return sum + num;
        }) || 0;
        if (scope.tipOutBasedOnOriginal()) {
          this.data.initialPointsHourPrice = {
            cc: this.data.totalCollectedMoney.cc / totalPointsSumm,
            cash: this.data.totalCollectedMoney.cash / totalPointsSumm,
            agSvc: this.data.totalCollectedMoney.agSvc / totalPointsSumm,
            fee: this.data.totalCollectedMoney.fee / totalPointsSumm
          };
        } else {
          this.data.initialPointsHourPrice = {
            cc: this.data.totalCollectedMoney.cc / totalPointsSumm,
            cash: this.data.totalCollectedMoney.cash / totalPointsSumm,
            agSvc: this.data.totalCollectedMoney.agSvc / totalPointsSumm,
            fee: this.data.totalCollectedMoney.fee / totalPointsSumm
          };
        }
        this.data.totalPointsSumm = totalPointsSumm;
        _.each($("[data-table-type='employee-distribution']"), (function(_this) {
          return function(empDistrTable) {
            var agSvcTeamMap, agSvcTeamTotalSum, base, base1, base2, cashTeamMap, cashTeamTotalSum, ccTeamMap, ccTeamTotalSum, feeTeamMap, feeTeamTotalSum, finalTeamTODistributeAgSvc, finalTeamTODistributeCC, finalTeamTODistributeCCMinusCash, finalTeamTODistributeCash, finalTeamTODistributeFee, hourPrice, hoursMap, positionPoints, positionTeamsCount, positionTotalPoints, positionType, positionTypeIsASource, salesBeverageMap, salesFoodMap, salesMapCC, salesMapCash, teamNumber, totalBeverageSalesForPosition, totalFoodSalesForPosition, totalHoursForPosition, totalMoneyToDistribute, totalPositionHoursWorked, totalSalesForPosition, totalSalesForPositionCC, totalSalesForPositionCash, totalTeamCollectedMoney, totalTeamFoodSales, totalTeamFoodSalesBeverage, totalTeamFoodSalesFood, totalTeamMoneyToDistribute, totalTeamSales, totalTeamSalesCC, totalTeamSalesCash, totalTeamTOGivenAgSvc, totalTeamTOGivenCC, totalTeamTOGivenCash, totalTeamTOGivenFee, totalTeamTOReceivedAgSvc, totalTeamTOReceivedCC, totalTeamTOReceivedCash, totalTeamTOReceivedFee;
            positionType = $(empDistrTable).attr("data-position-table");
            positionTypeIsASource = $(empDistrTable).attr("data-position-is-a-source");
            positionTeamsCount = $("[data-table-type='employee-distribution'][data-position-table=\"" + positionType + "\"]").length || 0;
            teamNumber = $(empDistrTable).attr("data-team");
            hoursMap = _.map($(empDistrTable).find(".employee-distribution-line .number-hrs"), function(num) {
              return parseFloat($(num).val());
            });
            totalHoursForPosition = _.reduce(hoursMap, function(sum, num) {
              return sum + num;
            }) || 0;
            salesMapCC = _.map($(empDistrTable).find(".employee-distribution-line .sales-cc input"), function(num) {
              return parseFloat($(num).val());
            });
            salesMapCash = _.map($(empDistrTable).find(".employee-distribution-line .sales-cash input"), function(num) {
              return parseFloat($(num).val());
            });
            totalSalesForPositionCC = _.reduce(salesMapCC, function(sum, num) {
              return sum + num;
            }) || 0;
            totalSalesForPositionCash = _.reduce(salesMapCash, function(sum, num) {
              return sum + num;
            }) || 0;
            totalSalesForPosition = {
              cc: totalSalesForPositionCC,
              cash: totalSalesForPositionCash
            };
            salesFoodMap = _.map($(empDistrTable).find(".employee-distribution-line .food-sales input"), function(num) {
              return parseFloat($(num).val());
            });
            salesBeverageMap = _.map($(empDistrTable).find(".employee-distribution-line .beverage-sales input"), function(num) {
              return parseFloat($(num).val());
            });
            totalFoodSalesForPosition = _.reduce(salesFoodMap, function(sum, num) {
              return sum + num;
            }) || 0;
            totalBeverageSalesForPosition = _.reduce(salesBeverageMap, function(sum, num) {
              return sum + num;
            }) || 0;
            totalFoodSalesForPosition = {
              cc: totalFoodSalesForPosition,
              cash: totalBeverageSalesForPosition
            };
            positionPoints = ($("#positions-table input[data-percentage-for=\"" + positionType + "\"]").val()) || 0;
            positionTotalPoints = positionPoints * totalHoursForPosition;
            totalPositionHoursWorked = totalHoursForPosition;
            (base = _this.data).positionsMoney || (base.positionsMoney = {});
            (base1 = _this.data.positionsMoney)[positionType] || (base1[positionType] = {
              positionTypeIsASource: positionTypeIsASource,
              positionTeamsCount: positionTeamsCount,
              totalHoursForPosition: totalHoursForPosition,
              positionPoints: positionPoints,
              positionTotalPoints: positionTotalPoints,
              totalSalesForPosition: totalSalesForPosition,
              totalFoodSalesForPosition: totalFoodSalesForPosition
            });
            if (scope.tipOutBasedOnOriginal()) {
              _this.data.positionsMoney[positionType].totalPositionMoney = {
                cc: ((_this.data.totalCollectedMoney.cc / totalPointsSumm) * positionTotalPoints) || 0,
                cash: ((_this.data.totalCollectedMoney.cash / totalPointsSumm) * positionTotalPoints) || 0,
                agSvc: ((_this.data.totalCollectedMoney.agSvc / totalPointsSumm) * positionTotalPoints) || 0,
                fee: ((_this.data.totalCollectedMoney.fee / totalPointsSumm) * positionTotalPoints) || 0
              };
              _this.data.positionsMoney[positionType].totalPositionMoneyToShow = {
                cc: (((_this.data.totalCollectedMoney.cc - _this.data.totalTipOuts.cc) / totalPointsSumm) * positionTotalPoints) || 0,
                cash: (((_this.data.totalCollectedMoney.cash - _this.data.tipOuts.given.cash) / totalPointsSumm) * positionTotalPoints) || 0,
                agSvc: (((_this.data.totalCollectedMoney.agSvc - _this.data.tipOuts.given.agSvc) / totalPointsSumm) * positionTotalPoints) || 0,
                fee: (((_this.data.totalCollectedMoney.fee - _this.data.tipOuts.given.fee) / totalPointsSumm) * positionTotalPoints) || 0
              };
            } else {
              _this.data.positionsMoney[positionType].totalPositionMoney = {
                cc: ((_this.data.totalCollectedMoney.cc / totalPointsSumm) * positionTotalPoints) || 0,
                cash: ((_this.data.totalCollectedMoney.cash / totalPointsSumm) * positionTotalPoints) || 0,
                agSvc: ((_this.data.totalCollectedMoney.agSvc / totalPointsSumm) * positionTotalPoints) || 0,
                fee: ((_this.data.totalCollectedMoney.fee / totalPointsSumm) * positionTotalPoints) || 0
              };
              _this.data.positionsMoney[positionType].totalPositionMoneyToShow = {
                cc: ((_this.data.totalCollectedMoney.cc / totalPointsSumm) * positionTotalPoints * _this.data.collectedMoneyDistributePercentsPart) || 0,
                cash: ((_this.data.totalCollectedMoney.cash / totalPointsSumm) * positionTotalPoints * _this.data.collectedMoneyDistributePercentsPart) || 0,
                agSvc: ((_this.data.totalCollectedMoney.agSvc / totalPointsSumm) * positionTotalPoints * _this.data.collectedMoneyDistributePercentsPart) || 0,
                fee: ((_this.data.totalCollectedMoney.fee / totalPointsSumm) * positionTotalPoints * _this.data.collectedMoneyDistributePercentsPart) || 0
              };
            }
            (base2 = _this.data.positionsMoney[positionType]).teams || (base2.teams = {});
            if (positionTypeIsASource === "true") {
              ccTeamMap = _.map($(empDistrTable).find(".employee-distribution-line .cc-in input"), function(num) {
                return parseFloat($(num).val()) || 0;
              });
              ccTeamTotalSum = _.reduce(ccTeamMap, function(sum, num) {
                return sum + num;
              }) || 0;
              cashTeamMap = _.map($(empDistrTable).find(".employee-distribution-line .cash-in input"), function(num) {
                return parseFloat($(num).val()) || 0;
              });
              cashTeamTotalSum = _.reduce(cashTeamMap, function(sum, num) {
                return sum + num;
              }) || 0;
              agSvcTeamMap = _.map($(empDistrTable).find(".employee-distribution-line .ag-svc-in input"), function(num) {
                return parseFloat($(num).val()) || 0;
              });
              agSvcTeamTotalSum = _.reduce(agSvcTeamMap, function(sum, num) {
                return sum + num;
              }) || 0;
              feeTeamMap = _.map($(empDistrTable).find(".employee-distribution-line .fee-in input"), function(num) {
                return parseFloat($(num).val()) || 0;
              });
              feeTeamTotalSum = _.reduce(feeTeamMap, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamCollectedMoney = {
                cc: parseFloat(ccTeamTotalSum),
                cash: parseFloat(cashTeamTotalSum),
                agSvc: parseFloat(agSvcTeamTotalSum),
                fee: parseFloat(feeTeamTotalSum)
              };
              totalTeamMoneyToDistribute = {
                cc: (totalTeamCollectedMoney.cc / totalPointsSumm) * positionTotalPoints,
                cash: (totalTeamCollectedMoney.cash / totalPointsSumm) * positionTotalPoints,
                agSvc: (totalTeamCollectedMoney.agSvc / totalPointsSumm) * positionTotalPoints,
                fee: (totalTeamCollectedMoney.fee / totalPointsSumm) * positionTotalPoints
              };
              _this.data.positionsMoney[positionType].teams[teamNumber] = {
                totalTeamCollectedMoney: totalTeamCollectedMoney,
                totalTeamMoneyToDistribute: totalTeamMoneyToDistribute
              };
            } else {
              _this.data.positionsMoney[positionType].teams[teamNumber] = {
                totalTeamMoneyToDistribute: _this.data.positionsMoney[positionType].totalPositionMoney
              };
            }
            hourPrice = _this.data.initialPointsHourPrice;
            _this.data.positionsMoney[positionType].teams[teamNumber].hourPrice = _this.data.initialPointsHourPrice;
            _this.data.positionsMoney[positionType].teams[teamNumber].employees = {};
            _.each($(empDistrTable).find(".employee-distribution-line"), function(empDistrTableLine) {
              var cc_cash_minus_cash_collected, empDistrId, empDistrStatus, employeeData, employeePointsPart, hourly_rate, hoursWorkedInHours, id, salesFoodSumm, salesSumm, totalTipOutsGivenAgSvc, totalTipOutsGivenCC, totalTipOutsGivenCash, totalTipOutsGivenFee, totalTipOutsReceivedAgSvc, totalTipOutsReceivedCC, totalTipOutsReceivedCash, totalTipOutsReceivedFee;
              salesSumm = {
                cc: parseFloat($(empDistrTableLine).find(".sales-cc input").val() || 0),
                cash: parseFloat($(empDistrTableLine).find(".sales-cash input").val() || 0)
              };
              salesFoodSumm = {
                food: parseFloat($(empDistrTableLine).find(".food-sales input").val() || 0),
                beverage: parseFloat($(empDistrTableLine).find(".beverage-sales input").val() || 0)
              };
              hoursWorkedInHours = parseFloat($(empDistrTableLine).find(".number-hrs").val() || 0);
              if ($(empDistrTableLine).find(".employee-id").val() === void 0 || $(empDistrTableLine).find(".employee-id").val().length === 0) {
                id = $(empDistrTableLine).find(".employee-id").attr("data-employee-id");
              } else {
                id = $(empDistrTableLine).find(".employee-id").val();
              }
              empDistrId = $(empDistrTableLine).attr("data-emp-distribution-id");
              empDistrStatus = $(empDistrTableLine).attr("data-emp-distribution-status");
              employeePointsPart = (hoursWorkedInHours * positionPoints) / totalPointsSumm || 0;
              _this.data.positionsMoney[positionType].teams[teamNumber].employees[id] = {
                salesSumm: salesSumm,
                salesFoodSumm: salesFoodSumm,
                hoursWorkedInHours: hoursWorkedInHours,
                distributionStatus: empDistrStatus,
                distributionId: empDistrId,
                totalMoneyIn: {
                  cc: $(empDistrTableLine).find(".cc-in input").val() || 0,
                  cash: $(empDistrTableLine).find(".cash-in input").val() || 0,
                  agSvc: $(empDistrTableLine).find(".ag-svc-in input").val() || 0,
                  fee: $(empDistrTableLine).find(".fee-in input").val() || 0
                },
                totalMoneyOut: {
                  cc: (hourPrice.cc * hoursWorkedInHours * positionPoints) || 0,
                  cash: (hourPrice.cash * hoursWorkedInHours * positionPoints) || 0,
                  agSvc: (hourPrice.agSvc * hoursWorkedInHours * positionPoints) || 0,
                  fee: (hourPrice.fee * hoursWorkedInHours * positionPoints) || 0
                }
              };
              if (gon.restaurant.no_fee_calc_enabled) {
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyOut.fee = parseFloat(_this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyIn.fee);
              }
              _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].employeePointsPart = employeePointsPart;
              if (scope.tipOutBasedOnOriginal()) {
                totalTipOutsGivenCC = employeePointsPart * _this.data.totalTipOuts.cc || 0;
                totalTipOutsGivenCash = employeePointsPart * _this.data.tipOuts.given.cash || 0;
                totalTipOutsGivenAgSvc = employeePointsPart * _this.data.tipOuts.given.agSvc || 0;
                totalTipOutsGivenFee = employeePointsPart * _this.data.tipOuts.given.fee || 0;
              } else {
                totalTipOutsGivenCC = employeePointsPart * _this.data.tipOuts.givenTipOutsTotalPercentsCC * _this.data.totalCollectedMoney.cc * 1 / 100 || 0;
                totalTipOutsGivenCash = employeePointsPart * _this.data.tipOuts.givenTipOutsTotalPercents * _this.data.totalCollectedMoney.cash * 1 / 100 || 0;
                totalTipOutsGivenAgSvc = employeePointsPart * _this.data.tipOuts.givenTipOutsTotalPercents * _this.data.totalCollectedMoney.agSvc * 1 / 100 || 0;
                totalTipOutsGivenFee = employeePointsPart * _this.data.tipOuts.givenTipOutsTotalPercents * _this.data.totalCollectedMoney.fee * 1 / 100 || 0;
              }
              _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalTipOutsGiven = {
                cc: totalTipOutsGivenCC || 0,
                cash: totalTipOutsGivenCash || 0,
                agSvc: totalTipOutsGivenAgSvc || 0,
                fee: totalTipOutsGivenFee || 0
              };
              if (gon.restaurant.no_fee_calc_enabled) {
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalTipOutsGiven.fee = 0;
              }
              totalTipOutsReceivedCC = employeePointsPart * _this.data.tipOuts.received.cc || 0;
              totalTipOutsReceivedCash = employeePointsPart * _this.data.tipOuts.received.cash || 0;
              totalTipOutsReceivedAgSvc = employeePointsPart * _this.data.tipOuts.received.agSvc || 0;
              totalTipOutsReceivedFee = employeePointsPart * _this.data.tipOuts.received.fee || 0;
              _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalTipOutsReceived = {
                cc: totalTipOutsReceivedCC || 0,
                cash: totalTipOutsReceivedCash || 0,
                agSvc: totalTipOutsReceivedAgSvc || 0,
                fee: totalTipOutsReceivedFee || 0
              };
              _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute = {
                cc: ((_this.data.totalCollectedMoney.cc * employeePointsPart || 0) + totalTipOutsReceivedCC - totalTipOutsGivenCC) || 0,
                cash: ((_this.data.totalCollectedMoney.cash * employeePointsPart || 0) + totalTipOutsReceivedCash - totalTipOutsGivenCash) || 0,
                agSvc: ((_this.data.totalCollectedMoney.agSvc * employeePointsPart || 0) + totalTipOutsReceivedAgSvc - totalTipOutsGivenAgSvc) || 0,
                fee: ((_this.data.totalCollectedMoney.fee * employeePointsPart || 0) + totalTipOutsReceivedFee - totalTipOutsGivenFee) || 0
              };
              if (gon.restaurant.no_fee_calc_enabled) {
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.fee = parseFloat(_this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyIn.fee);
              }
              cc_cash_minus_cash_collected = _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cc + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cash + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.agSvc + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.fee - _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyIn.cash;
              _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cc_cash_minus_cash_collected = cc_cash_minus_cash_collected;
              employeeData = _this.data.positionsMoney[positionType].teams[teamNumber].employees[id];
              hourly_rate = (employeeData.finalMoneyToDistribute.cc + employeeData.finalMoneyToDistribute.cash + employeeData.finalMoneyToDistribute.agSvc + employeeData.finalMoneyToDistribute.fee) / employeeData.hoursWorkedInHours;
              return _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].hourly_rate = hourly_rate;
            });
            totalPositionHoursWorked = {};
            totalPositionHoursWorked = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.hoursWorkedInHours);
            });
            totalPositionHoursWorked = _.reduce(totalPositionHoursWorked, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamSales = {};
            totalTeamSalesCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.salesSumm.cc);
            });
            totalTeamSalesCC = _.reduce(totalTeamSalesCC, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamSalesCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.salesSumm.cash);
            });
            totalTeamSalesCash = _.reduce(totalTeamSalesCash, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamSales = {
              cc: totalTeamSalesCC,
              cash: totalTeamSalesCash
            };
            totalTeamFoodSales = {};
            totalTeamFoodSalesFood = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.salesFoodSumm.food);
            });
            totalTeamFoodSalesFood = _.reduce(totalTeamFoodSalesFood, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamFoodSalesBeverage = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.salesFoodSumm.beverage);
            });
            totalTeamFoodSalesBeverage = _.reduce(totalTeamFoodSalesBeverage, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamFoodSales = {
              food: totalTeamFoodSalesFood,
              beverage: totalTeamFoodSalesBeverage
            };
            totalMoneyToDistribute = {};
            totalMoneyToDistribute.cc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return data.totalMoneyOut.cc;
            });
            totalMoneyToDistribute.cc = _.reduce(totalMoneyToDistribute.cc, function(sum, num) {
              return sum + num;
            }) || 0;
            totalMoneyToDistribute.cash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return data.totalMoneyOut.cash;
            });
            totalMoneyToDistribute.cash = _.reduce(totalMoneyToDistribute.cash, function(sum, num) {
              return sum + num;
            }) || 0;
            totalMoneyToDistribute.agSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return data.totalMoneyOut.agSvc;
            });
            totalMoneyToDistribute.agSvc = _.reduce(totalMoneyToDistribute.agSvc, function(sum, num) {
              return sum + num;
            }) || 0;
            totalMoneyToDistribute.fee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return data.totalMoneyOut.fee;
            });
            totalMoneyToDistribute.fee = _.reduce(totalMoneyToDistribute.fee, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamTOGivenCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return data.totalTipOutsGiven.cc;
            });
            totalTeamTOGivenCC = _.reduce(totalTeamTOGivenCC, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamTOGivenCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return data.totalTipOutsGiven.cash;
            });
            totalTeamTOGivenCash = _.reduce(totalTeamTOGivenCash, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamTOGivenAgSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return data.totalTipOutsGiven.agSvc;
            });
            totalTeamTOGivenAgSvc = _.reduce(totalTeamTOGivenAgSvc, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamTOGivenFee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return data.totalTipOutsGiven.fee;
            });
            totalTeamTOGivenFee = _.reduce(totalTeamTOGivenFee, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamTOReceivedCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return data.totalTipOutsReceived.cc;
            });
            totalTeamTOReceivedCC = _.reduce(totalTeamTOReceivedCC, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamTOReceivedCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return data.totalTipOutsReceived.cash;
            });
            totalTeamTOReceivedCash = _.reduce(totalTeamTOReceivedCash, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamTOReceivedAgSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return data.totalTipOutsReceived.agSvc;
            });
            totalTeamTOReceivedAgSvc = _.reduce(totalTeamTOReceivedAgSvc, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamTOReceivedFee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return data.totalTipOutsReceived.fee;
            });
            totalTeamTOReceivedFee = _.reduce(totalTeamTOReceivedFee, function(sum, num) {
              return sum + num;
            }) || 0;
            finalTeamTODistributeCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return data.finalMoneyToDistribute.cc;
            });
            finalTeamTODistributeCC = _.reduce(finalTeamTODistributeCC, function(sum, num) {
              return sum + num;
            }) || 0;
            finalTeamTODistributeCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return data.finalMoneyToDistribute.cash;
            });
            finalTeamTODistributeCash = _.reduce(finalTeamTODistributeCash, function(sum, num) {
              return sum + num;
            }) || 0;
            finalTeamTODistributeCCMinusCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return data.finalMoneyToDistribute.cc_cash_minus_cash_collected;
            });
            finalTeamTODistributeCCMinusCash = _.reduce(finalTeamTODistributeCCMinusCash, function(sum, num) {
              return sum + num;
            }) || 0;
            finalTeamTODistributeAgSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return data.finalMoneyToDistribute.agSvc;
            });
            finalTeamTODistributeAgSvc = _.reduce(finalTeamTODistributeAgSvc, function(sum, num) {
              return sum + num;
            }) || 0;
            finalTeamTODistributeFee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return data.finalMoneyToDistribute.fee;
            });
            finalTeamTODistributeFee = _.reduce(finalTeamTODistributeFee, function(sum, num) {
              return sum + num;
            }) || 0;
            return _this.data.positionsMoney[positionType].teams[teamNumber].teamTotal = {
              totalTeamSales: totalTeamSales || 0,
              totalTeamFoodSales: totalTeamFoodSales || 0,
              totalPositionHoursWorked: totalPositionHoursWorked || 0,
              ccToDistribute: totalMoneyToDistribute.cc || 0,
              cashToDistribute: totalMoneyToDistribute.cash || 0,
              agSvcToDistribute: totalMoneyToDistribute.agSvc || 0,
              feeToDistribute: totalMoneyToDistribute.fee || 0,
              totalTeamTOGivenCC: totalTeamTOGivenCC || 0,
              totalTeamTOGivenCash: totalTeamTOGivenCash || 0,
              totalTeamTOGivenAgSvc: totalTeamTOGivenAgSvc || 0,
              totalTeamTOGivenFee: totalTeamTOGivenFee || 0,
              totalTeamTOReceivedCC: totalTeamTOReceivedCC || 0,
              totalTeamTOReceivedCash: totalTeamTOReceivedCash || 0,
              totalTeamTOReceivedAgSvc: totalTeamTOReceivedAgSvc || 0,
              totalTeamTOReceivedFee: totalTeamTOReceivedFee || 0,
              finalTeamTODistributeCC: finalTeamTODistributeCC || 0,
              finalTeamTODistributeCash: finalTeamTODistributeCash || 0,
              finalTeamTODistributeCCMinusCash: finalTeamTODistributeCCMinusCash || 0,
              finalTeamTODistributeAgSvc: finalTeamTODistributeAgSvc || 0,
              finalTeamTODistributeFee: finalTeamTODistributeFee || 0
            };
          };
        })(this));
        return this.data;
      };

      CalculationPercentsHelper.prototype.processPercents = function(data, scope) {
        this.data = data;
        _.each($("[data-table-type='employee-distribution']"), (function(_this) {
          return function(empDistrTable) {
            var agSvcPositionMap, agSvcPositionTotalSum, agSvcTeamMap, agSvcTeamTotalSum, base, base1, base2, cashPositionMap, cashPositionTotalSum, cashTeamMap, cashTeamTotalSum, ccPositionMap, ccPositionTotalSum, ccTeamMap, ccTeamTotalSum, feePositionMap, feePositionTotalSum, feeTeamMap, feeTeamTotalSum, finalTeamTODistributeAgSvc, finalTeamTODistributeCC, finalTeamTODistributeCCMinusCash, finalTeamTODistributeCash, finalTeamTODistributeFee, hourPrice, hoursMap, positionPercentage, positionTeamsCount, positionType, positionTypeId, positionTypeIsASource, salesFoodMapBeverage, salesFoodMapFood, salesMapCC, salesMapCash, teamNumber, teamPart, totalFoodSalesForPosition, totalFoodSalesForPositionTeam, totalFoodTipOutsForPositionToSubtract, totalFoodTipOutsForPositionToSubtractSumm, totalHoursForPosition, totalMoneyToDistribute, totalPositionHoursWorked, totalSalesForPosition, totalSalesForPositionBeverage, totalSalesForPositionCC, totalSalesForPositionCash, totalSalesForPositionFood, totalTeamCollectedMoney, totalTeamFoodSales, totalTeamFoodSalesBeverage, totalTeamFoodSalesFood, totalTeamMoneyToDistribute, totalTeamSales, totalTeamSalesCC, totalTeamSalesCash, totalTeamTOGivenAgSvc, totalTeamTOGivenCC, totalTeamTOGivenCash, totalTeamTOGivenFee, totalTeamTOReceivedAgSvc, totalTeamTOReceivedCC, totalTeamTOReceivedCash, totalTeamTOReceivedFee;
            positionType = $(empDistrTable).attr("data-position-table");
            positionTypeId = $(empDistrTable).attr("data-position-type-id");
            positionTypeIsASource = $(empDistrTable).attr("data-position-is-a-source");
            positionPercentage = parseFloat($("#positions-table input[data-percentage-for=\"" + positionType + "\"]").attr("percentage-final-value")) || 0;
            positionTeamsCount = $("[data-table-type='employee-distribution'][data-position-table=\"" + positionType + "\"]").length || 0;
            teamNumber = $(empDistrTable).attr("data-team");
            salesMapCC = _.map($(empDistrTable).find(".employee-distribution-line .sales-cc input"), function(num) {
              return parseFloat($(num).val());
            });
            salesMapCash = _.map($(empDistrTable).find(".employee-distribution-line .sales-cash input"), function(num) {
              return parseFloat($(num).val());
            });
            totalSalesForPositionCC = _.reduce(salesMapCC, function(sum, num) {
              return sum + num;
            }) || 0;
            totalSalesForPositionCash = _.reduce(salesMapCash, function(sum, num) {
              return sum + num;
            }) || 0;
            totalSalesForPosition = {
              cc: totalSalesForPositionCC,
              cash: totalSalesForPositionCash
            };
            salesFoodMapFood = _.map($(empDistrTable).find(".employee-distribution-line .food-sales input"), function(num) {
              return parseFloat($(num).val());
            });
            totalSalesForPositionFood = _.reduce(salesFoodMapFood, function(sum, num) {
              return sum + num;
            }) || 0;
            salesFoodMapBeverage = _.map($(empDistrTable).find(".employee-distribution-line .beverage-sales input"), function(num) {
              return parseFloat($(num).val());
            });
            totalSalesForPositionBeverage = _.reduce(salesFoodMapBeverage, function(sum, num) {
              return sum + num;
            }) || 0;
            totalFoodSalesForPosition = {
              food: totalSalesForPositionFood,
              beverage: totalSalesForPositionBeverage
            };
            totalFoodSalesForPositionTeam = {
              food: totalSalesForPositionFood,
              beverage: totalSalesForPositionBeverage
            };
            totalFoodTipOutsForPositionToSubtract = {
              food: totalSalesForPositionFood * data.foodTipOutPercents.food / 100,
              beverage: totalSalesForPositionBeverage * data.foodTipOutPercents.beverage / 100,
              total: (totalSalesForPositionFood + totalSalesForPositionBeverage) * data.foodTipOutPercents.total / 100
            };
            totalFoodTipOutsForPositionToSubtractSumm = totalFoodTipOutsForPositionToSubtract.food + totalFoodTipOutsForPositionToSubtract.beverage + totalFoodTipOutsForPositionToSubtract.total;
            ccPositionMap = _.map(scope.positionDistrTables(positionType).find(".employee-distribution-line .cc-in input"), function(num) {
              return parseFloat($(num).val()) || 0;
            });
            ccPositionTotalSum = _.reduce(ccPositionMap, function(sum, num) {
              return sum + num;
            }) || 0;
            cashPositionMap = _.map(scope.positionDistrTables(positionType).find(".employee-distribution-line .cash-in input"), function(num) {
              return parseFloat($(num).val()) || 0;
            });
            cashPositionTotalSum = _.reduce(cashPositionMap, function(sum, num) {
              return sum + num;
            }) || 0;
            agSvcPositionMap = _.map(scope.positionDistrTables(positionType).find(".employee-distribution-line .ag-svc-in input"), function(num) {
              return parseFloat($(num).val()) || 0;
            });
            agSvcPositionTotalSum = _.reduce(agSvcPositionMap, function(sum, num) {
              return sum + num;
            }) || 0;
            feePositionMap = _.map(scope.positionDistrTables(positionType).find(".employee-distribution-line .fee-in input"), function(num) {
              return parseFloat($(num).val()) || 0;
            });
            feePositionTotalSum = _.reduce(feePositionMap, function(sum, num) {
              return sum + num;
            }) || 0;
            _this.data.totalPositionCollectedMoney = {
              cc: ccPositionTotalSum,
              cash: cashPositionTotalSum,
              agSvc: agSvcPositionTotalSum,
              fee: feePositionTotalSum
            };
            (base = _this.data).positionsMoney || (base.positionsMoney = {});
            (base1 = _this.data.positionsMoney)[positionType] || (base1[positionType] = {
              positionTypeIsASource: positionTypeIsASource,
              positionPercentage: positionPercentage,
              positionTeamsCount: positionTeamsCount,
              totalSalesForPosition: totalSalesForPosition,
              totalFoodSalesForPosition: totalFoodSalesForPosition,
              totalFoodTipOutsForPositionToSubtract: totalFoodTipOutsForPositionToSubtract,
              totalFoodTipOutsForPositionToSubtractSumm: totalFoodTipOutsForPositionToSubtractSumm
            });
            _this.data.positionsMoney[positionType].totalPositionMoney = {
              cc: (_this.data.totalCollectedMoney.cc * positionPercentage) / 100,
              cash: (_this.data.totalCollectedMoney.cash * positionPercentage) / 100,
              agSvc: (_this.data.totalCollectedMoney.agSvc * positionPercentage) / 100,
              fee: (_this.data.totalCollectedMoney.fee * positionPercentage) / 100
            };
            _this.data.positionsMoney[positionType].totalPositionMoneyToShow = {
              cc: (_this.data.totalCollectedMoney.cc * positionPercentage * _this.data.collectedMoneyDistributePercentsPart) / 100,
              cash: (_this.data.totalCollectedMoney.cash * positionPercentage * _this.data.collectedMoneyDistributePercentsPart) / 100,
              agSvc: (_this.data.totalCollectedMoney.agSvc * positionPercentage * _this.data.collectedMoneyDistributePercentsPart) / 100,
              fee: (_this.data.totalCollectedMoney.fee * positionPercentage * _this.data.collectedMoneyDistributePercentsPart) / 100
            };
            (base2 = _this.data.positionsMoney[positionType]).teams || (base2.teams = {});
            if (positionTypeIsASource === "true") {
              ccTeamMap = _.map($(empDistrTable).find(".employee-distribution-line .cc-in input"), function(num) {
                return parseFloat($(num).val()) || 0;
              });
              ccTeamTotalSum = _.reduce(ccTeamMap, function(sum, num) {
                return sum + num;
              }) || 0;
              cashTeamMap = _.map($(empDistrTable).find(".employee-distribution-line .cash-in input"), function(num) {
                return parseFloat($(num).val()) || 0;
              });
              cashTeamTotalSum = _.reduce(cashTeamMap, function(sum, num) {
                return sum + num;
              }) || 0;
              agSvcTeamMap = _.map($(empDistrTable).find(".employee-distribution-line .ag-svc-in input"), function(num) {
                return parseFloat($(num).val()) || 0;
              });
              agSvcTeamTotalSum = _.reduce(agSvcTeamMap, function(sum, num) {
                return sum + num;
              }) || 0;
              feeTeamMap = _.map($(empDistrTable).find(".employee-distribution-line .fee-in input"), function(num) {
                return parseFloat($(num).val()) || 0;
              });
              feeTeamTotalSum = _.reduce(feeTeamMap, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamCollectedMoney = {
                cc: parseFloat(ccTeamTotalSum),
                cash: parseFloat(cashTeamTotalSum),
                agSvc: parseFloat(agSvcTeamTotalSum),
                fee: parseFloat(feeTeamTotalSum)
              };
              if (positionTeamsCount === 1) {
                teamPart = {
                  cc: 1,
                  cash: 1,
                  agSvc: 1,
                  fee: 1
                };
              } else {
                teamPart = {
                  cc: totalTeamCollectedMoney.cc / _this.data.totalPositionCollectedMoney.cc,
                  cash: totalTeamCollectedMoney.cash / _this.data.totalPositionCollectedMoney.cash,
                  agSvc: totalTeamCollectedMoney.agSvc / _this.data.totalPositionCollectedMoney.agSvc,
                  fee: totalTeamCollectedMoney.fee / _this.data.totalPositionCollectedMoney.fee
                };
              }
              totalTeamMoneyToDistribute = {
                cc: (_this.data.totalCollectedMoney.cc * positionPercentage * teamPart.cc) / 100 || 0,
                cash: (_this.data.totalCollectedMoney.cash * positionPercentage * teamPart.cash) / 100 || 0,
                agSvc: (_this.data.totalCollectedMoney.agSvc * positionPercentage * teamPart.agSvc) / 100 || 0,
                fee: (_this.data.totalCollectedMoney.fee * positionPercentage * teamPart.fee) / 100 || 0
              };
              _this.data.positionsMoney[positionType].teams[teamNumber] = {
                totalFoodTipOutsForPositionToSubtractSumm: totalFoodTipOutsForPositionToSubtractSumm,
                totalFoodSalesForPositionTeam: totalFoodSalesForPositionTeam,
                totalTeamCollectedMoney: totalTeamCollectedMoney,
                totalTeamMoneyToDistribute: totalTeamMoneyToDistribute
              };
            } else {
              totalFoodTipOutsForPositionToSubtractSumm = _this.data.totalFoodTipOutsSumm * positionPercentage / 100;
              _this.data.positionsMoney[positionType].teams[teamNumber] = {
                totalTeamMoneyToDistribute: _this.data.positionsMoney[positionType].totalPositionMoney,
                totalFoodTipOutsForPositionToSubtractSumm: totalFoodTipOutsForPositionToSubtractSumm
              };
            }
            hoursMap = _.map($(empDistrTable).find(".employee-distribution-line .number-hrs"), function(num) {
              return parseFloat($(num).val());
            });
            totalHoursForPosition = _.reduce(hoursMap, function(sum, num) {
              return sum + num;
            }) || 0;
            hourPrice = {
              cc: (_this.data.positionsMoney[positionType].teams[teamNumber].totalTeamMoneyToDistribute.cc / totalHoursForPosition) || 0,
              cash: (_this.data.positionsMoney[positionType].teams[teamNumber].totalTeamMoneyToDistribute.cash / totalHoursForPosition) || 0,
              agSvc: (_this.data.positionsMoney[positionType].teams[teamNumber].totalTeamMoneyToDistribute.agSvc / totalHoursForPosition) || 0,
              fee: (_this.data.positionsMoney[positionType].teams[teamNumber].totalTeamMoneyToDistribute.fee / totalHoursForPosition) || 0
            };
            _this.data.positionsMoney[positionType].teams[teamNumber].hourPrice = hourPrice;
            _this.data.positionsMoney[positionType].teams[teamNumber].hourPrice = hourPrice;
            _this.data.positionsMoney[positionType].teams[teamNumber].employees = {};
            _.each($(empDistrTable).find(".employee-distribution-line"), function(empDistrTableLine) {
              var cc_cash_minus_cash_collected, empDistrId, empDistrStatus, employeeData, foodTipoutsSubtractionForEmployee, hourly_rate, hoursWorkedInHours, id, originalTipOutsForEmployee, salesFoodSumm, salesSumm, totalTipOutsGivenAgSvc, totalTipOutsGivenCC, totalTipOutsGivenCCFood, totalTipOutsGivenCCOriginal, totalTipOutsGivenCash, totalTipOutsGivenFee, totalTipOutsReceivedAgSvc, totalTipOutsReceivedCC, totalTipOutsReceivedCash, totalTipOutsReceivedFee;
              salesSumm = {
                cc: parseFloat($(empDistrTableLine).find(".sales-cc input").val() || 0),
                cash: parseFloat($(empDistrTableLine).find(".sales-cash input").val() || 0)
              };
              salesFoodSumm = {
                food: parseFloat($(empDistrTableLine).find(".food-sales input").val() || 0),
                beverage: parseFloat($(empDistrTableLine).find(".beverage-sales input").val() || 0)
              };
              hoursWorkedInHours = parseFloat($(empDistrTableLine).find(".number-hrs").val() || 0);
              if ($(empDistrTableLine).find(".employee-id").val() === void 0 || $(empDistrTableLine).find(".employee-id").val().length === 0) {
                id = $(empDistrTableLine).find(".employee-id").attr("data-employee-id");
              } else {
                id = $(empDistrTableLine).find(".employee-id").val();
              }
              empDistrId = $(empDistrTableLine).attr("data-emp-distribution-id");
              empDistrStatus = $(empDistrTableLine).attr("data-emp-distribution-status");
              _this.data.positionsMoney[positionType].teams[teamNumber].employees[id] = {
                salesSumm: salesSumm,
                salesFoodSumm: salesFoodSumm,
                hoursWorkedInHours: hoursWorkedInHours,
                distributionStatus: empDistrStatus,
                distributionId: empDistrId,
                totalMoneyIn: {
                  cc: $(empDistrTableLine).find(".cc-in input").val() || 0,
                  cash: $(empDistrTableLine).find(".cash-in input").val() || 0,
                  agSvc: $(empDistrTableLine).find(".ag-svc-in input").val() || 0,
                  fee: $(empDistrTableLine).find(".fee-in input").val() || 0
                },
                totalMoneyOut: {
                  cc: (hourPrice.cc * hoursWorkedInHours) || 0,
                  cash: (hourPrice.cash * hoursWorkedInHours) || 0,
                  agSvc: (hourPrice.agSvc * hoursWorkedInHours) || 0,
                  fee: (hourPrice.fee * hoursWorkedInHours) || 0
                }
              };
              if (gon.restaurant.no_fee_calc_enabled) {
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyOut.fee = parseFloat(_this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyIn.fee);
              }
              if (positionTeamsCount === 1) {
                totalTipOutsGivenCC = ((hourPrice.cc * hoursWorkedInHours) * _this.data.tipOuts.given.cc) / _this.data.totalCollectedMoney.cc || 0;
                totalTipOutsGivenCCOriginal = ((hourPrice.cc * hoursWorkedInHours) * _this.data.tipOuts.given.cc) / _this.data.totalCollectedMoney.cc || 0;
                totalTipOutsGivenCCFood = _this.data.totalFoodTipOutsSumm * (positionPercentage / 100) * (hoursWorkedInHours / totalHoursForPosition);
                totalTipOutsGivenCC = (totalTipOutsGivenCCOriginal + totalTipOutsGivenCCFood) || 0;
              } else {
                originalTipOutsForEmployee = ((hourPrice.cc * hoursWorkedInHours) * _this.data.tipOuts.given.cc) / _this.data.totalCollectedMoney.cc;
                foodTipoutsSubtractionForEmployee = hoursWorkedInHours * _this.data.positionsMoney[positionType].teams[teamNumber].totalFoodTipOutsForPositionToSubtractSumm / totalHoursForPosition;
                totalTipOutsGivenCC = originalTipOutsForEmployee + foodTipoutsSubtractionForEmployee * (positionPercentage / 100);
                totalTipOutsGivenCC = totalTipOutsGivenCC || 0;
              }
              totalTipOutsGivenCash = (((hourPrice.cash * hoursWorkedInHours) * _this.data.tipOuts.given.cash) / _this.data.totalCollectedMoney.cash) || 0;
              totalTipOutsGivenAgSvc = (((hourPrice.agSvc * hoursWorkedInHours) * _this.data.tipOuts.given.agSvc) / _this.data.totalCollectedMoney.agSvc) || 0;
              totalTipOutsGivenFee = (((hourPrice.fee * hoursWorkedInHours) * _this.data.tipOuts.given.fee) / _this.data.totalCollectedMoney.fee) || 0;
              _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalTipOutsGiven = {
                cc: totalTipOutsGivenCC || 0,
                cash: totalTipOutsGivenCash || 0,
                agSvc: totalTipOutsGivenAgSvc || 0,
                fee: totalTipOutsGivenFee || 0
              };
              if (gon.restaurant.no_fee_calc_enabled) {
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalTipOutsGiven.fee = 0;
              }
              totalTipOutsReceivedCC = (_this.data.tipOuts.received.cc * (positionPercentage * (1 / 100) * hoursWorkedInHours / totalHoursForPosition)) / positionTeamsCount;
              totalTipOutsReceivedCash = (_this.data.tipOuts.received.cash * (positionPercentage * (1 / 100) * hoursWorkedInHours / totalHoursForPosition)) / positionTeamsCount;
              totalTipOutsReceivedAgSvc = (_this.data.tipOuts.received.agSvc * (positionPercentage * (1 / 100) * hoursWorkedInHours / totalHoursForPosition)) / positionTeamsCount;
              totalTipOutsReceivedFee = (_this.data.tipOuts.received.fee * (positionPercentage * (1 / 100) * hoursWorkedInHours / totalHoursForPosition)) / positionTeamsCount;
              _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalTipOutsReceived = {
                cc: totalTipOutsReceivedCC || 0,
                cash: totalTipOutsReceivedCash || 0,
                agSvc: totalTipOutsReceivedAgSvc || 0,
                fee: totalTipOutsReceivedFee || 0
              };
              _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute = {
                cc: ((hourPrice.cc * hoursWorkedInHours) + totalTipOutsReceivedCC - totalTipOutsGivenCC) || 0,
                cash: ((hourPrice.cash * hoursWorkedInHours) + totalTipOutsReceivedCash - totalTipOutsGivenCash) || 0,
                agSvc: ((hourPrice.agSvc * hoursWorkedInHours) + totalTipOutsReceivedAgSvc - totalTipOutsGivenAgSvc) || 0,
                fee: ((hourPrice.fee * hoursWorkedInHours) + totalTipOutsReceivedFee - totalTipOutsGivenFee) || 0
              };
              if (gon.restaurant.no_fee_calc_enabled) {
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.fee = parseFloat(_this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyIn.fee);
              }
              cc_cash_minus_cash_collected = _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cc + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cash + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.agSvc + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.fee - _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyIn.cash;
              _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cc_cash_minus_cash_collected = cc_cash_minus_cash_collected;
              employeeData = _this.data.positionsMoney[positionType].teams[teamNumber].employees[id];
              hourly_rate = (employeeData.finalMoneyToDistribute.cc + employeeData.finalMoneyToDistribute.cash + employeeData.finalMoneyToDistribute.agSvc + employeeData.finalMoneyToDistribute.fee) / employeeData.hoursWorkedInHours;
              return _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].hourly_rate = hourly_rate;
            });
            totalPositionHoursWorked = {};
            totalPositionHoursWorked = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.hoursWorkedInHours);
            });
            totalPositionHoursWorked = _.reduce(totalPositionHoursWorked, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamSales = {};
            totalTeamSalesCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.salesSumm.cc);
            });
            totalTeamSalesCC = _.reduce(totalTeamSalesCC, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamSalesCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.salesSumm.cash);
            });
            totalTeamSalesCash = _.reduce(totalTeamSalesCash, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamSales = {
              cc: totalTeamSalesCC,
              cash: totalTeamSalesCash
            };
            totalTeamFoodSales = {};
            totalTeamFoodSalesFood = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.salesFoodSumm.food);
            });
            totalTeamFoodSalesFood = _.reduce(totalTeamFoodSalesFood, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamFoodSalesBeverage = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.salesFoodSumm.beverage);
            });
            totalTeamFoodSalesBeverage = _.reduce(totalTeamFoodSalesBeverage, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamFoodSales = {
              food: totalTeamFoodSalesFood,
              beverage: totalTeamFoodSalesBeverage
            };
            totalMoneyToDistribute = {};
            totalMoneyToDistribute.cc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.totalMoneyOut.cc);
            });
            totalMoneyToDistribute.cc = _.reduce(totalMoneyToDistribute.cc, function(sum, num) {
              return sum + num;
            }) || 0;
            totalMoneyToDistribute.cash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.totalMoneyOut.cash);
            });
            totalMoneyToDistribute.cash = _.reduce(totalMoneyToDistribute.cash, function(sum, num) {
              return sum + num;
            }) || 0;
            totalMoneyToDistribute.agSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.totalMoneyOut.agSvc);
            });
            totalMoneyToDistribute.agSvc = _.reduce(totalMoneyToDistribute.agSvc, function(sum, num) {
              return sum + num;
            }) || 0;
            totalMoneyToDistribute.fee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.totalMoneyOut.fee);
            });
            totalMoneyToDistribute.fee = _.reduce(totalMoneyToDistribute.fee, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamTOGivenCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.totalTipOutsGiven.cc);
            });
            totalTeamTOGivenCC = _.reduce(totalTeamTOGivenCC, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamTOGivenCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.totalTipOutsGiven.cash);
            });
            totalTeamTOGivenCash = _.reduce(totalTeamTOGivenCash, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamTOGivenAgSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.totalTipOutsGiven.agSvc);
            });
            totalTeamTOGivenAgSvc = _.reduce(totalTeamTOGivenAgSvc, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamTOGivenFee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.totalTipOutsGiven.fee);
            });
            totalTeamTOGivenFee = _.reduce(totalTeamTOGivenFee, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamTOReceivedCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.totalTipOutsReceived.cc);
            });
            totalTeamTOReceivedCC = _.reduce(totalTeamTOReceivedCC, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamTOReceivedCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.totalTipOutsReceived.cash);
            });
            totalTeamTOReceivedCash = _.reduce(totalTeamTOReceivedCash, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamTOReceivedAgSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.totalTipOutsReceived.agSvc);
            });
            totalTeamTOReceivedAgSvc = _.reduce(totalTeamTOReceivedAgSvc, function(sum, num) {
              return sum + num;
            }) || 0;
            totalTeamTOReceivedFee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.totalTipOutsReceived.fee);
            });
            totalTeamTOReceivedFee = _.reduce(totalTeamTOReceivedFee, function(sum, num) {
              return sum + num;
            }) || 0;
            finalTeamTODistributeCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.finalMoneyToDistribute.cc);
            });
            finalTeamTODistributeCC = _.reduce(finalTeamTODistributeCC, function(sum, num) {
              return sum + num;
            }) || 0;
            finalTeamTODistributeCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.finalMoneyToDistribute.cash);
            });
            finalTeamTODistributeCash = _.reduce(finalTeamTODistributeCash, function(sum, num) {
              return sum + num;
            }) || 0;
            finalTeamTODistributeCCMinusCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.finalMoneyToDistribute.cc_cash_minus_cash_collected);
            });
            finalTeamTODistributeCCMinusCash = _.reduce(finalTeamTODistributeCCMinusCash, function(sum, num) {
              return sum + num;
            }) || 0;
            finalTeamTODistributeAgSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.finalMoneyToDistribute.agSvc);
            });
            finalTeamTODistributeAgSvc = _.reduce(finalTeamTODistributeAgSvc, function(sum, num) {
              return sum + num;
            }) || 0;
            finalTeamTODistributeFee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
              return parseFloat(data.finalMoneyToDistribute.fee);
            });
            finalTeamTODistributeFee = _.reduce(finalTeamTODistributeFee, function(sum, num) {
              return sum + num;
            }) || 0;
            return _this.data.positionsMoney[positionType].teams[teamNumber].teamTotal = {
              totalTeamSales: totalTeamSales || 0,
              totalTeamFoodSales: totalTeamFoodSales || 0,
              totalPositionHoursWorked: totalPositionHoursWorked || 0,
              ccToDistribute: totalMoneyToDistribute.cc || 0,
              cashToDistribute: totalMoneyToDistribute.cash || 0,
              agSvcToDistribute: totalMoneyToDistribute.agSvc || 0,
              feeToDistribute: totalMoneyToDistribute.fee || 0,
              totalTeamTOGivenCC: totalTeamTOGivenCC || 0,
              totalTeamTOGivenCash: totalTeamTOGivenCash || 0,
              totalTeamTOGivenAgSvc: totalTeamTOGivenAgSvc || 0,
              totalTeamTOGivenFee: totalTeamTOGivenFee || 0,
              totalTeamTOReceivedCC: totalTeamTOReceivedCC || 0,
              totalTeamTOReceivedCash: totalTeamTOReceivedCash || 0,
              totalTeamTOReceivedAgSvc: totalTeamTOReceivedAgSvc || 0,
              totalTeamTOReceivedFee: totalTeamTOReceivedFee || 0,
              finalTeamTODistributeCC: finalTeamTODistributeCC || 0,
              finalTeamTODistributeCash: finalTeamTODistributeCash || 0,
              finalTeamTODistributeCCMinusCash: finalTeamTODistributeCCMinusCash || 0,
              finalTeamTODistributeAgSvc: finalTeamTODistributeAgSvc || 0,
              finalTeamTODistributeFee: finalTeamTODistributeFee || 0
            };
          };
        })(this));
        return this.data;
      };

      return CalculationPercentsHelper;

    })();
    return Tipmetric.Modules.calculationPercentsHelper = new CalculationPercentsHelper;
  })();

}).call(this);
(function() {
  (function() {
    var CalculationSTOHelper;
    CalculationSTOHelper = (function() {
      function CalculationSTOHelper() {}

      CalculationSTOHelper.prototype.process = function(data, scope) {
        this.data = data;
        this.data.positionTipSharings = this.percentsHash(data, scope);
        this.calculateSingleSourceTipSharings(data, scope);
        this.processPercents(data, scope);
        if (gon.restaurant.overtips_enabled && gon.calculation_distribution_logic_type === "source_tip_outs") {
          this.calculateOvertips(data, scope);
          this.calculateOvertipsHash(data, scope);
          this.calculateOvertipFinalNumbers(data, scope);
          this.calculateOvertipTeamNumbers(data, scope);
        }
        return this.data;
      };

      CalculationSTOHelper.prototype.calculationSourcePositionsNow = function() {
        return Tipmetric.Controllers.tipSharingToggleView.getSourcePositions();
      };

      CalculationSTOHelper.prototype.calculateSingleSourceTipSharings = function(data, scope) {
        var i, len, ref, total, tr, ts;
        ref = this.data.sourceTipSharings;
        for (i = 0, len = ref.length; i < len; i++) {
          ts = ref[i];
          tr = $("#position_tip_sharings .position-tip-sharing[data-temp-id='" + ts.tempId + "']");
          if (ts.sourceType === 'hours') {
            $(tr).find(".total").text(total);
            continue;
          }
          if (ts.sourceType === 'custom_cash_tips_distr') {
            $(tr).find("input.percentage").val(0);
            $(tr).find(".total").text(0);
            continue;
          }
          if (gon.restaurant.sto_condition_rules_enabled && ts.conditionResult === false) {
            ts['totalSumm'] = 0;
            $(tr).find(".total").text(0);
            continue;
          }
          total = data.totalCollectedMoneyByPositions[ts.sourcePositionId][ts.sourceType] * ts.percentage / 100;
          $(tr).find(".total").text(total);
          ts['totalSumm'] = total;
        }
        total = _.reduce(_.collect(this.data.sourceTipSharings, function(ts) {
          return ts.totalSumm || 0;
        }), function(sum, num) {
          return sum + num;
        });
        return $("#position_tip_sharings .total-tips").text(total);
      };

      CalculationSTOHelper.prototype.calculateOvertips = function(data, scope) {
        var employeeCollectedData, i, len, money, overtip, overtipExtended, overtipsExtended, ref, total, tr;
        this.data = data;
        overtipsExtended = [];
        ref = data.overtips;
        for (i = 0, len = ref.length; i < len; i++) {
          overtip = ref[i];
          overtipExtended = overtip;
          tr = $("#overtips .overtip[data-temp-id='" + overtip.tempId + "']");
          employeeCollectedData = data.positionsMoney[overtip.sourcePositionName].teams[1].employees[overtip.sourceEmployeeId];
          if (employeeCollectedData) {
            money = employeeCollectedData.totalMoneyIn[overtip.sourceType];
            if (_.contains(["cc_amount", "cash_amount"], overtip.sourceType)) {
              total = overtip.percentage;
            } else {
              total = money * overtip.percentage / 100;
            }
            $(tr).find(".total").text(total);
            overtipExtended['totalSumm'] = total;
          } else {
            total = 0;
            $(tr).find(".total").text(total);
            overtipExtended['totalSumm'] = total;
          }
          overtipsExtended.push(overtipExtended);
        }
        data.overtips = overtipsExtended;
        return this.data = data;
      };

      CalculationSTOHelper.prototype.percentsHash = function(data, scope) {
        var i, j, k, l, len, len1, len2, len3, len4, m, percents, source, sourcePosition, sourcePositions, sourceTipSharings, sourceTipSharingsByPositionAndSource, sourceTipSharingsByPositionAndSourceTotals, sourceTipSharingsByTargetAndSource, sourceTipSharingsByTargetAndSourceReceived, sources, targetPosition, targetPositions;
        sourceTipSharings = data.sourceTipSharings;
        if (gon.restaurant.sto_condition_rules_enabled) {
          sourceTipSharings = _.filter(data.sourceTipSharings, function(ts) {
            return ts.conditionResult === true;
          });
        }
        sourcePositions = _.collect(sourceTipSharings, function(ts) {
          return ts.sourcePositionId;
        });
        targetPositions = _.collect(sourceTipSharings, function(ts) {
          return ts.targetPositionId;
        });
        sourceTipSharingsByPositionAndSource = {};
        sourceTipSharingsByPositionAndSourceTotals = {};
        for (i = 0, len = sourcePositions.length; i < len; i++) {
          sourcePosition = sourcePositions[i];
          sourceTipSharingsByPositionAndSource[sourcePosition] = _.flatten(_.groupBy(_.filter(sourceTipSharings, function(ts) {
            return ts.sourcePositionId === sourcePosition;
          }), function(ts) {
            if (ts.sourceType === 'cc_cash') {
              return ['cc', 'cash'];
            } else {
              return ts.sourceType;
            }
          }));
          sources = _.flatten(_.collect(_.filter(sourceTipSharings, function(ts) {
            return ts.sourcePositionId === sourcePosition;
          }), function(ts) {
            if (ts.sourceType === 'cc_cash') {
              return ['cc', 'cash'];
            } else {
              return ts.sourceType;
            }
          }));
          sourceTipSharingsByPositionAndSourceTotals[sourcePosition] = {};
          for (j = 0, len1 = sources.length; j < len1; j++) {
            source = sources[j];
            sourceTipSharingsByPositionAndSourceTotals[sourcePosition][source] = _.reduce(_.collect(_.filter(sourceTipSharings, function(ts) {
              var sourcePositionMatch, sourceTypeMatch;
              sourcePositionMatch = ts.sourcePositionId === sourcePosition;
              sourceTypeMatch = _.contains(['cc', 'cash', 'cc_cash'], source) ? source === 'cc' ? ts.sourceType === 'cc' || ts.sourceType === 'cc_cash' : source === 'cash' ? ts.sourceType === 'cash' || ts.sourceType === 'cc_cash' : void 0 : ts.sourceType === source;
              return sourcePositionMatch && sourceTypeMatch;
            }), function(ts) {
              return parseFloat(ts.percentage);
            }), function(sum, num) {
              return sum + num;
            });
          }
        }
        sourceTipSharingsByTargetAndSource = {};
        sourceTipSharingsByTargetAndSourceReceived = {};
        for (k = 0, len2 = targetPositions.length; k < len2; k++) {
          targetPosition = targetPositions[k];
          sourceTipSharingsByTargetAndSource[targetPosition] = _.flatten(_.groupBy(_.filter(sourceTipSharings, function(ts) {
            return ts.targetPositionId === targetPosition;
          }), function(ts) {
            if (ts.sourceType === 'cc_cash') {
              return ['cc', 'cash'];
            } else {
              return ts.sourceType;
            }
          }));
          sources = _.flatten(_.collect(_.filter(sourceTipSharings, function(ts) {
            return ts.targetPositionId === targetPosition;
          }), function(ts) {
            if (ts.sourceType === 'cc_cash') {
              return ['cc', 'cash'];
            } else {
              return ts.sourceType;
            }
          }));
          sourceTipSharingsByTargetAndSourceReceived[targetPosition] = {};
          for (l = 0, len3 = sources.length; l < len3; l++) {
            source = sources[l];
            sourcePositions = _.collect(_.filter(sourceTipSharings, function(ts) {
              var sourceTypeMatch, targetPositionMatch;
              targetPositionMatch = ts.targetPositionId === targetPosition;
              sourceTypeMatch = _.contains(['cc', 'cash'], source) ? source === 'cc' ? ts.sourceType === 'cc' || ts.sourceType === 'cc_cash' : source === 'cash' ? ts.sourceType === 'cash' || ts.sourceType === 'cc_cash' : void 0 : ts.sourceType === source;
              return targetPositionMatch && sourceTypeMatch;
            }), function(ts) {
              return ts.sourcePositionId;
            });
            sourceTipSharingsByTargetAndSourceReceived[targetPosition][source] = {};
            for (m = 0, len4 = sourcePositions.length; m < len4; m++) {
              sourcePosition = sourcePositions[m];
              percents = _.collect(_.filter(sourceTipSharings, function(ts) {
                var sourceTypeMatch;
                sourceTypeMatch = _.contains(['cc', 'cash'], source) ? source === 'cc' ? ts.sourceType === 'cc' || ts.sourceType === 'cc_cash' : source === 'cash' ? ts.sourceType === 'cash' || ts.sourceType === 'cc_cash' : void 0 : ts.sourceType === source;
                return ts.targetPositionId === targetPosition && sourceTypeMatch && ts.sourcePositionId === sourcePosition;
              }), function(ts) {
                return parseFloat(ts.percentage);
              });
              sourceTipSharingsByTargetAndSourceReceived[targetPosition][source][sourcePosition] = _.reduce(percents, function(sum, num) {
                return sum + num;
              });
            }
          }
        }
        return {
          bySourcePositionAndSource: sourceTipSharingsByPositionAndSource,
          bySourcePositionAndSourceTotals: sourceTipSharingsByPositionAndSourceTotals,
          byTargetAndSource: sourceTipSharingsByTargetAndSource,
          byTargetAndSourceReceived: sourceTipSharingsByTargetAndSourceReceived
        };
      };

      CalculationSTOHelper.prototype.calculateDistributions = function(data, scope) {};

      CalculationSTOHelper.prototype.processPercents = function(data, scope) {
        var totalSalesCash;
        this.data = data;
        _.each($(".position-block"), (function(_this) {
          return function(positionBlock) {
            var agSvcPositionMap, agSvcPositionTotalSum, base, base1, cashPositionMap, cashPositionTotalSum, ccPositionMap, ccPositionTotalSum, feePositionMap, feePositionTotalSum, hoursMapForPosition, positionTeamsCount, positionType, positionTypeId, positionTypeIsASource, salesFoodMapBeverage, salesFoodMapFood, salesMapCC, salesMapCash, totalHoursForPosition, totalSalesForPositionBeverage, totalSalesForPositionCC, totalSalesForPositionCash, totalSalesForPositionFood;
            positionType = $(positionBlock).attr("data-position-block-name");
            positionTypeId = $(positionBlock).attr("data-position-block-id");
            positionTypeIsASource = $(positionBlock).attr("data-position-is-a-source");
            positionTeamsCount = $("[data-table-type='employee-distribution'][data-position-table=\"" + positionType + "\"]").length || 0;
            ccPositionMap = _.map($(positionBlock).find(".employee-distribution-line .cc-in input"), function(num) {
              return parseFloat($(num).val()) || 0;
            });
            ccPositionTotalSum = _.reduce(ccPositionMap, function(sum, num) {
              return sum + num;
            }) || 0;
            cashPositionMap = _.map($(positionBlock).find(".employee-distribution-line .cash-in input"), function(num) {
              return parseFloat($(num).val()) || 0;
            });
            cashPositionTotalSum = _.reduce(cashPositionMap, function(sum, num) {
              return sum + num;
            }) || 0;
            agSvcPositionMap = _.map($(positionBlock).find(".employee-distribution-line .ag-svc-in input"), function(num) {
              return parseFloat($(num).val()) || 0;
            });
            agSvcPositionTotalSum = _.reduce(agSvcPositionMap, function(sum, num) {
              return sum + num;
            }) || 0;
            feePositionMap = _.map($(positionBlock).find(".employee-distribution-line .fee-in input"), function(num) {
              return parseFloat($(num).val()) || 0;
            });
            feePositionTotalSum = _.reduce(feePositionMap, function(sum, num) {
              return sum + num;
            }) || 0;
            salesMapCC = _.map($(positionBlock).find(".employee-distribution-line .sales-cc input"), function(num) {
              return parseFloat($(num).val());
            });
            totalSalesForPositionCC = _.reduce(salesMapCC, function(sum, num) {
              return sum + num;
            }) || 0;
            salesMapCash = _.map($(positionBlock).find(".employee-distribution-line .sales-cash input"), function(num) {
              return parseFloat($(num).val());
            });
            totalSalesForPositionCash = _.reduce(salesMapCash, function(sum, num) {
              return sum + num;
            }) || 0;
            salesFoodMapFood = _.map($(positionBlock).find(".employee-distribution-line .food-sales input"), function(num) {
              return parseFloat($(num).val());
            });
            totalSalesForPositionFood = _.reduce(salesFoodMapFood, function(sum, num) {
              return sum + num;
            }) || 0;
            salesFoodMapBeverage = _.map($(positionBlock).find(".employee-distribution-line .beverage-sales input"), function(num) {
              return parseFloat($(num).val());
            });
            totalSalesForPositionBeverage = _.reduce(salesFoodMapBeverage, function(sum, num) {
              return sum + num;
            }) || 0;
            hoursMapForPosition = _.map($(positionBlock).find(".employee-distribution-line .number-hrs"), function(num) {
              return parseFloat($(num).val());
            });
            totalHoursForPosition = _.reduce(hoursMapForPosition, function(sum, num) {
              return sum + num;
            }) || 0;
            (base = _this.data).positionsMoney || (base.positionsMoney = {});
            return (base1 = _this.data.positionsMoney)[positionType] || (base1[positionType] = {
              positionTypeIsASource: positionTypeIsASource,
              positionTeamsCount: positionTeamsCount,
              totalHoursForPosition: totalHoursForPosition,
              totalSalesForPosition: {
                cc: totalSalesForPositionCC,
                cash: totalSalesForPositionCash
              },
              totalFoodSalesForPosition: {
                food: totalSalesForPositionFood,
                beverage: totalSalesForPositionBeverage,
                foodAndBeverageSalesSumm: totalSalesForPositionFood + totalSalesForPositionBeverage
              },
              totalPositionMoney: {
                cc: ccPositionTotalSum * _this.data.cc_minus_fee,
                cc_original: ccPositionTotalSum,
                cc_cash: ccPositionTotalSum + cashPositionTotalSum,
                cash: cashPositionTotalSum,
                agSvc: agSvcPositionTotalSum * _this.data.ag_svc_minus_fee,
                fee: feePositionTotalSum
              }
            });
          };
        })(this));
        totalSalesCash = _.map(_.pairs(this.data.positionsMoney), function(f) {
          return f[1].totalSalesForPosition.cash;
        });
        this.data.totalSalesCash = _.reduce(totalSalesCash, function(sum, num) {
          return sum + num;
        }) || 0;
        _.each($(".position-block"), (function(_this) {
          return function(positionBlock) {
            var positionTeamsCount, positionType, positionTypeId, positionTypeIsASource;
            positionType = $(positionBlock).attr("data-position-block-name");
            positionTypeId = $(positionBlock).attr("data-position-block-id");
            positionTypeIsASource = $(positionBlock).attr("data-position-is-a-source");
            positionTeamsCount = $("[data-table-type='employee-distribution'][data-position-table=\"" + positionType + "\"]").length || 0;
            return _.each($(positionBlock).find("[data-table-type='employee-distribution']"), function(empDistrTable) {
              var agSvcTeamMap, agSvcTeamTotalSum, base, base1, base2, base3, base4, cashTeamMap, cashTeamTotalSum, ccTeamMap, ccTeamTotalSum, feeTeamMap, feeTeamTotalSum, finalTeamTODistributeAgSvc, finalTeamTODistributeCC, finalTeamTODistributeCCMinusCash, finalTeamTODistributeCash, finalTeamTODistributeFee, salesFoodMapBeverage, salesFoodMapFood, salesMapCC, salesMapCash, teamHoursMap, teamNumber, teamPart, totalGivenOutPercents, totalHoursForteam, totalMoneyToDistribute, totalPositionHoursWorked, totalReceivedPercents, totalSalesForPositionBeverage, totalSalesForPositionCC, totalSalesForPositionCash, totalSalesForPositionFood, totalTeamCollectedMoney, totalTeamFoodSales, totalTeamFoodSalesBeverage, totalTeamFoodSalesFood, totalTeamMoneyToDistribute, totalTeamSales, totalTeamSalesCC, totalTeamSalesCash, totalTeamTOGivenAgSvc, totalTeamTOGivenCC, totalTeamTOGivenCash, totalTeamTOGivenFee, totalTeamTOReceivedAgSvc, totalTeamTOReceivedCC, totalTeamTOReceivedCash, totalTeamTOReceivedFee;
              teamNumber = $(empDistrTable).attr("data-team");
              (base = _this.data.positionsMoney[positionType]).teams || (base.teams = {});
              if (positionTypeIsASource === "true") {
                ccTeamMap = _.map($(empDistrTable).find(".employee-distribution-line .cc-in input"), function(num) {
                  return parseFloat($(num).val()) || 0;
                });
                ccTeamTotalSum = _.reduce(ccTeamMap, function(sum, num) {
                  return sum + num;
                }) || 0;
                cashTeamMap = _.map($(empDistrTable).find(".employee-distribution-line .cash-in input"), function(num) {
                  return parseFloat($(num).val()) || 0;
                });
                cashTeamTotalSum = _.reduce(cashTeamMap, function(sum, num) {
                  return sum + num;
                }) || 0;
                agSvcTeamMap = _.map($(empDistrTable).find(".employee-distribution-line .ag-svc-in input"), function(num) {
                  return parseFloat($(num).val()) || 0;
                });
                agSvcTeamTotalSum = _.reduce(agSvcTeamMap, function(sum, num) {
                  return sum + num;
                }) || 0;
                feeTeamMap = _.map($(empDistrTable).find(".employee-distribution-line .fee-in input"), function(num) {
                  return parseFloat($(num).val()) || 0;
                });
                feeTeamTotalSum = _.reduce(feeTeamMap, function(sum, num) {
                  return sum + num;
                }) || 0;
                salesFoodMapFood = _.map($(empDistrTable).find(".employee-distribution-line .food-sales input"), function(num) {
                  return parseFloat($(num).val());
                });
                totalSalesForPositionFood = _.reduce(salesFoodMapFood, function(sum, num) {
                  return sum + num;
                }) || 0;
                salesFoodMapBeverage = _.map($(empDistrTable).find(".employee-distribution-line .beverage-sales input"), function(num) {
                  return parseFloat($(num).val());
                });
                totalSalesForPositionBeverage = _.reduce(salesFoodMapBeverage, function(sum, num) {
                  return sum + num;
                }) || 0;
                salesMapCC = _.map($(empDistrTable).find(".employee-distribution-line .sales-cc input"), function(num) {
                  return parseFloat($(num).val());
                });
                totalSalesForPositionCC = _.reduce(salesMapCC, function(sum, num) {
                  return sum + num;
                }) || 0;
                salesMapCash = _.map($(empDistrTable).find(".employee-distribution-line .sales-cash input"), function(num) {
                  return parseFloat($(num).val());
                });
                totalSalesForPositionCash = _.reduce(salesMapCash, function(sum, num) {
                  return sum + num;
                }) || 0;
                totalTeamCollectedMoney = {
                  cc: parseFloat(ccTeamTotalSum),
                  cash: parseFloat(cashTeamTotalSum),
                  agSvc: parseFloat(agSvcTeamTotalSum),
                  salesCC: parseFloat(totalSalesForPositionCC),
                  salesCash: parseFloat(totalSalesForPositionCash),
                  fee: parseFloat(feeTeamTotalSum),
                  food: parseFloat(totalSalesForPositionFood),
                  beverage: parseFloat(totalSalesForPositionBeverage),
                  foodAndBeverageSalesSumm: parseFloat(totalSalesForPositionFood + totalSalesForPositionBeverage)
                };
                teamPart = {
                  cc: 1,
                  cash: 1,
                  agSvc: 1,
                  fee: 1
                };
                totalTeamMoneyToDistribute = totalTeamCollectedMoney;
                _this.data.positionsMoney[positionType].teams[teamNumber] = {
                  totalTeamCollectedMoney: totalTeamCollectedMoney,
                  totalTeamMoneyToDistribute: totalTeamMoneyToDistribute
                };
              } else {
                _this.data.positionsMoney[positionType].teams[teamNumber] = {
                  totalTeamMoneyToDistribute: _this.data.positionsMoney[positionType].totalPositionMoney
                };
              }
              teamHoursMap = _.map($(empDistrTable).find(".employee-distribution-line .number-hrs"), function(num) {
                return parseFloat($(num).val());
              });
              totalHoursForteam = _.reduce(teamHoursMap, function(sum, num) {
                return sum + num;
              }) || 0;
              _this.data.positionsMoney[positionType].teams[teamNumber].totalHoursForteam = totalHoursForteam;
              (base1 = _this.data).positionsMoney || (base1.positionsMoney = {});
              (base2 = _this.data.positionsMoney)[positionType] || (base2[positionType] = {});
              (base3 = _this.data.positionsMoney[positionType]).teams || (base3.teams = {});
              (base4 = _this.data.positionsMoney[positionType].teams)[teamNumber] || (base4[teamNumber] = {});
              _this.data.positionsMoney[positionType].teams[teamNumber].employees = {};
              if (positionTypeIsASource === "true") {
                totalGivenOutPercents = data.positionTipSharings.bySourcePositionAndSourceTotals[positionTypeId] || {};
                totalReceivedPercents = data.positionTipSharings.byTargetAndSourceReceived[positionTypeId] || {};
              } else {
                totalReceivedPercents = data.positionTipSharings.byTargetAndSourceReceived[positionTypeId] || {};
              }
              _.each($(empDistrTable).find(".employee-distribution-line"), function(empDistrTableLine) {
                var agSvcMoney, beverageSalesMoney, cashMoney, ccMoney, cc_cash_minus_cash_collected, empDistrId, empDistrStatus, employeeData, feeMoney, finalMoneyReceived, finalMoneyToDistribute, finalMoneyToSubtract, foodSalesMoney, hourly_rate, hoursMultiplied, hoursWorkedInHours, i, id, j, joinedDistribution, joinedPositionsTables, k, l, len, len1, len2, len3, line, lines, partOfTeamMoney, ref, ref1, salesCCMoney, salesCashMoney, salesFoodSumm, salesSumm, sourcePositionName, table, totalAgSvcMoney, totalBeverageSalesMoney, totalCashMoney, totalCcMoney, totalFBSalesMoney, totalFeeMoney, totalFoodAndBeverageSalesMoney, totalFoodSalesMoney, totalHoursForJoinedDistribution, totalHoursMap, totalMoneyIn, totalMoneyOut, totalMoneyOutMinusFee, totalPositionHoursWorked, totalSalesCCMoney, totalSalesCashMoney;
                hoursWorkedInHours = parseFloat($(empDistrTableLine).find(".number-hrs").val() || 0);
                partOfTeamMoney = hoursWorkedInHours / totalHoursForteam;
                empDistrId = $(empDistrTableLine).attr("data-emp-distribution-id");
                empDistrStatus = $(empDistrTableLine).attr("data-emp-distribution-status");
                if ($(empDistrTableLine).find(".employee-id").val() === void 0 || $(empDistrTableLine).find(".employee-id").val().length === 0) {
                  id = $(empDistrTableLine).find(".employee-id").attr("data-employee-id");
                } else {
                  id = $(empDistrTableLine).find(".employee-id").val();
                }
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id] = {
                  hoursWorkedInHours: hoursWorkedInHours,
                  distributionStatus: empDistrStatus,
                  distributionId: empDistrId
                };
                salesSumm = {
                  cc: parseFloat($(empDistrTableLine).find(".sales-cc input").val() || 0),
                  cash: parseFloat($(empDistrTableLine).find(".sales-cash input").val() || 0)
                };
                salesFoodSumm = {
                  food: parseFloat($(empDistrTableLine).find(".food-sales input").val() || 0),
                  beverage: parseFloat($(empDistrTableLine).find(".beverage-sales input").val() || 0)
                };
                salesFoodSumm['total_food_sales'] = salesFoodSumm.food + salesFoodSumm.beverage;
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].salesSumm = salesSumm;
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].salesFoodSumm = salesFoodSumm;
                totalMoneyIn = {
                  cc: parseFloat($(empDistrTableLine).find(".cc-in input").val()) || 0,
                  cash: parseFloat($(empDistrTableLine).find(".cash-in input").val()) || 0,
                  agSvc: parseFloat($(empDistrTableLine).find(".ag-svc-in input").val()) || 0,
                  salesCC: parseFloat($(empDistrTableLine).find(".sales-cc input").val() || 0),
                  salesCash: parseFloat($(empDistrTableLine).find(".sales-cash input").val() || 0),
                  fee: parseFloat($(empDistrTableLine).find(".fee-in input").val()) || 0,
                  foodSales: parseFloat($(empDistrTableLine).find(".food-sales input").val()) || 0,
                  beverageSales: parseFloat($(empDistrTableLine).find(".beverage-sales input").val()) || 0
                };
                totalMoneyIn['foodAndBeverageSalesSumm'] = totalMoneyIn.foodSales + totalMoneyIn.beverageSales;
                totalMoneyOutMinusFee = {
                  cc: parseFloat($(empDistrTableLine).find(".cc-in input").val()) * _this.data.cc_minus_fee || 0,
                  cash: parseFloat($(empDistrTableLine).find(".cash-in input").val()) || 0,
                  agSvc: parseFloat($(empDistrTableLine).find(".ag-svc-in input").val()) * _this.data.ag_svc_minus_fee || 0,
                  salesCC: parseFloat($(empDistrTableLine).find(".sales-cc input").val() || 0),
                  salesCash: parseFloat($(empDistrTableLine).find(".sales-cash input").val() || 0),
                  fee: parseFloat($(empDistrTableLine).find(".fee-in input").val()) || 0,
                  foodSales: parseFloat($(empDistrTableLine).find(".food-sales input").val()) || 0,
                  beverageSales: parseFloat($(empDistrTableLine).find(".beverage-sales input").val()) || 0
                };
                totalMoneyOutMinusFee['foodAndBeverageSalesSumm'] = totalMoneyOutMinusFee.foodSales + totalMoneyOutMinusFee.beverageSales;
                if (positionTypeIsASource === "true") {
                  if (!_.isEmpty(totalReceivedPercents)) {
                    if (Tipmetric.Controllers.tipSharingToggleView.isPartOfJoinedDistribution(_this.data, positionTypeId)) {
                      joinedDistribution = _.filter(_this.data.joinedTipSharings, function(ts) {
                        return ts.sourcePositionId === positionTypeId || ts.targetPositionId === positionTypeId;
                      })[0];
                      joinedPositionsTables = _.filter($("table.team-table"), function(table) {
                        return _.contains(joinedDistribution.joinedPositionIds, $(table).attr("data-position-type-id"));
                      });
                      lines = [];
                      for (i = 0, len = joinedPositionsTables.length; i < len; i++) {
                        table = joinedPositionsTables[i];
                        ref = $(table).find(".employee-distribution-line");
                        for (j = 0, len1 = ref.length; j < len1; j++) {
                          line = ref[j];
                          lines.push(line);
                        }
                      }
                      totalHoursMap = _.map(lines, function(e) {
                        var originalHoursValue;
                        originalHoursValue = parseFloat($(e).find(".number-hrs").val());
                        if ($(e).closest(".team-table").attr('data-position-type-id') === joinedDistribution.targetPositionId) {
                          originalHoursValue = originalHoursValue * joinedDistribution.multiplier;
                        }
                        return originalHoursValue;
                      });
                      totalHoursForJoinedDistribution = _.reduce(totalHoursMap, function(sum, num) {
                        return sum + num;
                      }) || 0;
                      partOfTeamMoney = hoursWorkedInHours / totalHoursForJoinedDistribution;
                    }
                    totalMoneyOut = {
                      cc: _this.data.positionsMoney[positionType].teams[teamNumber].totalTeamCollectedMoney.cc * partOfTeamMoney * _this.data.cc_minus_fee || 0,
                      cash: _this.data.positionsMoney[positionType].teams[teamNumber].totalTeamCollectedMoney.cash * partOfTeamMoney || 0,
                      agSvc: _this.data.positionsMoney[positionType].teams[teamNumber].totalTeamCollectedMoney.agSvc * partOfTeamMoney * _this.data.ag_svc_minus_fee || 0,
                      fee: _this.data.positionsMoney[positionType].teams[teamNumber].totalTeamCollectedMoney.fee * partOfTeamMoney || 0,
                      salesCC: _this.data.positionsMoney[positionType].teams[teamNumber].totalTeamCollectedMoney.salesCC * partOfTeamMoney || 0,
                      salesCash: _this.data.positionsMoney[positionType].teams[teamNumber].totalTeamCollectedMoney.salesCash * partOfTeamMoney || 0,
                      foodSales: _this.data.positionsMoney[positionType].teams[teamNumber].totalTeamCollectedMoney.food * partOfTeamMoney || 0,
                      beverageSales: _this.data.positionsMoney[positionType].teams[teamNumber].totalTeamCollectedMoney.beverage * partOfTeamMoney || 0
                    };
                    ccMoney = _.map(_.pairs(totalReceivedPercents['cc']), function(ccSourcePosition) {
                      var positionName;
                      positionName = _.findWhere(_this.calculationSourcePositionsNow(), {
                        id: ccSourcePosition[0]
                      }).name;
                      return _this.data.positionsMoney[positionName].totalPositionMoney["cc"] * (ccSourcePosition[1] / 100);
                    });
                    cashMoney = _.map(_.pairs(totalReceivedPercents['cash']), function(cashSourcePosition) {
                      var positionName;
                      positionName = _.findWhere(_this.calculationSourcePositionsNow(), {
                        id: cashSourcePosition[0]
                      }).name;
                      return _this.data.positionsMoney[positionName].totalPositionMoney["cash"] * (cashSourcePosition[1] / 100);
                    });
                    feeMoney = _.map(_.pairs(totalReceivedPercents['fee']), function(feeSourcePosition) {
                      var positionName;
                      positionName = _.findWhere(_this.calculationSourcePositionsNow(), {
                        id: feeSourcePosition[0]
                      }).name;
                      return _this.data.positionsMoney[positionName].totalPositionMoney["fee"] * (feeSourcePosition[1] / 100);
                    });
                    agSvcMoney = _.map(_.pairs(totalReceivedPercents['agSvc']), function(agSvcSourcePosition) {
                      var positionName;
                      positionName = _.findWhere(_this.calculationSourcePositionsNow(), {
                        id: agSvcSourcePosition[0]
                      }).name;
                      return _this.data.positionsMoney[positionName].totalPositionMoney["agSvc"] * (agSvcSourcePosition[1] / 100);
                    });
                    salesCCMoney = _.map(_.pairs(totalReceivedPercents['salesCC']), function(sourcePosition) {
                      var positionName;
                      positionName = _.findWhere(_this.calculationSourcePositionsNow(), {
                        id: sourcePosition[0]
                      }).name;
                      return _this.data.positionsMoney[positionName].totalSalesForPosition["cc"] * (sourcePosition[1] / 100);
                    });
                    salesCashMoney = _.map(_.pairs(totalReceivedPercents['salesCash']), function(sourcePosition) {
                      var positionName;
                      positionName = _.findWhere(_this.calculationSourcePositionsNow(), {
                        id: sourcePosition[0]
                      }).name;
                      return _this.data.positionsMoney[positionName].totalSalesForPosition["cash"] * (sourcePosition[1] / 100);
                    });
                    totalFBSalesMoney = _.map(_.pairs(totalReceivedPercents['foodAndBeverageSalesSumm']), function(sourcePosition) {
                      var positionName;
                      positionName = _.findWhere(_this.calculationSourcePositionsNow(), {
                        id: sourcePosition[0]
                      }).name;
                      return _this.data.positionsMoney[positionName].totalFoodSalesForPosition["foodAndBeverageSalesSumm"] * (sourcePosition[1] / 100);
                    });
                    foodSalesMoney = _.map(_.pairs(totalReceivedPercents['foodSales']), function(sourcePosition) {
                      var positionName;
                      positionName = _.findWhere(_this.calculationSourcePositionsNow(), {
                        id: sourcePosition[0]
                      }).name;
                      return _this.data.positionsMoney[positionName].totalFoodSalesForPosition["food"] * (sourcePosition[1] / 100);
                    });
                    beverageSalesMoney = _.map(_.pairs(totalReceivedPercents['beverageSales']), function(sourcePosition) {
                      var positionName;
                      positionName = _.findWhere(_this.calculationSourcePositionsNow(), {
                        id: sourcePosition[0]
                      }).name;
                      return _this.data.positionsMoney[positionName].totalFoodSalesForPosition["beverage"] * (sourcePosition[1] / 100);
                    });
                    finalMoneyReceived = {
                      cc: (_.reduce(_.flatten(ccMoney), function(sum, num) {
                        return sum + num;
                      }) || 0) * partOfTeamMoney,
                      cash: (_.reduce(_.flatten(cashMoney), function(sum, num) {
                        return sum + num;
                      }) || 0) * partOfTeamMoney,
                      fee: (_.reduce(_.flatten(feeMoney), function(sum, num) {
                        return sum + num;
                      }) || 0) * partOfTeamMoney,
                      agSvc: (_.reduce(_.flatten(agSvcMoney), function(sum, num) {
                        return sum + num;
                      }) || 0) * partOfTeamMoney,
                      salesCC: (_.reduce(_.flatten(salesCCMoney), function(sum, num) {
                        return sum + num;
                      }) || 0) * partOfTeamMoney,
                      salesCash: (_.reduce(_.flatten(salesCashMoney), function(sum, num) {
                        return sum + num;
                      }) || 0) * partOfTeamMoney,
                      foodSales: (_.reduce(_.flatten(foodSalesMoney), function(sum, num) {
                        return sum + num;
                      }) || 0) * partOfTeamMoney,
                      beverageSales: (_.reduce(_.flatten(beverageSalesMoney), function(sum, num) {
                        return sum + num;
                      }) || 0) * partOfTeamMoney,
                      foodAndBeverageSales: (_.reduce(_.flatten(totalFBSalesMoney), function(sum, num) {
                        return sum + num;
                      }) || 0) * partOfTeamMoney
                    };
                    finalMoneyReceived.cc = finalMoneyReceived.cc + finalMoneyReceived.foodSales + finalMoneyReceived.beverageSales + finalMoneyReceived.foodAndBeverageSales + finalMoneyReceived.salesCC + finalMoneyReceived.salesCash;
                    finalMoneyToSubtract = {
                      cc: totalMoneyOut['cc'] * (totalGivenOutPercents['cc'] || 0) / 100,
                      cash: totalMoneyOut['cash'] * (totalGivenOutPercents['cash'] || 0) / 100,
                      agSvc: totalMoneyOut['agSvc'] * (totalGivenOutPercents['agSvc'] || 0) / 100,
                      fee: totalMoneyOut['fee'] * (totalGivenOutPercents['fee'] || 0) / 100,
                      salesCC: totalMoneyOut['salesCC'] * (totalGivenOutPercents['salesCC'] || 0) / 100,
                      salesCash: totalMoneyOut['salesCash'] * (totalGivenOutPercents['salesCash'] || 0) / 100,
                      foodSales: totalMoneyOut['foodSales'] * (totalGivenOutPercents['foodSales'] || 0) / 100,
                      beverageSales: totalMoneyOut['beverageSales'] * (totalGivenOutPercents['beverageSales'] || 0) / 100,
                      foodAndBeverageSalesSumm: (totalMoneyOut['foodSales'] + totalMoneyOut['beverageSales']) * (totalGivenOutPercents['foodAndBeverageSalesSumm'] || 0) / 100
                    };
                    finalMoneyToSubtract.cc = finalMoneyToSubtract.cc + finalMoneyToSubtract.foodSales + finalMoneyToSubtract.beverageSales + finalMoneyToSubtract.foodAndBeverageSalesSumm + finalMoneyToSubtract.salesCC + finalMoneyToSubtract.salesCash;
                    finalMoneyToDistribute = {
                      cc: totalMoneyOut['cc'] - finalMoneyToSubtract.cc + finalMoneyReceived.cc,
                      cash: totalMoneyOut['cash'] - finalMoneyToSubtract.cash + finalMoneyReceived.cash,
                      agSvc: totalMoneyOut['agSvc'] - finalMoneyToSubtract.agSvc + finalMoneyReceived.agSvc,
                      fee: totalMoneyOut['fee'] - finalMoneyToSubtract.fee + finalMoneyReceived.fee
                    };
                    if (_.filter(_this.data.sourceTipSharings, function(ts) {
                      return ts.sourceType === 'custom_cash_tips_distr' && ts.sourcePositionId === positionTypeId;
                    }).length > 0) {
                      finalMoneyToDistribute.cash = totalMoneyIn.cash;
                    }
                    _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyIn = totalMoneyIn;
                    _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyOut = totalMoneyOut;
                    _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute = finalMoneyToDistribute;
                    _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalOvertipsGiven = {};
                    _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistributePlusOvertips = finalMoneyToDistribute;
                    cc_cash_minus_cash_collected = _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cc + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cash + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.agSvc + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.fee + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.agSvc + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.fee - _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyIn.cash;
                    _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cc_cash_minus_cash_collected = cc_cash_minus_cash_collected;
                  } else {
                    finalMoneyToSubtract = {
                      cc: totalMoneyOutMinusFee['cc'] * (totalGivenOutPercents['cc'] || 0) / 100,
                      cash: totalMoneyOutMinusFee['cash'] * (totalGivenOutPercents['cash'] || 0) / 100,
                      agSvc: totalMoneyOutMinusFee['agSvc'] * (totalGivenOutPercents['agSvc'] || 0) / 100,
                      fee: totalMoneyOutMinusFee['fee'] * (totalGivenOutPercents['fee'] || 0) / 100,
                      salesCC: totalMoneyOutMinusFee['salesCC'] * (totalGivenOutPercents['salesCC'] || 0) / 100,
                      salesCash: totalMoneyOutMinusFee['salesCash'] * (totalGivenOutPercents['salesCash'] || 0) / 100,
                      foodSales: totalMoneyOutMinusFee['foodSales'] * (totalGivenOutPercents['foodSales'] || 0) / 100,
                      beverageSales: totalMoneyOutMinusFee['beverageSales'] * (totalGivenOutPercents['beverageSales'] || 0) / 100,
                      foodAndBeverageSalesSumm: (totalMoneyOutMinusFee['foodSales'] + totalMoneyOutMinusFee['beverageSales']) * (totalGivenOutPercents['foodAndBeverageSalesSumm'] || 0) / 100
                    };
                    finalMoneyToSubtract.cc = finalMoneyToSubtract.cc + finalMoneyToSubtract.foodSales + finalMoneyToSubtract.beverageSales + finalMoneyToSubtract.foodAndBeverageSalesSumm + finalMoneyToSubtract.salesCC + finalMoneyToSubtract.salesCash;
                    finalMoneyToDistribute = {
                      cc: totalMoneyOutMinusFee['cc'] - finalMoneyToSubtract.cc,
                      cash: totalMoneyOutMinusFee['cash'] - finalMoneyToSubtract.cash,
                      agSvc: totalMoneyOutMinusFee['agSvc'] - finalMoneyToSubtract.agSvc,
                      fee: totalMoneyOutMinusFee['fee'] - finalMoneyToSubtract.fee
                    };
                    if (_.filter(_this.data.sourceTipSharings, function(ts) {
                      return ts.sourceType === 'custom_cash_tips_distr' && ts.sourcePositionId === positionTypeId;
                    }).length > 0) {
                      finalMoneyToDistribute.cash = totalMoneyIn.cash;
                    }
                    _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyIn = totalMoneyIn;
                    _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyOut = totalMoneyOutMinusFee;
                    _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute = finalMoneyToDistribute;
                    _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistributePlusOvertips = finalMoneyToDistribute;
                    cc_cash_minus_cash_collected = _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cc + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cash + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.agSvc + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.fee + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.agSvc + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.fee - _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyIn.cash;
                    _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cc_cash_minus_cash_collected = cc_cash_minus_cash_collected;
                  }
                } else {
                  ccMoney = _.map(_.pairs(totalReceivedPercents['cc']), function(ccSourcePosition) {
                    var positionName;
                    positionName = _.findWhere(_this.calculationSourcePositionsNow(), {
                      id: ccSourcePosition[0]
                    }).name;
                    return _this.data.positionsMoney[positionName].totalPositionMoney["cc"] * (ccSourcePosition[1] / 100);
                  });
                  cashMoney = _.map(_.pairs(totalReceivedPercents['cash']), function(cashSourcePosition) {
                    var positionName;
                    positionName = _.findWhere(_this.calculationSourcePositionsNow(), {
                      id: cashSourcePosition[0]
                    }).name;
                    return _this.data.positionsMoney[positionName].totalPositionMoney["cash"] * (cashSourcePosition[1] / 100);
                  });
                  feeMoney = _.map(_.pairs(totalReceivedPercents['fee']), function(feeSourcePosition) {
                    var positionName;
                    positionName = _.findWhere(_this.calculationSourcePositionsNow(), {
                      id: feeSourcePosition[0]
                    }).name;
                    return _this.data.positionsMoney[positionName].totalPositionMoney["fee"] * (feeSourcePosition[1] / 100);
                  });
                  agSvcMoney = _.map(_.pairs(totalReceivedPercents['agSvc']), function(agSvcSourcePosition) {
                    var positionName;
                    positionName = _.findWhere(_this.calculationSourcePositionsNow(), {
                      id: agSvcSourcePosition[0]
                    }).name;
                    return _this.data.positionsMoney[positionName].totalPositionMoney["agSvc"] * (agSvcSourcePosition[1] / 100);
                  });
                  salesCCMoney = _.map(_.pairs(totalReceivedPercents['salesCC']), function(sourcePosition) {
                    var positionName;
                    positionName = _.findWhere(_this.calculationSourcePositionsNow(), {
                      id: sourcePosition[0]
                    }).name;
                    return _this.data.positionsMoney[positionName].totalSalesForPosition["cc"] * (sourcePosition[1] / 100);
                  });
                  salesCashMoney = _.map(_.pairs(totalReceivedPercents['salesCash']), function(sourcePosition) {
                    var positionName;
                    positionName = _.findWhere(_this.calculationSourcePositionsNow(), {
                      id: sourcePosition[0]
                    }).name;
                    return _this.data.positionsMoney[positionName].totalSalesForPosition["cash"] * (sourcePosition[1] / 100);
                  });
                  totalFBSalesMoney = _.map(_.pairs(totalReceivedPercents['foodAndBeverageSalesSumm']), function(sourcePosition) {
                    var positionName;
                    positionName = _.findWhere(_this.calculationSourcePositionsNow(), {
                      id: sourcePosition[0]
                    }).name;
                    return _this.data.positionsMoney[positionName].totalFoodSalesForPosition["foodAndBeverageSalesSumm"] * (sourcePosition[1] / 100);
                  });
                  foodSalesMoney = _.map(_.pairs(totalReceivedPercents['foodSales']), function(sourcePosition) {
                    var positionName;
                    positionName = _.findWhere(_this.calculationSourcePositionsNow(), {
                      id: sourcePosition[0]
                    }).name;
                    return _this.data.positionsMoney[positionName].totalFoodSalesForPosition["food"] * (sourcePosition[1] / 100);
                  });
                  beverageSalesMoney = _.map(_.pairs(totalReceivedPercents['beverageSales']), function(sourcePosition) {
                    var positionName;
                    positionName = _.findWhere(_this.calculationSourcePositionsNow(), {
                      id: sourcePosition[0]
                    }).name;
                    return _this.data.positionsMoney[positionName].totalFoodSalesForPosition["beverage"] * (sourcePosition[1] / 100);
                  });
                  partOfTeamMoney = hoursWorkedInHours / totalHoursForteam;
                  totalCcMoney = (_.reduce(_.flatten(ccMoney), function(sum, num) {
                    return sum + num;
                  }) || 0) * partOfTeamMoney;
                  totalCashMoney = (_.reduce(_.flatten(cashMoney), function(sum, num) {
                    return sum + num;
                  }) || 0) * partOfTeamMoney;
                  totalFeeMoney = (_.reduce(_.flatten(feeMoney), function(sum, num) {
                    return sum + num;
                  }) || 0) * partOfTeamMoney;
                  totalAgSvcMoney = (_.reduce(_.flatten(agSvcMoney), function(sum, num) {
                    return sum + num;
                  }) || 0) * partOfTeamMoney;
                  totalFoodSalesMoney = (_.reduce(_.flatten(foodSalesMoney), function(sum, num) {
                    return sum + num;
                  }) || 0) * partOfTeamMoney;
                  totalSalesCCMoney = (_.reduce(_.flatten(salesCCMoney), function(sum, num) {
                    return sum + num;
                  }) || 0) * partOfTeamMoney;
                  totalSalesCashMoney = (_.reduce(_.flatten(salesCashMoney), function(sum, num) {
                    return sum + num;
                  }) || 0) * partOfTeamMoney;
                  totalBeverageSalesMoney = (_.reduce(_.flatten(beverageSalesMoney), function(sum, num) {
                    return sum + num;
                  }) || 0) * partOfTeamMoney;
                  totalFoodAndBeverageSalesMoney = (_.reduce(_.flatten(totalFBSalesMoney), function(sum, num) {
                    return sum + num;
                  }) || 0) * partOfTeamMoney;
                  if (Tipmetric.Controllers.tipSharingToggleView.isPartOfJoinedDistribution(_this.data, positionTypeId)) {
                    joinedDistribution = _.filter(_this.data.joinedTipSharings, function(ts) {
                      return ts.sourcePositionId === positionTypeId || ts.targetPositionId === positionTypeId;
                    })[0];
                    joinedPositionsTables = _.filter($("table.team-table"), function(table) {
                      return _.contains(joinedDistribution.joinedPositionIds, $(table).attr("data-position-type-id"));
                    });
                    lines = [];
                    for (k = 0, len2 = joinedPositionsTables.length; k < len2; k++) {
                      table = joinedPositionsTables[k];
                      ref1 = $(table).find(".employee-distribution-line");
                      for (l = 0, len3 = ref1.length; l < len3; l++) {
                        line = ref1[l];
                        lines.push(line);
                      }
                    }
                    totalHoursMap = _.map(lines, function(e) {
                      var originalHoursValue;
                      originalHoursValue = parseFloat($(e).find(".number-hrs").val());
                      if ($(e).closest(".team-table").attr('data-position-type-id') === joinedDistribution.targetPositionId) {
                        originalHoursValue = originalHoursValue * joinedDistribution.multiplier;
                      }
                      return originalHoursValue;
                    });
                    totalHoursForJoinedDistribution = _.reduce(totalHoursMap, function(sum, num) {
                      return sum + num;
                    }) || 0;
                    partOfTeamMoney = (hoursWorkedInHours * joinedDistribution.multiplier) / totalHoursForJoinedDistribution;
                    sourcePositionName = _.findWhere(gon.calculation_source_positions, {
                      id: joinedDistribution.sourcePositionId
                    }).name;
                    totalPositionHoursWorked = _this.data.positionsMoney[sourcePositionName].teams[1].teamTotal.totalPositionHoursWorked;
                    hoursMultiplied = hoursWorkedInHours * joinedDistribution.multiplier;
                    finalMoneyToDistribute = {
                      cc: hoursMultiplied * _this.data.positionsMoney[sourcePositionName].teams[1].teamTotal.finalTeamTODistributeCC / totalPositionHoursWorked,
                      cash: hoursMultiplied * _this.data.positionsMoney[sourcePositionName].teams[1].teamTotal.finalTeamTODistributeCash / totalPositionHoursWorked,
                      fee: hoursMultiplied * _this.data.positionsMoney[sourcePositionName].teams[1].teamTotal.finalTeamTODistributeFee / totalPositionHoursWorked,
                      agSvc: hoursMultiplied * _this.data.positionsMoney[sourcePositionName].teams[1].teamTotal.finalTeamTODistributeAgSvc / totalPositionHoursWorked
                    };
                  } else {
                    finalMoneyToDistribute = {
                      cc: totalCcMoney + totalFoodSalesMoney + totalBeverageSalesMoney + totalFoodAndBeverageSalesMoney + totalSalesCCMoney + totalSalesCashMoney,
                      cash: totalCashMoney,
                      fee: totalFeeMoney,
                      agSvc: totalAgSvcMoney,
                      salesCC: totalSalesCCMoney,
                      salesCash: totalSalesCashMoney,
                      foodSales: totalFoodSalesMoney,
                      beverageSales: totalBeverageSalesMoney,
                      totalFoodAndBeverageSalesMoney: totalFoodAndBeverageSalesMoney
                    };
                  }
                  _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyIn = totalMoneyIn;
                  _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyOut = totalMoneyIn;
                  _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute = finalMoneyToDistribute;
                  _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalOvertipsGiven = {};
                  _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistributePlusOvertips = finalMoneyToDistribute;
                  cc_cash_minus_cash_collected = _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cc + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cash + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.agSvc + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.fee - _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyIn.cash;
                  _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cc_cash_minus_cash_collected = cc_cash_minus_cash_collected;
                }
                if (positionTypeIsASource === "true") {
                  _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalTipOutsGiven = {
                    cc: finalMoneyToSubtract.cc || 0,
                    cash: finalMoneyToSubtract.cash || 0,
                    agSvc: finalMoneyToSubtract.agSvc || 0,
                    fee: finalMoneyToSubtract.fee || 0,
                    salesCC: finalMoneyToSubtract.salesCC || 0,
                    salesCash: finalMoneyToSubtract.salesCash || 0,
                    foodSales: finalMoneyToSubtract.foodSales || 0,
                    beverageSales: finalMoneyToSubtract.beverageSales || 0,
                    totalFoodAndBeverageSalesMoney: finalMoneyToSubtract.totalFoodAndBeverageSalesMoney || 0
                  };
                  finalMoneyReceived || (finalMoneyReceived = {});
                  _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalTipOutsReceived = {
                    cc: finalMoneyReceived.cc || 0,
                    cash: finalMoneyReceived.cash || 0,
                    agSvc: finalMoneyReceived.agSvc || 0,
                    fee: finalMoneyReceived.fee || 0,
                    salesCC: finalMoneyReceived.salesCC || 0,
                    salesCash: finalMoneyReceived.salesCash || 0,
                    foodSales: finalMoneyReceived.foodSales || 0,
                    beverageSales: finalMoneyReceived.beverageSales || 0,
                    totalFoodAndBeverageSalesMoney: finalMoneyReceived.cc || 0
                  };
                } else {
                  _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalTipOutsGiven = {
                    cc: 0,
                    cash: 0,
                    agSvc: 0,
                    fee: 0,
                    salesCC: 0,
                    salesCash: 0,
                    foodSales: 0,
                    beverageSales: 0,
                    totalFoodAndBeverageSalesMoney: 0
                  };
                  _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalTipOutsReceived = _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute;
                }
                employeeData = _this.data.positionsMoney[positionType].teams[teamNumber].employees[id];
                hourly_rate = (employeeData.finalMoneyToDistribute.cc + employeeData.finalMoneyToDistribute.cash + employeeData.finalMoneyToDistribute.agSvc + employeeData.finalMoneyToDistribute.fee) / employeeData.hoursWorkedInHours;
                return _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].hourly_rate = hourly_rate;
              });
              totalPositionHoursWorked = {};
              totalPositionHoursWorked = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.hoursWorkedInHours);
              });
              totalPositionHoursWorked = _.reduce(totalPositionHoursWorked, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamSales = {};
              totalTeamSalesCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.salesSumm.cc);
              });
              totalTeamSalesCC = _.reduce(totalTeamSalesCC, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamSalesCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.salesSumm.cash);
              });
              totalTeamSalesCash = _.reduce(totalTeamSalesCash, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamSales = {
                cc: totalTeamSalesCC,
                cash: totalTeamSalesCash
              };
              totalTeamFoodSales = {};
              totalTeamFoodSalesFood = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.salesFoodSumm.food);
              });
              totalTeamFoodSalesFood = _.reduce(totalTeamFoodSalesFood, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamFoodSalesBeverage = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.salesFoodSumm.beverage);
              });
              totalTeamFoodSalesBeverage = _.reduce(totalTeamFoodSalesBeverage, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamFoodSales = {
                food: totalTeamFoodSalesFood,
                beverage: totalTeamFoodSalesBeverage
              };
              totalMoneyToDistribute = {};
              totalMoneyToDistribute.cc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalMoneyOut.cc);
              });
              totalMoneyToDistribute.cc = _.reduce(totalMoneyToDistribute.cc, function(sum, num) {
                return sum + num;
              }) || 0;
              totalMoneyToDistribute.cash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalMoneyOut.cash);
              });
              totalMoneyToDistribute.cash = _.reduce(totalMoneyToDistribute.cash, function(sum, num) {
                return sum + num;
              }) || 0;
              totalMoneyToDistribute.agSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalMoneyOut.agSvc);
              });
              totalMoneyToDistribute.agSvc = _.reduce(totalMoneyToDistribute.agSvc, function(sum, num) {
                return sum + num;
              }) || 0;
              totalMoneyToDistribute.fee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalMoneyOut.fee);
              });
              totalMoneyToDistribute.fee = _.reduce(totalMoneyToDistribute.fee, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOGivenCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsGiven.cc);
              });
              totalTeamTOGivenCC = _.reduce(totalTeamTOGivenCC, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOGivenCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsGiven.cash);
              });
              totalTeamTOGivenCash = _.reduce(totalTeamTOGivenCash, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOGivenAgSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsGiven.agSvc);
              });
              totalTeamTOGivenAgSvc = _.reduce(totalTeamTOGivenAgSvc, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOGivenFee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsGiven.fee);
              });
              totalTeamTOGivenFee = _.reduce(totalTeamTOGivenFee, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOReceivedCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsReceived.cc);
              });
              totalTeamTOReceivedCC = _.reduce(totalTeamTOReceivedCC, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOReceivedCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsReceived.cash);
              });
              totalTeamTOReceivedCash = _.reduce(totalTeamTOReceivedCash, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOReceivedAgSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsReceived.agSvc);
              });
              totalTeamTOReceivedAgSvc = _.reduce(totalTeamTOReceivedAgSvc, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOReceivedFee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsReceived.fee);
              });
              totalTeamTOReceivedFee = _.reduce(totalTeamTOReceivedFee, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.finalMoneyToDistribute.cc);
              });
              finalTeamTODistributeCC = _.reduce(finalTeamTODistributeCC, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.finalMoneyToDistribute.cash);
              });
              finalTeamTODistributeCash = _.reduce(finalTeamTODistributeCash, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeCCMinusCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return data.finalMoneyToDistribute.cc_cash_minus_cash_collected;
              });
              finalTeamTODistributeCCMinusCash = _.reduce(finalTeamTODistributeCCMinusCash, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeAgSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.finalMoneyToDistribute.agSvc);
              });
              finalTeamTODistributeAgSvc = _.reduce(finalTeamTODistributeAgSvc, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeFee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.finalMoneyToDistribute.fee);
              });
              finalTeamTODistributeFee = _.reduce(finalTeamTODistributeFee, function(sum, num) {
                return sum + num;
              }) || 0;
              return _this.data.positionsMoney[positionType].teams[teamNumber].teamTotal = {
                totalTeamSales: totalTeamSales || 0,
                totalTeamFoodSales: totalTeamFoodSales || 0,
                totalTeamCollectedMoney: totalTeamCollectedMoney,
                totalPositionHoursWorked: totalPositionHoursWorked || 0,
                ccToDistribute: totalMoneyToDistribute.cc || 0,
                cashToDistribute: totalMoneyToDistribute.cash || 0,
                agSvcToDistribute: totalMoneyToDistribute.agSvc || 0,
                feeToDistribute: totalMoneyToDistribute.fee || 0,
                totalTeamTOGivenCC: totalTeamTOGivenCC || 0,
                totalTeamTOGivenCash: totalTeamTOGivenCash || 0,
                totalTeamTOGivenAgSvc: totalTeamTOGivenAgSvc || 0,
                totalTeamTOGivenFee: totalTeamTOGivenFee || 0,
                totalTeamTOReceivedCC: totalTeamTOReceivedCC || 0,
                totalTeamTOReceivedCash: totalTeamTOReceivedCash || 0,
                totalTeamTOReceivedAgSvc: totalTeamTOReceivedAgSvc || 0,
                totalTeamTOReceivedFee: totalTeamTOReceivedFee || 0,
                finalTeamTODistributeCC: finalTeamTODistributeCC || 0,
                finalTeamTODistributeCash: finalTeamTODistributeCash || 0,
                finalTeamTODistributeCCMinusCash: finalTeamTODistributeCCMinusCash || 0,
                finalTeamTODistributeAgSvc: finalTeamTODistributeAgSvc || 0,
                finalTeamTODistributeFee: finalTeamTODistributeFee || 0,
                finalOvertipsGiven: {},
                finalMoneyToDistributePlusOvertips: totalMoneyToDistribute
              };
            });
          };
        })(this));
        return this.data;
      };

      CalculationSTOHelper.prototype.calculateOvertipsHash = function(data, scope) {
        var collectedMoney, employeeId, i, j, k, keyAndValues, l, len, len1, len2, len3, overtipPercents, overtips, overtipsByPositionAndEmployeeAsSource, overtipsByPositionAndEmployeeAsSourceReceived, overtipsByPositionAndEmployeeAsSourceReceivedTotalSumms, overtipsByPositionAndEmployeeAsSourceTotalPercents, overtipsFiltered, overtipsFilteredGrouped, overtipsFilteredTotalSumms, overtipsGivenSumms, overtipsReceivedSumms, positionId, positionName, ref, ref1, sourceAndValues, sourcePositionEmployee, sourcePositionsEmployees, sourceType, sourceTypeOriginal, targetPositionEmployee, targetPositionsEmployees, totalPercents;
        this.data = data;
        this.data.overtipsCalculations = {};
        overtips = data.overtips;
        sourcePositionsEmployees = _.collect(overtips, function(ot) {
          return ot.sourcePositionId + "-" + ot.sourceEmployeeId;
        });
        targetPositionsEmployees = _.collect(overtips, function(ot) {
          return ot.targetPositionId + "-" + ot.targetEmployeeId;
        });
        overtipsByPositionAndEmployeeAsSource = {};
        overtipsByPositionAndEmployeeAsSourceTotalPercents = {};
        overtipsGivenSumms = {};
        for (i = 0, len = sourcePositionsEmployees.length; i < len; i++) {
          sourcePositionEmployee = sourcePositionsEmployees[i];
          overtipsFiltered = _.filter(overtips, function(ot) {
            return (ot.sourcePositionId + "-" + ot.sourceEmployeeId) === sourcePositionEmployee;
          });
          overtipsFilteredGrouped = _.groupBy(overtipsFiltered, function(ot) {
            return ot.sourceType;
          });
          overtipsByPositionAndEmployeeAsSource[sourcePositionEmployee] = overtipsFilteredGrouped;
          ref = _.pairs(overtipsFilteredGrouped);
          for (j = 0, len1 = ref.length; j < len1; j++) {
            sourceAndValues = ref[j];
            sourceType = sourceAndValues[0];
            overtipPercents = sourceAndValues[1];
            overtipsByPositionAndEmployeeAsSourceTotalPercents[sourcePositionEmployee] || (overtipsByPositionAndEmployeeAsSourceTotalPercents[sourcePositionEmployee] = {});
            totalPercents = _.reduce(_.collect(overtipPercents, function(ot) {
              return parseFloat(ot.percentage);
            }), function(sum, num) {
              return sum + num;
            });
            overtipsByPositionAndEmployeeAsSourceTotalPercents[sourcePositionEmployee][sourceType] = totalPercents;
            positionId = sourcePositionEmployee.split("-")[0];
            employeeId = sourcePositionEmployee.split("-")[1];
            positionName = _.filter(gon.restaurant.restaurant_position_types, function(position) {
              return position._id.$oid === positionId;
            })[0].name;
            collectedMoney = data.positionsMoney[positionName].teams[1].employees[employeeId].totalMoneyIn[sourceType];
            overtipsGivenSumms[sourcePositionEmployee] || (overtipsGivenSumms[sourcePositionEmployee] = {});
            if (_.contains(["cc_amount", "cash_amount"], sourceType)) {
              sourceTypeOriginal = sourceType.replace("_amount");
              overtipsGivenSumms[sourcePositionEmployee][sourceType] = totalPercents;
            } else {
              overtipsGivenSumms[sourcePositionEmployee][sourceType] = totalPercents * collectedMoney / 100;
            }
            this.data.overtipsCalculations['overtipsByPositionAndEmployeeAsSourceTotalPercents'] = overtipsByPositionAndEmployeeAsSourceTotalPercents;
            this.data.overtipsCalculations['overtipsGivenSumms'] = overtipsGivenSumms;
          }
        }
        overtipsReceivedSumms = {};
        overtipsByPositionAndEmployeeAsSourceReceived = {};
        overtipsByPositionAndEmployeeAsSourceReceivedTotalSumms = {};
        for (k = 0, len2 = targetPositionsEmployees.length; k < len2; k++) {
          targetPositionEmployee = targetPositionsEmployees[k];
          overtipsFiltered = _.filter(overtips, function(ot) {
            return (ot.targetPositionId + "-" + ot.targetEmployeeId) === targetPositionEmployee;
          });
          overtipsFilteredGrouped = _.groupBy(overtipsFiltered, function(ot) {
            return ot.sourceType;
          });
          overtipsFilteredTotalSumms = {};
          ref1 = _.pairs(overtipsFilteredGrouped);
          for (l = 0, len3 = ref1.length; l < len3; l++) {
            keyAndValues = ref1[l];
            overtipsFilteredTotalSumms[keyAndValues[0]] = _.reduce(_.collect(keyAndValues[1], function(ot) {
              return ot.totalSumm;
            }), function(sum, num) {
              return sum + num;
            });
          }
          overtipsByPositionAndEmployeeAsSourceReceivedTotalSumms[targetPositionEmployee] = overtipsFilteredTotalSumms;
        }
        return this.data.overtipsByPositionAndEmployeeAsSourceReceivedTotalSumms = overtipsByPositionAndEmployeeAsSourceReceivedTotalSumms;
      };

      CalculationSTOHelper.prototype.calculateOvertipFinalNumbers = function(data, scope) {
        var employee, employeeId, finalMoneyInclOvertips, finalMoneyToDistribute, finalOvertipsReceived, i, j, k, len, len1, len2, moneyGivenByThisEmployee, positionEmployeeIdentifier, positionId, positionName, ref, ref1, ref2, teamIndex;
        ref = _.keys(data.positionsMoney);
        for (i = 0, len = ref.length; i < len; i++) {
          positionName = ref[i];
          positionId = _.findWhere(gon.restaurant.restaurant_position_types, {
            name: positionName
          })._id.$oid;
          ref1 = _.keys(data.positionsMoney[positionName].teams);
          for (j = 0, len1 = ref1.length; j < len1; j++) {
            teamIndex = ref1[j];
            ref2 = _.keys(data.positionsMoney[positionName].teams[teamIndex].employees);
            for (k = 0, len2 = ref2.length; k < len2; k++) {
              employeeId = ref2[k];
              employee = data.positionsMoney[positionName].teams[teamIndex].employees[employeeId];
              finalMoneyToDistribute = employee.finalMoneyToDistribute;
              positionEmployeeIdentifier = positionId + "-" + employeeId;
              if (data.overtipsCalculations && data.overtipsCalculations['overtipsGivenSumms']) {
                moneyGivenByThisEmployee = data.overtipsCalculations['overtipsGivenSumms'][positionEmployeeIdentifier];
              }
              if (data.overtipsByPositionAndEmployeeAsSourceReceivedTotalSumms) {
                finalOvertipsReceived = data.overtipsByPositionAndEmployeeAsSourceReceivedTotalSumms[positionEmployeeIdentifier];
              }
              finalMoneyInclOvertips = {};
              finalMoneyInclOvertips['cc'] = finalMoneyToDistribute.cc;
              finalMoneyInclOvertips['cash'] = finalMoneyToDistribute.cash;
              if (moneyGivenByThisEmployee) {
                finalMoneyInclOvertips['cc'] = finalMoneyInclOvertips['cc'] - (moneyGivenByThisEmployee.cc || 0) - (moneyGivenByThisEmployee.foodSales || 0) - (moneyGivenByThisEmployee.beverageSales || 0) - (moneyGivenByThisEmployee.foodAndBeverageSalesSumm || 0) - (moneyGivenByThisEmployee.salesCC || 0) - (moneyGivenByThisEmployee.cc_amount || 0);
                finalMoneyInclOvertips['cash'] = finalMoneyInclOvertips['cash'] - (moneyGivenByThisEmployee.cash || 0) - (moneyGivenByThisEmployee.cash_amount || 0);
              }
              if (finalOvertipsReceived) {
                finalMoneyInclOvertips['cc'] = finalMoneyInclOvertips['cc'] + (finalOvertipsReceived.cc || 0) + (finalOvertipsReceived.foodSales || 0) + (finalOvertipsReceived.beverageSales || 0) + (finalOvertipsReceived.foodAndBeverageSalesSumm || 0) + (finalOvertipsReceived.salesCC || 0) + (finalOvertipsReceived.cc_amount || 0);
                finalMoneyInclOvertips['cash'] = finalMoneyInclOvertips['cash'] + (finalOvertipsReceived.cash || 0) + (finalOvertipsReceived.cash_amount || 0);
              }
              this.data.positionsMoney[positionName].teams[1].employees[employeeId].finalMoneyToDistributePlusOvertips = finalMoneyInclOvertips;
            }
          }
        }
        return this.data;
      };

      CalculationSTOHelper.prototype.calculateOvertipTeamNumbers = function(data, scope) {
        var i, len, positionName, ref, results, teamIndex, total;
        ref = _.keys(data.positionsMoney);
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          positionName = ref[i];
          results.push((function() {
            var j, len1, ref1, results1;
            ref1 = _.keys(data.positionsMoney[positionName].teams);
            results1 = [];
            for (j = 0, len1 = ref1.length; j < len1; j++) {
              teamIndex = ref1[j];
              total = {};
              total["cc"] = _.reduce(_.collect(_.pairs(data.positionsMoney[positionName].teams[teamIndex].employees), function(f) {
                return f[1].finalMoneyToDistributePlusOvertips.cc || 0;
              }), function(sum, num) {
                return sum + num;
              }) || 0;
              total["cash"] = _.reduce(_.collect(_.pairs(data.positionsMoney[positionName].teams[teamIndex].employees), function(f) {
                return f[1].finalMoneyToDistributePlusOvertips.cash || 0;
              }), function(sum, num) {
                return sum + num;
              }) || 0;
              results1.push(this.data.positionsMoney[positionName].teams[teamIndex].teamTotal['finalMoneyToDistributePlusOvertips'] = total);
            }
            return results1;
          }).call(this));
        }
        return results;
      };

      return CalculationSTOHelper;

    })();
    return Tipmetric.Modules.calculationSTOHelper = new CalculationSTOHelper;
  })();

}).call(this);
(function() {
  (function() {
    var CalculationConditionsSTOHelper;
    CalculationConditionsSTOHelper = (function() {
      function CalculationConditionsSTOHelper() {}

      CalculationConditionsSTOHelper.prototype.process = function(data, scope) {
        this.data = data;
        this.processPercents(data, scope);
        return this.data;
      };

      CalculationConditionsSTOHelper.prototype.getReceivedPercentsHash = function(data, positionTypeId, teamNumber) {
        var cashSTOs, ccSTOs, sourceTipSharings, targetSourceTipSharings;
        sourceTipSharings = _.values(data.sourceTipSharingsHash);
        targetSourceTipSharings = _.filter(sourceTipSharings, function(ts) {
          return ts.targetPositionId === positionTypeId;
        });
        ccSTOs = _.filter(targetSourceTipSharings, function(ts) {
          return ts.sourceType === 'cc' || ts.sourceType === 'cc_cash';
        });
        cashSTOs = _.filter(targetSourceTipSharings, function(ts) {
          return ts.sourceType === 'cash' || ts.sourceType === 'cc_cash';
        });
        return {
          cc: _.reduce(_.map(ccSTOs, function(ts) {
            if (ts.sourceType === 'cc') {
              return ts.totalSumm;
            } else {
              return ts.totalCCSumm;
            }
          }), function(sum, num) {
            return sum + num;
          }) || 0,
          cash: _.reduce(_.map(cashSTOs, function(ts) {
            if (ts.sourceType === 'cash') {
              return ts.totalSumm;
            } else {
              return ts.totalCashSumm;
            }
          }), function(sum, num) {
            return sum + num;
          }) || 0,
          fee: _.reduce(_.map(_.filter(targetSourceTipSharings, function(ts) {
            return ts.sourceType === 'fee';
          }), function(ts) {
            return ts.totalSumm;
          }), function(sum, num) {
            return sum + num;
          }) || 0,
          agSvc: _.reduce(_.map(_.filter(targetSourceTipSharings, function(ts) {
            return ts.sourceType === 'agSvc';
          }), function(ts) {
            return ts.totalSumm;
          }), function(sum, num) {
            return sum + num;
          }) || 0,
          foodSales: _.reduce(_.map(_.filter(targetSourceTipSharings, function(ts) {
            return ts.sourceType === 'foodSales';
          }), function(ts) {
            return ts.totalSumm;
          }), function(sum, num) {
            return sum + num;
          }) || 0,
          beverageSales: _.reduce(_.map(_.filter(targetSourceTipSharings, function(ts) {
            return ts.sourceType === 'beverageSales';
          }), function(ts) {
            return ts.totalSumm;
          }), function(sum, num) {
            return sum + num;
          }) || 0,
          foodAndBeverageSalesSumm: _.reduce(_.map(_.filter(targetSourceTipSharings, function(ts) {
            return ts.sourceType === 'foodAndBeverageSalesSumm';
          }), function(ts) {
            return ts.totalSumm;
          }), function(sum, num) {
            return sum + num;
          }) || 0,
          salesCC: _.reduce(_.map(_.filter(targetSourceTipSharings, function(ts) {
            return ts.sourceType === 'salesCC';
          }), function(ts) {
            return ts.totalSumm;
          }), function(sum, num) {
            return sum + num;
          }) || 0,
          salesCash: _.reduce(_.map(_.filter(targetSourceTipSharings, function(ts) {
            return ts.sourceType === 'salesCash';
          }), function(ts) {
            return ts.totalSumm;
          }), function(sum, num) {
            return sum + num;
          }) || 0
        };
      };

      CalculationConditionsSTOHelper.prototype.getGivenPercentsHash = function(data, positionType, teamNumber, id) {
        var appliedSTORules, employeeData, i, len, source, sourcePosition, sourceTipSharingsByPositionAndSourceTotals, sources;
        employeeData = data.positionsMoney[positionType].teams[teamNumber].employees[id];
        appliedSTORules = employeeData.appliedSTORules;
        sourceTipSharingsByPositionAndSourceTotals = {};
        sourcePosition = positionType;
        sources = _.flatten(_.collect(appliedSTORules, function(ts) {
          if (ts.sourceType === 'cc_cash') {
            return ['cc', 'cash'];
          } else {
            return ts.sourceType;
          }
        }));
        sourceTipSharingsByPositionAndSourceTotals = {};
        for (i = 0, len = sources.length; i < len; i++) {
          source = sources[i];
          sourceTipSharingsByPositionAndSourceTotals[source] = _.reduce(_.collect(_.filter(appliedSTORules, function(ts) {
            var sourceTypeMatch;
            sourceTypeMatch = _.contains(['cc', 'cash', 'cc_cash'], source) ? source === 'cc' ? ts.sourceType === 'cc' || ts.sourceType === 'cc_cash' : source === 'cash' ? ts.sourceType === 'cash' || ts.sourceType === 'cc_cash' : void 0 : ts.sourceType === source;
            return sourceTypeMatch;
          }), function(ts) {
            return parseFloat(ts.percentage);
          }), function(sum, num) {
            return sum + num;
          });
        }
        return sourceTipSharingsByPositionAndSourceTotals;
      };

      CalculationConditionsSTOHelper.prototype.calculateTotalPositionHoursForPosition = function(positionId) {
        var empDistributions;
        return empDistributions = _.reduce(_.map($(".position-block[data-position-block-id='" + positionId + "'] .team-table .employee-distribution-line .number-hrs"), function(num) {
          return parseFloat($(num).val());
        }), function(sum, num) {
          return sum + num;
        }) || 0;
      };

      CalculationConditionsSTOHelper.prototype.processPercents = function(data, scope) {
        var allBlankSumms, calculateTotalPositionHoursForPosition, employeesTotalCCSum, employeesTotalCashSum, employeesTotalSum, i, len, nonSourcePositions, positionsThatReceiveSTOIds, ref, sourcePositionIds, sourcePositionsThatOnlyReceiveSTOIds, sourcePositionsThatOnlySendSTOIds, sourceTipSharing, totalHoursForPosition;
        this.data = data;
        calculateTotalPositionHoursForPosition = this.calculateTotalPositionHoursForPosition;
        allBlankSumms = {
          cc: 0,
          cash: 0,
          agSvc: 0,
          fee: 0,
          salesCC: 0,
          salesCash: 0,
          foodSales: 0,
          beverageSales: 0,
          foodAndBeverageSalesSumm: 0
        };
        sourcePositionIds = _.collect(Tipmetric.Controllers.tipSharingToggleView.getSourcePositions(), function(f) {
          return f.id;
        });
        positionsThatReceiveSTOIds = _.uniq(_.map($("#position_tip_sharings select.target-position-select"), function(f) {
          return $(f).val();
        }));
        sourcePositionsThatOnlySendSTOIds = _.difference(sourcePositionIds, positionsThatReceiveSTOIds);
        sourcePositionsThatOnlyReceiveSTOIds = _.intersection(sourcePositionIds, positionsThatReceiveSTOIds);
        nonSourcePositions = _.collect(Tipmetric.Controllers.tipSharingToggleView.getNonSourcePositions(), function(f) {
          return f.id;
        });
        _.each(_.filter($(".position-block"), function(block) {
          return _.contains(sourcePositionIds, $(block).attr("data-position-block-id"));
        }), (function(_this) {
          return function(positionBlock) {
            var base, base1, positionTeamsCount, positionType, positionTypeId, positionTypeIsASource, totalHoursForPosition;
            positionType = $(positionBlock).attr("data-position-block-name");
            positionTypeId = $(positionBlock).attr("data-position-block-id");
            positionTypeIsASource = $(positionBlock).attr("data-position-is-a-source");
            positionTeamsCount = $("[data-table-type='employee-distribution'][data-position-table=\"" + positionType + "\"]").length || 0;
            totalHoursForPosition = _.reduce(_.map($(positionBlock).find(".employee-distribution-line .number-hrs"), function(num) {
              return parseFloat($(num).val());
            }), function(sum, num) {
              return sum + num;
            }) || 0;
            (base = _this.data).positionsMoney || (base.positionsMoney = {});
            (base1 = _this.data.positionsMoney)[positionType] || (base1[positionType] = {
              positionTypeIsASource: positionTypeIsASource,
              positionTeamsCount: positionTeamsCount
            });
            return _.each($(positionBlock).find("[data-table-type='employee-distribution']"), function(empDistrTable) {
              var agSvcTeamTotalSum, base2, base3, base4, base5, cashTeamTotalSum, ccTeamTotalSum, feeTeamTotalSum, finalTeamTODistributeAgSvc, finalTeamTODistributeCC, finalTeamTODistributeCCMinusCash, finalTeamTODistributeCash, finalTeamTODistributeFee, teamEmployees, teamNumber, totalHoursForteam, totalMoneyToDistribute, totalPositionHoursWorked, totalSalesForPositionBeverage, totalSalesForPositionCC, totalSalesForPositionCash, totalSalesForPositionFood, totalTeamCollectedMoney, totalTeamFoodSales, totalTeamFoodSalesBeverage, totalTeamFoodSalesFood, totalTeamMoneyToDistribute, totalTeamSales, totalTeamSalesCC, totalTeamSalesCash, totalTeamTOGivenAgSvc, totalTeamTOGivenCC, totalTeamTOGivenCash, totalTeamTOGivenFee, totalTeamTOReceivedAgSvc, totalTeamTOReceivedCC, totalTeamTOReceivedCash, totalTeamTOReceivedFee;
              teamNumber = $(empDistrTable).attr("data-team");
              totalHoursForteam = _.reduce(_.map($(empDistrTable).find(".employee-distribution-line .number-hrs"), function(num) {
                return parseFloat($(num).val());
              }), function(sum, num) {
                return sum + num;
              }) || 0;
              (base2 = _this.data).positionsMoney || (base2.positionsMoney = {});
              (base3 = _this.data.positionsMoney)[positionType] || (base3[positionType] = {});
              (base4 = _this.data.positionsMoney[positionType]).teams || (base4.teams = {});
              (base5 = _this.data.positionsMoney[positionType].teams)[teamNumber] || (base5[teamNumber] = {});
              _this.data.positionsMoney[positionType].teams[teamNumber].employees = {};
              _.each($(empDistrTable).find(".employee-distribution-line"), function(empDistrTableLine) {
                var cc_cash_minus_cash_collected, conditionResult, empDistrId, empDistrStatus, employeeData, finalMoneyReceived, finalMoneyToDistribute, finalMoneyToSubtract, hourly_rate, hoursWorkedInHours, i, id, len, ref, salesFoodSumm, salesSumm, sourceTipSharing, totalGivenOutPercents, totalMoneyIn, totalMoneyOutMinusFee;
                hoursWorkedInHours = parseFloat($(empDistrTableLine).find(".number-hrs").val() || 0);
                empDistrId = $(empDistrTableLine).attr("data-emp-distribution-id");
                empDistrStatus = $(empDistrTableLine).attr("data-emp-distribution-status");
                if ($(empDistrTableLine).find(".employee-id").val() === void 0 || $(empDistrTableLine).find(".employee-id").val().length === 0) {
                  id = $(empDistrTableLine).find(".employee-id").attr("data-employee-id");
                } else {
                  id = $(empDistrTableLine).find(".employee-id").val();
                }
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id] = {
                  hoursWorkedInHours: hoursWorkedInHours,
                  distributionStatus: empDistrStatus,
                  distributionId: empDistrId
                };
                salesSumm = {
                  cc: parseFloat($(empDistrTableLine).find(".sales-cc input").val() || 0),
                  cash: parseFloat($(empDistrTableLine).find(".sales-cash input").val() || 0)
                };
                salesFoodSumm = {
                  food: parseFloat($(empDistrTableLine).find(".food-sales input").val() || 0),
                  beverage: parseFloat($(empDistrTableLine).find(".beverage-sales input").val() || 0)
                };
                salesFoodSumm['total_food_sales'] = salesFoodSumm.food + salesFoodSumm.beverage;
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].salesSumm = salesSumm;
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].salesFoodSumm = salesFoodSumm;
                totalMoneyIn = {
                  cc: parseFloat($(empDistrTableLine).find(".cc-in input").val()) || 0,
                  cash: parseFloat($(empDistrTableLine).find(".cash-in input").val()) || 0,
                  agSvc: parseFloat($(empDistrTableLine).find(".ag-svc-in input").val()) || 0,
                  salesCC: parseFloat($(empDistrTableLine).find(".sales-cc input").val() || 0),
                  salesCash: parseFloat($(empDistrTableLine).find(".sales-cash input").val() || 0),
                  fee: parseFloat($(empDistrTableLine).find(".fee-in input").val()) || 0,
                  foodSales: parseFloat($(empDistrTableLine).find(".food-sales input").val()) || 0,
                  beverageSales: parseFloat($(empDistrTableLine).find(".beverage-sales input").val()) || 0,
                  hours: parseFloat($(empDistrTableLine).find("input.number-hrs").val()) || 0
                };
                totalMoneyIn.cc_cash = totalMoneyIn.cc + totalMoneyIn.cash;
                totalMoneyIn.foodAndBeverageSalesSumm = totalMoneyIn.foodSales + totalMoneyIn.beverageSales;
                totalMoneyOutMinusFee = {
                  cc: parseFloat($(empDistrTableLine).find(".cc-in input").val()) * _this.data.cc_minus_fee || 0,
                  cash: parseFloat($(empDistrTableLine).find(".cash-in input").val()) || 0,
                  agSvc: parseFloat($(empDistrTableLine).find(".ag-svc-in input").val()) * _this.data.ag_svc_minus_fee || 0,
                  salesCC: parseFloat($(empDistrTableLine).find(".sales-cc input").val() || 0),
                  salesCash: parseFloat($(empDistrTableLine).find(".sales-cash input").val() || 0),
                  fee: parseFloat($(empDistrTableLine).find(".fee-in input").val()) || 0,
                  foodSales: parseFloat($(empDistrTableLine).find(".food-sales input").val()) || 0,
                  beverageSales: parseFloat($(empDistrTableLine).find(".beverage-sales input").val()) || 0
                };
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].appliedSTORules = [];
                ref = _.values(_this.data.sourceTipSharingsHash);
                for (i = 0, len = ref.length; i < len; i++) {
                  sourceTipSharing = ref[i];
                  if (positionTypeId !== sourceTipSharing.sourcePositionId) {
                    continue;
                  }
                  if (sourceTipSharing.conditionFirst === "hours_no_source") {
                    conditionResult = (function() {
                      switch (sourceTipSharing.conditionType) {
                        case "gt":
                          return calculateTotalPositionHoursForPosition(sourceTipSharing.targetPositionId) > sourceTipSharing.conditionSecond;
                        case "lt":
                          return calculateTotalPositionHoursForPosition(sourceTipSharing.targetPositionId) < sourceTipSharing.conditionSecond;
                        case "eq":
                          return calculateTotalPositionHoursForPosition(sourceTipSharing.targetPositionId) === sourceTipSharing.conditionSecond;
                      }
                    })();
                  } else {
                    conditionResult = (function() {
                      switch (sourceTipSharing.conditionType) {
                        case "gt":
                          return totalMoneyIn[sourceTipSharing.conditionFirst] > sourceTipSharing.conditionSecond;
                        case "lt":
                          return totalMoneyIn[sourceTipSharing.conditionFirst] < sourceTipSharing.conditionSecond;
                        case "eq":
                          return totalMoneyIn[sourceTipSharing.conditionFirst] === sourceTipSharing.conditionSecond;
                      }
                    })();
                  }
                  if (!sourceTipSharing.conditionEnabled || conditionResult) {
                    _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].appliedSTORules.push(sourceTipSharing);
                    _this.data.sourceTipSharingsHash[sourceTipSharing.tempId].employeesAppliedTo.push(id);
                  }
                }
                totalGivenOutPercents = _this.getGivenPercentsHash(_this.data, positionType, teamNumber, id);
                finalMoneyToSubtract = {
                  cc: totalMoneyOutMinusFee['cc'] * (totalGivenOutPercents['cc'] || 0) / 100,
                  cash: totalMoneyOutMinusFee['cash'] * (totalGivenOutPercents['cash'] || 0) / 100,
                  agSvc: totalMoneyOutMinusFee['agSvc'] * (totalGivenOutPercents['agSvc'] || 0) / 100,
                  fee: totalMoneyOutMinusFee['fee'] * (totalGivenOutPercents['fee'] || 0) / 100,
                  salesCC: totalMoneyOutMinusFee['salesCC'] * (totalGivenOutPercents['salesCC'] || 0) / 100,
                  salesCash: totalMoneyOutMinusFee['salesCash'] * (totalGivenOutPercents['salesCash'] || 0) / 100,
                  foodSales: totalMoneyOutMinusFee['foodSales'] * (totalGivenOutPercents['foodSales'] || 0) / 100,
                  beverageSales: totalMoneyOutMinusFee['beverageSales'] * (totalGivenOutPercents['beverageSales'] || 0) / 100,
                  foodAndBeverageSalesSumm: (totalMoneyOutMinusFee['foodSales'] + totalMoneyOutMinusFee['beverageSales']) * (totalGivenOutPercents['foodAndBeverageSalesSumm'] || 0) / 100
                };
                finalMoneyToSubtract.cc = finalMoneyToSubtract.cc + finalMoneyToSubtract.foodSales + finalMoneyToSubtract.beverageSales + finalMoneyToSubtract.foodAndBeverageSalesSumm + finalMoneyToSubtract.salesCC + finalMoneyToSubtract.salesCash;
                finalMoneyToDistribute = {
                  cc: totalMoneyOutMinusFee['cc'] - finalMoneyToSubtract.cc,
                  cash: totalMoneyOutMinusFee['cash'] - finalMoneyToSubtract.cash,
                  agSvc: totalMoneyOutMinusFee['agSvc'] - finalMoneyToSubtract.agSvc,
                  fee: totalMoneyOutMinusFee['fee'] - finalMoneyToSubtract.fee
                };
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyIn = totalMoneyIn;
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyOut = totalMoneyOutMinusFee;
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToSubtract = finalMoneyToSubtract;
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute = finalMoneyToDistribute;
                cc_cash_minus_cash_collected = _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cc + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cash + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.agSvc + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.fee + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.agSvc + _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.fee - _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyIn.cash;
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cc_cash_minus_cash_collected = cc_cash_minus_cash_collected;
                if (positionTypeIsASource === "true") {
                  _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalTipOutsGiven = {
                    cc: finalMoneyToSubtract.cc || 0,
                    cash: finalMoneyToSubtract.cash || 0,
                    agSvc: finalMoneyToSubtract.agSvc || 0,
                    fee: finalMoneyToSubtract.fee || 0,
                    salesCC: finalMoneyToSubtract.salesCC || 0,
                    salesCash: finalMoneyToSubtract.salesCash || 0,
                    foodSales: finalMoneyToSubtract.foodSales || 0,
                    beverageSales: finalMoneyToSubtract.beverageSales || 0,
                    foodAndBeverageSalesSumm: finalMoneyToSubtract.foodAndBeverageSalesSumm || 0
                  };
                  if (finalMoneyReceived === void 0) {
                    finalMoneyReceived = {};
                  }
                  _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalTipOutsReceived = {
                    cc: finalMoneyReceived.cc || 0,
                    cash: finalMoneyReceived.cash || 0,
                    agSvc: finalMoneyReceived.agSvc || 0,
                    fee: finalMoneyReceived.fee || 0,
                    salesCC: finalMoneyReceived.salesCC || 0,
                    salesCash: finalMoneyReceived.salesCash || 0,
                    foodSales: finalMoneyReceived.foodSales || 0,
                    beverageSales: finalMoneyReceived.beverageSales || 0,
                    foodAndBeverageSalesSumm: finalMoneyReceived.foodAndBeverageSalesSumm || 0
                  };
                }
                employeeData = _this.data.positionsMoney[positionType].teams[teamNumber].employees[id];
                hourly_rate = (employeeData.finalMoneyToDistribute.cc + employeeData.finalMoneyToDistribute.cash + employeeData.finalMoneyToDistribute.agSvc + employeeData.finalMoneyToDistribute.fee) / employeeData.hoursWorkedInHours;
                return _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].hourly_rate = hourly_rate;
              });
              teamEmployees = _.values(_this.data.positionsMoney[positionType].teams[teamNumber].employees || {});
              ccTeamTotalSum = _.reduce(_.map(teamEmployees, function(num) {
                return parseFloat(num.totalMoneyIn.cc || 0);
              }), function(sum, num) {
                return sum + num;
              }) || 0;
              cashTeamTotalSum = _.reduce(_.map(teamEmployees, function(num) {
                return parseFloat(num.totalMoneyIn.cash || 0);
              }), function(sum, num) {
                return sum + num;
              }) || 0;
              feeTeamTotalSum = _.reduce(_.map(teamEmployees, function(num) {
                return parseFloat(num.totalMoneyIn.fee || 0);
              }), function(sum, num) {
                return sum + num;
              }) || 0;
              agSvcTeamTotalSum = _.reduce(_.map(teamEmployees, function(num) {
                return parseFloat(num.totalMoneyIn.agSvc || 0);
              }), function(sum, num) {
                return sum + num;
              }) || 0;
              totalSalesForPositionFood = _.reduce(_.map(teamEmployees, function(num) {
                return parseFloat(num.salesFoodSumm.food || 0);
              }), function(sum, num) {
                return sum + num;
              }) || 0;
              totalSalesForPositionBeverage = _.reduce(_.map(teamEmployees, function(num) {
                return parseFloat(num.salesFoodSumm.beverage || 0);
              }), function(sum, num) {
                return sum + num;
              }) || 0;
              totalSalesForPositionCC = _.reduce(_.map(teamEmployees, function(num) {
                return parseFloat(num.salesSumm.cc || 0);
              }), function(sum, num) {
                return sum + num;
              }) || 0;
              totalSalesForPositionCash = _.reduce(_.map(teamEmployees, function(num) {
                return parseFloat(num.salesSumm.cash || 0);
              }), function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamCollectedMoney = {
                cc: parseFloat(ccTeamTotalSum),
                cash: parseFloat(cashTeamTotalSum),
                agSvc: parseFloat(agSvcTeamTotalSum),
                salesCC: parseFloat(totalSalesForPositionCC),
                salesCash: parseFloat(totalSalesForPositionCash),
                fee: parseFloat(feeTeamTotalSum),
                food: parseFloat(totalSalesForPositionFood),
                beverage: parseFloat(totalSalesForPositionBeverage),
                foodAndBeverageSalesSumm: parseFloat(totalSalesForPositionFood + totalSalesForPositionBeverage)
              };
              totalTeamMoneyToDistribute = totalTeamCollectedMoney;
              _this.data.positionsMoney[positionType].teams[teamNumber].totalTeamCollectedMoney = totalTeamCollectedMoney;
              _this.data.positionsMoney[positionType].teams[teamNumber].totalTeamMoneyToDistribute = totalTeamMoneyToDistribute;
              totalHoursForteam = _.reduce(_.map(teamEmployees, function(num) {
                return parseFloat(num.hoursWorkedInHours);
              }), function(sum, num) {
                return sum + num;
              }) || 0;
              _this.data.positionsMoney[positionType].teams[teamNumber].totalHoursForteam = totalHoursForteam;
              totalPositionHoursWorked = {};
              totalPositionHoursWorked = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.hoursWorkedInHours);
              });
              totalPositionHoursWorked = _.reduce(totalPositionHoursWorked, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamSales = {};
              totalTeamSalesCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.salesSumm.cc);
              });
              totalTeamSalesCC = _.reduce(totalTeamSalesCC, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamSalesCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.salesSumm.cash);
              });
              totalTeamSalesCash = _.reduce(totalTeamSalesCash, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamSales = {
                cc: totalTeamSalesCC,
                cash: totalTeamSalesCash
              };
              totalTeamFoodSales = {};
              totalTeamFoodSalesFood = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.salesFoodSumm.food);
              });
              totalTeamFoodSalesFood = _.reduce(totalTeamFoodSalesFood, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamFoodSalesBeverage = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.salesFoodSumm.beverage);
              });
              totalTeamFoodSalesBeverage = _.reduce(totalTeamFoodSalesBeverage, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamFoodSales = {
                food: totalTeamFoodSalesFood,
                beverage: totalTeamFoodSalesBeverage
              };
              totalMoneyToDistribute = {};
              totalMoneyToDistribute.cc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalMoneyOut.cc);
              });
              totalMoneyToDistribute.cc = _.reduce(totalMoneyToDistribute.cc, function(sum, num) {
                return sum + num;
              }) || 0;
              totalMoneyToDistribute.cash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalMoneyOut.cash);
              });
              totalMoneyToDistribute.cash = _.reduce(totalMoneyToDistribute.cash, function(sum, num) {
                return sum + num;
              }) || 0;
              totalMoneyToDistribute.agSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalMoneyOut.agSvc);
              });
              totalMoneyToDistribute.agSvc = _.reduce(totalMoneyToDistribute.agSvc, function(sum, num) {
                return sum + num;
              }) || 0;
              totalMoneyToDistribute.fee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalMoneyOut.fee);
              });
              totalMoneyToDistribute.fee = _.reduce(totalMoneyToDistribute.fee, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOGivenCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsGiven.cc);
              });
              totalTeamTOGivenCC = _.reduce(totalTeamTOGivenCC, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOGivenCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsGiven.cash);
              });
              totalTeamTOGivenCash = _.reduce(totalTeamTOGivenCash, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOGivenAgSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsGiven.agSvc);
              });
              totalTeamTOGivenAgSvc = _.reduce(totalTeamTOGivenAgSvc, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOGivenFee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsGiven.fee);
              });
              totalTeamTOGivenFee = _.reduce(totalTeamTOGivenFee, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOReceivedCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsReceived.cc);
              });
              totalTeamTOReceivedCC = _.reduce(totalTeamTOReceivedCC, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOReceivedCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsReceived.cash);
              });
              totalTeamTOReceivedCash = _.reduce(totalTeamTOReceivedCash, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOReceivedAgSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsReceived.agSvc);
              });
              totalTeamTOReceivedAgSvc = _.reduce(totalTeamTOReceivedAgSvc, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOReceivedFee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsReceived.fee);
              });
              totalTeamTOReceivedFee = _.reduce(totalTeamTOReceivedFee, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.finalMoneyToDistribute.cc);
              });
              finalTeamTODistributeCC = _.reduce(finalTeamTODistributeCC, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.finalMoneyToDistribute.cash);
              });
              finalTeamTODistributeCash = _.reduce(finalTeamTODistributeCash, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeCCMinusCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return data.finalMoneyToDistribute.cc_cash_minus_cash_collected;
              });
              finalTeamTODistributeCCMinusCash = _.reduce(finalTeamTODistributeCCMinusCash, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeAgSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.finalMoneyToDistribute.agSvc);
              });
              finalTeamTODistributeAgSvc = _.reduce(finalTeamTODistributeAgSvc, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeFee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.finalMoneyToDistribute.fee);
              });
              finalTeamTODistributeFee = _.reduce(finalTeamTODistributeFee, function(sum, num) {
                return sum + num;
              }) || 0;
              return _this.data.positionsMoney[positionType].teams[teamNumber].teamTotal = {
                totalTeamSales: totalTeamSales || 0,
                totalTeamFoodSales: totalTeamFoodSales || 0,
                totalTeamCollectedMoney: totalTeamCollectedMoney,
                totalPositionHoursWorked: totalPositionHoursWorked || 0,
                ccToDistribute: totalMoneyToDistribute.cc || 0,
                cashToDistribute: totalMoneyToDistribute.cash || 0,
                agSvcToDistribute: totalMoneyToDistribute.agSvc || 0,
                feeToDistribute: totalMoneyToDistribute.fee || 0,
                totalTeamTOGivenCC: totalTeamTOGivenCC || 0,
                totalTeamTOGivenCash: totalTeamTOGivenCash || 0,
                totalTeamTOGivenAgSvc: totalTeamTOGivenAgSvc || 0,
                totalTeamTOGivenFee: totalTeamTOGivenFee || 0,
                totalTeamTOReceivedCC: totalTeamTOReceivedCC || 0,
                totalTeamTOReceivedCash: totalTeamTOReceivedCash || 0,
                totalTeamTOReceivedAgSvc: totalTeamTOReceivedAgSvc || 0,
                totalTeamTOReceivedFee: totalTeamTOReceivedFee || 0,
                finalTeamTODistributeCC: finalTeamTODistributeCC || 0,
                finalTeamTODistributeCash: finalTeamTODistributeCash || 0,
                finalTeamTODistributeCCMinusCash: finalTeamTODistributeCCMinusCash || 0,
                finalTeamTODistributeAgSvc: finalTeamTODistributeAgSvc || 0,
                finalTeamTODistributeFee: finalTeamTODistributeFee || 0
              };
            });
          };
        })(this));
        data = this.data;
        ref = _.values(this.data.sourceTipSharingsHash);
        for (i = 0, len = ref.length; i < len; i++) {
          sourceTipSharing = ref[i];
          if (sourceTipSharing.sourceType === "cc_cash") {
            employeesTotalCCSum = _.reduce(_.map(sourceTipSharing.employeesAppliedTo, function(id) {
              return parseFloat(data.positionsMoney[sourceTipSharing.sourcePositionName].teams[1].employees[id].totalMoneyIn.cc || 0) * sourceTipSharing.percentage / 100;
            }), function(sum, num) {
              return sum + num;
            }) || 0;
            this.data.sourceTipSharingsHash[sourceTipSharing.tempId].totalCCSumm = employeesTotalCCSum;
            employeesTotalCashSum = _.reduce(_.map(sourceTipSharing.employeesAppliedTo, function(id) {
              return parseFloat(data.positionsMoney[sourceTipSharing.sourcePositionName].teams[1].employees[id].totalMoneyIn.cash || 0) * sourceTipSharing.percentage / 100;
            }), function(sum, num) {
              return sum + num;
            }) || 0;
            this.data.sourceTipSharingsHash[sourceTipSharing.tempId].totalCashSumm = employeesTotalCashSum;
            employeesTotalSum = employeesTotalCCSum + employeesTotalCashSum;
            this.data.sourceTipSharingsHash[sourceTipSharing.tempId].totalSumm = employeesTotalSum;
          } else {
            employeesTotalSum = _.reduce(_.map(sourceTipSharing.employeesAppliedTo, function(id) {
              return parseFloat(data.positionsMoney[sourceTipSharing.sourcePositionName].teams[1].employees[id].totalMoneyIn[sourceTipSharing.sourceType] || 0) * sourceTipSharing.percentage / 100;
            }), function(sum, num) {
              return sum + num;
            }) || 0;
            this.data.sourceTipSharingsHash[sourceTipSharing.tempId].totalSumm = employeesTotalSum;
          }
          $("#position_tip_sharings tr.position-tip-sharing[data-temp-id='" + sourceTipSharing.tempId + "'] .total").text(employeesTotalSum);
        }
        totalHoursForPosition = _.reduce(_.map(_.pairs(this.data.sourceTipSharingsHash), function(f) {
          return f[1].totalSumm;
        }), function(sum, num) {
          return sum + num;
        }) || 0;
        $(".total-tips").text(totalHoursForPosition);
        _.each(_.filter($(".position-block"), function(block) {
          return _.contains(sourcePositionsThatOnlyReceiveSTOIds, $(block).attr("data-position-block-id"));
        }), (function(_this) {
          return function(positionBlock) {
            var positionTeamsCount, positionType, positionTypeId, positionTypeIsASource;
            positionType = $(positionBlock).attr("data-position-block-name");
            positionTypeId = $(positionBlock).attr("data-position-block-id");
            positionTypeIsASource = $(positionBlock).attr("data-position-is-a-source");
            positionTeamsCount = $("[data-table-type='employee-distribution'][data-position-table=\"" + positionType + "\"]").length || 0;
            totalHoursForPosition = _.reduce(_.map($(positionBlock).find(".employee-distribution-line .number-hrs"), function(num) {
              return parseFloat($(num).val());
            }), function(sum, num) {
              return sum + num;
            }) || 0;
            return _.each($(positionBlock).find("[data-table-type='employee-distribution']"), function(empDistrTable) {
              var base, base1, base2, base3, base4, finalTeamMoneyReceived, finalTeamTODistributeAgSvc, finalTeamTODistributeCC, finalTeamTODistributeCCMinusCash, finalTeamTODistributeCash, finalTeamTODistributeFee, teamNumber, totalHoursForteam, totalMoneyToDistribute, totalTeamTOReceivedAgSvc, totalTeamTOReceivedCC, totalTeamTOReceivedCash, totalTeamTOReceivedFee;
              teamNumber = $(empDistrTable).attr("data-team");
              totalHoursForteam = _.reduce(_.map($(empDistrTable).find(".employee-distribution-line .number-hrs"), function(num) {
                return parseFloat($(num).val());
              }), function(sum, num) {
                return sum + num;
              }) || 0;
              (base = _this.data).positionsMoney || (base.positionsMoney = {});
              (base1 = _this.data.positionsMoney)[positionType] || (base1[positionType] = {});
              (base2 = _this.data.positionsMoney[positionType]).teams || (base2.teams = {});
              (base3 = _this.data.positionsMoney[positionType].teams)[teamNumber] || (base3[teamNumber] = {});
              (base4 = _this.data.positionsMoney[positionType].teams[teamNumber]).employees || (base4.employees = {});
              finalTeamMoneyReceived = _this.getReceivedPercentsHash(_this.data, positionTypeId, teamNumber);
              finalTeamMoneyReceived.cc = finalTeamMoneyReceived.cc + finalTeamMoneyReceived.foodSales + finalTeamMoneyReceived.beverageSales + finalTeamMoneyReceived.foodAndBeverageSalesSumm + finalTeamMoneyReceived.salesCC + finalTeamMoneyReceived.salesCash;
              _.each($(empDistrTable).find(".employee-distribution-line"), function(empDistrTableLine) {
                var cc_cash_minus_cash_collected, empDistrId, empDistrStatus, employeeData, finalMoneyReceived, finalMoneyToDistribute, finalMoneyToSubtract, hourly_rate, hoursWorkedInHours, id, j, joinedDistribution, joinedPositionsTables, k, len1, len2, line, lines, moneyCollected, moneyDistributed, partOfTeamMoney, ref1, table, totalHoursForJoinedDistribution, totalHoursMap, totalMoneyOut;
                hoursWorkedInHours = parseFloat($(empDistrTableLine).find(".number-hrs").val() || 0);
                partOfTeamMoney = hoursWorkedInHours / totalHoursForteam;
                empDistrId = $(empDistrTableLine).attr("data-emp-distribution-id");
                empDistrStatus = $(empDistrTableLine).attr("data-emp-distribution-status");
                if ($(empDistrTableLine).find(".employee-id").val() === void 0 || $(empDistrTableLine).find(".employee-id").val().length === 0) {
                  id = $(empDistrTableLine).find(".employee-id").attr("data-employee-id");
                } else {
                  id = $(empDistrTableLine).find(".employee-id").val();
                }
                if (Tipmetric.Controllers.tipSharingToggleView.isPartOfJoinedDistribution(_this.data, positionTypeId)) {
                  joinedDistribution = _.filter(_this.data.joinedTipSharings, function(ts) {
                    return ts.sourcePositionId === positionTypeId || ts.targetPositionId === positionTypeId;
                  })[0];
                  joinedPositionsTables = _.filter($("table.team-table"), function(table) {
                    return _.contains(joinedDistribution.joinedPositionIds, $(table).attr("data-position-type-id"));
                  });
                  lines = [];
                  for (j = 0, len1 = joinedPositionsTables.length; j < len1; j++) {
                    table = joinedPositionsTables[j];
                    ref1 = $(table).find(".employee-distribution-line");
                    for (k = 0, len2 = ref1.length; k < len2; k++) {
                      line = ref1[k];
                      lines.push(line);
                    }
                  }
                  totalHoursMap = _.map(lines, function(e) {
                    var originalHoursValue;
                    originalHoursValue = parseFloat($(e).find(".number-hrs").val());
                    if ($(e).closest(".team-table").attr('data-position-type-id') === joinedDistribution.targetPositionId) {
                      originalHoursValue = originalHoursValue * joinedDistribution.multiplier;
                    }
                    return originalHoursValue;
                  });
                  totalHoursForJoinedDistribution = _.reduce(totalHoursMap, function(sum, num) {
                    return sum + num;
                  }) || 0;
                  partOfTeamMoney = hoursWorkedInHours / totalHoursForJoinedDistribution;
                }
                totalMoneyOut = {
                  cc: _this.data.positionsMoney[positionType].teams[teamNumber].totalTeamCollectedMoney.cc * partOfTeamMoney * _this.data.cc_minus_fee || 0,
                  cash: _this.data.positionsMoney[positionType].teams[teamNumber].totalTeamCollectedMoney.cash * partOfTeamMoney || 0,
                  agSvc: _this.data.positionsMoney[positionType].teams[teamNumber].totalTeamCollectedMoney.agSvc * partOfTeamMoney * _this.data.ag_svc_minus_fee || 0,
                  fee: _this.data.positionsMoney[positionType].teams[teamNumber].totalTeamCollectedMoney.fee * partOfTeamMoney || 0,
                  salesCC: _this.data.positionsMoney[positionType].teams[teamNumber].totalTeamCollectedMoney.salesCC * partOfTeamMoney || 0,
                  salesCash: _this.data.positionsMoney[positionType].teams[teamNumber].totalTeamCollectedMoney.salesCash * partOfTeamMoney || 0,
                  foodSales: _this.data.positionsMoney[positionType].teams[teamNumber].totalTeamCollectedMoney.food * partOfTeamMoney || 0,
                  beverageSales: _this.data.positionsMoney[positionType].teams[teamNumber].totalTeamCollectedMoney.beverage * partOfTeamMoney || 0
                };
                finalMoneyReceived = {
                  cc: finalTeamMoneyReceived.cc * partOfTeamMoney,
                  cash: finalTeamMoneyReceived.cash * partOfTeamMoney,
                  fee: finalTeamMoneyReceived.fee * partOfTeamMoney,
                  agSvc: finalTeamMoneyReceived.agSvc * partOfTeamMoney,
                  salesCC: finalTeamMoneyReceived.salesCC * partOfTeamMoney,
                  salesCash: finalTeamMoneyReceived.salesCash * partOfTeamMoney,
                  foodSales: finalTeamMoneyReceived.foodSales * partOfTeamMoney,
                  beverageSales: finalTeamMoneyReceived.beverageSales * partOfTeamMoney,
                  foodAndBeverageSalesSumm: finalTeamMoneyReceived.foodAndBeverageSalesSumm * partOfTeamMoney
                };
                finalMoneyToSubtract = {
                  cc: _this.data.positionsMoney[positionType].teams[teamNumber].teamTotal.totalTeamTOGivenCC * partOfTeamMoney,
                  cash: _this.data.positionsMoney[positionType].teams[teamNumber].teamTotal.totalTeamTOGivenCash * partOfTeamMoney,
                  agSvc: _this.data.positionsMoney[positionType].teams[teamNumber].teamTotal.totalTeamTOGivenAgSvc * partOfTeamMoney,
                  fee: _this.data.positionsMoney[positionType].teams[teamNumber].teamTotal.totalTeamTOGivenFee * partOfTeamMoney
                };
                finalMoneyToDistribute = {
                  cc: totalMoneyOut['cc'] - finalMoneyToSubtract.cc + finalMoneyReceived.cc,
                  cash: totalMoneyOut['cash'] - finalMoneyToSubtract.cash + finalMoneyReceived.cash,
                  agSvc: totalMoneyOut['agSvc'] - finalMoneyToSubtract.agSvc + finalMoneyReceived.agSvc,
                  fee: totalMoneyOut['fee'] - finalMoneyToSubtract.fee + finalMoneyReceived.fee
                };
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyOut = totalMoneyOut;
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToSubtract = finalMoneyToSubtract;
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute = finalMoneyToDistribute;
                moneyDistributed = _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute;
                moneyCollected = _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyIn;
                cc_cash_minus_cash_collected = moneyDistributed.cc + moneyDistributed.cash + moneyDistributed.agSvc + moneyDistributed.fee - moneyCollected.cash;
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cc_cash_minus_cash_collected = cc_cash_minus_cash_collected;
                if (positionTypeIsASource === "true") {
                  _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalTipOutsGiven = {
                    cc: finalMoneyToSubtract.cc || 0,
                    cash: finalMoneyToSubtract.cash || 0,
                    agSvc: finalMoneyToSubtract.agSvc || 0,
                    fee: finalMoneyToSubtract.fee || 0,
                    salesCC: finalMoneyToSubtract.salesCC || 0,
                    salesCash: finalMoneyToSubtract.salesCash || 0,
                    foodSales: finalMoneyToSubtract.foodSales || 0,
                    beverageSales: finalMoneyToSubtract.beverageSales || 0,
                    foodAndBeverageSalesSumm: finalMoneyToSubtract.foodAndBeverageSalesSumm || 0
                  };
                  if (finalMoneyReceived === void 0) {
                    finalMoneyReceived = {};
                  }
                  _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalTipOutsReceived = {
                    cc: finalMoneyReceived.cc || 0,
                    cash: finalMoneyReceived.cash || 0,
                    agSvc: finalMoneyReceived.agSvc || 0,
                    fee: finalMoneyReceived.fee || 0,
                    salesCC: finalMoneyReceived.salesCC || 0,
                    salesCash: finalMoneyReceived.salesCash || 0,
                    foodSales: finalMoneyReceived.foodSales || 0,
                    beverageSales: finalMoneyReceived.beverageSales || 0,
                    foodAndBeverageSalesSumm: finalMoneyReceived.foodAndBeverageSalesSumm || 0
                  };
                }
                employeeData = _this.data.positionsMoney[positionType].teams[teamNumber].employees[id];
                hourly_rate = (employeeData.finalMoneyToDistribute.cc + employeeData.finalMoneyToDistribute.cash + employeeData.finalMoneyToDistribute.agSvc + employeeData.finalMoneyToDistribute.fee) / employeeData.hoursWorkedInHours;
                return _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].hourly_rate = hourly_rate;
              });
              totalMoneyToDistribute = {};
              totalMoneyToDistribute.cc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalMoneyOut.cc);
              });
              totalMoneyToDistribute.cc = _.reduce(totalMoneyToDistribute.cc, function(sum, num) {
                return sum + num;
              }) || 0;
              totalMoneyToDistribute.cash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalMoneyOut.cash);
              });
              totalMoneyToDistribute.cash = _.reduce(totalMoneyToDistribute.cash, function(sum, num) {
                return sum + num;
              }) || 0;
              totalMoneyToDistribute.agSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalMoneyOut.agSvc);
              });
              totalMoneyToDistribute.agSvc = _.reduce(totalMoneyToDistribute.agSvc, function(sum, num) {
                return sum + num;
              }) || 0;
              totalMoneyToDistribute.fee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalMoneyOut.fee);
              });
              totalMoneyToDistribute.fee = _.reduce(totalMoneyToDistribute.fee, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOReceivedCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsReceived.cc);
              });
              totalTeamTOReceivedCC = _.reduce(totalTeamTOReceivedCC, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOReceivedCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsReceived.cash);
              });
              totalTeamTOReceivedCash = _.reduce(totalTeamTOReceivedCash, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOReceivedAgSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsReceived.agSvc);
              });
              totalTeamTOReceivedAgSvc = _.reduce(totalTeamTOReceivedAgSvc, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOReceivedFee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsReceived.fee);
              });
              totalTeamTOReceivedFee = _.reduce(totalTeamTOReceivedFee, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.finalMoneyToDistribute.cc);
              });
              finalTeamTODistributeCC = _.reduce(finalTeamTODistributeCC, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.finalMoneyToDistribute.cash);
              });
              finalTeamTODistributeCash = _.reduce(finalTeamTODistributeCash, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeCCMinusCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return data.finalMoneyToDistribute.cc_cash_minus_cash_collected;
              });
              finalTeamTODistributeCCMinusCash = _.reduce(finalTeamTODistributeCCMinusCash, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeAgSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.finalMoneyToDistribute.agSvc);
              });
              finalTeamTODistributeAgSvc = _.reduce(finalTeamTODistributeAgSvc, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeFee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.finalMoneyToDistribute.fee);
              });
              finalTeamTODistributeFee = _.reduce(finalTeamTODistributeFee, function(sum, num) {
                return sum + num;
              }) || 0;
              return _this.data.positionsMoney[positionType].teams[teamNumber].teamTotal = _.extend(_this.data.positionsMoney[positionType].teams[teamNumber].teamTotal, {
                ccToDistribute: totalMoneyToDistribute.cc,
                cashToDistribute: totalMoneyToDistribute.cash,
                agSvcToDistribute: totalMoneyToDistribute.agSvc,
                feeToDistribute: totalMoneyToDistribute.fee,
                totalTeamTOReceivedCC: totalTeamTOReceivedCC,
                totalTeamTOReceivedCash: totalTeamTOReceivedCash,
                totalTeamTOReceivedAgSvc: totalTeamTOReceivedAgSvc,
                totalTeamTOReceivedFee: totalTeamTOReceivedFee,
                finalTeamTODistributeCC: finalTeamTODistributeCC,
                finalTeamTODistributeCash: finalTeamTODistributeCash,
                finalTeamTODistributeCCMinusCash: finalTeamTODistributeCCMinusCash,
                finalTeamTODistributeAgSvc: finalTeamTODistributeAgSvc,
                finalTeamTODistributeFee: finalTeamTODistributeFee
              });
            });
          };
        })(this));
        _.each(_.filter($(".position-block"), function(block) {
          return _.contains(nonSourcePositions, $(block).attr("data-position-block-id"));
        }), (function(_this) {
          return function(positionBlock) {
            var base, base1, positionTeamsCount, positionType, positionTypeId, positionTypeIsASource;
            positionType = $(positionBlock).attr("data-position-block-name");
            positionTypeId = $(positionBlock).attr("data-position-block-id");
            positionTypeIsASource = $(positionBlock).attr("data-position-is-a-source");
            positionTeamsCount = $("[data-table-type='employee-distribution'][data-position-table=\"" + positionType + "\"]").length || 0;
            totalHoursForPosition = _.reduce(_.map($(positionBlock).find(".employee-distribution-line .number-hrs"), function(num) {
              return parseFloat($(num).val());
            }), function(sum, num) {
              return sum + num;
            }) || 0;
            (base = _this.data.positionsMoney)[positionType] || (base[positionType] = {});
            (base1 = _this.data.positionsMoney[positionType]).teams || (base1.teams = {});
            return _.each($(positionBlock).find("[data-table-type='employee-distribution']"), function(empDistrTable) {
              var base2, base3, finalTeamMoneyReceived, finalTeamTODistributeAgSvc, finalTeamTODistributeCC, finalTeamTODistributeCCMinusCash, finalTeamTODistributeCash, finalTeamTODistributeFee, teamHoursMap, teamNumber, totalHoursForteam, totalMoneyToDistribute, totalPositionHoursWorked, totalTeamCollectedMoney, totalTeamFoodSales, totalTeamSales, totalTeamTOGivenAgSvc, totalTeamTOGivenCC, totalTeamTOGivenCash, totalTeamTOGivenFee, totalTeamTOReceivedAgSvc, totalTeamTOReceivedCC, totalTeamTOReceivedCash, totalTeamTOReceivedFee;
              teamNumber = $(empDistrTable).attr("data-team");
              totalHoursForteam = _.reduce(_.map($(empDistrTable).find(".employee-distribution-line .number-hrs"), function(num) {
                return parseFloat($(num).val());
              }), function(sum, num) {
                return sum + num;
              }) || 0;
              (base2 = _this.data.positionsMoney[positionType].teams)[teamNumber] || (base2[teamNumber] = {});
              (base3 = _this.data.positionsMoney[positionType].teams[teamNumber]).employees || (base3.employees = {});
              teamHoursMap = _.map($(empDistrTable).find(".employee-distribution-line .number-hrs"), function(num) {
                return parseFloat($(num).val());
              });
              totalHoursForteam = _.reduce(teamHoursMap, function(sum, num) {
                return sum + num;
              }) || 0;
              _this.data.positionsMoney[positionType].teams[teamNumber].totalHoursForteam = totalHoursForteam;
              finalTeamMoneyReceived = _this.getReceivedPercentsHash(_this.data, positionTypeId, teamNumber);
              finalTeamMoneyReceived.cc = finalTeamMoneyReceived.cc + finalTeamMoneyReceived.foodSales + finalTeamMoneyReceived.beverageSales + finalTeamMoneyReceived.foodAndBeverageSalesSumm + finalTeamMoneyReceived.salesCC + finalTeamMoneyReceived.salesCash;
              _.each($(empDistrTable).find(".employee-distribution-line"), function(empDistrTableLine) {
                var cc_cash_minus_cash_collected, empDistrId, empDistrStatus, employeeData, finalMoneyReceived, finalMoneyToDistribute, hourly_rate, hoursMultiplied, hoursWorkedInHours, id, j, joinedDistribution, joinedPositionsTables, k, len1, len2, line, lines, partOfTeamMoney, ref1, sourcePositionName, table, totalHoursForJoinedDistribution, totalHoursMap, totalMoneyIn, totalMoneyOut, totalPositionHoursWorked;
                hoursWorkedInHours = parseFloat($(empDistrTableLine).find(".number-hrs").val() || 0);
                partOfTeamMoney = hoursWorkedInHours / totalHoursForteam;
                empDistrId = $(empDistrTableLine).attr("data-emp-distribution-id");
                empDistrStatus = $(empDistrTableLine).attr("data-emp-distribution-status");
                if ($(empDistrTableLine).find(".employee-id").val() === void 0 || $(empDistrTableLine).find(".employee-id").val().length === 0) {
                  id = $(empDistrTableLine).find(".employee-id").attr("data-employee-id");
                } else {
                  id = $(empDistrTableLine).find(".employee-id").val();
                }
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id] = {
                  hoursWorkedInHours: hoursWorkedInHours,
                  distributionStatus: empDistrStatus,
                  distributionId: empDistrId
                };
                totalMoneyIn = allBlankSumms;
                totalMoneyOut = allBlankSumms;
                partOfTeamMoney = hoursWorkedInHours / totalHoursForteam;
                finalMoneyReceived = {
                  cc: finalTeamMoneyReceived.cc * partOfTeamMoney,
                  cash: finalTeamMoneyReceived.cash * partOfTeamMoney,
                  fee: finalTeamMoneyReceived.fee * partOfTeamMoney,
                  agSvc: finalTeamMoneyReceived.agSvc * partOfTeamMoney,
                  salesCC: finalTeamMoneyReceived.salesCC * partOfTeamMoney,
                  salesCash: finalTeamMoneyReceived.salesCash * partOfTeamMoney,
                  foodSales: finalTeamMoneyReceived.foodSales * partOfTeamMoney,
                  beverageSales: finalTeamMoneyReceived.beverageSales * partOfTeamMoney,
                  foodAndBeverageSalesSumm: finalTeamMoneyReceived.foodAndBeverageSalesSumm * partOfTeamMoney
                };
                if (Tipmetric.Controllers.tipSharingToggleView.isPartOfJoinedDistribution(_this.data, positionTypeId)) {
                  joinedDistribution = _.filter(_this.data.joinedTipSharings, function(ts) {
                    return ts.sourcePositionId === positionTypeId || ts.targetPositionId === positionTypeId;
                  })[0];
                  joinedPositionsTables = _.filter($("table.team-table"), function(table) {
                    return _.contains(joinedDistribution.joinedPositionIds, $(table).attr("data-position-type-id"));
                  });
                  lines = [];
                  for (j = 0, len1 = joinedPositionsTables.length; j < len1; j++) {
                    table = joinedPositionsTables[j];
                    ref1 = $(table).find(".employee-distribution-line");
                    for (k = 0, len2 = ref1.length; k < len2; k++) {
                      line = ref1[k];
                      lines.push(line);
                    }
                  }
                  totalHoursMap = _.map(lines, function(e) {
                    var originalHoursValue;
                    originalHoursValue = parseFloat($(e).find(".number-hrs").val());
                    if ($(e).closest(".team-table").attr('data-position-type-id') === joinedDistribution.targetPositionId) {
                      originalHoursValue = originalHoursValue * joinedDistribution.multiplier;
                    }
                    return originalHoursValue;
                  });
                  totalHoursForJoinedDistribution = _.reduce(totalHoursMap, function(sum, num) {
                    return sum + num;
                  }) || 0;
                  partOfTeamMoney = (hoursWorkedInHours * joinedDistribution.multiplier) / totalHoursForJoinedDistribution;
                  sourcePositionName = _.findWhere(gon.calculation_source_positions, {
                    id: joinedDistribution.sourcePositionId
                  }).name;
                  totalPositionHoursWorked = _this.data.positionsMoney[sourcePositionName].teams[1].teamTotal.totalPositionHoursWorked;
                  hoursMultiplied = hoursWorkedInHours * joinedDistribution.multiplier;
                  finalMoneyToDistribute = {
                    cc: hoursMultiplied * _this.data.positionsMoney[sourcePositionName].teams[1].teamTotal.finalTeamTODistributeCC / totalPositionHoursWorked,
                    cash: hoursMultiplied * _this.data.positionsMoney[sourcePositionName].teams[1].teamTotal.finalTeamTODistributeCash / totalPositionHoursWorked,
                    fee: hoursMultiplied * _this.data.positionsMoney[sourcePositionName].teams[1].teamTotal.finalTeamTODistributeFee / totalPositionHoursWorked,
                    agSvc: hoursMultiplied * _this.data.positionsMoney[sourcePositionName].teams[1].teamTotal.finalTeamTODistributeAgSvc / totalPositionHoursWorked
                  };
                } else {
                  finalMoneyToDistribute = {
                    cc: finalMoneyReceived.cc,
                    cash: finalMoneyReceived.cash,
                    fee: finalMoneyReceived.fee,
                    agSvc: finalMoneyReceived.agSvc,
                    salesCC: finalMoneyReceived.salesCC,
                    salesCash: finalMoneyReceived.salesCash,
                    foodSales: finalMoneyReceived.foodSales,
                    beverageSales: finalMoneyReceived.beverageSales,
                    foodAndBeverageSalesSumm: finalMoneyReceived.foodAndBeverageSalesSumm
                  };
                }
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyIn = allBlankSumms;
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalMoneyOut = allBlankSumms;
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalTipOutsGiven = allBlankSumms;
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].totalTipOutsReceived = finalMoneyReceived;
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute = finalMoneyToDistribute;
                cc_cash_minus_cash_collected = finalMoneyToDistribute.cc + finalMoneyToDistribute.cash + finalMoneyToDistribute.agSvc + finalMoneyToDistribute.fee - totalMoneyIn.cash;
                _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].finalMoneyToDistribute.cc_cash_minus_cash_collected = cc_cash_minus_cash_collected;
                employeeData = _this.data.positionsMoney[positionType].teams[teamNumber].employees[id];
                hourly_rate = (employeeData.finalMoneyToDistribute.cc + employeeData.finalMoneyToDistribute.cash + employeeData.finalMoneyToDistribute.agSvc + employeeData.finalMoneyToDistribute.fee) / employeeData.hoursWorkedInHours;
                return _this.data.positionsMoney[positionType].teams[teamNumber].employees[id].hourly_rate = hourly_rate;
              });
              totalTeamCollectedMoney = allBlankSumms;
              totalPositionHoursWorked = {};
              totalPositionHoursWorked = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.hoursWorkedInHours);
              });
              totalPositionHoursWorked = _.reduce(totalPositionHoursWorked, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamSales = {
                cc: 0,
                cash: 0
              };
              totalTeamFoodSales = {
                food: 0,
                beverage: 0
              };
              totalMoneyToDistribute = {};
              totalMoneyToDistribute.cc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalMoneyOut.cc);
              });
              totalMoneyToDistribute.cc = _.reduce(totalMoneyToDistribute.cc, function(sum, num) {
                return sum + num;
              }) || 0;
              totalMoneyToDistribute.cash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalMoneyOut.cash);
              });
              totalMoneyToDistribute.cash = _.reduce(totalMoneyToDistribute.cash, function(sum, num) {
                return sum + num;
              }) || 0;
              totalMoneyToDistribute.agSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalMoneyOut.agSvc);
              });
              totalMoneyToDistribute.agSvc = _.reduce(totalMoneyToDistribute.agSvc, function(sum, num) {
                return sum + num;
              }) || 0;
              totalMoneyToDistribute.fee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalMoneyOut.fee);
              });
              totalMoneyToDistribute.fee = _.reduce(totalMoneyToDistribute.fee, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOGivenCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsGiven.cc);
              });
              totalTeamTOGivenCC = _.reduce(totalTeamTOGivenCC, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOGivenCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsGiven.cash);
              });
              totalTeamTOGivenCash = _.reduce(totalTeamTOGivenCash, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOGivenAgSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsGiven.agSvc);
              });
              totalTeamTOGivenAgSvc = _.reduce(totalTeamTOGivenAgSvc, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOGivenFee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsGiven.fee);
              });
              totalTeamTOGivenFee = _.reduce(totalTeamTOGivenFee, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOReceivedCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsReceived.cc);
              });
              totalTeamTOReceivedCC = _.reduce(totalTeamTOReceivedCC, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOReceivedCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsReceived.cash);
              });
              totalTeamTOReceivedCash = _.reduce(totalTeamTOReceivedCash, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOReceivedAgSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsReceived.agSvc);
              });
              totalTeamTOReceivedAgSvc = _.reduce(totalTeamTOReceivedAgSvc, function(sum, num) {
                return sum + num;
              }) || 0;
              totalTeamTOReceivedFee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.totalTipOutsReceived.fee);
              });
              totalTeamTOReceivedFee = _.reduce(totalTeamTOReceivedFee, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeCC = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.finalMoneyToDistribute.cc);
              });
              finalTeamTODistributeCC = _.reduce(finalTeamTODistributeCC, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.finalMoneyToDistribute.cash);
              });
              finalTeamTODistributeCash = _.reduce(finalTeamTODistributeCash, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeCCMinusCash = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return data.finalMoneyToDistribute.cc_cash_minus_cash_collected;
              });
              finalTeamTODistributeCCMinusCash = _.reduce(finalTeamTODistributeCCMinusCash, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeAgSvc = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.finalMoneyToDistribute.agSvc);
              });
              finalTeamTODistributeAgSvc = _.reduce(finalTeamTODistributeAgSvc, function(sum, num) {
                return sum + num;
              }) || 0;
              finalTeamTODistributeFee = _.map(_this.data.positionsMoney[positionType].teams[teamNumber].employees, function(data, id) {
                return parseFloat(data.finalMoneyToDistribute.fee);
              });
              finalTeamTODistributeFee = _.reduce(finalTeamTODistributeFee, function(sum, num) {
                return sum + num;
              }) || 0;
              _this.data.positionsMoney[positionType].teams[teamNumber].totalTeamMoneyToDistribute = totalMoneyToDistribute;
              return _this.data.positionsMoney[positionType].teams[teamNumber].teamTotal = {
                totalTeamSales: totalTeamSales || 0,
                totalTeamFoodSales: totalTeamFoodSales || 0,
                totalTeamCollectedMoney: totalTeamCollectedMoney,
                totalPositionHoursWorked: totalPositionHoursWorked || 0,
                ccToDistribute: totalMoneyToDistribute.cc,
                cashToDistribute: totalMoneyToDistribute.cash,
                agSvcToDistribute: totalMoneyToDistribute.agSvc,
                feeToDistribute: totalMoneyToDistribute.fee,
                totalTeamTOGivenCC: totalTeamTOGivenCC || 0,
                totalTeamTOGivenCash: totalTeamTOGivenCash || 0,
                totalTeamTOGivenAgSvc: totalTeamTOGivenAgSvc || 0,
                totalTeamTOGivenFee: totalTeamTOGivenFee || 0,
                totalTeamTOReceivedCC: totalTeamTOReceivedCC,
                totalTeamTOReceivedCash: totalTeamTOReceivedCash,
                totalTeamTOReceivedAgSvc: totalTeamTOReceivedAgSvc,
                totalTeamTOReceivedFee: totalTeamTOReceivedFee,
                finalTeamTODistributeCC: finalTeamTODistributeCC,
                finalTeamTODistributeCash: finalTeamTODistributeCash,
                finalTeamTODistributeCCMinusCash: finalTeamTODistributeCCMinusCash,
                finalTeamTODistributeAgSvc: finalTeamTODistributeAgSvc,
                finalTeamTODistributeFee: finalTeamTODistributeFee
              };
            });
          };
        })(this));
        return this.data;
      };

      return CalculationConditionsSTOHelper;

    })();
    return Tipmetric.Modules.CalculationConditionsSTOHelper = new CalculationConditionsSTOHelper;
  })();

}).call(this);
(function() {
  (function() {
    var OvertipsView;
    OvertipsView = Backbone.View.extend({
      el: "#overtips",
      events: {
        "click [data-action='edit-overtip']": "editOvertips",
        "click [data-action='add-overtip']": "addOvertip",
        "click [data-action='destroy-overtip']": "destroyOvertip",
        "change select.source-type-select, select.target-employee-select, input.percentage, select.source-employee-select": "sourceTipsChanged"
      },
      initialize: function() {
        return this.renderOvertips();
      },
      sourceTipsChanged: function() {
        return this.updateSourceTipOutAttributes();
      },
      editOvertips: function(e) {
        var mode;
        e.preventDefault();
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return;
        }
        mode = $("#overtips").attr("data-mode");
        if (mode === "show") {
          return $("#overtips").attr("data-mode", "editing");
        } else {
          return $("#overtips").attr("data-mode", "show");
        }
      },
      syncPositionTipOutsWithData: function(data) {
        console.log(data);
        return _.each($("#overtips-list .overtip[data-status='new']"), (function(_this) {
          return function(line) {
            var options, overtipData;
            options = {
              source_employee_id: $(line).attr("data-source-employee-id").split("-")[0],
              source_position_id: $(line).attr("data-source-employee-id").split("-")[1],
              target_employee_id: $(line).attr("data-target-employee-id").split("-")[0],
              target_position_id: $(line).attr("data-target-employee-id").split("-")[1],
              source_type: $(line).attr("data-source-type"),
              percentage: parseFloat($(line).attr("data-percentage"))
            };
            overtipData = _.findWhere(data, options);
            $(line).attr("data-status", "persisted");
            return $(line).attr("data-overtip-id", overtipData.id);
          };
        })(this));
      },
      updateSourceTipOutAttributes: function() {
        var i, len, ref, results, tr;
        ref = $("#overtips-list .overtip");
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          tr = ref[i];
          $(tr).attr("data-source-employee-id", $(tr).find("select.source-employee-select").val());
          $(tr).find(".source-employee-name").text($(tr).find("select.source-employee-select option:selected").text());
          $(tr).attr("data-percentage", $(tr).find("input.percentage").val());
          $(tr).find(".percentage-info").text($(tr).find("input.percentage").val());
          $(tr).attr("data-source-type", $(tr).find("select.source-type-select").val());
          $(tr).find(".source-type-info").text($(tr).find("select.source-type-select option:selected").text());
          $(tr).attr("data-target-employee-id", $(tr).find("select.target-employee-select").val());
          results.push($(tr).find(".target-employee-name").text($(tr).find("select.target-employee-select option:selected").text()));
        }
        return results;
      },
      getSourceTypes: function() {
        var types;
        types = [
          {
            id: "cc",
            name: this.labelFor('cc').name || "CC Tips"
          }, {
            id: "cash",
            name: this.labelFor('cash').name || "Cash Tips"
          }, {
            id: "cc_amount",
            name: this.labelFor('cc_amount').name || "CC Amount"
          }, {
            id: "cash_amount",
            name: this.labelFor('cash_amount').name || "Cash Amount"
          }, {
            id: "foodSales",
            name: this.labelFor('foodSales').name || "Food Sales"
          }, {
            id: "beverageSales",
            name: this.labelFor('beverageSales').name || "Beverage Sales"
          }, {
            id: "foodAndBeverageSalesSumm",
            name: this.labelFor('foodAndBeverageSalesSumm').name || "Total Sales"
          }, {
            id: "salesCC",
            name: this.labelFor('salesCC').name || "Sales CC"
          }
        ];
        return _.flatten(types);
      },
      labelFor: function(key) {
        return _.findWhere(gon.restaurant.custom_labels.source_type_labels, {
          id: key
        });
      },
      getEmployees: function() {
        var employee, employees, i, j, len, len1, positionId, positionInfo, ref, ref1;
        employees = [];
        ref = _.keys(gon.all_related_employees_unscoped);
        for (i = 0, len = ref.length; i < len; i++) {
          positionId = ref[i];
          positionInfo = gon.all_related_employees_unscoped[positionId];
          ref1 = positionInfo.employees;
          for (j = 0, len1 = ref1.length; j < len1; j++) {
            employee = ref1[j];
            employees.push({
              identifier: employee.id + "-" + positionId,
              employeeInfo: employee,
              position: {
                name: positionInfo.position_type_name,
                id: positionInfo.position_type_id
              }
            });
          }
        }
        return employees;
      },
      select2Template: function(state) {
        var $state, calc_date, employeeId, employee_position_status, hidden_from, isActive, position_type_id;
        position_type_id = $(state.element).attr("data-position-id");
        employeeId = $(state.element).attr("data-employee-id");
        if (!gon.restaurant.hidden_from_enabled) {
          isActive = true;
        } else if (!_.isEmpty(_.findWhere(gon.restaurant_inheritance_data_raw.employee_position_statuses, {
          employee_id: employeeId,
          position_type_id: position_type_id
        }))) {
          employee_position_status = _.findWhere(gon.restaurant_inheritance_data_raw.employee_position_statuses, {
            employee_id: employeeId,
            position_type_id: position_type_id
          });
          hidden_from = moment(employee_position_status.hidden_from);
          calc_date = moment($("#calculation-form-wrapper").find("input#date").val());
          if (calc_date >= hidden_from) {
            isActive = false;
          } else {
            isActive = true;
          }
        } else {
          isActive = true;
        }
        if (!state.id) {
          return state.text;
        }
        $state = $(("<span data-employee-enabled='" + isActive + "'>") + state.text + "</span>");
        return $state;
      },
      addOvertip: function(e) {
        var employees, html, tempId;
        e.preventDefault();
        tempId = Tipmetric.Controllers.helpers.randomNumber();
        employees = this.getEmployees();
        html = JST["calculation/overtips/overtip"]({
          employees: employees,
          sourceEmployeeSelected: null,
          nonSourceEmployeeSelected: null,
          sourceTypes: this.getSourceTypes(),
          sourceTypeSelected: null,
          status: 'new',
          tempId: tempId
        });
        $("#overtips-list > .total").before(html);
        $("#overtips-list").find("select.source-employee-select, select.target-employee-select").select2({
          theme: "bootstrap",
          templateResult: this.select2Template,
          templateSelection: this.select2Template
        });
        $("#overtips-list select.source-type-select").select2({
          theme: "bootstrap"
        });
        return this.updateSourceTipOutAttributes();
      },
      destroyOvertip: function(e) {
        var overtipId, tr;
        e.preventDefault();
        tr = $(e.currentTarget).closest("tr");
        if (tr.attr("data-status") === "new") {
          tr.remove();
          toastr["success"]("Tip Sharing removed", "Success");
          return Tipmetric.Controllers.calculationView.calculate();
        } else {
          overtipId = tr.attr("data-overtip-id");
          return $.ajax({
            method: "delete",
            url: "/api/overtips/" + overtipId,
            success: (function(_this) {
              return function(response) {
                tr.remove();
                toastr["success"]("Tip Sharing removed", "Success");
                return Tipmetric.Controllers.calculationView.calculate();
              };
            })(this),
            error: (function(_this) {
              return function(response) {
                return toastr["error"](response.responseText, "Error");
              };
            })(this)
          });
        }
      },
      renderOvertips: function() {
        var employees, html, i, len, overtip, ref, tempId;
        employees = this.getEmployees();
        ref = gon.overtips;
        for (i = 0, len = ref.length; i < len; i++) {
          overtip = ref[i];
          tempId = Tipmetric.Controllers.helpers.randomNumber();
          html = JST["calculation/overtips/overtip"]({
            id: overtip._id.$oid,
            percentage: overtip.percentage,
            employees: employees,
            sourceEmployeeSelected: overtip.source_employee_id.$oid + "-" + overtip.source_position_id.$oid,
            nonSourceEmployeeSelected: overtip.target_employee_id.$oid + "-" + overtip.target_position_id.$oid,
            sourceTypes: this.getSourceTypes(),
            sourceTypeSelected: overtip.source_type,
            status: 'persisted',
            tempId: tempId
          });
          $("#overtips-list").prepend(html);
          $("#overtips-list").find("select.source-employee-select, select.target-employee-select").select2({
            theme: "bootstrap",
            templateResult: this.select2Template,
            templateSelection: this.select2Template
          });
          $("#overtips-list").find("select.condition-first-select, select.condition-type-select").select2({
            theme: "bootstrap"
          });
          $("#overtips-list select.source-type-select").select2({
            theme: "bootstrap"
          });
        }
        return this.updateSourceTipOutAttributes();
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("calculation") && gon.calculation_id !== void 0) {
        return Tipmetric.Controllers.overtipsView = new OvertipsView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var CalculationPendingView;
    CalculationPendingView = Backbone.View.extend({
      el: "body.calculation",
      events: {
        "click [data-pending-action]": "pendingAction"
      },
      pendingAction: function(e) {
        var approval_action, params, position_type_id, team_number, tr;
        e.preventDefault();
        if ($(e.currentTarget).isDisabled()) {
          return true;
        }
        $(e.currentTarget).disable();
        if (this.isTeamsQuantityUpdateRequired(e)) {
          tr = $(e.currentTarget).closest("tr");
          team_number = tr.find(".team_number").text();
          Tipmetric.Controllers.calculationFormView.changeTeamQuantityTo({
            teamNumber: team_number
          });
        }
        if (this.isAdditionalSourceUpdateRequired(e)) {
          tr = $(e.currentTarget).closest("tr");
          position_type_id = tr.attr("data-position-type-id");
          Tipmetric.Controllers.calculationFormView.addSourcePositionById({
            id: position_type_id
          });
        }
        Tipmetric.Controllers.calculationView.distributionsView.addMissingTables();
        if (Tipmetric.Controllers.calculationFormView.pendingDistributionPositionRemoveAttempt()) {
          Tipmetric.Controllers.calculationFormView.cancelSourcePositions();
        }
        params = {
          updateCalculationRequired: true,
          afterUpdateCallback: {
            action: Tipmetric.Controllers.calculationPendingView.pendingActionRequest,
            event: e
          }
        };
        approval_action = $(e.currentTarget).attr("data-pending-action");
        if (approval_action === "accept") {
          Tipmetric.Controllers.spinner.show();
          return this.pendingActionRequest(e);
        } else {
          return Tipmetric.Controllers.calculationView.checkCalculationExistance(params);
        }
      },
      pendingActionRequest: function(e) {
        var approval_action, id, tr;
        tr = $(e.currentTarget).closest("tr");
        approval_action = $(e.currentTarget).attr("data-pending-action");
        id = $(e.currentTarget).attr("data-employee-distribution-id");
        return $.ajax({
          url: "api/employee_distributions/check_approval_status",
          method: "patch",
          data: {
            approval_action: approval_action,
            id: id,
            calculation_id: gon.calculation_id
          },
          success: (function(_this) {
            return function(response) {
              var emp, employeeListForPosition, existedEmployeeIds, i, len, line, moneyInFields, ref, table, team_td, tr_line;
              response = response.employee_distribution;
              if (response.persisted === false) {
                tr.remove();
                Tipmetric.Controllers.calculationPendingView.checkPendingBlock();
                Tipmetric.Controllers.calculationPendingView.bindActions();
                toastr["success"]("Distribution successfully removed", "Removed");
              } else {
                table = $("table[data-position-table=\"" + response.position_type_name + "\"][data-team='" + response.team_number + "']");
                existedEmployeeIds = _.map(table.find("select.employee-id"), function(e) {
                  return $(e).val();
                });
                employeeListForPosition = "";
                ref = gon.related_employees[response.position_type_name].employees;
                for (i = 0, len = ref.length; i < len; i++) {
                  emp = ref[i];
                  if (!_.contains(existedEmployeeIds, emp.id)) {
                    employeeListForPosition += "<option value='" + emp.id + "'>" + emp.name + "</option>";
                  }
                }
                team_td = "<td class='team-number'>" + response.team_number + "</td>";
                moneyInFields = "<td class='notactive ag-svc-in source-only-fields ag-svc-cell'> <div class='input-group m-b'><span class='input-group-addon'>$</span> <input type='text' placeholder='0.00' class='form-control' value='" + (parseFloat(response.ag_svc_tips).toFixed(2)) + "' data-recalculate='true'></div> </td> <td class='notactive fee-in source-only-fields fee-cell'> <div class='input-group m-b'><span class='input-group-addon'>$</span> <input type='text' placeholder='0.00' class='form-control' value='" + (parseFloat(response.fee_tips).toFixed(2)) + "' data-recalculate='true'></div> </td> <td class='notactive sales-cc source-only-fields sales-cell'> <div class='input-group m-b'><span class='input-group-addon'>$</span> <input type='text' placeholder='Sales' class='form-control' value='" + (parseFloat(response.sales_cc).toFixed(2)) + "' data-recalculate='true'></div> </td> <td class='notactive sales-cash source-only-fields sales-cell'> <div class='input-group m-b'><span class='input-group-addon'>$</span> <input type='text' placeholder='Sales' class='form-control' value='" + (parseFloat(response.sales_cash).toFixed(2)) + "' data-recalculate='true'></div> </td> <td class='notactive food-sales source-only-fields food-and-beverage-cell'> <div class='input-group m-b'><span class='input-group-addon'>$</span> <input type='text' placeholder='Sales' class='form-control' value='" + (parseFloat(response.food_sales).toFixed(2)) + "' data-recalculate='true'></div> </td> <td class='notactive beverage-sales source-only-fields food-and-beverage-cell'> <div class='input-group m-b'><span class='input-group-addon'>$</span> <input type='text' placeholder='Sales' class='form-control' value='" + (parseFloat(response.beverage_sales).toFixed(2)) + "' data-recalculate='true'></div> </td> <td class='notactive source-only-fields cc-in'> <div class='input-group m-b'><span class='input-group-addon'>$</span> <input type='text' placeholder='CC tips' class='form-control' value='" + (parseFloat(response.cc_tips).toFixed(2)) + "' data-recalculate='true'></div> </td> <td class='notactive source-only-fields cash-in'> <div class='input-group m-b'><span class='input-group-addon'>$</span> <input type='text' placeholder='Cash tips' class='form-control' value='" + (parseFloat(response.cash_tips).toFixed(2)) + "' data-recalculate='true'></div> </td>";
                tr_line = ("<tr class='employee-distribution-line' data-emp-distribution-status='persisted' data-employee-id='" + response.employee_id + "' data-emp-distribution-id='" + response.id + "'> <td> <select class='select-2 employee-id' style='width: 100%'>") + employeeListForPosition + "</select> </td>" + team_td + ("<td><input class='number-hrs form-control' type='text'  name='number-hrs' value='" + (parseFloat(response.hours_worked).toFixed(2)) + "' data-recalculate='true'> </td>") + moneyInFields + ("<td class='notactive before-tip-out-td cc-out'> - </td> <td class='notactive before-tip-out-td cash-out'> - </td> <td class='notactive before-tip-out-td ag-svc-out ag-svc-cell'> - </td> <td class='notactive before-tip-out-td fee-out fee-cell'> - </td> <td class='notactive tip-outs tip-outs-given-cc'> - </td> <td class='notactive tip-outs tip-outs-given-cash'> - </td> <td class='notactive tip-outs tip-outs-given-ag-svc ag-svc-cell'> - </td> <td class='notactive tip-outs tip-outs-given-fee fee-cell'> - </td> <td class='notactive tip-outs tip-outs-received-cc'> - </td> <td class='notactive tip-outs tip-outs-received-cash'> - </td> <td class='notactive tip-outs tip-outs-received-ag-svc ag-svc-cell'> - </td> <td class='notactive tip-outs tip-outs-received-fee fee-cell'> - </td> <td class='active final-tips-distributed-cc'> - </td> <td class='active final-tips-distributed-cash'> - </td> <td class='active final-distributed-ag-svc ag-svc-cell'> - </td> <td class='active final-distributed-fee fee-cell'> - </td> <td class='active final-with-overtips-cc overtips-cell'> - </td> <td class='active final-with-overtips-cash overtips-cell'> - </td> <td class='actions hidden'> <button class='btn btn-danger' type='button' data-action='remove-employee-distribution' data-position=\"" + response.position_type_name + "\" data-team='" + response.team_number + "'><i class='fa fa-minus-circle'></i></button> </td> </tr>");
                $(table).find("tbody tr.info").before(tr_line);
                line = $(table).find("tr.employee-distribution-line[data-employee-id='" + response.employee_id + "']");
                line.find("select.employee-id").first().val(response.employee_id).trigger("change");
                tr.remove();
                Tipmetric.Controllers.calculationPendingView.checkPendingBlock();
                Tipmetric.Controllers.calculationPendingView.bindActions();
                Tipmetric.Controllers.calculationView.calculate();
                Tipmetric.Controllers.spinner.hide();
                toastr["success"]("Distribution successfully added to the calculation", "Approved");
              }
              $(e.currentTarget).enable();
              return Tipmetric.Controllers.calculationView.disableInputs();
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              toastr["error"](response.responseJSON.errors, "Error");
              Tipmetric.Controllers.spinner.hide();
              return $(e.currentTarget).enable();
            };
          })(this)
        });
      },
      isTeamsQuantityUpdateRequired: function(e) {
        var team_number, tr;
        tr = $(e.currentTarget).closest("tr");
        team_number = parseInt(tr.find(".team_number").text());
        return team_number > gon.calculation_params.teams_quantity && team_number > parseInt($("#teams_quantity").val());
      },
      isAdditionalSourceUpdateRequired: function(e) {
        var hasTips, position_type, tr;
        tr = $(e.currentTarget).closest("tr");
        position_type = tr.attr("data-position-type-id");
        hasTips = parseInt(tr.find(".cc_tips").text()) > 0 || parseInt(tr.find(".cash_tips").text()) > 0 || parseInt(tr.find(".fee_tips").text()) > 0 || parseInt(tr.find(".ag_svc_tips").text()) > 0;
        return hasTips && !_.contains(gon.calculation_params.source_position_ids, position_type);
      },
      checkPendingBlock: function() {
        if ($("#pending-distributions-view table tbody tr").length === 0) {
          $("#pending-distributions-view").remove();
        }
        return Tipmetric.Controllers.calculationView.calculate();
      },
      bindActions: function() {
        window.bindSelect2();
        window.bindPercentSelect();
        return window.bindCheckboxes();
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("calculation")) {
        return Tipmetric.Controllers.calculationPendingView = new CalculationPendingView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var SourceTipOutsView;
    SourceTipOutsView = Backbone.View.extend({
      el: "#source-tip-outs",
      events: {
        "click [data-action='edit-tip-sharing']": "editTipSharing",
        "click [data-action='add-tip-sharing']": "addTipSharing",
        "click [data-action='destroy-tip-sharing']": "destroyTipSharing",
        "change select.source-type-select, select.target-position-select, input.percentage, select.source-position-select, select.condition-first-select, input.condition-second-input, select.condition-type-select": "sourceTipsChanged"
      },
      initialize: function() {
        gon.getAllAvailablePositions = this.getAllAvailablePositions();
        this.renderTipSharings();
        if (!gon.restaurant.sto_edit_button_enabled) {
          return $("[data-action='edit-tip-sharing']").attr("disabled", "disabled");
        }
      },
      sourceTipsChanged: function() {
        return this.updateSourceTipOutAttributes();
      },
      editTipSharing: function(e) {
        var mode;
        e.preventDefault();
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return;
        }
        mode = $("#source-tip-outs").attr("data-mode");
        if (mode === "show") {
          return $("#source-tip-outs").attr("data-mode", "editing");
        } else {
          return $("#source-tip-outs").attr("data-mode", "show");
        }
      },
      syncPositionTipOutsWithData: function(data) {
        return _.each($("#position_tip_sharings .position-tip-sharing[data-status='new']"), (function(_this) {
          return function(line) {
            var options, tipOutData;
            options = {
              source_position_id: $(line).attr("data-source-position-id"),
              target_position_id: $(line).attr("data-target-position-id"),
              source_type: $(line).attr("data-source-type"),
              percentage: parseFloat($(line).attr("data-percentage"))
            };
            tipOutData = _.findWhere(data, options);
            $(line).attr("data-status", "persisted");
            return $(line).attr("data-position-tip-sharing-id", tipOutData.id);
          };
        })(this));
      },
      positionsThatReceiveTipSharingsIds: function() {
        return _.uniq(_.map($("#position_tip_sharings select.target-position-select"), function(f) {
          return $(f).val();
        }));
      },
      updateSourceTipOutAttributes: function() {
        var i, len, positionsThatReceiveTipSharingsIds, ref, results, tr;
        positionsThatReceiveTipSharingsIds = this.positionsThatReceiveTipSharingsIds();
        ref = $("#position_tip_sharings .position-tip-sharing");
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          tr = ref[i];
          $(tr).attr("data-source-position-id", $(tr).find("select.source-position-select").val());
          $(tr).find(".source-position-name").text($(tr).find("select.source-position-select option:selected").text());
          $(tr).attr("data-percentage", $(tr).find("input.percentage").val());
          $(tr).find(".percentage-info").text($(tr).find("input.percentage").val());
          $(tr).attr("data-source-type", $(tr).find("select.source-type-select").val());
          $(tr).find(".source-type-info").text($(tr).find("select.source-type-select option:selected").text());
          $(tr).attr("data-target-position-id", $(tr).find("select.target-position-select").val());
          $(tr).find(".target-position-name").text($(tr).find("select.target-position-select option:selected").text());
          $(tr).attr("data-condition-first", $(tr).find("select.condition-first-select").val());
          $(tr).find(".condition-first-info").text($(tr).find("select.condition-first-select option:selected").text());
          $(tr).attr("data-condition-type", $(tr).find("select.condition-type-select").val());
          $(tr).find(".condition-type-info").text($(tr).find("select.condition-type-select option:selected").text());
          $(tr).attr("data-condition-second", $(tr).find("input.condition-second-input").val());
          $(tr).find(".condition-second-info").text($(tr).find("input.condition-second-input").val());
          if (gon.restaurant.sto_condition_rules_enabled) {
            results.push($(tr).attr("data-condition-enabled", true));
          } else {
            results.push(void 0);
          }
        }
        return results;
      },
      getSourceTypes: function() {
        var types;
        types = [
          {
            id: "cc",
            name: this.labelFor('cc').name || "CC Tips"
          }, {
            id: "cash",
            name: this.labelFor('cash').name || "Cash Tips"
          }
        ];
        if (gon.restaurant.custom_cash_tips_distr_enabled) {
          types.push({
            id: "custom_cash_tips_distr",
            name: this.labelFor('custom_cash_tips_distr').name || "Cash Tips Distr"
          });
        }
        types.push([
          {
            id: "cc_cash",
            name: this.labelFor('cc_cash').name || "CC + Cash Tips"
          }, {
            id: "fee",
            name: this.labelFor('fee').name || "Fee"
          }, {
            id: "agSvc",
            name: this.labelFor('agSvc').name || "AG/SVC"
          }, {
            id: "foodSales",
            name: this.labelFor('foodSales').name || "Food Sales"
          }, {
            id: "beverageSales",
            name: this.labelFor('beverageSales').name || "Beverage Sales"
          }, {
            id: "foodAndBeverageSalesSumm",
            name: this.labelFor('foodAndBeverageSalesSumm').name || "Total Sales"
          }, {
            id: "salesCC",
            name: this.labelFor('salesCC').name || "Sales CC"
          }, {
            id: "salesCash",
            name: this.labelFor('salesCash').name || "Sales Cash"
          }, {
            id: "hours",
            name: this.labelFor('hours').name || "Hours"
          }, {
            id: "hours_no_source",
            name: "Hours - No Source"
          }
        ]);
        return _.flatten(types);
      },
      getSourceTypeById: function(id) {
        return _.find(this.getSourceTypes(), function(f) {
          return f.id === id;
        }).name;
      },
      getConditionSourceTypes: function() {
        if (gon.restaurant.sto_condition_rules_enabled) {
          return _.filter(this.getSourceTypes(), function(f) {
            return f.id !== 'hours';
          });
        } else {
          return this.getSourceTypes();
        }
      },
      conditionTypes: function() {
        return [
          {
            id: "gt",
            name: ">"
          }, {
            id: "lt",
            name: "<"
          }, {
            id: "eq",
            name: "="
          }
        ];
      },
      conditionTypeNameById: function(id) {
        return _.find(this.conditionTypes(), function(f) {
          return f.id === id;
        }).name;
      },
      labelFor: function(key) {
        return _.findWhere(gon.restaurant.custom_labels.source_type_labels, {
          id: key
        });
      },
      getNonSourcePositions: function() {
        return _.map($("form #source_position_ids option:not(:selected)"), function(e) {
          return {
            id: $(e).val(),
            name: $(e).text()
          };
        });
      },
      getSourcePositions: function() {
        return _.map($("form #source_position_ids option:selected"), function(e) {
          return {
            id: $(e).val(),
            name: $(e).text()
          };
        });
      },
      getSourcePositionIds: function() {
        return _.map(this.getSourcePositions(), function(e) {
          return e.id;
        });
      },
      getAllAvailablePositions: function() {
        return _.map($("form #source_position_ids option"), function(e) {
          return {
            id: $(e).val(),
            name: $(e).text()
          };
        });
      },
      isPartOfJoinedDistribution: function(data, positionId) {
        return !_.isEmpty(_.filter(data.joinedTipSharings, function(ts) {
          return ts.sourcePositionId === positionId || ts.targetPositionId === positionId;
        }));
      },
      select2Template: function(state) {
        var $state, activePositionIds, avtiveAvailablePositions, getAllAvailablePositionsOnSetupPage, isActive;
        activePositionIds = _.map(_.filter(gon.restaurant.restaurant_position_types, function(e) {
          return e.active === true;
        }), function(f) {
          return f._id.$oid;
        });
        getAllAvailablePositionsOnSetupPage = _.map(gon.getAllAvailablePositions, function(f) {
          return f.id;
        });
        avtiveAvailablePositions = _.intersection(activePositionIds, getAllAvailablePositionsOnSetupPage);
        if (_.contains(avtiveAvailablePositions, state.id)) {
          isActive = true;
        } else {
          isActive = false;
        }
        if (!state.id) {
          return state.text;
        }
        $state = $(("<span data-position-enabled='" + isActive + "'>") + state.text + "</span>");
        return $state;
      },
      addTipSharing: function(e) {
        var html, restaurant_position_types, tempId;
        e.preventDefault();
        tempId = Tipmetric.Controllers.helpers.randomNumber();
        restaurant_position_types = _.map(gon.restaurant.restaurant_position_types, function(pt) {
          pt.id = pt._id.$oid;
          return pt;
        });
        html = JST["calculation/position_tip_sharings/position_tip_sharing"]({
          sourcePositions: restaurant_position_types,
          nonSourcePositions: restaurant_position_types,
          sourcePositionSelected: null,
          nonSourcePositionSelected: null,
          sourceTypes: this.getSourceTypes(),
          sourceTypeSelected: null,
          conditionSourceTypes: this.getConditionSourceTypes(),
          conditionFirstSelected: null,
          conditionTypeSelected: null,
          conditionSecond: null,
          conditionTypes: this.conditionTypes(),
          status: 'new',
          tempId: tempId
        });
        $("#position_tip_sharings > .total").before(html);
        $("#position_tip_sharings").find("select.source-position-select, select.target-position-select").select2({
          theme: "bootstrap",
          templateResult: this.select2Template,
          templateSelection: this.select2Template
        });
        $("#position_tip_sharings").find("select.condition-first-select, select.condition-type-select").select2({
          theme: "bootstrap"
        });
        $("#position_tip_sharings select.source-type-select").select2({
          theme: "bootstrap"
        });
        return this.updateSourceTipOutAttributes();
      },
      destroyTipSharing: function(e) {
        var positionTipSharingId, tr;
        e.preventDefault();
        tr = $(e.currentTarget).closest("tr");
        if (tr.attr("data-status") === "new") {
          tr.remove();
          toastr["success"]("Tip Sharing removed", "Success");
          return Tipmetric.Controllers.calculationView.calculate();
        } else {
          positionTipSharingId = tr.attr("data-position-tip-sharing-id");
          return $.ajax({
            method: "delete",
            url: "/api/position_tip_sharings/" + positionTipSharingId,
            success: (function(_this) {
              return function(response) {
                tr.remove();
                toastr["success"]("Tip Sharing removed", "Success");
                return Tipmetric.Controllers.calculationView.calculate();
              };
            })(this),
            error: (function(_this) {
              return function(response) {
                return toastr["error"](response.responseText, "Error");
              };
            })(this)
          });
        }
      },
      renderTipSharings: function() {
        var html, i, len, positionTipSharing, ref, restaurant_position_types, tempId;
        ref = gon.position_tip_sharings;
        for (i = 0, len = ref.length; i < len; i++) {
          positionTipSharing = ref[i];
          tempId = Tipmetric.Controllers.helpers.randomNumber();
          restaurant_position_types = _.map(gon.restaurant.restaurant_position_types, function(pt) {
            pt.id = pt._id.$oid;
            return pt;
          });
          html = JST["calculation/position_tip_sharings/position_tip_sharing"]({
            id: positionTipSharing._id.$oid,
            percentage: positionTipSharing.percentage,
            sourcePositions: restaurant_position_types,
            nonSourcePositions: restaurant_position_types,
            sourcePositionSelected: positionTipSharing.source_position_id.$oid,
            nonSourcePositionSelected: positionTipSharing.target_position_id.$oid,
            sourceTypes: this.getSourceTypes(),
            sourceTypeSelected: positionTipSharing.source_type,
            conditionSourceTypes: this.getConditionSourceTypes(),
            conditionFirstSelected: positionTipSharing.condition_first,
            conditionTypeSelected: positionTipSharing.condition_type,
            conditionSecond: positionTipSharing.condition_second,
            conditionTypes: this.conditionTypes(),
            status: 'persisted',
            tempId: tempId
          });
          $("#position_tip_sharings").prepend(html);
          $("#position_tip_sharings").find("select.source-position-select, select.target-position-select").select2({
            theme: "bootstrap",
            templateResult: this.select2Template,
            templateSelection: this.select2Template
          });
          $("#position_tip_sharings").find("select.condition-first-select, select.condition-type-select").select2({
            theme: "bootstrap"
          });
          $("#position_tip_sharings select.source-type-select").select2({
            theme: "bootstrap"
          });
        }
        return this.updateSourceTipOutAttributes();
      }
    });
    return $(document).ready(function() {
      if (($("body").hasClass("calculation") && gon.calculation_id !== void 0) || $("body").hasClass("brink-import-view") || $("body").hasClass("toast-import-view")) {
        return Tipmetric.Controllers.tipSharingToggleView = new SourceTipOutsView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var STOTemplatesView;
    STOTemplatesView = Backbone.View.extend({
      el: "body",
      events: {
        "click .save-template-data": "saveTemplateData",
        "click .use-template": "useTemplate",
        "click .show-percent-variations": "showPercentVariations",
        "click [data-action='save-template']": "saveTemplateData",
        "click [data-action='remove-template']": "destroyStoTemplate"
      },
      saveTemplateData: function(e) {
        var template;
        template = this.buildTemplateData();
        if (_.isEmpty(template.name)) {
          toastr["error"]("Please, enter distribution name.", "Error");
          return false;
        }
        if (gon.calculation_distribution_logic_type === 'default' && _.isEmpty(template.percent_distributions)) {
          toastr["error"]("Please, add some percent distributions.", "Error");
          return false;
        }
        if (gon.calculation_distribution_logic_type === 'source_tip_outs' && _.isEmpty(template.source_tip_outs)) {
          toastr["error"]("Please add at least 1 Tip Distribution Rule before Saving Template.", "Error");
          return false;
        }
        return $.ajax({
          url: "api/sto_templates",
          method: "post",
          data: {
            sto_template: template
          },
          success: function(response) {
            var percentsTemplate, stoTemplate;
            if (gon.calculation_distribution_logic_type === 'default') {
              percentsTemplate = JST["calculation/percent_variation"](response.sto_template);
              $("#percent_variations").append(percentsTemplate);
            } else {
              stoTemplate = JST["calculation/sto_template"](response.sto_template);
              $("#percent_variations").append(stoTemplate);
            }
            return toastr["success"]("Template saved", "Success");
          },
          errors: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      getTemplatesData: function(showVariations) {
        return $.ajax({
          url: "/api/sto_templates",
          method: "get",
          data: {},
          success: function(response) {
            window.fetched_sto_templates = response.sto_templates;
            return showVariations(response.sto_templates);
          },
          error: function(response) {
            return console.log(response);
          }
        });
      },
      destroyStoTemplate: function(e) {
        var templateId;
        templateId = $(e.currentTarget).attr("template-id");
        return $.ajax({
          url: "api/sto_templates/" + templateId,
          method: "delete",
          success: function(response) {
            $(".implement-percents[template-id='" + templateId + "']").closest(".implement-percents-wrapper").remove();
            return toastr["success"]("Template removed", "Success");
          },
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      buildTemplateData: function(e) {
        var template;
        template = {};
        template.name = $("input.template-name").val();
        template.distribution_logic_type = gon.calculation_distribution_logic_type;
        if (gon.calculation_distribution_logic_type === 'default') {
          template.distribution_type = Tipmetric.Controllers.calculationView.distribution_type();
        }
        template.source_tip_outs = _.map($("#position_tip_sharings .position-tip-sharing"), function(line) {
          var conditions_enabled;
          conditions_enabled = $(line).attr("data-condition-enabled") === "true";
          return {
            source_position_id: $(line).find("select.source-position-select").val(),
            source_position_name: $(line).find("select.source-position-select option:selected").text(),
            source_type: $(line).find("select.source-type-select").val(),
            source_type_name: $(line).find("select.source-type-select option:selected").text(),
            target_position_id: $(line).find("select.target-position-select").val(),
            target_position_name: $(line).find("select.target-position-select option:selected").text(),
            percentage: $(line).find("input.percentage").val(),
            condition_first: conditions_enabled ? $(line).find("select.condition-first-select").val() : null,
            condition_type: conditions_enabled ? $(line).find("select.condition-type-select").val() : null,
            condition_second: conditions_enabled ? parseFloat($(line).find("input.condition-second-input").val()) : null
          };
        });
        template.percent_distributions = _.map($("#percentage .total-position-tips"), function(line) {
          return {
            position_type_id: $(line).attr("data-position-id"),
            position_type_name: $(line).attr("data-position-type"),
            percentage: $(line).find("input.percentage-point").val()
          };
        });
        return template;
      },
      destroyOldSourceTipOuts: function(template) {
        var source_tip_out_ids;
        $("#position_tip_sharings .position-tip-sharing[data-status='new']").remove();
        source_tip_out_ids = _.map($("#position_tip_sharings .position-tip-sharing[data-status='persisted']"), function(e) {
          return $(e).attr("data-position-tip-sharing-id");
        });
        if (_.some(source_tip_out_ids)) {
          return $.ajax({
            method: "delete",
            url: "/api/position_tip_sharings/multiple",
            data: {
              source_tip_out_ids: source_tip_out_ids
            },
            success: (function(_this) {
              return function(response) {
                var i, id, len, ref;
                ref = response.deleted_source_tip_outs;
                for (i = 0, len = ref.length; i < len; i++) {
                  id = ref[i];
                  $("#position_tip_sharings .position-tip-sharing[data-position-tip-sharing-id='" + id + "']").remove();
                }
                return _this.applyStoTemplate(template);
              };
            })(this),
            error: (function(_this) {
              return function(response) {
                return toastr["error"](response.responseText, "Error");
              };
            })(this)
          });
        } else {
          return this.applyStoTemplate(template);
        }
      },
      useTemplate: function(e) {
        var currentPositions, i, id, len, percentage, ref, template, templatePositions;
        this.scope = Tipmetric.Controllers.tipSharingToggleView;
        id = $(e.currentTarget).attr("template-id");
        template = _.find(window.fetched_sto_templates, function(e) {
          return e._id.$oid === id;
        });
        if (gon.calculation_distribution_logic_type === 'default') {
          currentPositions = _.map($("#positions-table .total-position-tips"), function(e) {
            return $(e).attr("data-position-id");
          });
          templatePositions = _.map(template.percent_distributions, function(e) {
            return e.position_type_id;
          });
          if (_.some(_.difference(currentPositions, templatePositions)) || _.some(_.difference(templatePositions, currentPositions))) {
            toastr["error"]("Cannot use template because current Calculation Available Positions do not exactly match Template Available Positions. Please create new template.", "Error");
            return false;
          }
          ref = template.percent_distributions;
          for (i = 0, len = ref.length; i < len; i++) {
            percentage = ref[i];
            $("#positions-table tr[data-position-id='" + percentage.position_type_id + "']").find("input.percentage-point").val(percentage.percentage);
          }
          $("input#distribution_type_" + template.distribution_type).prop('checked', true).trigger("change");
          $('.modal').modal('toggle');
          return Tipmetric.Controllers.calculationView.updateTotalNumbers();
        } else {
          if (_.some($("#position_tip_sharings .position-tip-sharing"))) {
            return this.destroyOldSourceTipOuts(template);
          } else {
            return this.applyStoTemplate(template);
          }
        }
      },
      applyStoTemplate: function(template) {
        var addedPositions, html, i, j, len, len1, names, namesString, ref, ref1, source_tip_out, tempId;
        addedPositions = [];
        ref = template.source_tip_outs;
        for (i = 0, len = ref.length; i < len; i++) {
          source_tip_out = ref[i];
          if (!_.contains(Tipmetric.Controllers.tipSharingToggleView.getSourcePositionIds(), source_tip_out.source_position_id)) {
            addedPositions.push(source_tip_out.source_position_id);
          }
        }
        if (!_.isEmpty(addedPositions)) {
          Tipmetric.Controllers.calculationFormView.addSourcePositionByIds({
            ids: addedPositions
          });
        }
        ref1 = template.source_tip_outs;
        for (j = 0, len1 = ref1.length; j < len1; j++) {
          source_tip_out = ref1[j];
          tempId = Tipmetric.Controllers.helpers.randomNumber();
          html = JST["calculation/position_tip_sharings/position_tip_sharing"]({
            sourcePositions: this.scope.getSourcePositions(),
            nonSourcePositions: this.scope.getAllAvailablePositions(),
            sourcePositionSelected: source_tip_out.source_position_id,
            nonSourcePositionSelected: source_tip_out.target_position_id,
            sourceTypes: this.scope.getSourceTypes(),
            sourceTypeSelected: source_tip_out.source_type,
            status: 'new',
            tempId: tempId,
            conditionSourceTypes: this.scope.getConditionSourceTypes(),
            conditionFirstSelected: source_tip_out.condition_first,
            conditionTypeSelected: source_tip_out.condition_type,
            conditionSecond: source_tip_out.condition_second,
            conditionTypes: this.scope.conditionTypes(),
            percentage: source_tip_out.percentage
          });
          $("#position_tip_sharings > .total").before(html);
          $("#position_tip_sharings select").select2({
            theme: "bootstrap"
          });
          this.scope.updateSourceTipOutAttributes();
        }
        $('.modal').modal('toggle');
        Tipmetric.Controllers.calculationView.updateTotalNumbers();
        if (!_.isEmpty(addedPositions)) {
          names = _.collect(_.uniq(addedPositions), function(f) {
            return _.findWhere(Tipmetric.Controllers.tipSharingToggleView.getAllAvailablePositions(), {
              id: f
            }).name.toUpperCase();
          });
          namesString = _.reduce(names, function(memo, num) {
            return memo + ', ' + num;
          });
          return swal({
            title: "",
            html: "To match the Template just applied, the position types below have been added to the 'Collect Tips' selection box. Please press UPDATE below before you continue.<br><br><b>" + namesString + "</b>",
            type: "info",
            confirmButtonColor: "#66CCFF",
            confirmButtonText: "UPDATE",
            showCancelButton: false
          }).then((function(_this) {
            return function(isConfirmed) {
              if (isConfirmed.value) {
                return $(".show-calculation").click();
              }
            };
          })(this));
        }
      },
      showPercentVariations: function() {
        var showVariations;
        if (Tipmetric.Controllers.calculationView.locked) {
          return false;
        }
        $("#percent_variations").html();
        showVariations = function(data) {
          var distribution_logic_type, filteredData, i, j, len, len1, percentTemplate, percentsData, stoTemplate, stoTemplateData, wrapper;
          distribution_logic_type = gon.calculation_distribution_logic_type;
          filteredData = _.filter(data, function(e) {
            return e.distribution_logic_type === distribution_logic_type;
          });
          if (distribution_logic_type === 'default') {
            wrapper = JST["calculation/percent_variations"](filteredData);
            $(".implement-percents-wrapper").html(wrapper);
            for (i = 0, len = filteredData.length; i < len; i++) {
              percentsData = filteredData[i];
              percentTemplate = JST["calculation/percent_variation"](percentsData);
              $("#percent_variations").append(percentTemplate);
            }
          } else {
            wrapper = JST["calculation/sto_templates"](filteredData);
            $(".implement-percents-wrapper").html(wrapper);
            for (j = 0, len1 = filteredData.length; j < len1; j++) {
              stoTemplateData = filteredData[j];
              stoTemplate = JST["calculation/sto_template"](stoTemplateData);
              $("#percent_variations").append(stoTemplate);
            }
          }
          return $('.modal').modal('toggle');
        };
        return this.getTemplatesData(showVariations);
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("calculation") && gon.calculation_id !== void 0) {
        return Tipmetric.Controllers.stoTemplatesView = new STOTemplatesView;
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var TipOutView;
    TipOutView = Backbone.View.extend({
      el: "#tip-out",
      events: {
        "keyup [data-recalculate='true']": function() {
          return this.scope.updateTotalNumbers();
        },
        "change [data-recalculate='true']": function() {
          return this.scope.updateTotalNumbers();
        },
        "keyup [data-recalculate-total-numbers='true']": function() {
          return this.scope.updateTotalNumbers();
        },
        "change [data-recalculate-total-numbers='true']": function() {
          return this.scope.updateTotalNumbers();
        },
        "change select#position_type_id": "updatePositionSelects",
        "change select#area_type_id": "updateAreaSelects",
        "change select#shift_type_id": "updateTipOutShift",
        "click [data-action='add-area']": "addTipout",
        "click [data-action='edit-tip-outs']": "editTipouts",
        "click [data-action='edit-tip-outs-cancel']": "editTipouts",
        "click [data-action='remove-tip-out-distribution']": "removeTipOutDistribution"
      },
      initialize: function(options) {
        this.scope = options.scope;
        if (!_.isEmpty($("#date").val())) {
          this.updateAreaSelects();
          return this.updateAllPositionSelects();
        }
      },
      updateAllPositionSelects: function(e) {
        _.each($("#tip-out #given-tipouts-table .tip-out-line #position_type_id"), function(select) {
          var tipOutType, val;
          val = $(select).val();
          if (val === "all") {
            tipOutType = "area";
          } else {
            tipOutType = "position";
          }
          $(select).closest(".tip-out-line").attr("data-tip-out-type", tipOutType);
          return $(select).closest(".tip-out-line").attr("data-tip-out-position-id", val);
        });
        this.scope.updateTotalNumbers();
        return this.scope.calculate();
      },
      updatePositionSelects: function(e) {
        var tipOutType, val;
        val = $(e.currentTarget).val();
        if (val === "all") {
          tipOutType = "area";
        } else {
          tipOutType = "position";
        }
        $(e.currentTarget).closest(".tip-out-line").attr("data-tip-out-type", tipOutType);
        $(e.currentTarget).closest(".tip-out-line").attr("data-tip-out-position-id", val);
        this.scope.updateTotalNumbers();
        return this.scope.calculate();
      },
      updateTipOutArea: function(e) {
        return _.each($("#tip-out #given-tipouts-table .tip-out-line #area_type_id"), function(select) {
          var areaName, tr, val;
          val = $(select).val();
          areaName = $(select).closest("tr").find("option[value='" + val + "']").text();
          return tr = $(select).closest("tr").attr("data-area-name", areaName);
        });
      },
      updateTipOutShift: function(e) {
        return _.each($("#tip-out #given-tipouts-table .tip-out-line #shift_type_id"), function(shiftSelect) {
          var shiftName, tr, val;
          val = $(shiftSelect).val();
          shiftName = $(shiftSelect).closest("tr").find("option[value='" + val + "']").text();
          return tr = $(shiftSelect).closest("tr").attr("data-shift-type-name", shiftName);
        });
      },
      updateTipOutShiftForTipOutByArea: function(areaSelect) {
        var shiftSelect, tipOutLine;
        tipOutLine = $(areaSelect).closest(".tip-out-line");
        shiftSelect = $(tipOutLine).find("#area_type_id");
        return this.updateTipOutShiftForTipOut(shiftSelect);
      },
      updateTipOutShiftForTipOut: function(shiftSelect) {
        var areaId, areaName, areaSelect, dayName, optionsHtmlArray, shiftName, shiftSelectVal, shifts, tipOutLine;
        dayName = moment($("#date").val()).format('dddd').toLowerCase();
        tipOutLine = $(shiftSelect).closest(".tip-out-line");
        areaSelect = $(tipOutLine).find("#area_type_id");
        areaId = $(areaSelect).val();
        areaName = $(areaSelect).find("option[value='" + areaId + "']")[0].text;
        shiftSelect = $(shiftSelect).closest(".tip-out-line").find("#shift_type_id");
        shiftSelectVal = $(shiftSelect).val();
        shiftName = $(shiftSelect).find("option[value='" + shiftSelectVal + "']")[0].text;
        shifts = gon.restaurant_inheritance_data[dayName].areas[areaName].shifts;
        optionsHtmlArray = (_.map(shifts, function(item) {
          if (item.id === shiftSelectVal) {
            return "<option value='" + item.id + "' selected='selected'>" + item.name + "</option>";
          } else {
            return "<option value='" + item.id + "'>" + item.name + "</option>";
          }
        })).join();
        return $(shiftSelect).html(optionsHtmlArray);
      },
      availablePositionsForTipOuts: function() {
        return _.values(gon.calculation_source_positions);
      },
      upadteAllPositionTipoutsOptions: function() {
        var availablePositions;
        availablePositions = _.union(this.availablePositionsForTipOuts(), [
          {
            id: "all",
            name: 'all'
          }
        ]);
        return _.each($("#tip-out #given-tipouts-table tbody tr.tip-out-line"), (function(_this) {
          return function(tipOutLine) {
            var finalHtml, id, name, select;
            select = $(tipOutLine).find("select#position_type_id");
            id = $(select).val();
            name = $(select).find("option[value='" + id + "']").text();
            finalHtml = (_.map(availablePositions, function(item) {
              if (item.id === id) {
                return "<option value='" + item.id + "' selected='selected'>" + item.name + "</option>";
              } else {
                return "<option value='" + item.id + "'>" + item.name + "</option>";
              }
            })).join();
            return $(select).html(finalHtml);
          };
        })(this));
      },
      availableAreasForTipOuts: function() {
        var allValues, dayName;
        dayName = moment($("#date").val()).format('dddd').toLowerCase();
        if (typeof Rollbar !== "undefined" && typeof Rollbar.options !== "undefined" && typeof Rollbar.options.payload !== "undefined") {
          Rollbar.options.payload.formParams = {
            date: $("#date").val(),
            day: dayName
          };
        }
        if (gon.calculation_params !== void 0) {
          allValues = _.values(gon.restaurant_inheritance_data[dayName].areas);
          allValues = _.filter(allValues, function(item) {
            return !_.isEmpty(item.shifts);
          });
          if (!gon.restaurant.shifted_tip_outs_enabled) {
            allValues = _.filter(allValues, function(item) {
              return _.findWhere(item.shifts, {
                id: gon.calculation_params.shift_type_id
              });
            });
          }
          return allValues;
        } else {
          return [];
        }
      },
      usedPositionTipOuts: function(data) {
        var positions;
        positions = _.map($("#tip-out #given-tipouts-table tbody tr.tip-out-line[data-tip-out-type='position']"), (function(_this) {
          return function(tipOutLine) {
            return $(tipOutLine).attr("data-tip-out-position-id");
          };
        })(this));
        return positions;
      },
      updateAreaSelects: function(e) {
        var allValues;
        allValues = this.availableAreasForTipOuts();
        _.each($("#tip-out #given-tipouts-table .tip-out-line #area_type_id"), (function(_this) {
          return function(select) {
            var finalHtml, freeItems, id, name, otherAreaSelects, usedValues;
            if (gon.restaurant.shifted_tip_outs_enabled) {
              usedValues = [];
            } else {
              otherAreaSelects = _.filter($("#tip-out #given-tipouts-table .tip-out-line #area_type_id"), function(e) {
                return $(e).get(0) !== $(select).get(0);
              });
              usedValues = _.map(otherAreaSelects, function(e) {
                return $(e).val();
              });
            }
            freeItems = _.filter(_this.availableAreasForTipOuts(), function(item) {
              return !_.contains(usedValues, item.id);
            });
            id = $(select).val();
            name = $(select).find("option[value='" + id + "']").text();
            finalHtml = (_.map(freeItems, function(item) {
              if (item.id === id) {
                return "<option value='" + item.id + "' selected='selected'>" + item.name + "</option>";
              } else {
                return "<option value='" + item.id + "'>" + item.name + "</option>";
              }
            })).join();
            $(select).html(finalHtml);
            if (gon.restaurant.shifted_tip_outs_enabled) {
              return _this.updateTipOutShiftForTipOutByArea(select);
            }
          };
        })(this));
        return this.updateTipOutArea(e);
      },
      editTipouts: function() {
        $(this.el).find(".actions").toggleClass("hidden");
        $(this.el).find("[data-action='edit-tip-outs-cancel']").toggleClass("hidden");
        return $(this.el).find("[data-action='edit-tip-outs']").toggleClass("hidden");
      },
      syncTipOutsWithData: function(data) {
        data = _.filter(data, function(d) {
          return d.tip_out_type !== "food";
        });
        return _.each($("#tip-out #given-tipouts-table .tip-out-line[data-tip-out-status='new']"), (function(_this) {
          return function(line) {
            var areaId, options, shiftTypeId, tipOutData;
            areaId = $(line).find("select#area_type_id").val();
            if (gon.restaurant.shifted_tip_outs_enabled) {
              shiftTypeId = $(line).find("select#shift_type_id").val();
              options = {
                area_type_id: areaId,
                shift_type_id: shiftTypeId
              };
            } else {
              options = {
                area_type_id: areaId
              };
            }
            tipOutData = _.findWhere(data, options);
            $(line).attr("data-tip-out-status", "persisted");
            return $(line).attr("data-tip-out-id", tipOutData.id);
          };
        })(this));
      },
      removeTipOutDistribution: function(e) {
        var destroyButton, table, tr;
        tr = $(e.currentTarget).closest("tr");
        table = $(e.currentTarget).closest("table");
        destroyButton = $(e.currentTarget);
        if (destroyButton.isDisabled()) {
          return true;
        }
        destroyButton.disable();
        if (tr.attr("data-tip-out-status") === "new") {
          tr.remove();
          this.scope.calculate();
          this.updateAreaSelects();
          this.scope.updateTotalNumbers();
          return destroyButton.enable();
        } else if (tr.attr("data-tip-out-status") === "persisted") {
          Tipmetric.Controllers.spinner.show();
          return $.ajax({
            url: "/api/tip_outs/" + tr.attr("data-tip-out-id"),
            method: "delete",
            data: {
              calculation_id: gon.calculation_id
            },
            success: (function(_this) {
              return function(e) {
                toastr["success"]("Tip out removed", "Success");
                tr.remove();
                _this.scope.calculate();
                _this.updateAreaSelects();
                _this.scope.updateTotalNumbers();
                Tipmetric.Controllers.spinner.hide();
                return destroyButton.enable();
              };
            })(this),
            error: (function(_this) {
              return function(response) {
                toastr["error"](response.responseText, "Error");
                Tipmetric.Controllers.spinner.hide();
                return destroyButton.enable();
              };
            })(this)
          });
        }
      },
      addTipout: function(e) {
        var areaOptionsHtmlArray, freeItems, freePositionItems, positionOptionsHtmlArray, shiftOptionsHtmlArray, shifted_tipout_part, table, tr_example, usedValues;
        usedValues = _.map($("#tip-out #given-tipouts-table .tip-out-line #area_type_id"), function(e) {
          return $(e).val();
        });
        freeItems = _.filter(this.availableAreasForTipOuts(), function(item) {
          return !_.contains(usedValues, item.id);
        });
        freePositionItems = this.availablePositionsForTipOuts();
        if (freeItems.length === 0) {
          return toastr["error"]("There are no areas for making tip out.", "Error");
        } else {
          table = $("#tip-out table#given-tipouts-table");
          areaOptionsHtmlArray = (_.map(freeItems, function(item) {
            return "<option value='" + item.id + "'>" + item.name + "</option>";
          })).join();
          positionOptionsHtmlArray = (_.map(freePositionItems, function(item) {
            var htmline;
            if ("all" === item.id) {
              htmline = "<option value='" + item.id + "' selected='selected'>" + item.name + "</option>";
            } else {
              htmline = "<option value='" + item.id + "'>" + item.name + "</option>";
            }
            return htmline;
          })).join();
          shiftOptionsHtmlArray = (_.map(gon.shifts, function(item) {
            var htmline;
            if (gon.calculation_params.shift_type_id === item.id) {
              htmline = "<option value='" + item.id + "' selected='selected'>" + item.name + "</option>";
            } else {
              htmline = "<option value='" + item.id + "'>" + item.name + "</option>";
            }
            return htmline;
          })).join();
          if (gon.restaurant.shifted_tip_outs_enabled) {
            shifted_tipout_part = "<td class='shift-type-select-wrapper'> <select id='shift_type_id' class='select-2 shift-type-id' style='width: 100%' >" + shiftOptionsHtmlArray + "</select> </td>";
          } else {
            shifted_tipout_part = "";
          }
          tr_example = "<tr class='tip-out-line' data-area-name='' data-shift-type-name='' data-tip-out-status='new' data-tip-out-type='area'> <td class='position-type-select-wrapper position-cell'> <select id='position_type_id' class='select-2 position-id' style='width: 100%' >" + positionOptionsHtmlArray + "</select> </td> <td class='area-type-select-wrapper'> <select id='area_type_id' class='select-2 area-id' style='width: 100%' >" + areaOptionsHtmlArray + "</select> <span class='actions hidden'> <span class='btn btn-danger btn-sm' type='button' data-action='remove-tip-out-distribution'><i class='fa fa-minus-circle'></i></span> </span> </td>" + shifted_tipout_part + "<td><input class='touch-spin tip-out-percentage' type='text'  name='touch-spin' value='0' data-recalculate='true' data-recalculate-total-numbers='true'></td> <td class='active cc_summ'>-</td> <td class='active cash_summ'>-</td> <td class='active ag_svc_summ ag-svc-cell'>-</td> <td class='active fee_summ fee-cell'>-</td> </tr>";
          $(table).find("tbody tr.total").before(tr_example);
          window.bindSelectWrappedBy(".tip-out-line");
          window.bindPercentSelect();
          this.updateAreaSelects(e);
          return this.updateTipOutShift();
        }
      }
    });
    return Tipmetric.Controllers.TipOutViewPrototype = TipOutView;
  })();

}).call(this);
(function() {
  (function() {
    var CalculationTourView;
    CalculationTourView = Backbone.View.extend({
      el: "body.calculation",
      events: {
        "click .start-tour": "startTour"
      },
      startTour: function() {
        var tour;
        tour = new Tour({
          name: "calculation_tour",
          steps: [
            {
              element: "[data-tour-step='date']",
              title: "Date",
              content: "Choose Date you would like to enter Tip Data."
            }, {
              element: "[data-tour-step='area']",
              title: "Area",
              content: "Choose Area you would like to enter Tip Data."
            }, {
              element: "[data-tour-step='shift']",
              title: "Shift",
              content: "Choose Shift you would like to enter Tip Data. Note: depending on the date and area selected certain shifts may not be available due to configuration in the Setup page. For example Dinner may only be selected for Tuesdays in the Setup page therefore you will only see Dinner shift available in this dropdown box if the date today is Tuesday. Please refer to Setup page."
            }, {
              element: "[data-tour-step='source-position']",
              title: "Position",
              content: "Choose position(s) that collect tips for the shift selected. Note: Only select positions that will be collecting tips like Server, Bartender. Do NOT select all positions in that shift...TipMetric already knows all positions in that shift based on configuration in the Setup page."
            }, {
              element: "[data-tour-step='teams-quantity']",
              title: "Teams quantity",
              content: "Select the number of teams working during the shift selected. If there are no teams choose 1. It means everyone is working as 1 Team.",
              placement: "left"
            }, {
              element: "[data-tour-step='show']",
              title: "Show calculation",
              content: "Press the \"Show\" button to display tables to enter percentage/ points per position, employee working during the shift, hours worked per employee, tips collected and sales.",
              placement: "left"
            }, {
              element: "[data-tour-step='percents']",
              title: "Percents",
              content: "Enter percentages for each position for the shift."
            }, {
              element: "[data-tour-step='tip-outs']",
              title: "Tip outs",
              content: "Click here to enter Tip-Outs for another area if available."
            }, {
              element: "#distributions-list > div > div:first-child [data-tour-step='add-employee']",
              title: "Adding employee",
              content: "Press \"+Employee\" to add Employee names, hours, sales and/or tips collected working the shift selected.",
              placement: "left"
            }, {
              element: "#distributions-list > div > div:first-child [data-tour-step='employee-id']",
              title: "Adding employee",
              content: "Select Employee that works the position."
            }, {
              element: "#distributions-list > div > div:first-child [data-tour-step='employee-hours']",
              title: "Hours",
              content: "Input hours worked for that shift."
            }, {
              element: "#distributions-list > div > div:first-child [data-tour-step='employee-tips-collected']",
              title: "Tips collected",
              content: "Enter Credit Card Tips and Cash Tips Collected"
            }, {
              element: "#distributions-list > div > div:first-child [data-tour-step='employee-tips-sales']",
              title: "Sales",
              content: "Enter Sales for the Tips Collected."
            }, {
              element: "[data-tour-step='show-tip-outs']",
              title: "Tip outs",
              content: "Click here to see Tip-Out data per employee if there are any Tip-Outs.",
              placement: "left"
            }, {
              element: "[data-tour-step='save-calculation']",
              title: "Saving",
              content: "Click Here to Save Tip record for Date + Area + Shift selected in Steps 1 – 4.",
              placement: "left"
            }, {
              element: "[data-tour-step='history']",
              title: "History",
              content: "Click Calendar icon to go to History Page and see Tip record just created and also previously created Tip Records.",
              placement: "left"
            }
          ],
          backdrop: true
        });
        tour.init();
        return tour.restart();
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("calculation")) {
        return Tipmetric.Controllers.calculationTourView = new CalculationTourView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var CalculationView;
    CalculationView = Backbone.View.extend({
      el: "body.calculation",
      data: {},
      events: {
        "keyup [data-recalculate='true']": "updateTotalNumbers",
        "change [data-recalculate='true']": "updateTotalNumbers",
        "blur #cc_fee": "correctCCFee",
        "blur #ag_svc_fee": "correctAgSvcFee",
        "keyup [data-recalculate-total-numbers='true']": "updateTotalNumbers",
        "change [data-recalculate-total-numbers='true']": "updateTotalNumbers",
        "change input[name='distribution_type']": "changeDistributionType",
        "change select.employee-id": function(e) {
          return this.distributionsView.updateEmployeeLinesIds(e);
        },
        "click #save-calculation": "saveCalculationIfExist",
        "click .implement-percents button": "implementPercents",
        "click .calculation-note .fa-edit": "editNote",
        "click .calculation-note .fa-save": "saveNote",
        "click .tgl-btn": "changeDefaultAndStoLogicSubtype"
      },
      changeDefaultAndStoLogicSubtype: function(e) {
        var type;
        if ($("#distribution_logic_type").attr("disabled") === "disabled") {
          e.preventDefault;
          return;
        }
        if (_.some(Tipmetric.Controllers.calculationView.buildTips().given) || $("#tip-out, #food-tip-outs").find("#received-tipouts-table tbody tr.received-tip-out-line").length > 0) {
          swal({
            title: "",
            text: "Can't change calculation distribution logic. Please manually remove all given/received tip outs and food tip outs first.",
            type: "info",
            confirmButtonColor: "#28C256",
            confirmButtonText: "OK",
            showCancelButton: false
          });
          return false;
        }
        if (gon.calculation_params.teams_quantity > 1) {
          swal({
            title: "",
            text: "Can't change calculation distribution logic. STO logic allows only 1 team.",
            type: "info",
            confirmButtonColor: "#28C256",
            confirmButtonText: "OK",
            showCancelButton: false
          });
          return false;
        }
        if ($("#distribution_logic_type").prop("checked") === false) {
          type = "source_tip_outs";
        } else {
          type = "default";
        }
        $("#percents-and-tip-outs-block, #percents-and-tip-outs-block-nav").attr("data-distribution-logic-type", type);
        gon.calculation_distribution_logic_type = type;
        if (type === 'source_tip_outs') {
          $("[data-toggle-name]").removeClass('active');
          $("[data-toggle-name='source_tip_outs']").addClass('active');
        } else {
          $("[data-toggle-name]").removeClass('active');
          $("[data-toggle-name='percentage']").addClass('active');
        }
        return this.calculate();
      },
      editNote: function(e) {
        $(".calculation-note .fa-edit").addClass("hidden");
        $(".calculation-note .fa-save").removeClass("hidden");
        $(".calculation-note .note-content").attr("contenteditable", true);
        return $(".calculation-note .note-content").focus();
      },
      saveNote: function(e) {
        $(".calculation-note .fa-edit").removeClass("hidden");
        $(".calculation-note .fa-save").addClass("hidden");
        $(".calculation-note .note-content").attr("contenteditable", false);
        return this.dataChanged();
      },
      empDistrTables: function() {
        return $("[data-table-type='employee-distribution']");
      },
      positionDistrTables: function(position) {
        return $("[data-table-type='employee-distribution'][data-position-table=\"" + position + "\"]");
      },
      distribution_type: function() {
        return $("[name='distribution_type']:checked").val();
      },
      enableSaveButton: function() {
        return this.dataChanged();
      },
      dataChanged: function() {
        return $("#save-calculation").removeAttr("disabled");
      },
      isDataSaved: function() {
        return $("#save-calculation").attr("disabled") === "disabled";
      },
      initialize: function() {
        var initialData;
        this.loadModules();
        this.setConfig();
        this.updateTotalNumbers();
        this.dataSaved();
        if (gon.enable_save_button_on_start === true) {
          this.enableSaveButton();
        }
        this.bindWindowClose();
        if (this.calculationLoaded()) {
          this.distributionsView.updateTableSelectBoxes();
          this.restyleCheckbox();
          if (gon.record_action !== "manage") {
            this.lockCalculation();
          }
          this.seedPrintableData();
          initialData = this.buildData();
          this.initialData = this.clearDataForSaving(initialData);
          if (gon.calculation_distribution_logic_type === "source_tip_outs") {
            $("#distribution_logic_type").prop("checked", true);
          } else {
            $("#distribution_logic_type").prop("checked", false);
          }
          this.setTotalTrColspan();
        }
        return this.disableInputs();
      },
      disableInputs: function() {
        if ($("#calculation-view").attr("data-cc-tips-input-enabled") === "false") {
          $(".employee-distribution-line .cc-in input").attr("disabled", "disabled");
        }
        if ($("#calculation-view").attr("data-cash-tips-input-enabled") === "false") {
          $(".employee-distribution-line .cash-in input").attr("disabled", "disabled");
        }
        if ($("#calculation-view").attr("data-hours-input-enabled") === "false") {
          return $(".employee-distribution-line .number-hrs-td input").attr("disabled", "disabled");
        }
      },
      setTotalTrColspan: function() {
        return $("tr.employee-buttons .buttons-td").attr("colspan", "22");
      },
      setConfig: function() {
        return this.decimals = gon.restaurant.decimals_value;
      },
      lockCalculation: function() {
        $("#calculation-form-wrapper").find(".select-2").select2("enable", false);
        $(".datapicker").prop("disabled", true);
        $("#distributions-list").find(".select-2, .select-2-ed").select2("enable", false);
        $("#distributions-list").find(".source-only-fields input, .number-hrs").prop('disabled', true);
        $("[data-action='edit-employee']").prop("disabled", true);
        $("[data-action='add-employee']").prop("disabled", true);
        $("[data-action='edit-employee-cancel']").prop("disabled", true);
        $(".percentage-point").prop('disabled', true);
        $("[name='distribution_type']").prop('disabled', true);
        $("#tip-out, #food-tip-outs").find(".touch-spin").prop('disabled', true);
        $("#tip-out, #food-tip-outs").find(".select-2").select2("enable", false);
        $("#tip-out, #food-tip-outs").find(".btn").prop("disabled", true);
        $("#tip-out [data-action='edit-employee']").prop("disabled", true);
        $("#day-pos-total").prop('disabled', true);
        $("#ag_svc_fee").prop('disabled', true);
        $("#cc_fee").prop('disabled', true);
        $("#distribution_logic_type").attr("disabled", "disabled");
        return this.locked = true;
      },
      calculationLoaded: function() {
        return $("body").hasClass("calculation") && gon.calculation_id !== void 0;
      },
      loadModules: function() {
        if (this.calculationLoaded()) {
          this.tipOutView = new Tipmetric.Controllers.TipOutViewPrototype({
            scope: this
          });
          this.foodTipOutView = new Tipmetric.Controllers.FoodTipOutViewPrototype({
            scope: this
          });
          this.distributionsView = new Tipmetric.Controllers.DistributionsViewPrototype({
            scope: this
          });
          this.positionTipSharingView = Tipmetric.Controllers.tipSharingToggleView;
          return this.overtipsView = Tipmetric.Controllers.overtipsView;
        }
      },
      restyleCheckbox: function() {
        return $('body.calculation input[type=checkbox]:not(.tgl)').iCheck({
          checkboxClass: 'icheckbox_square-green',
          radioClass: 'iradio_square-green'
        });
      },
      bindWindowClose: function() {
        return window.onbeforeunload = function(e) {
          var message;
          if ($("#save-calculation").attr("disabled") === void 0) {
            message = "Please save information before close the window.";
            if (typeof e === "undefined") {
              e = window.event;
            }
            if (e) {
              e.returnValue = message;
            }
            return message;
          }
        };
      },
      changeDistributionType: function(e) {
        $("#percentage").attr("data-distribution-type", $(e.currentTarget).val());
        this.dataChanged();
        return this.updateTotalNumbers();
      },
      updateTotalNumbers: function(e) {
        var areaTipoutNumbers, givenTipoutsTotal, multiplier, total, totalTipOutsGiven;
        areaTipoutNumbers = _.map($("#tip-out #given-tipouts-table .tip-out-line[data-tip-out-type='area'] .tip-out-percentage"), function(num) {
          return parseFloat($(num).val());
        });
        totalTipOutsGiven = _.reduce(areaTipoutNumbers, function(memo, num) {
          return memo + num;
        }) || 0;
        $(".total-tip-out-summ .percentage-point").closest("tr").attr("data-total-tip-out-given-percentage", totalTipOutsGiven);
        $(".total-tip-out-summ .percentage-point .number").text(totalTipOutsGiven);
        givenTipoutsTotal = parseFloat($(".total-tip-out-summ .percentage-point").closest("tr").attr("data-total-tip-out-given-percentage"));
        if (givenTipoutsTotal > 0) {
          multiplier = 100 / (100 - givenTipoutsTotal);
        } else {
          multiplier = 1;
        }
        _.each($("#percentage .percentage-point"), function(e) {
          return $(e).attr("percentage-final-value", parseFloat($(e).val()) * multiplier);
        });
        if (this.distribution_type() === "percents") {
          areaTipoutNumbers = _.map($("#percentage .percentage-point"), function(num) {
            return parseFloat($(num).val() || $(num).text()) || 0;
          });
          total = _.reduce(areaTipoutNumbers, function(memo, num) {
            return memo + num;
          }) || 0;
          if (total !== 100) {
            $(".percentage-total-cell").addClass("danger");
          } else {
            $(".percentage-total-cell").removeClass("danger");
          }
        } else {
          $(".percentage-total-cell").removeClass("danger");
          total = this.totalPoints();
        }
        this.calculate();
        return $(".percentage-total").text(total.toFixed(this.decimals));
      },
      clearDataForSaving: function(data) {
        delete data.totalPointsSumm;
        delete data.totalCollectedMoney;
        delete data.tipOuts;
        delete data.finalToDistribute;
        delete data.collectedMoneyDistributePercentsPart;
        delete data.initialPointsHourPrice;
        _.each(data.positionsMoney, function(v, positionName) {
          delete data.positionsMoney[positionName].positionTeamsCount;
          delete data.positionsMoney[positionName].totalHoursForPosition;
          delete data.positionsMoney[positionName].positionPoints;
          delete data.positionsMoney[positionName].positionTotalPoints;
          delete data.positionsMoney[positionName].totalPositionMoney;
          delete data.positionsMoney[positionName].totalPositionMoneyToShow;
          return _.each(data.positionsMoney[positionName].teams, function(team) {
            delete team.totalTeamMoneyToDistribute;
            delete team.totalTeamCollectedMoney;
            delete team.hourPrice;
            delete team.teamTotal;
            return _.each(team.employees, function(emp, id) {
              delete team.employees[id].totalMoneyOut;
              delete team.employees[id].employeePointsPart;
              delete team.employees[id].totalTipOutsGiven;
              delete team.employees[id].totalTipOutsReceived;
              return delete team.employees[id].finalMoneyToDistribute;
            });
          });
        });
        return data;
      },
      saveCalculationIfExist: function(e) {
        e.preventDefault();
        if (this.noSourcePositions()) {
          return Tipmetric.Controllers.notifier.alert("", "Please add at least 1 source position for this calculation.", false, false, (function(_this) {
            return function() {
              return true;
            };
          })(this));
        } else {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return false;
          }
          if (this.locked) {
            return false;
          }
          if (this.noSourcePositions()) {
            return false;
          }
          if (this.allowSaveRequest === false) {
            return false;
          }
          this.allowSaveRequest = false;
          if (Tipmetric.Controllers.calculationFormView.updatableParamsChanged()) {
            return $(".show-calculation").click();
          } else {
            return this.checkCalculationExistance({});
          }
        }
      },
      noSourcePositions: function() {
        return _.size($("#calculation-form-wrapper").find("select#source_position_ids").val()) === 0;
      },
      checkCalculationExistance: function(params) {
        if (this.locked) {
          return false;
        }
        return $.ajax({
          url: "/api/calculations/" + gon.calculation_id + "/check_existance",
          method: "post",
          data: {
            frontend_restaurant_id: gon.restaurant.id,
            new_calculation_data: this.buildData(params)
          },
          success: (function(_this) {
            return function(response) {
              return _this.saveCalculation(params);
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              $("#save-calculation").attr("disabled", "disabled");
              _this.allowSaveRequest = true;
              if (!_.isEmpty(response.responseJSON) && _.some(response.responseJSON.error_codes) && _.contains(response.responseJSON.error_codes, "tip_outs_given_to_sto_calculations")) {
                swal({
                  title: "",
                  text: response.responseJSON.error_message,
                  type: "info",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "OK",
                  showCancelButton: false
                }, function(isDelete) {
                  return console.log(isDelete);
                });
                return false;
              }
              return swal({
                title: "",
                text: "Can't change Tip Distribution Record. Please contact Manager to make changes.",
                type: "info",
                confirmButtonColor: "#28C256",
                confirmButtonText: "OK",
                showCancelButton: false
              }, function(isDelete) {
                return console.log(isDelete);
              });
            };
          })(this)
        });
      },
      saveCalculation: function(params) {
        var data, forceSave;
        if (this.locked) {
          return false;
        }
        forceSave = params.updateCalculationRequired;
        if ($("#save-calculation").attr("disabled") !== "disabled" || forceSave) {
          Tipmetric.Controllers.spinner.show();
          data = this.buildData(params);
          return $.ajax({
            url: "/api/calculations/" + data.calculationId,
            method: "patch",
            data: data,
            success: (function(_this) {
              return function(response) {
                if (!params.afterUpdateCallback) {
                  toastr["success"]("Calculation successfully saved", "Success");
                }
                _this.updateNewdistributions(response.distributions);
                _this.tipOutView.syncTipOutsWithData(response.tip_outs);
                _this.foodTipOutView.syncTipOutsWithData(response.tip_outs);
                if (_this.positionTipSharingView) {
                  _this.positionTipSharingView.syncPositionTipOutsWithData(response.position_tip_sharings);
                }
                if (_this.overtipsView) {
                  _this.overtipsView.syncPositionTipOutsWithData(response.overtips);
                }
                _this.updateGonCalculationParams(response.calculation_params);
                _this.updateTeamsCount();
                _this.tipOutView.upadteAllPositionTipoutsOptions();
                if (params.afterUpdateCallback) {
                  params.afterUpdateCallback.action(params.afterUpdateCallback.event);
                }
                _this.calculate();
                _this.dataSaved();
                Tipmetric.Controllers.spinner.hide();
                return _this.allowSaveRequest = true;
              };
            })(this),
            error: function(response) {
              toastr["error"](response.responseText, "Error");
              Tipmetric.Controllers.spinner.hide();
              return this.allowSaveRequest = true;
            }
          });
        } else {
          return this.allowSaveRequest = true;
        }
      },
      updateNewdistributions: function(distributions) {
        var distribution, distribution_id, employee_id, i, len, line, position_type_id, results;
        results = [];
        for (i = 0, len = distributions.length; i < len; i++) {
          distribution = distributions[i];
          position_type_id = distribution.position_type_id.$oid;
          employee_id = distribution.employee_id.$oid;
          distribution_id = distribution._id.$oid;
          line = $("table[data-position-type-id='" + position_type_id + "'] .employee-distribution-line[data-employee-id='" + employee_id + "']")[0];
          $(line).attr("data-emp-distribution-id", distribution_id);
          results.push($(line).attr("data-emp-distribution-status", "persisted"));
        }
        return results;
      },
      updateTeamsCount: function() {
        return $("#calculation-view").attr("data-teams-count", gon.calculation_params.teams_quantity);
      },
      updateGonCalculationParams: function(params) {
        gon.calculation_params = params;
        gon.calculation_source_positions = params['source_position_data'];
        return Tipmetric.Controllers.calculationFormView.calculationChanged();
      },
      dataSaved: function() {
        $("#save-calculation").attr("disabled", "disabled");
        return $(".employee-distribution-line").attr("data-emp-distribution-status", "persisted");
      },
      seedData: function(data) {
        _.each(data.positionsMoney, (function(_this) {
          return function(positionData, positionName) {
            var percentageTable, positionId, tableCss;
            tableCss = "[data-table-type='employee-distribution'][data-position-table=\"" + positionName + "\"]";
            positionId = _.findWhere(gon.restaurant.restaurant_position_types, {
              name: positionName
            }).id;
            if (_this.processingMethod() === "default") {
              percentageTable = $("#positions-table [data-position-type=\"" + positionName + "\"]");
              percentageTable.find(".total-position-cc-tips").text(positionData.totalPositionMoneyToShow.cc.toFixed(2));
              percentageTable.find(".total-position-cash-tips").text(positionData.totalPositionMoneyToShow.cash.toFixed(2));
              percentageTable.find(".total-position-cash-tips").text(positionData.totalPositionMoneyToShow.cash.toFixed(2));
              percentageTable.find(".total-position-ag-svc-tips").text(positionData.totalPositionMoneyToShow.agSvc.toFixed(2));
              percentageTable.find(".total-position-fee-tips").text(positionData.totalPositionMoneyToShow.fee.toFixed(2));
            }
            return _.each(positionData.teams, function(teamData, teamNumber) {
              var teamTable, totalTeamTr, totalTr;
              teamTable = $(tableCss + "[data-team='" + teamNumber + "']");
              _.each(teamData.employees, function(empData, id) {
                var tr;
                tr = $(teamTable).find(".employee-distribution-line[data-employee-id='" + id + "']");
                tr.find(".cc-out").text(empData.totalMoneyOut.cc.toFixed(2));
                tr.find(".cash-out").text(empData.totalMoneyOut.cash.toFixed(2));
                tr.find(".ag-svc-out").text(empData.totalMoneyOut.agSvc.toFixed(2));
                tr.find(".fee-out").text(empData.totalMoneyOut.fee.toFixed(2));
                tr.find(".tip-outs-given-cc").text(empData.totalTipOutsGiven.cc.toFixed(2));
                tr.find(".tip-outs-given-cash").text(empData.totalTipOutsGiven.cash.toFixed(2));
                tr.find(".tip-outs-given-ag-svc").text(empData.totalTipOutsGiven.agSvc.toFixed(2));
                tr.find(".tip-outs-given-fee").text(empData.totalTipOutsGiven.fee.toFixed(2));
                tr.find(".tip-outs-received-cc").text(empData.totalTipOutsReceived.cc.toFixed(2));
                tr.find(".tip-outs-received-cash").text(empData.totalTipOutsReceived.cash.toFixed(2));
                tr.find(".tip-outs-received-ag-svc").text(empData.totalTipOutsReceived.agSvc.toFixed(2));
                tr.find(".tip-outs-received-fee").text(empData.totalTipOutsReceived.fee.toFixed(2));
                tr.find(".final-tips-distributed-cc").text(empData.finalMoneyToDistribute.cc.toFixed(2));
                tr.find(".final-tips-distributed-cash").text(empData.finalMoneyToDistribute.cash.toFixed(2));
                tr.find(".final-distributed-ag-svc").text(empData.finalMoneyToDistribute.agSvc.toFixed(2));
                tr.find(".final-distributed-fee").text(empData.finalMoneyToDistribute.fee.toFixed(2));
                tr.find(".hourly-rate-column").text((empData.hourly_rate.toFixed(2)) + "/hr");
                if (empData.finalMoneyToDistribute.cc_cash_minus_cash_collected) {
                  tr.find(".final-tips-column").text(empData.finalMoneyToDistribute.cc_cash_minus_cash_collected.toFixed(2));
                }
                if (empData.finalMoneyToDistributePlusOvertips) {
                  tr.find(".final-with-overtips-cc").text((empData.finalMoneyToDistributePlusOvertips.cc || 0).toFixed(2));
                  return tr.find(".final-with-overtips-cash").text((empData.finalMoneyToDistributePlusOvertips.cash || 0).toFixed(2));
                } else {
                  tr.find(".final-with-overtips-cc").text("0.00");
                  return tr.find(".final-with-overtips-cash").text("0.00");
                }
              });
              if (teamData.employees.length === 0) {
                return true;
              }
              totalTeamTr = teamTable.find(".info");
              totalTeamTr.find(".total-hours h4").text(teamData.teamTotal.totalPositionHoursWorked.toFixed(2));
              totalTeamTr.find(".total-sales-cc h4").text(teamData.teamTotal.totalTeamSales.cc.toFixed(2));
              totalTeamTr.find(".total-sales-cash h4").text(teamData.teamTotal.totalTeamSales.cash.toFixed(2));
              totalTeamTr.find(".total-food-sales h4").text(teamData.teamTotal.totalTeamFoodSales.food.toFixed(2));
              totalTeamTr.find(".total-beverage-sales h4").text(teamData.teamTotal.totalTeamFoodSales.beverage.toFixed(2));
              totalTeamTr.find(".total-cc-out h4").text(teamData.teamTotal.ccToDistribute.toFixed(2));
              totalTeamTr.find(".total-cash-out h4").text(teamData.teamTotal.cashToDistribute.toFixed(2));
              totalTeamTr.find(".total-ag-svc-out h4").text(teamData.teamTotal.agSvcToDistribute.toFixed(2));
              totalTeamTr.find(".total-fee-out h4").text(teamData.teamTotal.feeToDistribute.toFixed(2));
              totalTeamTr.find(".total-tip-outs-given-cc h4").text(teamData.teamTotal.totalTeamTOGivenCC.toFixed(2));
              totalTeamTr.find(".total-tip-outs-given-cash h4").text(teamData.teamTotal.totalTeamTOGivenCash.toFixed(2));
              totalTeamTr.find(".total-tip-outs-given-ag-svc h4").text(teamData.teamTotal.totalTeamTOGivenAgSvc.toFixed(2));
              totalTeamTr.find(".total-tip-outs-given-fee h4").text(teamData.teamTotal.totalTeamTOGivenFee.toFixed(2));
              totalTeamTr.find(".total-tip-outs-received-cc h4").text(teamData.teamTotal.totalTeamTOReceivedCC.toFixed(2));
              totalTeamTr.find(".total-tip-outs-received-cash h4").text(teamData.teamTotal.totalTeamTOReceivedCash.toFixed(2));
              totalTeamTr.find(".total-tip-outs-received-ag-svc h4").text(teamData.teamTotal.totalTeamTOReceivedAgSvc.toFixed(2));
              totalTeamTr.find(".total-tip-outs-received-fee h4").text(teamData.teamTotal.totalTeamTOReceivedFee.toFixed(2));
              totalTeamTr.find(".total-tips-distributed-cc h4").text(teamData.teamTotal.finalTeamTODistributeCC.toFixed(3));
              totalTeamTr.find(".total-tips-distributed-cash h4").text(teamData.teamTotal.finalTeamTODistributeCash.toFixed(3));
              totalTeamTr.find(".total-tips-distributed-ag-svc h4").text(teamData.teamTotal.finalTeamTODistributeAgSvc.toFixed(3));
              totalTeamTr.find(".total-tips-distributed-fee h4").text(teamData.teamTotal.finalTeamTODistributeFee.toFixed(3));
              if (teamData.teamTotal.finalMoneyToDistributePlusOvertips) {
                totalTeamTr.find(".total-overtips-cc-column h4").text(teamData.teamTotal.finalMoneyToDistributePlusOvertips.cc.toFixed(3));
                totalTeamTr.find(".total-overtips-cash-column h4").text(teamData.teamTotal.finalMoneyToDistributePlusOvertips.cash.toFixed(3));
              } else {
                totalTeamTr.find(".total-overtips-cc-column h4").text("0.00");
                totalTeamTr.find(".total-overtips-cash-column h4").text("0.00");
              }
              if (teamData.teamTotal.finalTeamTODistributeCCMinusCash) {
                totalTeamTr.find(".total-final-tips-column h4").text(teamData.teamTotal.finalTeamTODistributeCCMinusCash.toFixed(3));
              } else {
                totalTeamTr.find(".total-final-tips-column h4").text("0.00");
              }
              if (positionData.positionTypeIsASource === "true") {
                totalTeamTr.find(".total-cc-in h4").text(teamData.totalTeamCollectedMoney.cc.toFixed(2));
                totalTeamTr.find(".total-cash-in h4").text(teamData.totalTeamCollectedMoney.cash.toFixed(2));
                totalTeamTr.find(".total-ag-svc h4").text(teamData.totalTeamCollectedMoney.agSvc.toFixed(2));
                totalTeamTr.find(".total-fee h4").text(teamData.totalTeamCollectedMoney.fee.toFixed(2));
              }
              totalTr = teamTable.find(".total-position-tips");
              totalTr.find(".total-position-cc-tips").text(teamData.totalTeamMoneyToDistribute.cc.toFixed(2));
              totalTr.find(".total-position-cash-tips").text(teamData.totalTeamMoneyToDistribute.cash.toFixed(2));
              totalTr.find(".total-position-ag-svc-tips").text(teamData.totalTeamMoneyToDistribute.agSvc.toFixed(2));
              return totalTr.find(".total-position-fee-tips").text(teamData.totalTeamMoneyToDistribute.fee.toFixed(2));
            });
          };
        })(this));
        $("#total-block .hours").text(data.totalHoursWorked);
        $("#total-block .sales-cc").text(data.totalSales.cc.toFixed(2));
        $("#total-block .sales-cash").text(data.totalSales.cash.toFixed(2));
        $("#total-block .food-sales").text(data.totalFoodSales.food.toFixed(2));
        $("#total-block .beverage-sales").text(data.totalFoodSales.beverage.toFixed(2));
        $("#total-block .fee-cc").text(data.cc_fee_summ);
        $("#total-block .fee-ag-svc").text(data.ag_svc_fee_summ);
        $("#positions-table .total-tip-out-cc-tips").text(data.tipOuts.given.cc.toFixed(2));
        $("#positions-table .total-tip-out-cash-tips").text(data.tipOuts.given.cash.toFixed(2));
        $("#positions-table .total-tip-out-ag-svc-tips").text(data.tipOuts.given.agSvc.toFixed(2));
        $("#positions-table .total-tip-out-fee-tips").text(data.tipOuts.given.fee.toFixed(2));
        $("#total-block .cc-out-total, .tips-total .cc-out-total").text(data.totalCollectedMoney.cc_original.toFixed(2));
        $("#total-block .cash-out-total, .tips-total .cash-out-total").text(data.totalCollectedMoney.cash_original.toFixed(2));
        $("#total-block .ag-svc-out-total, .tips-total .ag-svc-out-total").text(data.totalCollectedMoney.ag_svc_original.toFixed(2));
        $("#total-block .fee-out-total, .tips-total .fee-out-total").text(data.totalCollectedMoney.fee_original.toFixed(2));
        $(".total .total-collected-cc-tips").text(data.totalCollectedMoney.cc.toFixed(2));
        $(".total .total-collected-cash-tips").text(data.totalCollectedMoney.cash.toFixed(2));
        $(".total .total-collected-ag-svc-tips").text(data.totalCollectedMoney.agSvc.toFixed(2));
        $(".total .total-collected-fee-tips").text(data.totalCollectedMoney.fee.toFixed(2));
        $("#total-block .food-tip-outs-given .food").text(data.totalFoodTipOutsSeparateSumms.food.toFixed(2));
        $("#total-block .food-tip-outs-given .beverage").text(data.totalFoodTipOutsSeparateSumms.beverage.toFixed(2));
        $("#total-block .food-tip-outs-given .total").text(data.totalFoodTipOutsSeparateSumms.total.toFixed(2));
        $("#total-block .food-tip-outs-given .food-tip-outs-given-total").text(data.totalFoodTipOutsSumm.toFixed(2));
        $("#total-block .tip-outs-given-cc").text(data.tipOuts.given.cc.toFixed(2));
        $("#total-block .tip-outs-given-cash").text(data.tipOuts.given.cash.toFixed(2));
        $("#total-block .tip-outs-given-ag-svc").text(data.tipOuts.given.agSvc.toFixed(2));
        $("#total-block .tip-outs-given-fee").text(data.tipOuts.given.fee.toFixed(2));
        $("#total-block .tip-outs-received-cc").text(data.tipOuts.received.cc.toFixed(2));
        $("#total-block .tip-outs-received-cash").text(data.tipOuts.received.cash.toFixed(2));
        $("#total-block .tip-outs-received-ag-svc").text(data.tipOuts.received.agSvc.toFixed(2));
        $("#total-block .tip-outs-received-fee").text(data.tipOuts.received.fee.toFixed(2));
        $("#total-block .tips-distributed-cc").text(data.finalToDistribute.cc.toFixed(2));
        $("#total-block .tips-distributed-cash").text(data.finalToDistribute.cash.toFixed(2));
        $("#total-block .tips-distributed-ag-svc").text(data.finalToDistribute.agSvc.toFixed(2));
        $("#total-block .tips-distributed-fee").text(data.finalToDistribute.fee.toFixed(2));
        $("#food-tip-outs #given-tipouts-table .total .cc_summ").text(data.totalFoodTipOutsSumm.toFixed(2));
        $("#tip-out #given-tipouts-table .total .cc_summ").text((data.tipOuts.given.cc + data.totalPositionTipOuts.cc).toFixed(2));
        $("#tip-out #given-tipouts-table .total .cash_summ").text((data.tipOuts.given.cash + data.totalPositionTipOuts.cash).toFixed(2));
        $("#tip-out #given-tipouts-table .total .ag_svc_summ").text((data.tipOuts.given.agSvc + data.totalPositionTipOuts.agSvc).toFixed(2));
        $("#tip-out #given-tipouts-table .total .fee_summ").text((data.tipOuts.given.fee + data.totalPositionTipOuts.fee).toFixed(2));
        $(".position-tip-outs-cc").text(data.totalPositionTipOuts.cc.toFixed(2));
        $(".position-tip-outs-cash").text(data.totalPositionTipOuts.cash.toFixed(2));
        $(".position-tip-outs-ag-svc").text(data.totalPositionTipOuts.agSvc.toFixed(2));
        return $(".position-tip-outs-fee").text(data.totalPositionTipOuts.fee.toFixed(2));
      },
      calculate: function(params) {
        var data;
        data = this.buildData();
        this.seedData(data);
        this.dataChanged();
        this.varianceCheck();
        return this.checkBlankHoursFields();
      },
      checkBlankHoursFields: function() {
        var field, i, len, ref, results;
        ref = $(".number-hrs");
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          field = ref[i];
          if ($(field).val() === "" || parseFloat($(field).val()) === 0) {
            results.push($(field).addClass("blank-hours"));
          } else {
            results.push($(field).removeClass("blank-hours"));
          }
        }
        return results;
      },
      totalPoints: function() {
        var totalPoints, totalPointsMap;
        totalPointsMap = _.map($("[data-table-type='employee-distribution']"), (function(_this) {
          return function(empDistrTable) {
            var hoursMap, positionPoints, positionTotalPoints, positionType, totalHoursForPosition;
            positionType = $(empDistrTable).attr("data-position-table");
            hoursMap = _.map($(empDistrTable).find(".employee-distribution-line .number-hrs"), function(num) {
              return parseFloat($(num).val());
            });
            totalHoursForPosition = _.reduce(hoursMap, function(sum, num) {
              return sum + num;
            }) || 0;
            positionPoints = ($("#positions-table input[data-percentage-for=\"" + positionType + "\"]").val()) || 0;
            positionTotalPoints = positionPoints * totalHoursForPosition;
            return positionTotalPoints;
          };
        })(this));
        totalPoints = _.reduce(totalPointsMap, function(sum, num) {
          return sum + num;
        }) || 0;
        totalPoints = totalPoints;
        return totalPoints;
      },
      buildPosTotals: function() {
        return {
          calculationPosTotal: parseFloat($("#calculation-pos-total").val()) || 0,
          dayPosTotal: parseFloat($("#day-pos-total").val()) || 0
        };
      },
      totalHoursWorked: function() {
        var totalHoursWorked;
        totalHoursWorked = _.map($(".employee-distribution-line .number-hrs"), function(input) {
          return parseFloat($(input).val());
        });
        totalHoursWorked = _.reduce(totalHoursWorked, function(sum, num) {
          return sum + num;
        }) || 0;
        return totalHoursWorked = totalHoursWorked.toFixed(2);
      },
      totalSales: function() {
        var totalSalesCC, totalSalesCash;
        totalSalesCC = _.map($(".employee-distribution-line .sales-cc input"), function(input) {
          return parseFloat($(input).val());
        });
        totalSalesCash = _.map($(".employee-distribution-line .sales-cash input"), function(input) {
          return parseFloat($(input).val());
        });
        totalSalesCC = _.reduce(totalSalesCC, function(sum, num) {
          return sum + num;
        }) || 0;
        totalSalesCash = _.reduce(totalSalesCash, function(sum, num) {
          return sum + num;
        }) || 0;
        totalSalesCC = totalSalesCC;
        totalSalesCash = totalSalesCash;
        return {
          cc: totalSalesCC,
          cash: totalSalesCash
        };
      },
      totalFoodSales: function() {
        var totalBeverageSales, totalFoodSales;
        totalFoodSales = _.map($(".employee-distribution-line .food-sales input"), function(input) {
          return parseFloat($(input).val());
        });
        totalBeverageSales = _.map($(".employee-distribution-line .beverage-sales input"), function(input) {
          return parseFloat($(input).val());
        });
        totalFoodSales = _.reduce(totalFoodSales, function(sum, num) {
          return sum + num;
        }) || 0;
        totalBeverageSales = _.reduce(totalBeverageSales, function(sum, num) {
          return sum + num;
        }) || 0;
        totalFoodSales = totalFoodSales;
        totalBeverageSales = totalBeverageSales;
        return {
          food: totalFoodSales,
          beverage: totalBeverageSales,
          total: totalFoodSales + totalBeverageSales
        };
      },
      buildTotalCollectedMoneyByPositions: function() {
        var collectedMoney;
        collectedMoney = {
          positions: {}
        };
        _.each($("#distributions-list .position-block"), (function(_this) {
          return function(positionBlock) {
            var agSvcPositionMap, agSvcPositionTotalSum, beverageSalesPositionMap, beverageSalesPositionTotalSum, cashPositionMap, cashPositionTotalSum, ccPositionMap, ccPositionTotalSum, feePositionMap, feePositionTotalSum, foodSalesPositionMap, foodSalesPositionTotalSum, percentToDistributeForPosition, positionId, salesCCPositionMap, salesCCPositionTotalSum, salesCashPositionMap, salesCashPositionTotalSum;
            positionId = $(positionBlock).attr("data-position-block-id");
            collectedMoney.positions[positionId] = {};
            percentToDistributeForPosition = _this.percentToDistributeForPosition(positionId) / 100;
            ccPositionMap = _.map($(positionBlock).find("[data-table-type='employee-distribution'] .employee-distribution-line .cc-in input"), function(num) {
              return parseFloat($(num).val()) || 0;
            });
            ccPositionTotalSum = _.reduce(ccPositionMap, function(sum, num) {
              return sum + num;
            }) || 0;
            collectedMoney.positions[positionId]['cc'] = ccPositionTotalSum * (100 - _this.totalTipOutsPercentsByPosition(positionId) - _this.buildCCFee()) / 100;
            collectedMoney.positions[positionId]['cc_original'] = ccPositionTotalSum;
            cashPositionMap = _.map($(positionBlock).find("[data-table-type='employee-distribution'] .employee-distribution-line .cash-in input"), function(num) {
              return parseFloat($(num).val()) || 0;
            });
            cashPositionTotalSum = _.reduce(cashPositionMap, function(sum, num) {
              return sum + num;
            }) || 0;
            collectedMoney.positions[positionId]['cash'] = cashPositionTotalSum * (100 - _this.totalTipOutsPercentsByPosition(positionId)) / 100;
            collectedMoney.positions[positionId]['cash_original'] = cashPositionTotalSum;
            collectedMoney.positions[positionId]['cc_cash'] = collectedMoney.positions[positionId]['cc'] + collectedMoney.positions[positionId]['cash'];
            collectedMoney.positions[positionId]['cc_cash_original'] = ccPositionTotalSum + cashPositionTotalSum;
            agSvcPositionMap = _.map($(positionBlock).find("[data-table-type='employee-distribution'] .employee-distribution-line .ag-svc-in input"), function(num) {
              return parseFloat($(num).val()) || 0;
            });
            agSvcPositionTotalSum = _.reduce(agSvcPositionMap, function(sum, num) {
              return sum + num;
            }) || 0;
            collectedMoney.positions[positionId]['agSvc'] = agSvcPositionTotalSum * (100 - _this.totalTipOutsPercentsByPosition(positionId) - _this.buildAgSvcFee()) / 100;
            collectedMoney.positions[positionId]['agSvc_original'] = agSvcPositionTotalSum;
            feePositionMap = _.map($(positionBlock).find("[data-table-type='employee-distribution'] .employee-distribution-line .fee-in input"), function(num) {
              return parseFloat($(num).val()) || 0;
            });
            feePositionTotalSum = _.reduce(feePositionMap, function(sum, num) {
              return sum + num;
            }) || 0;
            collectedMoney.positions[positionId]['fee'] = feePositionTotalSum * (100 - _this.totalTipOutsPercentsByPosition(positionId)) / 100;
            collectedMoney.positions[positionId]['fee_original'] = feePositionTotalSum;
            salesCCPositionMap = _.map($(positionBlock).find("[data-table-type='employee-distribution'] .employee-distribution-line .sales-cc input"), function(num) {
              return parseFloat($(num).val()) || 0;
            });
            salesCCPositionTotalSum = _.reduce(salesCCPositionMap, function(sum, num) {
              return sum + num;
            }) || 0;
            collectedMoney.positions[positionId]['salesCC'] = salesCCPositionTotalSum * (100 - _this.totalTipOutsPercentsByPosition(positionId)) / 100;
            salesCashPositionMap = _.map($(positionBlock).find("[data-table-type='employee-distribution'] .employee-distribution-line .sales-cash input"), function(num) {
              return parseFloat($(num).val()) || 0;
            });
            salesCashPositionTotalSum = _.reduce(salesCashPositionMap, function(sum, num) {
              return sum + num;
            }) || 0;
            collectedMoney.positions[positionId]['salesCash'] = salesCashPositionTotalSum * (100 - _this.totalTipOutsPercentsByPosition(positionId)) / 100;
            foodSalesPositionMap = _.map($(positionBlock).find("[data-table-type='employee-distribution'] .employee-distribution-line .food-sales input"), function(num) {
              return parseFloat($(num).val()) || 0;
            });
            foodSalesPositionTotalSum = _.reduce(foodSalesPositionMap, function(sum, num) {
              return sum + num;
            }) || 0;
            collectedMoney.positions[positionId]['foodSales'] = foodSalesPositionTotalSum;
            beverageSalesPositionMap = _.map($(positionBlock).find("[data-table-type='employee-distribution'] .employee-distribution-line .beverage-sales input"), function(num) {
              return parseFloat($(num).val()) || 0;
            });
            beverageSalesPositionTotalSum = _.reduce(beverageSalesPositionMap, function(sum, num) {
              return sum + num;
            }) || 0;
            collectedMoney.positions[positionId]['beverageSales'] = beverageSalesPositionTotalSum;
            return collectedMoney.positions[positionId]['foodAndBeverageSalesSumm'] = foodSalesPositionTotalSum + beverageSalesPositionTotalSum;
          };
        })(this));
        return collectedMoney.positions;
      },
      buildTotalCollectedMoney: function(data) {
        var agSvcMap, agSvcOriginalMap, agSvcTotalSum, agSvcTotalSumOriginal, cashMap, cashOriginalMap, cashTotalSum, cashTotalSumOriginal, ccMap, ccOriginalMap, ccTotalSum, ccTotalSumOriginal, feeMap, feeOriginalMap, feeTotalSum, feeTotalSumOriginal, positionMoney;
        positionMoney = data.totalCollectedMoneyByPositions;
        ccMap = _.map(positionMoney, function(position) {
          return parseFloat(position.cc);
        });
        ccTotalSum = _.reduce(ccMap, function(sum, num) {
          return sum + num;
        }) || 0;
        ccOriginalMap = _.map(positionMoney, function(position) {
          return parseFloat(position.cc_original);
        });
        ccTotalSumOriginal = _.reduce(ccOriginalMap, function(sum, num) {
          return sum + num;
        }) || 0;
        agSvcMap = _.map(positionMoney, function(position) {
          return parseFloat(position.agSvc);
        });
        agSvcTotalSum = _.reduce(agSvcMap, function(sum, num) {
          return sum + num;
        }) || 0;
        agSvcOriginalMap = _.map(positionMoney, function(position) {
          return parseFloat(position.agSvc_original);
        });
        agSvcTotalSumOriginal = _.reduce(agSvcOriginalMap, function(sum, num) {
          return sum + num;
        }) || 0;
        cashMap = _.map(positionMoney, function(position) {
          return parseFloat(position.cash);
        });
        cashTotalSum = _.reduce(cashMap, function(sum, num) {
          return sum + num;
        }) || 0;
        cashOriginalMap = _.map(positionMoney, function(position) {
          return parseFloat(position.cash_original);
        });
        cashTotalSumOriginal = _.reduce(cashOriginalMap, function(sum, num) {
          return sum + num;
        }) || 0;
        feeMap = _.map(positionMoney, function(position) {
          return parseFloat(position.fee);
        });
        feeTotalSum = _.reduce(feeMap, function(sum, num) {
          return sum + num;
        }) || 0;
        feeOriginalMap = _.map(positionMoney, function(position) {
          return parseFloat(position.fee_original);
        });
        feeTotalSumOriginal = _.reduce(feeOriginalMap, function(sum, num) {
          return sum + num;
        }) || 0;
        return {
          cc: ccTotalSum,
          cc_original: ccTotalSumOriginal,
          cash: cashTotalSum,
          cash_original: cashTotalSumOriginal,
          agSvc: agSvcTotalSum,
          ag_svc_original: agSvcTotalSumOriginal,
          fee: feeTotalSum,
          fee_original: feeTotalSumOriginal
        };
      },
      tipOutBasedOnOriginal: function() {
        return $("#calculation-view").attr("data-position-tip-outs-enabled") === 'true';
      },
      sourceOfTips: function(e) {
        switch (e) {
          case 'cc':
            return 'cc_original';
          case 'cash':
            return 'cash_original';
          case 'agSvc':
            return 'ag_svc_original';
          case 'fee':
            return 'fee_original';
        }
      },
      buildFoodTipOuts: function(data) {
        var tipOuts;
        tipOuts = [];
        _.each($("#food-tip-outs #given-tipouts-table tbody tr.tip-out-line[data-tip-out-type='food']"), (function(_this) {
          return function(tipOutLine) {
            var amount, percent, subType;
            subType = $(tipOutLine).attr("data-tip-out-food-subtype");
            percent = parseFloat($(tipOutLine).find("input.tip-out-percentage").val() || 0) / 100;
            amount = data.totalFoodSales[subType] * percent;
            $(tipOutLine).find(".cc_summ").text(amount.toFixed(2));
            return tipOuts.push({
              tipOutType: "food",
              subType: subType,
              percent: percent,
              amount: amount
            });
          };
        })(this));
        return tipOuts;
      },
      buildTotalFoodTipOutsSumm: function(data) {
        var givenFoodTipOuts;
        givenFoodTipOuts = _.map(data.foodTipOuts, function(to) {
          return to.amount;
        });
        givenFoodTipOuts = _.reduce(givenFoodTipOuts, function(sum, num) {
          return sum + num;
        }) || 0;
        return givenFoodTipOuts;
      },
      buildTotalFoodTipOutPercents: function(data) {
        var beverageTipOutsPercents, foodTipOutsPercents, totalTipOutsPercents;
        foodTipOutsPercents = _.filter(data.foodTipOuts, function(to) {
          return to.subType === "food";
        });
        foodTipOutsPercents = _.map(foodTipOutsPercents, function(to) {
          return to.percent * 100;
        });
        foodTipOutsPercents = _.reduce(foodTipOutsPercents, function(sum, num) {
          return sum + num;
        }) || 0;
        beverageTipOutsPercents = _.filter(data.foodTipOuts, function(to) {
          return to.subType === "beverage";
        });
        beverageTipOutsPercents = _.map(beverageTipOutsPercents, function(to) {
          return to.percent * 100;
        });
        beverageTipOutsPercents = _.reduce(beverageTipOutsPercents, function(sum, num) {
          return sum + num;
        }) || 0;
        totalTipOutsPercents = _.filter(data.foodTipOuts, function(to) {
          return to.subType === "total";
        });
        totalTipOutsPercents = _.map(totalTipOutsPercents, function(to) {
          return to.percent * 100;
        });
        totalTipOutsPercents = _.reduce(totalTipOutsPercents, function(sum, num) {
          return sum + num;
        }) || 0;
        return {
          food: foodTipOutsPercents,
          beverage: beverageTipOutsPercents,
          total: totalTipOutsPercents
        };
      },
      buildTotalFoodTipOuts: function(data) {
        var beverageTipOuts, foodTipOuts, totalTipOuts;
        foodTipOuts = _.filter(this.buildFoodTipOuts(data), function(to) {
          return to.subType === "food";
        });
        foodTipOuts = _.map(foodTipOuts, function(to) {
          return to.amount;
        });
        foodTipOuts = _.reduce(foodTipOuts, function(sum, num) {
          return sum + num;
        }) || 0;
        beverageTipOuts = _.filter(this.buildFoodTipOuts(data), function(to) {
          return to.subType === "beverage";
        });
        beverageTipOuts = _.map(beverageTipOuts, function(to) {
          return to.amount;
        });
        beverageTipOuts = _.reduce(beverageTipOuts, function(sum, num) {
          return sum + num;
        }) || 0;
        totalTipOuts = _.filter(this.buildFoodTipOuts(data), function(to) {
          return to.subType === "total";
        });
        totalTipOuts = _.map(totalTipOuts, function(to) {
          return to.amount;
        });
        totalTipOuts = _.reduce(totalTipOuts, function(sum, num) {
          return sum + num;
        }) || 0;
        return {
          food: foodTipOuts,
          beverage: beverageTipOuts,
          total: totalTipOuts
        };
      },
      calculateTotalTipOuts: function(data) {
        return {
          cc: data.tipOuts.given.cc + data.totalFoodTipOutsSumm,
          cash: data.tipOuts.given.cash,
          agSvc: data.tipOuts.given.agSvc,
          fee: data.tipOuts.given.fee
        };
      },
      buildAreaTipOuts: function(data) {
        var givenTipOutsPercents, givenTipOutsTotalMoneyAgSvc, givenTipOutsTotalMoneyCC, givenTipOutsTotalMoneyCash, givenTipOutsTotalMoneyFee, givenTipOutsTotalPercents, givenTipOutsTotalPercentsCC, receivedTipOutsAgSvc, receivedTipOutsCash, receivedTipOutsCc, receivedTipOutsFee, receivedTipOutsTotalAgSvc, receivedTipOutsTotalCash, receivedTipOutsTotalCc, receivedTipOutsTotalFee;
        givenTipOutsPercents = _.map($("#tip-out #given-tipouts-table tbody tr.tip-out-line[data-tip-out-type='area'] input.tip-out-percentage"), function(num) {
          return parseFloat($(num).val());
        });
        givenTipOutsTotalPercents = _.reduce(givenTipOutsPercents, function(sum, num) {
          return sum + num;
        }) || 0;
        if (gon.restaurant.food_tip_outs_enabled) {
          givenTipOutsTotalPercentsCC = givenTipOutsTotalPercents + data.totalFoodTipOutsSumm * 100 / data.totalCollectedMoney["cc"];
        } else {
          givenTipOutsTotalPercentsCC = givenTipOutsTotalPercents;
        }
        if (this.tipOutBasedOnOriginal()) {
          givenTipOutsTotalMoneyCC = givenTipOutsTotalPercents * this.data.totalCollectedMoney[this.sourceOfTips("cc")] / 100 || 0;
          givenTipOutsTotalMoneyCash = givenTipOutsTotalPercents * this.data.totalCollectedMoney[this.sourceOfTips("cash")] / 100 || 0;
          givenTipOutsTotalMoneyAgSvc = givenTipOutsTotalPercents * this.data.totalCollectedMoney[this.sourceOfTips("agSvc")] / 100 || 0;
          givenTipOutsTotalMoneyFee = givenTipOutsTotalPercents * this.data.totalCollectedMoney[this.sourceOfTips("fee")] / 100 || 0;
        } else {
          givenTipOutsTotalMoneyCC = givenTipOutsTotalPercents * this.data.totalCollectedMoney.cc / 100 || 0;
          givenTipOutsTotalMoneyCash = givenTipOutsTotalPercents * this.data.totalCollectedMoney.cash / 100 || 0;
          givenTipOutsTotalMoneyAgSvc = givenTipOutsTotalPercents * this.data.totalCollectedMoney.agSvc / 100 || 0;
          givenTipOutsTotalMoneyFee = givenTipOutsTotalPercents * this.data.totalCollectedMoney.fee / 100 || 0;
        }
        _.each($("#tip-out #given-tipouts-table tbody tr.tip-out-line[data-tip-out-type='area']"), (function(_this) {
          return function(tipOutLine) {
            var agSvcSumm, cashSumm, ccSumm, feeSumm, percent;
            percent = parseFloat($(tipOutLine).find("input.tip-out-percentage").val() || 0) / 100;
            if (_this.tipOutBasedOnOriginal()) {
              ccSumm = data.totalCollectedMoney[_this.sourceOfTips('cc')] * percent;
              cashSumm = data.totalCollectedMoney[_this.sourceOfTips('cash')] * percent;
              agSvcSumm = data.totalCollectedMoney[_this.sourceOfTips('agSvc')] * percent;
              feeSumm = data.totalCollectedMoney[_this.sourceOfTips('fee')] * percent;
            } else {
              ccSumm = data.totalCollectedMoney.cc * percent;
              cashSumm = data.totalCollectedMoney.cash * percent;
              agSvcSumm = data.totalCollectedMoney.agSvc * percent;
              feeSumm = data.totalCollectedMoney.fee * percent;
            }
            $(tipOutLine).find(".cc_summ").text(ccSumm.toFixed(2));
            $(tipOutLine).find(".cash_summ").text(cashSumm.toFixed(2));
            $(tipOutLine).find(".ag_svc_summ").text(agSvcSumm.toFixed(2));
            return $(tipOutLine).find(".fee_summ").text(feeSumm.toFixed(2));
          };
        })(this));
        receivedTipOutsCc = _.map($("#tip-out, #food-tip-outs").find("#received-tipouts-table tbody tr .cc-received"), function(num) {
          return parseFloat($(num).text());
        });
        receivedTipOutsTotalCc = _.reduce(receivedTipOutsCc, function(sum, num) {
          return sum + num;
        }) || 0;
        receivedTipOutsTotalCc = parseFloat(receivedTipOutsTotalCc) || 0;
        receivedTipOutsCash = _.map($("#tip-out, #food-tip-outs").find("#received-tipouts-table tbody tr .cash-received"), function(num) {
          return parseFloat($(num).text());
        });
        receivedTipOutsTotalCash = _.reduce(receivedTipOutsCash, function(sum, num) {
          return sum + num;
        }) || 0;
        receivedTipOutsTotalCash = parseFloat(receivedTipOutsTotalCash) || 0;
        receivedTipOutsAgSvc = _.map($("#tip-out, #food-tip-outs").find("#received-tipouts-table tbody tr .ag-svc-received"), function(num) {
          return parseFloat($(num).text());
        });
        receivedTipOutsTotalAgSvc = _.reduce(receivedTipOutsAgSvc, function(sum, num) {
          return sum + num;
        }) || 0;
        receivedTipOutsTotalAgSvc = parseFloat(receivedTipOutsTotalAgSvc) || 0;
        receivedTipOutsFee = _.map($("#tip-out, #food-tip-outs").find("#received-tipouts-table tbody tr .fee-received"), function(num) {
          return parseFloat($(num).text());
        });
        receivedTipOutsTotalFee = _.reduce(receivedTipOutsFee, function(sum, num) {
          return sum + num;
        }) || 0;
        receivedTipOutsTotalFee = parseFloat(receivedTipOutsTotalFee) || 0;
        return {
          givenTipOutsTotalPercents: givenTipOutsTotalPercents,
          givenTipOutsTotalPercentsCC: givenTipOutsTotalPercentsCC,
          given: {
            cc: givenTipOutsTotalMoneyCC,
            cash: givenTipOutsTotalMoneyCash,
            agSvc: givenTipOutsTotalMoneyAgSvc,
            fee: givenTipOutsTotalMoneyFee
          },
          received: {
            cc: receivedTipOutsTotalCc,
            cash: receivedTipOutsTotalCash,
            agSvc: receivedTipOutsTotalAgSvc,
            fee: receivedTipOutsTotalFee
          }
        };
      },
      buildPositionTipOuts: function(data) {
        return _.each($("#tip-out #given-tipouts-table tbody tr.tip-out-line[data-tip-out-type='position']"), (function(_this) {
          return function(tipOutLine) {
            var positionId, positionMoney, tipOutPercentage, tips;
            positionId = $(tipOutLine).attr("data-tip-out-position-id");
            positionMoney = data.totalCollectedMoneyByPositions[positionId];
            tipOutPercentage = $(tipOutLine).find(".tip-out-percentage").val();
            tips = {
              cc: tipOutPercentage * positionMoney.cc_original / 100,
              cash: tipOutPercentage * positionMoney.cash_original / 100,
              agSvc: tipOutPercentage * positionMoney.agSvc_original / 100,
              fee: tipOutPercentage * positionMoney.fee_original / 100
            };
            $(tipOutLine).find(".cc_summ").text(tips.cc.toFixed(2));
            $(tipOutLine).find(".cash_summ").text(tips.cash.toFixed(2));
            $(tipOutLine).find(".ag_svc_summ").text(tips.agSvc.toFixed(2));
            return $(tipOutLine).find(".fee_summ").text(tips.fee.toFixed(2));
          };
        })(this));
      },
      buildTotalPositionTipOuts: function(data) {
        var positions, tipOutPositions, tips, totalValueAgSvc, totalValueCC, totalValueCash, totalValueFee, uniqTipOutPositions;
        tipOutPositions = _.map($("#tip-out #given-tipouts-table tbody tr.tip-out-line[data-tip-out-type='position']"), (function(_this) {
          return function(tipOutLine) {
            return $(tipOutLine).attr("data-tip-out-position-id");
          };
        })(this));
        uniqTipOutPositions = _.uniq(tipOutPositions);
        positions = {};
        _.each(uniqTipOutPositions, function(positionId) {
          var mapPercents, tips, totalPercents;
          positions[positionId] = {};
          mapPercents = _.map($("#tip-out #given-tipouts-table tbody tr.tip-out-line[data-tip-out-position-id='" + positionId + "']"), (function(_this) {
            return function(tipOutLine) {
              return parseFloat($(tipOutLine).find(".tip-out-percentage").val());
            };
          })(this));
          totalPercents = _.reduce(mapPercents, function(sum, num) {
            return sum + num;
          }) || 0;
          tips = {
            cc: totalPercents * data.totalCollectedMoneyByPositions[positionId].cc_original / 100,
            cash: totalPercents * data.totalCollectedMoneyByPositions[positionId].cash_original / 100,
            agSvc: totalPercents * data.totalCollectedMoneyByPositions[positionId].agSvc_original / 100,
            fee: totalPercents * data.totalCollectedMoneyByPositions[positionId].fee_original / 100
          };
          return positions[positionId] = tips;
        });
        totalValueCC = _.reduce(_.map(positions, (function(_this) {
          return function(position) {
            return parseFloat(position.cc);
          };
        })(this)), function(sum, num) {
          return sum + num;
        }) || 0;
        totalValueCash = _.reduce(_.map(positions, (function(_this) {
          return function(position) {
            return parseFloat(position.cash);
          };
        })(this)), function(sum, num) {
          return sum + num;
        }) || 0;
        totalValueAgSvc = _.reduce(_.map(positions, (function(_this) {
          return function(position) {
            return parseFloat(position.agSvc);
          };
        })(this)), function(sum, num) {
          return sum + num;
        }) || 0;
        totalValueFee = _.reduce(_.map(positions, (function(_this) {
          return function(position) {
            return parseFloat(position.fee);
          };
        })(this)), function(sum, num) {
          return sum + num;
        }) || 0;
        return tips = {
          cc: totalValueCC,
          cash: totalValueCash,
          agSvc: totalValueAgSvc,
          fee: totalValueFee
        };
      },
      buildFinalDistributionMoney: function(data) {
        return {
          cc: data.totalCollectedMoney.cc - data.tipOuts.given.cc + data.tipOuts.received.cc - data.totalFoodTipOutsSumm,
          cash: data.totalCollectedMoney.cash - data.tipOuts.given.cash + data.tipOuts.received.cash,
          agSvc: data.totalCollectedMoney.agSvc - data.tipOuts.given.agSvc + data.tipOuts.received.agSvc,
          fee: data.totalCollectedMoney.fee - data.tipOuts.given.fee + data.tipOuts.received.fee
        };
      },
      buildPercentsPart: function(data) {
        var collectedMoneyDistributePercents;
        collectedMoneyDistributePercents = 100 - data.tipOuts.givenTipOutsTotalPercents;
        return collectedMoneyDistributePercents / 100;
      },
      buildTips: function() {
        var i, j, k, len, len1, len2, ref, ref1, ref2, tips, tr;
        tips = {
          given: []
        };
        ref = $("#tip-out #given-tipouts-table").find(".tip-out-line[data-tip-out-type='area']");
        for (i = 0, len = ref.length; i < len; i++) {
          tr = ref[i];
          tips.given.push({
            tip_out_type: 'area',
            area_type_name: $(tr).attr("data-area-name"),
            area_type_id: $(tr).find(".area-id").val(),
            shift_type_id: $(tr).find(".shift-type-id").val(),
            status: $(tr).attr("data-tip-out-status"),
            id: $(tr).attr("data-tip-out-id"),
            percentage: $(tr).find(".tip-out-percentage").val()
          });
        }
        ref1 = $("#tip-out #given-tipouts-table").find(".tip-out-line[data-tip-out-type='position']");
        for (j = 0, len1 = ref1.length; j < len1; j++) {
          tr = ref1[j];
          tips.given.push({
            tip_out_type: 'position',
            position_type_id: $(tr).find(".position-id").val(),
            area_type_name: $(tr).attr("data-area-name"),
            area_type_id: $(tr).find(".area-id").val(),
            shift_type_id: $(tr).find(".shift-type-id").val(),
            status: $(tr).attr("data-tip-out-status"),
            id: $(tr).attr("data-tip-out-id"),
            percentage: $(tr).find(".tip-out-percentage").val()
          });
        }
        ref2 = $("#food-tip-outs #given-tipouts-table").find(".tip-out-line[data-tip-out-type='food']");
        for (k = 0, len2 = ref2.length; k < len2; k++) {
          tr = ref2[k];
          tips.given.push({
            tip_out_type: 'food',
            tip_out_food_subtype: $(tr).attr("data-tip-out-food-subtype"),
            area_type_name: $(tr).attr("data-area-name"),
            area_type_id: $(tr).find(".area-id").val(),
            shift_type_id: $(tr).find(".shift-type-id").val(),
            status: $(tr).attr("data-tip-out-status"),
            id: $(tr).attr("data-tip-out-id"),
            percentage: $(tr).find(".tip-out-percentage").val()
          });
        }
        return tips;
      },
      buildPercentage: function() {
        var i, j, len, len1, percentage, positionLine, ref, ref1;
        percentage = {};
        if (this.distribution_type() === "percents") {
          ref = $(".total-position-tips");
          for (i = 0, len = ref.length; i < len; i++) {
            positionLine = ref[i];
            percentage[$(positionLine).find(".percentage-point").attr("data-percent-distribution-id")] = $(positionLine).find(".percentage-point").attr("percentage-final-value");
          }
        } else {
          ref1 = $(".total-position-tips");
          for (j = 0, len1 = ref1.length; j < len1; j++) {
            positionLine = ref1[j];
            percentage[$(positionLine).find(".percentage-point").attr("data-percent-distribution-id")] = $(positionLine).find(".percentage-point").val();
          }
        }
        return percentage;
      },
      buildOriginalPercentage: function() {
        var i, len, percentage, positionLine, ref;
        percentage = {};
        ref = $(".total-position-tips");
        for (i = 0, len = ref.length; i < len; i++) {
          positionLine = ref[i];
          percentage[$(positionLine).find(".percentage-point").attr("data-percent-distribution-id")] = $(positionLine).find(".percentage-point").val();
        }
        return percentage;
      },
      correctCCFee: function() {
        var cc_fee;
        cc_fee = this.buildCCFee();
        if (!cc_fee) {
          cc_fee = 0;
        }
        return $("#cc_fee").val(cc_fee);
      },
      buildCCFee: function() {
        var cc_fee;
        cc_fee = parseFloat(parseFloat($("#cc_fee").val()).toFixed(2));
        if (!cc_fee) {
          cc_fee = 0;
        }
        return cc_fee;
      },
      buildCCMinusFee: function() {
        return (100 - this.data.cc_fee) / 100;
      },
      buildAgSvcMinusFee: function() {
        return (100 - this.data.ag_svc_fee) / 100;
      },
      buildCCFeeAmount: function(data) {
        var cc_fee, cc_fee_summ;
        cc_fee = this.buildCCFee();
        cc_fee_summ = data.totalCollectedMoney.cc_original * (cc_fee / 100);
        return cc_fee_summ.toFixed(3);
      },
      correctAgSvcFee: function() {
        var ag_svc_fee;
        ag_svc_fee = this.buildAgSvcFee();
        if (!ag_svc_fee) {
          ag_svc_fee = 0;
        }
        return $("#ag_svc_fee").val(ag_svc_fee);
      },
      buildAgSvcFee: function() {
        var ag_svc_fee;
        ag_svc_fee = parseFloat(parseFloat($("#ag_svc_fee").val()).toFixed(2));
        if (!ag_svc_fee) {
          ag_svc_fee = 0;
        }
        return ag_svc_fee;
      },
      buildAgSvcFeeAmount: function(data) {
        var ag_svc_fee, ag_svc_fee_summ;
        ag_svc_fee = this.buildAgSvcFee();
        ag_svc_fee_summ = data.totalCollectedMoney.ag_svc_original * (ag_svc_fee / 100);
        return ag_svc_fee_summ.toFixed(3);
      },
      buildData: function(params) {
        var i, len, positionsThatReceiveTipSharingIds, ref, tr;
        this.data = {
          frontend_restaurant_id: gon.restaurant.id,
          calculationId: gon.calculation_id,
          distribution_logic_type: gon.calculation_distribution_logic_type,
          distribution_type: this.distribution_type(),
          totalHoursWorked: this.totalHoursWorked(),
          totalSales: this.totalSales(),
          totalFoodSales: this.totalFoodSales(),
          percentage: this.buildPercentage(),
          originalPercentage: this.buildOriginalPercentage(),
          posTotals: this.buildPosTotals(),
          tips: this.buildTips()
        };
        if (params && params.updateCalculationRequired) {
          this.data.newCalculationParams = Tipmetric.Controllers.calculationFormView.getNewPositionsAndTeams();
        }
        this.data.cc_fee = this.buildCCFee();
        this.data.cc_minus_fee = this.buildCCMinusFee();
        this.data.ag_svc_fee = this.buildAgSvcFee();
        this.data.ag_svc_minus_fee = this.buildAgSvcMinusFee();
        this.data.totalCollectedMoneyByPositions = this.buildTotalCollectedMoneyByPositions(this.data);
        this.data.totalCollectedMoney = this.buildTotalCollectedMoney(this.data);
        this.buildPositionTipOuts(this.data);
        this.data.totalPositionTipOuts = this.buildTotalPositionTipOuts(this.data);
        this.data.foodTipOuts = this.buildFoodTipOuts(this.data);
        this.data.foodTipOutPercents = this.buildTotalFoodTipOutPercents(this.data);
        this.data.totalFoodTipOutsSumm = this.buildTotalFoodTipOutsSumm(this.data);
        this.data.totalFoodTipOutsSeparateSumms = this.buildTotalFoodTipOuts(this.data);
        this.data.tipOuts = this.buildAreaTipOuts(this.data);
        this.data.totalTipOuts = this.calculateTotalTipOuts(this.data);
        this.data.finalToDistribute = this.buildFinalDistributionMoney(this.data);
        this.data.collectedMoneyDistributePercentsPart = this.buildPercentsPart(this.data);
        this.data.cc_fee_summ = this.buildCCFeeAmount(this.data);
        this.data.ag_svc_fee_summ = this.buildAgSvcFeeAmount(this.data);
        this.data.sourceTipSharings = _.map($("#position_tip_sharings .position-tip-sharing"), (function(_this) {
          return function(tr) {
            var json;
            json = {
              positionTipSharingId: $(tr).attr("data-position-tip-sharing-id"),
              sourcePositionId: $(tr).find("select.source-position-select").val(),
              percentage: parseFloat($(tr).find("input.percentage").val()),
              sourceType: $(tr).find("select.source-type-select").val(),
              targetPositionId: $(tr).find("select.target-position-select").val(),
              tempId: $(tr).attr("data-temp-id")
            };
            if (gon.restaurant.sto_condition_rules_enabled) {
              json.conditionFirst = $(tr).find("select.condition-first-select").val();
              json.conditionType = $(tr).find("select.condition-type-select").val();
              json.conditionSecond = parseFloat($(tr).find("input.condition-second-input").val());
            }
            return json;
          };
        })(this));
        this.data.overtips = _.map($("#overtips .overtip"), (function(_this) {
          return function(tr) {
            return {
              overtipId: $(tr).attr("data-overtip-id"),
              sourceEmployeeId: $(tr).find("select.source-employee-select").val().split('-')[0],
              sourcePositionId: $(tr).find("select.source-employee-select").val().split('-')[1],
              sourcePositionName: $(tr).find("select.source-employee-select option:selected").attr("data-position-name"),
              percentage: parseFloat($(tr).find("input.percentage").val()),
              sourceType: $(tr).find("select.source-type-select").val(),
              targetEmployeeId: $(tr).find("select.target-employee-select").val().split('-')[0],
              targetPositionId: $(tr).find("select.target-employee-select").val().split('-')[1],
              targetPositionName: $(tr).find("select.target-employee-select option:selected").attr("data-position-name"),
              tempId: $(tr).attr("data-temp-id")
            };
          };
        })(this));
        positionsThatReceiveTipSharingIds = _.uniq(_.map($("#position_tip_sharings select.target-position-select"), function(f) {
          return $(f).val();
        }));
        this.data.sourceTipSharingsHash = {};
        ref = _.map($("#position_tip_sharings .position-tip-sharing"));
        for (i = 0, len = ref.length; i < len; i++) {
          tr = ref[i];
          this.data.sourceTipSharingsHash[$(tr).attr("data-temp-id")] = {
            positionTipSharingId: $(tr).attr("data-position-tip-sharing-id"),
            sourcePositionId: $(tr).find("select.source-position-select").val(),
            sourcePositionName: $(tr).find("select.source-position-select option:selected").text(),
            percentage: parseFloat($(tr).find("input.percentage").val()),
            sourceType: $(tr).find("select.source-type-select").val(),
            targetPositionId: $(tr).find("select.target-position-select").val(),
            tempId: $(tr).attr("data-temp-id"),
            employeesAppliedTo: []
          };
          if (gon.restaurant.sto_condition_rules_enabled) {
            this.data.sourceTipSharingsHash[$(tr).attr("data-temp-id")].conditionEnabled = true;
            this.data.sourceTipSharingsHash[$(tr).attr("data-temp-id")].conditionFirst = $(tr).find("select.condition-first-select").val();
            this.data.sourceTipSharingsHash[$(tr).attr("data-temp-id")].conditionType = $(tr).find("select.condition-type-select").val();
            this.data.sourceTipSharingsHash[$(tr).attr("data-temp-id")].conditionSecond = parseFloat($(tr).find("input.condition-second-input").val());
          }
        }
        this.data.joinedTipSharings = _.map($("#position_tip_sharings .position-tip-sharing[data-source-type='hours']"), (function(_this) {
          return function(tr) {
            var json, sourcePositionId, targetPositionId;
            sourcePositionId = $(tr).find("select.source-position-select").val();
            targetPositionId = $(tr).find("select.target-position-select").val();
            json = {
              positionTipSharingId: $(tr).attr("data-position-tip-sharing-id"),
              sourcePositionId: sourcePositionId,
              multiplier: parseFloat($(tr).find("input.percentage").val()),
              sourceType: $(tr).find("select.source-type-select").val(),
              targetPositionId: targetPositionId,
              tempId: $(tr).attr("data-temp-id"),
              joinedPositionIds: [sourcePositionId, targetPositionId]
            };
            if (gon.restaurant.sto_condition_rules_enabled) {
              json.conditionFirst = $(tr).find("select.condition-first-select").val();
              json.conditionType = $(tr).find("select.condition-type-select").val();
              json.conditionSecond = parseFloat($(tr).find("input.condition-second-input").val());
            }
            return json;
          };
        })(this));
        this.data.note = $(".calculation-note .note-content").text();
        if (this.processingMethod() === "source_tip_outs") {
          if (gon.restaurant.sto_condition_rules_enabled) {
            Tipmetric.Modules.CalculationConditionsSTOHelper.process(this.data, this);
          } else {
            Tipmetric.Modules.calculationSTOHelper.process(this.data, this);
          }
        } else {
          if (this.distribution_type() === "percents") {
            Tipmetric.Modules.calculationPercentsHelper.processPercents(this.data, this);
          } else {
            Tipmetric.Modules.calculationPercentsHelper.processPoints(this.data, this);
          }
        }
        window.d = this.data;
        return this.data;
      },
      processingMethod: function() {
        if (gon.calculation_distribution_logic_type === "source_tip_outs") {
          return "source_tip_outs";
        } else {
          return "default";
        }
      },
      totalTipOutsPercentsByPosition: function(positionTypeId) {
        var numbers, summ;
        numbers = _.map($("#tip-out #given-tipouts-table tbody tr.tip-out-line[data-tip-out-type='position'][data-tip-out-position-id='" + positionTypeId + "']"), function(position) {
          return parseFloat($(position).find("input.tip-out-percentage").val() || 0);
        });
        summ = _.reduce(numbers, function(sum, num) {
          return sum + num;
        }) || 0;
        return summ;
      },
      percentToDistributeForPosition: function(positionTypeId) {
        return 100 - this.totalTipOutsPercentsByPosition(positionTypeId);
      },
      buildPrintableData: function() {
        return {
          restaurant: gon.restaurant,
          currentUser: gon.current_user,
          calculation: {
            date: $("#date").val(),
            distributionType: this.distribution_type(),
            area: $("#calculation-form-wrapper").find("select#area_type_id option[value='" + ($("select#area_type_id").val()) + "']").text(),
            shift: $("#calculation-form-wrapper").find("select#shift_type_id option[value='" + ($("select#shift_type_id").val()) + "']").text(),
            sourePositions: _.map($("select#source_position_ids").select2('data'), function(e) {
              return e.text;
            }),
            teamsQuantity: $("#calculation-form-wrapper").find("select#teams_quantity").val()
          }
        };
      },
      seedPrintableData: function() {
        var calculation, data, info, restaurant, user;
        data = this.buildPrintableData();
        info = $(".printable.calculation-info");
        calculation = info.find(".calculation");
        calculation.find(".date").text(data.calculation.date);
        calculation.find(".area").text(data.calculation.area);
        calculation.find(".shift").text(data.calculation.shift);
        calculation.find(".sources").text(data.calculation.sourePositions);
        calculation.find(".teams").text(data.calculation.teamsQuantity);
        calculation.find(".distributionType").text(data.calculation.distributionType);
        restaurant = info.find(".restaurant");
        restaurant.find(".name").text(data.restaurant.name);
        user = info.find(".current-user");
        user.find(".name").text(data.currentUser.name);
        return user.find(".workposition").text(data.currentUser.workposition);
      },
      varianceCheck: function() {
        var agSvcMap, agSvcToDistributeTotalSum, allowedVariance, cashMap, cashToDistributeTotalSum, ccMap, ccToDistributeTotalSum, distributedAgSvc, distributedCash, distributedCc, distributedFee, feeMap, feeToDistributeTotalSum, noPendingDistributions, numbersAreAlmostEqual, varianceStatus;
        allowedVariance = 0.01;
        ccMap = _.map($("[data-table-type='employee-distribution'] .total-tips-distributed-cc h4"), function(num) {
          return parseFloat($(num).text());
        });
        ccToDistributeTotalSum = _.reduce(ccMap, function(sum, num) {
          return sum + num;
        }) || 0;
        ccToDistributeTotalSum = ccToDistributeTotalSum.toFixed(2);
        $(".tips-to-distribute-cc").text(ccToDistributeTotalSum);
        cashMap = _.map($("[data-table-type='employee-distribution'] .total-tips-distributed-cash h4"), function(num) {
          return parseFloat($(num).text());
        });
        cashToDistributeTotalSum = _.reduce(cashMap, function(sum, num) {
          return sum + num;
        }) || 0;
        cashToDistributeTotalSum = cashToDistributeTotalSum.toFixed(2);
        $(".tips-to-distribute-cash").text(cashToDistributeTotalSum);
        agSvcMap = _.map($("[data-table-type='employee-distribution'] .total-tips-distributed-ag-svc h4"), function(num) {
          return parseFloat($(num).text());
        });
        agSvcToDistributeTotalSum = _.reduce(agSvcMap, function(sum, num) {
          return sum + num;
        }) || 0;
        agSvcToDistributeTotalSum = agSvcToDistributeTotalSum.toFixed(2);
        $(".tips-to-distribute-ag-svc").text(agSvcToDistributeTotalSum);
        feeMap = _.map($("[data-table-type='employee-distribution'] .total-tips-distributed-fee h4"), function(num) {
          return parseFloat($(num).text());
        });
        feeToDistributeTotalSum = _.reduce(feeMap, function(sum, num) {
          return sum + num;
        }) || 0;
        feeToDistributeTotalSum = feeToDistributeTotalSum.toFixed(2);
        $(".tips-to-distribute-fee").text(feeToDistributeTotalSum);
        distributedCc = parseFloat($(".tips-distributed-cc").text()).toFixed(2);
        distributedCash = parseFloat($(".tips-distributed-cash").text()).toFixed(2);
        distributedAgSvc = parseFloat($(".tips-distributed-ag-svc").text()).toFixed(2);
        distributedFee = parseFloat($(".tips-distributed-fee").text()).toFixed(2);
        numbersAreAlmostEqual = Math.abs(distributedCc - ccToDistributeTotalSum).toFixed(2) <= allowedVariance && Math.abs(distributedCash - cashToDistributeTotalSum).toFixed(2) <= allowedVariance;
        noPendingDistributions = $("#pending-distributions-view table tbody tr").length === 0;
        if (numbersAreAlmostEqual && noPendingDistributions) {
          varianceStatus = "yes";
        } else {
          varianceStatus = "no";
        }
        return $(".variance-status").attr("data-variance-status", varianceStatus);
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("calculation")) {
        return Tipmetric.Controllers.calculationView = new CalculationView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var CsvImportView;
    CsvImportView = Backbone.View.extend({
      el: "#csv-import-view",
      data: {
        tm_fields: ["", ":emp_id", ":first_name", ":last_name", ":first_name :last_name", ":last_name, :first_name", "", ":hours_worked", ":hours_worked_2", ":cc_tips", ":cash_tips", ":ag_svc_tips", ":fee_tips", ":food_sales", ":beverage_sales", ":sales_cc", ":sales_cash", ":position_type", ":start_time", ":end_time", ":date_range", "", ":tip_field", ":tip_field_amount", "", ":filter_by_values"],
        tm_data_fields: [":hours_worked", ":hours_worked_2", ":cc_tips", ":cash_tips", ":ag_svc_tips", ":fee_tips", ":food_sales", ":beverage_sales", ":sales_cc", ":sales_cash", ":tip_field_amount"]
      },
      events: {
        "click #upload-file": "uploadFile",
        "click #reset-labels": "resetLabels",
        "click #set-labels": "setLabels",
        "click [data-action='show-csv-shift-select']": "showCsvShiftSelect",
        "change #csv_file": "readCsv",
        "change #selected-field": "columnKeyChanged",
        "change #selected-filter-option": "filterTableByOption",
        "change [data-required-field='true']": "setShiftMatching",
        "ifChanged #header_present": "rebuildTableWithOrWithoutHeader",
        "click [data-action='save-associated-positions']": "updatePositionAssociatedPositions",
        "click [data-action='save-tip-field-associated-field']": "updateTipFieldAssociatedField",
        "click #set-tip-fields": "linkAssociatedFields",
        "click #reset-tip-fields": "resetAssociatedFields"
      },
      initialize: function() {
        var that;
        this.headerPresent = gon.restaurant.csv_import_header_present;
        if (this.headerPresent) {
          this.$el.find("#header_present").iCheck("check");
        }
        Tipmetric.Controllers.helpers.bindFilteredRulesSaving(5, function() {
          return Tipmetric.Controllers.csvImportView.buildCsvTable();
        });
        $.fn.editable.defaults.mode = 'inline';
        $.fn.editable.defaults.ajaxOptions = {
          type: "PUT"
        };
        $.fn.editableform.buttons = "<button type=\"submit\" class=\"btn btn-sm btn-success editable-submit\">SAVE</button> <button type=\"button\" class=\"btn btn-sm btn-default editable-cancel\">CANCEL</button>";
        $(".csv-associated-position-table").find('.start-time, .end-time').editable({
          url: function(params) {
            var json;
            json = {};
            json[$(this).attr("data-field-name")] = params.value;
            return $.ajax({
              url: "/api/position_types/" + params.pk,
              method: "put",
              data: {
                position_type: json
              },
              success: (function(_this) {
                return function(response) {
                  return toastr["success"]("successfully updated", "Success");
                };
              })(this),
              error: function(response) {
                return toastr["error"](response.responseText, "Error");
              }
            });
          }
        });
        that = this;
        return $(".csv-shifts-table-wrapper").find('.start-time, .end-time').editable({
          url: function(params) {
            var fieldName, json;
            json = {};
            json[$(this).attr("data-field-name")] = params.value;
            fieldName = $(this).attr("data-field-name");
            return $.ajax({
              url: "/api/shift_types/" + params.pk,
              method: "put",
              data: {
                shift_type: json
              },
              success: (function(_this) {
                return function(response) {
                  $(_this).attr("data-field-value", response.shift_type[fieldName]);
                  return that.setShiftMatching();
                };
              })(this),
              error: function(response) {
                return toastr["error"](response.responseText, "Error");
              }
            });
          }
        });
      },
      showCsvShiftSelect: function(e) {
        if (e) {
          e.preventDefault();
        }
        return $(".csv-shifts-table-wrapper").toggle();
      },
      resetLabels: function(e) {
        if (e) {
          e.preventDefault();
        }
        return $("tbody.csv-import-data tr .label").remove();
      },
      setLabels: function(e) {
        e.preventDefault();
        this.resetLabels(false);
        this.buildAssociatedPositionsJson();
        return this.identifyPositions();
      },
      updatePositionAssociatedPositions: function(e) {
        var input, json;
        input = $(e.currentTarget).closest('td').find(".csv-associated-positions");
        json = {};
        json[$(input).attr("data-field-name")] = input.val();
        return $.ajax({
          url: "/api/position_types/" + ($(input).attr("data-id")),
          method: "put",
          data: {
            position_type: json
          },
          success: (function(_this) {
            return function(response) {
              return toastr["success"]("successfully updated", "Success");
            };
          })(this),
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      updateTipFieldAssociatedField: function(e) {
        var input, json;
        input = $(e.currentTarget).closest('td').find(".tip-field-associated-fields");
        json = {};
        json[$(input).attr("data-associated-fields")] = input.val();
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          data: {
            restaurant: json
          },
          success: (function(_this) {
            return function(response) {
              return toastr["success"]("successfully updated", "Success");
            };
          })(this),
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      linkAssociatedFields: function() {
        var columnNumberForTipFieldAssociation, data, j, k, len, len1, ref, ref1, results, td, tipField, tipFieldData;
        data = {};
        ref = $("#csv-tip-fields-association-table input.tip-field-associated-fields");
        for (j = 0, len = ref.length; j < len; j++) {
          tipField = ref[j];
          data[$(tipField).attr("data-field-name")] = _.map($(tipField).val().split(','), function(f) {
            return f.trim();
          });
        }
        data;
        columnNumberForTipFieldAssociation = $("th[data-tm-field-name=':tip_field']").attr("data-column-number");
        ref1 = $("td[data-column-number='" + columnNumberForTipFieldAssociation + "']");
        results = [];
        for (k = 0, len1 = ref1.length; k < len1; k++) {
          td = ref1[k];
          tipFieldData = _.find(_.pairs(data), function(d) {
            return _.contains(d[1], $(td).attr("data-value"));
          });
          if (tipFieldData) {
            $(td).closest("tr").attr("data-tip-field", tipFieldData[0]);
            results.push($(td).closest("tr").append("<span class='associated-field-label label label-success'>" + tipFieldData[0] + "</span>"));
          } else {
            results.push(void 0);
          }
        }
        return results;
      },
      resetAssociatedFields: function() {
        $(".associated-field-label").remove();
        return $(".csv-import-data tr").removeAttr("data-tip-field");
      },
      applyTipFieldLogic: function() {
        return !!(_.some($("th[data-tm-field-name=':tip_field']")) && _.some($("th[data-tm-field-name=':tip_field_amount']")));
      },
      buildAssociatedPositionsJson: function() {
        var j, json, len, ref, tr;
        json = {};
        ref = $(".csv-associated-position-table tbody tr");
        for (j = 0, len = ref.length; j < len; j++) {
          tr = ref[j];
          json[$(tr).attr("data-position-type-id")] = {
            name: $(tr).attr("data-position-type-name"),
            associatedPositions: $(tr).find("input.csv-associated-positions").val(),
            start_time: moment((this.getDate()) + " " + ($(tr).find(".start-time").text()), "MM/DD/YYYY HH:mm A"),
            end_time: moment((this.getDate()) + " " + ($(tr).find(".end-time").text()), "MM/DD/YYYY HH:mm A")
          };
          if (json[$(tr).attr("data-position-type-id")].start_time > json[$(tr).attr("data-position-type-id")].end_time) {
            json[$(tr).attr("data-position-type-id")].end_time = moment((this.getDate()) + " " + ($(tr).find(".end-time").text()), "MM/DD/YYYY HH:mm A").add(1, 'days');
          }
        }
        return this.assiciatedPositionsJson = json;
      },
      getDate: function() {
        if (!_.isEmpty($("#date").val())) {
          return $("#date").val();
        } else {
          return moment().format("MM/DD/YYYY");
        }
      },
      selectedTableValues: function() {
        return _.filter(_.map(this.$el.find(".csv-import-headers th select option:selected"), function(e) {
          return $(e).val();
        }), function(f) {
          return !_.isEmpty(f);
        });
      },
      identifyPositions: function(e) {
        var associatedLineColumnNumber, associatedPosition, csvLine, endTimeLineColumnNumber, isEndTimeCheckEnabled, isPositionCheckEnabled, isStartTimeCheckEnabled, j, len, matchByStartAndEndTime, matchFounded, pair, position, ref, results, shiftEndTime, shiftEndTimeProcessed, shiftStartTime, shiftStartTimeProcessed, startTimeLineColumnNumber;
        matchByStartAndEndTime = _.contains(this.selectedTableValues(), ":start_time") && _.contains(this.selectedTableValues(), ":end_time");
        associatedLineColumnNumber = $(".csv-import-headers th[data-tm-field-name=':position_type']").attr("data-column-number");
        startTimeLineColumnNumber = $(".csv-import-headers th[data-tm-field-name=':start_time']").attr("data-column-number");
        endTimeLineColumnNumber = $(".csv-import-headers th[data-tm-field-name=':end_time']").attr("data-column-number");
        isPositionCheckEnabled = associatedLineColumnNumber !== void 0;
        isStartTimeCheckEnabled = startTimeLineColumnNumber !== void 0;
        isEndTimeCheckEnabled = endTimeLineColumnNumber !== void 0;
        ref = $(".csv-import-data tr");
        results = [];
        for (j = 0, len = ref.length; j < len; j++) {
          csvLine = ref[j];
          associatedPosition = $(csvLine).find("td[data-column-number='" + associatedLineColumnNumber + "']").text();
          if (matchByStartAndEndTime) {
            shiftStartTimeProcessed = $(csvLine).find("[data-tm-field-name=':start_time']").attr("data-value").replace(/^\s*\d{1,2}(\/|-)\d{1,2}(\/|-)\d{2,4}\s/, "").replace(/^\s*\d{2,4}(\/|-)\d{1,2}(\/|-)\d{1,2}\s/, "");
            shiftEndTimeProcessed = $(csvLine).find("[data-tm-field-name=':end_time']").attr("data-value").replace(/^\s*\d{1,2}(\/|-)\d{1,2}(\/|-)\d{2,4}\s/, "").replace(/^\s*\d{2,4}(\/|-)\d{1,2}(\/|-)\d{1,2}\s/, "");
            shiftStartTime = moment((this.getDate()) + " " + shiftStartTimeProcessed, "MM/DD/YYYY HH:mm A");
            shiftEndTime = moment((this.getDate()) + " " + shiftEndTimeProcessed, "MM/DD/YYYY HH:mm A");
            if (shiftStartTime > shiftEndTime) {
              shiftEndTime = shiftEndTime.add(1, 'days');
            }
          }
          if (!isPositionCheckEnabled && !isStartTimeCheckEnabled && !isEndTimeCheckEnabled) {
            continue;
          }
          results.push((function() {
            var k, l, len1, len2, ref1, ref2, results1;
            ref1 = _.pairs(this.assiciatedPositionsJson);
            results1 = [];
            for (k = 0, len1 = ref1.length; k < len1; k++) {
              pair = ref1[k];
              if (isPositionCheckEnabled) {
                matchFounded = false;
                ref2 = pair[1].associatedPositions.split(",");
                for (l = 0, len2 = ref2.length; l < len2; l++) {
                  position = ref2[l];
                  if (associatedPosition.trim() === position.trim()) {
                    matchFounded = true;
                  }
                }
                if (_.isEmpty(pair[1].associatedPositions) || _.isEmpty(associatedPosition) || !matchFounded) {
                  continue;
                }
              }
              if (matchByStartAndEndTime) {
                if (isStartTimeCheckEnabled) {
                  if (shiftStartTime <= pair[1].start_time) {
                    continue;
                  }
                }
                if (isEndTimeCheckEnabled) {
                  if (shiftEndTime >= pair[1].end_time) {
                    continue;
                  }
                }
              }
              $(csvLine).attr("data-position-type-id", pair[0]);
              results1.push($(csvLine).append("<span class='label label-success'>" + pair[1].name + "</span>"));
            }
            return results1;
          }).call(this));
        }
        return results;
      },
      setShiftMatching: function() {
        var csvLine, currentShiftId, filtedPassed, filterData, j, len, ref, results, shiftEndTime, shiftEndTimeProcessed, shiftSelected, shiftStartTime, shiftStartTimeProcessed, shiftTr, tipmetricShiftEnd, tipmetricShiftStart;
        currentShiftId = this.$el.find("select#shift_type_id").val();
        shiftSelected = !_.isEmpty(currentShiftId);
        shiftTr = $(".csv-shifts-table").find("tr[data-shift-type-id='" + currentShiftId + "']");
        tipmetricShiftStart = moment((this.getDate()) + " " + ($(shiftTr).find(".start-time").attr('data-field-value')), "MM/DD/YYYY HH:mm A");
        tipmetricShiftEnd = moment((this.getDate()) + " " + ($(shiftTr).find(".end-time").attr('data-field-value')), "MM/DD/YYYY HH:mm A");
        if (tipmetricShiftStart > tipmetricShiftEnd) {
          tipmetricShiftEnd = tipmetricShiftEnd.add(1, 'days');
        }
        if (_.any($("thead.csv-import-headers").find("[data-tm-field-name=':start_time']")) && _.any($("thead.csv-import-headers").find("[data-tm-field-name=':end_time']")) && shiftSelected) {
          filterData = true;
        } else {
          filterData = false;
        }
        ref = $(".csv-import-data tr");
        results = [];
        for (j = 0, len = ref.length; j < len; j++) {
          csvLine = ref[j];
          if (filterData) {
            shiftStartTimeProcessed = $(csvLine).find("[data-tm-field-name=':start_time']").attr("data-value").replace(/^\s*\d{1,2}(\/|-)\d{1,2}(\/|-)\d{2,4}\s/, "").replace(/^\s*\d{2,4}(\/|-)\d{1,2}(\/|-)\d{1,2}\s/, "");
            shiftEndTimeProcessed = $(csvLine).find("[data-tm-field-name=':end_time']").attr("data-value").replace(/^\s*\d{1,2}(\/|-)\d{1,2}(\/|-)\d{2,4}\s/, "").replace(/^\s*\d{2,4}(\/|-)\d{1,2}(\/|-)\d{1,2}\s/, "");
            shiftStartTime = moment((this.getDate()) + " " + shiftStartTimeProcessed, "MM/DD/YYYY HH:mm A");
            shiftEndTime = moment((this.getDate()) + " " + shiftEndTimeProcessed, "MM/DD/YYYY HH:mm A");
            if (shiftStartTime > shiftEndTime) {
              shiftEndTime = shiftEndTime.add(1, 'days');
            }
            if (shiftStartTime >= tipmetricShiftStart && shiftEndTime <= tipmetricShiftEnd) {
              filtedPassed = "yes";
            } else {
              filtedPassed = "no";
            }
          } else {
            filtedPassed = "yes";
          }
          results.push($(csvLine).attr("data-filter-is-passed", filtedPassed));
        }
        return results;
      },
      getDate: function() {
        if (!_.isEmpty($("#date").val())) {
          return $("#date").val();
        } else {
          return moment().format("MM/DD/YYYY");
        }
      },
      selectHtml: function() {
        var field, html, j, len, options, ref, visibleFieldName;
        html = '';
        options = '';
        ref = this.data.tm_fields;
        for (j = 0, len = ref.length; j < len; j++) {
          field = ref[j];
          visibleFieldName = this.applyEditLabels(field);
          options += "<option value='" + field + "'>" + visibleFieldName + "</option>";
        }
        return html += '<select name="field" id="selected-field" class="form-control select-22" data-required-field="true"><option value="">Tipmetric field</option>' + options + '</select>';
      },
      applyEditLabels: function(field) {
        if (_.contains([":ag_svc_tips", ":food_sales", ":beverage_sales", ":sales_cc", ":sales_cash"], field)) {
          if (!_.isEmpty(_.findWhere(gon.restaurant.custom_labels.source_type_labels_nulled, {
            key: field
          }).name)) {
            return _.findWhere(gon.restaurant.custom_labels.source_type_labels_nulled, {
              key: field
            }).name;
          } else {
            return field;
          }
        } else {
          return field;
        }
      },
      selectHtmlWithOptions: function(values) {
        var field, html, j, len, options, visibleFieldName;
        html = '';
        options = '';
        console.log(values);
        for (j = 0, len = values.length; j < len; j++) {
          field = values[j];
          visibleFieldName = this.applyEditLabels(field);
          options += "<option value='" + field + "' selected='selected'>" + field + "</option>";
        }
        html += '<select name="field" id="selected-filter-option" class="form-control select-22" data-required-field="true" placeholder="Select filter options" multiple="multiple">' + options + '</select>';
        return html;
      },
      addSelectHtmlWithOptions: function(columnNumber) {
        var values;
        values = _.uniq(_.map($("tbody.csv-import-data td[data-column-number='" + columnNumber + "']"), function(td) {
          return $(td).text();
        }));
        $("th[data-column-number='" + columnNumber + "']").append(this.selectHtmlWithOptions(values));
        return window.bindMultipleSelectTo($("th[data-column-number='" + columnNumber + "'] select#selected-filter-option"));
      },
      checkIfFilterAppliable: function(e) {
        var columnNumber, tmKey;
        tmKey = $(e.currentTarget).val();
        columnNumber = $(e.currentTarget).closest("th").attr("data-column-number");
        if (tmKey === ":filter_by_values") {
          return this.addSelectHtmlWithOptions(columnNumber);
        } else {
          $(e.currentTarget).closest("th").find("#selected-filter-option, .select2.select2-container").remove();
          $("td[data-column-number='" + columnNumber + "']").attr("data-filter-by-option-is-passed", "yes");
          return $("tr").attr("data-filter-by-option-is-passed", "yes");
        }
      },
      filterTableByOption: function(e) {
        var columnNumber, j, len, ref, refValue, td, value;
        refValue = $(e.currentTarget).val();
        columnNumber = $(e.currentTarget).closest("th").attr("data-column-number");
        ref = $("tbody.csv-import-data td[data-column-number='" + columnNumber + "']");
        for (j = 0, len = ref.length; j < len; j++) {
          td = ref[j];
          value = $(td).text();
          if (_.contains(refValue, value) || refValue === [""] || refValue === null) {
            $(td).attr("data-filter-by-option-is-passed", "yes");
          } else {
            $(td).attr("data-filter-by-option-is-passed", "no");
          }
        }
        return this.setRowsFilterAttributes();
      },
      setRowsFilterAttributes: function() {
        var j, len, ref, results, row;
        ref = $(".csv-import-data > tr");
        results = [];
        for (j = 0, len = ref.length; j < len; j++) {
          row = ref[j];
          if (this.showRow(row)) {
            results.push($(row).attr("data-filter-by-option-is-passed", "yes"));
          } else {
            results.push($(row).attr("data-filter-by-option-is-passed", "no"));
          }
        }
        return results;
      },
      showRow: function(tr) {
        var noLength, yesLength;
        noLength = _.filter($(tr).find("td"), function(td) {
          return $(td).attr("data-filter-by-option-is-passed") === "no";
        }).length;
        yesLength = _.filter($(tr).find("td"), function(td) {
          return $(td).attr("data-filter-by-option-is-passed") === "yes";
        }).length;
        return _.filter($(tr).find("td"), function(td) {
          return $(td).attr("data-filter-by-option-is-passed") === "no";
        }).length === 0;
      },
      columnKeyChanged: function(e) {
        this.setColumnKey(e);
        this.setShiftMatching();
        this.setDateActivity();
        this.checkIfFilterAppliable(e);
        return this.setRowsFilterAttributes();
      },
      setColumnKey: function(e) {
        var columnNumber, j, len, ref, results, td, tmKey;
        columnNumber = $(e.currentTarget).closest("th").attr("data-column-number");
        tmKey = $(e.currentTarget).val();
        $(e.currentTarget).closest("table").find("[data-column-number='" + columnNumber + "']").attr("data-tm-field-name", tmKey);
        ref = this.$el.find("[data-tm-field-name='" + tmKey + "']");
        results = [];
        for (j = 0, len = ref.length; j < len; j++) {
          td = ref[j];
          results.push(this.$el.find(td).attr("data-value", this.$el.find(td).text()));
        }
        return results;
      },
      setDateActivity: function() {
        if (this.isDateRangePresent()) {
          return $("#date").attr("disabled", "disabled");
        } else {
          return $("#date").removeAttr("disabled");
        }
      },
      isDateRangePresent: function() {
        var selectedValues;
        selectedValues = _.filter(_.map(this.$el.find(".csv-import-headers th select option:selected"), (function(_this) {
          return function(e) {
            return $(e).val();
          };
        })(this)), function(f) {
          return !_.isEmpty(f);
        });
        return _.contains(selectedValues, ":date_range");
      },
      buildImportJson: function() {
        var employeeData, employeeTr, fieldName, id, j, json, k, len, len1, position_type_id, ref, ref1, rowNumber, td, value;
        json = {
          calcualtionParams: {},
          employeesData: [],
          frontend_restaurant_id: gon.restaurant.id
        };
        ref = this.$el.find(".csv-import-data tr");
        for (j = 0, len = ref.length; j < len; j++) {
          employeeTr = ref[j];
          if ($(employeeTr).attr("data-filter-is-passed") === "no") {
            continue;
          }
          if ($(employeeTr).attr("data-filter-by-option-is-passed") === "no") {
            continue;
          }
          employeeData = {};
          id = this.$el.find(employeeTr).attr("data-emp-id");
          if (!_.isEmpty(id)) {
            employeeData[":emp_id"] = id;
          }
          position_type_id = this.$el.find(employeeTr).attr("data-position-type-id");
          if (!_.isEmpty(position_type_id)) {
            employeeData["position_type_id"] = position_type_id;
          }
          rowNumber = this.$el.find(employeeTr).attr("data-row-number");
          employeeData["row_number"] = rowNumber;
          ref1 = this.$el.find(employeeTr).find("td");
          for (k = 0, len1 = ref1.length; k < len1; k++) {
            td = ref1[k];
            fieldName = this.$el.find(td).attr("data-tm-field-name");
            value = this.$el.find(td).text();
            if (_.isEmpty(value.replace(" ", ""))) {
              value = "0";
            }
            if (!_.isEmpty(fieldName) && !_.isEmpty(value)) {
              employeeData[fieldName] = value;
            }
          }
          employeeData[":hours_worked"] = parseFloat(employeeData[":hours_worked"] || 0) + parseFloat(employeeData[":hours_worked_2"] || 0);
          employeeData[":tip_field_tm_key"] = this.$el.find(employeeTr).attr("data-tip-field");
          if (this.applyTipFieldLogic()) {
            employeeData[employeeData[":tip_field_tm_key"]] = employeeData[":tip_field_amount"];
          }
          json.employeesData.push(employeeData);
        }
        json.selectedTableValues = _.filter(_.map(this.$el.find(".csv-import-headers th select option:selected"), function(e) {
          return $(e).val();
        }), function(f) {
          return !_.isEmpty(f);
        });
        json.applyTipFieldLogic = this.applyTipFieldLogic();
        json.calcualtionParams = {
          date: this.$el.find("#date").val(),
          area_type_id: this.$el.find("select#area_type_id").val(),
          shift_type_id: this.$el.find("select#shift_type_id").val(),
          source_position_type_ids: this.$el.find("select#source_position_ids").val(),
          teams_quantity: 1
        };
        return json;
      },
      uploadFile: function(e) {
        e.preventDefault();
        this.resetImportData();
        return this.$el.find("#csv_file").click();
      },
      resetImportData: function(e) {
        this.$el.find("#csv_file").val("");
        this.$el.find(".csv-import-headers").html("");
        this.$el.find(".csv-import-data").html("");
        this.$el.find(".import-errors > *").html("");
        return Tipmetric.Controllers.csvImportFormView.checkIfDataValid();
      },
      readCsv: function(e) {
        var file, reader;
        file = e.target.files[0];
        reader = new FileReader();
        reader.readAsText(file);
        reader.onload = (function(_this) {
          return function(event) {
            var csv;
            csv = event.target.result;
            _this.csv = csv;
            return _this.buildCsvTable();
          };
        })(this);
        return reader.onerror = function() {
          return alert('Unable to read ' + file.fileName);
        };
      },
      rebuildTableWithOrWithoutHeader: function(e) {
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return true;
        }
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: {
            restaurant: {
              csv_import_header_present: e.currentTarget.checked
            }
          },
          success: (function(_this) {
            return function() {
              _this.headerPresent = e.currentTarget.checked;
              if (_.some($(".csv-import-data tr"))) {
                return _this.buildCsvTable();
              }
            };
          })(this),
          error: function() {
            return console.log("-");
          }
        });
      },
      buildCsvTable: function() {
        var data, header_row, i, item, j, k, l, len, len1, len2, row, row_i, tr_data, tr_head;
        this.resetImportData();
        data = $.csv.toArrays(this.csv);
        tr_head = '';
        tr_data = '';
        row_i = 1;
        header_row = data[0];
        tr_head += '<tr>';
        i = 0;
        tr_head += "<th></th>";
        for (j = 0, len = header_row.length; j < len; j++) {
          item = header_row[j];
          if (!this.headerPresent) {
            item = "";
          }
          tr_head += ("<th data-column-number='" + i + "' data-tm-field-name='" + item + "'>") + item + this.selectHtml() + "</th>";
          i += 1;
        }
        tr_head += '</tr>';
        for (k = 0, len1 = data.length; k < len1; k++) {
          row = data[k];
          if (row === data[0] && this.headerPresent) {
            continue;
          }
          if (_.isEmpty(_.filter(row, function(e) {
            return !_.isEmpty(e);
          }))) {
            continue;
          }
          tr_data += "<tr data-filter-is-passed='' data-row-number='" + row_i + "'><td data-tm-field-name='employee-line-number' data-tm-field-val='" + row_i + "'>" + row_i + "</td>";
          i = 0;
          for (l = 0, len2 = row.length; l < len2; l++) {
            item = row[l];
            if (!_.isEmpty(item.match(/\d+,\d+/))) {
              item = item.replace(",", "");
            }
            tr_data += ("<td data-column-number='" + i + "'>") + item + "</td>";
            i += 1;
          }
          tr_data += '</tr>';
          row_i += 1;
        }
        this.$el.find(".csv-import-headers").prepend(tr_head);
        this.$el.find(".csv-import-data").prepend(tr_data);
        this.$el.find("#calculation-form-wrapper").show();
        this.$el.find(".header_present_wrapper").show();
        window.bindCheckboxes();
        return Tipmetric.Controllers.helpers.filterImportDataByPosition($("#built-import-table"), 5);
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("csv-import-view") && $("body").attr("data-sub-class") === "default-logic" && gon.restaurant.csv_import_enabled) {
        return Tipmetric.Controllers.csvImportView = new CsvImportView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var CsvImportFormView;
    CsvImportFormView = Backbone.View.extend({
      el: "#csv-import-view",
      events: {
        "change #area_type_id": "areaUpdated",
        "change #shift_type_id": "shiftUpdated",
        "change [data-required-field='true']": "checkIfDataValid",
        "change .changeble-data select": "calculationChanged",
        "blur #date": "dateBlured",
        "click .import-csv": "checkCsvImportData",
        "click .import-errors [data-action='remove']": "removeRow",
        "click .import-errors [data-action='create']": "createEmployee",
        "click .import-errors [data-action='add']": "addEmployee",
        "click .import-errors [data-action='match']": "matchEmployee",
        "click .import-errors [data-action='remove-match']": "removeMatchEmployee",
        "click .import-errors [data-action='remove-duplicated-line']": "removeDuplication",
        "click .import-errors [data-action='merge-duplications']": "mergeDuplications",
        "click .import-errors [data-action='select-position']": "selectPosition",
        "click .import-errors [data-action='link-to-position']": "linkEmployeeToPosition",
        "click button[data-calculation-action]": "chooseCalculationVariant",
        "click .date-selection-arrow b": "showDatePicker",
        "click [data-action='show-csv-config']": "toggleConfigBlock"
      },
      initialize: function() {
        gon.restaurant_inheritance_data = Tipmetric.Controllers.helpers.buildSmartData();
        $('.datapicker').datepicker({
          todayHighlight: true
        }).on("changeDate", (function(_this) {
          return function() {
            return _this.dateUpdated();
          };
        })(this)).on("show", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').addClass('open');
          };
        })(this)).on("hide", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').removeClass('open');
          };
        })(this));
        this.checkIfDataValid();
        this.dateUpdated();
        this.areaUpdated();
        return this.shiftUpdated();
      },
      importCsv: function(e) {
        if (e) {
          e.preventDefault();
        }
        return this.sendImportCsv(false);
      },
      sendImportCsv: function(allowUpdate) {
        var data;
        data = Tipmetric.Controllers.csvImportView.buildImportJson();
        data.allowUpdate = allowUpdate;
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          method: "post",
          url: "/api/calculations/import_csv",
          data: data,
          success: function(response) {
            toastr["success"]("Data has been imported.", "Success");
            Tipmetric.Controllers.spinner.hide();
            return Tipmetric.Controllers.csvImportView.resetImportData();
          },
          error: (function(_this) {
            return function(response) {
              Tipmetric.Controllers.spinner.hide();
              if (response.responseJSON.calculation_exists === true) {
                return swal({
                  title: "",
                  text: response.responseJSON.errors,
                  type: "info",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "Yes",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirmed) {
                  if (isConfirmed.value) {
                    return _this.sendImportCsv(true);
                  }
                });
              } else {
                return toastr["error"]("Data is not imported.", "Error");
              }
            };
          })(this)
        });
      },
      checkCsvImportData: function(e) {
        e.preventDefault();
        return this.sendCheckCsvImportDataRequest(false, e);
      },
      sendCheckCsvImportDataRequest: function(allowUpdate, e) {
        var data, selectedValues;
        if ($(e.currentTarget).attr("disabled") !== void 0) {
          return false;
        }
        data = Tipmetric.Controllers.csvImportView.buildImportJson();
        data.allowUpdate = allowUpdate;
        selectedValues = _.filter(_.map(this.$el.find(".csv-import-headers th select option:selected"), function(e) {
          return $(e).val();
        }), function(f) {
          return !_.isEmpty(f);
        });
        if (!_.contains(selectedValues, ":last_name") && !_.contains(selectedValues, ":emp_id") && !_.contains(selectedValues, ":first_name :last_name") && !_.contains(selectedValues, ":last_name, :first_name")) {
          toastr["error"]("Cannot Import...Please select an Emp ID or Last Name", "Error");
          return false;
        }
        return $.ajax({
          method: "post",
          url: "/api/calculations/check_csv_import_data",
          data: data,
          success: (function(_this) {
            return function(response) {
              _this.matchIdentifiedemployees(response);
              _this.$el.find(".import-errors > *").html('');
              return _this.sendImportCsv(allowUpdate);
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              var duplications, employee, employeeEmpId, html1, html3, html4, html5, html6, html7, html8, i, j, k, l, len, len1, len2, len3, len4, len5, len6, m, o, p, ref, ref1, ref2, ref3, ref4, ref5, ref6;
              window.response = response;
              _this.applyTipFieldLogic = Tipmetric.Controllers.csvImportView.applyTipFieldLogic();
              _this.matchIdentifiedemployees(response.responseJSON);
              Tipmetric.TestSupport.displayErrors(response.responseJSON.errors);
              if (response.responseJSON.critical_error) {
                swal({
                  title: "",
                  text: response.responseJSON.critical_error_message,
                  type: "error",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "OK",
                  showCancelButton: false
                });
                return;
              }
              if (response.responseJSON.calculation_exists === true && allowUpdate === false) {
                return swal({
                  title: "",
                  text: response.responseJSON.errors,
                  type: "info",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "Yes",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirmed) {
                  if (isConfirmed.value) {
                    return _this.sendCheckCsvImportDataRequest(true, e);
                  }
                });
              } else {
                if (response.responseJSON.errors) {
                  toastr["error"](response.responseJSON.errors, "Error");
                  return;
                }
                html1 = '';
                ref = response.responseJSON.employees_doesnt_exist;
                for (i = 0, len = ref.length; i < len; i++) {
                  employee = ref[i];
                  html1 += JST["csv_import/does_not_exist"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-exist").html(html1);
                html3 = '';
                ref1 = response.responseJSON.last_name_duplication;
                for (j = 0, len1 = ref1.length; j < len1; j++) {
                  employee = ref1[j];
                  html3 += JST["csv_import/last_name_duplication"](employee);
                }
                _this.$el.find(".import-errors .last-name-duplication").html(html3);
                html4 = '';
                ref2 = response.responseJSON.employee_in_multiple_positions;
                for (k = 0, len2 = ref2.length; k < len2; k++) {
                  employee = ref2[k];
                  html4 += JST["csv_import/employee_in_multiple_positions"](employee);
                }
                _this.$el.find(".import-errors .employee-in-multiple-positions").html(html4);
                html5 = '';
                ref3 = response.responseJSON.employees_doesnt_belong_to_any_position;
                for (l = 0, len3 = ref3.length; l < len3; l++) {
                  employee = ref3[l];
                  html5 += JST["csv_import/employees_doesnt_belong_to_any_position"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-belong-to-any-position").html(html5);
                html6 = '';
                ref4 = _.keys(response.responseJSON.duplicated_employees_grouped);
                for (m = 0, len4 = ref4.length; m < len4; m++) {
                  employeeEmpId = ref4[m];
                  duplications = _.map(response.responseJSON.duplicated_employees_grouped[employeeEmpId], function(f) {
                    f.filledFields = _this.filledFields(f);
                    return f;
                  });
                  html6 += JST["csv_import/duplicated_employee"](duplications);
                }
                _this.$el.find(".import-errors .employees-are-duplicated").html(html6);
                html7 = '';
                ref5 = response.responseJSON.employees_doesnt_belong_auto_selected_position;
                for (o = 0, len5 = ref5.length; o < len5; o++) {
                  employee = ref5[o];
                  html7 += JST["csv_import/employees_doesnt_belong_auto_selected_position"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-belong-auto-selected-position").html(html7);
                html8 = '';
                ref6 = response.responseJSON.first_and_last_name_convention_mismatch;
                for (p = 0, len6 = ref6.length; p < len6; p++) {
                  employee = ref6[p];
                  html8 += JST["csv_import/first_and_last_name_convention_mismatch"](employee);
                }
                _this.$el.find(".import-errors .first-and-last-name-convention-mismatch").html(html8);
                return toastr["error"]("Looks like import was not successfull.", "Error");
              }
            };
          })(this)
        });
      },
      matchIdentifiedemployees: function(response) {
        var employee, i, len, ref, results, tr;
        window.response = response;
        ref = response.identified_employees;
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          employee = ref[i];
          tr = this.$el.find(".csv-import-data tr[data-row-number='" + employee.row_number + "']");
          results.push(tr.attr("data-emp-id", employee.employee_data.emp_id));
        }
        return results;
      },
      removeRow: function(e) {
        var empId, firstAndLastName, lastAndFirstName, row, rowNumber;
        empId = $(e.currentTarget).attr("data-emp-id");
        rowNumber = $(e.currentTarget).attr("data-row-number");
        firstAndLastName = $(e.currentTarget).attr("data-first-and-last-name");
        lastAndFirstName = $(e.currentTarget).attr("data-last-and-fist-name");
        if (empId) {
          row = this.$el.find("td[data-tm-field-name=':emp_id'][data-value='" + empId + "']").closest("tr");
        } else if (rowNumber) {
          row = this.$el.find("tr[data-row-number='" + rowNumber + "']");
        } else if (firstAndLastName) {
          row = this.$el.find("td[data-tm-field-name=':first_name :last_name'][data-value='" + firstAndLastName + "']").closest("tr");
        } else if (lastAndFirstName) {
          row = this.$el.find("td[data-tm-field-name=':last_name, :first_name'][data-value='" + lastAndFirstName + "']").closest("tr");
        }
        row.remove();
        return $(e.currentTarget).closest(".doesnt-exist-warning-wrapper, .first-and-last-name-convention-mismatch-warning-wrapper").remove();
      },
      matchEmployee: function(e) {
        var currentParagraph, empId, empName, lineNumber, positionId, tr;
        currentParagraph = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper");
        empId = $(currentParagraph).find("select.employee-select option:selected").val();
        empName = $(currentParagraph).find("select.employee-select option:selected").text();
        positionId = $(currentParagraph).find("select.employee-select option:selected").attr("position-id");
        lineNumber = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        tr.attr("data-emp-id", empId);
        tr.attr("data-position-type-id", positionId);
        return $(currentParagraph).remove();
      },
      removeMatchEmployee: function(e) {
        var currentParagraph, lineNumber, tr;
        currentParagraph = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper, .employees-doesnt-belong-to-any-position-wrapper, .employees-doesnt-belong-auto-selected-position-wrapper");
        lineNumber = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper, .employees-doesnt-belong-to-any-position-wrapper, .employees-doesnt-belong-auto-selected-position-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        $(tr).remove();
        return $(currentParagraph).remove();
      },
      removeDuplication: function(e) {
        var currentParagraph, duplicationBlock, lineNumber, tr;
        duplicationBlock = $(e.currentTarget).closest(".employee-is-duplicated");
        currentParagraph = $(e.currentTarget).closest(".employee-duplication-line");
        lineNumber = $(e.currentTarget).attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        $(tr).remove();
        $(currentParagraph).remove();
        if ($(duplicationBlock).find(".employee-duplication-line").length === 0) {
          return $(duplicationBlock).remove();
        }
      },
      filledFields: function(data) {
        var dataType, html, i, j, len, len1, tm_data_fields_to_add;
        html = "";
        tm_data_fields_to_add = Tipmetric.Controllers.csvImportView.data.tm_data_fields;
        if (this.applyTipFieldLogic) {
          for (i = 0, len = tm_data_fields_to_add.length; i < len; i++) {
            dataType = tm_data_fields_to_add[i];
            if (data[dataType]) {
              if (dataType === ":tip_field_amount") {
                html += " " + data[dataType] + " (" + data[':tip_field_tm_key'] + ") (" + dataType + ")";
              } else if (dataType === data[':tip_field_tm_key']) {

              } else {
                html += " " + data[dataType] + " (" + dataType + ")";
              }
            }
          }
        } else {
          for (j = 0, len1 = tm_data_fields_to_add.length; j < len1; j++) {
            dataType = tm_data_fields_to_add[j];
            if (data[dataType]) {
              html += " " + data[dataType] + " (" + dataType + ")";
            }
          }
        }
        return html;
      },
      mergeDuplications: function(e) {
        var dataType, duplicationBlock, empId, finalAddedNumbers, finalFilledLine, i, j, k, len, len1, len2, lineNumbers, lineToRemove, lines, otherLines, summ, tableRows, td, tm_data_fields_to_add, tr, values;
        duplicationBlock = $(e.currentTarget).closest(".employee-is-duplicated");
        lines = $(duplicationBlock).find(".employee-duplication-line");
        lineNumbers = _.collect(lines, function(line) {
          return $(line).attr("data-line-number");
        });
        console.log(lineNumbers);
        empId = $(duplicationBlock).attr("data-emp-id");
        tm_data_fields_to_add = Tipmetric.Controllers.csvImportView.data.tm_data_fields;
        finalAddedNumbers = {};
        tableRows = _.filter($(".csv-import-data tr[data-emp-id='" + empId + "']"), function(f) {
          return _.contains(lineNumbers, $(f).attr("data-row-number"));
        });
        for (i = 0, len = tm_data_fields_to_add.length; i < len; i++) {
          dataType = tm_data_fields_to_add[i];
          values = _.map($(tableRows).find("td[data-tm-field-name='" + dataType + "']"), function(f) {
            return parseFloat($(f).attr("data-value")) || 0;
          });
          if (!_.isEmpty(values)) {
            summ = _.reduce(values, function(memo, num) {
              return memo + num;
            }) || 0;
            finalAddedNumbers[dataType] = summ.toFixed(2);
          }
        }
        finalFilledLine = lineNumbers[0];
        for (j = 0, len1 = tm_data_fields_to_add.length; j < len1; j++) {
          dataType = tm_data_fields_to_add[j];
          if (finalAddedNumbers[dataType]) {
            td = $(".csv-import-data tr[data-emp-id='" + empId + "'][data-row-number='" + finalFilledLine + "'] td[data-tm-field-name='" + dataType + "'] ");
            $(td).attr("data-value", finalAddedNumbers[dataType]);
            $(td).text(finalAddedNumbers[dataType]);
          }
        }
        otherLines = _.filter(lineNumbers, function(n) {
          return n !== finalFilledLine;
        });
        for (k = 0, len2 = otherLines.length; k < len2; k++) {
          lineToRemove = otherLines[k];
          tr = this.$el.find("tr[data-emp-id='" + empId + "'][data-row-number='" + lineToRemove + "']");
          tr.remove();
        }
        return $(duplicationBlock).remove();
      },
      selectPosition: function(e) {
        var currentParagraph, lineNumber, positionId, tr;
        currentParagraph = $(e.currentTarget).closest(".employee-in-multiple-positions-wrapper");
        positionId = $(currentParagraph).find("select.position-type-select option:selected").val();
        lineNumber = $(e.currentTarget).closest(".employee-in-multiple-positions-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        tr.attr("data-position-type-id", positionId);
        return $(currentParagraph).remove();
      },
      addEmployee: function(e) {
        var currentParagraph;
        currentParagraph = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        return currentParagraph.find(".doesnt-exist-warning-form").show();
      },
      createEmployee: function(e) {
        var currentParagraph, empId, firstName, lastName, positionId;
        e.preventDefault();
        currentParagraph = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        positionId = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper").find("select.position-type-select option:selected").val();
        empId = $(currentParagraph).find("input[name='emp_id']").val();
        firstName = $(currentParagraph).find("input[name='first_name']").val();
        lastName = $(currentParagraph).find("input[name='last_name']").val();
        return $.ajax({
          url: "api/employees",
          method: "post",
          data: {
            employee: {
              emp_id: empId,
              first_name: firstName,
              last_name: lastName,
              position_type_ids: [positionId]
            }
          },
          success: function(response) {
            $(currentParagraph).remove();
            return toastr["success"]("Emloyee has been created.", "Success");
          },
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      linkEmployeeToPosition: function(e) {
        var currentParagraph, empId, positionId;
        e.preventDefault();
        currentParagraph = $(e.currentTarget).closest(".employees-doesnt-belong-auto-selected-position-wrapper");
        empId = $(e.currentTarget).attr("data-emp-id");
        positionId = $(e.currentTarget).attr("data-position-id");
        return $.ajax({
          url: "api/employees/add_position",
          method: "patch",
          data: {
            empId: empId,
            position_type_id: positionId
          },
          success: function(response) {
            $(currentParagraph).remove();
            return toastr["success"]("Emloyee has been added to position.", "Success");
          },
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      hidePopover: function() {
        return this.$el.find("[data-toggle='popover']").popover('hide');
      },
      toggleConfigBlock: function(e) {
        return $("#config-wrapper-block").toggle();
      },
      showCalculation: function(e) {
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return false;
        }
        e.preventDefault();
        if (Tipmetric.Controllers.calculationView && Tipmetric.Controllers.calculationView.locked) {
          return false;
        }
        if (this.$el.find(".changeble-data").hasClass("changed")) {
          return this.updateCalculation(e);
        } else {
          return this.checkCalculation();
        }
      },
      showDatePicker: function(e) {
        e.preventDefault();
        return $('#date').focus();
      },
      dateUpdated: function() {
        var area, areas, dayName, i, len, options, savedVal;
        if (!_.isEmpty(this.$el.find("#date").val())) {
          this.$el.find("#date").attr("data-saved-date", this.$el.find("#date").val());
        }
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        if (gon.restaurant_inheritance_data[dayName]) {
          areas = _.map(gon.restaurant_inheritance_data[dayName].areas, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          options = "";
          options += "<option value=''>Area</option>";
          for (i = 0, len = areas.length; i < len; i++) {
            area = areas[i];
            options += "<option value='" + area.id + "'>" + area.name + "</option>";
          }
          savedVal = this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          this.$el.find("#calculation-form-wrapper").find("select#area_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val(savedVal).trigger("change");
        } else {
          this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val("").trigger("change").html("<option value=''>Area</option>");
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return this.$el.find("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      areaUpdated: function() {
        var areaName, dayName, i, len, options, savedVal, shift, shifts;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option[value='" + (this.$el.find("select#area_type_id").val()) + "']").text();
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas[areaName]) {
          shifts = _.map(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          options = "";
          options += "<option value=''>Shift</option>";
          for (i = 0, len = shifts.length; i < len; i++) {
            shift = shifts[i];
            options += "<option value='" + shift.id + "'>" + shift.name + "</option>";
          }
          savedVal = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val(savedVal).trigger("change");
        } else {
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return this.$el.find("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      shiftUpdated: function() {
        var areaData, areaName, dayName, firstPosition, i, len, options, position, positions, savedVal, shiftName;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option[value='" + ($('select#area_type_id').val()) + "']").text();
        shiftName = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id option[value='" + (this.$el.find("select#shift_type_id").val()) + "']").text();
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas) {
          areaData = gon.restaurant_inheritance_data[dayName].areas[areaName];
        }
        if (areaData && areaData.shifts[shiftName]) {
          positions = _.map(areaData.shifts[shiftName].positions, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          if (_.some(positions)) {
            firstPosition = positions[0].id;
          } else {
            firstPosition = "";
          }
          options = "";
          for (i = 0, len = positions.length; i < len; i++) {
            position = positions[i];
            options += "<option value='" + position.id + "'>" + position.name + "</option>";
          }
          savedVal = this.$el.find("select#source_position_ids").val();
          if (!savedVal) {
            savedVal = firstPosition;
          }
          this.$el.find("select#source_position_ids").html(options);
          return this.$el.find("select#source_position_ids").val(savedVal).trigger("change");
        } else {
          return this.$el.find("select#source_position_ids").val("").trigger("change");
        }
      },
      checkIfDataValid: function(e) {
        var blankFields, data, fields;
        fields = this.$el.find("#calculation-form-wrapper").find("input[name='date'], select[name='area_type_id'], select[name='shift_type_id'], select[id='source_position_ids'], select[name='teams_quantity']");
        blankFields = _.filter(fields, function(e) {
          return $(e).val() === null || $(e).val().length === 0 || $(e).val().length === void 0;
        }) || [];
        data = Tipmetric.Controllers.csvImportView.buildImportJson();
        if (blankFields.length === 0 && _.some(data.employeesData)) {
          return this.$el.find(".import-csv").removeAttr("disabled");
        } else {
          return this.$el.find(".import-csv").attr("disabled", "disabled");
        }
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("csv-import-view") && $("body").attr("data-sub-class") === "default-logic" && gon.restaurant.csv_import_enabled) {
        return Tipmetric.Controllers.csvImportFormView = new CsvImportFormView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var CsvMultidateImportView;
    CsvMultidateImportView = Backbone.View.extend({
      el: "#csv-import-view",
      data: {
        tm_fields: ["", ":emp_id", ":first_name", ":last_name", ":first_name :last_name", ":last_name, :first_name", "", ":hours_worked", ":hours_worked_2", ":cc_tips", ":cash_tips", ":ag_svc_tips", ":fee_tips", ":food_sales", ":beverage_sales", ":sales_cc", ":sales_cash", ":position_type", ":start_time", ":end_time", ":date_range"],
        tm_data_fields: [":hours_worked", ":hours_worked_2", ":cc_tips", ":cash_tips", ":ag_svc_tips", ":fee_tips", ":food_sales", ":beverage_sales", ":sales_cc", ":sales_cash"]
      },
      events: {
        "click #upload-file": "uploadFile",
        "click #reset-labels": "resetLabels",
        "click #set-labels": "setLabels",
        "click [data-action='show-csv-shift-select']": "showCsvShiftSelect",
        "change #csv_file": "readCsv",
        "change #selected-field": "columnKeyChanged",
        "change [data-required-field='true']": "setShiftMatching",
        "ifChanged #header_present": "rebuildTableWithOrWithoutHeader",
        "click [data-action='save-associated-positions']": "updatePositionAssociatedPositions",
        "click [data-action='previous-date']": "changeDatePrevious",
        "click [data-action='next-date']": "changeDateNext"
      },
      initialize: function() {
        var that;
        this.headerPresent = gon.restaurant.csv_import_header_present;
        if (this.headerPresent) {
          this.$el.find("#header_present").iCheck("check");
        }
        Tipmetric.Controllers.helpers.bindFilteredRulesSaving(5, function() {
          return Tipmetric.Controllers.csvMultidateImportView.buildCsvTable();
        });
        $.fn.editable.defaults.mode = 'inline';
        $.fn.editable.defaults.ajaxOptions = {
          type: "PUT"
        };
        $.fn.editableform.buttons = "<button type=\"submit\" class=\"btn btn-sm btn-success editable-submit\">SAVE</button> <button type=\"button\" class=\"btn btn-sm btn-default editable-cancel\">CANCEL</button>";
        $(".csv-associated-position-table").find('.start-time, .end-time').editable({
          url: function(params) {
            var json;
            json = {};
            json[$(this).attr("data-field-name")] = params.value;
            return $.ajax({
              url: "/api/position_types/" + params.pk,
              method: "put",
              data: {
                position_type: json
              },
              success: (function(_this) {
                return function(response) {
                  return toastr["success"]("successfully updated", "Success");
                };
              })(this),
              error: function(response) {
                return toastr["error"](response.responseText, "Error");
              }
            });
          }
        });
        that = this;
        return $(".csv-shifts-table-wrapper").find('.start-time, .end-time').editable({
          url: function(params) {
            var fieldName, json;
            json = {};
            json[$(this).attr("data-field-name")] = params.value;
            fieldName = $(this).attr("data-field-name");
            return $.ajax({
              url: "/api/shift_types/" + params.pk,
              method: "put",
              data: {
                shift_type: json
              },
              success: (function(_this) {
                return function(response) {
                  $(_this).attr("data-field-value", response.shift_type[fieldName]);
                  return that.setShiftMatching();
                };
              })(this),
              error: function(response) {
                return toastr["error"](response.responseText, "Error");
              }
            });
          }
        });
      },
      showCsvShiftSelect: function(e) {
        if (e) {
          e.preventDefault();
        }
        return $(".csv-shifts-table-wrapper").toggle();
      },
      resetLabels: function(e) {
        if (e) {
          e.preventDefault();
        }
        return $("tbody.csv-import-data tr .label").remove();
      },
      setLabels: function(e) {
        e.preventDefault();
        this.resetLabels(false);
        this.buildAssociatedPositionsJson();
        return this.identifyPositions();
      },
      updatePositionAssociatedPositions: function(e) {
        var input, json;
        input = $(e.currentTarget).closest('td').find(".csv-associated-positions");
        json = {};
        json[$(input).attr("data-field-name")] = input.val();
        return $.ajax({
          url: "/api/position_types/" + ($(input).attr("data-id")),
          method: "put",
          data: {
            position_type: json
          },
          success: (function(_this) {
            return function(response) {
              return toastr["success"]("successfully updated", "Success");
            };
          })(this),
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      buildAssociatedPositionsJson: function() {
        var j, json, len, ref, tr;
        json = {};
        ref = $(".csv-associated-position-table tbody tr");
        for (j = 0, len = ref.length; j < len; j++) {
          tr = ref[j];
          json[$(tr).attr("data-position-type-id")] = {
            name: $(tr).attr("data-position-type-name"),
            associatedPositions: $(tr).find("input.csv-associated-positions").val(),
            start_time: moment((this.getDate()) + " " + ($(tr).find(".start-time").text()), "MM/DD/YYYY HH:mm A"),
            end_time: moment((this.getDate()) + " " + ($(tr).find(".end-time").text()), "MM/DD/YYYY HH:mm A")
          };
          if (json[$(tr).attr("data-position-type-id")].start_time > json[$(tr).attr("data-position-type-id")].end_time) {
            json[$(tr).attr("data-position-type-id")].end_time = moment((this.getDate()) + " " + ($(tr).find(".end-time").text()), "MM/DD/YYYY HH:mm A").add(1, 'days');
          }
        }
        return this.assiciatedPositionsJson = json;
      },
      getDate: function() {
        if (!_.isEmpty($("#date").val())) {
          return $("#date").val();
        } else {
          return moment().format("MM/DD/YYYY");
        }
      },
      selectedTableValues: function() {
        return _.filter(_.map(this.$el.find(".csv-import-headers th select option:selected"), function(e) {
          return $(e).val();
        }), function(f) {
          return !_.isEmpty(f);
        });
      },
      identifyPositions: function(e) {
        var associatedLineColumnNumber, associatedPosition, csvLine, endTimeLineColumnNumber, isEndTimeCheckEnabled, isPositionCheckEnabled, isStartTimeCheckEnabled, j, len, matchByStartAndEndTime, matchFounded, pair, position, ref, results, shiftEndTime, shiftEndTimeProcessed, shiftStartTime, shiftStartTimeProcessed, startTimeLineColumnNumber;
        matchByStartAndEndTime = _.contains(this.selectedTableValues(), ":start_time") && _.contains(this.selectedTableValues(), ":end_time");
        associatedLineColumnNumber = $(".csv-import-headers th[data-tm-field-name=':position_type']").attr("data-column-number");
        startTimeLineColumnNumber = $(".csv-import-headers th[data-tm-field-name=':start_time']").attr("data-column-number");
        endTimeLineColumnNumber = $(".csv-import-headers th[data-tm-field-name=':end_time']").attr("data-column-number");
        isPositionCheckEnabled = associatedLineColumnNumber !== void 0;
        isStartTimeCheckEnabled = startTimeLineColumnNumber !== void 0;
        isEndTimeCheckEnabled = endTimeLineColumnNumber !== void 0;
        ref = $(".csv-import-data tr");
        results = [];
        for (j = 0, len = ref.length; j < len; j++) {
          csvLine = ref[j];
          associatedPosition = $(csvLine).find("td[data-column-number='" + associatedLineColumnNumber + "']").text();
          if (matchByStartAndEndTime) {
            shiftStartTimeProcessed = $(csvLine).find("[data-tm-field-name=':start_time']").attr("data-value").replace(/^\s*\d{1,2}(\/|-)\d{1,2}(\/|-)\d{2,4}\s/, "").replace(/^\s*\d{2,4}(\/|-)\d{1,2}(\/|-)\d{1,2}\s/, "");
            shiftEndTimeProcessed = $(csvLine).find("[data-tm-field-name=':end_time']").attr("data-value").replace(/^\s*\d{1,2}(\/|-)\d{1,2}(\/|-)\d{2,4}\s/, "").replace(/^\s*\d{2,4}(\/|-)\d{1,2}(\/|-)\d{1,2}\s/, "");
            shiftStartTime = moment((this.getDate()) + " " + shiftStartTimeProcessed, "MM/DD/YYYY HH:mm A");
            shiftEndTime = moment((this.getDate()) + " " + shiftEndTimeProcessed, "MM/DD/YYYY HH:mm A");
            if (shiftStartTime > shiftEndTime) {
              shiftEndTime = shiftEndTime.add(1, 'days');
            }
          }
          if (!isPositionCheckEnabled && !isStartTimeCheckEnabled && !isEndTimeCheckEnabled) {
            continue;
          }
          results.push((function() {
            var k, l, len1, len2, ref1, ref2, results1;
            ref1 = _.pairs(this.assiciatedPositionsJson);
            results1 = [];
            for (k = 0, len1 = ref1.length; k < len1; k++) {
              pair = ref1[k];
              if (isPositionCheckEnabled) {
                matchFounded = false;
                ref2 = pair[1].associatedPositions.split(",");
                for (l = 0, len2 = ref2.length; l < len2; l++) {
                  position = ref2[l];
                  if (associatedPosition.trim() === position.trim()) {
                    matchFounded = true;
                  }
                }
                if (_.isEmpty(pair[1].associatedPositions) || _.isEmpty(associatedPosition) || !matchFounded) {
                  continue;
                }
              }
              if (matchByStartAndEndTime) {
                if (isStartTimeCheckEnabled) {
                  if (shiftStartTime <= pair[1].start_time) {
                    continue;
                  }
                }
                if (isEndTimeCheckEnabled) {
                  if (shiftEndTime >= pair[1].end_time) {
                    continue;
                  }
                }
              }
              $(csvLine).attr("data-position-type-id", pair[0]);
              results1.push($(csvLine).append("<span class='label label-success'>" + pair[1].name + "</span>"));
            }
            return results1;
          }).call(this));
        }
        return results;
      },
      setShiftMatching: function() {
        var csvLine, currentShiftId, filtedPassed, filterData, j, len, ref, results, shiftEndTime, shiftEndTimeProcessed, shiftSelected, shiftStartTime, shiftStartTimeProcessed, shiftTr, tipmetricShiftEnd, tipmetricShiftStart;
        currentShiftId = this.$el.find("select#shift_type_id").val();
        shiftSelected = !_.isEmpty(currentShiftId);
        shiftTr = $(".csv-shifts-table").find("tr[data-shift-type-id='" + currentShiftId + "']");
        tipmetricShiftStart = moment((this.getDate()) + " " + ($(shiftTr).find(".start-time").attr('data-field-value')), "MM/DD/YYYY HH:mm A");
        tipmetricShiftEnd = moment((this.getDate()) + " " + ($(shiftTr).find(".end-time").attr('data-field-value')), "MM/DD/YYYY HH:mm A");
        if (tipmetricShiftStart > tipmetricShiftEnd) {
          tipmetricShiftEnd = tipmetricShiftEnd.add(1, 'days');
        }
        if (_.any($("thead.csv-import-headers").find("[data-tm-field-name=':start_time']")) && _.any($("thead.csv-import-headers").find("[data-tm-field-name=':end_time']")) && shiftSelected) {
          filterData = true;
        } else {
          filterData = false;
        }
        ref = $(".csv-import-data tr");
        results = [];
        for (j = 0, len = ref.length; j < len; j++) {
          csvLine = ref[j];
          if (filterData) {
            shiftStartTimeProcessed = $(csvLine).find("[data-tm-field-name=':start_time']").attr("data-value").replace(/^\s*\d{1,2}(\/|-)\d{1,2}(\/|-)\d{2,4}\s/, "").replace(/^\s*\d{2,4}(\/|-)\d{1,2}(\/|-)\d{1,2}\s/, "");
            shiftEndTimeProcessed = $(csvLine).find("[data-tm-field-name=':end_time']").attr("data-value").replace(/^\s*\d{1,2}(\/|-)\d{1,2}(\/|-)\d{2,4}\s/, "").replace(/^\s*\d{2,4}(\/|-)\d{1,2}(\/|-)\d{1,2}\s/, "");
            shiftStartTime = moment((this.getDate()) + " " + shiftStartTimeProcessed, "MM/DD/YYYY HH:mm A");
            shiftEndTime = moment((this.getDate()) + " " + shiftEndTimeProcessed, "MM/DD/YYYY HH:mm A");
            if (shiftStartTime > shiftEndTime) {
              shiftEndTime = shiftEndTime.add(1, 'days');
            }
            if (shiftStartTime >= tipmetricShiftStart && shiftEndTime <= tipmetricShiftEnd) {
              filtedPassed = "yes";
            } else {
              filtedPassed = "no";
            }
          } else {
            filtedPassed = "yes";
          }
          results.push($(csvLine).attr("data-filter-is-passed", filtedPassed));
        }
        return results;
      },
      getDate: function() {
        if (!_.isEmpty($("#date").val())) {
          return $("#date").val();
        } else {
          return moment().format("MM/DD/YYYY");
        }
      },
      selectHtml: function() {
        var field, html, j, len, options, ref, visibleFieldName;
        html = '';
        options = '';
        ref = this.data.tm_fields;
        for (j = 0, len = ref.length; j < len; j++) {
          field = ref[j];
          visibleFieldName = this.applyEditLabels(field);
          options += "<option value='" + field + "'>" + visibleFieldName + "</option>";
        }
        return html += '<select name="field" id="selected-field" class="form-control select-22" data-required-field="true"><option value="">Tipmetric field</option>' + options + '</select>';
      },
      applyEditLabels: function(field) {
        if (_.contains([":ag_svc_tips", ":food_sales", ":beverage_sales", ":sales_cc", ":sales_cash"], field)) {
          if (!_.isEmpty(_.findWhere(gon.restaurant.custom_labels.source_type_labels_nulled, {
            key: field
          }).name)) {
            return _.findWhere(gon.restaurant.custom_labels.source_type_labels_nulled, {
              key: field
            }).name;
          } else {
            return field;
          }
        } else {
          return field;
        }
      },
      columnKeyChanged: function(e) {
        this.setColumnKey(e);
        this.setShiftMatching();
        return this.setDateActivity();
      },
      setColumnKey: function(e) {
        var columnNumber, j, len, ref, td, tmKey, tmKeyForAttribute;
        columnNumber = $(e.currentTarget).closest("th").attr("data-column-number");
        tmKey = $(e.currentTarget).val();
        $(e.currentTarget).closest("table").find("[data-column-number='" + columnNumber + "']").attr("data-tm-field-name", tmKey);
        ref = this.$el.find("[data-tm-field-name='" + tmKey + "']");
        for (j = 0, len = ref.length; j < len; j++) {
          td = ref[j];
          this.$el.find(td).attr("data-value", this.$el.find(td).text());
          tmKeyForAttribute = tmKey.replaceAll("_", "-").replaceAll(" ", "-").replaceAll(", ", "-").replaceAll(",", "-").replaceAll(":", "").replaceAll("--", "-");
          this.$el.find(td).closest("tr").attr(tmKeyForAttribute, this.$el.find(td).text());
        }
        if (this.isDateRangePresent()) {
          $(".selecting-date-wrapper").show();
        } else {
          $(".selecting-date-wrapper").hide();
        }
        return this.collectUniqSortedDates();
      },
      updateDateSelectMarker: function() {
        var currentIndex, length;
        length = this.importedDataDatesUniq.length;
        currentIndex = this.importedDataDatesUniq.indexOf(this.currentDate) + 1;
        return $(".date-select-marker").text("(" + currentIndex + "/" + length + ")");
      },
      collectUniqSortedDates: function() {
        var date, dates, j, len;
        $(".date-select-options").html("");
        dates = _.sortBy(_.uniq(_.collect($(".csv-import-data tr"), function(f) {
          return $(f).attr("date-range");
        })), function(f) {
          return moment(f, "MM/DD/YYYY HH:mm A");
        });
        for (j = 0, len = dates.length; j < len; j++) {
          date = dates[j];
          $(".date-select-options").append("<span class='date-option btn btn-default' data-date='" + date + "'>" + date + "</span>");
        }
        $(".date-select-options .date-option").hide();
        this.importedDataDatesUniq = dates;
        this.currentDate = dates[0];
        this.updateDateSelectMarker();
        $(".date-select-options").find(".date-option[data-date='" + this.currentDate + "']").show();
        return this.filterDataByDate();
      },
      changeDateNext: function() {
        var currentDateIndex, nextDate;
        currentDateIndex = this.importedDataDatesUniq.indexOf(this.currentDate);
        nextDate = this.importedDataDatesUniq[currentDateIndex + 1];
        if (nextDate) {
          $(".date-select-options .date-option").hide();
          $(".date-select-options").find(".date-option[data-date='" + nextDate + "']").show();
          this.currentDate = nextDate;
        }
        this.filterDataByDate();
        return this.updateDateSelectMarker();
      },
      changeDatePrevious: function() {
        var currentDateIndex, previous;
        currentDateIndex = this.importedDataDatesUniq.indexOf(this.currentDate);
        previous = this.importedDataDatesUniq[currentDateIndex - 1];
        if (previous) {
          $(".date-select-options .date-option").hide();
          $(".date-select-options").find(".date-option[data-date='" + previous + "']").show();
          this.currentDate = previous;
        }
        this.filterDataByDate();
        return this.updateDateSelectMarker();
      },
      filterDataByDate: function() {
        if (this.isDateRangePresent()) {
          $(".csv-import-data").find("tr").hide();
          $(".csv-import-data").find("tr").attr("data-include-to-import", "no");
          $(".csv-import-data").find("tr[date-range='" + this.currentDate + "']").show();
          return $(".csv-import-data").find("tr[date-range='" + this.currentDate + "']").attr("data-include-to-import", "yes");
        } else {
          $(".csv-import-data").find("tr").show();
          return $(".csv-import-data").find("tr").attr("data-include-to-import", "yes");
        }
      },
      setDateActivity: function() {
        if (this.isDateRangePresent()) {
          return $("#date").attr("disabled", "disabled");
        } else {
          return $("#date").removeAttr("disabled");
        }
      },
      isDateRangePresent: function() {
        var selectedValues;
        selectedValues = _.filter(_.map(this.$el.find(".csv-import-headers th select option:selected"), (function(_this) {
          return function(e) {
            return $(e).val();
          };
        })(this)), function(f) {
          return !_.isEmpty(f);
        });
        return _.contains(selectedValues, ":date_range");
      },
      buildImportJson: function() {
        var employeeData, employeeTr, fieldName, id, j, json, k, len, len1, position_type_id, ref, ref1, rowNumber, td, value;
        json = {
          calcualtionParams: {},
          employeesData: [],
          frontend_restaurant_id: gon.restaurant.id
        };
        json.importedDataDates = [];
        ref = this.$el.find(".csv-import-data tr");
        for (j = 0, len = ref.length; j < len; j++) {
          employeeTr = ref[j];
          if ($(employeeTr).attr("data-filter-is-passed") === "no") {
            continue;
          }
          if ($(employeeTr).attr("data-include-to-import") === "no") {
            continue;
          }
          employeeData = {};
          id = this.$el.find(employeeTr).attr("data-emp-id");
          if (!_.isEmpty(id)) {
            employeeData[":emp_id"] = id;
          }
          position_type_id = this.$el.find(employeeTr).attr("data-position-type-id");
          if (!_.isEmpty(position_type_id)) {
            employeeData["position_type_id"] = position_type_id;
          }
          rowNumber = this.$el.find(employeeTr).attr("data-row-number");
          employeeData["row_number"] = rowNumber;
          ref1 = this.$el.find(employeeTr).find("td");
          for (k = 0, len1 = ref1.length; k < len1; k++) {
            td = ref1[k];
            fieldName = this.$el.find(td).attr("data-tm-field-name");
            value = this.$el.find(td).text();
            if (_.isEmpty(value.replace(" ", ""))) {
              value = "0";
            }
            if (!_.isEmpty(fieldName) && !_.isEmpty(value)) {
              employeeData[fieldName] = value;
            }
          }
          if (!_.isEmpty(employeeData[":date_range"])) {
            json.importedDataDates.push(employeeData[":date_range"]);
          }
          employeeData[":hours_worked"] = parseFloat(employeeData[":hours_worked"] || 0) + parseFloat(employeeData[":hours_worked_2"] || 0);
          json.employeesData.push(employeeData);
        }
        json.importedDataDates = _.uniq(json.importedDataDates);
        json.selectedTableValues = _.filter(_.map(this.$el.find(".csv-import-headers th select option:selected"), function(e) {
          return $(e).val();
        }), function(f) {
          return !_.isEmpty(f);
        });
        json.isDateRangePresent = this.isDateRangePresent();
        json.calcualtionParams = {
          date: this.currentDate,
          area_type_id: this.$el.find("select#area_type_id").val(),
          shift_type_id: this.$el.find("select#shift_type_id").val(),
          source_position_type_ids: this.$el.find("select#source_position_ids").val(),
          teams_quantity: 1
        };
        return json;
      },
      uploadFile: function(e) {
        e.preventDefault();
        this.resetImportData();
        return this.$el.find("#csv_file").click();
      },
      resetImportDataByPageType: function(e) {
        if (this.isDateRangePresent()) {
          return this.clearCurrentDateData();
        } else {
          return this.resetImportData();
        }
      },
      resetImportData: function(e) {
        this.$el.find("#csv_file").val("");
        this.$el.find(".csv-import-headers").html("");
        this.$el.find(".csv-import-data").html("");
        this.$el.find(".import-errors > *").html("");
        Tipmetric.Data.allowedDates = [""];
        Tipmetric.Data.skippedDates = [""];
        return Tipmetric.Controllers.csvMultidateImportFormView.checkIfDataValid();
      },
      clearCurrentDateData: function(e) {
        $(".csv-import-data").find("tr[date-range='" + this.currentDate + "'][data-include-to-import='yes']").remove();
        this.collectUniqSortedDates();
        this.$el.find(".import-errors > *").html("");
        Tipmetric.Data.allowedDates = [""];
        Tipmetric.Data.skippedDates = [""];
        return Tipmetric.Controllers.csvMultidateImportFormView.checkIfDataValid();
      },
      readCsv: function(e) {
        var file, reader;
        file = e.target.files[0];
        reader = new FileReader();
        reader.readAsText(file);
        reader.onload = (function(_this) {
          return function(event) {
            var csv;
            csv = event.target.result;
            _this.csv = csv;
            return _this.buildCsvTable();
          };
        })(this);
        return reader.onerror = function() {
          return alert('Unable to read ' + file.fileName);
        };
      },
      rebuildTableWithOrWithoutHeader: function(e) {
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return true;
        }
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: {
            restaurant: {
              csv_import_header_present: e.currentTarget.checked
            }
          },
          success: (function(_this) {
            return function() {
              _this.headerPresent = e.currentTarget.checked;
              if (_.some($(".csv-import-data tr"))) {
                return _this.buildCsvTable();
              }
            };
          })(this),
          error: function() {
            return console.log("-");
          }
        });
      },
      buildCsvTable: function() {
        var data, header_row, i, item, j, k, l, len, len1, len2, row, row_i, tr_data, tr_head;
        this.resetImportData();
        data = $.csv.toArrays(this.csv);
        tr_head = '';
        tr_data = '';
        row_i = 1;
        header_row = data[0];
        tr_head += '<tr>';
        i = 0;
        tr_head += "<th></th>";
        for (j = 0, len = header_row.length; j < len; j++) {
          item = header_row[j];
          if (!this.headerPresent) {
            item = "";
          }
          tr_head += ("<th data-column-number='" + i + "' data-tm-field-name='" + item + "'>") + item + this.selectHtml() + "</th>";
          i += 1;
        }
        tr_head += '</tr>';
        for (k = 0, len1 = data.length; k < len1; k++) {
          row = data[k];
          if (row === data[0] && this.headerPresent) {
            continue;
          }
          if (_.isEmpty(_.filter(row, function(e) {
            return !_.isEmpty(e);
          }))) {
            continue;
          }
          tr_data += "<tr data-filter-is-passed='' data-row-number='" + row_i + "'><td data-tm-field-name='employee-line-number' data-tm-field-val='" + row_i + "'>" + row_i + "</td>";
          i = 0;
          for (l = 0, len2 = row.length; l < len2; l++) {
            item = row[l];
            if (!_.isEmpty(item.match(/\d+,\d+/))) {
              item = item.replace(",", "");
            }
            tr_data += ("<td data-column-number='" + i + "'>") + item + "</td>";
            i += 1;
          }
          tr_data += '</tr>';
          row_i += 1;
        }
        this.$el.find(".csv-import-headers").prepend(tr_head);
        this.$el.find(".csv-import-data").prepend(tr_data);
        this.$el.find("#calculation-form-wrapper").show();
        this.$el.find(".header_present_wrapper").show();
        window.bindCheckboxes();
        return Tipmetric.Controllers.helpers.filterImportDataByPosition($("#built-import-table"), 5);
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("csv-import-view") && $("body").attr("data-sub-class") === "multidate-logic" && gon.restaurant.csv_multidate_import_enabled) {
        return Tipmetric.Controllers.csvMultidateImportView = new CsvMultidateImportView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var CsvMultidateImportFormView;
    CsvMultidateImportFormView = Backbone.View.extend({
      el: "#csv-import-view",
      events: {
        "change #area_type_id": "areaUpdated",
        "change #shift_type_id": "shiftUpdated",
        "change [data-required-field='true']": "checkIfDataValid",
        "change .changeble-data select": "calculationChanged",
        "blur #date": "dateBlured",
        "click .import-csv": "checkCsvImportData",
        "click .import-errors [data-action='remove']": "removeRow",
        "click .import-errors [data-action='create']": "createEmployee",
        "click .import-errors [data-action='add']": "addEmployee",
        "click .import-errors [data-action='match']": "matchEmployee",
        "click .import-errors [data-action='remove-match']": "removeMatchEmployee",
        "click .import-errors [data-action='remove-duplicated-line']": "removeDuplication",
        "click .import-errors [data-action='merge-duplications']": "mergeDuplications",
        "click .import-errors [data-action='select-position']": "selectPosition",
        "click .import-errors [data-action='link-to-position']": "linkEmployeeToPosition",
        "click .import-errors [data-action='update-date']": "allowUpdatingDate",
        "click .import-errors [data-action='skip-date']": "skipDateImport",
        "click .import-errors [data-action='hide-error']": "hideError",
        "click button[data-calculation-action]": "chooseCalculationVariant",
        "click .date-selection-arrow b": "showDatePicker",
        "click [data-action='show-csv-config']": "toggleConfigBlock"
      },
      initialize: function() {
        var date, today;
        gon.restaurant_inheritance_data = Tipmetric.Controllers.helpers.buildSmartData();
        date = new Date();
        today = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        $('.datapicker').datepicker({
          todayHighlight: true
        }).on("changeDate", (function(_this) {
          return function() {
            return _this.dateUpdated();
          };
        })(this)).on("show", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').addClass('open');
          };
        })(this)).on("hide", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').removeClass('open');
          };
        })(this)).datepicker('setDate', today);
        this.checkIfDataValid();
        this.dateUpdated();
        this.areaUpdated();
        this.shiftUpdated();
        Tipmetric.Data.allowedDates = [""];
        return Tipmetric.Data.skippedDates = [""];
      },
      importCsv: function(e) {
        if (e) {
          e.preventDefault();
        }
        return this.sendImportCsv(false);
      },
      sendImportCsv: function(allowUpdate) {
        var data;
        data = Tipmetric.Controllers.csvMultidateImportView.buildImportJson();
        data.allowedDates = Tipmetric.Data.allowedDates;
        data.skippedDates = Tipmetric.Data.skippedDates;
        data.allowUpdate = allowUpdate;
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          method: "post",
          url: "/api/calculations/import_csv",
          data: data,
          success: function(response) {
            toastr["success"]("Data has been imported.", "Success");
            Tipmetric.Controllers.spinner.hide();
            return Tipmetric.Controllers.csvMultidateImportView.resetImportDataByPageType();
          },
          error: (function(_this) {
            return function(response) {
              Tipmetric.Controllers.spinner.hide();
              if (response.responseJSON.calculation_exists === true) {
                return swal({
                  title: "",
                  text: response.responseJSON.errors,
                  type: "info",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "Yes",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirmed) {
                  if (isConfirmed.value) {
                    return _this.sendImportCsv(true);
                  }
                });
              } else {
                return toastr["error"]("Data is not imported.", "Error");
              }
            };
          })(this)
        });
      },
      checkCsvImportData: function(e) {
        e.preventDefault();
        return this.sendCheckCsvImportDataRequest(false, e);
      },
      sendCheckCsvImportDataRequest: function(allowUpdate, e) {
        var data, selectedValues;
        if ($(e.currentTarget).attr("disabled") !== void 0) {
          return false;
        }
        data = Tipmetric.Controllers.csvMultidateImportView.buildImportJson();
        data.allowUpdate = allowUpdate;
        selectedValues = _.filter(_.map(this.$el.find(".csv-import-headers th select option:selected"), function(e) {
          return $(e).val();
        }), function(f) {
          return !_.isEmpty(f);
        });
        if (!_.contains(selectedValues, ":last_name") && !_.contains(selectedValues, ":emp_id") && !_.contains(selectedValues, ":first_name :last_name") && !_.contains(selectedValues, ":last_name, :first_name")) {
          toastr["error"]("Cannot Import...Please select an Emp ID or Last Name", "Error");
          return false;
        }
        if (!_.some(data.calcualtionParams.date.match(/\d+\/\d+\/\d{4}/))) {
          toastr["error"]("Cannot Import...Please select correct Date", "Error");
          return false;
        }
        return $.ajax({
          method: "post",
          url: "/api/calculations/check_csv_import_data",
          data: data,
          success: (function(_this) {
            return function(response) {
              _this.matchIdentifiedemployees(response);
              _this.$el.find(".import-errors > *").html('');
              return _this.sendImportCsv(allowUpdate);
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              var duplications, employee, employeeEmpId, html1, html3, html4, html5, html6, html7, i, j, k, l, len, len1, len2, len3, len4, len5, m, o, ref, ref1, ref2, ref3, ref4, ref5;
              window.response = response;
              _this.matchIdentifiedemployees(response.responseJSON);
              Tipmetric.TestSupport.displayErrors(response.responseJSON.errors);
              if (response.responseJSON.critical_error) {
                swal({
                  title: "",
                  text: response.responseJSON.critical_error_message,
                  type: "error",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "OK",
                  showCancelButton: false
                });
                return;
              }
              if (response.responseJSON.calculation_exists === true && allowUpdate === false) {
                return swal({
                  title: "",
                  text: response.responseJSON.errors,
                  type: "info",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "Yes",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirmed) {
                  if (isConfirmed.value) {
                    return _this.sendCheckCsvImportDataRequest(true, e);
                  }
                });
              } else {
                if (response.responseJSON.errors) {
                  toastr["error"](response.responseJSON.errors, "Error");
                  return;
                }
                html1 = '';
                ref = response.responseJSON.employees_doesnt_exist;
                for (i = 0, len = ref.length; i < len; i++) {
                  employee = ref[i];
                  html1 += JST["csv_import/does_not_exist"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-exist").html(html1);
                html3 = '';
                ref1 = response.responseJSON.last_name_duplication;
                for (j = 0, len1 = ref1.length; j < len1; j++) {
                  employee = ref1[j];
                  html3 += JST["csv_import/last_name_duplication"](employee);
                }
                _this.$el.find(".import-errors .last-name-duplication").html(html3);
                html4 = '';
                ref2 = response.responseJSON.employee_in_multiple_positions;
                for (k = 0, len2 = ref2.length; k < len2; k++) {
                  employee = ref2[k];
                  html4 += JST["csv_import/employee_in_multiple_positions"](employee);
                }
                _this.$el.find(".import-errors .employee-in-multiple-positions").html(html4);
                html5 = '';
                ref3 = response.responseJSON.employees_doesnt_belong_to_any_position;
                for (l = 0, len3 = ref3.length; l < len3; l++) {
                  employee = ref3[l];
                  html5 += JST["csv_import/employees_doesnt_belong_to_any_position"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-belong-to-any-position").html(html5);
                html6 = '';
                ref4 = _.keys(response.responseJSON.duplicated_employees_grouped);
                for (m = 0, len4 = ref4.length; m < len4; m++) {
                  employeeEmpId = ref4[m];
                  duplications = _.map(response.responseJSON.duplicated_employees_grouped[employeeEmpId], function(f) {
                    f.filledFields = _this.filledFields(f);
                    return f;
                  });
                  html6 += JST["csv_import/duplicated_employee"](duplications);
                }
                _this.$el.find(".import-errors .employees-are-duplicated").html(html6);
                html7 = '';
                ref5 = response.responseJSON.employees_doesnt_belong_auto_selected_position;
                for (o = 0, len5 = ref5.length; o < len5; o++) {
                  employee = ref5[o];
                  html7 += JST["csv_import/employees_doesnt_belong_auto_selected_position"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-belong-auto-selected-position").html(html7);
                return toastr["error"]("Looks like import was not successfull.", "Error");
              }
            };
          })(this)
        });
      },
      processResponse: function(key, response, allowUpdate) {
        var dateWrapper, dateWrapperKey, duplications, employee, employeeEmpId, html1, html3, html4, html5, html6, html7, html8, html9, i, j, k, l, len, len1, len2, len3, len4, len5, m, o, ref, ref1, ref2, ref3, ref4, ref5;
        window.response = response;
        this.matchIdentifiedemployees(response);
        Tipmetric.TestSupport.displayErrors(response);
        dateWrapperKey = ".import-errors[data-errors-for-date=\"" + key + "\"]";
        $("" + dateWrapperKey).remove();
        dateWrapper = JST["csv_import/date_errors_wrapper"](key);
        this.$el.find(".import-errors-wrapper").append(dateWrapper);
        html1 = '';
        ref = response.employees_doesnt_exist;
        for (i = 0, len = ref.length; i < len; i++) {
          employee = ref[i];
          html1 += JST["csv_import/does_not_exist"](employee);
        }
        this.$el.find(".import-errors" + dateWrapperKey + " .employees-doesnt-exist").html(html1);
        html3 = '';
        ref1 = response.last_name_duplication;
        for (j = 0, len1 = ref1.length; j < len1; j++) {
          employee = ref1[j];
          html3 += JST["csv_import/last_name_duplication"](employee);
        }
        this.$el.find(".import-errors" + dateWrapperKey + " .last-name-duplication").html(html3);
        html4 = '';
        ref2 = response.employee_in_multiple_positions;
        for (k = 0, len2 = ref2.length; k < len2; k++) {
          employee = ref2[k];
          html4 += JST["csv_import/employee_in_multiple_positions"](employee);
        }
        this.$el.find(".import-errors" + dateWrapperKey + " .employee-in-multiple-positions").html(html4);
        html5 = '';
        ref3 = response.employees_doesnt_belong_to_any_position;
        for (l = 0, len3 = ref3.length; l < len3; l++) {
          employee = ref3[l];
          html5 += JST["csv_import/employees_doesnt_belong_to_any_position"](employee);
        }
        this.$el.find(".import-errors" + dateWrapperKey + " .employees-doesnt-belong-to-any-position").html(html5);
        html6 = '';
        ref4 = _.keys(response.duplicated_employees_grouped);
        for (m = 0, len4 = ref4.length; m < len4; m++) {
          employeeEmpId = ref4[m];
          duplications = _.map(response.duplicated_employees_grouped[employeeEmpId], (function(_this) {
            return function(f) {
              f.filledFields = _this.filledFields(f);
              return f;
            };
          })(this));
          html6 += JST["csv_import/duplicated_employee"](duplications);
        }
        this.$el.find(".import-errors" + dateWrapperKey + " .employees-are-duplicated").html(html6);
        html7 = '';
        ref5 = response.employees_doesnt_belong_auto_selected_position;
        for (o = 0, len5 = ref5.length; o < len5; o++) {
          employee = ref5[o];
          html7 += JST["csv_import/employees_doesnt_belong_auto_selected_position"](employee);
        }
        this.$el.find(".import-errors" + dateWrapperKey + " .employees-doesnt-belong-auto-selected-position").html(html7);
        if (response.calculation_exists === true) {
          html8 = JST["csv_import/calculation_exists"](response);
          this.$el.find(".import-errors" + dateWrapperKey + " .calculation-exists-wrapper").html(html8);
        }
        if (response.day_is_not_enabled_error === true) {
          html9 = JST["csv_import/day_is_not_enabled_error"](response);
          return this.$el.find(".import-errors" + dateWrapperKey + " .day-is-not-enabled-error-wrapper").html(html9);
        }
      },
      hideError: function(e) {
        return $(e.currentTarget).closest(".day-is-not-enabled-error").remove();
      },
      skipDateImport: function(e) {
        var date;
        date = $(e.currentTarget).closest(".calculation-exists").attr("data-calculation-date");
        Tipmetric.Data.skippedDates.push(date);
        $(e.currentTarget).closest(".calculation-exists").remove();
        $(".import-errors[data-errors-for-date='" + date + "']").remove();
        console.log(Tipmetric.Data.skippedDates);
        return $("[data-tm-field-name=':date_range'][data-value='" + date + "']").closest("tr").remove();
      },
      allowUpdatingDate: function(e) {
        var date;
        date = $(e.currentTarget).closest(".calculation-exists").attr("data-calculation-date");
        Tipmetric.Data.allowedDates.push(date);
        $(e.currentTarget).closest(".calculation-exists").remove();
        $(".import-errors[data-errors-for-date='" + date + "']").remove();
        return console.log(Tipmetric.Data.allowedDates);
      },
      matchIdentifiedemployees: function(response) {
        var employee, i, len, ref, results, tr;
        ref = response.identified_employees;
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          employee = ref[i];
          tr = this.$el.find(".csv-import-data tr[data-row-number='" + employee.row_number + "']");
          results.push(tr.attr("data-emp-id", employee.employee_data.emp_id));
        }
        return results;
      },
      removeRow: function(e) {
        var empId, firstAndLastName, lastAndFirstName, row, rowNumber;
        empId = $(e.currentTarget).attr("data-emp-id");
        rowNumber = $(e.currentTarget).attr("data-row-number");
        firstAndLastName = $(e.currentTarget).attr("data-first-and-last-name");
        lastAndFirstName = $(e.currentTarget).attr("data-last-and-fist-name");
        if (rowNumber) {
          row = this.$el.find("tr[data-row-number='" + rowNumber + "']");
        } else if (firstAndLastName) {
          row = this.$el.find("td[data-tm-field-name=':first_name :last_name'][data-value='" + firstAndLastName + "']").closest("tr");
        } else if (lastAndFirstName) {
          row = this.$el.find("td[data-tm-field-name=':last_name, :first_name'][data-value='" + lastAndFirstName + "']").closest("tr");
        }
        row.remove();
        return $(e.currentTarget).closest(".doesnt-exist-warning-wrapper").remove();
      },
      matchEmployee: function(e) {
        var currentParagraph, empId, empName, lineNumber, positionId, tr;
        currentParagraph = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper");
        empId = $(currentParagraph).find("select.employee-select option:selected").val();
        empName = $(currentParagraph).find("select.employee-select option:selected").text();
        positionId = $(currentParagraph).find("select.employee-select option:selected").attr("position-id");
        lineNumber = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        tr.attr("data-emp-id", empId);
        tr.attr("data-position-type-id", positionId);
        return $(currentParagraph).remove();
      },
      removeMatchEmployee: function(e) {
        var currentParagraph, lineNumber, tr;
        currentParagraph = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper, .employees-doesnt-belong-to-any-position-wrapper, .employees-doesnt-belong-auto-selected-position-wrapper");
        lineNumber = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper, .employees-doesnt-belong-to-any-position-wrapper, .employees-doesnt-belong-auto-selected-position-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        $(tr).remove();
        return $(currentParagraph).remove();
      },
      removeDuplication: function(e) {
        var currentParagraph, duplicationBlock, lineNumber, tr;
        duplicationBlock = $(e.currentTarget).closest(".employee-is-duplicated");
        currentParagraph = $(e.currentTarget).closest(".employee-duplication-line");
        lineNumber = $(e.currentTarget).attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        $(tr).remove();
        $(currentParagraph).remove();
        if ($(duplicationBlock).find(".employee-duplication-line").length === 0) {
          return $(duplicationBlock).remove();
        }
      },
      filledFields: function(data) {
        var dataType, html, i, len, tm_data_fields_to_add;
        html = "";
        tm_data_fields_to_add = Tipmetric.Controllers.csvMultidateImportView.data.tm_data_fields;
        for (i = 0, len = tm_data_fields_to_add.length; i < len; i++) {
          dataType = tm_data_fields_to_add[i];
          if (data[dataType]) {
            html += " " + data[dataType] + " (" + dataType + ")";
          }
        }
        return html;
      },
      mergeDuplications: function(e) {
        var dataType, duplicationBlock, empId, finalAddedNumbers, finalFilledLine, i, j, k, len, len1, len2, lineNumbers, lineToRemove, lines, otherLines, summ, tableRows, td, tm_data_fields_to_add, tr, values;
        duplicationBlock = $(e.currentTarget).closest(".employee-is-duplicated");
        lines = $(duplicationBlock).find(".employee-duplication-line");
        lineNumbers = _.collect(lines, function(line) {
          return $(line).attr("data-line-number");
        });
        console.log(lineNumbers);
        empId = $(duplicationBlock).attr("data-emp-id");
        tm_data_fields_to_add = Tipmetric.Controllers.csvMultidateImportView.data.tm_data_fields;
        finalAddedNumbers = {};
        tableRows = _.filter($(".csv-import-data tr[data-emp-id='" + empId + "']"), function(f) {
          return _.contains(lineNumbers, $(f).attr("data-row-number"));
        });
        for (i = 0, len = tm_data_fields_to_add.length; i < len; i++) {
          dataType = tm_data_fields_to_add[i];
          values = _.map($(tableRows).find("td[data-tm-field-name='" + dataType + "']"), function(f) {
            return parseFloat($(f).attr("data-value")) || 0;
          });
          if (!_.isEmpty(values)) {
            summ = _.reduce(values, function(memo, num) {
              return memo + num;
            }) || 0;
            finalAddedNumbers[dataType] = summ.toFixed(2);
          }
        }
        finalFilledLine = lineNumbers[0];
        for (j = 0, len1 = tm_data_fields_to_add.length; j < len1; j++) {
          dataType = tm_data_fields_to_add[j];
          if (finalAddedNumbers[dataType]) {
            td = $(".csv-import-data tr[data-emp-id='" + empId + "'][data-row-number='" + finalFilledLine + "'] td[data-tm-field-name='" + dataType + "'] ");
            $(td).attr("data-value", finalAddedNumbers[dataType]);
            $(td).text(finalAddedNumbers[dataType]);
          }
        }
        otherLines = _.filter(lineNumbers, function(n) {
          return n !== finalFilledLine;
        });
        for (k = 0, len2 = otherLines.length; k < len2; k++) {
          lineToRemove = otherLines[k];
          tr = this.$el.find("tr[data-emp-id='" + empId + "'][data-row-number='" + lineToRemove + "']");
          tr.remove();
        }
        return $(duplicationBlock).remove();
      },
      selectPosition: function(e) {
        var currentParagraph, lineNumber, positionId, tr;
        currentParagraph = $(e.currentTarget).closest(".employee-in-multiple-positions-wrapper");
        positionId = $(currentParagraph).find("select.position-type-select option:selected").val();
        lineNumber = $(e.currentTarget).closest(".employee-in-multiple-positions-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        tr.attr("data-position-type-id", positionId);
        return $(currentParagraph).remove();
      },
      addEmployee: function(e) {
        var currentParagraph;
        currentParagraph = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        return currentParagraph.find(".doesnt-exist-warning-form").show();
      },
      createEmployee: function(e) {
        var currentParagraph, empId, firstName, lastName, positionId;
        e.preventDefault();
        currentParagraph = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        positionId = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper").find("select.position-type-select option:selected").val();
        empId = $(currentParagraph).find("input[name='emp_id']").val();
        firstName = $(currentParagraph).find("input[name='first_name']").val();
        lastName = $(currentParagraph).find("input[name='last_name']").val();
        return $.ajax({
          url: "api/employees",
          method: "post",
          data: {
            employee: {
              emp_id: empId,
              first_name: firstName,
              last_name: lastName,
              position_type_ids: [positionId]
            }
          },
          success: function(response) {
            $(currentParagraph).remove();
            return toastr["success"]("Emloyee has been created.", "Success");
          },
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      linkEmployeeToPosition: function(e) {
        var currentParagraph, empId, positionId;
        e.preventDefault();
        currentParagraph = $(e.currentTarget).closest(".employees-doesnt-belong-auto-selected-position-wrapper");
        empId = $(e.currentTarget).attr("data-emp-id");
        positionId = $(e.currentTarget).attr("data-position-id");
        return $.ajax({
          url: "api/employees/add_position",
          method: "patch",
          data: {
            empId: empId,
            position_type_id: positionId
          },
          success: function(response) {
            $(currentParagraph).remove();
            return toastr["success"]("Emloyee has been added to position.", "Success");
          },
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      hidePopover: function() {
        return this.$el.find("[data-toggle='popover']").popover('hide');
      },
      toggleConfigBlock: function(e) {
        return $("#config-wrapper-block").toggle();
      },
      showCalculation: function(e) {
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return false;
        }
        e.preventDefault();
        if (Tipmetric.Controllers.calculationView && Tipmetric.Controllers.calculationView.locked) {
          return false;
        }
        if (this.$el.find(".changeble-data").hasClass("changed")) {
          return this.updateCalculation(e);
        } else {
          return this.checkCalculation();
        }
      },
      showDatePicker: function(e) {
        e.preventDefault();
        return $('#date').focus();
      },
      dateUpdated: function() {
        var area, areas, dayName, i, len, options, savedVal;
        if (!_.isEmpty(this.$el.find("#date").val())) {
          this.$el.find("#date").attr("data-saved-date", this.$el.find("#date").val());
        }
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        if (gon.restaurant_inheritance_data[dayName]) {
          areas = _.map(gon.restaurant_inheritance_data[dayName].areas, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          options = "";
          options += "<option value=''>Area</option>";
          for (i = 0, len = areas.length; i < len; i++) {
            area = areas[i];
            options += "<option value='" + area.id + "'>" + area.name + "</option>";
          }
          savedVal = this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          this.$el.find("#calculation-form-wrapper").find("select#area_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val(savedVal).trigger("change");
        } else {
          this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val("").trigger("change").html("<option value=''>Area</option>");
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return this.$el.find("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      areaUpdated: function() {
        var areaName, dayName, i, len, options, savedVal, shift, shifts;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option[value='" + (this.$el.find("select#area_type_id").val()) + "']").text();
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas[areaName]) {
          shifts = _.map(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          options = "";
          options += "<option value=''>Shift</option>";
          for (i = 0, len = shifts.length; i < len; i++) {
            shift = shifts[i];
            options += "<option value='" + shift.id + "'>" + shift.name + "</option>";
          }
          savedVal = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val(savedVal).trigger("change");
        } else {
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return this.$el.find("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      shiftUpdated: function() {
        var areaData, areaName, dayName, firstPosition, i, len, options, position, positions, savedVal, shiftName;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option[value='" + ($('select#area_type_id').val()) + "']").text();
        shiftName = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id option[value='" + (this.$el.find("select#shift_type_id").val()) + "']").text();
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas) {
          areaData = gon.restaurant_inheritance_data[dayName].areas[areaName];
        }
        if (areaData && areaData.shifts[shiftName]) {
          positions = _.map(areaData.shifts[shiftName].positions, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          if (_.some(positions)) {
            firstPosition = positions[0].id;
          } else {
            firstPosition = "";
          }
          options = "";
          for (i = 0, len = positions.length; i < len; i++) {
            position = positions[i];
            options += "<option value='" + position.id + "'>" + position.name + "</option>";
          }
          savedVal = this.$el.find("select#source_position_ids").val();
          if (!savedVal) {
            savedVal = firstPosition;
          }
          this.$el.find("select#source_position_ids").html(options);
          return this.$el.find("select#source_position_ids").val(savedVal).trigger("change");
        } else {
          return this.$el.find("select#source_position_ids").val("").trigger("change");
        }
      },
      checkIfDataValid: function(e) {
        var blankFields, data, fields;
        fields = this.$el.find("#calculation-form-wrapper").find("input[name='date'], select[name='area_type_id'], select[name='shift_type_id'], select[id='source_position_ids'], select[name='teams_quantity']");
        blankFields = _.filter(fields, function(e) {
          return $(e).val() === null || $(e).val().length === 0 || $(e).val().length === void 0;
        }) || [];
        data = Tipmetric.Controllers.csvMultidateImportView.buildImportJson();
        if (blankFields.length === 0 && _.some(data.employeesData)) {
          return this.$el.find(".import-csv").removeAttr("disabled");
        } else {
          return this.$el.find(".import-csv").attr("disabled", "disabled");
        }
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("csv-import-view") && $("body").attr("data-sub-class") === "multidate-logic" && gon.restaurant.csv_multidate_import_enabled) {
        return Tipmetric.Controllers.csvMultidateImportFormView = new CsvMultidateImportFormView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var CustomReportsView;
    CustomReportsView = Backbone.View.extend({
      el: "body.custom-reports",
      data: {
        tmFields: ["", ":emp_id", ":emp_id_group_by", ":first_name", ":last_name", ":first_name :last_name", ":last_name, :first_name", ":position_type_id", "", ":hours_worked", ":cc_tips", ":cash_tips", ":ag_svc_tips", ":fee_tips", ":food_sales", ":beverage_sales", ":sales_cc", ":sales_cash", ":cc_tips_collected", ":cash_tips_collected", ":ag_svc_tips_collected", ":fee_tips_collected", ":calculation"]
      },
      events: {
        "click [data-action=download-custom-report]": "saveCustomReportParams",
        "click #add-icon-wrapper": "createColumn",
        "click [data-action='remove-column']": "removeColumn",
        "click [data-action='edit-column']": "editTitle",
        "click [data-action='add-column-source']": "addColumnSource",
        "click [data-action='remove-column-source']": "removeColumnSource",
        "click [data-action='show-templates-popup']": "showTemplatesPopup",
        "click [data-action='remove-template']": "removeTemplate",
        "click [data-action='apply-template']": "applyTemplate",
        "click [data-action='save-template']": "saveTemplate",
        "change .custom-report-column select": "updateColumn",
        "change [name='column-source-key']": "updateColumnSources"
      },
      initialize: function() {
        var columns;
        this.reportLogicState = "custom";
        this.bindStartDate();
        this.setEndDate(true, 366);
        window.bindReportFormSubmit($("form#report-form"));
        columns = _.map(gon.custom_report_templates[0].columns, function(c) {
          c.recordStatus = "persisted";
          return c;
        });
        this.buildColumns(columns);
        return this.updateColumnsWrapperHeight();
      },
      bindStartDate: function(maxDays) {
        if (maxDays == null) {
          maxDays = 356;
        }
        return $('#start').datepicker().on("changeDate", (function(_this) {
          return function(e) {
            return _this.setEndDate(false, maxDays);
          };
        })(this));
      },
      setEndDate: function(initial_date, maxDays) {
        var date, maxDate, startdate;
        if (maxDays == null) {
          maxDays = 6;
        }
        startdate = $("#start").datepicker("getDate");
        if (initial_date) {
          date = $("#end").datepicker("getDate");
        } else {
          date = startdate;
        }
        maxDate = moment(startdate).add(maxDays, "day").format("MM/DD/YYYY");
        $('#end').datepicker('remove');
        $("#end").datepicker();
        $("#end").datepicker("setStartDate", startdate);
        $("#end").datepicker("setEndDate", maxDate);
        return $("#end").datepicker("setDate", date);
      },
      saveCustomReportParams: function(e) {
        e.preventDefault();
        this.setSequenceNumbers();
        return $.ajax({
          url: "/api/custom_report_columns/save_all",
          method: "post",
          data: {
            custom_report_columns: this.fetchColumnData()
          },
          success: (function(_this) {
            return function(e) {
              return _this.downloadCustomReport();
            };
          })(this)
        });
      },
      downloadCustomReport: function() {
        var button, form;
        this.setFormOptions();
        form = $("form#report-form");
        button = $('#report-form').find("input[type='submit']");
        button.removeAttr('data-disable-with');
        form.submit();
        return button.removeAttr('data-disable-with');
      },
      setFormOptions: function() {
        var format;
        $("#excel_report_type").val("custom_report_template_weekly");
        format = $("input[name='custom_report_format']:checked").val();
        return this.setReportFormat("users", format);
      },
      setReportFormat: function(scope, format) {
        var action, form;
        form = $("form#report-form");
        action = "/reports/custom_reports/download." + format;
        form.attr("format", format);
        return form.attr("action", action);
      },
      fetchDateParams: function() {
        return {
          start: $("#report-form #start").val(),
          end: $("#report-form #end").val()
        };
      },
      setSequenceNumbers: function() {
        var columnTag, i, j, len, ref, results;
        i = 1;
        ref = $(".custom-report-column");
        results = [];
        for (j = 0, len = ref.length; j < len; j++) {
          columnTag = ref[j];
          $(columnTag).attr("data-sequence", i);
          results.push(i += 1);
        }
        return results;
      },
      buildColumns: function(data) {
        var columnData, columnsSequenced, j, len, results;
        columnsSequenced = _.sortBy(data, function(t) {
          return t.sequence;
        });
        results = [];
        for (j = 0, len = columnsSequenced.length; j < len; j++) {
          columnData = columnsSequenced[j];
          results.push(this.buildColumn(columnData));
        }
        return results;
      },
      buildColumn: function(columnData) {
        var html, selectHtml, sourcesHtml;
        sourcesHtml = this.sourcesHtml(columnData);
        selectHtml = this.selectHtml(columnData);
        html = JST["reports/custom_reports/column"](columnData);
        $("#custom-report-logic-columns #add-icon-wrapper").before(html);
        $(".custom-report-column#" + columnData._id.$oid + " .select-wrapper").html(selectHtml);
        $(".custom-report-column#" + columnData._id.$oid + " .column-sources-list").html(sourcesHtml);
        window.bindSelectTo($(".custom-report-column#" + columnData._id.$oid + " .column-sources-list select"));
        return this.updateColumnsWrapperWidth();
      },
      sourcesHtml: function(data) {
        var html, j, len, source, sources;
        sources = data.sources || [];
        html = "";
        for (j = 0, len = sources.length; j < len; j++) {
          source = sources[j];
          html += this.sourceHtml(source);
        }
        return html;
      },
      sourceHtml: function(source) {
        return "<div class='column-source-item'> <div class='column-source-wrapper'> " + (this.selectUniversalHtml(gon.restaurant.custom_labels.column_sources_matches, source)) + " </div> <a class='btn btn-sm btn-danger' data-action='remove-column-source'><i class='fa fa-remove'></i></a> </div>";
      },
      selectUniversalHtml: function(keysList, selectedKey) {
        var field, html, j, len, options;
        html = '';
        options = '';
        for (j = 0, len = keysList.length; j < len; j++) {
          field = keysList[j];
          if (field.key === selectedKey) {
            if (_.isEmpty(field.title)) {
              field.title = field.key;
            }
            options += "<option value='" + field.key + "' selected>" + field.title + "</option>";
          } else {
            options += "<option value='" + field.key + "'>" + field.title + "</option>";
          }
        }
        html += '<select name="column-source-key" class="form-control">' + options + '</select>';
        return html;
      },
      selectHtml: function(data) {
        var field, html, j, len, options, ref, visibleFieldName;
        html = '';
        options = '';
        ref = this.data.tmFields;
        for (j = 0, len = ref.length; j < len; j++) {
          field = ref[j];
          visibleFieldName = this.applyEditLabels(field);
          if (field === data.key) {
            options += "<option value='" + field + "' selected>" + visibleFieldName + "</option>";
          } else {
            options += "<option value='" + field + "'>" + visibleFieldName + "</option>";
          }
        }
        return html += '<select name="column-key" id="selected-field" class="form-control select-22" data-required-field="true"> <option value="">Tipmetric field</option>' + options + '</select>';
      },
      applyEditLabels: function(field) {
        var customField;
        customField = _.findWhere(gon.restaurant.custom_labels.custom_report_source_types, {
          key: field
        });
        if (!_.isEmpty(customField) && !_.isEmpty(customField.name.replace('_collected', ''))) {
          return ":" + customField.name;
        } else {
          return field;
        }
      },
      applyColumnSourcesLabels: function(field) {
        var customField;
        customField = _.findWhere(gon.restaurant.custom_labels.column_sources_matches, {
          key: field
        });
        if (!_.isEmpty(customField)) {
          return customField.title;
        } else {
          return field;
        }
      },
      updateColumnSources: function(e, tag) {
        var currentColumn, id;
        if (tag == null) {
          tag = void 0;
        }
        if (e) {
          currentColumn = $(e.currentTarget).closest(".custom-report-column");
        } else {
          currentColumn = $(tag);
        }
        id = currentColumn.attr("id");
        return $.ajax({
          url: "/api/custom_report_columns/" + id,
          method: "patch",
          data: {
            column: {
              sources: _.map($(currentColumn).find("[name='column-source-key']"), function(tag) {
                return $(tag).val();
              })
            }
          },
          success: (function(_this) {
            return function(response) {
              return $(currentColumn).attr("data-key", response.column.key);
            };
          })(this)
        });
      },
      updateColumn: function(e) {
        var currentColumn, id;
        e.preventDefault();
        currentColumn = $(e.currentTarget).closest(".custom-report-column");
        id = currentColumn.attr("id");
        return $.ajax({
          url: "/api/custom_report_columns/" + id,
          method: "patch",
          data: {
            column: {
              title: currentColumn.find(".title").text(),
              key: currentColumn.find("select[name='column-key']").val()
            }
          },
          success: (function(_this) {
            return function(response) {
              $(currentColumn).attr("data-key", response.column.key);
              return _this.updateColumnsWrapperHeight();
            };
          })(this)
        });
      },
      removeColumnSource: function(e) {
        var column;
        column = $(e.currentTarget).closest(".custom-report-column");
        $(e.currentTarget).closest(".column-source-item").remove();
        return this.updateColumnSources(void 0, column);
      },
      addColumnSource: function(e) {
        var column;
        column = $(e.currentTarget).closest(".custom-report-column");
        $(column).find(".column-sources-list").append(this.sourceHtml(""));
        window.bindSelectTo($(column).find(".column-sources-list select:not(.select2-hidden-accessible)"));
        this.updateColumnsWrapperHeight();
        return this.updateColumnSources(e);
      },
      createColumn: function(e) {
        var maxSequence, newSequence;
        maxSequence = parseInt(_.max(_.map($(".custom-report-column"), function(e) {
          return parseInt($(e).attr("data-sequence"));
        })));
        newSequence = maxSequence + 1;
        e.preventDefault();
        return $.ajax({
          url: "/api/custom_report_columns",
          method: "post",
          data: {
            column: {
              sequence: newSequence
            }
          },
          success: (function(_this) {
            return function(response) {
              var data;
              data = {
                _id: response.column._id,
                sequence: response.column.sequence,
                recordStatus: "persisted",
                key: "",
                title: response.column.title
              };
              return _this.buildColumn(data);
            };
          })(this)
        });
      },
      removeColumn: function(e) {
        var currentColumn, id;
        e.preventDefault();
        currentColumn = $(e.currentTarget).closest(".custom-report-column");
        id = currentColumn.attr("id");
        return $.ajax({
          url: "/api/custom_report_columns/" + id,
          method: "delete",
          success: (function(_this) {
            return function() {
              currentColumn.remove();
              return _this.updateColumnsWrapperWidth();
            };
          })(this)
        });
      },
      fetchColumnData: function(e) {
        var columnTag, data, j, len, ref, sequence;
        data = {};
        ref = $("#custom-report-logic-columns .custom-report-column");
        for (j = 0, len = ref.length; j < len; j++) {
          columnTag = ref[j];
          sequence = $(columnTag).attr("data-sequence");
          data[sequence] = {
            sequence: $(columnTag).attr("data-sequence"),
            columnKey: $(columnTag).find("select[name='column-key'] option:selected").val(),
            id: $(columnTag).attr("id"),
            recordStatus: $(columnTag).attr("data-record-status"),
            title: $(columnTag).find(".title").text(),
            sources: _.map($(columnTag).find("[name=column-source-key]"), function(tag) {
              return $(tag).val();
            })
          };
        }
        return data;
      },
      editTitle: function(e) {
        var isEditable, titleTag;
        titleTag = $(e.currentTarget).closest(".custom-report-column").find(".title");
        isEditable = $(titleTag).attr("contenteditable");
        if (isEditable === "true") {
          isEditable = false;
        } else {
          isEditable = true;
        }
        $(titleTag).attr("contenteditable", isEditable);
        $(titleTag).focus();
        if (!isEditable) {
          return this.updateColumn(e);
        }
      },
      updateColumnsWrapperHeight: function() {
        var column, columnHeight, j, len, maxColumnsHeight, ref;
        maxColumnsHeight = 0;
        ref = $("#custom-report-logic-columns .custom-report-column");
        for (j = 0, len = ref.length; j < len; j++) {
          column = ref[j];
          columnHeight = $(column).height();
          if (columnHeight > maxColumnsHeight) {
            maxColumnsHeight = columnHeight + 50;
          }
        }
        return $("#custom-report-logic-columns").height(maxColumnsHeight);
      },
      updateColumnsWrapperWidth: function() {
        var columnTag, j, len, ref, width;
        width = 0;
        ref = $("#custom-report-logic-columns").find(".custom-report-column");
        for (j = 0, len = ref.length; j < len; j++) {
          columnTag = ref[j];
          width += $(columnTag).width() + 15;
        }
        width += $("#add-icon-wrapper").width();
        width += 180;
        return $("#custom-report-logic-columns").width(width);
      },
      saveTemplate: function(e) {
        if (e) {
          e.preventDefault();
        }
        this.setSequenceNumbers();
        return $.ajax({
          url: "/api/custom_report_templates",
          method: "post",
          data: {
            custom_report_columns: this.fetchColumnData(),
            name: $(".save-template-wrapper .template-name").val()
          },
          success: (function(_this) {
            return function(response) {
              var templateData;
              templateData = response.template;
              templateData.columns = response.columns;
              gon.custom_report_templates.push(templateData);
              _this.addTemplateToPopup(templateData);
              return _this.updateTemplatesHeight();
            };
          })(this)
        });
      },
      saveCustomReportParams: function(e) {
        e.preventDefault();
        this.setSequenceNumbers();
        return $.ajax({
          url: "/api/custom_report_columns/save_all",
          method: "post",
          data: {
            custom_report_columns: this.fetchColumnData()
          },
          success: (function(_this) {
            return function(e) {
              return _this.downloadCustomReport();
            };
          })(this)
        });
      },
      applyTemplate: function(e) {
        var id, template, templateDiv;
        e.preventDefault;
        templateDiv = $(e.currentTarget).closest('.template-item-wrapper');
        id = $(templateDiv).attr("id");
        $("#custom-report-logic-columns-wrapper .custom-report-column").remove();
        template = _.find(gon.custom_report_templates, function(t) {
          return t._id.$oid === id;
        });
        return $.ajax({
          url: "/api/custom_report_templates/reset_default",
          method: "post",
          data: {
            id: id
          },
          success: (function(_this) {
            return function(response) {
              _this.buildColumns(response.columns);
              $("#custom-templates-wrapper.modal").modal('toggle');
              return _this.updateColumnsWrapperHeight();
            };
          })(this),
          error: function(response) {
            return console.log(response);
          }
        });
      },
      removeTemplate: function(e) {
        var id, templateDiv;
        e.preventDefault;
        templateDiv = $(e.currentTarget).closest('.template-item-wrapper');
        id = $(templateDiv).attr("id");
        gon.custom_report_templates = _.filter(gon.custom_report_templates, function(t) {
          return t._id.$oid !== id;
        });
        return $.ajax({
          url: "/api/custom_report_templates/" + id,
          method: "delete",
          success: function(response) {
            return templateDiv.remove();
          },
          error: function(response) {
            return console.log(response);
          }
        });
      },
      showTemplatesPopup: function() {
        var j, len, popupOptions, savedTemplates, templateData;
        popupOptions = {
          wrapperId: "custom-templates-wrapper",
          afterElementId: "custom-report-logic-columns-wrapper",
          modalTitle: "Select Custom Report Template",
          saveTitle: "Save template"
        };
        Tipmetric.Controllers.modalWrapper.prepareWrapper(popupOptions);
        $("#" + popupOptions.wrapperId + " .modal-body").html("");
        savedTemplates = _.filter(gon.custom_report_templates, function(t) {
          return !t["default"];
        });
        for (j = 0, len = savedTemplates.length; j < len; j++) {
          templateData = savedTemplates[j];
          this.addTemplateToPopup(templateData);
        }
        this.updateTemplatesHeight();
        return $("#" + popupOptions.wrapperId + ".modal").modal('toggle');
      },
      updateTemplatesHeight: function() {
        var columnsLengh, j, len, ref, templateDiv, templateMaxColumnsCount, templateWithMaxColumns;
        templateWithMaxColumns = void 0;
        templateMaxColumnsCount = 0;
        ref = $(".template-item-wrapper .template-item");
        for (j = 0, len = ref.length; j < len; j++) {
          templateDiv = ref[j];
          columnsLengh = $(templateDiv).find(".column").length;
          if (columnsLengh > templateMaxColumnsCount) {
            templateMaxColumnsCount = columnsLengh;
            templateWithMaxColumns = templateDiv;
          }
        }
        return $(".template-item-wrapper .template-item").height(templateMaxColumnsCount * 20 + 80);
      },
      addTemplateToPopup: function(templateData) {
        var template;
        template = JST["reports/custom_reports/template_item"](templateData);
        return $("#custom-templates-wrapper .modal-body").append(template);
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("custom-reports")) {
        return Tipmetric.Controllers.customReportsView = new CustomReportsView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var DashboardView;
    DashboardView = Backbone.View.extend({
      el: "body.dashboard",
      events: {
        "click .toggle-collapse": "toggleCollapse",
        "click .top-header button": "switchDateRange",
        "ifChanged #top_employees_is_shown": "topEmployeesIsShownToggle",
        "ifChanged #employee_history_is_shown": "employeeHistoryIsShown",
        "ifChanged #employee_history_is_filtered": "employeeHistoryIsFiltered",
        "ifChanged #employee_submit_is_shown": "employeeSubmitIsShown",
        "click #download_top_employees": "downloadTipEmployeesData",
        "click #show_top_employees": "showTipEmployeesData"
      },
      initialize: function() {
        this.initDataTables();
        this.selectDate();
        $("input[type='submit']").removeAttr('data-disable-with');
        return this.updateToggleButton();
      },
      topEmployeesIsShownToggle: function(e) {
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: {
            restaurant: {
              top_employees_is_shown: e.currentTarget.checked
            }
          },
          success: (function(_this) {
            return function() {
              return console.log("+");
            };
          })(this),
          error: function() {
            return console.log("-");
          }
        });
      },
      employeeSubmitIsShown: function(e) {
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: {
            restaurant: {
              employee_submit_is_shown: e.currentTarget.checked
            }
          },
          success: (function(_this) {
            return function() {
              return console.log("+");
            };
          })(this),
          error: function() {
            return console.log("-");
          }
        });
      },
      downloadTipEmployeesData: function(e) {
        e.preventDefault();
        return $("#top-employees-form input[type='submit']").click();
      },
      showTipEmployeesData: function(e) {
        if (!(_.some($("#datepicker").find("#start").val().match(/\d+\/\d+\/\d{4}/)) && _.some($("#datepicker").find("#end").val().match(/\d+\/\d+\/\d{4}/)))) {
          toastr["error"]("Cannot get data... Please select correct Date Range", "Error");
          return false;
        }
        e.preventDefault();
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          url: "/dashboard_get_by_date_range.json",
          method: "post",
          id: "1",
          data: {
            start: $("#datepicker").find("#start").val(),
            end: $("#datepicker").find("#end").val()
          },
          success: (function(_this) {
            return function(response) {
              var table;
              console.log("+");
              table = $("#top-employees").DataTable();
              table.destroy();
              $("#top-employees-data").html(JST["dashboard/top_employees"]({
                employees: response
              }));
              _this.initDataTables();
              Tipmetric.Controllers.spinner.hide();
              return _this.updateToggleButton();
            };
          })(this),
          error: function(response) {
            console.log("-");
            return Tipmetric.Controllers.spinner.hide();
          }
        });
      },
      updateToggleButton: function() {
        $("#top-employees").addClass("collapsed");
        $(".toggle-collapse").addClass("collapsed");
        return $(".toggle-collapse-wrapper").toggle($("#top-employees-data tr").length > 5);
      },
      employeeHistoryIsFiltered: function(e) {
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: {
            restaurant: {
              employee_history_is_filtered: e.currentTarget.checked
            }
          },
          success: (function(_this) {
            return function() {
              return console.log("+");
            };
          })(this),
          error: function() {
            return console.log("-");
          }
        });
      },
      employeeHistoryIsShown: function(e) {
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: {
            restaurant: {
              employee_history_is_shown: e.currentTarget.checked
            }
          },
          success: (function(_this) {
            return function() {
              return console.log("+");
            };
          })(this),
          error: function() {
            return console.log("-");
          }
        });
      },
      switchDateRange: function(e) {
        var data, j, len, range, ref, table, td;
        $(".top-header button").removeClass("active");
        range = $(e.currentTarget).attr("data-date-range");
        $(e.currentTarget).addClass("active");
        $("#top-employees .data-range-type").attr("data-date-range", range);
        ref = $("#top-employees .data-range-type");
        for (j = 0, len = ref.length; j < len; j++) {
          td = ref[j];
          data = $(td).find("div." + range).text().replace("$", "");
          $(td).attr("data-sort", data);
        }
        table = $("#top-employees").DataTable();
        return table.cells(".data-range-type").invalidate().draw();
      },
      initDataTables: function() {
        $("#top-employees").dataTable({
          "order": [],
          "bInfo": false,
          "paging": false,
          "searching": false,
          "columnDefs": [
            {
              "targets": 'no-sort',
              "orderable": false
            }
          ]
        });
        return $('#top-employees').on('draw.dt', function() {
          var i, j, len, ref, results, tr;
          i = 1;
          ref = $("#top-employees tbody tr");
          results = [];
          for (j = 0, len = ref.length; j < len; j++) {
            tr = ref[j];
            $(tr).find("td:first-child").text(i);
            results.push(i += 1);
          }
          return results;
        });
      },
      selectDate: function() {
        var weekStart;
        if (gon.restaurant && (gon.restaurant.name === "Jams" || gon.restaurant.name === "Baccarat Hotel") && $("[data-dashboard-scope]").attr("data-dashboard-scope") === "employees") {
          weekStart = 6;
        } else {
          weekStart = 1;
        }
        return $('.datapicker').datepicker({
          weekStart: weekStart,
          disableTouchKeyboard: true
        });
      },
      toggleCollapse: function(e) {
        e.preventDefault();
        $("#top-employees").toggleClass("collapsed");
        return $(e.currentTarget).toggleClass("collapsed");
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("dashboard")) {
        return Tipmetric.Controllers.dashboardView = new DashboardView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var SnapshotView;
    SnapshotView = Backbone.View.extend({
      el: "#db_snapshot",
      events: {
        "click [data-action='make-db-snapshot']": "makeDbSnapshot"
      },
      initialize: function() {
        return this.bindStartDate();
      },
      bindStartDate: function() {
        $('#start').datepicker();
        return $('#end').datepicker();
      },
      checkIfDataValid: function() {
        var enddate, startdate;
        startdate = $(".select-backup-dates #start").datepicker("getDate");
        enddate = $(".select-backup-dates #end").datepicker("getDate");
        return enddate >= startdate;
      },
      makeDbSnapshot: function(e) {
        var end, start;
        e.preventDefault();
        start = $(".select-backup-dates #start").val();
        end = $(".select-backup-dates #end").val();
        if (!this.checkIfDataValid()) {
          toastr["error"]("Please select correct dates");
          return false;
        }
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          url: "/api/make_db_snapshot",
          method: "post",
          data: {
            snapshot_range: {
              start: start,
              end: end
            }
          },
          success: (function(_this) {
            return function(response) {
              Tipmetric.Controllers.spinner.hide(response);
              swal({
                title: "",
                text: "Snapshot ready - " + response.db_path,
                type: "info",
                confirmButtonColor: "#28C256",
                confirmButtonText: "OK",
                showCancelButton: false
              });
              return toastr["success"]("Snapshot is created", "Success");
            };
          })(this),
          error: function() {
            Tipmetric.Controllers.spinner.hide();
            return toastr["error"]("Something went wrong.", "Error");
          }
        });
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("db_snapshot")) {
        return Tipmetric.Controllers.snapshotView = new SnapshotView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var EmployeeView;
    EmployeeView = Backbone.View.extend({
      el: "body.employee",
      events: {
        "click [data-emp-action='add']": "empAdd",
        "click [data-emp-action='create']": "empCreate",
        "click [data-emp-action='update']": "empUpdateStart",
        "click [data-emp-action='edit']": "empEdit",
        "click [data-emp-action='cancel']": "empCancel",
        "click [data-emp-action='remove']": "empRemove",
        "click [data-emp-action='reactivate']": "empReactivate",
        "click [data-emp-action='import-employees']": "saveReceiverPosition",
        "click [data-emp-action='import-start']": "empImport",
        "blur .new-employee-form .emp-id": "checkUser",
        "click .question-line .answers button": "answerToQuestion",
        "ifChanged #toggle-inactive-users": "onlyActiveEmployeesShown"
      },
      initialize: function() {
        this.loadData();
        this.initDataTables();
        this.restyleRadioBox();
        return $(document).ready((function(_this) {
          return function() {
            return _this.disableForPayroll();
          };
        })(this));
      },
      linkHiddenFromField: function() {
        return $("tr[data-emp-data-status='editing'], tr[data-emp-data-status='new']").find('.hidden-from-td .datapicker').datepicker({
          dateFormat: 'mm/dd/yyyy'
        }).on("changeDate", (function(_this) {
          return function(e) {
            return _this.dateUpdated(e.currentTarget);
          };
        })(this)).on("blur", (function(_this) {
          return function(e) {
            return _this.dateBlurred(e.currentTarget);
          };
        })(this)).on("show", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').addClass('open');
          };
        })(this)).on("hide", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').removeClass('open');
          };
        })(this));
      },
      dateUpdated: function(that) {
        $(that).attr("value", $(that).val());
        return $(that).closest("tr").find("span.hidden-from").text($(that).val());
      },
      dateBlurred: function(that) {
        if ($(that).val().length === 0) {
          return $(that).closest("tr").find("span.hidden-from").text($(that).val());
        }
      },
      onlyActiveEmployeesShown: function(e) {
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: {
            restaurant: {
              only_active_employees_shown: e.currentTarget.checked
            }
          },
          success: (function(_this) {
            return function() {
              if (e.currentTarget.checked) {
                return $("#positions-list").attr("data-inactive-users-shown", "false");
              } else {
                return $("#positions-list").attr("data-inactive-users-shown", "true");
              }
            };
          })(this),
          error: function() {
            return console.log("-");
          }
        });
      },
      toggleInactiveUsers: function(e) {
        if (e.currentTarget.checked) {
          return $("#positions-list").attr("data-inactive-users-shown", "false");
        } else {
          return $("#positions-list").attr("data-inactive-users-shown", "true");
        }
      },
      disableForPayroll: function() {
        return $("body[data-role='payroll']").find(".checkboxes-wrapper , [data-emp-action]").attr("disabled", "disabled");
      },
      loadData: function() {
        var emp_html, employee, html, i, j, len, len1, position, positionBlock, ref, ref1, results;
        ref = _.pairs(gon.positions);
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          position = ref[i];
          html = JST["employees/position"](position[1]);
          $("#positions-list").append(html);
          positionBlock = $("table[data-position-type-id='" + position[1].id + "']");
          ref1 = _.pairs(position[1].employees);
          for (j = 0, len1 = ref1.length; j < len1; j++) {
            employee = ref1[j];
            emp_html = JST["employees/employee"](employee[1]);
            positionBlock.append(emp_html);
          }
          results.push(this.updatePositionEmployeesCount(position[1].id));
        }
        return results;
      },
      updatePositionEmployeesCount: function(positionId) {
        var length, positionBlock;
        positionBlock = $("table[data-position-type-id='" + positionId + "']");
        length = $(positionBlock).find(".employee-line[data-employee-status='active']").length;
        return $(".total-position-employees-count[data-position-type-id='" + positionId + "']").text(length);
      },
      restyleRadioBox: function() {
        $('body.employee input[type=radio]').iCheck({
          checkboxClass: 'icheckbox_square-green',
          radioClass: 'iradio_square-green'
        });
        return $('body.employee input[type=checkbox]').iCheck({
          checkboxClass: 'icheckbox_square-green'
        });
      },
      initDataTables: function() {
        return _.each($('table[data-position-name]'), function(e) {
          return $(e).DataTable({
            "order": [],
            "bInfo": false,
            "paging": false,
            "searching": false,
            "columnDefs": [
              {
                "targets": 'no-sort',
                "orderable": false
              }
            ]
          });
        });
      },
      saveReceiverPosition: function(e) {
        var id;
        id = $(e.currentTarget).attr("data-emp-position-type-id");
        return $("[data-emp-action='import-start']").attr("data-emp-position-type-id", id);
      },
      updatePositionCount: function(positionName) {
        var table, tableEmployeesCount;
        table = $("[data-position-table-for='" + positionName + "-table']").first();
        tableEmployeesCount = $(table).find(".employee-line").length;
        return $(".total-position-employees-count[data-position-name='" + positionName + "']").text(tableEmployeesCount);
      },
      empImport: function(e) {
        var receiverId, sourceId;
        sourceId = $(".import-employees-wrapper input:checked").val();
        receiverId = $(e.currentTarget).attr("data-emp-position-type-id");
        return $.ajax({
          url: "/api/employees/import_employees",
          method: "patch",
          data: {
            sourceId: sourceId,
            receiverId: receiverId
          },
          success: function(e) {
            return window.location.href = "/employee";
          },
          error: function(e) {
            return toastr["error"]("Employees are not imported", "Success");
          }
        });
      },
      empAdd: function(e) {
        var datatable, emp_id, first_name, last_name, mobile_number, newEmployee, position, positionTypesTable, position_id, tbody, template;
        position = $(e.currentTarget).attr("data-emp-position-type");
        position_id = $(e.currentTarget).attr("data-emp-position-type-id");
        first_name = "First Name";
        last_name = "Last Name";
        emp_id = "Employee id";
        mobile_number = "Phone number";
        newEmployee = {
          position: position,
          position_type_id: position_id,
          tr_class: "new-employee-form",
          emp_data_status: "new",
          status: "new",
          available_areas: []
        };
        template = JST["employees/employee"](newEmployee);
        positionTypesTable = $("[data-position-table-for=\"" + position + "-table\"]");
        if ($.fn.dataTable.isDataTable("[data-position-table-for=\"" + position + "-table\"]")) {
          datatable = $("[data-position-table-for=\"" + position + "-table\"]").DataTable();
        } else {
          tbody = $("[data-position-table-for=\"" + position + "-table\"] tbody .employee-line");
          if (tbody.children().length === 0) {
            $("[data-position-table-for=\"" + position + "-table\"] tbody").empty();
          }
          datatable = $("[data-position-table-for=\"" + position + "-table\"]").DataTable({
            "destroy": true,
            "order": [],
            "bInfo": false,
            "paging": false,
            "searching": false,
            "columnDefs": [
              {
                "targets": 'no-sort',
                "orderable": false
              }
            ]
          });
        }
        datatable.row.add($(template));
        datatable.draw();
        this.linkHiddenFromField();
        positionTypesTable.find("tr[data-emp-data-status='new']").find(".emp-id, .first-name, .mobile-number, .last-name").editAndFocus(true);
        return positionTypesTable.find("tr[data-emp-data-status='new']").find(".emp-id").focus();
      },
      checkUser: function(e) {
        var empId, firstName, lastName, position, position_id, table, tableIds, tr, trs, userLine;
        empId = $(e.currentTarget).val().cleanup();
        empId || (empId = "");
        $(e.currentTarget).val(empId);
        empId = $(e.currentTarget).val();
        if (empId.length <= 0) {
          return;
        }
        position = $(e.currentTarget).attr("data-position");
        tr = $(e.currentTarget).closest("tr");
        table = $(e.currentTarget).closest("table");
        position_id = table.attr("data-position-type-id");
        tr.attr("data-emp-id", empId);
        trs = table.find("tr[data-emp-data-status='persisted']");
        tableIds = _.map(trs, function(e) {
          return $(e).find(".emp-id").val();
        });
        if (_.contains(tableIds, empId)) {
          userLine = _.find(trs, function(e) {
            return $(e).find(".emp-id").val() === empId;
          });
          firstName = $(userLine).find(".first-name").val();
          lastName = $(userLine).find(".last-name").val();
          $(e.currentTarget).val("");
          $(e.currentTarget).focus();
          return toastr["info"](firstName + " " + lastName + " already exists in the table.", "Employee exists");
        } else {
          return $.ajax({
            url: "/api/employees/check_user",
            method: "get",
            data: {
              empId: empId
            },
            success: (function(_this) {
              return function(response) {
                table.find(".question-line").remove();
                if ($("table[data-position-table-for=\"" + position + "-table\"] tr[data-emp-id='" + response.employee.emp_id + "'].question-line").length === 0) {
                  tr = $("table[data-position-table-for=\"" + position + "-table\"] .new-employee-form").first();
                  return tr.after("<tr class='question-line' data-emp-id='" + response.employee.emp_id + "' data-position=\"" + position + "\" data-position-type-id=\"" + position_id + "\"> <td colspan='6'> <div class='question-block'> <p class='question-text'>" + response.employee.first_name + " " + response.employee.last_name + " has already been assigned this ID. Do you want to use existing user data?</p> <p class='answers'> <button class='btn btn-success btn-xs' type='button' data-emp-answer='yes'>yes</button> <button class='btn btn-warning btn-xs' type='button' data-emp-answer='no'>no</button> </p> </div> </td> </tr>");
                }
              };
            })(this)
          });
        }
      },
      answerToQuestion: function(e) {
        var empId, formTr, position, position_id, questionTable, questionTr;
        questionTable = $(e.currentTarget).closest("table");
        questionTr = $(e.currentTarget).closest("tr");
        empId = questionTr.attr("data-emp-id");
        position = questionTr.attr("data-position");
        position_id = questionTr.attr("data-position-type-id");
        formTr = questionTable.find(".new-employee-form[data-emp-id='" + empId + "']");
        if ($(e.currentTarget).attr("data-emp-answer") === "yes") {
          if (this.idIsFree(empId, questionTable)) {
            return $.ajax({
              url: "api/employees/add_position",
              method: "patch",
              data: {
                empId: empId,
                position: position
              },
              success: (function(_this) {
                return function(response) {
                  var datatable, status;
                  formTr.find(".first-name").val(response.employee.first_name);
                  formTr.find(".last-name").val(response.employee.last_name);
                  if (response.employee.active === true) {
                    status = "active";
                  } else {
                    status = "deactivated";
                  }
                  formTr.attr("data-employee-status", status);
                  formTr.attr("data-employee-id", response.employee.id);
                  formTr.attr("data-emp-data-status", "persisted");
                  formTr.attr("data-position-type-id", position_id);
                  formTr.removeClass("new-employee-form");
                  formTr.find(".emp-id, .first-name, .last-name, .mobile-number").editAndFocus(false);
                  $("table[data-position-table-for=\"" + position + "-table\"] .question-line").remove();
                  formTr.find("td[data-sort]:first-child").attr("data-sort", response.employee.emp_id);
                  formTr.find("td[data-sort]:nth-child(2)").attr("data-sort", response.employee.first_name);
                  formTr.find("td[data-sort]:nth-child(3)").attr("data-sort", response.employee.last_name);
                  datatable = questionTable.DataTable({
                    "destroy": true,
                    "order": [],
                    "bInfo": false,
                    "paging": false,
                    "searching": false,
                    "columnDefs": [
                      {
                        "targets": 'no-sort',
                        "orderable": false
                      }
                    ]
                  });
                  datatable.cell(formTr.find("td[data-sort]:first-child")).data('<input contenteditable="false" class="form-control emp-id" data-position="bar" placeholder="Employee id" value="' + response.employee.emp_id + '" disabled="disabled">');
                  datatable.cell(formTr.find("td[data-sort]:nth-child(2)")).data('<input contenteditable="false" class="form-control first-name" placeholder="First name" value="' + response.employee.first_name + '" disabled="disabled">');
                  datatable.cell(formTr.find("td[data-sort]:nth-child(3)")).data('<input contenteditable="false" class="form-control last-name" placeholder="First name" value="' + response.employee.last_name + '" disabled="disabled">');
                  datatable.draw();
                  _this.linkHiddenFromField();
                  return _this.updatePositionCount(position);
                };
              })(this)
            });
          }
        } else {
          formTr.find(".emp-id").val("");
          return $("table[data-position-table-for=\"" + position + "-table\"] .question-line").remove();
        }
      },
      empCreate: function(e) {
        var positionTypeId, positionTypeName, positionTypesTable, table, tr;
        positionTypesTable = $(e.currentTarget).closest("table");
        positionTypeId = positionTypesTable.attr("data-position-type-id");
        positionTypeName = positionTypesTable.attr("data-position-name");
        tr = $(e.currentTarget).closest("tr");
        table = $(e.currentTarget).closest("table");
        tr.find(".emp-id").val(tr.find(".emp-id").val().cleanup());
        if (this.idIsFree(tr.find(".emp-id").val(), table)) {
          return $.ajax({
            url: "api/employees",
            method: "post",
            data: {
              employee: {
                position_type_ids: [positionTypeId],
                first_name: tr.find(".first-name").val(),
                last_name: tr.find(".last-name").val(),
                mobile_number: tr.find(".mobile-number").val(),
                emp_id: tr.find(".emp-id").val(),
                hidden_from: tr.find("input.hidden-from").val()
              },
              position_type_id: tr.attr("data-emp-position-type-id")
            },
            success: (function(_this) {
              return function(e) {
                var datatable;
                tr.removeClass("new-employee-form");
                tr.attr("data-employee-id", e['employee']['id']);
                tr.attr("data-emp-data-status", "persisted");
                tr.attr("data-employee-status", "active");
                tr.find(".emp-id, .first-name, .last-name, .mobile-number").editAndFocus(false);
                tr.find("td[data-sort]:first-child").attr("data-sort", e.employee.emp_id);
                tr.find("td[data-sort]:nth-child(2)").attr("data-sort", e.employee.first_name);
                tr.find("td[data-sort]:nth-child(3)").attr("data-sort", e.employee.last_name);
                datatable = positionTypesTable.DataTable({
                  "destroy": true,
                  "order": [],
                  "bInfo": false,
                  "paging": false,
                  "searching": false,
                  "columnDefs": [
                    {
                      "targets": 'no-sort',
                      "orderable": false
                    }
                  ]
                });
                datatable.cell(tr.find("td[data-sort]:first-child")).data('<input contenteditable="false" class="form-control emp-id" data-position="bar" placeholder="Employee id" value="' + e.employee.emp_id + '" disabled="disabled">');
                datatable.cell(tr.find("td[data-sort]:nth-child(2)")).data('<input contenteditable="false" class="form-control first-name" placeholder="First name" value="' + e.employee.first_name + '" disabled="disabled">');
                datatable.cell(tr.find("td[data-sort]:nth-child(3)")).data('<input contenteditable="false" class="form-control last-name" placeholder="First name" value="' + e.employee.last_name + '" disabled="disabled">');
                datatable.draw();
                _this.linkHiddenFromField();
                toastr["success"]("Employee successfully added", "Success");
                _this.updatePositionCount(positionTypeName);
                _this.updateEmployeesCount(e.employee.restaurant_employees_count);
                return _this.updatePositionEmployeesCount(positionTypeId);
              };
            })(this),
            error: function(response) {
              return toastr["error"](response.responseText, "Error");
            }
          });
        }
      },
      empRemove: function(e) {
        var id, positionTypeName, position_type_id, table, tr;
        tr = $(e.currentTarget).closest("tr");
        id = tr.attr("data-employee-id");
        table = tr.closest("table");
        position_type_id = table.attr("data-position-type-id");
        positionTypeName = table.attr("data-position-name");
        return swal({
          title: "",
          text: "Are you sure you want to remove employee?",
          type: "info",
          confirmButtonColor: "#28C256",
          confirmButtonText: "Remove",
          showCancelButton: true,
          cancelButtonText: "Cancel"
        }, (function(_this) {
          return function(isDelete) {
            console.log(isDelete);
            if (isDelete) {
              return $.ajax({
                url: "api/employees/" + id,
                method: "delete",
                data: {
                  position_type_id: position_type_id
                },
                success: function(response) {
                  var datatable;
                  console.log(response.employee_status);
                  datatable = table.DataTable({
                    "destroy": true,
                    "order": [],
                    "bInfo": false,
                    "paging": false,
                    "searching": false,
                    "columnDefs": [
                      {
                        "targets": 'no-sort',
                        "orderable": false
                      }
                    ]
                  });
                  switch (response.employee_status) {
                    case "depositioned":
                      datatable.rows(tr).remove();
                      toastr["success"]("Employee removed from this position", "Success");
                      break;
                    case "deactivated":
                      $("tr[data-employee-id='" + id + "']").attr("data-employee-status", "deactivated");
                      toastr["success"]("Employee successfully deactivated", "Success");
                      break;
                    case "destroyed":
                      datatable.rows($("tr[data-employee-id='" + id + "']")).remove();
                      toastr["success"]("Employee is successfully removed", "Success");
                  }
                  datatable.draw();
                  _this.linkHiddenFromField();
                  _this.updatePositionCount(positionTypeName);
                  _this.updateEmployeesCount(response.restaurant_employees_count);
                  return _this.updatePositionEmployeesCount(position_type_id);
                },
                error: function() {
                  return toastr["error"]("Employee is not deactivated", "Error");
                }
              });
            }
          };
        })(this)).then((function(_this) {
          return function(isDelete) {
            if (isDelete.value) {
              return $.ajax({
                url: "api/employees/" + id,
                method: "delete",
                data: {
                  position_type_id: position_type_id
                },
                success: function(response) {
                  var datatable;
                  console.log(response.employee_status);
                  datatable = table.DataTable({
                    "destroy": true,
                    "order": [],
                    "bInfo": false,
                    "paging": false,
                    "searching": false,
                    "columnDefs": [
                      {
                        "targets": 'no-sort',
                        "orderable": false
                      }
                    ]
                  });
                  switch (response.employee_status) {
                    case "depositioned":
                      datatable.rows(tr).remove();
                      toastr["success"]("Employee removed from this position", "Success");
                      break;
                    case "deactivated":
                      $("tr[data-employee-id='" + id + "']").attr("data-employee-status", "deactivated");
                      toastr["success"]("Employee successfully deactivated", "Success");
                      break;
                    case "destroyed":
                      datatable.rows($("tr[data-employee-id='" + id + "']")).remove();
                      toastr["success"]("Employee is successfully removed", "Success");
                  }
                  datatable.draw();
                  _this.linkHiddenFromField();
                  _this.updatePositionCount(positionTypeName);
                  _this.updateEmployeesCount(response.restaurant_employees_count);
                  return _this.updatePositionEmployeesCount(position_type_id);
                },
                error: function() {
                  return toastr["error"]("Employee is not deactivated", "Error");
                }
              });
            }
          };
        })(this));
      },
      empReactivate: function(e) {
        var id, position_type_id, table, tr;
        tr = $(e.currentTarget).closest("tr");
        id = tr.attr("data-employee-id");
        table = tr.closest("table");
        position_type_id = table.attr("data-position-type-id");
        return swal({
          title: "",
          text: "Are you sure you want to reactivate employee?",
          type: "info",
          confirmButtonColor: "#28C256",
          confirmButtonText: "Reactivate",
          showCancelButton: true,
          cancelButtonText: "Cancel"
        }, (function(_this) {
          return function(isReactivate) {
            if (isReactivate) {
              return $.ajax({
                url: "api/employees/" + id + "/reactivate",
                method: "patch",
                data: {
                  employee_id: id
                },
                success: function() {
                  $("tr[data-employee-id='" + id + "']").attr("data-employee-status", "active");
                  toastr["success"]("Employee successfully reactivated", "Success");
                  return _this.updatePositionEmployeesCount(position_type_id);
                },
                error: function() {
                  return toastr["error"]("Employee is not reactivated", "Error");
                }
              });
            }
          };
        })(this)).then((function(_this) {
          return function(isReactivate) {
            if (isReactivate.value) {
              return $.ajax({
                url: "api/employees/" + id + "/reactivate",
                method: "patch",
                data: {
                  employee_id: id
                },
                success: function() {
                  $("tr[data-employee-id='" + id + "']").attr("data-employee-status", "active");
                  toastr["success"]("Employee successfully reactivated", "Success");
                  return _this.updatePositionEmployeesCount(position_type_id);
                },
                error: function() {
                  return toastr["error"]("Employee is not reactivated", "Error");
                }
              });
            }
          };
        })(this));
      },
      empEdit: function(e) {
        var area, form, i, len, options, ref, selectedAreas, tr;
        tr = $(e.currentTarget).closest("tr");
        tr.attr("data-emp-data-status", "editing");
        tr.find(".first-name, .last-name, .mobile-number, .emp-id").saveData().editAndFocus(true);
        selectedAreas = _.map(tr.find(".list p"), function(div) {
          return $(div).attr("data-area-id");
        });
        options = "";
        ref = gon.areas;
        for (i = 0, len = ref.length; i < len; i++) {
          area = ref[i];
          if (_.contains(selectedAreas, area.id)) {
            options += "<option value=\"" + area.id + "\" selected='selected'>" + area.name + "</option>";
          } else {
            options += "<option value=\"" + area.id + "\">" + area.name + "</option>";
          }
        }
        form = "<select class=\"form-control area_select\" multiple placeholder='Areas'>" + options + "</select>";
        tr.find(".form").html(form);
        window.bindSelectTo(tr.find("select"));
        return this.linkHiddenFromField();
      },
      empCancel: function(e) {
        var datatable, id, table, tr;
        tr = $(e.currentTarget).closest("tr");
        table = tr.closest("table");
        if (tr.attr("data-emp-data-status") === "new") {
          datatable = table.DataTable({
            "destroy": true,
            "order": [],
            "bInfo": false,
            "paging": false,
            "searching": false,
            "columnDefs": [
              {
                "targets": 'no-sort',
                "orderable": false
              }
            ]
          });
          datatable.rows(tr).remove();
          datatable.draw();
          return this.linkHiddenFromField();
        } else {
          id = tr.attr("data-employee-id");
          tr.attr("data-emp-data-status", "persisted");
          return tr.find(".emp-id, .first-name, .last-name, .mobile-number").restoreData().editAndFocus(false);
        }
      },
      empUpdateStart: function(e) {
        var id, tr;
        tr = $(e.currentTarget).closest("tr");
        id = tr.attr("data-employee-id");
        tr.find(".emp-id").val(tr.find(".emp-id").val().cleanup());
        return $.ajax({
          url: "api/employees/check_employee_is_locked",
          method: "get",
          data: {
            employee_id: id,
            emp_id: tr.find(".emp-id").val()
          },
          success: (function(_this) {
            return function() {
              return _this.empUpdate(e);
            };
          })(this),
          error: (function(_this) {
            return function() {
              return swal({
                title: "",
                html: "<p>Are you sure you want to change Employee ID that has current shift records? This will affect Payroll.<p> <p style='text-decoration: underline;'>Do not replace OLD EMPLOYEE ID & NAME with a NEW EMPLOYEE ID & NAME because you will lose all the saved tip records for the Old Employee</p>",
                type: "info",
                confirmButtonColor: "#28C256",
                confirmButtonText: "Update",
                showCancelButton: true,
                cancelButtonText: "Cancel"
              }, function(isUpdate) {
                if (isUpdate) {
                  return _this.empUpdate(e);
                }
              }).then(function(isUpdate) {
                if (isUpdate.value) {
                  return _this.empUpdate(e);
                }
              });
            };
          })(this)
        });
      },
      empUpdate: function(e) {
        var id, table, tr;
        tr = $(e.currentTarget).closest("tr");
        table = tr.closest("table");
        id = tr.attr("data-employee-id");
        tr.find(".emp-id").val(tr.find(".emp-id").val().cleanup());
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          url: "api/employees/" + id,
          method: "patch",
          data: {
            employee: {
              allowed_area_ids: tr.find(".area_select").val(),
              first_name: tr.find(".first-name").val(),
              last_name: tr.find(".last-name").val(),
              mobile_number: tr.find(".mobile-number").val(),
              emp_id: tr.find(".emp-id").val(),
              hidden_from: tr.find("input.hidden-from").val()
            },
            position_type_id: tr.attr("data-emp-position-type-id")
          },
          success: (function(_this) {
            return function(employee) {
              var area, available_areas, datatable, emp_id, first_name, i, last_name, len, mobile_number, ref;
              console.log("1");
              console.log(employee);
              available_areas = "";
              ref = employee.allowed_areas;
              for (i = 0, len = ref.length; i < len; i++) {
                area = ref[i];
                available_areas += "<p data-area-id='" + area.id + "'>" + area.name + "</p>";
              }
              tr.find(".list").html(available_areas);
              tr.attr("data-emp-data-status", "persisted");
              tr.find("[contenteditable='true']").attr("contenteditable", false).clearCashedData().attr("disabled", true);
              emp_id = tr.find(".emp-id").val();
              first_name = tr.find(".first-name").val();
              last_name = tr.find(".last-name").val();
              mobile_number = tr.find(".mobile-number").val();
              tr.find("td[data-sort]:first-child").attr("data-sort", emp_id);
              tr.find("td[data-sort]:nth-child(2)").attr("data-sort", first_name);
              tr.find("td[data-sort]:nth-child(3)").attr("data-sort", last_name);
              tr.find("td[data-sort]:nth-child(4)").attr("data-sort", mobile_number);
              datatable = table.DataTable({
                "destroy": true,
                "order": [],
                "bInfo": false,
                "paging": false,
                "searching": false,
                "columnDefs": [
                  {
                    "targets": 'no-sort',
                    "orderable": false
                  }
                ]
              });
              datatable.cell(tr.find("td[data-sort]:first-child")).data('<input contenteditable="false" class="form-control emp-id" data-position="bar" placeholder="Employee id" value="' + emp_id + '" disabled="disabled">');
              datatable.cell(tr.find("td[data-sort]:nth-child(2)")).data('<input contenteditable="false" class="form-control first-name" placeholder="First name" value="' + first_name + '" disabled="disabled">');
              datatable.cell(tr.find("td[data-sort]:nth-child(3)")).data('<input contenteditable="false" class="form-control last-name" placeholder="First name" value="' + last_name + '" disabled="disabled">');
              datatable.cell(tr.find("td[data-sort]:nth-child(4)")).data('<input contenteditable="false" class="form-control mobile-number" placeholder="Mobile Number" value="' + mobile_number + '" disabled="disabled">');
              datatable.draw();
              _this.linkHiddenFromField();
              Tipmetric.Controllers.spinner.hide();
              return toastr["success"]("Emloyee successfully edited", "Success");
            };
          })(this),
          error: function(response) {
            Tipmetric.Controllers.spinner.hide();
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      idIsFree: function(empId, table) {
        var idExist;
        idExist = _.find(table.find("tbody [data-emp-data-status='persisted']"), function(e) {
          return $(e).find(".emp-id").val() === empId;
        });
        if (idExist) {
          toastr["warning"]("Employee id already exist in this table", "Error");
          return false;
        } else {
          return true;
        }
      },
      updateEmployeesCount: function(count) {
        return gon.employees_count = count;
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("employee")) {
        return Tipmetric.Controllers.employeeView = new EmployeeView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var FocusImportView;
    FocusImportView = Backbone.View.extend({
      el: "#focus-import-view",
      events: {
        "change #area_type_id": "areaUpdated",
        "change #shift_type_id": "shiftUpdated",
        "blur #date": "dateBlured",
        "click #run": "getData",
        "click #import": "sendImportData",
        "click .import-errors [data-action='remove']": "removeRow",
        "click .import-errors [data-action='create']": "createEmployee",
        "click .import-errors [data-action='add']": "addEmployee",
        "click .import-errors [data-action='match']": "matchEmployee",
        "click .import-errors [data-action='remove-match']": "removeMatchEmployee",
        "click .import-errors [data-action='select-position']": "selectPosition",
        "click .import-errors [data-action='merge-duplications']": "mergeDuplications",
        "click .import-errors [data-action='remove-duplicated-line']": "removeDuplication",
        "click [data-action='remove-row-inline']": "removeRowInline",
        "click [data-action='show-focus-config']": "toggleTokenBlock",
        "click [data-action='show-focus-shift-select']": "showFocusShiftSelect",
        "click [data-action='get-report-group-ids']": "getReportGroupIds",
        "click [data-action='add-report-group']": "addGroupToInput",
        "click [data-action='remove-report-group']": "removeGroupToInput",
        "click [data-action='close-report-group-popup']": "closeReportGroupPopup",
        "click [data-action='save-lbw-report-group-ids']": "saveLbwReportGroupIds",
        "ifChanged #focus_filtering_enabled": "toggleFiltering",
        "change #focus_import_identifier": "saveFocusImportIdentifier"
      },
      initialize: function() {
        var dateUpdatedCallback;
        gon.restaurant_inheritance_data = Tipmetric.Controllers.helpers.buildSmartData();
        $.fn.editable.defaults.mode = 'inline';
        $.fn.editable.defaults.ajaxOptions = {
          type: "PUT"
        };
        $.fn.editableform.buttons = "<button type=\"submit\" class=\"btn btn-sm btn-success editable-submit\">SAVE</button> <button type=\"button\" class=\"btn btn-sm btn-default editable-cancel\">CANCEL</button>";
        dateUpdatedCallback = (function(_this) {
          return function() {
            return _this.dateUpdated();
          };
        })(this);
        $('.start-time, .end-time').editable({
          url: function(params) {
            var json;
            json = {};
            json[$(this).attr("data-field-name")] = params.value;
            return $.ajax({
              url: "/api/shift_types/" + params.pk,
              method: "put",
              data: {
                shift_type: json
              },
              success: (function(_this) {
                return function(response) {
                  gon.restaurant_inheritance_data = response.restaurant_inheritance_data;
                  dateUpdatedCallback();
                  return toastr["success"]("successfully updated", "Success");
                };
              })(this),
              error: function(response) {
                return toastr["error"](response.responseText, "Error");
              }
            });
          }
        });
        $('#focus-api-key, #focus-api-secret, #focus-venue-key').editable({
          type: 'text',
          url: (function(_this) {
            return function(params) {
              var obj;
              return $.ajax({
                url: "api/restaurants/1",
                method: "put",
                id: "1",
                data: {
                  restaurant: (
                    obj = {},
                    obj["" + (params.name.replace(/-/g, "_"))] = params.value,
                    obj
                  )
                },
                success: function(response) {
                  $("#" + params.name).removeAttr("contenteditable");
                  $("#access-token-wrapper").attr("token-form-status", "locked");
                  return toastr["success"]("Token updated.", "Success");
                },
                error: function(response) {
                  return toastr["error"](response.responseText, "Error");
                }
              });
            };
          })(this)
        });
        $('.datapicker').datepicker().on("changeDate", (function(_this) {
          return function() {
            return _this.dateUpdated();
          };
        })(this)).on("show", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').addClass('open');
          };
        })(this)).on("hide", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').removeClass('open');
          };
        })(this));
        this.dateUpdated();
        this.areaUpdated();
        this.shiftUpdated();
        return Tipmetric.Controllers.helpers.bindFilteredRulesSaving(5, function() {
          return Tipmetric.Controllers.focusImportView.buildTable();
        });
      },
      toggleFiltering: function(e) {
        return $.ajax({
          url: "/api/shift_types/" + ($(e.currentTarget).attr("data-shift-id")),
          method: "put",
          data: {
            shift_type: {
              focus_filtering_enabled: e.currentTarget.checked
            }
          },
          success: (function(_this) {
            return function() {
              return console.log("+");
            };
          })(this),
          error: function() {
            return console.log("-");
          }
        });
      },
      showFocusShiftSelect: function(e) {
        e.preventDefault();
        return $(".focus-shifts-table").toggle();
      },
      toggleTokenBlock: function(e) {
        return $("#access-token-wrapper-block").toggle();
      },
      resetImportData: function(e) {
        this.$el.find(".focus-import-headers").html("");
        this.$el.find(".focus-import-data").html("");
        return this.$el.find(".import-errors > *").html("");
      },
      sendImportData: function(e) {
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return false;
        }
        e.preventDefault();
        window.focus_json = this.buildImportJson();
        return this.sendCheckCsvImportDataRequest(false, void 0);
      },
      buildTable: function() {
        var data, i, len, ref, row, row_i, tr_data, tr_head;
        data = this.lastFetchedData;
        this.resetImportData();
        tr_head = '';
        tr_head += "<tr>";
        tr_head += "<th data-tm-field-name='employee-line-number' data-tm-field-val='0'></th>";
        tr_head += "<th data-column-number='1'>Owner Id</th>";
        tr_head += "<th data-column-number='2'>Id2</th>";
        tr_head += "<th data-column-number='3'>First name</th>";
        tr_head += "<th data-column-number='4'>Last name</th>";
        tr_head += "<th data-column-number='5'>Job Name</th>";
        tr_head += "<th data-column-number='6'>Total time</th>";
        tr_head += "<th data-column-number='7'>Time in</th>";
        tr_head += "<th data-column-number='8'>Time out</th>";
        tr_head += "<th data-column-number='9'>Net Sales</th>";
        tr_head += "<th data-column-number='10'>Total Sales</th>";
        tr_head += "<th data-column-number='11'>Retained Tips</th>";
        tr_head += "<th data-column-number='12'>Declared Tips</th>";
        tr_head += "<th data-column-number='13'>FoodSumm + other</th>";
        tr_head += "<th data-column-number='14'>BeverageSumm (Liquor, Beer & Wine)</th>";
        tr_head += "<th data-column-number='15'></th>";
        tr_head += "</tr>";
        tr_data = '';
        row_i = 1;
        ref = data.get_sales_employee_summary;
        for (i = 0, len = ref.length; i < len; i++) {
          row = ref[i];
          tr_data += "<tr data-row-number='" + row_i + "'>";
          tr_data += "<td data-tm-field-name='employee-line-number' data-tm-field-val='" + row_i + "'>" + row_i + "</td>";
          tr_data += "<td data-column-number='1' data-tm-field-name=':emp_id_secondary'>" + row.EmployeeRecord.ID + "</td>";
          tr_data += "<td data-column-number='2' data-tm-field-name=':emp_id_primary'>" + row.EmployeeRecord.ID2 + "</td>";
          tr_data += "<td data-column-number='3' data-tm-field-name=':first_name'>" + row.EmployeeRecord.FirstName + "</td>";
          tr_data += "<td data-column-number='4' data-tm-field-name=':last_name'>" + row.EmployeeRecord.LastName + "</td>";
          tr_data += "<td data-column-number='5' data-tm-field-name=':position_type'>" + row.Job.Name + "</td>";
          tr_data += "<td data-column-number='6' data-tm-field-name=':hours_worked'>" + row.TotalTime + "</td>";
          tr_data += "<td data-column-number='7' data-tm-field-name=':start_at'>" + (row.TimeIn.replace("T", " T ")) + "</td>";
          tr_data += "<td data-column-number='8' data-tm-field-name=':end_at' class='" + (_.isEmpty(row.TimeOut) ? 'missing-time-out' : '') + "'>" + (!_.isEmpty(row.TimeOut) ? row.TimeOut.replace("T", " T ") : '') + "</td>";
          tr_data += "<td data-column-number='9' data-tm-field-name=':sales_cc'>" + (row.EmployeeSalesTotal.NetSales || '0.00') + "</td>";
          tr_data += "<td data-column-number='10' data-tm-field-name=':sales_cash'>" + (row.EmployeeSalesTotal.TotalSales || '0.00') + "</td>";
          tr_data += "<td data-column-number='11' data-tm-field-name=':cc_tips'>" + (row.EmployeeSalesTotal.RetainedTips || '0.00') + "</td>";
          tr_data += "<td data-column-number='12' data-tm-field-name=':cash_tips'>" + row.DeclaredTips + "</td>";
          tr_data += "<td data-column-number='13' data-tm-field-name=':food_sales'>" + row.food + "</td>";
          tr_data += "<td data-column-number='14' data-tm-field-name=':beverage_sales'>" + row.beverages + "</td>";
          tr_data += "<td data-column-number='15' data-tm-field-name=':actions'><a class='btn btn-xs btn-danger' data-action='remove-row-inline'><i class='fa fa-remove'></i></a></td>";
          tr_data += "</tr>";
          row_i += 1;
        }
        this.$el.find(".focus-import-headers").prepend(tr_head);
        this.$el.find(".focus-import-data").prepend(tr_data);
        this.$el.find("#calculation-form-wrapper").show();
        Tipmetric.Controllers.helpers.zeroHoursForFilteredRoles($("#built-import-table"), 5);
        Tipmetric.Controllers.helpers.filterImportDataByPosition($("#built-import-table"), 5);
        return Tipmetric.Controllers.helpers.filterImportDataByEmployeeId($("#built-import-table"), 2);
      },
      dateUpdated: function() {
        var area, areas, dayName, i, len, options, savedVal;
        if (!_.isEmpty(this.$el.find("#date").val())) {
          this.$el.find("#date").attr("data-saved-date", this.$el.find("#date").val());
        }
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        if (gon.restaurant_inheritance_data[dayName]) {
          areas = _.map(gon.restaurant_inheritance_data[dayName].areas, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          options = "";
          options += "<option value=''>Area</option>";
          for (i = 0, len = areas.length; i < len; i++) {
            area = areas[i];
            options += "<option value='" + area.id + "'>" + area.name + "</option>";
          }
          savedVal = this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          this.$el.find("#calculation-form-wrapper").find("select#area_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val(savedVal).trigger("change");
        } else {
          this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val("").trigger("change").html("<option value=''>Area</option>");
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return this.$el.find("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      saveFocusImportIdentifier: function(e) {
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return true;
        }
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: {
            restaurant: {
              focus_import_identifier: $(e.currentTarget).val()
            }
          },
          success: function(response) {
            return toastr["success"]("Focus identifier updated.", "Success");
          },
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      areaUpdated: function() {
        var areaName, dayName, i, len, options, savedVal, shift, shifts;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option[value='" + (this.$el.find("select#area_type_id").val()) + "']").text();
        if (this.areaEmpty()) {
          return false;
        }
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas[areaName]) {
          shifts = _.map(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts, function(e) {
            return {
              id: e.id,
              name: e.name,
              start_time: e.start_time,
              end_time: e.end_time
            };
          });
          options = "";
          options += "<option value=''>Shift</option>";
          for (i = 0, len = shifts.length; i < len; i++) {
            shift = shifts[i];
            options += "<option value='" + shift.id + "'>" + shift.name + " (<b>" + shift.start_time + "-" + shift.end_time + "</b>)</option>";
          }
          savedVal = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val(savedVal).trigger("change");
        } else {
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return this.$el.find("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      areaEmpty: function() {
        return _.isEmpty(this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val());
      },
      shiftUpdated: function() {
        var areaData, areaName, dayName, firstPosition, i, len, options, position, positions, savedVal, shift, shiftId, shiftName;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option[value='" + ($('select#area_type_id').val()) + "']").text();
        if (this.areaEmpty()) {
          return false;
        }
        shiftId = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id option:selected").val();
        shift = _.find(_.pairs(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts), function(f) {
          return f[1].id === shiftId;
        });
        if (shift) {
          shiftName = shift[0];
        }
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas && areaName) {
          areaData = gon.restaurant_inheritance_data[dayName].areas[areaName];
        }
        if (areaData && shift && areaData.shifts[shiftName]) {
          positions = _.map(areaData.shifts[shiftName].positions, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          if (_.some(positions)) {
            firstPosition = positions[0].id;
          } else {
            firstPosition = "";
          }
          options = "";
          for (i = 0, len = positions.length; i < len; i++) {
            position = positions[i];
            options += "<option value='" + position.id + "'>" + position.name + "</option>";
          }
          savedVal = this.$el.find("select#source_position_ids").val();
          if (!savedVal) {
            savedVal = firstPosition;
          }
          this.$el.find("select#source_position_ids").html(options);
          return this.$el.find("select#source_position_ids").val(savedVal).trigger("change");
        } else {
          return this.$el.find("select#source_position_ids").val("").trigger("change");
        }
      },
      getData: function(e) {
        var data;
        e.preventDefault();
        data = {
          date: this.$el.find("#date").val(),
          area_type_id: this.$el.find("#area_type_id").val(),
          shift_type_id: this.$el.find("#shift_type_id").val(),
          position_type: this.$el.find("#source_position_ids").val()
        };
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          url: "/api/focus/get_data",
          method: "get",
          data: data,
          success: (function(_this) {
            return function(response) {
              console.log('success');
              console.log(response);
              _this.lastFetchedData = response;
              _this.buildTable();
              $("#import").removeAttr("disabled");
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              console.log('error');
              console.log(response);
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this)
        });
      },
      saveLbwReportGroupIds: function(e) {
        var values;
        e.preventDefault();
        values = $("#lbw_report_group_ids").val().split(",");
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: {
            restaurant: {
              focus_lbw_report_group_ids: values
            }
          },
          success: (function(_this) {
            return function(response) {
              toastr["success"]("Report Groups saved", "Success");
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              toastr["error"]("Report Groups not saved", "Error");
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this)
        });
      },
      closeReportGroupPopup: function() {
        $("#report-group-ids-popup .list").html('');
        return $("#report-group-ids-popup-wrapper").hide();
      },
      addGroupToInput: function(e) {
        var currentValuesList, id, newList;
        id = $(e.currentTarget).closest("div").attr("data-group-id");
        $(e.currentTarget).closest("div").attr("data-added-to-list", true);
        currentValuesList = $("#lbw_report_group_ids").val().split(",");
        newList = _.uniq(_.compact(_.union(currentValuesList, [id])));
        console.log("newList " + newList);
        return $("#lbw_report_group_ids").val(newList.join(","));
      },
      removeGroupToInput: function(e) {
        var currentValuesList, id, newList;
        id = $(e.currentTarget).closest("div").attr("data-group-id").toString();
        $(e.currentTarget).closest("div").attr("data-added-to-list", false);
        currentValuesList = $("#lbw_report_group_ids").val().split(",");
        console.log(id);
        console.log(currentValuesList);
        newList = _.uniq(_.compact(_.without(currentValuesList, id)));
        console.log("newList " + newList);
        return $("#lbw_report_group_ids").val(newList.join(","));
      },
      getReportGroupIds: function(e) {
        e.preventDefault();
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          url: "/api/focus/get_report_groups",
          method: "get",
          success: (function(_this) {
            return function(response) {
              var groupedBySuperGroupGroups, reportGroupsJson;
              reportGroupsJson = {};
              groupedBySuperGroupGroups = _.groupBy(response, function(f) {
                return !_.isEmpty(f.SuperGroup);
              });
              reportGroupsJson['groupedBySuperGroupNames'] = _.groupBy(groupedBySuperGroupGroups[true], function(f) {
                return f.SuperGroup.Name;
              });
              reportGroupsJson['groupsWithoutSuperGroups'] = groupedBySuperGroupGroups[false];
              _this.currentValuesList = $("#lbw_report_group_ids").val().split(",");
              reportGroupsJson['currentValuesList'] = _this.currentValuesList;
              _this.buildReportIdsPopup(reportGroupsJson);
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this)
        });
      },
      buildReportIdsPopup: function(reportGroupsJson) {
        var html;
        html = JST["focus_import/report_groups_popup"](reportGroupsJson);
        $("#report-group-ids-popup .list").html(html);
        return $("#report-group-ids-popup-wrapper").show();
      },
      buildImportJson: function() {
        var employeeData, employeeTr, fieldName, i, id, j, json, len, len1, position_type_id, ref, ref1, rowNumber, td, value;
        json = {
          calcualtionParams: {},
          employeesData: [],
          frontend_restaurant_id: gon.restaurant.id
        };
        ref = this.$el.find(".focus-import-data tr");
        for (i = 0, len = ref.length; i < len; i++) {
          employeeTr = ref[i];
          employeeData = {};
          id = this.$el.find(employeeTr).attr("data-emp-id");
          if (!_.isEmpty(id)) {
            employeeData[":emp_id"] = id;
          }
          id = this.$el.find(employeeTr).attr("data-focus-id");
          if (!_.isEmpty(id)) {
            employeeData[":focus_id"] = id;
          }
          position_type_id = this.$el.find(employeeTr).attr("data-position-type-id");
          if (!_.isEmpty(position_type_id)) {
            employeeData["position_type_id"] = position_type_id;
          }
          rowNumber = this.$el.find(employeeTr).attr("data-row-number");
          employeeData["row_number"] = rowNumber;
          ref1 = this.$el.find(employeeTr).find("td");
          for (j = 0, len1 = ref1.length; j < len1; j++) {
            td = ref1[j];
            fieldName = this.$el.find(td).attr("data-tm-field-name");
            value = this.$el.find(td).text();
            if (!_.isEmpty(fieldName) && !_.isEmpty(value)) {
              employeeData[fieldName] = value;
            }
          }
          json.employeesData.push(employeeData);
        }
        json.ordersData = {
          total_tip_money: $(".orders-data").attr("data-total-tip-money"),
          total_service_charge_money: $(".orders-data").attr("data-total-service-charge-money"),
          money_holder: {
            position_type_id: this.$el.find("select#money_holder_id option:selected").attr("data-position-id"),
            employee_id: this.$el.find("select#money_holder_id option:selected").attr("data-employee-id")
          }
        };
        json.calcualtionParams = {
          date: this.$el.find("#date").val(),
          area_type_id: this.$el.find("select#area_type_id").val(),
          shift_type_id: this.$el.find("select#shift_type_id").val(),
          source_position_type_ids: this.$el.find("select#source_position_ids").val(),
          teams_quantity: 1
        };
        return json;
      },
      matchIdentifiedemployees: function(response) {
        var employee, i, len, ref, results, tr;
        window.response = response;
        ref = response.identified_employees;
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          employee = ref[i];
          tr = this.$el.find(".focus-import-data tr[data-row-number='" + employee.row_number + "']");
          results.push(tr.attr("data-emp-id", employee.employee_data.emp_id));
        }
        return results;
      },
      addEmployee: function(e) {
        var currentParagraph;
        currentParagraph = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        return currentParagraph.find(".doesnt-exist-warning-form").show();
      },
      matchEmployee: function(e) {
        var currentParagraph, empId, empName, lineNumber, positionId, tr;
        currentParagraph = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper");
        empId = $(currentParagraph).find("select.employee-select option:selected").val();
        empName = $(currentParagraph).find("select.employee-select option:selected").text();
        positionId = $(currentParagraph).find("select.employee-select option:selected").attr("position-id");
        lineNumber = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        tr.attr("data-emp-id", empId);
        tr.attr("data-position-type-id", positionId);
        return $(currentParagraph).remove();
      },
      removeMatchEmployee: function(e) {
        var currentParagraph, lineNumber, tr;
        currentParagraph = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper, .employees-doesnt-belong-to-any-position-wrapper");
        lineNumber = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper, .employees-doesnt-belong-to-any-position-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        $(tr).remove();
        return $(currentParagraph).remove();
      },
      selectPosition: function(e) {
        var currentParagraph, lineNumber, positionId, tr;
        currentParagraph = $(e.currentTarget).closest(".employee-in-multiple-positions-wrapper");
        positionId = $(currentParagraph).find("select.position-type-select option:selected").val();
        lineNumber = $(e.currentTarget).closest(".employee-in-multiple-positions-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        tr.attr("data-position-type-id", positionId);
        return $(currentParagraph).remove();
      },
      createEmployee: function(e) {
        var brinkId, currentParagraph, empId, firstName, lastName, positionId;
        e.preventDefault();
        currentParagraph = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        positionId = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper").find("select.position-type-select option:selected").val();
        empId = $(currentParagraph).find("input[name='emp_id']").val();
        brinkId = $(currentParagraph).attr("data-brink-id");
        firstName = $(currentParagraph).find("input[name='first_name']").val();
        lastName = $(currentParagraph).find("input[name='last_name']").val();
        return $.ajax({
          url: "api/employees",
          method: "post",
          data: {
            employee: {
              emp_id: empId,
              first_name: firstName,
              brink_id: brinkId,
              last_name: lastName,
              position_type_ids: [positionId]
            }
          },
          success: function(response) {
            $(currentParagraph).remove();
            return toastr["success"]("Emloyee has been created.", "Success");
          },
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      removeRowInline: function(e) {
        return $(e.currentTarget).closest('tr').remove();
      },
      removeRow: function(e) {
        var row, rowNumber;
        rowNumber = $(e.currentTarget).attr("data-row-number");
        row = this.$el.find("tr[data-row-number='" + rowNumber + "']");
        row.remove();
        return $(e.currentTarget).closest(".doesnt-exist-warning-wrapper").remove();
      },
      sendCheckCsvImportDataRequest: function(allowUpdate, e) {
        var data;
        if (e && $(e.currentTarget).attr("disabled") !== void 0) {
          return false;
        }
        data = this.buildImportJson();
        data.allowUpdate = allowUpdate;
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          method: "post",
          url: "/api/calculations/check_focus_import_data",
          data: data,
          success: (function(_this) {
            return function(response) {
              console.log('success');
              console.log(response);
              _this.matchIdentifiedemployees(response);
              _this.$el.find(".import-errors > *").html('');
              return _this.sendImportFocusRequest(allowUpdate);
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              var duplications, employee, employeeEmpId, html1, html3, html4, html5, html6, i, j, k, l, len, len1, len2, len3, len4, m, ref, ref1, ref2, ref3, ref4;
              console.log('error');
              window.response = response;
              _this.matchIdentifiedemployees(response.responseJSON);
              Tipmetric.Controllers.spinner.hide();
              Tipmetric.TestSupport.displayErrors(response.responseJSON.errors);
              if (response.responseJSON.critical_error) {
                swal({
                  title: "",
                  text: response.responseJSON.critical_error_message,
                  type: "error",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "OK",
                  showCancelButton: false
                });
                return;
              }
              if (response.responseJSON.calculation_exists === true && allowUpdate === false) {
                return swal({
                  title: "",
                  text: response.responseJSON.errors,
                  type: "info",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "Yes",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirmed) {
                  if (isConfirmed.value) {
                    return _this.sendCheckCsvImportDataRequest(true, e);
                  }
                });
              } else {
                if (response.responseJSON.errors) {
                  toastr["error"](response.responseJSON.errors, "Error");
                  return;
                }
                html1 = '';
                ref = response.responseJSON.employees_doesnt_exist;
                for (i = 0, len = ref.length; i < len; i++) {
                  employee = ref[i];
                  html1 += JST["csv_import/does_not_exist"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-exist").html(html1);
                html3 = '';
                ref1 = response.responseJSON.name_duplication;
                for (j = 0, len1 = ref1.length; j < len1; j++) {
                  employee = ref1[j];
                  html3 += JST["csv_import/last_name_duplication"](employee);
                }
                _this.$el.find(".import-errors .last-name-duplication").html(html3);
                html4 = '';
                ref2 = response.responseJSON.employee_in_multiple_positions;
                for (k = 0, len2 = ref2.length; k < len2; k++) {
                  employee = ref2[k];
                  html4 += JST["csv_import/employee_in_multiple_positions"](employee);
                }
                _this.$el.find(".import-errors .employee-in-multiple-positions").html(html4);
                html5 = '';
                ref3 = response.responseJSON.employees_doesnt_belong_to_any_position;
                for (l = 0, len3 = ref3.length; l < len3; l++) {
                  employee = ref3[l];
                  html5 += JST["csv_import/employees_doesnt_belong_to_any_position"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-belong-to-any-position").html(html5);
                html6 = '';
                ref4 = _.keys(response.responseJSON.duplicated_employees_grouped);
                for (m = 0, len4 = ref4.length; m < len4; m++) {
                  employeeEmpId = ref4[m];
                  duplications = _.map(response.responseJSON.duplicated_employees_grouped[employeeEmpId], function(f) {
                    f.filledFields = _this.filledFields(f);
                    return f;
                  });
                  html6 += JST["csv_import/duplicated_employee"](duplications);
                }
                _this.$el.find(".import-errors .employees-are-duplicated").html(html6);
                return toastr["error"]("Looks like import was not successfull.", "Error");
              }
            };
          })(this)
        });
      },
      filledFields: function(data) {
        return data[':hours_worked'] + " (" + ':hours_worked' + ")";
      },
      mergeDuplications: function(e) {
        var beverageSalesValues, declaredTipsValues, duplicationBlock, empId, finalAddedNumbers, finalFilledLine, foodSalesValues, hoursValues, i, len, lineNumbers, lineToRemove, lines, otherLines, retainedTipsValues, salesCCValues, tableRows, td, totalSalesValues, tr;
        duplicationBlock = $(e.currentTarget).closest(".employee-is-duplicated");
        lines = $(duplicationBlock).find(".employee-duplication-line");
        lineNumbers = _.collect(lines, function(line) {
          return $(line).attr("data-line-number");
        });
        empId = $(duplicationBlock).attr("data-emp-id");
        finalAddedNumbers = {};
        tableRows = _.filter($(".focus-import-data tr[data-emp-id='" + empId + "']"), function(f) {
          return _.contains(lineNumbers, $(f).attr("data-row-number"));
        });
        hoursValues = _.map($(tableRows).find("td[data-tm-field-name=':hours_worked']"), function(f) {
          return parseFloat($(f).text());
        });
        finalAddedNumbers[':hours_worked'] = _.reduce(hoursValues, function(sum, num) {
          return sum + num;
        }) || 0;
        salesCCValues = _.map($(tableRows).find("td[data-tm-field-name=':sales_cc']"), function(f) {
          return parseFloat($(f).text());
        });
        finalAddedNumbers[':sales_cc'] = _.reduce(salesCCValues, function(sum, num) {
          return sum + num;
        }) || 0;
        totalSalesValues = _.map($(tableRows).find("td[data-tm-field-name=':sales_cash']"), function(f) {
          return parseFloat($(f).text());
        });
        finalAddedNumbers[':sales_cash'] = _.reduce(totalSalesValues, function(sum, num) {
          return sum + num;
        }) || 0;
        retainedTipsValues = _.map($(tableRows).find("td[data-tm-field-name=':cc_tips']"), function(f) {
          return parseFloat($(f).text());
        });
        finalAddedNumbers[':cc_tips'] = _.reduce(retainedTipsValues, function(sum, num) {
          return sum + num;
        }) || 0;
        declaredTipsValues = _.map($(tableRows).find("td[data-tm-field-name=':cash_tips']"), function(f) {
          return parseFloat($(f).text());
        });
        finalAddedNumbers[':cash_tips'] = _.reduce(declaredTipsValues, function(sum, num) {
          return sum + num;
        }) || 0;
        foodSalesValues = _.map($(tableRows).find("td[data-tm-field-name=':food_sales']"), function(f) {
          return parseFloat($(f).text());
        });
        finalAddedNumbers[':food_sales'] = _.reduce(foodSalesValues, function(sum, num) {
          return sum + num;
        }) || 0;
        beverageSalesValues = _.map($(tableRows).find("td[data-tm-field-name=':beverage_sales']"), function(f) {
          return parseFloat($(f).text());
        });
        finalAddedNumbers[':beverage_sales'] = _.reduce(beverageSalesValues, function(sum, num) {
          return sum + num;
        }) || 0;
        finalFilledLine = lineNumbers[0];
        td = $(".focus-import-data tr[data-emp-id='" + empId + "'][data-row-number='" + finalFilledLine + "'] td[data-tm-field-name=':hours_worked']");
        $(td).attr("data-value", finalAddedNumbers[':hours_worked']);
        $(td).text(finalAddedNumbers[':hours_worked']);
        td = $(".focus-import-data tr[data-emp-id='" + empId + "'][data-row-number='" + finalFilledLine + "'] td[data-tm-field-name=':sales_cc']");
        $(td).attr("data-value", finalAddedNumbers[':sales_cc']);
        $(td).text(finalAddedNumbers[':sales_cc']);
        td = $(".focus-import-data tr[data-emp-id='" + empId + "'][data-row-number='" + finalFilledLine + "'] td[data-tm-field-name=':sales_cash']");
        $(td).attr("data-value", finalAddedNumbers[':sales_cash']);
        $(td).text(finalAddedNumbers[':sales_cash']);
        td = $(".focus-import-data tr[data-emp-id='" + empId + "'][data-row-number='" + finalFilledLine + "'] td[data-tm-field-name=':cc_tips']");
        $(td).attr("data-value", finalAddedNumbers[':cc_tips']);
        $(td).text(finalAddedNumbers[':cc_tips']);
        td = $(".focus-import-data tr[data-emp-id='" + empId + "'][data-row-number='" + finalFilledLine + "'] td[data-tm-field-name=':cash_tips']");
        $(td).attr("data-value", finalAddedNumbers[':cash_tips']);
        $(td).text(finalAddedNumbers[':cash_tips']);
        td = $(".focus-import-data tr[data-emp-id='" + empId + "'][data-row-number='" + finalFilledLine + "'] td[data-tm-field-name=':food_sales']");
        $(td).attr("data-value", finalAddedNumbers[':food_sales']);
        $(td).text(finalAddedNumbers[':food_sales']);
        td = $(".focus-import-data tr[data-emp-id='" + empId + "'][data-row-number='" + finalFilledLine + "'] td[data-tm-field-name=':beverage_sales']");
        $(td).attr("data-value", finalAddedNumbers[':beverage_sales']);
        $(td).text(finalAddedNumbers[':beverage_sales']);
        otherLines = _.filter(lineNumbers, function(n) {
          return n !== finalFilledLine;
        });
        for (i = 0, len = otherLines.length; i < len; i++) {
          lineToRemove = otherLines[i];
          tr = this.$el.find("tr[data-emp-id='" + empId + "'][data-row-number='" + lineToRemove + "']");
          tr.remove();
        }
        return $(duplicationBlock).remove();
      },
      removeDuplication: function(e) {
        var currentParagraph, duplicationBlock, lineNumber, tr;
        duplicationBlock = $(e.currentTarget).closest(".employee-is-duplicated");
        currentParagraph = $(e.currentTarget).closest(".employee-duplication-line");
        lineNumber = $(e.currentTarget).attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        $(tr).remove();
        $(currentParagraph).remove();
        if ($(duplicationBlock).find(".employee-duplication-line").length === 0) {
          return $(duplicationBlock).remove();
        }
      },
      sendImportFocusRequest: function(allowUpdate) {
        var data;
        data = this.buildImportJson();
        data.allowUpdate = allowUpdate;
        return $.ajax({
          method: "post",
          url: "/api/calculations/import_focus",
          data: data,
          success: (function(_this) {
            return function(response) {
              toastr["success"]("Data has been imported.", "Success");
              Tipmetric.Controllers.spinner.hide();
              _this.resetImportData();
              return $("#import").attr("disabled", "disabled");
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              Tipmetric.Controllers.spinner.hide();
              if (response.responseJSON.calculation_exists === true) {
                return swal({
                  title: "",
                  text: response.responseJSON.errors,
                  type: "info",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "Yes",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirmed) {
                  if (isConfirmed.value) {
                    return _this.sendImportFocusRequest(true);
                  }
                });
              } else {
                return toastr["error"]("Data is not imported.", "Error");
              }
            };
          })(this)
        });
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("focus-import-view")) {
        return Tipmetric.Controllers.focusImportView = new FocusImportView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var HeaderView;
    HeaderView = Backbone.View.extend({
      el: "#header",
      events: {
        "click .toggle-timezone-select": "toggleTimezoneSelect",
        "click .save-timezone": "saveTimezone",
        "change .restaurant-timezone-select": "timezoneChanged"
      },
      toggleTimezoneSelect: function(e) {
        if (e) {
          e.preventDefault();
        }
        return $(".select-timezone").toggleClass("is-visible");
      },
      timezoneChanged: function(e) {
        var val;
        val = $(e.currentTarget).select2("val");
        if ($(e.currentTarget).hasClass("timezone-select-mobile")) {
          if ($(".timezone-select-desktop").val() !== val) {
            return $(".timezone-select-desktop").val(val).trigger("change");
          }
        } else {
          if ($(".timezone-select-mobile").val() !== val) {
            return $(".timezone-select-mobile").val(val).trigger("change");
          }
        }
      },
      saveTimezone: function(e) {
        var tz;
        tz = $("#main_timezone").val();
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: {
            restaurant: {
              timezone: tz
            }
          },
          success: (function(_this) {
            return function(response) {
              toastr["success"]("Time Zone successfully updated", "Success");
              $(".toggle-timezone-select .date").text(response.restaurant.current_date);
              $(".toggle-timezone-select .timezone").text(tz);
              return _this.toggleTimezoneSelect();
            };
          })(this),
          error: function() {
            return toastr["error"]("Something went wrong.", "Error");
          }
        });
      }
    });
    return $(document).ready(function() {
      return Tipmetric.Controllers.headerView = new HeaderView();
    });
  })();

}).call(this);
(function() {
  var extend = function(child, parent) { for (var key in parent) { if (hasProp.call(parent, key)) child[key] = parent[key]; } function ctor() { this.constructor = child; } ctor.prototype = parent.prototype; child.prototype = new ctor(); child.__super__ = parent.prototype; return child; },
    hasProp = {}.hasOwnProperty;

  (function() {
    return this.Tipmetric.module("Calculation", function(Calculation, App, Backbone, Marionette, $, _) {
      return Calculation.HistoryView = (function(superClass) {
        extend(HistoryView, superClass);

        function HistoryView() {
          return HistoryView.__super__.constructor.apply(this, arguments);
        }

        HistoryView.prototype.template = "calculation/history";

        HistoryView.prototype.events = {
          "click .duplicate-start": "duplicateStart",
          "click .duplicate-init": "duplicateInit",
          "click .destroy-calculation": "destroyCalculation",
          "click .show-calculation": function() {
            return Tipmetric.Controllers.spinner.show();
          },
          "click [data-day-calculation-locked] .lock-icon": "toggleLock"
        };

        HistoryView.prototype.initialize = function() {
          return App.Controllers.historyView = this;
        };

        HistoryView.prototype.toggleLock = function(e) {
          var dayBlock, toggleRequest;
          dayBlock = $(e.currentTarget).closest("[data-day-calculation-locked]");
          toggleRequest = (function(_this) {
            return function() {
              return $.ajax({
                url: "/api/day_calculations/" + (dayBlock.attr('data-day-calculation-id')),
                method: "patch",
                data: {
                  day_calculation: {
                    locked: dayBlock.attr("data-day-calculation-locked") === "true"
                  }
                },
                success: function(response) {
                  var status;
                  dayBlock.attr("data-day-calculation-locked", response.day_calculation_locked);
                  if (response.day_calculation_locked) {
                    status = 'locked';
                  } else {
                    status = 'unlocked';
                  }
                  return toastr["success"]("Day successfully " + status, "Success");
                },
                error: function(response) {
                  return toastr["error"](response.responseJSON.message, "Error");
                }
              });
            };
          })(this);
          if (dayBlock.attr('data-day-calculation-locked') === 'true') {
            return swal({
              title: "",
              text: "Are you sure you want to unlock this day?",
              type: "info",
              confirmButtonColor: "#28C256",
              confirmButtonText: "Unlock",
              showCancelButton: true,
              cancelButtonText: "Cancel"
            }, (function(_this) {
              return function(isToggleLock) {
                if (isToggleLock) {
                  return toggleRequest();
                }
              };
            })(this)).then((function(_this) {
              return function(isToggleLock) {
                if (isToggleLock.value) {
                  return toggleRequest();
                }
              };
            })(this));
          } else {
            return toggleRequest();
          }
        };

        HistoryView.prototype.duplicateInit = function(e) {
          e.preventDefault();
          return $(".duplicate-start").attr("data-calculation-id", $(e.currentTarget).attr("data-calculation-id"));
        };

        HistoryView.prototype.duplicateStart = function(e) {
          var originalId, positionTypeIds;
          originalId = $(e.currentTarget).attr("data-calculation-id");
          positionTypeIds = $("#source_positions").val();
          return $.ajax({
            url: "api/calculations/duplicate",
            method: "get",
            data: {
              originalId: originalId,
              positionTypeIds: positionTypeIds
            },
            success: function(e) {
              console.log(e.duplicated_calculation_id);
              toastr["success"]("Calculation successfully duplicated. Redirecting...", "Success");
              return window.location.href = "/show_calculation_get?calculation_id=" + e.duplicated_calculation_id;
            },
            error: function(e) {
              return toastr["error"]("Calculation is not duplicated.", "Error");
            }
          });
        };

        HistoryView.prototype.destroyCalculation = function(e) {
          var calculationId, destroyButton, tr;
          e.preventDefault();
          calculationId = $(e.currentTarget).attr("data-calculation-id");
          tr = $(e.currentTarget).closest("tr");
          destroyButton = $(e.currentTarget);
          if (destroyButton.isDisabled()) {
            return true;
          }
          return swal({
            title: "",
            text: "Are you sure you want to destroy the calculation?",
            type: "warning",
            confirmButtonColor: "#DD6B55",
            confirmButtonText: "Delete",
            showCancelButton: true,
            cancelButtonText: "Cancel"
          }, (function(_this) {
            return function(isDelete) {
              if (isDelete) {
                Tipmetric.Controllers.spinner.show();
                destroyButton.disable();
                return $.ajax({
                  url: "/api/calculations/" + calculationId,
                  method: "delete",
                  success: function(e) {
                    toastr["success"]("Calculation successfully removed.", "Success");
                    tr.remove();
                    Tipmetric.Controllers.spinner.hide();
                    return destroyButton.enable();
                  },
                  error: function(e) {
                    console.log(e);
                    toastr["error"](e.responseText, "Error");
                    Tipmetric.Controllers.spinner.hide();
                    return destroyButton.enable();
                  }
                });
              }
            };
          })(this)).then((function(_this) {
            return function(isDelete) {
              if (isDelete.value) {
                Tipmetric.Controllers.spinner.show();
                destroyButton.disable();
                return $.ajax({
                  url: "/api/calculations/" + calculationId,
                  method: "delete",
                  success: function(e) {
                    toastr["success"]("Calculation successfully removed.", "Success");
                    tr.remove();
                    Tipmetric.Controllers.spinner.hide();
                    return destroyButton.enable();
                  },
                  error: function(e) {
                    swal({
                      title: "",
                      text: e.responseText,
                      type: "info",
                      confirmButtonText: "Ok",
                      showCancelButton: false
                    });
                    Tipmetric.Controllers.spinner.hide();
                    return destroyButton.enable();
                  }
                });
              }
            };
          })(this));
        };

        return HistoryView;

      })(Marionette.ItemView);
    });
  })();

}).call(this);
(function() {
  (function() {
    var LeftMenuView;
    LeftMenuView = Backbone.View.extend({
      el: "#side-menu",
      events: {
        "click a": "checkEmployeesCount"
      },
      checkEmployeesCount: function(e) {
        if ($(e.currentTarget).hasClass("calculation") && gon.employees_count === 0) {
          e.preventDefault();
          return toastr["error"]("Please add at least 1 employee.", "Error");
        }
      }
    });
    return $(document).ready(function() {
      return Tipmetric.Controllers.leftMenuView = new LeftMenuView();
    });
  })();

}).call(this);
(function() {
  (function() {
    var messageView;
    messageView = Backbone.View.extend({
      el: "body.messages",
      events: {
        "click .delete-message": "deleteMessage"
      },
      deleteMessage: function(e) {
        var id, tr;
        e.preventDefault();
        id = $(e.currentTarget).attr("data-message-id");
        tr = $(e.currentTarget).closest(".message-line");
        return $.ajax({
          url: "/api/messages/" + id,
          method: "delete",
          success: function(e) {
            toastr["success"]("Message removed", "Success");
            return tr.remove();
          },
          error: function(response) {
            return toastr["error"]("Message not removed", "Error");
          }
        });
      }
    });
    return $(document).ready(function() {
      return Tipmetric.Controllers.messageView = new messageView();
    });
  })();

}).call(this);
(function() {
  (function() {
    var PaychexImportView;
    PaychexImportView = Backbone.View.extend({
      el: "#paychex-import-view",
      tm_data_fields: [":hours_worked", ":hours_worked_2"],
      events: {
        "change #area_type_id": "areaUpdated",
        "change #shift_type_id": "shiftUpdated",
        "click #run": "getData",
        "click #import": "sendImportData",
        "click [data-action='show-paychex-config']": "toggleTokenBlock",
        "click [data-action='remove-row-inline']": "removeRowInline",
        "click .import-errors [data-action='remove']": "removeRow",
        "click .import-errors [data-action='add']": "addEmployee",
        "click .import-errors [data-action='create']": "createEmployee",
        "click .import-errors [data-action='remove-match']": "removeMatchEmployee",
        "click .import-errors [data-action='select-position']": "selectPosition",
        "click .import-errors [data-action='merge-duplications']": "mergeDuplications",
        "click .import-errors [data-action='remove-duplicated-line']": "removeDuplication",
        "click [data-action='show-paychex-shift-select']": "showPaychexShiftSelect"
      },
      initialize: function() {
        gon.restaurant_inheritance_data = Tipmetric.Controllers.helpers.buildSmartData();
        $.fn.editable.defaults.mode = 'inline';
        $.fn.editable.defaults.ajaxOptions = {
          type: "PUT"
        };
        $.fn.editableform.buttons = "<button type=\"submit\" class=\"btn btn-sm btn-success editable-submit\">SAVE</button> <button type=\"button\" class=\"btn btn-sm btn-default editable-cancel\">CANCEL</button>";
        $('.start-time, .end-time').editable({
          url: function(params) {
            var json;
            json = {};
            json[$(this).attr("data-field-name")] = params.value;
            return $.ajax({
              url: "/api/shift_types/" + params.pk,
              method: "put",
              data: {
                shift_type: json
              },
              success: (function(_this) {
                return function(response) {
                  gon.restaurant_inheritance_data = response.restaurant_inheritance_data;
                  return toastr["success"]("successfully updated", "Success");
                };
              })(this),
              error: function(response) {
                return toastr["error"](response.responseText, "Error");
              }
            });
          }
        });
        $('#paychex-customer-alias, #paychex-customer-alias-external, #paychex-shared-key, #paychex-user-name, #paychex-user-pass').editable({
          type: 'text',
          url: (function(_this) {
            return function(params) {
              var obj;
              console.log(params.name);
              return $.ajax({
                url: "api/restaurants/1",
                method: "put",
                id: "1",
                data: {
                  restaurant: (
                    obj = {},
                    obj["" + (params.name.replace(/-/g, "_"))] = params.value,
                    obj
                  )
                },
                success: function(response) {
                  $("#" + params.name).removeAttr("contenteditable");
                  $("#access-token-wrapper").attr("token-form-status", "locked");
                  return toastr["success"]("Access Token updated.", "Success");
                },
                error: function(response) {
                  return toastr["error"](response.responseText, "Error");
                }
              });
            };
          })(this)
        });
        this.bindTimezoneSelect();
        $('.datapicker').datepicker().on("changeDate", (function(_this) {
          return function() {
            return _this.dateUpdated();
          };
        })(this)).on("show", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').addClass('open');
          };
        })(this)).on("hide", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').removeClass('open');
          };
        })(this));
        this.dateUpdated();
        this.areaUpdated();
        this.shiftUpdated();
        return Tipmetric.Controllers.helpers.bindFilteredRulesSaving(4, function() {
          return Tipmetric.Controllers.paychexImportView.buildTable();
        });
      },
      showPaychexShiftSelect: function(e) {
        e.preventDefault();
        return $(".paychex-shifts-table").toggle();
      },
      removeDuplication: function(e) {
        var currentParagraph, duplicationBlock, lineNumber, tr;
        duplicationBlock = $(e.currentTarget).closest(".employee-is-duplicated");
        currentParagraph = $(e.currentTarget).closest(".employee-duplication-line");
        lineNumber = $(e.currentTarget).attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        $(tr).remove();
        $(currentParagraph).remove();
        if ($(duplicationBlock).find(".employee-duplication-line").length === 0) {
          return $(duplicationBlock).remove();
        }
      },
      mergeDuplications: function(e) {
        var dataType, duplicationBlock, empId, finalAddedNumbers, finalFilledLine, i, j, k, len, len1, len2, lineNumbers, lineToRemove, lines, otherLines, summ, tableRows, td, tm_data_fields_to_add, tr, values;
        duplicationBlock = $(e.currentTarget).closest(".employee-is-duplicated");
        lines = $(duplicationBlock).find(".employee-duplication-line");
        lineNumbers = _.collect(lines, function(line) {
          return $(line).attr("data-line-number");
        });
        empId = $(duplicationBlock).attr("data-emp-id");
        tm_data_fields_to_add = this.tm_data_fields;
        finalAddedNumbers = {};
        tableRows = _.filter($(".paychex-import-data tr[data-emp-id='" + empId + "']"), function(f) {
          return _.contains(lineNumbers, $(f).attr("data-row-number"));
        });
        for (i = 0, len = tm_data_fields_to_add.length; i < len; i++) {
          dataType = tm_data_fields_to_add[i];
          values = _.map($(tableRows).find("td[data-tm-field-name='" + dataType + "']"), function(f) {
            return parseFloat($(f).text()) || 0;
          });
          if (!_.isEmpty(values)) {
            summ = _.reduce(values, function(memo, num) {
              return memo + num;
            }) || 0;
            finalAddedNumbers[dataType] = summ.toFixed(2);
          }
        }
        finalFilledLine = lineNumbers[0];
        for (j = 0, len1 = tm_data_fields_to_add.length; j < len1; j++) {
          dataType = tm_data_fields_to_add[j];
          if (finalAddedNumbers[dataType]) {
            td = $(".paychex-import-data tr[data-emp-id='" + empId + "'][data-row-number='" + finalFilledLine + "'] td[data-tm-field-name='" + dataType + "'] ");
            $(td).attr("data-value", finalAddedNumbers[dataType]);
            $(td).text(finalAddedNumbers[dataType]);
          }
        }
        otherLines = _.filter(lineNumbers, function(n) {
          return n !== finalFilledLine;
        });
        for (k = 0, len2 = otherLines.length; k < len2; k++) {
          lineToRemove = otherLines[k];
          tr = this.$el.find("tr[data-emp-id='" + empId + "'][data-row-number='" + lineToRemove + "']");
          tr.remove();
        }
        return $(duplicationBlock).remove();
      },
      selectPosition: function(e) {
        var currentParagraph, lineNumber, positionId, tr;
        currentParagraph = $(e.currentTarget).closest(".employee-in-multiple-positions-wrapper");
        positionId = $(currentParagraph).find("select.position-type-select option:selected").val();
        lineNumber = $(e.currentTarget).closest(".employee-in-multiple-positions-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        tr.attr("data-position-type-id", positionId);
        return $(currentParagraph).remove();
      },
      removeMatchEmployee: function(e) {
        var currentParagraph, lineNumber, tr;
        currentParagraph = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper, .employees-doesnt-belong-to-any-position-wrapper");
        lineNumber = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper, .employees-doesnt-belong-to-any-position-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        $(tr).remove();
        return $(currentParagraph).remove();
      },
      addEmployee: function(e) {
        var currentParagraph;
        currentParagraph = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        return currentParagraph.find(".doesnt-exist-warning-form").show();
      },
      createEmployee: function(e) {
        var currentParagraph, empId, firstName, lastName, positionId;
        e.preventDefault();
        currentParagraph = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        positionId = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper").find("select.position-type-select option:selected").val();
        empId = $(currentParagraph).find("input[name='emp_id']").val();
        firstName = $(currentParagraph).find("input[name='first_name']").val();
        lastName = $(currentParagraph).find("input[name='last_name']").val();
        return $.ajax({
          url: "api/employees",
          method: "post",
          data: {
            employee: {
              emp_id: empId,
              first_name: firstName,
              last_name: lastName,
              position_type_ids: [positionId]
            }
          },
          success: (function(_this) {
            return function(response) {
              $(currentParagraph).remove();
              return toastr["success"]("Emloyee has been created.", "Success");
            };
          })(this),
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      removeRow: function(e) {
        var row, rowNumber;
        rowNumber = $(e.currentTarget).attr("data-row-number");
        row = this.$el.find("tr[data-row-number='" + rowNumber + "']");
        row.remove();
        return $(e.currentTarget).closest(".doesnt-exist-warning-wrapper").remove();
      },
      removeRowInline: function(e) {
        return $(e.currentTarget).closest('tr').remove();
      },
      bindTimezoneSelect: function() {
        Tipmetric.Data.square_locations = gon.timezones;
        return $('.timezone-select').editable({
          type: 'select',
          url: (function(_this) {
            return function(params) {
              return $.ajax({
                url: "/api/shift_types/" + params.pk,
                method: "put",
                data: {
                  shift_type: {
                    square_timezone: params.value
                  }
                },
                success: function() {
                  return toastr["success"]("successfully updated", "Success");
                },
                error: function(response) {
                  return toastr["error"](response.responseText, "Error");
                }
              });
            };
          })(this),
          prepend: 'not selected',
          source: _.map(_.pairs(gon.timezones), function(f) {
            return {
              value: f[0],
              text: f[0]
            };
          }),
          display: function(value, sourceData) {
            var elem;
            elem = $.grep(sourceData, function(o) {
              return o.value === value;
            });
            return $(this).text(elem[0].text);
          },
          success: function(response, newValue) {
            $(this).attr("data-value", newValue);
            return console.log(newValue);
          },
          error: function(response) {
            return console.log(response);
          }
        });
      },
      toggleTokenBlock: function(e) {
        return $("#access-token-wrapper-block").toggle();
      },
      resetImportData: function(e) {
        this.$el.find("#built-import-table").html("");
        return this.$el.find(".import-errors > *").html("");
      },
      sendImportData: function(e) {
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return false;
        }
        e.preventDefault();
        window.paychex_json = this.buildImportJson();
        return this.sendCheckPaychexImportDataRequest(false, void 0);
      },
      sendCheckPaychexImportDataRequest: function(allowUpdate, e) {
        var data;
        if (e && $(e.currentTarget).attr("disabled") !== void 0) {
          return false;
        }
        data = this.buildImportJson();
        data.allowUpdate = allowUpdate;
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          method: "post",
          url: "/api/paychex/check_paychex_import_data",
          data: data,
          success: (function(_this) {
            return function(response) {
              console.log('success');
              console.log(response);
              _this.matchIdentifiedemployees(response);
              _this.$el.find(".import-errors > *").html('');
              return _this.sendImportPaychexRequest(allowUpdate);
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              var duplications, employee, employeeEmpId, html1, html3, html4, html5, html6, html7, i, j, k, l, len, len1, len2, len3, len4, len5, m, p, ref, ref1, ref2, ref3, ref4, ref5;
              console.log('error');
              window.response = response;
              _this.matchIdentifiedemployees(response.responseJSON);
              Tipmetric.Controllers.spinner.hide();
              Tipmetric.TestSupport.displayErrors(response.responseJSON.errors);
              if (response.responseJSON.critical_error) {
                swal({
                  title: "",
                  text: response.responseJSON.critical_error_message,
                  type: "error",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "OK",
                  showCancelButton: false
                });
                return;
              }
              if (response.responseJSON.calculation_exists === true && allowUpdate === false) {
                return swal({
                  title: "",
                  text: response.responseJSON.errors,
                  type: "info",
                  confirmButtonColor: "#c30",
                  confirmButtonText: "Continue",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirmed) {
                  if (isConfirmed.value) {
                    return _this.sendCheckPaychexImportDataRequest(true, e);
                  }
                });
              } else {
                if (response.responseJSON.errors) {
                  toastr["error"](response.responseJSON.errors, "Error");
                  return;
                }
                html1 = '';
                ref = response.responseJSON.employees_doesnt_exist;
                for (i = 0, len = ref.length; i < len; i++) {
                  employee = ref[i];
                  html1 += JST["csv_import/does_not_exist"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-exist").html(html1);
                html3 = '';
                ref1 = response.responseJSON.name_duplication;
                for (j = 0, len1 = ref1.length; j < len1; j++) {
                  employee = ref1[j];
                  html3 += JST["csv_import/last_name_duplication"](employee);
                }
                _this.$el.find(".import-errors .last-name-duplication").html(html3);
                html4 = '';
                ref2 = response.responseJSON.employee_in_multiple_positions;
                for (k = 0, len2 = ref2.length; k < len2; k++) {
                  employee = ref2[k];
                  html4 += JST["csv_import/employee_in_multiple_positions"](employee);
                }
                _this.$el.find(".import-errors .employee-in-multiple-positions").html(html4);
                html5 = '';
                ref3 = response.responseJSON.employees_doesnt_belong_to_any_position;
                for (l = 0, len3 = ref3.length; l < len3; l++) {
                  employee = ref3[l];
                  html5 += JST["csv_import/employees_doesnt_belong_to_any_position"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-belong-to-any-position").html(html5);
                html6 = '';
                ref4 = _.keys(response.responseJSON.duplicated_employees_grouped);
                for (m = 0, len4 = ref4.length; m < len4; m++) {
                  employeeEmpId = ref4[m];
                  duplications = _.map(response.responseJSON.duplicated_employees_grouped[employeeEmpId], function(f) {
                    f.filledFields = _this.filledFields(f);
                    return f;
                  });
                  html6 += JST["csv_import/duplicated_employee"](duplications);
                }
                _this.$el.find(".import-errors .employees-are-duplicated").html(html6);
                html7 = '';
                ref5 = _.keys(response.responseJSON.employees_with_positions_grouped_selected);
                for (p = 0, len5 = ref5.length; p < len5; p++) {
                  employeeEmpId = ref5[p];
                  duplications = _.map(response.responseJSON.employees_with_positions_grouped_selected[employeeEmpId], function(f) {
                    f.filledFields = _this.filledFields(f);
                    return f;
                  });
                  html7 += JST["csv_import/duplicated_employee"](duplications);
                }
                _this.$el.find(".import-errors .employees-are-duplicated").append(html7);
                return toastr["error"]("Looks like import was not successfull.", "Error");
              }
            };
          })(this)
        });
      },
      filledFields: function(data) {
        var dataType, html, i, len, tm_data_fields_to_add;
        html = "";
        tm_data_fields_to_add = Tipmetric.Controllers.paychexImportView.tm_data_fields;
        for (i = 0, len = tm_data_fields_to_add.length; i < len; i++) {
          dataType = tm_data_fields_to_add[i];
          if (data[dataType]) {
            html += " " + data[dataType] + " (" + dataType + ")";
          }
        }
        return html;
      },
      matchIdentifiedemployees: function(response) {
        var employee, i, len, ref, results, tr;
        window.response = response;
        ref = response.identified_employees;
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          employee = ref[i];
          tr = this.$el.find(".paychex-import-data tr[data-row-number='" + employee.row_number + "']");
          results.push(tr.attr("data-emp-id", employee.employee_data.emp_id));
        }
        return results;
      },
      sendImportPaychexRequest: function(allowUpdate) {
        this.buildImportJson();
        this.data.allowUpdate = allowUpdate;
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          method: "post",
          url: "/api/paychex/import_paychex",
          data: this.data,
          success: function(response) {
            toastr["success"]("Data has been imported.", "Success");
            Tipmetric.Controllers.spinner.hide();
            return Tipmetric.Controllers.paychexImportView.resetImportData();
          },
          error: (function(_this) {
            return function(response) {
              Tipmetric.Controllers.spinner.hide();
              console.log(response);
              if (response.responseJSON.calculation_exists === true) {
                return swal({
                  title: "",
                  text: response.responseJSON.errors,
                  type: "info",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "Yes",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirmed) {
                  if (isConfirmed.value) {
                    return _this.sendImportPaychexRequest(true);
                  }
                });
              } else {
                return toastr["error"]("Data is not imported.", "Error");
              }
            };
          })(this)
        });
      },
      buildImportJson: function() {
        var employeeData, employeeTr, fieldName, i, id, j, len, len1, position_type_id, ref, ref1, rowNumber, td, value;
        this.data = {
          employeesData: [],
          frontend_restaurant_id: gon.restaurant.id
        };
        this.data.calcualtionParams = {
          date: this.$el.find("#date").val(),
          area_type_id: this.$el.find("select#area_type_id").val(),
          shift_type_id: this.$el.find("select#shift_type_id").val(),
          source_position_type_ids: this.$el.find("select#source_position_ids").val(),
          teams_quantity: 1
        };
        ref = $(".paychex-import-data tr");
        for (i = 0, len = ref.length; i < len; i++) {
          employeeTr = ref[i];
          employeeData = {};
          id = $(employeeTr).attr("data-emp-id");
          if (!_.isEmpty(id)) {
            employeeData[":emp_id"] = id;
          }
          position_type_id = $(employeeTr).attr("data-position-type-id");
          if (!_.isEmpty(position_type_id)) {
            employeeData["position_type_id"] = position_type_id;
          }
          rowNumber = $(employeeTr).attr("data-row-number");
          employeeData["row_number"] = rowNumber;
          ref1 = $(employeeTr).find("td");
          for (j = 0, len1 = ref1.length; j < len1; j++) {
            td = ref1[j];
            fieldName = $(td).attr("data-tm-field-name");
            value = $(td).text();
            if (!_.isEmpty(fieldName) && !_.isEmpty(value)) {
              employeeData[fieldName] = value;
            }
          }
          this.data.employeesData.push(employeeData);
        }
        return this.data;
      },
      buildTable: function() {
        var html;
        this.resetImportData();
        html = JST["paychex_import/employees"](this.lastFetchedData);
        this.$el.find("#built-import-table").html(html);
        return Tipmetric.Controllers.helpers.filterImportDataByPosition($("#built-import-table"), 4);
      },
      dateUpdated: function() {
        var area, areas, dayName, i, len, options, savedVal;
        if (!_.isEmpty(this.$el.find("#date").val())) {
          this.$el.find("#date").attr("data-saved-date", this.$el.find("#date").val());
        }
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        if (gon.restaurant_inheritance_data[dayName]) {
          areas = _.map(gon.restaurant_inheritance_data[dayName].areas, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          options = "";
          options += "<option value=''>Area</option>";
          for (i = 0, len = areas.length; i < len; i++) {
            area = areas[i];
            options += "<option value='" + area.id + "'>" + area.name + "</option>";
          }
          savedVal = this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          this.$el.find("#calculation-form-wrapper").find("select#area_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val(savedVal).trigger("change");
        } else {
          this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val("").trigger("change").html("<option value=''>Area</option>");
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return this.$el.find("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      areaUpdated: function() {
        var areaName, dayName, i, len, options, savedVal, shift, shifts;
        console.log('areaUpdated');
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option[value='" + (this.$el.find("select#area_type_id").val()) + "']").text();
        if (this.areaEmpty()) {
          return false;
        }
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas[areaName]) {
          shifts = _.map(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts, function(e) {
            return {
              id: e.id,
              name: e.name,
              start_time: e.start_time,
              end_time: e.end_time
            };
          });
          options = "";
          options += "<option value=''>Shift</option>";
          for (i = 0, len = shifts.length; i < len; i++) {
            shift = shifts[i];
            options += "<option value='" + shift.id + "'>" + shift.name + "</option>";
          }
          savedVal = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").html(options).select2({
            theme: "bootstrap"
          });
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val(savedVal).trigger("change");
          return console.log('areaUpdated 2');
        } else {
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return this.$el.find("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      availablePositions: function() {
        var areaName, dayName, shift, shiftId, shiftName;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option:selected").text();
        shiftId = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id option:selected").val();
        shift = _.find(_.pairs(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts), function(f) {
          return f[1].id === shiftId;
        });
        if (shift) {
          shiftName = shift[0];
        }
        if (gon.restaurant_inheritance_data[dayName].areas[areaName] && shiftName && gon.restaurant_inheritance_data[dayName].areas[areaName].shifts[shiftName]) {
          return gon.restaurant_inheritance_data[dayName].areas[areaName].shifts[shiftName].positions;
        } else {
          return false;
        }
      },
      areaEmpty: function() {
        return _.isEmpty(this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val());
      },
      shiftUpdated: function() {
        var areaData, areaName, dayName, firstPosition, i, len, options, position, positions, savedVal, shift, shiftId, shiftName;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option[value='" + ($('select#area_type_id').val()) + "']").text();
        if (this.areaEmpty()) {
          return false;
        }
        shiftId = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id option:selected").val();
        shift = _.find(_.pairs(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts), function(f) {
          return f[1].id === shiftId;
        });
        if (shift) {
          shiftName = shift[0];
        }
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas && areaName) {
          areaData = gon.restaurant_inheritance_data[dayName].areas[areaName];
        }
        if (areaData && shift && areaData.shifts[shiftName]) {
          positions = _.map(areaData.shifts[shiftName].positions, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          if (_.some(positions)) {
            firstPosition = positions[0].id;
          } else {
            firstPosition = "";
          }
          options = "";
          for (i = 0, len = positions.length; i < len; i++) {
            position = positions[i];
            options += "<option value='" + position.id + "'>" + position.name + "</option>";
          }
          savedVal = this.$el.find("select#source_position_ids").val();
          if (!savedVal) {
            savedVal = firstPosition;
          }
          this.$el.find("select#source_position_ids").html(options);
          return this.$el.find("select#source_position_ids").val(savedVal).trigger("change");
        } else {
          return this.$el.find("select#source_position_ids").val("").trigger("change");
        }
      },
      getData: function(e) {
        var data;
        e.preventDefault();
        data = {
          date: moment(this.$el.find("#date").val()).format("YYYY-MM-DD"),
          area_type_id: this.$el.find("#area_type_id").val(),
          shift_type_id: this.$el.find("#shift_type_id").val(),
          position_type: this.$el.find("#source_position_ids").val()
        };
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          url: "/api/paychex/get_data",
          method: "get",
          data: data,
          success: (function(_this) {
            return function(response) {
              console.log('success');
              console.log(response);
              _this.lastFetchedData = response;
              _this.buildTable();
              $("#import").removeAttr("disabled");
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              console.log('error');
              console.log(response);
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this)
        });
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("paychex-import-view")) {
        return Tipmetric.Controllers.paychexImportView = new PaychexImportView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var PaycomImportView;
    PaycomImportView = Backbone.View.extend({
      el: "#paycom-import-view",
      events: {
        "click [data-action='show-paycom-config']": "toggleTokenBlock",
        "click [data-action='show-paycom-shift-select']": "showPaycomShiftSelect",
        "blur #date": "dateBlured",
        "click #import": "sendImportData",
        "change #area_type_id": "areaUpdated",
        "change #shift_type_id": "shiftUpdated",
        "click #run": "getData",
        "click .import-errors [data-action='remove']": "removeRow",
        "click .import-errors [data-action='remove-match']": "removeMatchEmployee",
        "click [data-action='remove-row-inline']": "removeRowInline",
        "click .import-errors [data-action='create']": "createEmployee",
        "click .import-errors [data-action='add']": "addEmployee",
        "click .import-errors [data-action='select-position']": "selectPosition"
      },
      initialize: function() {
        var dateUpdatedCallback;
        gon.restaurant_inheritance_data = Tipmetric.Controllers.helpers.buildSmartData();
        $.fn.editable.defaults.mode = 'inline';
        $.fn.editable.defaults.ajaxOptions = {
          type: "PUT"
        };
        $.fn.editableform.buttons = "<button type=\"submit\" class=\"btn btn-sm btn-success editable-submit\">SAVE</button> <button type=\"button\" class=\"btn btn-sm btn-default editable-cancel\">CANCEL</button>";
        dateUpdatedCallback = (function(_this) {
          return function() {
            return _this.dateUpdated();
          };
        })(this);
        $('.start-time, .end-time').editable({
          url: function(params) {
            var json;
            json = {};
            json[$(this).attr("data-field-name")] = params.value;
            return $.ajax({
              url: "/api/shift_types/" + params.pk,
              method: "put",
              data: {
                shift_type: json
              },
              success: (function(_this) {
                return function(response) {
                  gon.restaurant_inheritance_data = response.restaurant_inheritance_data;
                  dateUpdatedCallback();
                  return toastr["success"]("successfully updated", "Success");
                };
              })(this),
              error: function(response) {
                return toastr["error"](response.responseText, "Error");
              }
            });
          }
        });
        $('#paycom-sid, #paycom-token').editable({
          type: 'text',
          url: (function(_this) {
            return function(params) {
              var obj;
              return $.ajax({
                url: "api/restaurants/1",
                method: "put",
                id: "1",
                data: {
                  restaurant: (
                    obj = {},
                    obj["" + (params.name.replace(/-/g, "_"))] = params.value,
                    obj
                  )
                },
                success: function(response) {
                  $("#" + params.name).removeAttr("contenteditable");
                  $("#access-token-wrapper").attr("token-form-status", "locked");
                  return toastr["success"]("Token updated.", "Success");
                },
                error: function(response) {
                  return toastr["error"](response.responseText, "Error");
                }
              });
            };
          })(this)
        });
        $('.datapicker').datepicker().on("changeDate", (function(_this) {
          return function() {
            return _this.dateUpdated();
          };
        })(this)).on("show", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').addClass('open');
          };
        })(this)).on("hide", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').removeClass('open');
          };
        })(this));
        this.dateUpdated();
        this.areaUpdated();
        this.shiftUpdated();
        return Tipmetric.Controllers.helpers.bindFilteredRulesSaving(4, function() {
          return Tipmetric.Controllers.paycomImportView.buildTable();
        });
      },
      toggleTokenBlock: function(e) {
        return $("#access-token-wrapper-block").toggle();
      },
      showPaycomShiftSelect: function(e) {
        e.preventDefault();
        return $(".paycom-shifts-table").toggle();
      },
      dateUpdated: function() {
        var area, areas, dayName, i, len, options, savedVal;
        if (!_.isEmpty(this.$el.find("#date").val())) {
          this.$el.find("#date").attr("data-saved-date", this.$el.find("#date").val());
        }
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        if (gon.restaurant_inheritance_data[dayName]) {
          areas = _.map(gon.restaurant_inheritance_data[dayName].areas, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          options = "";
          options += "<option value=''>Area</option>";
          for (i = 0, len = areas.length; i < len; i++) {
            area = areas[i];
            options += "<option value='" + area.id + "'>" + area.name + "</option>";
          }
          savedVal = this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          this.$el.find("#calculation-form-wrapper").find("select#area_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val(savedVal).trigger("change");
        } else {
          this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val("").trigger("change").html("<option value=''>Area</option>");
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return this.$el.find("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      areaUpdated: function() {
        var areaName, dayName, i, len, options, savedVal, shift, shifts;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option[value='" + (this.$el.find("select#area_type_id").val()) + "']").text();
        if (this.areaEmpty()) {
          return false;
        }
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas[areaName]) {
          shifts = _.map(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts, function(e) {
            return {
              id: e.id,
              name: e.name,
              start_time: e.start_time,
              end_time: e.end_time
            };
          });
          console.log(shifts);
          options = "";
          options += "<option value=''>Shift</option>";
          for (i = 0, len = shifts.length; i < len; i++) {
            shift = shifts[i];
            options += "<option value='" + shift.id + "'>" + shift.name + " (<b>" + shift.start_time + "-" + shift.end_time + "</b>)</option>";
          }
          savedVal = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val(savedVal).trigger("change");
        } else {
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return this.$el.find("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      areaEmpty: function() {
        return _.isEmpty(this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val());
      },
      shiftUpdated: function() {
        var areaData, areaName, dayName, firstPosition, i, len, options, position, positions, savedVal, shift, shiftId, shiftName;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option[value='" + ($('select#area_type_id').val()) + "']").text();
        if (this.areaEmpty()) {
          return false;
        }
        shiftId = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id option:selected").val();
        shift = _.find(_.pairs(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts), function(f) {
          return f[1].id === shiftId;
        });
        if (shift) {
          shiftName = shift[0];
        }
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas && areaName) {
          areaData = gon.restaurant_inheritance_data[dayName].areas[areaName];
        }
        if (areaData && shift && areaData.shifts[shiftName]) {
          positions = _.map(areaData.shifts[shiftName].positions, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          if (_.some(positions)) {
            firstPosition = positions[0].id;
          } else {
            firstPosition = "";
          }
          options = "";
          for (i = 0, len = positions.length; i < len; i++) {
            position = positions[i];
            options += "<option value='" + position.id + "'>" + position.name + "</option>";
          }
          savedVal = this.$el.find("select#source_position_ids").val();
          if (!savedVal) {
            savedVal = firstPosition;
          }
          this.$el.find("select#source_position_ids").html(options);
          return this.$el.find("select#source_position_ids").val(savedVal).trigger("change");
        } else {
          return this.$el.find("select#source_position_ids").val("").trigger("change");
        }
      },
      getData: function(e) {
        var data;
        e.preventDefault();
        data = {
          date: moment(this.$el.find("#date").val()).format("MM-DD-YYYY"),
          area_type_id: this.$el.find("#area_type_id").val(),
          shift_type_id: this.$el.find("#shift_type_id").val(),
          position_type: this.$el.find("#source_position_ids").val()
        };
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          url: "/api/paycom/get_data",
          method: "get",
          data: data,
          success: (function(_this) {
            return function(response) {
              console.log('success');
              console.log(response);
              _this.lastFetchedData = _this.processData(response);
              _this.buildTable();
              $("#import").removeAttr("disabled");
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              console.log('error');
              console.log(response);
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this)
        });
      },
      resetImportData: function(e) {
        this.$el.find(".paycom-import-headers").html("");
        this.$el.find(".paycom-import-data").html("");
        return this.$el.find(".import-errors > *").html("");
      },
      processData: function(data) {
        var employees_with_shifts, precessedData;
        employees_with_shifts = _.map(data.employees_with_shifts, (function(_this) {
          return function(f) {
            f.position = _.uniq(_.filter(_.map(f.shifts, function(f) {
              return f.cat1desc;
            }), function(d) {
              return !_.isEmpty(d);
            }))[0];
            f.id_punch_time = _.some(_.where(f.shifts, {
              punchtype: "ID"
            })) ? _.where(f.shifts, {
              punchtype: "ID"
            })[0].punchtime : '';
            f.ol_punch_time = _.some(_.where(f.shifts, {
              punchtype: "OL"
            })) ? _.where(f.shifts, {
              punchtype: "OL"
            })[0].punchtime : '';
            f.il_punch_time = _.some(_.where(f.shifts, {
              punchtype: "IL"
            })) ? _.where(f.shifts, {
              punchtype: "IL"
            })[0].punchtime : '';
            f.od_punch_time = _.some(_.where(f.shifts, {
              punchtype: "OD"
            })) ? _.where(f.shifts, {
              punchtype: "OD"
            })[0].punchtime : '';
            if (!_.isEmpty(f.id_punch_time) && !_.isEmpty(f.ol_punch_time)) {
              f.hours_1 = window.timeDifference(f.id_punch_time, f.ol_punch_time);
            } else {
              f.hours_1 = "00:00:00";
            }
            if (!_.isEmpty(f.od_punch_time) && !_.isEmpty(f.il_punch_time)) {
              f.hours_2 = window.timeDifference(f.il_punch_time, f.od_punch_time);
            } else {
              f.hours_2 = "00:00:00";
            }
            if (!_.isEmpty(f.od_punch_time) && !_.isEmpty(f.id_punch_time) && _.isEmpty(f.ol_punch_time) && _.isEmpty(f.il_punch_time)) {
              f.hours_3 = window.timeDifference(f.id_punch_time, f.od_punch_time);
            } else {
              f.hours_3 = "00:00:00";
            }
            f.hours_total = window.summTimesWithSeconds([f.hours_1, f.hours_2, f.hours_3]);
            return f;
          };
        })(this));
        precessedData = {};
        precessedData.employees_with_shifts = employees_with_shifts;
        return precessedData;
      },
      removeRowInline: function(e) {
        return $(e.currentTarget).closest('tr').remove();
      },
      buildTable: function() {
        var data, i, len, ref, row, row_i, tr_data, tr_head;
        data = this.lastFetchedData;
        this.resetImportData();
        tr_head = '';
        tr_head += "<tr>";
        tr_head += "<th data-tm-field-name='employee-line-number' data-tm-field-val='0'></th>";
        tr_head += "<th data-column-number='1'>EECode</th>";
        tr_head += "<th data-column-number='2'>Lastname</th>";
        tr_head += "<th data-column-number='3'>Firstname</th>";
        tr_head += "<th data-column-number='4'>IPosition</th>";
        tr_head += "<th data-column-number='5'>InPunchTime (ID)</th>";
        tr_head += "<th data-column-number='6'>OutPunchTime (OL)</th>";
        tr_head += "<th data-column-number='7'>Hours 1</th>";
        tr_head += "<th data-column-number='8'>InPunchTime (IL)</th>";
        tr_head += "<th data-column-number='9'>OutPunchTime (OD)</th>";
        tr_head += "<th data-column-number='10'>Hours 2</th>";
        tr_head += "<th data-column-number='11'>Hours 3</th>";
        tr_head += "<th data-column-number='12'>Total hours</th>";
        tr_head += "<th data-column-number='13'></th>";
        tr_head += "</tr>";
        tr_data = '';
        row_i = 1;
        ref = data.employees_with_shifts;
        for (i = 0, len = ref.length; i < len; i++) {
          row = ref[i];
          tr_data += "<tr data-row-number='" + row_i + "'>";
          tr_data += "<td data-tm-field-name='employee-line-number' data-tm-field-val='" + row_i + "'>" + row_i + "</td>";
          tr_data += "<td data-column-number='1' data-tm-field-name=':emp_id'>" + row.eecode + "</td>";
          tr_data += "<td data-column-number='2' data-tm-field-name=':first_name'>" + row.firstname + "</td>";
          tr_data += "<td data-column-number='3' data-tm-field-name=':last_name'>" + row.lastname + "</td>";
          tr_data += "<td data-column-number='4' data-tm-field-name=':position_name'>" + row.position + "</td>";
          tr_data += "<td data-column-number='5' data-tm-field-name=':id_punch_time' data-tm-field-val='" + row.id_punch_time + "'>" + (row.id_punch_time.replace('T', ' T ')) + "</td>";
          tr_data += "<td data-column-number='6' data-tm-field-name=':ol_punch_time' data-tm-field-val='" + row.ol_punch_time + "'>" + (row.ol_punch_time.replace('T', ' T ')) + "</td>";
          tr_data += "<td data-column-number='7' data-tm-field-name=':hours_1'>" + row.hours_1 + "</td>";
          tr_data += "<td data-column-number='8' data-tm-field-name=':il_punch_time' data-tm-field-val='" + row.il_punch_time + "'>" + (row.il_punch_time.replace('T', ' T ')) + "</td>";
          tr_data += "<td data-column-number='9' data-tm-field-name=':od_punch_time' data-tm-field-val='" + row.od_punch_time + "'>" + (row.od_punch_time.replace('T', ' T ')) + "</td>";
          tr_data += "<td data-column-number='10' data-tm-field-name=':hours_2'>" + row.hours_2 + "</td>";
          tr_data += "<td data-column-number='11' data-tm-field-name=':hours_3'>" + row.hours_3 + "</td>";
          tr_data += "<td data-column-number='12' data-tm-field-name=':hours_worked'>" + row.hours_total + "</td>";
          tr_data += "<td data-column-number='13' data-tm-field-name=':actions'><a class='btn btn-xs btn-danger' data-action='remove-row-inline'><i class='fa fa-remove'></i></a></td>";
          tr_data += "</tr>";
          row_i += 1;
        }
        this.$el.find(".paycom-import-headers").prepend(tr_head);
        this.$el.find(".paycom-import-data").prepend(tr_data);
        this.$el.find("#calculation-form-wrapper").show();
        return Tipmetric.Controllers.helpers.filterImportDataByPosition($("#built-import-table"), 4);
      },
      buildImportJson: function() {
        var employeeData, employeeTr, fieldName, i, id, j, json, len, len1, position_type_id, ref, ref1, rowNumber, td, value;
        json = {
          calcualtionParams: {},
          employeesData: [],
          frontend_restaurant_id: gon.restaurant.id
        };
        ref = this.$el.find(".paycom-import-data tr");
        for (i = 0, len = ref.length; i < len; i++) {
          employeeTr = ref[i];
          employeeData = {};
          id = this.$el.find(employeeTr).attr("data-emp-id");
          if (!_.isEmpty(id)) {
            employeeData[":emp_id"] = id;
          }
          position_type_id = this.$el.find(employeeTr).attr("data-position-type-id");
          if (!_.isEmpty(position_type_id)) {
            employeeData["position_type_id"] = position_type_id;
          }
          rowNumber = this.$el.find(employeeTr).attr("data-row-number");
          employeeData["row_number"] = rowNumber;
          ref1 = this.$el.find(employeeTr).find("td");
          for (j = 0, len1 = ref1.length; j < len1; j++) {
            td = ref1[j];
            fieldName = this.$el.find(td).attr("data-tm-field-name");
            value = this.$el.find(td).text();
            if (!_.isEmpty(fieldName) && !_.isEmpty(value) && _.isEmpty(employeeData[fieldName])) {
              employeeData[fieldName] = value;
            }
            if (_.contains([":id_punch_time", ":ol_punch_time", ":il_punch_time", ":od_punch_time"], fieldName)) {
              employeeData[fieldName] = value.replace(" T ", "T");
            }
          }
          json.employeesData.push(employeeData);
        }
        json.calcualtionParams = {
          date: this.$el.find("#date").val(),
          area_type_id: this.$el.find("select#area_type_id").val(),
          shift_type_id: this.$el.find("select#shift_type_id").val(),
          source_position_type_ids: this.$el.find("select#source_position_ids").val(),
          teams_quantity: 1
        };
        return json;
      },
      matchIdentifiedemployees: function(response) {
        var employee, i, len, ref, results, tr;
        window.response = response;
        ref = response.identified_employees;
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          employee = ref[i];
          tr = this.$el.find(".paycom-import-data tr[data-row-number='" + employee.row_number + "']");
          results.push(tr.attr("data-emp-id", employee.employee_data.emp_id));
        }
        return results;
      },
      sendCheckCsvImportDataRequest: function(allowUpdate, e) {
        var data;
        if (e && $(e.currentTarget).attr("disabled") !== void 0) {
          return false;
        }
        data = this.buildImportJson();
        data.allowUpdate = allowUpdate;
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          method: "post",
          url: "/api/paycom/check_import_data",
          data: data,
          success: (function(_this) {
            return function(response) {
              console.log('success');
              console.log(response);
              _this.matchIdentifiedemployees(response);
              _this.$el.find(".import-errors > *").html('');
              return _this.sendImportRequest(allowUpdate);
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              var duplications, employee, employeeEmpId, html1, html3, html4, html5, html6, i, j, k, l, len, len1, len2, len3, len4, m, ref, ref1, ref2, ref3, ref4;
              console.log('error');
              window.response = response;
              _this.matchIdentifiedemployees(response.responseJSON);
              Tipmetric.Controllers.spinner.hide();
              Tipmetric.TestSupport.displayErrors(response.responseJSON.errors);
              if (response.responseJSON.critical_error) {
                swal({
                  title: "",
                  text: response.responseJSON.critical_error_message,
                  type: "error",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "OK",
                  showCancelButton: false
                });
                return;
              }
              if (response.responseJSON.calculation_exists === true && allowUpdate === false) {
                return swal({
                  title: "",
                  text: response.responseJSON.errors,
                  type: "info",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "Yes",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirmed) {
                  if (isConfirmed.value) {
                    return _this.sendCheckCsvImportDataRequest(true, e);
                  }
                });
              } else {
                if (response.responseJSON.errors) {
                  toastr["error"](response.responseJSON.errors, "Error");
                  return;
                }
                html1 = '';
                ref = response.responseJSON.employees_doesnt_exist;
                for (i = 0, len = ref.length; i < len; i++) {
                  employee = ref[i];
                  html1 += JST["csv_import/does_not_exist"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-exist").html(html1);
                html3 = '';
                ref1 = response.responseJSON.name_duplication;
                for (j = 0, len1 = ref1.length; j < len1; j++) {
                  employee = ref1[j];
                  html3 += JST["csv_import/last_name_duplication"](employee);
                }
                _this.$el.find(".import-errors .last-name-duplication").html(html3);
                html4 = '';
                ref2 = response.responseJSON.employee_in_multiple_positions;
                for (k = 0, len2 = ref2.length; k < len2; k++) {
                  employee = ref2[k];
                  html4 += JST["csv_import/employee_in_multiple_positions"](employee);
                }
                _this.$el.find(".import-errors .employee-in-multiple-positions").html(html4);
                html5 = '';
                ref3 = response.responseJSON.employees_doesnt_belong_to_any_position;
                for (l = 0, len3 = ref3.length; l < len3; l++) {
                  employee = ref3[l];
                  html5 += JST["csv_import/employees_doesnt_belong_to_any_position"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-belong-to-any-position").html(html5);
                html6 = '';
                ref4 = _.keys(response.responseJSON.duplicated_employees_grouped);
                for (m = 0, len4 = ref4.length; m < len4; m++) {
                  employeeEmpId = ref4[m];
                  duplications = _.map(response.responseJSON.duplicated_employees_grouped[employeeEmpId], function(f) {
                    f.filledFields = _this.filledFields(f);
                    return f;
                  });
                  html6 += JST["csv_import/duplicated_employee"](duplications);
                }
                _this.$el.find(".import-errors .employees-are-duplicated").html(html6);
                return toastr["error"]("Looks like import was not successfull.", "Error");
              }
            };
          })(this)
        });
      },
      sendImportData: function(e) {
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return false;
        }
        e.preventDefault();
        window.focus_json = this.buildImportJson();
        return this.sendCheckCsvImportDataRequest(false, void 0);
      },
      addEmployee: function(e) {
        var currentParagraph;
        currentParagraph = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        return currentParagraph.find(".doesnt-exist-warning-form").show();
      },
      createEmployee: function(e) {
        var currentParagraph, empId, firstName, lastName, positionId;
        e.preventDefault();
        currentParagraph = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        positionId = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper").find("select.position-type-select option:selected").val();
        empId = $(currentParagraph).find("input[name='emp_id']").val();
        firstName = $(currentParagraph).find("input[name='first_name']").val();
        lastName = $(currentParagraph).find("input[name='last_name']").val();
        return $.ajax({
          url: "api/employees",
          method: "post",
          data: {
            employee: {
              emp_id: empId,
              first_name: firstName,
              last_name: lastName,
              position_type_ids: [positionId]
            }
          },
          success: function(response) {
            $(currentParagraph).remove();
            return toastr["success"]("Emloyee has been created.", "Success");
          },
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      selectPosition: function(e) {
        var currentParagraph, lineNumber, positionId, tr;
        currentParagraph = $(e.currentTarget).closest(".employee-in-multiple-positions-wrapper");
        positionId = $(currentParagraph).find("select.position-type-select option:selected").val();
        lineNumber = $(e.currentTarget).closest(".employee-in-multiple-positions-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        tr.attr("data-position-type-id", positionId);
        return $(currentParagraph).remove();
      },
      removeRow: function(e) {
        var row, rowNumber;
        rowNumber = $(e.currentTarget).attr("data-row-number");
        row = this.$el.find("tr[data-row-number='" + rowNumber + "']");
        row.remove();
        return $(e.currentTarget).closest(".doesnt-exist-warning-wrapper").remove();
      },
      removeMatchEmployee: function(e) {
        var currentParagraph, lineNumber, tr;
        currentParagraph = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper, .employees-doesnt-belong-to-any-position-wrapper");
        lineNumber = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper, .employees-doesnt-belong-to-any-position-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        $(tr).remove();
        return $(currentParagraph).remove();
      },
      resetImportData: function(e) {
        this.$el.find(".paycom-import-headers").html("");
        this.$el.find(".paycom-import-data").html("");
        return this.$el.find(".import-errors > *").html("");
      },
      sendImportRequest: function(allowUpdate) {
        var data;
        data = this.buildImportJson();
        data.allowUpdate = allowUpdate;
        return $.ajax({
          method: "post",
          url: "/api/paycom/import",
          data: data,
          success: (function(_this) {
            return function(response) {
              toastr["success"]("Data has been imported.", "Success");
              Tipmetric.Controllers.spinner.hide();
              _this.resetImportData();
              return $("#import").attr("disabled", "disabled");
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              Tipmetric.Controllers.spinner.hide();
              if (response.responseJSON.calculation_exists === true) {
                return swal({
                  title: "",
                  text: response.responseJSON.errors,
                  type: "info",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "Yes",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirmed) {
                  if (isConfirmed.value) {
                    return _this.sendImportRequest(true);
                  }
                });
              } else {
                return toastr["error"]("Data is not imported.", "Error");
              }
            };
          })(this)
        });
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("paycom-import-view")) {
        return Tipmetric.Controllers.paycomImportView = new PaycomImportView();
      }
    });
  })();

}).call(this);
(function() {
  $(document).ready(function() {
    $(".add-payroll-manager .fa").click(function() {
      return $(".add-payroll-manager-form").show();
    });
    return $(".payroll-email-check").blur(function(e) {
      var scope;
      scope = $(e.currentTarget).closest("form");
      return $.ajax({
        url: "/managers/payrolls/check_existance",
        method: "get",
        data: {
          email: $(e.currentTarget).val()
        },
        success: function(response) {
          return scope.find(".email-belongs-to-manager, .add-existing-payroll").hide();
        },
        error: function(response) {
          console.log(response);
          scope.find(".email-belongs-to-manager, .add-existing-payroll").hide();
          if (response.responseJSON.error_code === 1) {
            swal({
              title: "",
              text: response.responseJSON.user_info.email + " is associated with " + response.responseJSON.user_info.restaurants + "... Would you like to link " + gon.restaurant.name + "?",
              type: "info",
              confirmButtonColor: "#28C256",
              confirmButtonText: "Yes",
              showCancelButton: true,
              cancelButtonText: "Cancel"
            }, (function(_this) {
              return function(isConfirmed) {
                if (isConfirmed) {
                  scope.find("input[name='name']").val(response.responseJSON.user_info.name);
                  scope.find("input[name='password']").removeAttr("required");
                  scope.find("input[name='password_confirmation']").removeAttr("required");
                  scope.find("input[name='add_restaurant']").val("true");
                  return scope.submit();
                } else {
                  scope.find(".payroll-email-check").val("");
                  scope.find("input[name='password']").attr("required", "required");
                  scope.find("input[name='password_confirmation']").attr("required", "required");
                  return scope.find("input[name='add_restaurant']").val("");
                }
              };
            })(this)).then((function(_this) {
              return function(isConfirmed) {
                if (isConfirmed.value) {
                  scope.find("input[name='name']").val(response.responseJSON.user_info.name);
                  scope.find("input[name='password']").removeAttr("required");
                  scope.find("input[name='password_confirmation']").removeAttr("required");
                  scope.find("input[name='add_restaurant']").val("true");
                  return scope.submit();
                } else {
                  scope.find(".payroll-email-check").val("");
                  scope.find("input[name='password']").attr("required", "required");
                  scope.find("input[name='password_confirmation']").attr("required", "required");
                  return scope.find("input[name='add_restaurant']").val("");
                }
              };
            })(this));
          }
          if (response.responseJSON.error_code === 2) {
            scope.find(".payroll-email-check").val("").focus();
            return scope.find(".email-belongs-to-manager").show();
          }
        }
      });
    });
  });

}).call(this);
(function() {
  (function() {
    var registrationView;
    registrationView = Backbone.View.extend({
      el: ".registration-wrapper",
      events: {
        "click .register-link": "checkTermsAreAgreed"
      },
      isChecked: function(e) {
        return $(".terms-block input").is(':checked');
      },
      checkTermsAreAgreed: function(e) {
        if (!this.isChecked()) {
          e.preventDefault();
          return toastr["error"]("Please accept TipMetric's Terms of service & it's Privacy Policy", "Error");
        }
      }
    });
    return $(document).ready(function() {
      return Tipmetric.Controllers.registrationView = new registrationView();
    });
  })();

}).call(this);
(function() {
  (function() {
    var ReportsView;
    ReportsView = Backbone.View.extend({
      el: "body.reports",
      events: {
        "click .select-numbers-type button": "selectNumbersType",
        "change [data-required-field='true']": "checkIfDataValid",
        "click form a[data-format]": "getDataByFormat",
        "click #show-lock-unlock-options": "showLockUnlockOptions",
        "ifChanged #extended_sales_data": "showExtendedSalesData",
        "ifChanged #show_only_totals": "showOnlyTotalsToggle",
        "ifChanged #toggle_distribution_logic_report": "distributionLogicReportToggle",
        "ifChanged #kickfin_filtering_blanks_enabled": "toggleKickfinFilteringBlanks",
        "ifChanged #paycom_filtering_blanks_enabled": "togglePaycomFilteringBlanks",
        "ifChanged #yearly_reports_tips_collected_enabled": "toggleYearlyReportsTipsCollectedEnabled",
        "ifChanged #daily_report_new_version_default_layout": "updateDailyReportNewVersionDefaultLayout"
      },
      initialize: function() {
        this.bindStartDate();
        this.setEndDate(true);
        this.showTotalChart("global");
        this.checkIfDataValid();
        if (gon.run_last_gate_validation) {
          this.lastGateValidationCheck();
        }
        return window.bindReportFormSubmit($("form#report-form"));
      },
      bindStartDate: function() {
        return $('#start').datepicker().on("changeDate", (function(_this) {
          return function(e) {
            return _this.setEndDate();
          };
        })(this));
      },
      setEndDate: function(initial_date) {
        var date, maxDate, startdate;
        startdate = $("#start").datepicker("getDate");
        if (initial_date) {
          date = $("#end").datepicker("getDate");
        } else {
          date = startdate;
        }
        maxDate = moment(startdate).add(6, "day").format("MM/DD/YYYY");
        $('#end').datepicker('remove');
        $("#end").datepicker();
        $("#end").datepicker("setStartDate", startdate);
        $("#end").datepicker("setEndDate", maxDate);
        return $("#end").datepicker("setDate", date);
      },
      checkIfDataValid: function(e) {
        var blankFields, fields;
        fields = $("[data-required-field='true']");
        blankFields = _.filter(fields, function(e) {
          return $(e).val() === null || $(e).val().length === 0 || $(e).val() === void 0;
        }) || [];
        if (_.isEmpty(blankFields)) {
          return $(".get-html, .get-xlsx").removeAttr("disabled");
        } else {
          return $(".get-html, .get-xlsx").attr("disabled", "disabled");
        }
      },
      updateDailyReportNewVersionDefaultLayout: function(e) {
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return true;
        }
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: {
            restaurant: {
              daily_report_new_version_default_layout: e.currentTarget.checked
            }
          },
          success: (function(_this) {
            return function() {
              return console.log("+");
            };
          })(this),
          error: function() {
            return console.log("-");
          }
        });
      },
      setReportFormat: function(scope, format) {
        var action, form;
        form = $("form#report-form");
        if (scope === "users") {
          if (format === 'html') {
            action = "/show_reports." + format;
          } else if (format === 'xlsx' || format === 'csv' || format === 'xls') {
            action = "/reports/download." + format;
          }
        } else if (scope === "employees") {
          action = "/employees/show_reports." + format;
        }
        form.attr("format", format);
        return form.attr("action", action);
      },
      finalTipOutRangeIsFineCheck: function(start, end) {
        var ftoDate;
        if (this.finalTipOutRangeIsFine(start, end)) {
          return true;
        }
        ftoDate = moment(gon.restaurant.fto_date);
        toastr["error"]("Cannot Download Report between 2 logics Cash handling and Non-Cashhandling please adjust range... Start Date needs to be " + (ftoDate.format("MM/DD/YYYY")) + " and End Date after start date OR End date needs to be " + (ftoDate.format("MM/DD/YYYY")) + " AND Start date before End Date", "Error");
        return false;
      },
      finalTipOutRangeIsFine: function(start, end) {
        var endDate, ftoDate, startdate;
        if (start && end) {
          startdate = moment(start);
          endDate = moment(end);
        } else {
          startdate = moment($("#start").datepicker("getDate"));
          endDate = moment($("#end").datepicker("getDate"));
        }
        ftoDate = moment(gon.restaurant.fto_date);
        if (startdate < ftoDate && ftoDate < endDate) {
          return false;
        } else {
          return true;
        }
      },
      lastGateValidationCheck: function(startDate, endDate) {
        var data;
        if (gon.current_user.workposition === 'staff' || gon.restaurant.distribution_logic_type === 'tip_sharings') {
          return;
        }
        data = {
          start: $("#start").val(),
          end: $("#end").val(),
          area_type_id: $("#area_type_id").val(),
          shift_type_id: $("#shift_type_id").val(),
          position_type_id: $("#position_type_id").val(),
          employee_id: $("#employee_id").val()
        };
        return $.ajax({
          method: "get",
          url: "/reports/run_last_gate_validation",
          data: data,
          success: function(response) {
            return console.log(response);
          },
          error: function(response) {
            var calculations;
            console.log(response);
            window.r = response;
            calculations = _.compact(_.union(response.responseJSON.errors.wrong_received_tips, response.responseJSON.errors.calculation_is_not_correct));
            return swal({
              title: "",
              html: JST["reports/last_gate_popup"]({
                calculations: calculations
              }),
              type: "error",
              confirmButtonColor: "#28C256",
              confirmButtonText: "Ok",
              showCancelButton: false,
              cancelButtonText: "Cancel"
            });
          }
        });
      },
      showLockUnlockOptions: function(e) {
        var html_content, view;
        console.log('ok');
        e.preventDefault();
        view = this;
        html_content = '<div class="col-sm-12"> <div id="lock-records" style="display:none"> <div id="lock-records-multi-restaurants-list"></div> <div class="input-daterange input-group" id="tips-today-datepicker" style="padding-top: 15px;"> <input type="text" name="start" id="lock-records-start" placeholder="Date" class="form-control datapicker" data-required-field="true"><span class="input-group-addon">to</span> <input type="text" name="end" id="lock-records-end" placeholder="Date" class="form-control datapicker" data-required-field="true"> </div> <div class="row"> <div class="col-sm-12"> <div class="col-sm-6"> <button type="button" id="lock-records-submit" class="swal2-styled" style="margin-top: 15px;">Submit Lock Records</button> </div> <div class="col-sm-6"> <button type="button" id="unlock-records-submit" class="swal2-styled" style="margin-top: 15px;">Submit Unlock Records</button> </div> </div> </div> </div> </div>';
        return swal({
          modalIdentifier: "reports-type",
          title: "",
          html: html_content,
          customClass: 'swal2-overflow',
          text: "What type of report do you want to download?",
          type: "info",
          confirmButtonColor: "#28C256",
          confirmButtonText: "Weekly report",
          showCancelButton: false,
          showConfirmButton: false,
          cancelButtonText: "Daily Report",
          cancelButtonColor: '#28C256',
          onOpen: function() {
            var html, i, len, ref, restaurantHash;
            $('#lock-records').show();
            html = "";
            ref = gon.current_user.restaurants;
            for (i = 0, len = ref.length; i < len; i++) {
              restaurantHash = ref[i];
              html = html + ("<input class=\"restaurant-checkbox\" value=" + restaurantHash.id + " type=\"checkbox\" checked='checkbox'\">" + restaurantHash.name + " ");
            }
            $("#lock-records-multi-restaurants-list").html(html);
            $('#lock-records-start').datepicker().on("changeDate", (function(_this) {
              return function(e) {
                var date, maxDate, startdate;
                startdate = $("#lock-records-start").datepicker("getDate");
                date = startdate;
                maxDate = moment(startdate).add(30, "day").format("MM/DD/YYYY");
                $('#lock-records-end').datepicker('remove');
                $("#lock-records-end").datepicker();
                $("#lock-records-end").datepicker("setStartDate", startdate);
                $("#lock-records-end").datepicker("setEndDate", maxDate);
                return $("#lock-records-end").datepicker("setDate", date);
              };
            })(this));
            window.bindCheckboxes();
            return $('#lock-records-submit, #unlock-records-submit').on('click', function(e) {
              var checkBox, j, json, len1, ref1, restaurant_ids;
              restaurant_ids = [];
              ref1 = $("#lock-records-multi-restaurants-list .restaurant-checkbox");
              for (j = 0, len1 = ref1.length; j < len1; j++) {
                checkBox = ref1[j];
                if (checkBox.checked) {
                  restaurant_ids.push($(checkBox).val());
                }
              }
              restaurant_ids;
              json = {
                lock_start: $("#lock-records-start").val(),
                lock_end: $("#lock-records-end").val(),
                action_type: $(e.currentTarget).attr("id"),
                restaurant_ids: restaurant_ids
              };
              Tipmetric.Controllers.spinner.show();
              return $.ajax({
                method: "patch",
                url: "/api/day_calculations/lock_days",
                data: json,
                success: function(response) {
                  toastr["success"]("Days updated.", "Success");
                  return Tipmetric.Controllers.spinner.hide();
                },
                error: function(response) {
                  toastr["error"]("Days are not updated.", "Error");
                  return Tipmetric.Controllers.spinner.hide();
                }
              });
            });
          }
        });
      },
      getDataByFormat: function(e) {
        var adp_reports_extended_html, csvReportButton, customReportButton, dailyNewButton, distribution_logic_type_input, feeReportButton, form, format, html_content, kickfin_checkbox, monthly_or_year_button, paycomExtendedPart, paycom_checkbox, scope, submitButton, view, yearly_tips_collected_checkbox;
        e.preventDefault();
        view = this;
        if ($(e.currentTarget).attr("disabled") !== "disabled") {
          format = $(e.currentTarget).attr("data-format");
          scope = $(e.currentTarget).attr("data-scope");
          form = $("form#report-form");
          submitButton = $('#report-form').find("input[type='submit']");
          view.setReportFormat(scope, format);
          if (format === 'html') {
            Tipmetric.Controllers.spinner.show();
          }
          if (format === 'html') {
            return setTimeout(function() {
              return form.find("input[type='submit']").click();
            }, 1000);
          } else if (format === 'xlsx' || format === 'csv') {
            if (gon.restaurant.paycom_reports_enabled) {
              if (gon.restaurant.paycom_extended_reports_enabled) {
                if (gon.restaurant.paycom_filtering_blanks_enabled) {
                  paycom_checkbox = "<div class='paycom-checkbox-wrapper input-group'><input id='paycom_filtering_blanks_enabled' type='checkbox' value='' type='checkbox' checked='checked'> Filter amount (0 or -)</div>";
                } else {
                  paycom_checkbox = "<div class='paycom-checkbox-wrapper input-group'><input id='paycom_filtering_blanks_enabled' type='checkbox' value='' type='checkbox'> Filter amount (0 or -)</div>";
                }
                paycomExtendedPart = ("<div class='col-sm-12'> <input type='text' class='form-control paycom-field' id='paycom-ag-svc-code' placeholder='Earnings 3 Code AG/SVC:' value='" + gon.restaurant.paycom_reports_params.paycom_ag_svc_code + "'> </div> <div class='col-sm-12'>") + paycom_checkbox + "<div class='input-daterange input-group' id='bi-week-datepicker-for-paycom' style='padding-top: 15px;'> <input type='text' name='start' id='paycom-start' placeholder='Date' class='form-control datapicker' data-required-field='true'><span class='input-group-addon'>to</span> <input type='text' name='end' id='paycom-end' placeholder='Date' class='form-control datapicker' data-required-field='true'> </div> </div>";
              } else {
                paycomExtendedPart = "";
              }
              csvReportButton = ("<div id='paycom-options' class='hidden'> <div class='col-sm-6'> <input type='text' class='form-control paycom-field' id='paycom-cash-code' placeholder='Earnings 3 Code Cash Tips:' value='" + gon.restaurant.paycom_reports_params.paycom_cash_code + "'> </div> <div class='col-sm-6'> <input type='text' class='form-control paycom-field' id='paycom-cc-code' placeholder='Earnings 3 Code CC Tips:' value='" + gon.restaurant.paycom_reports_params.paycom_cc_code + "'> </div>") + paycomExtendedPart + "<div class='col-sm-12'> <button type='button' id='custom-weekly-report' class='swal2-styled' style='margin-top: 15px;'>Submit</button> </div> </div>";
              customReportButton = '<button type="button" class="swal2-styled" id="show-paycom-options">PAYCOM</button>';
            } else if (gon.restaurant.cliq_reports_enabled) {
              csvReportButton = "<div id='cliq-options' class='hidden'> <div class='col-sm-12'> <div class='input-daterange input-group' id='bi-week-datepicker-for-cliq' style='padding-top: 15px;'> <input type='text' name='start' id='cliq-start' placeholder='Date' class='form-control datapicker' data-required-field='true'><span class='input-group-addon'>to</span> <input type='text' name='end' id='cliq-end' placeholder='Date' class='form-control datapicker' data-required-field='true'> </div> </div> <div class='col-sm-12'> <button type='button' id='cliq-report-submit' class='swal2-styled' style='margin-top: 15px;'>Submit</button> </div> </div>";
              customReportButton = '<button type="button" class="swal2-styled" id="show-cliq-options">CLIQ</button>';
            } else if (gon.restaurant.paychex_reports_enabled) {
              csvReportButton = "<div id='paychex-options' class='hidden'> <div class='col-sm-12'> <div class='col-sm-6'> <input type='text' class='form-control paycom-field' id='paychex-client-code' placeholder='Client ID:' value='" + gon.restaurant.paychex_reports_params.paychex_client_code + "'> </div> </div> <div class='col-sm-12'> <button type='button' id='paychex-weekly-report' class='swal2-styled' style='margin-top: 15px;'>Submit</button> </div> </div>";
              customReportButton = '<button type="button" class="swal2-styled" id="show-paychex-options">PAYCHEX</button>';
            } else if (gon.restaurant.adp_reports_enabled) {
              if (gon.restaurant.adp_reports_extended) {
                adp_reports_extended_html = "<div class='col-sm-12'> <div class='input-daterange input-group' id='bi-week-datepicker-for-adp' style='padding-top: 15px;'> <input type='text' name='start' id='bi-week-start-for-adp' placeholder='Date' class='form-control datapicker' data-required-field='true'><span class='input-group-addon'>to</span> <input type='text' name='end' id='bi-week-end-for-adp' placeholder='Date' class='form-control datapicker' data-required-field='true'> </div> </div>";
              } else {
                adp_reports_extended_html = "";
              }
              csvReportButton = "<div id='adp-options' class='hidden'> " + adp_reports_extended_html + " <div class='col-sm-12'> <div class='row'> <div class='col-sm-6'> <input type='text' class='form-control adp-field' id='adp-co-code' placeholder='Co Code:' value='" + gon.restaurant.adp_reports_params.adp_co_code + "'> <input type='text' class='form-control adp-field' id='adp-cash-code' placeholder='Earnings 3 Code Cash Tips:' value='" + gon.restaurant.adp_reports_params.adp_cash_code + "'> </div> <div class='col-sm-6'> <input type='text' class='form-control adp-field' id='adp-batch-id' placeholder='Batch ID:' value='" + gon.restaurant.adp_reports_params.adp_batch_id + "'> <input type='text' class='form-control adp-field' id='adp-cc-code' placeholder='Earnings 3 Code CC Tips:' value='" + gon.restaurant.adp_reports_params.adp_cc_code + "'> </div> </div> <div class='row'> <div class='col-sm-6'> <input type='text' class='form-control adp-field' id='adp-cash-code-column-name' placeholder='Cash Code Column name:' value='" + gon.restaurant.adp_reports_params.adp_cash_code_column_name + "'> <input type='text' class='form-control adp-field' id='adp-cash-amount-column-name' placeholder='Cash Amount Column name:' value='" + gon.restaurant.adp_reports_params.adp_cash_amount_column_name + "'> </div> <div class='col-sm-6'> <input type='text' class='form-control adp-field' id='adp-cc-code-column-name' placeholder='CC Code Column name:' value='" + gon.restaurant.adp_reports_params.adp_cc_code_column_name + "'> <input type='text' class='form-control adp-field' id='adp-cc-amount-column-name' placeholder='CC Amount Column name:' value='" + gon.restaurant.adp_reports_params.adp_cc_amount_column_name + "'> </div> </div> </div> <div class='col-sm-12'> <button type='button' id='custom-adp-report' class='swal2-styled' style='margin-top: 15px;'>Submit</button> </div> </div>";
              customReportButton = '<button type="button" class="swal2-styled" id="show-adp-options">ADP</button>';
            } else if (gon.restaurant.paylocity_reports_params.paylocity_reports_enabled) {
              csvReportButton = "<div id='paylocity-options' class='hidden'> <div class='col-sm-12'> <div class='row'> <div class='col-sm-6'> <input type='text' class='form-control paylocity-field' id='paylocity-payroll-code' placeholder='Payroll code:' value='" + gon.restaurant.paylocity_reports_params.paylocity_payroll_code + "'> <input type='text' class='form-control paylocity-field' id='paylocity-casht-label' placeholder='Earnings 3 CC Tips:' value='" + gon.restaurant.paylocity_reports_params.paylocity_casht_label + "'> </div> <div class='col-sm-6'> <input type='text' class='form-control paylocity-field' id='paylocity-building-number' placeholder='Building number:' value='" + gon.restaurant.paylocity_reports_params.paylocity_building_number + "'> <input type='text' class='form-control paylocity-field' id='paylocity-3-cash-tips-label' placeholder='Earnings 3 Cash Tips:' value='" + gon.restaurant.paylocity_reports_params.paylocity_3_cash_tips_label + "'> </div> </div> </div> <div class='col-sm-12'> <button type='button' id='custom-paylocity-report' class='swal2-styled' style='margin-top: 15px;'>Submit</button> </div> </div>";
              customReportButton = '<button type="button" class="swal2-styled" id="show-paylocity-report-options">Paylocity</button>';
            } else if (gon.restaurant.tips_today_enabled) {
              customReportButton = '<button type="button" class="swal2-styled" id="show-tips-today-report-options">TipsToday</button>';
            } else if (gon.restaurant.toast_payroll_enabled) {
              customReportButton = '<button type="button" class="swal2-styled" id="show-toast-payroll-report-options">Toast Payroll</button>';
            } else if (gon.restaurant.kickfin_report_enabled) {
              if (gon.restaurant.kickfin_filtering_blanks_enabled) {
                kickfin_checkbox = "<input id='kickfin_filtering_blanks_enabled' type='checkbox' value='' type='checkbox' checked='checked'> Filter amount (0 or -)";
              } else {
                kickfin_checkbox = "<input id='kickfin_filtering_blanks_enabled' type='checkbox' value='' type='checkbox'> Filter amount (0 or -)";
              }
              customReportButton = '<button type="button" class="swal2-styled" id="show-kickfin-report-options">Kickfin</button>';
            } else if (gon.restaurant.isolved_report_enabled) {
              customReportButton = '<button type="button" class="swal2-styled" id="show-isolved-report-options">iSolved</button>';
            } else {
              customReportButton = '';
            }
            if (gon.restaurant.fee_reports_enabled) {
              feeReportButton = '<button type="button" class="swal2-styled" id="show-fee-report-options">Fee Report</button>';
            } else {
              feeReportButton = '';
            }
            csvReportButton || (csvReportButton = '');
            if (gon.current_user.distribution_logic_report_access_enabled) {
              distribution_logic_type_input = "<input id='toggle_distribution_logic_report' type='checkbox'> Distribution Logic Report";
            } else {
              distribution_logic_type_input = "";
            }
            if (gon.restaurant.daily_report_new_version_enabled) {
              dailyNewButton = "<button type='button' id='show-daily-options' class='swal2-styled'>Daily report (Monthly)</button>";
            } else {
              dailyNewButton = "";
            }
            if (gon.restaurant.yearly_reports_enabled) {
              monthly_or_year_button = "<button type=\"button\" class=\"swal2-styled\" id=\"yearly-report\">Yearly Report</button>";
            } else {
              monthly_or_year_button = "<button type=\"button\" class=\"swal2-styled\" id=\"bi-weekly-report\">Bi-Weekly & Monthly Report</button>";
            }
            if (gon.restaurant.yearly_reports_tips_collected_enabled) {
              yearly_tips_collected_checkbox = "<div class='yearly-tips-collected-checkbox-wrapper input-group'><input id='yearly_reports_tips_collected_enabled' type='checkbox' value='' type='checkbox' checked='checked'> Tips Collected</div>";
            } else {
              yearly_tips_collected_checkbox = "<div class='yearly-tips-collected-checkbox-wrapper input-group'><input id='yearly_reports_tips_collected_enabled' type='checkbox' value='' type='checkbox'> Tips Collected</div>";
            }
            yearly_tips_collected_checkbox;
            html_content = '<p>What type of report do you want to download?</p> <div class="col-sm-6"> <button type="button" id="daily-report" class="swal2-styled">Daily report</button>' + dailyNewButton + monthly_or_year_button + '</div> <div class="col-sm-6"> <button type="button" class="swal2-styled" id="weekly-report">Weekly Report</button>' + customReportButton + '' + feeReportButton + '</div>' + csvReportButton + '<div class="col-sm-12"> <div id="weekly" style="display:none"> <div id="multi_restaurants_list"> </div> <div class="input-daterange input-group" id="bi-week-datepicker" style="padding-top: 15px;"> <input type="text" name="start" id="bi-week-start" placeholder="Date" class="form-control datapicker" data-required-field="true"><span class="input-group-addon">to</span> <input type="text" name="end" id="bi-week-end" placeholder="Date" class="form-control datapicker" data-required-field="true"> </div> <button type="button" id="bi-weekly-report-submit" class="swal2-styled" style="margin-top: 15px;">Submit</button> </div> <div id="yearly" style="display:none"> <div id="multi_restaurants_list"> </div>' + yearly_tips_collected_checkbox + '<div class="input-daterange input-group" id="year-datepicker" style="padding-top: 15px;"> <input type="text" name="start" id="year-start" placeholder="Date" class="form-control datapicker" data-required-field="true"><span class="input-group-addon">to</span> <input type="text" name="end" id="year-end" placeholder="Date" class="form-control datapicker" data-required-field="true"> </div> <button type="button" id="yearly-report-submit" class="swal2-styled" style="margin-top: 15px;">Submit</button> </div> <div id="daily-new-options" style="display:none"> <div class="input-daterange input-group" id="daily-datepicker" style="padding-top: 15px;"> <input type="text" name="start" id="daily-new-range-start" placeholder="Date" class="form-control datapicker" data-required-field="true"><span class="input-group-addon">to</span> <input type="text" name="end" id="daily-new-range-end" placeholder="Date" class="form-control datapicker" data-required-field="true"> </div> <div class="checkbox"> <input id="daily_report_new_version_default_layout" type="checkbox"> Default layout </div> <button type="button" id="daily-new-report-submit" class="swal2-styled" style="margin-top: 15px;">Submit Daily Report</button> </div> <div id="daily-form-options" style="display:none"> <div id="daily_multi_restaurants_list"> </div> <div class="input-daterange input-group" id="daily-datepicker" style="padding-top: 15px;"> <input type="text" name="start" id="daily-range-start" placeholder="Date" class="form-control datapicker" data-required-field="true"><span class="input-group-addon">to</span> <input type="text" name="end" id="daily-range-end" placeholder="Date" class="form-control datapicker" data-required-field="true"> </div> <div class="show_only_totals-wrapper"> <input id="show_only_totals" type="checkbox" checked> Show only totals' + distribution_logic_type_input + '</div> <button type="button" id="daily-report-submit" class="swal2-styled" style="margin-top: 15px;">Submit Daily Report</button> </div> <div id="daily_fee" style="display:none"> <div class="input-daterange input-group" id="bi-week-datepicker" style="padding-top: 15px;"> <input type="text" name="start" id="daily-fi-week-start" placeholder="Date" class="form-control datapicker" data-required-field="true"><span class="input-group-addon">to</span> <input type="text" name="end" id="daily-fi-week-end" placeholder="Date" class="form-control datapicker" data-required-field="true"> </div> <button type="button" id="fee-report-submit" class="swal2-styled" style="margin-top: 15px;">Submit</button> </div> <div id="tips_today" style="display:none"> <div class="input-daterange input-group" id="tips-today-datepicker" style="padding-top: 15px;"> <input type="text" name="start" id="tips-today-start" placeholder="Date" class="form-control datapicker" data-required-field="true"><span class="input-group-addon">to</span> <input type="text" name="end" id="tips-today-end" placeholder="Date" class="form-control datapicker" data-required-field="true"> </div> <div class="input-daterange input-group" style="padding-top: 15px;"> <input type="text" name="start" id="tips-today-store-id" placeholder="Store ID" class="form-control" value="' + gon.restaurant.store_id + '"> </div> <button type="button" id="tips-today-report-submit" class="swal2-styled" style="margin-top: 15px;">Submit</button> </div> <div id="toast_payroll" style="display:none"> <div class="input-daterange input-group" id="toast-payroll-datepicker" style="padding-top: 15px;"> <input type="text" name="start" id="toast-payroll-start" placeholder="Date" class="form-control datapicker" data-required-field="true"><span class="input-group-addon">to</span> <input type="text" name="end" id="toast-payroll-end" placeholder="Date" class="form-control datapicker" data-required-field="true"> </div> <button type="button" id="toast-payroll-report-submit" class="swal2-styled" style="margin-top: 15px;">Submit</button> </div> <div id="kickfin" style="display:none"> <div class="input-daterange input-group" id="kickfin-filter" style="padding-top: 15px;">' + kickfin_checkbox + '</div> <div class="input-daterange input-group" id="kickfin-datepicker" style="padding-top: 15px;"> <input type="text" name="start" id="kickfin-start" placeholder="Date" class="form-control datapicker" data-required-field="true"><span class="input-group-addon">to</span> <input type="text" name="end" id="kickfin-end" placeholder="Date" class="form-control datapicker" data-required-field="true"> </div> <button type="button" id="kickfin-report-submit" class="swal2-styled" style="margin-top: 15px;">Submit</button> </div> <div id="isolved" style="display:none"> <div class="input-daterange input-group" id="isolved-datepicker" style="padding-top: 15px;"> <input type="text" name="start" id="isolved-start" placeholder="Date" class="form-control datapicker" data-required-field="true"><span class="input-group-addon">to</span> <input type="text" name="end" id="isolved-end" placeholder="Date" class="form-control datapicker" data-required-field="true"> </div> <button type="button" id="isolved-report-submit" class="swal2-styled" style="margin-top: 15px;">Submit</button> </div> </div>';
            return swal({
              modalIdentifier: "reports-type",
              title: "",
              html: html_content,
              customClass: 'swal2-overflow',
              text: "What type of report do you want to download?",
              type: "info",
              confirmButtonColor: "#28C256",
              confirmButtonText: "Weekly report",
              showCancelButton: false,
              showConfirmButton: false,
              cancelButtonText: "Daily Report",
              cancelButtonColor: '#28C256',
              onOpen: function() {
                $('#bi-week-start').datepicker().on("changeDate", (function(_this) {
                  return function(e) {
                    var date, maxDate, startdate;
                    startdate = $("#bi-week-start").datepicker("getDate");
                    date = startdate;
                    if (gon.bi_weekly_report_max_days) {
                      maxDate = moment(startdate).add(gon.bi_weekly_report_max_days, "day").format("MM/DD/YYYY");
                    } else {
                      maxDate = moment(startdate).add(30, "day").format("MM/DD/YYYY");
                    }
                    $('#bi-week-end').datepicker('remove');
                    $("#bi-week-end").datepicker();
                    $("#bi-week-end").datepicker("setStartDate", startdate);
                    $("#bi-week-end").datepicker("setEndDate", maxDate);
                    return $("#bi-week-end").datepicker("setDate", date);
                  };
                })(this));
                $('#year-start').datepicker().on("changeDate", (function(_this) {
                  return function(e) {
                    var date, maxDate, startdate;
                    startdate = $("#year-start").datepicker("getDate");
                    date = startdate;
                    if (gon.bi_weekly_report_max_days) {
                      maxDate = moment(startdate).add(gon.bi_weekly_report_max_days, "day").format("MM/DD/YYYY");
                    } else {
                      maxDate = moment(startdate).add(365, "day").format("MM/DD/YYYY");
                    }
                    $('#year-end').datepicker('remove');
                    $("#year-end").datepicker();
                    $("#year-end").datepicker("setStartDate", startdate);
                    $("#year-end").datepicker("setEndDate", maxDate);
                    return $("#year-end").datepicker("setDate", date);
                  };
                })(this));
                $('#daily-range-start').datepicker().on("changeDate", (function(_this) {
                  return function(e) {
                    var date, maxDate, startdate;
                    startdate = $("#daily-range-start").datepicker("getDate");
                    date = startdate;
                    maxDate = moment(startdate).add(30, "day").format("MM/DD/YYYY");
                    $('#daily-range-end').datepicker('remove');
                    $("#daily-range-end").datepicker();
                    $("#daily-range-end").datepicker("setStartDate", startdate);
                    $("#daily-range-end").datepicker("setEndDate", maxDate);
                    return $("#daily-range-end").datepicker("setDate", date);
                  };
                })(this));
                $('#daily-new-range-start').datepicker().on("changeDate", (function(_this) {
                  return function(e) {
                    var date, maxDate, startdate;
                    startdate = $("#daily-new-range-start").datepicker("getDate");
                    date = startdate;
                    maxDate = moment(startdate).add(30, "day").format("MM/DD/YYYY");
                    $('#daily-new-range-end').datepicker('remove');
                    $("#daily-new-range-end").datepicker();
                    $("#daily-new-range-end").datepicker("setStartDate", startdate);
                    $("#daily-new-range-end").datepicker("setEndDate", maxDate);
                    return $("#daily-new-range-end").datepicker("setDate", date);
                  };
                })(this));
                $('#tips-today-start').datepicker().on("changeDate", (function(_this) {
                  return function(e) {
                    var date, maxDate, startdate;
                    startdate = $("#tips-today-start").datepicker("getDate");
                    date = startdate;
                    if (gon.bi_weekly_report_max_days) {
                      maxDate = moment(startdate).add(gon.bi_weekly_report_max_days, "day").format("MM/DD/YYYY");
                    } else {
                      maxDate = moment(startdate).add(30, "day").format("MM/DD/YYYY");
                    }
                    $('#tips-today-end').datepicker('remove');
                    $("#tips-today-end").datepicker();
                    $("#tips-today-end").datepicker("setStartDate", startdate);
                    $("#tips-today-end").datepicker("setEndDate", maxDate);
                    return $("#tips-today-end").datepicker("setDate", date);
                  };
                })(this));
                $('#toast-payroll-start').datepicker().on("changeDate", (function(_this) {
                  return function(e) {
                    var date, maxDate, startdate;
                    view.setReportFormat(scope, "csv");
                    format = "csv";
                    startdate = $("#toast-payroll-start").datepicker("getDate");
                    date = startdate;
                    if (gon.bi_weekly_report_max_days) {
                      maxDate = moment(startdate).add(gon.bi_weekly_report_max_days, "day").format("MM/DD/YYYY");
                    } else {
                      maxDate = moment(startdate).add(30, "day").format("MM/DD/YYYY");
                    }
                    $('#toast-payroll-end').datepicker('remove');
                    $("#toast-payroll-end").datepicker();
                    $("#toast-payroll-end").datepicker("setStartDate", startdate);
                    $("#toast-payroll-end").datepicker("setEndDate", maxDate);
                    return $("#toast-payroll-end").datepicker("setDate", date);
                  };
                })(this));
                $('#kickfin-start').datepicker().on("changeDate", (function(_this) {
                  return function(e) {
                    var date, maxDate, startdate;
                    view.setReportFormat(scope, "csv");
                    format = "csv";
                    startdate = $("#kickfin-start").datepicker("getDate");
                    date = startdate;
                    maxDate = moment(startdate).add(30, "day").format("MM/DD/YYYY");
                    $('#kickfin-end').datepicker('remove');
                    $("#kickfin-end").datepicker();
                    $("#kickfin-end").datepicker("setStartDate", startdate);
                    $("#kickfin-end").datepicker("setEndDate", maxDate);
                    return $("#kickfin-end").datepicker("setDate", date);
                  };
                })(this));
                $('#isolved-start').datepicker().on("changeDate", (function(_this) {
                  return function(e) {
                    var date, maxDate, startdate;
                    startdate = $("#isolved-start").datepicker("getDate");
                    date = startdate;
                    if (gon.bi_weekly_report_max_days) {
                      maxDate = moment(startdate).add(gon.bi_weekly_report_max_days, "day").format("MM/DD/YYYY");
                    } else {
                      maxDate = moment(startdate).add(30, "day").format("MM/DD/YYYY");
                    }
                    $('#isolved-end').datepicker('remove');
                    $("#isolved-end").datepicker();
                    $("#isolved-end").datepicker("setStartDate", startdate);
                    $("#isolved-end").datepicker("setEndDate", maxDate);
                    return $("#isolved-end").datepicker("setDate", date);
                  };
                })(this));
                $('#bi-week-start-for-adp').datepicker().on("changeDate", (function(_this) {
                  return function(e) {
                    var date, maxDate, startdate;
                    startdate = $("#bi-week-start-for-adp").datepicker("getDate");
                    date = startdate;
                    if (gon.bi_weekly_report_max_days) {
                      maxDate = moment(startdate).add(gon.bi_weekly_report_max_days, "day").format("MM/DD/YYYY");
                    } else {
                      maxDate = moment(startdate).add(30, "day").format("MM/DD/YYYY");
                    }
                    $('#bi-week-end-for-adp').datepicker('remove');
                    $("#bi-week-end-for-adp").datepicker();
                    $("#bi-week-end-for-adp").datepicker("setStartDate", startdate);
                    $("#bi-week-end-for-adp").datepicker("setEndDate", maxDate);
                    return $("#bi-week-end-for-adp").datepicker("setDate", date);
                  };
                })(this));
                if (gon.restaurant.paycom_extended_reports_enabled) {
                  $('#paycom-start').datepicker().on("changeDate", (function(_this) {
                    return function(e) {
                      var date, maxDate, startdate;
                      startdate = $("#paycom-start").datepicker("getDate");
                      date = startdate;
                      maxDate = moment(startdate).add(30, "day").format("MM/DD/YYYY");
                      $('#paycom-end').datepicker('remove');
                      $("#paycom-end").datepicker();
                      $("#paycom-end").datepicker("setStartDate", startdate);
                      $("#paycom-end").datepicker("setEndDate", maxDate);
                      return $("#paycom-end").datepicker("setDate", date);
                    };
                  })(this));
                }
                $('#daily-fi-week-start').datepicker().on("changeDate", (function(_this) {
                  return function(e) {
                    var date, maxDate, startdate;
                    startdate = $("#daily-fi-week-start").datepicker("getDate");
                    date = startdate;
                    maxDate = moment(startdate).add(30, "day").format("MM/DD/YYYY");
                    $('#daily-fi-week-end').datepicker('remove');
                    $("#daily-fi-week-end").datepicker();
                    $("#daily-fi-week-end").datepicker("setStartDate", startdate);
                    $("#daily-fi-week-end").datepicker("setEndDate", maxDate);
                    return $("#daily-fi-week-end").datepicker("setDate", date);
                  };
                })(this));
                $('#cliq-start').datepicker().on("changeDate", (function(_this) {
                  return function(e) {
                    var date, maxDate, startdate;
                    startdate = $("#cliq-start").datepicker("getDate");
                    date = startdate;
                    maxDate = moment(startdate).add(30, "day").format("MM/DD/YYYY");
                    $('#cliq-end').datepicker('remove');
                    $("#cliq-end").datepicker();
                    $("#cliq-end").datepicker("setStartDate", startdate);
                    $("#cliq-end").datepicker("setEndDate", maxDate);
                    return $("#cliq-end").datepicker("setDate", date);
                  };
                })(this));
                $('#daily-fi-week-start').datepicker().on("changeDate", (function(_this) {
                  return function(e) {
                    var date, maxDate, startdate;
                    startdate = $("#daily-fi-week-start").datepicker("getDate");
                    date = startdate;
                    maxDate = moment(startdate).add(30, "day").format("MM/DD/YYYY");
                    $('#daily-fi-week-end').datepicker('remove');
                    $("#daily-fi-week-end").datepicker();
                    $("#daily-fi-week-end").datepicker("setStartDate", startdate);
                    $("#daily-fi-week-end").datepicker("setEndDate", maxDate);
                    return $("#daily-fi-week-end").datepicker("setDate", date);
                  };
                })(this));
                $('#daily-report').on('click', function() {
                  var $button, html, i, len, ref, restaurantHash;
                  if (!view.finalTipOutRangeIsFineCheck(null, null)) {
                    return false;
                  }
                  if (gon.current_user.multirestaurant_reports_enabled) {
                    html = "";
                    ref = gon.current_user.restaurants;
                    for (i = 0, len = ref.length; i < len; i++) {
                      restaurantHash = ref[i];
                      html = html + ("<input class=\"restaurant-checkbox\" value=" + restaurantHash.id + " type=\"checkbox\" checked='checkbox'\">" + restaurantHash.name + " ");
                    }
                    $("#daily_multi_restaurants_list").html(html);
                    window.bindCheckboxes();
                    $('#daily-form-options').show();
                    $("#adp-options").addClass("hidden");
                    $('#daily_fee').hide();
                    $('#lock-records').hide();
                    return $('#weekly').hide();
                  } else {
                    $('#weekly').hide();
                    $("#excel_report_type").val("daily");
                    swal.close();
                    $button = $('#report-form').find("input[type='submit']");
                    $button.removeAttr('data-disable-with');
                    form.submit();
                    return $button.removeAttr('data-disable-with');
                  }
                });
                $('#daily-report-submit').on("click", function(e) {
                  var $button, checkBox, end_old_val, i, ids, len, ref, report_end, report_start, start_old_val;
                  ids = [];
                  ref = $("#daily_multi_restaurants_list .restaurant-checkbox");
                  for (i = 0, len = ref.length; i < len; i++) {
                    checkBox = ref[i];
                    if (checkBox.checked) {
                      ids.push($(checkBox).val());
                    }
                  }
                  ids;
                  $("#multirestaurant_restaurants").val(ids);
                  $('#daily-form-options').hide();
                  $('#lock-records').hide();
                  $('#weekly').hide();
                  report_start = $('#report-form').find("#start");
                  report_end = $('#report-form').find("#end");
                  start_old_val = report_start.val();
                  end_old_val = report_end.val();
                  report_start.val($("#daily-range-start").val());
                  report_end.val($("#daily-range-end").val());
                  $("#excel_report_type").val("daily");
                  swal.close();
                  $button = $('#report-form').find("input[type='submit']");
                  $button.removeAttr('data-disable-with');
                  form.submit();
                  report_start.val(start_old_val);
                  report_end.val(end_old_val);
                  return $button.removeAttr('data-disable-with');
                });
                $('#daily-new-report-submit').on("click", function(e) {
                  var $button, end_old_val, report_end, report_start, start_old_val;
                  $('#daily-form-options').hide();
                  $('#lock-records').hide();
                  $('#weekly').hide();
                  report_start = $('#report-form').find("#start");
                  report_end = $('#report-form').find("#end");
                  start_old_val = report_start.val();
                  end_old_val = report_end.val();
                  report_start.val($("#daily-new-range-start").val());
                  report_end.val($("#daily-new-range-end").val());
                  $("#excel_report_type").val("daily_new");
                  swal.close();
                  $button = $('#report-form').find("input[type='submit']");
                  $button.removeAttr('data-disable-with');
                  form.submit();
                  report_start.val(start_old_val);
                  report_end.val(end_old_val);
                  return $button.removeAttr('data-disable-with');
                });
                $('#custom-weekly-report').on('click', function() {
                  var $button, end_old_val, report_end, report_start, start_old_val;
                  view.setReportFormat(scope, "csv");
                  format = "csv";
                  $('#weekly').hide();
                  $('#lock-records').hide();
                  $("#excel_report_type").val("custom_1");
                  $("#report-form #paycom_cash_code").val($("#paycom-cash-code").val());
                  $("#report-form #paycom_cc_code").val($("#paycom-cc-code").val());
                  if (gon.restaurant.paycom_extended_reports_enabled) {
                    $("#report-form #paycom_ag_svc_code").val($("#paycom-ag-svc-code").val());
                    gon.restaurant.paycom_reports_params.paycom_ag_svc_code = $("#paycom-ag-svc-code").val();
                  }
                  gon.restaurant.paycom_reports_params.paycom_cash_code = $("#paycom-cash-code").val();
                  gon.restaurant.paycom_reports_params.paycom_cc_code = $("#paycom-cc-code").val();
                  swal.close();
                  $button = $('#report-form').find("input[type='submit']");
                  $button.removeAttr('data-disable-with');
                  if (gon.restaurant.paycom_extended_reports_enabled) {
                    report_start = $('#report-form').find("#start");
                    report_end = $('#report-form').find("#end");
                    start_old_val = report_start.val();
                    end_old_val = report_end.val();
                    report_start.val($("#paycom-start").val());
                    report_end.val($("#paycom-end").val());
                  }
                  form.submit();
                  if (gon.restaurant.paycom_extended_reports_enabled) {
                    report_start.val(start_old_val);
                    report_end.val(end_old_val);
                  }
                  return $button.removeAttr('data-disable-with');
                });
                $('#show-paychex-options').on('click', function() {
                  $("#paychex-options").removeClass("hidden");
                  $('#lock-records').hide();
                  return $('#weekly').hide();
                });
                $('#show-daily-options').on('click', function() {
                  var checkBox;
                  window.bindCheckboxes();
                  checkBox = $("#daily_report_new_version_default_layout");
                  if (gon.restaurant.daily_report_new_version_default_layout === true) {
                    $(checkBox).iCheck("check");
                  } else {
                    $(checkBox).iCheck("uncheck");
                  }
                  $('#daily-new-options').show();
                  $("#adp-options").addClass("hidden");
                  $('#daily_fee').hide();
                  $('#lock-records').hide();
                  return $('#weekly').hide();
                });
                $('#paychex-weekly-report').on('click', function() {
                  var $button;
                  view.setReportFormat(scope, "csv");
                  format = "csv";
                  $('#weekly').hide();
                  $('#lock-records').hide();
                  $("#excel_report_type").val("paychex");
                  $("#report-form #paychex_client_code").val($("#paychex-client-code").val());
                  gon.restaurant.paychex_reports_params.paychex_client_code = $("#paychex-client-code").val();
                  swal.close();
                  $button = $('#report-form').find("input[type='submit']");
                  $button.removeAttr('data-disable-with');
                  form.submit();
                  return $button.removeAttr('data-disable-with');
                });
                $('#show-adp-options').on('click', function() {
                  $("#adp-options").removeClass("hidden");
                  $('#weekly').hide();
                  $('#lock-records').hide();
                  return window.bindCheckboxes();
                });
                $('#show-cliq-options').on('click', function() {
                  $("#cliq-options").removeClass("hidden");
                  $("#adp-options").hide("hidden");
                  $('#weekly').hide();
                  $('#lock-records').hide();
                  return window.bindCheckboxes();
                });
                $('#show-paylocity-report-options').on('click', function() {
                  $("#paylocity-options").removeClass("hidden");
                  $('#weekly').hide();
                  $('#lock-records').hide();
                  return window.bindCheckboxes();
                });
                $('#show-paycom-options').on('click', function() {
                  window.bindCheckboxes();
                  $("#paycom-options").removeClass("hidden");
                  $('#weekly').hide();
                  return $('#lock-records').hide();
                });
                $('#custom-adp-report').on('click', function() {
                  var $button, report_end, report_start;
                  if (!view.finalTipOutRangeIsFineCheck(null, null)) {
                    return false;
                  }
                  view.setReportFormat(scope, "csv");
                  format = "csv";
                  $('#weekly').hide();
                  $('#lock-records').hide();
                  $("#excel_report_type").val("adp");
                  $("#report-form #adp_co_code").val($("#adp-co-code").val());
                  $("#report-form #adp_batch_id").val($("#adp-batch-id").val());
                  $("#report-form #adp_cash_code").val($("#adp-cash-code").val());
                  $("#report-form #adp_cc_code").val($("#adp-cc-code").val());
                  $("#report-form #adp_cc_code_column_name").val($("#adp-cc-code-column-name").val());
                  $("#report-form #adp_cc_amount_column_name").val($("#adp-cc-amount-column-name").val());
                  $("#report-form #adp_cash_code_column_name").val($("#adp-cash-code-column-name").val());
                  $("#report-form #adp_cash_amount_column_name").val($("#adp-cash-amount-column-name").val());
                  gon.restaurant.adp_reports_params.adp_co_code = $("#adp-co-code").val();
                  gon.restaurant.adp_reports_params.adp_batch_id = $("#adp-batch-id").val();
                  gon.restaurant.adp_reports_params.adp_cash_code = $("#adp-cash-code").val();
                  gon.restaurant.adp_reports_params.adp_cc_code = $("#adp-cc-code").val();
                  gon.restaurant.adp_reports_params.adp_cc_code_column_name = $("#adp-cc-code-column-name").val();
                  gon.restaurant.adp_reports_params.adp_cc_amount_column_name = $("#adp-cc-amount-column-name").val();
                  gon.restaurant.adp_reports_params.adp_cash_code_column_name = $("#adp-cash-code-column-name").val();
                  gon.restaurant.adp_reports_params.adp_cash_amount_column_name = $("#adp-cash-amount-column-name").val();
                  if (gon.restaurant.adp_reports_extended) {
                    report_start = $('#report-form').find("#start");
                    report_end = $('#report-form').find("#end");
                    report_start.val($("#adp-options #bi-week-start-for-adp").val());
                    report_end.val($("#adp-options #bi-week-end-for-adp").val());
                  }
                  swal.close();
                  $button = $('#report-form').find("input[type='submit']");
                  $button.removeAttr('data-disable-with');
                  form.submit();
                  return $button.removeAttr('data-disable-with');
                });
                $('#custom-paylocity-report').on('click', function() {
                  var $button;
                  if (!view.finalTipOutRangeIsFineCheck(null, null)) {
                    return false;
                  }
                  view.setReportFormat(scope, "csv");
                  format = "csv";
                  $('#weekly').hide();
                  $('#lock-records').hide();
                  $("#excel_report_type").val("paylocity");
                  $("#report-form #paylocity_payroll_code").val($("#paylocity-payroll-code").val());
                  $("#report-form #paylocity_casht_label").val($("#paylocity-casht-label").val());
                  $("#report-form #paylocity_building_number").val($("#paylocity-building-number").val());
                  $("#report-form #paylocity_3_cash_tips_label").val($("#paylocity-3-cash-tips-label").val());
                  gon.restaurant.paylocity_reports_params.paylocity_payroll_code = $("#paylocity-payroll-code").val();
                  gon.restaurant.paylocity_reports_params.paylocity_casht_label = $("#paylocity-casht-label").val();
                  gon.restaurant.paylocity_reports_params.paylocity_building_number = $("#paylocity-building-number").val();
                  gon.restaurant.paylocity_reports_params.paylocity_3_cash_tips_label = $("#paylocity-3-cash-tips-label").val();
                  swal.close();
                  $button = $('#report-form').find("input[type='submit']");
                  $button.removeAttr('data-disable-with');
                  form.submit();
                  return $button.removeAttr('data-disable-with');
                });
                $('#weekly-report').on('click', function() {
                  var $button;
                  if (!view.finalTipOutRangeIsFineCheck(null, null)) {
                    return false;
                  }
                  $('#weekly').hide();
                  $('#lock-records').hide();
                  $("#excel_report_type").val("weekly");
                  swal.close();
                  $button = $('#report-form').find("input[type='submit']");
                  $button.removeAttr('data-disable-with');
                  form.submit();
                  return $button.removeAttr('data-disable-with');
                });
                $('#bi-weekly-report').on('click', function() {
                  var html, i, len, ref, restaurantHash;
                  if (gon.current_user.multirestaurant_reports_enabled) {
                    html = "";
                    ref = gon.current_user.restaurants;
                    for (i = 0, len = ref.length; i < len; i++) {
                      restaurantHash = ref[i];
                      html = html + ("<input class=\"restaurant-checkbox\" value=" + restaurantHash.id + " type=\"checkbox\" checked='checkbox'\">" + restaurantHash.name + " ");
                    }
                    $("#multi_restaurants_list").html(html);
                    window.bindCheckboxes();
                  }
                  $('#weekly').show();
                  $('#daily-form-options').hide();
                  $("#adp-options").addClass("hidden");
                  $('#daily_fee').hide();
                  return $('#lock-records').hide();
                });
                $('#yearly-report').on('click', function() {
                  var html, i, len, ref, restaurantHash;
                  if (gon.current_user.multirestaurant_reports_enabled) {
                    html = "";
                    ref = gon.current_user.restaurants;
                    for (i = 0, len = ref.length; i < len; i++) {
                      restaurantHash = ref[i];
                      html = html + ("<input class=\"restaurant-checkbox\" value=" + restaurantHash.id + " type=\"checkbox\" checked='checkbox'\">" + restaurantHash.name + " ");
                    }
                    $("#multi_restaurants_list").html(html);
                  }
                  window.bindCheckboxes();
                  $('#yearly').show();
                  $('#daily-form-options').hide();
                  $("#adp-options").addClass("hidden");
                  $('#daily_fee').hide();
                  return $('#lock-records').hide();
                });
                $('#show-fee-report-options').on('click', function() {
                  $('#daily_fee').show();
                  $('#weekly').hide();
                  $("#adp-options").addClass("hidden");
                  return $('#lock-records').hide();
                });
                $('#show-tips-today-report-options').on('click', function() {
                  $('#tips_today').show();
                  $('#weekly').hide();
                  $("#adp-options").addClass("hidden");
                  return $('#lock-records').hide();
                });
                $('#show-toast-payroll-report-options').on('click', function() {
                  $('#toast_payroll').show();
                  $('#weekly').hide();
                  $("#adp-options").addClass("hidden");
                  return $('#lock-records').hide();
                });
                $('#show-kickfin-report-options').on('click', function() {
                  window.bindCheckboxes();
                  $('#kickfin').show();
                  $('#toast_payroll').hide();
                  $('#weekly').hide();
                  $("#adp-options").addClass("hidden");
                  return $('#lock-records').hide();
                });
                $('#show-isolved-report-options').on('click', function() {
                  $('#isolved').show();
                  $('#weekly').hide();
                  $("#adp-options").addClass("hidden");
                  return $('#lock-records').hide();
                });
                $('#bi-weekly-report-submit').on('click', function() {
                  var $button, checkBox, end_old_val, i, ids, len, ref, report_end, report_start, start_old_val;
                  if (!view.finalTipOutRangeIsFineCheck($("#bi-week-start").val(), $("#bi-week-end").val())) {
                    return false;
                  }
                  ids = [];
                  ref = $("#multi_restaurants_list .restaurant-checkbox");
                  for (i = 0, len = ref.length; i < len; i++) {
                    checkBox = ref[i];
                    if (checkBox.checked) {
                      ids.push($(checkBox).val());
                    }
                  }
                  ids;
                  $("#multirestaurant_restaurants").val(ids);
                  report_start = $('#report-form').find("#start");
                  report_end = $('#report-form').find("#end");
                  start_old_val = report_start.val();
                  end_old_val = report_end.val();
                  report_start.val($("#bi-week-start").val());
                  report_end.val($("#bi-week-end").val());
                  $("#excel_report_type").val("Bi-Weekly");
                  swal.close();
                  $button = $('#report-form').find("input[type='submit']");
                  $button.removeAttr('data-disable-with');
                  form.submit();
                  report_start.val(start_old_val);
                  report_end.val(end_old_val);
                  return $button.removeAttr('data-disable-with');
                });
                $('#yearly-report-submit').on('click', function() {
                  var $button, checkBox, end_old_val, i, ids, len, ref, report_end, report_start, start_old_val;
                  if (!view.finalTipOutRangeIsFineCheck($("#bi-week-start").val(), $("#bi-week-end").val())) {
                    return false;
                  }
                  ids = [];
                  ref = $("#multi_restaurants_list .restaurant-checkbox");
                  for (i = 0, len = ref.length; i < len; i++) {
                    checkBox = ref[i];
                    if (checkBox.checked) {
                      ids.push($(checkBox).val());
                    }
                  }
                  ids;
                  $("#multirestaurant_restaurants").val(ids);
                  report_start = $('#report-form').find("#start");
                  report_end = $('#report-form').find("#end");
                  start_old_val = report_start.val();
                  end_old_val = report_end.val();
                  report_start.val($("#year-start").val());
                  report_end.val($("#year-end").val());
                  $("#excel_report_type").val("yearly");
                  swal.close();
                  $button = $('#report-form').find("input[type='submit']");
                  $button.removeAttr('data-disable-with');
                  form.submit();
                  report_start.val(start_old_val);
                  report_end.val(end_old_val);
                  return $button.removeAttr('data-disable-with');
                });
                $('#cliq-report-submit').on('click', function() {
                  var $button, end_old_val, report_end, report_start, start_old_val;
                  view.setReportFormat(scope, "xls");
                  format = "xls";
                  report_start = $('#report-form').find("#start");
                  report_end = $('#report-form').find("#end");
                  start_old_val = report_start.val();
                  end_old_val = report_end.val();
                  report_start.val($("#cliq-start").val());
                  report_end.val($("#cliq-end").val());
                  $("#excel_report_type").val("cliq");
                  swal.close();
                  $button = $('#report-form').find("input[type='submit']");
                  $button.removeAttr('data-disable-with');
                  form.submit();
                  report_start.val(start_old_val);
                  report_end.val(end_old_val);
                  return $button.removeAttr('data-disable-with');
                });
                $('#fee-report-submit').on('click', function() {
                  var $button, end_old_val, report_end, report_start, start_old_val;
                  report_start = $('#report-form').find("#start");
                  report_end = $('#report-form').find("#end");
                  start_old_val = report_start.val();
                  end_old_val = report_end.val();
                  report_start.val($("#daily-fi-week-start").val());
                  report_end.val($("#daily-fi-week-end").val());
                  $("#excel_report_type").val("daily_fee");
                  swal.close();
                  $button = $('#report-form').find("input[type='submit']");
                  $button.removeAttr('data-disable-with');
                  form.submit();
                  report_start.val(start_old_val);
                  report_end.val(end_old_val);
                  return $button.removeAttr('data-disable-with');
                });
                $('#tips-today-report-submit').on('click', function() {
                  var $button, end_old_val, report_end, report_start, start_old_val;
                  report_start = $('#report-form').find("#start");
                  report_end = $('#report-form').find("#end");
                  start_old_val = report_start.val();
                  end_old_val = report_end.val();
                  report_start.val($("#tips-today-start").val());
                  report_end.val($("#tips-today-end").val());
                  $("#excel_report_type").val("tips_today");
                  if (!_.any($("form#report-form").find("#tips-today-store-id-input"))) {
                    $("form#report-form").prepend("<input type=\"hidden\" name=\"tips-today-store-id-input\" id=\"tips-today-store-id-input\">");
                  }
                  $("#tips-today-store-id-input").val($("#tips-today-store-id").val());
                  if (!_.isEmpty($("#tips-today-store-id-input").val())) {
                    view.setReportFormat(scope, "csv");
                    format = "csv";
                  }
                  swal.close();
                  $button = $('#report-form').find("input[type='submit']");
                  $button.removeAttr('data-disable-with');
                  form.submit();
                  report_start.val(start_old_val);
                  report_end.val(end_old_val);
                  return $button.removeAttr('data-disable-with');
                });
                $('#toast-payroll-report-submit').on('click', function() {
                  var $button, end_old_val, report_end, report_start, start_old_val;
                  report_start = $('#report-form').find("#start");
                  report_end = $('#report-form').find("#end");
                  start_old_val = report_start.val();
                  end_old_val = report_end.val();
                  report_start.val($("#toast-payroll-start").val());
                  report_end.val($("#toast-payroll-end").val());
                  $("#excel_report_type").val("toast_payroll");
                  swal.close();
                  $button = $('#report-form').find("input[type='submit']");
                  $button.removeAttr('data-disable-with');
                  form.submit();
                  report_start.val(start_old_val);
                  report_end.val(end_old_val);
                  return $button.removeAttr('data-disable-with');
                });
                $('#kickfin-report-submit').on('click', function() {
                  var $button, end_old_val, report_end, report_start, start_old_val;
                  view.setReportFormat(scope, "csv");
                  format = "csv";
                  report_start = $('#report-form').find("#start");
                  report_end = $('#report-form').find("#end");
                  start_old_val = report_start.val();
                  end_old_val = report_end.val();
                  report_start.val($("#kickfin-start").val());
                  report_end.val($("#kickfin-end").val());
                  $("#excel_report_type").val("kickfin");
                  swal.close();
                  $button = $('#report-form').find("input[type='submit']");
                  $button.removeAttr('data-disable-with');
                  form.submit();
                  report_start.val(start_old_val);
                  report_end.val(end_old_val);
                  return $button.removeAttr('data-disable-with');
                });
                return $('#isolved-report-submit').on('click', function() {
                  var $button, end_old_val, report_end, report_start, start_old_val;
                  report_start = $('#report-form').find("#start");
                  report_end = $('#report-form').find("#end");
                  start_old_val = report_start.val();
                  end_old_val = report_end.val();
                  report_start.val($("#isolved-start").val());
                  report_end.val($("#isolved-end").val());
                  $("#excel_report_type").val("isolved");
                  swal.close();
                  $button = $('#report-form').find("input[type='submit']");
                  $button.removeAttr('data-disable-with');
                  form.submit();
                  report_start.val(start_old_val);
                  report_end.val(end_old_val);
                  return $button.removeAttr('data-disable-with');
                });
              }
            });
          }
        }
      },
      selectNumbersType: function(e) {
        var showClass;
        $(".select-numbers-type button").removeClass("active");
        $(e.currentTarget).addClass("active");
        showClass = $(e.currentTarget).attr("data-num-type");
        $("[data-type='changeble'], .extended-sales-data-wrapper").find(".cc, .cash, .cc_incl_overtips, .cash_incl_overtips, .total_tips_incl_overtips, .cc_cash_minus_cash_collected, .global, .tip-outs-cc, .tip-outs-cash, .tip-outs-ag-svc, .sales-cc, .sales-cash, .ag-svc, .ag_svc, .tip-outs-fee, .fee, .food-sales, .beverage-sales, .sales_cc, .sales_cash, .hourly_rate").hide();
        $("[data-type='changeble'], .extended-sales-data-wrapper").find("." + showClass + ", ." + (showClass.replace('-', '_'))).show();
        return this.showTotalChart(showClass);
      },
      showTotalChart: function(type) {
        var areas, ctx, data, dataPresent, labels, sharpLineData, sharpLineOptions, text, uselessDataPresent;
        if (type === 'sales-cc' || type === 'sales-cash') {
          $(".chart-block").hide();
          return true;
        }
        $(".chart-block").show();
        if (this.myNewChart) {
          this.myNewChart.destroy();
        }
        dataPresent = $("#totals-data-block").length > 0;
        uselessDataPresent = (type === "tip-outs-cc" || type === "tip-outs-cash" || type === "tip-outs-ag-svc" || type === "tip-outs-fee") && $("select#area_type_id").val().toLowerCase() === "all";
        $("#area-colors").html("");
        if (dataPresent && !uselessDataPresent) {
          labels = _.map($("#totals-data-block thead tr.dates th.date"), function(e) {
            return $(e).text();
          });
          data = $("#totals-data-block tbody tr.total-" + type + "-tips td.data ." + type);
          if (type === "tip-outs-cc" || type === "tip-outs-cash") {
            data = [
              {
                label: "Example dataset",
                fillColor: "rgba(98,203,49,0.5)",
                strokeColor: "rgba(98,203,49,0.7)",
                pointColor: "rgba(98,203,49,1)",
                pointStrokeColor: "#fff",
                pointHighlightFill: "#fff",
                pointHighlightStroke: "rgba(98,203,49,1)",
                data: _.map(data, function(e) {
                  return parseFloat(Math.abs($(e).find(".received").text()));
                })
              }, {
                label: "Example dataset",
                fillColor: "rgba(255, 128, 0, 0.5)",
                strokeColor: "rgba(255, 128, 0, 0.7)",
                pointColor: "rgba(255, 128, 0, 1)",
                pointStrokeColor: "#fff",
                pointHighlightFill: "#fff",
                pointHighlightStroke: "rgba(26,179,148,1)",
                data: _.map(data, function(e) {
                  return parseFloat(Math.abs($(e).find(".given").text()));
                })
              }
            ];
          } else {
            data = [];
            areas = _.map($("#totals-data-block tbody tr.area-totals"), function(e) {
              return $(e).attr("data-area-name").toLowerCase();
            });
            areas = _.uniq(areas);
            text = "";
            _.each(areas, function(e) {
              var color;
              color = gon.area_colors[e];
              text += "<div class='area-label' style='color:" + color + ";'><span class='area-sign' style='background-color:" + color + ";'></span>" + e + "</div>";
              if (type === "ag-svc") {
                type = "ag_svc";
              }
              return data.push({
                fillColor: color,
                strokeColor: color,
                highlightFill: color,
                highlightStroke: color,
                data: _.map($("#totals-data-block tbody tr[data-area-name=\"" + e + "\"].area-totals.total-" + type + "-tips td.area-data ." + type), function(e) {
                  return parseFloat($(e).text());
                })
              });
            });
            $("#area-colors").html(text);
          }
          sharpLineData = {
            labels: labels,
            datasets: data
          };
          sharpLineOptions = {
            scaleBeginAtZero: true,
            scaleShowGridLines: true,
            scaleGridLineColor: "rgba(0,0,0,.05)",
            scaleGridLineWidth: 1,
            barShowStroke: true,
            barStrokeWidth: 1,
            barValueSpacing: 5,
            barDatasetSpacing: 1,
            responsive: true,
            maintainAspectRatio: false
          };
          ctx = document.getElementById("singleBarOptions").getContext("2d");
          return this.myNewChart = new Chart(ctx).Bar(sharpLineData, sharpLineOptions);
        }
      },
      showExtendedSalesData: function(e) {
        var checked;
        checked = e.currentTarget.checked;
        return $("body.reports").attr("data-sales-data-extended-shown", checked);
      },
      showOnlyTotalsToggle: function(e) {
        var checked;
        checked = e.currentTarget.checked;
        if (checked) {
          return $("form input#show_only_totals").val("true");
        } else {
          return $("form input#show_only_totals").val("false");
        }
      },
      distributionLogicReportToggle: function(e) {
        var checked;
        checked = e.currentTarget.checked;
        if (checked) {
          return $("form input#distribution_logic_report").val("true");
        } else {
          return $("form input#distribution_logic_report").val("false");
        }
      },
      togglePaycomFilteringBlanks: function(e) {
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: {
            restaurant: {
              paycom_filtering_blanks_enabled: e.currentTarget.checked
            }
          },
          success: (function(_this) {
            return function() {
              return console.log("+");
            };
          })(this),
          error: function() {
            return console.log("-");
          }
        });
      },
      toggleYearlyReportsTipsCollectedEnabled: function(e) {
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: {
            restaurant: {
              yearly_reports_tips_collected_enabled: e.currentTarget.checked
            }
          },
          success: (function(_this) {
            return function() {
              return console.log("+");
            };
          })(this),
          error: function() {
            return console.log("-");
          }
        });
      },
      toggleKickfinFilteringBlanks: function(e) {
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: {
            restaurant: {
              kickfin_filtering_blanks_enabled: e.currentTarget.checked
            }
          },
          success: (function(_this) {
            return function() {
              return console.log("+");
            };
          })(this),
          error: function() {
            return console.log("-");
          }
        });
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("reports")) {
        return Tipmetric.Controllers.reportsView = new ReportsView();
      }
    });
  })();

}).call(this);
(function() {
  var extend = function(child, parent) { for (var key in parent) { if (hasProp.call(parent, key)) child[key] = parent[key]; } function ctor() { this.constructor = child; } ctor.prototype = parent.prototype; child.prototype = new ctor(); child.__super__ = parent.prototype; return child; },
    hasProp = {}.hasOwnProperty;

  (function() {
    this.Tipmetric.module("Setup", function(Setup, App, Backbone, Marionette, $, _) {
      return Setup.MainView = (function(superClass) {
        extend(MainView, superClass);

        function MainView() {
          return MainView.__super__.constructor.apply(this, arguments);
        }

        MainView.prototype.el = "#setup-view";

        MainView.prototype.events = {
          "click #change-restaurant-name": "changeRestaurantName",
          "click #save-restaurant-name": "saveRestaurantName",
          "click #cancel-restaurant-name": "cancelRestaurantName",
          "click [data-asp-action='asp-add']": "aspAdd",
          "click [data-asp-action='asp-create']": "aspCreate",
          "click [data-asp-action='asp-cancel-creation']": "aspCancelCreation",
          "click [data-asp-action='asp-cancel']": "aspCancel",
          "click [data-asp-action='asp-update']": "aspUpdate",
          "click [data-asp-action='asp-edit']": "aspEdit",
          "click [data-asp-action='asp-remove']": "aspRemove",
          "click [data-asp-action='asp-reactivate']": "aspReactivate",
          "click [data-action='save-source-labels']": "saveSourceLabels",
          "click [data-action='save-restricted-access-message']": "saveCustomRestrictionMessage",
          "ifChanged [data-action-type='ps-action']": "psToggle",
          "ifChanged [data-action-type='as-action']": "asToggle",
          "ifChanged #calc_auto_prefill_percentage": "calcAutoPrefillPercentage",
          "ifChanged #calc_auto_prefill_sto": "calcAutoPrefillSto",
          "ifChanged #ag_svc_in_history_and_dashboard_is_shown": "agSvcInHistoryAndDashboardIsShown",
          "ifChanged #fee_in_history_and_dashboard_is_shown": "feeInHistoryAndDashboardIsShown",
          "ifChanged #ag_svc_fee_subsctracting_enabled": "agSvcFeeSubsctractingEnabled",
          "ifChanged #cc_fee_subsctracting_enabled": "ccSvcFeeSubsctractingEnabled",
          "ifChanged #food_tip_outs_enabled": "foodTipOutsEnabled",
          "ifChanged #adp_reports_enabled": "adpReportsEnabled",
          "ifChanged #adp_reports_extended": "adpReportsExtended",
          "ifChanged #paycom_reports_enabled": "paycomReportsEnabled",
          "ifChanged #paycom_extended_reports_enabled": "paycomExtendedReportsEnabled",
          "ifChanged #paychex_reports_enabled": "paychexReportsEnabled",
          "ifChanged #paylocity_reports_enabled": "paylocityReportsEnabled",
          "ifChanged #fee_reports_enabled": "feeReportsEnabled",
          "ifChanged #kickfin_report_enabled": "kickfinReportEnabled",
          "ifChanged #csv_import_enabled": "csvImportEnabled",
          "ifChanged #csv_multidate_import_enabled": "csvMultidateImportEnabled",
          "ifChanged #allow_unauth_submit": "allowUnauthSubmit",
          "ifChanged #history_daily_reports_enabled": "historyDailyReportsEnabled",
          "ifChanged #accept_approval_enabled": "acceptApprovalEnabled",
          "ifChanged #final_tip_out_column_enabled": "finalTipOutColumnEnabled",
          "ifChanged #fto_cc_tips_enabled": "ftoCcTipsEnabled",
          "ifChanged #scope_reports_by_employee": "scopeReportsByEmployee",
          "ifChanged #access_restricted_by_admin": "accessRestrictedByAdmin",
          "ifChanged #notes_enabled": "notesEnabled",
          "ifChanged #transfer_enabled": "transferEnabled",
          "ifChanged #transfer_employees_enabled": "transferEmployeesEnabled",
          "ifChanged #hourly_rate_enabled": "hourlyRateEnabled",
          "ifChanged #sto_condition_rules_enabled": "stoConditionRules",
          "ifChanged #csv_tip_field_autoselection_enabled": "csvTipFieldAutoselectionEnabled",
          "ifChanged #transfer_hours_format_enabled": "transferHoursFormatEnabled",
          "ifChanged #paychex_integration_enabled": "paychexIntegrationEnabled",
          "ifChanged #square_integration_enabled": "squareIntegrationEnabled",
          "ifChanged #brink_integration_enabled": "brinkIntegrationEnabled",
          "ifChanged #focus_integration_enabled": "focusIntegrationEnabled",
          "ifChanged #paycom_integration_enabled": "paycomIntegrationEnabled",
          "ifChanged #cliq_reports_enabled": "cliqIntegrationEnabled",
          "ifChanged #hidden_from_enabled": "hiddenFromEnabled",
          "ifChanged #no_fee_calc_enabled": "noFeeCalcEnabled",
          "ifChanged #tip_out_report_enabled": "tipOutReportEnabled",
          "ifChanged #isolved_report_enabled": "isolvedReportEnabled",
          "ifChanged #seven_shift_integration_enabled": "sevenShiftIntegrationEnabled",
          "ifChanged #tips_today_enabled": "tipsTodayEnabled",
          "ifChanged #cc_tips_input_enabled": "ccTipsInputEnabled",
          "ifChanged #cash_tips_input_enabled": "cashTipsInputEnabled",
          "ifChanged #hours_input_enabled": "hoursInputEnabled",
          "ifChanged #overtips_enabled": "overtipsEnabled",
          "ifChanged #toast_payroll_enabled": "toastPayrollEnabled",
          "ifChanged #toast_integration_enabled": "toastIntegrationEnabled",
          "ifChanged #daily_report_new_version_enabled": "dailyReportNewVersionEnabled",
          "ifChanged #custom_cash_tips_distr_enabled": "customFinalCashStoLogic",
          "ifChanged #sto_edit_button_enabled": "stoEditButtonEnabled",
          "ifChanged #csv_position_auto_selection_enabled": "csvPositionAutoSelectionEnabled",
          "ifChanged #yearly_reports_enabled": "yearlyReportsEnabled",
          "ifChanged #seven_shift_position_auto_selection_enabled": "sevenShiftPositionAutoSelectionEnabled",
          "ifChanged #managers_edit_for_employees_enabled": "managersEditForEmployeesEnabled",
          "ifChanged #custom_report_templates_enabled": "customReportTemplatesEnabled",
          "ifChanged #custom_positions_ordering_enabled": "customPositionsOrderingEnabled",
          "ifChanged #submit_fields input": "updateRestaurantField",
          "ifChanged .plan-checkbox": "updateAvailableBillingPlans",
          "change #restaurant_distribution_logic_type": "restaurantDistributionLogicType",
          "change #restaurant_hours_label": "restaurantHoursLabelChanged"
        };

        MainView.prototype.initialize = function() {
          return $(document).ready((function(_this) {
            return function() {
              _this.disableForPayroll();
              return _this.setDate();
            };
          })(this));
        };

        MainView.prototype.saveSourceLabels = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                cc_source_label: $("#source_labels #cc_source_label").val(),
                cash_source_label: $("#source_labels #cash_source_label").val(),
                fee_source_label: $("#source_labels #fee_source_label").val(),
                ag_svc_source_label: $("#source_labels #ag_svc_source_label").val(),
                food_sales_source_label: $("#source_labels #food_sales_source_label").val(),
                beverage_sales_source_label: $("#source_labels #beverage_sales_source_label").val(),
                food_and_beverage_sales_summ_source_label: $("#source_labels #food_and_beverage_sales_summ_source_label").val(),
                sales_cc_source_label: $("#source_labels #sales_cc_source_label").val(),
                sales_cash_source_label: $("#source_labels #sales_cash_source_label").val(),
                hours_source_label: $("#source_labels #hours_source_label").val(),
                cc_table_label: $("#table_labels #cc_table_label").val(),
                cash_table_label: $("#table_labels #cash_table_label").val(),
                fee_table_label: $("#table_labels #fee_table_label").val(),
                ag_svc_table_label: $("#table_labels #ag_svc_table_label").val(),
                food_sales_table_label: $("#table_labels #food_sales_table_label").val(),
                beverage_sales_table_label: $("#table_labels #beverage_sales_table_label").val(),
                food_and_beverage_sales_summ_table_label: $("#table_labels #food_and_beverage_sales_summ_table_label").val(),
                sales_cc_table_label: $("#table_labels #sales_cc_table_label").val(),
                sales_cash_table_label: $("#table_labels #sales_cash_table_label").val(),
                hours_table_label: $("#table_labels #hours_table_label").val()
              }
            },
            success: function() {
              return toastr["success"]("Labels are changed.", "Success");
            },
            error: function(response) {
              return toastr["error"](response.responseText, "Error");
            }
          });
        };

        MainView.prototype.saveCustomRestrictionMessage = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                restricted_access_message: $("#restricted_access_message").val()
              }
            },
            success: function() {
              return toastr["success"]("Restriction message updated.", "Success");
            },
            error: function(response) {
              return toastr["error"](response.responseText, "Error");
            }
          });
        };

        MainView.prototype.setDate = function() {
          return $('#fto_date').datepicker({
            todayHighlight: true
          }).on("changeDate", (function(_this) {
            return function() {
              return _this.dateUpdated();
            };
          })(this));
        };

        MainView.prototype.dateUpdated = function() {
          if ($("#fto_date").attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                fto_date: moment($("#fto_date").datepicker("getDate")).format("YYYY-MM-DD")
              }
            },
            success: function(response) {
              return toastr["success"]("FTO date changed.", "Success");
            },
            error: function(response) {
              return toastr["error"](response.responseText, "Error");
            }
          });
        };

        MainView.prototype.disableForPayroll = function() {
          $("body[data-role='payroll']").find(".checkboxes-wrapper , #area-type-add, #shift-type-add, #position-type-add, #change-restaurant-name").attr("disabled", "disabled");
          return $("body[data-role='payroll'] [type='checkbox']").iCheck("disable");
        };

        MainView.prototype.acceptApprovalEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                accept_approval_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.stoEditButtonEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                sto_edit_button_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.toastIntegrationEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                toast_integration_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.toastPayrollEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                toast_payroll_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.dailyReportNewVersionEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                daily_report_new_version_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.yearlyReportsEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                yearly_reports_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.tipOutReportEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                tip_out_report_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.isolvedReportEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                isolved_report_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.tipsTodayEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                tips_today_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.ccTipsInputEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                cc_tips_input_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.cashTipsInputEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                cash_tips_input_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.hoursInputEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                hours_input_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.overtipsEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                overtips_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.csvPositionAutoSelectionEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                csv_position_auto_selection_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.sevenShiftPositionAutoSelectionEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                seven_shift_position_auto_selection_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.updateRestaurantField = function(e) {
          var data;
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          data = {
            restaurant: {}
          };
          data.restaurant[$(e.currentTarget).attr("id")] = e.currentTarget.checked;
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: data,
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.historyDailyReportsEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                history_daily_reports_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.stoConditionRules = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                sto_condition_rules_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.csvTipFieldAutoselectionEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                csv_tip_field_autoselection_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.notesEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                notes_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.transferEmployeesEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                transfer_employees_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.transferEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                transfer_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.hourlyRateEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                hourly_rate_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.squareIntegrationEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                square_integration_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.paychexIntegrationEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                paychex_integration_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.brinkIntegrationEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                brink_integration_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.focusIntegrationEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                focus_integration_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.paycomIntegrationEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                paycom_integration_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.cliqIntegrationEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                cliq_reports_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.hiddenFromEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                hidden_from_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.noFeeCalcEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                no_fee_calc_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.sevenShiftIntegrationEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                seven_shift_integration_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.customFinalCashStoLogic = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                custom_cash_tips_distr_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.transferHoursFormatEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                transfer_hours_format_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.accessRestrictedByAdmin = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                access_restricted_by_admin: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return toastr["success"]("User access changed.", "Success");
              };
            })(this),
            error: function(response) {
              return toastr["error"](response.responseText, "Error");
            }
          });
        };

        MainView.prototype.ftoCcTipsEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                fto_cc_tips_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.finalTipOutColumnEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                final_tip_out_column_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.scopeReportsByEmployee = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                scope_reports_by_employee: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.foodTipOutsEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                food_tip_outs_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.restaurantHoursLabelChanged = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                hours_label: $(e.currentTarget).val()
              }
            },
            success: function(response) {
              return toastr["success"]("Hours label updated.", "Success");
            },
            error: function(response) {
              return toastr["error"](response.responseText, "Error");
            }
          });
        };

        MainView.prototype.restaurantDistributionLogicType = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                distribution_logic_type: $(e.currentTarget).val()
              }
            },
            success: function(response) {
              return toastr["success"]("Distribution logic updated.", "Success");
            },
            error: function(response) {
              return toastr["error"](response.responseText, "Error");
            }
          });
        };

        MainView.prototype.allowUnauthSubmit = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                allow_unauth_submit: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.csvImportEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                csv_import_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.csvMultidateImportEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                csv_multidate_import_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.adpReportsEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                adp_reports_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.adpReportsExtended = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                adp_reports_extended: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.paycomReportsEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                paycom_reports_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.paycomExtendedReportsEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                paycom_extended_reports_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.paychexReportsEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                paychex_reports_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.paylocityReportsEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                paylocity_reports_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.feeReportsEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                fee_reports_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.kickfinReportEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                kickfin_report_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.ccSvcFeeSubsctractingEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                cc_fee_subsctracting_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.agSvcFeeSubsctractingEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                ag_svc_fee_subsctracting_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.feeInHistoryAndDashboardIsShown = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                fee_in_history_and_dashboard_is_shown: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.agSvcInHistoryAndDashboardIsShown = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                ag_svc_in_history_and_dashboard_is_shown: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.calcAutoPrefillPercentage = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                calc_auto_prefill_percentage: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.customPositionsOrderingEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                custom_positions_ordering_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function(response) {
                $("#setup-view").attr("data-position-ordering", response.restaurant.custom_positions_ordering_enabled);
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.customReportTemplatesEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                custom_report_templates_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.managersEditForEmployeesEnabled = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                managers_edit_for_employees_enabled: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.calcAutoPrefillSto = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                calc_auto_prefill_sto: e.currentTarget.checked
              }
            },
            success: (function(_this) {
              return function() {
                return console.log("+");
              };
            })(this),
            error: function() {
              return console.log("-");
            }
          });
        };

        MainView.prototype.saveRestaurantName = function() {
          var restaurantName;
          restaurantName = $("#restaurant-name").val();
          if (restaurantName.length <= 0) {
            return toastr["info"]("Please fill in the restaurant name.", "Can not be blank");
          } else {
            $.ajax({
              url: "api/restaurants/1",
              method: "put",
              id: "1",
              data: {
                restaurant: {
                  name: restaurantName
                }
              },
              success: (function(_this) {
                return function() {
                  return toastr["success"]("Restaurant name successfully updated", "Success");
                };
              })(this),
              error: (function(_this) {
                return function(response) {
                  $("#restaurant-name").restoreData();
                  return toastr["error"](response.responseText, "Error");
                };
              })(this)
            });
            $("#restaurant-name").editAndFocus(false);
            return $("#save-restaurant-name, #change-restaurant-name, #cancel-restaurant-name").toggleClass("hidden");
          }
        };

        MainView.prototype.changeRestaurantName = function() {
          $("#restaurant-name").saveData();
          $("#restaurant-name").editAndFocus(true);
          return $("#save-restaurant-name, #change-restaurant-name, #cancel-restaurant-name").toggleClass("hidden");
        };

        MainView.prototype.cancelRestaurantName = function() {
          $("#restaurant-name").restoreData();
          $("#restaurant-name").editAndFocus(false);
          return $("#save-restaurant-name, #change-restaurant-name, #cancel-restaurant-name").toggleClass("hidden");
        };

        MainView.prototype.aspAdd = function(e) {
          var areaTypesTable, bookmark, sequenceHtml, totalAreaTypeCount, tr;
          this.type = $(e.currentTarget).attr("data-asp-type");
          tr = $(e.currentTarget).closest("tr");
          if (this.type === "area-type") {
            bookmark = "<i class='fa fa-bookmark' style='color: #f3f3f3;'></i>";
          } else {
            bookmark = "";
          }
          if (this.type === "position-type") {
            sequenceHtml = " <input class='position-type-sequence form-control' value='0' placeholder='Position sequence' data-original-value='0' contenteditable='true'>";
          } else {
            sequenceHtml = "";
          }
          areaTypesTable = $("#" + this.type + "s-table");
          totalAreaTypeCount = areaTypesTable.find("tr").length;
          areaTypesTable.find("tbody").append("<tr class='new-asp'> <td>" + bookmark + ((totalAreaTypeCount + 1) + ") </td> <td class='name-td'> <input class='" + this.type + "-name form-control' disabled='true' placeholder='" + (this.type.replace('-type', '')) + " name'>") + sequenceHtml + ("</td> <td class='actions'> <span id='" + this.type + "-update' data-asp-action='asp-update' data-asp-type='" + this.type + "' class='save hidden btn btn-success btn' data-" + this.type + "-id=''><i class='fa fa-floppy-o'></i></span> <span id='" + this.type + "-cancel' data-asp-action='asp-cancel' data-asp-type='" + this.type + "' class='save hidden btn btn-default btn' data-" + this.type + "-id=''><i class='fa fa-ban'></i></span> <button class='btn btn-info btn-xs' type='button' id='" + this.type + "-edit' data-asp-action='asp-edit' data-asp-type='" + this.type + "' class='edit' data-" + this.type + "-id=''><i class='fa fa-edit'></i></button> <button class='btn btn-danger btn-xs' type='button' id='" + this.type + "-remove' data-asp-action='asp-remove' data-asp-type='" + this.type + "' class='remove' data-" + this.type + "-id='' class='btn btn-danger btn'><i class='fa fa-remove'></i></button> <button id='" + this.type + "-create' data-asp-action='asp-create' data-asp-type='" + this.type + "' class='btn btn-success' type='button'><i class='fa fa-floppy-o'></i></button> <button id='" + this.type + "-cancel-creation' data-asp-action='asp-cancel-creation' data-asp-type='" + this.type + "' class='btn btn-default' type='button'><i class='fa fa-ban'></i></button> </td> </tr>"));
          areaTypesTable.find(".new-asp ." + this.type + "-name").editAndFocus(true);
          $("#" + this.type + "-add").toggleClass("hidden");
          return tr.find("#" + this.type + "-create, #" + this.type + "-cancel-creation").toggleClass("hidden");
        };

        MainView.prototype.aspCancelCreation = function(e) {
          var areaTypesTable, tr;
          this.type = $(e.currentTarget).attr("data-asp-type");
          areaTypesTable = $("#" + this.type + "s-table");
          areaTypesTable.find(".new-asp").remove();
          $("#" + this.type + "-add").toggleClass("hidden");
          tr = $(e.currentTarget).closest("tr");
          return tr.find("#" + this.type + "-add, #" + this.type + "-create, #" + this.type + "-cancel-creation").toggleClass("hidden");
        };

        MainView.prototype.aspCancel = function(e) {
          var tr;
          this.type = $(e.currentTarget).attr("data-asp-type");
          tr = $(e.currentTarget).closest("tr");
          tr.find("#" + this.type + "-edit, #" + this.type + "-update, #" + this.type + "-remove, #" + this.type + "-cancel").toggleClass("hidden");
          return tr.find("." + this.type + "-name").restoreData().editAndFocus(false);
        };

        MainView.prototype.aspCreate = function(e) {
          var areaTypesTable, obj, sequence, tr;
          this.type = $(e.currentTarget).attr("data-asp-type");
          tr = $(e.currentTarget).closest("tr");
          areaTypesTable = $("#" + this.type + "s-table");
          areaTypesTable.find(".new-asp");
          Tipmetric.Controllers.spinner.show();
          if (this.type === 'position-type') {
            sequence = areaTypesTable.find(".new-asp ." + this.type + "-sequence").val().toLowerCase();
          } else {
            sequence = "";
          }
          return $.ajax({
            url: "api/" + (this.urlByType(this.type)) + "s",
            method: "post",
            data: (
              obj = {},
              obj["" + (this.urlByType(this.type))] = {
                name: areaTypesTable.find(".new-asp ." + this.type + "-name").val().toLowerCase(),
                sequence: sequence
              },
              obj
            ),
            success: (function(_this) {
              return function(response) {
                var allPositionLines, allShiftLines, areaTable, areaTableSchedule, areaTableWorkload, areaType, areaTypesActive, blockHtml, color, currentLine, html, i, id, index, j, k, lastPositionIndex, lastShiftIndex, len, len1, len2, len3, len4, m, n, positionAreaShifts, positionAreaShiftsForArea, positionIndex, positionName, positionType, positionsForShiftAreaData, previousPositionId, previousShiftId, ref, ref1, ref2, scheduleLabelHtml, schedule_shift_day_tr_html, shiftAreaDaysData, shiftDataForArea, shiftIndex, shiftType, shiftsTypesActive, workloadLabelHtml, workload_position_shift_td_html, workload_position_shift_th_html;
                id = response[_this.urlByType(_this.type)]["id"];
                color = response[_this.urlByType(_this.type)]["chart_color"];
                tr.find(".sequence i").css({
                  color: color
                });
                tr.removeAttr("class");
                tr.attr("data-" + _this.type + "-id", id);
                tr.find("[data-" + _this.type + "-id]").attr("data-" + _this.type + "-id", id);
                tr.find("." + _this.type + "-name").editAndFocus(false);
                tr.find("." + _this.type + "-sequence").editAndFocus(false);
                tr.find("#" + _this.type + "-create, #" + _this.type + "-cancel-creation").toggleClass("hidden");
                tr.attr("data-asp-type", _this.type);
                tr.attr("data-asp-status", "active");
                $("#" + _this.type + "-add").toggleClass("hidden");
                if (_this.type === "position-type") {
                  gon.restaurant.area_shifts = response.area_shifts;
                  gon.restaurant.restaurant_area_types = response.restaurant_area_types;
                  gon.restaurant.restaurant_shift_types = response.restaurant_shift_types;
                  gon.restaurant.restaurant_position_types = response.restaurant_position_types;
                  positionName = _.filter(gon.restaurant.restaurant_position_types, function(pt) {
                    return pt._id.$oid === id;
                  })[0].name;
                  allPositionLines = $("#position-types-table tbody tr:not([data-position-type-id='" + id + "'])");
                  lastPositionIndex = allPositionLines.last().attr("position-index");
                  currentLine = $(e.currentTarget).closest("tr");
                  if (lastPositionIndex) {
                    positionIndex = parseInt(lastPositionIndex) + 1;
                  } else {
                    positionIndex = 0;
                  }
                  $(currentLine).attr("position-index", positionIndex);
                  index = positionIndex - 1;
                  previousPositionId = void 0;
                  while (previousPositionId === void 0 && index >= 0) {
                    if ($(allPositionLines[index]).attr("data-asp-status") === "active") {
                      previousPositionId = $(allPositionLines[index]).attr("data-position-type-id");
                    } else {
                      index = index - 1;
                    }
                  }
                  positionAreaShifts = _.filter(gon.restaurant.area_shifts, function(as) {
                    return _.contains(_.collect(as.position_type_ids, function(pt) {
                      return pt['$oid'];
                    }), id);
                  });
                  shiftsTypesActive = _.filter(gon.restaurant.restaurant_shift_types, function(st) {
                    return st.active === true;
                  });
                  areaTypesActive = _.filter(gon.restaurant.restaurant_area_types, function(st) {
                    return st.active === true;
                  });
                  for (i = 0, len = areaTypesActive.length; i < len; i++) {
                    areaType = areaTypesActive[i];
                    areaTable = $("#workload-" + areaType._id.$oid);
                    positionAreaShiftsForArea = _.filter(positionAreaShifts, function(pas) {
                      return pas.area_type_id['$oid'] === areaType._id.$oid;
                    });
                    html = JST["setup/position_shift_tr"]({
                      positionAreaShiftsForArea: positionAreaShiftsForArea,
                      positionName: positionName,
                      areaType: areaType,
                      shiftTypes: shiftsTypesActive,
                      id: id
                    });
                    if (index === -1) {
                      $("#workload-" + areaType._id.$oid + " tbody").prepend(html);
                    } else {
                      $("#workload-" + areaType._id.$oid + " tbody .position-shift-tr[data-position-type-id='" + previousPositionId + "']").after(html);
                    }
                    setTimeout(function() {
                      return window.bindCheckboxesScoped($(".position-shift-tr[data-position-type-id='" + id + "']"));
                    }, 100);
                  }
                } else if (_this.type === "shift-type") {
                  gon.restaurant.area_shifts = response.area_shifts;
                  gon.restaurant.restaurant_area_types = response.restaurant_area_types;
                  gon.restaurant.restaurant_shift_types = response.restaurant_shift_types;
                  gon.restaurant.restaurant_position_types = response.restaurant_position_types;
                  shiftType = _.filter(gon.restaurant.restaurant_shift_types, function(st) {
                    return st._id.$oid === id;
                  })[0];
                  allShiftLines = $("#shift-types-table tbody tr:not([data-shift-type-id='" + id + "']");
                  lastShiftIndex = allShiftLines.last().attr("shift-index");
                  shiftIndex = parseInt(lastShiftIndex) + 1;
                  currentLine = $(e.currentTarget).closest("tr");
                  if (!shiftIndex) {
                    shiftIndex = 0;
                  }
                  $(currentLine).attr("shift-index", shiftIndex);
                  index = shiftIndex - 1;
                  previousShiftId = void 0;
                  while (previousShiftId === void 0 && index >= 0) {
                    if ($(allShiftLines[index]).attr("data-asp-status") === "active") {
                      previousShiftId = $(allShiftLines[index]).attr("data-shift-type-id");
                    } else {
                      index = index - 1;
                    }
                  }
                  shiftAreaDaysData = _.filter(gon.restaurant.area_shifts, function(as) {
                    return as.shift_type_id.$oid === id;
                  });
                  areaTypesActive = _.filter(gon.restaurant.restaurant_area_types, function(st) {
                    return st.active === true;
                  });
                  for (j = 0, len1 = areaTypesActive.length; j < len1; j++) {
                    areaType = areaTypesActive[j];
                    areaTable = $("#schedule-" + areaType._id.$oid);
                    shiftDataForArea = _.find(shiftAreaDaysData, function(f) {
                      return f.area_type_id.$oid === areaType._id.$oid;
                    });
                    schedule_shift_day_tr_html = JST["setup/shift_day_tr"]({
                      areaType: areaType,
                      shiftType: shiftType,
                      shiftDataForArea: shiftDataForArea,
                      id: id
                    });
                    if (index === -1) {
                      $("#schedule-" + areaType._id.$oid + " tbody").prepend(schedule_shift_day_tr_html);
                    } else {
                      $("#schedule-" + areaType._id.$oid + " tbody .shift-row[data-shift-type-id='" + previousShiftId + "']").after(schedule_shift_day_tr_html);
                    }
                    workload_position_shift_th_html = JST["setup/position_shift_th"]({
                      shiftType: shiftType
                    });
                    if (index === -1) {
                      $("#workload-" + areaType._id.$oid + " thead tr th:first-child").after(workload_position_shift_th_html);
                    } else {
                      $("#workload-" + areaType._id.$oid + " thead tr .position-shift-th[data-shift-type-id='" + previousShiftId + "']").after(workload_position_shift_th_html);
                    }
                    positionsForShiftAreaData = _.collect(shiftDataForArea.position_type_ids, function(p) {
                      return p.$oid;
                    });
                    ref = gon.restaurant.restaurant_position_types;
                    for (k = 0, len2 = ref.length; k < len2; k++) {
                      positionType = ref[k];
                      workload_position_shift_td_html = JST["setup/position_shift_td"]({
                        areaType: areaType,
                        shiftType: shiftType,
                        shiftDataForArea: shiftDataForArea,
                        positionType: positionType,
                        positionsForShiftAreaData: positionsForShiftAreaData
                      });
                      if (index === -1) {
                        $("#workload-" + areaType._id.$oid + " tbody tr[data-position-type-id='" + positionType._id.$oid + "'] td:first-child").after(workload_position_shift_td_html);
                      } else {
                        $("#workload-" + areaType._id.$oid + " tbody tr[data-position-type-id='" + positionType._id.$oid + "'] .position-shift-td[data-shift-type-id='" + previousShiftId + "']").after(workload_position_shift_td_html);
                      }
                    }
                  }
                  setTimeout(function() {
                    window.bindCheckboxesScoped($(".shift-row input[data-as-shift-type-id='" + id + "']"));
                    return window.bindCheckboxesScoped($(".area-workload-table-wrapper tbody tr .position-shift-td[data-shift-type-id='" + id + "']"));
                  }, 100);
                } else if (_this.type === "area-type") {
                  gon.restaurant.area_shifts = response.area_shifts;
                  gon.restaurant.restaurant_area_types = response.restaurant_area_types;
                  gon.restaurant.restaurant_shift_types = response.restaurant_shift_types;
                  gon.restaurant.restaurant_position_types = response.restaurant_position_types;
                  areaType = _.filter(gon.restaurant.restaurant_area_types, function(st) {
                    return st._id.$oid === id;
                  })[0];
                  scheduleLabelHtml = "<li class=''><a data-toggle='tab' href='#schedule-" + areaType._id.$oid + "'>" + areaType.name + "</a></li>";
                  $("#schedule-wrapper ul.nav.nav-tabs").append(scheduleLabelHtml);
                  blockHtml = JST["setup/area_block"]({
                    id: id,
                    areaType: areaType
                  });
                  $("#shedule-areas-list-wrapper").append(blockHtml);
                  areaTableSchedule = $("#schedule-" + areaType._id.$oid);
                  ref1 = gon.restaurant.restaurant_shift_types = response.restaurant_shift_types;
                  for (m = 0, len3 = ref1.length; m < len3; m++) {
                    shiftType = ref1[m];
                    schedule_shift_day_tr_html = JST["setup/shift_day_tr"]({
                      areaType: areaType,
                      shiftType: shiftType,
                      shiftDataForArea: [],
                      id: id
                    });
                    $("#schedule-" + areaType._id.$oid + " tbody").append(schedule_shift_day_tr_html);
                  }
                  workloadLabelHtml = "<li class=''><a data-toggle='tab' href='#workload-" + areaType._id.$oid + "'>" + areaType.name + "</a></li>";
                  $("#workload-wrapper ul.nav.nav-tabs").append(workloadLabelHtml);
                  blockHtml = JST["setup/workload_area_block"]({
                    id: id,
                    areaType: areaType,
                    shiftTypes: gon.restaurant.restaurant_shift_types
                  });
                  $("#workload-areas-list-wrapper").append(blockHtml);
                  areaTableWorkload = $("#workload-" + areaType._id.$oid);
                  ref2 = gon.restaurant.restaurant_position_types;
                  for (n = 0, len4 = ref2.length; n < len4; n++) {
                    positionType = ref2[n];
                    html = JST["setup/position_shift_tr"]({
                      positionAreaShiftsForArea: [],
                      positionName: positionType.name,
                      areaType: areaType,
                      shiftTypes: gon.restaurant.restaurant_shift_types,
                      id: positionType._id.$oid
                    });
                    $("#workload-" + areaType._id.$oid + " tbody").append(html);
                  }
                  if (gon.restaurant.restaurant_area_types.length === 1) {
                    $("#schedule-wrapper .nav.nav-tabs li:first-child a").trigger("click");
                    $("#workload-wrapper .nav.nav-tabs li:first-child a").trigger("click");
                  }
                  setTimeout(function() {
                    window.bindCheckboxesScoped(areaTableSchedule.find(".shift-row input[type='checkbox']"));
                    return window.bindCheckboxesScoped(areaTableWorkload.find(".position-shift-tr input[type='checkbox']"));
                  }, 100);
                }
                Tipmetric.Controllers.spinner.hide();
                return toastr["success"]((_this.modelNameByType(_this.type)) + " successfully added", "Success");
              };
            })(this),
            error: function(response) {
              Tipmetric.Controllers.spinner.hide();
              return toastr["error"](response.responseText, "Error");
            }
          });
        };

        MainView.prototype.aspRemove = function(e) {
          var id;
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          this.type = $(e.currentTarget).attr("data-asp-type");
          id = $(e.currentTarget).attr("data-" + this.type + "-id");
          Tipmetric.Controllers.spinner.show();
          return $.ajax({
            url: "api/" + (this.urlByType(this.type)) + "s/" + id,
            method: "delete",
            success: (function(_this) {
              return function(response) {
                if (response.persisted === true) {
                  toastr["success"]((_this.modelNameByType(_this.type)) + " is successfully deactivated", "Success");
                  $("tr[data-" + _this.type + "-id=" + id + "]").attr("data-asp-status", "deactivated");
                } else {
                  toastr["success"]((_this.modelNameByType(_this.type)) + " is successfully removed", "Success");
                  $("tr[data-" + _this.type + "-id=" + id + "]").remove();
                }
                if (_this.type === "position-type") {
                  $("tr.position-shift-tr[data-position-type-id='" + id + "']").remove();
                } else if (_this.type === "shift-type") {
                  $("#schedule-wrapper tr.shift-row[data-shift-type-id='" + id + "']").remove();
                  $(".area-workload-table-wrapper").find("[class*='position-shift-t'][data-shift-type-id='" + id + "']").remove();
                } else if (_this.type === "area-type") {
                  $("#schedule-wrapper a[href='#schedule-" + id + "']").closest("li").remove();
                  $("#schedule-wrapper #shedule-areas-list-wrapper #schedule-" + id).remove();
                  $("#workload-wrapper a[href='#workload-" + id + "']").closest("li").remove();
                  $("#workload-wrapper #workload-areas-list-wrapper #workload-" + id).remove();
                  if ($("#schedule-wrapper .nav.nav-tabs li.active").length === 0) {
                    $("#schedule-wrapper .nav.nav-tabs li:first-child a").trigger("click");
                  }
                  if ($("#workload-wrapper .nav.nav-tabs li.active").length === 0) {
                    $("#workload-wrapper .nav.nav-tabs li:first-child a").trigger("click");
                  }
                }
                return Tipmetric.Controllers.spinner.hide();
              };
            })(this),
            error: function(response) {
              toastr["error"](response.responseText, "Error");
              return Tipmetric.Controllers.spinner.hide();
            }
          });
        };

        MainView.prototype.aspReactivate = function(e) {
          var id;
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          this.type = $(e.currentTarget).attr("data-asp-type");
          id = $(e.currentTarget).attr("data-" + this.type + "-id");
          return $.ajax({
            url: "api/" + (this.urlByType(this.type)) + "s/" + id + "/reactivate",
            method: "patch",
            success: (function(_this) {
              return function(response) {
                var allAreaLines, allPositionLines, allShiftLines, areaIndex, areaShiftDataForArea, areaShiftsForArea, areaTable, areaTableSchedule, areaTableWorkload, areaType, areaTypesActive, blockHtml, currentLine, html, i, index, j, k, len, len1, len2, len3, len4, m, n, positionAreaShifts, positionAreaShiftsForArea, positionIndex, positionName, positionType, positionsForShiftAreaData, previousAreaId, previousPositionId, previousShiftId, ref, ref1, ref2, results, scheduleLabelHtml, schedule_shift_day_tr_html, shiftAreaDaysData, shiftDataForArea, shiftIndex, shiftType, shiftsTypesActive, workloadLabelHtml, workload_position_shift_td_html, workload_position_shift_th_html;
                toastr["success"]((_this.modelNameByType(_this.type)) + " is successfully activated", "Success");
                $("tr[data-" + _this.type + "-id=" + id + "]").attr("data-asp-status", "active");
                if (_this.type === "position-type") {
                  gon.restaurant.area_shifts = response.area_shifts;
                  gon.restaurant.restaurant_area_types = response.restaurant_area_types;
                  gon.restaurant.restaurant_shift_types = response.restaurant_shift_types;
                  positionName = _.filter(gon.restaurant.restaurant_position_types, function(pt) {
                    return pt._id.$oid === id;
                  })[0].name;
                  allPositionLines = $("#position-types-table tbody tr");
                  currentLine = $(e.currentTarget).closest("tr");
                  positionIndex = parseInt($(currentLine).attr("position-index"));
                  index = positionIndex - 1;
                  previousPositionId = void 0;
                  while (previousPositionId === void 0 && index >= 0) {
                    if ($(allPositionLines[index]).attr("data-asp-status") === "active") {
                      previousPositionId = $(allPositionLines[index]).attr("data-position-type-id");
                    } else {
                      index = index - 1;
                    }
                  }
                  positionAreaShifts = _.filter(gon.restaurant.area_shifts, function(as) {
                    return _.contains(_.collect(as.position_type_ids, function(pt) {
                      return pt['$oid'];
                    }), id);
                  });
                  shiftsTypesActive = _.filter(gon.restaurant.restaurant_shift_types, function(st) {
                    return st.active === true;
                  });
                  areaTypesActive = _.filter(gon.restaurant.restaurant_area_types, function(st) {
                    return st.active === true;
                  });
                  results = [];
                  for (i = 0, len = areaTypesActive.length; i < len; i++) {
                    areaType = areaTypesActive[i];
                    areaTable = $("#workload-" + areaType._id.$oid);
                    positionAreaShiftsForArea = _.filter(positionAreaShifts, function(pas) {
                      return pas.area_type_id['$oid'] === areaType._id.$oid;
                    });
                    html = JST["setup/position_shift_tr"]({
                      positionAreaShiftsForArea: positionAreaShiftsForArea,
                      positionName: positionName,
                      areaType: areaType,
                      shiftTypes: shiftsTypesActive,
                      id: id
                    });
                    if (index === -1) {
                      $("#workload-" + areaType._id.$oid + " tbody").prepend(html);
                    } else {
                      $("#workload-" + areaType._id.$oid + " tbody .position-shift-tr[data-position-type-id='" + previousPositionId + "']").after(html);
                    }
                    results.push(setTimeout(function() {
                      return window.bindCheckboxesScoped($(".position-shift-tr[data-position-type-id='" + id + "']"));
                    }, 100));
                  }
                  return results;
                } else if (_this.type === "shift-type") {
                  gon.restaurant.area_shifts = response.area_shifts;
                  gon.restaurant.restaurant_area_types = response.restaurant_area_types;
                  gon.restaurant.restaurant_shift_types = response.restaurant_shift_types;
                  gon.restaurant.restaurant_position_types = response.restaurant_position_types;
                  shiftType = _.filter(gon.restaurant.restaurant_shift_types, function(st) {
                    return st._id.$oid === id;
                  })[0];
                  allShiftLines = $("#shift-types-table tbody tr");
                  currentLine = $(e.currentTarget).closest("tr");
                  shiftIndex = parseInt($(currentLine).attr("shift-index"));
                  index = shiftIndex - 1;
                  previousShiftId = void 0;
                  while (previousShiftId === void 0 && index >= 0) {
                    if ($(allShiftLines[index]).attr("data-asp-status") === "active") {
                      previousShiftId = $(allShiftLines[index]).attr("data-shift-type-id");
                    } else {
                      index = index - 1;
                    }
                  }
                  shiftAreaDaysData = _.filter(gon.restaurant.area_shifts, function(as) {
                    return as.shift_type_id.$oid === id;
                  });
                  areaTypesActive = _.filter(gon.restaurant.restaurant_area_types, function(st) {
                    return st.active === true;
                  });
                  for (j = 0, len1 = areaTypesActive.length; j < len1; j++) {
                    areaType = areaTypesActive[j];
                    areaTable = $("#schedule-" + areaType._id.$oid);
                    shiftDataForArea = _.find(shiftAreaDaysData, function(f) {
                      return f.area_type_id.$oid === areaType._id.$oid;
                    });
                    schedule_shift_day_tr_html = JST["setup/shift_day_tr"]({
                      areaType: areaType,
                      shiftType: shiftType,
                      shiftDataForArea: shiftDataForArea,
                      id: id
                    });
                    if (index === -1) {
                      $("#schedule-" + areaType._id.$oid + " tbody").prepend(schedule_shift_day_tr_html);
                    } else {
                      $("#schedule-" + areaType._id.$oid + " tbody .shift-row[data-shift-type-id='" + previousShiftId + "']").after(schedule_shift_day_tr_html);
                    }
                    workload_position_shift_th_html = JST["setup/position_shift_th"]({
                      shiftType: shiftType
                    });
                    if (index === -1) {
                      $("#workload-" + areaType._id.$oid + " thead tr th:first-child").after(workload_position_shift_th_html);
                    } else {
                      $("#workload-" + areaType._id.$oid + " thead tr .position-shift-th[data-shift-type-id='" + previousShiftId + "']").after(workload_position_shift_th_html);
                    }
                    positionsForShiftAreaData = _.collect(shiftDataForArea.position_type_ids, function(p) {
                      return p.$oid;
                    });
                    ref = gon.restaurant.restaurant_position_types;
                    for (k = 0, len2 = ref.length; k < len2; k++) {
                      positionType = ref[k];
                      workload_position_shift_td_html = JST["setup/position_shift_td"]({
                        areaType: areaType,
                        shiftType: shiftType,
                        shiftDataForArea: shiftDataForArea,
                        positionType: positionType,
                        positionsForShiftAreaData: positionsForShiftAreaData
                      });
                      if (index === -1) {
                        $("#workload-" + areaType._id.$oid + " tbody tr[data-position-type-id='" + positionType._id.$oid + "'] td:first-child").after(workload_position_shift_td_html);
                      } else {
                        $("#workload-" + areaType._id.$oid + " tbody tr[data-position-type-id='" + positionType._id.$oid + "'] .position-shift-td[data-shift-type-id='" + previousShiftId + "']").after(workload_position_shift_td_html);
                      }
                    }
                  }
                  return setTimeout(function() {
                    window.bindCheckboxesScoped($(".shift-row input[data-as-shift-type-id='" + id + "']"));
                    return window.bindCheckboxesScoped($(".area-workload-table-wrapper tbody tr .position-shift-td[data-shift-type-id='" + id + "']"));
                  }, 100);
                } else if (_this.type === "area-type") {
                  gon.restaurant.area_shifts = response.area_shifts;
                  gon.restaurant.restaurant_area_types = response.restaurant_area_types;
                  gon.restaurant.restaurant_shift_types = response.restaurant_shift_types;
                  gon.restaurant.restaurant_position_types = response.restaurant_position_types;
                  areaType = _.filter(gon.restaurant.restaurant_area_types, function(st) {
                    return st._id.$oid === id;
                  })[0];
                  id = areaType._id.$oid;
                  allAreaLines = $("#area-types-table tbody tr");
                  currentLine = $(e.currentTarget).closest("tr");
                  areaIndex = parseInt($(currentLine).attr("area-index"));
                  index = areaIndex - 1;
                  previousAreaId = void 0;
                  while (previousAreaId === void 0 && index >= 0) {
                    if ($(allAreaLines[index]).attr("data-asp-status") === "active") {
                      previousAreaId = $(allAreaLines[index]).attr("data-area-type-id");
                    } else {
                      index = index - 1;
                    }
                  }
                  scheduleLabelHtml = "<li class=''><a data-toggle='tab' href='#schedule-" + areaType._id.$oid + "'>" + areaType.name + "</a></li>";
                  if (index === -1) {
                    $("#schedule-wrapper ul.nav.nav-tabs").prepend(scheduleLabelHtml);
                  } else {
                    $("#schedule-wrapper a[href='#schedule-" + previousAreaId + "']").closest("li").after(scheduleLabelHtml);
                  }
                  blockHtml = JST["setup/area_block"]({
                    id: id,
                    areaType: areaType
                  });
                  $("#shedule-areas-list-wrapper").append(blockHtml);
                  areaTableSchedule = $("#schedule-" + areaType._id.$oid);
                  areaShiftDataForArea = _.filter(gon.restaurant.area_shifts, function(f) {
                    return f.area_type_id.$oid === areaType._id.$oid;
                  });
                  ref1 = gon.restaurant.restaurant_shift_types = response.restaurant_shift_types;
                  for (m = 0, len3 = ref1.length; m < len3; m++) {
                    shiftType = ref1[m];
                    shiftDataForArea = _.find(areaShiftDataForArea, function(as) {
                      return as.shift_type_id.$oid === shiftType._id.$oid;
                    });
                    schedule_shift_day_tr_html = JST["setup/shift_day_tr"]({
                      areaType: areaType,
                      shiftType: shiftType,
                      shiftDataForArea: shiftDataForArea,
                      id: id
                    });
                    $("#schedule-" + areaType._id.$oid + " tbody").append(schedule_shift_day_tr_html);
                  }
                  workloadLabelHtml = "<li class=''><a data-toggle='tab' href='#workload-" + areaType._id.$oid + "'>" + areaType.name + "</a></li>";
                  if (index === -1) {
                    $("#workload-wrapper ul.nav.nav-tabs").prepend(workloadLabelHtml);
                  } else {
                    $("#workload-wrapper a[href='#workload-" + previousAreaId + "']").closest("li").after(workloadLabelHtml);
                  }
                  blockHtml = JST["setup/workload_area_block"]({
                    id: id,
                    areaType: areaType,
                    shiftTypes: gon.restaurant.restaurant_shift_types
                  });
                  $("#workload-areas-list-wrapper").append(blockHtml);
                  areaTableWorkload = $("#workload-" + areaType._id.$oid);
                  areaShiftsForArea = _.filter(gon.restaurant.area_shifts, function(pas) {
                    return pas.area_type_id['$oid'] === areaType._id.$oid;
                  });
                  ref2 = gon.restaurant.restaurant_position_types;
                  for (n = 0, len4 = ref2.length; n < len4; n++) {
                    positionType = ref2[n];
                    positionAreaShifts = _.filter(areaShiftsForArea, function(as) {
                      return _.contains(_.collect(as.position_type_ids, function(pt) {
                        return pt['$oid'];
                      }), positionType._id.$oid);
                    });
                    html = JST["setup/position_shift_tr"]({
                      positionAreaShiftsForArea: positionAreaShifts,
                      positionName: positionType.name,
                      areaType: areaType,
                      shiftTypes: gon.restaurant.restaurant_shift_types,
                      id: positionType._id.$oid
                    });
                    $("#workload-" + areaType._id.$oid + " tbody").append(html);
                  }
                  return setTimeout(function() {
                    window.bindCheckboxesScoped(areaTableSchedule.find(".shift-row input[type='checkbox']"));
                    return window.bindCheckboxesScoped(areaTableWorkload.find(".position-shift-tr input[type='checkbox']"));
                  }, 100);
                }
              };
            })(this),
            error: function(response) {
              return toastr["error"](response.responseText, "Error");
            }
          });
        };

        MainView.prototype.aspEdit = function(e) {
          var id;
          this.type = $(e.currentTarget).attr("data-asp-type");
          id = $(e.currentTarget).attr("data-" + this.type + "-id");
          $("tr[data-" + this.type + "-id=" + id + "]").find("#" + this.type + "-edit, #" + this.type + "-update, #" + this.type + "-remove, #" + this.type + "-cancel").toggleClass("hidden");
          $("tr[data-" + this.type + "-id=" + id + "]").find("." + this.type + "-name").saveData().editAndFocus(true);
          return $("tr[data-" + this.type + "-id=" + id + "]").find("." + this.type + "-sequence").saveData().editAndFocus(true);
        };

        MainView.prototype.aspUpdate = function(e) {
          var id, newName, newSequence, obj;
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          this.type = $(e.currentTarget).attr("data-asp-type");
          id = $(e.currentTarget).attr("data-" + this.type + "-id");
          newName = $("tr[data-" + this.type + "-id=" + id + "]").find("." + this.type + "-name").val();
          newSequence = $("tr[data-" + this.type + "-id=" + id + "]").find("." + this.type + "-sequence").val();
          return $.ajax({
            url: "api/" + (this.urlByType(this.type)) + "s/" + id,
            method: "patch",
            data: (
              obj = {},
              obj["" + (this.urlByType(this.type))] = {
                name: newName,
                sequence: newSequence
              },
              obj
            ),
            success: (function(_this) {
              return function() {
                toastr["success"]((_this.modelNameByType(_this.type)) + " successfully updated", "Success");
                $("tr[data-" + _this.type + "-id=" + id + "]").find("#" + _this.type + "-edit, #" + _this.type + "-update, #" + _this.type + "-remove, #" + _this.type + "-cancel").toggleClass("hidden");
                $("tr[data-" + _this.type + "-id=" + id + "]").find("." + _this.type + "-name").clearCashedData().editAndFocus(false);
                $("tr[data-" + _this.type + "-id=" + id + "]").find("." + _this.type + "-sequence").clearCashedData().editAndFocus(false);
                if (_this.type === "position-type") {
                  return $("tr.position-shift-tr[data-" + _this.type + "-id=" + id + "] .position-type-name-td").text(newName);
                } else {
                  _this.updateScheduleHtml();
                  return _this.updateWorkloadHtml();
                }
              };
            })(this),
            error: function(response) {
              return toastr["error"](response.responseText, "Error");
            }
          });
        };

        MainView.prototype.urlByType = function(type) {
          return type.replace(/-/g, "_");
        };

        MainView.prototype.modelNameByType = function(type) {
          var name, nameCapitilized;
          name = type.replace(/-type/, "");
          nameCapitilized = name.replace(/\b\w/g, (function(_this) {
            return function(l) {
              return l.toUpperCase();
            };
          })(this));
          return nameCapitilized;
        };

        MainView.prototype.updateScheduleHtml = function() {
          return $.ajax({
            url: "/api/setup/schedule_html",
            method: "get",
            success: function(e) {
              $("#schedule-wrapper").html(e);
              return window.bindCheckboxes();
            },
            error: function(response) {
              return toastr["error"](response.responseText, "Error");
            }
          });
        };

        MainView.prototype.psToggle = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "/api/setup/update_ps_relation",
            method: "patch",
            data: {
              checked: e.currentTarget.checked,
              shift_type_id: $(e.currentTarget).attr("data-shift-type"),
              position_type_id: $(e.currentTarget).attr("data-position-type"),
              area_type_id: $(e.currentTarget).attr("data-area-type")
            },
            success: function() {},
            error: function(response) {
              return toastr["error"](response.responseText, "Error");
            }
          });
        };

        MainView.prototype.updateWorkloadHtml = function() {
          return $.ajax({
            url: "/api/setup/workload_html",
            method: "get",
            success: function(e) {
              $("#workload-wrapper").html(e);
              return window.bindCheckboxes();
            },
            error: function(response) {
              return toastr["error"](response.responseText, "Error");
            }
          });
        };

        MainView.prototype.asToggle = function(e) {
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          return $.ajax({
            url: "/api/setup/update_as_relation",
            method: "patch",
            data: {
              checked: e.currentTarget.checked,
              day: $(e.currentTarget).attr("data-as-day"),
              shift_type_id: $(e.currentTarget).attr("data-as-shift-type-id"),
              area_type_id: $(e.currentTarget).attr("data-as-area-type-id")
            },
            success: function() {},
            error: function(response) {
              return toastr["error"](response.responseText, "Error");
            }
          });
        };

        MainView.prototype.updateAvailableBillingPlans = function(e) {
          var availablePlans;
          if ($(e.currentTarget).attr("disabled") === "disabled") {
            return true;
          }
          availablePlans = _.map($("#edit-subscription-plans input:checked"), function(f) {
            return $(f).attr('data-package-name');
          }) || [];
          if (_.isEmpty(availablePlans)) {
            availablePlans = "blank";
          }
          return $.ajax({
            url: "api/restaurants/1",
            method: "put",
            id: "1",
            data: {
              restaurant: {
                available_billing_plans: availablePlans
              }
            },
            success: function() {
              return toastr["success"]("Restaurant available billing plans updated.", "Success");
            },
            error: function(response) {
              return toastr["error"](response.responseText, "Error");
            }
          });
        };

        return MainView;

      })(Marionette.ItemView);
    });
    return $(document).ready(function() {
      if ($("body").hasClass("setup")) {
        return Tipmetric.Controllers.setupView = new Tipmetric.Setup.MainView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var SetupTourView;
    SetupTourView = Backbone.View.extend({
      el: "body.setup",
      events: {
        "click .start-tour": "startTour",
        "click *": "hidePopover"
      },
      hidePopover: function() {
        return $("[data-toggle='popover']").popover('hide');
      },
      initialize: function() {
        if (gon.setup_tour_run_count < 5) {
          return $("[data-toggle='popover']").popover("show");
        }
      },
      startTour: function() {
        var tour;
        tour = new Tour({
          name: "setup_tour",
          steps: [
            {
              element: "[data-tour-step='edit-name']",
              title: "Restaurant name",
              content: "Click edit to add or edit name of existing restaurant."
            }, {
              element: "[data-tour-step='submit-link']",
              title: "Submit link",
              content: "This link is to give employees so that they can submit hours and tips collected or login to see historical individual tip data.",
              placement: "bottom"
            }, {
              element: "[data-tour-step='areas']",
              title: "Areas",
              content: "Enter all the Areas in the Restaurant. (Bar, Dinning, Bistro etc.)."
            }, {
              element: "[data-tour-step='shifts']",
              title: "Shifts",
              content: "Enter all the Shifts in the Restaurant (Breakfast, Lunch, Dinner etc.)."
            }, {
              element: "[data-tour-step='positions']",
              title: "Positions",
              content: "Enter all the Position types in the Restaurant (Server, Bartender, Busser, Runner, Stocker, Barback etc.).",
              placement: "left"
            }, {
              element: "[data-tour-step='schedule']",
              title: "Schedule",
              content: "This section allows you to select the Shifts available per day for all the Areas...For example In the Dining Area on Sunday you might have only Brunch Shift but Monday to Saturday you have Lunch and Dinner Shifts in the Dining Area. Note: Whatever is selected here will influence the available choices when setting up a Tip Record in the Calculation page. (Please see Tour in Calculation Page).",
              placement: "top"
            }, {
              element: "[data-tour-step='workload']",
              title: "Workload",
              content: "This section allows you to select the Positions available per shift for all the Areas...For example In the Dining Area during Lunch Shift you might have Servers & Runners working but Dinning Area during Dinner Shift you might have Servers, Runners and Barbacks. Note: Whatever is selected here will influence the available choices when setting up a Tip Record in the Calculation page. (Please see Tour in Calculation Page).",
              placement: "top"
            }
          ],
          backdrop: true
        });
        tour.init();
        return tour.restart();
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("setup")) {
        return Tipmetric.Controllers.setupTourView = new SetupTourView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var SevenShiftImportView;
    SevenShiftImportView = Backbone.View.extend({
      el: "#seven-shift-import-view",
      events: {
        "click #set-token": "setToken",
        "click #change-token": "changeToken",
        "click #change-seven-shift-config": "changeSevenShiftConfig",
        "click [data-action='show-seven-shift-config']": "toggleTokenBlock",
        "change #area_type_id": "areaUpdated",
        "change #shift_type_id": "shiftUpdated",
        "change .changeble-data select": "calculationChanged",
        "blur #date": "dateBlured",
        "click #run": "getData",
        "click [data-action='show-seven-shift-area-select']": "showSevenShiftAreaSelect",
        "click [data-action='show-seven-shift-shift-select']": "showSevenShiftShiftSelect",
        "click [data-action='remove-row-inline']": "removeRowInline",
        "click #import": "sendImportData",
        "click .import-errors [data-action='remove']": "removeRow",
        "click .import-errors [data-action='create']": "createEmployee",
        "click .import-errors [data-action='add']": "addEmployee",
        "click .import-errors [data-action='match']": "matchEmployee",
        "click .import-errors [data-action='remove-match']": "removeMatchEmployee",
        "click .import-errors [data-action='select-position']": "selectPosition",
        "click .import-errors [data-action='merge-duplications']": "mergeDuplications",
        "click .import-errors [data-action='remove-duplicated-line']": "removeDuplication",
        "click [data-action='save-associated-positions']": "updatePositionAssociatedPositions",
        "click #reset-labels": "resetLabels",
        "click #set-labels": "setLabels",
        "click [data-action='set-auth-type']": "setAuthType"
      },
      initialize: function() {
        var dateUpdatedCallback;
        gon.restaurant_inheritance_data = Tipmetric.Controllers.helpers.buildSmartData();
        $.fn.editable.defaults.mode = 'inline';
        $.fn.editable.defaults.ajaxOptions = {
          type: "PUT"
        };
        $.fn.editableform.buttons = "<button type=\"submit\" class=\"btn btn-sm btn-success editable-submit\">SAVE</button> <button type=\"button\" class=\"btn btn-sm btn-default editable-cancel\">CANCEL</button>";
        dateUpdatedCallback = (function(_this) {
          return function() {
            return _this.dateUpdated();
          };
        })(this);
        $(".seven-shift-shifts-table").find('.start-time, .end-time').editable({
          url: function(params) {
            var json;
            json = {};
            json[$(this).attr("data-field-name")] = params.value;
            return $.ajax({
              url: "/api/shift_types/" + params.pk,
              method: "put",
              data: {
                shift_type: json
              },
              success: (function(_this) {
                return function(response) {
                  gon.restaurant_inheritance_data = response.restaurant_inheritance_data;
                  dateUpdatedCallback();
                  return toastr["success"]("successfully updated", "Success");
                };
              })(this),
              error: function(response) {
                return toastr["error"](response.responseText, "Error");
              }
            });
          }
        });
        $('#seven-shift-access-token').editable({
          type: 'text',
          url: (function(_this) {
            return function(params) {
              return $.ajax({
                url: "api/restaurants/1",
                method: "put",
                id: "1",
                data: {
                  restaurant: {
                    seven_shift_access_token: params.value
                  }
                },
                success: function(response, newValue) {
                  $("#seven-shift-access-token").removeAttr("contenteditable");
                  $("#access-token-wrapper").attr("token-form-status", "locked");
                  toastr["success"]("Token updated.", "Success");
                  console.log(params);
                  if (params.value.length > 30) {
                    return _this.bindLocationsSelect(true);
                  } else {
                    return _this.bindLocationsSelect(false);
                  }
                },
                error: function(response) {
                  return toastr["error"](response.responseText, "Error");
                }
              });
            };
          })(this)
        });
        $('#seven-shift-bearer-access-token').editable({
          type: 'text',
          url: (function(_this) {
            return function(params) {
              return $.ajax({
                url: "api/restaurants/1",
                method: "put",
                id: "1",
                data: {
                  restaurant: {
                    seven_shift_bearer_access_token: params.value
                  }
                },
                success: function(response, newValue) {
                  $("#seven-shift-bearer-access-token").removeAttr("contenteditable");
                  $("#access-token-wrapper").attr("token-form-status", "locked");
                  toastr["success"]("Token updated.", "Success");
                  if (params.value.length > 30) {
                    return _this.bindLocationsSelect(true);
                  } else {
                    return _this.bindLocationsSelect(false);
                  }
                },
                error: function(response) {
                  return toastr["error"](response.responseText, "Error");
                }
              });
            };
          })(this)
        });
        $(".seven-shift-associated-position-table").find('.start-time, .end-time').editable({
          url: function(params) {
            var json;
            json = {};
            json[$(this).attr("data-field-name")] = params.value;
            return $.ajax({
              url: "/api/position_types/" + params.pk,
              method: "put",
              data: {
                position_type: json
              },
              success: (function(_this) {
                return function(response) {
                  dateUpdatedCallback();
                  return toastr["success"]("successfully updated", "Success");
                };
              })(this),
              error: function(response) {
                return toastr["error"](response.responseText, "Error");
              }
            });
          }
        });
        this.bindLocationsSelect();
        $('.datapicker').datepicker().on("changeDate", (function(_this) {
          return function() {
            return _this.dateUpdated();
          };
        })(this)).on("show", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').addClass('open');
          };
        })(this)).on("hide", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').removeClass('open');
          };
        })(this));
        this.dateUpdated();
        this.areaUpdated();
        this.shiftUpdated();
        this.bindTimezoneSelect();
        this.setAuthTypeByValue();
        return Tipmetric.Controllers.helpers.bindFilteredRulesSaving(4, function() {
          return Tipmetric.Controllers.sevenShiftImportView.buildTable();
        });
      },
      tokenAdded: function() {
        var bearerTokenAdded, productionTokenAdded;
        productionTokenAdded = $("#seven-shift-access-token").text().replace(/ /g, '').replace(/\n/g, '').length > 30;
        bearerTokenAdded = $("#seven-shift-bearer-access-token").text().replace(/ /g, '').replace(/\n/g, '').length > 30;
        return productionTokenAdded || bearerTokenAdded;
      },
      toggleTokenBlock: function(e) {
        return $("#access-token-wrapper-block").toggle();
      },
      changeSevenShiftConfig: function(e) {
        $(".location-id").attr("contenteditable", "true");
        $("#access-token-wrapper").attr("token-form-status", "editing");
        return e.preventDefault();
      },
      changeToken: function(e) {
        $("#seven-shift-access-token").attr("contenteditable", "true");
        $("#access-token-wrapper").attr("token-form-status", "editing");
        return e.preventDefault();
      },
      setToken: function(e) {
        e.preventDefault();
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return true;
        }
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: {
            restaurant: {
              seven_shift_access_token: $("#seven-shift-access-token").text().replace(/ /g, '').replace(/\n/g, '')
            }
          },
          success: (function(_this) {
            return function() {
              $("#seven-shift-access-token").removeAttr("contenteditable");
              $("#access-token-wrapper").attr("token-form-status", "locked");
              toastr["success"]("Token updated.", "Success");
              return _this.bindLocationsSelect();
            };
          })(this),
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      setAuthType: function(e) {
        e.preventDefault();
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: {
            restaurant: {
              seven_shift_auth_type: $(e.currentTarget).attr("data-auth-type")
            }
          },
          success: (function(_this) {
            return function() {
              gon.restaurant.seven_shift_auth_type = $(e.currentTarget).attr("data-auth-type");
              _this.setAuthTypeByValue();
              _this.disableLocationSelect();
              _this.bindLocationsSelect();
              return toastr["success"]("Auth type updated.", "Success");
            };
          })(this),
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      setAuthTypeByValue: function() {
        var auth_type;
        auth_type = gon.restaurant.seven_shift_auth_type;
        $("[data-action='set-auth-type']").removeClass("btn-info").addClass("btn-default");
        return $("[data-action='set-auth-type'][data-auth-type='" + auth_type + "']").removeClass("btn-default").addClass("btn-info");
      },
      areaHasLocation: function() {
        var area, area_id;
        area_id = this.$el.find("#area_type_id").val();
        area = _.filter(gon.restaurant.restaurant_area_types, function(d) {
          return d._id.$oid === area_id;
        })[0];
        if (_.isEmpty(area.seven_shift_location_id)) {
          return false;
        } else {
          return true;
        }
      },
      setAreaLocationInGonData: function(area_id, square_location_id) {
        var area, area_types, index;
        area_types = gon.restaurant.restaurant_area_types;
        area = _.filter(gon.restaurant.restaurant_area_types, function(d) {
          return d._id.$oid === area_id;
        })[0];
        area.square_location_id = square_location_id;
        index = area_types.indexOf(area);
        if (index > -1) {
          area_types.splice(index, 1);
        }
        area_types.push(area);
        return gon.restaurant.restaurant_area_types = area_types;
      },
      productionTokenAdded: function() {
        return $("#seven-shift-access-token").text().replace(/ /g, '').replace(/\n/g, '').length > 30;
      },
      bearerTokenAdded: function() {
        return $("#seven-shift-bearer-access-token").text().replace(/ /g, '').replace(/\n/g, '').length > 30;
      },
      bindLocationsSelect: function(valueReceived, newValue) {
        return setTimeout((function(_this) {
          return function() {
            return _this.bindLocationsSelectTimeOuted(valueReceived, newValue);
          };
        })(this), 500);
      },
      bindLocationsSelectTimeOuted: function(valueReceived, newValue) {
        if (gon.restaurant.seven_shift_auth_type === "basic") {
          if (this.productionTokenAdded()) {
            return this.updateLocationSelects();
          } else {
            return this.disableLocationSelect();
          }
        } else if (_.contains(["bearer_auth_1", "bearer_auth_2"], gon.restaurant.seven_shift_auth_type)) {
          if (this.bearerTokenAdded()) {
            return this.updateLocationSelects();
          } else {
            return this.disableLocationSelect();
          }
        }
      },
      disableLocationSelect: function() {
        $('.location-id-select').editable("destroy");
        $('.location-id-select').attr("data-value", "");
        $('.location-id-select').attr("data-pk", "not selected");
        return $('.location-id-select').text("not selected");
      },
      updateLocationSelects: function() {
        return setTimeout((function(_this) {
          return function() {
            return $.ajax({
              url: "/api/seven_shift/get_locations",
              method: "get",
              success: function(response) {
                Tipmetric.Data.seven_shift_locations = response;
                return $('.location-id-select').editable({
                  type: 'select',
                  url: function(params) {
                    return $.ajax({
                      url: "/api/area_types/" + params.pk,
                      method: "put",
                      data: {
                        area_type: {
                          seven_shift_location_id: params.value
                        }
                      },
                      success: function() {
                        _this.setAreaLocationInGonData(params.pk, params.value);
                        return toastr["success"]("successfully updated", "Success");
                      },
                      error: function(response) {
                        return toastr["error"](response.responseText, "Error");
                      }
                    });
                  },
                  prepend: 'not selected',
                  source: _.map(Tipmetric.Data.seven_shift_locations, function(f) {
                    return {
                      value: f.id,
                      text: f.name
                    };
                  }),
                  display: function(value, sourceData) {
                    var elem;
                    elem = $.grep(sourceData, function(o) {
                      return o.value.toString() === value.toString();
                    });
                    if (elem[0]) {
                      return $(this).text(elem[0].text);
                    }
                  },
                  success: function(response, newValue) {
                    $(this).attr("data-value", newValue);
                    return console.log(newValue);
                  },
                  error: function(response) {
                    return console.log(response);
                  }
                });
              },
              error: function(response) {
                return toastr["error"]("Looks like something went wrong.", "Error");
              }
            });
          };
        })(this), 500);
      },
      bindTimezoneSelect: function() {
        return $('.timezone-select').editable({
          type: 'select',
          url: (function(_this) {
            return function(params) {
              return $.ajax({
                url: "/api/shift_types/" + params.pk,
                method: "put",
                data: {
                  shift_type: {
                    seven_shift_timezone: params.value
                  }
                },
                success: function() {
                  return toastr["success"]("successfully updated", "Success");
                },
                error: function(response) {
                  return toastr["error"](response.responseText, "Error");
                }
              });
            };
          })(this),
          prepend: 'not selected',
          source: _.map(_.pairs(gon.timezones), function(f) {
            return {
              value: f[0],
              text: f[0]
            };
          }),
          display: function(value, sourceData) {
            var elem;
            elem = $.grep(sourceData, function(o) {
              return o.value === value;
            });
            return $(this).text(elem[0].text);
          },
          success: function(response, newValue) {
            $(this).attr("data-value", newValue);
            return console.log(newValue);
          },
          error: function(response) {
            return console.log(response);
          }
        });
      },
      showSevenShiftAreaSelect: function(e) {
        if (e) {
          e.preventDefault();
        }
        return $(".seven-shift-areas-table").toggle();
      },
      showSevenShiftShiftSelect: function(e) {
        e.preventDefault();
        return $(".seven-shift-shifts-table").toggle();
      },
      updatePositionAssociatedPositions: function(e) {
        var input, json;
        input = $(e.currentTarget).closest('td').find(".seven-shift-associated-positions");
        json = {};
        json[$(input).attr("data-field-name")] = input.val();
        return $.ajax({
          url: "/api/position_types/" + ($(input).attr("data-id")),
          method: "put",
          data: {
            position_type: json
          },
          success: (function(_this) {
            return function(response) {
              return toastr["success"]("successfully updated", "Success");
            };
          })(this),
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      getDate: function() {
        if (!_.isEmpty($("#date").val())) {
          return $("#date").val();
        } else {
          return moment().format("MM/DD/YYYY");
        }
      },
      setLabels: function(e) {
        e.preventDefault();
        this.resetLabels(false);
        this.buildAssociatedPositionsJson();
        return this.identifyPositions();
      },
      showLabelsButtons: function(e) {
        return $(".position-labels-buttons").show();
      },
      resetLabels: function(e) {
        var labels;
        if (e) {
          e.preventDefault();
        }
        labels = $(".position-label");
        $(labels).closest("tr").removeAttr("data-position-type-id");
        return labels.remove();
      },
      buildAssociatedPositionsJson: function() {
        var i, json, len, ref, tr;
        json = {};
        ref = $(".seven-shift-associated-position-table tbody tr");
        for (i = 0, len = ref.length; i < len; i++) {
          tr = ref[i];
          json[$(tr).attr("data-position-type-id")] = {
            name: $(tr).attr("data-position-type-name"),
            associatedPositions: $(tr).find("input.seven-shift-associated-positions").val(),
            start_time: moment.parseZone((this.getDate()) + " " + ($(tr).find(".start-time").text())),
            end_time: moment.parseZone((this.getDate()) + " " + ($(tr).find(".end-time").text()))
          };
          if (json[$(tr).attr("data-position-type-id")].start_time > json[$(tr).attr("data-position-type-id")].end_time) {
            json[$(tr).attr("data-position-type-id")].end_time = moment.parseZone((this.getDate()) + " " + ($(tr).find(".end-time").text())).add(1, 'days');
          }
        }
        return this.assiciatedPositionsJson = json;
      },
      identifyPositions: function(e) {
        var associatedLineColumnNumber, associatedPosition, dataLine, i, isPositionCheckEnabled, len, matchFounded, pair, position, ref, results, shiftEndTime, shiftStartTime;
        associatedLineColumnNumber = $(".seven-shift-import-headers th[data-tm-field-name=':position_type']").attr("data-column-number");
        isPositionCheckEnabled = associatedLineColumnNumber !== void 0;
        ref = $(".seven-shift-import-data tr");
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          dataLine = ref[i];
          associatedPosition = $(dataLine).find("td[data-column-number='" + associatedLineColumnNumber + "']").text();
          shiftStartTime = moment(moment.parseZone($(dataLine).find("[data-tm-field-name=':start_at']").text()).format("MM/DD/YYYY HH:mm"));
          shiftEndTime = moment(moment.parseZone($(dataLine).find("[data-tm-field-name=':end_at']").text()).format("MM/DD/YYYY HH:mm"));
          if (shiftStartTime > shiftEndTime) {
            shiftEndTime = shiftEndTime.add(1, 'days');
          }
          if (!isPositionCheckEnabled && !isStartTimeCheckEnabled && !isEndTimeCheckEnabled) {
            continue;
          }
          results.push((function() {
            var j, k, len1, len2, ref1, ref2, results1;
            ref1 = _.pairs(this.assiciatedPositionsJson);
            results1 = [];
            for (j = 0, len1 = ref1.length; j < len1; j++) {
              pair = ref1[j];
              matchFounded = false;
              ref2 = pair[1].associatedPositions.split(",");
              for (k = 0, len2 = ref2.length; k < len2; k++) {
                position = ref2[k];
                if (associatedPosition.trim() === position.trim()) {
                  matchFounded = true;
                }
              }
              if (_.isEmpty(pair[1].associatedPositions) || _.isEmpty(associatedPosition) || !matchFounded) {
                continue;
              }
              if (shiftStartTime <= pair[1].start_time) {
                continue;
              }
              if (shiftEndTime >= pair[1].end_time) {
                continue;
              }
              $(dataLine).attr("data-position-type-id", pair[0]);
              results1.push($(dataLine).append("<span class='label label-success position-label'>" + pair[1].name + "</span>"));
            }
            return results1;
          }).call(this));
        }
        return results;
      },
      dateUpdated: function() {
        var area, areas, dayName, i, len, options, savedVal;
        if (!_.isEmpty(this.$el.find("#date").val())) {
          this.$el.find("#date").attr("data-saved-date", this.$el.find("#date").val());
        }
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        if (gon.restaurant_inheritance_data[dayName]) {
          areas = _.map(gon.restaurant_inheritance_data[dayName].areas, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          options = "";
          options += "<option value=''>Area</option>";
          for (i = 0, len = areas.length; i < len; i++) {
            area = areas[i];
            options += "<option value='" + area.id + "'>" + area.name + "</option>";
          }
          savedVal = this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          this.$el.find("#calculation-form-wrapper").find("select#area_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val(savedVal).trigger("change");
        } else {
          this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val("").trigger("change").html("<option value=''>Area</option>");
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return this.$el.find("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      areaUpdated: function() {
        var areaName, dayName, i, len, options, savedVal, shift, shifts;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option[value='" + (this.$el.find("select#area_type_id").val()) + "']").text();
        if (this.areaEmpty()) {
          return false;
        }
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas[areaName]) {
          shifts = _.map(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts, function(e) {
            return {
              id: e.id,
              name: e.name,
              start_time: e.start_time,
              end_time: e.end_time
            };
          });
          options = "";
          options += "<option value=''>Shift</option>";
          for (i = 0, len = shifts.length; i < len; i++) {
            shift = shifts[i];
            options += "<option value='" + shift.id + "'>" + shift.name + " (<b>" + shift.start_time + "-" + shift.end_time + "</b>)</option>";
          }
          savedVal = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val(savedVal).trigger("change");
        } else {
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return this.$el.find("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      availablePositions: function() {
        var areaName, dayName, shift, shiftId, shiftName;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option:selected").text();
        shiftId = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id option:selected").val();
        shift = _.find(_.pairs(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts), function(f) {
          return f[1].id === shiftId;
        });
        if (shift) {
          shiftName = shift[0];
        }
        if (gon.restaurant_inheritance_data[dayName].areas[areaName] && shiftName && gon.restaurant_inheritance_data[dayName].areas[areaName].shifts[shiftName]) {
          return gon.restaurant_inheritance_data[dayName].areas[areaName].shifts[shiftName].positions;
        } else {
          return false;
        }
      },
      areaEmpty: function() {
        return _.isEmpty(this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val());
      },
      shiftUpdated: function() {
        var areaData, areaName, dayName, firstPosition, i, len, options, position, positions, savedVal, shift, shiftId, shiftName;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option[value='" + ($('select#area_type_id').val()) + "']").text();
        if (this.areaEmpty()) {
          return false;
        }
        shiftId = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id option:selected").val();
        shift = _.find(_.pairs(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts), function(f) {
          return f[1].id === shiftId;
        });
        if (shift) {
          shiftName = shift[0];
        }
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas && areaName) {
          areaData = gon.restaurant_inheritance_data[dayName].areas[areaName];
        }
        if (areaData && shift && areaData.shifts[shiftName]) {
          positions = _.map(areaData.shifts[shiftName].positions, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          if (_.some(positions)) {
            firstPosition = positions[0].id;
          } else {
            firstPosition = "";
          }
          options = "";
          for (i = 0, len = positions.length; i < len; i++) {
            position = positions[i];
            options += "<option value='" + position.id + "'>" + position.name + "</option>";
          }
          savedVal = this.$el.find("select#source_position_ids").val();
          if (!savedVal) {
            savedVal = firstPosition;
          }
          this.$el.find("select#source_position_ids").html(options);
          return this.$el.find("select#source_position_ids").val(savedVal).trigger("change");
        } else {
          return this.$el.find("select#source_position_ids").val("").trigger("change");
        }
      },
      getData: function(e) {
        var data;
        e.preventDefault();
        if (!this.areaHasLocation()) {
          this.showSevenShiftAreaSelect(null);
          toastr["error"]("Looks like this area is not linked to SevenShift location id. Please link them before making request.", "Error");
          return false;
        }
        data = {
          date: this.$el.find("#date").val(),
          area_type_id: this.$el.find("#area_type_id").val(),
          shift_type_id: this.$el.find("#shift_type_id").val(),
          position_type: this.$el.find("#source_position_ids").val()
        };
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          url: "/api/seven_shift/get_data",
          method: "get",
          data: data,
          success: (function(_this) {
            return function(response) {
              console.log('success');
              console.log(response);
              _this.lastFetchedData = response;
              _this.buildTable();
              $("#import").removeAttr("disabled");
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              console.log('error');
              console.log(response);
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this)
        });
      },
      resetImportData: function(e) {
        this.$el.find(".seven-shift-import-headers").html("");
        this.$el.find(".seven-shift-import-data").html("");
        return this.$el.find(".import-errors > *").html("");
      },
      buildTable: function() {
        var actions_td_number, actions_tr_number, data, i, len, row, row_i, time_punches, tr_data, tr_head;
        this.resetImportData();
        data = this.lastFetchedData;
        time_punches = data.time_punches;
        tr_head = '';
        tr_head += "<tr>";
        tr_head += "<th data-tm-field-name='employee-line-number' data-tm-field-val='0'></th>";
        tr_head += "<th data-column-number='1'>Employee 7Shift id</th>";
        tr_head += "<th data-column-number='2'>First name</th>";
        tr_head += "<th data-column-number='3'>Last name</th>";
        tr_head += "<th data-column-number='4' data-tm-field-name=':position_type'>Role</th>";
        tr_head += "<th data-column-number='5'>Location</th>";
        tr_head += "<th data-column-number='6' data-tm-field-name=':start_time'>Clocked in</th>";
        tr_head += "<th data-column-number='7' data-tm-field-name=':end_time'>Clocked out</th>";
        tr_head += "<th data-column-number='8'>Hours worked</th>";
        if (gon.restaurant.seven_shift_auth_type !== "bearer_auth_2") {
          tr_head += "<th data-column-number='9'>Approved</th>";
          actions_tr_number = "10";
        } else {
          tr_head += "<th data-column-number='9'>pos_declared_tips</th>";
          tr_head += "<th data-column-number='10'>cc_tips</th>";
          tr_head += "<th data-column-number='11'>ag_svc</th>";
          actions_tr_number = "12";
        }
        tr_head += "<th data-column-number='" + actions_tr_number + "'></th>";
        tr_head += "</tr>";
        tr_data = '';
        row_i = 1;
        for (i = 0, len = time_punches.length; i < len; i++) {
          row = time_punches[i];
          tr_data += "<tr data-row-number='" + row_i + "' data-seven-shift-id='" + row.user_id + "'>";
          tr_data += "<td data-tm-field-name='employee-line-number' data-tm-field-val='" + row_i + "'>" + row_i + "</td>";
          tr_data += "<td data-column-number='1' data-tm-field-name=':seven_shift_user_id'>" + row.user_id + "</td>";
          tr_data += "<td data-column-number='2' data-tm-field-name=':first_name'>" + (row.employee ? row.employee.firstname || row.employee.first_name : void 0) + "</td>";
          tr_data += "<td data-column-number='3' data-tm-field-name=':last_name'>" + (row.employee ? row.employee.lastname || row.employee.last_name : void 0) + "</td>";
          tr_data += "<td data-column-number='4' data-tm-field-name=':position_type_name'>" + (row.role && row.role !== void 0 ? row.role.name : 'No role') + "</td>";
          tr_data += "<td data-column-number='5' data-tm-field-name=':location_id'>" + row.location.name + "</td>";
          tr_data += "<td data-column-number='6' data-tm-field-name=':start_at'>" + row.clocked_in_datetime_zoned + "</td>";
          tr_data += "<td data-column-number='7' data-tm-field-name=':end_at'>" + row.clocked_out_datetime_zoned + "</td>";
          tr_data += "<td data-column-number='8' data-tm-field-name=':hours_worked'>" + row.hours_worked + "</td>";
          if (gon.restaurant.seven_shift_auth_type !== "bearer_auth_2") {
            tr_data += "<td data-column-number='9' data-tm-field-name=':status'>" + row.approved + "</td>";
            actions_td_number = "10";
          } else {
            tr_data += "<td data-column-number='9' data-tm-field-name=':pos_declared_tips'>" + row.pos_declared_tips + "</td>";
            tr_data += "<td data-column-number='10' data-tm-field-name=':cc_tips'>" + row.cc_tips + "</td>";
            tr_data += "<td data-column-number='11' data-tm-field-name=':ag_svc'>" + row.ag_svc + "</td>";
            actions_td_number = "12";
          }
          tr_data += "<td data-column-number='" + actions_td_number + "' data-tm-field-name=':actions'><a class='btn btn-xs btn-danger' data-action='remove-row-inline'><i class='fa fa-remove'></i></a></td>";
          tr_data += "</tr>";
          row_i += 1;
        }
        this.$el.find(".seven-shift-import-headers").prepend(tr_head);
        this.$el.find(".seven-shift-import-data").prepend(tr_data);
        this.$el.find("#calculation-form-wrapper").show();
        this.showLabelsButtons();
        return Tipmetric.Controllers.helpers.filterImportDataByPosition($("#built-import-table"), 4);
      },
      removeRowInline: function(e) {
        return $(e.currentTarget).closest('tr').remove();
      },
      sendImportData: function(e) {
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return false;
        }
        e.preventDefault();
        window.seven_shift_json = this.buildImportJson();
        return this.sendCheckCsvImportDataRequest(false, void 0);
      },
      buildImportJson: function() {
        var employeeData, employeeTr, fieldName, i, id, j, json, len, len1, position_type_id, ref, ref1, rowNumber, td, value;
        json = {
          calcualtionParams: {},
          employeesData: [],
          frontend_restaurant_id: gon.restaurant.id
        };
        ref = this.$el.find(".seven-shift-import-data tr");
        for (i = 0, len = ref.length; i < len; i++) {
          employeeTr = ref[i];
          employeeData = {};
          id = this.$el.find(employeeTr).attr("data-emp-id");
          if (!_.isEmpty(id)) {
            employeeData[":emp_id"] = id;
          }
          id = this.$el.find(employeeTr).attr("data-seven-shift-id");
          if (!_.isEmpty(id)) {
            employeeData[":seven_shift_id"] = id;
          }
          position_type_id = this.$el.find(employeeTr).attr("data-position-type-id");
          if (!_.isEmpty(position_type_id)) {
            employeeData["position_type_id"] = position_type_id;
          }
          rowNumber = this.$el.find(employeeTr).attr("data-row-number");
          employeeData["row_number"] = rowNumber;
          ref1 = this.$el.find(employeeTr).find("td");
          for (j = 0, len1 = ref1.length; j < len1; j++) {
            td = ref1[j];
            fieldName = this.$el.find(td).attr("data-tm-field-name");
            value = this.$el.find(td).text();
            if (!_.isEmpty(fieldName) && !_.isEmpty(value)) {
              employeeData[fieldName] = value;
            }
          }
          json.employeesData.push(employeeData);
        }
        json.calcualtionParams = {
          date: this.$el.find("#date").val(),
          area_type_id: this.$el.find("select#area_type_id").val(),
          shift_type_id: this.$el.find("select#shift_type_id").val(),
          source_position_type_ids: this.$el.find("select#source_position_ids").val(),
          teams_quantity: 1
        };
        return json;
      },
      matchIdentifiedemployees: function(response) {
        var employee, i, len, ref, results, tr;
        window.response = response;
        ref = response.identified_employees;
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          employee = ref[i];
          tr = this.$el.find(".seven-shift-import-data tr[data-row-number='" + employee.row_number + "']");
          results.push(tr.attr("data-emp-id", employee.employee_data.emp_id));
        }
        return results;
      },
      addEmployee: function(e) {
        var currentParagraph;
        currentParagraph = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        return currentParagraph.find(".doesnt-exist-warning-form").show();
      },
      matchEmployee: function(e) {
        var currentParagraph, empId, empName, lineNumber, positionId, tr;
        currentParagraph = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper");
        empId = $(currentParagraph).find("select.employee-select option:selected").val();
        empName = $(currentParagraph).find("select.employee-select option:selected").text();
        positionId = $(currentParagraph).find("select.employee-select option:selected").attr("position-id");
        lineNumber = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        tr.attr("data-emp-id", empId);
        tr.attr("data-position-type-id", positionId);
        return $(currentParagraph).remove();
      },
      removeMatchEmployee: function(e) {
        var currentParagraph, lineNumber, tr;
        currentParagraph = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper, .employees-doesnt-belong-to-any-position-wrapper");
        lineNumber = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper, .employees-doesnt-belong-to-any-position-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        $(tr).remove();
        return $(currentParagraph).remove();
      },
      selectPosition: function(e) {
        var currentParagraph, lineNumber, positionId, tr;
        currentParagraph = $(e.currentTarget).closest(".employee-in-multiple-positions-wrapper");
        positionId = $(currentParagraph).find("select.position-type-select option:selected").val();
        lineNumber = $(e.currentTarget).closest(".employee-in-multiple-positions-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        tr.attr("data-position-type-id", positionId);
        return $(currentParagraph).remove();
      },
      createEmployee: function(e) {
        var brinkId, currentParagraph, empId, firstName, lastName, positionId, sevenShiftId, squareId;
        e.preventDefault();
        currentParagraph = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        positionId = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper").find("select.position-type-select option:selected").val();
        empId = $(currentParagraph).find("input[name='emp_id']").val();
        brinkId = $(currentParagraph).attr("data-brink-id");
        squareId = $(currentParagraph).attr("data-square-id");
        sevenShiftId = $(currentParagraph).attr("data-seven-shift-id");
        firstName = $(currentParagraph).find("input[name='first_name']").val();
        lastName = $(currentParagraph).find("input[name='last_name']").val();
        return $.ajax({
          url: "api/employees",
          method: "post",
          data: {
            employee: {
              emp_id: empId,
              first_name: firstName,
              brink_id: brinkId,
              square_id: squareId,
              seven_shift_id: sevenShiftId,
              last_name: lastName,
              position_type_ids: [positionId]
            }
          },
          success: function(response) {
            $(currentParagraph).remove();
            return toastr["success"]("Emloyee has been created.", "Success");
          },
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      removeRow: function(e) {
        var row, rowNumber;
        rowNumber = $(e.currentTarget).attr("data-row-number");
        row = this.$el.find("tr[data-row-number='" + rowNumber + "']");
        row.remove();
        return $(e.currentTarget).closest(".doesnt-exist-warning-wrapper").remove();
      },
      sendCheckCsvImportDataRequest: function(allowUpdate, e) {
        var data;
        if (e && $(e.currentTarget).attr("disabled") !== void 0) {
          return false;
        }
        data = this.buildImportJson();
        data.allowUpdate = allowUpdate;
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          method: "post",
          url: "/api/calculations/check_seven_shift_import_data",
          data: data,
          success: (function(_this) {
            return function(response) {
              console.log('success');
              console.log(response);
              _this.matchIdentifiedemployees(response);
              _this.$el.find(".import-errors > *").html('');
              return _this.sendImportSevenShiftRequest(allowUpdate);
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              var duplications, employee, employeeEmpId, html1, html3, html4, html5, html6, i, j, k, l, len, len1, len2, len3, len4, m, ref, ref1, ref2, ref3, ref4;
              console.log('error');
              window.response = response;
              _this.matchIdentifiedemployees(response.responseJSON);
              Tipmetric.Controllers.spinner.hide();
              Tipmetric.TestSupport.displayErrors(response.responseJSON.errors);
              if (response.responseJSON.critical_error) {
                swal({
                  title: "",
                  text: response.responseJSON.critical_error_message,
                  type: "error",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "OK",
                  showCancelButton: false
                });
                return;
              }
              if (response.responseJSON.calculation_exists === true && allowUpdate === false) {
                return swal({
                  title: "",
                  text: response.responseJSON.errors,
                  type: "info",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "Yes",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirmed) {
                  if (isConfirmed.value) {
                    return _this.sendCheckCsvImportDataRequest(true, e);
                  }
                });
              } else {
                if (response.responseJSON.errors) {
                  toastr["error"](response.responseJSON.errors, "Error");
                  return;
                }
                html1 = '';
                ref = response.responseJSON.employees_doesnt_exist;
                for (i = 0, len = ref.length; i < len; i++) {
                  employee = ref[i];
                  html1 += JST["csv_import/does_not_exist"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-exist").html(html1);
                html3 = '';
                ref1 = response.responseJSON.name_duplication;
                for (j = 0, len1 = ref1.length; j < len1; j++) {
                  employee = ref1[j];
                  html3 += JST["csv_import/last_name_duplication"](employee);
                }
                _this.$el.find(".import-errors .last-name-duplication").html(html3);
                html4 = '';
                ref2 = response.responseJSON.employee_in_multiple_positions;
                for (k = 0, len2 = ref2.length; k < len2; k++) {
                  employee = ref2[k];
                  html4 += JST["csv_import/employee_in_multiple_positions"](employee);
                }
                _this.$el.find(".import-errors .employee-in-multiple-positions").html(html4);
                html5 = '';
                ref3 = response.responseJSON.employees_doesnt_belong_to_any_position;
                for (l = 0, len3 = ref3.length; l < len3; l++) {
                  employee = ref3[l];
                  html5 += JST["csv_import/employees_doesnt_belong_to_any_position"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-belong-to-any-position").html(html5);
                html6 = '';
                ref4 = _.keys(response.responseJSON.duplicated_employees_grouped);
                for (m = 0, len4 = ref4.length; m < len4; m++) {
                  employeeEmpId = ref4[m];
                  duplications = _.map(response.responseJSON.duplicated_employees_grouped[employeeEmpId], function(f) {
                    f.filledFields = _this.filledFields(f);
                    return f;
                  });
                  html6 += JST["csv_import/duplicated_employee"](duplications);
                }
                _this.$el.find(".import-errors .employees-are-duplicated").html(html6);
                return toastr["error"]("Looks like import was not successfull.", "Error");
              }
            };
          })(this)
        });
      },
      filledFields: function(data) {
        return data[':hours_worked'] + " (" + ':hours_worked' + ")";
      },
      mergeDuplications: function(e) {
        var agSvcValues, ag_svc_tips_td, ccTipsValues, cc_tips_td, declaredValues, declared_tips_td, duplicationBlock, empId, finalAddedNumbers, finalFilledLine, hoursWorkedValues, i, len, lineNumbers, lineToRemove, lines, otherLines, tableRows, td, tr, values;
        duplicationBlock = $(e.currentTarget).closest(".employee-is-duplicated");
        lines = $(duplicationBlock).find(".employee-duplication-line");
        lineNumbers = _.collect(lines, function(line) {
          return $(line).attr("data-line-number");
        });
        empId = $(duplicationBlock).attr("data-emp-id");
        finalAddedNumbers = {};
        tableRows = _.filter($(".seven-shift-import-data tr[data-emp-id='" + empId + "']"), function(f) {
          return _.contains(lineNumbers, $(f).attr("data-row-number"));
        });
        if (_.contains(["basic", "bearer_auth_1"], gon.restaurant.seven_shift_auth_type)) {
          values = _.map($(tableRows).find("td[data-tm-field-name=':hours_worked']"), function(f) {
            return $(f).text();
          });
          finalAddedNumbers[':hours_worked'] = window.summTimes(values);
        } else {
          hoursWorkedValues = _.map($(tableRows).find("td[data-tm-field-name=':hours_worked']"), function(f) {
            return $(f).text();
          });
          finalAddedNumbers[':hours_worked'] = (_.reduce(hoursWorkedValues, function(a, b) {
            return parseFloat(a) + parseFloat(b);
          }) || 0).toFixed(2);
          declaredValues = _.map($(tableRows).find("td[data-tm-field-name=':pos_declared_tips']"), function(f) {
            return $(f).text();
          });
          finalAddedNumbers[':pos_declared_tips'] = (_.reduce(declaredValues, function(a, b) {
            return parseFloat(a) + parseFloat(b);
          }) || 0).toFixed(2);
          ccTipsValues = _.map($(tableRows).find("td[data-tm-field-name=':cc_tips']"), function(f) {
            return $(f).text();
          });
          finalAddedNumbers[':cc_tips'] = (_.reduce(ccTipsValues, function(a, b) {
            return parseFloat(a) + parseFloat(b);
          }) || 0).toFixed(2);
          agSvcValues = _.map($(tableRows).find("td[data-tm-field-name=':ag_svc']"), function(f) {
            return $(f).text();
          });
          finalAddedNumbers[':ag_svc'] = (_.reduce(agSvcValues, function(a, b) {
            return parseFloat(a) + parseFloat(b);
          }) || 0).toFixed(2);
        }
        finalFilledLine = lineNumbers[0];
        td = $(".seven-shift-import-data tr[data-emp-id='" + empId + "'][data-row-number='" + finalFilledLine + "'] td[data-tm-field-name=':hours_worked']");
        $(td).attr("data-value", finalAddedNumbers[':hours_worked']);
        $(td).text(finalAddedNumbers[':hours_worked']);
        if (gon.restaurant.seven_shift_auth_type === "bearer_auth_2") {
          declared_tips_td = $(".seven-shift-import-data tr[data-emp-id='" + empId + "'][data-row-number='" + finalFilledLine + "'] td[data-tm-field-name=':pos_declared_tips']");
          $(declared_tips_td).attr("data-value", finalAddedNumbers[':pos_declared_tips']);
          $(declared_tips_td).text(finalAddedNumbers[':pos_declared_tips']);
          cc_tips_td = $(".seven-shift-import-data tr[data-emp-id='" + empId + "'][data-row-number='" + finalFilledLine + "'] td[data-tm-field-name=':cc_tips']");
          $(cc_tips_td).attr("data-value", finalAddedNumbers[':cc_tips']);
          $(cc_tips_td).text(finalAddedNumbers[':cc_tips']);
          ag_svc_tips_td = $(".seven-shift-import-data tr[data-emp-id='" + empId + "'][data-row-number='" + finalFilledLine + "'] td[data-tm-field-name=':ag_svc']");
          $(ag_svc_tips_td).attr("data-value", finalAddedNumbers[':ag_svc']);
          $(ag_svc_tips_td).text(finalAddedNumbers[':ag_svc']);
        }
        otherLines = _.filter(lineNumbers, function(n) {
          return n !== finalFilledLine;
        });
        for (i = 0, len = otherLines.length; i < len; i++) {
          lineToRemove = otherLines[i];
          tr = this.$el.find("tr[data-emp-id='" + empId + "'][data-row-number='" + lineToRemove + "']");
          tr.remove();
        }
        return $(duplicationBlock).remove();
      },
      removeDuplication: function(e) {
        var currentParagraph, duplicationBlock, lineNumber, tr;
        duplicationBlock = $(e.currentTarget).closest(".employee-is-duplicated");
        currentParagraph = $(e.currentTarget).closest(".employee-duplication-line");
        lineNumber = $(e.currentTarget).attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        $(tr).remove();
        $(currentParagraph).remove();
        if ($(duplicationBlock).find(".employee-duplication-line").length === 0) {
          return $(duplicationBlock).remove();
        }
      },
      sendImportSevenShiftRequest: function(allowUpdate) {
        var data;
        data = this.buildImportJson();
        data.allowUpdate = allowUpdate;
        return $.ajax({
          method: "post",
          url: "/api/calculations/import_seven_shift",
          data: data,
          success: (function(_this) {
            return function(response) {
              toastr["success"]("Data has been imported.", "Success");
              Tipmetric.Controllers.spinner.hide();
              _this.resetImportData();
              return $("#import").attr("disabled", "disabled");
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              Tipmetric.Controllers.spinner.hide();
              if (response.responseJSON.calculation_exists === true) {
                return swal({
                  title: "",
                  text: response.responseJSON.errors,
                  type: "info",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "Yes",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirmed) {
                  if (isConfirmed.value) {
                    return _this.sendImportSevenShiftRequest(true);
                  }
                });
              } else {
                return toastr["error"]("Data is not imported.", "Error");
              }
            };
          })(this)
        });
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("seven-shift-import-view")) {
        return Tipmetric.Controllers.sevenShiftImportView = new SevenShiftImportView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var SquareImportView;
    SquareImportView = Backbone.View.extend({
      el: "#square-import-view",
      events: {
        "click #set-token": "setToken",
        "click #change-token": "changeToken",
        "click #change-square-config": "changeSquareConfig",
        "change #area_type_id": "areaUpdated",
        "change #shift_type_id": "shiftUpdated",
        "change .changeble-data select": "calculationChanged",
        "blur #date": "dateBlured",
        "click #run": "getData",
        "click #import": "sendImportData",
        "click .import-errors [data-action='remove']": "removeRow",
        "click .import-errors [data-action='create']": "createEmployee",
        "click .import-errors [data-action='add']": "addEmployee",
        "click .import-errors [data-action='remove-match']": "removeMatchEmployee",
        "click .import-errors [data-action='select-position']": "selectPosition",
        "click .import-errors [data-action='merge-duplications']": "mergeDuplications",
        "click .import-errors [data-action='remove-duplicated-line']": "removeDuplication",
        "click [data-action='remove-row-inline']": "removeRowInline",
        "click [data-action='show-square-config']": "toggleTokenBlock",
        "click [data-action='show-square-area-select']": "showSquareAreaSelect",
        "click [data-action='show-square-shift-select']": "showSquareShiftSelect"
      },
      initialize: function() {
        var dateUpdatedCallback;
        gon.restaurant_inheritance_data = Tipmetric.Controllers.helpers.buildSmartData();
        $.fn.editable.defaults.mode = 'inline';
        $.fn.editable.defaults.ajaxOptions = {
          type: "PUT"
        };
        $.fn.editableform.buttons = "<button type=\"submit\" class=\"btn btn-sm btn-success editable-submit\">SAVE</button> <button type=\"button\" class=\"btn btn-sm btn-default editable-cancel\">CANCEL</button>";
        dateUpdatedCallback = (function(_this) {
          return function() {
            return _this.dateUpdated();
          };
        })(this);
        $('.start-time, .end-time').editable({
          url: function(params) {
            var json;
            json = {};
            json[$(this).attr("data-field-name")] = params.value;
            return $.ajax({
              url: "/api/shift_types/" + params.pk,
              method: "put",
              data: {
                shift_type: json
              },
              success: (function(_this) {
                return function(response) {
                  gon.restaurant_inheritance_data = response.restaurant_inheritance_data;
                  dateUpdatedCallback();
                  return toastr["success"]("successfully updated", "Success");
                };
              })(this),
              error: function(response) {
                return toastr["error"](response.responseText, "Error");
              }
            });
          }
        });
        $('#square-access-token').editable({
          type: 'text',
          url: (function(_this) {
            return function(params) {
              return $.ajax({
                url: "api/restaurants/1",
                method: "put",
                id: "1",
                data: {
                  restaurant: {
                    square_access_token: params.value
                  }
                },
                success: function(response) {
                  $("#square-access-token").removeAttr("contenteditable");
                  $("#access-token-wrapper").attr("token-form-status", "locked");
                  toastr["success"]("Token updated.", "Success");
                  return _this.bindLocationsSelect();
                },
                error: function(response) {
                  return toastr["error"](response.responseText, "Error");
                }
              });
            };
          })(this)
        });
        this.bindTimezoneSelect();
        if (this.tokenAdded()) {
          this.bindLocationsSelect();
        }
        $('.datapicker').datepicker().on("changeDate", (function(_this) {
          return function() {
            return _this.dateUpdated();
          };
        })(this)).on("show", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').addClass('open');
          };
        })(this)).on("hide", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').removeClass('open');
          };
        })(this));
        this.dateUpdated();
        this.areaUpdated();
        this.shiftUpdated();
        return Tipmetric.Controllers.helpers.bindFilteredRulesSaving(4, function() {
          return Tipmetric.Controllers.squareImportView.buildTable();
        });
      },
      toggleTokenBlock: function(e) {
        return $("#access-token-wrapper-block").toggle();
      },
      showSquareAreaSelect: function(e) {
        if (e) {
          e.preventDefault();
        }
        return $(".square-areas-table").toggle();
      },
      showSquareShiftSelect: function(e) {
        e.preventDefault();
        return $(".square-shifts-table").toggle();
      },
      tokenAdded: function() {
        return $("#square-access-token").text().replace(/ /g, '').replace(/\n/g, '').length > 30;
      },
      bindLocationsSelect: function() {
        return $.ajax({
          url: "/api/square/get_locations",
          method: "get",
          success: (function(_this) {
            return function(response) {
              Tipmetric.Data.square_locations = response.locations;
              return $('.location-id-select').editable({
                type: 'select',
                url: function(params) {
                  return $.ajax({
                    url: "/api/area_types/" + params.pk,
                    method: "put",
                    data: {
                      area_type: {
                        square_location_id: params.value
                      }
                    },
                    success: function() {
                      _this.setAreaLocationInGonData(params.pk, params.value);
                      return toastr["success"]("successfully updated", "Success");
                    },
                    error: function(response) {
                      return toastr["error"](response.responseText, "Error");
                    }
                  });
                },
                prepend: 'not selected',
                source: _.map(Tipmetric.Data.square_locations, function(f) {
                  return {
                    value: f.id,
                    text: f.business_name
                  };
                }),
                display: function(value, sourceData) {
                  var elem;
                  elem = $.grep(sourceData, function(o) {
                    return o.value === value;
                  });
                  return $(this).text(elem[0].text);
                },
                success: function(response, newValue) {
                  $(this).attr("data-value", newValue);
                  return console.log(newValue);
                },
                error: function(response) {
                  return console.log(response);
                }
              });
            };
          })(this),
          error: function(response) {
            return toastr["error"]("Looks like something went wrong.", "Error");
          }
        });
      },
      bindTimezoneSelect: function() {
        Tipmetric.Data.square_locations = gon.timezones;
        return $('.timezone-select').editable({
          type: 'select',
          url: (function(_this) {
            return function(params) {
              return $.ajax({
                url: "/api/shift_types/" + params.pk,
                method: "put",
                data: {
                  shift_type: {
                    square_timezone: params.value
                  }
                },
                success: function() {
                  return toastr["success"]("successfully updated", "Success");
                },
                error: function(response) {
                  return toastr["error"](response.responseText, "Error");
                }
              });
            };
          })(this),
          prepend: 'not selected',
          source: _.map(_.pairs(gon.timezones), function(f) {
            return {
              value: f[0],
              text: f[0]
            };
          }),
          display: function(value, sourceData) {
            var elem;
            elem = $.grep(sourceData, function(o) {
              return o.value === value;
            });
            return $(this).text(elem[0].text);
          },
          success: function(response, newValue) {
            $(this).attr("data-value", newValue);
            return console.log(newValue);
          },
          error: function(response) {
            return console.log(response);
          }
        });
      },
      changeSquareConfig: function(e) {
        $(".location-id").attr("contenteditable", "true");
        $("#access-token-wrapper").attr("token-form-status", "editing");
        return e.preventDefault();
      },
      changeToken: function(e) {
        $("#square-access-token").attr("contenteditable", "true");
        $("#access-token-wrapper").attr("token-form-status", "editing");
        return e.preventDefault();
      },
      setToken: function(e) {
        e.preventDefault();
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return true;
        }
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: {
            restaurant: {
              square_access_token: $("#square-access-token").text().replace(/ /g, '').replace(/\n/g, '')
            }
          },
          success: (function(_this) {
            return function() {
              $("#square-access-token").removeAttr("contenteditable");
              $("#access-token-wrapper").attr("token-form-status", "locked");
              toastr["success"]("Token updated.", "Success");
              return _this.bindLocationsSelect();
            };
          })(this),
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      resetImportData: function(e) {
        this.$el.find(".square-import-headers").html("");
        this.$el.find(".square-import-data").html("");
        return this.$el.find(".import-errors > *").html("");
      },
      sendImportData: function(e) {
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return false;
        }
        e.preventDefault();
        window.square_json = this.buildImportJson();
        return this.sendCheckCsvImportDataRequest(false, void 0);
      },
      buildTable: function() {
        var data, i, len, money_holder_select, ordersData, row, row_i, shiftsData, tr_data, tr_head, tr_head_orders;
        data = this.lastFetchedData;
        this.resetImportData();
        shiftsData = data.shifts;
        ordersData = data.orders;
        money_holder_select = "<select name='money_holder_id' id='money_holder_id' class='form-control select-2'></select>";
        tr_head_orders = '';
        tr_head_orders += "<tr>";
        tr_head_orders += "<th colspan='11' class='orders-data' data-total-tip-money='" + (ordersData.total_tip_money / 100) + "' data-total-service-charge-money='" + (ordersData.total_service_charge_money / 100) + "'> Total Tip Money: " + (ordersData.total_tip_money / 100) + ", Total Service Charge Money: " + (ordersData.total_service_charge_money / 100) + " " + money_holder_select + " </th>";
        tr_head_orders += "</tr>";
        tr_head = '';
        tr_head += "<tr>";
        tr_head += "<th data-tm-field-name='employee-line-number' data-tm-field-val='0'></th>";
        tr_head += "<th data-column-number='1'>Employee square id</th>";
        tr_head += "<th data-column-number='2'>First name</th>";
        tr_head += "<th data-column-number='3'>Last name</th>";
        tr_head += "<th data-column-number='4'>Position id</th>";
        tr_head += "<th data-column-number='5'>Location id</th>";
        tr_head += "<th data-column-number='6'>Start at</th>";
        tr_head += "<th data-column-number='7'>End at</th>";
        tr_head += "<th data-column-number='8'>Hours worked</th>";
        tr_head += "<th data-column-number='9'>Status</th>";
        tr_head += "<th data-column-number='10'></th>";
        tr_head += "</tr>";
        tr_data = '';
        row_i = 1;
        for (i = 0, len = shiftsData.length; i < len; i++) {
          row = shiftsData[i];
          tr_data += "<tr data-row-number='" + row_i + "'>";
          tr_data += "<td data-tm-field-name='employee-line-number' data-tm-field-val='" + row_i + "'>" + row_i + "</td>";
          tr_data += "<td data-column-number='1' data-tm-field-name=':square_id'>" + row.employee_id + "</td>";
          tr_data += "<td data-column-number='2' data-tm-field-name=':first_name'>" + row.employee_data.given_name + "</td>";
          tr_data += "<td data-column-number='3' data-tm-field-name=':last_name'>" + row.employee_data.family_name + "</td>";
          tr_data += "<td data-column-number='4' data-tm-field-name=':position_type_name'>" + row.wage.title + "</td>";
          tr_data += "<td data-column-number='5' data-tm-field-name=':location_id'>" + row.location_id + "</td>";
          tr_data += "<td data-column-number='6' data-tm-field-name=':start_at'>" + row.start_at + "</td>";
          tr_data += "<td data-column-number='7' data-tm-field-name=':end_at'>" + row.end_at + "</td>";
          tr_data += "<td data-column-number='8' data-tm-field-name=':hours_worked'>" + row.hours_worked + "</td>";
          tr_data += "<td data-column-number='9' data-tm-field-name=':status'>" + row.status + "</td>";
          tr_data += "<td data-column-number='10' data-tm-field-name=':actions'><a class='btn btn-xs btn-danger' data-action='remove-row-inline'><i class='fa fa-remove'></i></a></td>";
          tr_data += "</tr>";
          row_i += 1;
        }
        this.$el.find(".square-import-headers").prepend(tr_head);
        this.$el.find(".square-import-headers").prepend(tr_head_orders);
        this.$el.find(".square-import-data").prepend(tr_data);
        this.$el.find("#calculation-form-wrapper").show();
        this.seedPossibleMoneyholdersList();
        return Tipmetric.Controllers.helpers.filterImportDataByPosition($("#built-import-table"), 4);
      },
      dateUpdated: function() {
        var area, areas, dayName, i, len, options, savedVal;
        if (!_.isEmpty(this.$el.find("#date").val())) {
          this.$el.find("#date").attr("data-saved-date", this.$el.find("#date").val());
        }
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        if (gon.restaurant_inheritance_data[dayName]) {
          areas = _.map(gon.restaurant_inheritance_data[dayName].areas, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          options = "";
          options += "<option value=''>Area</option>";
          for (i = 0, len = areas.length; i < len; i++) {
            area = areas[i];
            options += "<option value='" + area.id + "'>" + area.name + "</option>";
          }
          savedVal = this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          this.$el.find("#calculation-form-wrapper").find("select#area_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val(savedVal).trigger("change");
        } else {
          this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val("").trigger("change").html("<option value=''>Area</option>");
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return this.$el.find("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      areaUpdated: function() {
        var areaName, dayName, i, len, options, savedVal, shift, shifts;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option[value='" + (this.$el.find("select#area_type_id").val()) + "']").text();
        if (this.areaEmpty()) {
          return false;
        }
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas[areaName]) {
          shifts = _.map(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts, function(e) {
            return {
              id: e.id,
              name: e.name,
              start_time: e.start_time,
              end_time: e.end_time
            };
          });
          options = "";
          options += "<option value=''>Shift</option>";
          for (i = 0, len = shifts.length; i < len; i++) {
            shift = shifts[i];
            options += "<option value='" + shift.id + "'>" + shift.name + " (<b>" + shift.start_time + "-" + shift.end_time + "</b>)</option>";
          }
          savedVal = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val(savedVal).trigger("change");
        } else {
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return this.$el.find("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      availablePositions: function() {
        var areaName, dayName, shift, shiftId, shiftName;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option:selected").text();
        shiftId = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id option:selected").val();
        shift = _.find(_.pairs(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts), function(f) {
          return f[1].id === shiftId;
        });
        if (shift) {
          shiftName = shift[0];
        }
        if (gon.restaurant_inheritance_data[dayName].areas[areaName] && shiftName && gon.restaurant_inheritance_data[dayName].areas[areaName].shifts[shiftName]) {
          return gon.restaurant_inheritance_data[dayName].areas[areaName].shifts[shiftName].positions;
        } else {
          return false;
        }
      },
      seedPossibleMoneyholdersList: function() {
        var employee, i, j, k, len, len1, len2, options, position, positionsAndEmployees, ref, ref1;
        positionsAndEmployees = [];
        ref = _.pairs(this.availablePositions());
        for (i = 0, len = ref.length; i < len; i++) {
          position = ref[i];
          ref1 = position[1].employees;
          for (j = 0, len1 = ref1.length; j < len1; j++) {
            employee = ref1[j];
            positionsAndEmployees.push({
              positionId: position[1].id,
              employeeId: employee._id.$oid,
              text: employee.first_name + " " + employee.last_name + " (" + employee.emp_id + ") " + position[0]
            });
          }
        }
        positionsAndEmployees = _.sortBy(positionsAndEmployees, 'text');
        options = "";
        for (k = 0, len2 = positionsAndEmployees.length; k < len2; k++) {
          employee = positionsAndEmployees[k];
          options += "<option value='" + employee.employeeId + "' data-position-id='" + employee.positionId + "' data-employee-id='" + employee.employeeId + "'>" + employee.text + "</option>";
        }
        return this.$el.find("select#money_holder_id").html(options).select2({
          theme: "bootstrap"
        });
      },
      areaEmpty: function() {
        return _.isEmpty(this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val());
      },
      shiftUpdated: function() {
        var areaData, areaName, dayName, firstPosition, i, len, options, position, positions, savedVal, shift, shiftId, shiftName;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option[value='" + ($('select#area_type_id').val()) + "']").text();
        if (this.areaEmpty()) {
          return false;
        }
        shiftId = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id option:selected").val();
        shift = _.find(_.pairs(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts), function(f) {
          return f[1].id === shiftId;
        });
        if (shift) {
          shiftName = shift[0];
        }
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas && areaName) {
          areaData = gon.restaurant_inheritance_data[dayName].areas[areaName];
        }
        if (areaData && shift && areaData.shifts[shiftName]) {
          positions = _.map(areaData.shifts[shiftName].positions, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          if (_.some(positions)) {
            firstPosition = positions[0].id;
          } else {
            firstPosition = "";
          }
          options = "";
          for (i = 0, len = positions.length; i < len; i++) {
            position = positions[i];
            options += "<option value='" + position.id + "'>" + position.name + "</option>";
          }
          savedVal = this.$el.find("select#source_position_ids").val();
          if (!savedVal) {
            savedVal = firstPosition;
          }
          this.$el.find("select#source_position_ids").html(options);
          return this.$el.find("select#source_position_ids").val(savedVal).trigger("change");
        } else {
          return this.$el.find("select#source_position_ids").val("").trigger("change");
        }
      },
      areaHasLocation: function() {
        var area, area_id;
        area_id = this.$el.find("#area_type_id").val();
        area = _.filter(gon.restaurant.restaurant_area_types, function(d) {
          return d._id.$oid === area_id;
        })[0];
        if (_.isEmpty(area.square_location_id)) {
          return false;
        } else {
          return true;
        }
      },
      setAreaLocationInGonData: function(area_id, square_location_id) {
        var area, area_types, index;
        area_types = gon.restaurant.restaurant_area_types;
        area = _.filter(gon.restaurant.restaurant_area_types, function(d) {
          return d._id.$oid === area_id;
        })[0];
        area.square_location_id = square_location_id;
        index = area_types.indexOf(area);
        if (index > -1) {
          area_types.splice(index, 1);
        }
        area_types.push(area);
        return gon.restaurant.restaurant_area_types = area_types;
      },
      getData: function(e) {
        var data;
        e.preventDefault();
        if (!this.areaHasLocation()) {
          this.showSquareAreaSelect(null);
          toastr["error"]("Looks like this area is not linked to Square location id. Please link them before making request.", "Error");
          return false;
        }
        data = {
          date: this.$el.find("#date").val(),
          area_type_id: this.$el.find("#area_type_id").val(),
          shift_type_id: this.$el.find("#shift_type_id").val(),
          position_type: this.$el.find("#source_position_ids").val()
        };
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          url: "/api/square/get_data",
          method: "get",
          data: data,
          success: (function(_this) {
            return function(response) {
              console.log('success');
              console.log(response);
              _this.lastFetchedData = response;
              _this.buildTable();
              $("#import").removeAttr("disabled");
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              console.log('error');
              console.log(response);
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this)
        });
      },
      buildImportJson: function() {
        var employeeData, employeeTr, fieldName, i, id, j, json, len, len1, position_type_id, ref, ref1, rowNumber, td, value;
        json = {
          calcualtionParams: {},
          employeesData: [],
          frontend_restaurant_id: gon.restaurant.id
        };
        ref = this.$el.find(".square-import-data tr");
        for (i = 0, len = ref.length; i < len; i++) {
          employeeTr = ref[i];
          employeeData = {};
          id = this.$el.find(employeeTr).attr("data-emp-id");
          if (!_.isEmpty(id)) {
            employeeData[":emp_id"] = id;
          }
          id = this.$el.find(employeeTr).attr("data-square-id");
          if (!_.isEmpty(id)) {
            employeeData[":square_id"] = id;
          }
          position_type_id = this.$el.find(employeeTr).attr("data-position-type-id");
          if (!_.isEmpty(position_type_id)) {
            employeeData["position_type_id"] = position_type_id;
          }
          rowNumber = this.$el.find(employeeTr).attr("data-row-number");
          employeeData["row_number"] = rowNumber;
          ref1 = this.$el.find(employeeTr).find("td");
          for (j = 0, len1 = ref1.length; j < len1; j++) {
            td = ref1[j];
            fieldName = this.$el.find(td).attr("data-tm-field-name");
            value = this.$el.find(td).text();
            if (!_.isEmpty(fieldName) && !_.isEmpty(value)) {
              employeeData[fieldName] = value;
            }
          }
          json.employeesData.push(employeeData);
        }
        json.ordersData = {
          total_tip_money: $(".orders-data").attr("data-total-tip-money"),
          total_service_charge_money: $(".orders-data").attr("data-total-service-charge-money"),
          money_holder: {
            position_type_id: this.$el.find("select#money_holder_id option:selected").attr("data-position-id"),
            employee_id: this.$el.find("select#money_holder_id option:selected").attr("data-employee-id")
          }
        };
        json.calcualtionParams = {
          date: this.$el.find("#date").val(),
          area_type_id: this.$el.find("select#area_type_id").val(),
          shift_type_id: this.$el.find("select#shift_type_id").val(),
          source_position_type_ids: this.$el.find("select#source_position_ids").val(),
          teams_quantity: 1
        };
        return json;
      },
      matchIdentifiedemployees: function(response) {
        var employee, i, len, ref, results, tr;
        window.response = response;
        ref = response.identified_employees;
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          employee = ref[i];
          tr = this.$el.find(".square-import-data tr[data-row-number='" + employee.row_number + "']");
          results.push(tr.attr("data-emp-id", employee.employee_data.emp_id));
        }
        return results;
      },
      addEmployee: function(e) {
        var currentParagraph;
        currentParagraph = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        return currentParagraph.find(".doesnt-exist-warning-form").show();
      },
      removeMatchEmployee: function(e) {
        var currentParagraph, lineNumber, tr;
        currentParagraph = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper, .employees-doesnt-belong-to-any-position-wrapper");
        lineNumber = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper, .employees-doesnt-belong-to-any-position-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        $(tr).remove();
        return $(currentParagraph).remove();
      },
      selectPosition: function(e) {
        var currentParagraph, lineNumber, positionId, tr;
        currentParagraph = $(e.currentTarget).closest(".employee-in-multiple-positions-wrapper");
        positionId = $(currentParagraph).find("select.position-type-select option:selected").val();
        lineNumber = $(e.currentTarget).closest(".employee-in-multiple-positions-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        tr.attr("data-position-type-id", positionId);
        return $(currentParagraph).remove();
      },
      createEmployee: function(e) {
        var brinkId, currentParagraph, empId, firstName, lastName, positionId;
        e.preventDefault();
        currentParagraph = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        positionId = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper").find("select.position-type-select option:selected").val();
        empId = $(currentParagraph).find("input[name='emp_id']").val();
        brinkId = $(currentParagraph).attr("data-brink-id");
        firstName = $(currentParagraph).find("input[name='first_name']").val();
        lastName = $(currentParagraph).find("input[name='last_name']").val();
        return $.ajax({
          url: "api/employees",
          method: "post",
          data: {
            employee: {
              emp_id: empId,
              first_name: firstName,
              brink_id: brinkId,
              last_name: lastName,
              position_type_ids: [positionId]
            }
          },
          success: function(response) {
            $(currentParagraph).remove();
            return toastr["success"]("Emloyee has been created.", "Success");
          },
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      removeRowInline: function(e) {
        return $(e.currentTarget).closest('tr').remove();
      },
      removeRow: function(e) {
        var row, rowNumber;
        rowNumber = $(e.currentTarget).attr("data-row-number");
        row = this.$el.find("tr[data-row-number='" + rowNumber + "']");
        row.remove();
        return $(e.currentTarget).closest(".doesnt-exist-warning-wrapper").remove();
      },
      checkCsvImportData: function(e) {
        e.preventDefault();
        return this.sendCheckCsvImportDataRequest(false, e);
      },
      sendCheckCsvImportDataRequest: function(allowUpdate, e) {
        var data;
        if (e && $(e.currentTarget).attr("disabled") !== void 0) {
          return false;
        }
        data = this.buildImportJson();
        data.allowUpdate = allowUpdate;
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          method: "post",
          url: "/api/calculations/check_square_import_data",
          data: data,
          success: (function(_this) {
            return function(response) {
              console.log('success');
              console.log(response);
              _this.matchIdentifiedemployees(response);
              _this.$el.find(".import-errors > *").html('');
              return _this.sendImportSquareRequest(allowUpdate);
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              var duplications, employee, employeeEmpId, html1, html3, html4, html5, html6, i, j, k, l, len, len1, len2, len3, len4, m, ref, ref1, ref2, ref3, ref4;
              console.log('error');
              window.response = response;
              _this.matchIdentifiedemployees(response.responseJSON);
              Tipmetric.Controllers.spinner.hide();
              Tipmetric.TestSupport.displayErrors(response.responseJSON.errors);
              if (response.responseJSON.critical_error) {
                swal({
                  title: "",
                  text: response.responseJSON.critical_error_message,
                  type: "error",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "OK",
                  showCancelButton: false
                });
                return;
              }
              if (response.responseJSON.calculation_exists === true && allowUpdate === false) {
                return swal({
                  title: "",
                  text: response.responseJSON.errors,
                  type: "info",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "Yes",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirmed) {
                  if (isConfirmed.value) {
                    return _this.sendCheckCsvImportDataRequest(true, e);
                  }
                });
              } else {
                if (response.responseJSON.errors) {
                  toastr["error"](response.responseJSON.errors, "Error");
                  return;
                }
                html1 = '';
                ref = response.responseJSON.employees_doesnt_exist;
                for (i = 0, len = ref.length; i < len; i++) {
                  employee = ref[i];
                  html1 += JST["csv_import/does_not_exist"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-exist").html(html1);
                html3 = '';
                ref1 = response.responseJSON.name_duplication;
                for (j = 0, len1 = ref1.length; j < len1; j++) {
                  employee = ref1[j];
                  html3 += JST["csv_import/last_name_duplication"](employee);
                }
                _this.$el.find(".import-errors .last-name-duplication").html(html3);
                html4 = '';
                ref2 = response.responseJSON.employee_in_multiple_positions;
                for (k = 0, len2 = ref2.length; k < len2; k++) {
                  employee = ref2[k];
                  html4 += JST["csv_import/employee_in_multiple_positions"](employee);
                }
                _this.$el.find(".import-errors .employee-in-multiple-positions").html(html4);
                html5 = '';
                ref3 = response.responseJSON.employees_doesnt_belong_to_any_position;
                for (l = 0, len3 = ref3.length; l < len3; l++) {
                  employee = ref3[l];
                  html5 += JST["csv_import/employees_doesnt_belong_to_any_position"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-belong-to-any-position").html(html5);
                html6 = '';
                ref4 = _.keys(response.responseJSON.duplicated_employees_grouped);
                for (m = 0, len4 = ref4.length; m < len4; m++) {
                  employeeEmpId = ref4[m];
                  duplications = _.map(response.responseJSON.duplicated_employees_grouped[employeeEmpId], function(f) {
                    f.filledFields = _this.filledFields(f);
                    return f;
                  });
                  html6 += JST["csv_import/duplicated_employee"](duplications);
                }
                _this.$el.find(".import-errors .employees-are-duplicated").html(html6);
                return toastr["error"]("Looks like import was not successfull.", "Error");
              }
            };
          })(this)
        });
      },
      filledFields: function(data) {
        return data[':hours_worked'] + " (" + ':hours_worked' + ")";
      },
      mergeDuplications: function(e) {
        var duplicationBlock, empId, finalAddedNumbers, finalFilledLine, i, len, lineNumbers, lineToRemove, lines, otherLines, tableRows, td, tr, values;
        duplicationBlock = $(e.currentTarget).closest(".employee-is-duplicated");
        lines = $(duplicationBlock).find(".employee-duplication-line");
        lineNumbers = _.collect(lines, function(line) {
          return $(line).attr("data-line-number");
        });
        empId = $(duplicationBlock).attr("data-emp-id");
        finalAddedNumbers = {};
        tableRows = _.filter($(".square-import-data tr[data-emp-id='" + empId + "']"), function(f) {
          return _.contains(lineNumbers, $(f).attr("data-row-number"));
        });
        values = _.map($(tableRows).find("td[data-tm-field-name=':hours_worked']"), function(f) {
          return $(f).text();
        });
        finalAddedNumbers[':hours_worked'] = window.summTimes(values);
        finalFilledLine = lineNumbers[0];
        td = $(".square-import-data tr[data-emp-id='" + empId + "'][data-row-number='" + finalFilledLine + "'] td[data-tm-field-name=':hours_worked']");
        $(td).attr("data-value", finalAddedNumbers[':hours_worked']);
        $(td).text(finalAddedNumbers[':hours_worked']);
        otherLines = _.filter(lineNumbers, function(n) {
          return n !== finalFilledLine;
        });
        for (i = 0, len = otherLines.length; i < len; i++) {
          lineToRemove = otherLines[i];
          tr = this.$el.find("tr[data-emp-id='" + empId + "'][data-row-number='" + lineToRemove + "']");
          tr.remove();
        }
        return $(duplicationBlock).remove();
      },
      removeDuplication: function(e) {
        var currentParagraph, duplicationBlock, lineNumber, tr;
        duplicationBlock = $(e.currentTarget).closest(".employee-is-duplicated");
        currentParagraph = $(e.currentTarget).closest(".employee-duplication-line");
        lineNumber = $(e.currentTarget).attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        $(tr).remove();
        $(currentParagraph).remove();
        if ($(duplicationBlock).find(".employee-duplication-line").length === 0) {
          return $(duplicationBlock).remove();
        }
      },
      sendImportSquareRequest: function(allowUpdate) {
        var data;
        data = this.buildImportJson();
        data.allowUpdate = allowUpdate;
        return $.ajax({
          method: "post",
          url: "/api/calculations/import_square",
          data: data,
          success: (function(_this) {
            return function(response) {
              toastr["success"]("Data has been imported.", "Success");
              Tipmetric.Controllers.spinner.hide();
              _this.resetImportData();
              return $("#import").attr("disabled", "disabled");
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              Tipmetric.Controllers.spinner.hide();
              if (response.responseJSON.calculation_exists === true) {
                return swal({
                  title: "",
                  text: response.responseJSON.errors,
                  type: "info",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "Yes",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirmed) {
                  if (isConfirmed.value) {
                    return _this.sendImportSquareRequest(true);
                  }
                });
              } else {
                return toastr["error"]("Data is not imported.", "Error");
              }
            };
          })(this)
        });
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("square-import-view")) {
        return Tipmetric.Controllers.squareImportView = new SquareImportView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var SubmitView;
    SubmitView = Backbone.View.extend({
      el: "body.submit-tips",
      events: {
        "change #employee_distribution_date": "updateAreas",
        "change #employee_distribution_area_type": "updateShifts",
        "change #employee_distribution_shift_type": "updatePositions",
        "change #employee_distribution_position_type": "updateEmployees",
        "change #employee_distribution_employee": "validateWithTimeout",
        "click [type=submit]": "submitStarted",
        "keyup .validate": "validate"
      },
      initialize: function() {
        gon.restaurant_inheritance_data = Tipmetric.Controllers.helpers.buildSmartData();
        this.updateAreas();
        this.checkHours();
        this.rerenderEmployeesList();
        this.disableCCInputs();
        this.disableCashInputs();
        return this.validate();
      },
      select2Template: function(state) {
        var $state, calc_date, employee_position_status, hidden_from, isActive, position_type_id;
        if ($(state.element).closest("select").attr("id") === "employee_distribution_employee") {
          position_type_id = $("select#employee_distribution_position_type option:selected").val();
        } else if ($(state.element).closest("select").attr("id") === "employee_id") {
          position_type_id = $(state.element).closest(".tip-sharings-position-block").attr("data-position-id");
        }
        if (!gon.restaurant.hidden_from_enabled) {
          isActive = true;
        } else if (!_.isEmpty(_.findWhere(gon.restaurant_inheritance_data_raw.employee_position_statuses, {
          employee_id: state.id,
          position_type_id: position_type_id
        }))) {
          employee_position_status = _.findWhere(gon.restaurant_inheritance_data_raw.employee_position_statuses, {
            employee_id: state.id,
            position_type_id: position_type_id
          });
          hidden_from = moment(employee_position_status.hidden_from);
          calc_date = moment($("#employee_distribution_date").val());
          if (calc_date >= hidden_from) {
            isActive = false;
          } else {
            isActive = true;
          }
        } else {
          isActive = true;
        }
        if (!state.id) {
          return state.text;
        }
        $state = $(("<span data-position-enabled='" + isActive + "'>") + state.text + "</span>");
        return $state;
      },
      rerenderEmployeesList: function() {
        return $("select#employee_distribution_employee, select#employee_id").select2({
          theme: "bootstrap",
          templateResult: this.select2Template,
          templateSelection: this.select2Template
        });
      },
      disableCCInputs: function() {
        if (!gon.restaurant.cc_tips_input_enabled) {
          return $("#employee_distribution_cc_tips").attr("disabled", "disabled");
        }
      },
      disableCashInputs: function() {
        if (!gon.restaurant.cash_tips_input_enabled) {
          return $("#employee_distribution_cash_tips").attr("disabled", "disabled");
        }
      },
      submitStarted: function(e) {
        var data;
        e.preventDefault();
        data = $("form").serialize();
        if ($("form").valid()) {
          Tipmetric.Controllers.spinner.show();
          return $.ajax({
            url: "/employees/employee_distributions",
            method: "post",
            data: data,
            success: (function(_this) {
              return function(response) {
                toastr["success"](response.notice, "Success");
                Tipmetric.Controllers.spinner.hide();
                return _this.resetForm();
              };
            })(this),
            error: function(response) {
              toastr["error"](response.responseJSON.error, "Error");
              return Tipmetric.Controllers.spinner.hide();
            }
          });
        }
      },
      resetForm: function() {
        $("input[type='text']").val('0.0');
        $("#employee_distribution_employee").val("").trigger("change");
        $(".tip-sharings .tip-sharing.form-group").remove();
        return $(".tip-sharings-position-block").attr("data-tip-sharings-count", 0);
      },
      validate: function() {
        $("form").valid();
        return this.toggleSubmitButton();
      },
      validateWithTimeout: function() {
        return setTimeout((function(_this) {
          return function() {
            return _this.validate();
          };
        })(this), 100);
      },
      toggleTipSharingPositions: function(positions) {
        var i, len, positionBlock, positionId, ref;
        ref = $("#tip-sharings-block .tip-sharings-position-block");
        for (i = 0, len = ref.length; i < len; i++) {
          positionBlock = ref[i];
          positionId = $(positionBlock).attr("data-position-id");
          if (!_.findWhere(positions, {
            id: positionId
          })) {
            $(positionBlock).hide();
            $(positionBlock).find(".tip-sharings .tip-sharing").remove();
            $(positionBlock).attr("data-tip-sharings-count", 0);
          } else {
            $(positionBlock).show();
          }
        }
        if (Tipmetric.Controllers.tipSharingView) {
          return Tipmetric.Controllers.tipSharingView.updateFormTipSrarings();
        }
      },
      checkHours: function(e) {
        jQuery.extend(jQuery.validator.messages, {
          required: "This field is required. Enter 0.0 if there is no data to record.",
          number: "Please enter a valid number.",
          min: jQuery.validator.format("Please enter a value greater than or equal to {0}.")
        });
        return $("form").validate({
          rules: {
            'employee_distribution[hours_worked]': {
              required: true,
              number: true,
              min: 0
            },
            'employee_distribution[cash_tips]': {
              required: true,
              number: true,
              min: 0
            },
            'employee_distribution[cc_tips]': {
              required: true,
              number: true,
              min: 0
            },
            'employee_distribution[ag_svc_tips]': {
              required: true,
              number: true,
              min: 0
            },
            'employee_distribution[fee_tips]': {
              required: true,
              number: true,
              min: 0
            },
            'employee_distribution[sales_cc]': {
              required: true,
              number: true,
              min: 0
            },
            'employee_distribution[sales_cash]': {
              required: true,
              number: true,
              min: 0
            },
            'employee_distribution[food_sales]': {
              required: true,
              number: true,
              min: 0
            },
            'employee_distribution[beverage_sales]': {
              required: true,
              number: true,
              min: 0
            },
            'employee_distribution[employee]': {
              required: true,
              minlength: 1
            }
          },
          messages: {
            'employee_distribution[employee]': {
              required: "Please select employee"
            }
          }
        });
      },
      toggleSubmitButton: function() {
        if ($("form").valid()) {
          return $("#submit-button").removeAttr("disabled");
        } else {
          return $("#submit-button").attr("disabled", "disabled");
        }
      },
      updateAreas: function() {
        var area, areas, dayName, i, len, options, savedVal;
        dayName = moment($("#employee_distribution_date").val()).format('dddd').toLowerCase();
        if (gon.restaurant_inheritance_data[dayName]) {
          areas = _.map(gon.restaurant_inheritance_data[dayName].areas, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          options = "";
          for (i = 0, len = areas.length; i < len; i++) {
            area = areas[i];
            options += "<option value='" + area.id + "'>" + area.name + "</option>";
          }
          savedVal = $("select#employee_distribution_area_type").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          if (_.some(areas)) {
            savedVal = areas[0].id;
          }
          $("select#employee_distribution_area_type").html(options);
          $("select#employee_distribution_area_type").val(savedVal).trigger("change");
          savedVal = $("select#employee_distribution_area_type").val();
          if (savedVal === null || savedVal.length < 0) {
            $("select#employee_distribution_area_type").val("").trigger("change");
          }
        } else {
          $("select#employee_distribution_area_type").val("").trigger("change").html("<option value=''>Area</option>");
          $("select#employee_distribution_shift_type").val("").trigger("change").html("<option value=''>Shift</option>");
          $("select#employee_distribution_position_type").val("").trigger("change");
        }
        return this.updateShifts();
      },
      updateShifts: function() {
        var areaName, dayName, i, len, options, savedVal, shift, shifts;
        dayName = moment($("#employee_distribution_date").val()).format('dddd').toLowerCase();
        areaName = $("select#employee_distribution_area_type option[value='" + ($("select#employee_distribution_area_type").val()) + "']").text();
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas[areaName]) {
          shifts = _.map(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          options = "";
          for (i = 0, len = shifts.length; i < len; i++) {
            shift = shifts[i];
            options += "<option value='" + shift.id + "'>" + shift.name + "</option>";
          }
          savedVal = $("select#employee_distribution_shift_type").val();
          if (savedVal === null && shifts[0]) {
            savedVal = shifts[0].id;
          }
          $("select#employee_distribution_shift_type").html(options);
          $("select#employee_distribution_shift_type option[value='" + savedVal + "']").attr("selected", true).trigger("change");
        } else {
          $("select#employee_distribution_shift_type").val("").trigger("change").html("");
          $("select#employee_distribution_position_type").val("").trigger("change").html("");
        }
        return this.updatePositions();
      },
      updatePositions: function() {
        var areaData, areaName, dayName, i, len, options, position, positionsScoped, positionsUnscoped, savedVal, shiftName;
        dayName = moment($("#employee_distribution_date").val()).format('dddd').toLowerCase();
        areaName = $("select#employee_distribution_area_type option[value='" + ($("select#employee_distribution_area_type").val()) + "']").text();
        shiftName = $("select#employee_distribution_shift_type option[value='" + ($("select#employee_distribution_shift_type").val()) + "']").text();
        areaData = gon.restaurant_inheritance_data[dayName].areas[areaName];
        if (areaData && areaData.shifts[shiftName]) {
          positionsUnscoped = _.map(areaData.shifts[shiftName].positions, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          if (!_.isEmpty(gon.restaurant_inheritance_data.scope)) {
            positionsScoped = _.filter(positionsUnscoped, function(position) {
              return _.contains(gon.restaurant_inheritance_data.scope.position_type_ids, position.id);
            });
          } else {
            positionsScoped = positionsUnscoped;
          }
          this.toggleTipSharingPositions(positionsUnscoped);
          options = "";
          for (i = 0, len = positionsScoped.length; i < len; i++) {
            position = positionsScoped[i];
            options += "<option value='" + position.id + "'>" + position.name + "</option>";
          }
          savedVal = $("select#employee_distribution_position_type").val();
          if (!savedVal && positionsScoped[0]) {
            savedVal = positionsScoped[0].id;
          }
          $("select#employee_distribution_position_type").html(options);
          $("select#employee_distribution_position_type option[value='" + savedVal + "']").attr("selected", true).trigger("change");
        } else {
          $("select#employee_distribution_position_type").val("").trigger("change").html("");
        }
        return this.updateEmployees();
      },
      updateEmployees: function() {
        var areaName, dayName, employee, employees, employeesData, i, len, options, positionName, savedVal, shiftName;
        dayName = moment($("#employee_distribution_date").val()).format('dddd').toLowerCase();
        areaName = $("select#employee_distribution_area_type option[value='" + ($("select#employee_distribution_area_type").val()) + "']").text();
        shiftName = $("select#employee_distribution_shift_type option[value='" + ($("select#employee_distribution_shift_type").val()) + "']").text();
        positionName = $("select#employee_distribution_position_type option[value='" + ($("select#employee_distribution_position_type").val()) + "']").text();
        if (gon.restaurant_inheritance_data[dayName].areas[areaName] && gon.restaurant_inheritance_data[dayName].areas[areaName].shifts[shiftName] && gon.restaurant_inheritance_data[dayName].areas[areaName].shifts[shiftName].positions[positionName]) {
          employeesData = gon.restaurant_inheritance_data[dayName].areas[areaName].shifts[shiftName].positions[positionName];
          employees = _.map(employeesData.employees, function(e) {
            return {
              id: e._id.$oid,
              name: e.first_name + " " + e.last_name
            };
          });
          if (!_.isEmpty(gon.restaurant_inheritance_data.scope)) {
            employees = _.filter(employees, function(employee) {
              return gon.restaurant_inheritance_data.scope.employee._id.$oid === employee.id;
            });
          }
          options = "";
          options += "<option value=''></option>";
          for (i = 0, len = employees.length; i < len; i++) {
            employee = employees[i];
            options += "<option value='" + employee.id + "'>" + employee.name + "</option>";
          }
          savedVal = $("select#employee_distribution_employee").val();
          if (!savedVal && employees[0] && !_.findWhere(employees, {
            id: savedVal
          })) {
            savedVal = employees[0].id;
          }
          $("select#employee_distribution_employee").html(options);
          $("select#employee_distribution_employee option[value='" + savedVal + "']").attr("selected", true).trigger("change");
        } else {
          $("select#employee_distribution_employee").val("").trigger("change").html("");
        }
        this.rerenderEmployeesList();
        return this.validateWithTimeout();
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("submit-tips") && !gon.restrict_submit_form && _.some($("form#new_employee_distribution"))) {
        return Tipmetric.Controllers.submitView = new SubmitView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var SubmitTipsTourView;
    SubmitTipsTourView = Backbone.View.extend({
      el: "body.submit-tips",
      events: {
        "click .start-tour": "startTour",
        "click *": "hidePopover"
      },
      hidePopover: function() {
        return $("[data-toggle='popover']").popover('hide');
      },
      initialize: function() {
        return $("[data-toggle='popover']").popover("show");
      },
      startTour: function() {
        var tour;
        tour = new Tour({
          name: "setup_tour",
          steps: [
            {
              element: "[data-tour-step='date']",
              content: "Select the date of your shift worked (the date will be the date of today by default).",
              placement: 'bottom'
            }, {
              element: "[data-tour-step='hours']",
              content: "Enter the amount of hours worked during your shift.",
              placement: 'bottom'
            }, {
              element: "[data-tour-step='inputs']",
              content: "Enter the amount of cash tips and credit card tips collected during that shift (only the positions collecting tips directly from clients will fill out those two fields. Usually Servers and Bartenders collect tips directly from clients and then tip out the other positions such as Runner, Busser, Bar Back).",
              placement: 'bottom'
            }, {
              element: "[data-tour-step='sales']",
              content: "Enter your Credit Card Sales & Cash Sales, if instructed by your manager.",
              placement: 'bottom'
            }, {
              element: "[data-tour-step='team']",
              content: "If there are different Teams (Tip Pools) in the same area, indicate the number of your team for that shift. Otherwise leave “1”.",
              placement: 'bottom'
            }, {
              element: "[data-tour-step='area']",
              content: "Select the name of the area you worked in.",
              placement: 'bottom'
            }, {
              element: "[data-tour-step='shift']",
              content: "Indicate the shift worked.",
              placement: 'bottom'
            }, {
              element: "[data-tour-step='position']",
              content: "Select your position.",
              placement: 'bottom'
            }, {
              element: "[data-tour-step='name']",
              content: "Select your name.",
              placement: 'bottom'
            }, {
              element: "[data-tour-step='submit']",
              content: "Make sure to Submit.",
              placement: 'bottom'
            }
          ],
          backdrop: true
        });
        tour.init();
        return tour.restart();
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("submit-tips")) {
        return Tipmetric.Controllers.submitTipsTourView = new SubmitTipsTourView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var AccessManagementView;
    AccessManagementView = Backbone.View.extend({
      el: "#access-management-view",
      events: {
        "click [data-action='save-emails']": "updateEmails"
      },
      updateEmails: function(e) {
        var fieldName, json;
        Tipmetric.Controllers.spinner.show();
        json = {};
        fieldName = $(e.currentTarget).closest(".config-field-wrapper").find("input").attr("id");
        json[fieldName] = $("#" + fieldName).val();
        return $.ajax({
          url: "config_manager",
          method: "put",
          id: "1",
          data: json,
          success: (function(_this) {
            return function() {
              toastr["success"]("Emails updated.", "Success");
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this),
          error: function() {
            toastr["error"]("Something went wrong.", "Error");
            return Tipmetric.Controllers.spinner.hide();
          }
        });
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("access-management")) {
        return Tipmetric.Controllers.accessManagementView = new AccessManagementView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var TipSharingView;
    TipSharingView = Backbone.View.extend({
      el: "#tip-sharings-block",
      events: {
        "click .add-tip-sharing": "addTipSharing",
        "click .remove-tip-sharing": "removeTipSharing",
        "keyup .tip-sharing input": "updateFormTipSrarings",
        "change .tip-sharing select": "updateFormTipSrarings"
      },
      buildJson: function() {
        var i, j, json, len, len1, position, positionBlock, ref, ref1, tipSharing, tipSharingBlock;
        json = [];
        ref = $("#tip-sharings-block .tip-sharings-position-block");
        for (i = 0, len = ref.length; i < len; i++) {
          positionBlock = ref[i];
          if (!_.some($(positionBlock).find(".tip-sharings .tip-sharing"))) {
            continue;
          }
          position = {
            id: '',
            employees: []
          };
          position['id'] = $(positionBlock).attr("data-position-id");
          ref1 = $(positionBlock).find(".tip-sharings .tip-sharing");
          for (j = 0, len1 = ref1.length; j < len1; j++) {
            tipSharingBlock = ref1[j];
            tipSharing = {};
            tipSharing['position_id'] = position['id'];
            tipSharing['employee_id'] = $(tipSharingBlock).find("#employee_id").val();
            tipSharing['cc_tips_shared'] = parseFloat($(tipSharingBlock).find(".tip_sharing_cc_tips").val()) || 0;
            tipSharing['cash_tips_shared'] = parseFloat($(tipSharingBlock).find(".tip_sharing_cash_tips").val()) || 0;
            position['employees'].push(tipSharing);
          }
          json.push(position);
        }
        return json;
      },
      updateFormTipSrarings: function(e) {
        var json, jsonStringified;
        json = Tipmetric.Controllers.tipSharingView.buildJson();
        jsonStringified = JSON.stringify(json);
        $("form #employee_distribution_tip_sharings_string").val(jsonStringified);
        if (e) {
          return this.updateSelects(e);
        }
      },
      updateSelects: function(e) {
        var allEmployees, positionBlock, positionid;
        positionBlock = $(e.currentTarget).closest(".tip-sharings-position-block");
        positionid = $(positionBlock).attr("data-position-id");
        allEmployees = this.employeesForPosition(positionid);
        return this.updateEmployeesWithinSelectBox(allEmployees, $(positionBlock).find(".tip-sharings .tip-sharing select#employee_id"));
      },
      addTipSharing: function(e) {
        var allEmployees, disabledEmployees, employees, employeesToSubtract, html, positionBlock, positionid, selectedEmployeeId, selectedEmployeesWithinScope, tipSharingsCount;
        positionBlock = $(e.currentTarget).closest(".tip-sharings-position-block");
        positionid = $(positionBlock).attr("data-position-id");
        allEmployees = this.employeesForPosition(positionid);
        selectedEmployeeId = $("#employee_distribution_employee").val();
        selectedEmployeesWithinScope = _.map($(positionBlock).find(".tip-sharings .tip-sharing select#employee_id"), function(select) {
          return $(select).val();
        });
        disabledEmployees = _.map(_.filter(allEmployees, function(e) {
          return e.is_active === false;
        }), function(eHash) {
          return eHash.id;
        });
        employeesToSubtract = _.union(selectedEmployeesWithinScope, [selectedEmployeeId], disabledEmployees);
        employees = _.reject(allEmployees, function(e) {
          return _.contains(employeesToSubtract, e.id);
        });
        employees = _.sortBy(employees, function(e) {
          return e.full_info;
        });
        if (_.some(employees)) {
          html = JST["submit/tip_sharing"](employees);
          positionBlock.find(".tip-sharings").append(html);
        } else {
          toastr["error"]("Sorry, but there are no employees, for this position", "Error");
        }
        tipSharingsCount = $(positionBlock).find(".tip-sharing").length;
        $(positionBlock).attr("data-tip-sharings-count", tipSharingsCount);
        this.updateEmployeesWithinSelectBox(allEmployees, $(positionBlock).find(".tip-sharings .tip-sharing select#employee_id"));
        this.updateFormTipSrarings(e);
        return Tipmetric.Controllers.submitView.rerenderEmployeesList();
      },
      employeesForPosition: function(positionId) {
        return gon.all_related_employees[positionId].employees;
      },
      updateEmployeesWithinSelectBox: function(allEmployees, selects) {
        var disabledEmployees, employees, employeesToSubtract, i, len, otherSelects, results, select, selectedEmployeeId, selectedEmployeesWithinScope;
        selectedEmployeeId = $("#employee_distribution_employee").val();
        results = [];
        for (i = 0, len = selects.length; i < len; i++) {
          select = selects[i];
          otherSelects = _.filter(selects, function(sel) {
            return $(sel).val() !== $(select).val();
          });
          selectedEmployeesWithinScope = _.map(otherSelects, function(select) {
            return $(select).val();
          });
          disabledEmployees = _.map(_.filter(allEmployees, function(e) {
            return e.is_active === false;
          }), function(eHash) {
            return eHash.id;
          });
          employeesToSubtract = _.union(selectedEmployeesWithinScope, [selectedEmployeeId], disabledEmployees);
          employees = _.reject(allEmployees, function(e) {
            return _.contains(employeesToSubtract, e.id);
          });
          employees = _.sortBy(employees, function(e) {
            return e.full_info;
          });
          results.push($(select).updateSelectInputOptions(employees));
        }
        return results;
      },
      removeTipSharing: function(e) {
        var allEmployees, positionBlock, positionid, tipSharing, tipSharingsCount;
        tipSharing = $(e.currentTarget).closest(".tip-sharing");
        positionBlock = $(e.currentTarget).closest(".tip-sharings-position-block");
        positionid = $(positionBlock).attr("data-position-id");
        allEmployees = this.employeesForPosition(positionid);
        tipSharing.remove();
        tipSharingsCount = $(positionBlock).find(".tip-sharing").length;
        $(positionBlock).attr("data-tip-sharings-count", tipSharingsCount);
        this.updateEmployeesWithinSelectBox(allEmployees, $(positionBlock).find(".tip-sharings .tip-sharing select#employee_id"));
        return this.updateFormTipSrarings();
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("submit-tips") && gon.restaurant.tip_sharings_logic) {
        return Tipmetric.Controllers.tipSharingView = new TipSharingView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var TipSharingToggleView;
    TipSharingToggleView = Backbone.View.extend({
      el: ".tip-sharings",
      events: {
        "click .employee-distribution-line": "showTipsharings",
        "click [data-action='edit-tip-sharing']": "editTipSharing",
        "click [data-action='save-tip-sharing']": "saveTipSharing",
        "click [data-action='add-tip-sharing']": "addTipSharing",
        "click [data-action='create-tip-sharing']": "createTipSharing",
        "change #position_type_id": "positionIsUpdated",
        "change #employee_id": "employeeIsUpdated"
      },
      initialize: function() {
        return this.calculateTotals();
      },
      showTipsharings: function(e) {
        var collapseStatus, distributionId, tipSharingsLine;
        distributionId = $(e.currentTarget).attr("data-emp-distribution-id");
        tipSharingsLine = $(".tip-sharings-line[data-emp-distribution-id='" + distributionId + "']");
        if ($(tipSharingsLine).attr("content-editing-enabled") === "true") {
          return;
        }
        collapseStatus = $(e.currentTarget).attr("data-collapse-status");
        if (collapseStatus === "collapsed") {
          $(e.currentTarget).attr("data-collapse-status", "expanded");
          return tipSharingsLine.attr("data-collapse-status", "expanded");
        } else {
          $(e.currentTarget).attr("data-collapse-status", "collapsed");
          return tipSharingsLine.attr("data-collapse-status", "collapsed");
        }
      },
      editTipSharing: function(e) {
        var distributionId, scope;
        e.preventDefault();
        e.stopPropagation();
        $(".tip-sharing[data-tip-sharing-status='new']").remove();
        distributionId = $(e.currentTarget).closest("tr").attr("data-emp-distribution-id");
        scope = $("[data-emp-distribution-id='" + distributionId + "']");
        if (scope.attr("content-editing-enabled") === "true") {
          scope.attr("content-editing-enabled", false);
          return scope.find(".hours-editable, .cc-editable-distribution, .cash-editable-distribution, .cc-editable, .cash-editable, .food-sales-editable-distribution, .beverage-sales-editable-distribution").attr("contenteditable", false);
        } else {
          scope.attr("content-editing-enabled", true);
          return scope.find(".hours-editable, .cc-editable-distribution, .cash-editable-distribution, .cc-editable, .cash-editable, .food-sales-editable-distribution, .beverage-sales-editable-distribution").attr("contenteditable", true);
        }
      },
      getScope: function(e) {
        var distributionId;
        distributionId = $(e.currentTarget).closest("tr").attr("data-emp-distribution-id");
        return $("[data-emp-distribution-id='" + distributionId + "']");
      },
      calculateTotals: function() {
        var i, len, ref, results, scope, teamBlock, totalBeverageMap, totalBeverageSumm, totalCCMap, totalCCSumm, totalCashMap, totalCashSumm, totalFoodSalesMap, totalFoodSalesSumm, totalHoursMap, totalHoursSumm;
        scope = $("#total-block");
        totalHoursMap = _.map($(".employee-distribution-line .hours-editable"), function(num) {
          return parseFloat($(num).text());
        });
        totalHoursSumm = _.reduce(totalHoursMap, function(memo, num) {
          return memo + num;
        }) || 0;
        scope.find(".hours").text(totalHoursSumm.toFixed(2));
        totalCCMap = _.map($(".employee-distribution-line .before-tip-out-td .cc-editable-distribution"), function(num) {
          return parseFloat($(num).text());
        });
        totalCCSumm = _.reduce(totalCCMap, function(memo, num) {
          return memo + num;
        }) || 0;
        scope.find(".cc-out-total").text(totalCCSumm.toFixed(2));
        totalCashMap = _.map($(".employee-distribution-line .before-tip-out-td .cash-editable-distribution"), function(num) {
          return parseFloat($(num).text());
        });
        totalCashSumm = _.reduce(totalCashMap, function(memo, num) {
          return memo + num;
        }) || 0;
        scope.find(".cash-out-total").text(totalCashSumm.toFixed(2));
        ref = $(".team-table-block");
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          teamBlock = ref[i];
          scope = $(teamBlock);
          totalHoursMap = _.map(scope.find(".employee-distribution-line .hours-editable"), function(num) {
            return parseFloat($(num).text());
          });
          totalHoursSumm = _.reduce(totalHoursMap, function(memo, num) {
            return memo + num;
          }) || 0;
          scope.find(".total-hours h4").text(totalHoursSumm.toFixed(2));
          totalCCMap = _.map(scope.find(".employee-distribution-line .before-tip-out-td .cc-editable-distribution"), function(num) {
            return parseFloat($(num).text());
          });
          totalCCSumm = _.reduce(totalCCMap, function(memo, num) {
            return memo + num;
          }) || 0;
          scope.find(".total-cc-out h4").text(totalCCSumm.toFixed(2));
          totalFoodSalesMap = _.map(scope.find(".employee-distribution-line .food-sales-td div"), function(num) {
            return parseFloat($(num).text());
          });
          totalFoodSalesSumm = _.reduce(totalFoodSalesMap, function(memo, num) {
            return memo + num;
          }) || 0;
          scope.find(".total-food-sales-out h4").text(totalFoodSalesSumm.toFixed(2));
          totalBeverageMap = _.map(scope.find(".employee-distribution-line .beverage-sales-td div"), function(num) {
            return parseFloat($(num).text());
          });
          totalBeverageSumm = _.reduce(totalBeverageMap, function(memo, num) {
            return memo + num;
          }) || 0;
          scope.find(".total-beverage-sales-out h4").text(totalBeverageSumm.toFixed(2));
          totalCashMap = _.map(scope.find(".employee-distribution-line .before-tip-out-td .cash-editable-distribution"), function(num) {
            return parseFloat($(num).text());
          });
          totalCashSumm = _.reduce(totalCashMap, function(memo, num) {
            return memo + num;
          }) || 0;
          scope.find(".total-cash-out h4").text(totalCashSumm.toFixed(2));
          totalCashMap = _.map(scope.find(".employee-distribution-line .before-tip-out-td .ag-svc-out"), function(num) {
            return parseFloat($(num).text());
          });
          totalCashSumm = _.reduce(totalCashMap, function(memo, num) {
            return memo + num;
          }) || 0;
          scope.find(".total-ag-svc-out h4").text(totalCashSumm.toFixed(2));
          totalCashMap = _.map(scope.find(".employee-distribution-line .before-tip-out-td .fee-out"), function(num) {
            return parseFloat($(num).text());
          });
          totalCashSumm = _.reduce(totalCashMap, function(memo, num) {
            return memo + num;
          }) || 0;
          scope.find(".total-fee-out h4").text(totalCashSumm.toFixed(2));
          totalCCMap = _.map(scope.find(".employee-distribution-line .final-tips-distributed-cc"), function(num) {
            return parseFloat($(num).text());
          });
          totalCCSumm = _.reduce(totalCCMap, function(memo, num) {
            return memo + num;
          }) || 0;
          scope.find(".total-tips-distributed-cc h4").text(totalCCSumm.toFixed(2));
          totalCashMap = _.map(scope.find(".employee-distribution-line .final-tips-distributed-cash"), function(num) {
            return parseFloat($(num).text());
          });
          totalCashSumm = _.reduce(totalCashMap, function(memo, num) {
            return memo + num;
          }) || 0;
          scope.find(".total-tips-distributed-cash h4").text(totalCashSumm.toFixed(2));
          totalCCMap = _.map(scope.find(".employee-distribution-line .final-tips-distributed-ag-svc"), function(num) {
            return parseFloat($(num).text());
          });
          totalCCSumm = _.reduce(totalCCMap, function(memo, num) {
            return memo + num;
          }) || 0;
          scope.find(".total-tips-distributed-ag-svc h4").text(totalCCSumm.toFixed(2));
          totalCashMap = _.map(scope.find(".employee-distribution-line .final-tips-distributed-fee"), function(num) {
            return parseFloat($(num).text());
          });
          totalCashSumm = _.reduce(totalCashMap, function(memo, num) {
            return memo + num;
          }) || 0;
          results.push(scope.find(".total-tips-distributed-fee h4").text(totalCashSumm.toFixed(2)));
        }
        return results;
      },
      addTipSharing: function(e) {
        var html, newTipSharing, positionsSelectHtml, scope, tempId;
        e.preventDefault();
        e.stopPropagation();
        scope = this.getScope(e);
        tempId = Math.random() * (1000000000 - 0) + 0;
        html = JST["calculation/tip_sharings/tip_sharing"]({
          status: 'new',
          tempId: tempId
        });
        scope.find(".shared-tip-sharings").append(html);
        newTipSharing = scope.find(".shared-tip-sharings [data-tip-sharing-id='" + tempId + "']");
        positionsSelectHtml = JST["calculation/tip_sharings/position_types"](_.select(_.map(gon.all_related_employees_unscoped), function(e) {
          return e.employees.length > 0;
        }));
        newTipSharing.find(".position-name-select").append(positionsSelectHtml);
        newTipSharing.find(".position-name-select #position_type_id").trigger("change");
        return $(".receiver-info-select select#employee_id").select2({
          theme: "bootstrap",
          templateResult: this.select2Template,
          templateSelection: this.select2Template
        });
      },
      createTipSharing: function(e) {
        var data, distributionId, scope, tipSharingLine;
        Tipmetric.Controllers.spinner.show();
        scope = this.getScope(e);
        distributionId = $(e.currentTarget).closest("tr").attr("data-emp-distribution-id");
        tipSharingLine = $(e.currentTarget).closest(".tip-sharing");
        data = {
          calculation_id: gon.calculation_id,
          distribution_id: distributionId,
          position_type_id: $(tipSharingLine).attr("data-position-id"),
          receiver_id: $(tipSharingLine).attr("data-receiver-id"),
          cc_summ: parseFloat($(tipSharingLine).find(".cc-editable").text()) || 0,
          cash_summ: parseFloat($(tipSharingLine).find(".cash-editable").text()) || 0
        };
        return $.ajax({
          url: "/api/tip_sharings",
          method: "post",
          data: data,
          success: (function(_this) {
            return function(response) {
              var i, id, len, ref, tipSharing;
              Tipmetric.Controllers.spinner.hide();
              $(tipSharingLine).attr("data-tip-sharing-id", response.newly_tip_sharing.id);
              $(tipSharingLine).removeAttr("data-tip-sharing-status");
              scope.find(".hours-editable").text(response.distribution_info.hours_worked.toFixed(2));
              scope.find(".cc-editable-distribution").text(response.distribution_info.cc_tips.toFixed(2));
              scope.find(".cash-editable-distribution").text(response.distribution_info.cash_tips.toFixed(2));
              scope.find(".final-tips-distributed-cc").text(response.distribution_info.cc_tips_distr_final.toFixed(2));
              scope.find(".final-tips-distributed-cash").text(response.distribution_info.cash_tips_distr_final.toFixed(2));
              ref = $(scope).find(".tip-sharing");
              for (i = 0, len = ref.length; i < len; i++) {
                tipSharingLine = ref[i];
                id = $(tipSharingLine).attr("data-tip-sharing-id");
                tipSharing = response.tip_sharings[id];
                $(tipSharingLine).find(".cc-editable").text(tipSharing.cc_summ.toFixed(2));
                $(tipSharingLine).find(".cash-editable").text(tipSharing.cash_summ.toFixed(2));
              }
              scope.attr("content-editing-enabled", false);
              scope.find(".hours-editable, .cc-editable-distribution, .cash-editable-distribution, .cc-editable, .cash-editable").attr("contenteditable", false);
              toastr["success"]("Distribution updated.", "Success");
              return _this.calculateTotals();
            };
          })(this),
          error: function(response) {
            Tipmetric.Controllers.spinner.hide();
            this.calculateTotals();
            return toastr["error"](response.responseJSON.errors, "Error");
          }
        });
      },
      positionIsUpdated: function(e) {
        var positionTypeId, positionTypeName, tipSharingLine;
        tipSharingLine = $(e.currentTarget).closest(".tip-sharing");
        positionTypeId = $(e.currentTarget).val();
        $(tipSharingLine).attr("data-position-id", positionTypeId);
        positionTypeName = $(e.currentTarget).find("option[value='" + positionTypeId + "']").text();
        $(tipSharingLine).find(".position-name").text(positionTypeName);
        return this.updateEmployeesList(e);
      },
      employeeIsUpdated: function(e) {
        var receiverId, receiverName, tipSharingLine;
        tipSharingLine = $(e.currentTarget).closest(".tip-sharing");
        receiverId = $(e.currentTarget).val();
        $(tipSharingLine).attr("data-receiver-id", receiverId);
        receiverName = $(e.currentTarget).find("option[value='" + receiverId + "']").text();
        return $(tipSharingLine).find(".receiver-info").text(receiverName);
      },
      updateEmployeesList: function(e) {
        var employees, html, position_type_id, tipSharingLine;
        tipSharingLine = $(e.currentTarget).closest(".tip-sharing");
        position_type_id = $(e.currentTarget).val();
        employees = gon.all_related_employees_unscoped[position_type_id].employees;
        html = JST["calculation/tip_sharings/employee_select"]({
          employees: employees
        });
        $(tipSharingLine).find(".receiver-info-select").html(html);
        return $(tipSharingLine).find(".receiver-info-select #employee_id").trigger("change");
      },
      saveTipSharing: function(e) {
        var distributionId, distributionInfo, i, len, ref, scope, tipSharingId, tipSharingLine;
        Tipmetric.Controllers.spinner.show();
        distributionId = $(e.currentTarget).closest(".employee-distribution-line").attr("data-emp-distribution-id");
        scope = $("[data-emp-distribution-id='" + distributionId + "']");
        distributionInfo = {
          id: distributionId,
          hours_worked: scope.find(".hours-editable").text(),
          cc_tips: parseFloat(scope.find(".cc-editable-distribution").text()) || 0,
          cash_tips: parseFloat(scope.find(".cash-editable-distribution").text()) || 0,
          food_sales: parseFloat(scope.find(".food-sales-editable-distribution").text()) || 0,
          beverage_sales: parseFloat(scope.find(".beverage-sales-editable-distribution").text()) || 0,
          tip_sharings: {}
        };
        ref = $(scope).find(".tip-sharing");
        for (i = 0, len = ref.length; i < len; i++) {
          tipSharingLine = ref[i];
          tipSharingId = $(tipSharingLine).attr("data-tip-sharing-id");
          distributionInfo.tip_sharings[tipSharingId] = {
            id: tipSharingId,
            position_type_id: $(tipSharingLine).attr("data-position-id"),
            employee_id: $(tipSharingLine).attr("data-receiver-id"),
            cc_summ: parseFloat($(tipSharingLine).find(".cc-editable").text()) || 0,
            cash_summ: parseFloat($(tipSharingLine).find(".cash-editable").text()) || 0
          };
        }
        return $.ajax({
          url: "/api/tip_sharings/" + tipSharingId,
          method: "patch",
          data: {
            distribution_info: distributionInfo
          },
          success: (function(_this) {
            return function(response) {
              var id, j, len1, ref1, tipSharing;
              Tipmetric.Controllers.spinner.hide();
              scope = $("[data-emp-distribution-id='" + response.distribution_info.id + "']");
              scope.find(".hours-editable").text(response.distribution_info.hours_worked.toFixed(2));
              scope.find(".cc-editable-distribution").text(response.distribution_info.cc_tips.toFixed(2));
              scope.find(".cash-editable-distribution").text(response.distribution_info.cash_tips.toFixed(2));
              scope.find(".final-tips-distributed-cc").text(response.distribution_info.cc_tips_distr_final.toFixed(2));
              scope.find(".final-tips-distributed-cash").text(response.distribution_info.cash_tips_distr_final.toFixed(2));
              ref1 = $(scope).find(".tip-sharing");
              for (j = 0, len1 = ref1.length; j < len1; j++) {
                tipSharingLine = ref1[j];
                id = $(tipSharingLine).attr("data-tip-sharing-id");
                tipSharing = response.tip_sharings[id];
                $(tipSharingLine).find(".cc-editable").text(tipSharing.cc_summ.toFixed(2));
                $(tipSharingLine).find(".cash-editable").text(tipSharing.cash_summ.toFixed(2));
              }
              scope.attr("content-editing-enabled", false);
              scope.find(".hours-editable, .cc-editable-distribution, .cash-editable-distribution, .cc-editable, .cash-editable, .food-sales-editable-distribution, .beverage-sales-editable-distribution").attr("contenteditable", false);
              _this.calculateTotals();
              return toastr["success"]("Distribution updated.", "Success");
            };
          })(this),
          error: function(response) {
            this.calculateTotals();
            Tipmetric.Controllers.spinner.hide();
            return toastr["error"](response.responseJSON.errors, "Error");
          }
        });
      },
      select2Template: function(state) {
        var $state, calc_date, employee_position_status, hidden_from, isActive, position_type_id;
        position_type_id = $(state.element).closest(".tip-sharing").find(".position-name-wrapper .position-name-select select#position_type_id option:selected").val();
        if (!gon.restaurant.hidden_from_enabled) {
          isActive = true;
        } else if (!_.isEmpty(_.findWhere(gon.restaurant_inheritance_data_raw.employee_position_statuses, {
          employee_id: state.id,
          position_type_id: position_type_id
        }))) {
          employee_position_status = _.findWhere(gon.restaurant_inheritance_data_raw.employee_position_statuses, {
            employee_id: state.id,
            position_type_id: position_type_id
          });
          hidden_from = moment(employee_position_status.hidden_from);
          calc_date = moment($("#date").val());
          if (calc_date >= hidden_from) {
            isActive = false;
          } else {
            isActive = true;
          }
        } else {
          isActive = true;
        }
        if (!state.id) {
          return state.text;
        }
        $state = $(("<span data-position-enabled='" + isActive + "'>") + state.text + "</span>");
        return $state;
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("tip-sharing") && gon.calculation_distribution_logic_type === "tip_sharings") {
        return Tipmetric.Controllers.tipSharingToggleView = new TipSharingToggleView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var ToastImportView;
    ToastImportView = Backbone.View.extend({
      el: "#toast-import-view",
      tm_data_fields: [":hours_worked", ":net_sales", ":tip_amount", ":declared", ":food_summ", ":beverage_summ"],
      events: {
        "click [data-action='show-toast-config']": "toggleTokenBlock",
        "change [data-required-field='true']": "checkIfDataValid",
        "change #area_type_id": "areaUpdated",
        "change #shift_type_id": "shiftUpdated",
        "click [data-action='show-toast-shift-select']": "showToastShiftSelect",
        "click #run": "getData",
        "click #import": "sendImportData",
        "click [data-action='show-toast-sto-rules']": "showToastStoRules",
        "blur #position_tip_sharings [data-recalculate='true']": "setPercents",
        "click .import-errors [data-action='remove']": "removeRow",
        "click .import-errors [data-action='add']": "addEmployee",
        "click .import-errors [data-action='match']": "matchEmployee",
        "click .import-errors [data-action='create']": "createEmployee",
        "click .import-errors [data-action='remove-match']": "removeMatchEmployee",
        "click .import-errors [data-action='select-position']": "selectPosition",
        "click .import-errors [data-action='merge-duplications']": "mergeDuplications",
        "click .import-errors [data-action='remove-duplicated-line']": "removeDuplication",
        "click [data-action='remove-row-inline']": "removeRowInline",
        "ifChanged [data-action='toggle-additional-record']": "toggleAdditionalRecord",
        "ifChanged [data-action='toggle-additional-record']": "toggleAdditionalRecord",
        "click [data-action='import-excluded-svc']": "importExcludedSvc",
        "click .import-errors-excluded-svc [data-action='remove']": "removeForm",
        "click .import-errors-excluded-svc [data-action='add']": "addEmployee",
        "click .import-errors-excluded-svc [data-action='create']": "createEmployee"
      },
      initialize: function() {
        gon.restaurant_inheritance_data = Tipmetric.Controllers.helpers.buildSmartData();
        $.fn.editable.defaults.mode = 'inline';
        $.fn.editable.defaults.ajaxOptions = {
          type: "PUT"
        };
        $.fn.editableform.buttons = "<button type=\"submit\" class=\"btn btn-sm btn-success editable-submit\">SAVE</button> <button type=\"button\" class=\"btn btn-sm btn-default editable-cancel\">CANCEL</button>";
        $('#toast-client-id, #toast-client-secret, #toast-use-access-type, ' + '#toast-location-id, #toast-api-access-url').editable({
          type: 'text',
          url: (function(_this) {
            return function(params) {
              var obj;
              return $.ajax({
                url: "api/restaurants/1",
                method: "put",
                id: "1",
                data: {
                  restaurant: (
                    obj = {},
                    obj["" + (params.name.replace(/-/g, "_"))] = params.value,
                    obj
                  )
                },
                success: function(response) {
                  $("#" + params.name).removeAttr("contenteditable");
                  $("#access-token-wrapper").attr("token-form-status", "locked");
                  return toastr["success"]("Access Token updated.", "Success");
                },
                error: function(response) {
                  return toastr["error"](response.responseText, "Error");
                }
              });
            };
          })(this)
        });
        $('.datapicker').datepicker().on("changeDate", (function(_this) {
          return function() {
            return _this.dateUpdated();
          };
        })(this)).on("show", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').addClass('open');
          };
        })(this)).on("hide", (function(_this) {
          return function() {
            return $('.date-selection-arrow b').removeClass('open');
          };
        })(this));
        this.checkIfDataValid();
        this.dateUpdated();
        this.areaUpdated();
        this.shiftUpdated();
        this.bindTimeSelect();
        this.bindTimezoneSelect();
        Tipmetric.Controllers.helpers.bindFilteredRulesSaving(5, function() {
          return Tipmetric.Controllers.toastImportView.buildTable();
        });
        if (gon.position_tip_sharings.length === 0) {
          return this.hideToastStoRules();
        }
      },
      toggleTokenBlock: function(e) {
        return $("#access-token-wrapper-block").toggle();
      },
      toggleAdditionalRecord: function(e) {
        var data;
        data = {
          restaurant: {}
        };
        data.restaurant[$(e.currentTarget).attr("id")] = e.currentTarget.checked;
        return $.ajax({
          url: "api/restaurants/1",
          method: "put",
          id: "1",
          data: data,
          success: function() {
            return console.log("+");
          },
          error: function() {
            return console.log("-");
          }
        });
      },
      dateUpdated: function() {
        var area, areas, dayName, i, len, options, savedVal;
        if (!_.isEmpty(this.$el.find("#date").val())) {
          this.$el.find("#date").attr("data-saved-date", this.$el.find("#date").val());
        }
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        if (gon.restaurant_inheritance_data[dayName]) {
          areas = _.map(gon.restaurant_inheritance_data[dayName].areas, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          options = "";
          options += "<option value=''>Area</option>";
          for (i = 0, len = areas.length; i < len; i++) {
            area = areas[i];
            options += "<option value='" + area.id + "'>" + area.name + "</option>";
          }
          savedVal = this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          this.$el.find("#calculation-form-wrapper").find("select#area_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val(savedVal).trigger("change");
        } else {
          this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val("").trigger("change").html("<option value=''>Area</option>");
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return this.$el.find("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      areaUpdated: function() {
        var areaName, dayName, i, len, options, savedVal, shift, shifts;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option[value='" + (this.$el.find("select#area_type_id").val()) + "']").text();
        if (this.areaEmpty()) {
          return false;
        }
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas[areaName]) {
          shifts = _.map(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts, function(e) {
            return {
              id: e.id,
              name: e.name,
              start_time: e.start_time,
              end_time: e.end_time
            };
          });
          options = "";
          options += "<option value=''>Shift</option>";
          for (i = 0, len = shifts.length; i < len; i++) {
            shift = shifts[i];
            options += "<option value='" + shift.id + "'>" + shift.name + "</option>";
          }
          savedVal = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val(savedVal).trigger("change");
        } else {
          this.$el.find("#calculation-form-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return this.$el.find("#calculation-form-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      shiftUpdated: function() {
        var areaData, areaName, dayName, firstPosition, i, len, options, position, positions, savedVal, shift, shiftId, shiftName;
        dayName = moment(this.$el.find("#date").val()).format('dddd').toLowerCase();
        areaName = this.$el.find("#calculation-form-wrapper").find("select#area_type_id option[value='" + ($('select#area_type_id').val()) + "']").text();
        if (this.areaEmpty()) {
          return false;
        }
        shiftId = this.$el.find("#calculation-form-wrapper").find("select#shift_type_id option:selected").val();
        shift = _.find(_.pairs(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts), function(f) {
          return f[1].id === shiftId;
        });
        if (shift) {
          shiftName = shift[0];
        }
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas && areaName) {
          areaData = gon.restaurant_inheritance_data[dayName].areas[areaName];
        }
        if (areaData && shift && areaData.shifts[shiftName]) {
          positions = _.map(areaData.shifts[shiftName].positions, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          if (_.some(positions)) {
            firstPosition = positions[0].id;
          } else {
            firstPosition = "";
          }
          options = "";
          for (i = 0, len = positions.length; i < len; i++) {
            position = positions[i];
            options += "<option value='" + position.id + "'>" + position.name + "</option>";
          }
          savedVal = this.$el.find("select#source_position_ids").val();
          if (!savedVal) {
            savedVal = firstPosition;
          }
          this.$el.find("select#source_position_ids").html(options);
          return this.$el.find("select#source_position_ids").val(savedVal).trigger("change");
        } else {
          return this.$el.find("select#source_position_ids").val("").trigger("change");
        }
      },
      areaEmpty: function() {
        return _.isEmpty(this.$el.find("#calculation-form-wrapper").find("select#area_type_id").val());
      },
      showToastShiftSelect: function(e) {
        e.preventDefault();
        return $(".toast-shifts-table").toggle();
      },
      bindTimeSelect: function() {
        var dateUpdatedCallback;
        dateUpdatedCallback = (function(_this) {
          return function() {
            return _this.dateUpdated();
          };
        })(this);
        return $('.start-time, .end-time').editable({
          url: function(params) {
            var json;
            json = {};
            json[$(this).attr("data-field-name")] = params.value;
            return $.ajax({
              url: "/api/shift_types/" + params.pk,
              method: "put",
              data: {
                shift_type: json
              },
              success: (function(_this) {
                return function(response) {
                  gon.restaurant_inheritance_data = response.restaurant_inheritance_data;
                  dateUpdatedCallback();
                  return toastr["success"]("successfully updated", "Success");
                };
              })(this),
              error: function(response) {
                return toastr["error"](response.responseText, "Error");
              }
            });
          }
        });
      },
      bindTimezoneSelect: function() {
        return $('.timezone-select').editable({
          type: 'select',
          url: (function(_this) {
            return function(params) {
              return $.ajax({
                url: "/api/shift_types/" + params.pk,
                method: "put",
                data: {
                  shift_type: {
                    toast_timezone: params.value
                  }
                },
                success: function() {
                  return toastr["success"]("successfully updated", "Success");
                },
                error: function(response) {
                  return toastr["error"](response.responseText, "Error");
                }
              });
            };
          })(this),
          prepend: 'not selected',
          source: _.map(_.pairs(gon.timezones), function(f) {
            return {
              value: f[0],
              text: f[0]
            };
          }),
          display: function(value, sourceData) {
            var elem;
            elem = $.grep(sourceData, function(o) {
              return o.value === value;
            });
            return $(this).text(elem[0].text);
          },
          success: function(response, newValue) {
            $(this).attr("data-value", newValue);
            return console.log(newValue);
          },
          error: function(response) {
            return console.log(response);
          }
        });
      },
      hideToastStoRules: function(e) {
        return $(".toast-sto-rules-table-wrapper").toggle();
      },
      showToastStoRules: function(e) {
        if (e) {
          e.preventDefault();
        }
        return $(".toast-sto-rules-table-wrapper").toggle();
      },
      getData: function(e) {
        var data;
        e.preventDefault();
        data = {
          date: moment(this.$el.find("#date").val()).format("MM-DD-YYYY"),
          area_type_id: this.$el.find("#area_type_id").val(),
          shift_type_id: this.$el.find("#shift_type_id").val(),
          position_type: this.$el.find("#source_position_ids").val()
        };
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          url: "/api/import/toast/get_data",
          method: "get",
          data: data,
          success: (function(_this) {
            return function(response) {
              console.log('success');
              _this.lastFetchedData = response;
              _this.buildTable();
              _this.showExcludedServiceCharges();
              $("#import").removeAttr("disabled");
              Tipmetric.Controllers.spinner.hide();
              return _this.setEditablePercents();
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              console.log('error');
              console.log(response);
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this)
        });
      },
      showExcludedServiceCharges: function() {
        var date, html, i, len, results, scope, scopes, value;
        $("#excluded-service-charges tbody").html("");
        $("#excluded-service-charges").addClass("hidden");
        if (!(this.lastFetchedData.total_summs_excluded.zzz || this.lastFetchedData.total_summs_excluded.yyy)) {
          return true;
        }
        scopes = [this.lastFetchedData.total_summs_excluded.zzz, this.lastFetchedData.total_summs_excluded.yyy];
        results = [];
        for (i = 0, len = scopes.length; i < len; i++) {
          scope = scopes[i];
          results.push((function() {
            var j, len1, ref, results1;
            ref = _.keys(scope);
            results1 = [];
            for (j = 0, len1 = ref.length; j < len1; j++) {
              date = ref[j];
              results1.push((function() {
                var k, len2, ref1, results2;
                ref1 = scope[date];
                results2 = [];
                for (k = 0, len2 = ref1.length; k < len2; k++) {
                  value = ref1[k];
                  html = "<tr class='excluded-svc-record' data-date='" + value.date + "' data-position-type-id='' data-emp-id=''>";
                  html += "<td>" + value.paid_date + "</td>";
                  html += "<td>" + value.date + "</td>";
                  html += "<td data-tm-field-name=':ag_svc_tips'>" + value.summ + "</td>";
                  if (value.summ > 0) {
                    html += "<td><button class='btn btn-primary' data-action='import-excluded-svc'>Import</button></td>";
                  } else {
                    html += "<td></td>";
                  }
                  html += "</tr>";
                  $("#excluded-service-charges tbody").append(html);
                  results2.push($("#excluded-service-charges").removeClass("hidden"));
                }
                return results2;
              })());
            }
            return results1;
          })());
        }
        return results;
      },
      buildTable: function() {
        var helpers, html;
        this.resetImportData();
        this.processData();
        this.addTotalEmployeeRecords();
        html = JST["toast_import/employees"](this.lastFetchedData);
        this.$el.find("#employees-wrapper").html(html);
        this.setPercents();
        helpers = Tipmetric.Controllers.helpers;
        helpers.zeroHoursForFilteredRoles($("#built-import-table"), 5);
        helpers.filterImportDataByPosition($("#built-import-table"), 5);
        helpers.filterImportDataByEmployeeId($("#built-import-table"), 2);
        return this.checkIfDataValid();
      },
      resetImportData: function(e) {
        this.$el.find("#csv_file").val("");
        this.$el.find(".csv-import-headers").html("");
        this.$el.find(".csv-import-data").html("");
        this.$el.find(".import-errors > *").html("");
        return this.checkIfDataValid();
      },
      processData: function(e) {
        var beverageSummOfOrders, beverageSummOfOrdersTotal, empId, filteredOrders, i, len, orders, ref, time_entries_with_employees, time_entries_with_food_bev_summ;
        time_entries_with_employees = _.map(this.lastFetchedData.time_entries, (function(_this) {
          return function(te) {
            te.employeeInfo = _.filter(_this.lastFetchedData.employees, function(e) {
              return te.employeeReference.guid === e.guid;
            })[0];
            te.jobInfo = _.filter(_this.lastFetchedData.jobs, function(e) {
              return te.jobReference && te.jobReference.guid === e.guid;
            })[0];
            te.agSvcTips = te.nonCashGratuityServiceCharges + te.cashGratuityServiceCharges;
            te.netSales = te.nonCashSales + te.cashSales;
            return te;
          };
        })(this));
        this.lastFetchedData.time_entries = time_entries_with_employees;
        this.lastFetchedData.bevCategoriesIds = _.collect(_.filter(this.lastFetchedData.sales_categories, function(sc) {
          return _.contains(["Beer", "Wine", "Liquor"], sc.name);
        }), function(sc) {
          return sc.guid;
        });
        filteredOrders = _.filter(this.lastFetchedData.orders, function(o) {
          return o.server !== null;
        });
        this.lastFetchedData.ordersForEmployees = _.groupBy(filteredOrders, function(o) {
          return o.server.guid;
        });
        this.lastFetchedData.ordersForEmployeesWithBeverageSumm = {};
        ref = _.keys(this.lastFetchedData.ordersForEmployees);
        for (i = 0, len = ref.length; i < len; i++) {
          empId = ref[i];
          orders = this.lastFetchedData.ordersForEmployees[empId];
          beverageSummOfOrders = _.collect(orders, (function(_this) {
            return function(o) {
              var checkBeverageSumms, orderSumm;
              checkBeverageSumms = _.collect(o.checks, function(check) {
                var beverageSelections, beverageSelectionsSumm;
                beverageSelections = _.filter(check.selections, function(s) {
                  if (!_.isEmpty(s.salesCategory) && !_.isEmpty(s.salesCategory.guid)) {
                    return _.contains(_this.lastFetchedData.toast_sales_beverage_categories_ids, s.salesCategory.guid);
                  } else {
                    return false;
                  }
                });
                if (!_.isEmpty(beverageSelections)) {
                  beverageSelectionsSumm = _.reduce(_.collect(beverageSelections, function(s) {
                    if (s.voided === false) {
                      return s.price;
                    } else {
                      return 0;
                    }
                  }), function(memo, num) {
                    return memo + num;
                  }) || 0;
                  return beverageSelectionsSumm;
                } else {
                  return 0;
                }
              });
              orderSumm = _.reduce(checkBeverageSumms, function(memo, num) {
                return memo + num;
              }) || 0;
              return orderSumm;
            };
          })(this));
          beverageSummOfOrdersTotal = _.reduce(beverageSummOfOrders, function(memo, num) {
            return memo + num;
          }) || 0;
          this.lastFetchedData.ordersForEmployeesWithBeverageSumm[empId] = beverageSummOfOrdersTotal;
        }
        time_entries_with_food_bev_summ = _.map(this.lastFetchedData.time_entries, (function(_this) {
          return function(te) {
            te.beverage_summ = _this.lastFetchedData.ordersForEmployeesWithBeverageSumm[te.employeeInfo.guid];
            te.food_summ = (te.netSales - te.beverage_summ) || 0;
            return te;
          };
        })(this));
        return this.lastFetchedData.time_entries = time_entries_with_employees;
      },
      addTotalEmployeeRecords: function() {
        var timeEntriesExtended;
        timeEntriesExtended = _.union(this.lastFetchedData.time_entries, this.lastFetchedData.total_employee_records);
        return this.lastFetchedData.time_entries = timeEntriesExtended;
      },
      setPercents: function() {
        var amBartenderId, barbackHoursValues, barbackHoursValuesSumm, barbackId, beverageSalesValues, beverageSalesValuesSumm, bohId, counterKitchenId, netSalesValues, netSalesValuesSumm, percent, pmBartenderId, tr, trs, virtualPoolId;
        if (gon.restaurant.id === "5f04b425c86de264f39b6ac5") {
          virtualPoolId = "5f297a3fc86de24e92d6a0cb";
          counterKitchenId = "5f04b4f7c86de264f17bdf4f";
          amBartenderId = "5f297980c86de24e8fbbd65f";
          pmBartenderId = "5f29798bc86de24e9899fef7";
          barbackId = "5f04b503c86de264eb92faba";
        } else if (gon.restaurant.id === "5f594c6ac86de2163e85b547") {
          virtualPoolId = "5f594ccfc86de2163e85b548";
          counterKitchenId = "5f594cadc86de216411e6c8e";
          amBartenderId = "5f594cbac86de21646e9b2aa";
          pmBartenderId = "5f594cc0c86de216411e6c8f";
          barbackId = "5f594cb3c86de21646e9b2a9";
        } else if (gon.restaurant.id === "5f5be24ec86de2241c3e3597") {
          virtualPoolId = "5f5be2c2c86de2241c3e359b";
          counterKitchenId = "5f5be29bc86de2241fef5d3d";
          amBartenderId = "5f5be2aac86de2241c3e3599";
          pmBartenderId = "5f5be2b0c86de2241c3e359a";
          barbackId = "5f5be2a1c86de2241c3e3598";
        } else if (gon.restaurant.id === "5f5be337c86de22419ecdd7e") {
          virtualPoolId = "5f5be3cbc86de2241fef5d3e";
          counterKitchenId = "5f5be3acc86de2241c3e370a";
          amBartenderId = "5f5be3b8c86de22422f32fc6";
          pmBartenderId = "5f5be3c0c86de22422f32fc7";
          barbackId = "5f5be3b2c86de22422f32fc4";
        } else if (gon.restaurant.id === "5f5be474c86de22422f32fc9") {
          virtualPoolId = "5f5be4bfc86de2241c3e3881";
          counterKitchenId = "5f5be4a0c86de2241c3e3880";
          amBartenderId = "5f5be4aec86de22422f32fcb";
          pmBartenderId = "5f5be4b4c86de22419ecdeee";
          barbackId = "5f5be4a6c86de22422f32fca";
        } else if (gon.restaurant.id === "5f5be4f6c86de22422f32fcd") {
          virtualPoolId = "5f5be556c86de2241fef5d40";
          counterKitchenId = "5f5be531c86de22419ece05f";
          amBartenderId = "5f5be53ec86de22422f33146";
          pmBartenderId = "5f5be547c86de22422f33147";
          barbackId = "5f5be538c86de22422f33145";
          bohId = "5f5fb35ec86de25954b13f3c";
        } else if (gon.restaurant.id === "5fac3174c86de20e17513204") {
          virtualPoolId = "5fac32cec86de20e17513205";
          amBartenderId = "5fac32b6c86de20e0e402d9b";
          pmBartenderId = "5fac32bdc86de20e1438f595";
          counterKitchenId = "5fac32a7c86de20e0e402d9a";
          barbackId = "5fac32aec86de20e116dbde7";
        } else if (gon.restaurant.id === "62019bb3c86de2646c07240a") {
          virtualPoolId = "620c04eac86de274a3c266ff";
          counterKitchenId = "620bf38ac86de274a626f3fb";
          amBartenderId = "62019c63c86de26467654336";
          pmBartenderId = "620c3cb3c86de274a99a7b88";
          barbackId = "62019c73c86de264644119d5";
        } else {
          return false;
        }
        netSalesValues = _.map($(".toast-import-data tr[data-identifier='eod_till']").find("td[data-tm-field-name=':net_sales']"), function(f) {}, parseFloat($(f).text()) || 0);
        netSalesValuesSumm = (_.reduce(netSalesValues, function(sum, num) {
          return sum + num;
        }) || 0).toFixed(2);
        beverageSalesValues = _.map($(".toast-import-data tr[data-identifier='eod_till']").find("td[data-tm-field-name=':beverage_summ']"), function(f) {}, parseFloat($(f).text()) || 0);
        beverageSalesValuesSumm = (_.reduce(beverageSalesValues, function(sum, num) {
          return sum + num;
        }) || 0).toFixed(2);
        if (gon.restaurant.id === "5f5be4f6c86de22422f32fcd") {
          percent = (((netSalesValuesSumm - beverageSalesValuesSumm) / netSalesValuesSumm) * 100).toFixed(2);
        } else {
          percent = (((netSalesValuesSumm - beverageSalesValuesSumm) / netSalesValuesSumm) * 100).toFixed(2);
        }
        tr = $(("tr.position-tip-sharing[data-source-position-id='" + virtualPoolId + "']") + ("[data-target-position-id='" + counterKitchenId + "'][data-source-type='cc']"));
        tr.find("input.percentage").val(percent).trigger("change");
        if (gon.restaurant.id === "5f5be4f6c86de22422f32fcd") {
          percent = (((netSalesValuesSumm - beverageSalesValuesSumm) / netSalesValuesSumm) * 0).toFixed(2);
          tr = $(("tr.position-tip-sharing[data-source-position-id='" + virtualPoolId + "']") + ("[data-target-position-id='" + bohId + "'][data-source-type='cc']"));
          tr.find("input.percentage").val(percent).trigger("change");
        }
        if (gon.restaurant.id !== "62019bb3c86de2646c07240a") {
          trs = _.filter($(".toast-import-data").find("tr"), function(f) {
            return $(f).find("td[data-tm-field-name=':job_code']").text().toLowerCase() === "barback";
          });
          barbackHoursValues = _.map(trs, function(f) {
            return parseFloat($(f).find("td[data-tm-field-name=':hours_worked']").text()) || 0;
          });
          barbackHoursValuesSumm = _.reduce(barbackHoursValues, function(sum, num) {
            return sum + num;
          }) || 0;
          percent = (0.375 * barbackHoursValuesSumm).toFixed(2);
          tr = $(("tr.position-tip-sharing[data-source-position-id='" + amBartenderId + "']") + ("[data-target-position-id='" + barbackId + "'][data-source-type='foodAndBeverageSalesSumm']"));
          tr.find("input.percentage").val(percent).trigger("change");
          tr = $(("tr.position-tip-sharing[data-source-position-id='" + pmBartenderId + "']") + ("[data-target-position-id='" + barbackId + "'][data-source-type='foodAndBeverageSalesSumm']"));
          return tr.find("input.percentage").val(percent).trigger("change");
        }
      },
      setEditablePercents: function() {
        var am_barback_tm_id, am_busser_tm_id, am_foodRunner_tm_id, barback_job_id, barback_tm_id, busser_job_id, busser_tm_id, foodRunner_job_id, foodRunner_tm_id, i, id, j, k, l, len, len1, len2, len3, len4, len5, m, p, percent, ref, ref1, ref2, ref3, ref4, ref5, results, results1, tr, trs;
        gon.updateTikiBartenderToTikiBarbackRule = true;
        if (gon.restaurant.id === "5f5be24ec86de2241c3e3597") {
          tr = $("tr.position-tip-sharing[data-source-position-id='5f88763cc86de2777868a2e8']" + "[data-target-position-id='60b6514ec86de22e9a9d263d'][data-source-type='cc']");
          if (_.isEmpty(_.filter(Tipmetric.Controllers.toastImportView.lastFetchedData.time_entries, function(f) {
            return f.jobReference.guid === "640496834";
          }))) {
            percent = 0;
            tr.find("input.percentage").val(percent).trigger("change");
            gon.updateTikiBartenderToTikiBarbackRule = false;
          } else {
            percent = $(tr).attr("data-original-percentage");
            tr.find("input.percentage").val(percent).trigger("change");
          }
        }
        gon.forbiddenUpdateRulesList = [""];
        gon.settingCalculationRulesToZeroList = [""];
        if (gon.restaurant.id === "62019bb3c86de2646c07240a") {
          foodRunner_tm_id = "620c50cbc86de274a3c270fd";
          barback_tm_id = "620c50e4c86de274a99a7bf9";
          busser_tm_id = "620c50ddc86de274a3c270fe";
          am_foodRunner_tm_id = "62019c67c86de26467654337";
          am_barback_tm_id = "62019c73c86de264644119d5";
          am_busser_tm_id = "62019c6fc86de26467654338";
          foodRunner_job_id = "640450778";
          barback_job_id = "640464375";
          busser_job_id = "4013188";
          if (_.isEmpty(_.filter(Tipmetric.Controllers.toastImportView.lastFetchedData.time_entries, function(f) {
            return f.jobReference.guid === busser_job_id;
          }))) {
            trs = $(("tr.position-tip-sharing[data-source-position-id='" + busser_tm_id + "'], ") + ("tr.position-tip-sharing[data-target-position-id='" + busser_tm_id + "'], ") + ("tr.position-tip-sharing[data-source-position-id='" + am_busser_tm_id + "'], ") + ("tr.position-tip-sharing[data-target-position-id='" + am_busser_tm_id + "']"));
            ref = $(trs);
            for (i = 0, len = ref.length; i < len; i++) {
              tr = ref[i];
              id = $(tr).attr("data-position-tip-sharing-id");
              $(tr).find("input.percentage").val(0).trigger("change");
              gon.forbiddenUpdateRulesList.push(id);
              gon.settingCalculationRulesToZeroList.push(id);
            }
          } else {
            trs = $(("tr.position-tip-sharing[data-source-position-id='" + busser_tm_id + "'], ") + ("tr.position-tip-sharing[data-target-position-id='" + busser_tm_id + "'], ") + ("tr.position-tip-sharing[data-source-position-id='" + am_busser_tm_id + "'], ") + ("tr.position-tip-sharing[data-target-position-id='" + am_busser_tm_id + "']"));
            ref1 = $(trs);
            for (j = 0, len1 = ref1.length; j < len1; j++) {
              tr = ref1[j];
              percent = $(tr).attr("data-original-percentage");
              $(tr).find("input.percentage").val(percent).trigger("change");
            }
          }
          if (_.isEmpty(_.filter(Tipmetric.Controllers.toastImportView.lastFetchedData.time_entries, function(f) {
            return f.jobReference.guid === foodRunner_job_id;
          }))) {
            trs = $(("tr.position-tip-sharing[data-source-position-id='" + foodRunner_tm_id + "'], ") + ("tr.position-tip-sharing[data-target-position-id='" + foodRunner_tm_id + "'], ") + ("tr.position-tip-sharing[data-source-position-id='" + am_foodRunner_tm_id + "'], ") + ("tr.position-tip-sharing[data-target-position-id='" + am_foodRunner_tm_id + "']"));
            ref2 = $(trs);
            for (k = 0, len2 = ref2.length; k < len2; k++) {
              tr = ref2[k];
              id = $(tr).attr("data-position-tip-sharing-id");
              $(tr).find("input.percentage").val(0).trigger("change");
              gon.forbiddenUpdateRulesList.push(id);
              gon.settingCalculationRulesToZeroList.push(id);
            }
          } else {
            trs = $(("tr.position-tip-sharing[data-source-position-id='" + foodRunner_tm_id + "'], ") + ("tr.position-tip-sharing[data-target-position-id='" + foodRunner_tm_id + "'], ") + ("tr.position-tip-sharing[data-source-position-id='" + am_foodRunner_tm_id + "'], ") + ("tr.position-tip-sharing[data-target-position-id='" + am_foodRunner_tm_id + "']"));
            ref3 = $(trs);
            for (l = 0, len3 = ref3.length; l < len3; l++) {
              tr = ref3[l];
              percent = $(tr).attr("data-original-percentage");
              $(tr).find("input.percentage").val(percent).trigger("change");
            }
          }
          if (_.isEmpty(_.filter(Tipmetric.Controllers.toastImportView.lastFetchedData.time_entries, function(f) {
            return f.jobReference.guid === barback_job_id;
          }))) {
            trs = $(("tr.position-tip-sharing[data-source-position-id='" + barback_tm_id + "'], ") + ("tr.position-tip-sharing[data-target-position-id='" + barback_tm_id + "'], ") + ("tr.position-tip-sharing[data-source-position-id='" + am_barback_tm_id + "'], ") + ("tr.position-tip-sharing[data-target-position-id='" + am_barback_tm_id + "']"));
            ref4 = $(trs);
            results = [];
            for (m = 0, len4 = ref4.length; m < len4; m++) {
              tr = ref4[m];
              id = $(tr).attr("data-position-tip-sharing-id");
              $(tr).find("input.percentage").val(0).trigger("change");
              gon.forbiddenUpdateRulesList.push(id);
              results.push(gon.settingCalculationRulesToZeroList.push(id));
            }
            return results;
          } else {
            trs = $(("tr.position-tip-sharing[data-source-position-id='" + barback_tm_id + "'], ") + ("tr.position-tip-sharing[data-target-position-id='" + barback_tm_id + "'], ") + ("tr.position-tip-sharing[data-source-position-id='" + am_barback_tm_id + "'], ") + ("tr.position-tip-sharing[data-target-position-id='" + am_barback_tm_id + "']"));
            ref5 = $(trs);
            results1 = [];
            for (p = 0, len5 = ref5.length; p < len5; p++) {
              tr = ref5[p];
              percent = $(tr).attr("data-original-percentage");
              results1.push($(tr).find("input.percentage").val(percent).trigger("change"));
            }
            return results1;
          }
        }
      },
      removeRowInline: function(e) {
        $(e.currentTarget).closest('tr').remove();
        return this.setPercents();
      },
      buildImportJson: function() {
        var employeeData, employeeTr, fieldName, i, id, j, len, len1, position_type_id, ref, ref1, rowNumber, td, value;
        this.data = {
          employeesData: [],
          frontend_restaurant_id: gon.restaurant.id,
          update_tikiBartender_to_tiki_barback_rule: gon.updateTikiBartenderToTikiBarbackRule,
          forbiddenUpdateRulesList: gon.forbiddenUpdateRulesList,
          settingCalculationRulesToZeroList: gon.settingCalculationRulesToZeroList
        };
        this.buildSTORules();
        this.data.calcualtionParams = {
          date: this.$el.find("#date").val(),
          area_type_id: this.$el.find("select#area_type_id").val(),
          shift_type_id: this.$el.find("select#shift_type_id").val(),
          source_position_type_ids: this.$el.find("select#source_position_ids").val(),
          teams_quantity: 1
        };
        ref = $(".toast-import-data tr");
        for (i = 0, len = ref.length; i < len; i++) {
          employeeTr = ref[i];
          employeeData = {};
          id = $(employeeTr).attr("data-emp-id");
          if (!_.isEmpty(id)) {
            employeeData[":emp_id"] = id;
          }
          position_type_id = $(employeeTr).attr("data-position-type-id");
          if (!_.isEmpty(position_type_id)) {
            employeeData["position_type_id"] = position_type_id;
          }
          rowNumber = $(employeeTr).attr("data-row-number");
          employeeData["row_number"] = rowNumber;
          ref1 = $(employeeTr).find("td");
          for (j = 0, len1 = ref1.length; j < len1; j++) {
            td = ref1[j];
            fieldName = $(td).attr("data-tm-field-name");
            value = $(td).text();
            if (!_.isEmpty(fieldName) && !_.isEmpty(value)) {
              employeeData[fieldName] = value;
            }
          }
          this.data.employeesData.push(employeeData);
        }
        return this.data;
      },
      buildSTORules: function() {
        var i, len, positionsThatReceiveTipSharingIds, ref, results, tr;
        this.data.sourceTipSharings = _.map($("#position_tip_sharings .position-tip-sharing"), (function(_this) {
          return function(tr) {
            return {
              positionTipSharingId: $(tr).attr("data-position-tip-sharing-id"),
              sourcePositionId: $(tr).find("select.source-position-select").val(),
              percentage: parseFloat($(tr).find("input.percentage").val()),
              sourceType: $(tr).find("select.source-type-select").val(),
              targetPositionId: $(tr).find("select.target-position-select").val(),
              tempId: $(tr).attr("data-temp-id")
            };
          };
        })(this));
        positionsThatReceiveTipSharingIds = _.uniq(_.map($("#position_tip_sharings select.target-position-select"), function(f) {
          return $(f).val();
        }));
        this.data.sourceTipSharingsHash = {};
        ref = _.map($("#position_tip_sharings .position-tip-sharing"));
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          tr = ref[i];
          results.push(this.data.sourceTipSharingsHash[$(tr).attr("data-temp-id")] = {
            positionTipSharingId: $(tr).attr("data-position-tip-sharing-id"),
            sourcePositionId: $(tr).find("select.source-position-select").val(),
            sourcePositionName: $(tr).find("select.source-position-select option:selected").text(),
            percentage: parseFloat($(tr).find("input.percentage").val()),
            sourceType: $(tr).find("select.source-type-select").val(),
            targetPositionId: $(tr).find("select.target-position-select").val(),
            tempId: $(tr).attr("data-temp-id"),
            employeesAppliedTo: []
          });
        }
        return results;
      },
      checkIfDataValid: function(e) {
        var blankFields, data, fields;
        fields = $("#calculation-form-wrapper").find("input[name='date'], select[name='area_type_id'], " + "select[name='shift_type_id'], select[id='source_position_ids'], " + "select[name='teams_quantity']");
        blankFields = _.filter(fields, function(e) {
          return $(e).val() === null || $(e).val().length === 0 || $(e).val().length === void 0;
        }) || [];
        data = this.buildImportJson();
        if (blankFields.length === 0) {
          return $("#import-csv").removeAttr("disabled");
        } else {
          return $("#import-csv").attr("disabled", "disabled");
        }
      },
      sendImportData: function(e) {
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return false;
        }
        e.preventDefault();
        window.toast_json = this.buildImportJson();
        return this.sendCheckToastImportDataRequest(false, void 0);
      },
      sendCheckToastImportDataRequest: function(allowUpdate, e) {
        var data;
        if (e && $(e.currentTarget).attr("disabled") !== void 0) {
          return false;
        }
        data = this.buildImportJson();
        data.allowUpdate = allowUpdate;
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          method: "post",
          url: "/api/import/toast/check_data",
          data: data,
          success: (function(_this) {
            return function(response) {
              console.log('success');
              console.log(response);
              _this.matchIdentifiedemployees(response);
              _this.$el.find(".import-errors > *").html('');
              return _this.sendImportToastRequest(allowUpdate);
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              var duplications, employee, employeeEmpId, html1, html3, html4, html5, html6, i, j, k, l, len, len1, len2, len3, len4, m, ref, ref1, ref2, ref3, ref4;
              console.log('error');
              window.response = response;
              _this.matchIdentifiedemployees(response.responseJSON);
              Tipmetric.Controllers.spinner.hide();
              Tipmetric.TestSupport.displayErrors(response.responseJSON.errors);
              if (response.responseJSON.critical_error) {
                swal({
                  title: "",
                  text: response.responseJSON.critical_error_message,
                  type: "error",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "OK",
                  showCancelButton: false
                });
                return;
              }
              if (response.responseJSON.calculation_exists === true && allowUpdate === false) {
                return swal({
                  title: "",
                  text: response.responseJSON.errors,
                  type: "info",
                  confirmButtonColor: "#c30",
                  confirmButtonText: "Continue",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirmed) {
                  if (isConfirmed.value) {
                    return _this.sendCheckToastImportDataRequest(true, e);
                  }
                });
              } else {
                if (response.responseJSON.errors) {
                  toastr["error"](response.responseJSON.errors, "Error");
                  return;
                }
                html1 = '';
                ref = response.responseJSON.employees_doesnt_exist;
                for (i = 0, len = ref.length; i < len; i++) {
                  employee = ref[i];
                  html1 += JST["csv_import/does_not_exist"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-exist").html(html1);
                html3 = '';
                ref1 = response.responseJSON.name_duplication;
                for (j = 0, len1 = ref1.length; j < len1; j++) {
                  employee = ref1[j];
                  html3 += JST["csv_import/last_name_duplication"](employee);
                }
                _this.$el.find(".import-errors .last-name-duplication").html(html3);
                html4 = '';
                ref2 = response.responseJSON.employee_in_multiple_positions;
                for (k = 0, len2 = ref2.length; k < len2; k++) {
                  employee = ref2[k];
                  html4 += JST["csv_import/employee_in_multiple_positions"](employee);
                }
                _this.$el.find(".import-errors .employee-in-multiple-positions").html(html4);
                html5 = '';
                ref3 = response.responseJSON.employees_doesnt_belong_to_any_position;
                for (l = 0, len3 = ref3.length; l < len3; l++) {
                  employee = ref3[l];
                  html5 += JST["csv_import/employees_doesnt_belong_to_any_position"](employee);
                }
                _this.$el.find(".import-errors .employees-doesnt-belong-to-any-position").html(html5);
                html6 = '';
                ref4 = _.keys(response.responseJSON.duplicated_employees_grouped);
                for (m = 0, len4 = ref4.length; m < len4; m++) {
                  employeeEmpId = ref4[m];
                  duplications = _.map(response.responseJSON.duplicated_employees_grouped[employeeEmpId], function(f) {
                    f.filledFields = _this.filledFields(f);
                    return f;
                  });
                  html6 += JST["csv_import/duplicated_employee"](duplications);
                }
                _this.$el.find(".import-errors .employees-are-duplicated").html(html6);
                return toastr["error"]("Looks like import was not successfull.", "Error");
              }
            };
          })(this)
        });
      },
      resetImportData: function(e) {
        this.$el.find("#built-import-table").remove();
        return this.$el.find(".import-errors > *").html("");
      },
      sendImportToastRequest: function(allowUpdate) {
        this.buildImportJson();
        this.data.allowUpdate = allowUpdate;
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          method: "post",
          url: "/api/import/toast/send_data",
          data: this.data,
          success: (function(_this) {
            return function(response) {
              toastr["success"]("Data has been imported.", "Success");
              Tipmetric.Controllers.spinner.hide();
              return _this.resetImportData();
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              Tipmetric.Controllers.spinner.hide();
              console.log(response);
              if (response.responseJSON.calculation_exists === true) {
                return swal({
                  title: "",
                  text: response.responseJSON.errors,
                  type: "info",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "Yes",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirmed) {
                  if (isConfirmed.value) {
                    return _this.sendImportToastRequest(true);
                  }
                });
              } else {
                return toastr["error"]("Data is not imported.", "Error");
              }
            };
          })(this)
        });
      },
      matchIdentifiedemployees: function(response) {
        var employee, i, len, ref, results, tr;
        window.response = response;
        ref = response.identified_employees;
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          employee = ref[i];
          tr = this.$el.find(".toast-import-data tr[data-row-number='" + employee.row_number + "']");
          results.push(tr.attr("data-emp-id", employee.employee_data.emp_id));
        }
        return results;
      },
      importExcludedSvc: function(e) {
        return this.importExcludedSvcRequest(e, false);
      },
      importExcludedSvcRequest: function(e, allowUpdate) {
        var currentRecord, data, toast_svc_record;
        e.preventDefault();
        data = this.buildImportJson();
        currentRecord = $(e.currentTarget).closest(".excluded-svc-record");
        toast_svc_record = {};
        toast_svc_record[":first_name"] = "TOAST";
        toast_svc_record[":last_name"] = "SVC";
        toast_svc_record[":ag_svc_tips"] = currentRecord.find("[data-tm-field-name=':ag_svc_tips']").text();
        toast_svc_record["date"] = currentRecord.attr("data-date");
        data.calcualtionParams.date = currentRecord.attr("data-date");
        data.employeesData = [toast_svc_record];
        data.allowUpdate = allowUpdate;
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          method: "post",
          url: "/api/import/toast_excluded/send_data",
          data: data,
          success: (function(_this) {
            return function(response) {
              console.log('success');
              toastr["success"](response.message, "Success");
              currentRecord.remove();
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              var employee, html1;
              console.log('error');
              window.response = response;
              Tipmetric.Controllers.spinner.hide();
              if (response.responseJSON.error_type === "distribution_exists") {
                return swal({
                  title: "",
                  text: response.responseJSON.error,
                  type: "info",
                  confirmButtonColor: "#c30",
                  confirmButtonText: "Continue",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirmed) {
                  if (isConfirmed.value) {
                    return _this.importExcludedSvcRequest(e, true);
                  }
                });
              } else if (response.responseJSON.error_type === "no_employee") {
                html1 = '';
                employee = response.responseJSON.employee_doesnt_exist;
                html1 += JST["csv_import/does_not_exist"](employee);
                return _this.$el.find(".import-errors-excluded-svc .employees-doesnt-exist").html(html1);
              } else {
                return swal({
                  title: "",
                  text: response.responseJSON.error,
                  type: "error",
                  confirmButtonColor: "#28C256",
                  confirmButtonText: "OK",
                  showCancelButton: false
                });
              }
            };
          })(this)
        });
      },
      matchEmployee: function(e) {
        var currentParagraph, empId, empName, lineNumber, positionId, tr;
        currentParagraph = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper");
        empId = $(currentParagraph).find("select.employee-select option:selected").val();
        empName = $(currentParagraph).find("select.employee-select option:selected").text();
        positionId = $(currentParagraph).find("select.employee-select option:selected").attr("position-id");
        lineNumber = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        tr.attr("data-emp-id", empId);
        tr.attr("data-position-type-id", positionId);
        return $(currentParagraph).remove();
      },
      removeRow: function(e) {
        var row, rowNumber;
        rowNumber = $(e.currentTarget).attr("data-row-number");
        row = this.$el.find("tr[data-row-number='" + rowNumber + "']");
        row.remove();
        $(e.currentTarget).closest(".doesnt-exist-warning-wrapper").remove();
        return this.setPercents();
      },
      removeForm: function(e) {
        return $(e.currentTarget).closest(".doesnt-exist-warning-wrapper").remove();
      },
      removeDuplication: function(e) {
        var currentParagraph, duplicationBlock, lineNumber, tr;
        duplicationBlock = $(e.currentTarget).closest(".employee-is-duplicated");
        currentParagraph = $(e.currentTarget).closest(".employee-duplication-line");
        lineNumber = $(e.currentTarget).attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        $(tr).remove();
        $(currentParagraph).remove();
        if ($(duplicationBlock).find(".employee-duplication-line").length === 0) {
          $(duplicationBlock).remove();
        }
        return this.setPercents();
      },
      mergeDuplications: function(e) {
        var dataType, duplicationBlock, empId, finalAddedNumbers, finalFilledLine, i, j, k, len, len1, len2, lineNumbers, lineToRemove, lines, otherLines, summ, tableRows, td, tm_data_fields_to_add, tr, values;
        duplicationBlock = $(e.currentTarget).closest(".employee-is-duplicated");
        lines = $(duplicationBlock).find(".employee-duplication-line");
        lineNumbers = _.collect(lines, function(line) {
          return $(line).attr("data-line-number");
        });
        empId = $(duplicationBlock).attr("data-emp-id");
        tm_data_fields_to_add = this.tm_data_fields;
        finalAddedNumbers = {};
        tableRows = _.filter($(".toast-import-data tr[data-emp-id='" + empId + "']"), function(f) {
          return _.contains(lineNumbers, $(f).attr("data-row-number"));
        });
        for (i = 0, len = tm_data_fields_to_add.length; i < len; i++) {
          dataType = tm_data_fields_to_add[i];
          values = _.map($(tableRows).find("td[data-tm-field-name='" + dataType + "']"), function(f) {
            return parseFloat($(f).text()) || 0;
          });
          if (!_.isEmpty(values)) {
            summ = _.reduce(values, function(memo, num) {
              return memo + num;
            }) || 0;
            finalAddedNumbers[dataType] = summ.toFixed(2);
          }
        }
        finalFilledLine = lineNumbers[0];
        for (j = 0, len1 = tm_data_fields_to_add.length; j < len1; j++) {
          dataType = tm_data_fields_to_add[j];
          if (finalAddedNumbers[dataType]) {
            td = $((".toast-import-data tr[data-emp-id='" + empId + "'][data-row-number='" + finalFilledLine + "'] ") + ("td[data-tm-field-name='" + dataType + "'] "));
            $(td).attr("data-value", finalAddedNumbers[dataType]);
            $(td).text(finalAddedNumbers[dataType]);
          }
        }
        otherLines = _.filter(lineNumbers, function(n) {
          return n !== finalFilledLine;
        });
        for (k = 0, len2 = otherLines.length; k < len2; k++) {
          lineToRemove = otherLines[k];
          tr = this.$el.find("tr[data-emp-id='" + empId + "'][data-row-number='" + lineToRemove + "']");
          tr.remove();
        }
        $(duplicationBlock).remove();
        return this.setPercents();
      },
      selectPosition: function(e) {
        var currentParagraph, lineNumber, positionId, tr;
        currentParagraph = $(e.currentTarget).closest(".employee-in-multiple-positions-wrapper");
        positionId = $(currentParagraph).find("select.position-type-select option:selected").val();
        lineNumber = $(e.currentTarget).closest(".employee-in-multiple-positions-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        tr.attr("data-position-type-id", positionId);
        $(currentParagraph).remove();
        return this.setPercents();
      },
      removeMatchEmployee: function(e) {
        var currentParagraph, lineNumber, tr;
        currentParagraph = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper, " + ".employees-doesnt-belong-to-any-position-wrapper");
        lineNumber = $(e.currentTarget).closest(".last-name-duplication-warning-wrapper, .employees-doesnt-belong-to-any-position-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        $(tr).remove();
        $(currentParagraph).remove();
        return this.setPercents();
      },
      addEmployee: function(e) {
        var currentParagraph;
        currentParagraph = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        return currentParagraph.find(".doesnt-exist-warning-form").show();
      },
      createEmployee: function(e) {
        var currentParagraph, empId, firstName, lastName, positionId, toastId;
        e.preventDefault();
        currentParagraph = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        positionId = $(e.currentTarget).closest(".doesnt-exist-warning-wrapper").find("select.position-type-select option:selected").val();
        empId = $(currentParagraph).find("input[name='emp_id']").val();
        toastId = $(currentParagraph).attr("data-toast-id");
        firstName = $(currentParagraph).find("input[name='first_name']").val();
        lastName = $(currentParagraph).find("input[name='last_name']").val();
        return $.ajax({
          url: "api/employees",
          method: "post",
          data: {
            employee: {
              emp_id: empId,
              first_name: firstName,
              toast_id: toastId,
              last_name: lastName,
              position_type_ids: [positionId]
            }
          },
          success: (function(_this) {
            return function(response) {
              $(currentParagraph).remove();
              toastr["success"]("Emloyee has been created.", "Success");
              return _this.setPercents();
            };
          })(this),
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      filledFields: function(data) {
        return data[':hours_worked'] + " (" + ':hours_worked' + ")";
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("toast-import-view")) {
        return Tipmetric.Controllers.toastImportView = new ToastImportView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var TransferEmployeesFormView;
    TransferEmployeesFormView = Backbone.View.extend({
      el: "#transfer-employees-view",
      events: {
        "click .transfer-employees-data": "getEmployeeCalculations",
        "change #transfer-employees-form-wrapper .form-control": "switchTransferButton"
      },
      initialize: function() {
        return this.switchTransferButton();
      },
      buildTransferJson: function() {
        var json;
        json = {
          frontend_restaurant_id: gon.restaurant.id
        };
        json.employeeParams = {
          date_from: $("#transfer-employees-form-wrapper").find("#date_from").val(),
          date_to: $("#transfer-employees-form-wrapper").find("#date_to").val(),
          previous_employee_id: $("#transfer-employees-form-wrapper").find("#previous_employee_id").val()
        };
        return json;
      },
      switchTransferButton: function(e) {
        var json;
        json = this.buildTransferJson();
        if (_.isEmpty(json.employeeParams.date_from) || _.isEmpty(json.employeeParams.date_to) || _.isEmpty(json.employeeParams.previous_employee_id)) {
          return $(".transfer-employees-data").attr("disabled", "disabled");
        } else {
          return $(".transfer-employees-data").removeAttr("disabled");
        }
      },
      getEmployeeCalculations: function(e) {
        var data;
        e.preventDefault();
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return true;
        }
        data = this.buildTransferJson();
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          method: "post",
          url: "/api/calculations/transfer_employees_data",
          data: data,
          success: function(response) {
            var calculation, html, i, len, ref;
            $("#transfer-calculations").html("");
            ref = response.calculations;
            for (i = 0, len = ref.length; i < len; i++) {
              calculation = ref[i];
              html = JST["csv_import/transfer_employees_calculation"](calculation);
              $("#transfer-calculations").append(html);
            }
            return Tipmetric.Controllers.spinner.hide();
          },
          error: (function(_this) {
            return function(response) {
              toastr["error"]("Something went wrong.", "Error");
              window.r = response.responseJSON;
              return Tipmetric.Controllers.spinner.hide();
            };
          })(this)
        });
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("csv-import-view")) {
        return Tipmetric.Controllers.transferEmployeesFormView = new TransferEmployeesFormView();
      }
    });
  })();

}).call(this);
(function() {
  (function() {
    var TransferFormView;
    TransferFormView = Backbone.View.extend({
      el: "#transfer-view",
      events: {
        "change #area_type_id": "areaUpdated",
        "change #shift_type_id": "shiftUpdated",
        "change [data-required-field='true']": "checkIfDataValid",
        "change .changeble-data select": "calculationChanged",
        "blur #date": "dateBlured",
        "click .import-csv": "checkCsvImportData",
        "click .import-errors [data-action='remove']": "removeRow",
        "click .import-errors [data-action='create']": "createEmployee",
        "click .import-errors [data-action='add']": "addEmployee",
        "click .import-errors [data-action='match']": "matchEmployee",
        "click .import-errors [data-action='remove-match']": "removeMatchEmployee",
        "click .import-errors [data-action='select-position']": "selectPosition",
        "click .import-errors [data-action='link-to-position']": "linkEmployeeToPosition",
        "click button[data-calculation-action]": "chooseCalculationVariant",
        "click .date-selection-arrow b": "showDatePicker",
        "click .transfer-calculation-data": "trasferData",
        "change #transfer-form-wrapper .form-control": "switchTransferButton"
      },
      initialize: function() {
        gon.restaurant_inheritance_data = Tipmetric.Controllers.helpers.buildSmartData();
        return this.$el.find('.datapicker').datepicker({
          todayHighlight: true
        }).on("changeDate", (function(_this) {
          return function(e) {
            return _this.dateUpdated(e);
          };
        })(this)).on("show", (function(_this) {
          return function() {
            return _this.$el.find('.date-selection-arrow b').addClass('open');
          };
        })(this)).on("hide", (function(_this) {
          return function() {
            return _this.$el.find('.date-selection-arrow b').removeClass('open');
          };
        })(this));
      },
      importCsv: function(e) {
        var data;
        if (e) {
          e.preventDefault();
        }
        data = Tipmetric.Controllers.csvImportView.buildImportJson();
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          method: "post",
          url: "/api/calculations/import_csv",
          data: data,
          success: function(response) {
            toastr["success"]("Data has been imported.", "Success");
            Tipmetric.Controllers.spinner.hide();
            return Tipmetric.Controllers.csvImportView.resetImportData();
          },
          error: function(response) {
            toastr["error"](response.responseJSON.errors, "Error");
            return Tipmetric.Controllers.spinner.hide();
          }
        });
      },
      checkCsvImportData: function(e) {
        var data, selectedValues;
        e.preventDefault();
        if (this.$el.find(e.currentTarget).attr("disabled") !== void 0) {
          return false;
        }
        data = Tipmetric.Controllers.csvImportView.buildImportJson();
        selectedValues = _.filter(_.map(this.$el.find(".csv-import-headers th select option:selected"), (function(_this) {
          return function(e) {
            return _this.$el.find(e).val();
          };
        })(this)), function(f) {
          return !_.isEmpty(f);
        });
        if (!_.contains(selectedValues, ":last_name") && !_.contains(selectedValues, ":emp_id") && !_.contains(selectedValues, ":first_name :last_name") && !_.contains(selectedValues, ":last_name, :first_name")) {
          toastr["error"]("Cannot Import...Please select an Emp ID or Last Name", "Error");
          return false;
        }
        return $.ajax({
          method: "post",
          url: "/api/calculations/check_csv_import_data",
          data: data,
          success: (function(_this) {
            return function(response) {
              _this.matchIdentifiedemployees(response);
              _this.$el.find(".import-errors > *").html('');
              return _this.importCsv();
            };
          })(this),
          error: (function(_this) {
            return function(response) {
              var employee, html1, html3, html4, html5, i, j, k, l, len, len1, len2, len3, ref, ref1, ref2, ref3;
              window.response = response;
              _this.matchIdentifiedemployees(response.responseJSON);
              if (response.responseJSON.errors) {
                toastr["error"](response.responseJSON.errors, "Error");
                return;
              }
              html1 = '';
              ref = response.responseJSON.employees_doesnt_exist;
              for (i = 0, len = ref.length; i < len; i++) {
                employee = ref[i];
                html1 += JST["csv_import/does_not_exist"](employee);
              }
              _this.$el.find(".import-errors .employees-doesnt-exist").html(html1);
              html3 = '';
              ref1 = response.responseJSON.last_name_duplication;
              for (j = 0, len1 = ref1.length; j < len1; j++) {
                employee = ref1[j];
                html3 += JST["csv_import/last_name_duplication"](employee);
              }
              _this.$el.find(".import-errors .last-name-duplication").html(html3);
              html4 = '';
              ref2 = response.responseJSON.employee_in_multiple_positions;
              for (k = 0, len2 = ref2.length; k < len2; k++) {
                employee = ref2[k];
                html4 += JST["csv_import/employee_in_multiple_positions"](employee);
              }
              _this.$el.find(".import-errors .employee-in-multiple-positions").html(html4);
              html5 = '';
              ref3 = response.responseJSON.employees_doesnt_belong_to_any_position;
              for (l = 0, len3 = ref3.length; l < len3; l++) {
                employee = ref3[l];
                html5 += JST["csv_import/employees_doesnt_belong_to_any_position"](employee);
              }
              _this.$el.find(".import-errors .employees-doesnt-belong-to-any-position").html(html5);
              return toastr["error"]("Looks like import was not successfull.", "Error");
            };
          })(this)
        });
      },
      matchIdentifiedemployees: function(response) {
        var employee, i, len, ref, results, tr;
        window.response = response;
        ref = response.identified_employees;
        results = [];
        for (i = 0, len = ref.length; i < len; i++) {
          employee = ref[i];
          tr = this.$el.find(".csv-import-data tr[data-row-number='" + employee.row_number + "']");
          results.push(tr.attr("data-emp-id", employee.employee_data.emp_id));
        }
        return results;
      },
      removeRow: function(e) {
        var empId, firstAndLastName, lastAndFirstName, row, rowNumber;
        empId = this.$el.find(e.currentTarget).attr("data-emp-id");
        rowNumber = this.$el.find(e.currentTarget).attr("data-row-number");
        firstAndLastName = this.$el.find(e.currentTarget).attr("data-first-and-last-name");
        lastAndFirstName = this.$el.find(e.currentTarget).attr("data-last-and-fist-name");
        if (empId) {
          row = this.$el.find("td[data-tm-field-name=':emp_id'][data-value='" + empId + "']").closest("tr");
        } else if (rowNumber) {
          row = this.$el.find("tr[data-row-number='" + rowNumber + "']");
        } else if (firstAndLastName) {
          row = this.$el.find("td[data-tm-field-name=':first_name :last_name'][data-value='" + firstAndLastName + "']").closest("tr");
        } else if (lastAndFirstName) {
          row = this.$el.find("td[data-tm-field-name=':last_name, :first_name'][data-value='" + lastAndFirstName + "']").closest("tr");
        }
        row.remove();
        return this.$el.find(e.currentTarget).closest(".doesnt-exist-warning-wrapper").remove();
      },
      matchEmployee: function(e) {
        var currentParagraph, empId, empName, lineNumber, positionId, tr;
        currentParagraph = this.$el.find(e.currentTarget).closest(".last-name-duplication-warning-wrapper");
        empId = this.$el.find(currentParagraph).find("select.employee-select option:selected").val();
        empName = this.$el.find(currentParagraph).find("select.employee-select option:selected").text();
        positionId = this.$el.find(currentParagraph).find("select.employee-select option:selected").attr("position-id");
        lineNumber = this.$el.find(e.currentTarget).closest(".last-name-duplication-warning-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        tr.attr("data-emp-id", empId);
        tr.attr("data-position-type-id", positionId);
        this.$el.find(".last-name-duplication select option[value='" + empId + "']").remove();
        return this.$el.find(currentParagraph).remove();
      },
      removeMatchEmployee: function(e) {
        var currentParagraph, lineNumber, tr;
        currentParagraph = this.$el.find(e.currentTarget).closest(".last-name-duplication-warning-wrapper, .employees-doesnt-belong-to-any-position-wrapper");
        lineNumber = this.$el.find(e.currentTarget).closest(".last-name-duplication-warning-wrapper, .employees-doesnt-belong-to-any-position-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        this.$el.find(tr).remove();
        return this.$el.find(currentParagraph).remove();
      },
      selectPosition: function(e) {
        var currentParagraph, lineNumber, positionId, tr;
        currentParagraph = this.$el.find(e.currentTarget).closest(".employee-in-multiple-positions-wrapper");
        positionId = this.$el.find(currentParagraph).find("select.position-type-select option:selected").val();
        lineNumber = this.$el.find(e.currentTarget).closest(".employee-in-multiple-positions-wrapper").attr("data-line-number");
        tr = this.$el.find("td[data-tm-field-name='employee-line-number'][data-tm-field-val='" + lineNumber + "']").closest("tr");
        tr.attr("data-position-type-id", positionId);
        return this.$el.find(currentParagraph).remove();
      },
      addEmployee: function(e) {
        var currentParagraph;
        currentParagraph = this.$el.find(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        return currentParagraph.find(".doesnt-exist-warning-form").show();
      },
      createEmployee: function(e) {
        var currentParagraph, empId, firstName, lastName, positionId;
        e.preventDefault();
        currentParagraph = this.$el.find(e.currentTarget).closest(".doesnt-exist-warning-wrapper");
        positionId = this.$el.find(e.currentTarget).closest(".doesnt-exist-warning-wrapper").find("select.position-type-select option:selected").val();
        empId = this.$el.find(currentParagraph).find("input[name='emp_id']").val();
        firstName = this.$el.find(currentParagraph).find("input[name='first_name']").val();
        lastName = this.$el.find(currentParagraph).find("input[name='last_name']").val();
        return $.ajax({
          url: "api/employees",
          method: "post",
          data: {
            employee: {
              emp_id: empId,
              first_name: firstName,
              last_name: lastName,
              position_type_ids: [positionId]
            }
          },
          success: function(response) {
            this.$el.find(currentParagraph).remove();
            return toastr["success"]("Emloyee has been created.", "Success");
          },
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      linkEmployeeToPosition: function(e) {
        var currentParagraph, empId, positionId;
        e.preventDefault();
        currentParagraph = this.$el.find(e.currentTarget).closest(".employees_doesnt_belong_to_position");
        empId = this.$el.find(e.currentTarget).attr("data-emp-id");
        positionId = this.$el.find(e.currentTarget).attr("data-position-id");
        return $.ajax({
          url: "api/employees/add_position",
          method: "patch",
          data: {
            empId: empId,
            position_type_id: positionId
          },
          success: (function(_this) {
            return function(response) {
              _this.$el.find(currentParagraph).remove();
              return toastr["success"]("Emloyee has been added to position.", "Success");
            };
          })(this),
          error: function(response) {
            return toastr["error"](response.responseText, "Error");
          }
        });
      },
      hidePopover: function() {
        return this.$el.find("[data-toggle='popover']").popover('hide');
      },
      showCalculation: function(e) {
        if (this.$el.find(e.currentTarget).attr("disabled") === "disabled") {
          return false;
        }
        e.preventDefault();
        if (Tipmetric.Controllers.calculationView && Tipmetric.Controllers.calculationView.locked) {
          return false;
        }
        if (this.$el.find(".changeble-data").hasClass("changed")) {
          return this.updateCalculation(e);
        } else {
          return this.checkCalculation();
        }
      },
      showDatePicker: function(e) {
        e.preventDefault();
        return this.$el.find('#date').focus();
      },
      dateUpdated: function(e) {
        var area, areas, dayName, i, len, options, savedVal;
        if (!_.isEmpty(this.$el.find("#date").val())) {
          $(e.currentTarget).closest(".params-scope-wrapper").find("#date").attr("data-saved-date", $(e.currentTarget).closest(".params-scope-wrapper").find("#date").val());
        }
        dayName = moment($(e.currentTarget).closest(".params-scope-wrapper").find("#date").val()).format('dddd').toLowerCase();
        if (gon.restaurant_inheritance_data[dayName]) {
          areas = _.map(gon.restaurant_inheritance_data[dayName].areas, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          options = "";
          options += "<option value=''>Area</option>";
          for (i = 0, len = areas.length; i < len; i++) {
            area = areas[i];
            options += "<option value='" + area.id + "'>" + area.name + "</option>";
          }
          savedVal = $(e.currentTarget).closest(".params-scope-wrapper").find("select#area_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          $(e.currentTarget).closest(".params-scope-wrapper").find("select#area_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return $(e.currentTarget).closest(".params-scope-wrapper").find("select#area_type_id").val(savedVal).trigger("change");
        } else {
          $(e.currentTarget).closest(".params-scope-wrapper").find("select#area_type_id").val("").trigger("change").html("<option value=''>Area</option>");
          $(e.currentTarget).closest(".params-scope-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return $(e.currentTarget).closest(".params-scope-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      areaUpdated: function(e) {
        var areaName, dayName, i, len, options, savedVal, shift, shifts;
        dayName = moment($(e.currentTarget).closest(".params-scope-wrapper").find("#date").val()).format('dddd').toLowerCase();
        areaName = $(e.currentTarget).closest(".params-scope-wrapper").find("select#area_type_id option[value='" + ($(e.currentTarget).closest(".params-scope-wrapper").find("select#area_type_id").val()) + "']").text();
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas[areaName]) {
          shifts = _.map(gon.restaurant_inheritance_data[dayName].areas[areaName].shifts, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          options = "";
          options += "<option value=''>Shift</option>";
          for (i = 0, len = shifts.length; i < len; i++) {
            shift = shifts[i];
            options += "<option value='" + shift.id + "'>" + shift.name + "</option>";
          }
          savedVal = $(e.currentTarget).closest(".params-scope-wrapper").find("select#shift_type_id").val();
          if (savedVal === null || savedVal.length < 0) {
            savedVal = "";
          }
          $(e.currentTarget).closest(".params-scope-wrapper").find("select#shift_type_id").html(options).select2({
            theme: "bootstrap"
          });
          return $(e.currentTarget).closest(".params-scope-wrapper").find("select#shift_type_id").val(savedVal).trigger("change");
        } else {
          $(e.currentTarget).closest(".params-scope-wrapper").find("select#shift_type_id").val("").trigger("change").html("<option value=''>Shift</option>");
          return $(e.currentTarget).closest(".params-scope-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      shiftUpdated: function(e) {
        var areaData, areaName, dayName, firstPosition, i, len, options, position, positions, savedVal, shiftName;
        console.log('shiftUpdated');
        dayName = moment($(e.currentTarget).closest(".params-scope-wrapper").find("#date").val()).format('dddd').toLowerCase();
        areaName = $(e.currentTarget).closest(".params-scope-wrapper").find("select#area_type_id option[value='" + ($(e.currentTarget).closest(".params-scope-wrapper").find('select#area_type_id').val()) + "']").text();
        shiftName = $(e.currentTarget).closest(".params-scope-wrapper").find("select#shift_type_id option[value='" + ($(e.currentTarget).closest(".params-scope-wrapper").find("select#shift_type_id").val()) + "']").text();
        if (gon.restaurant_inheritance_data[dayName] && gon.restaurant_inheritance_data[dayName].areas) {
          areaData = gon.restaurant_inheritance_data[dayName].areas[areaName];
        }
        if (areaData && areaData.shifts[shiftName]) {
          positions = _.map(areaData.shifts[shiftName].positions, function(e) {
            return {
              id: e.id,
              name: e.name
            };
          });
          if (_.some(positions)) {
            firstPosition = positions[0].id;
          } else {
            firstPosition = "";
          }
          options = "";
          for (i = 0, len = positions.length; i < len; i++) {
            position = positions[i];
            options += "<option value='" + position.id + "'>" + position.name + "</option>";
          }
          savedVal = $(e.currentTarget).closest(".params-scope-wrapper").find("select#source_position_ids").val();
          if (!savedVal) {
            savedVal = firstPosition;
          }
          $(e.currentTarget).closest(".params-scope-wrapper").find("select#source_position_ids").html(options);
          return $(e.currentTarget).closest(".params-scope-wrapper").find("select#source_position_ids").val(savedVal).trigger("change");
        } else {
          return $(e.currentTarget).closest(".params-scope-wrapper").find("select#source_position_ids").val("").trigger("change");
        }
      },
      buildTransferJson: function() {
        var json;
        json = {
          frontend_restaurant_id: gon.restaurant.id,
          rewrite_existing_calculation: $("#rewrite_existing_calculation").val()
        };
        json.sourceParams = {
          date: $("#source-params").find("#date").val(),
          area_type_id: $("#source-params").find("select#area_type_id").val(),
          shift_type_id: $("#source-params").find("select#shift_type_id").val(),
          source_position_type_ids: $("#source-params").find("select#source_position_ids").val()
        };
        json.targetParams = {
          date: $("#target-params").find("#date").val(),
          area_type_id: $("#target-params").find("select#area_type_id").val(),
          shift_type_id: $("#target-params").find("select#shift_type_id").val(),
          source_position_type_ids: $("#target-params").find("select#source_position_ids").val()
        };
        return json;
      },
      switchTransferButton: function(e) {
        var json;
        console.log("switchTransferButton");
        json = this.buildTransferJson();
        if (_.isEmpty(json.sourceParams.date) || _.isEmpty(json.sourceParams.area_type_id) || _.isEmpty(json.sourceParams.shift_type_id) || _.isEmpty(json.sourceParams.source_position_type_ids) || _.isEmpty(json.targetParams.date) || _.isEmpty(json.targetParams.area_type_id) || _.isEmpty(json.targetParams.shift_type_id) || _.isEmpty(json.targetParams.source_position_type_ids)) {
          return $(".transfer-calculation-data").attr("disabled", "disabled");
        } else {
          return $(".transfer-calculation-data").removeAttr("disabled");
        }
      },
      trasferData: function(e) {
        var data;
        e.preventDefault();
        if ($(e.currentTarget).attr("disabled") === "disabled") {
          return true;
        }
        data = this.buildTransferJson();
        Tipmetric.Controllers.spinner.show();
        return $.ajax({
          method: "post",
          url: "/api/calculations/transfer_data",
          data: data,
          success: function(response) {
            if (!_.isEmpty(response.notice_additional)) {
              swal({
                title: "",
                text: response.notice_additional,
                type: "info",
                confirmButtonColor: "#28C256",
                confirmButtonText: "Ok"
              }).then((function(_this) {
                return function(confirmed) {
                  return toastr["success"]("Data has been transfered.", "Success");
                };
              })(this));
            } else {
              toastr["success"]("Data has been transfered.", "Success");
            }
            $("#rewrite_existing_calculation").val('false');
            $("#remove_deactivated_positions_distributions").val('false');
            return Tipmetric.Controllers.spinner.hide();
          },
          error: (function(_this) {
            return function(response) {
              var i, len, position, positions, positions_string;
              console.log(response.responseJSON);
              window.r = response.responseJSON;
              Tipmetric.Controllers.spinner.hide();
              if (_.contains(response.responseJSON.errors, 'days_are_locked')) {
                return Tipmetric.Controllers.notifier.alert("", "The day is locked. Please unlock it before making transfer.", false, false, function() {
                  return true;
                });
              } else if (_.contains(response.responseJSON.errors, 'source_calculation_not_found')) {
                return Tipmetric.Controllers.notifier.alert("", "Ooops. Looks like you want to transfer calculation, that doesn't exist.", false, false, function() {
                  return true;
                });
              } else if (_.contains(response.responseJSON.errors, 'disabled_positions_persist')) {
                positions = response.responseJSON.errors_additional.disabled_positions;
                positions_string = "";
                for (i = 0, len = positions.length; i < len; i++) {
                  position = positions[i];
                  positions_string += position.name.charAt(0).toUpperCase() + position.name.slice(1);
                  if (position !== _.last(positions)) {
                    positions_string += ", ";
                  }
                }
                return Tipmetric.Controllers.notifier.alert("", positions_string + " doesn't work that DAY / SHIFT (according to your setup settings). Please change your setup settings or choose another DATE to make this transfer", false, false, function() {
                  return true;
                });
              } else if (_.contains(response.responseJSON.errors, 'target_calculation_not_blank')) {
                return swal({
                  title: "",
                  text: "Looks like calculation with target params already exists and has some data. Do you want to rewrite it?",
                  type: "error",
                  confirmButtonColor: "#BB473E",
                  confirmButtonText: "Ok",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirm) {
                  if (isConfirm.value === true) {
                    $("#rewrite_existing_calculation").val('true');
                    return _this.trasferData(e);
                  } else {
                    return $("#rewrite_existing_calculation").val('false');
                  }
                });
              } else if (_.contains(response.responseJSON.errors, 'deactivated_positions_used')) {
                return swal({
                  title: "",
                  text: response.responseJSON.errors_additional.error_notice_additional,
                  type: "error",
                  confirmButtonColor: "#BB473E",
                  confirmButtonText: "Ok",
                  showCancelButton: true,
                  cancelButtonText: "Cancel"
                }).then(function(isConfirm) {
                  if (isConfirm.value === true) {
                    $("#remove_deactivated_positions_distributions").val('true');
                    return _this.trasferData(e);
                  } else {
                    return $("#remove_deactivated_positions_distributions").val('false');
                  }
                });
              }
            };
          })(this)
        });
      }
    });
    return $(document).ready(function() {
      if ($("body").hasClass("csv-import-view")) {
        return Tipmetric.Controllers.transferFormView = new TransferFormView();
      }
    });
  })();

}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["billing/billing"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var i, j, k, l, len, len1, len2, len3, period_plan, plan, ref, ref1, ref2, ref3, size;
      
        __out.push('<div id="billing-view">\n  <br>\n  <div class="row">\n    <div class="col-lg-12">\n      <div class="col-lg-12">\n        <div class="hpanel">\n          <div class="panel-body">\n            <h2 class="font-light m-b-xs">\n            Billing \n            <div class="show-subscription-blocks toggle-billing-pages">\n              <i class="pe-7s-news-paper"></i>\n            </div>\n            </h2>\n          </div>\n        </div>\n      </div>\n      ');
      
        if (gon.restaurant.access_restricted) {
          __out.push('\n      <div class="col-lg-12">\n        <div class=" alert alert-danger" role="alert">\n          ');
          if (!_.isEmpty(gon.restaurant.restricted_access_message)) {
            __out.push('\n            ');
            __out.push(__sanitize(gon.restaurant.restricted_access_message));
            __out.push('\n          ');
          } else {
            __out.push('\n            Your Free trial has ended :( To continue service please enter your billing information & subscribe.\n          ');
          }
          __out.push('\n        </div>\n      </div>\n      ');
        }
      
        __out.push('\n\n      <div class="col-sm-12 plans-wrapper">\n        <div class="hpanel hyellow">\n          <div class="panel-body">\n            <div class="col-sm-12 text-center">\n              <div class="row">\n                <h3 class="font-light m-b-xs">\n                  1. Choose your plan\n                </h3>\n\n\n                <div class="col-sm-12 text-center">\n                  <div class="row">\n                    <div class="form-group">\n                      <div class="col-sm-6 table-class text-right">\n                          <label class="control-label table-cell-class">');
      
        __out.push(__sanitize(this.restaurant_name));
      
        __out.push(': entire organization</label>\n                      </div>\n                      <div class="col-sm-6 table-class text-left">\n                        <select class="form-control table-cell-class m-b period_editable" name="company_size">\n                          ');
      
        ref = _.pairs(gon.restaurant.available_billing_plans);
        for (i = 0, len = ref.length; i < len; i++) {
          plan = ref[i];
          __out.push('\n                            <option value="');
          __out.push(__sanitize(plan[1]));
          __out.push('">');
          __out.push(__sanitize(gon.stripe_plans.plans_to_show_to_admin[plan[1].replace(/-/g, "_")].without_prices));
          __out.push('</option>\n                          ');
        }
      
        __out.push('\n                        </select>\n                      </div>\n                    </div>\n                    <div class="period-type">\n                      ');
      
        ref1 = _.pairs(this.stripe_plans.periods);
        for (j = 0, len1 = ref1.length; j < len1; j++) {
          size = ref1[j];
          __out.push('\n                        <div class="radio-option"><label><input class="period_editable" type="radio" value="');
          __out.push(__sanitize(size[0]));
          __out.push('" id="optionsRadios1" name="billing_period">');
          __out.push(__sanitize(size[1].description));
          __out.push('</label></div>\n                      ');
        }
      
        __out.push('\n                    </div>\n                  </div>\n                </div>\n              </div>\n            </div>\n          </div>\n        </div>\n      </div>\n\n      <div class="col-sm-12 prices-wrapper">\n        <div class="row">\n          <div class="col-sm-12">\n            <h2 class="font-light m-b-xs text-center">\n              ');
      
        ref2 = _.pairs(this.stripe_plans.periods);
        for (k = 0, len2 = ref2.length; k < len2; k++) {
          size = ref2[k];
          __out.push('\n                ');
          ref3 = size[1].plans;
          for (l = 0, len3 = ref3.length; l < len3; l++) {
            period_plan = ref3[l];
            __out.push('\n                  ');
            if (!(period_plan.id === 'test-monthly' && period_plan.admin_only && !gon.current_user_testing_payments)) {
              __out.push('\n                    <div class="plan-span ');
              __out.push(__sanitize(period_plan.id));
              __out.push('">\n                      <span class="dollar-sign">$</span>');
              __out.push(__sanitize(period_plan.amount));
              __out.push('/');
              __out.push(__sanitize(period_plan.period));
              __out.push('\n                      ');
              if (period_plan.free_month) {
                __out.push('\n                        <div class="tax-line">(includes free month)</div>\n                      ');
              }
              __out.push('\n                      ');
              if (period_plan.tax_percent) {
                __out.push('\n                      <div class="tax-line">(+ ');
                __out.push(__sanitize(period_plan.tax_percent));
                __out.push('% Sales Tax)</div>\n                      ');
              }
              __out.push('\n                    </div>\n                  ');
            }
            __out.push('\n                ');
          }
          __out.push('\n              ');
        }
      
        __out.push('\n            </h2>\n            <br>\n          </div>\n        </div>\n      </div>\n\n      <div class="col-sm-12 cards-wrapper">\n        <div class="hpanel hblue">\n          <div class="panel-body">\n            <div class="col-sm-6 col-sm-offset-3">\n              <div class="row">\n                <h3 class="font-light m-b-xs">\n                  2. Make payment\n                </h3>\n\n                <form action="/charge" method="post" id="payment-form">\n                  <div class="form-row">\n                    <div id="card-element">\n                      <!-- a Stripe Element will be inserted here. -->\n                    </div>\n\n                    <!-- Used to display Element errors -->\n                    <div id="card-errors" role="alert"></div>\n                  </div>\n\n                  <div id="added-cards text-center">\n                    ');
      
        if (gon.default_card) {
          __out.push('\n                      <div class="added-card text-center">\n                        <input data-card-id="');
          __out.push(__sanitize(gon.default_card.id));
          __out.push('" class="use-card" type="checkbox" value=""> Use xxxx-');
          __out.push(__sanitize(gon.default_card.last4));
          __out.push(' (');
          __out.push(__sanitize(gon.default_card.exp_month));
          __out.push('/');
          __out.push(__sanitize(gon.default_card.exp_year));
          __out.push(') <i class="fa fa-cc-');
          __out.push(__sanitize(gon.default_card.brand));
          __out.push('"></i> <span class="btn btn-default toggle-billing-pages"> Update card</span>\n                      </div>\n                    ');
        }
      
        __out.push('\n                  </div>\n\n                  <button>Subscribe</button>\n                </form>\n              </div>\n            </div>\n          </div>\n        </div>\n      </div>\n\n      <div class="col-lg-12 cards-wrapper hidden">\n        <div class="row">\n          <div class="cards-list">\n            <!-- User cards will be inserted here -->\n          </div>\n        </div>\n      </div>\n\n    </div>\n  </div>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["billing/card"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<div class="col-sm-12 card" data-card-id=\'');
      
        __out.push(__sanitize(this.id));
      
        __out.push('\' data-card-is-default=\'');
      
        __out.push(__sanitize(this.is_default));
      
        __out.push('\'>\n  <div class="row">\n    <div class="col-sm-12">\n      <div class="hpanel hyellow">\n        <div class="panel-body">\n          <div class="card-description-item brand"><i class="fa fa-cc-');
      
        __out.push(__sanitize(this.brand));
      
        __out.push('"></i>');
      
        __out.push(__sanitize(this.brand));
      
        __out.push('</div>\n          <div class="card-description-item last4">xxxx xxxx xxxx ');
      
        __out.push(__sanitize(this.last4));
      
        __out.push('</div>\n          <div class="card-description-item expire">Expire: ');
      
        __out.push(__sanitize(this.exp_month));
      
        __out.push('/');
      
        __out.push(__sanitize(this.exp_year));
      
        __out.push('</div>\n          <br>\n          <div class="card-description-item actions">\n            <a href="" class="btn btn-default btn-xs delete"><span class="fa fa-remove"></span> Delete</a>\n            <div class="card-description-item is-default">\n              <span class="label label-success">Default</span>\n            </div>\n            <a href="" class="btn btn-default btn-xs make-default">Make default</a>\n          </div>\n        </div>\n      </div>\n    </div>\n  </div>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["billing/charge"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<tr>\n  <td>');
      
        __out.push(__sanitize(moment.unix(this.created).format("MM/DD/YYYY")));
      
        __out.push('<span class="payment-sign" data-paid="');
      
        __out.push(__sanitize(this.paid));
      
        __out.push('"><i class="fa fa-check-circle"></i><i class="fa fa-times-circle"></i></span></td>\n  <td>$');
      
        __out.push(__sanitize(parseFloat(this.amount) / 100));
      
        __out.push('</td>\n  <td>');
      
        __out.push(__sanitize(this.source.last4));
      
        __out.push('/');
      
        __out.push(__sanitize(this.source.exp_month));
      
        __out.push('/');
      
        __out.push(__sanitize(this.source.exp_year));
      
        __out.push('</td>\n</tr>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["billing/subscription"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<div class="plan-span ');
      
        __out.push(__sanitize(this.plan.id));
      
        __out.push(' plan-wrapper text-center" data-plan-name="');
      
        __out.push(__sanitize(this.plan.id));
      
        __out.push('">\n  <div>\n    <span class="dollar-sign">$</span>');
      
        __out.push(__sanitize(parseFloat(this.plan.amount) / 100));
      
        __out.push('/');
      
        __out.push(__sanitize(this.plan.interval));
      
        __out.push('\n  </div>\n  <div class="text-center cancel-wrapper">\n    <div class="btn btn-default cancel">Cancel</div>\n  </div>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["billing/subscriptions"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<div id="subscription-view">\n  <br>\n  <div class="row">\n    <div class="col-lg-12">\n      <div class="col-lg-12">\n        <div class="hpanel">\n          <div class="panel-body">\n            <h2 class="font-light m-b-xs toggle-billing-pages">\n            Billing\n            <div class="show-subscription-blocks">\n              <i class="pe-7s-news-paper"></i>\n            </div>\n            </h2>\n          </div>\n        </div>\n      </div>\n\n      <div class="col-sm-12 plans-wrapper">\n        <div class="hpanel hyellow">\n          <div class="panel-body nopadding">\n            <div class="col-sm-12 text-center">\n              <div class="row">\n                <h3 class="font-light m-b-xs">\n                  History\n                </h3>\n\n\n                <div class="col-sm-12 text-center">\n                  <div class="row">\n                    <!-- User charges will be inserted here -->\n                    <table class="table table-striped table-bordered">\n                      <thead>\n                        <tr>\n                          <th class="text-center">Data</th>\n                          <th class="text-center">Amount</th>\n                          <th class="text-center">Card</th>\n                        </tr>\n                        <tbody class="charges-list">\n                          \n                        </tbody>\n                      </thead>\n                    </table>\n                  </div>\n                </div>\n              </div>\n            </div>\n          </div>\n        </div>\n      </div>\n\n      <div class="col-sm-12 prices-wrapper">\n        <div class="row">\n          <div class="col-sm-6">\n            <div class="hpanel hblue">\n              <div class="panel-body nopadding">\n                <h3 class="font-light m-b-xs">Update Billing Card</h3>\n\n                <div class="cards-list">\n                  <!-- User cards will be inserted here -->\n                </div>\n\n                <div class="card-form">\n                  <form action="/charge" method="post" id="payment-form">\n                    <div class="form-row">\n                      <div id="card-element">\n                        <!-- a Stripe Element will be inserted here. -->\n                      </div>\n\n                      <!-- Used to display Element errors -->\n                      <div id="card-errors" role="alert"></div>\n                    </div>\n\n                    <button class="add-card">Add card</button>\n                  </form>\n                </div>\n              </div>\n            </div>\n          </div>\n\n\n          <div class="col-sm-6">\n            <div class="hpanel hblue">\n              <div class="panel-body nopadding">\n                <h3 class="font-light m-b-xs">Subscription</h3>\n                <div class="subscriptions-list plans-wrapper text-center">\n                  <!-- User subscriptions will be inserted here -->\n                </div>\n              </div>\n            </div>\n          </div>\n        </div>\n      </div>\n\n    </div>\n  </div>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["brink_import/employees"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var employee, i, len, ref;
      
        __out.push('<table id="built-import-table" class="table table-bordered table-striped">\n  <thead>\n    <tr>\n      <th data-tm-field-name=\'employee-line-number\' data-tm-field-val=\'0\'></th>\n      <th data-column-number=\'1\'>PayrollId</th>\n      <th data-column-number=\'2\'>EmployeeId</th>\n      <th data-column-number=\'3\'>FirstName</th>\n      <th data-column-number=\'4\'>LastName</th>\n      <th data-column-number=\'5\'>JobCode</th>\n      <th data-column-number=\'6\'>Hoursworked</th>\n      <th data-column-number=\'7\'>Start</th>\n      <th data-column-number=\'8\'>End</th>\n      <th data-column-number=\'9\'>NetSales</th>\n      <th data-column-number=\'10\'>TipAmount</th>\n      <th data-column-number=\'11\'>Service Charge</th>\n      <th data-column-number=\'12\'>DeclaredTips</th>\n      <th data-column-number=\'13\'>FoodSumm + other</th>\n      <th data-column-number=\'14\'>BeverageSumm (Liquor, Beer & Wine)</th>\n      <th data-column-number=\'15\'></th>\n    </tr>\n  </thead>\n  <tbody class="brink-import-data">\n    ');
      
        ref = this.default_employees;
        for (i = 0, len = ref.length; i < len; i++) {
          employee = ref[i];
          __out.push('\n      <tr data-row-number=\'');
          __out.push(__sanitize(this.default_employees.indexOf(employee) + 1));
          __out.push('\' data-shift-id="');
          __out.push(__sanitize(employee.shift_id));
          __out.push('">\n        <td data-tm-field-name=\'employee-line-number\' data-tm-field-val=\'');
          __out.push(__sanitize(this.default_employees.indexOf(employee) + 1));
          __out.push('\'>');
          __out.push(__sanitize(this.default_employees.indexOf(employee) + 1));
          __out.push('</td>\n        <td data-column-number=\'1\' data-tm-field-name=\':emp_id\'>');
          __out.push(__sanitize(employee.payroll_id));
          __out.push('</td>\n        <td data-column-number=\'2\' data-tm-field-name=\':employee_id\'>');
          __out.push(__sanitize(employee.employee_id));
          __out.push('</td>\n        <td data-column-number=\'3\' data-tm-field-name=\':first_name\'>');
          __out.push(__sanitize(employee.first_name));
          __out.push('</td>\n        <td data-column-number=\'4\' data-tm-field-name=\':last_name\'>');
          __out.push(__sanitize(employee.last_name));
          __out.push('</td>\n        <td data-column-number=\'5\' data-tm-field-name=\':job_code\'>');
          __out.push(__sanitize(employee.job_code));
          __out.push('</td>\n        <td data-column-number=\'6\' data-tm-field-name=\':hours_worked\'>');
          __out.push(__sanitize(employee.hours));
          __out.push('</td>\n        <td data-column-number=\'7\' data-tm-field-name=\':start_time\'>');
          __out.push(__sanitize(employee.start_time));
          __out.push('</td>\n        <td data-column-number=\'8\' data-tm-field-name=\':end_time\'>');
          __out.push(__sanitize(employee.end_time));
          __out.push('</td>\n        <td data-column-number=\'9\' data-tm-field-name=\':net_sales\'>');
          __out.push(__sanitize(employee.net_sales));
          __out.push('</td>\n        <td data-column-number=\'10\' data-tm-field-name=\':tip_amount\'>');
          __out.push(__sanitize(employee.tip_amount));
          __out.push('</td>\n        <td data-column-number=\'11\' data-tm-field-name=\':ag_svc_tips\'>');
          __out.push(__sanitize(employee.ag_svc_tips));
          __out.push('</td>\n        <td data-column-number=\'12\' data-tm-field-name=\':declared\'>');
          __out.push(__sanitize(employee.declared));
          __out.push('</td>\n        <td data-column-number=\'13\' data-tm-field-name=\':food_summ\'>');
          __out.push(__sanitize(employee.food_summ));
          __out.push('</td>\n        <td data-column-number=\'14\' data-tm-field-name=\':beverage_summ\'>');
          __out.push(__sanitize(employee.beverage_summ));
          __out.push('</td>\n        <td data-column-number=\'15\' data-tm-field-name=\':actions\'><a class=\'btn btn-xs btn-danger\' data-action=\'remove-row-inline\'><i class=\'fa fa-remove\'></i></a></td>\n      </tr>\n    ');
        }
      
        __out.push('\n\n    <tr data-row-number=\'');
      
        __out.push(__sanitize(this.default_employees.length + 1));
      
        __out.push('\' data-identifier="eod_till">\n      <td data-tm-field-name=\'employee-line-number\' data-tm-field-val=\'');
      
        __out.push(__sanitize(this.default_employees.length + 1));
      
        __out.push('\'>');
      
        __out.push(__sanitize(this.default_employees.length + 1));
      
        __out.push('</td>\n      <td data-column-number=\'1\' data-tm-field-name=\':emp_id\'>');
      
        __out.push(__sanitize(this.eod_till_employee.payroll_id));
      
        __out.push('</td>\n      <td data-column-number=\'2\' data-tm-field-name=\':employee_id\'>');
      
        __out.push(__sanitize(this.eod_till_employee.employee_id));
      
        __out.push('</td>\n      <td data-column-number=\'3\' data-tm-field-name=\':first_name\'>');
      
        __out.push(__sanitize(this.eod_till_employee.first_name));
      
        __out.push('</td>\n      <td data-column-number=\'4\' data-tm-field-name=\':last_name\'>');
      
        __out.push(__sanitize(this.eod_till_employee.last_name));
      
        __out.push('</td>\n      <td data-column-number=\'5\' data-tm-field-name=\':job_code\'>');
      
        __out.push(__sanitize(this.eod_till_employee.job_code));
      
        __out.push('</td>\n      <td data-column-number=\'6\' data-tm-field-name=\':hours_worked\'>');
      
        __out.push(__sanitize(this.eod_till_employee.hours));
      
        __out.push('</td>\n      <td data-column-number=\'7\' data-tm-field-name=\':start_time\'>');
      
        __out.push(__sanitize(this.eod_till_employee.start_time));
      
        __out.push('</td>\n      <td data-column-number=\'8\' data-tm-field-name=\':end_time\'>');
      
        __out.push(__sanitize(this.eod_till_employee.end_time));
      
        __out.push('</td>\n      <td data-column-number=\'9\' data-tm-field-name=\':net_sales\'>');
      
        __out.push(__sanitize(this.eod_till_employee.net_sales));
      
        __out.push('</td>\n      <td data-column-number=\'10\' data-tm-field-name=\':tip_amount\'>');
      
        __out.push(__sanitize(this.eod_till_employee.tip_amount));
      
        __out.push('</td>\n      <td data-column-number=\'11\' data-tm-field-name=\':ag_svc_tips\'>');
      
        __out.push(__sanitize(this.eod_till_employee.ag_svc_tips));
      
        __out.push('</td>\n      <td data-column-number=\'12\' data-tm-field-name=\':declared\'>');
      
        __out.push(__sanitize(this.eod_till_employee.declared));
      
        __out.push('</td>\n      <td data-column-number=\'13\' data-tm-field-name=\':food_summ\'>');
      
        __out.push(__sanitize(this.eod_till_employee.food_summ));
      
        __out.push('</td>\n      <td data-column-number=\'14\' data-tm-field-name=\':beverage_summ\'>');
      
        __out.push(__sanitize(this.eod_till_employee.beverage_summ));
      
        __out.push('</td>\n      <td data-column-number=\'15\' data-tm-field-name=\':actions\'><a class=\'btn btn-xs btn-danger\' data-action=\'remove-row-inline\'><i class=\'fa fa-remove\'></i></a></td>\n    </tr>\n\n    <tr data-row-number=\'');
      
        __out.push(__sanitize(this.default_employees.length + 2));
      
        __out.push('\' data-identifier="eod_till">\n      <td data-tm-field-name=\'employee-line-number\' data-tm-field-val=\'');
      
        __out.push(__sanitize(this.default_employees.length + 2));
      
        __out.push('\'>');
      
        __out.push(__sanitize(this.default_employees.length + 2));
      
        __out.push('</td>\n      <td data-column-number=\'1\' data-tm-field-name=\':emp_id\'>');
      
        __out.push(__sanitize(this.catering_employee.payroll_id));
      
        __out.push('</td>\n      <td data-column-number=\'2\' data-tm-field-name=\':employee_id\'>');
      
        __out.push(__sanitize(this.catering_employee.employee_id));
      
        __out.push('</td>\n      <td data-column-number=\'3\' data-tm-field-name=\':first_name\'>');
      
        __out.push(__sanitize(this.catering_employee.first_name));
      
        __out.push('</td>\n      <td data-column-number=\'4\' data-tm-field-name=\':last_name\'>');
      
        __out.push(__sanitize(this.catering_employee.last_name));
      
        __out.push('</td>\n      <td data-column-number=\'5\' data-tm-field-name=\':job_code\'>');
      
        __out.push(__sanitize(this.catering_employee.job_code));
      
        __out.push('</td>\n      <td data-column-number=\'6\' data-tm-field-name=\':hours_worked\'>');
      
        __out.push(__sanitize(this.catering_employee.hours));
      
        __out.push('</td>\n      <td data-column-number=\'7\' data-tm-field-name=\':start_time\'>');
      
        __out.push(__sanitize(this.catering_employee.start_time));
      
        __out.push('</td>\n      <td data-column-number=\'8\' data-tm-field-name=\':end_time\'>');
      
        __out.push(__sanitize(this.catering_employee.end_time));
      
        __out.push('</td>\n      <td data-column-number=\'9\' data-tm-field-name=\':net_sales\'>');
      
        __out.push(__sanitize(this.catering_employee.net_sales));
      
        __out.push('</td>\n      <td data-column-number=\'10\' data-tm-field-name=\':tip_amount\'>');
      
        __out.push(__sanitize(this.catering_employee.tip_amount));
      
        __out.push('</td>\n      <td data-column-number=\'11\' data-tm-field-name=\':ag_svc_tips\'>');
      
        __out.push(__sanitize(this.catering_employee.ag_svc_tips));
      
        __out.push('</td>\n      <td data-column-number=\'12\' data-tm-field-name=\':declared\'>');
      
        __out.push(__sanitize(this.catering_employee.declared));
      
        __out.push('</td>\n      <td data-column-number=\'13\' data-tm-field-name=\':food_summ\'>');
      
        __out.push(__sanitize(this.catering_employee.food_summ));
      
        __out.push('</td>\n      <td data-column-number=\'14\' data-tm-field-name=\':beverage_summ\'>');
      
        __out.push(__sanitize(this.catering_employee.beverage_summ));
      
        __out.push('</td>\n      <td data-column-number=\'15\' data-tm-field-name=\':actions\'><a class=\'btn btn-xs btn-danger\' data-action=\'remove-row-inline\'><i class=\'fa fa-remove\'></i></a></td>\n    </tr>\n\n    <tr data-row-number=\'');
      
        __out.push(__sanitize(this.default_employees.length + 3));
      
        __out.push('\' data-identifier="eod_till">\n      <td data-tm-field-name=\'employee-line-number\' data-tm-field-val=\'');
      
        __out.push(__sanitize(this.default_employees.length + 3));
      
        __out.push('\'>');
      
        __out.push(__sanitize(this.default_employees.length + 3));
      
        __out.push('</td>\n      <td data-column-number=\'1\' data-tm-field-name=\':emp_id\'>');
      
        __out.push(__sanitize(this.delivery_employee.payroll_id));
      
        __out.push('</td>\n      <td data-column-number=\'2\' data-tm-field-name=\':employee_id\'>');
      
        __out.push(__sanitize(this.delivery_employee.employee_id));
      
        __out.push('</td>\n      <td data-column-number=\'3\' data-tm-field-name=\':first_name\'>');
      
        __out.push(__sanitize(this.delivery_employee.first_name));
      
        __out.push('</td>\n      <td data-column-number=\'4\' data-tm-field-name=\':last_name\'>');
      
        __out.push(__sanitize(this.delivery_employee.last_name));
      
        __out.push('</td>\n      <td data-column-number=\'5\' data-tm-field-name=\':job_code\'>');
      
        __out.push(__sanitize(this.delivery_employee.job_code));
      
        __out.push('</td>\n      <td data-column-number=\'6\' data-tm-field-name=\':hours_worked\'>');
      
        __out.push(__sanitize(this.delivery_employee.hours));
      
        __out.push('</td>\n      <td data-column-number=\'7\' data-tm-field-name=\':start_time\'>');
      
        __out.push(__sanitize(this.delivery_employee.start_time));
      
        __out.push('</td>\n      <td data-column-number=\'8\' data-tm-field-name=\':end_time\'>');
      
        __out.push(__sanitize(this.delivery_employee.end_time));
      
        __out.push('</td>\n      <td data-column-number=\'9\' data-tm-field-name=\':net_sales\'>');
      
        __out.push(__sanitize(this.delivery_employee.net_sales));
      
        __out.push('</td>\n      <td data-column-number=\'10\' data-tm-field-name=\':tip_amount\'>');
      
        __out.push(__sanitize(this.delivery_employee.tip_amount));
      
        __out.push('</td>\n      <td data-column-number=\'11\' data-tm-field-name=\':ag_svc_tips\'>');
      
        __out.push(__sanitize(this.delivery_employee.ag_svc_tips));
      
        __out.push('</td>\n      <td data-column-number=\'12\' data-tm-field-name=\':declared\'>');
      
        __out.push(__sanitize(this.delivery_employee.declared));
      
        __out.push('</td>\n      <td data-column-number=\'13\' data-tm-field-name=\':food_summ\'>');
      
        __out.push(__sanitize(this.delivery_employee.food_summ));
      
        __out.push('</td>\n      <td data-column-number=\'14\' data-tm-field-name=\':beverage_summ\'>');
      
        __out.push(__sanitize(this.delivery_employee.beverage_summ));
      
        __out.push('</td>\n      <td data-column-number=\'15\' data-tm-field-name=\':actions\'><a class=\'btn btn-xs btn-danger\' data-action=\'remove-row-inline\'><i class=\'fa fa-remove\'></i></a></td>\n    </tr>\n\n    <tr data-row-number=\'');
      
        __out.push(__sanitize(this.default_employees.length + 4));
      
        __out.push('\' data-identifier="eod_till">\n      <td data-tm-field-name=\'employee-line-number\' data-tm-field-val=\'');
      
        __out.push(__sanitize(this.default_employees.length + 4));
      
        __out.push('\'>');
      
        __out.push(__sanitize(this.default_employees.length + 4));
      
        __out.push('</td>\n      <td data-column-number=\'1\' data-tm-field-name=\':emp_id\'>');
      
        __out.push(__sanitize(this.open_dining.payroll_id));
      
        __out.push('</td>\n      <td data-column-number=\'2\' data-tm-field-name=\':employee_id\'>');
      
        __out.push(__sanitize(this.open_dining.employee_id));
      
        __out.push('</td>\n      <td data-column-number=\'3\' data-tm-field-name=\':first_name\'>');
      
        __out.push(__sanitize(this.open_dining.first_name));
      
        __out.push('</td>\n      <td data-column-number=\'4\' data-tm-field-name=\':last_name\'>');
      
        __out.push(__sanitize(this.open_dining.last_name));
      
        __out.push('</td>\n      <td data-column-number=\'5\' data-tm-field-name=\':job_code\'>');
      
        __out.push(__sanitize(this.open_dining.job_code));
      
        __out.push('</td>\n      <td data-column-number=\'6\' data-tm-field-name=\':hours_worked\'>');
      
        __out.push(__sanitize(this.open_dining.hours));
      
        __out.push('</td>\n      <td data-column-number=\'7\' data-tm-field-name=\':start_time\'>');
      
        __out.push(__sanitize(this.open_dining.start_time));
      
        __out.push('</td>\n      <td data-column-number=\'8\' data-tm-field-name=\':end_time\'>');
      
        __out.push(__sanitize(this.open_dining.end_time));
      
        __out.push('</td>\n      <td data-column-number=\'9\' data-tm-field-name=\':net_sales\'>');
      
        __out.push(__sanitize(this.open_dining.net_sales));
      
        __out.push('</td>\n      <td data-column-number=\'10\' data-tm-field-name=\':tip_amount\'>');
      
        __out.push(__sanitize(this.open_dining.tip_amount));
      
        __out.push('</td>\n      <td data-column-number=\'11\' data-tm-field-name=\':ag_svc_tips\'>');
      
        __out.push(__sanitize(this.open_dining.ag_svc_tips));
      
        __out.push('</td>\n      <td data-column-number=\'12\' data-tm-field-name=\':declared\'>');
      
        __out.push(__sanitize(this.open_dining.declared));
      
        __out.push('</td>\n      <td data-column-number=\'13\' data-tm-field-name=\':food_summ\'>');
      
        __out.push(__sanitize(this.open_dining.food_summ));
      
        __out.push('</td>\n      <td data-column-number=\'14\' data-tm-field-name=\':beverage_summ\'>');
      
        __out.push(__sanitize(this.open_dining.beverage_summ));
      
        __out.push('</td>\n      <td data-column-number=\'15\' data-tm-field-name=\':actions\'><a class=\'btn btn-xs btn-danger\' data-action=\'remove-row-inline\'><i class=\'fa fa-remove\'></i></a></td>\n    </tr>\n  </tbody>\n</table>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["brink_import/report_groups_popup"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var group, i, len, ref;
      
        window.data = this;
      
        __out.push('\n\n<div class="no-super-group">\n<h3>Revenue Center Ids</h3>\n');
      
        ref = this.allRevenueCenterValues;
        for (i = 0, len = ref.length; i < len; i++) {
          group = ref[i];
          __out.push('\n  <div class=\'report-group-item\' data-group-name="');
          __out.push(__sanitize(group.Name));
          __out.push('" data-group-id="');
          __out.push(__sanitize(group.Id));
          __out.push('" data-added-to-list="');
          __out.push(__sanitize(_.contains(this.currentValuesList, group.Id.toString())));
          __out.push('">\n    ');
          __out.push(__sanitize(group.Name));
          __out.push(' (ID: ');
          __out.push(__sanitize(group.Id));
          __out.push(') <a class=\'btn btn-xs btn-success\' data-action=\'add-report-group\'>Add</a><a class=\'btn btn-xs btn-danger\' data-action=\'remove-report-group\'>Remove</a>\n  </div>\n');
        }
      
        __out.push('\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["calculation/distributions/distributions_team_table"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<div class="col-lg-12 team-table-block">\n  <div class="hpanel hgreen">\n    <div class="panel-body">\n      <h2 class="font-light m-b-xs team-table-name">\n        ');
      
        __out.push(__sanitize(this.position_type_name.toUpperCase()));
      
        __out.push(' (Team #');
      
        __out.push(__sanitize(this.team_no));
      
        __out.push(')\n      </h2>\n      <div class="table-responsive">\n        <table id="');
      
        __out.push(__sanitize(this.position_type_name + '-' + this.team_no));
      
        __out.push('" cellpadding="1" cellspacing="1" class="table table-condensed team-table" data-table-type="employee-distribution" data-position-type-id="');
      
        __out.push(__sanitize(this.position_type_id));
      
        __out.push('" data-position-table="');
      
        __out.push(__sanitize(this.position_type_name));
      
        __out.push('" data-position-is-a-source="');
      
        __out.push(__sanitize(this.position_type_is_a_source));
      
        __out.push('" data-team="');
      
        __out.push(__sanitize(this.team_no));
      
        __out.push('" data-team-count="');
      
        __out.push(__sanitize(this.team_count));
      
        __out.push('">\n          <thead>\n            <tr>\n              <th colspan="');
      
        __out.push(__sanitize(this.team_count > 1 ? 3 : 2));
      
        __out.push('"></th>\n              <!-- Inputs -->\n              <th class="source-only-fields ag-svc-cell"></th>\n              <th class="source-only-fields fee-cell"></th>\n              <th colspan="2" class="font-bold source-only-fields sales-cell" data-tour-step="employee-sales-made">Sales</th>\n              <th colspan="2" class="font-bold source-only-fields food-and-beverage-cell" data-tour-step="employee-food-sales-made">Food & Bev Sales</th>\n              <th colspan="2" class="font-bold source-only-fields" data-tour-step="employee-tips-collected" data-tour-step="employee-tips-sales">Tips collected</th>\n\n              <!-- Distributed -->\n\n              <th colspan="2" class="font-bold before-tip-out-th">Before Tip-Out Other areas</th>\n              <th class="before-tip-out-th ag-svc-cell"></th>\n              <th class="before-tip-out-th fee-cell"></th>\n\n              <!-- Tip out given -->\n              <th colspan="2" class="font-bold tip-outs">Tip outs given</th>\n              <th class="tip-outs ag-svc-cell"></th>\n              <th class="tip-outs fee-cell"></th>\n\n              <!-- Tip outs received -->\n              <th colspan="2" class="font-bold tip-outs">Tip outs received</th>\n              <th class="tip-outs ag-svc-cell"></th>\n              <th class="tip-outs fee-cell"></th>\n\n              <!-- Final distribution -->\n              <th colspan="2" class="font-bold final-distribution-th">Final distribution</th>\n              <th class="ag-svc-cell"></th>\n              <th class="fee-cell"></th>\n              <!-- Final tips column -->\n              <th class="final-tips-column"></th>\n              <th class="hourly-rate-column"></th>\n              <th class="actions hidden"></th>\n            </tr>\n            <tr>\n              <th data-tour-step="employee-id">Employee (id)</th>\n              ');
      
        if (this.team_count > 1) {
          __out.push('\n                <th>Team number</th>\n              ');
        }
      
        __out.push('\n              <th data-tour-step="employee-hours" class="employee-hours">Hrs worked</th>\n\n              <!-- Inputs -->\n\n              <th class="source-only-fields ag-svc source-only-fields ag-svc-cell">Auto-gratuity<br>service charge</th>\n              <th class="source-only-fields source-only-fields fee-cell">Fees</th>\n              <th class="source-only-fields sales-cell">Sales CC</th>\n              <th class="source-only-fields sales-cell">Sales Cash</th>\n              <th class="source-only-fields food-and-beverage-cell">Food</th>\n              <th class="source-only-fields food-and-beverage-cell">Beverage</th>\n              <th class="source-only-fields">Cc</th>\n              <th class="source-only-fields">Cash</th>\n\n              <!-- Tips distributed -->\n\n              <th class="before-tip-out-th">Cc</th>\n              <th class="before-tip-out-th">Cash</th>\n              <th class="before-tip-out-th ag-svc-cell">AG/SVC</th>\n              <th class="before-tip-out-th fee-cell">Fees</th>\n\n              <!-- Tip outs -->\n\n              <!-- Given -->\n              <th class="tip-outs">Cc</th>\n              <th class="tip-outs">Cash</th>\n              <th class="tip-outs ag-svc-cell">AG/SVC</th>\n              <th class="tip-outs fee-cell">Fees</th>\n\n              <!-- Received -->\n              <th class="tip-outs">Cc</th>\n              <th class="tip-outs">Cash</th>\n              <th class="tip-outs ag-svc-cell">AG/SVC</th>\n              <th class="tip-outs fee-cell">Fees</th>\n\n              <!-- Final distribution -->\n\n              <th>Final CC</th>\n              <th>Final Cash</th>\n              <th class="ag-svc-cell">Final AG/SVC</th>\n              <th class="fee-cell">Final Fees</th>\n\n              <!-- Final tips column -->\n              <th class="final-tips-column">Final Tip-Outs</th>\n              <th class="hourly-rate-column">Hourly Rate</th>\n\n              <!-- Actions -->\n\n              <th class="actions hidden"></th>\n            </tr>\n          </thead>\n          <tbody>\n            <!-- New employee distributions goes here -->\n\n            <tr class="info">\n              <td colspan="');
      
        __out.push(__sanitize(this.team_count > 1 ? 2 : 1));
      
        __out.push('" class="text-left font-bold"><h4>Total:</h4></td>\n              <td class="font-bold text-left total-hours"><h4>-</h4></td>\n\n              <!-- Inputs -->\n\n              <td class="font-bold text-left total-ag-svc source-only-fields ag-svc-cell"><h4>-</h4></td>\n              <td class="font-bold text-left total-fee source-only-fields fee-cell"><h4>-</h4></td>\n              <td class="font-bold text-left total-sales-cc source-only-fields sales-cell"><h4>-</h4></td>\n              <td class="font-bold text-left total-sales-cash source-only-fields sales-cell"><h4>-</h4></td>\n              <td class="font-bold text-left total-food-sales source-only-fields food-and-beverage-cell"><h4>-</h4></td>\n              <td class="font-bold text-left total-beverage-sales source-only-fields food-and-beverage-cell"><h4>-</h4></td>\n              <td class="font-bold text-left total-cc-in source-only-fields"><h4>-</h4></td>\n              <td class="font-bold text-left total-cash-in source-only-fields"><h4>-</h4></td>\n\n              <!-- Tips distributed -->\n\n              <td class="before-tip-out-td font-bold text-left total-cc-out"><h4>-</h4></td>\n              <td class="before-tip-out-td font-bold text-left total-cash-out"><h4>-</h4></td>\n              <td class="before-tip-out-td font-bold text-left total-ag-svc-out ag-svc-cell"><h4>-</h4></td>\n              <td class="before-tip-out-td font-bold text-left total-fee-out fee-cell"><h4>-</h4></td>\n\n              <!-- Tip outs -->\n\n              <td class="font-bold text-left tip-outs total-tip-outs-given-cc"><h4>-</h4></td>\n              <td class="font-bold text-left tip-outs total-tip-outs-given-cash"><h4>-</h4></td>\n              <td class="font-bold text-left tip-outs total-tip-outs-given-ag-svc ag-svc-cell"><h4>-</h4></td>\n              <td class="font-bold text-left tip-outs total-tip-outs-given-fee fee-cell"><h4>-</h4></td>\n\n              <td class="font-bold text-left tip-outs total-tip-outs-received-cc"><h4>-</h4></td>\n              <td class="font-bold text-left tip-outs total-tip-outs-received-cash"><h4>-</h4></td>\n              <td class="font-bold text-left tip-outs total-tip-outs-received-ag-svc ag-svc-cell"><h4>-</h4></td>\n              <td class="font-bold text-left tip-outs total-tip-outs-received-fee fee-cell"><h4>-</h4></td>\n\n              <!-- Final distributed -->\n\n              <td class="font-bold text-left total-tips-distributed-cc"><h4>-</h4></td>\n              <td class="font-bold text-left total-tips-distributed-cash"><h4>-</h4></td>\n              <td class="font-bold text-left total-tips-distributed-ag-svc ag-svc-cell"><h4>-</h4></td>\n              <td class="font-bold text-left total-tips-distributed-fee fee-cell"><h4>-</h4></td>\n\n              <!-- Final tips column -->\n              <td class="total-final-tips-column"><h4>-</h4></td>\n              <td class="total-hourly-rate-column"><h4></h4></td>\n\n              <td class="actions hidden"></td>\n            </tr>\n            <tr class="employee-buttons">\n              <td colspan="');
      
        __out.push(__sanitize(this.team_count > 1 ? 21 : 20));
      
        __out.push('" class="text-right font-bold">\n                <button class="btn btn-info" type="button" data-action="edit-employee" data-position="');
      
        __out.push(__sanitize(this.position_type_name));
      
        __out.push('" data-team="');
      
        __out.push(__sanitize(this.team_no));
      
        __out.push('"><i class="fa fa-edit"></i> Edit</button>\n                <button class="btn btn-default hidden" type="button" data-action="edit-employee-cancel" data-position="');
      
        __out.push(__sanitize(this.position_type_name));
      
        __out.push('" data-team="');
      
        __out.push(__sanitize(this.team_no));
      
        __out.push('"><i class="fa fa-ban"></i> Cancel</button>\n                <button class="btn btn-success" type="button" data-action="add-employee" data-position="');
      
        __out.push(__sanitize(this.position_type_name));
      
        __out.push('" data-team="');
      
        __out.push(__sanitize(this.team_no));
      
        __out.push('" data-tour-step="add-employee"><i class="fa fa-plus"></i> EMPLOYEE</button>\n              </td>\n            </tr>\n          </tbody>\n        </table>\n      </div>\n    </div>\n  </div>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["calculation/history"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var calculation, dateData, i, j, len, len1, ref, ref1;
      
        ref = Tipmetric.Data.history_data;
        for (i = 0, len = ref.length; i < len; i++) {
          dateData = ref[i];
          __out.push('\n  <div class="row day-calculation-block" data-day-calculation-id=\'');
          __out.push(__sanitize(dateData[1].date_data._id.$oid));
          __out.push('\' data-day-calculation-locked=\'');
          __out.push(__sanitize(dateData[1].date_data.locked));
          __out.push('\'>\n    <div class="col-lg-12">\n      <div class="col-lg-12">\n        <div class="hpanel hgreen">\n          <div class="panel-body">\n            <h2 class="font-light m-b-xs">\n            ');
          __out.push(__sanitize(dateData[1].day_title));
          __out.push('\n\n            ');
          if (gon.current_user.workposition !== 'staff') {
            __out.push('\n              <span class="lock-icon">\n                <i class="fa fa-lock"></i>\n                <i class="fa fa-unlock-alt"></i>\n              </span>\n              ');
            if (gon.current_user.is_admin) {
              __out.push('\n                <a href="/day_calculations/');
              __out.push(__sanitize(dateData[1].date_data._id.$oid));
              __out.push('" class="track-icon" target="_blank"><i class=\'fa fa-history\'></i></a>\n              ');
            }
            __out.push('\n            ');
          }
          __out.push('\n\n            ');
          if (gon.restaurant.history_daily_reports_enabled) {
            __out.push('\n              <a href="');
            __out.push(__sanitize(dateData[1].day_history_report_url));
            __out.push('" class="track-icon" target="_blank"><i class=\'fa fa-print\'></i></a>\n            ');
          }
          __out.push('\n            </h2>\n            <div class="table-responsive">\n              <table class="table table-condensed">\n                <thead>\n                  <tr>\n                    <th>Area</th>\n                    <th>Shift</th>\n                    <th>Positions</th>\n                    <th>Source of tips</th>\n                    <th>');
          __out.push(__sanitize(_.findWhere(gon.restaurant.custom_labels.source_type_labels, {
            id: "cc"
          }).name));
          __out.push('</th>\n                    <th>');
          __out.push(__sanitize(_.findWhere(gon.restaurant.custom_labels.source_type_labels, {
            id: "cash"
          }).name));
          __out.push('</th>\n                    ');
          if (gon.restaurant.ag_svc_in_history_and_dashboard_is_shown) {
            __out.push('\n                    <th>');
            __out.push(__sanitize(_.findWhere(gon.restaurant.custom_labels.source_type_labels, {
              id: "agSvc"
            }).name));
            __out.push('</th>\n                    ');
          }
          __out.push('\n                    ');
          if (gon.restaurant.fee_in_history_and_dashboard_is_shown) {
            __out.push('\n                    <th>');
            __out.push(__sanitize(_.findWhere(gon.restaurant.custom_labels.source_type_labels, {
              id: "fee"
            }).name));
            __out.push('</th>\n                    ');
          }
          __out.push('\n                    <th class="actions">Actions</th>\n                  </tr>\n                </thead>\n                <tbody>\n                  ');
          ref1 = dateData[1].calculations;
          for (j = 0, len1 = ref1.length; j < len1; j++) {
            calculation = ref1[j];
            __out.push('\n                    <tr>\n                      <td>');
            __out.push(__sanitize(calculation.area_type_name_string));
            __out.push('\n                        ');
            if (gon.current_user.is_admin) {
              __out.push('\n                          <br><span class="label label-');
              __out.push(__sanitize(calculation.distribution_logic_label_color));
              __out.push('">');
              __out.push(__sanitize(calculation.distribution_logic_label));
              __out.push('</span>\n                        ');
            }
            __out.push('\n                        ');
            if (gon.restaurant.tip_out_report_enabled && calculation.distribution_logic_label === "source_tip_outs") {
              __out.push('\n                          <form id="report-form" action="/reports/download.xlsx" accept-charset="UTF-8" method="post">\n                            <input name="utf8" type="hidden" value="✓">\n                            <input type="hidden" name="excel_report_type" id="excel_report_type" value="tip_out_report">\n                            <input type="hidden" name="start" id="start" value="');
              __out.push(__sanitize(calculation.date));
              __out.push('">\n                            <input type="hidden" name="end" id="end" value="');
              __out.push(__sanitize(calculation.date));
              __out.push('">\n                            <input type="hidden" name="area_type_id" id="area_type_id" value="');
              __out.push(__sanitize(calculation.area_type_id));
              __out.push('">\n                            <input type="hidden" name="employee_id" id="employee_id" value="all">\n                            <input type="hidden" name="shift_type_id" id="shift_type_id" value="');
              __out.push(__sanitize(calculation.shift_type_id));
              __out.push('">\n                            <input type="hidden" name="position_type_id" id="position_type_id" value="all">\n                            <input type="submit" name="commit" value="Tip-Out Report" class="btn btn-primary btn-xs">\n                          </form>\n                        ');
            }
            __out.push('\n                      </td>\n                      <td>');
            __out.push(__sanitize(calculation.shift_type_name_string));
            __out.push('\n                        ');
            if (calculation.is_duplicated) {
              __out.push('\n                          <i class=\'fa fa-files-o\' title=\'Duplicated calculation\'></i>\n                        ');
            }
            __out.push('\n\n                        ');
            if (!calculation.is_correct) {
              __out.push('\n                          <i class=\'incorrect fa fa-exclamation-triangle\'></i>\n                        ');
            }
            __out.push('\n                      </td>\n                      <td>');
            __out.push(__sanitize(calculation.used_position_types_string));
            __out.push(' </td>\n                      <td>');
            __out.push(__sanitize(calculation.source_position_type_name_string));
            __out.push(' </td>\n                      <td>');
            __out.push(__sanitize(calculation.total_cc_tips_string));
            __out.push(' </td>\n                      <td>');
            __out.push(__sanitize(calculation.total_cash_tips_string));
            __out.push(' </td>\n                      ');
            if (gon.restaurant.ag_svc_in_history_and_dashboard_is_shown) {
              __out.push('\n                        <td>');
              __out.push(__sanitize(calculation.total_ag_svc_tips_string));
              __out.push(' </td>\n                      ');
            }
            __out.push('\n                      ');
            if (gon.restaurant.fee_in_history_and_dashboard_is_shown) {
              __out.push('\n                        <td>');
              __out.push(__sanitize(calculation.total_fee_tips_string));
              __out.push(' </td>\n                      ');
            }
            __out.push('\n                      <td class="actions">\n                        ');
            if (gon.restaurant.managers_edit_for_employees_enabled && _.contains(gon.current_user.allowed_area_ids, calculation.area_type_id)) {
              __out.push('\n                         <a href="');
              if (gon.current_user.workposition === 'staff') {
                __out.push(__sanitize('/employees'));
              }
              __out.push('/show_calculation_get?calculation_id=');
              __out.push(__sanitize(calculation.id));
              __out.push('&access_level=manage" class="btn btn-info btn-xs show-calculation manage">Manager Edit</a> \n                        ');
            }
            __out.push('\n                        <a href="');
            if (gon.current_user.workposition === 'staff') {
              __out.push(__sanitize('/employees'));
            }
            __out.push('/show_calculation_get?calculation_id=');
            __out.push(__sanitize(calculation.id));
            __out.push('" class="btn btn-info btn-xs show-calculation">Show</a>\n\n                        ');
            if (gon.current_user.workposition !== 'staff') {
              __out.push('\n                          <a href="#" data-calculation-id="');
              __out.push(__sanitize(calculation.id));
              __out.push('" class="btn btn-danger btn-xs destroy-calculation" data-toggle="modal" data-target=".duplicate-calculation" data-calculation-id="');
              __out.push(__sanitize(calculation.id));
              __out.push('" title="Remove calculation"><i class=\'fa fa-remove\'></i></a>\n                        ');
            }
            __out.push('\n                      </td>\n                    </tr>\n                  ');
          }
          __out.push('\n                </tbody>\n              </table>\n            </div>\n          </div>\n        </div>\n      </div>\n    </div>\n  </div>\n');
        }
      
        __out.push('\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["calculation/overtips/overtip"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var employee, i, j, k, len, len1, len2, ref, ref1, ref2, sourceType;
      
        __out.push('<tr\n  class="overtip"\n  data-overtip-id="');
      
        __out.push(__sanitize(this.id));
      
        __out.push('"\n  data-source-employee-id="');
      
        __out.push(__sanitize(this.sourceEmployeeSelected));
      
        __out.push('"\n  data-target-employee-id="');
      
        __out.push(__sanitize(this.nonSourceEmployeeSelected));
      
        __out.push('"\n  data-source-type="');
      
        __out.push(__sanitize(this.sourceTypeSelected));
      
        __out.push('"\n  data-temp-id="');
      
        __out.push(__sanitize(this.tempId));
      
        __out.push('"\n  data-status="');
      
        __out.push(__sanitize(this.status));
      
        __out.push('"\n  data-original-percentage="');
      
        __out.push(__sanitize(this.percentage));
      
        __out.push('"\n>\n  <td class="source-employee-name-wrapper">\n    <div class="source-employee-name"></div>\n    <div class="source-employee-select">\n      <select class=\'select-2 source-employee-select\' data-recalculate=\'true\' style=\'width: 100%\' >\n        ');
      
        ref = this.employees;
        for (i = 0, len = ref.length; i < len; i++) {
          employee = ref[i];
          __out.push('\n          ');
          if (employee.identifier === this.sourceEmployeeSelected) {
            __out.push('\n            <option value="');
            __out.push(__sanitize(employee.identifier));
            __out.push('" class=\'123\' data-disabled=\'321\' data-employee-id="');
            __out.push(__sanitize(employee.employeeInfo.id));
            __out.push('" data-position-id="');
            __out.push(__sanitize(employee.position.id));
            __out.push('" data-position-name="');
            __out.push(__sanitize(employee.position.name));
            __out.push('" selected=\'selected\'>');
            __out.push(__sanitize(employee.employeeInfo.full_info + " (" + employee.position.name + ")"));
            __out.push('</option>\n          ');
          } else {
            __out.push('\n            <option value="');
            __out.push(__sanitize(employee.identifier));
            __out.push('" class=\'123\' data-disabled=\'321\' data-employee-id="');
            __out.push(__sanitize(employee.employeeInfo.id));
            __out.push('" data-position-id="');
            __out.push(__sanitize(employee.position.id));
            __out.push('" data-position-name="');
            __out.push(__sanitize(employee.position.name));
            __out.push('">');
            __out.push(__sanitize(employee.employeeInfo.full_info + " (" + employee.position.name + ")"));
            __out.push('</option>\n          ');
          }
          __out.push('\n        ');
        }
      
        __out.push('\n      </select>\n    </div>\n  </td>\n  <td class="percentage-wrapper">\n    <span class="percentage-info"></span>\n    <input class="percentage form-control" type="text" name="percentage" value="');
      
        __out.push(__sanitize(this.percentage || 0));
      
        __out.push('" data-recalculate="true">\n  </td>\n  <td class="source-type-wrapper">\n    <div class="source-type-info"></div>\n    <div class="source-type-select">\n      <select class=\'select-2 source-type-select\' data-recalculate=\'true\' style=\'width: 100%\' >\n        ');
      
        ref1 = this.sourceTypes;
        for (j = 0, len1 = ref1.length; j < len1; j++) {
          sourceType = ref1[j];
          __out.push('\n          ');
          if (sourceType.id === this.sourceTypeSelected) {
            __out.push('\n            <option value=\'');
            __out.push(__sanitize(sourceType.id));
            __out.push('\' selected=\'selected\'>');
            __out.push(__sanitize(sourceType.name));
            __out.push('</option>\n          ');
          } else {
            __out.push('\n            <option value=\'');
            __out.push(__sanitize(sourceType.id));
            __out.push('\'>');
            __out.push(__sanitize(sourceType.name));
            __out.push('</option>\n          ');
          }
          __out.push('\n        ');
        }
      
        __out.push('\n      </select>\n    </div>\n  </td>\n  <td class="target-employee-name-wrapper">\n    <div class="target-employee-name"></div>\n    <div class="target-employee-select">\n      <select class=\'select-2 target-employee-select\' data-recalculate=\'true\' style=\'width: 100%\' >\n        ');
      
        ref2 = this.employees;
        for (k = 0, len2 = ref2.length; k < len2; k++) {
          employee = ref2[k];
          __out.push('\n          ');
          if (employee.identifier === this.nonSourceEmployeeSelected) {
            __out.push('\n            <option value="');
            __out.push(__sanitize(employee.identifier));
            __out.push('" selected=\'selected\' data-employee-id="');
            __out.push(__sanitize(employee.employeeInfo.id));
            __out.push('" data-position-id="');
            __out.push(__sanitize(employee.position.id));
            __out.push('" data-position-name="');
            __out.push(__sanitize(employee.position.name));
            __out.push('">');
            __out.push(__sanitize(employee.employeeInfo.full_info + " (" + employee.position.name + ")"));
            __out.push('</option>\n          ');
          } else {
            __out.push('\n            <option value="');
            __out.push(__sanitize(employee.identifier));
            __out.push('" data-employee-id="');
            __out.push(__sanitize(employee.employeeInfo.id));
            __out.push('" data-position-id="');
            __out.push(__sanitize(employee.position.id));
            __out.push('" data-position-name="');
            __out.push(__sanitize(employee.position.name));
            __out.push('">');
            __out.push(__sanitize(employee.employeeInfo.full_info + " (" + employee.position.name + ")"));
            __out.push('</option>\n          ');
          }
          __out.push('\n        ');
        }
      
        __out.push('\n      </select>\n    </div>\n  </td>\n\n  <td class="total">-</td>\n  <td class="actions">\n    <a href="#" class="btn btn-danger btn-sm" data-action="destroy-overtip"><i class="fa fa-remove"></i></a>\n  </td>\n</tr>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["calculation/percent_variation"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var i, len, position, ref;
      
        __out.push('<div class="col-sm-4 implement-percents-wrapper">\n  <div class=\'implement-percents\' template-id="');
      
        __out.push(__sanitize(this._id.$oid));
      
        __out.push('">\n    ');
      
        if (this.name) {
          __out.push('\n      <h5>');
          __out.push(__sanitize(this.name.toUpperCase()));
          __out.push(' (');
          __out.push(__sanitize(this.distribution_type === 'percents' ? '%' : 'pts'));
          __out.push(')</h5>\n    ');
        }
      
        __out.push('\n    ');
      
        ref = this.percent_distributions;
        for (i = 0, len = ref.length; i < len; i++) {
          position = ref[i];
          __out.push('\n      ');
          __out.push(__sanitize(position.position_type_name.toUpperCase()));
          __out.push(': ');
          __out.push(__sanitize(position.percentage));
          __out.push(__sanitize(this.distribution_type === 'percents' ? '%' : 'pts'));
          __out.push('<br>\n    ');
        }
      
        __out.push('\n    <br>\n    <button class="btn btn-sm btn-success use-template" template-id="');
      
        __out.push(__sanitize(this._id.$oid));
      
        __out.push('">Apply</button>\n    <span class="btn btn-sm btn-danger remove-template" data-action="remove-template" template-id="');
      
        __out.push(__sanitize(this._id.$oid));
      
        __out.push('"><i class="fa fa-remove"></i></span>\n  </div>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["calculation/percent_variations"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<div id="percent_variations" class="col-sm-12">\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["calculation/position_tip_sharings/position_tip_sharing"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var conditionFirst, conditionType, i, j, k, l, len, len1, len2, len3, len4, m, ref, ref1, ref2, ref3, ref4, sourcePosition, sourceType;
      
        __out.push('<tr\n  class="position-tip-sharing"\n  data-position-tip-sharing-id="');
      
        __out.push(__sanitize(this.id));
      
        __out.push('"\n  data-source-position-id="');
      
        __out.push(__sanitize(this.sourcePositionSelected));
      
        __out.push('"\n  data-target-position-id="');
      
        __out.push(__sanitize(this.nonSourcePositionSelected));
      
        __out.push('"\n  data-source-type="');
      
        __out.push(__sanitize(this.sourceTypeSelected));
      
        __out.push('"\n  data-temp-id="');
      
        __out.push(__sanitize(this.tempId));
      
        __out.push('"\n  data-status="');
      
        __out.push(__sanitize(this.status));
      
        __out.push('"\n  data-original-percentage="');
      
        __out.push(__sanitize(this.percentage));
      
        __out.push('"\n>\n  <td class="source-position-name-wrapper">\n    <div class="source-position-name"></div>\n    <div class="source-position-select">\n      <select class=\'select-2 source-position-select\' data-recalculate=\'true\' style=\'width: 100%\' >\n        ');
      
        ref = this.sourcePositions;
        for (i = 0, len = ref.length; i < len; i++) {
          sourcePosition = ref[i];
          __out.push('\n          ');
          if (sourcePosition.id === this.sourcePositionSelected) {
            __out.push('\n            <option value="');
            __out.push(__sanitize(sourcePosition.id));
            __out.push('" class=\'123\' data-disabled=\'321\' selected=\'selected\'>');
            __out.push(__sanitize(sourcePosition.name));
            __out.push('</option>\n          ');
          } else {
            __out.push('\n            <option value="');
            __out.push(__sanitize(sourcePosition.id));
            __out.push('" class=\'123\' data-disabled=\'321\'>');
            __out.push(__sanitize(sourcePosition.name));
            __out.push('</option>\n          ');
          }
          __out.push('\n        ');
        }
      
        __out.push('\n      </select>\n    </div>\n  </td>\n  <td class="percentage-wrapper">\n    <span class="percentage-info"></span>\n    <input class="percentage form-control" type="text" name="percentage" value="');
      
        __out.push(__sanitize(this.percentage || 0));
      
        __out.push('" data-recalculate="true">\n  </td>\n  <td class="source-type-wrapper">\n    <div class="source-type-info"></div>\n    <div class="source-type-select">\n      <select class=\'select-2 source-type-select\' data-recalculate=\'true\' style=\'width: 100%\' >\n        ');
      
        ref1 = this.sourceTypes;
        for (j = 0, len1 = ref1.length; j < len1; j++) {
          sourceType = ref1[j];
          __out.push('\n          ');
          if (sourceType.id === this.sourceTypeSelected) {
            __out.push('\n            <option value=\'');
            __out.push(__sanitize(sourceType.id));
            __out.push('\' selected=\'selected\'>');
            __out.push(__sanitize(sourceType.name));
            __out.push('</option>\n          ');
          } else {
            __out.push('\n            <option value=\'');
            __out.push(__sanitize(sourceType.id));
            __out.push('\'>');
            __out.push(__sanitize(sourceType.name));
            __out.push('</option>\n          ');
          }
          __out.push('\n        ');
        }
      
        __out.push('\n      </select>\n    </div>\n  </td>\n  <td class="target-position-name-wrapper">\n    <div class="target-position-name"></div>\n    <div class="target-position-select">\n      <select class=\'select-2 target-position-select\' data-recalculate=\'true\' style=\'width: 100%\' >\n        ');
      
        ref2 = this.nonSourcePositions;
        for (k = 0, len2 = ref2.length; k < len2; k++) {
          sourcePosition = ref2[k];
          __out.push('\n          ');
          if (sourcePosition.id === this.nonSourcePositionSelected) {
            __out.push('\n            <option value="');
            __out.push(__sanitize(sourcePosition.id));
            __out.push('" selected=\'selected\'>');
            __out.push(__sanitize(sourcePosition.name));
            __out.push('</option>\n          ');
          } else {
            __out.push('\n            <option value="');
            __out.push(__sanitize(sourcePosition.id));
            __out.push('">');
            __out.push(__sanitize(sourcePosition.name));
            __out.push('</option>\n          ');
          }
          __out.push('\n        ');
        }
      
        __out.push('\n      </select>\n    </div>\n  </td>\n\n  <td class=\'conditions-cell\'>\n    <div class=\'conditions-cell-line\'>\n      <div class="condition-first-wrapper">\n        <div class="condition-first-info"></div>\n        <div class="condition-first-select">\n          <select class=\'select-2 condition-first-select\' data-recalculate=\'true\' style=\'width: 100%\' >\n            ');
      
        ref3 = this.sourceTypes;
        for (l = 0, len3 = ref3.length; l < len3; l++) {
          conditionFirst = ref3[l];
          __out.push('\n              ');
          if (conditionFirst.id === this.conditionFirstSelected) {
            __out.push('\n                <option value="');
            __out.push(__sanitize(conditionFirst.id));
            __out.push('" selected=\'selected\'>');
            __out.push(__sanitize(conditionFirst.name));
            __out.push('</option>\n              ');
          } else {
            __out.push('\n                <option value="');
            __out.push(__sanitize(conditionFirst.id));
            __out.push('">');
            __out.push(__sanitize(conditionFirst.name));
            __out.push('</option>\n              ');
          }
          __out.push('\n            ');
        }
      
        __out.push('\n          </select>\n        </div>\n      </div>\n\n      <div class="condition-type-wrapper">\n        <div class="condition-type-info"></div>\n        <div class="condition-type-select">\n          <select class=\'select-2 condition-type-select\' data-recalculate=\'true\' style=\'width: 100%\' >\n            ');
      
        ref4 = this.conditionTypes;
        for (m = 0, len4 = ref4.length; m < len4; m++) {
          conditionType = ref4[m];
          __out.push('\n              ');
          if (conditionType.id === this.conditionTypeSelected) {
            __out.push('\n                <option value="');
            __out.push(__sanitize(conditionType.id));
            __out.push('" selected=\'selected\'>');
            __out.push(__sanitize(conditionType.name));
            __out.push('</option>\n              ');
          } else {
            __out.push('\n                <option value="');
            __out.push(__sanitize(conditionType.id));
            __out.push('">');
            __out.push(__sanitize(conditionType.name));
            __out.push('</option>\n              ');
          }
          __out.push('\n            ');
        }
      
        __out.push('\n          </select>\n        </div>\n      </div>\n\n      <div class="condition-second-wrapper">\n        <span class="condition-second-info"></span>\n        <input class="condition-second-input form-control" type="text" name="condition_second" value="');
      
        __out.push(__sanitize(this.conditionSecond || 0));
      
        __out.push('" data-recalculate="true">\n      </div>\n    </div>\n  </td>\n\n  <td class="total">-</td>\n  <td class="actions">\n    <a href="#" class="btn btn-danger btn-sm" data-action="destroy-tip-sharing"><i class="fa fa-remove"></i></a>\n  </td>\n</tr>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["calculation/sto_template"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var i, len, position, ref;
      
        __out.push('<div class="col-sm-4 implement-percents-wrapper">\n  <div class=\'implement-percents\' template-id="');
      
        __out.push(__sanitize(this._id.$oid));
      
        __out.push('">\n    ');
      
        if (this.name) {
          __out.push('\n      <h5>');
          __out.push(__sanitize(this.name.toUpperCase()));
          __out.push('</h5>\n    ');
        }
      
        __out.push('\n    ');
      
        if (_.some(this.source_tip_outs)) {
          __out.push('\n    ');
          ref = this.source_tip_outs;
          for (i = 0, len = ref.length; i < len; i++) {
            position = ref[i];
            __out.push('\n      <span>');
            __out.push(__sanitize(position.source_position_name));
            __out.push('</span> ');
            __out.push(__sanitize(position.percentage));
            __out.push('% <span>');
            __out.push(__sanitize(position.source_type_name));
            __out.push('</span> to <span>');
            __out.push(__sanitize(position.target_position_name));
            __out.push('</span>\n      ');
            if (gon.restaurant.sto_condition_rules_enabled && !_.isEmpty(position.condition_first)) {
              __out.push('\n        (<span>');
              __out.push(__sanitize(Tipmetric.Controllers.tipSharingToggleView.getSourceTypeById(position.condition_first)));
              __out.push('</span> <span>');
              __out.push(__sanitize(Tipmetric.Controllers.tipSharingToggleView.conditionTypeNameById(position.condition_type)));
              __out.push('</span> <span>');
              __out.push(__sanitize(position.condition_second));
              __out.push('</span>)\n      ');
            }
            __out.push('\n      <br>\n      <br>\n    ');
          }
          __out.push('\n    ');
        }
      
        __out.push('\n    <br>\n    <button class="btn btn-success use-template" template-id="');
      
        __out.push(__sanitize(this._id.$oid));
      
        __out.push('">Apply</button>\n    <span class="btn btn-danger remove-template" data-action="remove-template" template-id="');
      
        __out.push(__sanitize(this._id.$oid));
      
        __out.push('"><i class="fa fa-remove"></i></span>\n  </div>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["calculation/sto_templates"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<div id="percent_variations" class="col-sm-12">\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["calculation/tip_outs/existing_tip_out"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var area, i, j, k, l, len, len1, len2, len3, position, ref, ref1, ref2, ref3, shift, subtype;
      
        __out.push('<tr\n  class=\'tip-out-line\'\n  data-area-name=\'\'\n  data-shift-type-name=\'\'\n  data-tip-out-status=\'persisted\'\n  data-tip-out-id=\'');
      
        __out.push(__sanitize(this._id.$oid));
      
        __out.push('\'\n  data-tip-out-type=\'');
      
        __out.push(__sanitize(this.tip_out_type));
      
        __out.push('\'\n  data-tip-out-food-subtype=\'');
      
        __out.push(__sanitize(this.food_subtype));
      
        __out.push('\'\n>\n <!--  ');
      
        if (this.partialType === "general") {
          __out.push('\n  <td class=\'position-type-select-wrapper position-cell\'>\n    <select id=\'position_type_id\' class=\'select-2 position-id\' style=\'width: 100%\' >\n      ');
          ref = this.freePositionItems;
          for (i = 0, len = ref.length; i < len; i++) {
            position = ref[i];
            __out.push('\n        ');
            if (position.id === "all") {
              __out.push('\n          <option value=\'');
              __out.push(__sanitize(position.id));
              __out.push('\' selected=\'selected\'>');
              __out.push(__sanitize(position.name));
              __out.push('</option>\n        ');
            } else {
              __out.push('\n          <option value=\'');
              __out.push(__sanitize(position.id));
              __out.push('\'>');
              __out.push(__sanitize(position.name));
              __out.push('</option>\n        ');
            }
            __out.push('\n      ');
          }
          __out.push('\n    </select>\n  </td>\n  ');
        }
      
        __out.push(' -->\n\n  <td class=\'food-subtype-select-wrapper\'>\n    <select id=\'subtype\' class=\'select-2 subtype\' data-recalculate=\'true\' style=\'width: 100%\' >\n      ');
      
        ref1 = ['food', 'beverage', 'total'];
        for (j = 0, len1 = ref1.length; j < len1; j++) {
          subtype = ref1[j];
          __out.push('\n        ');
          if (subtype === this.food_subtype) {
            __out.push('\n          <option value=\'');
            __out.push(__sanitize(subtype));
            __out.push('\' selected=\'selected\'>');
            __out.push(__sanitize(subtype));
            __out.push('</option>\n        ');
          } else {
            __out.push('\n          <option value=\'');
            __out.push(__sanitize(subtype));
            __out.push('\'>');
            __out.push(__sanitize(subtype));
            __out.push('</option>\n        ');
          }
          __out.push('\n      ');
        }
      
        __out.push('\n    </select>\n  </td>\n\n  <td class=\'area-type-select-wrapper\'>\n    <select id=\'area_type_id\' class=\'select-2 area-id\' data-recalculate=\'true\' style=\'width: 100%\' >\n      ');
      
        ref2 = gon.restaurant.restaurant_area_types;
        for (k = 0, len2 = ref2.length; k < len2; k++) {
          area = ref2[k];
          __out.push('\n        ');
          if (area._id.$oid === this.receiver_id.$oid) {
            __out.push('\n          <option value=\'');
            __out.push(__sanitize(area._id.$oid));
            __out.push('\' selected=\'selected\'>');
            __out.push(__sanitize(area.name));
            __out.push('</option>\n        ');
          } else {
            __out.push('\n          <option value=\'');
            __out.push(__sanitize(area._id.$oid));
            __out.push('\'>');
            __out.push(__sanitize(area.name));
            __out.push('</option>\n        ');
          }
          __out.push('\n      ');
        }
      
        __out.push('\n    </select>\n    <span class=\'actions hidden\'>\n      <span class=\'btn btn-danger btn-sm\' type=\'button\' data-action=\'remove-tip-out-distribution\'><i class=\'fa fa-minus-circle\'></i></span>\n    </span>\n  </td>\n\n  ');
      
        if (gon.restaurant.shifted_tip_outs_enabled) {
          __out.push('\n    <td class=\'shift-type-select-wrapper\'>\n      <select id=\'shift_type_id\' class=\'select-2 shift-type-id\' data-recalculate=\'true\' style=\'width: 100%\' >\n        ');
          ref3 = gon.restaurant.restaurant_shift_types;
          for (l = 0, len3 = ref3.length; l < len3; l++) {
            shift = ref3[l];
            __out.push('\n          ');
            if (shift._id.$oid === this.shift_type_id.$oid) {
              __out.push('\n            <option value=\'');
              __out.push(__sanitize(shift._id.$oid));
              __out.push('\' selected=\'selected\'>');
              __out.push(__sanitize(shift.name));
              __out.push('</option>\n          ');
            } else {
              __out.push('\n            <option value=\'');
              __out.push(__sanitize(shift._id.$oid));
              __out.push('\'>');
              __out.push(__sanitize(shift.name));
              __out.push('</option>\n          ');
            }
            __out.push('\n        ');
          }
          __out.push('\n      </select>\n    </td>\n  ');
        }
      
        __out.push('\n\n  <td><input class=\'touch-spin tip-out-percentage\' type=\'text\'  name=\'touch-spin\' value=\'');
      
        __out.push(__sanitize(this.percentage));
      
        __out.push('\' data-recalculate=\'true\' data-recalculate-total-numbers=\'true\'></td>\n  <td class=\'active cc_summ\'>-</td>\n  <!-- <td class=\'active cash_summ\'>-</td> -->\n  <td class=\'active ag_svc_summ ag-svc-cell\'>-</td>\n  <td class=\'active fee_summ fee-cell\'>-</td>\n</tr>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["calculation/tip_outs/tip_out"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var area, i, j, k, l, len, len1, len2, len3, position, ref, ref1, ref2, ref3, shift, subtype;
      
        __out.push('<tr\n  class=\'tip-out-line\'\n  data-area-name=\'\'\n  data-shift-type-name=\'\'\n  data-tip-out-status=\'new\'\n  data-tip-out-type=\'');
      
        __out.push(__sanitize(this.tipOutType));
      
        __out.push('\'\n  data-tip-out-food-subtype=\'');
      
        __out.push(__sanitize(this.tipOutType));
      
        __out.push('\'\n>\n  ');
      
        if (this.partialType === "general") {
          __out.push('\n  <td class=\'position-type-select-wrapper position-cell\'>\n    <select id=\'position_type_id\' class=\'select-2 position-id\' data-recalculate=\'true\' style=\'width: 100%\' >\n      ');
          ref = this.freePositionItems;
          for (i = 0, len = ref.length; i < len; i++) {
            position = ref[i];
            __out.push('\n        ');
            if (position.id === "all") {
              __out.push('\n          <option value=\'');
              __out.push(__sanitize(position.id));
              __out.push('\' selected=\'selected\'>');
              __out.push(__sanitize(position.name));
              __out.push('</option>\n        ');
            } else {
              __out.push('\n          <option value=\'');
              __out.push(__sanitize(position.id));
              __out.push('\'>');
              __out.push(__sanitize(position.name));
              __out.push('</option>\n        ');
            }
            __out.push('\n      ');
          }
          __out.push('\n    </select>\n  </td>\n  ');
        }
      
        __out.push('\n\n  <td class=\'food-subtype-select-wrapper\'>\n    <select id=\'subtype\' class=\'select-2 subtype\' data-recalculate=\'true\' style=\'width: 100%\' >\n      ');
      
        ref1 = ['food', 'beverage', 'total'];
        for (j = 0, len1 = ref1.length; j < len1; j++) {
          subtype = ref1[j];
          __out.push('\n        <option value=\'');
          __out.push(__sanitize(subtype));
          __out.push('\'>');
          __out.push(__sanitize(subtype));
          __out.push('</option>\n      ');
        }
      
        __out.push('\n    </select>\n  </td>\n\n  <td class=\'area-type-select-wrapper\'>\n    <select id=\'area_type_id\' class=\'select-2 area-id\' data-recalculate=\'true\' style=\'width: 100%\' >\n      ');
      
        ref2 = this.freeAreaItems;
        for (k = 0, len2 = ref2.length; k < len2; k++) {
          area = ref2[k];
          __out.push('\n        <option value=\'');
          __out.push(__sanitize(area.id));
          __out.push('\'>');
          __out.push(__sanitize(area.name));
          __out.push('</option>\n      ');
        }
      
        __out.push('\n    </select>\n    <span class=\'actions hidden\'>\n      <span class=\'btn btn-danger btn-sm\' type=\'button\' data-action=\'remove-tip-out-distribution\'><i class=\'fa fa-minus-circle\'></i></span>\n    </span>\n  </td>\n\n    ');
      
        if (gon.restaurant.shifted_tip_outs_enabled) {
          __out.push('\n      <td class=\'shift-type-select-wrapper\'>\n        <select id=\'shift_type_id\' class=\'select-2 shift-type-id\' data-recalculate=\'true\' style=\'width: 100%\' >\n          ');
          ref3 = this.shiftItems;
          for (l = 0, len3 = ref3.length; l < len3; l++) {
            shift = ref3[l];
            __out.push('\n            ');
            if (shift.id === gon.calculation_params.shift_type_id) {
              __out.push('\n              <option value=\'');
              __out.push(__sanitize(shift.id));
              __out.push('\' selected=\'selected\'>');
              __out.push(__sanitize(shift.name));
              __out.push('</option>\n            ');
            } else {
              __out.push('\n              <option value=\'');
              __out.push(__sanitize(shift.id));
              __out.push('\'>');
              __out.push(__sanitize(shift.name));
              __out.push('</option>\n            ');
            }
            __out.push('\n          ');
          }
          __out.push('\n        </select>\n      </td>\n    ');
        }
      
        __out.push('\n\n  <td><input class=\'touch-spin tip-out-percentage\' type=\'text\'  name=\'touch-spin\' value=\'0\' data-recalculate=\'true\' data-recalculate-total-numbers=\'true\'></td>\n  <td class=\'active cc_summ\'>-</td>\n  <!-- <td class=\'active cash_summ\'>-</td> -->\n  <td class=\'active ag_svc_summ ag-svc-cell\'>-</td>\n  <td class=\'active fee_summ fee-cell\'>-</td>\n</tr>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["calculation/tip_sharings/employee_select"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var employee, i, len, ref;
      
        __out.push('<select class="form-control" name="employee" id="employee_id" >\n  ');
      
        ref = this.employees;
        for (i = 0, len = ref.length; i < len; i++) {
          employee = ref[i];
          __out.push('\n    ');
          if (employee.id === this.selected_id) {
            __out.push('\n      <option value="');
            __out.push(__sanitize(employee.id));
            __out.push('" selected="selected">');
            __out.push(__sanitize(employee.full_info));
            __out.push('</option>\n    ');
          } else {
            __out.push('\n      <option value="');
            __out.push(__sanitize(employee.id));
            __out.push('">');
            __out.push(__sanitize(employee.full_info));
            __out.push('</option>\n    ');
          }
          __out.push('\n  ');
        }
      
        __out.push('\n</select>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["calculation/tip_sharings/position_types"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var i, len, position_type, ref;
      
        __out.push('<select class="form-control" name="position_type" id="position_type_id">\n  ');
      
        ref = this;
        for (i = 0, len = ref.length; i < len; i++) {
          position_type = ref[i];
          __out.push('\n    ');
          if (position_type.position_type_id === this.selected_id) {
            __out.push('\n      <option value="');
            __out.push(__sanitize(position_type.position_type_id));
            __out.push('" selected="selected">');
            __out.push(__sanitize(position_type.position_type_name));
            __out.push('</option>\n    ');
          } else {
            __out.push('\n      <option value="');
            __out.push(__sanitize(position_type.position_type_id));
            __out.push('">');
            __out.push(__sanitize(position_type.position_type_name));
            __out.push('</option>\n    ');
          }
          __out.push('\n  ');
        }
      
        __out.push('\n</select>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["calculation/tip_sharings/tip_sharing"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<div class="tip-sharing" data-tip-sharing-status="');
      
        __out.push(__sanitize(this.status));
      
        __out.push('" data-tip-sharing-id="');
      
        __out.push(__sanitize(this.tempId));
      
        __out.push('" data-position-id="');
      
        __out.push(__sanitize(this.position_type_id));
      
        __out.push('" data-receiver-id="');
      
        __out.push(__sanitize(this.receiver_id));
      
        __out.push('">\n  <div class="position-name-wrapper">\n    <span class="position-name">');
      
        __out.push(__sanitize(this.position_name));
      
        __out.push('</span>\n    <span class="position-name-select"></span>\n  </div>:\n  <div class="receiver-info-wrapper">\n    <span class="receiver-info">');
      
        __out.push(__sanitize(this.receiver_info));
      
        __out.push('</span>\n    <span class="receiver-info-select"></span>\n  </div>\n   -<span class="cc-editable" contenteditable="');
      
        __out.push(__sanitize(this.status === 'new'));
      
        __out.push('">');
      
        __out.push(__sanitize(this.cc_summ || '0.00'));
      
        __out.push('</span> CC\n   -<span class="cash-editable" contenteditable="');
      
        __out.push(__sanitize(this.status === 'new'));
      
        __out.push('">');
      
        __out.push(__sanitize(this.cash_summ || '0.00'));
      
        __out.push('</span> CASH\n\n   <span class="btn btn-success" data-action="create-tip-sharing">Create</span>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["csv_import/calculation_exists"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<div class="calculation-exists" data-calculation-date="');
      
        __out.push(__sanitize(this['date']));
      
        __out.push('">\n  <div class=\'employee-is-duplicated\' data-emp-id="">\n    ');
      
        __out.push(__sanitize(this['calculation_exists_message']));
      
        __out.push('\n    <p>\n      <button class=\'btn btn-xs btn-success\' data-action=\'update-date\'>Yes</button>\n      <button class=\'btn btn-xs btn-default\' data-action=\'skip-date\'>Skip</button>\n    </p>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["csv_import/date_errors_wrapper"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<div class="import-errors" data-errors-for-date="');
      
        __out.push(__sanitize(this));
      
        __out.push('">\n  <h2>Errors for ');
      
        __out.push(__sanitize(this));
      
        __out.push('</h2>\n  <div class="calculation-exists-wrapper">\n  </div>\n  <div class="day-is-not-enabled-error-wrapper">\n  </div>\n  <div class="employees-doesnt-exist">\n  </div>\n  <div class="last-name-duplication">\n  </div>\n  <div class="employee-in-multiple-positions">\n  </div>\n  <div class="employees-doesnt-belong-to-any-position">\n  </div>\n  <div class="employees-are-duplicated">\n  </div>\n  <div class="employees-doesnt-belong-auto-selected-position">\n  </div>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["csv_import/day_is_not_enabled_error"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<div class="day-is-not-enabled-error" data-calculation-date="');
      
        __out.push(__sanitize(this['date']));
      
        __out.push('">\n  <div class=\'employee-is-duplicated\' data-emp-id="">\n    ');
      
        __out.push(__sanitize(this['day_is_not_enabled_error_message']));
      
        __out.push('\n    <p>\n      <button class=\'btn btn-xs btn-default\' data-action=\'hide-error\'>Ok, got it</button>\n    </p>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["csv_import/does_not_exist"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var i, len, positionType, ref;
      
        __out.push('<div class=\'doesnt-exist-warning-wrapper\' data-row-number="');
      
        __out.push(__sanitize(this['row_number']));
      
        __out.push('" data-emp-id="');
      
        __out.push(__sanitize(this[':emp_id']));
      
        __out.push('" data-square-id="');
      
        __out.push(__sanitize(this[':square_id']));
      
        __out.push('" data-seven-shift-id="');
      
        __out.push(__sanitize(this[':seven_shift_id']));
      
        __out.push('" data-brink-id="');
      
        __out.push(__sanitize(this[':brink_id']));
      
        __out.push('" data-first-and-last-name="');
      
        __out.push(__sanitize(this[':first_name :last_name']));
      
        __out.push('" data-last-and-first-name="');
      
        __out.push(__sanitize(this[':last_name, :first_name']));
      
        __out.push('">\n  <div class=\'doesnt-exist-warning\' data-emp-id="');
      
        __out.push(__sanitize(this[':emp_id']));
      
        __out.push('">\n    ');
      
        if (!_.isEmpty(this[":first_name :last_name"])) {
          __out.push('\n      ');
          __out.push(__sanitize(_.compact(this[':first_name :last_name'].split(' '))[0]));
          __out.push(' ');
          __out.push(__sanitize(_.compact(this[':first_name :last_name'].split(' '))[1]));
          __out.push(' (');
          __out.push(__sanitize(this[':emp_id']));
          __out.push(') is missing from Employee Page...press Remove button to skip Employee during import.\n    ');
        } else if (!_.isEmpty(this[":last_name, :first_name"])) {
          __out.push('\n      ');
          __out.push(__sanitize(this[':last_name, :first_name'].split(', ')[1]));
          __out.push(' ');
          __out.push(__sanitize(this[':last_name, :first_name'].split(', ')[0]));
          __out.push(' (');
          __out.push(__sanitize(this[':emp_id']));
          __out.push(') is missing from Employee Page...press Remove button to skip Employee during import.\n    ');
        } else {
          __out.push('\n      ');
          __out.push(__sanitize(this[':first_name']));
          __out.push(' ');
          __out.push(__sanitize(this[':last_name']));
          __out.push(' (');
          __out.push(__sanitize(this[':emp_id']));
          __out.push(') is missing from Employee Page...press Remove button to skip Employee during import.\n    ');
        }
      
        __out.push('\n    <button class=\'btn btn-xs btn-danger\' data-row-number="');
      
        __out.push(__sanitize(this['row_number']));
      
        __out.push('" data-emp-id="');
      
        __out.push(__sanitize(this[':emp_id']));
      
        __out.push('" data-first-and-last-name="');
      
        __out.push(__sanitize(this[':first_name :last_name']));
      
        __out.push('" data-last-and-first-name="');
      
        __out.push(__sanitize(this[':last_name, :first_name']));
      
        __out.push('" data-action=\'remove\'>Remove</button>\n    <button class=\'btn btn-xs btn-success\' data-emp-id="');
      
        __out.push(__sanitize(this[':emp_id']));
      
        __out.push('" data-first-and-last-name="');
      
        __out.push(__sanitize(this[':first_name :last_name']));
      
        __out.push('" data-last-and-first-name="');
      
        __out.push(__sanitize(this[':last_name, :first_name']));
      
        __out.push('" data-action=\'add\'>Add</button>\n    <!-- <button class=\'btn btn-xs btn-success\' data-emp-id="');
      
        __out.push(__sanitize(this[':emp_id']));
      
        __out.push('" data-emp-id="');
      
        __out.push(__sanitize(this[':first_name']));
      
        __out.push('" data-emp-id="');
      
        __out.push(__sanitize(this[':last_name']));
      
        __out.push('" data-action=\'create\'>Add</button> -->\n  </div>\n\n  <div class=\'doesnt-exist-warning-form\' data-emp-id="');
      
        __out.push(__sanitize(this[':emp_id']));
      
        __out.push('">\n    <input class="form-control" type="text" name="emp_id" value="');
      
        __out.push(__sanitize(this[':emp_id']));
      
        __out.push('" placeholder="emp_id param">\n    ');
      
        if (!_.isEmpty(this[":first_name :last_name"])) {
          __out.push('\n      <input class="form-control" type="text" name="first_name" value="');
          __out.push(__sanitize(_.compact(this[':first_name :last_name'].split(' '))[0]));
          __out.push('" placeholder="First name">\n      <input class="form-control" type="text" name="last_name" value="');
          __out.push(__sanitize(_.compact(this[':first_name :last_name'].split(' '))[1]));
          __out.push('" placeholder="Last name">\n    ');
        } else if (!_.isEmpty(this[":last_name, :first_name"])) {
          __out.push('\n      <input class="form-control" type="text" name="first_name" value="');
          __out.push(__sanitize(this[':last_name, :first_name'].split(', ')[1]));
          __out.push('" placeholder="First name">\n      <input class="form-control" type="text" name="last_name" value="');
          __out.push(__sanitize(this[':last_name, :first_name'].split(', ')[0]));
          __out.push('" placeholder="Last name">\n    ');
        } else {
          __out.push('\n      <input class="form-control" type="text" name="first_name" value="');
          __out.push(__sanitize(this[':first_name']));
          __out.push('" placeholder="First name">\n      <input class="form-control" type="text" name="last_name" value="');
          __out.push(__sanitize(this[':last_name']));
          __out.push('" placeholder="Last name">\n    ');
        }
      
        __out.push('\n\n    <div class="position-type-select">\n      <select class=\'select-2 position-type-select form-control\' data-recalculate=\'true\' style=\'width: 100%\' >\n        ');
      
        ref = gon.restaurant.restaurant_position_types;
        for (i = 0, len = ref.length; i < len; i++) {
          positionType = ref[i];
          __out.push('\n          <option value="');
          __out.push(__sanitize(positionType._id.$oid));
          __out.push('">');
          __out.push(__sanitize(positionType.name));
          __out.push('</option>\n        ');
        }
      
        __out.push('\n      </select>\n    </div>\n    <button class=\'btn btn-success\' data-emp-id="');
      
        __out.push(__sanitize(this[':emp_id']));
      
        __out.push('" data-action=\'create\'>Create</button>\n  </div>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["csv_import/duplicated_employee"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var employeeLine, i, len, ref;
      
        __out.push('<div class="employe-is-duplicted-wrapper">\n  <div class=\'employee-is-duplicated\' data-emp-id="');
      
        __out.push(__sanitize(this[0]['employee_data']['emp_id']));
      
        __out.push('">\n    ');
      
        __out.push(__sanitize(this[0]['employee_data']["first_name"]));
      
        __out.push(' ');
      
        __out.push(__sanitize(this[0]['employee_data']["last_name"]));
      
        __out.push(' (');
      
        __out.push(__sanitize(this[0]['employee_data']["emp_id"]));
      
        __out.push(') is a duplicate and appears in:\n\n    ');
      
        ref = this;
        for (i = 0, len = ref.length; i < len; i++) {
          employeeLine = ref[i];
          __out.push('\n      <p class="employee-duplication-line" data-line-number="');
          __out.push(__sanitize(employeeLine['employee-line-number']));
          __out.push('">\n        <strong>Row ');
          __out.push(__sanitize(employeeLine.row_number));
          __out.push(':</strong> ');
          __out.push(__sanitize(employeeLine.filledFields));
          __out.push(' <button class=\'btn btn-danger btn-xs\' data-emp-id="');
          __out.push(__sanitize(employeeLine['employee_data']['emp_id']));
          __out.push('" data-line-number="');
          __out.push(__sanitize(employeeLine['employee-line-number']));
          __out.push('" data-action=\'remove-duplicated-line\'>remove</button>\n      </p>\n    ');
        }
      
        __out.push('\n\n    <p><button class=\'btn btn-success btn-xs\' data-emp-id="');
      
        __out.push(__sanitize(this[0]['employee_data']['emp_id']));
      
        __out.push('" data-action=\'merge-duplications\'>Add / Merge Matched Fields</button>  OR  Press Remove above to remove row from import</p>\n  </div>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["csv_import/employee_in_multiple_positions"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var i, len, positionType, ref;
      
        __out.push('<div class=\'employee-in-multiple-positions-wrapper\' data-emp-id="');
      
        __out.push(__sanitize(this[':emp_id']));
      
        __out.push('" data-line-number="');
      
        __out.push(__sanitize(this['employee-line-number']));
      
        __out.push('" data-first-and-last-name="');
      
        __out.push(__sanitize(this[':first_name :last_name']));
      
        __out.push('" data-last-and-first-name="');
      
        __out.push(__sanitize(this[':last_name, :first_name']));
      
        __out.push('">\n  ');
      
        __out.push(__sanitize(this["first_name"]));
      
        __out.push(' ');
      
        __out.push(__sanitize(this["last_name"]));
      
        __out.push(' (');
      
        __out.push(__sanitize(this["emp_id"]));
      
        __out.push(') belongs to multiple positions. Please, select one of them:\n\n  <div class="position-type-select">\n    <select class=\'select-2 position-type-select form-control\' data-recalculate=\'true\' style=\'width: 100%\' >\n      ');
      
        ref = this["available_positions"];
        for (i = 0, len = ref.length; i < len; i++) {
          positionType = ref[i];
          __out.push('\n        <option value="');
          __out.push(__sanitize(positionType._id.$oid));
          __out.push('">');
          __out.push(__sanitize(this["first_name"]));
          __out.push(' ');
          __out.push(__sanitize(this["last_name"]));
          __out.push(' (');
          __out.push(__sanitize(this["emp_id"]));
          __out.push(') ');
          __out.push(__sanitize(positionType.name));
          __out.push('</option>\n      ');
        }
      
        __out.push('\n    </select>\n  </div>\n\n  <button class=\'btn btn-success\' data-emp-id="');
      
        __out.push(__sanitize(this[':emp_id']));
      
        __out.push('" data-first-and-last-name="');
      
        __out.push(__sanitize(this[':first_name :last_name']));
      
        __out.push('" data-action=\'select-position\'>Select</button>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["csv_import/employees_doesnt_belong_auto_selected_position"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('  <div class=\'employees-doesnt-belong-auto-selected-position-wrapper\' data-emp-id="');
      
        __out.push(__sanitize(this['emp_id']));
      
        __out.push('" data-line-number="');
      
        __out.push(__sanitize(this['employee-line-number']));
      
        __out.push('" data-first-and-last-name="');
      
        __out.push(__sanitize(this[':first_name :last_name']));
      
        __out.push('" data-last-and-first-name="');
      
        __out.push(__sanitize(this[':last_name, :first_name']));
      
        __out.push('">\n  ');
      
        __out.push(__sanitize(this["first_name"]));
      
        __out.push(' ');
      
        __out.push(__sanitize(this["last_name"]));
      
        __out.push(' (');
      
        __out.push(__sanitize(this["emp_id"]));
      
        __out.push(') has been autoselected to ');
      
        __out.push(__sanitize(this["position"].name.toUpperCase()));
      
        __out.push(' but has not been added in TipMetric\'s Employee page under ');
      
        __out.push(__sanitize(this["position"].name.toUpperCase()));
      
        __out.push('.\n\n  <button class=\'btn btn-xs btn-success\' data-emp-id="');
      
        __out.push(__sanitize(this['emp_id']));
      
        __out.push('" data-first-and-last-name="');
      
        __out.push(__sanitize(this[':first_name :last_name']));
      
        __out.push('" data-position-id="');
      
        __out.push(__sanitize(this['position_id']));
      
        __out.push('" data-action=\'link-to-position\'>Add</button>\n  <button class=\'btn btn-xs btn-danger\' data-emp-id="');
      
        __out.push(__sanitize(this['emp_id']));
      
        __out.push('" data-first-and-last-name="');
      
        __out.push(__sanitize(this[':first_name :last_name']));
      
        __out.push('" data-action=\'remove-match\'>Remove</button>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["csv_import/employees_doesnt_belong_to_any_position"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<div class=\'employees-doesnt-belong-to-any-position-wrapper\' data-emp-id="');
      
        __out.push(__sanitize(this.employee_data['emp_id']));
      
        __out.push('" data-line-number="');
      
        __out.push(__sanitize(this['employee-line-number']));
      
        __out.push('" data-first-and-last-name="');
      
        __out.push(__sanitize(this[':first_name :last_name']));
      
        __out.push('" data-last-and-first-name="');
      
        __out.push(__sanitize(this[':last_name, :first_name']));
      
        __out.push('">\n  ');
      
        __out.push(__sanitize(this.employee_data["first_name"]));
      
        __out.push(' ');
      
        __out.push(__sanitize(this.employee_data["last_name"]));
      
        __out.push(' (');
      
        __out.push(__sanitize(this.employee_data["emp_id"]));
      
        __out.push(') is not part of ');
      
        __out.push(__sanitize($("#calculation-form-wrapper").find("select#area_type_id option:selected").text().toUpperCase()));
      
        __out.push(' + ');
      
        __out.push(__sanitize($("#calculation-form-wrapper").find("select#shift_type_id option:selected").text().toUpperCase()));
      
        __out.push(' press remove to continue:\n\n  <button class=\'btn btn-xs btn-danger\' data-emp-id="');
      
        __out.push(__sanitize(this.employee_data['emp_id']));
      
        __out.push('" data-first-and-last-name="');
      
        __out.push(__sanitize(this[':first_name :last_name']));
      
        __out.push('" data-action=\'remove-match\'>Remove</button>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["csv_import/first_and_last_name_convention_mismatch"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<div class=\'first-and-last-name-convention-mismatch-warning-wrapper\' data-row-number="');
      
        __out.push(__sanitize(this['row_number']));
      
        __out.push('" data-emp-id="');
      
        __out.push(__sanitize(this[':emp_id']));
      
        __out.push('" data-square-id="');
      
        __out.push(__sanitize(this[':square_id']));
      
        __out.push('" data-seven-shift-id="');
      
        __out.push(__sanitize(this[':seven_shift_id']));
      
        __out.push('" data-brink-id="');
      
        __out.push(__sanitize(this[':brink_id']));
      
        __out.push('" data-first-and-last-name="');
      
        __out.push(__sanitize(this[':first_name :last_name']));
      
        __out.push('" data-last-and-first-name="');
      
        __out.push(__sanitize(this[':last_name, :first_name']));
      
        __out.push('">\n  <div class=\'first-and-last-name-convention-mismatch-warning\' data-emp-id="');
      
        __out.push(__sanitize(this[':emp_id']));
      
        __out.push('">\n    Missing comma separator in "');
      
        __out.push(__sanitize(this[':last_name, :first_name']));
      
        __out.push('". Click REMOVE to ignore row from Import. Please change name format in Excel file if you would like to Import row.\n\n    <button class=\'btn btn-xs btn-danger\' data-row-number="');
      
        __out.push(__sanitize(this['row_number']));
      
        __out.push('" data-emp-id="');
      
        __out.push(__sanitize(this[':emp_id']));
      
        __out.push('" data-first-and-last-name="');
      
        __out.push(__sanitize(this[':first_name :last_name']));
      
        __out.push('" data-last-and-first-name="');
      
        __out.push(__sanitize(this[':last_name, :first_name']));
      
        __out.push('" data-action=\'remove\'>Remove</button>\n  </div>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["csv_import/last_name_duplication"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var employee, i, len, ref;
      
        __out.push('<div class=\'last-name-duplication-warning-wrapper\' data-emp-id="');
      
        __out.push(__sanitize(this[':emp_id']));
      
        __out.push('" data-line-number="');
      
        __out.push(__sanitize(this['employee-line-number']));
      
        __out.push('">\n  ');
      
        if (this[':first_name :last_name']) {
          __out.push('\n    <div class=\'last-name-duplication-warning\' data-emp-id="');
          __out.push(__sanitize(this[':emp_id']));
          __out.push('">');
          __out.push(__sanitize(this[':first_name :last_name'].split(' ')[0]));
          __out.push(' ');
          __out.push(__sanitize(this[':first_name :last_name'].split(' ')[1]));
          __out.push(' (');
          __out.push(__sanitize(this[':emp_id']));
          __out.push(') has multiple matches... Please, choose right employee to match. ');
          __out.push(__sanitize(this[':first_name :last_name']));
          __out.push(' (');
          __out.push(__sanitize(this['employee-line-number']));
          __out.push(' row in the table) is:\n    </div>\n  ');
        } else if (this[':last_name, :first_name']) {
          __out.push('\n    <div class=\'last-name-duplication-warning\' data-emp-id="');
          __out.push(__sanitize(this[':emp_id']));
          __out.push('">');
          __out.push(__sanitize(this[':last_name, :first_name'].split(', ')[1]));
          __out.push(' ');
          __out.push(__sanitize(this[':last_name, :first_name'].split(', ')[0]));
          __out.push(' (');
          __out.push(__sanitize(this[':emp_id']));
          __out.push(') has multiple matches... Please, choose right employee to match. ');
          __out.push(__sanitize(this[':last_name, :first_name']));
          __out.push(' (');
          __out.push(__sanitize(this['employee-line-number']));
          __out.push(' row in the table) is:\n    </div>\n  ');
        } else {
          __out.push('\n    <div class=\'last-name-duplication-warning\' data-emp-id="');
          __out.push(__sanitize(this[':emp_id']));
          __out.push('">');
          __out.push(__sanitize(this[':first_name']));
          __out.push(' ');
          __out.push(__sanitize(this[':last_name']));
          __out.push(' (');
          __out.push(__sanitize(this[':emp_id']));
          __out.push(') has multiple matches... Please, choose right employee to match. ');
          __out.push(__sanitize(this[':first_name']));
          __out.push(' ');
          __out.push(__sanitize(this[':last_name']));
          __out.push(' (');
          __out.push(__sanitize(this['employee-line-number']));
          __out.push(' row in the table) is:\n    </div>\n  ');
        }
      
        __out.push('\n\n  <div class="employee-select">\n    <select class=\'select-2 employee-select form-control\' data-recalculate=\'true\' style=\'width: 100%\' >\n      ');
      
        ref = this["available_employees"];
        for (i = 0, len = ref.length; i < len; i++) {
          employee = ref[i];
          __out.push('\n        <option value="');
          __out.push(__sanitize(employee.emp_id));
          __out.push('" position-id= "');
          __out.push(__sanitize(employee.position._id.$oid));
          __out.push('">');
          __out.push(__sanitize(employee.first_name));
          __out.push(' ');
          __out.push(__sanitize(employee.last_name));
          __out.push('(');
          __out.push(__sanitize(employee.emp_id));
          __out.push(') (');
          __out.push(__sanitize(employee.position.name));
          __out.push(')</option>\n      ');
        }
      
        __out.push('\n    </select>\n  </div>\n  <button class=\'btn btn-success\' data-last-name="');
      
        __out.push(__sanitize(this[':last_name']));
      
        __out.push('" data-last-and-first-name="');
      
        __out.push(__sanitize(this[':last_name, :first_name']));
      
        __out.push('" data-first-and-last-name="');
      
        __out.push(__sanitize(this[':first_name :last_name']));
      
        __out.push('" data-action=\'match\'>Match</button>\n  <button class=\'btn btn-danger\' data-last-name="');
      
        __out.push(__sanitize(this[':last_name']));
      
        __out.push('" data-last-and-first-name="');
      
        __out.push(__sanitize(this[':last_name, :first_name']));
      
        __out.push('" data-first-and-last-name="');
      
        __out.push(__sanitize(this[':first_name :last_name']));
      
        __out.push('" data-action=\'remove-match\'>Remove</button>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["csv_import/transfer_employees_calculation"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<div class="col-lg-12">\n  <div class="transfer-calculation">\n    <span>');
      
        __out.push(__sanitize(this.date));
      
        __out.push('</span>\n    <span>');
      
        __out.push(__sanitize(this.day));
      
        __out.push('</span>\n    <span>');
      
        __out.push(__sanitize(this.area_name));
      
        __out.push('</span>\n    <span>');
      
        __out.push(__sanitize(this.shift_name));
      
        __out.push('</span>\n    <a class=\'show-calculation btn btn-xs btn-success\' target="_blank" data-calculation-id="');
      
        __out.push(__sanitize(this.id));
      
        __out.push('" href="/show_calculation_get?calculation_id=');
      
        __out.push(__sanitize(this.id));
      
        __out.push('">show</a>\n    ');
      
        if (this.is_locked) {
          __out.push('\n      <span class="label label-primary label-xs">Day Locked</span>\n    ');
        }
      
        __out.push('\n    ');
      
        if (this.is_deactivated) {
          __out.push('\n      <span class="label label-primary label-xs">Employee Deactivated</span>\n    ');
        }
      
        __out.push('\n  </div>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["dashboard/top_employees"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var employee_data, i, len, ref;
      
        ref = this.employees;
        for (i = 0, len = ref.length; i < len; i++) {
          employee_data = ref[i];
          __out.push('\n  <tr>\n    <td>');
          __out.push(__sanitize(this.employees.indexOf(employee_data) + 1));
          __out.push('</td>\n    <td>');
          __out.push(__sanitize(employee_data.employee.first_name));
          __out.push('</td>\n    <td>');
          __out.push(__sanitize(employee_data.employee.last_name));
          __out.push('</td>\n    <td data-sort="');
          __out.push(__sanitize(employee_data.sales_month));
          __out.push('">\n      <div class="month">$');
          __out.push(__sanitize(employee_data.sales_month));
          __out.push('</div>\n    </td>\n    <td data-sort="');
          __out.push(__sanitize(employee_data.tips_collected_month));
          __out.push('">\n      <div class="month">$');
          __out.push(__sanitize(employee_data.tips_collected_month));
          __out.push('</div>\n    </td>\n    <td data-sort="');
          __out.push(__sanitize(employee_data.tips_per_sales_month));
          __out.push('">\n      <div class="month">');
          __out.push(__sanitize(employee_data.tips_per_sales_month));
          __out.push(' %</div>\n    </td>\n  </tr>\n');
        }
      
        __out.push('\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["employees/employee"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var area, i, len, ref;
      
        __out.push('<tr class="');
      
        __out.push(__sanitize(this.tr_class));
      
        __out.push(' employee-line" data-employee-id="');
      
        __out.push(__sanitize(this.id));
      
        __out.push('" data-emp-position-type="');
      
        __out.push(__sanitize(this.position));
      
        __out.push('" data-emp-data-status="');
      
        __out.push(__sanitize(this.emp_data_status));
      
        __out.push('" data-employee-status="');
      
        __out.push(__sanitize(this.status));
      
        __out.push('" data-emp-position-type-id="');
      
        __out.push(__sanitize(this.position_type_id));
      
        __out.push('">\n  <td data-sort="');
      
        __out.push(__sanitize(this.emp_id));
      
        __out.push('"><input disabled="true" contenteditable="false" class="form-control emp-id" data-position="');
      
        __out.push(__sanitize(this.position));
      
        __out.push('" placeholder="Employee id" value="');
      
        __out.push(__sanitize(this.emp_id));
      
        __out.push('"></td>\n  <td data-sort="');
      
        __out.push(__sanitize(this.first_name));
      
        __out.push('"><input disabled="true" contenteditable="false" class="form-control first-name" value="');
      
        __out.push(__sanitize(this.first_name));
      
        __out.push('" placeholder="First name"></td>\n  <td data-sort="');
      
        __out.push(__sanitize(this.last_name));
      
        __out.push('"><input disabled="true" contenteditable="false" class="form-control last-name" placeholder="Last name" value="');
      
        __out.push(__sanitize(this.last_name));
      
        __out.push('"></td>\n  <td class="mobile-number-td" data-sort="');
      
        __out.push(__sanitize(this.mobile_number));
      
        __out.push('"><input disabled="true" contenteditable="false" class="form-control mobile-number" placeholder="Phone number" value="');
      
        __out.push(__sanitize(this.mobile_number));
      
        __out.push('"></td>\n  <td class="allowed-areas">\n    <span class="list">\n      <!-- List of available areas for access will be inserted here -->\n      ');
      
        ref = this.available_areas;
        for (i = 0, len = ref.length; i < len; i++) {
          area = ref[i];
          __out.push('\n        <p data-area-id="');
          __out.push(__sanitize(area.id));
          __out.push('">');
          __out.push(__sanitize(area.name));
          __out.push('</p>\n      ');
        }
      
        __out.push('\n    </span>\n    <span class="form">\n      <!-- Form for available areas for access will be inserted here -->\n    </span>\n  </td>\n  <td class="hidden-from-td" data-sort="');
      
        __out.push(__sanitize(this.hidden_from));
      
        __out.push('">\n    <input type="text" name="hidden_from" id="hidden_from" value="');
      
        __out.push(__sanitize(this.hidden_from));
      
        __out.push('" placeholder="Hidden from" class="form-control datapicker hidden-from" data-required-field="true">\n    <span class="hidden-from" placeholder="');
      
        __out.push(__sanitize(this.hidden_from));
      
        __out.push('" value="">');
      
        __out.push(__sanitize(this.hidden_from));
      
        __out.push('</span>\n  </td>\n  <td class="employee-actions">\n    <button class="btn btn-success btn" type="button" data-emp-action="update">Save</button>\n    <button class="btn btn-success btn" type="button" data-emp-action="create">Save</button>\n    <button class="btn btn-info btn" type="button" data-emp-action="edit">Edit</button>\n    <button class="btn btn-default btn" type="button" data-emp-action="cancel">Cancel</button>\n    <button class="btn btn-danger btn" type="button" data-emp-action="remove">Remove</button>\n    <button class="btn btn-default btn" type="button" data-emp-action="reactivate">Reactivate</button>\n  </td>\n</tr>\n                     \n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["employees/position"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<div class="col-lg-12">\n  <div class="hpanel hgreen" data-position-type-status="');
      
        __out.push(__sanitize(this.status));
      
        __out.push('">\n    <div class="panel-heading hbuilt text-left">\n      <div class="row">\n        <div class="col-lg-6">\n          <h4 class="font-bold">');
      
        __out.push(__sanitize(this.name));
      
        __out.push('</h4>\n        </div>\n        <div class="col-lg-6 text-right">\n        </div>\n      </div>\n    </div>\n    <div class="panel-body">\n      <div class="table-responsive">\n        <table id="employees" cellpadding="1" cellspacing="1" class="table table-condensed" data-position-table-for="');
      
        __out.push(__sanitize(this.name));
      
        __out.push('-table" data-position-name="');
      
        __out.push(__sanitize(this.name));
      
        __out.push('" data-position-type-id="');
      
        __out.push(__sanitize(this.id));
      
        __out.push('">\n          <thead>\n            <tr>\n              <th>Emp ID</th>\n              <th>First Name</th>\n              <th>Last Name</th>\n              <th class=\'mobile-number-td\'>Mobile Number</th>\n              <th>Available areas</th>\n              <th class="no-sort hidden-from-td">Hidden from</th>\n              <th class="no-sort"></th>\n            </tr>\n          </thead>\n          <tbody>\n            <!-- Employees inserted here -->\n          </tbody>\n        </table>\n      </div>\n    </div>\n    <div class="panel-footer">\n      <div class="row">\n        <div class="col-lg-6">\n          <h4 class="font-bold">Total: <span class="total-position-employees-count" data-position-name="');
      
        __out.push(__sanitize(this.name));
      
        __out.push('" data-position-type-id="');
      
        __out.push(__sanitize(this.id));
      
        __out.push('"></span> </h4>\n        </div>\n        <div class="col-lg-6 text-right">\n          <button class="btn btn-info" type="button" data-emp-action="import-employees" data-toggle="modal", data-target=".import-employees-wrapper" data-emp-position-type-id="');
      
        __out.push(__sanitize(this.id));
      
        __out.push('">Import</button>\n          <button class="btn btn-success" type="button" data-emp-action="add" data-emp-position-type="');
      
        __out.push(__sanitize(this.name));
      
        __out.push('" data-emp-position-type-id="');
      
        __out.push(__sanitize(this.id));
      
        __out.push('">Add</button>\n        </div>\n      </div>\n    </div>\n  </div>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["focus_import/report_groups_popup"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var group, i, j, k, len, len1, len2, ref, ref1, ref2, superGroupName;
      
        window.data = this;
      
        __out.push('\n\n');
      
        ref = _.keys(this.groupedBySuperGroupNames);
        for (i = 0, len = ref.length; i < len; i++) {
          superGroupName = ref[i];
          __out.push('\n  <div class="super-group">\n  ');
          console.log("superGroupName is " + superGroupName);
          __out.push('\n\n  <h3>SuperGroup: ');
          __out.push(__sanitize(superGroupName));
          __out.push(' (ID: ');
          __out.push(__sanitize(this.groupedBySuperGroupNames[superGroupName][0].SuperGroup.ID));
          __out.push(')</h3>\n  ');
          ref1 = this.groupedBySuperGroupNames[superGroupName];
          for (j = 0, len1 = ref1.length; j < len1; j++) {
            group = ref1[j];
            __out.push('\n    <div class=\'report-group-item\' data-group-name="');
            __out.push(__sanitize(group.Name));
            __out.push('" data-group-id="');
            __out.push(__sanitize(group.ID));
            __out.push('" data-added-to-list="');
            __out.push(__sanitize(_.contains(this.currentValuesList, group.ID.toString())));
            __out.push('">\n      ');
            __out.push(__sanitize(group.Name));
            __out.push(' (ID: ');
            __out.push(__sanitize(group.ID));
            __out.push(', NonSales: ');
            __out.push(__sanitize(group.NonSales));
            __out.push(', NS: ');
            __out.push(__sanitize(group.NS));
            __out.push(') <a class=\'btn btn-xs btn-success\' data-action=\'add-report-group\'>Add</a><a class=\'btn btn-xs btn-danger\' data-action=\'remove-report-group\'>Remove</a>\n    </div>\n  ');
          }
          __out.push('\n  </div>\n');
        }
      
        __out.push('\n\n<div class="no-super-group">\n<h3>Without SuperGroup</h3>\n');
      
        ref2 = this.groupsWithoutSuperGroups;
        for (k = 0, len2 = ref2.length; k < len2; k++) {
          group = ref2[k];
          __out.push('\n  <div class=\'report-group-item\' data-group-name="');
          __out.push(__sanitize(group.Name));
          __out.push('" data-group-id="');
          __out.push(__sanitize(group.ID));
          __out.push('" data-added-to-list="');
          __out.push(__sanitize(_.contains(this.currentValuesList, group.ID.toString())));
          __out.push('">\n    ');
          __out.push(__sanitize(group.Name));
          __out.push(' (ID: ');
          __out.push(__sanitize(group.ID));
          __out.push(', NonSales: ');
          __out.push(__sanitize(group.NonSales));
          __out.push(', NS: ');
          __out.push(__sanitize(group.NS));
          __out.push(') <a class=\'btn btn-xs btn-success\' data-action=\'add-report-group\'>Add</a><a class=\'btn btn-xs btn-danger\' data-action=\'remove-report-group\'>Remove</a>\n  </div>\n');
        }
      
        __out.push('\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["paychex_import/employees"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var employee, i, len, ref;
      
        __out.push('<table id="built-import-table" class="table table-bordered table-striped">\n  <thead>\n    <tr>\n      <th data-tm-field-name=\'employee-line-number\' data-tm-field-val=\'0\'></th>\n      <th data-column-number=\'1\'>Employee Paychex Id</th>\n      <th data-column-number=\'2\'>First Name</th>\n      <th data-column-number=\'3\'>Last Name</th>\n      <th data-column-number=\'4\'>PayType</th>\n      <th data-column-number=\'5\'>PayCode</th>\n      <th data-column-number=\'6\'>Clocked In</th>\n      <th data-column-number=\'7\'>Clocked Out</th>\n      <th data-column-number=\'8\'>RegularMinutes</th>\n      <th data-column-number=\'9\'>OTMinutes</th>\n      <th data-column-number=\'10\'></th>\n    </tr>\n  </thead>\n  <tbody class="paychex-import-data">\n    ');
      
        ref = this;
        for (i = 0, len = ref.length; i < len; i++) {
          employee = ref[i];
          __out.push('\n      <tr data-row-number=\'');
          __out.push(__sanitize(this.indexOf(employee) + 1));
          __out.push('\' data-shift-id="');
          __out.push(__sanitize(employee.shift_id));
          __out.push('">\n        <td data-tm-field-name=\'employee-line-number\' data-tm-field-val=\'');
          __out.push(__sanitize(this.indexOf(employee) + 1));
          __out.push('\'>');
          __out.push(__sanitize(this.indexOf(employee) + 1));
          __out.push('</td>\n        <td data-column-number=\'1\' data-tm-field-name=\':emp_id\'>');
          __out.push(__sanitize(employee["EmpIdentifier"]));
          __out.push('</td>\n        <td data-column-number=\'2\' data-tm-field-name=\':first_name\'>');
          __out.push(__sanitize(employee["LastName"]));
          __out.push('</td>\n        <td data-column-number=\'3\' data-tm-field-name=\':last_name\'>');
          __out.push(__sanitize(employee["FirstName"]));
          __out.push('</td>\n        <td data-column-number=\'4\' data-tm-field-name=\':job_code\'>');
          __out.push(__sanitize(employee["PayTypeName"]));
          __out.push('</td>\n        <td data-column-number=\'5\' data-tm-field-name=\':job_code_2\'>');
          __out.push(__sanitize(employee["PayTypeCode"]));
          __out.push('</td>\n        <td data-column-number=\'6\' data-tm-field-name=\':start_time\'>');
          __out.push(__sanitize(employee["InTimeActual"]));
          __out.push('</td>\n        <td data-column-number=\'7\' data-tm-field-name=\':end_time\'>');
          __out.push(__sanitize(employee["OutTimeActual"]));
          __out.push('</td>\n        <td data-column-number=\'8\' data-tm-field-name=\':hours_worked\'>');
          __out.push(__sanitize(employee["RegularMinutes"]));
          __out.push('</td>\n        <td data-column-number=\'9\' data-tm-field-name=\':hours_worked_2\'>');
          __out.push(__sanitize(_.reduce(_.map(employee["OTInfo"], function(f) {
            return parseFloat(f["OTMinutes"]);
          }), function(memo, num) {
            return memo + num;
          }) || 0));
          __out.push('</td>\n        <td data-column-number=\'10\' data-tm-field-name=\':actions\'><a class=\'btn btn-xs btn-danger\' data-action=\'remove-row-inline\'><i class=\'fa fa-remove\'></i></a></td>\n      </tr>\n    ');
        }
      
        __out.push('\n  </tbody>\n</table>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["reports/custom_reports/column"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<div id="');
      
        __out.push(__sanitize(_.isEmpty(this._id.$oid) ? 1 : this._id.$oid));
      
        __out.push('" class="custom-report-column" data-key="');
      
        __out.push(__sanitize(this.key));
      
        __out.push('" data-sequence="');
      
        __out.push(__sanitize(this.sequence));
      
        __out.push('" data-record-status="');
      
        __out.push(__sanitize(this.recordStatus));
      
        __out.push('">\n  <div>\n    <span class="title" contenteditable="false">');
      
        __out.push(__sanitize(this.title));
      
        __out.push('</span>\n    <a class="btn btn-xs btn-primary" data-action="edit-column"><i class="fa fa-edit"></i></a>\n    <a class="btn btn-xs btn-danger" data-action="remove-column"><i class="fa fa-remove"></i></a>\n  </div>\n  <div class="select-wrapper">\n  </div>\n  <div class="column-sources-block">\n    <div class="add-field-wrapper">\n      <a class="btn btn-xs btn-primary" data-action="add-column-source">+</a>\n    </div>\n    <div class="column-sources-list">\n    </div>\n  </div>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["reports/custom_reports/template_item"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var column, i, len, ref;
      
        __out.push('<div class=\'col-sm-4 template-item-wrapper\' id=\'');
      
        __out.push(__sanitize(this._id.$oid));
      
        __out.push('\'>\n  <div class=\'template-item\'>\n    <h4>');
      
        __out.push(__sanitize(this.name ? this.name.toUpperCase() : "TEMPLATE"));
      
        __out.push('</h4>\n    ');
      
        ref = this.columns;
        for (i = 0, len = ref.length; i < len; i++) {
          column = ref[i];
          __out.push('\n      <div></div>\n      <div\n        class=\'column\'\n        data-title="');
          __out.push(__sanitize(column.title));
          __out.push('"\n        data-key="');
          __out.push(__sanitize(column.key));
          __out.push('"\n      >\n        ');
          if (column.key === ":calculation") {
            __out.push('\n          ');
            __out.push(__sanitize(column.sequence));
            __out.push('. ');
            __out.push(__sanitize(_.map(column.sources, function(f) {
              return Tipmetric.Controllers.customReportsView.applyColumnSourcesLabels(f);
            }).join('')));
            __out.push('\n        ');
          } else {
            __out.push('\n          ');
            __out.push(__sanitize(column.sequence));
            __out.push('. ');
            __out.push(__sanitize(Tipmetric.Controllers.customReportsView.applyEditLabels(column.key)));
            __out.push('\n        ');
          }
          __out.push('\n      </div>\n    ');
        }
      
        __out.push('\n    <br>\n    <div class="buttons-block">\n      <button class=\'btn btn-success\' data-action=\'apply-template\'>Apply</button>\n      <span class=\'btn btn-danger remove-template\' data-action=\'remove-template\'><i class=\'fa fa-remove\'></i></span>\n    </div>\n  </div>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["reports/custom_reports/templates_popup"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<div id="custom-templates-popup" class="col-sm-12">\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["reports/last_gate_popup"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var calculation, i, len, ref;
      
        __out.push('<div id="last-gate-popup">\n  <div class="wrong-calculations-list">\n    <p>Looks like tip calculation records below were not saved properly or finalized. Please click the "SHOW" button below...Review  and  "SAVE" record. Afterwards, please refresh reports  page for accurate results.</p>\n    ');
      
        ref = this.calculations;
        for (i = 0, len = ref.length; i < len; i++) {
          calculation = ref[i];
          __out.push('\n      <div class="wrong-calculation-line">\n        ');
          __out.push(__sanitize(moment(calculation.date).format("MM/DD/YYYY")));
          __out.push(' ');
          __out.push(__sanitize(moment(calculation.date).format('dddd').toLowerCase().capitalizeFirstLetter()));
          __out.push(' ');
          __out.push(__sanitize(calculation.area_type_name_string.toUpperCase()));
          __out.push(' ');
          __out.push(__sanitize(calculation.shift_type_name_string.toUpperCase()));
          __out.push('  <a href="/show_calculation_get?calculation_id=');
          __out.push(__sanitize(calculation._id.$oid));
          __out.push('&allow_immidiate_save=true" target="_blank" class="btn btn-primary btn-xs">Show</a>\n      </div>\n    ');
        }
      
        __out.push('\n  </div>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["setup/area_block"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var day, i, len, ref;
      
        __out.push('<div id="schedule-');
      
        __out.push(__sanitize(this.areaType._id.$oid));
      
        __out.push('" class="tab-pane">\n  <table cellpadding="1" cellspacing="1" class="table table-responsive table-condensed">\n    <thead>\n      ');
      
        ref = gon.restaurant.days_list;
        for (i = 0, len = ref.length; i < len; i++) {
          day = ref[i];
          __out.push('\n        <th>');
          __out.push(__sanitize(day));
          __out.push('</th>\n      ');
        }
      
        __out.push('\n    </thead>\n    <tbody>\n      <!-- shifts -->\n    </tbody>\n  </table>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["setup/position_shift_td"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<td class="position-shift-td" data-shift-type-id="');
      
        __out.push(__sanitize(this.shiftType._id.$oid));
      
        __out.push('">\n  <input\n    id="checkbox1"\n    type="checkbox"\n    data-action-type="ps-action"\n    data-shift-type="');
      
        __out.push(__sanitize(this.shiftType._id.$oid));
      
        __out.push('"\n    data-position-type="');
      
        __out.push(__sanitize(this.positionType._id.$oid));
      
        __out.push('"\n    data-area-type="');
      
        __out.push(__sanitize(this.areaType._id.$oid));
      
        __out.push('"\n    ');
      
        if (_.contains(this.positionsForShiftAreaData, this.positionType._id.$oid)) {
          __out.push(__sanitize('checked'));
        }
      
        __out.push('\n  >\n</td>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["setup/position_shift_th"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<th class="position-shift-th" data-shift-type-id="');
      
        __out.push(__sanitize(this.shiftType._id.$oid));
      
        __out.push('">');
      
        __out.push(__sanitize(this.shiftType.name));
      
        __out.push('</th>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["setup/position_shift_tr"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var i, len, ref, shiftType;
      
        __out.push('<tr class="position-shift-tr" data-position-type-id="');
      
        __out.push(__sanitize(this.id));
      
        __out.push('" data-position-is-active="true">\n  <td class="position-type-name-td">');
      
        __out.push(__sanitize(this.positionName));
      
        __out.push('</td>\n\n  ');
      
        ref = this.shiftTypes;
        for (i = 0, len = ref.length; i < len; i++) {
          shiftType = ref[i];
          __out.push('\n    <td class="position-shift-td" data-shift-type-id="');
          __out.push(__sanitize(shiftType._id.$oid));
          __out.push('">\n      <input\n      id="checkbox1"\n      type="checkbox"\n      data-action-type="ps-action"\n      data-shift-type="');
          __out.push(__sanitize(shiftType._id.$oid));
          __out.push('"\n      data-position-type="');
          __out.push(__sanitize(this.id));
          __out.push('"\n      data-area-type="');
          __out.push(__sanitize(this.areaType._id.$oid));
          __out.push('"\n      ');
          if (_.filter(this.positionAreaShiftsForArea, function(p) {
            return p.shift_type_id['$oid'] === shiftType._id.$oid;
          }).length > 0) {
            __out.push(__sanitize("checked"));
          }
          __out.push('\n      >\n    </td>\n  ');
        }
      
        __out.push('\n</tr>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["setup/shift_day_tr"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var day, i, len, ref;
      
        __out.push('<tr class="shift-row" data-shift-type-id="');
      
        __out.push(__sanitize(this.shiftType._id.$oid));
      
        __out.push('">\n  ');
      
        ref = gon.restaurant.days_list;
        for (i = 0, len = ref.length; i < len; i++) {
          day = ref[i];
          __out.push('\n    <td>\n      <input id="checkbox1" type="checkbox" data-action-type="as-action" data-as-area-type-id="');
          __out.push(__sanitize(this.areaType._id.$oid));
          __out.push('" data-as-shift-type-id="');
          __out.push(__sanitize(this.shiftType._id.$oid));
          __out.push('" data-as-day="');
          __out.push(__sanitize(day));
          __out.push('" ');
          if (_.contains(this.shiftDataForArea.days, day)) {
            __out.push(__sanitize("checked"));
          }
          __out.push('> ');
          __out.push(__sanitize(this.shiftType.name));
          __out.push('\n    </td>\n  ');
        }
      
        __out.push('\n</tr>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["setup/workload_area_block"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var i, len, ref, shiftType;
      
        __out.push('<div id="workload-');
      
        __out.push(__sanitize(this.areaType._id.$oid));
      
        __out.push('" class="area-workload-table-wrapper tab-pane" data-area-type-id="');
      
        __out.push(__sanitize(this.areaType._id.$oid));
      
        __out.push('">\n  <table cellpadding="1" cellspacing="1" class="table table-responsive table-condensed">\n    <thead>\n      <th></th>\n      ');
      
        ref = this.shiftTypes;
        for (i = 0, len = ref.length; i < len; i++) {
          shiftType = ref[i];
          __out.push('\n        <th class="position-shift-th" data-shift-type-id="');
          __out.push(__sanitize(shiftType._id.$oid));
          __out.push('">');
          __out.push(__sanitize(shiftType.name));
          __out.push('</th>\n      ');
        }
      
        __out.push('\n    </thead>\n    <tbody>\n      <!-- shifts and positions here -->\n    </tbody>\n  </table>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["shared/modal_wrapper"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        __out.push('<div class=\'modal fade universal-modal\' id=\'');
      
        __out.push(__sanitize(this.wrapperId));
      
        __out.push('\' tabindex=\'-1\' role=\'dialog\' aria-labelledby=\'myLargeModalLabel\'>\n  <div class=\'modal-dialog modal-lg\' role=\'document\'>\n    <div class=\'modal-header\'>\n      <button type=\'button\' class=\'close\' data-dismiss=\'modal\' aria-label=\'Close\'><span aria-hidden=\'true\'>&times;</span></button>\n      <h4 class=\'modal-title\'>');
      
        __out.push(__sanitize(this.modalTitle));
      
        __out.push('</h4>\n    </div>\n    <div class=\'modal-body col-sm-12 ');
      
        __out.push(__sanitize(this.wrapperClass));
      
        __out.push('\'>\n\n    </div>\n    <div class=\'modal-footer\'>\n      <div class=\'col-sm-6 col-sm-offset-3 save-template-wrapper\'>\n        <h4>');
      
        __out.push(__sanitize(this.saveTitle));
      
        __out.push('</h4>\n        <div class="col-sm-9">\n          <input class=\'form-control template-name\' type=\'text\' value=\'\' placeholder=\'Template name\'>\n        </div>\n        <div class="col-sm-3">\n          <button type=\'button\' class=\'btn btn-success\' data-action=\'save-template\'>Save</button>\n        </div>\n        <br>\n      </div>\n    </div>\n  </div>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["submit/tip_sharing"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var employee, i, len, ref;
      
        __out.push('<div class="tip-sharing form-group">\n  <div class="col-xs-5">\n    <select class="form-control" name="employee" id="employee_id" >\n      ');
      
        ref = this;
        for (i = 0, len = ref.length; i < len; i++) {
          employee = ref[i];
          __out.push('\n        <option value="');
          __out.push(__sanitize(employee.id));
          __out.push('" selected="selected">');
          __out.push(__sanitize(employee.full_info));
          __out.push('</option>\n      ');
        }
      
        __out.push('\n    </select>\n  </div>\n  <div class="col-xs-5">\n    <input class="form-control tip_sharing_cc_tips" type="text" value="0.0" name="tip_sharing_cc_tips">\n    <input class="form-control tip_sharing_cash_tips" type="text" value="0.0" name="tip_sharing_cash_tips">\n  </div>\n  <div class="col-xs-2">\n     <span class="btn btn-danger btn-sm remove-tip-sharing">-</span>\n  </div>\n</div>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);
(function() { this.JST || (this.JST = {}); this.JST["toast_import/employees"] = function(__obj) {
    if (!__obj) __obj = {};
    var __out = [], __capture = function(callback) {
      var out = __out, result;
      __out = [];
      callback.call(this);
      result = __out.join('');
      __out = out;
      return __safe(result);
    }, __sanitize = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else if (typeof value !== 'undefined' && value != null) {
        return __escape(value);
      } else {
        return '';
      }
    }, __safe, __objSafe = __obj.safe, __escape = __obj.escape;
    __safe = __obj.safe = function(value) {
      if (value && value.ecoSafe) {
        return value;
      } else {
        if (!(typeof value !== 'undefined' && value != null)) value = '';
        var result = new String(value);
        result.ecoSafe = true;
        return result;
      }
    };
    if (!__escape) {
      __escape = __obj.escape = function(value) {
        return ('' + value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      };
    }
    (function() {
      (function() {
        var employee, i, len, ref;
      
        __out.push('<table id="built-import-table" class="table table-bordered table-striped">\n  <thead>\n    <tr>\n      <th data-tm-field-name=\'employee-line-number\' data-tm-field-val=\'0\'></th>\n      <th data-column-number=\'1\'>PayrollId</th>\n      <th data-column-number=\'2\'>EmployeeId</th>\n      <th data-column-number=\'3\'>FirstName</th>\n      <th data-column-number=\'4\'>LastName</th>\n      <th data-column-number=\'5\'>JobCode</th>\n      <th data-column-number=\'6\'>Hoursworked</th>\n      <th data-column-number=\'7\'>Start</th>\n      <th data-column-number=\'8\'>End</th>\n      <th data-column-number=\'9\'>NetSales</th>\n      <th data-column-number=\'10\'>TipAmount</th>\n      <th data-column-number=\'11\'>Service Charge</th>\n      <th data-column-number=\'12\'>DeclaredTips</th>\n      <th data-column-number=\'13\'>FoodSumm + other</th>\n      <th data-column-number=\'14\'>BeverageSumm (Liquor, Beer & Wine)</th>\n      <th data-column-number=\'15\'></th>\n    </tr>\n  </thead>\n  <tbody class="toast-import-data">\n    ');
      
        ref = this.time_entries;
        for (i = 0, len = ref.length; i < len; i++) {
          employee = ref[i];
          __out.push('\n      <tr data-row-number=\'');
          __out.push(__sanitize(this.time_entries.indexOf(employee) + 1));
          __out.push('\'>\n        <td data-tm-field-name=\'employee-line-number\' data-tm-field-val=\'');
          __out.push(__sanitize(this.time_entries.indexOf(employee) + 1));
          __out.push('\'>');
          __out.push(__sanitize(this.time_entries.indexOf(employee) + 1));
          __out.push('</td>\n        <td data-column-number=\'1\' data-tm-field-name=\':emp_id\'>');
          __out.push(__sanitize(employee.employeeInfo.externalEmployeeId));
          __out.push('</td>\n        <td data-column-number=\'2\' data-tm-field-name=\':employee_id\'>');
          __out.push(__sanitize(employee.employeeReference.guid));
          __out.push('</td>\n        <td data-column-number=\'3\' data-tm-field-name=\':first_name\'>');
          __out.push(__sanitize(employee.employeeInfo.firstName));
          __out.push('</td>\n        <td data-column-number=\'4\' data-tm-field-name=\':last_name\'>');
          __out.push(__sanitize(employee.employeeInfo.lastName));
          __out.push('</td>\n        <td data-column-number=\'5\' data-tm-field-name=\':job_code\'>');
          if (employee.jobInfo) {
            __out.push(__sanitize(employee.jobInfo.title));
          }
          __out.push('</td>\n        <td data-column-number=\'6\' data-tm-field-name=\':hours_worked\'>');
          __out.push(__sanitize(employee.regularHours));
          __out.push('</td>\n        <td data-column-number=\'7\' data-tm-field-name=\':start_time\'>');
          __out.push(__sanitize(employee.inDate));
          __out.push('</td>\n        <td data-column-number=\'8\' data-tm-field-name=\':end_time\'>');
          __out.push(__sanitize(employee.outDate));
          __out.push('</td>\n        <td data-column-number=\'9\' data-tm-field-name=\':net_sales\'>');
          __out.push(__sanitize((employee.netSales || 0).toFixed(2)));
          __out.push('</td>\n        <td data-column-number=\'10\' data-tm-field-name=\':tip_amount\'>');
          __out.push(__sanitize((employee.nonCashTips || 0).toFixed(2)));
          __out.push('</td>\n        <td data-column-number=\'11\' data-tm-field-name=\':ag_svc_tips\'>');
          __out.push(__sanitize((employee.agSvcTips || 0).toFixed(2)));
          __out.push('</td>\n        <td data-column-number=\'12\' data-tm-field-name=\':declared\'>');
          __out.push(__sanitize((employee.declaredCashTips || 0).toFixed(2)));
          __out.push('</td>\n        <td data-column-number=\'13\' data-tm-field-name=\':food_summ\'>');
          __out.push(__sanitize((employee.food_summ || 0).toFixed(2)));
          __out.push('</td>\n        <td data-column-number=\'14\' data-tm-field-name=\':beverage_summ\'>');
          __out.push(__sanitize((employee.beverage_summ || 0).toFixed(2)));
          __out.push('</td>\n        <td data-column-number=\'15\' data-tm-field-name=\':actions\'><a class=\'btn btn-xs btn-danger\' data-action=\'remove-row-inline\'><i class=\'fa fa-remove\'></i></a></td>\n      </tr>\n    ');
        }
      
        __out.push('\n  </tbody>\n</table>\n');
      
      }).call(this);
      
    }).call(__obj);
    __obj.safe = __objSafe, __obj.escape = __escape;
    return __out.join('');
  };
}).call(this);






































